// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { nlModuleTips } from './nl-extras.js';
export const nl = {
    ai_governance_pages: {
        nav: {
            inventory: 'AI inventory reviews',
            payment: 'Payment authorities',
            interventions: 'Interventions',
            complaints: 'AI complaints',
            framework: 'Framework record and three lines',
            board: 'Board report: AI figures',
        },
        common: {
            loading: 'Loading…',
            loadFailed: 'This could not be read.',
            retry: 'Try again',
            retrySr: 'read it again',
            notRecorded: 'Not recorded',
            dueNotInForce: 'Not counted: the cadence is not yet in force',
            none: 'None',
            choose: 'Choose…',
            cancel: 'Cancel',
            close: 'Close',
            saving: 'Saving…',
            saveFailed: 'The record was not saved.',
            requiredMark: '(required)',
            missingHeading: 'The record cannot be saved yet. These are missing:',
            directoryFailed: 'The list of people could not be read.',
            directoryPartial: 'Showing {shown} people. The list is not complete.',
            yes: 'Yes',
            no: 'No',
            readOnly: 'Read only',
            noAnswer: 'The server did not answer.',
            listTruncated: 'Showing {shown} of {total} rows. The list is not complete.',
            awaitingOtherApprover: 'Awaiting approval by another person',
            obligationsRetrySr: 'read the dated obligations again',
            systemDatesRetrySr: "read this system's dated obligations again",
            actions: 'Actions',
        },
        states: {
            incomplete: 'Incomplete',
            complete: 'Complete',
            not_recorded: 'Not recorded',
            not_evidenced: 'Not evidenced',
            pending_approval: 'Awaiting a second approval',
            expired: 'Expired',
            matches: 'Matches',
            mismatch: 'Mismatch',
            requested: 'Requested',
            confirmed: 'Confirmed',
            recorded: 'Recorded',
            performed: 'Performed',
            not_mandatory: 'Not mandatory',
            not_applicable: 'Not applicable',
            open: 'Open',
            closed: 'Closed',
            routed: 'Routed',
            stale: 'Stale',
            signed: 'Signed',
            current: 'Current',
            overdue: 'Overdue',
            due: 'Due',
            cadence_not_declared: 'No cadence declared',
            not_yet_in_force: 'Not yet in force',
            not_judged: 'Not judged',
            not_required: 'Not required',
            not_trained: 'Not trained',
            completed: 'Completed',
            unsettled: 'Unsettled',
            present: 'Present',
            absent: 'Absent',
            pending: 'Pending',
            ready: 'Ready',
            active: 'Active',
            suspended: 'Suspended',
            revoked: 'Revoked',
            approved: 'Approved',
            reopened: 'Reopened',
            basis_not_retained: 'Basis not retained',
            reason_notified_basis_recorded: 'Reason notified; basis held',
            needs_evidence: 'Needs evidence',
            evidenced: 'Evidenced',
            not_started: 'Not started',
            in_progress: 'In progress',
            evidence_captured: 'Evidence captured',
            answered: 'Answered',
        },
        missing: {
            risk_tier: 'An approved risk level from a reviewed classification is needed.',
            impact_assessment: 'An approved impact assessment is needed for a high-risk system.',
            validation: 'A validation signed for this version is needed.',
            validation_findings: 'Every finding on this version’s validation must be closed on a re-test.',
            security_tests: 'Every attack category needs a record, and none of poisoning, evasion or extraction may be open for a high-risk version.',
            rollback_plan: 'A rollback plan naming an earlier version of this system is needed.',
            senior_management_approval: 'A high-risk go-live needs the senior management body that approved it, and the date.',
            documents_retained_for_audit: 'The documents kept for audit',
            data_disposition: 'How the data was disposed of or isolated',
            model_disposition: 'How the models were disposed of or isolated',
            configuration_disposition: 'How the configuration was disposed of or isolated',
        },
        revalidation: {
            revalidation_required: 'Re-validation required',
            revalidation_not_required: 'Re-validation not required',
        },
        separation: {
            required: 'Required: a different person re-tests',
            not_required: 'Not required: basis stated',
        },
        securityStates: {
            not_mandatory: 'Not mandatory',
            performed: 'Performed',
            not_applicable: 'Not applicable',
            open: 'Open',
        },
        attackCategories: {
            privacy_breach: 'Privacy breach',
            data_poisoning: 'Data poisoning',
            model_poisoning: 'Model poisoning',
            evasion: 'Evasion',
            model_extraction: 'Model extraction',
            prompt_injection: 'Prompt injection',
            hallucination: 'Hallucination',
            bias_in_generated_output: 'Bias in generated output',
        },
        validationAreas: {
            data_fit: 'Fit of training and test data',
            algorithm_soundness: 'Soundness of algorithm and architecture',
            performance_accuracy: 'Performance and accuracy',
            explainability: 'Explainability',
            fairness_bias_control: 'Fairness and bias control',
            stability: 'Stability',
            third_party_purpose_scope_risk: 'Third party: purpose, scope and risk',
            third_party_black_box_on_own_data: 'Third party: black-box tests on the bank data',
            third_party_output_under_operating_conditions: 'Third party: output under operating conditions',
            third_party_provider_documentation: 'Third party: provider documentation',
            third_party_human_controls_and_monitoring: 'Third party: human controls and monitoring',
        },
        versions: {
            heading: 'Versions, change control and rollback',
            description: 'Each change writes a version row; the prior value is kept. A change after release needs a re-validation decision by a named person.',
            add: 'Add a version',
            addTitle: 'Add a version',
            addSubmit: 'Save version',
            empty: 'No version is recorded yet.',
            colSequence: 'No.',
            colKind: 'Kind',
            colModel: 'Model version',
            colApplied: 'Applied',
            colChanged: 'Changed or stopped',
            colConfig: 'Configuration',
            colTests: 'Test result',
            colRollbackPlan: 'Rollback plan',
            colRestored: 'Version restored',
            colRestoredAt: 'Restored at',
            colReason: 'Reason',
            kind: { baseline: 'Baseline', change: 'Change', rollback: 'Rollback' },
            planRecorded: 'Recorded',
            planNotRecorded: 'Not recorded',
            stillInUse: 'In use',
            recordChange: 'Record a change',
            setPlan: 'Set rollback plan',
            fModel: 'Model version',
            fScope: 'Scope affected',
            fPrompt: 'Prompt or instruction set reference',
            fReferenceData: 'Reference-data reference',
            fIntegration: 'Integration reference',
            fConfig: 'Configuration reference',
            fTests: 'Test result reference',
            fAutonomy: 'Autonomy setting',
            fListedDevelopers: 'Listed developers of this version',
            fRevalidation: 'Re-validation decision',
            fRevalidationReason: 'Reason for the decision',
            revalidationRequiredHint: 'Required when the system is in production.',
            revalidationOptionalHint: 'Needed only for a change in production.',
            changeDecisionHint: 'A change needs a re-validation decision and a reason.',
            changeTitle: 'Record a change',
            changeSubmit: 'Save change',
            fChangeSummary: 'What changed',
            fChangedFields: 'Fields changed',
            changedField: {
                model_version: 'Model version',
                prompt_reference: 'Prompt reference',
                reference_data_reference: 'Reference-data reference',
                integration_reference: 'Integration reference',
                autonomy_level: 'Autonomy setting',
                scope_affected: 'Scope affected',
                configuration_reference: 'Configuration reference',
                test_result_reference: 'Test result reference',
                listed_developer_user_ids: 'Listed developers',
            },
            planTitle: 'Rollback plan',
            planSubmit: 'Save plan',
            fLastGoodVersion: 'Last known-good version',
            fSteps: 'Steps to restore',
            fPlanOwner: 'Owner of the plan',
            fLastRehearsal: 'Date of the last rehearsal',
            rollbackHeading: 'Rollback events',
            rollbackEmpty: 'No rollback is recorded.',
            recordRollback: 'Record a rollback',
            rollbackTitle: 'Record a rollback',
            rollbackSubmit: 'Save rollback',
            fRestoredVersion: 'Version restored',
            fRollbackReason: 'Reason',
        },
        security: {
            fNotMandatory: 'Reason not mandatory',
            notMandatoryHint: 'Required when the state is not mandatory. Only poisoning, evasion and extraction may be recorded so, and never for a high-risk system.',
            findingsOpen: 'Findings',
            findingsTitle: 'Security findings',
            findingsDescription: 'A finding closes only when it has a severity, a finding owner, a due date and a change record, and a re-test by someone other than the person who fixed it.',
            findingsEmpty: 'No finding is recorded for this category.',
            addFinding: 'Open a finding',
            fDescription: 'What the finding is',
            fSeverity: 'Severity',
            fFindingOwner: 'Finding owner',
            fDueDate: 'Due date',
            fChangeRecord: 'Change record',
            noChangeRecord: 'No change record yet',
            recordFix: 'Record the fix',
            closeOnRetest: 'Close on re-test',
            setDetails: 'Set severity, owner, due date and change record',
            fFixedBy: 'Fixed by',
            fRetestDate: 'Date of the re-test',
            fRetester: 'Re-tested by',
            fRetestResult: 'Re-test result',
            findingSubmit: 'Save finding',
            findingNotClosed: 'Not closed',
            findingStatus: {
                open: 'Open',
                fixed: 'Fixed',
                closed: 'Closed',
            },
            severities: {
                low: 'Low',
                medium: 'Medium',
                high: 'High',
                critical: 'Critical',
            },
            periodic: {
                heading: 'Periodic security testing',
                description: "The period and the last test date are the bank's own and are entered here. The platform derives the next test date and reads it. It runs no test.",
                nextDate: 'Next test date: {date}',
                noNextDate: 'No schedule is set for this version.',
                set: 'Set the schedule',
                formTitle: 'Periodic security testing schedule',
                fPeriod: 'Period in months',
                periodHint: 'A whole number of months, from 1 to 120.',
                fLastTest: 'Date of the last test',
                submit: 'Save schedule',
                states: {
                    not_scheduled: 'Not scheduled',
                    scheduled: 'Scheduled',
                    due: 'Due',
                    overdue: 'Overdue',
                },
            },
            heading: 'Security testing by attack category',
            description: 'One record per attack category for the current version. A blank category is shown as not recorded. The platform runs no test.',
            empty: 'No security test is recorded.',
            blank: 'Not recorded',
            colCategory: 'Attack category',
            colState: 'State',
            colOwner: 'Owner',
            colTest: 'Test',
            colFindings: 'Findings',
            findingCount: '{open} open of {total}',
            record: 'Record',
            update: 'Update',
            formTitle: 'Security test',
            submit: 'Save test record',
            fState: 'State',
            fNotApplicable: 'Reason not applicable',
            naHint: 'Required when the state is not applicable.',
            fMethod: 'Method',
            fTester: 'Tester',
            fTestDate: 'Date of the test',
            fResult: 'Result',
            fArtefact: 'Artefact reference',
            fPersonalData: 'Does the artefact hold personal data?',
            fOwner: 'Owner of this category',
            fSeparation: 'Separation of duties on a re-test',
            fSeparationBasis: 'Basis for not separating',
            separationBasisHint: 'Required when separation is not required.',
        },
        validation: {
            heading: 'Independent validation',
            description: 'A validator independent of the development, deployment, operation and supplier signs the validation. The platform records the attestation and does not judge it.',
            add: 'Open a validation',
            empty: 'No validation is signed.',
            colScope: 'Scope',
            colValidator: 'Validator',
            colUnit: 'Validating unit',
            colSigned: 'Signed',
            colTests: 'Tests',
            colFindings: 'Findings',
            closeFinding: 'Close finding',
            colStatus: 'Status',
            signNow: 'Sign off',
            signNowTitle: 'Sign off this validation',
            signNowMessage: 'Signing records the sign-off against the version under test and cannot be undone. The validator’s independence statement is recorded as stated.',
            signNowSubmit: 'Sign off',
            respond: 'Respond',
            respondTitle: 'Owner response',
            fResponse: 'Response',
            respondSubmit: 'Save response',
            responseLabel: 'Owner response',
            addFinding: 'Open a finding',
            signTitle: 'Open a validation',
            signSubmit: 'Open validation',
            fScope: 'Scope of the validation',
            fValidator: 'Validator',
            fUnit: 'Validating unit',
            fIndependence: 'Independence statement',
            independenceHint: 'The validator states how the unit is independent of development, deployment, operation and the supplier.',
            testsLegend: 'Test',
            fTestArea: 'Test area',
            fTestName: 'Test name',
            fTestMethod: 'Method',
            fTestData: 'Data',
            fTestResult: 'Result',
            confirmTitle: 'Confirm the validation',
            confirmMessage: 'Opening records this validation against the version under test. It is not signed until the validator signs it, and signing needs at least one test on the record.',
            findingTitle: 'Finding',
            openSubmit: 'Open finding',
            closeSubmit: 'Close finding',
            fFindingDescription: 'Description',
            fFindingOwner: 'Owner of the finding',
            fDueDate: 'Due date',
            fRetestDate: 'Date of the re-test',
            fRetestResult: 'Result of the re-test',
            retestHint: 'A finding closes only on a re-test dated after the fix.',
        },
        release: {
            heading: 'Release approval',
            description: 'A release is refused until each precondition is recorded. The approver cannot be the owner, and a listed developer cannot approve the release of their own version.',
            add: 'Approve a release',
            empty: 'No release is approved.',
            colStatus: 'Status',
            colGoLive: 'Go-live date',
            colLabel: 'Approval timing',
            colApprover: 'Approved by',
            colApproved: 'Approved on',
            colStale: 'Stale because',
            beforeGoLive: 'Before go-live',
            afterTheFact: 'After the fact',
            staleBecause: 'Changed:',
            formTitle: 'Approve a release',
            formDescription: 'The seven preconditions are checked when you save. A refused release names each one that is missing.',
            checklistLegend: 'Preconditions',
            checklistUnmet: 'Missing',
            checklistNotChecked: 'Checked on save',
            preconditions: {
                risk_tier: 'A risk level from an approved classification run',
                impact_assessment: 'An approved impact assessment, for a high-risk system',
                validation: 'A validation signed for this version',
                validation_findings: 'Every finding on the signed validation is closed on a re-test',
                security_tests: 'A security-test record for every category, with no open poisoning, evasion or extraction category',
                rollback_plan: 'A rollback plan naming an earlier version',
                senior_management_approval: 'For a high-risk go-live, the senior management body and the decision date',
            },
            rehearsalLabel: 'Rollback rehearsal',
            rehearsalNotDone: 'Not rehearsed',
            rehearsalDone: 'Rehearsed on',
            openFindingsLabel: 'Open validation findings',
            gateUnreadable: 'The gate for this version could not be read. Choose the version again to retry.',
            fVersion: 'Version to release',
            fGoLive: 'Go-live date',
            fSeniorBody: 'Senior management body',
            fSeniorDate: 'Date of the senior management decision',
            seniorHint: 'Required for a high-risk go-live.',
            submit: 'Approve release',
            confirmTitle: 'Approve this release',
            confirmMessage: 'This records the release approval for the chosen version. Approving does not deploy the system; the release is made in the bank’s own systems.',
        },
        impact: {
            heading: 'Impact assessment',
            description: 'The five contents of the assessment, the fairness section and the approval. A change of function, data or operating conditions reopens an approved assessment.',
            add: 'Record an impact assessment',
            empty: 'No impact assessment is recorded.',
            colStatus: 'Status',
            colPurpose: 'Purpose',
            colDpia: 'DPIA or reason',
            colFairness: 'Fairness section',
            colApproval: 'Approval',
            approve: 'Approve',
            fairnessAction: 'Fairness section',
            fairnessRecorded: 'Recorded',
            fairnessNotRecorded: 'Not recorded',
            addTitle: 'Record an impact assessment',
            addSubmit: 'Save assessment',
            fPurpose: 'Purpose',
            fScope: 'Scope',
            fMechanism: 'How the system works',
            fOversight: 'How a person oversees and intervenes',
            oversightHint: 'Describe the human oversight and the means of intervention.',
            fFairness: 'Fairness risk',
            fTransparency: 'Transparency risk',
            fExplainability: 'Explainability risk',
            fDiscrimination: 'Risk of discrimination against customers',
            fDpia: 'DPIA record identifier',
            dpiaHint: 'The identifier of the DPIA record. The DPIA text is not copied.',
            fPersonalDataNotApplicable: 'Reason the personal-data assessment does not apply',
            personalDataHint: 'Give a DPIA identifier, or a reason it does not apply.',
            fControls: 'Controls',
            fMonitoring: 'Monitoring plan',
            fIncident: 'Incident response',
            fairnessTitle: 'Fairness section',
            fairnessSubmit: 'Save fairness section',
            fFairnessAssessment: 'Fairness assessment',
            fBiasControls: 'Bias controls',
            fVulnerable: 'Vulnerable groups',
        },
        inventory: {
            heading: 'Annual inventory review',
            description: 'The annual review of this system for the year shown. A profile not confirmed by its due date reads overdue.',
            confirm: 'Confirm review',
            empty: 'This system has no review for this year.',
            colPeriod: 'Period',
            colAnchor: 'Anchor',
            colDue: 'Due',
            colReading: 'Reading',
            anchorKind: {
                first_recorded: 'First recorded',
                last_confirmation: 'Last confirmation',
                review_duty_effective: 'Review duty took effect',
            },
            formTitle: 'Confirm the annual review',
            submit: 'Confirm review',
            fOutcome: 'Outcome',
            outcome: { confirmed: 'Confirmed without change', changed: 'Changed' },
            fChangeSummary: 'What changed',
        },
        components: {
            heading: 'AI component register',
            description: 'Libraries, models, datasets and dependencies, each row with its integrity check. A row missing a field reads incomplete.',
            empty: 'No component register is recorded.',
            noRows: 'The register lists no component yet.',
            record: 'Record the register',
            integrity: 'Record an integrity check',
            advisory: 'Record an advisory',
            supplyReview: 'Record a supply-chain review',
            incompleteCount: '{count} rows are incomplete.',
            supplyReviewReading: 'Supply-chain review',
            colKind: 'Kind',
            colName: 'Name',
            colSupplier: 'Supplier or developer',
            colVersion: 'Version',
            colIntegrity: 'Integrity check',
            colReading: 'Reading',
            missingFields: 'Missing',
            integrityHeading: 'Integrity checks',
            integrityEmpty: 'No integrity check is recorded.',
            advisoryHeading: 'Advisories',
            advisoryEmpty: 'No advisory is recorded.',
            kind: { library: 'Library', model: 'Model', dataset: 'Dataset', dependency: 'Dependency' },
            formTitle: 'Record the component register',
            formDescription: 'Saving appends a new snapshot. The earlier register stays readable.',
            submit: 'Save',
            rowLegend: 'Component {n}',
            fKind: 'Kind',
            fName: 'Name',
            fSupplier: 'Supplier or developer',
            fVersion: 'Version',
            fLicence: 'Licence',
            fCheck: 'Integrity check value',
            fOrigin: 'Origin',
            fAffected: 'Attack categories this component affects',
            addRow: 'Add a component',
            removeRow: 'Remove',
            integrityFormTitle: 'Record an integrity check',
            fComponent: 'Component',
            fMethod: 'Method of check',
            fCheckValue: 'Check value',
            fCheckedOn: 'Date checked',
            advisoryFormTitle: 'Record an advisory',
            fAdvisoryRef: 'Advisory reference',
            fReceivedOn: 'Date received',
            fRiskDecision: 'Risk decision',
            reviewFormTitle: 'Record a supply-chain review',
            fReviewSummary: 'Summary of the review',
        },
        provenance: {
            heading: 'Data provenance and operational logs',
            description: 'The sources of training, test and operating data, the quality and leakage references, and the log retention with its citation.',
            record: 'Record provenance',
            empty: 'No provenance is recorded.',
            sources: 'Sources',
            thirdParty: 'Third party',
            synthetic: 'Synthetic data',
            qualityPolicy: 'Data-quality policy',
            leakageControl: 'Leakage control',
            retention: 'Log retention',
            citation: 'Citation',
            citationMissing: 'No citation recorded',
            revision: 'Revision',
            dataUse: { training: 'Training', test: 'Test', operating: 'Operating' },
            formTitle: 'Record data provenance',
            submit: 'Save provenance',
            fDataUse: 'Data use',
            fSource: 'Source of the data',
            fLawfulBasis: 'Lawful-basis statement',
            fThirdParty: 'Supplied by a third party',
            fSupplier: 'Supplier name',
            fWarranty: 'Warranty of source',
            fSharing: 'Right to share',
            fSynthetic: 'Uses synthetic data',
            fSyntheticAssessment: 'Synthetic-data assessment',
            fInputLog: 'Input-processing log reference',
            fQualityPolicy: 'Data-quality policy reference',
            fLeakage: 'Leakage control reference',
            fRetention: 'Log retention period',
            retentionHint: 'The period the bank sets for operational logs.',
            fCitation: 'Citation of the rule that sets the period',
            citationHint: 'Required with a retention period.',
        },
        decisions: {
            heading: 'Decision records',
            description: 'For each automated decision that affects an obligation, a financial position or access to a product: the basis and key factors, and the explanation given.',
            add: 'Record a decision',
            empty: 'No decision is recorded.',
            colDate: 'Decision date',
            colRef: 'Reference',
            colEffect: 'Effect',
            colBasis: 'Basis',
            colExplanation: 'Explanation given',
            colVersion: 'Version on the decision date',
            versionLegacy: 'Version current when recorded',
            versionNone: 'No version was in force on the decision date',
            basisRuleHeading: "The platform's reading",
            basisRule: "ComplianceOne's reading of Điều 20 khoản 5: for every automated decision that directly affects a customer's legal liability, financial obligations, or access to or use of a product or service, the basis and the main factors are kept, whatever the decision's purpose. For a decision made to prevent fraud, scams, money laundering or terrorism financing, or to ensure cyber security, your organisation need only tell the customer a reason suited to its risk review and to keeping the customer's assets safe; it may tell the customer more. No explanation may disclose source code, detailed algorithms, parameter sets, or business or technology secrets. This is the platform's reading of the text, not a legal review.",
            effect: {
                legal_or_financial_obligation: 'Legal liability or financial obligation',
                customer_access: 'Access to a product or service',
            },
            category: {
                standard: 'Standard',
                fraud: 'Fraud',
                scam: 'Scams',
                money_laundering: 'Money laundering',
                terrorism_financing: 'Terrorism financing',
                cyber_security: 'Cyber-security',
            },
            formTitle: 'Record a decision',
            submit: 'Save decision',
            fDate: 'Decision date',
            fRef: 'Decision reference',
            fEffect: 'Effect',
            fCategory: 'Decision category',
            categoryHint: 'For fraud, scams, money laundering, terrorism financing or cyber security, the institution need only tell the customer a reason; the basis and key factors are still stored.',
            fBasis: 'Basis for the decision',
            basisHint: 'Leave empty only if the basis was not kept; the record then shows basis not retained.',
            fKeyFactors: 'Key factors',
            factorsHint: 'One factor per line.',
            fExplanation: 'Explanation given to the customer',
            fDelivered: 'Date delivered',
            fChannel: 'Channel',
            fStatedReason: 'Stated reason',
            restrictedHint: 'What the customer is told. For this category the institution need only tell the customer a reason; if it told the customer more, record that explanation too.',
            restrictedBasisHint: 'Stored, not told to the customer. Leave empty only if the basis was not kept; the record then shows basis not retained.',
        },
        disclosures: {
            heading: 'AI disclosures in use',
            description: 'The text in use on the date shown, per kind, product and channel. A new text is recorded beside the old one; it never replaces it.',
            add: 'Record a disclosure',
            notRecorded: 'No disclosure is recorded for this date.',
            colKind: 'Kind',
            colProduct: 'Product',
            colChannel: 'Channel',
            colVersion: 'Version',
            colGoLive: 'Go-live date',
            colText: 'Text',
            kind: {
                pre_service_information: 'Information before the service',
                ai_interaction_statement: 'Statement that the customer is dealing with AI',
                internal_user_information: 'Information for internal users',
            },
            formTitle: 'Record a disclosure',
            submit: 'Save disclosure',
            fOn: 'Date in use',
            fKind: 'Kind',
            fProduct: 'Product',
            fChannel: 'Channel',
            fGoLive: 'Go-live date',
            fText: 'Text in use',
            textHint: 'Enter or paste the text. The platform does not write it.',
            fPurpose: 'Purpose',
            fScope: 'Scope',
            fLimits: 'Limits',
            fGuidance: 'Interpretation guidance',
            fRoute: 'Route to ask for human intervention',
            fFeedback: 'Feedback channel',
            fSameDayReason: 'Reason for a second version on the same day',
            sameDayHint: 'Required only when another version of this text goes live on the same day.',
            noChannels: 'Record a channel before recording a disclosure.',
            pendingHeading: 'Versions awaiting a second person',
            pendingDescription: 'A version takes effect only when a second person approves it. The person who recorded it cannot approve it.',
            pendingEmpty: 'No version is awaiting approval.',
            inUseHeading: 'In use on the date shown',
            approve: 'Approve version',
        },
        payment: {
            heading: 'Automated payment authority',
            description: 'An authority per customer agreement: the eleven elements, the limits against the ceiling the installed framework declares, and suspension or revocation. The platform does not execute or stop a payment.',
            add: 'Record an authority',
            empty: 'No payment authority is recorded for this system.',
            colState: 'State',
            colAgreement: 'Agreement',
            colAutonomy: 'Autonomy on the system',
            colPerDay: 'Per-day limit',
            colCeiling: 'Ceiling source',
            colMissing: 'Missing elements',
            colRelief: 'Confirmation relief',
            relief: {
                heading: 'Transaction confirmation relief',
                open: 'Confirmation relief',
                openSr: 'for agreement {agreement}',
                close: 'Close the relief',
                intro: 'What the installed framework declares about relief from transaction confirmation for payments the AI system executes within this agreement, and which of its conditions are recorded on this authority.',
                notApplied: 'The platform records what a person states. It does not apply the relief, confirm, count or classify a transaction, or compute any total the referenced instrument defines.',
                statedBy: 'Stated by {framework}, {provision}.',
                refersTo: 'Refers to',
                amendedBy: 'as amended by {list}',
                lifts: 'Confirmation it lifts',
                quantities: 'Totals the value is left out of',
                annexVersion: 'Annex versions',
                conditionsHeading: 'Conditions',
                colCondition: 'Condition',
                colBasis: 'Basis',
                colRecorded: 'On this authority',
                colMissing: 'Still to record',
                recorded: 'Recorded',
                notRecorded: 'Not recorded',
                effectHeading: 'Effect',
                notLiftedHeading: 'What it does not lift',
                reachHeading: 'Reach',
                namedSection: '{section}: {framework}, {provision}',
                reading: {
                    conditions_not_recorded: 'Conditions not recorded',
                    conditions_recorded: 'Conditions recorded',
                    relied_on: 'Relied on',
                    relied_on_conditions_no_longer_recorded: 'Relied on; conditions no longer recorded',
                },
                reliance: 'Relied on by {who} on {when}. Reason: {reason}',
                rely: 'Record that the unit relies on this relief',
                relyDisabled: 'Reliance can be recorded once every condition is recorded on this authority.',
                relyTitle: 'Record reliance on the relief',
                relyDescription: 'Records who relies on the relief for this agreement, when and why. It cannot be recorded while no approved terms are in force, by the person who wrote the terms in force, or by anyone who wrote an approved version that set or changed either of the two statements. The platform does not apply the relief.',
                relySubmit: 'Record reliance',
                fReason: 'Why the unit relies on the relief',
                relied: 'Reliance on the relief is recorded.',
                statements: 'Record the statements',
                statementsTitle: 'Record the statements the relief rests on',
                statementsDescription: 'Saved as a change to the authority. It takes effect when a second person approves it.',
                statementsSubmit: 'Save as a change',
                statementsSaved: 'The change is saved. A second person must approve it.',
                statementsPending: 'The statements can be recorded once a second person approves the change pending on this authority.',
                fAiExecution: 'How the unit tells a transaction the system executed',
                aiExecutionHint: 'The reference of the marker, field or log that shows a transaction was executed by the system and not by a person.',
                fAutomationConditions: 'Statement that the conditions for automation hold',
                automationConditionsHint: 'A person states that each condition the instrument sets for automating this system holds.',
                evidence: {
                    ai_execution_reference: 'Statement of how AI-executed transactions are identified',
                    automation_conditions_statement: 'Statement that the conditions for automation hold',
                    terms_approved: 'Terms approved by a second person',
                    ceiling_checked: 'Per-day limit checked against the declared ceiling',
                    authority_active: 'Authority active (not suspended, revoked or expired)',
                },
                noneDeclared: 'No installed framework declares a relief from transaction confirmation for payment authorities.',
                unreadable: 'A confirmation relief an installed framework declares could not be read.',
                notDeclared: 'Not declared',
                listReferenceOnly: 'Declared by a framework installed for reference only',
                listNotOperational: 'Declared by a framework that is not operational',
                listUnreadable: 'Declared, but could not be read',
                activationUpcoming: 'The framework that declares this relief is not yet in force. The relief applies from the date it comes into force; reliance can be recorded before then.',
            },
            scopeHeading: 'Scope flagged for legal review',
            scopeIntro: 'An organisation the installed framework names in one provision and leaves out of its scope provision. The platform treats it as neither covered nor excluded.',
            scopeNamed: 'Named in {named_in}; not named in {absent_from}.',
            scopeStatus: { under_legal_review: 'Flagged for legal review' },
            scopeSource: 'Stated by {framework}',
            scopeUnreadable: 'A scope qualification an installed framework declares could not be read.',
            scopeLoadFailed: 'The scope notice could not be read.',
            scopeRetrySr: 'read the scope notice again',
            recordEvent: 'Record an event',
            approve: 'Approve this change',
            exportHistory: 'Export authority history',
            customerClass: { individual: 'Individual', organisation: 'Organisation' },
            element: {
                customer_class: 'Customer class',
                agreement_reference: 'Agreement reference',
                automated_transaction_scope: 'Scope of automated transactions',
                per_transaction_limit_vnd: 'Per-transaction limit',
                per_day_limit_vnd: 'Per-day limit',
                valid_from: 'Valid from',
                valid_until: 'Valid until',
                suspension_conditions: 'Suspension conditions',
                customer_termination_clause_reference: 'Customer termination clause',
                identity_verification_statement: 'Identity-verification statement',
                pause_or_cancel_tool_reference: 'Customer pause or cancel tool',
            },
            formTitle: 'Record a payment authority',
            formDescription: 'A per-day limit above the ceiling the installed framework declares is refused, and the refusal names that framework and its clause.',
            submit: 'Save authority',
            fCustomerClass: 'Customer class',
            fAgreement: 'Agreement reference (a file or a reference)',
            agreementHint: 'The agreement is recorded by reference.',
            fScope: 'Scope of automated transactions',
            fScopeItem: 'Transaction type',
            scopeHint: 'Add each type, one at a time.',
            addScope: 'Add to the scope',
            removeScope: 'Remove',
            fPerTransaction: 'Per-transaction limit (VND)',
            fPerDay: 'Per-day limit (VND)',
            perDayHint: 'Checked against the ceiling the installed framework declares.',
            fValidFrom: 'Valid from',
            fValidUntil: 'Valid until',
            fSuspension: 'Suspension conditions',
            fTermination: 'Customer termination clause reference',
            fIdentity: 'Identity-verification statement',
            identityHint: 'Entered by the person who checked the verification. The platform does not check biometrics.',
            fPauseTool: 'Customer pause or cancel tool reference',
            fLead: 'Payments lead',
            eventTitle: 'Record an event',
            eventSubmit: 'Save event',
            eventKind: {
                activate: 'Activate',
                suspend: 'Suspend',
                revoke: 'Revoke',
                execution_summary: 'Execution summary',
            },
            fEventKind: 'Event',
            fReportedAt: 'Time reported',
            reportedHint: 'The time the stop was reported.',
            fImplementedAt: 'Time implemented',
            implementedHint: 'Leave empty until the stop is implemented; the state then reads not evidenced.',
            fReason: 'Reason',
            fCustomerRef: 'Customer reference',
            fExecutedOn: 'Date executed',
            fTxCount: 'Number of transactions',
            fLargest: 'Largest transaction (VND)',
            fLargestRef: 'Reference of the largest transaction',
            fDailyTotal: 'Daily total (VND)',
        },
        interventions: {
            heading: 'Human oversight and interventions',
            description: 'Each intervention is recorded with its reason. A pause or stop reads requested until a person enters the confirmed time.',
            add: 'Record an intervention',
            empty: 'No intervention is recorded.',
            colKind: 'Kind',
            colContent: 'Content changed',
            colReason: 'Reason',
            colReading: 'State',
            colRequested: 'Requested at',
            colConfirmed: 'Confirmed at',
            kind: {
                content_change: 'Content change',
                override: 'Override',
                pause: 'Pause',
                stop: 'Stop',
                emergency_stop: 'Emergency stop',
                manual_operation: 'Manual operation',
                resume: 'Resume',
            },
            confirm: 'Enter confirmed time',
            formTitle: 'Record an intervention',
            submit: 'Save intervention',
            fKind: 'Kind',
            fContent: 'Content changed',
            fReason: 'Reason',
            fRequestedAt: 'Time requested',
            requestedHint: 'Required for a pause, stop, emergency stop or manual operation.',
            confirmTitle: 'Enter the confirmed time',
            confirmSubmit: 'Save confirmed time',
            fConfirmedAt: 'Time confirmed in the bank’s system',
            confirmedHint: 'Taken from the bank’s own system; the platform does not read it.',
            alreadyRetired: 'This system is retired.',
            retireHeading: 'Retirement',
            retireSectionDescription: 'Retire this system when it is taken out of service; retiring it needs a decommissioning record.',
            retire: 'Retire this system',
            retireTitle: 'Retire this system',
            retireDescription: 'A retired system needs a decommissioning record: the documents kept for audit, and how the data, models and configuration were disposed of or isolated.',
            retireSubmit: 'Retire system',
            decom: {
                documents_retained_for_audit: 'Documents retained for audit',
                data_disposition: 'Disposition of the data',
                model_disposition: 'Disposition of the models',
                configuration_disposition: 'Disposition of the configuration',
            },
            colChannel: 'Channel',
            colDecision: 'Decision',
            fChannel: 'Channel',
            fDecision: 'Decision identifier',
            decisionHint: 'Required for a content change, an override or a manual operation. Leave blank for a pause, stop, emergency stop or resume.',
            fResumes: 'Intervention resumed',
            resumeHint: 'The pause, stop or emergency stop this resume follows.',
            requestedNote: 'The time of the request is recorded when you save.',
            approve: 'Approve',
            awaitingApproval: 'Awaiting a second person',
            colApproval: 'Second person',
            approved: 'Approved',
            notRequired: 'Not required',
        },
        channels: {
            heading: 'Channels and supervisor lines',
            description: 'Each AI-assisted channel of this system, with the supervisor’s line over it. Interventions and disclosures name a channel.',
            add: 'Record a channel',
            empty: 'No channel is recorded for this system.',
            colName: 'Channel',
            colLine: 'Supervisor’s line',
            colRecorded: 'Recorded on',
            export: 'Export log and disclosures',
            formTitle: 'Record a channel',
            submit: 'Save channel',
            fName: 'Channel name',
            fLine: 'Supervisor’s line',
            lineHint: 'The unit or role that supervises this channel.',
            exportTitle: 'Export the channel log',
            exportSubmit: 'Export',
            exportFrom: 'From',
            exportTo: 'To',
            colDualControl: 'Dual control',
            fDualControl: 'A second person approves each pause, stop and resume',
            dualControlHint: 'When set, a pause, a stop and a resume take effect only after a second person approves it. An emergency stop is never held.',
            registerExport: 'Export the pause and stop register',
            registerTitle: 'Export the pause and stop register',
        },
        channelStatus: {
            heading: 'Channel status',
            description: 'The state of each channel today, read from its record: the disclosures in use, the versions awaiting a second person, and the interventions still open. Read only.',
            empty: 'No channel is recorded for this system.',
            colChannel: 'Channel',
            colState: 'State',
            colDisclosures: 'Disclosures in use',
            colAwaiting: 'Awaiting a second person',
            colOpen: 'Open interventions',
            noneOpen: 'None',
            openAwaiting: 'awaiting a second person',
            openUnanswered: 'escalation unanswered',
            openRequested: 'awaiting the bank’s confirmation',
            state: {
                running: 'Running',
                paused: 'Paused',
                stopped: 'Stopped',
                emergency_stopped: 'Emergency stopped',
            },
        },
        escalations: {
            heading: 'Escalations and responses',
            description: 'Every intervention is escalated to the system owner for cause analysis and to the second line for the board report. Each escalation stays open until its addressee records a response.',
            empty: 'No escalation is raised.',
            colAddressee: 'Addressed to',
            colIntervention: 'Intervention',
            colRaised: 'Raised on',
            colResponse: 'Response',
            none: 'No one is named; the task went to the person who recorded it.',
            respond: 'Record a response',
            respondTitle: 'Record a response',
            respondSubmit: 'Save response',
            fResponse: 'Response',
            responseHint: 'Recorded once, with the addressee’s name and the time.',
            addressee: {
                owner: 'System owner',
                second_line: 'Second line',
            },
            colAnsweredAs: 'Answered as',
            answeredAs: {
                owner: 'System owner',
                attest_fallback: 'Attest holder, in place of the owner',
            },
        },
        briefings: {
            heading: 'Staff briefings on the system’s limits',
            description: 'Who was briefed, when, and on which version of the procedure. Completion of training is held in the training register.',
            add: 'Record a briefing',
            empty: 'No briefing is recorded.',
            colDate: 'Briefed on',
            colProcedure: 'Procedure version',
            colAttendees: 'Briefed',
            formTitle: 'Record a briefing',
            submit: 'Save briefing',
            fDate: 'Day of the briefing',
            fLimits: 'Limits covered',
            fProcedure: 'Procedure version',
            fAttendees: 'Staff briefed (user IDs, separated by commas)',
            fTraining: 'Training record reference (optional)',
            addAttendee: 'Add this person',
            removeAttendee: 'Remove',
        },
        monitoring: {
            heading: 'Performance and behaviour monitoring',
            description: 'Comparisons of predicted and actual outcomes entered or imported, the owner’s thresholds, and events that stay open until a person closes them.',
            addComparison: 'Record a comparison',
            addEvent: 'Record an event',
            setThresholds: 'Set thresholds',
            eventsHeading: 'Events',
            eventsEmpty: 'No event is recorded.',
            comparisonsHeading: 'Comparisons',
            comparisonsEmpty: 'No comparison is recorded.',
            colReading: 'State',
            colKind: 'Kind',
            colDetected: 'Detected on',
            colDescription: 'Description',
            colResponse: 'Response',
            colMetric: 'Metric',
            colMeasured: 'Measured on',
            colPredicted: 'Predicted',
            colActual: 'Actual',
            colDeviation: 'Deviation',
            colCrossed: 'Threshold crossed',
            crossedYes: 'Yes',
            crossedNo: 'No',
            close: 'Close with a response',
            kind: {
                output_deviation: 'Output deviation',
                quality_decline: 'Quality decline',
                bias: 'Bias',
                model_drift: 'Model drift',
                data_drift: 'Data drift',
                threshold_crossing: 'Threshold crossed',
            },
            source: { entered: 'Entered by a person', imported: 'Imported' },
            eventTitle: 'Record an event',
            submitEvent: 'Save event',
            fKind: 'Kind',
            fDescription: 'Description',
            fMetric: 'Metric',
            comparisonTitle: 'Record a comparison',
            submitComparison: 'Save comparison',
            fMeasuredOn: 'Measured on',
            fPredicted: 'Predicted value',
            fActual: 'Actual value',
            fSource: 'Source',
            closeTitle: 'Close the event',
            submitClose: 'Close event',
            fResponse: 'Response',
            thresholdsTitle: 'Thresholds',
            submitThresholds: 'Save thresholds',
            thresholdLegend: 'Threshold {n}',
            fMaxDeviation: 'Largest permitted deviation',
            addThreshold: 'Add a threshold',
        },
        duties: {
            heading: 'Duty holders and competence',
            description: 'The people named against a duty to monitor, detect deviation or intervene. Training is read against a completed assignment; the review falls due yearly.',
            add: 'Name a duty holder',
            capacity: 'Record capacity',
            empty: 'No duty holder is named.',
            colPerson: 'Person',
            colDuty: 'Duty',
            colTraining: 'Training',
            colCompetence: 'Competence review',
            colDue: 'Review due',
            duty: { monitor: 'Monitor', detect_deviation: 'Detect deviation', intervene: 'Intervene' },
            review: 'Record a competence review',
            addTitle: 'Name a duty holder',
            submit: 'Save duty holder',
            fPerson: 'Person',
            fDuty: 'Duty',
            fTraining: 'Training assignment reference (optional)',
            trainingHint: 'A training completion counts only against a completed assignment.',
            reviewTitle: 'Competence review',
            submitReview: 'Save review',
            fReviewedOn: 'Date reviewed',
            fSummary: 'Summary of the review',
            capacityTitle: 'Capacity record',
            submitCapacity: 'Save capacity',
            fBasis: 'Basis',
            basis: {
                unit_builds_or_runs: 'The unit builds or runs the system',
                outsourced: 'Outsourced',
            },
            fCapacity: 'Capacity statement',
            fSupplierName: 'Supplier name',
            supplierHint: 'Required for an outsourced system.',
            fSupplierCapacity: 'Supplier capacity statement',
            fAssessedOn: 'Date the supplier was assessed',
        },
        bankingTriggers: {
            heading: 'Banking triggers',
            intro: 'The trigger facts recorded for this system, compared with the thresholds the installed framework declares. Where the triggers reach the system and a fact exceeds a threshold, the platform raises a classification proposal, shown here and in the classification review. A person classifies; the platform sets no tier.',
            listLabel: 'Within a high-risk entry of the official list (conditions other than value)',
            listStatus: { listed: 'Within an entry', not_listed: 'Not within an entry' },
            listNotRecorded: 'Not recorded',
            listRecorded: 'Recorded by {user} on {date}',
            listHint: 'Recorded on the system form: whether the system meets the conditions of a high-risk entry of the official list other than the transaction or loan value, which the thresholds supply. You record the answer; the platform does not decide it.',
            preconditionLabel: 'Precondition',
            precondition: "The triggers reach only a system within an entry of {list}, on that entry's conditions other than value ({provision}).",
            preconditionNone: 'No installed framework declares a precondition: the triggers reach every system.',
            scopeLabel: 'Reach',
            scope: {
                applies: 'The triggers reach this system',
                not_applicable: 'Not within an entry of the list: no banking trigger applies',
                not_recorded: 'Not recorded: whether the triggers reach this system is not known',
                precondition_unreadable: 'The precondition an installed framework declares could not be read, so the platform cannot show whether the triggers reach this system, and raises no proposal',
            },
            factsNotRecorded: 'No trigger facts are recorded for this system.',
            noThresholds: 'No installed framework declares a banking threshold.',
            unreadable: '{count} declaration(s) an installed framework made could not be read.',
            exceedancesHeading: 'Thresholds exceeded',
            noExceedance: 'No recorded fact exceeds a declared threshold.',
            colThreshold: 'Trigger',
            colValue: 'Recorded value (VND)',
            colAmount: 'Threshold amount (VND)',
            colProvision: 'Provision',
            measure: {
                per_transaction_payment: 'One payment',
                daily_total_per_customer: "One customer's daily total",
                credit_amount: 'Credit amount',
            },
            customerClass: {
                individual: 'individual customer',
                organisation: 'organisation customer',
                any: 'any customer',
            },
            comparison: { greater_than: 'over', greater_or_equal: 'at least' },
            thresholdLabel: '{measure}, {class}: {comparison} the amount',
            proposalHeading: 'Classification proposal raised',
            proposalText: 'Run a classification screening to review them: the classification review shows them beside the tier control. The tier is the one a reviewer records there.',
            noProposal: 'No classification proposal is raised.',
            reviewProposalHeading: 'Banking-trigger proposal for this review',
            reviewProposalIntro: 'This system is recorded as within a high-risk entry of the official list the installed framework names, and a recorded fact exceeds the thresholds below. Weigh them in this review; the tier is the one you record.',
            reviewProposalIntroNoPrecondition: 'A recorded fact on this system exceeds the thresholds below, which the installed framework applies to every system. Weigh them in this review; the tier is the one you record.',
            reviewProposalItem: '{threshold}: {value} VND against {amount} VND ({provision})',
        },
        systemDates: {
            heading: 'Dated obligations for this system',
            intro: 'The commencement dates the installed framework declares, as they fall on this system: whether it was in operation before the framework took effect, and whether your organisation has a system of the tier a carve-out names.',
            inOperationSince: 'In official operation since',
            inOperationNotRecorded: 'Not recorded. Set it on the system form; until then a date that depends on it is not shown.',
            empty: 'No installed framework declares a dated obligation for AI systems.',
            colReference: 'Commencement provision',
            colDate: 'Date for this system',
            colReason: 'Why',
            colSource: 'From',
            noDate: 'No date',
            notStated: 'a date not stated',
            partNotNamed: 'the provisions the transitional stage does not move',
            decidedBy: 'For a system put into official operation before {cutoff}, {reference} sets another date for all or part of this.',
            decidedByRow: 'For a system put into official operation before {cutoff}, {reference} sets another date for all or part of this — see that row.',
            reason: {
                every_system: 'The same date for every system',
                unit_condition_not_held: 'No system of your organisation is recorded as {tier}, the tier the carve-out names, so the stated date applies to every system',
                unit_condition_undetermined: 'Depends on classification: {count} system of your organisation not yet classified. If it is classified {tier}, {cutoffDate} applies; otherwise {date}.',
                unit_condition_undetermined_plural: 'Depends on classification: {count} systems of your organisation not yet classified. If any is classified {tier}, {cutoffDate} applies; otherwise {date}.',
                new_systems_only: 'Applies only to a system put into operation on or after {cutoff}',
                new_system_in_stated_period: 'Put into operation on or after {cutoff}: this date applies if that was within the period the provision states',
                existing_system: 'In operation before {cutoff}',
                existing_system_moved: 'In operation before {cutoff}: the transitional date applies',
                existing_system_partly_moved: 'In operation before {cutoff}: this date applies to the provisions the transitional stage does not move; see that stage for the rest',
                new_system: 'Put into operation on or after {cutoff}',
                unit_holds_tier: 'Your organisation has a system classified {tier}, the tier the carve-out names',
                existing_systems_only: 'Applies only to a system in operation before {cutoff}',
                operation_start_not_recorded: 'Depends on when the system went into operation, which is not recorded',
                every_system_in_part: 'Applies to {part} for every system; for the rest it depends on when the system went into operation, which is not recorded',
                declaration_unreadable: 'The commencement the installed framework declares for this could not be read',
                applies_through_condition_met: 'In operation before {cutoff}, and its recorded autonomy level automates the execution of decisions: the provision applies to it through another from this date',
                applies_through_condition_not_met: 'In operation before {cutoff}, and its recorded autonomy level does not automate the execution of decisions: the date for systems already in operation applies',
                applies_through_condition_not_recorded: 'Depends on the autonomy level, which is not recorded: {dateMet} if the system automates the execution of decisions, {dateNotMet} if not. Nothing reads overdue.',
            },
            // fix-sprint-20261031-1200 F39(e) — a provision brought in through another, with its reading.
            throughNote: '{provision} through {via}: {reading}',
        },
        inventoryRegister: {
            heading: 'Inventory completeness',
            statement: 'This register holds the AI systems recorded in ComplianceOne and the legacy entries imported from your organisation’s existing inventory. It can show only what has been recorded: a system nobody recorded is not in it.',
            recordedSystems: 'Systems recorded',
            legacyEntries: 'Imported legacy entries',
            noImport: 'No legacy import has been made.',
            lastImport: 'Last import',
            received: 'Received',
            imported: 'Imported',
            notImported: 'Not imported',
            refusalHeading: 'Rows not imported',
            colRow: 'Row',
            colName: 'Name',
            colReason: 'Reason',
            refusal: {
                name_missing: 'No name',
                already_registered: 'Already registered',
                duplicate_in_batch: 'Repeated in this file',
            },
            importHeading: 'Legacy import',
            importHint: 'One system per line: name | legacy reference | owner as recorded | status as recorded. A name already in the register, or repeated in the file, is not imported.',
            importField: 'Rows from the existing inventory',
            importSource: 'Source label',
            importSubmit: 'Import rows',
            importDone: 'Import recorded.',
            importFailed: 'The import was not recorded.',
            importEmpty: 'Enter at least one row.',
            legacyHeading: 'Imported legacy entries',
            legacyEmpty: 'No legacy entry is imported.',
            colLegacyRef: 'Legacy reference',
            colOwnerRecorded: 'Owner as recorded',
            colStatusRecorded: 'Status as recorded',
            colImportedOn: 'Imported on',
            colTier: 'Risk tier',
            notClassified: 'Not classified',
            importedMarker: 'Imported',
            obligationsHeading: 'Dated obligations',
            obligationsHint: 'The dates the installed AI instruments state, each with the status and the source it is read from. What each date covers, including any transitional rule or carve-out, is in its own column, worded as the installed framework words it.',
            obligationsEmpty: 'No installed AI instrument states a dated obligation.',
            colReference: 'Article',
            colFramework: 'Instrument',
            colStatus: 'Status',
            colDate: 'Date or period',
            colReasonNotEnforced: 'Why not enforced',
            colCarveOut: 'What the date covers',
            colSource: 'Pack source',
            noPeriod: 'The framework states no date or period.',
            reasonNotApplicable: 'Not applicable',
            annualRecurrence: 'Each year, by {day}/{month}',
            unitUnknown: '{amount} ({unit})',
            unit: {
                hours: 'hours',
                days: 'days',
                working_days: 'working days',
                calendar_days: 'calendar days',
                weeks: 'weeks',
                months: 'months',
                years: 'years',
                annual: 'years',
                working_days_before_event: 'working days before the event',
                calendar_days_before_event: 'calendar days before the event',
                calendar_days_after_event: 'calendar days after the event',
                before_use: 'Before the system is put into use',
            },
            unitOne: {
                hours: 'hour',
                days: 'day',
                working_days: 'working day',
                calendar_days: 'calendar day',
                weeks: 'week',
                months: 'month',
                years: 'year',
                annual: 'year',
                working_days_before_event: 'working day before the event',
                calendar_days_before_event: 'calendar day before the event',
                calendar_days_after_event: 'calendar day after the event',
            },
            periodRecurring: 'Every {period}',
            noCarveOut: 'The installed framework states no separate scope for this date.',
            unitHoldsTier: 'Your organisation has a system classified at the tier the carve-out names, so the carve-out date applies. Without one, the date is {stated}.',
            status: {
                in_force: 'In force',
                upcoming: 'Upcoming',
                not_recorded: 'Not recorded',
                not_enforced: 'Not enforced',
            },
            reason: {
                reference_install: 'Installed for reference only',
                not_operational: 'Not operational for this organisation',
            },
            exportHeading: 'Export the inventory',
            exportIntro: 'The organisation-wide register as a file: every system and imported entry, with the completeness limit in its header. The platform assigns no tier.',
            exportButton: 'Export inventory',
            exportFailed: 'The inventory could not be exported.',
        },
        evidenceIndex: {
            heading: 'Regulator evidence index',
            intro: 'The regulator-facing evidence this system holds, by area, with where each area sits.',
            colArea: 'Area',
            colCount: 'Records',
            colLatest: 'Latest',
            colWhere: 'Where it sits',
            none: 'None recorded',
            area: {
                classification: 'Classification runs',
                validation: 'Validation',
                security_tests: 'Security tests',
                interventions: 'Interventions',
                complaints: 'Complaints',
                supplier_arrangements: 'Supplier arrangements',
            },
            where: {
                classification: 'This page, classification section',
                validation: 'This page, validation section',
                security_tests: 'This page, security section',
                interventions: 'This page, interventions section, and the interventions register',
                complaints: 'Complaints register',
                supplier_arrangements: 'This page, supplier section',
            },
        },
        suppliers: {
            heading: 'Supplier arrangements',
            description: 'The arrangement with a vendor, read on the vendor record. A term with no state reads not recorded. The register counts the terms recorded present, out of the eight Điều 18 khoản 3 names; it does not judge the contract.',
            add: 'Record an arrangement',
            empty: 'No arrangement is recorded with this vendor.',
            vendorHint: 'Choose the vendor to read its arrangements.',
            colPeriod: 'Period',
            colClauses: 'Clauses',
            colCompliant: 'Terms recorded',
            colAssurance: 'Assurance report',
            colTraining: 'Training data',
            compliantYes: 'All eight terms recorded present',
            compliantNo: 'Not all eight recorded',
            expires: 'expires',
            clause: {
                assurance_evidence: 'Assurance evidence',
                version_reporting: 'Version reporting',
                monitoring_access: 'Monitoring access',
                continuity_and_transition: 'Continuity and transition',
                regulatory_cooperation: 'Regulatory cooperation',
                training_data_commitment: 'Training-data commitment',
                it_service_contract_rules: 'IT-service contract rules',
                technical_information_and_limits: 'Technical information, model limits and security measures',
            },
            clauseState: { present: 'Present', absent: 'Absent', pending: 'Pending' },
            clauseHint: 'Choose a state; leave empty for not recorded.',
            training: {
                commitment: 'Commitment',
                consent: 'Written consent',
                not_recorded: 'Not recorded',
            },
            // Fix-sprint-20261031-1200 F32 — the supplier page's boundary statement.
            boundary: "ComplianceOne records what the contract says and what the supplier reported. It does not connect to the supplier's systems, does not verify a model's contents and does not judge the contract: monitoring access is evidenced by your organisation's record.",
            // F4 — the assurance warning period, and where its figure comes from.
            fWarnDays: 'Warning period (days before expiry)',
            warnDaysDefault: '30 days unless your organisation sets another figure. This is a ComplianceOne default: the installed framework sets no warning period.',
            warnDaysReading: 'Warning period: {days} days ({source})',
            warnDaysSource: {
                organisation: 'set by your organisation',
                platform_default: 'ComplianceOne default',
            },
            // F35 — the training-data term read with its recorded position.
            trainingBasis: "Điều 18 khoản 3 điểm h: the third party commits not to use your organisation's data to train, fine-tune or improve its model without your organisation's written consent.",
            trainingTerm: {
                recorded: 'Recorded with its position',
                position_not_recorded: 'Position not recorded: the eight terms do not read as recorded',
                clause_not_present: 'The clause is not recorded present',
            },
            fConsentReference: 'Written consent reference',
            fConsentBasis: "Your organisation's basis for consenting",
            consentBasisHint: 'Where personal data is involved, the DPO reviews this basis.',
            // F7 — the supplier record export per period.
            exportRecord: 'Export the supplier record',
            exportTitle: 'Export the supplier record',
            exportDescription: "The arrangements overlapping the period, their terms, the events dated in it, and each system's component register at the end of the period.",
            fExportPeriod: 'Period',
            exportPeriodHint: 'A year (YYYY) or a month (YYYY-MM).',
            fExportFormat: 'File format',
            exportFormat: { csv: 'CSV', xlsx: 'Excel (XLSX)', pdf: 'PDF' },
            exportSubmit: 'Export',
            exportFailed: 'The supplier record could not be exported.',
            formTitle: 'Record a supplier arrangement',
            submit: 'Save arrangement',
            fVendor: 'Vendor',
            fPeriodStart: 'Period starts',
            fPeriodEnd: 'Period ends',
            fTrainingPosition: 'Training-data position',
            fTrainingScope: 'Scope of the consent',
            colVersions: 'Version reports',
            validationSigned: 'validated',
            validationUnknown: 'validation unknown',
            recordedOn: 'Recorded',
            fReviewCycle: 'Review cycle (months)',
            fReviewCycleHint: 'The bank’s review cycle for this arrangement. Leave empty for no cycle.',
            fAssuranceRef: 'Assurance report reference',
            fAssuranceStart: 'Report period starts',
            fAssuranceEnd: 'Report period ends',
            fAssuranceExpires: 'Report expires on',
            colReadings: 'Readings',
            readingReview: 'Review',
            reviewNotSet: 'no cycle set',
            reviewCurrent: 'on cycle',
            reviewOverdue: 'overdue',
            reviewDueOn: 'due',
            assurance: {
                current: 'current',
                expiring: 'expiring',
                expired: 'expired',
            },
            terminatedOn: 'Terminated on',
            chainFlagged: 'Sub-processor change not yet reviewed',
            continuityTested: 'Continuity last tested',
            monitoringReviewed: 'Monitoring access last reviewed',
            regulatorContact: 'Regulator contact',
            regulatorRequests: 'Regulator requests logged',
            addEvent: 'Record a supplier event',
            eventFormTitle: 'Record a supplier event',
            eventSubmit: 'Save event',
            fEventArrangement: 'Arrangement',
            fEventKind: 'Event',
            eventKind: {
                model_version: 'Model version reported',
                sub_processor_change: 'Sub-processor change',
                termination: 'Termination',
                regulator_request: 'Regulator request',
                regulator_contact: 'Regulator contact named',
                continuity_test: 'Continuity test',
                monitoring_access_review: 'Monitoring access review',
                review: 'Arrangement review',
            },
            fModelVersion: 'Model version',
            fSupplierReportedOn: 'Supplier reported on',
            fSubProcessor: 'Sub-processor',
            fComponentRef: 'Component changed',
            fChangedOn: 'Changed on',
            fSummary: 'Summary',
            fEffectiveOn: 'Effective on',
            fReason: 'Reason',
            fRequestReference: 'Request reference',
            fReceivedOn: 'Received on',
            fContactUserId: 'Named contact (a person of this organisation)',
            fRoute: 'Route a regulator request takes',
            fTestedOn: 'Tested on',
            fReviewedOn: 'Reviewed on',
            fOutcome: 'Outcome',
        },
        panels: {
            heading: 'Duties by obligation kind',
            description: 'Each kind lists its duties with the status and the evidence state. A duty marked complete without evidence needs evidence.',
            kind: {
                human_oversight: 'Oversight',
                transparency_labeling: 'Transparency and labelling',
                monitoring: 'Monitoring',
                incident: 'Incident',
            },
            count: '{count} duties',
            empty: 'No duty of this kind is attached.',
            colDuty: 'Duty',
            colStatus: 'Status',
            colEvidence: 'Evidence',
            evidence: {
                needs_evidence: 'Needs evidence',
                evidenced: 'Evidence recorded',
                not_recorded: 'Not recorded',
            },
            needsEvidenceWhy: 'The status cannot stand as complete without an evidence record.',
            edit: 'Edit',
        },
        registers: {
            backToRegister: 'Back to the register',
            inventoryGuide: 'Use this page once a year: choose the period, then confirm each system or record what changed. The full guide explains each outcome and when a review reads overdue.',
            paymentGuide: "Read the payment authorities an AI system holds, with their limits and what each one still lacks. Authorities are recorded and approved on the system's own page.",
            interventionsGuide: "Read the pauses, stops and other human interventions recorded for an AI system. Interventions are recorded on the system's own page; the full guide explains when one reads confirmed.",
            complaintsGuide: 'Record a complaint about an AI-influenced decision, route it to an independent reviewer, note any item withheld from the customer, then record the re-review and the close. The counts below show when a product reaches the threshold your organisation sets.',
            frameworkGuide: 'Record the approved risk-management framework for AI and the map of the three lines of defence. The full guide explains what makes a line complete.',
            boardGuide: 'These are the AI figures the board report carries, read from the registers now. The table shows the time they were read. To change a figure, change the register it is read from.',
            systemsLoading: 'Loading the AI systems…',
            systemsFailed: 'The list of AI systems could not be read.',
            systemsRetrySr: 'read the list of AI systems again',
            systemsEmpty: 'No AI system is recorded in the register yet.',
            thresholdCurrent: 'Current threshold',
            thresholdComplaints: '{count} complaints',
            thresholdLoading: 'Loading the threshold…',
            thresholdFailed: 'The threshold setting could not be read.',
            thresholdRetrySr: 'read the threshold setting again',
            fSystem: 'AI system',
            inventoryTitle: 'AI inventory reviews',
            inventorySubtitle: 'Annual review of every AI system in the register',
            inventorySummary: 'Each system is confirmed or changed by a named person. A system not confirmed by its due date reads overdue.',
            inventoryHeading: 'Reviews for the period',
            inventoryEmpty: 'No system has a review for this period.',
            fPeriod: 'Period',
            colSystem: 'System',
            colPeriod: 'Period',
            colDue: 'Due',
            colReading: 'Reading',
            confirm: 'Confirm',
            confirmTitle: 'Confirm the annual review',
            submit: 'Save',
            fOutcome: 'Outcome',
            paymentTitle: 'Payment authorities',
            paymentSubtitle: 'Automated payment authorities, by system',
            paymentSummary: 'Choose a system to read its payment authorities. The platform records each authority; it does not execute a payment.',
            paymentHeading: 'Authorities for the system',
            paymentEmpty: 'No payment authority is recorded for this system.',
            openSystem: 'Open the system record',
            interventionsTitle: 'Interventions',
            interventionsSubtitle: 'Human oversight records, by system',
            interventionsSummary: 'Choose a system to read its interventions. A pause or stop reads requested until a person enters the confirmed time.',
            interventionsHeading: 'Interventions for the system',
            interventionsEmpty: 'No intervention is recorded for this system.',
            complaintsTitle: 'AI complaints',
            complaintsSubtitle: 'Complaints about AI-influenced decisions, and their human re-review',
            complaintsSummary: 'A complaint is routed to a reviewer who is neither the system owner nor a listed developer. It closes only with a recorded re-review.',
            complaintsHeading: 'Complaints',
            complaintAdd: 'Record a complaint',
            complaintsEmpty: 'No complaint is recorded.',
            colReceived: 'Received',
            colProduct: 'Product',
            colCategory: 'Decision category',
            colStatus: 'Status',
            colReviewer: 'Reviewer',
            colWithheld: 'Withheld items',
            colReReview: 'Re-review',
            reReviewRecorded: 'Recorded',
            route: 'Route',
            withhold: 'Withhold an item',
            reReview: 'Re-review',
            close: 'Close',
            countsHeading: 'Complaints by system and product',
            countsEmpty: 'No complaint is counted yet.',
            colCount: 'Count',
            colThreshold: 'Threshold',
            thresholdNotSet: 'Not set',
            colCrossed: 'Threshold crossed',
            crossedYes: 'Yes',
            crossedNo: 'No',
            setThreshold: 'Set the threshold',
            createTitle: 'Record a complaint',
            fCategory: 'Decision category',
            fReceivedOn: 'Date received',
            fSummary: 'Summary of the complaint',
            fProduct: 'Product',
            fStatedReason: 'Stated reason',
            fExplanation: 'Explanation given',
            colBasis: 'Decision basis',
            basisReading: {
                recorded: 'Recorded',
                basis_not_retained: 'Basis not retained',
                reason_notified_basis_recorded: 'Reason notified; basis held',
            },
            noDecision: 'No decision linked',
            fDecision: 'Decision complained of',
            fDecisionHint: 'Optional. The decision as recorded in the AI decision register.',
            fExplanationDay: 'Date the explanation was delivered',
            fExplanationChannel: 'Channel the explanation was delivered by',
            fNotifiedMethod: 'How the customer was notified',
            fNotifiedOn: 'Date the customer was notified',
            exportFile: 'Export the complaint file',
            routeTitle: 'Route the complaint',
            fReviewer: 'Reviewer',
            reviewerHint: 'Not the system owner or a listed developer.',
            submitRoute: 'Route complaint',
            withholdTitle: 'Withhold an item',
            fWithheldItem: 'Item withheld',
            fWithheldReason: 'Reason for withholding',
            fWithheldCategory: 'Category',
            withheldCategory: {
                source_code_algorithm_or_parameters: 'Source code, detailed algorithm or parameter set',
                business_or_technology_secret: 'Business or technology secret',
                third_party_information: 'Third-party information',
                security_sensitive: 'Security-sensitive',
                restricted_decision: 'Restricted decision',
                other: 'Other',
            },
            submitWithhold: 'Save item',
            reviewTitle: 'Re-review the complaint',
            fOutcomeSummary: 'Outcome of the re-review',
            reviewHint: 'The re-review is the final basis for the reply. Its outcome is recorded for every category.',
            reviewReasonHint: 'What the customer is told. For this category the institution need only tell the customer a reason; the outcome above is held internally.',
            submitReview: 'Save re-review',
            thresholdTitle: 'Complaint threshold',
            fThresholdCount: 'Complaints that raise a task',
            thresholdHint: 'The organisation sets this figure; the regulation states none.',
            fSecondLine: 'Second-line holder',
            submitThreshold: 'Save threshold',
            closeTitle: 'Close the complaint',
            closeHint: 'The server refuses a close with no re-review recorded.',
            submitClose: 'Close complaint',
            complaintsBoundary: 'ComplianceOne holds the complaint, the decision basis and the re-review. It does not decide a complaint and does not write the explanation to the customer.',
            closeNeedsReReview: 'No re-review is recorded for this complaint, so it cannot be closed yet. Record the re-review first.',
            fComplaintReason: 'Reason for the complaint',
            complaintReasonHint: "ComplianceOne's own categories; the Circular names none.",
            colComplaintReason: 'Reason',
            complaintReason: {
                decision_outcome: 'Disagrees with the outcome',
                explanation: 'Asks for an explanation, or finds it inadequate',
                unfair_treatment: 'Unfair treatment or discrimination',
                data_error: 'Wrong or outdated data',
                ai_interaction: 'Dealing with an AI system',
                service_failure: 'System error or interruption',
                other: 'Other',
                not_recorded: 'Not recorded',
            },
            colAiSystem: 'AI system',
            colCountInPeriod: 'Complaints in the period',
            colByReason: 'By reason',
            thresholdWithWindow: '{count} in {months} month(s)',
            thresholdNoWindow: 'counted over every complaint recorded; no window set',
            periodFrom: 'From month',
            periodTo: 'To month',
            periodHint: 'The default is the six months ending this month.',
            exportTrend: 'Export the trend by month',
            fThresholdWindow: 'Window, in months',
            thresholdWindowHint: 'Optional, from 1 to 12 months ending with the month counted. Left empty, every complaint recorded is counted.',
            patternHeading: 'Responses to a pattern of complaints',
            patternLabel: "Your organisation's response to a pattern of complaints. The Circular sets no threshold and no response for a pattern; ComplianceOne decides neither.",
            patternEmpty: 'No response to a pattern of complaints is recorded.',
            patternRespond: 'Record the response',
            patternTitle: 'Record the response to a pattern of complaints',
            patternSubmit: 'Save response',
            fPatternKind: 'What your organisation did',
            patternKind: {
                no_change_with_reason: 'No change, with the reason',
                revalidation_requested: 'Re-validation requested',
                intervention_recorded: 'Intervention recorded',
                incident_recorded: 'Incident recorded',
                change_recorded: 'Change recorded',
                other: 'Other',
            },
            fPatternLinked: 'The record it refers to',
            patternLinkedEmpty: 'This system has no record of this kind to link.',
            patternRespondSr: 'for {system}, {product}',
            patternRespondSrCrossed: 'for {system}, {product}, crossed on {date}',
            patternCrossedOn: 'Crossed on {date}.',
            patternCrossingNote: 'The response answers the latest complaint that brought this count to the threshold. ComplianceOne finds that complaint and records its day with the response.',
            patternWho: "Only the system's owner, the second-line holder the threshold names, or a person on the second line of the current three-lines map whose role in the register is P-VN-42 can record the response.",
            patternLinkedLoading: 'Reading the records of this system…',
            patternLinkedFailed: 'The records of this system could not be read.',
            patternLinkedRetrySr: 'read the records to link again',
            patternAlreadyAnswered: 'This crossing of the threshold already has its response.',
            periodInvalid: 'A period covers at most 60 months.',
            lineAddedInVersion: 'Attested by someone added to this line in the version they attested.',
            fPatternSummary: 'What was done, and why',
            fRespondedOn: 'Day of the response',
            colCrossedOn: 'Threshold crossed on',
            colResponseKind: 'Response',
            colRespondedBy: 'Responded by',
            colRespondedOn: 'Responded on',
            colExplanations: 'Explanations',
            explanationAdd: 'Record an explanation',
            explanationTitle: 'Record an explanation after the re-review',
            explanationHint: 'Record each explanation delivered to the customer after the re-review, as it was delivered. None is required to close the complaint.',
            fExplanationText: 'Explanation as delivered',
            explanationSubmit: 'Save explanation',
            explanationStage: {
                at_intake: 'At intake',
                after_re_review: 'After the re-review',
            },
            frameworkTitle: 'Framework record and three lines',
            frameworkSubtitle: 'The risk-management framework and the three lines of defence',
            frameworkSummary: 'The framework is approved by a named body and person. The three-lines map shows each line’s unit, people and attestation.',
            frameworkHeading: 'Risk-management framework',
            frameworkAdd: 'Record a framework',
            frameworkEmpty: 'No framework is recorded.',
            frameworkFormTitle: 'Record the framework',
            fFrameworkTitle: 'Framework title',
            fApproverBody: 'Approving body',
            fApprover: 'Approver',
            fApprovedOn: 'Date approved',
            fRiskCode: 'Risk type code',
            fDefinition: 'Definition',
            fThresholds: 'Thresholds',
            fControls: 'Controls',
            colTitle: 'Title',
            colApprover: 'Approver',
            colApproved: 'Approved on',
            colRiskTypes: 'Risk types',
            linesHeading: 'Three lines of defence',
            linesAdd: 'Record the map',
            linesEmpty: 'No three-lines map is recorded.',
            linesFormTitle: 'Record the three-lines map',
            linesFormDescription: 'Each line names its unit and its holders. A line is attested by one of its own holders; a line not attested, or attested by someone not on it, reads incomplete.',
            colVersion: 'Version',
            colIncomplete: 'Incomplete lines',
            colLines: 'Lines',
            attestedYes: 'attested',
            attestedNo: 'not attested',
            line: { first: 'First line', second: 'Second line', third: 'Third line' },
            fUnit: 'Unit',
            fHolder: 'Person',
            fRole: 'Role',
            fPersona: 'Role in the register',
            fAttestation: 'Attestation statement',
            attestationHint: 'Entered by the signatory.',
            fAttested: 'Attested',
            // Fix-sprint-20261031-1200 F32 — the framework and three-lines page's boundary statement.
            frameworkBoundary: "ComplianceOne holds the framework record, the three-lines map and each line's attestation, as the people named entered them. It approves no framework, judges no line's independence and attests nothing on anyone's behalf.",
            // F6 — one line per attestation, by one of that line's own holders.
            attestLine: 'Attest this line',
            attestTitle: 'Attest the {line}',
            attestDescription: 'You are named on this line. Your attestation is recorded in your name and dated today.',
            submitAttest: 'Save attestation',
            lineAttestedOn: 'Attested on {date}',
            lineCarried: 'Attested on {date} (carried from version {n})',
            lineNotAttested: 'Not attested',
            lineNonHolder: 'Attested by someone not named on this line: re-attest.',
            sharedHolder: '{name} is named on the {lineA} and the {lineB}. Điều 14 khoản 2 asks for independent lines.',
            lineLower: { first: 'first line', second: 'second line', third: 'third line' },
            lineJoin: ', the ',
            boardTitle: 'Board report: AI figures',
            boardSubtitle: 'The AI figures in the board report, read from the AI registers',
            boardSummary: 'Overdue inventory reviews count the reviews of the current calendar year. A pause, stop, emergency stop, manual operation or resume counts as an open intervention from when it is recorded until a person enters its confirmed time.',
            boardHeading: 'Current figures',
            boardEmpty: 'The register returned no AI figures.',
            colFigure: 'Figure',
            colValue: 'Value',
            figSystems: 'AI systems',
            figLow: 'Low risk',
            figMedium: 'Medium risk',
            figHigh: 'High risk',
            figUnclassified: 'Unclassified',
            figOverdue: 'Overdue inventory reviews',
            figFindings: 'Open validation findings',
            figInterventions: 'Open interventions',
            figDisclosures: 'Disclosures in use',
            figAsOf: 'As of',
        },
        // fix-sprint-20261031-1200 F1 and F34 — the periodic report contents, retirement and a change's significance.
        boardReport: {
            heading: 'Periodic report contents',
            intro: 'The contents the installed framework names for the report to the senior management body, read from the registers for the window below. ComplianceOne counts what the registers hold; it does not write or present the report.',
            windowFrom: 'Window from',
            windowTo: 'Window to',
            applyWindow: 'Read for this window',
            windowInvalid: 'Enter a window that opens on or before the day it closes.',
            statusComplete: 'Reads the five contents Điều 12 khoản 6 names — operation, risk, serious incidents, complaints and drift trends — from the registers, for {from} to {to}. ComplianceOne does not present the report to the senior management body or the parent bank.',
            statusPartial: 'Partial against Điều 12 khoản 6: {gaps}. The figures below are what the registers hold for {from} to {to}.',
            gapUnclassified: "{n} systems have no recorded tier, so they cannot be placed in or out of the report's scope",
            gapSignificance: '{n} changes to high-risk systems have no significance answer',
            gapOperationDate: '{n} systems have no operation date, so the day the report is first owed is not known for them',
            gapUnreadable: '{content} could not be read',
            windowLonger: 'This window is longer than the six months the text sets as the minimum frequency.',
            nextDue: 'Next report due by {date} (from {anchor})',
            anchorLastExport: 'the last board report export on {date}',
            anchorFirstApplies: 'the earliest day the report applies to a system in scope, {date}',
            nextDueNotDeclared: 'No next due date: no installed framework in force sets the report interval.',
            nextDueNoScope: "No next due date: no system of your organisation is in the report's scope.",
            nextDueUnknown: 'The next due date is not known: no system in scope has an operation date.',
            autonomyNotRecorded: '{n} systems in scope have no recorded autonomy level; the earlier of the two possible days is counted.',
            colContent: 'Content',
            opProduction: 'Systems in scope in production',
            opInterventionsKind: 'Interventions recorded in the window: {kind}',
            opChanges: 'Version changes recorded in the window',
            riskTier: 'Systems in scope: {tier}',
            incSerious: 'Incidents determined serious in the window',
            incUndetermined: 'Incidents opened in the window whose seriousness is not determined',
            incCompletion: 'Serious incidents with a completion report',
            cReceived: 'Complaints received in the window',
            cClosed: 'Complaints closed in the window',
            cOpen: 'Complaints open now',
            cCrossings: 'Complaint threshold crossings in the window',
            driftRow: '{kind}: {now} in the window; {before} from {from} to {to}',
            driftUnreadable: 'Monitoring events by kind, in the window and the window before it',
            nextDueUndated: 'No next due date: the installed framework states no date from which this applies.',
            retrySr: 'read the periodic report contents again',
            gapRecordNotInstalled: 'No installed framework declares the report to the senior management body, so its scope, contents and interval are not known',
            changesHeading: 'Change records',
            changesEmpty: 'No change is recorded.',
            colChangeRecorded: 'Recorded on',
            significanceRecord: 'Record significance',
            significanceRecordSr: 'for the change recorded on {date}',
            significanceBasisShort: 'Basis',
            significanceChoose: 'Choose yes or no.',
            significanceAnsweredBy: 'Answered by {user} on {date}',
            significanceAlreadyRecorded: 'This change already carries a significance answer.',
            significanceBasisRequired: 'Record the basis with the answer.',
            sigHeading: 'Significant changes to high-risk systems',
            sigSince: 'Recorded after the last report export on {date}.',
            sigSinceNone: 'No report export has carried these contents yet, so every change is listed.',
            sigEmpty: 'No change is recorded as significant.',
            sigRow: '{system}: {change} ({date})',
            sigNotRecorded: 'Changes with no significance answer',
            retiredFigure: 'Retired systems',
            retiredOn: 'Retired on {date}: no annual review is due after retirement.',
            retiredNoDay: 'Retired; retirement day not recorded.',
            retiredReading: 'Retired',
            significanceLabel: 'Significant change',
            significanceHint: 'Whether this is a significant change within Điều 2 khoản 9, which refers to the re-assessment cases of the decree that implements the AI law. A person records the answer and its basis; ComplianceOne does not decide it.',
            significanceYes: 'Yes, significant',
            significanceNo: 'No, not significant',
            significanceBasis: 'Basis for the answer',
            significanceBasisHint: 'Required with a yes or no answer.',
        },
        incidentAi: {
            heading: 'AI system in this incident',
            description: 'Link the incident to the AI system it concerns, and record the serious-incident duties of the AI record. The platform submits no report to a State authority.',
            fLink: 'AI system',
            linkHint: 'Choose the AI system whose operation this incident concerns.',
            noProfiles: 'No AI system is recorded for your organisation.',
            saveLink: 'Save link',
            linkSaved: 'Linked to this incident.',
            recordHeading: 'Serious-incident record',
            fSerious: 'Serious incident',
            seriousYes: 'Serious',
            seriousNo: 'Not serious',
            notDetermined: 'Not determined',
            fSeriousBasis: 'Basis for the determination',
            basisHint: 'A serious determination raises one reassessment task for the system owner.',
            reassessment: 'Reassessment task raised',
            reassessmentYes: 'Yes, to the system owner',
            reassessmentNo: 'No',
            deadline: 'Report deadline',
            deadlineStatedOne: 'Not computed. {framework} states no period for this report.',
            deadlineStatedMany: 'Not computed. These frameworks state no period for this report: {frameworks}.',
            deadlineNoneDeclared: 'Not computed. The platform sets no report date for this incident.',
            deadlineSeparateOne: '{framework} states reporting periods for serious AI incidents. The period that applies depends on how this incident is determined, which a person records, so none is applied here.',
            deadlineSeparateMany: 'Reporting periods for serious AI incidents are stated in {frameworks}. The period that applies depends on how this incident is determined, which a person records, so none is applied here.',
            deadlineRecordBelow: 'Record the report you made below.',
            notInForce: 'not yet in force',
            stateReports: 'Reports to the authorities',
            authority: {
                competent_state_authority: 'Competent State authority',
                state_bank_it_department: 'State Bank IT Department',
            },
            fRemediation: 'Remediation',
            fAuthority: 'Authority the report was made to',
            authorityHint: 'Record the reference of a report made by a person outside the platform.',
            fReportedOn: 'Date reported',
            fReference: 'Reference of the report',
            referenceHint: 'The reference the authority gave, or the reference in your own file.',
            completion: 'Completion report',
            fCause: 'Cause',
            fDamage: 'Damage',
            fCompletedOn: 'Date completed',
            emergency: 'Emergency stop or manual operation',
            emergencyNote: 'Recorded as an intervention of the AI system, with its time and person.',
            notification: 'Customer notification',
            notificationNote: 'Read from the notification statuses of this incident, not copied here.',
            status: 'Serious-incident state',
            recordAction: 'Record the serious-incident duties',
            recordTitle: 'Serious-incident record',
            saveRecord: 'Save record',
        },
    },
    // Sprint 63.24.0 Slice A — controlled-testing case pages. English copy until the R-57 deferral (sprint 57.7.0).
    controlled_testing: {
        // Sprint 63.36.0: authored English fallback for this module; locale translation remains separate.
        dossierChecklist: {
            heading: 'Dossier documentary reference',
            reference: 'Current amended source reference',
            regime: 'Whether this source regime governed this case is not determined.',
            unassigned: 'No documentary route is assigned. Source routes below are unselected references only.',
            selected: 'Selected documentary route',
            unselected: 'Unselected source reference — no case coverage is assigned',
            observations: 'Current documentary observations may include facts recorded after the filing or role reference date. These counts do not describe the dossier as it stood at filing or assess legal completeness.',
            recorded: 'Recorded fact items',
            partial: 'Partial items',
            pending: 'Pending items',
            historical: 'Historical items',
            source: 'Source',
            pages: 'PDF pages',
            referenceOn: 'Reference date',
            filing: 'Filing date used for route and role context',
            observedOn: 'Current observations as of',
            as_of: 'Current date used for route and role context',
            context: 'Documentary route context',
            form: 'Application form',
            kind: 'Applicant kind',
            unknown: 'Not recorded or not determinable',
            noProjection: 'No matching documentary projection is available; review required.',
            person: 'Personnel record',
            factDate: 'Actual fact reference date',
            status: {
                available: 'Source reference available',
                undeclared: 'No dossier checklist is declared.',
                unavailable: 'Dossier checklist is unavailable; review required.',
                not_applicable: 'This is not an admission-form documentary route.',
            },
            codes: {
                case_anchor: 'Immutable case form anchor',
                filed_snapshot: 'Immutable filed snapshot',
                draft_record: 'Current draft record',
                reported_registry_information: 'Reported registry information',
                none: 'No supported fact source',
                required: 'Source-listed component',
                conditional_if_any: 'Conditional source component',
                one_of: 'Documentary alternatives',
                historical_repealed: 'Historical separate attachment — repealed',
                documentary_route: 'Documentary route',
                unknown: 'Applicability not determined',
                historical: 'Historical reference',
                recorded_facts: 'Recorded bounded facts',
                recorded_fact: 'Recorded bounded fact',
                partial: 'Partial documentary observation',
                not_recorded: 'Not recorded',
                not_determinable: 'Not determinable',
                recorded: 'Recorded',
                pending: 'Pending',
                conflict: 'Conflicting facts',
                withheld: 'Withheld',
                not_held: 'Not held',
                field_only: 'Descriptive field only',
                filing_metadata_only: 'Filing metadata only; not a completed signed application',
                document_presence_only: 'Held document presence only; contents not assessed',
                classification_reference_only: 'Classification reference only; no approval or compliance assessment',
                statement_date_only: 'Statement date only; no held document inferred',
                reported_information_only: 'Reported information only',
                not_declared: 'No source declaration',
                invalid_declaration: 'Invalid source declaration',
                invalid_declaration_context: 'Invalid source context',
                ambiguous_declaration: 'Ambiguous source declarations',
                applicant_kind_required: 'Explicit applicant context is missing',
                track_not_declared: 'No matching documentary route is declared',
                ambiguous_track: 'Multiple matching documentary routes',
                not_admission_form: 'Not an admission form; no parent route is substituted',
                coverage_limit_exceeded: 'Documentary projection exceeds its technical bound',
                unsupported_binding: 'No supported documentary binding',
                missing_record: 'Required fact is not recorded',
                missing_person: 'Relevant person is not recorded',
                unknown_applicability: 'Conditional applicability is not determined',
                missing_actual_date: 'Actual date is not recorded',
                information_pending: 'Information remains pending; a request is not the information',
                legacy_unconverted: 'Legacy facts have not been converted',
            },
        },
        annexGuidance: {
            heading: 'Source guidance',
            undeclared: 'Guidance is not declared by the installed framework.',
            unavailable: 'Declared guidance is unavailable; review its source declaration.',
            internalSummary: 'Internal source summary. This guidance does not assess a submitted document, signature or filing.',
            sourceDocument: 'Source document',
            sourcePages: 'Source PDF pages',
            legacy: 'Historical record: a provisional report-format absence marker was stored. Current guidance is shown separately.',
            reasons: {
                not_declared: 'No guidance declaration is installed.',
                invalid_declaration: 'The guidance declaration is invalid.',
                invalid_declaration_context: 'The guidance source context is invalid.',
                ambiguous_declaration: 'Multiple guidance declarations require review.',
            },
        },
        parentCopy: {
            operationCapturedAt: 'Creation request captured at',
            acceptedAt: 'Creation accepted at',
            initiatedBy: 'Creation initiated by',
            operationPage: 'Recorded creations page',
            previous: 'Previous page',
            next: 'Next page',
            listFailed: 'Recorded child creations could not be read. Retry the status check; do not submit another creation request.',
            reviewOperations: 'Review recorded child creations',
            knownOperation: 'Recorded operation reference',
            noResume: 'The selected recorded creation has no resume action available.',
            heading: 'Frozen parent solution evidence',
            capturedVersion: 'Captured source solution version',
            capturedAt: 'Source captured at',
            captureHint: 'This snapshot preserves the source version and capture time shown here.',
            copy_absent: 'No parent solution copy recorded.',
            not_recorded: 'The copied solution has no recorded technical-statement file.',
            available: 'The original copied file is available.',
            withheld: 'Parent solution content is withheld. Independent parent read access is required.',
            legacy_lineage_unverified: 'The earlier copy has no verified source lineage. Its file cannot be downloaded through this snapshot.',
            source_unavailable: 'The source solution is unavailable. The copied reference is preserved.',
            lineage_invalid: 'The source lineage needs review. No file is available through this snapshot.',
            not_held: 'The copied file is not currently held.',
            deleted: 'The copied file is deleted. Its original reference remains recorded.',
            released: 'The copied file was released under retention rules. Its original reference remains recorded.',
            release_in_progress: 'Release or destruction is in progress. The copied file is unavailable.',
            loading: 'Checking copied evidence availability…',
            loadFailed: 'Copied evidence availability could not be checked.',
            refresh: 'Reload copied evidence',
            download: 'Download original copied statement',
            downloadFailed: 'The copied file could not be downloaded. Reload its availability and retry.',
            downloadUnavailable: 'Download is unavailable with the current access.',
            opening: 'Creating the child case…',
            creationPending: 'Child creation is awaiting completion. This form keeps the original request; retrying does not create another child.',
            creationUnknown: 'The result of this request is uncertain. Check its status before creating another child. Your original form is preserved.',
            checkCreation: 'Check child creation status',
            resume: 'Complete the recorded child creation',
            operationChoice: 'Recorded child creation',
            chooseOperation: 'Choose the recorded operation to review',
            preparing: 'Creation is being prepared',
            committed: 'Creation accepted; completion pending',
            published: 'Child case is available',
            cancelled: 'Creation was cancelled',
            integrity_conflict: 'Recorded creation needs integrity review',
            resumeFailed: 'The recorded child creation could not be completed.',
            originalRequest: 'Original child request',
            newRequest: 'Return to a new request after review',
            recoveryHint: 'Review the recorded operations explicitly. No operation is selected from its date or name.',
            noOperations: 'No recoverable operation is listed. Review the child list before starting a new request.',
        },
        stopReasonMessages: {
            framework_not_operational: 'The framework is not operational for this case. No transitional determination is made.',
            transitional_rule_ambiguous: 'Multiple transitional rule declarations apply. Review their source declarations.',
            transitional_rule_invalid: 'The declared receipt or confirmation references need source review.',
            transitional_documentary_sources_conflict: 'The actual receipt or confirmation sources disagree or are ambiguous. Compare the separate documentary records.',
            confirmation_mapping_not_declared: 'No source-declared confirmation reference is available. A receipt cannot replace it.',
            legacy_confirmation_claim_not_verified: 'A prior confirmation-date claim exists, but its documentary evidence is not verified. Review that claim separately from receipt.',
            confirmation_scope_conflict: 'The confirmation evidence and its applicable recording scope conflict. Review the preserved sources.',
            historical_recording_scope_review_required: 'The historical processing regime still needs source review. Verified receipt does not resolve that applicability question.',
            authored_documentary_counterpart_not_verified: 'A declared receipt or confirmation counterpart still lacks verified evidence. Review the separate source records.',
            confirmation_regime_not_verified: 'The actual confirmation is recorded, but its processing regime or commencement boundary is not verified.',
            main_and_nested_proceedings: 'Main and nested STOP facts both exist. Review the proceeding identity.',
            stop_reference_not_declared: 'The installed framework does not identify this STOP proceeding.',
            stop_reference_invalid: 'The declared STOP procedure or receipt reference needs review.',
            stored_stop_procedure_changed: 'The installed target differs from the preserved proceeding. Recorded facts have not been moved.',
            stop_lifecycle_not_declared: 'The source-declared proceeding steps are unavailable. Review the declaration.',
            receipt_occurrences_conflict: 'Multiple or conflicting receipt facts need documentary review.',
            receipt_not_verified: 'The actual receipt evidence still needs verification.',
            receipt_not_recorded: 'No actual receipt fact is recorded. Record it from its evidence when known.',
            multiple_decision_occurrences: 'Multiple lifecycle decision facts need documentary review.',
            decision_dates_disagree: 'The actual decision dates differ across documentary sources.',
            decision_references_disagree: 'The decision references differ across documentary sources.',
            decision_evidence_conflict: 'Decision evidence contains a conflict. Preserve the actual sources for review.',
            completion_and_termination_recorded: 'A completion certificate and termination facts are both recorded. Review their documentary conflict.',
            stop_context_unresolved: 'The proceeding context is unresolved. New requests need review; actual facts remain preserved.',
            grant_not_declared: 'No grant declaration is available for a new voluntary request.',
            grant_not_recorded: 'The declared grant has not been recorded. No grant date is inferred.',
            completion_certified: 'An actual completion certificate is recorded. A new voluntary stop request is unavailable.',
            termination_recorded_or_unresolved: 'Termination facts are recorded or need review. New initiation is unavailable.',
            stop_already_filed: 'A voluntary request is already recorded.',
            voluntary_ground_not_declared_or_ambiguous: 'The voluntary-request ground is missing or ambiguous in the installed source.',
            completion_already_filed: 'A completion request is already recorded.',
        },
        stopProceeding: {
            documentaryEnding: 'Documentary ending for this clock',
            verified: 'Evidence verified',
            pending_anchor: 'Required scope anchor not recorded',
            requestFiled: 'Applicant request recorded',
            heading: 'STOP proceeding and documentary decisions',
            intro: 'Record actual documentary acts in their declared proceeding. Applicant requests, receipt, authority decisions and post-stop duties remain separate records.',
            context: 'Proceeding context',
            main: 'Main case proceeding',
            nested: 'Separate proceeding within this case',
            not_declared: 'Not declared by the installed framework',
            unresolved: 'Proceeding declaration needs review',
            ambiguous: 'More than one proceeding context needs review',
            request: 'Voluntary applicant request',
            notFiled: 'No applicant request recorded',
            eligibility: 'Eligibility for a new voluntary request',
            allowed: 'New request available',
            refused: 'New request unavailable',
            review: 'Review the recorded facts and source declarations before this action.',
            receipt: 'Actual stop-dossier receipt',
            receiptHint: 'Enter the authority receipt date and its evidence. Filing, complete-valid confirmation and recording dates remain distinct.',
            pending: 'Pending evidence or applicability',
            recorded: 'Recorded',
            pending_evidence: 'Evidence needs verification',
            conflict: 'Documentary facts disagree or are ambiguous',
            none: 'No documentary decision recorded',
            recordReceipt: 'Record actual receipt',
            recordEvent: 'Record proceeding fact',
            recordTransition: 'Record actual proceeding step',
            recordAuthority: 'Record actual authority decision',
            authority: 'Authority termination record',
            legacy: 'Preserved earlier revocation record',
            raw: 'Decision in the proceeding history',
            decisions: 'Documentary decision comparison',
            decisionHint: 'Each source remains independent. A recorded decision does not fill a closure anchor or erase continuing duties.',
            protocol: 'Saving held evidence',
            ready: 'Held evidence can be linked',
            not_ready: 'Held evidence cannot be linked yet',
            protocolHint: 'Keep this draft and retry after evidence binding becomes available. Uploading holds a file; it does not file a request or record a decision.',
            noInference: 'Receipt does not confirm a complete-valid dossier. A pending phase does not replace an actual receipt date.',
            phase: 'Recorded proceeding state',
            history: 'Immutable proceeding history',
            events: 'Actual proceeding facts',
            clocks: 'Source-declared proceeding clocks',
            noFacts: 'No fact recorded',
            noStates: 'No declared next step available',
            event: 'Declared event',
            state: 'Declared next step',
            date: 'Actual documentary date',
            receivedOn: 'Actual receipt date',
            evidence: 'Evidence references',
            evidenceHint: 'One actual reference per line. A local file reference is subject to its held-evidence checks.',
            ground: 'Declared ground',
            groundNotRecorded: 'Ground not recorded',
            source: 'Source citation',
            actor: 'Documentary actor',
            recordedBy: 'Recorded by',
            recordedAt: 'Recorded at',
            fileHeld: 'Held',
            fileUnavailable: 'File unavailable',
            submit: 'Record documentary fact',
            saved: 'Documentary fact recorded',
            failed: 'The documentary fact could not be recorded.',
            refresh: 'Reload current proceeding',
            stale: 'The case changed. Your draft is retained. Reload before reviewing and submitting again.',
            draftChanged: 'The proceeding or selected declaration changed. Your retained draft has not been reinterpreted.',
            reset: 'Discard unavailable selection',
            close: 'Cancel',
            recipients: 'Actual notice recipients',
            recipient: 'Recipient description',
            addRecipient: 'Add recipient',
            removeRecipient: 'Remove recipient',
            channel: 'Actual notification channel',
            recipientsHint: 'Record actual recipients and channel. This does not establish complete customer coverage.',
            notRecorded: 'Not recorded',
            advisory: 'Source notice advisory',
            invalid_declaration: 'Advisory declaration needs review',
            not_determinable: 'Applicable rule is not determined',
            received_not_confirmed: 'Receipt recorded; complete-valid confirmation not recorded',
            review_required: 'Historical source applicability requires review',
            confirmation: 'Actual complete-valid confirmation',
            transitionalReceipt: 'Actual dossier receipt evidence',
            authorityHint: 'Record an actual authority act independently of applicant filing. Conflicting existing documentary sources remain visible.',
            decisionRef: 'Authority decision reference',
            decidedOn: 'Actual decision date',
            decisionText: 'Authority decision text',
            file: 'Held decision evidence',
            filingHint: 'A voluntary filing records the applicant request only; receipt and authority decision are recorded separately.',
            groundRequired: 'Choose the declared voluntary ground',
            noClock: 'No numeric clock output available',
            reason: 'Review reason',
            confirm: 'Confirm voluntary filing',
            confirmationHint: 'Confirm this applicant request with the entered date and held files. This does not record authority receipt or a decision.',
        },
        registryFacts: {
            correction: 'Outcome data correction',
            correctionHint: 'An evidence-backed correction identifies an erroneous previous fact. It is not a waiver or a successful retrieval.',
            correctionIntent: 'This change corrects an erroneous previous fact',
            correctionReason: 'Correction reason',
            outcomeHistory: 'Previous registry outcome facts',
            person: 'Exact linked personnel record',
            choosePerson: 'Choose a personnel record explicitly',
            personHint: 'Names do not identify a record. Choose the exact record ID; no person is inferred. An unassigned legacy row can be linked once.',
            personUnavailable: 'Personnel choices could not be read or no declared eligible role is available.',
            linkedOutcome: 'Outcome held only on the linked personnel record',
            outcomeHint: 'Record or correct this outcome in the personnel section. This registry row does not write a second outcome.',
            legacy: 'Preserved nonauthoritative registry outcome',
            retrievedOn: 'Actual source-retrieval date',
            suppliedOn: 'Actual component supplied date',
            requestHeading: 'Documentary follow-up requests',
            requestHint: 'Record an existing written request. A request is not the information supplied and does not waive missing information. Requests are append-only.',
            requestOn: 'Actual written-request issue date',
            reference: 'Written-document reference',
            request: 'Append documentary request',
            requestMissing: 'No declared source-specific request is available.',
            information: 'Information availability',
            available_as_reported: 'Available as reported at the recorded date; no eligibility conclusion',
            pending: 'Information remains pending',
            undetermined: 'Information availability is undetermined',
            requestRecorded: 'Documentary request recorded; this does not establish information receipt',
            evidenceNoLongerHeld: 'Request attachment is no longer held',
            noRequest: 'No documentary request is recorded',
            copy: 'Applicant-copy file',
            copyHint: 'Upload holds a file only. Bind it explicitly to this row. A held copy does not override the linked personnel outcome.',
            upload: 'Upload a registry documentary file',
            uploaded: 'Uploaded but not bound: {name}',
            bind: 'Bind uploaded applicant copy',
            unbind: 'Unbind applicant copy',
            held: 'Bound file is held for this exact row',
            notHeld: 'Bound file is not held for this row',
            notBound: 'No applicant-copy file is bound',
            useRequestFile: 'Use this upload as the request attachment',
            noRequestFile: 'No request attachment selected',
            decisionHint: 'A separately recorded decision is preserved as a fact; it does not waive request or information requirements.',
            coverage: 'Requirement coverage',
            count: 'Recorded rows: {count}; unassigned rows: {unassigned}',
            people: 'Eligible people: {eligible}; linked people: {linked}',
            scopeUnavailable: 'Requirement scope is not declared; person linkage remains undetermined',
            refresh: 'Read current registry row',
            refreshed: 'Current row read. Your entries are retained; review current scope, person and source declarations before retrying.',
            stale: 'The row changed. Read it again before retrying; your entries are retained.',
            unavailableDraft: 'Your unsaved person choice is no longer available or differs from a now-fixed link. Clear the draft choice to retain the current link.',
            keepLink: 'Use the current recorded link',
            failed: 'The documentary change could not be recorded.',
            history: 'Immutable request history',
            date: 'Actual date',
            actor: 'Recorded by',
            recordedAt: 'Recorded at',
            classificationOn: 'Outcome classification uses the recorded outcome date',
            outcomeUnresolved: 'The linked outcome or personnel scope is unresolved; the stored code is not treated as available information.',
            requestDeclarationChanged: 'The source-specific request declaration changed. Your draft is preserved and cannot be silently recorded as another kind of request.',
            resetRequestDraft: 'Discard this request draft and use the current declaration',
        },
        applicantSolution: {
            kind: 'Recorded applicant kind',
            kindHint: 'Choose the applicant kind explicitly. The shared application form does not determine it. Leaving it unrecorded keeps the track undetermined and does not create a filing requirement.',
            choose: 'Not recorded',
            kindLocked: 'Applicant kind is locked after filing or authority facts. The recorded value is preserved.',
            keepRecorded: 'Use the current recorded kind and discard the unsaved kind change',
            filedKind: 'Applicant kind in the immutable filed snapshot',
            absentAtFiling: 'Not captured in this legacy filed snapshot',
            track: 'Declared dossier track',
            recorded: 'Recorded using the current declaration',
            not_recorded: 'Applicant kind is not recorded',
            recorded_not_declared: 'Stored value is not resolved by the current declaration',
            not_declared: 'No usable declaration is available',
            invalid_declaration: 'The installed declaration requires review',
            resolved: 'Track resolved from the recorded form and applicant kind',
            applicant_kind_required: 'Record applicant kind to determine the track',
            applicant_kind_not_declared: 'The stored applicant kind cannot resolve a current track',
            track_not_declared: 'No matching declared track is available',
            ambiguous: 'Several conflicting declarations match; no choice is made',
            conditional: 'The cited dossier component includes an investment registration certificate if any. This does not establish that this applicant has or must obtain one.',
            notListed: 'An investment registration certificate is not listed in this cited dossier provision. This is not a general exemption from investment-registration obligations.',
            scheme: 'Referenced information-system security scheme',
            declared: 'The exact referenced scheme is installed',
            named_not_installed: 'The named scheme is declared but not installed. Install the referenced framework; no alternative scheme is substituted.',
            schemeNotDeclared: 'No usable scheme reference is declared. No other installed scheme is substituted.',
            reference: 'Declared target',
            source: 'Source',
            legacyLevel: 'This original security-level link is preserved. It has not been converted or assigned missing source provenance.',
            levelHint: 'New selections record the declaring framework, scheme and tier explicitly. Changing the live solution does not refresh existing frozen child copies or certify system compliance.',
            loadFailed: 'Applicant-kind declarations could not be read. The kind remains unrecorded; no choice is inferred.',
        },
        filing: {
            heading: 'Organisation filing record',
            record: 'Record a filing already made',
            recordHint: 'Record the filing the organisation already made. This action does not send documents or record authority receipt.',
            notDeclared: 'The installed procedure declares no filing requirements. A reviewed filing declaration is needed before a filing can be recorded.',
            immutable: 'The filed snapshot is preserved. This page cannot change a filing already recorded.',
            frozenGroup: 'Draft customer group in the frozen parent-solution copy',
            filedGroup: 'Customer group actually filed',
            filedReport: 'Results-report file reference at filing',
            notRecorded: 'Not recorded',
            notRecordedAtFiling: 'Not recorded in the filed snapshot',
            reference: 'Organisation’s filing reference',
            submittedOn: 'Actual filing date',
            childGroupHint: 'Prefilled from the child’s frozen parent-solution copy. Enter what was actually filed, including a missing draft group. This does not change that copy or the parent’s draft solution.',
            parentGroupHint: 'Enter the customer group actually filed. This records the filed snapshot and does not change the draft solution.',
            approvalReady: 'Internal sign-off is recorded as complete for this child.',
            approvalRequired: 'The child’s internal sign-off must be recorded as complete before its filing can be recorded.',
            draftReport: 'Draft results report',
            reportHint: 'Uploading holds a file. Attach it to this draft as a separate step. Neither action records a filing or verifies the results-report format.',
            reportNotBound: 'No results report is attached to this draft.',
            reportBound: 'Draft report attached and held (file reference: {reference}).',
            reportNotHeld: 'The draft report is no longer held or does not belong to this child. Detach or replace it before recording a filing.',
            reportUnknown: 'The draft report’s held status could not be determined. Read the case again before recording a filing.',
            reportChangeFailed: 'The draft report attachment could not be changed.',
            uploadReport: 'Upload a draft results report',
            uploadHeld: 'Upload completed: {name}. It is not yet attached to this draft.',
            bindReport: 'Attach uploaded report to draft',
            unbindReport: 'Detach draft report',
            leaveUnbound: 'Leave this upload unattached',
            saved: 'The filing record was saved.',
            failed: 'The filing record could not be saved.',
            refreshFailed: 'The current case could not be read. Reload the case before retrying.',
            stale: 'The case changed. Its current version has been read and your entries are retained. Review the current report and sign-off before retrying.',
        },
        lifecycle: {
            heading: 'Documentary facts and their actual dates',
            intro: 'Record what the evidence shows happened, including facts received after an authority outcome. The occurrence date is distinct from when this entry is recorded. The platform makes no authority decision.',
            record: 'Record a documentary fact',
            event: 'Declared fact',
            occurredOn: 'Actual occurrence date',
            evidence: 'Evidence references',
            verification: 'Verification against declared prerequisites',
            recordedAt: 'Entry recorded at',
            actor: 'Documentary actor: {actor}',
            actorAuthority: 'Authority',
            actorOrganisation: 'Organisation',
            actorRelevantMinistries: 'Relevant ministries',
            source: 'Source: {source}',
            recordedBy: 'Entry recorded by: {actor}',
            scopeVerified: 'This application’s recorded anchor is within a reviewed recording period.',
            scopePending: 'Recording period pending: record the declared anchor fact with its actual date and evidence.',
            scopeReview: 'This application’s recording period requires review. No historical regime is inferred.',
            scopeConflict: 'Recorded application anchors conflict. Review their dates and evidence.',
            scopeNotDeclared: 'No reviewed recording period is declared by the installed procedure.',
            scopeAnchor: 'Recorded application anchor: {date}',
            notDeclared: 'No documentary fact declared by the installed procedure',
            empty: 'No documentary facts recorded.',
            verified: 'Prerequisites verified from the recorded facts',
            pending: 'Pending prerequisite facts or a countable declared window',
            conflict: 'Dates or prerequisite facts conflict',
            verificationHint: 'Review the actual dates, supporting evidence and prerequisites in the source. A pending or conflicting fact cannot satisfy a transition prerequisite.',
            choose: 'Choose a declared fact',
            saved: 'The documentary fact was recorded.',
            failed: 'The documentary fact could not be recorded.',
            transitionRemedy: 'Only declared next steps are offered. If a prerequisite is missing, record the documentary fact and its actual date, then retry this step.',
            noNextStep: 'No next step is declared from the recorded state. Independent documentary facts remain recordable.',
            clockEnded: 'Clock ended',
            clockEndedOn: 'Ended on the source-specific actual date: {date}',
            clockRecordedStart: 'Recorded start date: {date}',
            clockHistoricalDue: 'Historical due date: {date}',
            clockConflict: 'Clock dates conflict',
            clockConflictHint: 'The recorded end precedes the start. Review the source evidence; no active due date is shown.',
            clockReview: 'Clock requires review',
            clockReviewHint: 'The installed declaration requires an anchor reading before this clock can be dated.',
            notRecorded: 'Not recorded',
        },
        title: 'Controlled-testing cases',
        subtitle: 'The organisation’s own record of each application to the controlled-testing mechanism. The authority records each step; the platform keeps it with its evidence and derives the clocks the installed record declares. The platform decides nothing.',
        moduleGuideSummary: 'Hold each controlled-testing application: the procedure it follows, the steps the authority has recorded and the clocks the installed record declares. The platform does not assess eligibility or decide the outcome.',
        restricted: 'You need permission to read controlled-testing cases to see them.',
        loading: 'Loading',
        reload: 'Reload',
        notYetRecorded: 'Not yet recorded by the authority',
        notDeclared: 'Not declared by the installed record',
        noChannel: 'No filing channel recorded',
        yes: 'Yes',
        no: 'No',
        list: {
            filtersLabel: 'Filter cases',
            chooseFrameworkFirst: 'Choose a framework first',
            chooseProcedureFirst: 'Choose a procedure first',
            noMatch: 'No match',
            heading: 'Cases',
            openCase: 'Open a case',
            filterFramework: 'Framework',
            filterProcedure: 'Procedure',
            filterStep: 'Recorded step',
            anyFramework: 'Any framework',
            anyProcedure: 'Any procedure',
            anyStep: 'Any step',
            clearFilters: 'Clear filters',
            colApplicant: 'Applicant',
            colProcedure: 'Procedure',
            colStep: 'Recorded step',
            colUpdated: 'Last updated',
            total: '{count} case(s) in total',
            empty: 'No case is held yet. Open one on an installed procedure to start.',
            emptyFiltered: 'No case matches these filters.',
            loadError: 'The cases could not be loaded.',
            retrySubject: 'the case list',
            pagination: 'Pages of cases',
            procedureLabelError: 'The procedure could not be read from the installed record.',
            stepLabelError: 'The step name could not be read from the installed record.',
        },
        pick: {
            frameworkHint: 'Only frameworks that declare a controlled-testing procedure are listed.',
            noCaseFrameworks: 'No installed framework declares a procedure that can carry a case.',
            noFormsOption: '{label} (no application form declared)',
            noFormsInFramework: 'No procedure in this framework declares an application form, so no case can be opened on it. A framework that amends it may declare the forms.',
            framework: 'Installed framework',
            procedure: 'Procedure',
            chooseFramework: 'Choose a framework',
            chooseProcedure: 'Choose a procedure',
            noProcedures: 'This framework declares no procedure that can carry a case.',
            noOpenableFrameworks: 'No installed framework can take a case: a framework installed for reference only does not.',
            hiddenUndeclared: '{count} procedure(s) in this framework declare no lifecycle and are not offered.',
            loadFrameworksError: 'The installed frameworks could not be read.',
            loadProceduresError: 'The procedures of this framework could not be read.',
            needFrameworkRead: 'Reading a framework’s procedures needs the framework read permission.',
        },
        open: {
            noForms: 'This procedure declares no application form, so a case cannot be opened on it. Choose another procedure.',
            title: 'Open a case',
            hint: 'Choose the installed procedure this application follows. The case opens with no authority step recorded.',
            form: 'Application form',
            formHint: 'The forms this procedure declares.',
            applicant: 'Applicant name',
            notes: 'Notes',
            submit: 'Open case',
            cancel: 'Cancel',
            saving: 'Saving…',
            blocked: 'Choose a framework, a procedure and a form, and enter the applicant name.',
            chooseForm: 'Choose a form',
            required: 'Required',
            opened: 'The case was opened.',
            failed: 'The case could not be opened.',
        },
        detail: {
            back: 'Back to cases',
            heading: 'Case',
            applicant: 'Applicant',
            procedure: 'Procedure',
            form: 'Application form',
            framework: 'Framework',
            channel: 'Filing channel',
            usb: 'USB copy',
            usbRecorded: 'USB copy recorded',
            usbNotRecorded: 'USB copy not yet recorded',
            receivedOn: 'Received on',
            notRecorded: 'Not recorded',
            state: 'Recorded step',
            stateUndeclared: 'The recorded step is no longer declared by the installed procedure.',
            history: 'Recorded steps',
            historyEmpty: 'No step has been recorded.',
            colStep: 'Step',
            colEnteredOn: 'Date on the authority’s document',
            colEvidence: 'Evidence',
            colRecordedAt: 'Recorded on',
            clocks: 'Clocks',
            clocksNone: 'The installed record declares no clock for this case.',
            clockRunning: 'Running',
            clockNotStarted: 'Not started',
            clockNoNumeric: 'No numeric period',
            clockNotStartedText: 'The step the clock starts from has no recorded entry, so nothing is counted yet.',
            clockNoNumericText: 'The installed record states no numeric period for this clock.',
            clockPeriod: '{period} {unit}',
            clockStartsOn: 'Counted from {date}',
            clockDue: 'Due {date}',
            clockPaused: 'Paused for {days} day(s)',
            clockSource: 'Source: computed from the recorded anchor',
            clockOwner: 'Obligation owner: {owner}',
            clockName: 'Clock',
            clockStatusHeading: 'Status',
            clockDueHeading: 'Due',
            clockSourceHeading: 'Source',
            clockUnitCalendarDays: 'calendar days',
            clockUnitWorkingDays: 'working days',
            clockUnitMonths: 'months',
            suppressedFrameworkNotOperational: 'Clocks are not computed: the framework is installed for reference only.',
            suppressedProcedureNotDeclared: 'Clocks are not computed: the installed procedure declares no lifecycle.',
            suppressedNotInForce: 'Clocks are not computed: the framework is not in force on {date}.',
            suppressedActivationUnknown: 'Clocks are not computed: the date they bind from is not stated by the installed record.',
            transitionalTitle: 'Transitional status',
            transitionalNotApplicable: 'This procedure carries no transitional rule.',
            transitionalNotDeterminable: 'The installed record does not let the platform determine the transitional status.',
            transitionalAnchorNotRecorded: 'The transitional status waits for the anchor date to be recorded.',
            transitionalYes: 'Transitional: the anchor date is before {cutoff}. No numeric clock is shown for it.',
            transitionalNo: 'Not transitional: the anchor date is on or after {cutoff}.',
            completeness: 'Open items',
            completenessOther: 'An open item applies to this case. The installed record states it.',
            completenessUsb: 'The filing channel requires a USB copy of the dossier set. Record it once it is received. An open item does not block a step.',
            channelUndeclared: 'The recorded channel is not declared by the installed framework.',
            asOf: 'Clocks computed on {date}',
            edit: 'Edit notes and channel',
            editNotes: 'Edit notes',
            recordStep: 'Record a step',
            loadError: 'The case could not be loaded.',
            frameworkUnreadable: 'The framework’s procedure and channel names could not be read, so steps and channels are shown by their identifiers.',
        },
        transition: {
            title: 'Record an authority step',
            hint: 'Enter the step as the authority’s own document states it, with the date on that document and the evidence that shows it. The platform records the step; it does not decide one.',
            step: 'Step',
            chooseStep: 'Choose a step',
            enteredOn: 'Date on the authority’s document',
            evidence: 'Evidence, one reference per line',
            evidenceHint: 'Name the document or attachment that shows this step. At least one is required.',
            submit: 'Record step',
            cancel: 'Cancel',
            saving: 'Saving…',
            recorded: 'The step was recorded.',
            failed: 'The step could not be recorded.',
            blocked: 'Choose a step, enter its date and at least one evidence reference.',
            required: 'Required',
        },
        edit: {
            title: 'Notes and channel',
            titleNotes: 'Notes',
            applicant: 'Applicant name',
            notes: 'Notes',
            channel: 'Filing channel',
            noChannel: 'No channel chosen',
            usbRecorded: 'A USB copy came with the filing',
            usbNotRecorded: 'No USB copy recorded',
            receivedOn: 'Date received',
            submit: 'Save changes',
            saving: 'Saving…',
            saved: 'The case was saved.',
            failed: 'The case could not be saved.',
        },
        stale: 'The case changed since it was opened. It has been reloaded and what you entered is still here. Check it, then submit again.',
        refusedUndeclared: 'The installed procedure does not declare that step. Nothing was recorded.',
        solution: {
            heading: 'Solution evidence',
            intro: 'The one solution this application proposes to test: its type, the customer group it reaches, the technical statement it rests on and the security level it is held to. The platform records what the solution states. It does not decide whether the solution is suitable.',
            loading: 'Loading the solution record…',
            loadError: 'The solution record could not be loaded.',
            retrySubject: 'the solution record',
            readOnly: 'You can read this record. Recording it, changing it and attaching the technical statement need the manage permission.',
            add: 'Record the solution',
            edit: 'Change the solution',
            notRecorded: 'Not yet recorded',
            typeLabel: 'Solution type',
            typeChosen: 'Chosen: {label}',
            typeNotUsed: 'Not used: {label}',
            typeNoneDeclared: 'The installed record declares no solution type, so none can be recorded yet.',
            typeSingleDeclared: 'The installed record declares one solution type only: {label}. The solution description states a choice between two.',
            typeUndeclared: 'Not declared by the installed record ({code})',
            customerGroupLabel: 'Prospective customer group',
            statementLabel: 'Technical statement',
            statementNotAttached: 'Not yet attached',
            statementAttached: 'Attached: {name}',
            statementAttachedNoName: 'Attached. Its name needs the permission to download attachments.',
            statementUpload: 'Attach the technical statement',
            statementRejected: 'The technical statement could not be attached.',
            statementNotBound: 'The file is held for this solution but is not yet attached to it. {message}',
            levelLabel: 'Security level',
            levelRecorded: '{label} ({scheme}; basis: {basis})',
            levelUndeclaredTier: 'Not declared by the installed record ({code})',
            levelNotDeclared: 'Not declared by an installed framework',
            criteriaLabel: 'Technical criteria',
            criteriaTextNotInCorpus: 'Text not in corpus. Source: {source}. No criterion is shown here as a verified requirement.',
            criteriaNotDeclared: 'Not declared by an installed framework.',
            dossierLabel: 'Solution record',
            dossierComplete: 'All four items are recorded. The authority decides what follows.',
            dossierIncomplete: 'Not yet complete. Missing: {items}.',
            item_solution_type: 'the solution type',
            item_customer_group: 'the customer group',
            item_technical_statement: 'the technical statement',
            item_security_level: 'the security level',
            editor_title: 'Record the solution',
            editor_titleEdit: 'Change the solution',
            editor_hint: 'Choose the solution type the installed record declares, enter the customer group as the solution description states it, and choose the security level an installed framework declares. Attach the technical statement after the solution is saved.',
            editor_typeChoose: 'Choose a solution type',
            editor_typeNone: 'The installed record declares no solution type, so none can be chosen.',
            editor_customerGroup: 'Prospective customer group',
            editor_levelChoose: 'Choose a security level',
            editor_levelNone: 'No installed framework declares a security level, so none can be chosen.',
            editor_levelOption: '{label} ({scheme})',
            editor_submit: 'Save the solution',
            editor_saving: 'Saving…',
            editor_cancel: 'Cancel',
            editor_saved: 'The solution was saved.',
            editor_failed: 'The solution could not be saved.',
            editor_stale: 'The solution changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
        },
        registry: {
            heading: 'Authoritative-source records',
            intro: 'Documentary source records for this application. Person-scoped rows link an exact personnel record; its criminal outcome remains in the personnel section. Requests, source retrieval and actual component supply are separate dated facts. A request or decision does not replace missing information.',
            loading: 'Loading the registry records…',
            loadError: 'The registry records could not be loaded.',
            retrySubject: 'the registry records',
            readOnly: 'You can read these records. Recording a document, changing it and attaching a supplement file need the manage permission.',
            add: 'Record a document',
            edit: 'Change',
            notRecorded: 'Not yet recorded',
            stateBankRetrieves: 'The State Bank retrieves this record. The platform does not call the registry, and the outcome shown is the one recorded from the State Bank’s answer.',
            requirementLabel: 'Requirement',
            requirementNotDeclared: 'Not declared by an installed framework',
            sourceLabel: 'Source',
            sourceRegistry: 'Retrieved from the registry by the State Bank',
            sourceApplicant: 'A copy the organisation supplies',
            outcomeLabel: 'State Bank outcome',
            outcomeClass_retrieved: 'Counts as retrieved',
            outcomeClass_not_retrieved: 'Does not count as retrieved',
            outcomeClass_incomplete_or_inaccurate: 'Does not count as retrieved: incomplete or inaccurate',
            outcomeSeen: 'Date seen: {date}',
            outcomeNotDeclared: 'Not declared by an installed framework, so no outcome can be recorded.',
            outcomeUndeclared: 'Not declared by the installed record ({code})',
            outcomeNotApplicable: 'Not applicable: the document is a copy the organisation supplies.',
            recorderLabel: 'Recorded by',
            supplementLabel: 'Supplement or decision',
            supplementNotNeeded: 'Not needed: the outcome counts as retrieved.',
            supplementRecorded: 'Supplement dated {date}: {reference}',
            supplementFileOnly: 'Supplement dated {date}, with its file attached.',
            supplementFileAttached: 'File: {name}',
            supplementFileNoName: 'File attached. Its name needs the permission to download attachments.',
            decisionRecorded: 'Decision dated {date}: {text}',
            supplementMissing: 'Needed. Record a dated supplement with its reference or file, or a dated decision that none is required.',
            limbLabel: 'Investment registration limb',
            limbApplies: 'Applies on this track. Source: {source}.',
            item_scope_declaration: 'declared requirement scope',
            item_personnel_link: 'exact personnel link',
            item_retrieved_on: 'actual source-retrieval date',
            item_failure_follow_up_declaration: 'source-specific follow-up declaration',
            item_failure_follow_up_request: 'documentary follow-up request',
            item_supplement: 'actual supplied component',
            item_applicant_copy_file: 'held applicant-copy file',
            item_information_pending: 'information remains pending',
            limbConditional: 'This cited dossier component includes an investment registration certificate if any. It does not establish that this applicant has or must obtain one. Source: {source}.',
            limbNotListed: 'This cited dossier component does not list an investment registration certificate. This is not a general investment-registration exemption. Source: {source}.',
            limbNotOnTrack: 'Does not apply on this track.',
            limbTrackNotDeclared: 'The track or source-component declaration is undetermined. No document obligation or exemption is inferred.',
            dossierLabel: 'Record',
            dossierComplete: 'Complete: the row holds what its source and outcome require. The authority decides what follows.',
            dossierIncomplete: 'Not yet complete. Missing: {items}.',
            item_source: 'the source',
            item_outcome: 'the State Bank outcome and the date it was seen',
            item_supplement_or_decision: 'a dated supplement or a recorded decision',
            editor_title: 'Record a document',
            editor_titleEdit: 'Change the document',
            editor_hint: 'Choose the requirement, the document’s source, and the outcome the State Bank answered with the date it was seen. Where the State Bank did not retrieve a document in full, it needs a dated supplement, or a dated decision that none is required.',
            editor_requirement: 'Requirement',
            editor_requirementChoose: 'Choose a requirement',
            editor_requirementNone: 'Every requirement the installed framework declares is already recorded, or none is declared.',
            editor_source: 'Source',
            editor_sourceChoose: 'Choose a source',
            editor_outcome: 'State Bank outcome',
            editor_outcomeChoose: 'Choose an outcome',
            editor_outcomeNone: 'The installed record declares no outcome for this requirement, so none can be chosen.',
            editor_outcomeOn: 'Date the outcome was seen',
            editor_supplementOn: 'Actual component supplied date',
            editor_supplementReference: 'Supplement reference',
            editor_supplementReferenceHint: 'Up to 500 characters: the letter or file reference.',
            editor_decisionOn: 'Decision date',
            editor_decisionText: 'Decision text',
            editor_decisionTextHint: 'Up to 1000 characters: preserve the actual documentary decision and explanation. It does not waive source-specific requests or missing information.',
            editor_submit: 'Save the document',
            editor_saving: 'Saving…',
            editor_cancel: 'Cancel',
            editor_saved: 'The document was saved.',
            editor_failed: 'The document could not be saved.',
            editor_stale: 'The document changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
            supplementUpload: 'Attach the supplement file',
            supplementRejected: 'The supplement file could not be attached.',
            supplementNotBound: 'The file is held for this document but is not yet attached to it. {message}',
        },
        personnel: {
            generic: {
                heading: 'Declared documentary conditions',
                hint: 'Record each declared answer with its own evidence reference. These are documentary records, not eligibility decisions. Changing a choice requires its evidence to be entered again.',
                evidence: 'Evidence reference for this condition',
                required: 'required when an answer is chosen',
                source: 'Source',
                notDeclared: 'No usable condition declaration is available. No eligibility conclusion is made.',
                not_recorded: 'Not recorded',
                recorded_with_evidence: 'Answer and its evidence reference recorded',
                evidence_missing: 'Recorded answer has no evidence reference',
                option_not_declared: 'The recorded option is no longer declared',
                does_not_apply: 'Does not apply to this record’s declared form and roles',
                not_declared: 'Condition declaration requires review',
                clear: 'Clear this condition answer explicitly',
                legacyHeading: 'Preserved earlier condition content',
                legacyHint: 'Earlier content is preserved separately and has not been converted into current condition answers.',
                legacyBasis: 'Preserved earlier role qualification',
                clearBasis: 'Clear earlier role qualification',
                history: 'Immutable earlier role-qualification history',
                historyNone: 'No earlier role snapshot is recorded.',
                role: 'Role',
                qualification: 'Earlier qualification code',
                dates: 'Recorded role dates',
                evidenceIds: 'Recorded evidence references; current held status is not asserted',
                actor: 'Recorded by',
                time: 'Recorded at',
                versions: 'Record versions',
                reload: 'Read current personnel record',
                stale: 'The record changed. Read the current record, then review your retained entries before retrying.',
                reloaded: 'Current record read. Your entries remain unchanged; review the declared choices and current version before retrying.',
                retiredAnswer: 'Preserved answer not declared by the installed record',
                retainedDraft: 'Retained draft change needs review against the current declaration. It has not been saved.',
                discardDraft: 'Discard this unsaved draft change',
            },
            heading: 'Personnel eligibility evidence',
            intro: 'The people this application names, the dated roles each one holds and the evidence each role rests on. The platform records what the evidence shows. It does not decide whether anyone is eligible.',
            loading: 'Loading the personnel records…',
            loadError: 'The personnel records could not be loaded.',
            retrySubject: 'the personnel records',
            vocabError: 'The roles and outcomes declared by the installed record could not be read.',
            notDeclaredRoles: 'The installed record declares no role for this framework, so no role can be recorded yet.',
            notDeclaredBases: 'The installed record declares no qualification alternative for this framework.',
            notDeclaredOutcomes: 'The installed record declares no criminal-record outcome for this framework, so none can be recorded yet.',
            empty: 'No person is recorded for this case yet.',
            total: '{count} personnel record(s) in total',
            pagination: 'Pages of personnel records',
            readOnly: 'You can read these records. Recording, changing them and attaching evidence need the manage permission.',
            add: 'Add a person',
            personName: 'Name of the person',
            personHint: 'Type the name as it appears on the personal record statement.',
            statementSignedOn: 'Date the personal record statement was signed',
            statementNotRecorded: 'Statement date not recorded',
            edit: 'Edit this record',
            colRole: 'Role',
            colBasis: 'Qualification alternative the evidence shows',
            colDates: 'Dates held',
            colApplicability: 'Applies to the application form',
            colEvidence: 'Evidence',
            noRoles: 'No role is recorded for this person yet.',
            basisNotRecorded: 'Not yet recorded',
            undeclaredRole: 'Role not declared by the installed record ({code})',
            undeclaredBasis: 'Alternative not declared by the installed record ({code})',
            undeclaredOutcome: 'Outcome not declared by the installed record ({code})',
            heldFrom: 'From {from}',
            heldUntil: 'to {to}',
            heldOpen: 'still held',
            applies: 'Applies to this application form',
            doesNotApply: 'Does not apply to this application form',
            applicabilityNotDeclared: 'Not declared by the installed record',
            evidenceNone: 'No evidence is attached to this role.',
            evidenceCount: '{count} file(s) attached. Their names need the permission to download attachments.',
            evidenceUpload: 'Attach evidence to {role}',
            evidenceRejected: 'The evidence could not be attached.',
            evidenceNotBound: 'The file is held for this record but is not yet attached to the role. {message}',
            currency: {
                heading: 'Currency of the statement',
                current: 'Within the declared limit until {date}.',
                older: 'Evidence is older than the declared limit.',
                notDeterminable: 'Not determinable: {reason}.',
                reasonNoStatementDate: 'no statement date is recorded',
                reasonNoLimit: 'the installed record declares no currency limit',
                reasonNoLimitInForce: 'the declared limit is not in force on this date',
                reasonActivationUnknown: 'the date the declared limit takes effect is not stated',
                reasonNotOperational: 'the framework is installed for reference only',
                basisReceipt: 'Judged on the receipt date, {date}.',
                basisAsOf: 'Judged on the date of this view, {date}.',
            },
            outcome: {
                heading: 'Criminal-record check outcome',
                recordOutcome: 'Record the authority’s outcome',
                changeOutcome: 'Change the recorded outcome',
                recordedOn: '{outcome}, reported on {date}',
                title: 'Record the criminal-record outcome the authority gave',
                hint: 'Choose the outcome the State Bank reported and enter the date the authority gave it. The platform keeps that outcome only. The content of the check stays with the authority that holds it, and none of it can be entered here.',
                code: 'Outcome',
                chooseCode: 'Choose an outcome',
                on: 'Date the authority gave the outcome',
                submit: 'Record outcome',
                saving: 'Saving…',
                cancel: 'Cancel',
                saved: 'The outcome was recorded.',
                failed: 'The outcome could not be recorded.',
                blocked: 'Choose an outcome and enter the date the authority gave it.',
            },
            record: {
                addTitle: 'Add a person',
                editTitle: 'Edit this personnel record',
                hint: 'Enter the person’s name and each role with the dates it was held, as the evidence states them. For each role, record the qualification alternative the evidence shows. The platform records that choice; it does not decide whether the person qualifies.',
                roleRow: 'Role {n}',
                role: 'Role',
                chooseRole: 'Choose a role',
                basis: 'Qualification alternative the evidence shows',
                basisNotRecorded: 'Not yet recorded',
                from: 'Held from',
                to: 'Held until',
                toOpen: 'Still held (leave empty)',
                removeRole: 'Remove this role',
                addRole: 'Add a role',
                submitAdd: 'Record person',
                submitEdit: 'Save record',
                saving: 'Saving…',
                cancel: 'Cancel',
                required: 'Required',
                blocked: 'Enter the person’s name and, for each role, the role and the date it was held from.',
                added: 'The person was recorded.',
                saved: 'The record was saved.',
                failed: 'The record could not be saved.',
                stale: 'The record changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
            },
            conditions: {
                heading: 'Conditions of the legal representative and general director',
                recordConditions: 'Record the conditions',
                changeConditions: 'Change the recorded conditions',
                notRecorded: 'Not recorded',
                nationality: 'Nationality',
                conviction: 'Criminal record',
                sanction: 'Administrative sanction in finance, banking or cybersecurity',
                notDeclared: 'The installed record declares no condition for this application form.',
                title: 'Record what the evidence shows about each condition',
                hint: 'For each condition, choose what the evidence shows. Leave a condition empty when it is not recorded. The platform keeps the choice the evidence shows. It does not decide on the condition.',
                chooseOption: 'Not recorded',
                undeclared: 'Not an option the installed record declares ({code})',
                submit: 'Record conditions',
                saving: 'Saving…',
                cancel: 'Cancel',
                saved: 'The conditions were recorded.',
                failed: 'The conditions could not be recorded.',
                clearRetired: 'Clear the retired criminal-record value',
                clearRetiredHint: 'The criminal-record code was retired, and the installed record no longer declares it. Clearing removes it from this record. The two conditions above are not changed.',
                clearedRetired: 'The retired value was cleared.',
            },
        },
        copy: {
            recordedBy: 'Recorded by',
            notice: 'Platform notice',
        },
        outcome: {
            heading: 'Stop, completion and closure',
            intro: 'A stop is the only way to end a case early. The platform records what the organisation files and what the State Bank decides. The State Bank decides every stop, revocation and completion, and issues every decision and certificate. The platform holds the copy the authority gives and says where that copy came from.',
            readOnly: 'You can read these records. Filing or recording needs the manage permission.',
            // Sprint 62.51.0 (CL-3) — a procedure that declares no organisation outcome filing.
            stopNotOffered: 'The installed procedure of this case offers no stop filing by the organisation, so none is recorded here.',
            completionNotOffered: 'The installed procedure of this case offers no completion filing by the organisation, so none is recorded here.',
            recordedOnIntro: 'Its outcome is recorded from the authority’s and the body’s own documents in:',
            recordedOnNone: 'The installed procedure names no register where its outcome is recorded.',
            loadError: 'The stop, completion, closure and requests could not be read.',
            retry: 'Try again',
            notDeclared: 'Not declared by an installed framework.',
            stop: {
                heading: 'Stop',
                none: 'No stop has been filed for this case.',
                filed: 'Stop filed. The State Bank’s decision is awaited. The platform records no decision of its own.',
                decided: 'The State Bank’s decision is recorded, as the authority gave it.',
                stopDate: 'Stop date, as a person entered it: {date}.',
                groundsHeading: 'Grounds',
                viewGrounds: 'View the grounds ({count})',
                groundsNone: 'Not declared by an installed framework. The platform records no ground.',
                filesHeading: 'Files the stop is filed with',
                role_application: 'Application',
                role_results_report: 'Results report',
                role_stop_plan: 'Stop plan',
                held: 'Held: {name}.',
                notHeld: 'Not held under this case.',
                notFiled: 'Not filed.',
                missing: 'Still missing: {roles}.',
                complete: 'Every file the stop needs is held.',
                endedByCertificate: 'The authority’s certificate of completion is recorded, so no stop is filed on this case.',
                decisionEndedByCertificate: 'The authority’s certificate of completion is recorded, so no decision to stop and revoke the test is recorded on this case.',
                filedEndedByCertificate: 'Stop filed. The authority’s certificate of completion is recorded, so no decision on this stop is awaited.',
                decisionHeading: 'The State Bank’s decision',
                decisionNone: 'No decision is recorded. The platform records one only from the authority’s document.',
                decisionRef: 'Decision reference: {ref}',
                decidedOn: 'Date of the decision: {date}',
                decisionText: 'Text of the decision, as the authority gave it:',
                evidence: 'Evidence: {names}',
            },
            completion: {
                heading: 'Completion',
                none: 'No completion request has been filed.',
                filed: 'Completion request filed. The State Bank evaluates it. The platform decides nothing.',
                role_evaluation_report: 'Evaluation report',
                role_results_report: 'Results report',
                endedByRevocation: 'The State Bank’s decision to stop and revoke the test is recorded, so no completion request or copy of a certificate is recorded on this case.',
                certificateNone: 'No copy of the certificate is recorded. The platform issues no certificate.',
                certificateRecorded: 'The authority’s copy is recorded.',
                copySource: 'Source: the authority’s copy.',
                copyDate: 'Date on the authority’s copy: {date}.',
            },
            closure: {
                heading: 'Closure',
                unavailable: 'Closure is recorded after the authority’s decision is recorded.',
                noticesHeading: 'Stop notices',
                noticesNone: 'No stop notice is recorded.',
                notice: 'Notice sent {date}: {name}.',
                liquidationHeading: 'Liquidation record',
                liquidationNone: 'No liquidation record is recorded.',
                liquidation: 'Liquidation record of {date}: {name}.',
                dutiesHeading: 'Post-stop duties',
                dutiesIntro: 'These duties come from the base instrument. The platform records them and does not create them. Each runs from its own anchor date, which a person enters on the closure record. A duty whose anchor date is not entered shows no date.',
                duty_entitlements_settled: 'Customer rights and obligations are settled',
                duty_settlement_report: 'The settlement is reported in writing',
                citation: 'Source: {citation}',
                dutyNotDeclared: 'Not declared by an installed framework. No period is shown and no date is computed.',
                dutyAmbiguous: 'More than one installed record declares this duty. The platform does not choose between them.',
                dutyNotCountable: 'The installed period is not one the platform counts. No date is computed.',
                dutyDueFromAnchor: 'Period: {period} {unit} from {anchor}. Due {due}.',
                dutyAnchorNotRecorded: 'Anchor not recorded ({anchor}). No date is shown.',
                dutyNotInForce: 'The installed clock for this duty is not in force yet, or the record states no start date from which it can be read. No date is shown.',
                dutyContinuingNoPeriod: 'Continuing duty. The text states no period for a solution outside the scope of this duty, so no period and no date are shown.',
                dutySolutionPointNotRecorded: 'Solution point not recorded. The case’s application form does not show which solution point it is, so no date is computed.',
                anchor_stop_decision: 'the State Bank stop decision date',
                anchor_settlement_completion: 'the settlement completion date',
                anchorLabel_stop_decision: 'State Bank stop decision date',
                anchorLabel_settlement_completion: 'Settlement completion date',
                anchorsHeading: 'Anchor dates',
                settlementReferencePending: 'Settlement reference: pending until the State Bank stop decision date is recorded.',
                settlementReferenceRecorded: 'Settlement reference: {date}, the recorded State Bank stop decision date.',
                anchorRecorded: '{label}: {date}. Evidence: {evidence}.',
                anchorNotRecorded: '{label}: not recorded.',
                unit_months: 'months',
                unit_calendar_days: 'calendar days',
                unit_working_days: 'working days',
            },
            directions: {
                heading: 'The authority’s requests and on-site notices',
                intro: 'Each is recorded in the direction register. Its response period is read from the installed record that declares its kind.',
                none: 'None recorded.',
                kindUndeclared: 'Recorded as “{kind}”, a kind no installed framework of this case declares.',
                registerWithheld: 'The reference, authority, receipt and response period are held in the direction register, which needs its own read permission.',
                manageNeeded: 'Recording a request or notice also needs the direction register’s manage permission.',
                ref: 'Reference: {ref}',
                authority: 'Authority: {name}',
                received: 'Received {date}',
                clockRunning: 'Response due {due}, from the installed record.',
                clockNotDeclared: 'Response period: not declared by an installed framework.',
                clockOther: 'Response period not computed: {reason}.',
                clockNotInForceFrom: 'Response period not computed: the framework that declares it applies only from {date}.',
            },
            actions: {
                fileStop: 'File a stop',
                recordDecision: 'Record the authority’s decision',
                fileCompletion: 'File a completion request',
                recordCertificate: 'Record the authority’s copy',
                recordClosure: 'Record closure',
                recordDirection: 'Record a request or notice',
                chooseFile: 'Choose a file',
                uploading: 'Holding the file…',
                uploaded: 'Held under this case.',
                uploadFailed: 'The file could not be held under this case.',
                cancel: 'Cancel',
                saving: 'Saving…',
                continue: 'Continue to confirmation',
            },
            stopForm: {
                title: 'File a stop',
                hint: 'The stop needs the application, the results report and the stop plan, each held under this case, and the stop date as a person knows it.',
                incomplete: 'Hold the application, the results report and the stop plan, and enter the stop date, before continuing.',
                stopDate: 'Stop date',
            },
            stopConfirm: {
                title: 'Confirm the stop',
                text: 'Filing records the organisation’s stop. The State Bank decides whether the test is stopped and whether the certificate is revoked. Nothing on this page decides for it.',
                confirm: 'File the stop',
                done: 'The stop is filed.',
                failed: 'The stop could not be filed.',
            },
            revoke: {
                title: 'Record the State Bank’s decision',
                hint: 'Record only the authority’s own decision, from its document. The reference and the text are kept as the authority gave them.',
                incomplete: 'Enter the decision reference, the date, the text and the decision file. Each is kept as the authority gave it.',
                ref: 'Decision reference',
                decidedOn: 'Date of the decision',
                text: 'Text of the decision',
                evidence: 'Decision file',
                submit: 'Record the decision',
                done: 'The decision is recorded.',
                failed: 'The decision could not be recorded.',
            },
            completionForm: {
                title: 'File a completion request',
                hint: 'The request needs the evaluation report and the results report, each held under this case.',
                incomplete: 'Hold both reports before filing the request.',
                submit: 'File the request',
                done: 'The completion request is filed.',
                failed: 'The completion request could not be filed.',
            },
            certificate: {
                title: 'Record the authority’s copy',
                hint: 'Record only a copy the authority gave. The platform issues no certificate.',
                incomplete: 'Hold the authority’s copy and enter its date.',
                copyDate: 'Date on the authority’s copy',
                file: 'The authority’s copy',
                submit: 'Record the copy',
                done: 'The authority’s copy is recorded.',
                failed: 'The authority’s copy could not be recorded.',
            },
            anchorCorrection: {
                heading: 'Anchor and documentary-event comparison',
                limitation: 'This compares declared events on this case only. Correcting an anchor does not change a lifecycle event or revocation record. Wrong lifecycle facts require a separate correction path.',
                not_declared: 'No same-case event comparison is declared.',
                pending_anchor: 'Documentary event recorded; an explicit anchor is still required.',
                anchor_only: 'Explicit anchor recorded; no counterpart event is recorded.',
                pending_fact: 'Counterpart evidence is pending verification. No affected due date is shown.',
                agreed: 'The explicit anchor and verified documentary event agree.',
                conflict: 'The records conflict. Both dates are preserved; no affected due date is shown.',
                mappingInvalid: 'The installed comparison declaration needs review.',
                multipleEvents: 'Several counterpart occurrences are recorded. None is selected as authoritative.',
                datesDisagree: 'The recorded actual dates differ.',
                evidenceReview: 'Review the counterpart event’s evidence and declared prerequisites.',
                anchorDate: 'Explicit anchor date',
                eventDate: 'Documentary event occurrence date',
                event: 'Declared event',
                source: 'Source',
                notRecorded: 'Not recorded',
                anchorNotDeclared: 'The installed duty declares no anchor. No due date is computed.',
                dutyConflict: 'The anchor conflicts with its documentary event. No due date is computed.',
                dutyPending: 'The anchor’s counterpart evidence is pending verification. No due date is computed.',
                declaredPeriod: 'Declared period: {period} {unit}.',
                settlementConflict: 'Settlement reference: conflicting documentary dates. No reference date is selected.',
                correct: 'Correct anchor',
                title: 'Correct an explicitly recorded anchor',
                hint: 'Enter the actual corrected date, evidence and justification. The previous record is preserved in immutable history. This action does not record or correct an authority decision, lifecycle fact or revocation.',
                date: 'Corrected actual date',
                evidence: 'Evidence reference for the correction',
                reason: 'Reason for correction',
                required: 'required',
                submit: 'Record anchor correction',
                saved: 'The anchor correction was recorded.',
                failed: 'The anchor correction could not be recorded.',
                stale: 'The case changed. Reload its closure record before trying again. Your entries are retained.',
                reload: 'Reload closure record',
                reloaded: 'Current closure read. Review the current anchor and retained entries before retrying.',
                history: 'Immutable anchor correction history',
                historyNone: 'No anchor correction is recorded.',
                previous: 'Previous anchor record',
                corrected: 'Corrected anchor record',
                recordedBy: 'Recorded by',
                recordedAt: 'Recorded at',
                versions: 'Case versions',
            },
            closureForm: {
                title: 'Record closure',
                hint: 'Record a stop notice, the liquidation record, or both, as each was made.',
                incomplete: 'Hold a stop notice with its date, the liquidation record with its date, or both.',
                noticeFile: 'Stop notice file',
                noticeDate: 'Date the notice was sent',
                liquidationFile: 'Liquidation record file',
                liquidationDate: 'Date of the liquidation record',
                anchorsHint: 'Enter each date as the record states it, with the evidence it rests on. No date is computed here.',
                stopDecisionDate: 'State Bank stop decision date',
                stopDecisionEvidence: 'Evidence of the stop decision',
                settlementDate: 'Settlement completion date',
                settlementEvidence: 'Evidence of the settlement',
                noticeIncomplete: 'A stop notice needs its file and the date it was sent. Enter both, or leave the notice empty.',
                liquidationIncomplete: 'The liquidation record needs its file and its date. Enter both, or leave it empty.',
                anchorIncomplete: 'Each anchor date needs its date and its evidence. Enter both, or leave that anchor empty.',
                anchorRecordedNote: '{label}: already recorded. It is not entered again.',
                refusal: {
                    STOP_DECISION_IN_FUTURE: 'The stop decision date cannot be after today.',
                    SETTLEMENT_BEFORE_STOP: 'The settlement completion date cannot be before the stop it follows.',
                    POST_STOP_ANCHOR_ALREADY_RECORDED: 'This date is already recorded. It is not entered again.',
                },
                submit: 'Record',
                done: 'The closure is recorded.',
                failed: 'The closure could not be recorded.',
            },
            directionForm: {
                kindsLoading: 'Reading the kinds the installed record declares…',
                kindsNone: 'No installed framework of this case declares a kind of request or notice, so none can be recorded here.',
                kindsLoadError: 'The kinds of request or notice could not be read.',
                kindChoose: 'Choose a kind',
                title: 'Record a request or notice',
                hint: 'Recorded in the direction register. Its clock is read from the installed record, not set here.',
                incomplete: 'Complete each field, and give at least one receipt reference.',
                kind: 'Kind',
                ref: 'Reference',
                authority: 'Authority',
                scope: 'Scope',
                received: 'Date and time received',
                receivedDay: 'Date received',
                receivedTime: 'Time received',
                receipt: 'Receipt references, one per line',
                submit: 'Record',
                done: 'The request or notice is recorded.',
                failed: 'The request or notice could not be recorded.',
            },
            fieldLabels: {
                application_file_id: 'Application',
                results_report_file_id: 'Results report',
                stop_plan_file_id: 'Stop plan',
                stop_date: 'Stop date',
                decision_ref: 'Decision reference',
                decided_on: 'Date of the decision',
                decision_text: 'Text of the decision',
                evidence_file_ids: 'Decision file',
                evaluation_report_file_id: 'Evaluation report',
                file_id: 'The authority’s copy',
                copy_dated_on: 'Date on the authority’s copy',
                notice: 'Stop notice',
                liquidation: 'Liquidation record',
                kind: 'Kind',
                direction_ref: 'Reference',
                authority_name: 'Authority',
                scope_description: 'Scope',
                received_at: 'Date and time received',
                receipt_evidence_refs: 'Receipt references',
                'anchors.stop_decision.date': 'State Bank stop decision date',
                'anchors.stop_decision.evidence_ref': 'Evidence of the stop decision',
                'anchors.settlement_completion.date': 'Settlement completion date',
                'anchors.settlement_completion.evidence_ref': 'Evidence of the settlement',
                'anchors.stop_decision': 'State Bank stop decision date',
                'anchors.settlement_completion': 'Settlement completion date',
            },
        },
        children: {
            heading: 'Adjustments and extensions',
            intro: 'A child case changes or continues an application this case already holds. The platform records the steps the State Bank reports and computes the periods the installed record declares. The State Bank decides each request.',
            kindAdjustment: 'Adjustment',
            kindExtension: 'Extension',
            openAdjustment: 'Open an adjustment',
            openExtension: 'Open an extension',
            openTitleAdjustment: 'Open an adjustment',
            openTitleExtension: 'Open an extension',
            openHint: 'The platform copies this case’s solution record as it stands now. The copy does not change when the solution is edited later.',
            formLabel: 'Application form',
            formNone: 'The installed record declares no application form for this kind.',
            notesLabel: 'Notes (optional)',
            submit: 'Open the case',
            saving: 'Opening…',
            cancel: 'Cancel',
            opened: 'The child case is open.',
            openFailed: 'The child case could not be opened.',
            empty: 'No adjustment or extension is recorded on this case.',
            loadError: 'The adjustments and extensions could not be read.',
            unavailable: 'No child case can be opened on this case. {reason}',
            refusedReason: {
                grant_not_declared: 'The parent case has no grant recorded by an installed declaration.',
                grant_not_recorded: 'The parent case has no grant recorded yet.',
                parent_procedure_not_declared: 'The parent case’s procedure declares no lifecycle, so no child case can open on it.',
                framework_not_operational: 'The framework is installed for reference, so no child case can open on it.',
                parent_framework_not_installed: 'The framework is not installed for this organisation.',
                kind_not_declared: 'No installed record declares this kind of child case.',
                kind_ambiguous: 'More than one installed record declares this kind of child case.',
                parent_stopped_and_revoked: 'The State Bank’s decision to stop and revoke the parent test is recorded, so no child case can open on it.',
            },
            readOnly: 'You can read these cases. Opening a child case needs the manage permission.',
            clocksHeading: 'Clocks',
            noClocks: 'No clock applies to this request under the installed record.',
            clockRunning: '{name}: due {due}. Counted {period} {unit} from {start}.',
            clockPaused: '{name}: due {due}. Counted {period} {unit} from {start}, with {paused} {unit} paused.',
            clockNotStarted: '{name}: not started. No entry is recorded for its start step.',
            clockNoNumeric: '{name}: the installed record declares no numeric period.',
            clockSource: 'Computed from the recorded step date. It is not a date set by the authority.',
            clockOwnerAuthority: 'Owed by the State Bank.',
            clockOwnerOrganisation: 'Owed by the organisation.',
            unitWorkingDays: 'working days',
            unitCalendarDays: 'calendar days',
            unitMonths: 'months',
            resultsHeading: 'Results report',
            resultsProvisional: 'Format provisional. The results-report format is in Phụ lục V of Decree 94, which is not in the corpus. No format is shown as verified.',
            filingHeading: 'Filing',
            filingNotRecorded: 'Not recorded. A filing is recorded only against an internal sign-off that is recorded as complete.',
            filingRecorded: 'Recorded by the organisation: reference {reference}, filed {date}.',
            signoffNone: 'No internal sign-off has been requested for this case.',
            signoffPending: 'Internal sign-off: pending. A filing cannot be recorded until the sign-off is recorded as complete.',
            signoffComplete: 'Internal sign-off: recorded as complete.',
            signoffNotComplete: 'Internal sign-off: recorded as not complete. A filing cannot be recorded on it.',
            solutionHeading: 'Solution copied from the parent',
            solutionCopied: 'Copied from the parent solution record at version {version}.',
            solutionNone: 'The parent holds no solution record, so nothing was copied.',
            recordStep: 'Record a step',
            retry: 'Try again',
            currentStep: 'Current step: {step}',
            noStep: 'No step is recorded yet.',
            chooseForm: 'Choose a form',
        },
    },
    // Sprint 63.23.0 — the OAuth consent screen at /oauth/consent.
    oauth_consent: {
        error_request_not_found: 'This request has expired or was already answered. Return to the app and start connecting it again.',
        error_request_already_decided: 'This request was already answered. Return to the app; if it is not connected, start connecting it again.',
        error_client_revoked: 'This app has been revoked, so it cannot connect. Ask an administrator of your organisation if you need it.',
        page_title: 'Approve an app',
        title: 'Allow {client} to use ComplianceOne?',
        title_pending: 'Approve an app',
        loading: 'Loading the request…',
        intro: '{client} is asking to use ComplianceOne on your behalf.',
        client_label: 'App',
        origin_label: 'Registered by',
        origin_admin: 'An administrator of your organisation',
        origin_dynamic: 'The app itself — no administrator has reviewed it',
        redirect_label: 'Sends you back to',
        organisation_label: 'Organisation',
        scopes_heading: 'It will be able to',
        scope_read: 'Read your organisation’s records (mcp:read)',
        scope_write: 'Propose changes for approval (mcp:write)',
        scope_write_note: 'A proposed change is not made until an administrator approves it in the approval queue.',
        scope_other: 'Another permission: {scope}',
        tools_heading: 'Tools it can use',
        tools_none: 'No tools.',
        acts_as_you: 'It acts as you, with no more access than your own role gives you. You can revoke it at any time under Connected apps on your profile.',
        approve: 'Allow',
        deny: 'Deny',
        submitting: 'Sending your decision…',
        missing_request: 'This page was opened without a request. Return to the app and start connecting it again.',
        error_load: 'The request could not be read. It may have expired or already been answered; return to the app and start connecting it again, or reload the request.',
        reload: 'Reload the request',
        error_decide: 'Your decision could not be sent.',
        redirecting: 'Returning you to the app…',
        // Gate E12.5 pass 1 (P2-8) — a redirect whose "host" says nothing about
        // who receives the approval.
        redirect_warning_title: 'Check where this approval goes',
        redirect_warning_loopback: 'This app receives the approval at an address on your own computer ({host}). Any program running on this computer could be listening there. Allow it only if you started this connection yourself, on this computer.',
        redirect_warning_private_use_scheme: 'This app receives the approval through a link that an app installed on your device opens ({host}). Any installed app can claim such a link. Allow it only if you started this connection yourself, from the app you expect.',
    },
    login: {
        title: 'AesirX ComplianceOne',
        subtitle: 'Log in op uw account',
        email: 'E-mail',
        password: 'Wachtwoord',
        sign_in: 'Inloggen',
        signing_in: 'Bezig met inloggen...',
        mfa_code: 'MFA-code',
        mfa_placeholder: 'Voer 6-cijferige code in',
        mfa_instruction: 'Voer de 6-cijferige code in van uw authenticatie-app.',
        // ── Sprint 63.7.0: organisation-level MFA enforcement + IP allowlist enforcement ──
        password_change_required_title: 'Change your password',
        password_change_required_intro: 'Your administrator requires you to choose a new password before you continue. Nothing else is available until you do.',
        password_change_current: 'Current password',
        password_change_current_hint: 'The password you signed in with.',
        password_change_new: 'New password',
        password_change_new_hint: 'At least 12 characters, with an upper-case letter, a lower-case letter, a digit and a special character. It must differ from your last 12 passwords.',
        password_change_confirm: 'Confirm new password',
        password_change_mismatch: 'The two new passwords do not match.',
        password_change_field_required: 'This field is required.',
        password_change_submit: 'Change password',
        password_change_failed: 'Your password could not be changed.',
        password_change_done: 'Your password has been changed and you have been signed out everywhere. Sign in with your new password.',
        password_change_sign_out: 'Sign out',
        mfa_enrolment_required_title: 'Set up two-factor authentication',
        mfa_enrolment_required_intro: 'Your organisation requires a second factor for your account. Set one up to continue — it takes a minute and you can finish it here.',
        mfa_enrolment_required_sign_out: 'Sign out instead',
        mfa_enrolment_required_continue: 'Continue to the platform',
        mfa_enrolment_required_blocked: 'Until this is set up, the rest of the platform is unavailable to you.',
        // ── Sprint 63.6.3, gate E12.5 pass 2: the TOTP step-up page for a LIVE session held at MFA_REQUIRED ──
        mfa_step_up_title: 'Enter your verification code',
        mfa_step_up_intro: 'Your organisation asks for a code from your authenticator app before this session can continue. Enter it below to carry on where you were.',
        mfa_step_up_instruction: 'Enter the 6-digit code from your authenticator app, or one of your backup codes.',
        mfa_step_up_locked: 'Too many incorrect codes were entered. Wait a few minutes, then try again.',
        mfa_step_up_not_enrolled: 'This account has no authenticator set up, so there is no code to enter. Sign out and sign in again.',
        mfa_step_up_accepted: 'Code accepted. Continuing.',
        mfa_step_up_code_label: 'Verification code',
        mfa_step_up_placeholder: 'Authenticator code or backup code',
        mfa_step_up_refresh_failed: 'Your code was accepted, but the page could not confirm it with the server. Try again to continue.',
        verify: 'Verifiëren',
        verifying: 'Bezig met verifiëren...',
        forgot_password: 'Wachtwoord vergeten?',
        account_locked: 'Het account is vergrendeld. Probeer het later opnieuw.',
        account_deactivated: 'Het account is gedeactiveerd. Neem contact op met uw beheerder.',
        invalid_mfa: 'Ongeldige MFA-code. Probeer het opnieuw.',
        invalid_credentials: 'Ongeldig e-mailadres of wachtwoord.',
        login_failed: 'Inloggen mislukt',
        session_limit_reached: 'Je account heeft de limiet voor actieve sessies bereikt. Meld je af op een ander apparaat of vraag je beheerder een bestaande sessie in te trekken en probeer het opnieuw.',
        session_expired: 'Uw sessie is verlopen. Herlaad de pagina en probeer het opnieuw.',
        sso_divider: 'or',
        sso_sign_in_with: 'Sign in with',
        sso_checking: 'Checking sign-in options…',
        sso_completing: 'Completing sign-in…',
        sso_failed: 'Single sign-on did not complete. Contact your administrator.',
        sso_required: 'This organisation requires single sign-on. Use the link your administrator gave you.',
        sso_options_label: 'Single sign-on options',
        sso_provider_session_not_ended: 'You are signed out of ComplianceOne, but your identity provider session was not ended. If you share this device, sign out at your identity provider as well.',
    },
    auth: {
        forgot_password: {
            title: 'Wachtwoord vergeten',
            subtitle: 'Voer uw e-mailadres in en wij sturen u een link om uw wachtwoord opnieuw in te stellen.',
            send: 'Herstelkoppeling verzenden',
            sending: 'Verzenden...',
            success: 'Als dat e-mailadres bestaat, is er een link voor het opnieuw instellen van het wachtwoord verzonden.',
            back_to_login: 'Terug naar inloggen',
        },
        reset_password: {
            title: 'Wachtwoord opnieuw instellen',
            subtitle: 'Voer hieronder uw nieuwe wachtwoord in.',
            new_password: 'Nieuw wachtwoord',
            confirm_password: 'Wachtwoord bevestigen',
            reset: 'Wachtwoord opnieuw instellen',
            resetting: 'Opnieuw instellen...',
            success: 'Uw wachtwoord is succesvol opnieuw ingesteld.',
            go_to_login: 'Naar inloggen',
            password_mismatch: 'Wachtwoorden komen niet overeen.',
            invalid_token: 'Ongeldig of ontbrekend hersteltoken. Vraag een nieuwe link voor het opnieuw instellen van het wachtwoord aan.',
            requirements_title: 'Wachtwoordvereisten:',
            password_requirements: 'Het wachtwoord moet aan de volgende vereisten voldoen:',
            rules: {
                min_length: 'Minimaal 12 tekens',
                uppercase: 'Minimaal één hoofdletter',
                lowercase: 'Minimaal één kleine letter',
                digit: 'Minimaal één cijfer',
                special: 'Minimaal één speciaal teken',
            },
        },
        verify_email: {
            title: 'E-mailverificatie',
            verifying: 'Uw e-mailadres wordt geverifieerd...',
            success: 'Uw e-mailadres is succesvol geverifieerd.',
            invalid_token: 'Ongeldig of ontbrekend verificatietoken.',
            failed: 'E-mailverificatie mislukt. Het token is mogelijk ongeldig of verlopen.',
            go_to_login: 'Naar inloggen',
        },
        offline: {
            title: 'U bent offline',
            message: 'Het lijkt erop dat u uw internetverbinding bent verloren. Controleer uw netwerk en probeer het opnieuw.',
        },
    },
    intake: {
        title: 'Verzoek om gegevensrechten',
        subtitle: 'Dien een verzoek om gegevensrechten in bij onze organisatie',
        fields: {
            full_name: 'Volledige naam',
            email: 'E-mailadres',
            phone: 'Telefoonnummer (optioneel)',
            request_type: 'Type verzoek',
            description: 'Beschrijf uw verzoek',
            preferred_language: 'Voorkeurstaal',
            consent: 'Ik geef toestemming voor de verwerking van dit verzoek',
        },
        submit: 'Verzoek indienen',
        validation: {
            name_required: 'Volledige naam is verplicht',
            email_required: 'Een geldig e-mailadres is verplicht',
            type_required: 'Selecteer een type verzoek',
            description_required: 'Beschrijf uw verzoek',
            consent_required: 'U moet toestemming geven voor de verwerking om door te gaan',
        },
        error_title: 'Fout',
        config_load_error: 'Configuratie kon niet worden geladen. Controleer de URL.',
        organization_label: 'Organisatie',
        customer_reference: 'Klantreferentienummer',
        country: 'Land / Rechtsgebied',
        preferred_channel: 'Voorkeurs antwoordkanaal',
        channels: {
            email: 'E-mail',
            postal: 'Post',
            phone: 'Telefoon',
            portal: 'Portaal',
        },
        confirmation: {
            // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
            next_steps_legal_matter: 'Your request will be handled as a legal matter: the organisation will review it and contact you about it. Please keep your reference number.',
            title: 'Verzoek ontvangen',
            message: 'Uw verzoek is succesvol geregistreerd. Uw referentienummer is:',
            next_steps: 'Wij zullen uw verzoek zo spoedig mogelijk verwerken in overeenstemming met de toepasselijke wetgeving. De verwachte verwerkingstijd is 30 werkdagen.',
        },
        // English until Phase 25 (R-57).
        // fix-sprint #431 (owner ruling 2026-10-03) — the acknowledgement emailed
        // to the requester when Settings › Notifications "Send confirmation email to
        // requester" is on. {name}, {organization} and {reference} are filled in.
        acknowledgement_email: {
            subject: 'We received your request (reference {reference})',
            greeting: 'Hello,',
            body: '{organization} has received your request concerning your personal data.',
            reference: 'Your reference is {reference}. Please quote it in any message about this request.',
            closing: 'We will contact you about the next steps. This message was sent automatically.',
        },
        request_type_not_offered: "This kind of request is not one this organisation's installed frameworks provide for. Please choose another request type.",
        // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
        legal_matter_right: 'What do you wish to raise?',
        legal_matter_right_hint: 'This request is received as a legal matter. Choose which of these you wish to raise.',
        legal_matter_right_not_sure: 'Not sure',
        error_summary: 'Your request was not sent. Please correct the following:',
        request_type_not_enabled: 'This form does not accept that request type. Please choose another request type.',
        submit_failed: 'Your request could not be submitted: {reason}',
    },
    request_types: {
        access: 'Inzage (DSAR)',
        deletion: 'Wissing',
        correction: 'Rectificatie / Bijwerking',
        objection: 'Bezwaar',
        portability: 'Gegevensoverdraagbaarheid',
        restriction: 'Beperking van verwerking',
        consent_withdrawal: 'Intrekking van toestemming',
        protective_measures: 'Maatregelen voor bescherming van persoonsgegevens verzoeken',
        processing_information: 'Be informed about the processing of personal data',
        consent_decision: 'Consent or refuse consent to processing',
        complaint_or_compensation: 'Complaint, denunciation, lawsuit or claim for damages',
    },
    statuses: {
        new: 'Nieuw',
        in_review: 'In beoordeling',
        waiting_on_requester: 'Wacht op verzoeker',
        waiting_on_internal_data: 'Wacht op interne gegevens',
        approved: 'Goedgekeurd',
        closed: 'Gesloten',
        rejected: 'Afgewezen',
    },
    admin: {
        nav: {
            rights_requests: 'Verzoeken om rechten',
            templates: 'Sjablonen',
            response_templates: 'Antwoordsjablonen',
            data_mapping: 'Gegevensinventarisatie',
            governance: 'Bestuur',
            consent: 'Toestemming',
            assessments: 'Beoordelingen',
            classification: 'Classificatie',
            monitoring: 'Toezicht',
            incidents: 'Incidenten',
            onboarding: 'Aan boord',
            connectors: 'Connectoren',
            vendor_risk: 'Leveranciersrisico',
            deletion_orchestration: 'Verwijderingsorkestratie',
            discovery: 'Gegevensontdekking',
            incident_ops: 'Incidentoperaties',
            program_governance: 'Programmagovernance',
            access_accountability: 'Toegangsverantwoording',
            consent_properties: 'Toestemmingseigenschappen',
            localization: 'Lokalisatie',
            regulatory: 'Regelgeving',
            partner_workspace: 'Partnerwerkruimte',
            audit_trail: 'Auditlogboek',
            forseti: 'Forseti',
            settings: 'Instellingen',
            help_center: 'Helpcentrum',
            dashboard: 'Overzicht',
            platform: 'Platformoverzicht',
            integrations: 'Integraties',
            cmp_dsar_sync: 'CMP DSAR-synchronisatie',
            cmp_integration: 'CMP-integratie',
            purpose_mapping: 'Doelkoppeling',
            webhook_config: 'Webhookconfiguratie',
            bulk_sync: 'Bulksynchronisatie',
            privacy_scanner: 'Privacyscanner',
            privacy_monitoring: 'Privacymonitoring',
            real_time_scanner: 'Realtimescanner',
            compliance_forms: 'Nalevingsformulieren',
            forms_dashboard: 'Formulierenoverzicht',
            form_templates: 'Sjablonen',
            form_catalog: 'Formulierencatalogus',
            pack_catalog: 'Pakketcatalogus',
            org_groups: 'Organisatiegroepen',
            consolidation: 'Consolidatie',
            organizations: 'Organisaties',
            org_list: 'Alle Organisaties',
            intake_configs: 'Intake-configuraties',
            task_management: 'Taakbeheer',
            unified_audit: 'Gecombineerde Audit',
            evidence_packs: 'Bewijspakketten',
            framework_evidence_packs: 'Framework evidence packs',
            audit_periods: 'Auditperioden',
            regulatory_updates: 'Regelgevingsupdates',
            departments: 'Afdelingen',
        },
        sidebar_category_data_privacy_rights: 'Gegevensprivacy & Rechten',
        sidebar_category_vendor_third_party: 'Leveranciers- & Derdenbeheer',
        sidebar_category_risk_assessment: 'Risico & Beoordeling',
        sidebar_category_operations_automation: 'Operaties & Automatisering',
        sidebar_category_sector_overlays: 'Sector Overlays',
        sidebar_category_task_workflow: 'Taak & Werkstroom',
        sidebar_groups: {
            consent_cmp_webhooks: 'CMP en webhooks',
            // Sprint 62.28.0 — marketing-communications registers.
            marketing_communications: 'Marketing Communications',
            data_mapping_ropa: 'Verwerkingsregister',
            data_mapping_classification: 'Classificatie',
            data_mapping_governance: 'Bestuur',
            access_live_monitoring: 'Realtime monitoring',
            access_reviews_investigations: 'Reviews & Onderzoeken',
            access_evidence_policies: 'Bewijsstukken & Beleid',
            classification_taxonomies: 'Taxonomieën & Labels',
            classification_workflow: 'Werkstroom',
            classification_insights: 'Inzichten',
            connectors_runtime: 'Looptijd',
            connectors_due_diligence: 'Due diligence',
            connectors_coverage: 'Dekking',
            cp_definitions: 'Definities',
            cp_lifecycle: 'Levenscyclus',
            cp_quality: 'Kwaliteit & Bewijs',
            deletion_execution: 'Uitvoering',
            deletion_vendors: 'Leveranciers',
            deletion_evidence: 'Bewijs & Integratie',
            discovery_rules: 'Regels & Sjablonen',
            discovery_scanning: 'Scannen',
            discovery_review: 'Review-werkstroom',
            discovery_insights: 'Inzichten',
            governance_oversight: 'Toezicht',
            governance_reporting: 'Rapportage',
            governance_vendors: 'Leveranciers & Grensoverschrijdend',
            governance_lifecycle: 'Levenscyclus',
            incident_ops_dashboards: 'Dashboards',
            incident_ops_sla: 'SLA-configuratie',
            incident_ops_playbooks: 'Speelboeken',
            incident_ops_vendor_regulator: 'Leverancier & Toezichthouder',
            incident_ops_evidence: 'Bewijs & Review',
            incidents_workload: 'Werklast',
            incidents_regulatory: 'Regelgeving',
            incidents_reporting: 'Rapportage',
            localization_content: 'Inhoud',
            regulatory_frameworks: 'Frameworks',
            localization_regulatory_registers: 'Regulatory Registers',
            localization_penalties_enforcement: 'Penalties & Enforcement',
            localization_sector_overlays: 'Sector Overlays',
            // Sprint 63.43.0 — Sector Overlays (Module 30): management and sector groups.
            sector_overlays_management: 'Overlay Management',
            sector_overlays_banking_payments: 'Banking & Payments',
            sector_overlays_crypto_assets: 'Crypto Assets',
            sector_overlays_manufacturing: 'Manufacturing',
            sector_overlays_food_traceability: 'Food Traceability',
            sector_overlays_transport_logistics: 'Transport & Logistics',
            sector_overlays_public_administration: 'Public Administration',
            sector_overlays_digital_technology: 'Digital Technology',
            sector_overlays_publishing: 'Publishing',
            sector_overlays_education: 'Education',
            sector_overlays_cross_sector: 'Cross-Sector',
            localization_data_exploitation: 'Data Exploitation',
            localization_cybersecurity_assurance_index: 'Cybersecurity Assurance Index',
            localization_food_traceability: 'Food Traceability',
            localization_observability: 'Waarneembaarheid',
            monitoring_metrics: 'Metrieken',
            monitoring_programs: 'Programma’s',
            monitoring_tasks: 'Taken',
            monitoring_controls: 'Beheersmaatregelen',
            org_entities: 'Entiteiten',
            org_config: 'Configuratie',
            org_frameworks: 'Kaders & Rollup',
            partner_overview: 'Overzicht',
            partner_partners: 'Partners',
            partner_delivery: 'Levering',
            partner_support: 'Ondersteuning',
            platform_identity: 'Identiteit & Rollen',
            platform_access_control: 'Toegangsbeheer',
            platform_auth_session: 'Authenticatie & Sessie',
            platform_access_reviews: 'Toegangsreviews',
            platform_files: 'Bestanden',
            platform_notifications: 'Notificaties',
            platform_module_config: 'Moduleconfiguratie',
            platform_ops: 'Operatie & Logs',
            pg_health: 'Programmastatus',
            pg_maturity: 'Volwassenheid',
            pg_audit: 'Audit & Bewijs',
            pg_training: 'Training',
            pg_privacy_office: 'Privacy Office',
            pg_obligations: 'Verplichtingen',
            pg_documents: 'Documenten',
            vr_overview: 'Overzicht',
            vr_onboarding: 'Aan boord',
            vr_assessment: 'Beoordeling',
            vr_oversight: 'Toezicht',
        },
        tickets: {
            title: 'Rights Verzoeken',
            create: 'Aanmaken Verzoek',
            filters: 'Filteropties',
            search_placeholder: 'Search tickets...',
            export_csv: 'Exporteer CSV',
            exporting: 'Exporteren...',
            bulk_export_failed: 'Bulkexport mislukt',
            bulk_export: {
                label: 'Exporteren',
                format: {
                    pdf: 'PDF Samenvatting',
                    zip: 'ZIP (bewijspakketten per zaak)',
                    csv: 'CSV',
                },
                warning_over_50: 'Hiermee worden ongeveer {count} casepacks gegenereerd. Dit kan enkele minuten duren. Doorgaan?',
                blocked_over_500: 'Beperk het filter tot 500 of minder cases voordat ZIP wordt geëxporteerd (huidig: {count}).',
                success: 'Bulkexport opgeslagen in de bestandsbrowser ({count} gevallen)',
                failed: 'Bulkexport mislukt',
                continue: 'Ga door',
                cancel: 'Annuleer',
            },
            no_results: 'Geen requests gevonden',
            ticket_id: 'Ticket-ID',
            request_type: 'Verzoek Type',
            requester: 'Aanvrager',
            source: 'Bron',
            all_sources: 'Alle bronnen',
            assigned_to: 'Toegewezen Naar',
            created: 'Aangemaakt',
            load_error: 'Kan niet load cases. Probeer het opnieuw.',
            case_creation_failed: 'Aanmaken zaak mislukt',
            business_unit: 'Bedrijfseenheid',
            reference: 'Referentie',
            sla: {
                title: 'SLA-status',
                on_track: '{days} dagen resterend',
                approaching: 'Deadline nadert',
                overdue: '{days} dagen te laat',
                ack_overdue: '{hours}u voorbij bevestigingsdeadline',
                ack_warning: 'Bevestiging binnenkort vereist',
                closed: 'Gesloten',
                completed_in: 'Afgerond in {days} dagen',
                source_statutory: 'Statutory period',
                source_tenant_target: 'Your SLA target',
                source_platform_default: 'Platform default, not a legal period',
                awaiting_activation: 'Awaiting activation from {date}',
                no_installed_clock: 'No installed clock applies',
                // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
                handled_in_legal_matters: 'Handled in Legal Matters',
            },
        },
        templates: {
            title: 'Reactie Sjablonen',
            new: 'Nieuw Sjabloon',
            edit: 'Bewerken Sjabloon',
            all_types: 'Alle Types',
            all_locales: 'Alle Locales',
            type_access: 'Toegang/DSAR',
            type_deletion: 'Verwijdering',
            type_correction: 'Correctie',
            type_objection: 'Bezwaar',
            type_portability: 'Overdraagbaarheid',
            type_other: 'Overig',
            type_restriction: 'Beperking',
            type_all: 'All',
            locale_en: 'Engels',
            locale_vi: 'Vietnamees',
            locale_all: 'Beide',
            name: 'Naam',
            request_type: 'Verzoek Type',
            locale: 'Taalinstelling',
            subject: 'Onderwerp',
            body: 'Inhoud',
            name_placeholder: 'Sjabloon naam...',
            subject_placeholder: 'E-mail/brief onderwerp...',
            body_placeholder: 'Sjabloontekst. Gebruik {{requester_name}}, {{ticket_id}}, {{request_type}}, {{org_name}}, {{response_date}} als tijdelijke aanduidingen.',
            available_placeholders: 'Beschikbaar placeholders:',
            create_template: 'Aanmaken Sjabloon',
            preview: 'Voorbeeld',
            close_preview: 'Sluiten Voorbeeld',
            default_badge: 'Standaard',
            no_templates: 'Geen sjablonen gevonden',
            confirm_delete: 'Verwijderen dit sjabloon?',
            load_error: 'Kan niet load sjablonen. Probeer het opnieuw.',
            create_error: 'Kan niet create sjabloon',
            update_error: 'Kan niet update sjabloon',
            delete_error: 'Kan niet delete sjabloon',
            preview_error: 'Kan niet preview sjabloon',
            response_templates_badge: 'Antwoordsjablonen',
            browse_form_templates: 'Bladeren in Compliance Formuliersjablonen',
            templates_loaded: 'Sjablonen geladen',
            sample_organization: 'Voorbeeldorganisatie',
        },
        detail: {
            requester_info: 'Aanvrager Information',
            status: 'Toestand',
            assignment: 'Toewijzing',
            notes: 'Opmerkingen',
            audit_trail: 'Auditspoor',
            add_note: 'Toevoegen Opmerking',
            case_not_found: 'Case not gevonden',
            load_error: 'Kan niet load case. Probeer het opnieuw.',
            partial_load_failed: 'Some information for this case could not be loaded, so parts of this page may be incomplete.',
            partial_load_parts: 'Not loaded: {parts}.',
            note_error: 'Kan niet add note. Probeer het opnieuw.',
            submitted_from: 'Ingediend vanuit',
            via_cmp: 'via AesirX CMP',
            cmp_ticket: 'CMP-ticket:',
            select_template: 'Selecteer een sjabloon',
            direction_inbound: 'CMP → GRC',
            direction_outbound: 'GRC → CMP',
            tabs: {
                details: 'Bijzonderheden',
                evidence: 'Bewijs',
                communications: 'Communicaties',
                escalations: 'Escalaties',
                audit_trail: 'Auditspoor',
                fulfillment: 'Uitvoering',
                response: 'Antwoord',
                extension: 'Verlenging',
            },
            requester: {
                name: 'Naam',
                email: 'E-mail',
                phone: 'Telefoon',
                language: 'Taal',
                source: 'Bron',
                customer_ref: 'Klantreferentie',
                country: 'Land',
                // fix-sprint #431 — the country as received, where it was stored as a code.
                country_as_received: '(received as “{value}”)',
                preferred_channel: 'Voorkeur Channel',
            },
            // fix-sprint #447 W9 (owner ruling OR3) — processor or third party must act
            third_party: {
                label: 'Processor or third party must act',
                hint: 'The installed deadline records may state a different period when a processor or third party must act. Changing this dates the case again from the day it was received, by the record that matches; an approved extension and closed pauses are carried onto the new date.',
                closed: 'The case is closed, so whether a processor or third party must act can no longer be changed.',
                confirm_title: 'Date the case again?',
                confirm_body: 'The case will be dated again from the day it was received, by the installed deadline record that matches. An approved extension and closed pauses are carried onto the new date. The old and the new due date are recorded.',
                confirm_label: 'Change and date again',
                saved: 'Saved. The case shows its old and new due date.',
                redated: 'Changed on {date}: dated again from receipt. Due date was {old}, now {new}.',
                not_redated_no_installed_record: 'Changed on {date}. No installed framework dates this case, so its due date ({due}) is unchanged.',
                not_redated_no_due_date: 'Changed on {date}. The case holds no due date, so nothing was dated again.',
                carried_extension: '{days} approved extension day(s) carried onto the new date.',
                carried_paused: '{amount} paused {unit} carried onto the new date.',
                units: {
                    calendar_days: 'calendar day(s)',
                    working_days: 'working day(s)',
                    hours: 'hour(s)',
                },
                mapping_invalid: 'An installed deadline record for this kind of request cannot be read as a period, so the case could not be dated again and the change was not saved.',
            },
            sla: {
                title: 'SLA',
                on_track: 'Op schema',
                due_soon: 'Binnenkort verschuldigd',
                overdue: 'Te laat',
                days_remaining: 'd resterend',
                due_date: 'Due Datum',
                breached: 'Geschonden',
                paused: 'Gepauzeerd',
                total_paused_days: 'Totaal Gepauzeerd Dagen',
                pause_intervals: 'Pause Intervals',
                pause_intervals_help: 'Each period the clock was stopped, so waiting-on-requester time can be told apart from processing time.',
                pause_intervals_none: 'No pauses recorded on this case.',
                no_due_date: 'No due date recorded',
                stopped_case_closed: 'SLA stopped — case closed',
                pause_interval_open: 'Paused since {from} — still waiting',
                pause_interval_closed: '{from} to {to} — {days} day(s)',
                pause_interval_by: 'Paused by {who}',
                pause_interval_actor_unknown: 'Paused by: not recorded',
                pause_interval_from_status: 'Paused from {status}',
                pause_interval_from_status_unknown: 'Paused from: not recorded',
                pause_interval_reason_unknown: 'Reason: not recorded',
                pause_interval_resumed_by: 'Resumed by {who}',
                processing_days: 'Processing time',
                waiting_days: 'Waiting on requester',
                days_elapsed: 'Dagen Elapsed',
                progress: 'Voortgang',
                prov_heading_ack: 'Acknowledgement deadline',
                prov_heading_fulfil: 'Fulfilment deadline',
                prov_source_statutory: 'Statutory period: {reference}',
                prov_source_statutory_from_activation: 'Statutory period: {reference}, running from {date}, the day it came into force',
                prov_source_tenant_target: 'Your SLA target: {days} days from receipt',
                prov_source_platform_default: 'Platform default, not a legal period: {days} days from receipt',
                prov_source_none: 'Not dated: no installed clock, organisation target or platform default applies',
                prov_due: 'Due {date}',
                prov_awaiting_activation: 'Awaiting activation from {date}: the installed clock {reference} applies from that date, and the case will be dated by it then.',
                prov_no_installed_clock: 'No installed clock applies to this kind of request.',
                // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
                prov_handled_in_legal_matters: 'Handled in Legal Matters: this request is received as a legal matter, which is the record that is worked. No deadline applies to this intake record.',
                prov_open_legal_matter: 'Open the legal matter',
                prov_framework_unnamed: 'an installed framework',
                prov_legal_matter_no_access: 'The legal matter is visible to people who hold the permission to read legal matters.',
                prov_excluded: 'Installed clocks that could not be dated and took no part: {reasons}',
                prov_excluded_activation_date_not_stated: '{reference}: no effective date stated',
                prov_excluded_framework_record_unreadable: '{reference}: framework record could not be read',
                prov_excluded_phase_reference_unresolved: '{reference}: phase start not dated',
            },
            identity: {
                title: 'Identity Verificatie',
                verified: 'Geverifieerd',
                not_verified: 'Niet verified',
                method: 'Methode',
                notes: 'Opmerkingen',
                verified_by: 'Geverifieerd door',
                verified_at: 'Geverifieerd op',
                warning: 'Identiteit nog niet geverifieerd.',
                verify_button: 'Verifiëren Identity',
                select_method: '-- Selecteren --',
                methods: {
                    email_confirmation: 'E-mail Confirmation',
                    id_document: 'Identiteitsdocument',
                    phone_call: 'Telefoon Call',
                    in_person: 'Persoonlijk',
                    other: 'Overig',
                },
            },
            closure: {
                reason: 'Afsluiting Reden',
                summary: 'Afsluiting Samenvatting',
                summary_placeholder: 'Samenvatting van how de request was resolved...',
                reason_required: 'Afsluiting samenvatting en reason are required',
                response_not_sent: 'Er moet een formeel antwoord naar de betrokkene worden gestuurd voordat deze zaak kan worden gesloten',
                reasons: {
                    fulfilled: 'Vervuld',
                    partially_fulfilled: 'Gedeeltelijk vervuld',
                    unfounded: 'Ongegrond',
                    excessive: 'Buitensporig',
                    withdrawn: 'Ingetrokken',
                    duplicate: 'Dupliceren',
                    identity_unverified: 'Identiteit niet geverifieerd',
                },
            },
            // English until Phase 25 (R-57).
            // fix-sprint #431 — moving a case into a waiting status stops its clock,
            // and the server requires the reason (`pause_reason`).
            pause: {
                reason_label: 'Why is the case waiting?',
                reason_placeholder: 'Select a reason',
                reason_required: 'Choose why the case is waiting before moving it to a waiting status.',
                reasons: {
                    awaiting_subject: 'Waiting for the data subject',
                    awaiting_verification: 'Waiting for identity verification',
                    third_party_dependency: 'Waiting for a third party',
                    other: 'Other',
                },
            },
            // fix-sprint #431 — rejecting a case requires a reason code
            // (`rejection_reason_code`).
            rejection: {
                code_label: 'Rejection reason',
                code_placeholder: 'Select a reason',
                code_required: 'Choose a rejection reason before rejecting the case.',
                description_label: 'Rejection details (optional)',
                codes: {
                    identity_not_verified: 'Identity not verified',
                    manifestly_unfounded: 'Manifestly unfounded',
                    repetitive: 'Repeated request',
                    exempt: 'Exempt',
                    out_of_scope: 'Out of scope',
                    duplicate: 'Duplicate request',
                },
            },
            // English until Phase 25 (R-57).
            // fix-sprint #431 — what the cross-border detector found when the case
            // was created (`cross_border_finding`). A record; it decides nothing.
            cross_border_finding: {
                title: 'Cross-border check at creation',
                result: 'Finding',
                evaluated_at: 'Checked at',
                triggered: 'The rule matched: the data subject’s country ({subject}) differs from the organisation’s ({org}).',
                reasons: {
                    request_type_not_disclosing: 'Not checked further: this request type discloses no data.',
                    subject_country_unknown: 'Not matched: the data subject’s country is not recorded.',
                    same_country: 'Not matched: the data subject is in the organisation’s country ({org}).',
                    // fix-sprint #431 — the organisation records no country: never assumed.
                    org_country_not_recorded: 'Not evaluated: the organisation’s country is not recorded.',
                    // fix-sprint #431 — a recorded country that reads as no code is not compared.
                    subject_country_not_recognised: 'Not evaluated: the data subject’s country, as recorded, is not a country code or name the platform recognises.',
                },
                other_reason: 'Not matched ({reason}).',
                org_country_pointer: 'The cross-border check compares the data subject’s country with the organisation’s. The organisation’s country is recorded on its record: Organizations › All Organizations, then the organisation’s Country field.',
                org_country_link: 'Record the organisation’s country',
                not_evaluated: 'Could not be evaluated when the case was created.',
                not_recorded: 'Not recorded: this case was created before the check ran on creation.',
                decides_nothing: 'This records what the rule found. It changes no status, deadline or assignment, and opens no cross-border dossier.',
                // fix-sprint #431 (owner ruling 2026-10-03) — viewing a case opens
                // nothing; a person opens the cross-border dossier with the button.
                current: 'Assessment now',
                dossier: 'Cross-border dossier',
                dossier_reading: 'Reading…',
                dossier_unreadable: 'Whether a dossier is open for this case could not be read.',
                no_dossier: 'No cross-border dossier has been opened for this case.',
                dossier_opened: 'Dossier {dossier}, opened {at}.',
                filing: 'Statutory filing',
                filing_no_form: 'None opened: no installed framework declares a filing form for this transfer.',
                filing_not_opened: 'None opened: the filing could not be created when the dossier was opened.',
                filing_not_recorded: 'None recorded.',
                open_button: 'Open cross-border dossier',
                open_hint: 'Opening the dossier is your action, recorded in the case audit log under your name. Where an installed framework declares a filing form for this transfer, the statutory filing is opened with it.',
                opening: 'Opening…',
                confirm_title: 'Open cross-border dossier?',
                confirm_message: 'This opens a cross-border dossier for case {case} and, where an installed framework declares a filing form for this transfer, a statutory filing. It is recorded under your name.',
                confirm_label: 'Open dossier',
                opened_toast: 'Cross-border dossier {dossier} opened.',
                already_open_toast: 'A cross-border dossier was already open for this case: {dossier}.',
                open_error: 'The cross-border dossier could not be opened.',
                // fix-sprint #431 (gate E12.5 remediation 1) — why opening the dossier was refused.
                not_detected: 'No cross-border transfer is detected for this case now, so no dossier was opened.',
                case_not_found: 'This case could not be found.',
                // gate E12.5 remediation 1 — retrying a filing that could not be opened; the subject country compared and its source.
                filing_opening: 'Being opened now.',
                retry_filing_button: 'Retry the statutory filing',
                retry_filing_hint: 'The statutory filing could not be opened with this dossier. Retrying it is your action, recorded in the case audit log under your name.',
                retry_filing_failed: 'The statutory filing still could not be opened.',
                filing_retried_toast: 'Statutory filing {filing} opened.',
                // Gate E12.5 remediation 2 — a stopped opening, and what a retry answered.
                filing_opening_stalled: 'None opened: the request that was opening it stopped before it finished.',
                retry_filing_in_progress: 'Another request is opening the statutory filing now, so this retry started nothing.',
                retry_filing_no_form: 'No statutory filing was opened: no installed framework declares a filing form for this transfer now.',
                // Gate E12.5 pass 3 — a filing retry that could not be attempted, and why.
                retry_filing_unavailable_service: 'This retry started nothing: statutory filings cannot be opened on this installation.',
                retry_filing_unavailable_org_country: 'This retry started nothing: the dossier records no organisation country, which the statutory filing needs.',
                confirm_retry_title: 'Retry the statutory filing?',
                confirm_retry_message: 'This tries again to open the statutory filing for dossier {dossier} of case {case}. It is recorded under your name.',
                confirm_retry_label: 'Retry filing',
                subject_compared: 'Data subject’s country compared',
                subject_compared_value: '{country}, from {source}',
                subject_sources: {
                    residence_country: 'the country of residence recorded with the request',
                    case_country: 'the case’s Country field',
                    address_country: 'the country in the data subject’s address',
                },
            },
            export_title: 'Exporteren',
            export_pdf: 'Exporteren PDF',
            export_zip: 'Exporteren ZIP',
            case_file_pdf: 'Exporteer PDF (zaakbestand)',
            evidence_pack_zip: 'ZIP exporteren (bewijspakket)',
            cross_border_warning: 'This request may involve a cross-border transfer. Review whether your transfer impact assessment covers it.',
            breach: {
                flag_btn: 'Markeer als inbreukveroorzakend',
                modal: {
                    title: 'Markeer de zaak als een inbreuk veroorzakend',
                    type_label: 'Type inbreuk',
                    type: {
                        misdirected_response: 'Verkeerd gerichte reactie',
                        unauthorised_disclosure: 'Ongeautoriseerde openbaarmaking',
                        identity_mismatch: 'Identiteitsmismatch',
                        other: 'Anders',
                    },
                    description: 'Beschrijving (optioneel)',
                    description_placeholder: 'Wat is er gebeurd? (max. 1000 tekens)',
                    confirm: 'Markeer en start de meldingsklok',
                    cancel: 'Annuleer',
                    flagging: 'Markeren...',
                },
                banner: {
                    title: 'Schending gemarkeerd: {type}',
                    detected_at: 'Gedetecteerd op {timestamp}',
                    filing_deadline: 'Meldingstermijn: over {hours} uur',
                    overdue: 'Meldingstermijn: VERSTREKEN',
                },
                toasts: {
                    flagged: 'Zaak gemarkeerd als oorzaak van inbreuk - meldingsklok gestart',
                    flag_failed: 'Kan de zaak niet markeren als een inbreuk veroorzakend',
                },
            },
            export: {
                generating: 'Bezig met genereren... even geduld a.u.b',
                success: 'Exporteer opgeslagen naar Bestandenbrowser',
                failed: 'Exporteren mislukt - controleer auditlogboek',
            },
            no_notes: 'No opmerkingen yet',
            assign_placeholder: 'Gebruiker ID naar assign...',
            reassign: 'Opnieuw toewijzen',
            evidence: {
                checklist_title: 'Bewijs Checklist',
                complete_pct: '% voltooid',
                no_checklist: 'Geen checklist-items',
                attachments: 'Bijlagen',
                upload_attachment: 'Bijlage uploaden',
                upload_button: 'Uploaden',
                uploading: 'Uploaden...',
                attachment_uploaded: 'Bijlage succesvol geüpload',
                upload_error: 'Upload mislukt',
                category: 'Categorie',
                categories: {
                    identity_proof: 'Identiteitsbewijs',
                    correspondence: 'Correspondentie',
                    internal_data: 'Interne gegevens',
                    legal: 'Juridisch',
                    other: 'Overig',
                },
                no_attachments: 'Geen bijlagen',
                references_title: 'Bewijs References',
                no_references: 'Geen referenties',
                add_reference: 'Toevoegen Referentie',
                ref_type: 'Soort',
                ref_value: 'Waarde',
                ref_desc: 'Beschrijving',
                ref_value_placeholder: 'URL, e-mail ID, of reference...',
                ref_desc_placeholder: 'Beschrijving...',
                ref_types: {
                    url: 'URL',
                    email_id: 'E-mail ID',
                    crm_record: 'CRM-record',
                    ticket_ref: 'Ticket Referentie',
                    other: 'Overig',
                },
            },
            communications: {
                title: 'Communicaties',
                add: 'Toevoegen Message',
                no_records: 'Geen communicatie vastgelegd',
                direction: 'Richting',
                channel: 'Kanaal',
                visibility: 'Zichtbaarheid',
                subject: 'Onderwerp',
                body: 'Inhoud',
                send: 'Verzenden',
                inbound: 'Inkomend',
                outbound: 'Uitgaand',
                internal: 'Intern',
                external: 'Extern',
                tab_all: 'All',
                tab_internal: 'Intern',
                tab_external: 'Extern',
                subject_placeholder: 'Onderwerp (optional)',
                body_placeholder: 'Berichttekst...',
                channels: {
                    email: 'E-mail',
                    portal: 'Portaal',
                    phone: 'Telefoon',
                    postal: 'Post',
                    in_person: 'Persoonlijk',
                },
                apply_template: 'Sjabloon Toepassen',
                select_template: 'Selecteer een sjabloon...',
                no_templates: 'Geen sjablonen beschikbaar',
                templates_load_error: 'Templates could not be loaded',
                template_applied: 'Sjabloon toegepast op antwoord.',
                template_apply_error: 'Kan sjabloon niet toepassen.',
            },
            escalations: {
                title: 'Escalatie Geschiedenis',
                no_events: 'No escalatie gebeurtenissen',
                tier: 'Niveau',
                threshold: 'Drempelwaarde',
                action: 'Actie',
                target: 'Doel',
                triggered_at: 'Geactiveerd At',
                // fix-sprint #431 (owner ruling 2026-10-03) — what a tier did. English until
                // Phase 25 (R-57).
                outcome: 'Outcome',
                outcome_acted: 'Acted',
                outcome_baselined: 'Passed before automatic escalation was on',
                // fix-sprint #431 — a tier recorded because a ladder edit added, switched on or lowered it.
                outcome_baselined_ladder_edit: 'Passed before this tier was added, switched on or lowered',
                outcome_baselined_detail: 'No one was notified and the case was not reassigned.',
            },
            cmp_sync: {
                title: 'AesirX CMP Synchronisatie',
                sync_status: 'Synchronisatie Status',
                last_synced: 'Laatste Synced',
                not_synced: 'Niet Synced',
                resync: 'Nu opnieuw synchroniseren',
                syncing: 'Synchroniseren...',
                sync_history: 'Synchronisatie Geschiedenis (Last 10)',
                direction: 'Richting',
                time: 'Tijd',
                retries: 'Pogingen',
                error_col: 'Fout',
            },
            audit: {
                filter_event_placeholder: 'Filteren op gebeurtenis type...',
                filter_actor_placeholder: 'Filteren op actor...',
            },
            fulfillment: {
                title: 'Afdelingsuitvoering',
                assign_departments: 'Afdelingen toewijzen',
                department: 'Afdeling',
                assignee: 'Toegewezen aan',
                deadline: 'Deadline',
                evidence_count: 'Bewijsmateriaal',
                signoff: 'Aftekening',
                signed_off: 'Afgetekend',
                pending_signoff: 'In afwachting',
                start_button: 'Starten',
                complete_button: 'Markeer als voltooid',
                signoff_button: 'Afmelden',
                no_tasks: 'Nog geen uitvoeringstaken toegewezen.',
                tasks_table: 'Uitvoeringstaken',
                progress_label: 'Uitvoeringsvoortgang',
                overdue: 'te laat',
                assigned_success: 'Afdelingsuitvoeringstaak succesvol toegewezen.',
                assign_error: 'Uitvoeringstaak kon niet worden toegewezen.',
                signoff_success: 'Afdelingsaftekening geregistreerd.',
                signoff_error: 'Aftekening kon niet worden geregistreerd.',
                identity_required: 'Identity verification required before fulfillment actions.',
                remove_task: 'Remove Task',
                remove_success: 'Fulfillment task removed successfully.',
                add_evidence_btn: 'Bewijs toevoegen',
                evidence_uploaded: 'Bewijs geüpload en gekoppeld',
                evidence_upload_error: 'Upload bewijs mislukt',
                suggest_data_stores: 'Gegevensopslag voorstellen',
                suggest_error: 'Gegevensopslagvoorstellen konden niet worden geladen.',
                suggest_stores_title: 'Voorgestelde gegevensarchieven',
                suggest_stores_none: 'Er zijn geen datastores die overeenkomen met dit geval. Configureer systemen in Data Mapping om suggesties te verbeteren.',
                suggest_stores_add: 'Toevoegen aan casus',
                suggest_stores_added: 'Gegevensopslag toegevoegd aan case',
                no_departments_title: 'Geen afdelingen geconfigureerd',
                no_departments_body: 'Met afdelingen kunt u DSR-uitvoeringstaken routeren naar een functie (Privacy, Legal, HR, IT Ops). Installeer voorbeeldafdelingen of vraag uw beheerder om afdelingen te configureren in de Organisatie-instellingen.',
                no_departments_cta: 'Installeer voorbeeldgegevens',
                department_placeholder: 'Afdelingsnaam...',
                assignee_placeholder: 'Gebruikers-ID om toe te wijzen...',
                assign_button: 'Toewijzen',
                status_pending: 'In behandeling',
                status_in_progress: 'In uitvoering',
                status_completed: 'Voltooid',
                status_verified: 'Geverifieerd',
                confirm_approve: 'Weet u zeker dat u deze verlenging wilt goedkeuren?',
                confirm_deny: 'Weet u zeker dat u deze verlenging wilt weigeren?',
            },
            response: {
                title: 'Antwoordgeneratie',
                select_template: 'Antwoordsjabloon',
                choose_template: 'Selecteer een sjabloon...',
                generate: 'Antwoord genereren',
                preview: 'Antwoordvoorbeeld',
                send: 'Antwoord verzenden',
                delivery_method: {
                    label: 'Bezorgmethode',
                    email: 'E-mail',
                    portal: 'Portaal',
                    postal: 'Postpost',
                    in_person: 'Persoonlijk',
                },
                recipient: {
                    email: 'E-mailadres van ontvanger',
                    portal: 'Portalgebruikers-ID',
                    address: 'Postadres',
                },
                auto_generate_btn: 'Reactie genereren en verzenden',
                auto_generate_success: 'Reactie gegenereerd en afgeleverd',
                auto_generate_missing_recipient: 'Geef een ontvanger op voordat u verzendt.',
                auto_fields: 'Automatisch ingevulde velden',
                case_id: 'Zaak-ID',
                requester_name: 'Naam aanvrager',
                rights_type: 'Rechtstype',
                history: 'Verzonden antwoorden',
                generated_at: 'Gegenereerd',
                confirmation: 'Bevestiging',
                sent_label: 'Verzonden',
                draft_label: 'Concept',
                generated_success: 'Antwoord succesvol gegenereerd.',
                generate_error: 'Antwoord kon niet worden gegenereerd.',
                sent_success: 'Antwoord succesvol verzonden.',
                send_error: 'Antwoord kon niet worden verzonden.',
                load_error: 'Antwoordsjablonen konden niet worden geladen.',
                pdf_title: 'Antwoordbrief aan betrokkene',
                download_pdf: 'PDF downloaden',
                download_all: 'Alles downloaden',
                download_error: 'Antwoord kon niet worden gedownload',
            },
            extension: {
                title: 'Deadlineverlengingen',
                acknowledgment_deadline: 'Bevestigingsdeadline',
                fulfillment_deadline: 'Uitvoeringsdeadline',
                request_extension: 'Verlenging aanvragen',
                reason: 'Reden',
                reason_placeholder: 'Reden voor het aanvragen van extra tijd...',
                additional_days: 'Extra dagen',
                submit_request: 'Verlengingsverzoek indienen',
                history: 'Verlengingsgeschiedenis',
                requested_at: 'Aangevraagd',
                approve: 'Goedkeuren',
                deny: 'Afwijzen',
                request_success: 'Verlengingsverzoek ingediend.',
                request_error: 'Verlengingsverzoek kon niet worden ingediend.',
                approved_success: 'Verlenging goedgekeurd.',
                denied_success: 'Verlenging afgewezen.',
                decision_error: 'Verlengingsbeslissing kon niet worden verwerkt.',
                status_pending: 'In behandeling',
                status_approved: 'Goedgekeurd',
                status_denied: 'Afgewezen',
                confirm_approve: 'Weet u zeker dat u dit verlengingsverzoek wilt goedkeuren?',
                confirm_deny: 'Weet u zeker dat u dit verlengingsverzoek wilt weigeren?',
                decided_by: 'Besloten door',
            },
            // fix-sprint #431 (gate E12.5 remediation 1) — the banner’s link to the check; why a status change was refused.
            cross_border_warning_link: 'See the cross-border check',
            status_errors: {
                invalid_transition: 'The case cannot move to this status from its current status.',
                invalid_sub_status: 'The chosen sub-status does not apply to this status.',
                assignment_required: 'Assign the case before moving it to In Review.',
                identity_verification_required: 'Verify the requester’s identity before fulfilling the request.',
                closure_delivery_required: 'Record how and when the response was delivered before closing the case.',
                invalid_value: 'The change was not saved: a value is missing or not accepted.',
            },
            evidence_unlink_read_failed: 'The attachment was deleted, but the fulfilment tasks could not be read, so it may still be listed as evidence on a task.',
            partial_load_part_names: {
                advisor_summary: 'advisor summary',
                sla_status: 'SLA status',
                attachments: 'attachments',
                fulfilment_tasks: 'fulfilment tasks',
                checklist: 'evidence checklist',
                references: 'references',
                escalations: 'escalations',
                cmp_sync_history: 'CMP sync history',
                flagged_breaches: 'flagged breaches',
                audit_trail: 'audit trail',
                communications: 'communications',
            },
            partial_load_reload: 'Reload the missing parts',
            request_type_not_granted: 'This kind of request is not a right under the frameworks this organisation has installed. The request is kept and can still be worked.',
        },
        audit: {
            export_csv: 'Exporteren CSV',
            exporting: 'Exporteren...',
            search_placeholder: 'Zoeken audit details...',
            filter_by_actor: 'Filteren op actor...',
            all_event_types: 'Alle Gebeurtenis Types',
            seq: '#',
            timestamp: 'Tijdstempel',
            event: 'Gebeurtenis',
            case_col: 'Zaak',
            actor: 'Betrokkene',
            summary: 'Samenvatting',
            no_events: 'Geen audit gebeurtenissen gevonden',
            export_success: 'Audittrail succesvol geëxporteerd.',
            export_error: 'Kan niet export audit CSV',
            load_error: 'Kan niet load audit trail. Probeer het opnieuw.',
            announce_loaded: 'Auditspoor geladen',
            start_date: 'Startdatum',
            end_date: 'Einddatum',
            per_page: '{count} / pagina',
            unknown: 'Onbekend',
            source_filter: {
                label: 'Bron',
                all: 'Allemaal',
                module: 'Module',
                forseti: 'Forseti',
                aria_label: 'Filter auditgebeurtenissen op bron',
            },
            event_types: {
                case_created: 'Case Aangemaakt',
                status_changed: 'Status Gewijzigd',
                case_assigned: 'Case Toegewezen',
                note_added: 'Opmerking Added',
                attachment_added: 'Bijlage Added',
                attachment_deleted: 'Bijlage Verwijderd',
                reference_added: 'Referentie Added',
                checklist_updated: 'Checklist Bijgewerkt',
                identity_verified: 'Identiteit geverifieerd',
                closure_summary_added: 'Afsluiting Samenvatting',
                approval_granted: 'Goedkeuring Granted',
                template_applied: 'Sjabloon Applied',
                export_generated: 'Exporteren Gegenereerd',
                sla_warning: 'SLA Waarschuwing',
                sla_overdue: 'SLA Te laat',
                sla_flag_revisited: 'SLA flag revisited',
                third_party_involvement_changed: 'Processor or third-party involvement changed',
                aml_diligence_linked: 'AML due diligence record linked',
                aml_diligence_confirmed: 'AML due diligence record confirmed',
                aml_scoping_recorded: 'AML reporting-entity scoping recorded',
                aml_scoping_deactivation_refused: 'AML programme-inactive role refused',
                aml_two_person_policy_changed: 'AML two-person rule changed',
                aml_retention_scope_recorded: 'AML records retention scope recorded',
                aml_retention_scope_updated: 'AML records retention scope updated',
                aml_indicator_row_updated: 'AML indicator coverage row updated',
                aml_indicator_entity_row_recorded: 'AML organisation-defined indicator recorded',
                aml_indicator_config_approved: 'AML indicator configuration approved',
                aml_case_opened: 'Suspicious-activity case opened',
                aml_case_updated: 'Suspicious-activity case edited',
                aml_case_viewed: 'Suspicious-activity case read',
                aml_case_listed: 'Suspicious-activity case list read',
                aml_case_read_denied: 'Suspicious-activity case read refused',
                aml_case_determined: 'Suspicious-activity case determination recorded',
                aml_case_approved: 'Suspicious-activity case determination approved',
                aml_case_self_approval_refused: 'Suspicious-activity case approval by the determiner refused',
                aml_case_approval_reverted: 'Suspicious-activity case approval taken back',
                aml_case_filing_recorded: 'Suspicious-activity case report reference recorded',
                aml_case_closed: 'Suspicious-activity case closed',
                aml_case_cdd_refresh_requested: 'Due diligence refresh asked for from a suspicious-activity case',
                aml_diligence_refresh_requested: 'AML due diligence refresh asked for',
                aml_beneficial_owner_recorded: 'Beneficial owners recorded',
                aml_beneficial_owner_updated: 'Beneficial owners updated',
                aml_launch_recorded: 'AML launch record created',
                aml_launch_scope_determined: 'AML launch scope determination recorded',
                aml_launch_assessment_recorded: 'AML pre-launch risk assessment recorded',
                aml_launch_assessment_decided: 'AML pre-launch risk assessment decision recorded',
                aml_launch_approved: 'AML launch approved',
                aml_launch_approval_refused: 'AML launch approval refused',
                aml_inspection_pack_exported: 'AML inspection pack exported',
                aml_indicator_config_self_approval_refused: 'AML indicator configuration approval by an editor of the draft refused',
                aml_indicator_review_task_raised: 'AML indicator review task raised from a launch assessment',
                aml_indicator_review_task_completed: 'AML indicator review recorded',
                aml_retention_scope_approved: 'AML records retention scope signed',
                aml_retention_scope_self_approval_refused: 'Second signature of an AML records retention scope entry by the same person refused',
                aml_case_related_action_recorded: 'Suspicious-activity case related action recorded',
                aml_case_retention_start_recorded: 'Suspicious-activity case retention start recorded',
                aml_launch_self_approval_refused: 'AML launch approval by the assessment decider refused',
                aml_launch_post_launch_review_set: 'AML launch post-launch review date recorded',
                aml_case_reads_retention_swept: 'Suspicious-activity case read log retention sweep',
                org_profile_statement_recorded: 'Organization profile statement recorded',
                dossier_authority_communication_recorded: 'Authority communication recorded on a filing',
                dossier_authority_communication_exported: 'Authority communications of a filing exported',
                framework_summary_exported: 'Framework summary exported',
                framework_briefing_recorded: 'Briefing or consultation comment recorded on a framework',
                framework_briefing_approved: 'Briefing on a framework approved',
                dossier_receipt_evidence_recorded: 'Receipt of a dossier recorded on a filing',
                dossier_handling_statement_recorded: 'Handling authority recorded on a filing',
                dossier_result_recorded: 'Authority’s result recorded on a filing',
                dossier_result_supplement_request_recorded: 'Supplement request after a result recorded on a filing',
                dossier_failure_follow_up_recorded: 'Follow-up to a result recorded on a filing',
                dossier_authority_stated_result_date_recorded: 'Result date stated by the authority recorded on a filing',
                dossier_completion_request_recorded: 'Completion request recorded on a filing',
                dossier_completion_submitted: 'Completion submitted on a filing',
                dossier_update_filing_created: 'Update filing created for a filing',
                dossier_update_addressee_recorded: 'Recipient and competent authority recorded on an update filing',
                regulatory_update_comparison_reviewed: 'Comparison review recorded on a regulatory update',
                access_statement_recorded: 'Access statement recorded on a processing activity',
            },
            unified_audit: {
                title: 'Gecombineerd Auditspoor',
                module_filter: 'Module',
                framework_filter: 'Raamwerk',
                select_framework: 'Selecteer een raamwerk...',
                entity_type_filter: 'Entiteitstype',
                action_filter: 'Actie',
                actor_filter: 'Uitvoerder',
                select_actor: 'Selecteer een uitvoerder...',
                search_filter: 'Zoeken',
                search_placeholder: 'Zoek in auditgebeurtenissen...',
                filters_heading: 'Filters',
                date_range: 'Datumbereik',
                no_events_found: 'Geen auditgebeurtenissen gevonden.',
                event_count: 'Aantal gebeurtenissen',
                action: 'Actie',
                actor: 'Uitvoerder',
                date_range_start: 'Begindatum bereik',
                date_range_end: 'Einddatum bereik',
                filtering_events: 'Auditgebeurtenissen filteren',
                framework_codes: 'Raamwerkcodes',
                entity_type_col: 'Entiteitstype',
                aria_table: 'Gecombineerde auditgebeurtenissen',
                page_of: 'Pagina {page} van {total}',
                entity_types: {
                    user: 'Gebruiker',
                    case: 'Geval',
                    incident: 'Voorval',
                    assessment: 'Beoordeling',
                    vendor: 'Verkoper',
                    consent: 'Toestemming',
                    policy: 'Beleid',
                    data_map: 'Gegevenskaart',
                    approval: 'Approval',
                    consent_record: 'Consent record',
                    data_flow: 'Data flow',
                    org_profile_statement: 'Organization profile statement',
                    dpia: 'DPIA',
                    dpia_mitigation: 'DPIA mitigation',
                    evidence_item: 'Evidence item',
                    processing_activity: 'Processing activity',
                    transfer_mechanism: 'Transfer mechanism',
                    consent_policy: 'Consent policy',
                    data_category: 'Data category',
                    drift_event: 'Drift event',
                    maturity_model: 'Maturity model',
                    program: 'Monitoring program',
                    purpose: 'Processing purpose',
                    review_record: 'Review record',
                    risk_score: 'Risk score',
                    system: 'System',
                    taxonomy: 'Taxonomy',
                },
                actions: {
                    form_dossier_statement_changed: 'Dossier statement changed',
                    create: 'Creëer',
                    update: 'Bijwerken',
                    delete: 'Verwijderen',
                    view: 'Bekijk',
                    export: 'Exporteren',
                    status_change: 'Statuswijziging',
                    // Sprint 63.25.0, gate E12.5 remediation 1 (P1-1) — the verbs producers write.
                    created: 'Created',
                    updated: 'Updated',
                    deleted: 'Deleted',
                    viewed: 'Viewed',
                    exported: 'Exported',
                    approved: 'Approved',
                    rejected: 'Rejected',
                    assigned: 'Assigned',
                    completed: 'Completed',
                    closed: 'Closed',
                    status_changed: 'Status changed',
                    submitted_for_review: 'Submitted for review',
                    risk_added: 'Risk added',
                    mitigation_added: 'Mitigation added',
                    evidence_linked: 'Evidence linked',
                    scored: 'Scored',
                    reviewed: 'Reviewed',
                    activated: 'Activated',
                    period_started: 'Period started',
                    version_created: 'Version created',
                    evaluated: 'Evaluated',
                    triage_completed: 'Triage completed',
                    impact_assessed: 'Impact assessed',
                    breach_assessed: 'Breach assessed',
                    case_created: 'Case created',
                    assignment_changed: 'Assignment changed',
                    case_closed: 'Case closed',
                    identity_verified: 'Identity verified',
                    maturity_model_created: 'Maturity model created',
                    maturity_model_activated: 'Maturity model activated',
                    maturity_model_evaluated: 'Maturity model evaluated',
                    maturity_model_approved: 'Maturity model approved',
                    response_generated: 'Response generated',
                    response_delivered: 'Response delivered',
                    assembled: 'Assembled',
                    reassembled: 'Reassembled',
                    verified: 'Verified',
                    returned: 'Returned',
                    recorded_as_submitted: 'Recorded as submitted',
                    audit_evidence_pack_assembled: 'Evidence pack assembled',
                    audit_evidence_pack_reassembled: 'Evidence pack reassembled',
                    audit_evidence_pack_partial: 'Evidence pack partial',
                    audit_evidence_pack_drift_checked: 'Evidence pack sources checked for changes',
                    audit_evidence_pack_verified: 'Evidence pack verified',
                    audit_evidence_pack_returned: 'Evidence pack returned',
                    audit_evidence_pack_recorded_as_submitted: 'Evidence pack recorded as submitted',
                    audit_evidence_pack_transition_refused: 'Evidence pack status change refused',
                    audit_evidence_pack_exported: 'Evidence pack exported',
                    // Sprint 63.37.0 — the form-instance events the sample packages write.
                    instance_created: 'Form created',
                    instance_updated: 'Form updated',
                    consent_captured: 'Consent captured',
                    revoked: 'Revoked',
                    mitigation_status_changed: 'Mitigation status changed',
                    ai_advisor_record_summary_fetched: 'Forseti read the record summary',
                    regulatory_guidance_installed: 'Regulatory guidance installed',
                    regulatory_guidance_version_bumped: 'Regulatory guidance moved to a new version',
                    unknown: 'Action not recorded',
                },
                additional_filters: 'Additional audit filters',
                sample_marker: 'Sample',
                sample_marker_label: 'Sample data',
                sample_package_label: 'Sample data from the industry package {package}',
                actor_system: 'The platform',
                entity_none: 'No record type',
                summary_composed: '{action} · {entity}',
                // Sprint 63.29.0 (#585, AC3/AC6) — the server export and the integrity check.
                export_title: 'Unified Audit Trail export',
                export_generated: 'Generated: {date}',
                export_filters: 'Filters: {filters}',
                export_no_filters: 'Filters: none',
                export_count: 'Events: {count}',
                export_source_unreadable: 'Source {source} ({collection}): could not be read; its events are not in this file.',
                list_sources_unreadable: 'These audit sources could not be read, so their events are neither listed nor counted: {sources}.',
                list_page_beyond_window: 'Pages beyond the first {max} events cannot be listed. Narrow the filters, or export the filtered set.',
                list_page_beyond_window_no_export: 'Pages beyond the first {max} events cannot be listed. Narrow the filters.',
                list_window_note: 'Only the first {max} of {total} events can be paged through here. To see the rest, narrow the filters or export the filtered set.',
                list_window_note_no_export: 'Only the first {max} of {total} events can be paged through here. To see the rest, narrow the filters.',
                export_col_entity_id: 'Record ID',
                export_col_actor_id: 'Actor ID',
                export_menu_label: 'Export',
                integrity: {
                    column: 'Integrity',
                    check: 'Check integrity',
                    check_aria: 'Check the integrity of the audit events of record {id}',
                    checking: 'Checking…',
                    heading: 'Integrity check: {type}',
                    heading_untyped: 'Integrity check',
                    record_ref: 'Record ID: {id}',
                    case_ref: 'Case ID: {case}',
                    chain_verified: 'Hash chain verified, {verified} of {events} events.',
                    chain_broken: '{verified} of {events} events verified before the break.',
                    chain_legacy_unverifiable: '{count} events were written before the current link rule; their content matches the stored checksum, but their order in the chain cannot be verified.',
                    no_chain: 'No hash chain names this record.',
                    break_checksum: 'Break found in case {case} at sequence {sequence}: the stored checksum does not match the one recomputed from the event before it.',
                    break_gap: 'Break found in case {case}: sequence {sequence} is missing from the chain.',
                    break_withheld: 'A break was found in the chain of case {case} at an event you are not permitted to read, so its position is not shown. The chain cannot be relied on; a holder of the restricted grant can see where it breaks.',
                    no_break: 'No break found.',
                    not_chained: '{source}: {events} events. Not chained; consistency checked only.',
                    not_chained_inconsistent: '{source}: {events} events. Not chained; consistency checked only. {inconsistent} carry an unreadable or future date (first: {at}).',
                    proofs_link: 'Proofs and anchors are verified in the Integrity Center.',
                    failed: 'The integrity check could not be run.',
                    close: 'Close the integrity result',
                    // Gate E12.5 pass 1 (P2-3) — a source the check could not read.
                    unreadable: 'The check could not read {sources}; the result does not cover the events held there.',
                },
            },
            evidence_packs: {
                title: 'Audit Bewijspakketten',
                assemble_pack: 'Bewijspakket samenstellen',
                framework: 'Raamwerk',
                select_framework: 'Selecteer een raamwerk...',
                framework_code_label: 'Raamwerkcode',
                entity_types: 'Entiteitstypen',
                select_entity_types: 'Selecteer entiteitstypen...',
                period: 'Periode',
                period_start: 'Begin periode',
                period_end: 'Einde periode',
                completeness: 'Volledigheid',
                no_packs_found: 'Geen bewijspakketten gevonden.',
                verify: 'Verifiëren',
                export_pack: 'Exporteren',
                exporting: 'Exporteren...',
                pack_assembled: 'Bewijspakket samengesteld',
                pack_exported: 'Bewijspakket geëxporteerd',
                aria_table: 'Audit-bewijspakketten',
                aria_export_pack: 'Pakket exporteren',
                export_format: 'Exportformaat',
                export_in_progress: 'Export bezig...',
                status_draft: 'Concept',
                status_assembled: 'Samengesteld',
                status_verified: 'Geverifieerd',
                status_submitted: 'Ingediend',
                entity_type_labels: {
                    user: 'Gebruiker',
                    case: 'Geval',
                    incident: 'Voorval',
                    assessment: 'Beoordeling',
                    vendor: 'Verkoper',
                    consent: 'Toestemming',
                    policy: 'Beleid',
                    data_map: 'Gegevenskaart',
                },
                template_id: 'Sjabloon-ID',
                csv_headers: {
                    // Sprint 63.29.0 (#542) — the actor's name from the user directory, beside the id.
                    actor_name: 'Actor Name',
                    module: 'Module',
                    action: 'Actie',
                    actor_id: 'Acteur',
                    actor_role: 'Actor Role',
                    entity_type: 'Entiteitstype',
                    entity_id: 'Entiteits-ID',
                    summary: 'Samenvatting',
                    timestamp: 'Tijdstempel',
                    record_type: 'Record Type',
                    framework_code: 'Kader',
                },
                // Sprint 63.25.0 — versioned packs: statuses, record types and the export header (spec G1, G2).
                status_partial: 'Partial',
                status_returned: 'Returned',
                status_recorded_as_submitted: 'Recorded as submitted',
                status_legacy_unversioned: 'Assembled before versioning',
                record_type_labels: {
                    consent: 'Consent',
                    data_map: 'Data Map',
                    assessment: 'Assessment',
                    vendor: 'Vendor',
                    incident: 'Incident',
                    case: 'Case',
                    policy: 'Policy',
                    deletion: 'Deletion',
                    monitoring: 'Monitoring',
                    forms: 'Forms',
                    task: 'Task',
                    access: 'Access',
                    user: 'User',
                    platform: 'Platform',
                    evidence_pack_lifecycle: 'Evidence pack lifecycle',
                },
                framework_none: 'no framework filter',
                export_header_pack: 'Pack {pack_id} — version {version}',
                export_header_name: 'Name: {name}',
                export_header_manifest: 'Manifest SHA-256: {sha}',
                export_header_period_tz: 'Period: {start} to {end} ({timezone})',
                export_header_untagged_included: 'Events not tagged to a framework: included',
                export_header_untagged_excluded: 'Events not tagged to a framework: not included',
                export_header_record_types: 'Record types: {types}',
                export_header_modules: 'Modules: {names}',
                export_header_all_modules: 'Modules: all',
                export_header_counts: 'Events: {total} ({tagged} tagged to a framework, {untagged} untagged)',
                export_header_coverage: 'Record-type coverage: {covered} of {denominator} selected record types have at least one event',
                export_header_coverage_missing: 'Selected record types with no event: {types}',
                export_header_unresolved: 'References not resolved: {count}',
                export_header_unresolved_unchecked: 'References not resolved: not checked',
                export_header_review: 'Review: {decision} by {reviewer} on {date}',
                export_header_review_note: 'Review note: {note}',
                export_header_not_reviewed: 'Review: not reviewed',
                export_header_submission: 'Recorded as submitted: to {recipient} on {sent_on} via {channel}, reference {reference}',
                export_header_submission_disclaimer: 'This records that the pack was sent. It is not proof that the recipient received or accepted it.',
                export_header_no_submission: 'Submission: none recorded',
                export_header_source_collected: 'Source {source} ({collection}): {count} events collected',
                export_header_source_failed: 'Source {source} ({collection}): could not be read ({error})',
                export_header_sources_not_read: 'Sources not read: {count} (not selected)',
                export_header_excluded_by_rule: 'Excluded by rule: record-summary read events ({count})',
                export_header_excluded_by_rule_uncounted: 'Excluded by rule: record-summary read events (not counted)',
                export_header_excluded_by_rule_not_applied: 'Excluded by rule: record-summary read events (rule not applied: this version was assembled before the rule existed)',
                value_none: 'none',
                // Gate E12.5 remediation 1 (P1-2): an event whose producer wrote no sentence.
                summary_composed: '{action} · {record_type}',
                export_doc_title: 'Evidence Pack {pack_id}',
                export_header_framework: 'Bewijspakket — Raamwerk: {framework}',
                export_header_period: 'Periode: {start} tot {end}',
                export_header_completeness: 'Volledigheid: {score}%',
                export_header_status: 'Status: {status}',
                col_entity_type: 'Entity Type',
                events_collected: 'events collected',
                missing_label: 'Missing',
                no_events_in_pack: 'No audit events found for this evidence pack.',
                // Sprint 63.25.0 (Slices C3, C4, F, H) — the versioned audit evidence pack page.
                page_description: 'Collect the audit events recorded in a period into a versioned dossier. Each version is a snapshot: its count, its event list, its coverage and every export read the same events.',
                cross_ref_audit: "Audit evidence packs collect the audit events recorded in a period; framework evidence packs gather one framework's evidence items for a regulator.",
                cross_ref_open_framework: 'Go to framework evidence packs',
                cross_ref_framework: "Framework evidence packs gather one framework's evidence items for a regulator; audit evidence packs collect the audit events recorded in a period.",
                cross_ref_open_audit: 'Go to audit evidence packs',
                filter_status: 'Status',
                filter_all_statuses: 'All statuses',
                col_name: 'Name',
                col_events: 'Events',
                col_version: 'Version',
                col_coverage: 'Record-type coverage',
                col_assembled: 'Assembled',
                coverage_short: '{covered} of {denominator}',
                not_a_member_count: 'Not a list of events',
                show_details: 'Show details for {name}',
                list_load_failed: 'The evidence pack list could not be read.',
                list_retry_subject: 'the evidence pack list',
                list_pagination: 'Pages of evidence packs',
                field_name: 'Name',
                name_hint: 'A name people will recognise, for example "Q3 inspection".',
                framework_hint: 'Optional. Only frameworks installed for your organisation are listed.',
                no_framework_option: 'No framework filter',
                include_untagged: 'Include events not tagged to any framework',
                include_untagged_hint: 'Most audit events carry no framework tag. Leave this ticked unless you want only the events tagged to the chosen framework. The pack shows how many of each it holds either way.',
                include_untagged_no_effect: 'With no framework chosen, every event in the period is included and this box has no effect.',
                period_hint: "Dates are read in your organisation's timezone, and the end date is included in full.",
                record_types_legend: 'Record types',
                record_types_hint: 'Choose at least one.',
                modules_legend: 'Modules',
                modules_hint: 'Optional. Leave every box unticked to include all modules.',
                error_name_required: 'Enter a name.',
                error_period_start_required: 'Choose a start date.',
                error_period_end_required: 'Choose an end date.',
                error_period_order: 'The end date must be on or after the start date.',
                error_record_types_required: 'Choose at least one record type.',
                assembling: 'Assembling…',
                detail_loading: 'Loading the pack…',
                detail_load_failed: 'This pack could not be read.',
                detail_retry_subject: 'this evidence pack',
                scope_heading: 'Scope and counts',
                meta_period: 'Period',
                meta_untagged: 'Events not tagged to a framework',
                meta_record_types: 'Record types',
                meta_modules: 'Modules',
                meta_counts: 'Events',
                meta_manifest: 'Manifest SHA-256',
                period_value: '{start} to {end} ({timezone})',
                untagged_included: 'Included',
                untagged_excluded: 'Not included',
                all_modules: 'All modules',
                counts_value: '{total} ({tagged} tagged to a framework, {untagged} untagged)',
                not_checked: 'Not checked',
                legacy_notice: 'This pack was assembled before versioning. Its stored count is not a list of events, so it cannot be reviewed or exported. Re-assemble it to create a reviewable version.',
                version_select: 'Version shown',
                version_option: 'Version {version}',
                reassemble: 'Re-assemble',
                reassembling: 'Re-assembling…',
                reassembled: 'Version {version} assembled.',
                drift_check: 'Check for changes since assembly',
                drift_checking: 'Checking…',
                drift_none: 'Nothing has changed since version {version} was assembled.',
                drift_new: '{count} events recorded in this period since assembly. Re-assemble to include them.',
                drift_gone: '{count} events in this version no longer exist at their source.',
                drift_changed: '{count} events in this version have changed at their source.',
                drift_failed_sources: 'Sources that could not be read during the check: {sources}.',
                drift_failed: 'The check for changes could not be run.',
                coverage_heading: 'Record-type coverage',
                coverage_sentence: '{covered} of {denominator} selected record types have at least one event.',
                coverage_missing: 'No event for: {types}.',
                coverage_not: 'This shows which selected record types have events. It does not measure whether the evidence is sufficient, or whether the organisation complies with any law.',
                history_heading: 'Version history',
                col_assembled_by: 'Assembled by',
                col_assembled_at: 'Assembled on',
                col_reviewer: 'Reviewer',
                col_decision: 'Decision',
                col_note: 'Note',
                col_reviewed_at: 'Reviewed on',
                col_submission: 'Recorded as submitted',
                decision_verified: 'Verified',
                decision_returned: 'Returned',
                not_reviewed: 'Not reviewed',
                no_submission: 'None recorded',
                submission_line: 'To {recipient} on {sent_on} via {channel}',
                items_heading: 'Events in version {version}',
                items_loading: 'Loading the events of this version…',
                items_empty_scope: 'It was assembled for {period}; framework: {framework}; record types: {types}; modules: {module_list}.',
                items_denied: 'You do not have permission to read the events of this pack. Reading them needs the "Read audit events" permission (audit:events:read).',
                items_failed: 'The events of this version could not be read.',
                items_retry_subject: 'the events of this version',
                items_populated: '{count} events in this version.',
                partial_banner: 'Some sources could not be read when this version was assembled, so it is partial and cannot be verified. Sources not read: {sources}.',
                partial_reassemble_hint: 'A person with permission to export audit events can re-assemble it.',
                partial_no_members: 'No events were collected from the sources that answered.',
                source_item: '{source} ({collection})',
                col_record_type: 'Record type',
                col_actor: 'Actor',
                col_timestamp: 'Time',
                col_module: 'Module',
                col_action: 'Action',
                col_summary: 'Summary',
                items_pagination: 'Pages of events',
                review: 'Review',
                review_title: 'Review version {version}',
                review_decision: 'Decision',
                review_option_verified: 'Verify this version',
                review_option_returned: 'Return it to the person who assembled it',
                review_note: 'Note',
                review_note_hint: 'Required when you return a version: say what needs to change.',
                error_decision_required: 'Choose a decision.',
                error_note_required: 'Enter a note saying why the version is returned.',
                review_submit: 'Record decision',
                review_recorded: 'Your decision on version {version} was recorded.',
                review_own_version: 'You assembled this version, so another person must review it.',
                record_submission: 'Record as submitted',
                submission_title: 'Record version {version} as submitted',
                submission_disclaimer: 'This records that the pack was sent. It is not proof that the recipient received or accepted it.',
                submission_recipient: 'Recipient',
                submission_sent_on: 'Sent on',
                submission_channel: 'Channel',
                submission_channel_hint: 'How it was sent, for example by email or by hand.',
                submission_reference: 'Reference',
                submission_reference_hint: 'Optional. For example a letter or ticket number.',
                submission_proof: 'Proof of sending',
                submission_proof_hint: 'Optional. A file showing the pack was sent, such as a dispatch receipt.',
                error_recipient_required: 'Enter the recipient.',
                error_sent_on_required: 'Choose the date it was sent.',
                error_channel_required: 'Enter the channel.',
                submission_recorded: 'Version {version} was recorded as submitted.',
                export_version: 'Export version {version}',
                // Sprint 63.25.0 (spec E3) — references that did not resolve at assembly.
                reference_not_found: 'Reference not found',
                reference_not_found_record: 'The source event names no record.',
                reference_not_found_actor: 'The person who acted is not in the user directory.',
                unresolved_line: 'References not resolved: {count}',
                unresolved_not_checked: 'References not resolved: not checked',
                unresolved_breakdown: '{records} with no record named; {actors} whose actor is not in the user directory.',
                unresolved_breakdown_actors_unchecked: '{records} with no record named; actors not checked.',
                server_error_reviewer_is_assembler: 'You assembled this version, so you cannot review it. Another person with the review permission must record the decision.',
                server_error_evidence_pack_version_not_found: 'This version of the pack was not found. The pack may have been re-assembled or removed.',
                server_error_submission_proof_not_owned: 'The proof file is not held under this version. Upload it to this version first.',
                server_error_evidence_pack_legacy: 'This pack was assembled before versioning. Re-assemble it to create a version that can be checked.',
                server_error_evidence_pack_too_large: 'More events match this scope than one pack can hold. Choose a shorter period or a narrower filter.',
                server_error_evidence_pack_definition_invalid: "This pack's stored definition cannot be assembled again. Create a new pack.",
            },
            audit_periods: {
                title: 'Auditperioden',
                create_period: 'Auditperiode aanmaken',
                framework_code_label: 'Raamwerkcode',
                select_framework: 'Selecteer een raamwerk...',
                period_label: 'Periodelabel',
                period_start: 'Begin periode',
                period_end: 'Einde periode',
                start: 'Begin',
                end: 'Einde',
                framework: 'Raamwerk',
                retention_days: 'Bewaardagen',
                section_period_dates: 'Periodedata',
                no_periods_found: 'Geen auditperioden gevonden.',
                period_created: 'Auditperiode aangemaakt',
                aria_table: 'Auditperiodes',
                suggested_boundaries: 'Voorgestelde grenzen',
                suggested_boundaries_info: 'Voorgestelde periodegrenzen zijn afgeleid van de regelgevende rapportagecyclus van elk raamwerk. Pas aan waar nodig voor uw organisatie.',
                reporting_cycle: 'Rapportagecyclus',
                suggested_start: 'Voorgesteld begin',
                suggested_end: 'Voorgesteld einde',
                suggested_next: 'Voorgestelde volgende periode',
                cycle_monthly: 'Maandelijks',
                cycle_quarterly: 'Driemaandelijks',
                cycle_semi_annual: 'Halfjaarlijks',
                cycle_annual: 'Jaarlijks',
                cycle_custom: 'Aangepast',
                period_deleted: 'Auditperiode verwijderd',
                list_truncated: 'Only {shown} of {total} audit periods could be read, so this list is incomplete.',
            },
        },
        create_case: {
            customer_ref: 'Customer Referentie',
            country: 'Land / Jurisdictie',
            preferred_channel: 'Voorkeur Channel',
            channels: {
                email: 'E-mail',
                postal: 'Post',
                phone: 'Telefoon',
                portal: 'Portaal',
            },
            source: 'Bron',
            sources: {
                admin_manual: 'Beheerdershandleiding',
                public_form: 'Publieke vorm',
                cmp_gateway: 'CMP Gateway',
                aesirx_cmp: 'AesirX CMP',
                phone_received: 'Telefoon ontvangen',
                postal_received: 'Post ontvangen',
                email_received: 'E-mail ontvangen',
                in_person: 'Persoonlijk',
                legacy_import: 'Verouderde import',
            },
            required_fields: 'Verzoek type, naam, en e-mail are required',
            create_error: 'Kan niet create case',
            // fix-sprint #447 W9 (owner ruling OR3)
            third_party_involved: 'A processor or third party must act on this request',
            third_party_involved_hint: 'The installed deadline records may state a different period when a processor or third party must act. The case is dated by the record that matches. It can be changed on the case later, which dates the case again from receipt.',
            request_type_not_offered: 'This kind of request is not a right under the frameworks your organisation has installed, so it cannot be recorded as a new request.',
            request_type_no_installed_clock: 'No installed framework states a period for this kind of request. The case will be dated from your SLA target for it or, failing that, the platform default, which is not a legal period.',
            // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
            request_type_handled_in_legal_matters: 'This kind of request is received as a legal matter in Legal Matters. No deadline is set for it.',
            legal_matter_right: 'Which right it raises',
            legal_matter_right_optional_hint: 'Optional. If it is not yet known, leave it empty: a person records it on the legal matter.',
            legal_matter_right_not_known: 'Not yet known',
        },
        settings: {
            title: 'Instellingen',
            request_types: 'Typen verzoeken',
            request_types_desc: 'Configureer welke typen verzoeken zijn ingeschakeld voor het openbare intakeformulier.',
            notifications: 'Meldingen',
            ticket_numbering: 'Ticketnummering',
            ticket_format: 'Huidig formaat',
            ticket_prefix: 'Voorvoegsel',
            ticket_prefix_note: 'niet configureerbaar',
            sla_deadlines: 'SLA-termijnen',
            sla_desc: 'Stel de maximale reactietijd (in kalenderdagen) in voor elk type verzoek. Zaken die de drempel naderen, tonen waarschuwingen.',
            days: 'dagen',
            warning_threshold: 'Waarschuwingsdrempel',
            escalation_label: 'Automatische escalatie bij SLA-overschrijding inschakelen',
            // fix-sprint #431 F1 — English until Phase 25 (R-57).
            escalation_hint: 'Runs the escalation ladder below. When off, no tier acts; warnings and breaches are still flagged.',
            warning_threshold_hint: 'A case no installed framework dates shows a warning at this share of its period and is flagged critical at 90%, or at this share if it is higher. A case an installed framework dates is warned at 75% and flagged critical at 90%.',
            // Gate E12.5 remediation 1 (fix-sprint #431) — escalation settings need sla:config:write.
            escalation_settings_need_write: 'Changing automatic escalation or the warning threshold needs the SLA configuration permission.',
            // Gate E12.5 remediation 2 — neither sla:config:write nor case:update: nothing here can be saved.
            sla_settings_read_only: 'You can view these settings but not change them: saving them needs the SLA configuration permission, or the case update permission for the deadlines alone.',
            sla_save_forbidden: 'The SLA settings were not saved: changing automatic escalation or the warning threshold needs the SLA configuration permission.',
            sla_save_invalid: 'The SLA settings were not saved: a deadline is set for something that is not a request type, or a value is out of range.',
            save_sla: 'SLA-configuratie opslaan',
            saving: 'Opslaan...',
            saved: 'Succesvol opgeslagen',
            announce_loaded: 'Instellingen geladen',
            email_on_creation: 'E-mailmelding bij aanmaken ticket',
            send_confirmation: 'Bevestigingsmail naar verzoeker sturen',
            users: 'Gebruikers',
            roles: 'Rollen',
            api_keys: 'API Toetsen',
            mfa: 'MFA',
            sessions: 'Sessies',
            webhooks: 'Webhaken',
            modules: 'Modules',
            // Sprint 62.45.0 (AC17) — the organisation calendar section.
            calendar: {
                heading: 'Working days and holidays',
                effect_notice: 'Every working-day deadline in the platform counts only the working days below, skips these holidays and counts these make-up working days — rights requests and their pauses, incidents, obligations, enforcement limitation periods, dossier filings and supervisory reports among them. A deadline worked out after you save uses the new calendar; a due date already recorded on a record keeps its date.',
                no_list_notice: "The platform supplies no public-holiday list: enter your organisation's own days off, public holidays included.",
                working_days_legend: 'Working days of the week',
                working_days_hint: 'Working-day deadlines count only these days.',
                working_days_required: 'Select at least one working day.',
                day_0: 'Sunday',
                day_1: 'Monday',
                day_2: 'Tuesday',
                day_3: 'Wednesday',
                day_4: 'Thursday',
                day_5: 'Friday',
                day_6: 'Saturday',
                holidays_heading: 'Holidays',
                holidays_caption: 'Holidays entered: {count}',
                empty: 'No holidays entered: working-day deadlines skip only the days outside the working week.',
                add_legend: 'Add a holiday',
                date_label: 'Date',
                label_label: 'Label (optional)',
                label_hint: 'A short name for the day, for example why the office is closed.',
                add: 'Add date',
                remove: 'Remove',
                remove_aria: 'Remove {date}',
                col_date: 'Date',
                col_label: 'Label',
                col_actions: 'Actions',
                no_label: 'No label',
                date_required: 'Enter a date.',
                date_out_of_range: 'Enter a date between {min} and {max}.',
                duplicate: '{date} is already in the list.',
                limit_reached: 'At most {max} holidays can be entered. Remove one before adding another.',
                save: 'Save calendar',
                saved: 'Calendar saved. Working-day deadlines worked out from now on count these working days, skip these holidays and count these make-up working days.',
                save_error: 'The calendar was not saved. Your entries are still here.',
                conflict: 'Someone else saved the calendar after you opened it, so your changes were not saved. Reload it to see their version, then enter your changes again.',
                reload: 'Reload calendar',
                load_error: 'The calendar could not be read, so it is not shown and cannot be changed here.',
                retry: 'Try again',
                last_saved: 'Last saved {date}.',
                never_saved: 'This calendar has not been saved here yet.',
                read_only: 'You can view this calendar but not change it.',
                unsaved: 'Unsaved changes.',
                holiday_is_extra: '{date} is entered as a make-up working day. A date is either a holiday or a make-up working day.',
                extra_heading: 'Make-up working days',
                extra_intro: 'A day your organisation works although it falls outside the working week, for example a Saturday worked in exchange for a day off beside a public holiday. Every working-day deadline counts it as a working day.',
                extra_caption: 'Make-up working days entered: {count}',
                extra_empty: 'No make-up working days entered.',
                extra_add_legend: 'Add a make-up working day',
                extra_date_label: 'Date worked',
                extra_label_label: 'Label for the day worked (optional)',
                extra_label_hint: 'A short name for the day, for example the day off it makes up for.',
                extra_add: 'Add day worked',
                extra_remove_aria: 'Remove make-up working day {date}',
                extra_is_holiday: '{date} is entered as a holiday. A date is either a holiday or a make-up working day.',
                extra_limit_reached: 'At most {max} make-up working days can be entered. Remove one before adding another.',
                extra_is_working_day: '{date} is a {weekday}, which is already a working day. A make-up working day is a date outside the working days of the week.',
                extra_on_working_weekday: 'These make-up working days fall on a working day of the week, so they would change nothing: {dates}. Remove them, or remove that day from the working days.',
            },
            // fix-sprint #431 F1 — Settings › Escalation ladder. English until Phase 25 (R-57).
            escalation_ladder: {
                heading: 'Escalation ladder',
                intro: "When a rights request reaches a tier's share of its period, the background SLA check acts on it once: it notifies the case's assignee and the people and role the tier names, in the platform or by email, as far as each person's own notification preferences allow. A reassign tier also moves the case. The share is the time from receipt of the request to the due date the case shows. Paused cases and cases with no due date are not escalated.",
                off_notice: 'Automatic escalation is off, so no tier acts. Warnings and breaches are still flagged. Turn it on under SLA Deadlines.',
                on_notice: 'Automatic escalation is on. The background SLA check runs these tiers.',
                suggested_notice: 'Your organisation has not saved a ladder, so these suggested tiers run. Save them, or change them and save, to make them your own.',
                caption: 'Escalation tiers: {count}',
                col_threshold: 'Share of period',
                col_name: 'Name',
                col_action: 'Action',
                col_targets: 'Notifies',
                col_channels: 'Sent by',
                col_actions: 'Actions',
                action_notify: 'Notify',
                action_escalate_to_role: 'Escalate to a role',
                action_reassign: 'Reassign',
                channel_in_app: 'In the platform',
                channel_email: 'Email',
                target_assignee: 'The assignee',
                target_role: 'Holders of {role}',
                target_users: 'Named: {names}',
                reassign_to: 'Moves the case to: {target}',
                threshold_breach: '100% (due date passed)',
                inactive: 'Inactive',
                edit: 'Edit',
                edit_aria: 'Edit the tier at {pct}%',
                remove: 'Remove',
                remove_aria: 'Remove the tier at {pct}%',
                add: 'Add tier',
                add_legend: 'Add a tier',
                edit_legend: 'Edit the tier at {pct}%',
                threshold_label: 'Share of the period elapsed (%)',
                threshold_hint: 'A whole number from 1 to 100. At 100 the tier acts once the due date has passed.',
                name_label: 'Name (optional)',
                name_hint: "Shown in notifications and in the case's escalation history. Left blank, the tier is named by its share.",
                action_label: 'Action',
                action_hint: 'Every tier notifies the assignee and the people it names. Escalate to a role also notifies everyone holding the role. Reassign moves the case to the first active person named, or else to the first person holding the role, unless the case is already with one of them.',
                role_label: 'Role',
                role_none: 'No role',
                users_label: 'Named people',
                users_hint: 'For a reassign tier, the case goes to the first active person in this list.',
                user_select_label: 'Person to add',
                user_placeholder: 'Choose a person',
                user_add: 'Add person',
                user_remove_aria: 'Remove {name}',
                users_empty: 'No one named.',
                users_retry_subject: 'the list of people',
                channels_legend: 'Send by',
                channels_hint: 'Each person still receives only what their own notification preferences allow.',
                active_label: 'Active',
                apply: 'Apply to the ladder',
                cancel: 'Cancel',
                save: 'Save escalation ladder',
                saved: 'Escalation ladder saved. The background SLA check runs it from its next sweep.',
                unsaved: 'Unsaved changes.',
                save_error: 'The escalation ladder was not saved. Your changes are kept.',
                conflict: 'Someone else saved the escalation ladder after you opened this page, so your changes were not saved. Reload the ladder to see that version, then make your changes again.',
                reload: 'Reload ladder',
                load_error: 'The escalation ladder could not be read, so it is not shown and cannot be changed here.',
                retry: 'Try again',
                read_only: 'You can view the escalation ladder but not change it.',
                empty: 'No tiers. Add at least one tier, or turn automatic escalation off under SLA Deadlines.',
                last_saved: 'Last saved {date}.',
                err_ladder_empty: 'Add at least one tier. To stop automatic escalation, turn it off under SLA Deadlines.',
                err_too_many_tiers: 'A ladder holds at most {max} tiers.',
                err_invalid_value: 'Tier {n}: a value is not valid.',
                err_threshold_out_of_range: 'Tier {n}: the share must be a whole number from 1 to 100.',
                err_threshold_duplicate: 'Tier {n}: another tier already acts at this share.',
                err_tier_id_duplicate: 'Tier {n}: this tier appears twice.',
                err_channels_required: 'Tier {n}: choose at least one way to send.',
                err_target_role_required: 'Tier {n}: choose the role to escalate to.',
                err_target_role_unknown: 'Tier {n}: the role is not an active role of your organisation.',
                err_reassign_target_required: 'Tier {n}: name a person or a role to reassign the case to.',
                err_target_user_unknown: 'Tier {n}: {name} is not an active user of your organisation.',
            },
            deadline_labels: {
                access: 'Inzage (DSAR)',
                deletion: 'Wissing',
                correction: 'Rectificatie',
                portability: 'Overdraagbaarheid',
                objection: 'Bezwaar',
                restriction: 'Beperking',
                consent_withdrawal: 'Intrekking van toestemming',
                protective_measures: 'Beschermingsmaatregelen',
                processing_information: 'Information about Processing',
                consent_decision: 'Consent Decision',
                complaint_or_compensation: 'Complaint, Denunciation, Lawsuit or Damages Claim',
            },
            sla_source_platform_default: 'Not saved: cases of this type that no installed clock dates are dated from the platform default of {days} days, which is not a legal period. Save a value to set your own target.',
            sla_source_tenant_target: 'Your SLA target: it dates cases of this type that no installed clock in force dates.',
            request_type_enabled_not_offered: 'Enabled, but not a right under the frameworks this organisation has installed: the public form does not offer it. Untick it to clear the setting.',
            request_types_from_frameworks: 'The request types listed are the rights the installed frameworks grant.',
        },
        section_assignments: {
            title: 'Sectietoewijzingen',
            assign_section: 'Sectie toewijzen',
            section_ref: 'Sectiereferentie',
            section_label: 'Sectielabel',
            department_id: 'Afdeling',
            assignee_id: 'Toegewezene',
            mandatory: 'Verplicht',
            assign: 'Toewijzen',
            no_assignments: 'Geen sectietoewijzingen',
            status: 'Status',
            completion: 'Voltooiing',
            mark_complete: 'Voltooien',
        },
        consolidation: {
            title: 'Consolidatievoortgang',
            add_child: 'Subtaak toevoegen',
            child_task_id: 'Subtaak-id',
            department_id: 'Afdeling',
            link: 'Koppeling',
            overall_completion: 'Totale voltooiing',
            departments_complete: 'Afdelingen voltooid',
            overdue: 'Achterstallig',
            ready: 'Gereed',
            not_ready: 'Niet gereed',
            master_status: 'Algehele status',
            department_breakdown: 'Afdelingsoverzicht',
            department: 'Afdeling',
            status: 'Status',
            completion: 'Voltooiing',
            due_date: 'Vervaldatum',
            overdue_flag: 'Achterstallig',
            child_tasks: 'Subtaken',
            task: 'Taak',
        },
        conflict_detection: {
            title: 'Conflictdetectie',
            unresolved: 'onopgelost',
            no_conflicts: 'Geen conflicten gedetecteerd',
            section: 'Sectie',
            field: 'Veld',
            dept_a: 'Afdeling A',
            value_a: 'Waarde A',
            dept_b: 'Afdeling B',
            value_b: 'Waarde B',
            severity: 'Ernst',
            status: 'Status',
            resolved: 'Opgelost',
            open: 'Openen',
            resolve: 'Oplossen',
        },
        org_maturity: {
            title: 'Organisatorische Volwassenheid',
            departments: 'Afdelingen',
            department_hierarchy: 'Afdelingshiërarchie',
            create_department: 'Afdeling Aanmaken',
            department_name: 'Afdelingsnaam',
            department_code: 'Codeer',
            level: 'Niveau',
            division: 'Divisie',
            department: 'Afdeling',
            team: 'Team',
            parent_department: 'Bovenliggende Afdeling',
            head_user: 'Afdelingshoofd',
            no_departments: 'Geen afdelingen geconfigureerd.',
            user_mappings: 'Gebruikerstoewijzingen',
            map_user: 'Gebruiker Toewijzen',
            user_id: 'Gebruikers-ID',
            role: 'Rol',
            no_users: 'Geen gebruikers toegewezen aan deze afdeling.',
            remove: 'Verwijderen',
            framework_assignments: 'Raamwerktoewijzingen',
            assign_framework: 'Raamwerk Toewijzen',
            entity_id: 'Entiteits-ID',
            entity_type: 'Entiteitstype',
            framework_id: 'Raamwerk-ID',
            framework_code: 'Raamwerkcode',
            no_assignments: 'Geen raamwerktoewijzingen.',
            compliance_rollup: 'Nalevingsoverzicht',
            entity_name: 'Entiteit',
            total_assessments: 'Totaal Beoordelingen',
            completed: 'Voltooid',
            completion_pct: 'Voltooiing %',
            open_gaps: 'Open Tekorten',
            critical_gaps: 'Kritiek',
            compliance_score: 'Scoren',
            no_rollup: 'Geen nalevingsoverzichtgegevens beschikbaar.',
            entity_summary: 'Nalevingsoverzicht Entiteit',
            frameworks_assigned: 'Raamwerken',
            overall_score: 'Totaalscore',
            load_departments_error: 'Laden van afdelingen mislukt',
            load_users_error: 'Laden van gebruikers mislukt',
            department_created: 'Afdeling aangemaakt',
            department_created_announce: 'Afdeling aangemaakt',
            create_department_error: 'Aanmaken van afdeling mislukt',
            user_mapped: 'Gebruiker toegewezen',
            user_mapped_announce: 'Gebruiker toegewezen aan afdeling',
            map_user_error: 'Toewijzen van gebruiker mislukt',
            user_removed: 'Gebruiker verwijderd',
            load_rollup_error: 'Laden van samenvatting mislukt',
            load_assignments_error: 'Laden van toewijzingen mislukt',
            framework_assigned: 'Raamwerk toegewezen',
            framework_assigned_announce: 'Raamwerk toegewezen aan entiteit',
            assign_framework_error: 'Toewijzen van raamwerk mislukt',
            assignment_removed: 'Toewijzing verwijderd',
            enter_entity_id_placeholder: 'Voer entiteit-ID in om toewijzingen te zien',
            entity_type_organization: 'Organisatie',
            entity_type_department: 'Afdeling',
        },
        onboarding_maturity: {
            title: 'Onboarding-volwassenheid',
            quick_starts: 'Snelle start',
            progress: 'Vooruitgang bij onboarding',
            personas: 'Persona-paden',
            persona_onboarding: 'Persona-onboarding',
            quick_start_scenarios: "Snelstartscenario's",
            start: 'Starten',
            restart: 'Opnieuw opstarten',
            estimated_minutes: 'Geschatte minuten',
            steps: 'Stappen',
            scenario: 'Scenario',
            no_scenarios: "Er zijn geen snelstartscenario's beschikbaar.",
            persona_id: 'Persona-ID',
            persona_label: 'Persoon',
            role_description: 'Rolbeschrijving',
            primary_modules: 'Primaire modules',
            recommended_tours: 'Aanbevolen rondleidingen',
            sample_data_modules: 'Voorbeeldgegevensmodules',
            quick_start_scenario: 'Snelstartscenario',
            no_personas: 'Er zijn geen personapaden beschikbaar.',
            framework_setup: 'Regelgevingskaders',
            framework_code: 'Kadercode',
            framework_name: 'Kadernaam',
            related_modules: 'Gerelateerde modules',
            persona_relevance: "Relevante Persona's",
            no_frameworks: 'Er zijn geen opties voor het instellen van het raamwerk beschikbaar.',
            tier_config: 'Configuratie van niveaumodule',
            tier: 'Niveau',
            available_modules: 'Beschikbare modules',
            module_count: 'Moduletelling',
            overall_completion: 'Algemene voltooiing',
            wizard_completed: 'Wizard voltooid',
            tours_completed: 'Rondleidingen voltooid',
            quick_starts_completed: 'Snelle start voltooid',
            selected_frameworks: 'Geselecteerde raamwerken',
            no_progress: 'Er zijn geen voortgangsgegevens over onboarding beschikbaar.',
            detected_persona: 'Gedetecteerde persoon',
            current_tier: 'Huidig niveau',
            completion_pct: 'Voltooiing',
            progress_page: {
                loaded: 'Voortgang geladen',
                no_progress: 'Geen onboarding-voortgangsgegevens beschikbaar.',
                overall: 'Algehele voortgang',
                detected_persona: 'Gedetecteerde persona',
                tier: 'Niveau',
                modules: 'modules',
                wizard: 'Tovenaar',
                completed: 'Voltooid',
                pending: 'In afwachting',
                tours: 'Rondleidingen',
                quick_starts: 'Snelstarts',
                frameworks: 'Geselecteerde raamwerken',
                no_frameworks: 'Geen raamwerken geselecteerd.',
                go_to_wizard: 'Installatiewizard voltooien',
                framework_selection: 'Raamwerkselectie',
                framework_toggle: 'Raamwerken in- of uitschakelen voor uw complianceprogramma.',
                framework_updated: 'Raamwerkselectie bijgewerkt.',
                view_tours: 'Rondleidingen bekijken',
                view_quick_starts: 'Snelstarts bekijken',
                // English until Phase 25 (R-57).
                // fix-sprint #447 W5 — no progress record yet; reading the page creates none.
                not_started: 'Not started',
                not_started_body: 'No onboarding progress record exists for you yet. Opening this page does not create one: it is created when you complete the Setup Wizard. The figures below count the tours and quick starts you have already completed.',
            },
            persona_page: {
                description: "Selecteer een persona om aanbevolen modules, rondleidingen en snelstartscenario's te bekijken.",
                no_personas: 'Geen personapaden beschikbaar.',
                modules: 'Modules',
                tours: 'Rondleidingen',
                quick_start: 'Snelstart',
                details: 'Details',
                primary_modules: 'Primaire modules',
                recommended_tours: 'Aanbevolen rondleidingen',
                framework_options: 'Regelgevingskaders',
                no_frameworks: 'Geen raamwerk-instellingsopties beschikbaar.',
                no_frameworks_explain: 'Er zijn nog geen regelgevingskaders geïnstalleerd voor uw organisatie, dus er is hier niets in te stellen.',
                no_frameworks_action: 'Installeer een marktpakket om de kaders toe te voegen die op u van toepassing zijn',
                start_tour: 'Starten',
                no_tours: 'Geen rondleidingen beschikbaar.',
                help_center: 'Helpcentrum',
                no_articles: 'Geen artikelen gevonden.',
                primary: 'Primair',
                framework_code: 'Raamwerkcode',
                framework_label: 'Etiket',
                region: 'Regio',
                category: 'Categorie',
                enabled: 'Ingeschakeld',
                disabled: 'Uitgeschakeld',
                tier_modules: 'Niveaumoduleconfiguratie',
                tiers_loaded: 'Niveauconfiguraties geladen',
                no_tier_config: 'Geen niveauconfiguratie beschikbaar.',
                view_tours: 'Modulerondleidingen bekijken',
                view_quick_start: 'Snelstart starten',
                toggle_framework: 'Raamwerk schakelen',
                framework_updated: 'Raamwerkselectie bijgewerkt.',
                tier_desc_light: 'Basis privacyfuncties — PDPL-compliancegrondslag',
                tier_desc_standard: 'Volledige compliancesuite — alle modules met werkstromen',
                tier_desc_enterprise: 'Geavanceerd bestuur en automatisering — multi-entiteit, groepsrapportage',
                persona_highlight: 'Uw modules',
                aria_go_to_module: '{name} — ga naar module',
                aria_start_tour: 'Begin {title}',
                aria_quick_start: 'Snelle start: {title}',
                aria_help_article: 'Help-artikel: {title}',
                aria_view_framework: '{label}{primary} — raamwerkdetails bekijken',
                aria_primary_for_persona: '{name} (primair voor deze persona)',
                primary_for_persona_suffix: ' (primair voor deze persona)',
                aria_view_in_persona_paths: '{name} — bekijken in Persona-paden',
                progress_of_total: '{completed} van {total} voltooid',
            },
            // Sprint 63.28.0 — Persona Paths revised. English copy until the R-57 deferral (sprint 57.7.0).
            persona_paths: {
                page_description: 'Find your role among the personas your organisation can use. The roles your installed frameworks name come first. Choose one to see the frameworks that name it, the registers it works in and its quick start.',
                recommended_heading: 'Recommended for you',
                your_persona: 'Your persona',
                source_chosen: 'Chosen by you',
                source_detected: 'Detected automatically',
                source_detected_role: 'Detected from your user role',
                source_detected_default: 'Suggested as a starting point, because nothing else matched',
                chosen_unavailable: 'The persona you chose is no longer available to your organisation, because the market pack it needs was removed. The detected persona is shown instead.',
                recommended_none_ranked: 'No installed framework names a role yet, so only your persona is shown.',
                recommended_failed: 'Your persona could not be read.',
                recommended_loading: 'Reading your persona…',
                recommended_ranked: 'Named by your installed frameworks',
                toolbar_label: 'Filter roles',
                search_label: 'Search roles',
                search_placeholder: 'Role, description, sector, framework name or ID',
                family_label: 'Role family',
                family_all: 'All',
                sector_label: 'Sector',
                sector_placeholder: 'All sectors',
                market_label: 'Market',
                market_all: 'All markets',
                named_toggle: 'Named by my installed frameworks',
                sort_label: 'Sort by',
                sort_relevance: 'Relevance',
                sort_name: 'Name',
                sort_id: 'Persona ID',
                showing: 'Showing {shown} of {total} roles',
                clear_filters: 'Clear filters',
                list_label: 'Roles by family',
                list_heading: 'All roles',
                list_heading_named: 'Roles named by your installed frameworks',
                group_count_one: '1 role',
                group_count: '{count} roles',
                relevance_named_one: 'Named by 1 installed framework',
                relevance_named: 'Named by {n} installed frameworks',
                relevance_legacy_only: 'Only legacy or draft frameworks installed',
                selected: 'selected',
                load_failed: 'The list of roles could not be loaded.',
                reload: 'Reload',
                none_available_title: 'No roles are available to your organisation',
                none_available_description: 'Install a market pack to make its roles available.',
                no_match_title: 'No role matches these filters',
                no_match_description: 'Change or clear the filters to see more roles.',
                no_match_outside_one: '1 role matches outside your installed frameworks.',
                no_match_outside: '{count} roles match outside your installed frameworks.',
                hidden_matches_one: '1 more matches outside your installed frameworks.',
                hidden_matches: '{count} more match outside your installed frameworks.',
                show_all: 'Show all',
                notice_unavailable: '{name} ({id}) needs a market pack for {market}, which your organisation has not installed.',
                notice_unknown: 'No persona has the ID "{id}", so nothing was opened.',
                notice_unknown_use_case: 'No use case has the ID "{id}", so nothing was opened.',
                detail_label: 'Role details',
                detail_back: 'Back to the list of roles',
                detail_loading: 'Loading this role…',
                detail_failed: 'The details of this role could not be loaded.',
                detail_market: 'Market',
                detail_sectors: 'Sectors',
                detail_family: 'Role family',
                detail_persona_id: 'Persona ID',
                frameworks_heading: 'Frameworks for this role',
                frameworks_description: 'Read from the regulatory packs: every framework that names this role, as primary or supporting.',
                frameworks_installed: 'Installed',
                frameworks_not_installed: 'Not installed',
                frameworks_not_installed_count: 'Not installed ({count})',
                frameworks_none_installed: 'None of your installed frameworks names this role.',
                frameworks_only_retired_installed: 'Only retired or draft instruments are installed.',
                frameworks_none_not_installed: 'Every framework that names this role is installed.',
                frameworks_retired: 'Retired and draft instruments ({count})',
                role_primary: 'Primary',
                role_supporting: 'Supporting',
                status_active: 'Active',
                status_upcoming: 'Upcoming, in force from {date}',
                status_upcoming_undated: 'Upcoming',
                status_legacy: 'Legacy',
                status_draft: 'Draft',
                status_unknown: 'Status not stated',
                status_temporary_active: 'Temporarily in force',
                find_in_catalog: 'Find in the Market Packs Catalog',
                find_in_catalog_aria: 'Find in the Market Packs Catalog: {name}',
                basis_label: 'Basis: {article}',
                basis_link_aria: 'Open {article} of {name} in the Source tab',
                attached_through: 'Attached through {parent}',
                second_read_pending: 'Second reading pending',
                basis_missing: 'No article recorded',
                destinations_heading: 'Where the work happens',
                destinations_description: 'The registers this role works in, each marked by whether your tier includes it.',
                in_tier: 'In your tier',
                not_in_tier: 'Not in your tier',
                tier_checking: 'Checking your tier…',
                tier_unchecked: 'Your tier could not be checked, so no register is marked.',
                tier_not_permitted: 'Your user role cannot see which modules your tier includes, so no register is marked.',
                destination_no_access: 'Your user role cannot open this register',
                quick_start_heading: 'Your quick start',
                quick_start_none: 'No quick start has been written for this role yet.',
                quick_start_not_listed: 'The quick start "{id}" is not in your list, so nothing is highlighted.',
                tours_heading: 'Guided tours',
                tour_title: '{name} tour',
                start_tour: 'Start',
                start_tour_aria: 'Start: {title}',
                starting: 'Starting…',
                no_tours: 'No tours for this role.',
                help_heading: 'Help articles',
                help_none: "No help articles for this role's modules.",
                use_cases_heading: 'Use cases',
                use_cases_none: 'No validated use case names this role as its primary persona.',
                about_link: 'About this persona',
                save_action: 'Use as my onboarding persona',
                save_action_aria: 'Use as my onboarding persona: {name}',
                saving: 'Saving…',
                save_success: '{name} is now your onboarding persona.',
                already_yours: 'This is your onboarding persona.',
                profile_label: 'Onboarding persona',
                change_in_persona_paths: 'Change it in Persona Paths',
            },
            // Sprint 63.28.0 (B8) — the shared persona select. English copy until the R-57 deferral (sprint 57.7.0).
            persona_select: {
                placeholder: 'Choose a persona',
                no_options: 'No persona matches',
                checking: 'Checking which personas your organisation can use…',
                availability_failed: 'Which personas your organisation can use could not be checked, so every persona is listed.',
                retry: 'Try again',
            },
            quick_starts_page: {
                description: "Oefen veelvoorkomende werkstromen met begeleide snelstartscenario's.",
                no_scenarios: "Geen snelstartscenario's beschikbaar.",
                started: 'Snelstart gestart',
                status_completed: 'Voltooid',
                status_in_progress: 'Bezig',
                status_not_started: 'Niet gestart',
                minutes: 'min',
                steps: 'stappen',
                step: 'Stap',
                restart: 'Herstarten',
                start: 'Starten',
                go_to_module: 'Ga naar module',
                next_step: 'Volgende stap',
                complete: 'Voltooien',
                reset: 'Herstellen',
                step_of: 'van',
                action_navigate: 'Navigeren',
                action_form_fill: 'Formulier invullen',
                action_review: 'Controleren',
                action_confirm: 'Bevestigen',
                step_changed: 'Stap {current} van {total}: {title}',
                completed_message: 'Snelstart succesvol voltooid!',
                reset_message: 'De snelstart is hersteld.',
            },
            persona_p_vn_01_label: 'Onderneming DPO',
            persona_p_vn_01_description: 'Data Protection Officer running the enterprise-wide personal data protection programme: impact assessments and their filings, rights requests, breach decisions and liaison with the authority',
            persona_p_vn_02_label: 'Afdelingseigenaar',
            persona_p_vn_02_description: 'Afdelingshoofd verantwoordelijk voor sectierapportage over naleving',
            persona_p_vn_03_label: 'Naleving van e-commerce',
            persona_p_vn_03_description: 'Compliance manager at an e-commerce platform owning its registration and change notifications, the registration dossier, and the seller and traceability information it supplies when an authority requests it',
            persona_p_vn_04_label: 'Telecom Regulatory Affairs Manager',
            persona_p_vn_04_description: 'Regulatory affairs lead at a telecom operator handling authority requests for subscriber information and disclosure governance',
            persona_p_vn_05_label: 'Leider databeheer',
            persona_p_vn_05_description: 'Lead owning data mapping, classification and the annual governance attestation, and the data-service provider notification and reports where they apply',
            persona_p_vn_06_label: 'Cyberbeveiliging en juridische activiteiten',
            persona_p_vn_06_description: 'Cybersecurity lead owning incident reporting decisions, authority requests and directions, disclosure logging, and data localisation and local presence readiness',
            persona_p_vn_07_label: 'AI Bestuursleider',
            persona_p_vn_07_description: 'Leidt toezicht op AI systeembeheer, risicobeoordelingen en classificatie',
            persona_p_vn_08_label: 'Grensoverschrijdende en leveranciersoverheid',
            persona_p_vn_08_description: 'Lead owning cross-border personal data transfer impact assessments and their filings, recipient and vendor safeguards, and data localisation checks',
            persona_p_vn_09_label: 'Leider voor inbreukreactie',
            persona_p_vn_09_description: 'Leider die de melding van inbreuken en de respons op incidenten coördineert',
            persona_p_vn_10_label: 'Leider Interne Audit',
            persona_p_vn_10_description: 'Lead running internal audits and regulator inspections across frameworks: evidence completeness, gaps, audit packs and sign-off',
            persona_p_vn_11_label: 'HR- & werknemersgegevens-verantwoordelijke',
            persona_p_vn_11_description: 'HR manager handling employee consent records, electronic labour contracts and internal DSARs',
            persona_p_vn_12_label: 'Compliance-leider multinationale dochteronderneming',
            persona_p_vn_12_description: 'FG in Vietnamese dochteronderneming van buitenlands bedrijf die lokale compliance-laag nodig heeft',
            persona_p_vn_13_label: 'Compliance-leider onderwijs',
            persona_p_vn_13_description: 'Compliance-leider op buitenlandse school die gegevensbescherming van leerlingen en ouders beheert',
            qs_hr_consent_title: 'Werknemerstoestemmingen registreren',
            qs_hr_consent_description: 'Doorloop van het registreren van werknemerstoestemming en het genereren van een dekkingsrapport.',
            qs_subsidiary_dpia_title: 'DPIA-beoordeling voor dochteronderneming aanmaken',
            qs_subsidiary_dpia_description: 'Doorloop van het aanmaken van een DPIA voor een verwerkingsactiviteit van een dochteronderneming.',
            qs_student_consent_title: 'Verzameling leerlingentoestemming instellen',
            qs_student_consent_description: 'Doorloop van het aanmaken van ouderlijk toestemmingsbeleid voor leerlinggegevens.',
            persona_p_vn_14_label: 'Extern juridisch adviseur',
            persona_p_vn_14_description: 'Senior advocatenkantoorpartner die meerdere klantorganisaties adviseert over PDPL-compliance',
            persona_p_vn_15_label: 'Operationeel manager advocatenkantoor',
            persona_p_vn_15_description: 'Coördinator klantbetrokkenheid die de tracking van deliverables en de coördinatie van deadlines beheert',
            persona_p_vn_16_label: 'Bedrijfseigenaar / CEO',
            persona_p_vn_16_description: 'CEO of GM, die juridisch gezien de gegevensbeheerder is onder PDPL, verantwoordelijk voor investeringen in compliance',
            qs_dpia_filing_title: 'Voltooi uw eerste DPIA-aanvraag',
            qs_dept_compliance_title: 'Dien de nalevingssectie van uw eerste afdeling in',
            qs_ecommerce_consent_title: 'Prepare your e-commerce platform registration dossier',
            qs_telecom_data_title: 'Gegevensverwerking van telecomabonnees instellen',
            qs_data_flow_title: 'Breng uw eerste gegevensstroom in kaart',
            qs_incident_response_title: 'Configureer de workflow voor incidentrespons',
            qs_ai_risk_title: 'Voer een AI systeemrisicobeoordeling uit',
            qs_cross_border_title: 'Beoordeel een grensoverschrijdende gegevensoverdracht',
            qs_breach_notification_title: 'Simuleer een workflow voor inbreukmeldingen',
            qs_audit_trail_title: 'Beoordeel een compliance-audittraject',
            qs_legal_advisor_portfolio_title: 'Beoordeel de complianceportfolio voor meerdere klanten',
            qs_legal_advisor_portfolio_description: 'Controleer de nalevingsstatus van meerdere klantorganisaties.',
            qs_law_firm_ops_title: 'Volg de status van de leverbare klant',
            qs_law_firm_ops_description: 'Bewaken van de voortgang van het advies en de betrokkenheid van klanten.',
            qs_ceo_dashboard_title: 'Bekijk het compliance-dashboard van het management',
            qs_ceo_dashboard_description: 'Beoordeel de nalevingsstatus en investeringsstatistieken op hoog niveau.',
        },
        rights_requests: {
            response_templates: {
                title: 'Antwoordsjablonen',
                subtitle: 'Beheer de DSR-antwoordbriefsjablonen die ResponseGenerator gebruikt om antwoorden aan betrokkenen op te stellen.',
                create_btn: 'Nieuwe sjabloon',
                edit_btn: 'Sjabloon bewerken',
                filter_right_type: 'Filter op het juiste type',
                filter_locale: 'Filter op landinstelling',
                filter_status: 'Filter op Status',
                all_right_types: 'Alle juiste typen',
                any_right_type: 'Alle (geldt voor alle typen)',
                all_locales: 'Alle landinstellingen',
                all_statuses: 'Allemaal',
                status_active: 'Actief',
                status_inactive: 'Inactief',
                empty_state: 'Er zijn geen sjablonen die overeenkomen met de huidige filters.',
                columns: {
                    template_id: 'Sjabloon-ID',
                    outcome: 'Resultaat',
                    locale: 'Lokaal',
                    right_type: 'Juiste soort',
                    is_active: 'Status',
                    last_updated: 'Laatst bijgewerkt',
                    actions: 'Acties',
                },
                actions: {
                    edit: 'Bewerken',
                    clone: 'Kloon',
                    deactivate: 'Deactiveren',
                    reactivate: 'Opnieuw activeren',
                    preview: 'Voorbeeld',
                },
                form: {
                    template_id: 'Sjabloon-ID',
                    template_id_help: 'Identificatie in kleine letters, bijv. dsr_access_fulfilled_vi',
                    outcome: 'Resultaat',
                    locale: 'Lokaal',
                    right_type: 'Juiste soort',
                    subject: 'Onderwerp',
                    body: 'Lichaamssjabloon',
                    body_help: 'Gebruik tijdelijke aanduidingen zoals {case_reference}, {data_subject_name}, {fulfilled_at}. Minimaal 15 regels voor brieven met meerdere secties.',
                    is_active: 'Actief',
                    official_form_id: 'Officieel formulier-ID (optioneel)',
                },
                outcomes: {
                    fulfilled: 'Vervuld',
                    partially_fulfilled: 'Gedeeltelijk vervuld',
                    denied: 'Geweigerd',
                },
                clone_modal_title: 'Kloon sjabloon',
                clone_target_locale: 'Doelland',
                clone_help: 'Er wordt een nieuwe sjabloon gemaakt in de doellandinstelling, waarbij deze sjabloon als uitgangspunt wordt gebruikt.',
                preview_modal_title: 'Sjabloonvoorbeeld',
                preview_note: 'Voorvertoning gebruikt tijdelijke aanduidingen voor democase DSR-DEMO-0020. Echte bezorging vervangt live casusgegevens.',
                preview_unavailable: 'Voorvoorbeeld is een bestaande case vereist. Voeg deze sjabloon toe aan een case en gebruik de actie Conceptreactie op de casedetailpagina.',
                deactivate_confirm: 'Dit sjabloon deactiveren? Het zal van toepassing blijven op bestaande gevallen waarin ernaar wordt verwezen.',
                toasts: {
                    created: 'Sjabloon gemaakt',
                    updated: 'Sjabloon bijgewerkt',
                    deactivated: 'Sjabloon gedeactiveerd',
                    reactivated: 'Sjabloon opnieuw geactiveerd',
                    cloned: 'Sjabloon gekloond',
                    load_error: 'Kan sjablonen niet laden',
                    save_error: 'Kan sjabloon niet opslaan',
                    clone_error: 'Kan de sjabloon niet klonen',
                },
            },
        },
        organizations: {
            departments: {
                title: 'Afdelingen',
                subtitle: 'Beheer de afdelingen die DSR-zaken, DPIA-taken en compliance-workflows doorsturen.',
                create_btn: 'Nieuwe afdeling',
                edit_btn: 'Afdeling bewerken',
                columns: {
                    name: 'Naam',
                    description: 'Beschrijving',
                    active_cases: 'Actieve gevallen',
                    members: 'Leden',
                    status: 'Status',
                    actions: 'Acties',
                },
                status_active: 'Actief',
                status_inactive: 'Inactief',
                form: {
                    name: 'Naam',
                    description: 'Beschrijving',
                },
                actions: {
                    edit: 'Bewerken',
                    deactivate: 'Deactiveren',
                    reactivate: 'Opnieuw activeren',
                    delete: 'Verwijderen',
                    manage_members: 'Leden beheren',
                },
                delete_modal_title: 'Afdeling verwijderen',
                delete_warning: 'Deze afdeling heeft nog actieve zaken. Kies een afdeling waaraan u ze opnieuw wilt toewijzen voordat u ze verwijdert.',
                delete_no_cases: 'Deze afdeling heeft geen actieve cases en kan veilig worden verwijderd.',
                reassign_prompt: 'Wijs actieve cases opnieuw toe aan:',
                reassign_none_available: 'Geen andere actieve afdelingen beschikbaar. Maak eerst een andere afdeling aan.',
                member_section_title: 'Leden',
                add_member: 'Lid toevoegen',
                remove_member: 'Verwijderen',
                member_role: 'Rol',
                no_members: 'Er zijn geen leden toegewezen aan deze afdeling.',
                toasts: {
                    created: 'Afdeling aangemaakt',
                    updated: 'Afdeling bijgewerkt',
                    deactivated: 'Afdeling gedeactiveerd',
                    reactivated: 'Afdeling opnieuw geactiveerd',
                    deleted: 'Afdeling verwijderd',
                    reassigned_count: '{count} zaak(en) opnieuw toegewezen',
                    member_added: 'Lid toegevoegd',
                    member_removed: 'Lid verwijderd',
                    load_error: 'Kan afdelingen niet laden',
                    save_error: 'Kan afdeling niet opslaan',
                    delete_error: 'Kan afdeling niet verwijderen',
                },
            },
            retention_policies: {
                title: 'Bewaarbeleid',
                subtitle: 'Configureer bewaarperioden per doeltype. Identiteitsverificatiedocumenten gebruiken dit om retention_expires_at op toekomstige uploads te stempelen.',
                create_btn: 'Nieuw beleid',
                edit_btn: 'Beleid bewerken',
                columns: {
                    target_type: 'Doeltype',
                    retention_days: 'Retentie (dagen)',
                    owner: 'Beleidseigenaar',
                    effective_from: 'Effectief vanaf',
                    last_reviewed: 'Laatst beoordeeld',
                    status: 'Status',
                    actions: 'Acties',
                },
                status_active: 'Actief',
                status_inactive: 'Inactief',
                form: {
                    target_type: 'Doeltype',
                    target_type_help: 'Op welke gegevenscategorie de bewaarperiode van toepassing is. Momenteel worden alleen DSR-identiteitsverificatiedocumenten ondersteund.',
                    retention_days: 'Bewaarperiode (dagen)',
                    retention_days_help: 'Tussen 1 en 3650 dagen (maximaal 10 jaar).',
                    owner: 'Beleidseigenaar',
                    owner_help: 'Meestal de DPO. Verantwoordelijk voor het bewaarbesluit onder DG-2.',
                    effective_from: 'Effectief vanaf',
                },
                target_types: {
                    dsr_identity_verification_document: 'DSR-identiteitsverificatiedocumenten',
                },
                deactivate_notice: 'Deactiveren heeft geen invloed op reeds gestempelde bijlagen; alleen toekomstige uploads krijgen geen bewaartermijn meer.',
                deactivate_confirm: 'Dit bewaarbeleid deactiveren?',
                toasts: {
                    created: 'Bewaarbeleid gemaakt',
                    updated: 'Bewaarbeleid bijgewerkt',
                    deactivated: 'Bewaarbeleid gedeactiveerd',
                    load_error: 'Kan bewaarbeleid niet laden',
                    save_error: 'Kan het bewaarbeleid niet opslaan',
                },
            },
        },
    },
    data_mapping: {
        // Sprint 62.58.0 (Slice N) — English for key parity until Phase 25 (R-57).
        deployment_privacy: {
            audience_kinds: {
                public: 'The public',
                data_subject_group: 'Named data subjects',
            },
            channels: {
                website: 'Website',
                point_of_collection: 'At the point of collection',
                employment_documentation: 'Employment documentation',
                contract_or_terms: 'Contract or terms',
                in_app: 'In the app',
                email: 'Email',
                other: 'Other',
            },
            access_kinds: {
                view: 'View',
                extract: 'Extract',
                export: 'Export',
                delete: 'Delete',
                configure: 'Configure',
            },
            export: {
                coverage_heading: 'Privacy notices by data subject',
                coverage_withheld: 'Notice details are shown only to a reader who may read the privacy notice register.',
                col_activity: 'Processing activity',
                col_data_subject: 'Data subject',
                col_notice: 'Notice',
                col_revision: 'Revision',
                col_channels: 'Channels',
                col_effective_from: 'In effect from',
                col_status: 'Status',
                not_recorded: 'Not recorded',
                no_current_notice: 'No current notice: every notice that names this data subject is withdrawn, superseded or replaced by a newer revision',
                status_replaced: '{status}, replaced by a newer revision',
                not_stated: 'Not stated',
                access_heading: 'Who may access the data',
                col_version: 'Version',
                col_reviewed_on: 'Reviewed on',
                col_role_or_position: 'Role or position',
                col_access_kinds: 'Permitted actions',
                col_means: 'By what means',
                col_conditions: 'On what conditions',
                no_access_statement: 'No access statement recorded',
                no_data_subjects: 'No data subjects recorded',
                public_linked: 'Also linked: {count} public notices, which name no data subject',
                public_linked_one: 'Also linked: 1 public notice, which names no data subject',
                coverage_truncated: 'More notices are linked to some processing activities than this export lists, so for those activities the notices are listed published first, then approved, draft, withdrawn and superseded, the most recently drafted first within each. The processing activities concerned: {activities}. A data subject reads "Not recorded" only when no linked notice names it.',
                access_withheld: 'Access statements are shown only to a reader who may read processing activities.',
                col_approved_by: 'Approved by',
                col_systems: 'Systems',
            },
            notice_form: {
                heading: 'Audience, channels and deployment',
                intro: 'Say whom this revision is given to, how they are given it, from which day, and the processing activities and systems it is given for.',
                audience_label: 'Whom the notice is given to',
                audience_public_hint: 'Anyone can read it, for example on your website. A public notice names no data subject, so the notice coverage of a processing activity does not list it against any data subject.',
                audience_group_hint: 'Named data subjects of the linked processing activities, such as drivers, passengers or staff.',
                audience_group_need_grant: 'Naming data subjects needs the permission to read processing activities, which your role does not hold: the data subjects are chosen from the linked processing activities. The notice can be given to the public.',
                data_subjects_label: 'Data subjects the notice is given to',
                data_subjects_hint: 'Chosen from the data subjects recorded on the linked processing activities.',
                data_subjects_need_activity: 'Link at least one processing activity first: the data subjects are chosen from those it records.',
                data_subjects_none: 'The linked processing activities record no data subjects.',
                data_subjects_required: 'Choose at least one data subject.',
                data_subjects_unknown: '{value} (not recorded on the linked processing activities)',
                channels_label: 'How the audience is given the notice',
                channels_hint: 'Choose at least one. A sticker or a screen where the data is collected, such as inside a vehicle, is "At the point of collection".',
                channels_required: 'Choose at least one channel.',
                channel_note_label: 'Describe the other channel',
                channel_note_required: 'Describe the other channel.',
                effective_from_label: 'In effect from',
                effective_from_hint: 'The day the notice takes effect, as your organization states it. Leave it empty if the day is not yet known.',
                activities_label: 'Processing activities the notice is given for',
                activities_hint: 'The data subjects of these activities are the ones the notice can name.',
                systems_label: 'Data-map systems the notice covers',
                links_need_grant: 'Linking a notice to processing activities or systems needs the permission to read processing activities, which your role does not hold. The notice can be drafted without links.',
                systems_need_grant: 'Choosing data-map systems needs the permission to read systems, which your role does not hold.',
                picker_loading: 'Loading the list…',
                activities_empty: 'No processing activity is recorded.',
                systems_empty: 'No data-map system is recorded.',
                activities_failed: 'The processing activities could not be read.',
                systems_failed: 'The data-map systems could not be read.',
                activities_manual_label: 'Processing activity IDs',
                systems_manual_label: 'Data-map system IDs',
                manual_hint: 'Enter the IDs separated by commas; they are checked when you save.',
                off_list: '{value} (not in the list read)',
                retry: 'Reload',
                reloading: 'Reloading…',
                activities_retry_subject: 'the processing activities',
                systems_retry_subject: 'the data-map systems',
                error_link_grant: 'Your role cannot link a notice to processing activities or systems. Remove the links, or ask for the permission to read processing activities.',
                error_link_not_found: 'A linked processing activity or system was not found in this organization. What you entered is kept.',
                error_audience_invalid: 'A chosen data subject is not recorded on the linked processing activities. What you entered is kept.',
                col_audience: 'Audience',
                col_channels: 'Channels',
                col_effective_from: 'In effect from',
                col_links: 'Linked to',
                links_activities: 'Processing activities: {names}',
                links_activities_count: '{count} processing activities',
                links_activities_count_one: '1 processing activity',
                links_systems: 'Systems: {names}',
                links_systems_count: '{count} systems',
                links_systems_count_one: '1 system',
                links_none: 'None',
                other_channel: 'Other: {note}',
                data_subjects_unconfirmed: '{value} (not confirmed: a linked processing activity could not be read)',
                error_systems_grant: 'Your role cannot link a notice to data-map systems. Remove the systems, or ask for the permission to read systems.',
                audience_with_subjects: '{label}: {subjects}',
            },
            coverage: {
                heading: 'Notices',
                intro: 'For each data subject recorded on this activity, the linked privacy notices that name it, with revision, channels, date in effect and status. Nothing here is counted or scored.',
                not_visible: 'Your role cannot read the privacy notice register, so the notices are not shown here. This does not say whether a notice exists.',
                no_subjects: 'This activity records no data subjects.',
                list_label: 'Notices by data subject',
                notice_line: '{title}, revision {revision}',
                notice_meta: 'Channels: {channels} · In effect from {date} · Status: {status}',
                load_failed: 'The notices could not be read.',
                retry_subject: 'the notices of this activity',
                pagination: 'Pages of data subjects',
                public_heading: 'Also linked: public notices',
                public_intro: 'These notices are addressed to the public. A public notice names no data subject, so it is not listed against any data subject above.',
                public_failed: 'The public notices linked to this activity could not be read.',
                public_retry_subject: 'the public notices',
                truncated: 'More notices are linked to this activity than are listed here, so they are listed published first, then approved, draft, withdrawn and superseded, the most recently drafted first within each. A data subject reads "Not recorded" only when no linked notice names it.',
                subject_state: '{subject}: {state}',
            },
            access: {
                intro: 'Who may view, extract, export, delete or configure the data of this activity, by what means and on what conditions. Every revision is kept; the latest is first.',
                record: 'Record a revision',
                record_first: 'Record the access statement',
                current: 'Current',
                version: 'Version {version}',
                reviewed: 'Reviewed on {date}',
                approved: 'Approved by {name}',
                not_approved: 'No approver recorded',
                recorded: 'Recorded by {name} on {date}',
                systems: 'Systems: {names}',
                entries_label: 'Entries of version {version}',
                evidence_count: 'Documents: {count}',
                evidence_none: 'No document attached',
                evidence_open: 'Documents',
                evidence_open_aria: 'Documents of version {version}',
                load_failed: 'The access statement could not be read.',
                retry_subject: 'the access statement',
                pagination: 'Pages of access statement revisions',
                history_label: 'Revisions of the access statement',
                unknown_person: 'Unknown person',
            },
            access_form: {
                title: 'Record a revision of the access statement',
                replaces: 'This revision replaces version {version}. The earlier versions are kept.',
                first: 'This is the first revision of the access statement for this activity.',
                entries_heading: 'Who may access the data',
                entry_legend: 'Entry {n}',
                role_label: 'Role or position',
                role_hint: 'A role or a position, such as "Fleet safety officer". A person\'s name is not needed.',
                access_kinds_label: 'Permitted actions',
                means_label: 'By what means',
                means_hint: 'For example, the extraction software named, a console, or a request to IT.',
                conditions_label: 'On what conditions',
                conditions_hint: 'For example, a ticket, two people present, or a stated purpose.',
                add_entry: 'Add an entry',
                remove_entry: 'Remove entry {n}',
                reviewed_on_label: 'Reviewed on',
                reviewed_on_hint: 'The day the statement was reviewed. It cannot be later than today.',
                approver_label: 'Approved by (optional)',
                approver_none: 'No approver',
                approver_retry_subject: 'the list of people',
                systems_label: 'Data-map systems the statement covers',
                systems_kept: 'The systems the current version covers are kept, because your role cannot read the data-map systems.',
                missing: 'Give every entry a role or position and at least one permitted action, and give the review date.',
                submit: 'Record the revision',
                saving: 'Recording…',
                recorded: 'Revision recorded.',
                record_failed: 'The revision could not be recorded. What you entered is kept.',
                conflict: 'Someone recorded version {version} of this statement while you were editing, so yours was not recorded. What you entered now builds on version {version}: the entries and systems you did not change read as in version {version}, and your additions, changes and removals are kept. Check the entries, then record again.',
                conflict_reread_failed: 'Someone recorded a newer revision of this statement while you were editing, so yours was not recorded, and the newer revision could not be read. What you entered is kept; record again to read it once more.',
            },
            access_files: {
                heading: 'Documents',
                intro: "The documents behind this revision, such as the access policy, the extraction software's role list or the signed approval.",
                slot: 'Documents of this revision',
                none: 'No document is attached to this revision.',
                file_hash: 'SHA-256: {hash}',
                not_held: 'Document no longer held',
                download: 'Download',
                remove: 'Remove',
                upload: 'Add a document',
                bound: 'Document added.',
                removed: 'Document removed.',
                bind_failed: 'The document could not be added.',
                remove_failed: 'The document could not be removed.',
                not_owned: 'The revision this document belongs to was not found.',
                remove_confirm: 'Remove this document from the revision and delete it?',
                load_failed: 'The documents could not be loaded.',
            },
        },
        lawful_basis: {
            title: 'Lawful basis',
            load_failed: 'The lawful bases your installed frameworks declare could not be read.',
            none_declared: 'No installed framework declares a lawful-basis vocabulary. Record the basis and the article it rests on below.',
            declared_by: 'Declared by {instrument}',
            citation: 'Citation',
            statement_label: 'Lawful basis (recorded by you)',
            statement_citation_label: 'Article this basis rests on',
            other_ground: 'Another lawful basis, recorded by you',
            needs_redetermination: 'This record carries "{basis}", which no installed framework declares. It needs re-determining.',
            needs_redetermination_declared: 'This record carries "{basis}" and is marked as needing re-determining. Confirm this basis or choose another, then save.',
            stored_unverified: 'This record carries "{basis}". Some installed frameworks have not been verified yet, so whether one of them declares it is not known.',
            instruments_heading: 'Instruments checked',
            instrument_declares_none: '{instrument} — verified: declares no lawful basis',
            instrument_not_verified: '{instrument} — not yet verified',
            retry: 'Try again',
            list_needs_redetermination: 'Needs re-determining: {basis}',
            declared_version_changed: 'Recorded against {framework} {version}; installed now: {installed}.',
            declared_version_withdrawn: 'Recorded against {framework} {version}; no installed framework declares this basis now.',
            loading: 'Loading the lawful bases your installed frameworks declare.',
            save_blocked_unread: 'A lawful basis is required, and the bases your installed frameworks declare have not been read yet.',
            citation_blank: 'Enter the article this basis rests on. Spaces alone are not an article.',
            citation_without_statement: 'An article is entered but no basis is recorded. Record the basis it supports, or clear the article.',
        },
        // OD42 (fix-sprint-20261003-0625 F28) — the transfer-mechanism control and
        // its list sentences. The mechanism is what the installed frameworks declare.
        transfer_mechanism_vocabulary: {
            title: 'Transfer mechanism',
            load_failed: 'The transfer mechanisms your installed frameworks declare could not be read.',
            loading: 'Loading the transfer mechanisms your installed frameworks declare.',
            retry: 'Try again',
            none_declared: 'No installed framework declares a transfer mechanism. Describe the transfer in the detail field.',
            none_declared_unverified: 'No installed framework has declared a transfer mechanism, and some have not been verified yet. Describe the transfer in the detail field.',
            declared_by: 'Declared by {instrument}',
            citation: 'Citation',
            needs_redetermination: 'This record carries "{mechanism}", which no installed framework declares. It needs re-determining.',
            needs_redetermination_declared: 'This record carries "{mechanism}" and is marked as needing re-determining. Confirm this mechanism or choose another, then save.',
            unverified: 'This record carries "{mechanism}". No verified installed framework declares it; not yet verified: {instruments}.',
            instruments_heading: 'Instruments checked',
            instrument_declares_none: '{instrument} — verified: declares no transfer mechanism',
            instrument_not_verified: '{instrument} — not yet verified',
            list_needs_redetermination: 'Needs re-determining: {mechanism}',
            list_unverified: '{mechanism} (not yet verified: {instruments})',
            stored_unmeasured: 'This record carries "{mechanism}". No installed framework has stated which transfer mechanisms it provides yet, so whether it is declared has not been checked.',
        },
        nav: {
            systems: 'Systemen',
            categories: 'Gegevenscategorieën',
            registry: 'Gegevensregister',
            purposes: 'Verwerkingsdoeleinden',
            retention: 'Bewaring',
            flows: 'Gegevensstromen',
            activities: 'Verwerkingsactiviteiten',
            evidence: 'Bewijs',
            exports: 'Exporten',
            attestation: 'Attestcycli',
            governance: 'Bestuur',
            review: 'Beoordelingstaken',
            inventory: 'Inventaris',
            exceptions: 'Uitzonderingen',
            history: 'Geschiedenis',
            ropa_completeness: 'ROPA-volledigheid',
        },
        ropa_completeness_autofix: {
            ask_button: 'Vraag Forseti',
            drafting: 'Opstellen…',
            accept: 'Accepteer',
            reject: 'Afwijzen',
            accepted: 'Geaccepteerd — solliciteer via de activiteiteneditor.',
            rejected: 'Afgewezen.',
            needs_human_input: 'Forseti heeft meer activiteitscontext nodig (bijvoorbeeld een gedocumenteerd doel) voordat dit veld wordt opgesteld.',
            ask_again: 'Ask again',
            draft_failed: 'Forseti could not draft a value.',
            decision_failed: 'The decision could not be saved.',
        },
        ropa_completeness: {
            rules: {
                nd165_scope_rationale: 'Confirm and evidence the transfer scope; review mixed data before selecting the applicable assessment route.',
                nd165_scope_condition: 'Important/core cross-border data with mixed or unresolved personal-data scope',
                nd165_personal_dpia_rationale: 'Record the processing impact assessment reference when required, including confirmed personal core/important cross-border data.',
                nd165_personal_dpia_condition: 'Confirmed personal important/core data + cross-border transfer',
                nd165_personal_transfer_rationale: 'Record the separate personal-data cross-border transfer impact assessment dossier reference.',
                nd165_personal_transfer_condition: 'Confirmed personal important/core data + cross-border transfer',
                nd165_nonpersonal_assessment_rationale: 'Core non-personal data requires a regulator-issued assessment for cross-border transfers.',
                nd165_nonpersonal_assessment_condition: 'Confirmed non-personal core data + cross-border transfer',
            },
            title: 'ROPA-volledigheidsrapport',
            subtitle: 'Hygiëne-audit per veld van uw gegevens over verwerkingsactiviteiten, afgestemd op het regelgevingskader.',
            framework_label: 'Kader',
            kpi_total: 'Activiteiten gescoord',
            kpi_critical: 'Kritisch',
            kpi_high: 'Hoog',
            kpi_conditional: 'Voorwaardelijk',
            field_chart_heading: 'Ontbrekende velden op frequentie',
            drilldown_heading: 'Activiteiten met gaten',
            col_activity: 'Activiteit',
            col_field: 'Veld',
            col_severity: 'Ernst',
            col_condition: 'Conditie',
            col_action: 'Fix',
            action_fix: 'Fix',
            severity_critical: 'Kritisch',
            severity_high: 'Hoog',
            severity_conditional: 'Voorwaardelijk',
            severity_informational: 'Informatief',
            include_informational: 'Informatief opnemen',
            export_csv: 'CSV downloaden',
            exporting: 'Exporteren...',
            loading: 'Laden…',
            empty_state: 'Alle actieve activiteiten hebben volledige ROPA-records voor dit raamwerk.',
            error_fetch: 'Kan het volledigheidsrapport niet laden.',
            error_unsupported_framework: 'Dit raamwerk wordt niet ondersteund.',
            error_framework_not_installed: 'Dit framework is niet geïnstalleerd voor uw organisatie.',
            no_misses_for_field: 'Er zijn geen activiteiten die dit veld missen.',
            framework_PDPL: 'PDPL (Wet 91/2025 + Decreet 356)',
            framework_GDPR: 'GDPR Artikel 30',
            framework_DECREE_165: 'Decreet 165 (Vietnamese gegevenswet)',
            framework_ISO_27701: 'ISO 27701 (informatief)',
        },
        registry: {
            // Sprint 64.72.0 — kolomkoppen van de export van het gegevensregister.
            export_col: {
                registry_item_id: 'Registeritem-ID',
                name: 'Naam',
                description: 'Omschrijving',
                field_identifiers: 'Veldidentificatoren',
                category_id: 'Categorie-ID',
                category_name: 'Categorienaam',
                data_type: 'Gegevenstype',
                effective_sensitivity: 'Geldende gevoeligheid',
                sensitivity_override: 'Afwijkende gevoeligheid',
                data_subjects: 'Betrokkenen',
                volume_band: 'Volumebereik',
                source_system_ids: 'Bronsysteem-ID’s',
                processing_activity_ids: 'Verwerkingsactiviteit-ID’s',
                retention_policy_id: 'Bewaarbeleid-ID',
                effective_retention_policy_id: 'Geldend bewaarbeleid-ID',
                purposes: 'Doeleinden',
                collection_method: 'Wijze van verzameling',
                legal_basis: 'Rechtsgrond',
                data_tier: 'Gegevensniveau',
                data_tier_source: 'Herkomst van het gegevensniveau',
                storage_locations: 'Opslaglocaties',
                cross_border: 'Grensoverschrijdend',
                status: 'Status',
                source_form_submission_id: 'Indienings-ID van het bronformulier',
                source_form_template_id: 'Sjabloon-ID van het bronformulier',
                last_reviewed_at: 'Laatst beoordeeld',
                next_review_at: 'Volgende beoordeling',
                created_at: 'Aangemaakt op',
                updated_at: 'Bijgewerkt op',
                data_element: 'Gegevenselement',
                data_category: 'Gegevenscategorie',
                systems: 'Systemen',
                retention_policy: 'Bewaarbeleid',
                name_of_processing: 'Naam van de verwerking',
                categories_of_personal_data: 'Categorieën van persoonsgegevens',
                categories_of_data_subjects: 'Categorieën van betrokkenen',
                recipients: 'Ontvangers',
                third_country_transfers: 'Doorgiften naar derde landen',
                retention_period: 'Bewaartermijn',
                doc_title: 'Gegevensregister',
                doc_generated: 'Export gegenereerd {timestamp}',
                doc_item_count: '{count} registeritem(s)',
                doc_template_full: 'Volledig',
            },
            export_vocab: {
                data_type: {
                    text: 'Tekst',
                    number: 'Getal',
                    date: 'Datum',
                    datetime: 'Datum en tijd',
                    boolean: 'Ja/nee',
                    binary: 'Binair',
                    structured: 'Gestructureerd',
                    file: 'Bestand',
                    identifier: 'Identificator',
                    geolocation: 'Geolocatie',
                },
                collection_method: {
                    direct_from_subject: 'Rechtstreeks van de betrokkene',
                    third_party: 'Van een derde partij',
                    derived: 'Afgeleid uit andere gegevens',
                    observed: 'Waargenomen',
                    inferred: 'Afgeleid door gevolgtrekking',
                    public_source: 'Openbare bron',
                },
                data_subject_type: {
                    customer: 'Klant',
                    employee: 'Werknemer',
                    contractor: 'Opdrachtnemer',
                    prospect: 'Prospect',
                    minor: 'Minderjarige',
                    patient: 'Patiënt',
                    student: 'Student',
                    beneficiary: 'Begunstigde',
                    vendor_contact: 'Contactpersoon van de leverancier',
                    website_visitor: 'Websitebezoeker',
                    other: 'Overig',
                },
                volume_band: {
                    lt_1k: 'Minder dan 1.000 records',
                    '1k_to_10k': '1.000 tot 10.000 records',
                    '10k_to_100k': '10.000 tot 100.000 records',
                    '100k_to_1m': '100.000 tot 1 miljoen records',
                    gt_1m: 'Meer dan 1 miljoen records',
                    unknown: 'Niet bekend',
                },
                tier_source: {
                    assessed_and_approved: 'Beoordeeld en goedgekeurd',
                    manual_legacy: 'Overgenomen uit een oude waarde',
                    legacy_platform_field: 'Overgeërfde platformwaarde',
                    scheme_scoped_only: 'Beoordeeld volgens schemaspecifieke niveaus — geen enkele code dekt meerdere wettelijke niveauschalen',
                },
            },
            page: {
                title: 'Gegevensregister',
                subtitle: 'Persoonlijke gegevenselementen die uw organisatie bewaart, georganiseerd onder Gegevenscategorieën.',
                empty_state: 'Nog geen registeritems. Voeg handmatig een item toe of importeer het uit een wettelijk register.',
                empty_filter_state: 'Er zijn geen artikelen die overeenkomen met uw filters.',
            },
            filter: {
                category: 'Categorie',
                sensitivity: 'Gevoeligheid',
                data_subject: 'Betrokkene',
                status: 'Status',
                data_tier: 'Gegevenslaag',
                cross_border: 'Grensoverschrijdend',
                has_owner: 'Heeft eigenaar',
                has_form_source: 'Geïmporteerd uit formulier',
                search_placeholder: 'Zoeknaam, beschrijving of veld-ID…',
                clear: 'Filters wissen',
                option_all: 'Allemaal',
            },
            col: {
                name: 'Naam',
                category: 'Categorie',
                sensitivity: 'Gevoeligheid',
                data_subjects: 'Betrokkenen',
                systems: 'Systemen',
                activities: 'Activiteiten',
                owner: 'Eigenaar',
                retention: 'Retentie',
                data_tier: 'Gegevenslaag',
                status: 'Status',
                last_reviewed: 'Laatst beoordeeld',
                actions: 'Acties',
            },
            action: {
                add_manual: 'Artikel toevoegen',
                add_from_form: 'Toevoegen vanuit regelgevingsregister',
                import_history: 'Importeer eerdere inzendingen',
                export: 'Exporteren',
                archive: 'Archief',
                restore: 'Herstellen',
                edit: 'Bewerken',
                assign_owner: 'Eigenaar toewijzen',
                attach_evidence: 'Bewijsmateriaal bijvoegen',
                open_lineage: 'Open afstamming',
            },
            modal_editor: {
                section_identity: 'Identiteit',
                section_classification: 'Classificatie',
                section_subjects: 'Onderwerpen & volume',
                section_location: 'Locatie en overdracht',
                section_systems: 'Systemen & Activiteiten',
                section_retention: 'Bewaring en juridisch',
                section_governance: 'Bestuur',
                section_notes: 'Opmerkingen',
                hint_inherited: '(overgenomen van categorie)',
                hint_overridden: '(overschreven)',
                placeholder_field_identifiers: 'bijvoorbeeld klanten.primary_email, hris.employee_id',
                validation_name_required: 'Naam is vereist',
                validation_category_required: 'Categorie is vereist',
                validation_data_subjects_required: 'Er is ten minste één type betrokkene vereist',
                label_data_type: 'Gegevenstype',
                label_field_identifiers: "Veld-ID's (door komma's gescheiden)",
                label_data_subject_types: 'Typen betrokkenen',
                label_volume_band: 'Volumeband',
                label_storage_locations: "Opslaglocaties (door komma's gescheiden)",
                placeholder_storage_locations: 'ISO-3166 codes of vrije tekst (bijv. VN, Hetzner EU)',
                label_cross_border: 'Grensoverschrijdende overdracht',
                label_source_systems: 'Bronsystemen',
                label_processing_activities: 'Verwerkingsactiviteiten',
                label_collection_method: 'Verzamelmethode',
                label_purposes: 'Doeleinden',
                hint_multi_select: 'Houd Cmd/Ctrl ingedrukt om meerdere te selecteren.',
                option_inherit_category: '— (erven van categorie)',
                option_dash: '—',
                // fix-sprint-20260925-2123 (Finding 11) — a stale save refused with 409.
                error_conflict: 'This registry item was changed by someone else while you were editing, so your change was not saved. Close this form and open the item again to see the current version.',
                // The form was re-read and now shows the saved version.
                error_conflict_refreshed: 'This registry item was changed by someone else while you were editing, so your change was not saved. The form now shows the current version, and your unsaved entries were not kept. Make your change again and save.',
            },
            modal_import: {
                scan_none: 'No mapped regulatory-register templates are registered yet.',
                scan_count: 'Scans {count} template(s) for historical submissions and imports items.',
                dry_run_heading: 'Voorbeeld importeren',
                label_to_create: 'Om te creëren',
                label_to_update: 'Om te updaten',
                label_skipped_archived: 'Overgeslagen (gearchiveerd)',
                label_conflicts: 'Conflicten',
                commit: 'Importeren',
                cancel: 'Annuleer',
                conflict_new_item: 'New item',
                conflict_value_own_words: 'a ground recorded in a person’s own words: “{statement}”',
                conflict_reason: 'Reason: {reason}',
                conflict_row_kept_edit: '{field} — the value you edited was kept: {current}. The form’s value was not applied: {mapper}.',
                conflict_row_kept_current: '{field} — the current value was kept: {current}. The form’s value was refused: {mapper}.',
                conflict_row_not_created: '{field} — the item was not created. The form’s value was refused: {mapper}.',
                conflict_reasons: {
                    field_user_edited: 'A person edited this field after an earlier import, and an import never overwrites a person’s edit.',
                    basis_not_declared: 'No installed framework declares a lawful basis, so a coded basis cannot be recorded.',
                    basis_required: 'This record needs a lawful basis, and the form gave none.',
                    basis_needs_person: 'Only an authenticated person may record or change this lawful basis, and the import did not run as one.',
                    basis_statement_rules: 'A lawful basis is an option your installed frameworks declare, or a ground in your own words with the article it rests on; the form’s value is neither.',
                    unknown: 'The import did not apply this value.',
                },
                conflict_fields: {
                    description: 'Description',
                    field_identifiers: 'Field identifiers',
                    data_type: 'Data type',
                    sensitivity_override: 'Sensitivity (override)',
                    data_subject_types: 'Data subject types',
                    volume_band: 'Volume band',
                    source_system_ids: 'Source systems',
                    processing_activity_ids: 'Processing activities',
                    retention_policy_id: 'Retention policy',
                    purposes: 'Purposes',
                    collection_method: 'Collection method',
                    legal_basis: 'Lawful basis',
                    data_tier: 'Data tier',
                    storage_locations: 'Storage locations',
                    cross_border: 'Cross-border transfer',
                },
            },
            drawer: {
                section_category: 'Categorie',
                section_systems: 'Bronsystemen',
                section_activities: 'Verwerkingsactiviteiten',
                section_flows: 'Gegevensstromen',
                section_retention: 'Effectieve retentie',
                section_governance: 'Bestuur',
                section_evidence: 'Bewijs',
                section_audit: 'Recent audittraject',
                section_source_form: 'Bron formulierinzending',
                badge_inherited: 'Geërfd',
                badge_overridden: 'Overschreven',
            },
            toast: {
                created: 'Registeritem gemaakt.',
                updated: 'Registeritem bijgewerkt.',
                archived: 'Registeritem gearchiveerd.',
                imported: '{count} items geïmporteerd.',
                conflict_warning: '{count} veld(en) hebben uw bewerkingen behouden — zie conflictlijst.',
            },
            // Sprint 62.35.0 (AC6) — English placeholder; locale work beyond EN/VI defers to Phase 25 (R-57).
            evidence: {
                section_title: 'Provenance, notice and quality',
                section_intro: 'Evidence the organisation records about this data. These are records, not verdicts: an empty field means not recorded.',
                not_recorded: 'Not recorded',
                group_provenance: 'Provenance and authorisation',
                group_notice: 'Notice',
                group_quality: 'Authenticity and quality',
                label_source_description: 'Source of the data',
                hint_source_description: 'Collected directly, received from whom, or created by whom.',
                label_data_owner: 'Data owner',
                label_authorisation_basis: 'Authorisation relied on',
                label_authorisation_evidence_refs: 'Where the authorisation evidence is held (comma-separated)',
                label_notified_purpose: 'Purpose notified',
                label_notified_scope: 'Scope notified',
                label_notice_reference: 'Notice reference',
                label_authenticity_checked: 'Authenticity checked',
                label_authenticity_method: 'How authenticity was checked, and the result',
                label_quality_checked: 'Quality checked',
                label_quality_method: 'How quality was checked, and the result',
                label_matches_source: 'Collected data matches the source data',
                label_checked_on: 'Checked on',
                hint_checked_on: 'The day the check was made. It cannot be after today.',
                label_checked_by: 'Checked by',
                checked_by_retry_subject: 'the list of people who can be named as the checker',
                error_checked_on_in_future: "The check cannot be dated after today in the organisation's calendar ({date}).",
                error_checked_by_not_found: 'The person named as the checker is not a user of this organisation.',
                label_recorded_by: 'Check recorded by',
                label_recorded_at: 'Check recorded at',
                hint_recorded_stamp: 'Recorded automatically when any check field changes.',
                option_yes: 'Yes',
                option_no: 'No',
                option_not_determined: 'Not determined',
                vocabulary_none: 'No installed framework declares an authorisation vocabulary, so this can only be left not recorded.',
                vocabulary_not_in_force: 'Declared by a framework that is not yet in force (effective {date}).',
                vocabulary_load_failed: 'The authorisation options could not be loaded.',
                value_undeclared: '{value} (not declared by any installed framework)',
            },
        },
        systems: {
            title: 'Systeeminventaris',
            new: 'Nieuw systeem',
            edit: 'Systeem bewerken',
            name: 'Naam',
            owner: 'Eigenaar',
            criticality: 'Kriticiteit',
            dpa_status: 'VVO-status',
            location: 'Locatie',
            system_type: 'Systeemtype',
            metadata: 'Metadata (JSON-indeling)',
            department: 'Afdeling',
            hosting_location: 'Hostinglocatie',
            hosting_provider: 'Hostingprovider',
            data_residency_country: 'Land van gegevensopslag',
            hosting: 'Hostingdienst',
            no_systems: 'Geen systemen gevonden',
            confirm_archive: 'Systeem "{name}" archiveren?',
            count: 'systemen',
            count_one: 'systeem',
            type_internal: 'Intern',
            type_external: 'Extern',
            type_cloud_service: 'Clouddienst',
            type_on_premise: 'On-premise',
            type_hybrid: 'Hybride',
            status_active: 'Actief',
            status_decommissioning: 'Wordt ontmanteld',
            status_decommissioned: 'Ontmanteld',
            status_planned: 'Gepland',
            dpa_not_required: 'Niet vereist',
            dpa_required_missing: 'Vereist ontbreekt',
            dpa_in_progress: 'In behandeling',
            dpa_signed: 'Ondertekend',
            all_criticalities: 'Alle kriticiteiten',
            status_inactive: 'Inactive',
        },
        categories: {
            title: 'Gegevenscategorieën',
            new: 'Nieuwe categorie',
            edit: 'Categorie bewerken',
            name: 'Naam',
            sensitivity: 'Gevoeligheid',
            tags: 'Labels',
            used_in: 'Gebruikt in',
            examples: 'Voorbeelden',
            examples_hint: 'Voorbeelden (kommagescheiden)',
            default: 'Standaard',
            all_sensitivities: 'Alle gevoeligheidsniveaus',
            all_statuses: 'Alle statussen',
            no_categories: 'Geen categorieën gevonden',
            confirm_delete: 'Categorie "{name}" verwijderen?',
            count: 'categorieën',
            count_one: 'categorie',
            status_draft: 'Concept',
            status_proposed: 'Voorgesteld',
            status_under_review: 'In beoordeling',
            status_approved: 'Goedgekeurd',
            status_deprecated: 'Verouderd',
            status_updated: 'Categoriestatus bijgewerkt',
            propose: 'Voorstellen',
            approve: 'Goedkeuren',
            deprecate: 'Verouderen',
            items: 'Registry Items',
        },
        purposes: {
            title: 'Verwerkingsdoeleinden',
            new: 'Nieuw doeleinde',
            edit: 'Doeleinde bewerken',
            name: 'Naam',
            legal_basis: 'Rechtsgrond',
            retention_period: 'Bewaartermijn (dagen)',
            justification: 'Rechtvaardiging gerechtvaardigd belang',
            legal_basis_detail: 'Details rechtsgrond',
            all_legal_bases: 'Alle rechtsgronden',
            no_purposes: 'Geen doeleinden gevonden',
            confirm_delete: '"{name}" verwijderen?',
            default: 'Standaard',
            count: '{count} doeleinden',
            count_one: '{count} doeleinde',
        },
        retention: {
            title: 'Bewaarbeleid',
            new: 'Nieuw beleid',
            edit: 'Beleid bewerken',
            name: 'Naam',
            retention_days: 'Bewaardagen',
            disposal_method: 'Verwijderingsmethode',
            data_categories: 'Gegevenscategorieën',
            activities: 'Activiteiten',
            review_frequency: 'Beoordelingsfrequentie',
            review_frequency_days: 'Beoordelingsfrequentie (dagen)',
            no_policies: 'Geen bewaarbeleid gevonden',
            confirm_delete: '"{name}" verwijderen?',
            count: '{count} beleidsregels',
            count_one: '{count} beleidsregel',
            days: 'dagen',
            disposal_deletion: 'Verwijdering',
            disposal_anonymization: 'Anonimisering',
            disposal_archival: 'Archivering',
            review_overdue: 'Beoordeling achterstallig',
            review_status: 'Beoordelingsstatus',
            review_ok: 'Op schema',
            department: 'Afdeling',
            all_departments: 'Alle afdelingen',
            status_draft: 'Draft',
            status_active: 'Active',
            status_superseded: 'Superseded',
            status_retired: 'Retired',
        },
        flows: {
            view_tabs_aria: 'Stromenweergave',
            view_list: 'Lijst',
            view_map: 'Kaart',
            title: 'Gegevensstromen',
            new: 'Nieuwe stroom',
            edit: 'Stroom bewerken',
            name: 'Naam',
            from_system: 'Van systeem',
            to_system: 'Naar systeem',
            flow_type: 'Stroomtype',
            cross_border: 'Grensoverschrijdend',
            risk_level: 'Risiconiveau',
            mechanism: 'Mechanisme',
            transfer_mechanism: 'Overdrachtsmechanisme',
            frequency: 'Frequentie',
            source_country: 'Bronland',
            destination_country: 'Bestemmingsland',
            transfer_safeguard: 'Overdrachtswaarborg',
            transfer_safeguard_detail: 'Transfer Safeguard Detail',
            all_risk_levels: 'Alle risiconiveaus',
            no_flows: 'Geen gegevensstromen gevonden',
            confirm_archive: '"{name}" archiveren?',
            count: '{count} stromen',
            count_one: '{count} stroom',
            status_active: 'Actief',
            status_inactive: 'Inactief',
            status_under_review: 'In beoordeling',
            mechanism_api: 'API',
            mechanism_file_transfer: 'Bestandsoverdracht',
            mechanism_database_replication: 'Databasereplicatie',
            mechanism_manual: 'Handmatig',
            mechanism_email: 'E-mail',
            mechanism_other: 'Overig',
            frequency_real_time: 'Realtime',
            frequency_hourly: 'Elk uur',
            frequency_daily: 'Dagelijks',
            frequency_weekly: 'Wekelijks',
            frequency_monthly: 'Maandelijks',
            frequency_on_demand: 'Op verzoek',
            frequency_one_time: 'Eenmalig',
            data_categories_label: 'Data Categories',
            purposes_label: 'Purposes',
            safeguard: 'Safeguard',
            section_scope_title: 'Processing scope',
            section_scope_description: 'Data categories and purposes carried by this flow.',
            jurisdiction: 'Jurisdictie',
        },
        activities: {
            title: 'Verwerkingsactiviteiten',
            new: 'Nieuwe activiteit',
            edit: 'Activiteit bewerken',
            activity_title: 'Titel',
            category: 'Categorie',
            legal_basis: 'Rechtsgrond',
            dpa_required: 'DPIA Required',
            ropa_generated: 'VVR gegenereerd',
            version: 'Versie',
            generate_ropa: 'VVR genereren',
            controller: 'Verwerkingsverantwoordelijke',
            controller_name: 'Naam verwerkingsverantwoordelijke',
            controller_contact: 'Contact verwerkingsverantwoordelijke',
            legal_basis_detail: 'Details rechtsgrond',
            organization_info: 'Organization Information',
            org_address: 'Organisatieadres',
            tax_code: 'Belastingcode',
            contact_person: 'Contactpersoon',
            contact_email: 'Contacte-mail',
            contact_phone: 'Contacttelefoon',
            select_entity: 'Selecteer…',
            no_entities: 'Geen artikelen gevonden',
            dpo_name: 'Naam FG',
            dpo_contact: 'Contact FG',
            processor_name: 'Naam verwerker',
            processor_contact: 'Contact verwerker',
            data_subjects: 'Betrokkenen',
            data_subjects_hint: 'Betrokkenen (kommagescheiden)',
            recipients: 'Ontvangers',
            recipients_hint: 'Ontvangers (kommagescheiden)',
            security_measures: 'Beveiligingsmaatregelen',
            dpia_reference: 'GEB-referentie',
            no_activities: 'Geen verwerkingsactiviteiten gevonden',
            confirm_archive: '"{name}" archiveren?',
            count: '{count} activiteiten',
            count_one: '{count} activiteit',
            ropa_success: 'VVR-registratie gegenereerd. Ga naar Exporteren om het te downloaden.',
            ropa_error: 'VVR kon niet worden gegenereerd',
            required_label: 'Vereist',
            status_draft: 'Concept',
            status_active: 'Actief',
            status_under_review: 'In beoordeling',
            status_archived: 'Gearchiveerd',
            decree_165_heading: 'Decreet 165 datalaag (Vietnamese datawet)',
            decree_165_helper: 'Record the data tier and whether the assessed transfer data is personal. Decree 356 Article 42(3) routes impact assessments for core or important personal data through personal-data protection law. Other applicable Data Law duties remain subject to review.',
            personal_data_scope: 'Personal-data scope of assessed transfer data',
            personal_data_scope_personal: 'Confirmed personal data',
            personal_data_scope_non_personal: 'Confirmed non-personal data',
            personal_data_scope_mixed: 'Mixed personal and non-personal data',
            personal_data_scope_unknown: 'Not yet determined',
            personal_data_scope_hint: 'Classify the assessed transfer data and record evidence before determining which impact-assessment route applies. No scope is inferred from the data tier.',
            personal_data_scope_evidence: 'Scope evidence and rationale',
            personal_data_scope_evidence_required: 'Record evidence before confirming personal or non-personal data scope.',
            cross_border_impact_assessment_reference: 'Cross-border impact-assessment reference',
            cross_border_impact_assessment_reference_label: 'Cross-border impact-assessment reference',
            cross_border_impact_assessment_reference_placeholder: 'Reference to the assessment for the applicable legal route',
            scope_review: 'Mixed or undetermined scope requires legal review before an impact-assessment route is selected. Record how each data component is assessed.',
            security_assessment_reference_help: 'Use only where the Decree 165 security-assessment route applies. Core or important personal-data impact assessments follow personal-data protection law under Decree 356 Article 42(3).',
            data_tier_label: 'Gegevenslaag',
            data_tier_help: 'Record regular, important or core data separately from personal-data scope. A tier alone does not determine the impact-assessment route.',
            data_tier_column_header: 'Gegevenslaag',
            data_tier_regular: 'Regelmatig',
            data_tier_important: 'Belangrijk',
            data_tier_core: 'Kern',
            transfer_mechanism_label: 'Mechanisme voor grensoverschrijdende overdracht',
            transfer_mechanism_placeholder: 'e.g. the transfer mechanism your installed frameworks provide, with its article',
            security_assessment_reference_label: 'Referentie voor beveiligingsbeoordeling',
            security_assessment_reference_placeholder: 'bijv. MPS-SA-2026-001',
            data_categories_label: 'Data Categories',
            purposes_label: 'Purposes',
            registry_items_label: 'Specific registry items (optional)',
            registry_items_hint: 'Most activities leave this empty — data categories already cover RoPA. Set it when an inspection asks which exact fields an activity touches.',
            registry_items_pending: 'Loading the registry items currently linked to this activity. Saving before they load will leave those links unchanged.',
            registry_items_unavailable: 'The registry items currently linked to this activity could not be loaded. Saving will leave those links unchanged.',
            registry_items_unlisted_option: 'Linked item {id} (not in the list)',
            retention_period_description: 'Retention Period Description',
        },
        evidence: {
            title: 'Bewijskluis',
            new: 'Nieuw bewijs',
            edit: 'Bewijs bewerken',
            name: 'Naam',
            evidence_type: 'Type bewijs',
            linked_entities: 'Gekoppelde entiteiten',
            uploaded: 'Geüpload',
            expiry: 'Vervaldatum',
            file_url: 'Bestands-URL',
            source_mode_label: 'Bewijsbron',
            source_mode_file: 'Bestand uploaden',
            source_mode_url: 'Externe URL koppelen',
            attachment_label: 'Bijlage',
            link_to: 'Bewijs koppelen aan',
            entity_type: 'Entiteitstype',
            entity_id: 'Entiteits-ID',
            no_evidence: 'Geen bewijsitems gevonden',
            manage_links: 'Beheer koppelingen',
            unlink: 'Ontkoppelen',
            confirm_delete: 'Bewijs "{name}" verwijderen?',
            count: '{count} artikelen',
            count_one: '{count} onderdeel',
            type_document: 'Documentbestand',
            type_screenshot: 'Schermafbeelding',
            type_policy: 'Beleid',
            type_contract: 'Overeenkomst',
            type_assessment: 'Beoordeling',
            type_correspondence: 'Correspondentie',
            type_other: 'Overig',
            linked_type_system: 'Systeem',
            linked_type_data_flow: 'Gegevensstroom',
            linked_type_processing_activity: 'Verwerkingsactiviteit',
            linked_type_data_category: 'Gegevenscategorie',
            linked_type_purpose: 'Doel',
            linked_type_retention_policy: 'Bewaarbeleid',
            link_entity: 'Link naar entiteit',
            link_success: 'Entiteit is succesvol gekoppeld',
            confirm_unlink: 'Weet u zeker dat u deze entiteit wilt ontkoppelen?',
            unlink_success: 'Entiteit is ontkoppeld',
            no_linked_entities: 'Geen gekoppelde entiteiten',
            select_entity: 'Selecteer…',
            no_entities: 'Geen artikelen',
        },
        exports: {
            title: 'Gegevensexports',
            recent: 'Recente exports',
            export_type: 'Exporttype',
            format: 'Formaat',
            requested_by: 'Aangevraagd door',
            requested_at: 'Aangevraagd op',
            download: 'Downloaden',
            csv_export: 'CSV-export',
            csv_description: 'Gegevens downloaden in CSV-formaat voor spreadsheetanalyse.',
            pdf_reports: 'PDF-rapporten',
            pdf_description: 'Opgemaakte rapporten genereren voor nalevingsdocumentatie.',
            csv_systems: 'Systeeminventaris',
            csv_categories: 'Gegevenscategorieën',
            csv_flows: 'Gegevensstromen',
            csv_activities: 'Verwerkingsactiviteiten',
            csv_cross_border: 'Grensoverschrijdende stromen',
            csv_retention_policies: 'Bewaarbeleid',
            csv_evidence: 'Bewijsstukken',
            csv_governance_assignments: 'Bestuursopdrachten',
            pdf_ropa: 'VVR-register',
            pdf_systems: 'Systeemrapport',
            pdf_cross_border: 'Grensoverschrijdend rapport',
            pdf_flow_map: 'Stroomkaartgegevens',
            export_failed: 'Export mislukt',
            pdf_generated: 'PDF succesvol gegenereerd.',
            ropa: 'RoPA-register',
            retention_policies: 'Bewaarbeleid',
            governance_assignments: 'Bestuursopdrachten',
            col_entity: 'Entiteit exporteren',
            col_description: 'Beschrijving',
            desc_ropa: 'Volledig ROPA-register van alle verwerkingsactiviteiten met wettelijke basis, DPIA-status en controles.',
            desc_systems: 'Systeeminventarisatie met eigenaar, gegevenscategorietags en nalevingsstatus.',
            desc_activities: 'Individuele verwerkingsactiviteiten met verwerkingsverantwoordelijke, wettelijke basis en DPIA-classificatie.',
            desc_categories: 'Gegevenscategorieën en gevoeligheidsniveaus die worden gebruikt bij classificatie en audits.',
            desc_purposes: 'Verwerkingsdoeleinden met de voor elk doel vastgelegde rechtsgrondslag.',
            desc_flows: 'Gegevensstromen tussen systemen, inclusief interne en grensoverschrijdende routes.',
            desc_cross_border: 'Alleen grensoverschrijdende stromen, met overdrachtsmechanisme en risicobeoordeling.',
            desc_retention_policies: 'Bewaarbeleid, verwijderingsmethoden en beoordelingsfrequentie.',
            desc_evidence: 'Bewijsstukken verbonden aan verwerkingsactiviteiten: certificaten, DPA-contracten, DPIA rapporten.',
            desc_governance_assignments: 'Governance-opdrachten: eigenaren, goedkeurders en ISO 27001-referenties.',
            unified_heading: 'Entiteiten exporteren',
            unified_description: 'Exporteer een gegevenstoewijzingsentiteit in de indeling CSV, XLSX, PDF of DOCX. Alle exports worden opgeslagen in de bestandsbrowser.',
            export_button_label: 'Exporteren',
            csv_download_success: 'CSV succesvol gedownload.',
            col_action: 'Export',
        },
        attestation: {
            title: 'Attestatiecycli',
            create: 'Nieuwe cyclus',
            name: 'Cyclusnaam',
            year: 'Jaar',
            deadline: 'Deadline',
            scope: 'Bereik',
            status: 'Status',
            tasks: 'Afdelingstaken',
            assign_tasks: 'Taken toewijzen',
            department: 'Afdeling',
            assignee: 'Toegewezene',
            progress: 'Voortgang',
            stale_entries: 'Verouderde items',
            detect_stale: 'Verouderde detecteren',
            export_bundle: 'Pakket exporteren',
            no_cycles: 'Nog geen attestatiecycli.',
            create_success: 'Attestatiecyclus aangemaakt.',
            assign_success: 'Taken toegewezen.',
            complete_success: 'Taak voltooid.',
            stale_detected: 'Verouderde items gedetecteerd.',
            stale_systems: 'Verouderde systemen',
            stale_flows: 'Verouderde stromen',
            no_stale: 'Geen verouderde items gedetecteerd.',
            stale_name: 'Naam',
            stale_announcement: '{0} verouderde systemen en {1} verouderde stromen gevonden',
            last_updated: 'Laatst bijgewerkt',
            status_draft: 'Concept',
            status_active: 'Actief',
            status_completed: 'Voltooid',
            task_status_pending: 'In afwachting',
            task_status_in_progress: 'Bezig',
            task_status_completed: 'Voltooid',
            scope_systems: 'Systemen',
            scope_flows: 'Gegevensstromen',
            scope_activities: 'Activiteiten',
            scope_categories: 'Categorieën',
            scope_purposes: 'Doeleinden',
            scope_retention: 'Bewaring',
            complete_cycle: 'Complete Cycle',
            confirm_activate: 'Are you sure you want to activate this cycle?',
            confirm_complete: 'Are you sure you want to complete this cycle? This action cannot be undone.',
            cycle_complete_success: 'Cycle completed successfully',
            task_status_verified: 'Verified',
            activate_success: 'Cycle activated successfully',
            activate: 'Activate Cycle',
        },
        governance: {
            title: 'Governance-toewijzingen',
            assign: 'Eigenaarschap toewijzen',
            entity_type: 'Entiteittype',
            entity: 'Entiteit',
            role: 'Rol',
            assigned_by: 'Toegewezen door',
            assigned_at: 'Toegewezen op',
            remove: 'Verwijderen',
            no_assignments: 'Geen governance-toewijzingen.',
            assign_success: 'Eigenaarschap toegewezen.',
            remove_success: 'Toewijzing verwijderd.',
            assignee: 'Toegewezene',
            department: 'Afdeling',
            all_departments: 'Alle afdelingen',
            roles: {
                data_owner: 'Data-eigenaar',
                data_steward: 'Databeheerder',
                data_custodian: 'Databewaarder',
                informed: 'Geïnformeerd',
            },
            entity_types: {
                system: 'Systeem',
                flow: 'Gegevensstroom',
                activity: 'Activiteit',
                category: 'Categorie',
                purpose: 'Doeleinde',
                retention_policy: 'Bewaarbeleid',
            },
        },
        risk_assessment: {
            title: 'Risicobeoordeling',
            create: 'Nieuwe beoordeling',
            safeguard_type: 'Waarborgstype',
            adequacy_status: 'Toereikendheidsstatus',
            risk_factors: 'Risicofactoren',
            mitigations: 'Maatregelen',
            reviewer: 'Beoordelaar',
            decision: 'Beslissing',
            approve: 'Goedkeuren',
            reject: 'Afwijzen',
            no_assessments: 'Geen risicobeoordelingen.',
            create_success: 'Risicobeoordeling aangemaakt.',
            approve_success: 'Beoordeling goedgekeurd.',
            reject_success: 'Beoordeling afgewezen.',
            one_per_line: 'Eén per regel',
            adequacy_adequate: 'Toereikend',
            adequacy_inadequate: 'Ontoereikend',
            adequacy_under_review: 'In beoordeling',
            adequacy_not_assessed: 'Niet beoordeeld',
            decision_approved: 'Goedgekeurd',
            decision_rejected: 'Afgewezen',
        },
        review: {
            title: 'Beoordelingstaken',
            description: 'Beoordelingstaken worden automatisch aangemaakt wanneer een gegevenscategorie wordt voorgesteld. Ga naar Gegevenscategorieën en klik op Voorstellen bij een conceptcategorie om een nieuwe taak te maken.',
            count: 'beoordelingstaken',
            summary: 'Beoordelingsoverzicht',
            pending: 'In afwachting',
            resolved_today: 'Vandaag opgelost',
            escalated: 'Geëscaleerd',
            overdue: 'Achterstallig (>5 dagen)',
            all_statuses: 'Alle statussen',
            status_pending: 'In afwachting',
            status_resolved: 'Opgelost',
            status_escalated: 'Geëscaleerd',
            category_name: 'Categorie',
            system_name: 'Systeem',
            owner: 'Eigenaar',
            resolve: 'Oplossen',
            resolve_task: 'Beoordelingstaak oplossen',
            resolution: 'Oplossing',
            resolution_approve: 'Goedkeuren',
            resolution_modify: 'Wijzigen en goedkeuren',
            resolution_reject: 'Afwijzen',
            resolution_escalate: 'Escaleren',
            notes: 'Oplossingsnotities',
            notes_required: 'Notities vereist (minimaal 10 tekens) voor Wijzigen, Afwijzen of Escaleren',
            resolved_success: 'Beoordelingstaak opgelost',
            bulk_approve: 'Groepsgoedkeuring',
            bulk_reject: 'Groepsafwijzing',
            bulk_success: '{count} taken verwerkt',
            no_tasks: 'Geen beoordelingstaken gevonden',
        },
        inventory: {
            title: 'Classificatie-inventaris',
            count: 'systemen',
            summary: 'Inventarisoverzicht',
            total_systems: 'Totaal systemen',
            total_labels: 'Totaal labels',
            system_name: 'Systeem',
            label_count: 'Etiketten',
            approved_count: 'Goedgekeurd',
            pending_count: 'In afwachting',
            no_items: 'Geen inventarisitems gevonden',
        },
        exceptions: {
            title: 'Classificatie-uitzonderingen',
            count: 'uitzonderingen',
            all_severities: 'Alle ernstniveaus',
            all_statuses: 'Alle statussen',
            system_name: 'Systeem',
            severity_label: 'Ernst',
            status_open: 'Openen',
            status_resolved: 'Opgelost',
            status_accepted_risk: 'Geaccepteerd risico',
            resolve: 'Oplossen',
            resolve_exception: 'Uitzondering oplossen',
            resolution_type: 'Oplossingstype',
            notes: 'Notities',
            justification_required: 'Rechtvaardiging vereist (minimaal 10 tekens) voor geaccepteerd risico',
            resolved_success: 'Uitzondering opgelost',
            no_exceptions: 'Geen uitzonderingen gevonden',
        },
        history: {
            title: 'Classificatiegeschiedenis',
            count: 'gebeurtenissen',
            actor: 'Acteur',
            action: 'Actie',
            entity_type: 'Entiteitstype',
            changes: 'Wijzigingen',
            view_lineage: 'Herkomst bekijken',
            lineage_title: 'Classificatieherkomst',
            no_events: 'Geen geschiedenisgebeurtenissen gevonden',
            no_lineage: 'Geen herkomstgegevens beschikbaar',
            modified: 'Gewijzigd',
            created: 'Aangemaakt',
            removed: 'Verwijderd',
            action_created: 'Gemaakt',
            action_updated: 'Bijgewerkt',
            action_archived: 'Gearchiveerd',
            action_approved: 'Goedgekeurd',
            action_deprecated: 'Verouderd',
            entity_label: 'Etiket',
            entity_taxonomy: 'Taxonomie',
            entity_review_task: 'Beoordelingstaak',
            entity_exception: 'Uitzondering',
            before: 'Voor',
            after: 'Na',
        },
    },
    governance: {
        audit_trail_title: 'Audit-spoor voor governance',
        audit_trail_subtitle: 'Cross-entiteits auditstroom voor governance-acties — goedkeuringen, playbook-uitvoeringen, bewijskoppelingen en meer.',
        audit_trail_item_count: 'auditgebeurtenissen',
        audit_trail_fetch_error: 'Kan auditgebeurtenissen niet ophalen.',
        audit_trail_no_events: 'Geen auditgebeurtenissen gevonden voor de huidige filters.',
        audit_trail_entity_type: 'Entiteitstype',
        audit_trail_entity_id: 'Entiteits-ID',
        audit_trail_action: 'Actie',
        audit_trail_actor: 'Uitvoerder',
        audit_trail_timestamp: 'Tijdstempel',
        audit_trail_date_from: 'Van',
        audit_trail_date_to: 'Tot',
        audit_trail_no_rows_to_export: 'Geen rijen om te exporteren.',
        // Gate E12.5 remediation A (Sprint 63.8.2) — the audit trail filter options.
        audit_trail_entity_types: {
            vendor: 'Vendor',
            vendor_subprocessor: 'Sub-processor',
            transfer_mechanism: 'Transfer mechanism',
            drift_event: 'Drift event',
            playbook: 'Playbook',
            execution: 'Execution',
            approval: 'Approval',
        },
        audit_trail_actions: {
            created: 'Created',
            updated: 'Updated',
            status_changed: 'Status changed',
            assigned: 'Assigned',
            approved: 'Approved',
            rejected: 'Rejected',
            evidence_linked: 'Evidence linked',
            step_completed: 'Step completed',
            executed: 'Executed',
            terminated: 'Terminated',
            exported: 'Exported',
        },
        nav: {
            vendors: 'Leveranciers',
            subprocessors: 'Subverwerkers',
            cross_border: 'Grensoverschrijdend',
            drift: 'Driftmonitor',
            scores: 'Nalevingsscore',
            playbooks: 'Draaiboeken',
            approvals: 'Goedkeuringen',
            audit_trail: 'Auditspoor',
            board_report: 'Bestuursrapport',
            evidence: 'Bewijs',
            onboarding: 'Onboarding-pijplijn',
            dpa_reviews: 'DPA-beoordelingen',
            periodic_reviews: 'Periodieke beoordelingen',
            offboarding: 'Offboarden',
        },
        vendors: {
            data_categories_processed: 'Data categories processed',
            title: 'Leveranciersregister',
            new: 'Nieuwe leverancier',
            edit: 'Leverancier bewerken',
            name: 'Naam',
            jurisdiction: 'Jurisdictie',
            dpa_status: 'VVO-status',
            risk_level: 'Risiconiveau',
            onboarding_status: 'Onboardingstatus',
            contact_email: 'Contact e-mail',
            contact_name: 'Contactnaam',
            contact_phone: 'Contacttelefoon',
            website: 'Webpagina',
            services_csv: 'Diensten (kommagescheiden)',
            country: 'Land',
            country_placeholder: 'bijv. NL',
            last_reviewed: 'Laatst beoordeeld',
            offboard: 'Beëindigen',
            offboard_vendor: 'Leverancier beëindigen',
            offboard_reason: 'Reden voor beëindiging',
            no_vendors: 'Geen leveranciers gevonden',
            vendor_count: '{count} leverancier(s)',
            all_dpa_status: 'Alle VVO-statussen',
            all_risk_levels: 'Alle risiconiveaus',
            all_onboarding: 'Alle onboardingstatussen',
            fetch_error: 'Mislukt. Probeer het opnieuw.',
            save_error: 'Leverancier kon niet worden opgeslagen',
            offboard_error: 'Beëindiging mislukt',
            reach: {
                action: 'Foreign reach',
                title: 'Foreign reach: {name}',
                intro: "An installed framework asks whether this vendor is within its foreign reach. A person records the answer and the reason; the vendor's country and processing locations do not decide it.",
                none_declared: 'No installed framework asks a foreign-reach question.',
                not_determined: 'Not determined',
                within: 'Within reach',
                not_within: 'Not within reach',
                decided: 'Recorded {date}',
                related: 'Provisions that act on a party within it: {list}',
                question: 'Question',
                answer: 'Is this vendor within the reach?',
                rationale: 'Reason',
                citation: 'Citation (optional)',
                save: 'Record determination',
                saved: 'Foreign-reach determination recorded.',
                load_error: 'The foreign-reach questions could not be read.',
                retry: 'Reload',
                retry_sr: 'the foreign-reach questions',
                loading: 'Reading the foreign-reach questions…',
                retry_busy: 'Reloading…',
                stale: 'This vendor was changed by someone else. The latest version has been loaded; review it and record the determination again.',
                stale_unreadable: 'This vendor was changed by someone else, and the latest version could not be read. Close this dialog and open it again before recording.',
                save_error: 'The determination could not be recorded. Try again.',
                answer_required: 'Choose an answer and give a reason.',
                country_context: 'Country on record: {country}',
                read_only: 'Recording a determination needs permission to edit vendors.',
            },
        },
        vendor_types: {
            processor: 'Verwerker',
            sub_processor: 'Subverwerker',
            controller: 'Verwerkingsverantwoordelijke',
            joint_controller: 'Gezamenlijke Verwerkingsverantwoordelijke',
        },
        onboarding_statuses: {
            pending_review: 'Beoordeling in Afwachting',
            approved: 'Goedgekeurd',
            active: 'Actief',
            suspended: 'Geschorst',
            offboarded: 'Beëindigd',
        },
        dpa_statuses: {
            not_started: 'Not Started',
            in_progress: 'In Progress',
            not_required: 'Niet Vereist',
            required_missing: 'Vereist Ontbreekt',
            draft: 'Concept',
            under_review: 'In Beoordeling',
            signed: 'Ondertekend',
            expired: 'Verlopen',
        },
        risk_levels: {
            critical: 'Kritiek',
            high: 'Hoog',
            medium: 'Middel',
            low: 'Laag',
        },
        approval_statuses: {
            pending: 'In Afwachting',
            approved: 'Goedgekeurd',
            rejected: 'Afgewezen',
            expired: 'Verlopen',
        },
        approval_action_types: {
            vendor_onboarding: 'Leveranciersregistratie',
            vendor_offboarding: 'Leveranciersbeëindiging',
            cross_border_acceptance: 'Grensoverschrijdende Goedkeuring',
            playbook_activation: 'Playbook-activering',
            drift_accept_risk: 'Afwijking Risico Geaccepteerd',
            activity_activation: 'Activiteitsactivering',
        },
        drift_check_types: {
            dpa_expiry: 'DPA-vervaldatum',
            dpa_missing: 'DPA Ontbreekt',
            review_overdue: 'Beoordeling Achterstallig',
            cross_border_gap: 'Grensoverschrijdende Lacune',
            sub_processor_chain: 'Subverwerkersketen',
            risk_assessment_overdue: 'Risicobeoordeling Achterstallig',
            retention_policy_missing: 'Bewaarbeleid Ontbreekt',
            evidence_expiring: 'Bewijsmateriaal Verloopt',
        },
        drift_statuses: {
            open: 'Geopend',
            acknowledged: 'Bevestigd',
            in_progress: 'In Behandeling',
            resolved: 'Opgelost',
            accepted_risk: 'Geaccepteerd Risico',
        },
        drift_entity_types: {
            vendor: 'Leverancier',
            system: 'Systeem',
            data_flow: 'Gegevensstroom',
            processing_activity: 'Verwerkingsactiviteit',
            evidence_item: 'Bewijsmateriaal',
        },
        playbook_statuses: {
            draft: 'Concept',
            active: 'Actief',
            archived: 'Gearchiveerd',
        },
        playbook_action_types: {
            delete_data: 'Gegevens Verwijderen',
            verify_deletion: 'Verwijdering Verifiëren',
            notify_vendor: 'Leverancier Informeren',
            confirm_receipt: 'Ontvangst Bevestigen',
            document_evidence: 'Documenteren',
            other: 'Overig',
        },
        playbook_target_types: {
            system: 'Systeem',
            vendor: 'Leverancier',
        },
        dpa_chain_statuses: {
            complete: 'Compleet',
            incomplete: 'Onvolledig',
            unknown: 'Onbekend',
        },
        sub_processor_statuses: {
            active: 'Actief',
            terminated: 'Beëindigd',
        },
        evidence_types: {
            dpa_agreement: 'DPA-overeenkomst',
            risk_assessment: 'Risicobeoordeling',
            audit_report: 'Auditrapport',
            certification: 'Certificering',
            deletion_confirmation: 'Verwijderingsbevestiging',
            training_record: 'Opleidingsregistratie',
            incident_report: 'Incidentrapport',
            document: 'Documentbestand',
            screenshot: 'Schermafbeelding',
            policy: 'Beleid',
            contract: 'Overeenkomst',
            assessment: 'Beoordeling',
            correspondence: 'Correspondentie',
            other: 'Overig',
        },
        linkable_entity_types: {
            vendor: 'Leverancier',
            transfer_mechanism: 'Overdrachtsmechanisme',
            playbook: 'Draaiboek',
            execution: 'Uitvoering',
            system: 'Systeem',
            activity: 'Activiteit',
        },
        subprocessors: {
            title: 'Subverwerkerrelaties',
            new: 'Nieuwe koppeling',
            vendor: 'Leverancier',
            subprocessor: 'Subverwerker',
            relationship_type: 'Type relatie',
            countries: 'Landen',
            countries_csv: 'Landen (kommagescheiden)',
            dpa_chain: 'DPA-keten',
            dpa_chain_status: 'DPA-ketenstatus',
            no_processors: 'Geen verwerkers gevonden.',
            no_subprocessors: 'Geen subverwerkers gevonden.',
            processor_vendor: 'Verwerkerleverancier',
            select_processor: 'Verwerker selecteren',
            select_vendor: 'Leverancier selecteren',
            services: 'Diensten',
            sub_processor_vendor_id: 'Subverwerker-ID',
            terminate: 'Beëindigen',
            confirm_terminate: 'Weet u zeker dat u deze relatie wilt beëindigen?',
            create_error: 'Koppeling kon niet worden aangemaakt.',
            fetch_error: 'Subverwerkers konden niet worden opgehaald.',
            processor: 'Verwerker',
            services_csv: 'Diensten (kommagescheiden)',
            terminate_error: 'Beëindiging mislukt.',
            version_history_heading: 'Wijziging van versiebeheer door subverwerker',
            change_tracking_note: 'Voor alle wijzigingen in de relatie tussen subverwerkers geldt een versiebeheer. Elke wijziging registreert de voor/na-status voor een audittrail.',
            history_retention_note: 'De wijzigingsgeschiedenis wordt bewaard gedurende de volledige bewaartermijn van gegevens. Bekijk wijzigingslogboeken via de detailweergave van de leverancier.',
            vendor_risk_cross_view_heading: 'Subverwerkersregister (Vendor Risk)',
            vendor_risk_cross_view_source: 'Bron: Vendor Risk Register',
            vendor_risk_cross_view_empty: 'Geen vermeldingen in het Vendor Risk-register.',
            vendor_risk_cross_view_load_error: 'The Vendor Risk sub-processor register could not be read.',
            partial_load: 'The sub-processors of {count} processor(s) could not be read, so the register below is incomplete.',
        },
        cross_border: {
            title: 'Grensoverschrijdend governancerapport',
            flows: 'Grensoverschrijdende stromen',
            transfers: 'Grensoverschrijdende doorgiften',
            detected_gaps: 'Gedetecteerde lacunes',
            safeguards: 'Waarborgen',
            cross_border_vendors: 'Grensoverschrijdende leveranciers',
            destination: 'Bestemming',
            no_cross_border_vendors: 'Geen grensoverschrijdende leveranciers gevonden.',
            no_safeguard: 'Geen waarborg',
            gap_needs_redetermination: 'Recorded mechanism needs re-determining',
            gap_unverified: 'Recorded mechanism not yet verified',
            gap_recorded_mechanism: 'Recorded: {mechanism}',
            redetermine_mechanism: 'Re-determine',
            redetermine_mechanism_title: "Re-determine the vendor's transfer mechanism",
            redetermine_mechanism_intro: '{vendor} records the transfer mechanism "{mechanism}" for {source} to {destination}, and no installed framework declares it as it stands. Choose a mechanism an installed framework declares. Only the mechanism is saved.',
            redetermine_choose_first: 'Choose a transfer mechanism first.',
            redetermine_success: 'Transfer mechanism re-determined.',
            redetermine_error: 'Could not re-determine the transfer mechanism. Please try again.',
            processing_locations: 'Verwerkingslocaties',
            export_error: 'Rapport kon niet worden geëxporteerd.',
            export_pdf: 'PDF exporteren',
            export_success: 'Rapport succesvol geëxporteerd.',
            exporting: 'Bezig met exporteren...',
            fetch_error: 'Grensoverschrijdende gegevens konden niet worden opgehaald.',
            source: 'Bron',
            pdpl_p2_heading: 'PDPL-P2 Naleving van grensoverschrijdende overdrachten',
            dossier_prep_note: 'Dossiervoorbereiding: verzamel effectbeoordeling, rechtsgrondslag en waarborgdocumentatie vóór indiening.',
            filing_tracking_note: 'Volgen van indieningen: volg de indieningsstatus bij de Autoriteit (PDPC) vanaf de eerste aanvraag tot en met de goedkeuring.',
            supplement_loop_note: 'Aanvullingscyclus: reageer op informatieverzoeken van de autoriteiten en dien indien nodig gewijzigde dossiers opnieuw in.',
            official_forms_note: 'Official forms: choose the official form this filing uses in the Form type selector when you create it.',
            vendor_search_label: 'Leverancier zoeken',
            vendor_search_placeholder: 'Zoek op leveranciersnaam…',
            jurisdiction_filter_label: 'Rechtsgebied',
            filings_section_heading: 'Grensoverschrijdende indieningen',
            filings_empty: 'Nog geen dossiers. Maak er een om een ​​dossier te starten.',
            filings_create_button: 'Nieuwe aangifte',
            filings_table_id: 'Indienings-ID',
            filings_table_jurisdiction: 'Jurisdictie',
            filings_table_type: 'Formuliertype',
            filings_table_status: 'Status',
            filings_table_updated: 'Bijgewerkt',
            filings_table_open: 'Openen',
            filings_detail_title: 'Grensoverschrijdende indiening',
            filings_back_to_list: 'Terug naar grensoverschrijdend',
            filings_status_heading: 'Levenscyclus',
            filings_evidence_heading: 'Bewijs',
            filings_history_heading: 'Audittrail',
            filings_field_filing_id: 'Indienings-ID',
            filings_field_jurisdiction: 'Jurisdictie',
            filings_field_type: 'Formuliertype',
            filings_field_status: 'Status',
            filings_field_reference: 'Referentie autoriteit',
            filings_field_mps_receipt: 'MPS ontvangstbewijs',
            filings_field_submitted_at: 'Ingediend op',
            filings_field_acknowledged_at: 'Erkend bij',
            filings_field_archived_at: 'Gearchiveerd op',
            filings_field_version: 'Versie',
            filings_field_notes: 'Opmerkingen',
            filings_action_advance: 'Vooruitgangsstatus',
            filings_action_reject: 'Afwijzen',
            filings_action_archive: 'Archief',
            filings_action_submit: 'Markeer ingediend',
            filings_action_acknowledge: 'Mark erkende het',
            filings_action_resume_draft: 'CV als concept',
            filings_status_draft: 'Diepgang',
            filings_status_assessed: 'Beoordeeld',
            filings_status_pending_review: 'In afwachting van beoordeling',
            filings_status_ready_to_file: 'Klaar om te archiveren',
            filings_status_submitted: 'Ingezonden',
            filings_status_acknowledged: 'Erkend',
            filings_status_rejected: 'Afgewezen',
            filings_status_archived: 'Gearchiveerd',
            filings_type_none_installed: 'No fileable form is installed for this organisation',
            filings_new_dialog_title: 'Nieuwe grensoverschrijdende indiening',
            filings_new_form_jurisdiction: 'Jurisdictie',
            filings_new_form_type: 'Formuliertype',
            filings_new_form_notes: 'Opmerkingen (optioneel)',
            filings_new_submit: 'Creëer',
            filings_new_cancel: 'Annuleer',
            filings_reason_label: 'Reden',
            filings_reference_label: 'Referentienummer van de autoriteit',
            filings_mps_receipt_label: 'MPS ontvangstreferentie',
            filings_create_error: 'Kan archief niet maken.',
            filings_create_success: 'Archief aangemaakt.',
            filings_update_error: 'Kan de archivering niet bijwerken.',
            filings_update_success: 'Bestand bijgewerkt.',
            filings_fetch_error: 'Kan de dossiers niet ophalen.',
            filings_archive_confirm: 'Deze aangifte archiveren? Dit kan niet ongedaan worden gemaakt.',
            filings_status_rejected_detour: 'De indiening werd na beoordeling afgewezen. Hervat als concept om de stroom opnieuw te starten.',
            recent_exports_heading: 'Recente exporten',
            recent_exports_empty: "Nog geen export. Klik op 'Exporteren PDF' om er een te genereren.",
            filings_forms_empty: 'No regulatory forms generated yet. Use the Forms module to generate the required outputs for this filing.',
            filings_forms_heading: 'Regulatory Forms',
            filings_type_placeholder: 'Select a form type…',
        },
        drift: {
            title: 'Configuratiedriftmonitor',
            drift_type: 'Type drift',
            severity: 'Ernst',
            detected_at: 'Gedetecteerd op',
            resolve: 'Oplossen',
            all_check_types: 'Alle controletypen',
            all_entity_types: 'Alle entiteitstypen',
            event_count: 'Aantal gebeurtenissen',
            accept_risk: 'Risico accepteren',
            acknowledge: 'Bevestigen',
            assign_error: 'Toewijzing mislukt.',
            assign_event: 'Toewijzen',
            assigned: 'Toegewezen',
            enter_resolution_notes: 'Oplossingsnotities invoeren...',
            enter_user_id: 'Gebruikers-ID invoeren...',
            entity: 'Entiteit',
            fetch_error: 'Driftgebeurtenissen konden niet worden opgehaald.',
            no_events: 'Geen driftgebeurtenissen gevonden.',
            resolution_notes: 'Oplossingsnotities',
            update_status_error: 'Status kon niet worden bijgewerkt.',
        },
        scores: {
            title: 'Nalevingsscoreoverzicht',
            overall: 'Totale nalevingsscore',
            breakdown: 'Score-uitsplitsing per module',
            trend: 'Historische trend',
            module: 'Onderdeel',
            score: 'Scoren',
            grade: 'Beoordeling',
            calculated_at: 'Berekend op',
            generate_board_report: 'Bestuursrapport genereren',
            lang_english: 'Engels',
            lang_vietnamese: 'Vietnamees',
            fetch_error: 'Nalevingsscores konden niet worden opgehaald.',
            generate_report: 'Rapport genereren',
            generating: 'Bezig met genereren...',
            recommendations: 'Aanbevelingen',
            report_error: 'Rapport kon niet worden gegenereerd.',
            factor_owner_assigned: 'Eigenaar toegewezen',
            factor_data_categories_linked: 'Gegevenscategorieën gekoppeld',
            factor_purposes_linked: 'Doeleinden gekoppeld',
            factor_data_flows_documented: 'Gegevensstromen gedocumenteerd',
            factor_processing_activity_linked: 'Verwerkingsactiviteit gekoppeld',
            factor_dpa_status: 'DPA-status',
            factor_retention_policy_linked: 'Bewaarbeleid gekoppeld',
            factor_last_reviewed: 'Laatst beoordeeld binnen drempel',
            factor_risk_assessment_current: 'Risicobeoordeling actueel',
            factor_sub_processors_documented: 'Subverwerkers gedocumenteerd',
            factor_cross_border_safeguards: 'Grensoverschrijdende waarborgen',
            factor_contact_info_complete: 'Contactgegevens volledig',
            factor_onboarding_approved: 'Onboarding goedgekeurd',
            factor_avg_system_score: 'Gemiddelde systeemscore',
            factor_avg_vendor_score: 'Gemiddelde leveranciersscore',
            factor_processing_activity_coverage: 'Dekking van verwerkingsactiviteiten',
            factor_ropa_completeness: 'RoPA-volledigheid',
            factor_open_drift_events: 'Open afwijkingsgebeurtenissen',
        },
        playbooks: {
            title: 'Verwijderingsdraaiboeken',
            new: 'Nieuw draaiboek',
            edit: 'Draaiboek bewerken',
            steps: 'Stappen',
            add_step: 'Stap toevoegen',
            remove_step: 'Stap verwijderen',
            data_categories_csv: 'Gegevenscategorieën (kommagescheiden)',
            estimated_duration: 'Geschatte duur',
            estimated_duration_label: 'Geschatte duur',
            evidence_description: 'Bewijsbeschrijving',
            no_playbooks: 'Geen draaiboeken gevonden.',
            playbook_count: 'Aantal draaiboeken',
            requires_evidence: 'Bewijs vereist',
            step_label: 'Staplabel',
            target_name: 'Doelnaam',
            target_type: 'Doeltype',
            action_type: 'Actietype',
            archive_error: 'Archivering mislukt.',
            confirm_archive: 'Weet u zeker dat u dit draaiboek wilt archiveren?',
            data_categories: 'Gegevenscategorieën',
            days: 'dagen',
            fetch_error: 'Draaiboeken konden niet worden opgehaald.',
            sla_days: 'SLA (dagen)',
            target_id: 'Doel-ID',
            template_class_heading: 'Sjabloonclassificatie',
            official: 'Officieel',
            internal: 'Intern',
            classification: 'Classificatie',
            official_note: 'Officieel: door de regelgeving verplichte sjablonen die zijn afgestemd op PDPL, GDPR of sectorspecifieke vereisten.',
            internal_note: 'Intern: organisatiespecifieke procedures die niet zijn afgeleid van regelgevingssjablonen.',
            active_executions_heading: 'Actieve uitvoeringen',
            no_active_executions: 'Geen actieve uitvoeringen.',
            playbook_label: 'Draaiboek',
            ticket_id_label: 'Kaartje',
            progress_label: 'Voortgang',
            actions_label: 'Acties',
            view_steps_label: 'Stappen bekijken',
            execution_detail_title: 'Uitvoeringsdetail',
            playbook_missing: 'Draaiboek niet gevonden.',
            evidence_required_label: 'Bewijs vereist',
            upload_evidence_label: 'Bewijs uploaden',
            mark_complete_label: 'Markeren als voltooid',
            evidence_required_toast: 'Bewijs vereist voordat deze stap kan worden voltooid.',
            evidence_blocking_message: 'Voeg een bewijsbestand toe om door te gaan.',
            step_completed_toast: 'Stap voltooid.',
            executions_fetch_error: 'Uitvoeringen laden mislukt.',
            step_evidence_default_name: 'Bewijs voor uitvoeringsstap',
            attached_evidence_label: 'Bijgevoegde bewijzen',
        },
        approvals: {
            // Sprint 63.30.0 (#485) — approval expiry.
            expiry_setting_heading: 'Approval expiry',
            expiry_setting_label: 'Approval requests expire after (days)',
            expiry_setting_hint: 'Whole days from {min} to {max}. Applies to requests made after the change. A request still undecided at its expiry is marked Expired and its requester is told.',
            expiry_setting_value: 'Approval requests expire after {days} days.',
            expiry_setting_save: 'Save expiry',
            expiry_setting_saved: 'Approval expiry set to {days} days',
            expiry_setting_load_failed: 'The approval expiry setting could not be read.',
            expiry_setting_save_failed: 'The approval expiry setting could not be saved.',
            expires_on: 'Expires {date}',
            expired_undecided_note: 'Past its expiry; it can no longer be decided.',
            already_decided: 'This request has already been decided. The list has been refreshed to show its decision.',
            expired_refused: 'This request is past its expiry and can no longer be decided. The list has been refreshed.',
            title: 'Goedkeuringsworkflows',
            new: 'Nieuwe goedkeuring',
            edit: 'Goedkeuring bewerken',
            request_type: 'Type verzoek',
            requester: 'Verzoeker',
            approve: 'Goedkeuren',
            reject: 'Afwijzen',
            justification: 'Rechtvaardiging',
            all_action_types: 'Alle actietypen',
            decided_by: 'Besloten door',
            no_approvals: 'Geen goedkeuringen gevonden.',
            approval_count: 'Aantal goedkeuringen',
            approval_notes_optional: 'Opmerkingen (optioneel)',
            approve_error: 'Goedkeuring mislukt.',
            approve_request: 'Verzoek goedkeuren',
            decision: 'Besluit',
            entity: 'Entiteit',
            fetch_error: 'Goedkeuringen konden niet worden opgehaald.',
            reject_error: 'Afwijzing mislukt.',
            reject_request: 'Verzoek afwijzen',
            rejection_notes_required: 'Reden voor afwijzing vereist',
            rejection_notes_required_msg: 'Geef een reden op voor de afwijzing.',
            requested_at: 'Aangevraagd op',
            decision_rules_heading: 'How decisions are recorded',
            decision_rule_second_person: 'A request is decided by someone other than the person who made it. You cannot approve your own request.',
            decision_rule_notes: 'Rejecting needs a written reason. Approving can carry notes, and either decision can attach supporting evidence.',
            decision_rule_scope: 'A decision is recorded here and in the governance audit trail. It does not carry out the action the request is about.',
            own_request_note: 'Your request. Another person decides it.',
            supporting_evidence_label: 'Ondersteunend bewijs (optioneel)',
            decision_evidence_name: 'Beslissingsbewijs',
        },
        evidence: {
            title: 'Governancebewijs',
            new: 'Nieuw bewijs',
            link_to: 'Koppelen aan',
            entity_id_placeholder: 'Entiteits-ID',
            file_path_placeholder: '/pad/naar/bestand',
            evidence_label: 'Bewijslabel',
            file_path: 'Bestandspad',
            source_mode_label: 'Bewijsbron',
            source_mode_file: 'Bestand uploaden',
            source_mode_url: 'Externe URL koppelen',
            attachment_label: 'Bijlage',
            item_count: 'Aantal items',
            link_button: 'Bewijs koppelen',
            confirm_delete: 'Weet u zeker dat u dit bewijs wilt verwijderen?',
            enter_entity_id: 'Entiteits-ID invoeren...',
            entity_id: 'Entiteits-ID',
            entity_type: 'Entiteitstype',
            expires: 'Verloopt',
            expires_at: 'Verloopt op',
            fetch_error: 'Bewijs kon niet worden opgehaald.',
            link_error: 'Bewijs kon niet worden gekoppeld.',
            no_evidence: 'Geen bewijs gevonden.',
            tags_csv: 'Tags (kommagescheiden)',
            uploaded: 'Geüpload',
            url: 'URL',
            framework_tag_heading: 'Framework-gelabeld bewijs',
            framework_tag_note: 'Elk bewijsrecord legt de raamwerkcode, actor en tijdstempel vast. Filter op raamwerk om auditrelevante records te isoleren.',
            framework_code: 'Kadercode',
            filter_by_framework: 'Filteren op raamwerkcode...',
            actor: 'Acteur',
        },
        certification: {
            not_applicable: 'Niet van toepassing',
            not_certified: 'Niet gecertificeerd',
            pending: 'In afwachting',
            certified: 'Gecertificeerd',
            revoked: 'Ingetrokken',
        },
        onboarding_pipeline: {
            title: 'Onboarding-pijplijn',
            new_workflow: 'Nieuwe onboarding-workflow',
            vendor_id: 'Leverancier-ID',
            stages: 'Fasen',
            current_stage: 'Huidige fase',
            advance: 'Fase doorvoeren',
            advance_success: 'Fase succesvol doorgevoerd',
            workflow_status: 'Workflowstatus',
            initiated: 'Gestart',
            in_progress: 'In uitvoering',
            completed: 'Voltooid',
            cancelled: 'Geannuleerd',
            no_workflows: 'Geen onboarding-workflows gevonden',
            stages_placeholder: 'Fase 1, Fase 2, Fase 3',
            assigned_to: 'Toegewezen aan',
            notes: 'Notities',
            pdpl_lifecycle_heading: 'PDPL Levenscyclus van leverancier: introductie',
            due_diligence_note: 'De due diligence-fase is verplicht voordat een verwerker onder PDPL wordt toegelaten. Controleer de reikwijdte van de gegevensverwerking, de rechtsgrondslag en de beveiligingsmaatregelen.',
            contract_review_note: 'Contractbeoordeling moet DPA-clausules, gegevensbewaarlimieten en beperkingen voor subverwerkers bevestigen.',
            create_dpa_review_cta: 'DPA-beoordeling aanmaken',
            dpa_review_linked: 'DPA-beoordeling gekoppeld',
        },
        dpa_review: {
            title: 'DPA-beoordelingen',
            new_review: 'Nieuwe DPA-beoordeling',
            review_type: 'Beoordelingstype',
            findings: 'Bevindingen',
            outcome: 'Uitkomst',
            complete_review: 'Beoordeling voltooien',
            complete_success: 'Beoordeling succesvol voltooid',
            link_assessment: 'Beveiligingsbeoordeling koppelen',
            link_success: 'Beoordeling succesvol gekoppeld',
            assessment_id: 'Beoordelings-ID',
            combined_title: 'Gecombineerde beoordeling',
            no_reviews: 'Geen DPA-beoordelingen gevonden',
            telecom_disclosure_heading: 'Beheer van de openbaarmaking van telecom',
            disclosure_trigger_note: 'Door openbaarmaking veroorzaakte beoordelingen leggen gegevensuitwisselingsgebeurtenissen van telecomabonnees vast volgens de VN Telecomwet.',
            telecom_t2_note: 'Goedkeuring van het niveau TELECOM-T2 is vereist voordat abonneegegevens aan de autoriteiten worden bekendgemaakt.',
            quarterly_log_note: 'Gebruik het kwartaallogboek om alle openbaarmakingsgebeurtenissen te documenteren en een continu audittraject bij te houden.',
        },
        periodic_review: {
            title: 'Periodieke beoordelingen',
            new_review: 'Nieuwe periodieke beoordeling',
            review_type: 'Beoordelingstype',
            due_date: 'Vervaldatum',
            next_review: 'Volgende beoordelingsdatum',
            trigger_reassessment: 'Herbeoordeling starten',
            reassessment_reason: 'Reden voor herbeoordeling',
            reassessment_success: 'Herbeoordeling succesvol gestart',
            complete_success: 'Beoordeling succesvol voltooid',
            scheduled: 'Gepland',
            in_progress: 'In uitvoering',
            completed: 'Voltooid',
            overdue: 'Achterstallig',
            material_change: 'Wezenlijke wijziging',
            no_reviews: 'Geen periodieke beoordelingen gevonden',
            mark_complete: 'Markeren als voltooid',
            pdpl_periodic_heading: 'PDPL Levenscyclus van leverancier: periodieke beoordelingen',
            scheduled_review_note: 'Voor alle actieve verwerkers moeten ten minste jaarlijks periodieke beoordelingen worden gepland. Achterstallige beoordelingen activeren nalevingswaarschuwingen.',
            material_change_note: 'Materiële veranderingen in de gegevensverwerking vereisen een onmiddellijke herbeoordeling, ongeacht het schema.',
            overdue_badge: 'Achterstallig',
            overdue_aria: 'Achterstallige beoordeling',
            only_overdue_filter: 'Alleen achterstallig',
        },
        offboarding: {
            title: 'Leverancier-offboarding',
            initiate: 'Offboarding starten',
            initiate_success: 'Offboarding succesvol gestart',
            reason: 'Reden voor offboarding',
            data_return_required: 'Gegevensretour vereist',
            verify_data: 'Gegevensretour verifiëren',
            verify_success: 'Gegevensretour succesvol geverifieerd',
            sign_off: 'Aftekenen',
            sign_off_success: 'Offboarding succesvol afgetekend',
            status_initiated: 'Gestart',
            status_data_pending: 'Gegevensretour in afwachting',
            status_data_verified: 'Gegevensretour geverifieerd',
            status_completed: 'Voltooid',
            status_cancelled: 'Geannuleerd',
            task_id: 'Taak-ID',
            no_tasks: 'Geen offboarding-taken gevonden',
            evidence_id: 'Bewijs-ID',
            pdpl_offboarding_heading: 'PDPL Levenscyclus van leverancier: offboarding en verwijdering',
            deletion_capture_note: 'Offboarding moet de bevestiging van de gegevensverwijdering van de leverancier vastleggen. Controleer de gegevensretour voordat u aftekent.',
            evidence_attachment_note: 'Voeg verwijderingsbewijs toe (certificaten, screenshots, leveranciersbevestigingen) om het offboarding-record te voltooien.',
            verify_mode_label: 'Verificatiemethode',
            verify_mode_upload: 'Nieuw bestand uploaden',
            verify_mode_link: 'Bestaand bewijs koppelen',
            verify_mode_none: 'Geen data te retourneren',
            upload_data_return_evidence: 'Upload bewijs van datateruggave',
            no_data_justification_label: 'Rechtvaardiging',
            no_data_justification_placeholder: 'Leg uit waarom geen data hoeft te worden teruggegeven',
            no_data_justification_hint: 'Minimaal 20 tekens',
            upload_required_toast: 'Voeg een bestand toe om door te gaan',
            evidence_link_required_toast: 'Selecteer een bewijs om door te gaan',
            no_data_justification_required_toast: 'Geef een rechtvaardiging van minstens 20 tekens',
            data_return_evidence_name: 'Bewijs van datateruggave',
            verify_before_signoff: 'Verify data return before sign-off',
        },
        dpa_review_types: {
            initial: 'Initieel',
            annual: 'Jaarlijks',
            renewal: 'Verlenging',
            incident_triggered: 'Incidentgerelateerd',
        },
        periodic_review_types: {
            initial: 'Initieel',
            annual: 'Jaarlijks',
            renewal: 'Verlenging',
            incident_triggered: 'Incidentgetriggerd',
            material_change: 'Materiële wijziging',
            disclosure_triggered: 'Openbaarmakingsgetriggerd',
            quarterly_log: 'Kwartaalrapport',
        },
        dpa_review_statuses: {
            pending: 'In afwachting',
            in_progress: 'In behandeling',
            completed: 'Voltooid',
            cancelled: 'Geannuleerd',
        },
        onboarding_stage_names: {
            due_diligence: 'Due diligence',
            contract_review: 'Contractbeoordeling',
            security_assessment: 'Beveiligingsbeoordeling',
            data_mapping: 'Gegevensmapping',
            approval: 'Goedkeuring',
            integration: 'Integratie',
        },
        access_denied_title: 'Toegang geweigerd',
        access_denied: 'U heeft geen toestemming om deze pagina te bekijken. Neem contact op met uw beheerder.',
    },
    consent: {
        nav: {
            policies: 'Beleid',
            purposes: 'Doelcatalogus',
            workflows: 'Werkstromen',
            snapshots: 'CMP-snapshots',
            change_awareness: 'Wijzigingsbewustzijn',
            audit: 'Toestemmingsaudit',
            evidence: 'Consent Evidence',
            marketing_consent: 'Marketing Consent',
            marketing_suppression: 'Suppression Register',
            marketing_suppression_enforcement: 'Suppression Enforcement',
            marketing_sender_name_certificates: 'Sender-name Certificates',
        },
        gap_analysis: {
            nav_label: 'Gap-analyse',
            page_title: 'Analyse van toestemmingskloof',
            page_subtitle: 'Forseti checks your active consent purposes against the consent families of your installed frameworks, and suggests a remediation for each critical or high gap. Only installed families are analysed.',
            framework_picker_label: 'Kaders',
            org_flags_label: 'Organisatiecontext',
            flag_withdrawal: 'Heeft een wereldwijd opnamemechanisme gedocumenteerd',
            flag_parental: 'Heeft ouderlijke toestemming voor proefpersonen onder de 16 jaar',
            flag_cross_border: 'Heeft grensoverschrijdende indiening geregistreerd',
            run_label: 'Voer een gap-analyse uit',
            running_label: 'Hardlopen…',
            requires_advisor_label: 'Vereist ai_advisor:chat-toestemming.',
            results_heading: 'Er zijn hiaten aan het licht gekomen ({count})',
            tokens_label: '≈ {count} tokens',
            empty_label: 'Er zijn geen hiaten gedetecteerd voor de geselecteerde raamwerken.',
            remediation_label: 'Voorgestelde oplossing:',
            dismiss_label: 'Afwijzen als niet van toepassing',
            resolve_label: 'Oplossen',
            dismissed_label: 'Afgewezen omdat het niet van toepassing is',
            resolved_label: 'Opgelost',
            // Sprint 62.27.0 gate E12.5 pass 2 — R-57: English until Phase 25.
            families_loading: 'Reading the installed frameworks…',
            families_load_error: 'The installed frameworks could not be read, so no family can be offered for analysis.',
            families_retry_subject: 'the installed frameworks',
            families_none: 'None of your installed frameworks belongs to a family this analysis reads, so there is nothing to analyse.',
            family_instruments: 'Installed: {codes}',
            analysed_label: 'Analysed: {families}',
            not_analysed_label: 'Not installed, so not analysed: {families}',
            nothing_analysed: 'Nothing was analysed: none of the selected families is installed, so no gap can be reported.',
            acknowledged_count_label: '{count} acknowledged finding(s), not counted as open gaps',
            run_failed: 'The gap analysis could not be run.',
            triage_failed: 'The decision could not be recorded.',
            purpose_scope: 'Purpose: {name}',
            org_wide_scope: 'Organisation-wide',
            attributes: {
                legal_basis_set: 'Lawful basis recorded',
                withdrawal_mechanism: 'Withdrawal mechanism',
                granular_per_purpose: 'Consent separable per purpose',
                parental_consent_for_minors: 'Consent for children',
                cross_border_filing: 'Cross-border transfer record',
                retention_period: 'Retention period',
                sensitive_data_ground_review: 'Sensitive-data ground review',
            },
            acknowledge_label: 'Acknowledge review',
            acknowledge_title: 'Acknowledge the sensitive-data ground review',
            acknowledge_description: 'Purpose: {name}. Record that you reviewed whether your installed frameworks set a condition for processing sensitive data for this purpose beyond its lawful basis and, if so, which. Later analyses show the finding as acknowledged until the purpose’s sensitivity or its recorded ground changes, or a framework family you did not review is installed.',
            acknowledge_note_label: 'Note (required)',
            acknowledge_note_hint: 'What you reviewed, and whether a condition is recorded on the purpose and, if so, which, with its article.',
            acknowledge_submit: 'Acknowledge',
            acknowledge_done: 'Review acknowledged',
            acknowledge_failed: 'The acknowledgement could not be recorded.',
            acknowledged_by: 'Acknowledged by {name} on {date}: {note}',
            acknowledgement_lapsed: 'An acknowledgement by {name} on {date} lapsed because {reasons}.',
            lapse_sensitivity_changed: 'the purpose’s sensitivity changed',
            lapse_basis_changed: 'its recorded ground changed',
            lapse_family_added: 'a family it did not review was installed: {families}',
            lapse_reason_joiner: ' and ',
        },
        import: {
            title: 'Toestemmingsworkflows importeren',
            nav_title: 'Bulkimport',
            description: 'Upload een CSV-bestand om toestemmingsworkflows in bulk te importeren.',
            select_file: 'CSV-bestand selecteren',
            file_help: 'CSV met kopteksten die overeenkomen met workflowvelden.',
            column_count_help: 'A row whose number of values differs from the number of columns in the header is not imported; it is listed under Errors with its row number.',
            required_columns_help: 'Vereiste kolommen: Workflowtype, Betrokkene, Kanaal.',
            file_too_large: 'Bestand te groot. Maximale grootte is 2MB.',
            preview: 'Voorbeeld',
            columns_detected: 'Kolommen gedetecteerd: {count}',
            column_mapping: 'Kolomtoewijzing',
            import_button: 'Importeren',
            import_complete: 'Import voltooid',
            import_error: 'Import mislukt',
            result: 'Importresultaat',
            total: 'Totaal',
            imported: 'Geïmporteerd',
            skipped: 'Overgeslagen',
            failed: 'Mislukt',
            errors: 'Fouten',
            error_row: 'Rij',
            error_field: 'Veld',
            error_message: 'Bericht',
            history: 'Importgeschiedenis',
            no_history: 'Geen importgeschiedenis.',
            history_file: 'Bestandsnaam',
            history_date: 'Datum',
            create_profiles_label: 'Betrokkene-profielen aanmaken voor nieuwe subjecten',
            create_profiles_help: 'Imports zonder bijpassend profiel maken er automatisch een aan. Schakel uit om alleen workflows te importeren.',
            profiles_created: 'Profielen aangemaakt',
            profiles_matched: 'Profielen gematcht',
            subject_kind_help: 'Optionele kolommen: subject_kind, subject_email, subject_display_name, subject_phone, external_ref_key.',
            error_subject_invalid_email: 'Ongeldig e-mailadres in kolom subject_email.',
            error_subject_unknown_kind: 'Onbekende subject_kind-waarde. Verwacht: email, visitor_uuid, employee_id, student_id, external_ref.',
            error_missing_external_ref_key: 'external_ref_key is vereist wanneer subject_kind=external_ref',
        },
        policies: {
            title: 'Toestemmingsbeleid',
            new: 'Nieuw beleid',
            edit: 'Beleid bewerken',
            name: 'Naam',
            version: 'Versie',
            policy_text: 'Beleidstekst',
            publish: 'Publiceren',
            versions: 'Versies',
            hide_versions: 'Versies verbergen',
            submit_for_review: 'Ter beoordeling indienen',
            suspend: 'Opschorten',
            retire: 'Buiten gebruik stellen',
            jurisdictions: 'Rechtsgebieden',
            jurisdictions_csv: 'Rechtsgebieden (kommagescheiden)',
            approval_mode: 'Goedkeuringsmodus',
            reviewer_ids: "Beoordelaars-ID's (kommagescheiden)",
            tags_csv: 'Tags (kommagescheiden)',
            effective_date: 'Ingangsdatum',
            review_date: 'Beoordelingsdatum',
            consent_model_statement: 'Toestemmingsmodelverklaring',
            consent_model_placeholder: 'Optionele toestemmingsmodelverklaring...',
            default_settings: 'Standaardinstellingen (JSON)',
            changes: 'Wijzigingen',
            fetch_error: 'Kon toestemmingsbeleid niet laden. Probeer het opnieuw.',
            save_error: 'Kon toestemmingsbeleid niet opslaan. Probeer het opnieuw.',
            submit_review_error: 'Kon beleid niet ter beoordeling indienen. Probeer het opnieuw.',
            activate_error: 'Kon beleid niet activeren. Probeer het opnieuw.',
            suspend_error: 'Kon beleid niet opschorten. Probeer het opnieuw.',
            retire_error: 'Kon beleid niet buiten gebruik stellen. Probeer het opnieuw.',
            submit_review_success: 'Beleid ter beoordeling ingediend.',
            activate_success: 'Beleid geactiveerd.',
            suspend_success: 'Beleid opgeschort.',
            retire_success: 'Beleid met pensioen.',
            versions_error: 'Kon beleidsversies niet laden. Probeer het opnieuw.',
            invalid_json: 'Ongeldige JSON in veld Standaardinstellingen. Probeer het opnieuw.',
            page_title: 'Toestemmingsbeleid',
            version_updated: 'Bijgewerkt',
            version_initial: 'Eerste versie',
            json_format: 'JSON-indeling',
            sensitive: 'Gevoelig',
            flag_sensitive: 'Vlaggevoelig',
            flag_sensitive_title: 'Markeer beleid als gevoelig',
            require_enhanced_review: 'Uitgebreide beoordeling vereisen',
            enhanced_review_success: 'Verbeterde beoordelingsvereiste ingeschakeld.',
            return_to_draft: 'Return to draft',
            return_to_draft_hint: 'End this review round and make the policy editable again',
            return_to_draft_title: 'Return policy to draft',
            return_to_draft_body: "The decisions recorded in this review round are kept in the policy's approval history. The policy becomes a draft, to be edited and submitted for review again.",
            return_to_draft_reason: 'Reason (required)',
            return_to_draft_reason_required: 'Enter the reason for returning this policy to draft.',
            return_to_draft_success: 'Policy returned to draft.',
            return_to_draft_error: 'The policy could not be returned to draft.',
            in_review_locked_note: 'In review: the policy cannot be edited, flagged sensitive or routed to enhanced review. Return it to draft to change it.',
            refusal_policy_in_review: 'This policy is in review and cannot be changed. Return it to draft to change it.',
            refusal_policy_not_in_review: 'This policy is no longer in review. The list has been refreshed.',
            refusal_policy_changed_concurrently: 'The policy changed while this was being saved. The list has been refreshed; check the policy and try again.',
            refusal_not_a_named_reviewer: 'Only the reviewers this policy names can decide it.',
            select_categories: 'Selecteer de toepasselijke PDPL categorieën van gevoelige gegevens uit artikel 2:',
            no_policies: 'Geen toestemmingsbeleid gevonden. Maak uw eerste beleid aan om te beginnen.',
            approval_success: 'Beleid goedgekeurd.',
            reviewers_label: 'Beoordelaars',
            enhanced_review_title: 'Uitgebreide beoordeling vereist',
            enhanced_review_badge: 'Uitgebreide beoordeling vereist',
            enhanced_review_status_pending: 'Beoordeling in behandeling',
            enhanced_review_status_approved: 'Beoordeling goedgekeurd',
            enhanced_review_status_rejected: 'Beoordeling afgewezen',
            enhanced_review_reviewed_by: 'Beoordeeld door',
            enhanced_review_notes: 'Beoordelingsnotities',
            enhanced_review_approve: 'Beoordeling goedkeuren',
            enhanced_review_reject: 'Beoordeling afwijzen',
            enhanced_review_notes_required: 'Beoordelingsnotities zijn verplicht',
            enhanced_review_approved: 'Uitgebreide beoordeling goedgekeurd',
            enhanced_review_rejected: 'Uitgebreide beoordeling afgewezen — beleid teruggezet naar concept',
            enhanced_review_reset_to_draft: 'Uitgebreide beoordeling afgewezen — beleid teruggezet naar concept',
            enhanced_review_reject_warning: 'Afwijzing zet dit beleid terug naar de Concept-status. De FG moet het opnieuw indienen voor beoordeling na het oplossen van de problemen.',
            submitted_modal_title: 'Ingediend voor uitgebreide beoordeling',
            submitted_modal_body: 'Dit beleid is ingediend. Een Juridische/Privacy-beoordelaar moet het goedkeuren voordat het geactiveerd kan worden.',
            activate_hint: 'Make this policy effective',
            approval_comments: 'Comments (optional)',
            approve_title: 'Approve Policy',
            flag_sensitive_hint: 'Mark as involving sensitive data',
            reactivate_hint: 'Resume this suspended policy',
            reject_hint: 'Decline this policy and return it to draft',
            reject_title: 'Reject Policy',
            rejection_reason: 'Rejection reason (required)',
            rejection_success: 'Policy rejected',
            require_enhanced_review_hint: 'Route to a second-level reviewer',
            retire_hint: 'Permanently retire this policy',
            review_context_description: 'Confirm the policy scope and review controls before saving.',
            review_context_hint: 'Reviewers, jurisdictions, dates, and consent defaults determine how this policy is governed.',
            review_context_title: 'Review context',
            sensitive_filter: 'Sensitive',
            suspend_hint: 'Temporarily pause this policy',
            suspend_reason_label: 'Suspension reason (required)',
            suspend_title: 'Suspend Policy',
            type_content_edit: 'Content Edit',
            type_status_change: 'Status Change',
            reactivate: 'Re-activate',
            no_versions: 'No version history yet.',
            approval_mode_hint: 'All: every named reviewer must approve, and only a named reviewer may decide. Any: one approval decides. In both, one rejection rejects the policy.',
            refusal_use_enhanced_review: 'This policy is routed to enhanced review, so it is decided there, not through the standard approval. The list has been refreshed.',
            refusal_reviewer_cannot_decide_named: 'These named reviewers could never decide this policy: {names}. Every named reviewer must be an active member of the organisation who holds the permission to approve consent policies, or, for a policy routed to enhanced review, the permission to carry out enhanced review.',
            refusal_reviewer_cannot_decide_none: 'This policy names no reviewer, and every named reviewer must approve it. Name at least one reviewer before submitting.',
            refusal_invalid_status_transition: 'This policy is no longer in a status that allows this. The list has been refreshed.',
            approval_waiting: 'Your approval is recorded. The policy is approved once {count} more named reviewer(s) approve.',
            enhanced_review_waiting: 'Your enhanced-review approval is recorded. The policy is approved once {count} more named reviewer(s) approve.',
            decision_recorded_in_review: 'Your decision is recorded. The policy is still in review.',
            decision_recorded: 'Your decision is recorded.',
            review_history: 'Review history',
            review_history_current: 'Decisions in the current review round',
            review_history_earlier: 'Earlier review rounds',
            review_history_empty: 'No review decision has been recorded for this policy yet.',
            history_col_reviewer: 'Reviewer',
            history_col_decision: 'Decision',
            history_col_review_kind: 'Review',
            history_col_decided_at: 'Decided',
            history_col_notes: 'Notes',
            history_kind_standard: 'Standard approval',
            history_kind_enhanced: 'Enhanced review',
            history_decision_approved: 'Approved',
            history_decision_rejected: 'Rejected',
            history_col_ended_at: 'Round ended',
            history_col_outcome: 'How it ended',
            history_col_ended_by: 'Ended by',
            history_col_reason: 'Reason',
            history_col_round_decisions: 'Decisions in the round',
            history_ended_resubmitted: 'Submitted for review again',
            history_ended_returned: 'Returned to draft',
            history_no_decisions: 'No decision was recorded',
            history_decision_by: '{decision} by {name}',
        },
        sensitive_categories: {
            health: 'Gezondheidsgegevens',
            biometric: 'Biometrische gegevens',
            political: 'Politieke opvattingen',
            ethnic: 'Etnische afkomst',
            genetic: 'Genetische gegevens',
            criminal: 'Strafblad',
        },
        purposes: {
            title: 'Doelcatalogus',
            new: 'Nieuw doel',
            edit: 'Doel bewerken',
            name: 'Naam',
            legal_basis: 'Rechtsgrond',
            retention_days: 'Bewaartermijn (dagen)',
            is_active: 'Actief',
            filter_by_category: 'Filteren op categorie',
            policy_ids: "Beleids-ID's (kommagescheiden)",
            ropa_activity_ids: "ROPA-activiteits-ID's (kommagescheiden)",
            policies: 'Beleid',
            deprecate: 'Afkeuren',
            fetch_error: 'Kon toestemmingsdoelen niet laden. Probeer het opnieuw.',
            save_error: 'Kon toestemmingsdoel niet opslaan. Probeer het opnieuw.',
            deprecate_error: 'Kon toestemmingsdoel niet afkeuren. Probeer het opnieuw.',
            page_title: 'Doel Catalogus',
            no_purposes: 'Geen toestemmingsdoelen gevonden. Maak uw eerste doel aan om te beginnen.',
            category_personal_data: 'Persoonlijke gegevens',
            category_sensitive_data: 'Gevoelige gegevens',
            category_health_data: 'Gezondheidsgegevens',
            category_financial_data: 'Financiële gegevens',
            category_biometric_data: 'Biometrische gegevens',
            category_children_data: 'Gegevens van kinderen',
            category_location_data: 'Locatiegegevens',
            category_communications_data: 'Communicatiegegevens',
            category_other: 'Anders',
        },
        workflows: {
            title: 'Toestemmingsworkflows',
            new: 'Nieuwe workflow',
            edit: 'Workflow bewerken',
            workflow_type: 'Type workflow',
            user_id: 'Gebruikers-ID',
            purpose_id: 'Doel-ID',
            purpose_ids_csv: 'Doel ID (comma-separated)',
            reason: 'Reden',
            process: 'Verwerken',
            data_subject_ref: 'Gegevens Onderwerp Referentie',
            data_subject: 'Gegevens Onderwerp',
            channel: 'Kanaal',
            policy_id: 'Beleid ID',
            scope: 'Bereik',
            legal_grounds: 'Juridisch Grounds',
            assessment: 'Beoordeling',
            select_outcome: 'Selecteren Resultaat',
            partially_approved: 'Partially Goedgekeurd',
            denied: 'Geweigerd',
            outcome_upheld: 'Bevestigd',
            outcome_pending: 'In afwachting',
            outcome_denied: 'Geweigerd',
            status_count: 'Statusaantal',
            channel_filter: 'Filteren op kanaal',
            add_action: '+ Actie',
            by_type: 'Per type',
            by_status: 'Per status',
            downstream_actions: 'Downstream Acties',
            downstream_actions_for: 'Downstream Acties voor {subject} ({type})',
            add_downstream_action: 'Toevoegen Downstream Actie',
            assigned_to: 'Toegewezen Naar',
            fetch_error: 'Kan niet fetch toestemming workflows. Probeer het opnieuw.',
            dashboard_error: 'Kan niet fetch workflow dashboard. Probeer het opnieuw.',
            save_error: 'Kan niet save toestemming workflow. Probeer het opnieuw.',
            status_update_error: 'Kan niet update workflow status. Probeer het opnieuw.',
            assessment_error: 'Kan niet update workflow beoordeling. Probeer het opnieuw.',
            add_action_error: 'Kan niet add workflow actie. Probeer het opnieuw.',
            update_action_error: 'Kan niet update workflow actie. Probeer het opnieuw.',
            type_refusal: 'Weigering',
            type_withdrawal: 'Intrekking',
            type_restriction: 'Beperking',
            type_objection: 'Bezwaar',
            status_received: 'Ontvangen',
            status_processing: 'Verwerking',
            status_under_assessment: 'Onder Beoordeling',
            status_implemented: 'Geïmplementeerd',
            evidence_button: 'Bewijs',
            evidence_modal_title: 'Bewijs van de workflow',
            no_evidence: 'Er is geen bewijs gekoppeld aan deze workflow.',
            upload_evidence_label: 'Upload bewijsmateriaal voor de workflow',
            completed_at: 'Voltooid op',
            effective_date: 'Ingangsdatum',
            page_title: 'Toestemmingsworkflows',
            no_workflows: 'Geen toestemmingsworkflows gevonden. Workflows worden aangemaakt wanneer betrokkenen hun rechten uitoefenen.',
            type_grant: 'Verlening',
            propagation_none: 'Geen',
            propagation_pending: 'In afwachting',
            propagation_pending_warning: 'Kan niet voltooien — intrekkingspropagatie is nog in afwachting',
            propagation_completed: 'Voltooid',
            propagation_partial: 'Gedeeltelijk',
            propagation_incomplete_error: 'Kan workflow niet voltooien terwijl intrekkingspropagatie nog in afwachting is',
            propagation_status: 'Propagatiestatus',
        },
        snapshots: {
            title: 'CMP-snapshots',
            new: 'Nieuwe snapshot',
            label: 'Kenmerk',
            snapshot_date: 'Snapshotdatum',
            purposes_count: 'Aantal doelen',
            compare: 'Vergelijken',
            snapshot_data: 'Snapshotgegevens (JSON)',
            select_left: 'Selecteren Left Snapshot',
            select_right: 'Selecteren Right Snapshot',
            comparison_results: 'Vergelijking Resultaten',
            field: 'Veld',
            left: 'Links',
            right: 'Rechts',
            close_comparison: 'Sluiten Vergelijking',
            trigger: 'Activeren',
            trigger_manual: 'Handmatig',
            trigger_policy_approval: 'Beleid Goedkeuring',
            cmp_version: 'CMP Versie',
            policy_version_id: 'Beleid Versie ID',
            banner_config: 'Banner Configuratie (JSON)',
            active_purposes: 'Actief Doelen (JSON)',
            active_legal_bases: 'Actief Juridisch Bases (JSON)',
            domain_scope: 'Domein Bereik',
            domain_scope_csv: 'Domein Bereik (comma-separated)',
            raw_config: 'Raw Configuratie (JSON)',
            checksum: 'Controlesom',
            created: 'Aangemaakt',
            fetch_error: 'Kan niet fetch CMP snapshots. Probeer het opnieuw.',
            save_error: 'Kan niet save CMP snapshot. Probeer het opnieuw.',
            compare_error: 'Kan niet compare snapshots. Probeer het opnieuw.',
            page_title: 'Toestemmingsmomentopnamen',
            json_format: 'JSON-indeling',
            json_array_format: 'JSON array-indeling',
            manual_snapshot_note: 'Alleen voor beheerders/ontwikkelaars. In normaal gebruik worden snapshots automatisch aangemaakt via de CMP-webhook of bij beleidsgoedkeuring. Plak de JSON geëxporteerd uit uw CMP-leverancier (bijv. OneTrust, Cookiebot).',
            banner_config_hint: 'Plak het bannerconfiguratieobject uit uw CMP-export.',
            active_purposes_hint: 'JSON-array van actieve doeleinden-objecten gekopieerd uit uw CMP-export.',
            active_legal_bases_hint: 'JSON-array van actieve rechtsgrondslagen-objecten gekopieerd uit uw CMP-export.',
            raw_config_hint: 'Volledige ruwe configuratie-JSON van uw CMP. Andere JSON-velden worden hieruit geëxtraheerd. Een SHA-256-controlesom wordt automatisch berekend.',
            changed_field: 'Gewijzigd veld: ',
            no_snapshots: 'Geen CMP-snapshots gevonden. Snapshots worden aangemaakt wanneer beleid wordt goedgekeurd of handmatig wordt geactiveerd.',
            detail_title: 'Snapshot-detail',
            view_snapshot_detail: 'Snapshot-detail bekijken',
            policy_version: 'Beleidsversie',
            removed: 'Voor',
            added: 'Na',
        },
        change_awareness: {
            title: 'Toestemmingswijzigingsbewustzijn',
            change_type: 'Type wijziging',
            entity_type: 'Entiteitstype',
            entity_id: 'Entiteits-ID',
            detected_at: 'Gedetecteerd op',
            acknowledge: 'Bevestigen',
            new_alert: 'Nieuw Waarschuwing',
            new_alert_form: 'Nieuw Wijzigen Awareness Waarschuwing',
            total_open: 'Totaal Open',
            by_severity: 'By Ernst',
            snapshot_id: 'Snapshot-ID',
            update_status: 'Bijwerken Status',
            resolution_notes: 'Oplossing Opmerkingen',
            resolution_required: 'Oplossing Opmerkingen (required)',
            resolution_required_msg: 'Oplossing opmerkingen are required when resolving een item.',
            status_open: 'Geopend',
            status_under_review: 'Onder Beoordelen',
            status_resolved: 'Opgelost',
            status_accepted: 'Geaccepteerd',
            severity_informational: 'Informatief',
            severity_requires_review: 'Requires Beoordelen',
            severity_policy_violation: 'Beleid Violation',
            create_reconciliation: 'Afstemmingstaak maken',
            reconciliation_created: 'Afstemmingstaak is succesvol aangemaakt.',
            reconciliation_error: 'Kan afstemmingstaak niet maken. Probeer het opnieuw.',
            fetch_error: 'Kan niet fetch change awareness items. Probeer het opnieuw.',
            dashboard_error: 'Kan niet fetch change awareness dashboard. Probeer het opnieuw.',
            create_error: 'Kan niet create change awareness item. Probeer het opnieuw.',
            status_update_error: 'Kan niet update change awareness status. Probeer het opnieuw.',
            page_title: 'Verander bewustzijn',
            no_items: 'Geen wijzigingsbewustzijnsitems gevonden. Items worden aangemaakt wanneer wijzigingen in de toestemmingsconfiguratie worden gedetecteerd.',
            affected_policies: 'Affected Policies',
            affected_purposes: 'Affected Purposes',
            estimated_affected_subjects: 'Estimated Affected Subjects',
            impact_scope: 'Impact Scope',
            affected_policies_short: 'Policies',
            affected_purposes_short: 'Purposes',
            affected_subjects_short: 'Subjects',
        },
        audit: {
            title: 'Toestemmingsauditlogboek',
            event_type: 'Type gebeurtenis',
            user_id: 'Gebruikers-ID',
            timestamp: 'Tijdstempel',
            details: 'Bijzonderheden',
            actor: 'Betrokkene',
            filter_by_entity_type: 'Filteren op entity type',
            filter_by_action: 'Filteren op actie',
            fetch_error: 'Kan niet fetch toestemming audit trail. Probeer het opnieuw.',
            page_title: 'Toestemmingscontrole',
            export_csv: 'Exporteer CSV',
            exporting: 'Exporteren…',
            export_success: 'Audittrail succesvol geëxporteerd.',
            export_error: 'Kan toestemmingscontroletraject niet exporteren. Probeer het opnieuw.',
            no_entries: 'Geen auditgegevens gevonden. Auditgebeurtenissen worden automatisch vastgelegd wanneer toestemmingsgegevens wijzigen.',
            entity_policy: 'Beleid',
            entity_purpose: 'Doel',
            entity_workflow: 'Werkstroom',
            entity_consent_record: 'Toestemmingsregistratie',
            entity_change_awareness: 'Wijzigingsbewustzijn',
            entity_cmp_snapshot: 'CMP-momentopname',
            action_created: 'Aangemaakt',
            action_updated: 'Bijgewerkt',
            action_status_changed: 'Status gewijzigd',
            action_drift_detected: 'Afwijking gedetecteerd',
            action_snapshot_created: 'Momentopname aangemaakt',
            action_approval_submitted: 'Goedkeuring ingediend',
            action_archived: 'Gearchiveerd',
            action_purpose_auto_mapped: 'Doel automatisch toegewezen',
            action_purpose_mapping_updated: 'Doeltoewijzing bijgewerkt',
            action_webhook_configured: 'Webhook geconfigureerd',
            entity_propagation: 'Voortplanting',
            entity_reconciliation: 'Verzoening',
            entity_marketing_consent: 'Marketing consent',
            entity_marketing_suppression: 'Marketing suppression',
            entity_marketing_suppression_enforcement: 'Suppression enforcement check',
            action_propagation_confirmed: 'Voortplanting bevestigd',
            action_propagation_failed: 'Voortplanting mislukt',
            action_consent_captured: 'Toestemming vastgelegd',
            action_reconciliation_created: 'Verzoening gecreëerd',
            action_reconciliation_resolved: 'Verzoening opgelost',
            action_submitted_for_review: 'Ingediend ter beoordeling',
            action_activated: 'Geactiveerd',
            action_suspended: 'Opgeschort',
            action_retired: 'Buiten gebruik gesteld',
            action_enhanced_review_required: 'Uitgebreide beoordeling vereist',
            action_enhanced_review_approved: 'Uitgebreide beoordeling goedgekeurd',
            action_enhanced_review_rejected: 'Uitgebreide beoordeling afgewezen — beleid teruggezet naar concept',
            action_consented: 'Toegestemd',
            action_consented_customize: 'Toestemming op maat',
            action_revoked: 'Ingetrokken',
            action_rejected: 'Afgewezen',
        },
        web_records: {
            title: 'Webtoestemmingsrecords',
            page_title: 'Webtoestemmingsrecords',
            domain: 'Domein',
            visitor_id: 'Bezoekers-ID',
            consent_action: 'Toestemmingsactie',
            received_at: 'Ontvangen bij',
            admin_status: 'Beheerdersstatus',
            revoke: 'Intrekken',
            reject: 'Afwijzen',
            reason: 'Reden',
            reason_placeholder: 'Reden voor deze actie (optioneel)',
            revoke_confirm: 'Deze toestemmingsregistratie intrekken?',
            reject_confirm: 'Deze toestemmingsregistratie afwijzen?',
            override_success: 'Recordstatus is succesvol bijgewerkt.',
            override_error: 'Kan de recordstatus niet bijwerken.',
            no_records: 'Er zijn geen webtoestemmingsrecords gevonden.',
            fetch_error: 'Kan webtoestemmingsrecords niet laden.',
            filter_by_domain: 'Filter op domein',
            filter_by_action: 'Filter op actie',
            active: 'Actief',
        },
        legal_bases: {
            all: 'Alle Juridisch Bases',
        },
        common_statuses: {
            suspended: 'Opgeschort',
            retired: 'Uitgefaseerd',
            deprecated: 'Afgeschreven',
        },
        approval_modes: {
            all: 'All',
            any: 'Any',
        },
        cmp_integration: {
            title: 'AesirX CMP Integratie',
            back_to_consent: 'Back naar Toestemming',
            back_to_integration: 'Back naar Integratie',
            webhook_status: 'Webhook-status',
            events_received: 'Gebeurtenissen Ontvangen',
            web_consent_records: 'Web Toestemming Records',
            unmapped_purposes: 'Niet toegewezen Doelen',
            integration_management: 'Integratie Beheer',
            webhook_configuration: 'Webhook Configuratie',
            webhook_config_desc: 'Configureren de CMP webhook endpoint en manage de HMAC secret.',
            purpose_mapping: 'Doel Toewijzing',
            purpose_mapping_desc: 'Toewijzen CMP purpose identifiers naar GRC toestemming catalog purposes.',
            bulk_sync: 'Bulk Synchronisatie',
            bulk_sync_desc: 'Doeltoewijzingen opnieuw toepassen op alle niet-toegewezen toestemmingsrecords.',
            consent_analytics: 'Web Toestemming Analytics (Last 30 Days)',
            consent_grant_rate: 'Toestemming Grant Tarief',
            top_domain: 'Top Domein',
            records: 'records',
            banner_interactions: 'Banner-interacties',
            consent_actions: 'Toestemmingsacties',
            opt_in_trend: 'Toestemming/Afmelding-trend',
            records_by_domain: 'Records per domein',
            date: 'Datum',
            grants: 'Toestemmingen',
            denials: 'Weigeringen',
            not_configured: 'Niet Configured',
            load_error: 'Kan niet load integratie gegevens',
            webhook_not_configured: 'Webhook niet geconfigureerd.',
            webhook_not_configured_msg: 'Gelieve {link} first.',
            configure_webhook_link: 'configureren de webhook',
            setup_wizard: 'Instellen Wizard',
            step_configure_webhook: 'Configureren Webhook',
            step_map_purposes: 'Toewijzen Doelen',
            step_run_sync: 'Uitvoeren Initieel Bulk Synchronisatie',
            step_verify_data: 'Verifiëren Gegevens in Dashboard',
            configure: 'Configureren',
            view_dashboard: 'Bekijken Dashboard',
            syncing: 'Synchroniseren...',
            sync_now: 'Synchronisatie Now',
            sync_completed: 'Synchronisatie completed: {imported} imported, {skipped} skipped, {failed} mislukt.',
            sync_failed: 'Synchronisatie mislukt',
            last_sync_result: 'Laatste Synchronisatie Result',
            imported: 'Geïmporteerd',
            skipped: 'Overgeslagen',
            errors: 'Fouten',
            index: 'Overzicht',
            error: 'Fout',
            sync_history: 'Synchronisatie Geschiedenis',
            no_sync_jobs: 'No synchronisatie jobs recorded yet.',
            load_sync_error: 'Kan niet load synchronisatie gegevens',
            purpose_mapping_title: 'CMP Doel Toewijzing',
            auto_mapping: 'Automatisch toewijzen...',
            auto_map_purposes: 'Auto-Map Doelen',
            unmapped_warning: '{count} niet-toegewezen doel(en) -- deze CMP doeleinden moeten worden toegewezen aan GRC-doeleinden.',
            mapping_saved: 'Toewijzing saved.',
            mapping_deleted: 'Toewijzing deleted.',
            auto_map_complete: 'Auto-map voltooid: {mapped} mapped, {unmatched} unmatched.',
            auto_map_failed: 'Auto-map mislukt',
            save_mapping_error: 'Kan niet save toewijzing',
            delete_mapping_error: 'Kan niet delete toewijzing',
            load_data_error: 'Kan niet load gegevens',
            cmp_purpose: 'CMP Doel',
            grc_purpose: 'GRC Doel',
            select_grc_purpose: '-- Selecteren GRC Doel --',
            auto_mapped: 'Automatisch toegewezen',
            mapped: 'Toegewezen',
            unmapped: 'Niet toegewezen',
            no_mappings: 'Geen doeltoewijzingen gevonden. Toewijzingen worden automatisch aangemaakt wanneer toestemmingsgebeurtenissen worden ontvangen.',
            total_mappings: 'Totaal: {count} mapping(s)',
            webhook_config_title: 'Webhook Configuratie',
            current_configuration: 'Huidig Configuratie',
            setup_webhook: 'Instellen Webhook',
            setup_webhook_desc: 'Configureren de AesirX CMP webhook naar receive toestemming gebeurtenissen.',
            allowed_domains: 'Toegestaan Domeinen (one per line)',
            webhook_secret_warning: 'Webhook Geheim (save dit now -- it will not be shown again):',
            last_event: 'Laatste Gebeurtenis',
            none_received: 'Geen',
            update_domains: 'Bijwerken Domeinen',
            pause_webhook: 'Pauzeren Webhook',
            resume_webhook: 'Hervatten Webhook',
            regenerate_secret: 'Regenerate Geheim',
            configuring: 'Configureren...',
            create_webhook_config: 'Aanmaken Webhook Configuratie',
            webhook_endpoint: 'Webhook Eindpunt',
            webhook_endpoint_desc: 'Configureren your AesirX CMP naar send toestemming gebeurtenissen naar:',
            webhook_signature_note: 'Opnemen de HMAC-SHA256 signature in de {header} header.',
            configured_success: 'Webhook succesvol geconfigureerd.',
            config_updated: 'Configuratie updated.',
            config_error: 'Kan niet configureren webhook',
            update_error: 'Kan niet update configuratie',
            toggle_error: 'Kan niet toggle webhook',
            deactivated: 'Webhook gedeactiveerd.',
            deactivate_error: 'Kan niet deactivate webhook',
            page_title: 'CMP Integratie',
            webhook_config_page_title: 'CMP Webhook-configuratie',
            bulk_sync_page_title: 'Records opnieuw in kaart brengen',
            purpose_mapping_page_title: 'Doel in kaart brengen',
            data_loaded: 'CMP integratiegegevens geladen',
            sync_all: 'Alles synchroniseren',
            sync_selected: 'Geselecteerde synchroniseren',
            sync_status: 'Synchronisatiestatus',
            last_sync: 'Laatste synchronisatie',
            no_records: 'Geen records gevonden.',
            sync_in_progress: 'Synchronisatie bezig...',
            sync_complete: 'Synchronisatie voltooid.',
            sync_error: 'Synchronisatie mislukt. Probeer het opnieuw.',
            map_purpose: 'Doel toewijzen',
            unmap: 'Toewijzing opheffen',
            mapping_status: 'Toewijzingsstatus',
            save_mapping: 'Toewijzing opslaan',
            remap_unmapped_records: 'Breng niet-toegewezen records opnieuw in kaart',
            remap_records: 'Records opnieuw in kaart brengen',
            add_mappings_first: 'Voeg eerst doeltoewijzingen toe (stap 2)',
            remap_result: 'Resultaat opnieuw in kaart brengen',
            remap_result_desc: 'Alle toestemmingsrecords verwerkt met niet-toegewezen of gedeeltelijk toegewezen doeleinden.',
            total_processed: 'Totaal verwerkt',
            newly_mapped: 'Nieuw in kaart gebracht',
            still_unmapped_label: 'Nog steeds niet in kaart gebracht',
            still_unmapped_msg: '{count} record(s) hebben nog steeds niet-toegewezen doeleinden. {link} en klik vervolgens opnieuw op Opnieuw toewijzen.',
            add_more_mappings: 'Voeg meer toewijzingen toe',
            remap_complete: 'Opnieuw toewijzen voltooid: {remapped} opnieuw in kaart gebracht, {still_unmapped} nog steeds niet in kaart gebracht.',
            add_mapping: 'Voeg toewijzing toe',
            cmp_purpose_id_label: 'CMP Doel-ID *',
            cmp_purpose_id_required: 'CMP Doel-ID is vereist',
            cmp_purpose_name_label: 'CMP Doelnaam',
            grc_purpose_label: 'GRC-doel',
        },
        propagation: {
            title: 'Intrekkingsverspreiding',
            systems: 'Gekoppelde systemen',
            status: 'Status',
            confirmed: 'Bevestigd',
            pending: 'In afwachting',
            failed: 'Mislukt',
            confirm: 'Intrekking bevestigen',
            fail: 'Markeren als mislukt',
            failure_reason: 'Reden van mislukking',
            can_close: 'Alle systemen bevestigd — workflow kan worden gesloten',
            cannot_close: 'Wachten op systeembevestigingen',
            no_tasks: 'Geen verspreidingstaken.',
            confirm_success: 'Systeemintrekking bevestigd.',
            fail_success: 'Systeem gemarkeerd als mislukt.',
            propagate_success: 'Intrekking verspreid naar gekoppelde systemen.',
            propagate: 'Verspreiden naar systemen',
            add_system: 'Systeem toevoegen',
            remove_system: 'Verwijderen',
            system_id: 'Systeem-ID',
            system_name: 'Systeemnaam',
            owner_id: 'Eigenaar-ID',
            propagate_error: 'Het is niet gelukt om de intrekking door te geven. Probeer het opnieuw.',
            no_systems: 'Voeg ten minste één systeem toe.',
            page_title: 'Intrekking propagatie',
            workflow_id: 'Selecteer Opnameworkflow',
            system_id_hint: 'bijv. crm-001, erp-prod',
            owner_id_hint: 'Systeemeigenaar',
            elapsed: 'Verstreken',
        },
        sensitive: {
            title: 'Beleid voor gevoelige gegevens',
            flag: 'Markeren als gevoelig',
            categories: 'Gevoelige categorieën',
            enhanced_review: 'Uitgebreide beoordeling vereisen',
            enhanced_review_success: 'Uitgebreide beoordeling vereist.',
            flag_success: 'Beleid gemarkeerd als gevoelig.',
            no_sensitive: 'Geen beleid voor gevoelige gegevens.',
        },
        reconciliation: {
            title: 'Afwijkingsafstemming',
            create: 'Nieuwe afstemmingstaak',
            system: 'Systeem',
            owner: 'Eigenaar',
            description: 'Beschrijving',
            resolution: 'Oplossing',
            resolve: 'Oplossen',
            update_grc: 'GRC bijwerken',
            update_cmp: 'CMP bijwerken',
            accept_difference: 'Verschil accepteren',
            escalate: 'Escaleren',
            resolution_notes: 'Oplossingsnotities',
            no_tasks: 'Geen afstemmingstaken.',
            resolve_success: 'Afstemmingstaak opgelost.',
            create_success: 'Afstemmingstaak aangemaakt.',
            open: 'Openen',
            resolved: 'Opgelost',
            escalated: 'Geëscaleerd',
            dashboard_open: 'Open taken',
            dashboard_resolved: 'Opgeloste taken',
            dashboard_escalated: 'Geëscaleerde taken',
            page_title: 'Mismatch-verzoening',
            my_tasks: 'Mijn taken',
            all_tasks: 'Alle taken',
            severity: 'Ernst',
        },
        sync: {
            title: 'CMP-synchronisatie',
            direction: 'Richting',
            cmp_to_grc: 'CMP → GRC',
            grc_to_cmp: 'GRC → CMP',
            conflict: 'Conflicten',
            resolve_conflict: 'Conflict oplossen',
            no_events: 'Geen synchronisatiegebeurtenissen.',
            resolve_success: 'Synchronisatieconflict opgelost.',
            create_success: 'Synchronisatiegebeurtenis geregistreerd.',
            page_title: 'CMP Synchroniseren',
            detect_drift: 'Conflicten detecteren',
            detecting: 'Detecteren...',
            drift_result: '{detected} conflict(en) gedetecteerd uit {total} vergeleken doel(en).',
            no_drift: 'Geen nieuwe conflicten gedetecteerd.',
            drift_error: 'Kan conflictdetectie niet uitvoeren.',
            resolve_modal_title: 'Conflicten oplossen',
            resolve_field: 'Veld',
            resolve_grc_value: 'GRC-waarde',
            resolve_cmp_value: 'CMP Waarde',
            accept_cmp: 'Accepteer CMP waarde',
            accept_cmp_desc: 'Update GRC zodat deze overeenkomt met de CMP: de waarde CMP wordt de nieuwe GRC-waarde.',
            keep_grc: 'GRC-waarde behouden',
            keep_grc_desc: 'Sluit dit conflict zonder GRC te wijzigen. Breng de eigenaar van CMP op de hoogte om uit te lijnen.',
            resolve_applied: 'Conflict opgelost. GRC-doel bijgewerkt zodat het overeenkomt met CMP.',
        },
    },
    assessments: {
        /*
         * Sprint 65.4.0 (AC5) — the DPIA review-reminder task body.
         *
         * It used to be an English sentence concatenated inside
         * `dpia-review-scheduler-service.ts`, asserting a "6-month review" over a
         * record free to declare any cadence at all. The period is gone from the
         * wording because the record owns it, and the string lives here so a
         * Vietnamese tenant does not read one English line in its task list.
         */
        review_reminder: {
            task_title: 'DPIA review due: {title}',
            task_description: 'The review scheduled on this assessment is due on {date}. Confirm the safeguards remain adequate, or file an updated assessment.',
        },
        // ── Sprint 65.4.0 (AC2) — the operator-recorded commencement moment ──
        //
        // Shared by the DPIA and cross-border transfer surfaces: one fact, one
        // vocabulary. The undated wording is deliberately explicit that a
        // lifecycle timestamp is NOT the answer, because a blank date field
        // reads as "nobody filled this in" while the truth is "the clock cannot
        // lawfully start yet".
        commencement: {
            section_title_dpia: 'When the processing began',
            section_title_transfer: 'When the transfer began',
            loading: 'Loading the recorded moment',
            load_failed: 'The recorded commencement moment could not be loaded. Refresh the page to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the recorded commencement moment',
            undated_label: 'Statutory clock undated',
            undated_icon: 'Undated',
            undated_reason: 'No one has recorded when this began, so the statutory period has nothing lawful to count from. It is never counted from the date this record was created, approved or last edited — those are the moments somebody opened a form here, not the moment the activity started.',
            recorded_label: 'Statutory clock anchored',
            recorded_icon: 'Anchored to a recorded moment',
            occurred_at_label: 'Began',
            recorded_by_label: 'Stated by',
            recorded_at_label: 'Stated on',
            source_field_label: 'Recorded in',
            correction_note_label: 'Reason for the restatement',
            instant_label: 'Date and time it began',
            instant_hint: 'Entered in your own time zone. The statutory period is counted from this moment, not from when you enter it.',
            note_label: 'Why is this being restated?',
            note_hint: 'Required. The record has to be able to explain why its due date moved.',
            save: 'Record the moment',
            save_correction: 'Correct the moment',
            saving: 'Saving',
            saved: 'Commencement moment recorded',
            saved_correction: 'Commencement moment corrected',
            saved_unchanged: 'That is already what this record states. Nothing was changed.',
            error_not_iso: 'Enter a full date and time. The moment is stored as an exact instant, so a time without a time zone cannot be accepted.',
            error_future: 'That moment is in the future. A statutory period cannot be counted from something that has not happened yet.',
            error_note_required: 'This record already states when it began. Say why you are restating it before saving.',
            error_note_not_correction: 'Nothing has been recorded here yet, so there is nothing to correct. Remove the note and record the moment.',
            error_generic: 'The commencement moment could not be recorded.',
        },
        // ── Sprint 65.7.0 — the two moments a FILED dossier carries ──
        //
        // Shared by the DPIA and cross-border transfer surfaces, like the
        // commencement vocabulary above: one fact, one wording. The first filing is
        // write-once, so this block carries NO edit copy for it — a "Change the
        // filing date" control would offer an act the server refuses, and the
        // sentence that explains why is the affordance instead.
        dossier_update: {
            section_title_dpia: 'The dossier filed for this assessment',
            section_title_transfer: 'The dossier filed for this transfer',
            loading: 'Loading the recorded dossier moments',
            load_failed: 'The recorded dossier moments could not be loaded. Refresh the page to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the recorded dossier moments',
            filing_heading: 'First filing',
            filing_absent_label: 'No first filing recorded',
            filing_absent_icon: 'No filing recorded',
            filing_absent_reason: 'No one has recorded when this dossier was first filed, so anything counted from that filing has nothing to count from. It is never counted from the date this record was created, approved or last edited — a dossier is filed by a person, on a day, outside this platform, and told to the platform afterwards.',
            filing_recorded_label: 'First filing recorded',
            filing_recorded_icon: 'Anchored to a recorded filing',
            filing_write_once_notice: 'Recorded once, and never moved. The update cycle is counted from the first filing, so changing it here would silently move every date derived from it. If the dossier was filed again, record what changed in it below.',
            filed_at_label: 'Filed on',
            filing_instant_label: 'Date and time it was first filed',
            filing_instant_hint: 'Entered in your own time zone. This is the moment the dossier was filed, not the moment you enter it here.',
            filing_save: 'Record the first filing',
            filing_saving: 'Saving',
            filing_saved: 'First filing recorded',
            filing_saved_unchanged: 'That is already what this record states. Nothing was changed.',
            change_heading: 'Change to the registered particulars',
            change_blocked_label: 'Nothing filed yet',
            change_blocked_reason: 'There are no registered particulars to change until a dossier has been filed. Record the first filing above, then record what changed in it.',
            change_absent_label: 'No change recorded',
            change_absent_reason: 'The dossier has been filed, and no one has recorded a change to the particulars registered in it.',
            change_recorded_label: 'Change recorded',
            change_latest_notice: 'The most recent change stands here. Recording another replaces it, and each statement is kept as its own fact in the record history.',
            changed_at_label: 'Changed on',
            change_description_value_label: 'What changed',
            change_instant_label: 'Date and time the particulars changed',
            change_instant_hint: 'Entered in your own time zone.',
            change_description_label: 'What changed?',
            change_description_hint: 'Required. Say what changed in your own words. Which changes oblige an update is stated by the frameworks you have installed, and the person acting on it needs your account of what happened.',
            change_note_label: 'Why is this being restated?',
            change_note_hint: 'Optional. Use it when this statement replaces one that already stood.',
            change_save: 'Record the change',
            change_save_restate: 'Replace the recorded change',
            change_saving: 'Saving',
            change_saved: 'Change to the registered particulars recorded',
            change_saved_unchanged: 'That is already what this record states. Nothing was changed.',
            recorded_by_label: 'Stated by',
            recorded_at_label: 'Stated on',
            source_field_label: 'Recorded in',
            correction_note_label: 'Reason for the restatement',
            error_not_iso: 'Enter a full date and time. The moment is stored as an exact instant, so a time without a time zone cannot be accepted.',
            error_future: 'That moment is in the future. Nothing can be counted from something that has not happened yet.',
            error_already_recorded: 'This record already states when its dossier was first filed, and that statement cannot be moved — every date counted from it would move too. If the dossier was filed again, record what changed in it instead.',
            error_before_first_filing: 'No dossier has been filed for this record yet, so there are no registered particulars to change. Record the first filing first.',
            error_description_required: 'Say what changed. A change with no account of what changed cannot be acted on by the person who has to review it.',
            error_generic: 'The dossier moment could not be recorded.',
        },
        dossier_preflight: {
            advisory_notice: 'Dit gereedheidsrapport is adviserend. Het keurt het dossier niet goed en stelt het niet samen, materialiseert het niet, dient het niet in en verzendt het niet.',
            run_label: 'Voer pre-flight uit',
            running: 'Pre-flight uitvoeren…',
            assemble_label: 'Pakket voor automatische montage',
            assembling: 'Monteren…',
            verdict_label: 'Oordeel vóór de vlucht',
            tokens_label: '≈ {count} tokens',
            remediation_label: 'Sanering',
            no_gaps: 'Er zijn geen gaten vóór de vlucht gedetecteerd. De vijl is klaar om te monteren.',
            override_label: 'Negeer het blokoordeel: de montage krijgt een audit-stempel.',
            accept_label: 'Rapport accepteren',
            reject_label: 'Rapport afwijzen',
            assembled_label: 'Inzendingspakket samengesteld',
            package_id_label: 'Pakket-ID',
            package_filename_label: 'Bestandsnaam',
            package_hash_label: 'Verpak hasj',
            evidence_count_label: '{count} bewijsmateriaal gebundeld',
            error_generic: 'Kon de pre-flight niet uitvoeren of het pakket in elkaar zetten. Probeer het opnieuw of controleer de archiveringsstatus.',
        },
        errors: {
            fetch: 'Kan niet load assessments..',
            save: 'Kan niet save beoordeling..',
            fetch_gap_report_failed: 'Kan niet fetch hiaat report..',
            fetch_transfers_failed: 'Kan niet fetch overdrachten..',
            save_transfer_failed: 'Kan niet save overdracht..',
            submit_review_failed: 'Kan niet submit voor beoordeling..',
            process_decision_failed: 'Kan niet process beslissing..',
            deprecate_failed: 'Kan niet deprecate..',
            evaluate_risk_failed: 'Kan niet evaluate risico..',
            fetch_safeguards_failed: 'Kan niet fetch waarborgen..',
            save_safeguard_failed: 'Kan niet save waarborg..',
            update_safeguard_status_failed: 'Kan niet update waarborg status..',
        },
        nav: {
            dpias: "GEB's",
            transfers: 'Doorgiftebeoordelingen',
            dashboard: 'Overzicht',
            gap_report: 'Lacunerapport',
        },
        risk_level_critical: 'Kritiek risico',
        risk_level_high: 'Hoog risico',
        risk_level_medium: 'Gemiddeld risico',
        risk_level_low: 'Laag risico',
        severity_critical: 'Kritieke ernst',
        severity_high: 'Hoge ernst',
        severity_medium: 'Gemiddelde ernst',
        severity_low: 'Lage ernst',
        overdue: 'Achterstallig',
        framework_filter_label: 'Kader',
        aria_filter_by_framework: 'Filter op raamwerk',
        dashboard: {
            title: 'Beoordelingsoverzicht',
            page_title: 'Beoordelingsdashboard',
            announce_loaded: 'Beoordelingsdashboard geladen',
            announce_chart_view: 'Grafiekweergave tonen',
            announce_table_view: 'Gegevenstabelweergave tonen',
            aria_metrics_table: 'Beoordelingsdashboard-statistieken',
            metric_col: 'Metriek',
            value_col: 'Waarde',
            dpia_prefix: 'DPIA',
            transfer_prefix: 'Overdracht',
            risk_prefix: 'Risico',
            aria_dpias_by_status: 'DPIAs op status',
            aria_transfers_by_status: 'Overdrachten op status',
            aria_top_risks: 'Toprisicos',
            aria_top_risks_table: 'Toprisicos over DPIAs',
            overdue_dpias_attention: '{count} achterstallige DPIA-beoordelingen vereisen aandacht',
            no_overdue_dpias: 'Geen achterstallige DPIA-beoordelingen',
            overdue_transfers_attention: '{count} achterstallige overdrachtsbeoordelingen vereisen aandacht',
            no_overdue_transfers: 'Geen achterstallige overdrachtsbeoordelingen',
            dpias_overview: 'GEB-overzicht',
            transfers_overview: 'Overzicht doorgiftebeoordelingen',
            overdue_items: 'Achterstallige items',
            overdue_dpias: 'Achterstallige GEB-beoordelingen',
            overdue_transfers: 'Achterstallige doorgiftebeoordelingen',
            count: 'Aantal',
            no_data: 'No gegevens available.',
            no_risks: "Geen risico's gevonden.",
            no_dashboard_data: 'No dashboard gegevens available.',
            fetch_error: 'Kan niet fetch dashboard gegevens. Probeer het opnieuw.',
        },
        dpias: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            continue: 'Continue',
            review: 'Review',
            section_basic_info: 'Basic Information',
            page_title: "DPIA's",
            announce_submitted_for_review: 'DPIA ingediend ter beoordeling',
            announce_deprecated: 'DPIA verouderd',
            announce_risks_loaded: '{count} risico(s) geladen',
            announce_mitigations_loaded: '{count} maatregel(en) geladen',
            announce_risk_deleted: 'Risico verwijderd',
            announce_mitigation_status_updated: 'Maatregelstatus bijgewerkt',
            aria_filter: 'DPIAs filteren',
            aria_filter_by_status: 'Filteren op status',
            aria_filter_by_lead: 'Filteren op beoordelingsleider',
            aria_table: 'Gegevensbeschermingseffectbeoordelingen',
            announce_found: '{count} DPIAs gevonden',
            announce_decision: 'DPIA {decision}',
            mitigation_start: 'Starten',
            mitigation_mark_implemented: 'Markeren als geïmplementeerd',
            mitigation_verify: 'Verifiëren',
            title: 'Gegevensbeschermingseffectbeoordelingen',
            new: 'Nieuwe GEB',
            edit: 'GEB bewerken',
            dpia_title: 'Titel',
            edit_dpia_aria: 'Edit DPIA: {title}',
            approve_or_reject_aria: 'Approve or reject: {title}',
            more_actions_for_aria: 'More actions for {title}',
            risk_details_aria: 'Risk details for {title}',
            edit_risk_aria: 'Edit risk: {title}',
            delete_risk_aria: 'Delete risk: {title}',
            mitigation_details_aria: 'Mitigation details for {title}',
            edit_mitigation_aria: 'Edit mitigation: {title}',
            dossier_filing_details_aria: 'Dossier filing details for {title}',
            activity: 'Activiteit',
            risk_score: 'Risicoscore',
            risks: "Risico's",
            mitigations: 'Maatregelen',
            add_risk: 'Risico toevoegen',
            add_mitigation: 'Maatregel toevoegen',
            necessity: 'Noodzakelijkheid / Evenredigheid',
            likelihood: 'Waarschijnlijkheid',
            impact: 'Impact',
            severity_label: 'Ernst',
            purpose: 'Doel',
            consultation_notes: 'Consultatie Opmerkingen',
            dpo_opinion: 'DPO-advies',
            processing_activity_ids: "Verwerking Activiteit ID's (comma-separated)",
            system_ids: "Systeem ID's (comma-separated)",
            vendor_ids: "Leverancier ID's (comma-separated)",
            data_types: 'Gegevens Types (comma-separated)',
            data_types_short: 'Gegevens Types',
            data_subjects: 'Gegevens Subjects (comma-separated)',
            assessment_lead_id: 'Beoordeling Leider ID',
            assessment_lead: 'Beoordeling Leider',
            dpo_id: 'FG-ID',
            reviewer_ids: "Beoordelaar ID's (comma-separated)",
            approval_mode: 'Goedkeuring Modus',
            approval_all: 'All',
            approval_any: 'Any',
            require_dpo_signoff: 'DPO-goedkeuring vereist',
            review_frequency_days: 'Review cadence (days)',
            review_frequency_days_hint: 'Optional. Days between reviews of this DPIA once it is approved. Leave empty and the DPIA gets no review date.',
            error_dpo_required: 'This DPIA requires a DPO sign-off. Choose the DPO before submitting it for review.',
            error_dpo_invalid: 'The DPO named on this DPIA is not an active user of this organisation. Choose an active DPO before submitting it for review.',
            error_dpo_cannot_approve: 'The DPO named on this DPIA does not hold the permission to approve DPIAs, so the sign-off could never be given. Choose a DPO who holds it before submitting it for review.',
            decision_awaiting_dpo: 'Your decision is recorded. The DPIA stays in review until its DPO signs it off.',
            withdraw_review: 'Withdraw from review',
            withdraw_review_hint: 'Return this DPIA to draft so it can be edited',
            withdraw_review_error: 'Failed to withdraw DPIA from review. Please try again.',
            announce_withdrawn: 'DPIA withdrawn from review',
            review_date: 'Beoordelen Datum',
            submit_for_review: 'Indienen voor Beoordelen',
            approve_reject: 'Goedkeuren/Afwijzen',
            reopen: 'Reopen as draft',
            reopen_hint: 'Start a new review round. The rejection is recorded on the record and no longer counts.',
            reopen_error: 'Could not reopen the DPIA. Please try again.',
            announce_reopened: 'DPIA reopened as a draft',
            decision_named_reviewers_note: 'This DPIA names its reviewers. A decision from anyone else is recorded in the approval log, but counts toward neither approval nor rejection.',
            decision_not_counted: 'Your decision was recorded, but it does not count: only the named reviewers decide this DPIA.',
            approval_incomplete_badge: 'Approval incomplete',
            approval_incomplete_note: 'This DPIA is marked approved, but its approval log does not show an approval from the named reviewers. It keeps its status. Whether to review it again is for your organisation to decide.',
            approval_rounds_count: 'Earlier review rounds recorded on this record: {count}',
            deprecate: 'Afschrijven',
            deprecated: 'Afgeschreven',
            hide_risks: "Verbergen Risico's",
            hide_mitigations: 'Verbergen Maatregelen',
            comments: 'Opmerkingen',
            submit_decision: 'Indienen Beslissing',
            category_label: 'Categorie',
            category_data_breach: 'Gegevens Inbreuk',
            category_unauthorized_access: 'Ongeautoriseerd Toegang',
            category_function_creep: 'Functie Creep',
            category_re_identification: 'Her-identificatie',
            category_discrimination: 'Discriminatie',
            category_surveillance: 'Bewaking',
            category_other: 'Overig',
            affected_rights: 'Getroffen rechten (kommagescheiden)',
            no_risks: "Geen risico's gevonden.",
            no_mitigations: 'Geen maatregelen gevonden.',
            no_dpias: 'Geen DPIAs gevonden.',
            mitigation_type: 'Soort',
            mitigation_technical: 'Technisch',
            mitigation_organizational: 'Organisatorisch',
            mitigation_contractual: 'Contractueel',
            risk_ids: "Risico ID's (comma-separated)",
            responsible_party: 'Verantwoordelijk Party',
            responsible_party_placeholder: 'Of voer naam handmatig in',
            affected_rights_placeholder: 'Geselecteerde rechten verschijnen hier',
            affected_rights_freetext_placeholder: 'Voer getroffen rechten in (kommagescheiden)',
            responsible: 'Verantwoordelijk',
            target_date: 'Doel Datum',
            created: 'Aangemaakt',
            fetch_error: 'Kan niet fetch DPIAs. Probeer het opnieuw.',
            save_error: 'Kan niet save DPIA. Probeer het opnieuw.',
            submit_review_error: 'Kan niet submit DPIA voor beoordeling. Probeer het opnieuw.',
            decision_error: 'Kan niet process beslissing. Probeer het opnieuw.',
            deprecate_error: 'Kan niet deprecate DPIA. Probeer het opnieuw.',
            fetch_risks_error: "Kan niet fetch risico's. Probeer het opnieuw.",
            save_risk_error: 'Kan niet save risico. Probeer het opnieuw.',
            delete_risk_error: 'Kan niet delete risico. Probeer het opnieuw.',
            fetch_mitigations_error: 'Kan niet fetch maatregelen. Probeer het opnieuw.',
            save_mitigation_error: 'Kan niet save maatregel. Probeer het opnieuw.',
            update_mitigation_status_error: 'Kan de maatregelstatus niet bijwerken. Probeer het opnieuw.',
            link_form_template: 'Link regelgevingsformuliersjabloon',
            link_form_template_placeholder: 'Selecteer een DPIA formuliersjabloon (optioneel)',
            link_form_template_none: 'Geen',
            linked_form: 'Gekoppeld formulier',
            linked_form_creating: 'Gekoppeld formulier maken...',
            linked_form_error: 'Kan gekoppeld formulierexemplaar niet maken. De DPIA is gemaakt zonder gekoppeld formulier.',
            view_linked_form: 'Bekijk formulier',
            announce_created_with_form: 'DPIA gemaakt met gekoppelde formulierinstantie',
            creation_mode: 'DPIA Creatiemodus',
            mode_standard: 'Standaard DPIA',
            mode_template: 'Gebruik een sjabloon voor regelgevingsformulieren',
            select_form_template: 'Selecteer Formuliersjabloon',
            create_and_fill_form: 'Formulier maken en invullen',
            template_sections_title: 'Sjabloonsecties',
            field_singular: 'veld',
            field_plural: 'velden',
            no_sections: 'Er zijn geen secties gevonden in deze sjabloon.',
            template_mode_hint: 'Na het aanmaken wordt u doorgestuurd om het volledige regelgevingsformulier in te vullen.',
            linked_dossier_filings: 'Gekoppelde dossierindieingen',
            no_linked_dossier_filings: 'Geen dossierindieingen gekoppeld aan deze DPIA.',
            view_dossier_filing: 'Indiening bekijken',
            hide_dossier_filings: 'Dossierindieingen verbergen',
            supporting_evidence: 'Ondersteunend bewijs',
            hide_supporting_evidence: 'Bewijs verbergen',
            no_supporting_evidence: 'Geen ondersteunend bewijsmateriaal bijgevoegd.',
            upload_supporting_evidence: 'Upload ondersteunend bewijsmateriaal',
            view_form_responses: 'Formulierreacties bekijken',
            regulatory_framework: 'Regelgevend kader',
            all_frameworks: 'Alle kaders',
            no_templates_for_market: 'Geen sjablonen beschikbaar voor de geselecteerde markt. Selecteer een andere markt of maak een sjabloon aan.',
            affected_rights_freetext_hint: 'No framework rights available — enter manually (comma-separated)',
            ai_draft_eyebrow: 'AI assist',
            deprecate_hint: 'Retire this DPIA without deleting it',
            inherent_risk_level: 'Inherent Risk',
            mitigation_contractual_hint: 'DPA clauses, SCCs, contractual obligations',
            mitigation_description_placeholder: 'Describe the mitigation measure in detail',
            mitigation_organizational_hint: 'Policies, training, procedures, staff awareness',
            mitigation_technical_hint: 'Encryption, pseudonymisation, access controls, system hardening',
            mitigation_title_placeholder: 'e.g. Encrypt data at rest',
            no_risks_to_link: 'No risks found for this DPIA. Add risks first.',
            score_high: 'High',
            score_low: 'Low',
            score_medium: 'Medium',
            score_very_high: 'Very High',
            score_very_low: 'Very Low',
            section_assessment_details: 'Assessment Details',
            section_implementation: 'Implementation',
            section_processing: 'Processing Details',
            section_scoring: 'Risk Scoring',
            section_team: 'Team & Approval',
            selected_rights_count: 'Selected',
            submit_for_review_hint: 'Advance this DPIA to review',
        },
        transfers: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            description_placeholder: 'Brief summary of this transfer',
            title_placeholder: 'e.g. EU–US CRM Data Transfer',
            vendor_id_placeholder: 'Vendor ID (optional)',
            page_title: 'Gegevensoverdrachten',
            announce_found: '{count} overdrachtsbeoordelingen gevonden',
            announce_submitted_for_review: 'Overdracht ingediend ter beoordeling',
            announce_deprecated: 'Overdracht verouderd',
            announce_decision: 'Overdracht {decision}',
            announce_risk_eval_completed: 'Risicobeoordeling voltooid',
            announce_safeguard_status_updated: 'Beschermingsstatus bijgewerkt',
            announce_safeguards_loaded: '{count} beschermingsmaatregel(en) geladen',
            aria_filter: 'Overdrachtsbeoordelingen filteren',
            aria_filter_by_status: 'Filteren op status',
            aria_filter_by_mechanism: 'Filteren op overdrachtsmechanisme',
            aria_table: 'Gegevensoverdrachtsbeoordelingen',
            title: 'Grensoverschrijdende doorgiftebeoordelingen',
            new: 'Nieuwe doorgifte',
            edit: 'Doorgifte bewerken',
            name: 'Naam',
            from_country: 'Van land',
            to_country: 'Naar land',
            safeguards: 'Waarborgen',
            risk_level: 'Risiconiveau',
            assessment_notes: 'Beoordelingsnotities',
            mechanism: 'Mechanisme',
            transfer_mechanism: 'Overdracht Mechanisme',
            transfer_mechanism_detail: 'Overdracht Mechanisme Detail',
            all_mechanisms: 'Alle Mechanisms',
            mechanism_other: 'Overig',
            data_types: 'Gegevens Types (comma-separated)',
            data_subjects: 'Gegevens Subjects (comma-separated)',
            purpose: 'Doel',
            recipient_name: 'Ontvanger Naam',
            create_success: 'Overdrachtsbeoordeling aangemaakt.',
            update_success: 'Overdrachtsbeoordeling bijgewerkt.',
            recipient_details: 'Ontvangergegevens',
            recipient_details_placeholder: 'Rol, locatie en systemen van de ontvanger en details over verdere doorgifte',
            vendor_id: 'Leverancier ID',
            processing_activity_ids: "Verwerking Activiteit ID's (comma-separated)",
            reviewer_ids: "Beoordelaar ID's (comma-separated)",
            approval_mode: 'Goedkeuring Modus',
            approval_all: 'All',
            approval_any: 'Any',
            require_dpo_signoff: 'DPO-goedkeuring vereist',
            review_date: 'Beoordelen Datum',
            created: 'Aangemaakt',
            deprecated: 'Afgeschreven',
            submit_for_review: 'Indienen voor Beoordelen',
            approve_reject: 'Goedkeuren/Afwijzen',
            reopen: 'Reopen as draft',
            reopen_hint: 'Start a new review round. The rejection is recorded on the record and no longer counts.',
            reopen_error: 'Could not reopen the transfer assessment. Please try again.',
            announce_reopened: 'Transfer assessment reopened as a draft',
            redetermine_mechanism: 'Re-determine mechanism',
            redetermine_mechanism_title: 'Re-determine the transfer mechanism',
            redetermine_mechanism_intro: 'This record is {status}, so it cannot be edited, but its transfer mechanism is waiting for a person to re-determine it. Choose a mechanism an installed framework declares. Only the mechanism is saved; the status and the approval record do not change.',
            redetermine_choose_first: 'Choose a transfer mechanism first.',
            redetermine_success: 'Transfer mechanism re-determined.',
            redetermine_error: 'Could not re-determine the transfer mechanism. Please try again.',
            withdraw_review: 'Withdraw from review',
            withdraw_review_hint: 'Return this transfer assessment to draft. The review round is closed and its decisions no longer count. Use this when a named reviewer can no longer decide.',
            withdraw_review_error: 'Could not withdraw the transfer assessment from review. Please try again.',
            announce_withdrawn: 'Transfer assessment withdrawn from review',
            decision_named_reviewers_note: 'This transfer assessment names its reviewers. A decision from anyone else is recorded in the approval log, but counts toward neither approval nor rejection.',
            decision_not_counted: 'Your decision was recorded, but it does not count: only the named reviewers decide this transfer assessment.',
            approval_incomplete_badge: 'Approval incomplete',
            approval_incomplete_note: 'This transfer assessment is marked approved, but its approval log does not show an approval from the named reviewers. It keeps its status, but a cross-border identity exchange flow cannot rest on it. Whether to review it again is for your organisation to decide.',
            approval_rounds_count: 'Earlier review rounds recorded on this record: {count}',
            deprecate: 'Afschrijven',
            risk_eval: 'Risico Eval',
            hide_safeguards: 'Verbergen Waarborgen',
            mechanism_proof: 'Mechanisme bewijs',
            hide_mechanism_proof: 'Mechanismebestendig verbergen',
            mechanism_proof_hint: 'Upload the signed document the transfer rests on (for example the contract, or the impact-assessment dossier your installed frameworks require) to prove this transfer assessment.',
            no_mechanism_proof: 'Geen mechanismebewijs bevestigd.',
            upload_mechanism_proof: 'Bewijs van uploadmechanisme',
            comments: 'Opmerkingen',
            submit_decision: 'Indienen Beslissing',
            data_sensitivity: 'Gegevens Sensitivity (1-5)',
            country_risk: 'Land Risico (1-5)',
            safeguard_adequacy: 'Waarborg Adequaatheid (1-5)',
            volume_factor: 'Volumefactor (1-5)',
            evaluate: 'Evalueren',
            add_safeguard: 'Toevoegen Waarborg',
            responsible_party: 'Verantwoordelijk Party',
            responsible_party_placeholder: 'Of voer naam handmatig in',
            responsible: 'Verantwoordelijk',
            implementation_date: 'Implementatie Datum',
            implementation: 'Implementatie',
            expiry_date: 'Vervaldatum Datum',
            expiry: 'Vervaldatum',
            expire: 'Verlopen',
            activate_unavailable_expired: 'An expired safeguard cannot be reactivated. Record a new safeguard instead.',
            no_safeguards: 'Geen waarborgen gevonden.',
            no_transfers: 'Geen overdracht assessments gevonden.',
            fetch_error: 'Kan niet fetch overdracht assessments. Probeer het opnieuw.',
            save_error: 'Kan niet save overdracht beoordeling. Probeer het opnieuw.',
            submit_review_error: 'Kan niet submit voor beoordeling. Probeer het opnieuw.',
            decision_error: 'Kan niet process beslissing. Probeer het opnieuw.',
            deprecate_error: 'Kan niet deprecate overdracht. Probeer het opnieuw.',
            risk_eval_error: 'Kan niet evaluate risico. Probeer het opnieuw.',
            fetch_safeguards_error: 'Kan niet fetch waarborgen. Probeer het opnieuw.',
            save_safeguard_error: 'Kan niet save waarborg. Probeer het opnieuw.',
            update_safeguard_status_error: 'Kan de waarborgstatus niet bijwerken. Probeer het opnieuw.',
            safeguard_type_encryption_in_transit: 'Versleuteling in Transit',
            safeguard_type_encryption_at_rest: 'Versleuteling bij Rest',
            safeguard_type_access_controls: 'Toegang Controles',
            safeguard_type_contractual_clauses: 'Contractueel Clausules',
            safeguard_type_data_minimization: 'Gegevens Minimization',
            safeguard_type_pseudonymization: 'Pseudonimisering',
            safeguard_type_audit_rights: 'Auditrechten',
            safeguard_type_breach_notification: 'Inbreuk Melding',
            safeguard_type_data_localization: 'Gegevens Lokalisatie',
            safeguard_type_other: 'Overig',
            activate: 'Activeren',
            all_statuses: 'Alle statussen',
            any: 'Any',
            mechanism_detail: 'Mechanisme Detail',
            safeguard_types: {
                encryption_in_transit: 'Versleuteling tijdens overdracht',
                encryption_at_rest: 'Versleuteling in rust',
                access_controls: 'Toegangscontroles',
                contractual_clauses: 'Contractuele clausules',
                data_minimization: 'Gegevensminimalisatie',
                pseudonymization: 'Pseudonimisering',
                audit_rights: 'Auditrechten',
                breach_notification: 'Inbreukmelding',
                data_localization: 'Gegevenslokalisatie',
                other: 'Overig',
            },
            activity_ids_placeholder: 'Activity IDs (optional)',
            deprecate_hint: 'Retire this assessment without deleting it',
            file_dossier: 'File Dossier',
            file_dossier_hint: 'Start a regulatory filing from this assessment',
            mark_implemented: 'Implement',
            mark_verified: 'Verify',
            mechanism_detail_placeholder: 'e.g. the contract or document the transfer rests on',
            purpose_placeholder: 'e.g. CRM, analytics, support',
            csv_hint: '(comma-separated)',
            data_subjects_placeholder: 'e.g. employees, customers',
            data_types_placeholder: 'e.g. name, email, phone',
            from_country_placeholder: 'e.g. EU',
            to_country_placeholder: 'e.g. US',
            recipient_placeholder: 'e.g. Salesforce Inc.',
            reviewers_placeholder: 'Select reviewers…',
            risk_eval_hint: 'Score data sensitivity, country & safeguards',
            section_mechanism: 'Mechanism & Data',
            section_review: 'Review & Approval',
            section_route: 'Transfer Route',
        },
        gap_report: {
            page_title: 'Beoordelingskloofsrapport',
            announce_loaded: 'Kloofsrapport geladen. {count} kloof(en) geïdentificeerd',
            aria_activities_without_dpia: 'Activiteiten zonder DPIA-beoordeling',
            aria_activities_unclassified: 'Activiteiten met ongeclassificeerde DPIA-vereiste',
            activities_unclassified_section_title: 'Activiteiten met ongeclassificeerde DPIA-vereiste',
            activities_unclassified_description: 'Deze activiteiten zijn niet beoordeeld om te bepalen of een DPIA wettelijk verplicht is. Classificeer elke activiteit om het signaal op te heffen.',
            activities_unclassified_empty: 'Alle actieve activiteiten zijn geclassificeerd.',
            aria_unmitigated_risks: 'Onbehandelde hoge en kritieke risicos',
            aria_transfers_without_safeguards: 'Overdrachten zonder voldoende beschermingsmaatregelen',
            aria_expired_assessments: 'Verlopen beoordelingen die vernieuwing vereisen',
            title: 'Lacunerapport beoordelingen',
            no_dpia: 'Activiteiten zonder GEB',
            no_assessment: 'Doorgiften zonder beoordeling',
            unmitigated: "GEB's met onbehandelde risico's",
            activity_id: 'Activiteit ID',
            dpia_id: 'DPIA-nummer',
            assessment_id: 'Beoordeling ID',
            expired_assessments: 'Verlopen Beoordelingen',
            no_gaps_activities: 'Geen hiaten gevonden Alle activities have DPIAs..',
            no_unmitigated_risks: "Geen unmitigated high/critical risico's gevonden.",
            no_gaps_safeguards: 'Geen hiaten gevonden Alle overdrachten have adequate waarborgen..',
            no_expired: 'Geen expired assessments gevonden.',
            no_report_data: 'No hiaat report gegevens available.',
            fetch_error: 'Kan niet fetch hiaat report. Probeer het opnieuw.',
            no_expired_assessments: 'Geen verlopen beoordelingen.',
            no_gaps_dpia: 'Geen DPIA hiaten gevonden.',
            no_data: 'No gegevens available.',
            all_clear: 'No compliance gaps detected.',
            list_truncated_notice: 'Only the first 50 activities are listed; more match. Resolve these, then reload the report to see the rest.',
            subtitle: 'Identify compliance gaps across DPIAs, risks, transfers, and assessments.',
        },
        dossier_filings: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            aria_filters: 'Filter dossier filings',
            description_placeholder: 'Optional summary of this filing',
            title_placeholder: 'e.g. DPIA Filing – Annex I',
            page_title_detail: 'Dossierindiening detail',
            page_title_list: 'Dossierindiening zaken',
            announce_filings_found: '{count} indiening(en) gevonden',
            announce_results: '{count} resultaten',
            aria_filter_by_status: 'Filteren op status',
            title: 'Dossierdossiers',
            nav_label: 'Dossierdossiers',
            create: 'Dossierarchivering maken',
            form_type: 'Formuliertype',
            phu_luc_i_label: 'Standard assessment workflow (internal)',
            phu_luc_ii_label: 'Cross-border / high-risk assessment workflow (internal)',
            dpia_reference: 'DPIA Referentie',
            transfer_reference: 'Transfer Assessment Reference',
            transfer_select_placeholder: 'Link to a transfer assessment',
            arrived_parent_heading: 'Filing for',
            prior_filing_heading: 'This organisation has already filed this dossier',
            prior_filing_body: 'The installed framework provides for one dossier of this kind per organisation, updated afterwards ({citation}). This organisation already filed one: {filing}. For a new purpose or another change to its content, start the update on this assessment instead. You can still open a first filing, for example to file again after the authority assessed the earlier dossier as not meeting the requirements.',
            prior_filing_start_update: 'Start the update: {form}',
            prior_filing_no_update_form: 'No update form is installed for this kind of dossier.',
            arrived_parent_loading: 'Looking up the assessment record…',
            arrived_parent_unresolved: 'The assessment this filing was started from could not be found. It may have been deleted, or it may belong to another organisation. Choose a parent below before filing.',
            parent_none: 'No assessment linked',
            parent_unresolved: 'linked record not found ({id})',
            error_parent_required: 'Choose the assessment this filing discharges — either an impact assessment or a transfer assessment.',
            error_transfer_parent_not_found: 'The transfer assessment this filing names no longer exists. Pick another one, or reopen the filing from the transfer register.',
            error_dpia_parent_not_found: 'The impact assessment this filing names no longer exists. Pick another one.',
            submitted_date: 'Ingediende datum',
            outcome: 'Resultaat',
            parent_filing: 'Ouder',
            no_filings: 'Geen dossierdossiers gevonden.',
            fetch_error: 'Kan dossierdossiers niet ophalen. Probeer het opnieuw.',
            save_error: 'Kan dossierarchivering niet maken. Probeer het opnieuw.',
            created: 'Dossierdossier aangemaakt.',
            loaded: 'Dossierdossier geladen.',
            not_found: 'Dossierdossier niet gevonden.',
            back_to_list: 'Terug naar lijst',
            section_assignments: 'Sectie-opdrachten',
            assign_sections: 'Secties toewijzen',
            section_id: 'Sectie',
            department: 'Afdeling',
            assignee: 'Ontvanger',
            deadline: 'Deadline',
            no_assignments: 'Nog geen sectieopdrachten.',
            sections_assigned_msg: 'Secties toegewezen.',
            assign_error: 'Kan secties niet toewijzen. Probeer het opnieuw.',
            lifecycle: 'Levenscyclus van archivering',
            submission: 'Inzendingsdetails',
            submission_channel: 'Inzendingskanaal',
            confirmation_number: 'Bevestigingsnummer',
            // Fix sprint #447 (round 2) — the Submit dialog offers the declared channels.
            select_channel: 'Select a channel',
            submission_note: 'This records the submission your organisation made. The platform files nothing with the authority.',
            channels_load_failed: 'The submission channels could not be read.',
            channel_refusal_no_framework: 'No installed framework declares an authority filing, so no submission can be recorded.',
            channel_refusal_no_channels: 'The filing framework declares no submission channels, so no submission can be recorded.',
            channel_refusal_no_authority: 'The filing framework names no authority that accepts filings, so no submission can be recorded.',
            status_submitted: 'Filed — no result recorded',
            submit_for_review: 'Indienen ter beoordeling',
            submitted_for_review: 'Voorgelegd voor interne beoordeling.',
            submit_review_error: 'Kan niet ter beoordeling worden ingediend. Probeer het opnieuw.',
            legal_signoff: 'Juridische ondertekening',
            legal_signoff_done: 'Juridische ondertekening voltooid.',
            legal_signoff_error: 'Kan de juridische ondertekening niet voltooien. Probeer het opnieuw.',
            approve: 'Approve internally',
            approved_msg: 'Filing approved internally. It has not been filed with the authority.',
            approve_error: 'Kan de indiening niet goedkeuren. Probeer het opnieuw.',
            materialisation_status_column: 'Inzending artefact',
            materialisation_na: 'Niet van toepassing',
            materialisation_pending: 'In behandeling',
            materialisation_success: 'Gematerialiseerd',
            materialisation_failed: 'Mislukt',
            materialisation_banner_title: 'Inzendingsartefact kan niet worden gemaakt',
            materialisation_banner_body: 'The filing was approved internally, but the underlying dossier record could not be created. Cover-letter and submission exports are unavailable until you retry.',
            materialisation_retry_button: 'Opnieuw proberen',
            materialisation_retry_count: 'Eerdere pogingen:',
            materialisation_retry_success: 'Het inzendingsartefact is met succes gerealiseerd.',
            materialisation_retry_announce: 'Opnieuw proberen gelukt',
            filter_materialisation_failed: 'Toon alleen materialisatiefouten',
            column_title: 'Titel',
            column_form_type: 'Vorm',
            submit_to_mps: 'Record filing with the authority',
            submitted_to_mps_msg: 'Your organisation’s submission to the authority is recorded.',
            submit_mps_error: 'Failed to record the filing with the authority. Please try again.',
            supplement_request: 'Aanvraag voor aanvulling',
            supplement_requests: 'Aanvullingsverzoeken',
            supplement_response: 'Aanvullend antwoord',
            record_supplement: 'Aanvraag supplement vastleggen',
            supplement_recorded: 'Supplementaanvraag geregistreerd.',
            supplement_absence_declaring_framework_unreadable: 'No deadline: the framework that states the period could not be read.',
            supplement_absence_declaring_framework_not_in_force: 'No deadline: the framework that states the period is not yet in force.',
            supplement_absence_uncomputable_period: 'No deadline: the installed period cannot be counted as a date.',
            supplement_absence_multiple_candidate_clocks: 'No deadline: more than one installed period could apply and the platform does not choose between them. Enter the deadline stated in the authority’s letter.',
            supplement_absence_no_installed_clock: 'No deadline: no installed framework states a period for responding.',
            supplement_absence_receipt_not_evidenced: 'No deadline: the date the applicant received the authority’s result has not been recorded.',
            supplement_absence_declaring_framework_reference_mode: 'No deadline: the framework that states the period is installed as Reference only, so its clocks are not run.',
            supplement_absence_declaring_framework_not_operational: 'No deadline: the framework that states the period is superseded or retired, so it no longer sets a deadline.',
            supplement_deadline_warning_differs_from_installed: 'The date you entered falls on a different day from the one the installed period gives. The date from the authority’s letter stands.',
            supplement_deadline_warning_in_the_past: 'The date you entered was already past when it was recorded.',
            supplement_deadline_platform_estimate: 'Platform estimate recorded earlier: not a period stated by an installed framework or by the authority’s letter.',
            supplement_deadline_installed_due: 'The installed period gives {date}.',
            supplement_deadline_none: 'No deadline is recorded for this request.',
            supplement_deadline_source_unknown: 'The source of this deadline was not recorded',
            supplement_deadline_source_operator_anon: 'Stated in the authority’s letter; entered by a user',
            supplement_deadline_source_operator: 'Stated in the authority’s letter; entered by {user}',
            supplement_deadline_source_installed: 'Counted from the date of receipt, using the period in the installed framework',
            supplement_response_received: 'Responded',
            supplement_response_pending: 'Awaiting response',
            supplement_response_deadline: 'Response deadline',
            supplement_items_requested: 'Items requested',
            supplement_received_future: 'The date of receipt cannot be in the future.',
            supplement_deadline_help: 'A completion period is counted from a communication your organisation recorded, once you state that it asks for the dossier to be completed, or that it states the dossier is incomplete, invalid or not in accordance with the regulations. With a deadline stated in the authority’s letter, the platform records that deadline and who entered it. With neither, the request has no deadline and the page says why.',
            supplement_response_deadline_input: 'Deadline stated in the authority’s letter (optional)',
            supplement_received_at: 'Date the applicant received the authority’s result',
            supplement_items: 'Items the authority requested (one per line)',
            supplement_error: 'Kan aanvullingsverzoek niet opnemen. Probeer het opnieuw.',
            submit_supplement: 'Aanvullend antwoord indienen',
            supplement_submitted_msg: 'Aanvullende reactie ingediend.',
            supplement_submit_error: 'Het is niet gelukt om een aanvullingsreactie in te dienen. Probeer het opnieuw.',
            response_status: 'Reactiestatus',
            response_notes: 'Reactienotities',
            resubmit: 'Opnieuw indienen',
            resubmitted_msg: 'Dossier opnieuw ingediend.',
            resubmit_error: 'Het dossier kan niet opnieuw worden ingediend. Probeer het opnieuw.',
            outcome_error: 'Het is niet gelukt om de uitkomst vast te leggen. Probeer het opnieuw.',
            accepted: 'Geaccepteerd',
            rejected: 'Afgewezen',
            close: 'Sluiten',
            closed_msg: 'Indiening gesloten.',
            close_error: 'Kan de dossieraanvraag niet sluiten. Probeer het opnieuw.',
            change_filing: 'Wijzigingsdossier aanmaken',
            change_filing_created: 'Wijzigingsdossier aangemaakt.',
            change_filing_error: 'Kan wijzigingsaanvraag niet aanmaken. Probeer het opnieuw.',
            change_reason: 'Reden voor verandering',
            filing_lineage: 'Afstammingslijn indienen',
            timers: 'Actieve timers',
            timer_mps_review: 'Authority review',
            timer_supplement_response: 'Aanvullingstermijn',
            timer_resubmission_review: 'Deadline voor herindiening',
            timer_department_input: 'Deadline van de afdeling',
            days_remaining: 'dagen resterend',
            days_overdue: 'dagen te laat',
            cover_letter: 'Sollicitatiebrief',
            checklist: 'Controlelijst',
            evidence_index: 'Bewijsindex',
            status_draft: 'Concept',
            status_internal_review: 'Interne beoordeling',
            status_legal_signoff: 'Juridische ondertekening',
            status_approved: 'Approved internally — not filed',
            status_supplement_requested: 'Aanvulling gevraagd',
            status_supplement_submitted: 'Aanvulling ingediend',
            status_resubmitted: 'Opnieuw ingediend',
            status_accepted: 'Result recorded: Accepted',
            status_rejected: 'Result recorded: Rejected',
            status_closed: 'Gesloten',
            // Sprint 62.57.0 (gate E12.5 pass 1, D10) — step_approved, step_submitted,
            // step_accepted and step_rejected are English until Phase 25 (R-57); the
            // other step_* keys keep this locale's translation.
            step_draft: 'Concept',
            step_internal_review: 'Interne beoordeling',
            step_legal_signoff: 'Juridische ondertekening',
            step_approved: 'Approved internally',
            step_submitted: 'Filed',
            step_supplement_requested: 'Aanvulling gevraagd',
            step_supplement_submitted: 'Aanvulling ingediend',
            step_resubmitted: 'Opnieuw ingediend',
            step_accepted: 'Result recorded: Accepted',
            step_rejected: 'Result recorded: Rejected',
            step_closed: 'Gesloten',
            linked_dpia: 'Gekoppelde DPIA',
            linked_dpia_none: 'Geen DPIA gekoppeld aan deze indiening.',
            linked_parents: 'Linked assessment',
            linked_parents_none: 'This filing names no assessment.',
            parent_kind_dpia: 'Impact assessment',
            parent_kind_transfer: 'Transfer assessment',
            view_linked_dpia: 'DPIA bekijken',
            linked_dpia_label: 'Deze dossierindiening is gekoppeld aan de volgende DPIA:',
            all_sections_must_complete: 'All sections must be completed or reviewed before submitting',
            change_section_status: 'Change status for',
            dpia_select_placeholder: 'Link to a DPIA (optional)',
            no_departments_hint: 'No departments available. You can still assign by selecting a user below.',
            no_users_hint: 'Unable to load users. Please check your connection and try again.',
            phu_luc_i_hint: 'Internal workflow category. Statutory processing notices are Mẫu số 02a for organisations and 02b for individuals, with report 10; cross-border notices are 01a/01b, with report 09. Select the formal form separately.',
            phu_luc_ii_hint: 'Internal workflow category. Statutory processing notices are Mẫu số 02a for organisations and 02b for individuals, with report 10; cross-border notices are 01a/01b, with report 09. Select the formal form separately.',
            section_add_new: 'Add new section...',
            section_assignments_explanation: 'Assign sections of this dossier filing to departments for completion. Each assigned section creates a task for the assignee with a deadline. All sections must be completed before the filing can proceed to internal review.',
            section_consultation: 'Consultation & Stakeholders',
            section_custom_placeholder: 'Enter custom section name',
            section_data_categories: 'Data Categories & Processing',
            section_data_subject_rights: 'Data Subject Rights',
            section_impact_assessment: 'Impact Assessment',
            section_legal_basis: 'Legal Basis & Necessity',
            section_risk_mitigation: 'Risk Mitigation Measures',
            section_status_label: 'Section status',
            section_status_updated: 'Section status updated to',
            transition_to: 'Transition to...',
            view_filing: 'View',
            // Sprint 63.37.0 (Stream J) — the forms a filing files, and the dossier assembled from it.
            filing_forms: 'Forms in this filing',
            filing_forms_none: 'No form is linked to this filing yet.',
            filing_forms_failed: 'The forms of this filing could not be read.',
            filing_form_label: 'Form this filing records',
            assembled_dossier: 'Assembled dossier {reference}',
            dossier_role_primary_application: 'Primary form of the dossier',
            dossier_role_supporting_document: 'Supporting document',
            dossier_role_authority_response: 'Authority response',
            dossier_role_supplement: 'Supplement',
            // Sprint 63.37.0 (Stream K, UC-VN-01) — the person who recorded the submission; the platform records, the organisation files.
            submission_recorded_by: 'Recorded by',
            submission_notes: 'Notes',
            // Sprint 63.37.0 (Stream K, UC-VN-01) — the four section statuses (DossierSectionStatus); the table printed the stored code.
            section_status_assigned: 'Assigned',
            section_status_in_progress: 'In Progress',
            section_status_completed: 'Completed',
            section_status_reviewed: 'Reviewed',
            timer_estimate_passed: '{days} days past the estimate',
            timer_platform_estimate: 'Platform estimate — not a statutory period',
            reload: 'Reload',
            reloading: 'Reloading…',
            timers_load_failed: 'The filing timers could not be read, so none are shown. That does not mean the filing has none.',
            retry_subject_timers: 'the filing timers',
            lineage_load_failed: 'The filing lineage could not be read, so earlier and later filings are not shown.',
            retry_subject_lineage: 'the filing lineage',
            form_status_load_failed: 'The status of the forms could not be read, so the Forms column may be incomplete.',
            form_status_not_read: 'Not read',
            retry_subject_form_status: 'the form status',
            supplement_absence_declaring_framework_phase_reference_unresolved: 'No deadline: the period applies from a phase of its framework, and the framework does not date that phase.',
            // Sprint 62.57.0 (Slice R) — authority communications on a filing, recorded as
            // documents; the limitation notice; the latest-communication column and filter.
            result_recorded_qualifier: 'as your organisation recorded it from the authority’s notice',
            limitation_heading: 'What ComplianceOne records about the authority',
            limitation_body: 'ComplianceOne records the authority’s written communications as documents, and records the handling body, the result and any completion request as your organisation states them from the authority’s communications, and an update’s recipient and competent authority as your organisation records them. Completion dates are provisional where the governing text’s start event is pending legal confirmation, and ComplianceOne never decides which body handles a dossier or an update.',
            comms_heading: 'Authority communications',
            comms_intro: 'The written communications your organisation received from an authority about this filing, as a person in your organisation recorded them. Recording one changes nothing else on the filing: no status, result or deadline.',
            comms_record: 'Record a communication',
            comms_export: 'Export communications',
            comms_export_done: 'The communications were exported.',
            comms_export_failed: 'The communications could not be exported.',
            comms_none: 'No communication has been recorded on this filing.',
            comms_loading: 'Loading the communications…',
            comms_load_failed: 'The communications could not be read, so none are shown. That does not mean none were recorded.',
            retry_subject_comms: 'the authority communications',
            comms_pagination: 'Pages of authority communications',
            comms_col_kind: 'Kind',
            comms_col_sender: 'Sender',
            comms_col_communication_date: 'Date on the communication',
            comms_col_received_at: 'Received',
            comms_col_wording: 'The authority’s wording',
            comms_col_evidence: 'Evidence',
            comms_col_correction: 'Correction',
            comms_col_recorded: 'Recorded',
            comms_row_statement: 'Recorded by {person} from the authority’s communication — ComplianceOne has not verified it.',
            comms_reference: 'Reference: {reference}',
            comms_stated_items: 'Items stated',
            comms_stated_period: 'Period stated: {period}',
            comms_notes: 'Notes: {notes}',
            comms_no_wording: 'No wording recorded',
            comms_not_evidenced: 'Not evidenced',
            comms_evidence_count: '{count} file(s)',
            comms_evidence_open: 'Files',
            comms_evidence_open_label: 'Evidence files of the communication received on {date}',
            comms_evidence_load_failed: 'The evidence files could not be read.',
            comms_corrects: 'Corrects the entry of {date}',
            comms_corrects_unknown: 'Corrects an earlier entry',
            comms_corrected_by: 'Corrected by a later entry',
            comms_export_corrects: 'Corrects the entry received {date} ({kind})',
            comms_export_corrected_by: 'Corrected by a later entry (received {date})',
            comms_correction_note: 'Why: {note}',
            comm_kind_receipt_acknowledgement: 'Receipt acknowledgement',
            comm_kind_transfer_notice: 'Transfer notice',
            comm_kind_result_notice: 'Result notice',
            comm_kind_supplement_request: 'Supplement request',
            comm_kind_refusal: 'Refusal',
            comm_kind_other_written_communication: 'Other written communication',
            comms_form_intro: 'Record what the authority sent, as it is written. This record changes no status, result or deadline on the filing.',
            comms_form_evidence_hint: 'Evidence files are attached after the communication is recorded, from its row in the history.',
            comms_field_kind: 'Kind of communication',
            comms_field_kind_placeholder: 'Choose the kind',
            comms_field_sender: 'Sender, as the communication names it',
            comms_field_reference: 'Reference on the communication',
            comms_field_communication_date: 'Date on the communication',
            comms_field_received_at: 'Date your organisation received it',
            comms_field_wording: 'The authority’s wording, verbatim',
            comms_field_wording_hint: 'Up to {max} characters. Required for a result notice or a refusal.',
            comms_field_wording_count: '{count} of {max} characters used',
            comms_field_items: 'Items the communication states',
            comms_field_item: 'Item {n}',
            comms_add_item: 'Add an item',
            comms_remove_item: 'Remove item {n}',
            comms_items_none: 'No item added.',
            comms_field_period: 'Period the communication states, verbatim',
            comms_field_period_hint: 'Recorded as written. No deadline is computed from it.',
            comms_field_notes: 'Internal notes',
            comms_field_corrects: 'Corrects an earlier entry',
            comms_field_corrects_none: 'Not a correction',
            comms_field_corrects_option: '{kind}, received {date}',
            comms_field_corrects_truncated: 'Only the {count} most recent entries are listed.',
            comms_field_corrects_failed: 'The earlier entries could not be read, so none can be chosen.',
            comms_field_correction_note: 'Why the earlier entry is corrected',
            comms_error_required: 'Enter this before recording.',
            comms_error_kind_required: 'Choose the kind of communication.',
            comms_error_wording_required: 'Enter the authority’s wording for a result notice or a refusal.',
            comms_error_correction_note_required: 'Say why the earlier entry is corrected.',
            comms_error_receipt_invalid: "The date of receipt must not be later than today in your organisation's time zone.",
            comms_error_not_submitted: 'This filing has not been filed, so no communication can be recorded. The page has updated the filing’s details; what you entered is kept.',
            comms_record_failed: 'The communication could not be recorded. What you entered is kept.',
            comms_recorded: 'Communication recorded.',
            comms_recording: 'Recording…',
            comms_files_heading: 'Evidence files',
            comms_files_intro: 'The files this communication rests on, as your organisation attached them. ComplianceOne has not verified them.',
            comms_files_slot: 'Files of this communication',
            comms_files_none: 'No file is attached. This communication is not evidenced.',
            comms_file_hash: 'SHA-256: {hash}',
            comms_file_not_held: 'File no longer held',
            comms_file_download: 'Download',
            comms_file_remove: 'Remove',
            comms_file_upload: 'Attach a file',
            comms_file_bound: 'File attached.',
            comms_file_removed: 'File removed.',
            comms_file_bind_failed: 'The file could not be attached.',
            comms_file_remove_failed: 'The file could not be removed.',
            comms_file_not_owned: 'The communication this file belongs to was not found.',
            comms_file_remove_confirm: 'Remove this file from the communication and delete it?',
            latest_communication: 'Latest communication',
            latest_communication_none: 'None recorded',
            latest_communication_value: '{kind}, received {date}',
            aria_filter_by_communication: 'Filter by latest communication',
            filter_communication_none: 'No communication recorded',
            comms_export_heading: 'Communications recorded by the organisation',
            comms_export_filing: 'Filing: {filing}',
            comms_export_entries: 'Entries: {count}',
            comms_export_col_reference: 'Reference',
            comms_export_col_statement: 'Recorded',
            comms_export_truncated: 'Only the newest {shown} of {total} entries are included.',
            // Sprint 62.57.0 (Slices A and B) — the governing text and receipt, the handling
            // authority, the result from pack options, the post-result supplement and follow-up,
            // the provisional authority period and its legal reservation.
            // English until this locale is translated (R-57).
            status_result_recorded: 'Result recorded',
            status_result_supplement_required: 'Result recorded — supplement required',
            status_result_supplement_submitted: 'Result recorded — supplement submitted',
            step_result_recorded: 'Result recorded',
            step_result_supplement_required: 'Supplement required after the result',
            step_result_supplement_submitted: 'Supplement submitted after the result',
            timer_authority_review: 'Authority review',
            timer_provisional: 'Provisional — pending legal confirmation',
            timer_obligation_authority: 'The authority’s period, not your organisation’s',
            timer_calendar: 'Counted on the installed public-service calendar of working days and public holidays',
            timer_authority_stated: 'Result date stated by the authority',
            timer_provisional_passed: 'Provisional period passed — no result recorded',
            timer_absence_complete_receipt: 'No provisional date — complete-dossier receipt not recorded',
            timer_absence_calendar: 'No provisional date — the public-service calendar for {year} is not installed',
            timer_absence_no_period: 'No authority period declared',
            timer_absence_governing_text: 'No provisional date — governing text not determined',
            timer_absence_multiple_periods: 'No provisional date — the installed content declares more than one period, and none is chosen',
            timer_absence_uncomputable: 'No provisional date — the declared period cannot be counted as a date',
            timer_absence_calendar_not_declared: 'No provisional date — the declared period names no calendar to count on',
            timer_absence_calendar_unavailable: 'No provisional date — the public-service calendar is not installed',
            timer_absence_reference_mode: 'No provisional date — the content that states the period is installed for reference only',
            timer_absence_unreadable: 'No provisional date — the content that states the period could not be read',
            timer_absence_reappraisal_not_declared: 'No provisional date after the completion — not declared',
            timer_absence_completed_receipt: 'No provisional date — receipt of the completed dossier not recorded',
            timer_period_closed: 'A result is recorded; the period no longer runs',
            timer_absence_heading: 'Authority review',
            reservation_pending: 'Pending legal confirmation',
            comm_pick_empty: 'No communication is recorded on this filing yet. Record it under Authority communications first.',
            comm_pick_failed: 'The communications on this filing could not be read.',
            comm_pick_manual_label: 'Communication record id',
            comm_pick_manual_hint: 'Enter the id of a communication recorded on this filing; it is checked when you save.',
            comm_pick_off_list: 'Communication {value} (not in the list)',
            comm_pick_none: 'No communication',
            comm_pick_option_reference: '{kind}, received {date}, reference {reference}',
            error_communication_invalid: 'Choose a communication recorded on this filing.',
            error_corrected_entry_not_found: 'The entry this corrects was not found on this filing. Choose it again.',
            error_entry_already_corrected: 'That entry has already been corrected. Correct the later entry instead.',
            correction_target_moved: 'The entry you chose was corrected by another entry; this now corrects that entry. Check it before you save.',
            correction_target_required: 'Choose the entry this corrects.',
            receipt_error_already_recorded: 'A receipt of this kind is already recorded. To change it, correct that entry.',
            error_assignee_not_in_organisation: 'A person chosen is not a member of your organisation. Choose someone else.',
            error_filing_changed: 'The filing changed while you were entering this. The page has read it again; what you entered is kept. Save again.',
            error_not_submitted: 'This filing has not been filed, so this cannot be recorded. The page has updated the filing’s details; what you entered is kept.',
            error_field_refused: 'This value was not accepted. Check it and try again.',
            gov_heading: 'Governing text',
            gov_intro: 'The text that governed this dossier when the authority received it, matched from the receipt date your organisation recorded. It is never worked out from the date the filing was created or submitted.',
            gov_not_determined: 'Governing text not determined — record the receipt date',
            gov_reason_receipt_not_evidenced: 'No receipt date has been recorded.',
            gov_reason_no_declaration: 'The installed content declares no governing text for this kind of dossier.',
            gov_reason_receipt_outside_declared_windows: 'The receipt date falls outside every period the installed content declares.',
            gov_reason_declaration_unreadable: 'The installed declaration could not be read.',
            gov_requirement: 'Practice requirement: {title}',
            gov_requirement_framework: 'Practice requirement: {title} ({framework})',
            gov_receipt_date: 'Receipt date used',
            gov_evidenced_by: 'Receipt recorded by',
            gov_end_label: 'End of this period',
            gov_declared_by: 'Declared by',
            gov_declared_by_not_installed: 'A framework that is not installed',
            gov_review_required: 'The stored governing text no longer matches the installed declaration — review.',
            receipt_heading: 'Receipt by the authority',
            receipt_record: 'Record the receipt date',
            receipt_correct: 'Correct the receipt date',
            receipt_form_intro: 'Record the date the authority received the dossier, as its receipt or your evidence shows it. The date decides which text governs the dossier. A correction is a new entry; the earlier one stays in the history.',
            receipt_field_event: 'What the authority received',
            receipt_event_dossier_received: 'Dossier received',
            receipt_event_complete_dossier_received: 'Complete and valid dossier received',
            receipt_field_received_on: 'Date the authority received it',
            receipt_field_postmarked_on: 'Postmark date (dossier sent by post)',
            receipt_field_postmarked_hint: 'Only for a dossier sent by post. Kept beside the receipt date, and read only where the installed text counts the postmark.',
            receipt_field_communication: 'Communication that evidences it (optional)',
            receipt_field_corrects: 'Entry this corrects',
            receipt_field_corrects_none: 'Not a correction',
            receipt_corrects_option: '{event}, {date}',
            receipt_error_date_invalid: 'The date cannot be later than today, and the dates must run in order: postmark, dossier received, then complete and valid dossier received.',
            receipt_error_event_required: 'Choose what the authority received.',
            receipt_error_event_mismatch: 'A correction must state the same thing the authority received as the entry it corrects.',
            receipt_record_failed: 'The receipt could not be recorded. What you entered is kept.',
            receipt_recorded: 'Receipt date recorded.',
            receipt_history: 'Receipt entries',
            receipt_col_event: 'Event',
            receipt_col_received_on: 'Received on',
            receipt_col_postmarked_on: 'Postmark',
            receipt_col_communication: 'Communication',
            receipt_col_correction: 'Correction',
            receipt_col_recorded: 'Recorded',
            receipt_source_submission_acknowledgment: 'Carried over from the date of the authority’s acknowledgement of receipt, recorded with the submission',
            receipt_row_statement: 'Recorded by {person} — ComplianceOne has not verified it.',
            handling_heading: 'Handling authority',
            handling_intro: 'The body the dossier was filed with is the one the installed form names. The body that handles the dossier is recorded only by a person in your organisation, from the authority’s own communication; ComplianceOne never works it out or proposes one.',
            handling_filed_with: 'Filed with',
            handling_filed_with_unknown: 'Not declared by the installed form',
            handling_handled_by: 'Handled by',
            handling_not_known: 'Not known — no transfer recorded',
            handling_other_body: 'Other body named',
            handling_option_not_named: 'A body the installed content no longer names',
            handling_class_label: 'Body',
            handling_named_as: 'Named in the communication',
            handling_undeclared: 'The installed form for this filing declares no handling authority, so none can be recorded here.',
            handling_transfer_basis: 'Transfer basis',
            handling_transfer_none_stated: 'No transfer is provided for in this text',
            handling_transfer_awaiting_receipt: 'The transfer basis is shown once the receipt is recorded',
            handling_criteria: 'Criteria for a transfer',
            handling_may_transfer_to: 'Bodies the installed form names',
            handling_applies_from: 'The installed form states this applies to dossiers received from {date}.',
            handling_record: 'Record the handling authority',
            handling_history: 'Statements recorded',
            handling_none: 'No statement has been recorded.',
            handling_loading: 'Loading the statements…',
            handling_load_failed: 'The statements could not be read, so none are shown. That does not mean none were recorded.',
            retry_subject_handling: 'the handling statements',
            handling_pagination: 'Pages of handling statements',
            handling_col_body: 'Body',
            handling_col_basis: 'Basis',
            handling_col_communication: 'Communication',
            handling_col_evidence: 'Evidence',
            handling_col_correction: 'Correction',
            handling_col_recorded: 'Recorded',
            handling_basis_transfer_notice: 'Transfer notice',
            handling_basis_authority_result: 'The authority’s result',
            handling_basis_other_written_communication: 'Other written communication',
            handling_source_corrected: 'Source communication corrected — review',
            handling_communication_value: '{kind}, received {date}',
            handling_communication_unknown: 'A communication not on the first page of the history',
            handling_form_intro: 'State the body that handles the dossier as the authority’s communication names it. Choose it from the bodies the installed form names, or name another body. Nothing is filled in for you.',
            handling_field_basis: 'Basis for the statement',
            handling_field_basis_placeholder: 'Choose the basis',
            handling_field_communication: 'Communication it rests on',
            handling_field_option: 'Body',
            handling_field_option_placeholder: 'Choose the body',
            handling_option_other: 'Another body (name it below)',
            handling_field_body_name: 'The body’s name, as the communication gives it',
            handling_field_body_name_hint: 'Up to {max} characters, copied from the communication.',
            handling_field_corrects: 'Statement this corrects',
            handling_field_corrects_none: 'Not a correction',
            handling_corrects_option: '{body}, recorded {date}',
            handling_error_basis_required: 'Choose the basis for the statement.',
            handling_error_option_required: 'Choose the body, or choose another body and name it.',
            handling_error_option_not_declared: 'That body is not among those the installed form names. Choose another, or name it as another body.',
            handling_record_failed: 'The statement could not be recorded. What you entered is kept.',
            handling_recorded: 'Handling authority recorded.',
            result_heading: 'The authority’s result',
            result_frame: 'Result recorded: {label} — as your organisation recorded it from the authority’s notice',
            result_legacy_frame: 'Result recorded: {label} — recorded before results followed the governing text',
            result_none: 'No result has been recorded.',
            result_label_not_installed: 'Result recorded — its label is not in the installed content',
            result_different_text: 'Recorded under a different governing text — review',
            result_date: 'Date on the notice',
            result_notice_received: 'Date your organisation received the notice',
            result_communication: 'Communication',
            result_notes: 'Notes',
            result_record: 'Record the authority’s result',
            result_needs_receipt: 'Recording a result needs the receipt date first: the result options are those of the text that governed the dossier when the authority received it.',
            result_absent: 'No installed declaration offers result options for this filing, so no result can be recorded here. Record the authority’s notice under Authority communications.',
            result_absent_update: 'No result is recorded on an update dossier. Record the authority’s notice under Authority communications.',
            result_form_intro: 'Record the result as the authority’s notice states it. The options are those of the text that governed the dossier when the authority received it.',
            result_field_option: 'Result, as the notice states it',
            result_field_option_placeholder: 'Choose the result',
            result_field_communication: 'Communication the result comes from',
            result_error_option_required: 'Choose the result the notice states.',
            result_error_option_not_offered: 'That result is not offered for this filing’s governing text.',
            result_error_date_invalid: 'The date must not be later than today or earlier than the day the filing was submitted.',
            result_error_notice_before_result: 'The date your organisation received the notice cannot be before the date on the notice.',
            result_error_not_determined: 'The governing text is not determined. Record the receipt date first.',
            result_error_declaration_absent: 'No installed declaration offers result options for this filing.',
            result_error_status: 'A result cannot be recorded in the filing’s current state. The page has read the filing again; what you entered is kept.',
            result_record_failed: 'The result could not be recorded. What you entered is kept.',
            result_saved: 'The result is recorded.',
            result_correct: 'Correct this result',
            result_correct_intro: 'Record the result again as the authority’s notice states it, and say why. The earlier entry stays in the history. A result can be corrected only while it is the latest result, the filing is not closed, and no supplement request, supplement submission or follow-up has been recorded for it.',
            result_correcting_target: 'You are correcting: {result}, dated {date}',
            result_field_correction_note: 'Why the result is corrected',
            result_corrects_earlier: 'Corrects an earlier entry. Reason: {note}',
            result_earlier_heading: 'Earlier entries',
            result_corrected: 'The result is corrected.',
            result_error_correction_not_open: 'This result can no longer be corrected: it is not the latest result, the filing is closed, or a supplement request, supplement submission or follow-up has been recorded for it. The page has read the filing again.',
            result_error_not_found: 'The result being corrected was not found on this filing. The page has read the filing again; what you entered is kept.',
            result_error_already_corrected: 'This result has already been corrected by another entry. The page is reading the filing again.',
            result_error_correction_blocked: 'Nothing can be saved from this dialog. Close it; what you entered will not be recorded. If the page still offers a correction, start it again from the current result.',
            result_row_statement: 'Recorded by {person} from the authority’s notice — ComplianceOne has not verified it.',
            post_result_citation: 'What the governing text provides after this result: {citation}',
            stated_date_heading: 'Result dates stated by the authority',
            stated_date_record: 'Record a result date the authority stated',
            stated_date_form_intro: 'Record the result date the authority itself states, on a receipt slip or a later notice. It replaces the provisional date. A correction is a new entry.',
            stated_date_field_date: 'Result date the communication states',
            stated_date_field_communication: 'Communication that states it',
            stated_date_field_corrects: 'Entry this corrects',
            stated_date_corrects_option: '{date}, recorded {recorded}',
            stated_date_value: 'Stated by the authority: {date}',
            stated_date_recorded: 'Stated result date recorded.',
            stated_date_record_failed: 'The stated date could not be recorded. What you entered is kept.',
            rsupp_heading: 'Supplement after the result',
            rsupp_intro: 'The result and any concrete request are separate records. Record the items and any period the notice states; ComplianceOne computes no date from the result.',
            rsupp_no_request: 'Supplement required — no request recorded',
            rsupp_requested: 'Supplement requested — not yet submitted',
            rsupp_submitted: 'Supplement submitted',
            rsupp_record: 'Record the supplement request',
            rsupp_no_period: 'No period stated in the notice',
            rsupp_stated_deadline: 'Date stated in the notice: {date}',
            rsupp_item_owner: 'Owner: {person}',
            rsupp_item_task: 'Task assigned',
            rsupp_col_items: 'Items requested',
            rsupp_col_communication: 'Communication',
            rsupp_col_period: 'Period',
            rsupp_col_recorded: 'Recorded',
            rsupp_form_intro: 'Record what the notice asks for, item by item. An item with an owner becomes a task for that person. Only a date the notice itself states is recorded; none is computed.',
            rsupp_field_communication: 'Communication the request comes from',
            rsupp_field_items: 'Items the notice asks for',
            rsupp_field_item: 'Item {n}',
            rsupp_field_item_owner: 'Owner of item {n} (optional)',
            rsupp_add_item: 'Add an item',
            rsupp_remove_item: 'Remove item {n}',
            rsupp_field_period: 'Period the notice states, verbatim',
            rsupp_field_deadline: 'Date the notice states (optional)',
            rsupp_field_deadline_hint: 'Only a date the notice itself states. Each item with an owner becomes a task due on this date, or with no due date when the notice states none.',
            rsupp_error_items_required: 'Add at least one item the notice asks for.',
            rsupp_error_not_open: 'No supplement is open after this result. The page has read the filing again; what you entered is kept.',
            rsupp_record_failed: 'The supplement request could not be recorded. What you entered is kept.',
            rsupp_recorded: 'Supplement request recorded.',
            followup_heading: 'Follow-up after the result',
            followup_intro: 'This result leaves the filing open. Record the reasons the authority stated, the remediation your organisation chose and any enforcement risk. Nothing on any other record changes.',
            followup_not_recorded: 'No follow-up recorded yet',
            followup_refusal: 'The authority’s communication',
            followup_reasons: 'Reasons stated',
            followup_remediation: 'Remediation',
            followup_actions: 'Remediation actions',
            followup_action_due: 'Your organisation’s own date: {date}',
            followup_action_owner: 'Owner: {person}',
            followup_enforcement_risk: 'Potential enforcement risk',
            followup_enforcement_risk_none: 'None stated',
            followup_revision: 'Recorded by {person} on {date}',
            followup_record: 'Record the follow-up',
            followup_revise: 'Revise the follow-up',
            followup_form_intro: 'Record the reasons the notice states and the remediation your organisation chose. Each action’s owner and date are your organisation’s own choice; ComplianceOne computes no date.',
            followup_field_reasons: 'Reasons the notice states',
            followup_field_summary: 'Remediation',
            followup_field_actions: 'Remediation actions',
            followup_field_action: 'Action {n}',
            followup_field_action_title: 'What is to be done (action {n})',
            followup_field_action_owner: 'Owner (action {n})',
            followup_field_action_due: 'Your organisation’s own date (action {n})',
            followup_add_action: 'Add an action',
            followup_remove_action: 'Remove action {n}',
            followup_actions_none: 'No action added.',
            followup_field_enforcement: 'Potential enforcement risk (optional)',
            followup_field_enforcement_hint: 'Leave it empty when none is stated; the record then says so.',
            followup_field_correction_note: 'Why the follow-up is revised',
            followup_error_not_open: 'No follow-up is open for this filing. The page has read the filing again; what you entered is kept.',
            followup_record_failed: 'The follow-up could not be recorded. What you entered is kept.',
            followup_recorded: 'Follow-up recorded.',
            close_title: 'Close the filing',
            close_intro: 'Closing records that your organisation has finished with this filing. It is not a decision of the authority.',
            close_field_reason: 'Closing reason',
            close_field_reason_hint: 'Required where the page says so; otherwise optional.',
            close_refusal_DOSSIER_RESULT_SUPPLEMENT_OPEN: 'The supplement after the result is still open. Record it as submitted before closing.',
            close_refusal_DOSSIER_FAILURE_FOLLOW_UP_OPEN: 'No follow-up is recorded for this result. Record the follow-up, or give the reason for closing without one.',
            close_refusal_DOSSIER_CLOSE_REASON_REQUIRED: 'Give the reason for closing this filing.',
            close_closing: 'Closing…',
            close_failed: 'The filing could not be closed. What you entered is kept.',
            closing_reason_value: 'Closing reason: {reason}',
            column_handled_by: 'Handled by',
            aria_filter_by_handling: 'Filter by handling authority',
            filter_handling_not_known: 'Not known — no transfer recorded',
            filter_handling_other: 'Other body named',
            prior_filing_open_follow_up: 'Open — follow-up recorded',
            prior_filing_open_no_follow_up: 'Open — the result did not pass, and no follow-up is recorded yet',
            prior_filing_body_open: 'The installed framework provides for one dossier of this kind per organisation, updated afterwards ({citation}). This organisation filed one: {filing}. A result is recorded on it. For a new purpose or another change to its content, start the update on this assessment instead.',
            // Sprint 62.57.0 (Slices A, B, F; A-1) — the filing record export’s labels.
            record_export_receipt_heading: 'Receipt of the dossier and the governing text',
            record_export_receipt_none: 'No receipt of the dossier has been recorded.',
            record_export_col_event: 'Receipt',
            record_export_col_received_on: 'Received by the authority',
            record_export_col_postmarked_on: 'Postmark',
            record_export_col_status: 'Entry',
            record_export_entry_standing: 'Standing',
            record_export_entry_corrected: 'Corrected by a later entry',
            record_export_governing_text: 'Governing text: {label}',
            record_export_governing_end: 'End of the window: {label}',
            governing_text_not_determined: 'Governing text not determined — record the receipt date',
            record_export_governing_review: 'The recorded governing text no longer matches the installed declaration — review',
            record_export_reservation: 'Pending legal confirmation: {note}',
            record_export_handling_heading: 'Handling authority',
            record_export_filed_with: 'Filed with: {body}',
            record_export_handled_by_unknown: 'Handled by: not known — no transfer recorded',
            record_export_transfer_basis: 'Transfer basis: {text}',
            record_export_transfer_none_stated: 'No transfer is provided for in this text',
            record_export_transfer_no_stamp: 'The transfer basis is shown once the receipt is recorded',
            record_export_col_body: 'Body named in the communication',
            record_export_col_option: 'Declared body',
            record_export_col_basis: 'Basis',
            record_export_col_source: 'Source communication',
            record_export_other_body: 'Other body named',
            record_export_source_corrected: 'Source communication corrected — review',
            record_export_result_heading: 'Result recorded',
            record_export_result_none: 'No result has been recorded.',
            record_export_result_corrected_by: 'Corrected by a later entry',
            record_export_result_corrects: 'Corrects an earlier entry. Reason: {note}',
            record_export_result_label: 'Result recorded: {label} — as your organisation recorded it from the authority’s notice',
            record_export_result_legacy: 'Result recorded: {legacy} — recorded before results followed the governing text',
            record_export_result_dates: 'Date on the notice: {result_date}. Received by the organisation: {received}.',
            result_recorded_under_different_governing_text: 'Recorded under a different governing text — review',
            record_export_result_follow_up: 'What the result opens: {citation}',
            record_export_supplement_heading: 'Supplement requests after the result',
            result_supplement_no_request: 'Supplement required — no request recorded',
            record_export_supplement_period: 'Period stated in the notice: {period}',
            no_period_stated_in_notice: 'No period stated in the notice',
            record_export_follow_up_heading: 'Follow-up to the result',
            record_export_follow_up_none: 'No follow-up has been recorded.',
            record_export_follow_up_reasons: 'Reasons stated: {text}',
            record_export_follow_up_remediation: 'Remediation: {text}',
            record_export_follow_up_risk: 'Potential enforcement risk: {text}',
            record_export_col_action: 'Remediation action',
            record_export_col_owner: 'Person responsible',
            record_export_col_own_date: 'Your organisation’s own date',
            record_export_period_heading: 'The authority’s period',
            record_export_period_stated: 'Result date stated by the authority: {date}',
            record_export_period_counted: 'Counted from {start} on the {calendar} calendar: {date}',
            record_export_period_passed: 'Provisional period passed — no result recorded',
            record_export_period_not_dated: 'No provisional date ({reason})',
            // Sprint 62.57.0 (Slices C and D, list half of AC-F5) — the completion period, the
            // completion request and submission, the update's recipient and competent authority,
            // the list's governing-text column and filters, the requirement link.
            status_result_completion_submitted: 'Completion submitted — no further result recorded',
            step_result_completion_submitted: 'Completion submitted',
            receipt_event_completed_dossier_received: 'Completed dossier received',
            receipt_field_completion_submission: 'Completion submission received',
            receipt_completion_option: 'Completion submitted on {date}',
            receipt_completion_option_recorded: 'Completion recorded on {date}',
            receipt_error_completion_invalid: 'Choose the completion submission the authority received.',
            completion_heading: 'Completion period',
            completion_intro: 'A completion period opens only when your organisation records that a communication asks it to complete the dossier, or records a result for which the governing text provides one. The dates are worked out from the installed regulatory content each time the filing is read.',
            completion_none: 'No completion period is open on this filing.',
            completion_current: 'Current period',
            completion_history: 'Earlier periods',
            completion_date_provisional: 'Provisional date — start event pending legal confirmation',
            completion_date_confirmed: 'Completion date',
            completion_date_label: 'Completion date',
            completion_passed_provisional: 'Provisional completion date passed',
            completion_passed_confirmed: 'Completion period passed',
            completion_running: 'Within the period',
            completion_completed: 'Completion submitted on {date}',
            completion_completed_no_date: 'Completion submitted',
            completion_not_dated: 'No completion date',
            completion_state: 'State',
            completion_notice_date: 'Notice date',
            completion_received_on: 'Received by your organisation',
            completion_authority_stated: 'Date stated by the authority',
            completion_trigger: 'Opened by',
            completion_trigger_completion_request: 'A completion request your organisation recorded',
            completion_trigger_result: 'The result your organisation recorded',
            completion_source: 'Source communication',
            completion_deficiency: 'What the governing text provides',
            completion_citation: 'Citation',
            completion_start_event: 'Start event',
            completion_period_length: 'Period: {period} {unit}',
            completion_unit_calendar_days: 'calendar days',
            completion_unit_working_days: 'working days',
            completion_end_roll_none: 'ComplianceOne shows the end date as counted and does not move it to the next working day when it falls on a day off.',
            completion_end_roll_next_statutory_working_day: 'An end that falls on a day off moves to the next working day.',
            completion_obligation: 'Your organisation’s period, not the authority’s',
            completion_task_raised: 'A task was raised when the date passed.',
            completion_col_period: 'Period',
            completion_absence_trigger_not_documented: 'No completion period — this request cites no recorded communication',
            completion_absence_trigger_not_declared: 'No completion period is declared for this result under the governing text',
            completion_absence_governing_text_not_determined: 'No completion date — the governing text is not determined; record the receipt date',
            completion_absence_no_period_declared: 'No completion period is declared in the installed content for this dossier',
            completion_absence_multiple_candidate_periods: 'No completion date — the installed content declares more than one period and none is chosen',
            completion_absence_statutory_calendar_unavailable: 'No completion date — the statutory calendar is not installed',
            completion_absence_receipt_not_evidenced: 'No completion date — your organisation’s receipt of the communication is not recorded',
            completion_absence_declaring_framework_reference_mode: 'No completion date — the content that declares the period is installed for reference only',
            completion_absence_declaring_framework_not_in_force: 'No completion date — the content that declares the period was not in force at the dossier’s receipt',
            completion_absence_declaring_framework_unreadable: 'No completion date — the content that declares the period could not be read',
            completion_absence_no_installed_clock: 'No completion period is declared in the installed content for this dossier',
            completion_absence_multiple_candidate_clocks: 'No completion date — the installed content declares more than one period and none is chosen',
            completion_absence_uncomputable_period: 'No completion date — the declared period cannot be counted as a date',
            completion_absence_declaring_framework_phase_reference_unresolved: 'No completion date — the content that declares the period does not date the phase it names',
            completion_absence_declaring_framework_not_operational: 'No completion date — the content that declares the period is not in operation',
            completion_consequence_heading: 'Potential enforcement risk',
            completion_body_handled_by: '{name} — from your recorded handling statement',
            completion_body_filed_with: '{name} — the body the dossier was filed with; no handling statement recorded',
            completion_fine_area_absent: 'The penalties framework that declares this is not installed in your organisation',
            completion_fine_area_articles: 'Articles: {articles}',
            completion_fine_area_reference: 'Installed for reference only',
            completion_fine_area_link: 'Open {area} in Penalty Readiness',
            completion_requests_heading: 'Completion requests recorded',
            completion_requests_none: 'No completion request has been recorded.',
            completion_col_communication: 'Communication',
            completion_col_recorded: 'Recorded',
            completion_col_correction: 'Correction',
            completion_request_record: 'Record a completion request',
            completion_request_correct: 'Correct this request',
            completion_request_intro: 'Choose the communication and state what it asks. ComplianceOne does not read the communication’s wording to decide this.',
            completion_field_communication: 'Communication',
            completion_statement: 'The communication asks your organisation to complete the dossier, or states that it is incomplete, invalid or not in accordance with the regulations',
            completion_statement_required: 'Tick the statement to record the request.',
            completion_context_heading: 'What the governing text provides (for reference)',
            completion_field_corrects: 'Request this corrects',
            completion_field_corrects_none: 'Not a correction — a new request',
            completion_corrects_option: '{communication}, recorded {date}',
            completion_request_recorded: 'Completion request recorded.',
            completion_request_failed: 'The completion request could not be recorded. What you entered is kept.',
            completion_error_not_open: 'A completion request can be recorded here only while a supplement request or a result is recorded on the filing. Before a result, record it with the supplement request.',
            completion_request_row: 'Recorded by {person} on {date}',
            completion_request_withdraw: 'Withdraw this entry',
            completion_request_withdraw_title: 'Withdraw this completion request entry',
            completion_request_withdraw_intro: 'Withdraw an entry that recorded a request in error: no completion period is counted from it any more. The entry stays in the history with your note. If another communication asks your organisation to complete the dossier, record a new request instead.',
            completion_request_withdraw_target: 'Entry withdrawn',
            completion_request_withdraw_note: 'Why this entry is withdrawn',
            completion_request_withdraw_note_required: 'Say why the entry is withdrawn.',
            completion_request_withdrawing: 'Withdrawing…',
            completion_request_withdrawn_msg: 'Completion request entry withdrawn.',
            completion_request_withdraw_failed: 'The withdrawal could not be recorded. What you entered is kept.',
            completion_request_withdraw_gone: 'This entry has been corrected or withdrawn meanwhile, or is no longer on this filing. The page has read the filing again.',
            completion_request_withdrawn: 'Withdrawn',
            completion_request_withdraws: 'Withdraws the entry recorded on {date}',
            completion_request_withdrawn_by: 'Withdrawn by a later entry',
            supplement_field_communication: 'Communication that asks for the supplement',
            supplement_communication_hint: 'A completion period is counted only from a recorded communication. Record the communication first under Authority communications.',
            supplement_error_completion_needs_communication: 'Choose the communication before you tick the statement.',
            supplement_request_failed: 'The supplement request could not be recorded. What you entered is kept.',
            result_completion_hint: 'Tick only if this communication asks your organisation to complete the dossier, or states that it is incomplete, invalid or not in accordance with the regulations. A result alone opens a completion period only where the governing text provides for one.',
            completion_submit: 'Record completion submitted',
            completion_submitted_on: 'Date submitted',
            completion_submit_intro: 'Record the day your organisation submitted the completed dossier to the authority. A further result may then be recorded on this filing.',
            completion_submit_intro_supplement: 'Record the day your organisation submitted the completed dossier in answer to the supplement request, and what it submitted.',
            supplement_submit_intro: 'Record what your organisation submitted in answer to the supplement request.',
            completion_submitted_msg: 'Completion submitted recorded.',
            completion_submit_failed: 'The submission could not be recorded. What you entered is kept.',
            completion_error_date_invalid: 'The date cannot be in the future or before your organisation received the request.',
            completion_error_status: 'This filing’s state no longer allows this. The page has been read again; what you entered is kept.',
            column_governing_text: 'Governing text',
            aria_filter_by_governing_text: 'Filter by governing text',
            filter_governing_not_determined: 'Not determined',
            filter_completion_passed: 'Completion period: date passed',
            list_completion_due_provisional: 'Provisional completion date: {date}',
            list_completion_due: 'Completion date: {date}',
            chip_governed_by_requirement: 'Filings governed by the requirement you opened',
            chip_governed_by_requirement_title: 'Filings governed by: {title}',
            chip_updates_of: 'Updates of the original filing',
            chip_remove: 'Remove filter: {label}',
            requirement_filings_link: 'Filings this governs: {n}',
            requirement_filings_link_failed: 'The filings this governs could not be counted.',
            change_revise: 'Revise this filing',
            change_update: 'Prepare an update filing',
            change_mode_legend: 'What do you want to do?',
            change_update_form: 'Update form',
            change_update_loading: 'Reading the original filing and the installed update forms…',
            change_update_load_failed: 'The original filing or the installed update forms could not be read.',
            change_update_intro: 'The update is a new filing in ComplianceOne that names this one; preparing it sends nothing to the authority. Choose each body yourself; nothing is chosen for you.',
            update_sent_to: 'Sent to',
            update_sent_to_help: 'The body your organisation files this update with; the cover letter is addressed to it when it is not the body the original was filed with.',
            update_competent: 'Competent to handle',
            update_competent_help: 'The body your organisation records, from the authority’s communications, as competent to handle the dossier. ComplianceOne does not determine it.',
            update_context: 'Original dossier — filed with: {filed_with}. Handled by: {handled_by}.',
            update_context_not_known: 'not known — no transfer recorded',
            update_context_filed_with_not_known: 'not declared by the installed form',
            update_choice_filed_with: 'The body the original dossier was filed with: {name}',
            update_choice_handled_by: 'The body recorded as handling the original dossier: {name}',
            update_choice_named: 'Another body (name it as the communication does)',
            update_named_label: 'Name of the body, as the communication gives it',
            update_reason: 'Why this differs from the body handling the original dossier',
            update_reason_now_required: 'A reason is now required: Why this differs from the body handling the original dossier',
            update_error_choice_required: 'Choose a body.',
            update_error_name_required: 'Enter the body’s name.',
            update_error_reason_required: 'Say why this differs from the body handling the original dossier.',
            update_error_body_invalid: 'This body can no longer be chosen: the original dossier has no recorded handling body. Choose another.',
            update_error_not_declared: 'No installed update form of this kind provides for recording the recipient and competent authority.',
            update_error_form_invalid: 'The update form could not be used for this filing. Start again.',
            update_error_target_invalid: 'An update cannot be prepared from an update. Open the original filing.',
            update_error_locked: 'These can no longer be changed: the update dossier has been prepared for filing or filed.',
            update_error_original_not_found: 'The original filing was not found. Choose it again.',
            update_error_original_invalid: 'This filing cannot be chosen as the original. Choose another.',
            update_error_forms_denied: 'Your role does not give you access to forms, so an update filing cannot be prepared here. Ask an administrator for access to forms.',
            update_original_choose_first: 'Choose the original filing first.',
            update_error_not_submitted: 'The original filing has not been filed, so no update can be prepared from it.',
            update_create_failed: 'The update could not be created. What you entered is kept.',
            update_form_kept: 'An update form was started but not linked to this filing. Prepare the update again from this filing to use it; do not complete it from the assessment’s forms, which would create an update that names no original filing.',
            update_form_reused: 'This update reuses the update form already started from this filing: {form}.',
            completion_mode_changed: 'The completion period on this filing changed while you were entering this. The dialog now records the supplement response only; what you entered is kept.',
            completion_mode_changed_completion: 'The completion period on this filing changed while you were entering this. The dialog now records the completion submitted and asks for the date submitted; what you entered is kept.',
            supplement_request_no_longer_open: 'A supplement request can no longer be recorded on this filing: its status changed meanwhile (for example, another person recorded one). The page has read it again. Close this dialog.',
            update_form_no_longer_offered: 'The update form started earlier is no longer offered by your installed content; choose a form.',
            update_created: 'Update filing created. Fill in the update form.',
            update_creating: 'Creating…',
            update_section_heading: 'Recipient and competent authority',
            update_section_intro: 'Your organisation records where it files the update, and, from the authority’s communications, which body is competent to handle it. ComplianceOne never decides which body handles a dossier or an update.',
            update_not_recorded: 'Not recorded yet.',
            update_record: 'Record the recipient and competent authority',
            update_edit: 'Change the recipient and competent authority',
            update_saving: 'Saving…',
            update_recorded_msg: 'Recipient and competent authority recorded.',
            update_record_failed: 'The recipient and competent authority could not be recorded. What you entered is kept.',
            update_history: 'Earlier records',
            update_recorded_by: 'Recorded by {person} on {date}',
            update_source_filed_with: 'The body the original dossier was filed with',
            update_source_handled_by: 'The body recorded as handling the original dossier',
            update_source_named: 'Named by your organisation',
            update_addressee_review_prompt: 'The body recorded as handling the original dossier has changed since this was recorded — review',
            update_window_rule_flag: 'The text that governs this update provides for filing it with {body}; your organisation recorded {sent_to} — review',
            update_window_rule_flag_unnamed: 'The text that governs this update provides for filing it with another body than the one your organisation recorded ({sent_to}) — review',
            update_bases_disclosure: 'Which body an update is filed with depends on when the authority receives it',
            update_basis_own_window: 'What the text that governs this update provides',
            update_original: 'Updates the filing',
            update_original_open: 'Open the original filing',
            updates_of_this: 'Updates of this dossier ({n})',
            gov_update_own_receipt: 'An update is dated by its own receipt, not by the original dossier’s.',
            completion_task_title_provisional: 'Provisional completion date passed: {filing}',
            completion_task_title_confirmed: 'Completion period passed: {filing}',
            update_original_pick_loading: 'Reading the filings…',
            update_original_pick_empty: 'No filed filing can be chosen as the original.',
            update_original_pick_failed: 'The filings could not be read.',
            update_original_pick_manual_label: 'Original filing record ID',
            update_original_pick_manual_hint: 'Enter the ID of a filed filing; it is checked when you save.',
            update_original_pick_off_list: 'Filing {value} (not in the list)',
            followup_completion_link: 'See the completion period',
            record_export_completion_heading: 'Completion period',
            record_export_completion_none: 'No completion request has been recorded.',
            record_export_completion_trigger_request: 'Completion request recorded from the communication {reference}',
            record_export_completion_trigger_result: 'Result recorded: {label}',
            record_export_completion_dates: 'Notice date: {notice}. Received by your organisation: {received}.',
            record_export_completion_provisional: 'Provisional date — start event pending legal confirmation: {date}',
            record_export_completion_confirmed: 'Completion date: {date}',
            record_export_completion_stated: 'Date stated by the authority: {date}',
            record_export_completion_passed_provisional: 'Provisional completion date passed',
            record_export_completion_passed: 'Completion period passed',
            record_export_completion_completed: 'Completion submitted: {date}',
            record_export_completion_body_not_recorded: 'the body competent to handle the dossier (not recorded on this filing)',
            record_export_completion_area: 'Penalty area: {name} ({articles})',
            record_export_completion_area_absent: 'The penalties framework that declares this is not installed in your organisation',
            record_export_update_heading: 'Recipient and competent authority',
            record_export_update_sent_to: 'Sent to: {body}',
            record_export_update_competent: 'Competent to handle: {body}',
            record_export_update_reason: 'Why this differs from the body handling the original dossier: {reason}',
            record_export_update_basis: '{window}: {basis}',
            record_export_update_window_flag: 'The text that governs this update provides for filing it with another body than the one recorded — review',
        },
        materialisation_health: {
            nav_label: 'Gezondheid van het artefact bij indiening',
            page_title: 'Inzending Artefactgezondheid',
            page_description: 'Detecteer en verhelp problemen met de materialisatie van dossiers. Lijsten zijn gericht op uw organisatie.',
            failed_title: 'Mislukte materialisaties',
            failed_empty: 'Geen mislukte materialisaties – goed.',
            pending_title: 'Goedgekeurd maar niet gerealiseerd',
            pending_empty: 'Alle goedgekeurde deponeringen hebben een ondersteunend dossier.',
            orphan_title: 'Weesdossiers',
            orphan_description: 'Dossiers die door materialisatie zijn ontstaan en waarnaar in geen enkele dossiers meer wordt verwezen – doorgaans de verliezer van een gelijktijdige race. Op de lijst voor onderzoek; het opruimen is handmatig.',
            orphan_empty: 'Geen weesdossiers gedetecteerd.',
            materialise_button: 'Materialiseer nu',
            column_failure_reason: 'Reden van falen',
            column_failed_at: 'Mislukt bij',
            column_retries: 'Nieuwe pogingen',
            column_approved_at: 'Goedgekeurd op',
            column_dossier_id: 'Dossier-ID',
            column_source_filing_id: 'Bronregistratie',
            column_dossier_type: 'Typ',
            column_created_at: 'Gemaakt op',
        },
        // Sprint 62.35.0 (Slice C) — structural parity; English until Phase 25 (R-57).
        pre_collection: {
            nav_label: 'Pre-Collection Assessments',
            page_title: 'Pre-Collection Assessments',
            page_description: 'An assessment of the protection measures for a data registry item, made before that data is first collected. The completion date may not be after the first collection date.',
            module_guide: 'Record, for each data registry item, the assessment of protection measures made before collection begins: the collection procedure and its version, the assessment, the measures applied, the date the assessment was completed and the date the data was first collected. Whether an assessment is required is set by your installed frameworks.',
            new_button: 'New assessment',
            create_title: 'New pre-collection assessment',
            edit_title: 'Edit pre-collection assessment',
            edit: 'Edit',
            field_registry_item: 'Data registry item',
            registry_item_placeholder: 'Select a registry item',
            registry_item_unresolved: 'Registry item not found',
            registry_load_error: 'The data registry could not be read, so no registry item can be offered.',
            registry_empty: 'The data registry has no items yet.',
            field_procedure_reference: 'Collection procedure reference',
            field_procedure_version: 'Procedure version',
            field_summary: 'Assessment',
            field_measures: 'Measures applied',
            field_measures_hint: 'One measure per line.',
            field_completed_on: 'Assessment completed on',
            field_first_collection_on: 'First collected on',
            date_hint: 'The assessment must be completed on or before the first collection date. Leave the first collection date empty if collection has not begun.',
            error_after_first_collection: 'The assessment must be made before the data is first collected: the completion date is after the first collection date.',
            field_status: 'Status',
            status_draft: 'Draft',
            status_approved: 'Approved',
            status_deprecated: 'Deprecated',
            column_registry_item: 'Registry item',
            column_procedure: 'Collection procedure',
            column_actions: 'Actions',
            not_recorded: 'Not recorded',
            empty: 'No pre-collection assessments have been recorded yet.',
            list_load_error: 'The pre-collection assessments could not be read.',
            reload: 'Reload',
            reload_sr: 'reload the pre-collection assessments',
            registry_reload_sr: 'reload the data registry',
            save: 'Save',
            saving: 'Saving…',
            cancel: 'Cancel',
            created_toast: 'Pre-collection assessment recorded.',
            updated_toast: 'Pre-collection assessment updated.',
            save_error: 'The assessment could not be saved.',
            total: 'Total',
            section_scope: 'What is assessed',
            section_assessment: 'Assessment and dates',
            approved_locked: 'This assessment is approved, so its content cannot be changed. It can only be deprecated.',
            error_conflict: 'This assessment was changed by someone else while you were editing, so your change was not saved. Close this form and open the assessment again to see the current version.',
            error_conflict_refreshed: 'This assessment was changed by someone else while you were editing, so your change was not saved. The form now shows the current version, and your unsaved entries were not kept. Make your change again and save.',
            registry_not_readable: 'Your role cannot read the data registry, so no registry item can be offered here.',
            create_needs_registry_read: 'Your role cannot read the data registry, so it cannot choose the registry item a new assessment needs. Existing assessments can still be edited.',
            registry_item_name_hidden: 'Name not visible to your role',
        },
    },
    classification: {
        access_denied_title: 'Toegang geweigerd',
        access_denied: 'U beschikt niet over de vereiste machtigingen voor toegang tot de classificatiemodule. Neem contact op met uw beheerder.',
        nav: {
            taxonomies: 'Taxonomieën',
            labeling: 'Kenmerken toewijzen',
            lifecycle: 'Levenscyclus',
            gaps: 'Lacunerapport',
            exports: 'Exporteert',
        },
        exports: {
            title: 'Classificatie-exports',
            csv_section: 'CSV-exports',
            pdf_section: 'PDF-rapporten',
            inventory: 'Inventaris',
            classification_register: 'Classificatieregister',
            raci: 'RACI-matrix',
            exceptions: 'Uitzonderingen',
            evidence_pack: 'Bewijspakket',
            delta_report: 'Deltarapport',
            executive_summary: 'Managementsamenvatting',
            exporting: 'Bezig met exporteren…',
            export_success: 'Export succesvol voltooid.',
            export_error: 'Export mislukt. Probeer het opnieuw.',
            csv_download: 'CSV-download gereed.',
            pdf_download: 'PDF-generatie gestart.',
            no_data: 'Geen gegevens beschikbaar om te exporteren.',
        },
        delta: {
            title: 'Wijzigingsdetails',
            added: 'Toegevoegd',
            removed: 'Verwijderd',
            changed: 'Gewijzigd',
            added_count: 'Toegevoegd ({count})',
            removed_count: 'Verwijderd ({count})',
            changed_count: 'Gewijzigd ({count})',
            object_id: 'Object-ID',
            object_type: 'Objecttype',
            taxonomy: 'Taxonomie',
            level: 'Niveau',
            previous: 'Vorig',
            current: 'Huidig',
            no_records: 'Geen records in deze sectie.',
            empty_section: 'Deze sectie is leeg.',
        },
        schedule: {
            title: 'Ontdekkingscyclus plannen',
            enabled: 'Ingeschakeld',
            interval_days: 'Interval (dagen)',
            interval_label: 'Elke N dagen uitvoeren',
            manual_only: 'Alleen handmatig',
            every_n_days: 'Elke {n} dagen',
            save: 'Planning opslaan',
            cancel: 'Annuleren',
            schedule_updated: 'Planning succesvol bijgewerkt.',
        },
        taxonomies: {
            title: 'Classificatietaxonomieën',
            new: 'Nieuwe taxonomie',
            edit: 'Taxonomie bewerken',
            name: 'Naam',
            levels: 'Classificatieniveaus',
            seed_defaults: 'Initialiseren Standaardwaarden',
            scope: 'Bereik',
            scope_organization: 'Organisatie',
            scope_module_specific: 'Modulespecifiek',
            deprecate: 'Afschrijven',
            hide_levels: 'Verbergen Niveaus',
            versions: 'Versies',
            hide_versions: 'Verbergen Versies',
            add_level: 'Toevoegen Niveau',
            new_level: 'Nieuw Niveau',
            edit_level: 'Bewerken Niveau',
            code: 'Codering',
            label: 'Kenmerk',
            color: 'Kleur',
            ordinal: 'Volgnummer',
            handling_requirements: 'Afhandeling Vereisten',
            version_history: 'Versie Geschiedenis',
            changes: 'Wijzigingen',
            failed_fetch: 'Kan niet fetch taxonomieën. Probeer het opnieuw.',
            failed_save: 'Kan niet save taxonomie. Probeer het opnieuw.',
            failed_activate: 'Kan niet activate taxonomie. Probeer het opnieuw.',
            failed_deprecate: 'Kan niet deprecate taxonomie. Probeer het opnieuw.',
            failed_seed: 'Kan niet seed standaard taxonomieën. Probeer het opnieuw.',
            failed_fetch_levels: 'Kan niet fetch taxonomie niveaus. Probeer het opnieuw.',
            failed_save_level: 'Kan niet save taxonomie niveau. Probeer het opnieuw.',
            failed_delete_level: 'Kan niet delete taxonomie niveau. Probeer het opnieuw.',
            failed_fetch_versions: 'Kan niet fetch taxonomie versies. Probeer het opnieuw.',
            loaded_count: '{count} taxonomieën geladen',
            defaults_seeded: 'Standaard-taxonomieën geïnitialiseerd.',
            no_levels: 'Geen classificatieniveaus gedefinieerd — voeg niveaus toe om uw classificatiehiërarchie op te bouwen.',
            no_versions: 'Nog geen versiegeschiedenis — versies worden aangemaakt wanneer een actieve taxonomie wordt bijgewerkt.',
        },
        labeling: {
            title: 'Gegevenslabeling',
            new: 'Nieuw label',
            edit: 'Label bewerken',
            entity_type: 'Entiteitstype',
            entity_id: 'Entiteits-ID',
            taxonomy: 'Taxonomie',
            classification_level: 'Classificatieniveau',
            applied_by: 'Toegepast door',
            applied_at: 'Toegepast op',
            bulk_apply: 'Bulk toepassen',
            bulk_remove: 'Bulk verwijderen',
            all_taxonomies: 'Alle Taxonomieën',
            system: 'Systeem',
            dataset: 'Gegevensset',
            level_code_placeholder: 'Niveau code...',
            select_taxonomy: 'Selecteren taxonomie...',
            select_level: 'Selecteren niveau...',
            rationale: 'Motivering',
            review_date: 'Beoordelen Datum',
            review: 'Beoordelen',
            selected: 'geselecteerd',
            select_all_labels: 'Selecteer alle labels',
            loaded_count: '{count} labels geladen',
            hide_stale_labels: 'Verbergen Stale Labels',
            show_conflicts: 'Tonen Conflicten',
            hide_conflicts: 'Verbergen Conflicten',
            label_conflicts: 'Label Conflicten',
            conflict_reason: 'Conflict Reden',
            labels: 'Kenmerken',
            failed_fetch: 'Kan niet fetch labels. Probeer het opnieuw.',
            failed_fetch_taxonomies: 'Kan niet fetch taxonomieën. Probeer het opnieuw.',
            failed_fetch_levels: 'Kan niet fetch taxonomie niveaus. Probeer het opnieuw.',
            failed_save: 'Kan niet save label. Probeer het opnieuw.',
            failed_review: 'Kan niet beoordeling label. Probeer het opnieuw.',
            failed_bulk: 'Kan niet bulk label. Probeer het opnieuw.',
            failed_stale: 'Kan niet fetch stale labels. Probeer het opnieuw.',
            failed_conflicts: 'Kan niet fetch label conflicts. Probeer het opnieuw.',
            review_permission_denied: 'U heeft geen toestemming om dit label te beoordelen.',
            review_not_found: 'Label niet gevonden — het is mogelijk verwijderd.',
            approve_selected: 'Geselecteerde goedkeuren',
            reject_selected: 'Geselecteerde afwijzen',
            approved: 'Goedgekeurd',
            rejected: 'Afgewezen',
            labels_count: 'labels',
            failed_count: 'mislukt',
            confirm_approve: 'Goedkeuring bevestigen',
            confirm_reject: 'Afwijzing bevestigen',
            approve_n_labels: '{count} labels goedkeuren?',
            reject_n_labels: '{count} labels afwijzen?',
            submit_for_review: 'Indienen voor beoordeling',
            approve: 'Goedkeuren',
            reject: 'Afwijzen',
            parent_system: 'Bovenliggend systeem',
            parent_system_placeholder: 'Selecteer bovenliggend systeem...',
            parent_system_help: 'Gebruikt voor conflictdetectie.',
            dataset_level: 'Datasetniveau',
            parent_level: 'Bovenliggend niveau',
            bulk_created: '{created} label(s) aangemaakt',
            bulk_skipped: '{skipped} overgeslagen',
            bulk_all_skipped: 'Alle {skipped} overgeslagen',
            ids_hint: 'Voer IDs in gescheiden door kommas',
            delete_confirm: 'Label voor "{id}" verwijderen?',
        },
        lifecycle: {
            title: 'Gegevenslevenscyclusmonitoring',
            new: 'Nieuw item',
            current_stage: 'Huidige fase',
            last_transition: 'Laatste overgang',
            transition: 'Overgang',
            view_history: 'Geschiedenis bekijken',
            stages: {
                creation: 'Aanmaak',
                collection: 'Verzameling',
                processing: 'Verwerking',
                storage: 'Opslag',
                retention: 'Bewaring',
                disposal: 'Verwijdering',
                archived: 'Gearchiveerd',
                active: 'Actief',
                pending_deletion: 'In afwachting Deletion',
                deleted: 'Verwijderd',
                on_hold: 'On Vasthouden',
            },
            all_stages: 'Alle Fases',
            object_id: 'Object-ID',
            object_type: 'Objecttype',
            policy_reference: 'Beleid Referentie',
            retention_period_days: 'Bewaring Periode (days)',
            retention_basis: 'Bewaring Grondslag',
            deletion_method: 'Deletion Methode',
            deletion_secure_erase: 'Beveiligd Erase',
            deletion_anonymization: 'Anonimisering',
            deletion_physical_destruction: 'Fysiek Destruction',
            deletion_other: 'Overig',
            responsible_party: 'Verantwoordelijk Party',
            responsible_user_id: 'Verantwoordelijk Gebruiker ID',
            retention_expiry: 'Bewaring Vervaldatum',
            hold_reason: 'Vasthouden Reden',
            hold: 'Vasthouden',
            release: 'Vrijgeven',
            hide_events: 'Verbergen Gebeurtenissen',
            events_timeline: 'Gebeurtenissen Tijdlijn',
            add_event: 'Toevoegen Gebeurtenis',
            event_type: 'Gebeurtenis Type',
            create_event: 'Aanmaken Gebeurtenis',
            from_stage: 'Van Fase',
            to_stage: 'Naar Fase',
            actor: 'Betrokkene',
            reason_placeholder: 'Reden...',
            go: 'Go',
            show_expired_retentions: 'Tonen Verlopen Retentions',
            hide_expired_retentions: 'Verbergen Verlopen Retentions',
            expired_retentions_alert: 'Verlopen Retentions Waarschuwing',
            failed_fetch: 'Kan niet fetch levenscyclus records. Probeer het opnieuw.',
            failed_save: 'Kan niet save levenscyclus record. Probeer het opnieuw.',
            failed_transition: 'Kan niet transition levenscyclus fase. Probeer het opnieuw.',
            failed_hold: 'Kan niet place vasthouden. Probeer het opnieuw.',
            failed_release: 'Kan niet release vasthouden. Probeer het opnieuw.',
            failed_fetch_events: 'Kan niet fetch levenscyclus gebeurtenissen. Probeer het opnieuw.',
            failed_create_event: 'Kan niet create levenscyclus gebeurtenis. Probeer het opnieuw.',
            failed_expired: 'Kan niet fetch expired retentions. Probeer het opnieuw.',
            hold_reason_prompt: 'Invoeren vasthouden reason:',
            loaded_count: '{count} levenscyclusrecords geladen',
            stage_transition_for: 'Faseovergang voor {id}',
            event_created: 'Aangemaakt',
            event_updated: 'Bijgewerkt',
            event_approved: 'Goedgekeurd',
            event_deprecated: 'Verouderd',
            event_proposed: 'Voorgesteld',
            event_reviewed: 'Beoordeeld',
            event_archived: 'Gearchiveerd',
            event_restored: 'Hersteld',
        },
        gap_report: {
            title: 'Classificatielacunerapport',
            unlabeled: 'Ongelabelde gegevens',
            missing_lifecycle: 'Ontbrekende levenscyclus',
            stale_labels: 'Verouderde labels',
            run_detection: 'Uitvoeren Hiaat Detectie',
            detecting: 'Detecteren...',
            detection_complete: 'Hiaat detection voltooid. {count} gap(s) detected.',
            detection_failed: 'Hiaat detection mislukt.',
            by_severity: 'By Ernst',
            by_gap_type: 'By Hiaat Type',
            all_gap_types: 'Alle Hiaat Types',
            conflicting_labels: 'Conflicterende labels',
            expired_retention: 'Verlopen Bewaring',
            missing_handling: 'Ontbrekend Afhandeling',
            info: 'Info',
            gap_type: 'Hiaat Type',
            object_id: 'Object-ID',
            object_type: 'Objecttype',
            detected_at: 'Gedetecteerd At',
            open: 'Geopend',
            acknowledged: 'Bevestigd',
            remediated: 'Hersteld',
            accepted: 'Geaccepteerd',
            acknowledge: 'Bevestigen',
            remediate: 'Herstellen',
            accept_risk: 'Accepteren Risico',
            accept_risk_prompt: 'Invoeren risico acceptance rechtvaardiging:',
            failed_fetch: 'Kan niet fetch hiaten. Probeer het opnieuw.',
            failed_acknowledge: 'Kan niet acknowledge hiaat. Probeer het opnieuw.',
            failed_remediate: 'Kan niet remediate hiaat. Probeer het opnieuw.',
            failed_accept_risk: 'Kan niet accept hiaat risico. Probeer het opnieuw.',
            failed_detect: 'Kan niet uitvoering hiaat detection. Probeer het opnieuw.',
            loaded_count: '{count} classificatielacunes geladen',
            related_object_id: 'Gerelateerd object-ID',
            related_object_type: 'Gerelateerd objecttype',
            gap_types: {
                unlabeled_asset: 'Ongelabeld object',
                missing_lifecycle: 'Ontbrekende levenscyclus',
                expired_retention: 'Verlopen bewaarperiode',
                extended_pending_deletion: 'Uitgestelde verwijdering',
            },
        },
        label_workflow: {
            title: 'Labelworkflow',
            proposed: 'Voorgesteld',
            under_review: 'In beoordeling',
            approved: 'Goedgekeurd',
            deprecated: 'Verouderd',
            submit_review: 'Indienen ter beoordeling',
            approve: 'Label goedkeuren',
            deprecate: 'Als verouderd markeren',
            bulk_approve: 'Bulk goedkeuren',
            bulk_approve_success: 'Labels goedgekeurd.',
            submit_success: 'Label ingediend ter beoordeling.',
            approve_success: 'Label goedgekeurd.',
            deprecate_success: 'Label als verouderd gemarkeerd.',
        },
        review_tasks: {
            title: 'Beoordelingstaken',
            create: 'Nieuwe beoordelingstaak',
            system: 'Systeem',
            owner: 'Eigenaar',
            description: 'Beschrijving',
            resolution: 'Oplossing',
            resolve: 'Oplossen',
            approve: 'Goedkeuren',
            modify: 'Wijzigen',
            reject: 'Afwijzen',
            escalate: 'Escaleren',
            escalated: 'Geëscaleerde taken',
            no_tasks: 'Geen beoordelingstaken.',
            resolve_success: 'Beoordelingstaak opgelost.',
            status_pending: 'In afwachting',
            status_resolved: 'Opgelost',
            status_escalated: 'Geëscaleerd',
            notes: 'Notities',
            create_success: 'Beoordelingstaak aangemaakt.',
            no_tasks_detail: 'Nog geen beoordelingstaken — maak een taak aan om labelbeoordelingen toe te wijzen aan systeemeigenaren.',
            label_id: 'Label-ID',
            system_id: 'Systeem-ID',
            system_name: 'Systeemnaam',
            owner_id: 'Eigenaar-ID',
            resolution_col: 'Oplossing',
            notes_col: 'Opmerkingen',
        },
        exceptions: {
            title: 'Uitzonderingswachtrij',
            create: 'Nieuwe uitzondering',
            severity: 'Ernst',
            system: 'Systeem',
            owner: 'Eigenaar',
            resolve: 'Oplossen',
            no_exceptions: 'Geen uitzonderingen gevonden.',
            resolve_success: 'Uitzondering opgelost.',
            dashboard_open: 'Open uitzonderingen',
            dashboard_resolved: 'Opgeloste uitzonderingen',
            dashboard_total: 'Totaal',
            dashboard_by_severity: 'Op ernst',
            status_open: 'Openen',
            status_resolved: 'Opgelost',
            status_accepted_risk: 'Geaccepteerd risico',
            create_success: 'Uitzondering aangemaakt.',
            no_exceptions_detail: 'Nog geen uitzonderingen — uitzonderingen worden aangemaakt vanuit ernstige lacunes die speciale afhandeling vereisen.',
            gap_id: 'Lacune-ID',
            system_id: 'Systeem-ID',
            system_name: 'Systeemnaam',
            owner_id: 'Eigenaar-ID',
            resolution_notes: 'Oplosnotities',
            resolution_notes_placeholder: 'Beschrijf hoe deze uitzondering is opgelost...',
            dashboard_accepted_risk: 'Accepted Risk',
            resolution_outcome: 'Outcome',
            resolve_success_accepted_risk: 'Exception closed as accepted risk.',
        },
        enterprise_map: {
            title: 'Bedrijfsdatakaart',
            inventory: 'Data-inventaris',
            total_systems: 'Totaal systemen',
            total_labels: 'Totaal labels',
            approved_count: 'Goedgekeurde labels',
            proposed_count: 'Voorgestelde labels',
            no_data: 'Geen classificatiegegevens beschikbaar.',
            no_approved_labels: 'Nog geen gelabelde objecten — een object verschijnt op de datakaart zodra het een classificatielabel heeft, of dat nu voorgesteld, in beoordeling of goedgekeurd is.',
            loaded_count: 'Geladen ondernemingsgegevenskaart',
        },
        discovery: {
            title: 'Ontdekkingscycli',
            create: 'Nieuwe cyclus',
            cycle_name: 'Cyclusnaam',
            run_delta: 'Deltadetectie uitvoeren',
            delta_success: 'Deltadetectie voltooid.',
            create_success: 'Ontdekkingscyclus aangemaakt.',
            no_cycles: 'Geen ontdekkingscycli.',
            status_created: 'Gemaakt',
            status_scanning: 'Scannen',
            status_review: 'Beoordeling',
            status_approved: 'Goedgekeurd',
            status_published: 'Gepubliceerd',
            status_draft: 'Concept',
            running: 'Actief',
            completed: 'Voltooid',
            cancelled: 'Geannuleerd',
            delta_result_summary: '{added} toegevoegd, {removed} verwijderd, {changed} gewijzigde labels gevonden.',
            delta_results: 'Deltaresultaten',
            view_results: 'Resultaten bekijken',
            scope_type: 'Bereiktype',
            scope_type_all: 'Allemaal',
            scope_type_systems: 'Systemen',
            scope_type_datasets: 'Gegevenssets',
            scope_ids: "Bereik-ID's",
            scope_ids_placeholder: "Voer ID's in, gescheiden door komma's",
            cycle_explanation: 'Een ontdekkingscyclus scant uw data-infrastructuur om data-assets te identificeren, classificeren en volgen. Configureer het bereik om specifieke systemen of datasets te targeten.',
            empty_state_title: 'Nog geen ontdekkingscycli',
            empty_state_description: 'Creëer uw eerste ontdekkingscyclus om te beginnen met het scannen en classificeren van uw gegevensassets.',
            empty_state_cta: 'Maak de eerste cyclus',
            validation_name_required: 'Cyclusnaam is vereist',
            validation_scope_type_required: 'Scopetype is vereist',
            validation_scope_ids_required: 'Er is ten minste één bereik-ID vereist',
        },
        regulatory: {
            sensitivity_importance_info: 'De gegevenswet (wet 60/2024/QH15, vereiste 18e4bfd1) vereist classificatie op basis van zowel gevoeligheid als belang als afzonderlijke dimensies. Creëer één taxonomie voor elke dimensie om aan deze vereiste te voldoen.',
            handling_rules_info: 'Elk classificatieniveau moet verwerkingsregels definiëren per vereiste. b89981ac ("Definieer afhandelingsregels op classificatieniveau").',
            template_internal_ids_info: "Alle classificatiesjablonen gebruiken interne ID's (bijvoorbeeld vn_data_law_data_classification_record). Er wordt geen sjabloon gepresenteerd als officieel ministerieformulier.",
            ai_risk_tiers_info: 'De AI-wet (Wet 134/2025/QH15, vereiste 3a87ad63) definieert vier AI-systeemrisiconiveaus: minimaal, beperkt, hoog en onaanvaardbaar. Maak een AI-risicotaxonomie met deze niveaus.',
            ai_risk_tier_minimal: 'Minimaal',
            ai_risk_tier_limited: 'Beperkt',
            ai_risk_tier_high: 'Hoog',
            ai_risk_tier_unacceptable: 'Onaanvaardbaar',
            ai_register_info: 'AI-classificatierecords moeten linken naar een AI-invoer in het systeemregister (req. 0f4475d3) waarin wordt vastgelegd: systeemnaam, eigenaar, doel, implementatiecontext, risiconiveau, betrokkenheid van de leverancier.',
            ai_template_internal_info: "AI-classificatiesjablonen gebruiken interne ID's (bijvoorbeeld AI-RISK-01). Er worden geen officiële tijdelijke AI-ID's (AI-HC-01 tot en met AI-HC-08) gebruikt.",
            ai_audit_trail_info: 'AI classificatiewijzigingen produceren een audittrail met versiebeheer per UC-VN-06: actor, tijdstempel, vorige waarde, nieuwe waarde en reden.',
            lifecycle_data_d1_info: 'Levenscyclusovergangen worden in kaart gebracht met de procedure voor gegevenswetgeving DATA-D1: catalogiseren, classificeren, verwerkingsregels definiëren, bewijsmateriaal vastleggen, beoordelingen plannen.',
            lifecycle_deletion_link_info: 'Een geclassificeerd data-item dat het einde van zijn levensduur bereikt, moet een verwijderingsschemarecord activeren via de Deletion Orchestration-module.',
            lifecycle_evidence_info: 'Bij elke wijziging in de levenscyclusstatus (concept, actief, onder beoordeling, gearchiveerd, verwijderd) worden de actor, het tijdstempel en de rechtvaardiging vastgelegd als een audittrailinvoer.',
            escalation_timers_info: 'UC-VN-10 escalatiedeadlines: 5 werkdagen voor beoordeling door de eigenaar, 48 uur voor escalatie van uitzonderingen, 72 uur voor risicovolle/gevoelige items.',
            escalation_owner_review: '5 werkdagen',
            escalation_exception: '48 uur',
            escalation_high_risk: '72 uur',
            gap_risk_areas_info: 'Vereiste gegevenswet f6c3060a ("Datarisicobeoordeling") vereist een analyse van de hiaten op vier risicogebieden: privacy, cyberbeveiliging, toegangscontrole en operationeel risico.',
            gap_risk_area_privacy: 'Privacy',
            gap_risk_area_cybersecurity: 'Cyberveiligheid',
            gap_risk_area_access: 'Toegangscontrole',
            gap_risk_area_operational: 'Operationeel',
            gap_framework_filter_info: 'Filter lacunes op regelgevingskader (VN_DATA_LAW of VN_AI_LAW_2026) om relevante lacunes voor elk regelgevingskader te bekijken.',
            framework_vn_data_law: 'VN Datawet',
            framework_vn_ai_law: 'VN AI-wet 2026',
            enterprise_map_approved_info: 'Deze gegevenskaart toont alleen goedgekeurde classificatierecords per UC-VN-10 workflowstap 10 ("Goedgekeurde labels worden de huidige bedrijfswaarheid"). Concept- en voorgestelde records zijn uitgesloten.',
            exports_bundle_info: 'Exportbundels omvatten: classificatieregister, verwerkingsregels, eigendom/RACI en bewijspakket – overeenkomend met UC-VN-10 exportvereisten en DATA-EVD-01 bewijsbladformaat.',
        },
    },
    monitoring: {
        no_permission_title: 'Onvoldoende machtigingen',
        no_permission_message: 'U heeft geen toestemming om deze monitoringmodule te openen.',
        errors: {
            fetch: 'Kan niet load..',
            save: 'Kan niet save..',
            fetch_programs_failed: "Kan niet fetch programma's..",
            fetch_kpi_failed: 'Kan niet fetch KPI gegevens..',
            create_snapshot_failed: 'Kan niet create snapshot..',
            fetch_periods_failed: 'Kan niet fetch periodes..',
            create_period_failed: 'Kan niet create periode..',
            auto_generate_failed: 'Kan niet auto-generate periodes..',
            start_period_failed: 'Kan niet start periode..',
            close_period_failed: 'Kan niet close periode..',
            save_program_failed: 'Kan niet save programma..',
            activate_program_failed: 'Kan niet activate programma..',
            pause_program_failed: 'Kan niet pause programma..',
            complete_program_failed: 'Kan niet voltooid programma..',
            archive_program_failed: 'Kan niet archive programma..',
            fetch_ric_templates_failed: 'Kan niet fetch RIC sjablonen..',
            fetch_ric_cases_failed: 'Kan niet fetch RIC cases..',
            create_ric_case_failed: 'Kan niet create RIC case..',
            transition_ric_failed: 'Kan niet transition RIC case..',
            add_follow_up_failed: 'Kan niet add follow-up..',
            respond_follow_up_failed: 'Kan niet respond naar follow-up..',
            link_evidence_failed: 'Kan niet link bewijs..',
            fetch_task_templates_failed: 'Kan niet fetch taak sjablonen..',
            save_task_template_failed: 'Kan niet save taak sjabloon..',
            delete_task_template_failed: 'Kan niet delete taak sjabloon..',
            delete_framework_link_failed: 'Kan de raamwerkkoppeling niet verwijderen.',
            seed_defaults_failed: 'Kan niet seed standaardwaarden..',
            fetch_tasks_failed: 'Kan niet fetch taken..',
            create_task_failed: 'Kan niet create taak..',
            instantiate_tasks_failed: 'Kan niet instantiate taken..',
            start_task_failed: 'Kan niet start taak..',
            complete_task_failed: 'Kan niet voltooid taak..',
            defer_task_failed: 'Kan niet defer taak..',
            block_task_failed: 'Kan niet block taak..',
            override_complete_failed: 'Kan niet override voltooid..',
            assign_task_failed: 'Kan niet assign taak..',
            update_checklist_failed: 'Kan niet update checklist..',
        },
        nav: {
            programs: "Programma's",
            periods: 'Perioden',
            task_templates: 'Taaksjablonen',
            tasks: 'Taken',
            kpi: 'KPI-overzicht',
            ric: 'Toezichthouderverzoeken',
            framework_link: 'Kadermonitoring',
            authority_readiness: 'Autoriteitsgereedheid',
            change_review: 'Wijzigingsbeoordeling',
            raci: 'RACI-matrix',
        },
        programs: {
            page_title: "Monitoringprogramma's",
            title: "Monitoringprogramma's",
            new: 'Nieuw programma',
            edit: 'Programma bewerken',
            name: 'Naam',
            scope: 'Reikwijdte',
            frequency: 'Frequentie',
            owner: 'Eigenaar',
            activate: 'Activeren',
            pause: 'Pauzeren',
            resume: 'Hervatten',
            complete: 'Voltooien',
            archive: 'Archiveren',
            custom_frequency_days: 'Aangepast Frequentie (days)',
            select_user: 'Gebruiker selecteren…',
            assign_training: 'Training toewijzen',
            linked_forms: 'Gekoppelde formulieren',
            linked_forms_none: 'Nog geen gekoppelde formulieren',
            loaded_count: "{0} programma's geladen",
            scope_labels: {
                full: 'Volledig',
                partial: 'Gedeeltelijk',
                targeted: 'Gericht',
                consent: 'Toestemming',
                dpia: 'DPIA',
                vendors: 'Leveranciers',
                classification: 'Classificatie',
                rights_requests: 'Rechtenverzoeken',
                data_mapping: 'Datakartering',
                transfers: 'Overdrachten',
                security_monitoring: 'Beveiligingsmonitoring',
                authority_response: 'Autoriteit antwoord',
                evidence_preservation: 'Bewijsbehoud',
                localization_review: 'Lokalisatiebeoordeling',
                ai_system_registration: 'AI Systeemregistratie',
                ai_pre_deployment: 'AI Beoordeling vóór implementatie',
                ai_transparency: 'AI Transparantiecontrole',
                ai_incident_review: 'AI Incidentbeoordeling',
            },
            framework_cadence_info: 'Cybersecurity Law programs typically use continuous/monthly/quarterly cadences. AI Law programs typically use annual review cadences.',
        },
        periods: {
            page_title: 'Monitoringperioden',
            title: 'Beoordelingsperioden',
            new: 'Nieuwe periode',
            label: 'Periodelabel',
            start_date: 'Startdatum',
            end_date: 'Einddatum',
            reviewers: 'Beoordelaars',
            auto_generate: 'Perioden automatisch genereren',
            start: 'Starten',
            close: 'Sluiten',
            change_digest: 'Wijzigingsoverzicht',
            no_digest: 'Geen samenvatting beschikbaar.',
            reviewers_count: 'Beoordelaars Aantal',
            select_program: 'Selecteren Programma',
            select_program_prompt: 'Selecteren een programma naar view periodes.',
            hide_digest: 'Verbergen Digest',
            loaded_count: '{0} perioden geladen',
            periods_auto_generated: 'Perioden automatisch gegenereerd',
            label_placeholder: 'bijv. 2026-K1',
        },
        task_templates: {
            page_title: 'Taaksjablonen',
            title: 'Taaksjablonen',
            new: 'Nieuw sjabloon',
            edit: 'Sjabloon bewerken',
            category: 'Categorie',
            priority: 'Prioriteit',
            checklist_items: 'Checklistitems',
            effort: 'Geschatte inspanning (uren)',
            pre_seeded: 'Vooraf ingevuld',
            seed_defaults: 'Standaardwaarden laden',
            add_checklist: 'Checklistitem toevoegen',
            guidance: 'Richtlijnen',
            all_categories: 'Alle Categorieën',
            loaded_count: '{0} taaksjablonen geladen',
            defaults_seeded: 'Standaard taaksjablonen aangemaakt',
            checklist_item_label: 'Checklistitem {0}',
            remove_checklist_item: 'Checklistitem {0} verwijderen',
            category_info: 'Categories cover PDPL compliance, Cybersecurity Law tasks, and AI Law tasks.',
        },
        tasks: {
            page_title: 'Beoordelingstaken',
            title: 'Beoordelingstaken',
            new: 'Nieuwe taak',
            assigned_to: 'Toegewezen aan',
            due_date: 'Uiterste datum',
            checklist: 'Controlelijst',
            start: 'Starten',
            complete: 'Voltooien',
            defer: 'Uitstellen',
            block: 'Blokkeren',
            override_complete: 'Geforceerd voltooien',
            link_evidence: 'Bewijs koppelen',
            direct_upload_label: 'Bewijsbestand uploaden',
            instantiate: 'Instantiëren vanuit sjablonen',
            defer_reason: 'Reden voor uitstel',
            override_justification: 'Rechtvaardiging voor overschrijving',
            filter_assignee: 'Filteren op assignee',
            select_period: 'Selecteren Periode',
            select_program: 'Selecteren Programma',
            select_program_period_prompt: 'Selecteren een programma en periode naar view taken.',
            assign_placeholder: 'Toewijzen naar...',
            loaded_count: '{0} taken geladen',
            tasks_instantiated: 'Taken aangemaakt van sjablonen',
            overdue: 'Achterstallig',
            checklist_for: 'checklist voor',
            manage_checklist: 'Checklist beheren',
            add_checklist_item: 'Item toevoegen',
            checklist_item_label: 'Item',
            checklist_item_description: 'Beschrijving',
            remove_checklist_item: 'Item verwijderen',
            save_checklist: 'Checklist opslaan',
            no_checklist_items: 'Nog geen checklistitems.',
            confirm_delete_attachment: 'Weet u zeker dat u dit bewijsbestand wilt verwijderen? Dit kan niet ongedaan worden gemaakt.',
        },
        kpi: {
            page_title: 'KPI-overzicht',
            title: 'KPI-overzicht',
            kpi_name: 'KPI-naam',
            current_value: 'Huidige waarde',
            target: 'Doel',
            trends: 'Ontwikkelingen',
            capture_snapshot: 'Vastleggen Snapshot',
            select_program: 'Selecteren Programma',
            select_program_prompt: 'Selecteren een programma naar view KPIs.',
            snapshot: 'Momentopname',
            kpi_labels: {
                on_track: 'Op schema',
                at_risk: 'At Risico',
                behind: 'Achter',
                rights_request_sla_rate: 'SLA-percentage rechtenverzoeken',
                vendor_review_currency: 'Leveranciersbeoordeling actualiteit',
                dpia_completion_rate: 'DPIA-voltooiingspercentage',
                classification_coverage: 'Classificatiedekking',
                consent_policy_currency: 'Toestemmingsbeleid actualiteit',
                security_event_detection_rate: 'Detectiesnelheid van beveiligingsgebeurtenissen',
                evidence_preservation_compliance_rate: 'Nalevingspercentage van bewijsbehoud',
                authority_response_timeliness: 'Tijdigheid van respons van autoriteiten',
                localization_readiness_score: 'Score voor lokalisatiegereedheid',
                ai_register_completeness: 'AI Volledigheid systeemregister',
                pre_deployment_gate_pass_rate: 'Gate-passpercentage vóór implementatie',
                transparency_disclosure_coverage: 'Dekking van openbaarmaking van transparantie',
                ai_incident_readiness_score: 'AI Incidentgereedheidsscore',
            },
            pdpl_kpi_info: 'The predefined KPI codes are PDPL-specific metrics. For Cybersecurity Law or AI Law monitoring, use framework-specific KPI codes.',
            cybersec_kpi_info: "KPI's op het gebied van de cyberbeveiligingswetgeving: detectiepercentage van beveiligingsgebeurtenissen, nalevingspercentage van bewijsbehoud, tijdigheid van respons van autoriteiten, score van gereedheid voor lokalisatie.",
            ai_law_kpi_info: "AI-wet-KPI's: volledigheid van het AI-systeemregister, slagingspercentage vóór implementatie, dekking van openbaarmaking van transparantie, score voor AI-incidentgereedheid.",
            no_data: 'No gegevens available.',
            snapshot_date: 'Snapshot Datum',
            loaded_count: '{0} KPI-momentopnames geladen',
            snapshot_captured: 'KPI-momentopname vastgelegd',
            showing_chart: 'Grafiekweergave',
            showing_table: 'Datatabelweergave',
            aria_current_values: 'KPI huidige waarden',
            kpi_label: 'KPI',
            actual: 'Werkelijk',
            trend: 'Trend',
            notes: 'Opmerkingen',
            trend_up: 'Stijgend',
            trend_down: 'Dalend',
            trend_stable: 'Stabiel',
        },
        ric: {
            page_title: 'RIC-zaken',
            title: 'Toezichthouderverzoeken',
            new: 'Nieuw RIC-dossier',
            reference: 'Referentie',
            regulator: 'Toezichthouder',
            inquiry_type: 'Type verzoek',
            deadline: 'Antwoordtermijn',
            lead: 'Verantwoordelijke',
            follow_ups: 'Opvolgingen',
            add_follow_up: 'Opvolging toevoegen',
            respond: 'Reageren',
            question: 'Vraag',
            response: 'Antwoord',
            asked_at: 'Gevraagd op',
            no_follow_ups: 'Nog geen opvolgingen.',
            received_date: 'Ontvangen Datum',
            responded_at: 'Beantwoord At',
            hide_follow_ups: 'Verbergen Follow-ups',
            inquiry_types: {
                general: 'Algemeen',
                investigation: 'Onderzoek',
                audit: 'Controle',
                complaint: 'Klacht',
                general_audit: 'Algemene audit',
                rights_requests_review: 'Beoordeling rechtenverzoeken',
                cross_border_transfer_review: 'Beoordeling grensoverschrijdende overdrachten',
                vendor_governance_review: 'Beoordeling leveranciersbeheer',
                consent_management_review: 'Beoordeling toestemmingsbeheer',
                data_mapping_ropa_review: 'Beoordeling datakartering / ROPA',
                incident_readiness_review: 'Beoordeling incidentgereedheid',
                security_access_control_review: 'Beoordeling beveiliging en toegangsbeheer',
                targeted_processing_review: 'Beoordeling gerichte verwerking',
                bank_readiness_pack: 'Bankgereedheidspakket',
            },
            link_evidence: 'Koppelen Bewijs',
            loaded_count: '{0} RIC-zaken geladen',
            to_follow_up: 'naar vervolgvraag',
            overdue: 'Te laat',
            overdue_indicator: 'Reactietermijn overschreden',
            template_info: 'RIC templates are internal platform templates. They are not official government-mandated forms.',
            status_labels: {
                received: 'Ontvangen',
                in_triage: 'In triage',
                evidence_gathering: 'Bewijsverzameling',
                response_drafting: 'Antwoord opstellen',
                internal_review: 'Interne beoordeling',
                submitted: 'Ingediend',
                follow_up: 'Opvolging',
                closed: 'Gesloten',
            },
        },
        framework_link: {
            title: 'Kadergekoppelde monitoring',
            link_program: 'Programma aan kader koppelen',
            framework_code: 'Kadercode',
            select_framework: 'Selecteer een kader...',
            select_program: 'Selecteer een programma...',
            readiness_score: 'Gereedheidsscore',
            no_links: "Geen kadergekoppelde programma's gevonden",
            seed_defaults: 'Standaardsjablonen aanmaken',
            program_id: 'Programma-ID',
            program: 'Programma',
        },
        authority_readiness: {
            title: 'Autoriteitsverzoekgereedheid',
            create_entry: 'Nieuwe gereedheidsvermelding',
            select_framework: 'Selecteer een kader...',
            framework_code: 'Kader',
            readiness_status: 'Gereedheidsstatus',
            readiness_package: 'Gereedheidspakket',
            run_drill: 'Gereedheidsoefening uitvoeren',
            drill_success: 'Gereedheidsoefening geregistreerd',
            last_drill: 'Laatste oefening',
            never_drilled: 'Nooit',
            no_readiness: 'Geen autoriteitsgereedheidsdata gevonden',
            entry_created: 'Gereedheidsvermelding aangemaakt',
            ready: 'Gereed',
            partial: 'Gedeeltelijk',
            not_ready: 'Niet gereed',
            items: 'items',
        },
        change_review: {
            title: 'Regelgevingswijzigingsbeoordeling',
            new_review: 'Nieuwe wijzigingsbeoordeling',
            change_id: 'Wijzigings-ID',
            select_change: 'Selecteer een wijziging...',
            select_framework: 'Selecteer een kader...',
            impact_assessment: 'Impactbeoordeling',
            decision: 'Beslissing',
            no_action: 'Geen actie',
            internal_update: 'Interne update',
            authority_filing: 'Autoriteitsmelding',
            no_reviews: 'Geen wijzigingsbeoordelingen gevonden',
            auto_cycle: 'Automatische beoordelingscyclus',
            cadence: 'Beoordelingsfrequentie',
            framework: 'Raamwerk',
        },
        export_csv: 'Exporteer CSV',
        exporting: 'Exporteren…',
        export_success: 'CSV is succesvol geëxporteerd.',
        export_failed: 'CSV exporteren mislukt.',
        export_attestation_pack: 'Attestpakket exporteren',
        staleness: {
            badge_label: '{0} verouderde items',
            days_since: '{0} dagen sinds de laatste update',
            detail_title: 'Verouderde artikelen',
            accept_justification: 'Accepteer met rechtvaardiging',
            justification_label: 'Rechtvaardiging',
            no_stale: 'Geen oude items gevonden.',
            threshold_label: 'Verouderingsdrempel (dagen)',
            threshold_min: 'Minimaal 30 dagen',
            threshold_max: 'Maximaal 730 dagen',
            threshold_saved: 'Drempel voor veroudering bijgewerkt.',
        },
        csv_columns: {
            name: 'Naam',
            status: 'Status',
            owner: 'Eigenaar',
            created_at: 'Gemaakt op',
            updated_at: 'Bijgewerkt op',
            scope: 'Reikwijdte',
            frameworks: 'Kaders',
            period_label: 'Periodelabel',
            start_date: 'Startdatum',
            end_date: 'Einddatum',
            task_count: 'Taaktelling',
            completion_rate: 'Voltooiingspercentage',
            title: 'Titel',
            assigned_to: 'Toegewezen aan',
            due_date: 'Vervaldatum',
            priority: 'Prioriteit',
            completed_at: 'Voltooid op',
            kpi_code: 'KPI Code',
            kpi_label: 'KPI Label',
            target: 'Doel',
            actual: 'Werkelijk',
            trend: 'Trend',
            reference: 'Referentie',
            regulator: 'Regelaar',
            inquiry_type: 'Onderzoekstype',
            lead: 'Lood',
            received_date: 'Datum ontvangen',
            deadline: 'Deadline',
            framework_code: 'Kadercode',
            readiness_status: 'Gereedheidsstatus',
            package_count: 'Aantal pakketten',
            change_id: 'ID wijzigen',
            impact_assessment: 'Effectbeoordeling',
            decision: 'Besluit',
            program: 'Programma',
            readiness_score: 'Gereedheidsscore',
            category: 'Categorie',
            checklist_count: 'Controlelijst tellen',
            effort_hours: 'Inspanning (uren)',
            version: 'Versie',
            department: 'Afdeling',
            function_area: 'Functiegebied',
            role: 'Rol',
            assignee: 'Ontvanger',
            frequency: 'Frequentie',
        },
        raci: {
            page_title: 'Bestuur RACI-matrix',
            title: 'Bestuur RACI-matrix',
            department: 'Afdeling',
            function_area: 'Functiegebied',
            responsible: 'Verantwoordelijk',
            accountable: 'Verantwoordelijk',
            consulted: 'Geraadpleegd',
            informed: 'Geïnformeerd',
            assign_role: 'RACI-rol toewijzen',
            select_program: 'Selecteer Programma',
            select_program_prompt: 'Selecteer een programma om de RACI-matrix te bekijken.',
            no_assignments: 'Geen RACI-toewijzingen gevonden.',
            role_updated: 'RACI-toewijzing bijgewerkt.',
            role_labels: {
                R: 'Verantwoordelijk',
                A: 'Verantwoordelijk',
                C: 'Geraadpleegd',
                I: 'Geïnformeerd',
            },
            loaded_count: '{0} RACI-toewijzingen geladen',
            single_accountability_warning: 'Per functiegebied is slechts één Accountable (A)-rol toegestaan. Als u een nieuwe Accountable-rol toewijst, wordt de bestaande vervangen.',
            cybersec_function_areas: 'Functiegebieden op het gebied van cyberveiligheidsrecht: incidentrespons, behoud van bewijsmateriaal, samenwerking tussen autoriteiten, lokalisatiebeoordeling.',
            ai_law_function_areas: 'AI Juridische functiegebieden: systeemregistratie, beoordeling vóór implementatie, transparantie, incidentafhandeling.',
            suggested_function_areas: 'Voorgestelde functiegebieden',
        },
        prior_cycle: {
            link_label: 'Link naar voorgaande cyclus',
            select_prior: 'Selecteer vorige cyclus…',
            linked: 'Jaar na jaar',
            no_prior: 'Geen eerdere cycli beschikbaar.',
            linked_success: 'Vorige cyclus gekoppeld.',
        },
        digest_table: {
            module: 'Module',
            entity_type: 'Entiteitstype',
            change_type: 'Type wijzigen',
            description: 'Beschrijving',
            changed_at: 'Gewijzigd op',
        },
        view_related_ric: 'Bekijk gerelateerde RIC-cases',
        linked_program: 'Gekoppeld programma',
        nav_raci: 'RACI-matrix',
    },
    incidents: {
        authority_response_draft: {
            draft_label: 'Concept reactie',
            drafting: 'Opstellen…',
            results_heading: 'Opgestelde antwoorden ({count})',
            tokens_label: '≈ {count} tokens',
            counsel_label: 'Beoordeling door een raadsman vereist:',
            counsel_default: 'Voor ten minste één antwoord is beoordeling door een raadsman vereist voordat het wordt verzonden.',
            requires_counsel_label: 'Vereist advies van een raadsman.',
            accept_label: 'Concept accepteren',
            reject_label: 'Concept afwijzen',
            error_generic: 'Kan het antwoord niet opstellen. Probeer het opnieuw of verfijn de aanvraagtekst.',
            question_action_accepted: 'Accept answer',
            question_action_rejected: 'Reject answer',
            question_action_needs_counsel: 'Needs counsel',
            question_action_aria: '{action} ({question})',
            question_decision_label: 'Decision on this answer: {decision}',
            question_decision_accepted: 'accepted',
            question_decision_rejected: 'rejected',
            question_decision_needs_counsel: 'sent for counsel review',
            download_docx_label: 'Download response template (DOCX)',
            draft_resolved_accepted: 'Every answer is decided. The draft is accepted.',
            draft_resolved_rejected: 'Every answer is decided. The draft is rejected.',
        },
        breach_timeline: {
            compute_label: 'Tijdlijn voor meldingen van inbreuken op computers',
            computing: 'Computergebruik…',
            results_heading: 'Deadline voor melding van inbreuk ({count})',
            tokens_label: '≈ {count} tokens',
            handoff_label: 'Forseti suggereert:',
            empty_label: 'Geen enkel geïnstalleerd raamwerk verklaart een meldingsplicht voor dit incident.',
            mandatory_label: 'Verplichte velden:',
            draft_label: 'Kennisgeving concept',
            remaining_label: '{count}u resterend',
            overdue_label: 'ACHTERLIJK door {count}h',
            acknowledge_label: 'Tijdlijn bevestigen',
            acknowledging: 'Erkennen…',
            error_generic: 'Kan de tijdlijn van de inbreuk niet berekenen. Probeer het opnieuw.',
            tasks_note: 'Urgent tasks were raised for the duties that match this incident’s jurisdictions. Computing again reuses them instead of creating more.',
        },
        breach_determination: {
            yes: 'Bevestigd Inbreuk',
            no: 'Niet een Inbreuk',
            uncertain: 'In afwachting',
        },
        errors: {
            fetch: 'Kan niet load incidenten..',
            save: 'Kan niet save incident..',
            fetch_failed: 'Kan niet fetch incidenten..',
            save_failed: 'Kan niet save incident..',
            transition_failed: 'Kan niet transition incident..',
            close_failed: 'Kan niet close incident..',
            fetch_timeline_failed: 'Kan niet fetch tijdlijn..',
            fetch_decisions_failed: 'Kan niet fetch decisions..',
            triage_failed: 'Kan niet update triage..',
            impact_update_failed: 'Kan niet update impact beoordeling..',
            breach_update_failed: 'Kan niet update breach beoordeling..',
            create_timeline_failed: 'Kan niet create tijdlijn item..',
            create_decision_failed: 'Kan niet create beslissing..',
            notification_update_failed: 'Kan niet update melding..',
            // Sprint 62.35.0 gate E12.5 pass 2 (P2-1) — English until Phase 25 (R-57)
            breach_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the breach assessment was not saved. The latest version of the incident is being loaded and your entries are kept: check them and save again.",
            notification_status_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the notification status was not changed. The latest version of the incident is being loaded: check the status and change it again if it still needs changing.",
            fetch_tasks_failed: 'Kan niet fetch taken..',
            create_task_failed: 'Kan niet create taak..',
            update_task_status_failed: 'Kan de taakstatus niet bijwerken.',
            update_checklist_failed: 'Kan niet update checklist..',
            assign_task_failed: 'Kan niet assign taak..',
            escalate_task_failed: 'Kan niet escalate taak..',
            fetch_templates_failed: 'Kan niet fetch sjablonen..',
            permission_denied: 'U hebt geen toestemming om deze sectie te openen. Neem contact op met uw beheerder.',
        },
        harm_likelihood: {
            unlikely: 'Onwaarschijnlijk',
            possible: 'Mogelijk',
            likely: 'Waarschijnlijk',
            very_likely: 'Zeer Waarschijnlijk',
        },
        // Sprint 63.37.0 (legal second read S12) — the impact assessment's two vocabularies as words.
        data_category_options: {
            personal_data: 'Personal data',
            sensitive_personal_data: 'Sensitive personal data',
            financial_data: 'Financial data',
            health_data: 'Health data',
            childrens_data: "Children's personal data",
            other: 'Other personal data',
        },
        consequence_options: {
            identity_theft: 'Identity theft',
            financial_loss: 'Financial loss',
            reputational_damage: 'Reputational damage',
            discrimination: 'Discrimination',
            emotional_distress: 'Emotional distress',
            physical_harm: 'Physical harm',
            loss_of_confidentiality: 'Loss of confidentiality',
            loss_of_access: 'Loss of access to the data',
        },
        reported_via: {
            email: 'E-mail',
            phone: 'Telefoon',
            internal_system: 'Intern Systeem',
            third_party: 'Derde Party',
            automated_detection: 'Geautomatiseerd Detectie',
        },
        task_transition_labels: {
            start: 'Starten',
            complete: 'Voltooien',
            block: 'Blokkeren',
            defer: 'Uitstellen',
            cancel: 'Annuleren',
            unblock: 'Deblokkeren',
        },
        transition_labels: {
            start: 'Starten',
            complete: 'Voltooien',
            block: 'Blokkeren',
            defer: 'Uitstellen',
        },
        nav: {
            all: 'Alle incidenten',
            tasks: 'Responstaken',
            reports: 'Rapporten',
            breach_notifications: 'Inbreukmeldingen',
            supplements: 'Aanvullingen',
            remediation: 'Herstelmaatregelen',
            authority_requests: 'Autoriteitsverzoeken',
            disclosure_register: 'Openbaarmakingsregister',
        },
        list: {
            page_title: 'Incidenten',
            title: 'Incidenten',
            new: 'Nieuw incident',
            edit: 'Incident bewerken',
            incident_title: 'Titel',
            severity: 'Ernst',
            reported_at: 'Gemeld op',
            reported_by: 'Gemeld door',
            assigned_to: 'Toegewezen aan',
            view_detail: 'Detail bekijken',
            reference: 'Referentie',
            lead: 'Leider',
            reported_via: 'Gerapporteerd Via',
            discovery_date: 'Ontdekking Datum',
            // fix-sprint #447 W10 — discovery is a date and time
            discovery_hint: 'The date and time the incident was discovered. Hour-based notification clocks run from this moment.',
            discovery_no_time: '(no time of day recorded)',
            discovery_day_only_hint: 'Discovery was recorded as the day {day}, with no time of day. Enter the date and time to record it, or leave it empty to keep the day.',
            hide_details: 'Verbergen Details',
            close_incident: 'Sluiten Incident',
            close_justification_prompt: 'Gelieve provide een rechtvaardiging voor closing dit incident.',
            justification: 'Rechtvaardiging',
            lead_id: 'Leider ID',
            lead_not_assigned: 'Not assigned',
            date_not_recorded: 'Not recorded',
            contact: 'Contactpersoon',
            category: 'Categorie',
            not_assessed: 'Niet assessed',
        },
        detail: {
            page_title: 'Incidentdetail',
            title: 'Incidentdetail',
            page_description: 'One incident in full: its overview, triage, impact and breach assessments, timeline, decisions, evidence and audit trail.',
            forseti_heading: 'Forseti for this incident',
            tabs: {
                overview: 'Overzicht',
                triage: 'Beoordeling',
                impact: 'Impactbeoordeling',
                breach: 'Datalekbeoordeling',
                timeline: 'Tijdlijn',
                decisions: 'Beslissingen',
                evidence: 'Bewijs',
                audit_trail: 'Audittraject',
            },
            evidence_upload_label: 'Bewijsbestand bijvoegen',
            evidence_uploaded: 'Bewijs geüpload',
            evidence_deleted: 'Bewijs verwijderd',
            evidence_delete_error: 'Bewijs verwijderen mislukt',
            evidence_uploaded_at: 'Geüpload',
            evidence_empty: 'Er zijn geen bewijsbestanden aan dit incident gekoppeld.',
            evidence_size: 'Grootte',
            triage_notes: 'Triagenotities',
            initial_classification: 'Initiële classificatie',
            individuals_affected: 'Aantal getroffen personen',
            data_categories: 'Betrokken gegevenscategorieën',
            systems_compromised: 'Gecompromitteerde systemen',
            geographic_scope: 'Geografische reikwijdte',
            breach_determination: 'Datalekbepaling',
            // fix-sprint #447 W10 — the notification clock runs from discovery
            notification_clock: {
                label: 'Notification clock',
                started_from_discovery: 'Running from the recorded discovery: {date}',
                started_at_triage: 'Running from {date}, when the breach was determined; no discovery was recorded then. Recording the discovery dates the clock from it.',
                started_from_earlier_discovery: 'Running from {date}, the discovery recorded when the breach was determined. The discovery recorded now is different; the clock is not moved.',
                not_started_no_discovery: 'Not started: a breach was determined, but no discovery date and time is recorded. The clock runs from discovery and is never started from triage. Record when the incident was discovered to start it.',
                not_started_no_determination: 'Not started: no breach has been determined. Once one is, the clock runs from the recorded discovery.',
            },
            notification_required: 'Melding vereist',
            notification_deadline: 'Meldingstermijn',
            pdpc_reportable: 'Meldingsplichtig bij AP',
            add_timeline: 'Tijdlijnitem toevoegen',
            add_decision: 'Beslissing toevoegen',
            not_found: 'Incident not gevonden.',
            team_members: 'Team Leden',
            checklist: 'Controlelijst',
            completed: 'Voltooid',
            impact_severity_score: 'Calculated Impact Ernst Score',
            records_affected: 'Getroffen records',
            potential_consequences: 'Potentieel Gevolgen',
            harm_likelihood: 'Schade Waarschijnlijkheid',
            legal_basis: 'Juridisch Grondslag',
            legal_basis_not_recorded: 'Not recorded',
            jurisdictions: 'Jurisdicties',
            jurisdictions_comma: 'Jurisdicties (comma-separated)',
            jurisdiction: 'Jurisdictie',
            authority_status: 'Autoriteit Status',
            subject_status: 'Onderwerp Status',
            authority_deadline: 'Autoriteit Deadline',
            subject_deadline: 'Onderwerp Deadline',
            mark_authority_done: 'Markeren Autoriteit Done',
            mark_subject_done: 'Markeren Onderwerp Done',
            // Sprint 62.35.0 gate E12.5 pass 2 (P2-2) — English until Phase 25 (R-57)
            requirement_statuses: {
                not_required: 'Not required',
                required: 'Required',
                in_progress: 'In progress',
                completed: 'Completed',
                overdue: 'Overdue',
            },
            status_contradiction: {
                declared_required: 'The assessment now declares the {notification} required; its recorded status is {status}.',
                declared_not_required: 'The assessment now declares the {notification} not required; its recorded status is {status}.',
                notifications: {
                    authority_status: 'notification to the authority',
                    subject_status: 'notification to the data subjects',
                    other_status: 'notification to other parties',
                },
            },
            // Sprint 62.35.0 (AC9) — English until Phase 25 (R-57)
            affected_parties: {
                title: 'Affected parties notified',
                intro: 'Organisations and individuals concerned, other than the data subjects, who were told of this incident under this requirement, who are still to be told, or who need not be told.',
                requirement_heading: 'Requirement: {jurisdiction}',
                no_requirements: 'No notification requirement is recorded on this incident, so there is no requirement to record affected parties against.',
                empty: 'No affected party is recorded for this requirement.',
                not_recorded: 'Not recorded',
                add: 'Add affected party',
                add_aria: 'Add affected party for requirement {jurisdiction}',
                edit: 'Edit',
                edit_aria: 'Edit affected party: {name}',
                remove: 'Remove',
                remove_aria: 'Remove affected party: {name}',
                remove_confirm_title: 'Remove this affected party?',
                remove_confirm_message: 'The row is removed from this requirement. The audit trail keeps what it recorded.',
                modal_add_title: 'Add affected party',
                modal_edit_title: 'Edit affected party',
                table_label: 'Affected parties for requirement {jurisdiction}',
                col_party: 'Party',
                col_type: 'Type',
                col_status: 'Status',
                col_date: 'Date notified',
                field_party_name: 'Party name',
                field_party_type: 'Party type',
                field_contact: 'Contact',
                field_status: 'Notification status',
                field_notified_on: 'Date notified',
                field_reason: 'Why notification is not required',
                field_note: 'Note',
                types: {
                    data_owner: 'Data owner',
                    partner: 'Partner',
                    handover_recipient: 'Recipient of a handover',
                    other: 'Other',
                },
                statuses: {
                    pending: 'Pending',
                    notified: 'Notified',
                    not_required: 'Not required',
                },
                errors: {
                    party_name_required: 'Enter the name of the party.',
                    notified_on_required: 'Enter the date the party was notified.',
                    notified_on_in_future: 'The date notified cannot be in the future.',
                    reason_required: 'Enter why notification is not required.',
                    save_failed: 'The affected party could not be saved.',
                    remove_failed: 'The affected party could not be removed.',
                    conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so nothing was saved. The latest version of the incident is being loaded and your entries are kept: check them and save again.",
                    remove_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the party was not removed. The latest version of the incident is being loaded: check the list and remove the party again if it is still there.",
                    conflict_party_changed: 'Another save changed this affected party after you opened it, so nothing was saved. The form now shows what that save recorded, and the entries you made were not kept: review the values and enter your changes again before saving.',
                    conflict_party_gone: 'Another save removed this affected party after you opened it, so nothing was saved and the form was closed.',
                    conflict_requirement_gone: 'Another save removed this notification requirement from the incident after you opened the form, so nothing was saved and the form was closed.',
                },
                saved: 'Affected party saved',
                removed: 'Affected party removed',
            },
            // Sprint 62.45.0 — the service-interruption window on the incident record
            interruption_window: {
                title: 'Interruption window',
                intro: 'When a system or service stopped, when it came back, and when remediation was completed. Recorded once, here, so anything that follows from these times reads them from this record rather than retyping them. Remediation completed is not the same as closing the incident.',
                // Gate E12.5 pass 3 (QA P2) — shown only to a viewer who can read the register.
                intro_register: 'When a supervised system or service stopped, when it came back, and when remediation was completed. Recorded once, here: the Supervisory Reporting register reads these times for the interruption notice and the incident report and never retypes them. Remediation completed is not the same as closing the incident.',
                started_at: 'Interruption started',
                ended_at: 'Interruption ended',
                remediation_completed_at: 'Remediation completed',
                not_recorded: 'Not recorded',
                ongoing: 'No end is recorded, so the interruption is treated as continuing.',
                edit: 'Edit interruption window',
                save: 'Save interruption window',
                saving: 'Saving…',
                cancel: 'Cancel',
                local_time_hint: 'Your local date and time. Leave a field empty to clear it.',
                unchanged: 'Change a time to save.',
                saved: 'Interruption window saved',
                save_failed: 'The interruption window could not be saved.',
                errors: {
                    start_first: 'Record when the interruption started first.',
                    before_start: 'This cannot be earlier than the interruption start.',
                    invalid: 'The interruption window is not in order, so nothing was saved.',
                },
            },
            not_assessed: 'Niet assessed',
            event_type: 'Gebeurtenis Type',
            timestamp: 'Tijdstempel',
            actor: 'Betrokkene',
            no_timeline_events: 'No tijdlijn gebeurtenissen yet.',
            decision_type: 'Beslissing Type',
            decision: 'Beslissing',
            rationale: 'Motivering',
            decision_made: 'Beslissing Made',
            no_decisions: 'Nog geen beslissingen vastgelegd.',
            no_audit_events: 'Er zijn nog geen auditgebeurtenissen geregistreerd.',
            export_pdf: 'PDF exporteren',
            pdf_title: 'Incidentoverzicht',
            description: 'Beschrijving',
            linked_records: 'Gekoppelde Records',
            export_success: 'Incident report exported',
        },
        tasks: {
            page_title: 'Incidentresponstaken',
            title: 'Incidentresponstaken',
            new: 'Nieuwe taak',
            edit: 'Taak bewerken',
            task_type: 'Type taak',
            assignee: 'Toegewezene',
            due_date: 'Uiterste datum',
            dependencies: 'Afhankelijkheden',
            mark_complete: 'Markeren als voltooid',
            templates: 'Taaksjablonen',
            select_incident: 'Incident selecteren',
            all_statuses: 'Alle statussen',
            all_categories: 'Alle categorieën',
            new_response_task: 'Nieuwe responstaak',
            category: 'Categorie',
            priority: 'Prioriteit',
            depends_on: "Afhankelijk van (kommagescheiden taak-ID's)",
            checklist_items: 'Checklistitems',
            remove: 'Verwijderen',
            add: 'Toevoegen',
            new_checklist_item: 'Nieuw checklistitem',
            no_templates: 'Geen sjablonen beschikbaar.',
            assign_task: 'Taak toewijzen',
            assign_to: 'Toewijzen aan',
            assign: 'Toewijzen',
            select_incident_prompt: 'Selecteer een incident om de responstaken te bekijken.',
            checklist: 'Controlelijst',
            hide_checklist: 'Checklist verbergen',
            escalate: 'Escaleren',
            escalations: 'Escalaties',
            no_checklist_items: 'Geen checklistitems.',
        },
        reports: {
            page_title: 'Incidentrapporten',
            title: 'Incidentrapporten',
            summary: 'Samenvattingsstatistieken',
            by_severity: 'Op ernst',
            by_status: 'Op status',
            recent: 'Recente incidenten',
            total_incidents: 'Totaal aantal incidenten',
            open: 'Geopend',
            closed: 'Gesloten',
            export_csv: 'CSV exporteren',
            export_pdf: 'PDF exporteren',
            export_success: 'Rapport succesvol geëxporteerd.',
            export_error: 'Rapport exporteren mislukt.',
            reference: 'Referentie',
            view_report: 'Rapport bekijken',
        },
        statuses: {
            reported: 'Gemeld',
            triage: 'Beoordeling',
            contained: 'Ingeperkt',
            investigation: 'Onderzoek',
            remediation: 'Herstel',
            closed: 'Gesloten',
            false_positive: 'Vals positief',
        },
        task_statuses: {
            pending: 'In afwachting',
            in_progress: 'In uitvoering',
            completed: 'Voltooid',
            blocked: 'Geblokkeerd',
            cancelled: 'Geannuleerd',
        },
        severity: {
            low: 'Laag',
            medium: 'Gemiddeld',
            high: 'Hoog',
            critical: 'Kritiek',
        },
        task_types: {
            containment: 'Inperking',
            investigation: 'Onderzoek',
            remediation: 'Herstel',
            communication: 'Communicatie',
            documentation: 'Documentatie',
            notification: 'Melding',
        },
        categories: {
            ai_system: 'AI System',
            data_breach: 'Gegevens Inbreuk',
            unauthorized_access: 'Ongeautoriseerd Toegang',
            data_loss: 'Gegevens Loss',
            system_compromise: 'Systeem Compromise',
            malware: 'Schadelijke software',
            phishing: 'Phishing-aanval',
            insider_threat: 'Insider-bedreiging',
            other: 'Overig',
        },
        reported_via_options: {
            email: 'E-mail',
            phone: 'Telefoon',
            internal_system: 'Intern Systeem',
            third_party: 'Derde Party',
            automated_detection: 'Geautomatiseerd Detectie',
        },
        harm_likelihood_options: {
            unlikely: 'Onwaarschijnlijk',
            possible: 'Mogelijk',
            likely: 'Waarschijnlijk',
            very_likely: 'Zeer Waarschijnlijk',
        },
        breach_determination_options: {
            confirmed_breach: 'Bevestigd Inbreuk',
            suspected_breach: 'Verdacht Inbreuk',
            not_a_breach: 'Niet een Inbreuk',
            pending: 'In afwachting',
        },
        timeline_event_types: {
            detection: 'Detectie',
            escalation: 'Escalatie',
            containment: 'Inperking',
            investigation_start: 'Onderzoek Starten',
            evidence_collected: 'Bewijs Collected',
            notification_sent: 'Melding Sent',
            remediation_action: 'Herstel Actie',
            status_change: 'Status Wijzigen',
            communication: 'Communicatie',
            other: 'Overig',
        },
        decision_types: {
            containment: 'Inperking',
            notification: 'Melding',
            remediation: 'Herstel',
            escalation: 'Escalatie',
            closure: 'Afsluiting',
            other: 'Overig',
        },
        task_transitions: {
            start: 'Starten',
            complete: 'Voltooien',
            block: 'Blokkeren',
            cancel: 'Annuleren',
            unblock: 'Deblokkeren',
        },
        announce: {
            loaded: '{count} incidenten geladen',
            reports_loaded: '{count} incidentrapporten geladen',
            tasks_loaded: '{count} taken geladen',
            created: 'Incident succesvol aangemaakt',
            updated: 'Incident succesvol bijgewerkt',
            closed: 'Incident succesvol afgesloten',
            status_changed: 'Incidentstatus gewijzigd',
            triage_completed: 'Triage succesvol afgerond',
            impact_updated: 'Effectbeoordeling bijgewerkt',
            breach_updated: 'Beoordeling van inbreuk bijgewerkt',
            timeline_added: 'Tijdlijngebeurtenis toegevoegd',
            decision_recorded: 'Beslissing vastgelegd',
            notification_updated: 'Meldingsstatus bijgewerkt',
            task_created: 'Taak is succesvol aangemaakt',
            task_status_changed: 'Taakstatus gewijzigd',
            checklist_completed: 'Controlelijstitem voltooid',
            checklist_unchecked: 'Controlelijstitem uitgeschakeld',
            task_assigned: 'Taak toegewezen',
            task_escalated: 'Taak geëscaleerd',
            export_initiated: 'Export gestart',
        },
        breach_notifications: {
            page_title: 'Meldingen van inbreuken',
            title: 'Meldingen van inbreuken',
            new_notification: 'Nieuwe melding',
            no_notifications: 'Geen inbreukmeldingen gevonden',
            status: 'Status',
            submit_to_mps: 'Verzenden naar MPS',
            decision: 'Besluit',
            decision_maker: 'Beslisser',
            notify: 'Informeer',
            do_not_notify: 'Niet op de hoogte stellen',
            rationale: 'Reden',
            filing_deadline: 'Deadline indiening',
            overdue: 'Achterstallig',
            due_soon: 'Bijna verlopen',
            on_track: 'Op schema',
            legal_review: 'Juridische beoordeling',
            incident_id: 'Incident-ID',
            // Fix sprint #447 W1 — the decision moves the status; submission through /submit.
            decision_hint: 'Notify moves the notification to Decision to Notify, ready to draft. Do Not Notify closes it; the decision and its rationale are kept.',
            rationale_required: 'Enter the rationale for this decision.',
            mark_drafted: 'Mark notification drafted',
            submission_channel: 'Submission channel',
            submission_confirmation: 'Confirmation or reference',
            submission_note: 'This records the submission your organisation made. The platform files nothing with the authority.',
            select_channel: 'Select a channel',
            channels_load_failed: 'The submission channels could not be read.',
            channel_refusal_no_framework: 'No installed framework declares an authority filing, so no submission can be recorded.',
            channel_refusal_no_channels: 'The filing framework declares no submission channels, so no submission can be recorded.',
            channel_refusal_no_authority: 'The filing framework names no authority that accepts filings, so no submission can be recorded.',
            notification_statuses: {
                assessment: 'Beoordeling',
                decision_to_notify: 'Besluit tot kennisgeving',
                notification_drafted: 'Kennisgeving opgesteld',
                legal_review: 'Juridische beoordeling',
                submitted_to_authority: 'Ingediend bij autoriteit',
                supplement_requested: 'Supplement gevraagd',
                supplement_submitted: 'Aanvulling ingediend',
                acknowledged: 'Erkend',
                closed: 'Gesloten',
            },
            select_incident: 'Linked Incident',
            // Breach notifications page layout — summary band, details dialog, forms dialog.
            page_description: 'Decide whether each personal data breach has to be notified, record that decision with its rationale, and follow every filing deadline through to the submission your organisation makes to the authority.',
            kpi_total: 'Notifications',
            kpi_awaiting_decision: 'Awaiting decision',
            kpi_deadline_at_risk: 'Due soon or overdue',
            kpi_submitted: 'Submitted',
            filter_toolbar: 'Filter breach notifications',
            results_count: 'Showing {shown} of {total} notifications',
            incident: 'Incident',
            forms: 'Forms',
            forms_count_one: '1 form',
            forms_count: '{count} forms',
            no_forms: 'No forms',
            forms_dialog_title: 'Forms filed on {incident}',
            view_details: 'View details',
            details_title: 'Breach notification details',
            details_decision_section: 'Decision and submission',
            created_at: 'Created',
            submitted_at: 'Submitted on',
        },
        supplements: {
            page_title: 'Aanvullingsverzoeken',
            title: 'Aanvullingsverzoeken',
            new_supplement: 'Nieuw supplement',
            no_supplements: 'Geen aanvullingsverzoeken gevonden',
            request_details: 'Details aanvragen',
            response_package: 'Reactiepakket',
            submit_response: 'Reactie verzenden',
            deadline: 'Deadline',
            notification_id: 'Meldings-ID',
            computed_deadline: 'Computed deadline',
            computed_deadline_none: 'None computed',
            computed_deadline_pending: 'Not started yet',
            enter_notification_id: 'Voer een meldings-ID in om supplementen te bekijken.',
            supplement_statuses: {
                requested: 'Gevraagd',
                drafting: 'Opstellen',
                submitted: 'Ingezonden',
                accepted: 'Geaccepteerd',
                rejected: 'Afgewezen',
            },
        },
        remediation: {
            page_title: 'Herstelacties',
            title: 'Herstelacties',
            new_action: 'Nieuwe actie',
            no_actions: 'Geen herstelacties gevonden',
            description: 'Beschrijving',
            owner: 'Eigenaar',
            department: 'Afdeling',
            deadline: 'Deadline',
            statuses: {
                planned: 'Gepland',
                in_progress: 'In uitvoering',
                completed: 'Voltooid',
                verified: 'Geverifieerd',
            },
            verify: 'Verifieer',
            incident_id: 'Incident-ID',
            enter_incident_id: 'Voer een incident-ID in om herstelacties te bekijken.',
            start: 'Begin',
            complete_action: 'Voltooien',
        },
        authority_requests: {
            page_title: 'Autoriteitsverzoeken',
            title: 'Autoriteitsverzoeken',
            new_request: 'Nieuw verzoek',
            no_requests: 'Geen autoriteitsverzoeken gevonden',
            authority_name: 'Naam autoriteit',
            request_details: 'Details aanvragen',
            response_package: 'Reactiepakket',
            submission_channel: 'Inzendingskanaal',
            submit_response: 'Reactie verzenden',
            deadline: 'Deadline',
            confirmation: 'Bevestiging',
            incident_id_optional: 'Incident-ID (optioneel)',
            response_context_label: 'Antwoordcontext (voor materialisatie van indieningsdocument)',
            response_context_none: '(materialisatie overslaan)',
            response_context_supplement: 'Aanvullend antwoord',
            response_context_information: 'Antwoord op informatieverzoek',
            response_context_inspection: 'Antwoord op inspectie',
            materialisation_status_column: 'Indieningsdocument',
            materialisation_pending: 'In behandeling',
            materialisation_success: 'Gematerialiseerd',
            materialisation_failed: 'Mislukt',
            materialisation_retry_button: 'Opnieuw proberen',
            ar_statuses: {
                received: 'Ontvangen',
                verified: 'Geverifieerd',
                assembling: 'Montage',
                submitted: 'Ingediend',
                closed: 'Gesloten',
                response_packaged: 'Response Packaged',
                scope_assessed: 'Scope Assessed',
                legal_review: 'Juridische beoordeling',
            },
            channel_in_person: 'In Person',
            channel_mps_portal: 'MPS Portal',
            channel_postal: 'Postal',
            channel_email: 'Email',
            verification_checklist_title: 'Verification Checklist',
            checklist: 'Controlelijst',
            no_checklist_items: 'No checklist items yet. Add items below.',
            add_checklist_item: 'Add checklist item...',
            advance_to_verified: 'Advance to Verified',
            advance_to_scope_assessed: 'Advance to Scope Assessed',
            advance_to_assembling: 'Advance to Assembling',
            advance_to_response_packaged: 'Advance to Response Packaged',
            advance_to_legal_review: 'Advance to Legal Review',
            advance_to_submitted: 'Advance to Submitted',
            advance_to_closed: 'Advance to Closed',
        },
        disclosure_register: {
            page_title: 'Openbaarmakingsregister',
            title: 'Openbaarmakingsregister',
            new_entry: 'Nieuwe invoer',
            no_entries: 'Geen openbaarmakingsregistergegevens gevonden',
            disclosure_type: 'Openbaarmakingstype',
            disclosure_date: 'Datum van openbaarmaking',
            recipient: 'Ontvanger',
            authority_name: 'Naam autoriteit',
            channel: 'Kanaal',
            content_summary: 'Samenvatting van de inhoud',
            incident_id_optional: 'Incident-ID (optioneel)',
            channel_in_person: 'In Person',
            channel_mps_portal: 'MPS Portal',
            channel_postal: 'Postal',
            type_court_order: 'Court-Ordered Disclosure',
            type_data_breach: 'Data Breach Notification',
            type_regulatory_response: 'Regulatory Response',
            type_voluntary: 'Voluntary Disclosure',
            channel_email: 'Email',
        },
        log_disclosure: 'Log Disclosure',
    },
    onboarding: {
        nav: {
            wizard: 'Installatiewizard',
            tours: 'Modulerondleidingen',
            sample_data: 'Voorbeeldgegevens',
        },
        wizard: {
            title: 'Installatiewizard',
            welcome: 'Welkom bij AesirX ComplianceOne',
            welcome_message: 'Laten we uw organisatie instellen. Deze wizard begeleidt u door de initiële configuratie.',
            setup_complete: 'Installatie voltooid',
            skipped_message: 'De installatiewizard is overgeslagen. U kunt deze op elk moment uitvoeren om uw organisatie te configureren.',
            start_setup: 'Installatie starten',
            skip_wizard: 'Wizard overslaan',
            go_to_admin: 'Ga naar Beheerder',
            skip_for_now: 'Voorlopig overslaan',
            next_step: 'Volgende stap',
            previous_step: 'Vorige stap',
            finish_setup: 'Installatie voltooien',
            rerun: 'Installatiewizard opnieuw uitvoeren',
            step_label: 'Stap {number}',
            progress_label: 'Setup progress: Step {number} of {total}',
            validation_org_name_required: 'Organisatienaam is verplicht.',
            validation_admin_email_required: 'E-mail van de beheerder is verplicht.',
            validation_admin_email_invalid: 'Voer een geldig e-mailadres in.',
            validation_cmp_endpoint_required: 'CMP-eindpunt is verplicht wanneer CMP is ingeschakeld.',
            steps: {
                license_activation: 'Licentie activatie',
                org_profile: 'Organisatieprofiel',
                module_selection: 'Moduleselectie',
                cmp_integration: 'CMP-integratie',
                notification_preferences: 'Meldingen',
                review: 'Beoordelen & bevestigen',
            },
            step_descriptions: {
                license_activation: 'Voer uw licentiesleutel in om uw pakket te activeren en te bepalen welke modules beschikbaar zijn.',
                org_profile: 'Stel de organisatienaam, jurisdictie, sector, omvang en voorkeurstaal in.',
                module_selection: 'Kies welke compliancemodules geactiveerd moeten worden voor uw organisatie.',
                cmp_integration: 'Configureer de integratie met het Consent Management Platform (optioneel).',
                notification_preferences: 'Configureer hoe u meldingen ontvangt voor compliance-gebeurtenissen.',
                review: 'Controleer uw configuratie en rond de installatie af.',
            },
            license_step: {
                validate_button: 'Valideer licentie',
                skip_warning: 'Moduletoegang is beperkt tot de standaard pakketlaag. U kunt een licentie later activeren in Platform > Licentie-instellingen.',
            },
            module_descriptions: {
                rights_requests: 'Verwerk verzoeken om toegang tot en verwijdering van persoonsgegevens.',
                data_mapping: 'Breng systemen, gegevensstromen en verwerkingsactiviteiten in kaart.',
                governance: 'Houd toezicht op leveranciers, goedkeuringen, drift en de bewijsregisters.',
                consent: 'Beheer toestemmingsbeleid en doelcatalogi.',
                consent_properties: 'Beheer toestemming over meerdere digitale eigendommen.',
                assessments: 'Voer impactbeoordelingen en grensoverschrijdende gegevensoverdrachten uit.',
                classification: 'Classificeer en label gegevensactiva met levenscyclustracking.',
                monitoring: 'Geplande compliancebeoordelingen en KPI-tracking.',
                incidents: 'Meld en beheer beveiligings- en privacyincidenten.',
                connectors: 'Maak verbinding met externe systemen voor geautomatiseerde gegevensuitwisseling.',
                vendor_risk: 'Scoor leveranciers, volg contracten en beheer due diligence.',
                deletion_orchestration: 'Coördineer gegevensverwijdering over systemen en leveranciers.',
                discovery: 'Ontdek en classificeer gegevens in uw hele infrastructuur.',
                incident_ops: 'Geavanceerde incidentdraaiboeken, SLA-tracking en escalaties.',
                program_governance: 'Volwassenheidsbeoordelingen, managementrapportage en auditgereedheid.',
                access_accountability: 'Monitor gebruikerstoegang, sessies en rechtvaardigingsworkflows.',
                localization: 'Marktspecifieke compliancepakketten en regelgevingskaders.',
                sector_overlays: 'Install sector overlays and open the registers an activated overlay brings into use, by sector.',
                partner_workspace: 'Partner- en wederverkoperwerkruimte.',
                privacy_scanner: 'Realtime privacymonitoring van digitale eigendommen.',
                onboarding: 'Installatiewizard, rondleidingen en beheer van voorbeeldgegevens.',
                compliance_forms: 'Interactieve compliancesjablonen en documentgeneratie.',
                audit_trail: 'Uitgebreide onveranderlijke auditlogboekregistratie en bewaring.',
                organizations: 'Multi-organisatie- en groepsbeheer.',
                task_management: 'Geïntegreerde taakplanning, workflowengine en agendaweergaven.',
            },
            module_categories: {
                data_privacy_rights: 'Gegevensprivacy en rechten',
                vendor_third_party: 'Beheer van leveranciers en derden',
                risk_assessment: 'Risico en beoordeling',
                operations_automation: 'Operaties & Automatisering',
                platform_admin: 'Platform & Administratie',
            },
            package_tier_label: 'Uw Pakket',
            package_module_unavailable: 'Niet inbegrepen in uw pakket',
            select_all: 'Alles Selecteren',
            deselect_all: 'Alles Deselecteren',
            load_sample_data: 'Voorbeeldgegevens laden na installatie',
            load_sample_data_description: 'Installeer realistische demogegevens om het platform te verkennen. Kan later worden verwijderd via Onboarding > Voorbeeldgegevens.',
            language_label: 'Voorkeurstaal',
            language_description: 'Kies de standaardtaal voor uw organisatie.',
            admin_account_title: 'Beheerdersaccount',
            admin_account_created: 'Beheerdersaccount aangemaakt',
            admin_full_name: 'Volledige naam',
            admin_email: 'E-mailadres',
            admin_password: 'Wachtwoord',
            admin_confirm_password: 'Wachtwoord bevestigen',
            admin_show_password: 'Wachtwoord tonen',
            admin_hide_password: 'Wachtwoord verbergen',
            admin_name_placeholder: 'bijv. Jane Smit',
            admin_email_placeholder: 'admin@example.com',
            admin_password_placeholder: 'Minimaal 8 tekens',
            admin_password_confirm_placeholder: 'Wachtwoord opnieuw invoeren',
            validation_admin_name_required: 'Beheerdersnaam is verplicht',
            validation_admin_password_required: 'Wachtwoord is verplicht',
            validation_admin_password_min_length: 'Wachtwoord moet minimaal 8 tekens bevatten',
            validation_passwords_mismatch: 'Wachtwoorden komen niet overeen',
            notification_email_enabled: 'E-mailmeldingen Inschakelen',
            notification_email_description: 'Ontvang e-mailmeldingen over taaktoewijzingen, SLA-schendingen en compliance-gebeurtenissen.',
            notification_digest_frequency: 'Samenvattingsfrequentie',
            notification_digest_daily: 'Dagelijks',
            notification_digest_weekly: 'Wekelijks',
            notification_digest_none: 'Geen samenvatting',
            notification_in_app_enabled: 'Platformmeldingen Inschakelen',
            notification_in_app_description: 'Toon meldingsbadge en -lade voor realtimewaarschuwingen.',
            jurisdictions: {
                vietnam: 'Vietnam',
                asean: 'ASEAN',
                eu_eea: 'EU/EER',
                global: 'Wereldwijd',
            },
            languages: {
                en: 'Engels',
                vi: 'Tiếng Việt',
                da: 'Dansk',
                sv: 'Zweeds',
                nb: 'Norsk Bokmal',
                nn: 'Norsk Nynorsk',
                de: 'Duits',
                fi: 'Suomi',
                nl: 'Nederlands',
                fr: 'Frans',
                lb: 'Lëtzebuergesch',
                ga: 'Gaeilge',
                es: 'Spaans',
                ca: 'Català',
                eu: 'Euskara',
                pt: 'Portugees',
            },
            industries: {
                financial_services: 'Financiële dienstverlening',
                healthcare: 'Gezondheidszorg',
                technology: 'Technologie',
                telecommunications: 'Telecommunicatie',
                government: 'Overheid',
                education: 'Onderwijs',
                manufacturing: 'Productie',
                retail: 'Detailhandel',
                other: 'Overig',
            },
            placeholders: {
                org_name: 'bijvoorbeeld ABC',
                admin_email: 'admin@company.vn',
                team_emails: 'gebruiker1@bedrijf.vn, gebruiker2@bedrijf.vn',
                cmp_endpoint: 'https://cmp.example.com/api',
            },
            fields: {
                org_name: 'Organisatienaam',
                jurisdiction: 'Jurisdictie',
                industry: 'Branche',
                org_size: 'Organisatiegrootte',
                language: 'Voorkeurstaal',
                admin_email: 'E-mail beheerder',
                team_emails: 'E-mailadressen teamleden (kommagescheiden)',
                enable_cmp: 'CMP-integratie inschakelen',
                cmp_endpoint: 'CMP API-eindpunt',
                email_notifications: 'E-mailmeldingen',
                in_app_notifications: 'Platformmeldingen',
                digest_frequency: 'Samenvattingsfrequentie',
            },
            validation_jurisdiction_required: 'Jurisdiction is required.',
        },
        tours: {
            title: 'Modulerondleidingen',
            start: 'Rondleiding starten',
            continue_tour: 'Rondleiding voortzetten',
            replay: 'Rondleiding herhalen',
            exit: 'Rondleiding verlaten',
            skip: 'Rondleiding overslaan',
            next: 'Volgende',
            previous: 'Vorige',
            finish: 'Rondleiding voltooien',
            step_of: 'Stap {current} van {total}',
            info: 'Volg begeleide rondleidingen door elke module om de belangrijkste functies en workflows te leren kennen. Rondleidingen kunnen op elk moment worden herhaald.',
            autoplay: 'Automatisch afspelen',
            interactive: 'Interactief',
            play_all: 'Speel alles',
            search_tours: 'Rondleidingen zoeken...',
            duration: 'Duur',
            steps_label: '{count} stappen',
            mode: 'Modus',
            pause: 'Pauze',
            resume: 'Hervatten',
            speed: 'Snelheid',
            restart: 'Opnieuw opstarten',
            reset: 'Opnieuw instellen',
            reset_success: 'De voortgang van de tour is gereset.',
            category_core_privacy: 'Kernprivacy',
            category_governance: 'Bestuur',
            category_operations: 'Operaties',
            category_platform: 'Platform',
            category_advanced: 'Geavanceerd',
            category_data_privacy_rights: 'Gegevensprivacy en rechten',
            category_vendor_third_party: 'Beheer van leveranciers en derden',
            category_risk_assessment: 'Risico en beoordeling',
            category_operations_automation: 'Operaties & Automatisering',
            category_platform_admin: 'Platform & Administratie',
            steps_count: '{count} stappen',
            autoplay_mode: 'Auto-play mode',
            interactive_mode: 'Interactive mode',
            mode_switch_name: '{current}: switch to {other}',
            revision_conflict: 'This tour has changed since you started it. Restart the tour to see the current version.',
        },
        sample_data: {
            evaluation_title: 'Forseti Evaluation — 100 Vietnam Cases',
            evaluation_info: 'Install synthetic linked assessment records for repeatable evaluation. Loading makes no AI calls. Ordinary sample data is unchanged. Run evaluations separately; legal review is still required.',
            evaluation_loaded: '{count} of {total} cases installed',
            evaluation_load: 'Install evaluation cases',
            evaluation_clear: 'Reset evaluation cases',
            evaluation_confirm_load: 'Install all 100 synthetic Vietnam cases without replacing existing evaluation work? Installation does not run AI.',
            evaluation_confirm_clear: 'Remove only this evaluation dataset’s activity and assessment records? AI runs and audit history remain.',
            title: 'Voorbeeldgegevensbeheer',
            select_modules: 'Modules selecteren',
            current_status: 'Huidige status',
            operation_history: 'Bewerkingsgeschiedenis',
            load: 'Voorbeeldgegevens laden',
            clear: 'Voorbeeldgegevens wissen',
            processing: 'Verwerken...',
            loaded: 'Geladen',
            not_loaded: 'Niet geladen',
            records: 'Voorbeeldrecords',
            confirm_clear_all: 'Clear the sample data of every module? Only cross-industry sample records are removed; real records are not affected. Industry packages are kept: remove a package from its own card.',
            confirm_clear: 'Weet u zeker dat u alle voorbeeldgegevens voor de geselecteerde modules wilt wissen?',
            confirm_clear_title: 'Voorbeeldgegevens wissen',
            confirm_load_title: 'Voorbeeldgegevens laden',
            confirm_load: 'Weet u zeker dat u voorbeeldgegevens wilt laden voor de geselecteerde modules?',
            action_load: 'Laden',
            action_clear: 'Wissen',
            info: 'Laad realistische demogegevens voor training en demonstraties. Voorbeeldgegevens zijn getagd en kunnen veilig worden verwijderd zonder echte gegevens te beïnvloeden.',
            clear_success: 'Sample data cleared successfully',
            load_success: 'Sample data loaded successfully',
            action_install: 'Install package',
            action_remove: 'Remove package',
            // Sprint 63.37.0 (I2) — the forms module's standing counts in the cross-industry status table.
            status_forms_counts: '{official} official, {internal} internal',
            cross_industry_title: 'Cross-industry sample data',
            cross_industry_info: 'Fictional records for every module, shared by all industries. Choose the modules to load or clear below.',
            packages: {
                title: 'Industry sample data',
                info: 'Each package is one connected, fictional scenario named for its industry, never for a customer. Installing writes sample records in this organisation only, and sends no notification, e-mail, webhook, connector run or AI call.',
                list_aria: 'Industry sample-data packages',
                list_failed: 'The industry packages could not be read.',
                list_retry_subject: 'the industry package list',
                list_forbidden: 'Reading industry packages needs the {permission} permission.',
                empty: 'No industry package is available on this platform.',
                synthetic_badge: 'Synthetic data',
                industry: 'Industry',
                line_of_business: 'Line of business',
                version: 'Version',
                reference_prefix: 'Reference prefix',
                contents_heading: 'Contents',
                contents_item: '{area}: {count} records',
                branches_heading: 'Optional parts',
                branch_default: 'chosen by default',
                branches_none: 'None',
                prerequisites_heading: 'Prerequisites',
                prerequisite_frameworks: 'Frameworks the package reads',
                prerequisite_modules: 'Modules the package writes',
                prerequisite_present: 'Installed',
                prerequisite_missing: 'Not installed',
                prerequisite_module_available: 'Included in your plan',
                prerequisite_module_unavailable: 'Not included in your plan',
                prerequisite_note: 'Records that need a missing framework or module are left out at install and listed with the reason.',
                status_heading: 'Status',
                status_not_installed: 'Not installed',
                status_installed: 'Installed',
                installed_language: 'Content language',
                installed_branches: 'Optional parts',
                installed_at: 'Installed on',
                installed_by: 'Installed by',
                installed_records: 'Records written',
                evidence_pack: 'Audit evidence pack',
                evidence_pack_link: 'Open audit evidence packs (pack {id})',
                evidence_pack_none: 'None assembled',
                attachments: 'Stored synthetic files',
                upgrade_available: 'Version {version} is available. Reinstall to replace the installed version {installed}.',
                install: 'Install',
                reinstall: 'Reinstall',
                remove: 'Remove',
                installing: 'Installing…',
                removing: 'Removing…',
                load_needs: 'Installing needs the {permission} permission.',
                clear_needs: 'Removing needs the {permission} permission.',
                dialog_install_title: 'Install {name}',
                dialog_reinstall_title: 'Reinstall {name}',
                language_legend: 'Content language',
                language_en: 'English',
                language_vi: 'Vietnamese',
                language_required: 'Choose the content language.',
                branches_legend: 'Optional parts',
                branches_none_declared: 'This package has no optional parts.',
                will_write: 'Writes up to {count} sample records in these modules:',
                will_drop: 'A record whose framework is not installed, or whose module your plan does not include, is left out and listed with the reason.',
                nothing_sent: 'No notification, e-mail, webhook, connector run or AI call is sent. When the Immutable Audit Trail is on, the audit entries for the stored files and the evidence pack are anchored like any other audit entry.',
                no_accounts: 'No sign-in accounts are created.',
                reinstall_note: "With the same language and parts, nothing changes. With different ones, the package's records are removed and written again.",
                remove_confirm_title: 'Remove {name}',
                remove_confirm: "Remove {name}? This deletes the {count} records the package wrote in its modules and its {files} stored synthetic files. Its audit evidence pack is deleted too, unless a person reviewed it or a framework evidence pack collected it: then it is kept as your organisation's own record. Audit entries recording the install and the removal stay. Cross-industry sample data and your real records stay.",
                install_failed: 'The package could not be installed.',
                remove_failed: 'The package could not be removed.',
                result_heading: 'Result of the last action',
                result_installed: '{name} installed.',
                result_reinstalled: '{name} reinstalled.',
                result_unchanged: 'Nothing changed: {name} is already installed at this version, in this language, with these parts.',
                result_removed: '{name} removed.',
                result_written: 'Records written: {count}',
                result_removed_first: 'Records removed before writing: {count}',
                result_attachments: 'Synthetic files stored: {count}',
                result_dropped_heading: 'Records left out ({count})',
                result_dropped_none: 'No record was left out.',
                drop_framework_not_installed: 'a framework it needs is not installed ({detail})',
                drop_requirement_not_installed: 'the requirement it maps to is not in the installed framework ({detail})',
                drop_module_not_available: 'your plan does not include this module ({detail})',
                drop_reference_dropped: 'it refers to a record that was left out ({detail})',
                dropped_item: '{record} ({area}): {reason}',
                // Sprint 63.37.0 (I2) — the Forms sub-section of a package card and the install result.
                forms_heading: 'Forms',
                forms_counts: '{official} official, {internal} internal, {not_installed} not installed on this organisation',
                forms_list_aria: 'Forms of {name}',
                form_official: 'Official',
                form_internal: 'Internal — no official form declares this',
                form_not_installed: 'Not installed on this organisation; it needs {framework}',
                form_not_installed_unnamed: 'Not installed on this organisation',
                form_restricted: 'Not available on this organisation: the form it needs is restricted',
                form_not_fileable: 'Not available on this organisation: the installed form cannot be filed',
                forms_failed: 'The forms of this package could not be read.',
                forms_retry_subject: 'the package forms',
                result_forms_written: 'Forms written ({count})',
                result_forms_dropped: 'Forms not written ({count})',
                result_form_written_item: '{record}: {form} ({standing})',
                drop_form_not_installed: 'no installed form answers it ({detail})',
                drop_form_restricted: 'the form it needs is restricted on this organisation ({detail})',
                drop_form_not_fileable: 'the installed form cannot be filed ({detail})',
                evidence_pack_assembled: 'Audit evidence pack assembled, not verified: {id}.',
                evidence_pack_failed: 'The audit evidence pack could not be assembled: {error}. The records stand.',
                evidence_pack_not_assembled: 'No audit evidence pack was assembled.',
                guide_heading: 'Scenario guide',
                guide_intro: 'The demonstration path, in order. Each step opens the record or its list.',
                guide_language: 'Guide language: {language}',
                guide_no_accounts: 'Sample data includes no sign-in accounts. Present with your own account; a record that names a person by account names the user who installed the package.',
                guide_reviewers: "Pending approvals and the impact assessment are decided by your organisation's own reviewers, not during the demonstration.",
                guide_point: 'Point',
                guide_do_not_claim: 'Do not claim',
                guide_step_unavailable: "This step's record was not installed.",
                guide_failed: 'The scenario guide could not be read.',
                guide_retry_subject: 'the scenario guide',
                result_removed_records: 'Records deleted from the modules: {count}',
                result_removed_pack: 'Audit evidence pack removed ({count} stored rows)',
                kept_heading: 'Audit evidence pack versions kept ({count})',
                kept_reviewed: "Pack {pack_id}, version {version}: kept because it was reviewed. It is now your organisation's own record. The events it lists were sample rows and went with the package, so Check for changes since assembly reports them as gone, or as changed where a reinstall wrote them again.",
                kept_collected: "Pack {pack_id}, version {version}: kept because a framework evidence pack collected it. It is now your organisation's own record. The events it lists were sample rows and went with the package, so Check for changes since assembly reports them as gone, or as changed where a reinstall wrote them again.",
                reinstall_failed_removed: 'The reinstall failed after the previous install was removed: {count} records were deleted and nothing was written. The package is no longer installed.',
                kept_outputs_heading: 'Exports of sample forms kept ({count})',
                kept_output_item: "Export {output_id} (file {file_id}): kept because {cited_by} names its file. It is now your organisation's own file. The sample form it was printed from went with the package.",
                kept_output_by_compliance_execution_proof: 'compliance proof {id}',
                kept_output_by_cross_border_filing_evidence: 'cross-border filing {id}',
                kept_output_by_handbook_assessment_evidence: 'handbook assessment {id}',
                kept_output_by_ai_obligation_evidence: 'AI obligation record {id}',
                kept_output_by_evidence_pack_dispatch_proof: 'evidence pack dispatch {id}',
                kept_output_by_legal_hold: 'legal hold {id}',
                kept_output_by_other: 'record {id}',
                kept_output_by_legal_hold_policy: 'the legal hold set on {id} files',
                kept_held_files_heading: 'Sample files kept, not deleted ({count})',
                kept_held_file_item: "File {file_id}: kept, not deleted, because {cited_by} names it. It stays as your organisation's file, and can be deleted in Files once nothing names it.",
            },
            // Sprint 63.37.0 (rehearsal J2) — the forms row when the load left forms out because their framework is not installed.
            status_forms_counts_not_installed: '{official} official, {internal} internal, {not_installed} not installed on this organisation',
        },
        help: {
            title: 'Helpcentrum',
            search: 'Zoeken',
            search_placeholder: 'Zoek in helpartikelen...',
            clear: 'Wissen',
            all_categories: 'Alle categorieën',
            all_modules: 'Alle Modules',
            back: 'Terug naar helpcentrum',
            callout_important: 'Belangrijk',
            callout_tip: 'Tip',
            no_results: 'Geen artikelen gevonden die overeenkomen met uw zoekopdracht.',
            no_articles: 'Nog geen helpartikelen beschikbaar. Artikelen verschijnen hier zodra inhoud wordt toegevoegd.',
            results_for: '{count} resultaat/resultaten voor "{query}"',
            quick_links: {
                getting_started: 'Aan de slag',
                getting_started_desc: 'Installatie, configuratie en snelstartgids.',
                user_guide: 'Gebruikershandleiding',
                user_guide_desc: 'Module-voor-module gebruiksdocumentatie.',
                admin_guide: 'Beheerdershandleiding',
                admin_guide_desc: 'Configuratie, onderhoud en probleemoplossing.',
                faq: 'Veelgestelde vragen',
                faq_desc: 'Veelgestelde vragen en antwoorden.',
            },
            search_articles_aria: 'Zoek hulpartikelen',
            article_metadata_aria: 'Artikelmetadata',
            faq_editor_title: 'FAQ-editor van het helpcentrum',
            faq_editor_description: 'Voeg vraag-en-antwoordparen toe aan een helpcentrumartikel, bewerk ze of verwijder ze. De editor is nog niet beschikbaar; dit adres is ervoor gereserveerd.',
            faq_editor_empty: 'De FAQ-editor is nog niet beschikbaar. Vraag tot die tijd uw beheerder om FAQ-secties voor u aan een helpcentrumartikel toe te voegen.',
            articles_list_aria: 'Hulpartikelen',
            category_nav_aria: 'Categorienavigatie',
            quick_links_aria: 'Snellinks naar helpcategorieën',
            returned_to_list: 'Terug naar artikellijst',
            opening_article: 'Artikel openen: {title}',
            read_article_aria: 'Artikel lezen: {title}',
            pagination_nav_aria: 'Paginering van artikelen',
            error_unavailable: 'Het helpcentrum is momenteel niet beschikbaar. Probeer het later opnieuw.',
            retry: 'Opnieuw proberen',
            list_truncated: 'Only {shown} of {total} help articles are listed. Use search to find the rest.',
        },
        // Sprint 63.11.0 (#34) — the persona and use-case detail pages. Every
        // value these pages show ABOUT a persona or a use case is read from the
        // shared registries; these strings are only the frame around it.
        // English seed for structural parity (R-57): translation is
        // docs/product/phases/phase-25/sprint-58.7.0.md.
        persona_detail: {
            page_title: 'Persona',
            text_loading: 'Loading the role text…',
            text_failed: 'The role text could not be loaded.',
            not_found_title: 'Persona not found',
            not_found_body: 'No persona in the reference set has the identifier "{slug}". Choose one from the persona paths instead.',
            back_to_list: 'Back to persona paths',
            open_path: 'Open onboarding path',
            profile_heading: 'Profile',
            field_persona_id: 'Persona ID',
            field_market: 'Market',
            field_quick_start: 'Suggested first task',
            modules_heading: 'Primary modules',
            modules_description: 'The modules this persona works in most, limited to those enabled for your organisation.',
            modules_checking: 'Checking which of these modules are enabled for your organisation…',
            modules_unchecked: 'We could not check which of these modules are enabled for your organisation, so all of them are listed.',
            modules_not_permitted: 'Your role cannot see which modules are enabled for your organisation, so all of them are listed.',
            modules_not_enabled: 'Not enabled for your organisation: {names}',
            modules_none_enabled: "None of this persona's primary modules is enabled for your organisation.",
            use_cases_heading: 'Use cases',
            use_cases_description: 'Validated use cases that name this persona as their primary persona.',
            use_cases_none: 'No validated use case names this persona as its primary persona.',
            availability_heading: 'Availability',
            availability_checking: 'Checking whether this persona is available to your organisation…',
            availability_available: 'Available to your organisation.',
            availability_ungated: 'Available to every organisation — this persona is not tied to one market.',
            availability_unavailable: 'This persona belongs to a market your organisation has not installed ({market}). Its onboarding path, quick start and recommendations are not offered to your organisation until a market pack for that market is installed.',
            availability_failed: 'We could not check whether this persona is available to your organisation, so this page does not say either way.',
            retry: 'Try again',
            retrying: 'Checking…',
            market_vn: 'Vietnam',
            market_eu: 'European Union / EEA',
            market_uk: 'United Kingdom',
            market_gl: 'Global — not tied to one market',
        },
        use_case_detail: {
            page_title: 'Use case {id}',
            description: 'A validated use case from the reference set and the persona it is written for. The full narrative is in the reference document named below; this page does not restate it.',
            not_found_title: 'Use case not found',
            not_found_body: 'No use case in the reference set has the identifier "{id}". Open a persona to see the use cases written for it.',
            back_to_list: 'Back to persona paths',
            open_path: 'Open the onboarding path for its primary persona',
            details_heading: 'Details',
            field_use_case_id: 'Use case ID',
            field_primary_persona: 'Primary persona',
            field_persona_id: 'Persona ID',
            field_market: 'Market',
            availability_heading: 'Availability',
            availability_checking: 'Checking whether this use case is available to your organisation…',
            availability_available: 'Available to your organisation.',
            availability_ungated: 'Available to every organisation — its primary persona is not tied to one market.',
            availability_unavailable: 'This use case belongs to a market your organisation has not installed ({market}). Its primary persona and onboarding path are not offered to your organisation until a market pack for that market is installed.',
            availability_failed: 'We could not check whether this use case is available to your organisation, so this page does not say either way.',
            retry: 'Try again',
            retrying: 'Checking…',
        },
    },
    common: {
        vendor: 'Vendor',
        // R-57 — structural parity with English values; real translations are EN and VI.
        list_truncation: {
            loading: 'Loading the list…',
            failed: 'The list could not be loaded, so nothing can be chosen from it right now.',
            partial_with_total: 'Showing {shown} of {total}. Narrow the search to reach the rest.',
            partial_without_total: 'Showing the first {shown}. Narrow the search to reach the rest — how many more there are is not known.',
        },
        // fix-sprint-20261013-0200 R1 — the remedy a failed directory read offers, said once for every
        // DirectoryNotice and UserSelect (components/register/DirectoryNotice.tsx `directoryRetry`).
        directory_retry: 'Reload',
        directory_retrying: 'Reloading…',
        directory_retry_subject: 'the user directory',
        // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
        // no locale declared, so each fell through to its hardcoded English third
        // argument and rendered English in all 16 locales. The two DPIA row-action
        // tooltips were worse: their fallbacks are themselves translated keys, so the
        // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
        // action read 'Approve/Reject' where it intends 'Review'.
        change: 'Change',
        current: 'current',
        next_page: 'Next page',
        selected: 'selected',
        // Fix sprint F30 — the shared "for example" wrapper. Three pages built
        // `e.g. ${code}` as a template literal, so the words rendered in English in
        // all 16 locales; the abbreviation differs per language (see
        // `escalation_contact_placeholder`, which already had it right).
        example_value: 'bijv. {value}',
        /* R-57 — structural parity; real translation deferred to the locale programme. */
        export_incomplete_heading: 'This export is incomplete',
        export_incomplete_body: '{count} value(s) in {fieldCount} field(s) ({fields}) were withheld because you are not authorized to view them. The file marks each one.',
        export_not_scanned: 'This export was produced from pre-formatted content, so its completeness was not checked.',
        save: 'Opslaan',
        cancel: 'Annuleren',
        system_actor: 'Systeem',
        delete: 'Verwijderen',
        edit: 'Bewerken',
        create: 'Aanmaken',
        update: 'Bijwerken',
        loading: 'Laden...',
        error: 'Er is een fout opgetreden',
        success: 'Gelukt',
        logout: 'Uitloggen',
        actions: 'Acties',
        retrying: 'Opnieuw proberen…',
        permission_denied: 'U heeft geen toegang tot deze pagina.',
        status: 'Toestand',
        description: 'Beschrijving',
        name: 'Naam',
        created_at: 'Aangemaakt op',
        updated_at: 'Bijgewerkt op',
        yes: 'Ja',
        no: 'Nee',
        confirm: 'Bevestigen',
        confirm_dialog_title: 'Confirm action',
        back: 'Terug',
        next: 'Volgende',
        previous: 'Vorige',
        search: 'Zoeken',
        filter: 'Filteren',
        page_x_of_y: 'Pagina {page} van {total}',
        pagination_label: 'Paginering',
        view_mode_label: 'Weergavemodus',
        export: 'Exporteren',
        exporting: 'Bezig met exporteren…',
        sidebar_navigation: 'Beheer-navigatiebalk',
        items_count: '{count} artikelen',
        items_count_one: '{count} artikel',
        export_success: 'Document succesvol gegenereerd.',
        generate_pdf: 'PDF genereren',
        import: 'Importeren',
        download: 'Downloaden',
        upload: 'Uploaden',
        view: 'Bekijken',
        close: 'Sluiten',
        close_notification: 'Close notification',
        discard_prompt: {
            title: 'Discard unsaved changes?',
            message: 'This dialog has unsaved changes. Closing it now discards them.',
            confirm: 'Discard',
            cancel: 'Keep editing',
        },
        submit: 'Indienen',
        saved: 'Opgeslagen',
        saving: 'Opslaan...',
        submitting: 'Verzenden...',
        created: 'Aangemaakt',
        all: 'Alle',
        none: 'Geen',
        select: 'Selecteren',
        select_user: 'Gebruiker selecteren...',
        custom_input: 'Handmatig invoeren',
        required: 'Verplicht',
        optional: 'Optioneel',
        notes: 'Notities',
        tags: 'Labels',
        archive: 'Archiveren',
        clone: 'Klonen',
        refresh: 'Vernieuwen',
        retry: 'Opnieuw proberen',
        // fix-sprint-20260810-2128 residue — English placeholder for structural
        // parity; locale work beyond EN/VI defers to Phase 25 (R-57, sprint-57.7.0).
        options_unreadable: 'The choices for this field could not be read. That does not mean there are none.',
        options_unreadable_pointer: 'Could not be read — see the message below.',
        reset: 'Herstellen',
        add: 'Toevoegen',
        remove: 'Verwijderen',
        removing: 'Verwijderen…',
        attachment: 'Bijlage',
        upload_file: 'Bestand uploaden',
        upload_file_required: 'Bestand uploaden (verplicht)',
        // fix-sprint-20260808-0511-followup-2 F1 — FileUpload.tsx drop-zone,
        // progress and validation-toast strings (were hardcoded EN in a shared
        // component reached from every file_upload form field).
        upload_drag_or_browse: 'Sleep en zet neer of klik om te bladeren',
        upload_max_size: 'Max. grootte: {size} MB',
        upload_accepted_types: 'Geaccepteerd: {types}',
        upload_in_progress: '{name} wordt geüpload...',
        upload_size_exceeded: 'De bestandsgrootte overschrijdt de maximaal toegestane grootte van {size} MB.',
        upload_type_not_accepted: 'Bestandstype niet geaccepteerd. Geaccepteerde typen: {types}',
        upload_succeeded: 'Bestand "{name}" is geüpload.',
        upload_failed: 'Uploaden van bestand mislukt.',
        select_entity_type_first: 'Selecteer eerst een entiteitstype',
        failed_to_remove_file: 'Bestand kon niet worden verwijderd.',
        entity_type_label: 'Entiteitstype',
        entity_label: 'Entiteit',
        entity_reference: 'Entiteitsreferentie',
        failed_to_load_entities: 'Entiteiten konden niet worden geladen',
        enable: 'Inschakelen',
        disable: 'Uitschakelen',
        no_data: 'Geen gegevens gevonden',
        confirm_delete: 'Weet u zeker dat u dit item wilt verwijderen?',
        confirm_action: 'Weet u zeker dat u wilt doorgaan?',
        save_success: 'Succesvol opgeslagen',
        save_error: 'Opslaan mislukt',
        delete_success: 'Succesvol verwijderd',
        delete_error: 'Verwijderen mislukt',
        hour: 'uur',
        hours: 'uren',
        load_error: 'Laden van gegevens mislukt',
        operation_error: 'Bewerking mislukt',
        something_went_wrong: 'Er is iets misgegaan',
        go_home: 'Naar overzicht',
        access_denied: 'Toegang geweigerd',
        no_permission: 'U heeft geen toestemming om deze pagina te openen.',
        no_permission_title: 'Onvoldoende rechten',
        no_permission_message: 'U heeft geen toestemming om deze pagina te openen.',
        unknown: 'Onbekend',
        back_to_dashboard: 'Terug naar overzicht',
        id: 'ID',
        type: 'Soort',
        date: 'Datum',
        created_by: 'Aangemaakt door',
        updated_by: 'Bijgewerkt door',
        page_of: 'Pagina {page} van {total}',
        showing_items: '{count} items weergegeven',
        items_per_page: 'Items per pagina',
        no_results: 'Geen records gevonden die aan uw filters voldoen',
        no_items: 'Geen items gevonden',
        sort_by: 'Sorteren op',
        ascending: 'Oplopend',
        descending: 'Aflopend',
        all_statuses: 'Alle statussen',
        all_types: 'Alle typen',
        all_categories: 'Alle categorieën',
        all_severities: 'Alle ernstniveaus',
        date_range: 'Datumbereik',
        from_date: 'Van',
        to_date: 'Tot',
        clear_filters: 'Filters wissen',
        apply_filters: 'Filters toepassen',
        save_changes: 'Wijzigingen opslaan',
        discard_changes: 'Wijzigingen verwerpen',
        confirm_discard: 'U heeft niet-opgeslagen wijzigingen. Wilt u deze verwerpen?',
        copy: 'Kopiëren',
        duplicate: 'Dupliceren',
        move: 'Verplaatsen',
        assign: 'Toewijzen',
        unassign: 'Toewijzing opheffen',
        approve: 'Goedkeuren',
        reject: 'Afwijzen',
        deleting: 'Verwijderen...',
        processing: 'Verwerken...',
        loading_page: 'Pagina laden...',
        operation_success: 'Bewerking succesvol voltooid',
        operation_failed: 'Bewerking mislukt. Probeer het opnieuw.',
        /*
         * Sprint 64.x-batch-17 — moved out of two inline `{ en, vi }` maps.
         *
         * `CHECKED_DOWNLOAD_FAILED_COPY` lived in `useCheckedDownload.ts` and is
         * the fallback toast for EVERY one of the 26 migrated download surfaces;
         * `RETRIEVAL_DENIED_COPY` lived in `EvidencePackDetailPage.tsx`. I18N.md
         * names `{ en, vi }` lookup objects as the documented root cause of "14
         * languages showing only English" and bans them outright, and neither
         * string had a key in any of the 16 bundles — so both were invisible to
         * the Phase 25 translation batches and could never reach parity.
         *
         * The hook's docblock justified the inline map with "a key earns its place
         * when more than one surface needs the same string", a rule that is not in
         * I18N.md; the string was in fact read by every migrated call site.
         */
        download_failed: 'Download failed.',
        download_forbidden: 'The file could not be downloaded: downloading files needs a permission your role does not hold.',
        export_needs_download: 'Exporting needs the permission to download files, which your role does not hold.',
        export_download_forbidden: 'The export was made, but the file could not be downloaded: downloading files needs a permission your role does not hold.',
        retrieval_denied: 'Retrieving this version requires a permission your role does not hold.',
        retrieval_denied_generic: 'Downloading this file requires a permission your role does not hold.',
        /* Sprint 62.58.0 PR 3 (product-qa walk N-2) — a stored file refused because
           it printed sections read under grants the role does not all hold; the
           grants named in plain words (`download_permission_names`). */
        download_refusal_sections: 'The file could not be downloaded: it includes sections that need these permissions, which your role does not hold: {permissions}.',
        /* Step-2 confirmation P3-4 — the same refusals in *File details*, which
           the reader only opened: nothing was downloaded. */
        details_refusal_sections: 'The file details cannot be shown: the file includes sections that need these permissions, which your role does not hold: {permissions}.',
        details_refused_generic: 'The file details cannot be shown: viewing this file needs a permission your role does not hold.',
        download_permission_names: {
            privacy_notice_read: 'read privacy notices',
            dm_activity_read: 'read processing activities',
            dm_system_read: 'read data-map systems',
            biometric_capture_read: 'read biometric capture components',
        },
        date_cleared: 'Date cleared',
        clear_date: 'Clear date',
        date_picker: 'Date picker',
        date_selected: 'Selected',
        not_measured: 'Not measured',
        empty_dash: '—',
        network_error: 'Netwerkfout. Controleer uw verbinding.',
        session_expired: 'Sessie verlopen. Log opnieuw in.',
        test: 'Testen',
        pause: 'Pauzeren',
        resume: 'Hervatten',
        activate: 'Activeren',
        last_sync: 'Laatste synchronisatie',
        health: 'Status',
        email: 'E-mail',
        role: 'Rol',
        title: 'Titel',
        owner: 'Eigenaar',
        priority: 'Prioriteit',
        category: 'Categorie',
        details: 'Bijzonderheden',
        overview: 'Overzicht',
        configuration: 'Configuratie',
        history: 'Geschiedenis',
        new_item: 'Nieuw',
        edit_item: 'Bewerken',
        select_option: 'Selecteren...',
        total: 'Totaal',
        active: 'Actief',
        inactive: 'Inactief',
        enabled: 'Ingeschakeld',
        disabled: 'Uitgeschakeld',
        last_login: 'Laatste aanmelding',
        never: 'Nooit',
        password_changed: 'Wachtwoord gewijzigd. U wordt uitgelogd.',
        password_change_failed: 'Wachtwoord wijzigen mislukt',
        passwords_mismatch: 'Wachtwoorden komen niet overeen',
        install: 'Installeren',
        install_named: 'Installeren {name}',
        uninstall: 'Deïnstalleren',
        installed: 'Geïnstalleerd',
        available: 'Beschikbaar',
        catalog: 'Catalogus',
        published: 'Gepubliceerd',
        version: 'Versie',
        market: 'Markt',
        language: 'Taal',
        framework: 'Regelgevingskader',
        template: 'Sjabloon',
        connector_type: 'Connectortype',
        polling_interval: 'Polling-interval (minuten)',
        new_instance: 'Nieuwe instantie',
        change_password: 'Wachtwoord wijzigen',
        current_password: 'Huidig wachtwoord',
        new_password: 'Nieuw wachtwoord',
        confirm_password: 'Wachtwoord bevestigen',
        comments: 'Opmerkingen',
        completed: 'Voltooid',
        contact: 'Contactpersoon',
        hide: 'Verbergen',
        hide_details: 'Verbergen Details',
        label: 'Kenmerk',
        max: 'Max',
        min: 'Min',
        na: 'N/A',
        of: 'of',
        showing: 'Toont',
        page: 'Pagina',
        preview: 'Voorbeeld',
        print: 'Afdrukken',
        show: 'Tonen',
        status_active: 'Actief',
        status_completed: 'Voltooid',
        status_draft: 'Concept',
        status_in_progress: 'In Voortgang',
        status_pending: 'In afwachting',
        view_details: 'Bekijken Details',
        all_priorities: 'Alle Priorities',
        pipeline: 'Werkstroom',
        all_requests: 'Alle Verzoeken',
        schedule: 'Plannen',
        deploy: 'Implementeren',
        rollback: 'Terugdraaien',
        advance_stage: 'Voortgang Fase',
        submit_for_approval: 'Indienen voor Goedkeuring',
        approval_log: 'Goedkeuring Logboek',
        approver: 'Goedkeurder',
        decision: 'Beslissing',
        decided_at: 'Beslist op',
        rollout_stages: 'Uitrol Fases',
        approval_comment: 'Goedkeuring Opmerking',
        optional_comment: 'Optioneel opmerking...',
        impact_summary: 'Impact Samenvatting',
        target_property_ids: "Doel Eigenschap ID's (comma-separated)",
        policy_id: 'Beleid ID',
        target_version: 'Doel Versie',
        target_version_placeholder: 'bv. 2.0.0',
        scheduled_label: 'Gepland',
        scheduled_start: 'Gepland Starten',
        scheduled_end: 'Gepland Einde',
        properties_count: '{count} eigenschappen',
        results: 'Resultaten',
        comparison_results: 'Vergelijking Resultaten',
        past_comparison_reports: 'Vorige Vergelijking Rapporten',
        scanning: 'Scannen...',
        run_inconsistency_scan: 'Uitvoeren Inconsistentie Scannen',
        property_ids_label: "Eigenschap ID's (comma-separated, min 2)",
        comparison_type: 'Vergelijking Type',
        golden_template_id: 'Golden Sjabloon ID (optional)',
        leave_blank_compare: 'Leave blank naar compare eigenschappen against each other',
        add_notes_placeholder: 'Toevoegen your analysis opmerkingen here...',
        covered_of_active: '{covered} van {active} actief eigenschappen covered',
        fetch_error: 'Kan niet fetch gegevens. Probeer het opnieuw.',
        run_comparison_error: 'Kan niet uitvoering comparison. Probeer het opnieuw.',
        save_notes_error: 'Kan niet save opmerkingen. Probeer het opnieuw.',
        scan_complete: 'Inconsistentie scan completed successfully.',
        scan_error: 'Kan niet uitvoering inconsistency scan. Probeer het opnieuw.',
        min_two_properties: "Gelieve provide bij least 2 property ID's voor comparison.",
        comma_separated: 'kommagescheiden',
        language_english: 'Engels',
        language_vietnamese: 'Vietnamees',
        language_vietnamese_native: 'Tiếng Việt',
        author: 'Auteur',
        dependencies: 'Afhankelijkheden',
        checksum: 'Controlesom',
        changelog: 'Wijzigingslogboek',
        tier: 'Niveau',
        phone: 'Telefoon',
        launch: 'Starten',
        complete: 'Voltooien',
        back_to_list: 'Back naar Lijst',
        clear_selection: 'Wissen Selectie',
        deactivate: 'Deactiveren',
        scope: 'Bereik',
        deadline: 'Uiterste datum',
        progress: 'Voortgang',
        frequency: 'Frequentie',
        reviewer: 'Beoordelaar',
        user: 'Gebruiker',
        campaign: 'Campagne',
        items: 'Onderdelen',
        next_run: 'Volgende Uitvoeren',
        last_run: 'Laatste Uitvoeren',
        overdue: 'TE LAAT',
        creating: 'Aanmaken...',
        launching: 'Starten...',
        loaded: 'loaded',
        page_size: 'Paginagrootte',
        per_page: '/ pagina',
        urgent: 'Dringend',
        pagination: 'Paginering',
        metric: 'Metrisch',
        value: 'Waarde',
        country: 'Land',
        showing_chart_view: 'Grafiekweergave weergeven',
        showing_table_view: 'Gegevenstabelweergave weergeven',
        policies: 'Beleid',
        compliant: 'Conform',
        non_compliant_status: 'Niet-conform',
        all_data: 'Alle gegevens',
        sr_urgent: 'Dringend',
        n_per_page: '{n} / pagina',
        select_user_placeholder: '-- Selecteer gebruiker --',
        field_required: 'Dit veld is verplicht',
        results_found: 'resultaten gevonden',
        select_placeholder: '-- Selecteer --',
        unknown_user: 'Onbekende gebruiker',
        // fix-sprint #431 F7 — the names of the platform's own actors
        // (`SystemActorId`), as the audit trail and an assignee column show them.
        // English until Phase 25 (R-57).
        system_actors: {
            intake: 'System Intake',
            sla_engine: 'SLA Engine',
            export_job: 'Export Job',
            cmp_gateway: 'CMP Gateway',
            notification: 'Notification System',
            migration: 'System Migration',
            other: 'System: {name}',
        },
        export_csv: 'CSV exporteren',
        not_applicable: 'N.v.t.',
        aria_overdue_since: 'Achterstallig sinds {date}',
        placeholders: {
            enter_justification: 'Invoeren rechtvaardiging',
            justification: 'Rechtvaardiging',
            campaign_name_example: 'Q1 2026 Toegang Beoordelen',
            optional_description: 'Optioneel beschrijving',
            enter_user_id: 'Invoeren de user ID van de reviewer',
            enter_user_id_short: 'Invoeren user ID',
            schedule_name_example: 'Kwartaal Toegang Attestering',
            enter_property_id: 'Invoeren property ID',
            enter_policy_id: 'Invoeren beleid ID',
            property_or_group_id: 'Eigenschap of groep ID',
            describe_template_purpose: 'Beschrijven de sjabloon purpose...',
            step_title: 'Stap title...',
            detailed_instructions: 'Gedetailleerde instructies...',
            filter_by_taxonomy_id: 'Filteren op Taxonomie ID...',
            describe_classification_rule: 'Beschrijven de classification regel...',
            describe_dedup_rule: 'Beschrijven de dedup regel...',
            describe_import_template: 'Beschrijven de import sjabloon...',
            reason_for_rejection: 'Reden voor rejection...',
            jurisdiction_codes_example: 'VN, SG, EU',
            filter_by_locale: 'Filteren op locale...',
            filter_by_event_type: 'Filteren op gebeurtenis type',
            filter_by_actor_id: 'Filteren op actor ID',
            filter_by_market_code: 'Filteren op market code...',
            filter_by_framework_id: 'Filteren op raamwerk ID...',
            add_comment: 'Toevoegen een opmerking...',
            provide_rejection_reason: 'Opgeven een reason voor rejecting dit indiening...',
            filter_by_jurisdiction: 'Filteren op jurisdiction code...',
            enter_report_title: 'Invoeren report title',
            enter_name: 'Invoeren naam',
            enter_description: 'Invoeren beschrijving',
            enter_org_id: 'Invoeren Org ID naar add',
            describe_evidence_gap: 'Beschrijven de bewijs hiaat...',
            expected_evidence: 'What bewijs is expected',
            entity_identifier: 'Entiteit identifier',
            remediation_steps: 'Stappen naar remediate dit hiaat',
            user_id_or_name: 'Gebruiker ID of naam',
            reporting_period_id: 'Rapportage periode ID',
            maturity_model_id: 'Volwassenheid model ID',
            describe_maturity_model: 'Beschrijven dit volwassenheid model...',
            describe_role_purpose: "Describe this role's purpose...",
            provide_deny_reason: 'Opgeven een reason voor denying dit request',
            describe_emergency: 'Beschrijven de emergency situation requiring elevated toegang (minimum 20 characters)',
            enter_delegate_id: 'Invoeren user ID van de delegate',
            provide_delegation_reason: 'Opgeven een reason voor dit delegation',
            client_identifier: 'Client-identificator',
            client_secret: 'Client-geheim',
            time_format: 'HH:MM',
            describe_sod_rule: 'Beschrijven de purpose van dit SoD regel...',
            enter_role_id: 'Invoeren role ID',
            explain_exception: 'Explain why dit exception is needed...',
            describe_compensating_controls: 'Beschrijven compensating controles in place...',
            full_name: 'Volledig naam',
            min_chars: 'Min. 12 chars',
            enter_override_justification: 'Invoeren rechtvaardiging voor overriding dit gate evaluatie',
            enter_evidence_id: 'Voer bewijs-ID in om te koppelen:',
            date_format: 'DD/MM/JJJJ',
            describe_pack_contents: 'Beschrijf de pakketinhoud en het doel',
            select_reviewers: 'Selecteer beoordelaars...',
        },
        aria_previous_page: 'Vorige pagina',
        aria_next_page: 'Volgende pagina',
        aria_results_per_page: 'Resultaten per pagina',
        per_page_25: '25 / pagina',
        per_page_50: '50 / pagina',
        per_page_100: '100 / pagina',
        select_language: 'Taal selecteren',
        today: 'Vandaag',
        tomorrow: 'Morgen',
        dashboard_load_error: 'Dashboard kon niet worden geladen. Probeer opnieuw.',
        enum_grade: {
            red: 'Rood',
            amber: 'Oranje',
            yellow: 'Geel',
            green: 'Groen',
        },
        enum_severity: {
            critical: 'Kritiek',
            high: 'Hoog',
            medium: 'Gemiddeld',
            low: 'Laag',
        },
        enum_priority: {
            critical: 'Kritiek',
            high: 'Hoog',
            medium: 'Gemiddeld',
            low: 'Laag',
        },
        enum_status: {
            pending: 'In afwachting',
            in_progress: 'Bezig',
            completed: 'Voltooid',
            blocked: 'Geblokkeerd',
            escalated: 'Geëscaleerd',
            cancelled: 'Geannuleerd',
            overdue: 'Achterstallig',
            not_started: 'Niet gestart',
            new: 'Nieuw',
            open: 'Openen',
            closed: 'Gesloten',
            acknowledged: 'Bevestigd',
            resolved: 'Opgelost',
        },
        enum_department: {
            unassigned: 'Niet toegewezen',
        },
        enum_form_category: {
            assessment: 'Beoordeling',
            assessment_report: 'Beoordelingsrapport',
            dpia: 'DPIA',
            authority_submission: 'Inzending van autoriteit',
            cross_border_transfer: 'Grensoverschrijdende overdracht',
            vendor: 'Verkoper',
            breach: 'Schending',
            consent: 'Toestemming',
            governance: 'Bestuur',
            register: 'Registreren',
            monitoring: 'Toezicht',
            dpa: 'DPA',
            dsr: 'DSR',
            privacy: 'Privacy',
            incident: 'Voorval',
            org: 'Organisatie',
            sector: 'Sector',
            classification: 'Classificatie',
            discovery: 'Ontdekking',
            // Sprint 63.37.0 (Stream J) — the pack categories the forms dashboard showed as raw codes.
            evidence_record: 'Evidence Record',
            application: 'Application',
            report: 'Report',
            data_mapping: 'Data Mapping',
            record: 'Record',
            evidence: 'Evidence',
            certificate: 'Certificate',
            log: 'Log',
            sandbox: 'Controlled Testing',
            decision: 'Decision',
            licence: 'Licence',
            declaration: 'Declaration',
            administrative_notice: 'Administrative Notice',
            plan: 'Plan',
            dossier: 'Dossier',
            training: 'Training',
            checklist: 'Checklist',
            training_report: 'Training Report',
            reference_list: 'Reference List',
            evidence_pack: 'Evidence Pack',
            forms: 'Forms',
            authority_response_record: 'Authority Response Record',
            request: 'Request',
            attestation: 'Attestation',
            notification: 'Notification',
            evidence_pack_manifest: 'Evidence Pack Manifest',
            email_submission_record: 'Email Submission Record',
            evidence_file: 'Evidence File',
            worksheet: 'Worksheet',
            uncategorized: 'Uncategorized',
        },
        enum_module: {
            rights_requests: 'Rechtenverzoeken',
            assessments: 'Beoordelingen',
            incidents: 'Incidenten',
            incident_ops: 'Incidentoperaties',
            monitoring: 'Toezicht',
            vendor_risk: 'Leveranciersrisico',
            governance: 'Bestuur',
            deletion_orchestration: 'Verwijdering',
            access_review: 'Toegang tot beoordeling',
            consent: 'Toestemming',
            consent_properties: 'Toestemmingseigenschappen',
            forms: 'Formulieren',
            localization: 'Lokalisatie',
            program_governance: 'Programmabeheer',
            privacy_scanner: 'Privacyscanner',
            classification: 'Classificatie',
            discovery: 'Ontdekking',
            manual: 'Handmatig',
            data_mapping: 'Gegevens in kaart brengen',
        },
        enum_risk_level: {
            critical: 'Kritiek',
            high: 'Hoog',
            medium: 'Gemiddeld',
            low: 'Laag',
        },
        enum_request_type: {
            all: 'All',
            access: 'Toegang (DSAR)',
            deletion: 'Verwijdering',
            correction: 'Correctie',
            objection: 'Bezwaar',
            portability: 'Draagbaarheid',
            restriction: 'Beperking',
            consent_withdrawal: 'Intrekking van toestemming',
            protective_measures: 'Beschermingsmaatregelen',
            processing_information: 'Information about Processing',
            consent_decision: 'Consent Decision',
            complaint_or_compensation: 'Complaint, Denunciation, Lawsuit or Damages Claim',
        },
        enum_locale: {
            en: 'Engels',
            vi: 'Vietnamees',
            all: 'Beide',
        },
        enum_event_type: {
            case_created: 'Case gemaakt',
            status_changed: 'Status gewijzigd',
            case_assigned: 'Casus toegewezen',
            note_added: 'Opmerking toegevoegd',
            attachment_added: 'Bijlage toegevoegd',
            attachment_deleted: 'Bijlage verwijderd',
            reference_added: 'Referentie toegevoegd',
            checklist_updated: 'Controlelijst bijgewerkt',
            identity_verified: 'Identiteit geverifieerd',
            closure_summary_added: 'Samenvatting van de sluiting',
            approval_granted: 'Goedkeuring verleend',
            template_applied: 'Sjabloon toegepast',
            export_generated: 'Gegenereerd exporteren',
            sla_warning: 'SLA Waarschuwing',
            sla_overdue: 'SLA Te laat',
            sla_flag_revisited: 'SLA flag revisited',
            third_party_involvement_changed: 'Processor or third-party involvement changed',
        },
        more: 'more',
        use: 'Use',
        ok: 'OK',
        back_to: 'Return to',
        click_to_copy: 'click to copy',
        copied: 'Copied',
        more_actions: 'More actions',
        actions_for: 'Actions for {title}',
        previous_page: 'Previous page',
        lookup_read_failed: 'A list this page offers for choosing could not be loaded, so a picker may be empty or incomplete.',
        lookup_read_subject: 'the lists offered for choosing',
        export_error: 'Export mislukt',
        you: 'You',
        export_failed: 'Export mislukt. Probeer het opnieuw.',
        filters: 'Filteropties',
        failed: 'Mislukt',
        attachment_release_action: 'Release and destroy the file',
        attachment_release_title: 'Release this evidence file',
        attachment_release_body: 'Releasing destroys the file and records the destruction on the deletion proof chain. Every entry that cites the file will then say it was destroyed, and when. This cannot be undone.',
        attachment_release_basis_legend: 'Why is the file released?',
        attachment_release_basis_retention: 'Its retention period has ended',
        attachment_release_basis_erasure: 'An erasure request reached it',
        attachment_release_reason: 'Reason (required)',
        attachment_release_retention_date: 'Date the retention period ended (required)',
        attachment_release_retention_date_hint: 'No retention period is installed for this evidence, so state the date its retention ended. The file cannot be released before that date.',
        attachment_release_erasure_request: 'Erasure request (required)',
        attachment_release_erasure_placeholder: 'Choose the erasure request',
        attachment_release_erasure_none: 'No erasure request is recorded.',
        attachment_release_erasure_none_not_decided: 'An erasure request about this person is recorded but has not been decided for fulfilment yet, so none is listed. Release the file once the erasure is decided.',
        attachment_release_erasure_none_not_qualifying: 'No erasure request recorded about this person qualifies.',
        attachment_release_confirm: 'Release and destroy',
        attachment_release_releasing: 'Releasing…',
        attachment_release_done: 'The file was released and destroyed.',
        attachment_release_failed: 'The file was not released.',
        attachment_release_not_ended: 'The retention period ends on {date}, so the file cannot be released before then.',
        attachment_release_under_hold: 'The file is under a hold, so it is not released.',
        attachment_release_not_needed: 'No evidence entry rests on this file, so it is not released: delete it the ordinary way.',
        attachment_release_decided_by_register: 'This file is evidence on a register whose own retention rule decides when its evidence is released, so it is not released by a stated date.',
        attachment_release_date_installed: 'The installed retention period ends on {date}; a different date cannot be stated.',
        attachment_released_retention: 'Evidence destroyed at the end of its retention period on {date}',
        attachment_released_erasure: 'Evidence destroyed for erasure request {request}',
        attachment_released_erasure_unnamed: 'Evidence destroyed for an erasure request',
        attachment_release_erasure_label_none: 'Erasure request',
        attachment_released_on: 'Destroyed on {date}',
        attachment_released_heading: 'Files destroyed at the end of their retention or for an erasure request',
        attachment_released_retention_stated: 'Evidence destroyed at the end of its retention period on {date}, a date stated by the person who released it',
        attachment_release_installed_date: 'Date the installed retention period ends',
        attachment_release_installed_date_hint: 'This date comes from the retention period installed for this evidence, so it is not stated here.',
        attachment_release_installed_floor_hint: 'A retention period installed for other evidence citing this file ends on {date}; the date stated may not be before it.',
        attachment_release_date_before_installed: 'A retention period installed for evidence citing this file ends on {date}; a date before it cannot be stated.',
        attachment_release_in_progress: 'This file is already being released by another request, so it was not released twice.',
        // fix-sprint-20261026-0900 F10 — the 502 the release returns when the stored object cannot be deleted.
        attachment_release_storage_delete_failed: 'The stored file could not be destroyed, so the file was not released. Submit the release again to retry it.',
        attachment_release_not_computable: 'A retention period is installed for this evidence, but when it ends cannot be computed, so the file is not released.',
        attachment_release_erasure_hint: "Only requests for erasure about the person this evidence concerns are listed: decided for fulfilment, not rejected, withdrawn or closed without being fulfilled, and with the requester's identity verified.",
        attachment_release_erasure_subject_not_named: 'The register this evidence is on does not record which person it concerns, so the file cannot be released for an erasure request.',
        attachment_release_erasure_subject_not_linked: 'The entry this evidence is on is not linked to a data subject profile, so the file cannot be released for an erasure request.',
        attachment_release_erasure_different_subjects: 'This file is evidence for entries about different people, so it cannot be released for one erasure request.',
        attachment_release_erasure_other_subject: 'That erasure request is about a different person from the one this evidence concerns.',
        attachment_release_erasure_not_actionable: "That erasure request was rejected, withdrawn or closed without being fulfilled, or the requester's identity is not verified.",
        attachment_release_erasure_not_decided: 'That erasure request has not been decided for fulfilment yet, so it does not reach this evidence. Release the file once the erasure is decided.',
        attachment_release_erasure_read_required: 'Naming a rights request as the reason for a release needs the permission to read rights requests, which you do not hold.',
        attachment_release_erasure_withheld: 'You do not hold the permission to read rights requests, so no erasure request can be listed or named here. A person who holds it can release the file for an erasure request.',
        attachment_release_preview_load_error: "What this file's release rests on could not be read.",
    },
    common_statuses: {
        removed: 'Verwijderd',
        draft: 'Concept',
        active: 'Actief',
        inactive: 'Inactief',
        pending: 'In afwachting',
        in_progress: 'In uitvoering',
        completed: 'Voltooid',
        archived: 'Gearchiveerd',
        published: 'Gepubliceerd',
        approved: 'Goedgekeurd',
        rejected: 'Afgewezen',
        cancelled: 'Geannuleerd',
        blocked: 'Geblokkeerd',
        deferred: 'Uitgesteld',
        not_started: 'Niet gestart',
        dismissed: 'Afgewezen',
        failed: 'Mislukt',
        in_review: 'In Beoordelen',
        deprecated: 'Afgeschreven',
        scheduled: 'Gepland',
        deploying: 'Implementeren',
        deployed: 'Geïmplementeerd',
        paused: 'Gepauzeerd',
        rolled_back: 'Teruggedraaid',
        received: 'Ontvangen',
        processing: 'Verwerking',
        under_assessment: 'Onder Beoordeling',
        implemented: 'Geïmplementeerd',
        pending_approval: 'In afwachting Goedkeuring',
        pending_deletion: 'In afwachting Deletion',
        on_hold: 'On Vasthouden',
        deleted: 'Verwijderd',
        expired: 'Verlopen',
        outdated: 'Verouderd',
        drifted: 'Afgeweken',
        decommissioned: 'Buiten gebruik',
        match: 'Matchen',
        different: 'Verschillend',
        missing: 'Ontbrekend',
        confirmed: 'Bevestigd',
        revoked: 'Ingetrokken',
        escalated: 'Geëscaleerd',
        system: 'Systeem',
        custom: 'Aangepast',
        warning: 'Waarschuwing',
        blocking: 'Blokkerend',
        analyst: 'Analist',
        viewer: 'Kijker',
        daily: 'Dagelijks',
        weekly: 'Wekelijks',
        policies: 'Beleid',
        denied: 'Geweigerd',
        verified: 'Geverifieerd',
        detected: 'Gedetecteerd',
        ignored: 'Genegeerd',
        open: 'Openen',
        resolved: 'Opgelost',
        suspended: 'Opgeschort',
        retired: 'Gepensioneerd',
        review: 'Beoordeling',
        generated: 'Gegenereerd',
        exported: 'Geëxporteerd',
        // Sprint 63.37.0 (Stream J) — the embedded form statuses a page prints.
        finalized: 'Finalized',
        superseded: 'Superseded',
    },
    severity: {
        low: 'Laag',
        medium: 'Gemiddeld',
        high: 'Hoog',
        critical: 'Kritiek',
    },
    sensitivity: {
        standard: 'Standaard',
        sensitive: 'Gevoelig',
        special_category: 'Bijzondere categorie',
        badge_sensitive: 'Gevoelig',
    },
    change_types: {
        policy_update: 'Beleid Bijwerken',
        new_binding: 'Nieuw Binding',
        remove_binding: 'Verwijderen Binding',
        configuration_change: 'Configuratie Wijzigen',
        banner_update: 'Banner Bijwerken',
    },
    comparison_types: {
        full: 'Volledig',
        banner_only: 'Alleen banner',
        purposes_only: 'Doelen Only',
        legal_bases_only: 'Juridisch Bases Only',
        consent_settings: 'Toestemming Instellingen',
    },
    lifecycle_stages: {
        active: 'Actief',
        archived: 'Gearchiveerd',
        pending_deletion: 'In afwachting Deletion',
        deleted: 'Verwijderd',
        on_hold: 'On Vasthouden',
        creation: 'Creation',
        exception_hold: 'On Hold',
    },
    object_types: {
        personal_data: 'Persoonlijk Gegevens',
        consent_record: 'Toestemming Record',
        user_account: 'Gebruiker Account',
        audit_log: 'Audit Logboek',
        document: 'Documentbestand',
        backup: 'Back-up',
    },
    risk_categories: {
        data_breach: 'Gegevens Inbreuk',
        unauthorized_access: 'Ongeautoriseerd Toegang',
        data_loss: 'Gegevens Loss',
        regulatory_non_compliance: 'Regelgevend Non-Compliance',
        third_party_risk: 'Third-Party Risico',
        operational: 'Operationeel',
        technical: 'Technisch',
        legal: 'Juridisch',
    },
    mitigation_types: {
        technical: 'Technisch',
        organizational: 'Organisatorisch',
        contractual: 'Contractueel',
        procedural: 'Procedureel',
    },
    safeguard_types: {
        encryption_in_transit: 'Versleuteling tijdens het transport',
        encryption_at_rest: 'Versleuteling in rust',
        access_controls: 'Toegangscontroles',
        contractual_clauses: 'Contractuele clausules',
        data_minimization: 'Gegevensminimalisatie',
        pseudonymization: 'Pseudonimisering',
        audit_rights: 'Auditrechten',
        breach_notification: 'Melding van inbreuk',
        data_localization: 'Gegevenslokalisatie',
        other: 'Anders',
    },
    scoring_methods: {
        weighted_average: 'Gewogen Gemiddeld',
        simple_average: 'Eenvoudig Gemiddeld',
        maximum: 'Maximumwaarde',
        manual: 'Handmatig',
    },
    dpa_status: {
        not_required: 'Niet vereist',
        required: 'Vereist',
        in_progress: 'In uitvoering',
        completed: 'Voltooid',
        signed: 'Ondertekend',
    },
    disposal_methods: {
        anonymize: 'Anonimiseren',
        pseudonymize: 'Pseudonimiseren',
        aggregate: 'Aggregeren',
        delete: 'Verwijderen',
    },
    dashboard: {
        workspace: {
            tabs_label: 'Dashboard sections',
            overview_heading: 'Your compliance posture',
            overview_description: 'Score, recommendations, coverage and operational signals.',
            tasks_heading: 'Turn priorities into progress',
            tasks_description: 'Ownership, overdue work, upcoming tasks and department delivery.',
            insights_heading: 'The full compliance picture',
            insights_description: 'Filings, third-party risk, assessments, incidents and regulatory activity.',
            visible_widgets: '{count} widgets visible',
            widgets_view: 'Widgets',
            config_description: 'Choose which widgets to show and their order. Changes are saved when you apply them.',
            search_widgets: 'Find a widget',
            search_widgets_placeholder: 'Search widgets',
            density: 'Widget spacing',
            density_comfortable: 'Comfortable',
            density_compact: 'Compact',
            default_tab: 'Open dashboard on',
            apply_configuration: 'Apply configuration',
            cancel_configuration: 'Cancel',
            move_up: 'Move {widget} up',
            move_down: 'Move {widget} down',
            enabled_count: '{enabled} of {total} items enabled',
            empty_section: 'No widgets are visible in this section. Use Configure Dashboard to choose available widgets.',
        },
        title: 'Overzicht',
        no_data: 'Geen overzichtsgegevens beschikbaar.',
        no_data_heading: 'Nog geen live gegevens',
        no_data_detail: 'Live figures could not be loaded. The layout is shown; values will appear once the connection is restored.',
        consent_coverage: 'Toestemmingsdekking',
        ctia_filings: 'CTIA-meldingen',
        next_dpia_review: 'Volgende DPIA-beoordeling',
        next_review_none: 'Geen gepland',
        /*
         * Sprint 65.4.0 (AC5) — why the next-review tile has four states.
         *
         * A blank date reads as "nothing due soon". Measured, it almost always
         * meant something else: the only production writer of
         * `grc_dpia_records.next_review_at` was the demo seed, so every real
         * tenant saw the same dash. The tile now says which absence it is.
         */
        next_review_no_dpias: 'No DPIAs recorded',
        next_review_cadence_absent: 'No review cadence declared',
        next_review_not_scheduled: 'Not yet scheduled',
        incident_mps_filings: 'Incident MPS-meldingen',
        compliance_score: 'Nalevingsscore',
        grade: 'Beoordeling',
        no_score: 'Nog geen nalevingsscore berekend.',
        rights_requests: 'Verzoeken om rechten',
        open: 'Geopend',
        sla_breached: 'SLA overschreden',
        sla_approaching: 'SLA nadert',
        closed_month: 'Gesloten (maand)',
        incidents: 'Incidenten & datalekken',
        active: 'Actief',
        breaches: 'Datalekken',
        overdue_tasks: 'Achterstallige taken',
        breach_72h_notice: 'Voor actieve inbreuken geldt een wettelijke kennisgevingstermijn van 72 uur',
        recommendations: 'Aanbevelingen',
        take_action: 'Onderneem actie',
        drift_events: 'Drift & goedkeuringen',
        open_events: 'Openstaande gebeurtenissen',
        pending_approvals: 'Wachtende goedkeuringen',
        monitoring: 'Monitoringtaken',
        programs: "Programma's",
        overdue: 'Achterstallig',
        pending: 'In afwachting',
        ric_cases: 'RIC-dossiers',
        coverage: 'Gegevensdekking',
        systems: 'Systemen',
        activities: 'Activiteiten',
        vendors: 'Leveranciers',
        dpias: "GEB's",
        open_gaps: 'openstaande lacunes',
        view_as_table: 'Weergeven als tabel',
        view_as_chart: 'Weergeven als grafiek',
        table_metric_header: 'Metriek',
        table_value_header: 'Waarde',
        announce_chart_view: 'Grafiekweergave wordt getoond',
        announce_table_view: 'Tabelweergave wordt getoond',
        section_core_metrics: 'Kernmetrieken',
        section_task_management: 'Taakbeheer',
        section_compliance_insights: 'Compliance-inzichten',
        configure_dashboard: 'Dashboard configureren',
        config_col_active: 'Actief',
        config_col_widget: 'Widget',
        collapse_section: 'Inklappen',
        expand_section: 'Uitklappen',
        reset_to_defaults: 'Standaardinstellingen herstellen',
        preferences_saved: 'Dashboard-instellingen opgeslagen',
        preferences_reset: 'Dashboard hersteld naar rolstandaard',
        preferences_load_error: 'Kan dashboard-instellingen niet laden',
        preferences_save_error: 'Dashboard preferences could not be saved.',
        factor_avg_system_score: 'Gemiddelde systeemscore',
        factor_avg_vendor_score: 'Gemiddelde leveranciersscore',
        factor_processing_coverage: 'Dekking van verwerkingsactiviteiten',
        factor_ropa_completeness: 'RoPA-volledigheid',
        factor_drift_penalty: 'Straf voor driftevenementen',
        rec_improve_system_gov: 'Verbeter de documentatie voor systeembeheer',
        rec_improve_vendor_gov: 'Verbeter de documentatie over leveranciersbeheer',
        rec_document_processing: 'Documentverwerkingsactiviteiten voor ongedekte systemen',
        rec_generate_ropa: 'Genereer RoPA-records voor actieve verwerkingsactiviteiten',
        rec_resolve_drift: 'Los open kritieke en zeer ernstige driftgebeurtenissen op',
        score_good: 'Goed',
        score_fair: 'Redelijk',
        score_poor: 'Slecht',
        score_critical: 'Kritiek',
        drag_to_reorder: 'Sleep om te herordenen',
        widget_moved: 'Widget verplaatst',
        press_space_to_grab: 'Druk op spatie om vast te pakken, pijltjestoetsen om te verplaatsen, spatie om los te laten',
        reorder_instructions: 'Gebruik slepen en neerzetten of sneltoetsen om widgets te herordenen',
        page_title: 'Compliance Dashboard',
        welcome_back: 'Welcome back, {name}',
        subtitle: 'Your real-time view of compliance posture, risk, and progress across your organization.',
        section_overview: 'Overview',
        kpi_strip_label: 'KPI overview strip',
        today_label: 'Today',
        avatar_aria: 'Signed in as {name}',
        no_prior_period: 'No prior period yet',
        period_current: 'Current period',
        gauge_aria: '{score} out of 100, {label}',
        donut_aria: '{total} vendors by risk level',
        bar_aria: '{label}: {value} percent',
        config_saving: 'Saving…',
        task_widgets: {
            tasks_overview: 'Takenoverzicht',
            pending: 'In behandeling',
            in_progress: 'In uitvoering',
            overdue: 'Te laat',
            done_7d: 'Klaar (7d)',
            by_priority: 'Op prioriteit',
            view_all_tasks: 'Bekijk alle taken',
            upcoming_tasks: 'Aankomende taken (volgende 7 dagen)',
            no_upcoming: 'Er zijn geen taken gepland in de komende zeven dagen.',
            view_calendar: 'Bekijk Agenda',
            overdue_alerts: 'Te laat',
            no_overdue: 'Geen achterstallige taken. Alles ligt op schema.',
            assigned_to: 'Toegewezen aan',
            unassigned_actor: 'Unassigned',
            untitled_task: 'Untitled task',
            view_all_overdue: 'Bekijk alles Achterstallig',
            notifications: 'Recente meldingen',
            unread: 'unread',
            no_notifications: 'Geen meldingen. Jullie zijn allemaal ingehaald!',
            view_all_notifications: 'Bekijk alle meldingen',
            load_error: 'Gegevens konden niet worden geladen.',
            day_late: 'dag te laat',
            days_late_plural: 'dagen te laat',
            due_today: 'Vandaag verschuldigd',
            just_now: 'zojuist',
            minutes_short: 'm',
            hours_short: 'h',
            days_short: 'd',
            cross_org_stats: 'Statistieken van meerdere organisaties',
            by_organization: 'Per organisatie',
            overdue_across_orgs: 'achterstallige taken binnen organisaties',
            overdue_count_label: '{count} overdue tasks',
            total: 'Totaal',
            tasks: 'tasks',
            organization: 'Organisatie',
            current_org: 'Huidige organisatie',
            all_orgs: 'Alle organisaties',
            all_organizations: 'Alle organisaties',
            filter_by_org: 'Filter op organisatie',
            cross_org: 'Cross-org',
            empty_calendar: 'Geen taken voor deze periode. Maak uw eerste taak of controleer uw filters.',
            empty_my_tasks: 'Er zijn geen taken toegewezen. Geweldig werk!',
            empty_overdue: 'Geen achterstallige taken. Alles ligt op schema.',
            empty_notifications: 'Geen meldingen. Jullie zijn allemaal ingehaald!',
            empty_workflows: 'Geen werkstromen geconfigureerd. Blader door sjablonen om aan de slag te gaan.',
            remediation_pipeline: 'Tasks & Remediation Pipeline',
            no_open_tasks: 'No open tasks — the pipeline is clear.',
            open_tasks: 'Open tasks',
        },
        my_tasks_widget: {
            title: 'Mijn Taken',
            no_tasks: 'Geen taken aan u toegewezen.',
            load_error: 'Taken konden niet worden geladen.',
            overdue: 'achterstallig',
            remaining: 'resterend',
            view_all: 'Alle Taken Bekijken',
        },
        my_section_status_widget: {
            title: 'Mijn Sectiestatus',
            overall_completion: 'Totaal',
            sections_count: 'Secties',
            no_sections: 'Geen secties toegewezen aan uw afdeling.',
            load_error: 'Sectiestatus kon niet worden geladen.',
            department_tasks_title: 'Taken van de afdeling',
            no_department_tasks: 'Uw afdeling heeft geen taken.',
            department_tasks_error: 'Taken van de afdeling konden niet worden geladen.',
            no_department: 'U bent niet aan een afdeling gekoppeld.',
        },
        revision_requests_widget: {
            title: 'Revisieaanvragen',
            no_revisions: 'Geen revisieaanvragen. Alle secties liggen op schema.',
            load_error: 'Revisieaanvragen konden niet worden geladen.',
        },
        consent_summary: {
            title: 'Toestemming',
            active: 'Actief',
            withdrawn_30d: 'Ingetrokken (30d)',
            expiring_7d: 'Verloopt (7d)',
        },
        transfer_assessments: {
            title: 'Overdracht van beoordelingen',
            open: 'Openen',
            approved: 'Goedgekeurd',
            gaps_flagged: 'Hiaten gemarkeerd',
        },
        breach_notifications: {
            title: 'Meldingen van inbreuken',
            deadline_72h: 'Deadline van 72 uur',
            submitted: 'Ingezonden',
            pending: 'In behandeling',
        },
        authority_requests: {
            title: 'Autoriteitsverzoeken',
            pending: 'In behandeling',
            overdue: 'Te laat',
            responded_30d: 'Gereageerd (30d)',
        },
        data_subjects_widget: {
            title: 'Betrokkenen',
            total: 'Totaal',
            high_risk: 'Hoog risico',
            active_30d: 'Actief (30d)',
        },
        audit_activity_summary: {
            title: 'Auditactiviteit',
            events_today: 'Evenementen vandaag',
            events_7d: 'Evenementen (7d)',
            unique_actors_30d: 'Unieke acteurs (30d)',
        },
        onboarding_progress: {
            title: 'Vooruitgang bij onboarding',
            completion_pct: 'Compleet',
            status_label: 'Status',
            current_step: 'Huidige stap',
            status: {
                in_progress: 'In uitvoering',
                completed: 'Voltooid',
                skipped: 'Overgeslagen',
                not_started: 'Niet gestart',
            },
        },
        sector_overlay_reports_due: {
            title: 'Sector Overlay Reports Due [NL]',
        },
        kpi: {
            framework_coverage: 'Framework Coverage',
            open_actions: 'Open Actions',
            high_priority: '{count} high priority',
            vendor_health: 'Vendor Health',
            at_risk: '{count} at risk',
            open_count: '{count} open',
            active_count: '{count} active',
            grade_label: 'Grade: {grade}',
            consent_coverage_hint: 'of active consent workflows',
            consent_coverage_source_web_consent_records: 'of web consent records, last 30 days',
            consent_coverage_source_web_consent_events: 'of web consent events, last 30 days',
            consent_coverage_source_consent_records: 'of consent records, last 30 days',
            of_installed_frameworks: 'of installed frameworks',
            no_frameworks_installed: 'No frameworks installed',
            unavailable: 'Not available',
            consent_no_records: 'No consent records in the last 30 days',
            tile_hint: {
                no_score: 'Not calculated yet',
                load_error: 'Could not be loaded',
                no_frameworks_installed: 'Mandatory: none installed',
                vendor_at_risk: '{at_risk} of {total} high or critical',
                vendor_load_error: 'Could not be read',
                no_vendor_data: 'No vendor data',
                consent_no_records: 'No records, last 30 days',
                consent_source_web_consent_records: 'of web consent records',
                consent_source_web_consent_events: 'of web consent events',
                consent_source_consent_records: 'of consent records',
                not_reported: 'Not reported',
                delta_since: 'points since {period}',
            },
        },
        summary_reloaded: 'Dashboard reloaded.',
        scope_incidents: 'Incidents still open. Breaches are the subset flagged as a personal-data breach.',
        scope_breach_response: 'The same open breaches, by notification and remediation state.',
        scope_breach_notifications: 'Notification filings. Due in 72h counts those with a filing deadline inside the next 72 hours; submitted and pending count every filing in that state, whatever its deadline.',
        vendor_health_hint: '{at_risk} of {total} vendors at high or critical risk',
        vendor_health_load_error: 'Vendor data could not be read.',
        figure_not_reported: 'This figure was not reported in the last dashboard read.',
        framework_coverage: {
            title: 'Framework Coverage',
            overall: 'Overall coverage',
            none: 'The installed frameworks declare no requirements yet.',
            none_installed: 'No frameworks installed. Install a regulatory pack to see its coverage here.',
            requirements_mapped: '{mapped} of {total} requirements mapped',
            no_requirements: 'No requirements declared',
            overall_denominator: 'across {count} frameworks that declare requirements',
            no_requirements_count: '{count} installed frameworks declare no requirements yet',
            no_requirements_count_one: '1 installed framework declares no requirements yet',
            showing_lowest: 'Showing the {shown} lowest-covered of {total}',
            gaps: '{count} open gaps',
            view_all: 'View frameworks',
            load_error: 'Framework coverage could not be loaded.',
        },
        automation_impact: {
            title: 'Automation Impact',
            scope: 'The controls your installed frameworks declare as applicable to this organization, and how many of them have automated work recorded.',
            load_error: 'Automation impact could not be loaded.',
            no_frameworks_installed: 'No installed framework is one your organization must comply with',
            not_declared: 'No installed framework states which of its controls apply to this organization',
            profile_incomplete: 'Record your organization profile to see which controls apply',
            none_applicable: 'No declared control applies to this organization',
            no_emitter: 'No automated work is recorded against controls yet',
            no_point: 'No completed month has a figure to show yet',
            figure_hint: '{automated} of {applicable} applicable controls automated in {period}',
            figure_hint_one: '{automated} of 1 applicable control automated in {period}',
            delta_since: 'percentage points since {period}',
            bucket_applicable: 'Applicable',
            bucket_out_of_scope: 'Declared not to apply to you',
            bucket_not_declared: 'Applicability not declared',
            bucket_profile_incomplete: 'Awaiting your organization profile',
            applicable_from: 'The applicable controls come from {count} installed frameworks',
            applicable_from_one: 'The applicable controls come from 1 installed framework',
            review_profile: 'Review your organization profile',
            tile_hint: {
                load_error: 'Could not be loaded',
                no_frameworks_installed: 'No mandatory framework installed',
                not_declared: 'Applicability not declared',
                profile_incomplete: 'Organization profile needed',
                none_applicable: 'No declared control applies',
                no_emitter: 'No automated work recorded',
                no_point: 'No month to show yet',
                figure: '{automated} of {applicable} controls, {period}',
                figure_one: '{automated} of 1 control, {period}',
            },
        },
        recommendations_widget: {
            title: 'Key Recommendations',
            view_all: 'View all ({count})',
            priority_high: 'High',
            priority_medium: 'Medium',
            priority_low: 'Low',
            none: 'No recommendations. Every score factor is within its healthy range.',
        },
        rights_status: {
            title: 'Rights Requests',
            open_requests: 'Open requests',
        },
        module_status: {
            title: 'Module Status',
            enabled: 'Enabled',
            modules_count: '{count} modules',
            modules_count_one: '{count} module',
            none: 'No modules are enabled for this organization.',
        },
        recent_activity: {
            title: 'Recent Activity',
            view_all: 'View all',
            empty: 'No recent activity.',
            load_error: 'Recent activity could not be loaded.',
        },
        persona_widgets: {
            framework_filter: 'Filteren op kader',
            all_frameworks: 'Alle kaders',
            filing_lifecycle: 'Indieningslevenscyclus',
            filing_pipeline: 'Indieningspijplijn',
            draft: 'Diepgang',
            internal_review: 'Interne beoordeling',
            approved: 'Goedgekeurd',
            submitted: 'Ingezonden',
            supplement_requested: 'Supplement gevraagd',
            resubmitted: 'Opnieuw ingediend',
            accepted: 'Geaccepteerd',
            rejected: 'Afgewezen',
            closed: 'Gesloten',
            deadline: 'Deadline',
            days_remaining: 'resterende dagen',
            days_overdue: 'dagen te laat',
            no_filings: 'Geen actieve indieingen.',
            department_consolidation: 'Afdelingsconsolidatie',
            departments_completed: 'Afdelingen voltooid',
            departments_pending: 'Afdelingen in afwachting',
            departments_overdue: 'Afdelingen te laat',
            no_consolidation_cases: 'Geen afdelingsoverschrijdende zaken in behandeling.',
            vendor_risk: 'Leveranciers- en derdepartijrisico',
            pending_assessment: 'Wachtende beoordeling',
            overdue_questionnaires: 'Te late vragenlijsten',
            dpa_review_queue: 'DPA-beoordelingswachtrij',
            risk_distribution: 'Risicoverdeling',
            total_vendors: 'Totaal leveranciers',
            by_risk_level: 'Op risiconiveau',
            no_vendor_data: 'Geen leveranciersrisicogegevens beschikbaar.',
            discovery_summary: 'Gegevens ontdekken',
            discovery_classification: 'Gegevensontdekking en -classificatie',
            active_scans: 'Actieve scans',
            labels_pending: 'Etiketten in afwachting van beoordeling',
            exceptions_unresolved: 'Uitzonderingen onopgelost',
            pending_review: 'Wachtende beoordeling',
            exceptions: 'Uitzonderingen',
            approved_labels: 'Goedgekeurde labels',
            total_classified: 'Totaal geclassificeerd',
            no_discovery_data: 'Geen ontdekkingsgegevens beschikbaar.',
            ai_governance: 'AI-systeembestuur',
            systems_inventory: 'Systemen in inventaris',
            systems_in_inventory: 'Systemen in inventaris',
            classification_breakdown: 'Classificatie-uitsplitsing',
            dossier_completion: 'Dossiervoltooing',
            by_classification: 'Op classificatie',
            labeling_status: 'Labelstatus',
            not_started: 'Niet gestart',
            no_ai_data: 'Geen AI-bestuursgegevens beschikbaar.',
            obligations_complete: 'Obligations Complete',
            obligations_complete_of: '{complete} of {applicable} applicable obligations',
            no_obligations_apply: 'No applicable obligation recorded',
            ai_register_restricted: 'Your role cannot read the AI system register.',
            breach_response: 'Inbreuk- en incidentrespons',
            active_breaches: 'Actieve inbreuken',
            notification_status: 'Meldingsstatus',
            remediation_progress: 'Herstelvoortgang',
            supplement_loops: 'Aanvullingslussen',
            no_breach_data: 'Geen actieve inbreukzaken.',
            audit_readiness: 'Audit- en beoordelingsgereedheid',
            assessments: 'Beoordelingen',
            gap_findings: 'Hiaatvindingen',
            evidence_completion: 'Bewijsvoltooing',
            export_ready: 'Klaar voor export',
            export_ready_yes: 'Ready',
            export_ready_no: 'Not ready',
            unspecified_framework_group: 'No framework recorded',
            not_ready: 'Niet klaar',
            no_audit_data: 'Geen auditgereedheidsgegevens beschikbaar.',
            framework_label_vn_pdpl: 'VN PDPL 2025',
            framework_label_vn_cybersecurity: 'VN Cyberbeveiliging 2018',
            framework_label_vn_ecommerce: 'VN E-commerce 2023',
            framework_label_vn_telecom: 'VN Telecommunicatie 2023',
            framework_label_vn_ai: 'VN AI-wet 2025',
            framework_label_gdpr: 'AVG',
            framework_label_unspecified: 'Niet gespecificeerd',
            my_department: 'Mijn afdeling',
            my_tasks: 'Mijn taken',
            section_status: 'Sectiestatus',
            revision_requests: 'Revisieverzoeken',
            completion: 'Voltooiing',
            reason: 'Reden',
            requested_at: 'Aangevraagd op',
            no_tasks_assigned: 'Geen taken aan u toegewezen.',
            no_sections: 'Geen secties toegewezen aan uw afdeling.',
            no_revisions: 'Geen revisieverzoeken.',
            load_error: 'Kan gegevens niet laden.',
            vendor_health_needs_attention: '{count} vendors require immediate attention',
            review_vendors: 'Review Vendors',
            total_vendors_label: 'Total Vendors',
        },
    },
    connectors: {
        nav: {
            instances: 'Instanties',
            credentials: 'Referentiekluis',
            event_bus: 'Gebeurtenisbus',
            definitions: 'Definities',
            brds: 'BRD-vastlegging',
            security_reviews: 'Beveiligingsbeoordelingen',
            poc_scorecards: 'POC-scorekaarten',
            automation_planning: 'Automatiseringsplanning',
            system_inventory: 'Systeeminventaris',
            traceability_matrix: 'Traceerbaarheidsmatrix',
            gap_register: 'Gap-register',
        },
        instances: {
            title: 'Connectorinstanties',
            page_title: 'Connectoren',
            add: 'Instantie toevoegen',
            test: 'Verbinding testen',
            sync_now: 'Nu synchroniseren',
            new_instance: 'Nieuw Connector Instantie',
            no_instances: 'Geen connector instanties gevonden',
            instance_count: '{count} instantie(s)',
            confirm_delete: 'Verwijderen connector instantie "{name}"?',
            all_connector_types: 'Alle Connector Types',
            select_connector: 'Selecteren een connector...',
            fetch_error: 'Kan niet fetch connector instanties. Probeer het opnieuw.',
            fetch_definitions_error: 'Kan niet fetch connector definities. Probeer het opnieuw.',
            create_error: 'Kan niet create connector instantie',
            test_error: 'Kan niet test connector. Probeer het opnieuw.',
            status_error: 'Kan de connectorstatus niet bijwerken. Probeer het opnieuw.',
            delete_error: 'Kan niet delete connector instantie. Probeer het opnieuw.',
            confirm_disable: 'Disable "{name}"? Sync will stop until the instance is reactivated.',
            credentials_label: 'Credentials',
            instance_created: 'Connector instance created',
            base_url_label: 'Base URL',
            base_url_hint: "The https address of this connection's endpoint. Test sends the connector type's declared health check to a path on it.",
            base_url_invalid: 'Enter an https address, such as https://api.example.com.',
            edit_instance: 'Edit Connector Instance',
            edit_instance_action: 'Edit',
            instance_updated: 'Connector instance updated',
            update_error: 'The connector instance could not be updated.',
            no_credentials_hint: 'No credentials available. Create one in the Credential Vault first.',
            test_passed: 'Test passed',
            test_failed: 'Test failed',
            status_changed: 'Status bijgewerkt',
            reactivate: 'Opnieuw activeren',
            mark_ready_for_testing: 'Mark ready for testing',
            marked_ready: 'Marked ready for testing. No connection check was made: this connector type declares no health check.',
            test_healthy: 'Health check passed ({detail})',
            test_unhealthy: 'Health check failed ({detail})',
            probe_detail_expected_status: 'the endpoint answered with the expected status',
            probe_detail_unexpected_status: 'the endpoint answered with a status other than the expected one',
            probe_detail_timeout: 'no answer before the timeout',
            probe_detail_network_error: 'the endpoint could not be reached',
            probe_detail_target_refused: "the endpoint's address is not allowed",
            record_sync: 'Record sync result',
            record_sync_title: 'Record sync result: {name}',
            record_sync_note: 'Record the outcome of a sync run that ran outside the platform. The platform does not run the sync itself.',
            sync_outcome: 'Outcome',
            sync_outcome_success: 'Success',
            sync_outcome_partial: 'Partial',
            sync_outcome_failure: 'Failure',
            records_processed: 'Records processed',
            records_created: 'Records created',
            records_failed: 'Records failed',
            sync_recorded: 'Sync result recorded.',
            sync_record_error: 'Failed to record the sync result.',
            recording: 'Recording…',
            refusal_base_url_invalid: 'This base URL cannot be used: enter the https address of an endpoint the platform may call, such as https://api.example.com. Private and local addresses are not allowed.',
            refusal_base_url_missing: 'This connection has no base URL, so its health check has nowhere to go. Use Edit to add the base URL, then test again.',
            refusal_base_url_missing_ask_admin: 'This connection has no base URL, so its health check has nowhere to go. Ask an administrator who can edit connections to add the base URL, then test again.',
            refusal_base_url_invalid_ask_admin: "This connection's base URL cannot be used: it must be the https address of an endpoint the platform may call, and private and local addresses are not allowed. Ask an administrator who can edit connections to correct it, then test again.",
        },
        credentials: {
            title: 'Referentiekluis',
            page_title: 'Connectorgegevens',
            add: 'Referentie toevoegen',
            rotate: 'Roteren',
            expired: 'Verlopen',
            expiring: 'Bijna verlopen',
            orphaned: 'Verweesd',
            new_credential: 'Nieuw Referentie',
            no_credentials: 'Geen credentials gevonden',
            no_expiry: 'No Vervaldatum',
            linked_connectors: 'Gekoppeld Connectoren',
            expires_at: 'Verloopt op',
            value_label: 'Waarde * (write-only)',
            confirm_delete_title: 'Bevestig verwijderen',
            confirm_delete_message: 'Deze referentie verwijderen? Gekoppelde connectors verliezen toegang.',
            rotate_title: 'Referentie roteren',
            new_credential_value: 'Nieuwe referentiewaarde',
            fetch_error: 'Kan niet load credentials. Probeer het opnieuw.',
            health_error: 'Kan niet load gezondheid. Probeer het opnieuw.',
            create_error: 'Kan niet create referentie',
            rotate_error: 'Kan niet rotate referentie',
            delete_error: 'Kan niet delete referentie',
        },
        event_bus: {
            title: 'Gebeurtenisbus',
            page_title: 'Evenementenbus',
            events: 'Gebeurtenissen',
            subscriptions: 'Abonnementen',
            dlq: 'Wachtrij voor mislukte berichten',
            publish: 'Gebeurtenis publiceren',
            retry: 'Opnieuw proberen',
            dismiss: 'Afwijzen',
            search_events: 'Zoeken gebeurtenissen...',
            filter_event_type: 'Filteren op gebeurtenis type...',
            event_type: 'Gebeurtenis Type',
            source_module: 'Bron Module',
            timestamp: 'Tijdstempel',
            delivery_status: 'Levering Status',
            correlation_id: 'Correlatie ID',
            tracking_id: 'Tracking-ID',
            delivery_status_hint: 'De bezorgstatus wordt automatisch bijgewerkt naarmate gebeurtenissen worden verwerkt.',
            no_events: 'Geen gebeurtenissen gevonden',
            total_with_count: '{count} totaal',
            prev: 'Vorige',
            new_subscription: '+ Nieuw Abonnement',
            subscriber_type: 'Abonnee Type',
            subscriber_id: 'Abonnee ID',
            event_type_pattern: 'Gebeurtenis Type Patroon',
            event_type_pattern_placeholder: 'bijv. incident.*',
            filter_expression: 'Filteren Expressie',
            filter_expression_placeholder: 'Optioneel filter',
            subscriber_type_hint: 'Kies hoe gebeurtenissen worden bezorgd: webhook stuurt HTTP-verzoeken, e-mail stuurt meldingen, wachtrij routeert naar een berichtenwachtrij.',
            subscriber_id_hint: 'Voer de bestemming voor gebeurtenissen in. Voor webhooks, gebruik een URL. Voor e-mail, gebruik een adres. Voor wachtrijen, gebruik de wachtrijnaam.',
            event_type_pattern_hint: 'Gebeurtenissen matchen op type met jokertekens. Gebruik * om elk segment te matchen (bijv. incident.* matcht incident.created, incident.resolved).',
            filter_expression_hint: 'Optioneel. Filter gebeurtenissen verder op inhoud (bijv. severity=high).',
            subscriber_id_placeholder_webhook: 'bijv. https://uw-app.com/webhook',
            subscriber_id_placeholder_email: 'bijv. alerts@uw-bedrijf.com',
            subscriber_id_placeholder_queue: 'bijv. gebeurtenissen-wachtrij',
            event_pattern: 'Gebeurtenis Patroon',
            filter: 'Filteren',
            enabled: 'Ingeschakeld',
            delivery_stats: 'Levering Stats',
            no_subscriptions: 'Geen abonnementen gevonden',
            on: 'ON',
            off: 'OFF',
            delivered_failed: '{delivered} delivered / {failed} mislukt',
            confirm_delete_subscription: 'Verwijderen dit abonnement?',
            unresolved: 'Onopgelost',
            resolved: 'Opgelost',
            event_id: 'Gebeurtenis ID',
            subscription_id: 'Abonnement ID',
            error_code: 'Fout Code',
            error_message: 'Fout Message',
            attempts: 'Pogingen',
            last_failed: 'Laatste Failed',
            no_dlq_entries: 'Geen dead letter items gevonden',
            dlq_count: '{count} entry/entries totaal',
            fetch_events_error: 'Kan niet fetch gebeurtenissen. Probeer het opnieuw.',
            fetch_subscriptions_error: 'Kan niet fetch abonnementen. Probeer het opnieuw.',
            fetch_dlq_error: 'Kan niet fetch DLQ items. Probeer het opnieuw.',
            create_subscription_error: 'Kan niet create abonnement',
            toggle_subscription_error: 'Kan niet toggle abonnement',
            delete_subscription_error: 'Kan niet delete abonnement',
            retry_error: 'Opnieuw proberen mislukt',
            dismiss_error: 'Afwijzen mislukt',
            event_published: 'Gebeurtenis gepubliceerd',
            publish_event_error: 'Kan gebeurtenis niet publiceren. Probeer het opnieuw.',
            priority_label: 'Prioriteit',
            priority_low: 'Laag',
            priority_normal: 'Normaal',
            priority_high: 'Hoog',
            payload_helper_text: 'Voer gebeurtenisgegevens in als JSON. Voorbeeld: {"incident_id": "INC-001", "severity": "high"}',
            subscription_created: 'Abonnement aangemaakt',
            subscription_toggled: 'Abonnement omgeschakeld',
            subscription_deleted: 'Abonnement verwijderd',
            dlq_retried: 'DLQ-bericht opnieuw geprobeerd',
            dlq_dismissed: 'DLQ-bericht afgewezen',
        },
        definitions: {
            title: 'Connectordefinities',
            page_title: 'Connectordefinities',
            add: 'Definitie toevoegen',
            new_definition: 'Nieuwe connectordefinitie',
            edit_definition: 'Connectordefinitie bewerken',
            no_definitions: 'Geen connectordefinities gevonden',
            definition_count: '{count} definitie(s)',
            all_categories: 'Alle categorieën',
            select_category: 'Selecteer een categorie...',
            capabilities: 'Mogelijkheden',
            capabilities_hint: "Mogelijkheden (door komma's gescheiden)",
            capabilities_placeholder: 'bijv. lezen, schrijven, synchroniseren',
            created: 'Connectordefinitie gemaakt',
            updated: 'Connectordefinitie bijgewerkt',
            fetch_error: 'Kan connectordefinities niet ophalen. Probeer het opnieuw.',
            create_error: 'Kan connectordefinitie niet maken',
            update_error: 'Bijwerken van connectordefinitie mislukt',
            health_check: 'Health check',
            health_check_none: 'None declared',
            health_check_declare: 'This connector type declares a health check',
            health_check_method: 'Method',
            health_check_path: 'Path',
            health_check_expected_status: 'Expected HTTP status',
            health_check_timeout: 'Timeout (seconds)',
            health_check_hint: "Test sends this one request to the connection's base URL (its base_url setting) and records it as healthy only when the answer has the expected status. Without a health check, Test only marks a connection ready for testing.",
        },
        brds: {
            title: 'BRD-vastlegging',
            page_title: 'BRD-vastlegging',
            create_brd: 'BRD aanmaken',
            brd_title: 'BRD-titel',
            description: 'Beschrijving',
            requirements: 'Vereisten',
            status: 'Status',
            stakeholders: 'Belanghebbenden',
            connector_label: 'Connector',
            select_connector: 'Selecteer een connector...',
            approval: 'Goedkeuring',
            no_brds_found: "Geen BRD's gevonden",
            decision_records: 'Besluitregistraties',
            create_decision: 'Besluit aanmaken',
            alternatives_considered: 'Overwogen alternatieven',
            alternative_name: 'Alternatief naam',
            reason_rejected: 'Reden van afwijzing',
            add_alternative: 'Alternatief toevoegen',
            remove_alternative: 'Alternatief {name} verwijderen',
            alternatives: 'Alternatieven',
            rationale: 'Motivering',
            selected_connector: 'Geselecteerde connector',
            comma_separated_names: "Door komma's gescheiden namen",
            brd_created: 'BRD aangemaakt',
            decision_record_created: 'Beslissingsrecord aangemaakt',
            no_decision_records_found: 'Geen beslissingsrecords gevonden',
            aria_brds_table: "BRD's",
            aria_decision_records_table: 'Beslissingsrecords',
            aria_approve: '{name} goedkeuren',
            aria_archive: '{name} archiveren',
            brd_title_help: 'Een korte, voor mensen leesbare naam voor deze BRD — b.v. "Jira-ticketingintegratie", "Okta SSO-uitrol".',
            brd_title_placeholder: 'bijv. Jira-ticketingintegratie',
            description_help: 'Het zakelijke probleem dat deze connector oplost. Welk resultaat verwacht je als het eenmaal live is?',
            description_placeholder: 'bijv. Automatiseer het aanmaken van incidenttickets op basis van monitoringwaarschuwingen, zodat een oproep binnen 60 seconden wordt opgeroepen.',
            connector_help: 'Optioneel. Kies een connector waarvoor deze BRD bedoeld is. Laat leeg als de connector nog niet is gekozen.',
            requirements_help: 'Door komma\'s gescheiden lijst met onmisbare mogelijkheden — b.v. "OAuth2-aanmelding, webhook-levering, auditexport".',
            requirements_placeholder: 'OAuth2-login, webhooklevering, auditexport',
            stakeholders_help: "Door komma's gescheiden namen of e-mailadressen van mensen die zich moeten afmelden.",
            selected_connector_help: 'Kies de connector waarop deze beslissing van toepassing is.',
            rationale_help: 'Waarom voor deze connector is gekozen. Behandel de beslissende factoren zodat een toekomstige lezer de oproep begrijpt.',
            rationale_placeholder: 'bijv. Kies Jira omdat het al onze ticketgeschiedenis bevat en het team erop is getraind.',
            alternatives_considered_help: 'Andere connectoren zijn geëvalueerd en waarom deze niet zijn gekozen. Voegt verdedigbaarheid toe voor audits.',
            alternative_name_placeholder: 'bijv. ServiceNu',
            reason_rejected_placeholder: 'bijv. Kosten 3× hoger, geen voordeel voor onze use case',
        },
        security_reviews: {
            title: 'Beveiligingsbeoordelingen',
            page_title: 'Beveiligingsbeoordelingen',
            create_review: 'Beoordeling aanmaken',
            connector_name: 'Connectornaam',
            connector_label: 'Connector',
            select_connector: 'Selecteer een connector...',
            reviewer: 'Beoordelaar',
            select_reviewer: 'Selecteer een beoordelaar...',
            findings: 'Bevindingen (één per regel)',
            risk_level: 'Risiconiveau',
            risk_level_placeholder: 'bijv. hoog, middel, laag',
            notes: 'Notities',
            review_status: 'Beoordelingsstatus',
            checklist: 'Controlelijst',
            privacy_impact: 'Privacy-impact',
            approval_gate: 'Goedkeuringspoort',
            no_reviews_found: 'Geen beoordelingen gevonden',
            review_created: 'Beveiligingsbeoordeling aangemaakt',
            create_control_mapping: 'Controltoewijzing aanmaken',
            control_mapping_created: 'Controltoewijzing aangemaakt',
            connector_id_label: 'Connector-ID',
            control_id_label: 'Controle-ID',
            control_name_label: 'Controlenaam',
            aria_approve: '{name} goedkeuren',
            aria_reject: '{name} afwijzen',
            scan_imported: 'Scan succesvol geïmporteerd',
            import_scan: 'Scan importeren',
            scan_format: 'Scanformaat',
            scan_data: 'Scangegevens',
            import_results: 'Importresultaten',
            recommendations: 'Aanbevelingen',
            auto_populated_from: 'Automatisch ingevuld uit {tool}',
            manual_entry: 'Handmatige invoer',
            automation_type: 'Automatiseringstype',
            select_automation_type: 'Automatiseringstype selecteren...',
            automation_full: 'Volledig - connector automatiseert deze bediening volledig',
            automation_partial: 'Gedeeltelijk – connector levert bewijs; mensen handelen nog steeds',
            automation_monitoring: 'Alleen monitoring: de connector observeert, dwingt niet af',
            control_id_help: 'Gebruik de code uit uw nalevingsschema (bijvoorbeeld ISO-27001-A.12.1, SOC2-CC6.1 of uw interne controle-ID).',
            control_name_help: "Korte, voor mensen leesbare naam (bijvoorbeeld 'Netwerktoegangsbeperking').",
            description_label: 'Beschrijving',
            load_sarif_example: 'SARIF-voorbeeld laden',
            load_json_example: 'Aangepast JSON voorbeeld laden',
            scan_format_help: 'SARIF is de OASIS-standaard die door de meeste scanners wordt uitgezonden (Snyk, CodeQL, Semgrep). Gebruik aangepast JSON voor tools die geen SARIF uitzenden.',
            scan_data_help: 'Plak de volledige scanuitvoer, upload een .sarif/.json-bestand of klik op de knop Voorbeeld laden om de vorm te bekijken.',
            upload_file_label: 'Upload een .sarif- of .json-bestand',
            invalid_scan_json: 'Kan scangegevens niet parseren als JSON. Controleer op syntaxisfouten in de buurt van het gemarkeerde teken.',
            save_as_review: 'Opslaan als beveiligingsbeoordeling',
            discard_preview: 'Voorbeeld negeren',
            preview_hint: "Dit is een voorbeeld; er is nog niets opgeslagen. Klik op 'Opslaan als beveiligingsbeoordeling' om een ​​beoordeling in behandeling te maken, die vooraf is ingevuld met deze waarden.",
            reviewer_required_for_save: 'Kies een reviewer om deze scan op te slaan als een beveiligingsbeoordeling.',
        },
        poc_scorecards: {
            title: 'POC-scorekaarten',
            page_title: 'POC-scorekaarten',
            create_scorecard: 'Scorekaart aanmaken',
            connector_label: 'Connector',
            select_connector: 'Selecteer een connector...',
            vendor_name: 'Leveranciersnaam',
            recommendation: 'Aanbeveling',
            recommendation_placeholder: 'bijv. Doorgaan, Niet doorgaan, Verdere evaluatie nodig',
            criteria: 'Beoordelingscriteria',
            criterion_name: 'Criteriumnaam',
            score: 'Scoren',
            max_score: 'Maximale score',
            notes: 'Notities',
            add_criterion: 'Criterium toevoegen',
            remove_criterion: 'Criterium {name} verwijderen',
            total_score: 'Totaalscore',
            api_quality: 'API-kwaliteit',
            reliability: 'Betrouwbaarheid',
            support_quality: 'Ondersteuningskwaliteit',
            cost_score: 'Kostenscore',
            overall_score: 'Totaalscore',
            evidence: 'Bewijsmateriaal',
            no_scorecards_found: 'Geen scorekaarten gevonden',
            scorecard_created: 'POC-scorekaart aangemaakt',
            connector_help: 'Kies de connector waarvoor deze POC-evaluatie is bedoeld.',
            vendor_name_help: 'De verkoper of het product dat wordt geëvalueerd – b.v. "Okta", "Datadog", "Sneeuwvlok".',
            vendor_name_placeholder: 'bijv. Okta, Datadog, Sneeuwvlok',
            recommendation_help: 'Uw laatste gesprek na de evaluatie. Houd het kort en beslissend.',
            criteria_help: 'Geef de leverancier een score op elk evaluatiecriterium. Voeg één rij toe per criterium (API kwaliteit, betrouwbaarheid, ondersteuning, kosten, etc.).',
            criterion_name_help: 'Wat u scoort — b.v. "API Kwaliteit", "Betrouwbaarheid", "Ondersteuning", "Kosten".',
            criterion_name_placeholder: 'bijv. API Kwaliteit',
            score_help: 'Score toegekend voor dit criterium. Mag de maximale score niet overschrijden.',
            max_score_help: 'Topscore beschikbaar voor dit criterium. Gebruik dezelfde schaal voor alle rijen, zodat de totalen vergelijkbaar zijn.',
            notes_help: 'Optioneel. Voeg bewijsmateriaal, kanttekeningen of links toe – alles wat de partituur verklaart.',
        },
        common_labels: {
            aria_filter_instances: 'Connectorinstanties filteren',
            aria_filter_definitions: 'Connectordefinities filteren',
            aria_filter_credentials: 'Referenties filteren',
            aria_filter_events: 'Gebeurtenissen filteren',
            aria_filter_dlq: 'Dead letter-wachtrij filteren',
            aria_instance_summary: 'Connectorinstantie-samenvatting',
            aria_active_connectors: 'Actieve connectoren',
            aria_error_connectors: 'Connectoren met fouten',
            aria_credential_health: 'Samenvatting referentiestatus',
            aria_items_per_page: 'Items per pagina',
            show_password: 'Wachtwoord tonen',
            hide_password: 'Wachtwoord verbergen',
            payload_json_label: 'Payload (JSON)',
            payload_invalid_json: 'Payload moet geldige JSON zijn',
            placeholder_event_type: 'bijv. incident.created',
            placeholder_source_module: 'bijv. monitoring',
            placeholder_version: 'bijv. 1.0',
        },
        statuses: {
            configuring: 'Configureren',
            testing: 'Testen',
            active: 'Actief',
            paused: 'Gepauzeerd',
            error: 'Fout',
            disabled: 'Uitgeschakeld',
            draft: 'Diepgang',
            approved: 'Goedgekeurd',
            archived: 'Gearchiveerd',
            pending: 'In behandeling',
            rejected: 'Afgewezen',
        },
        system_types: {
            application: 'Applicatie',
            database: 'Database',
            service: 'Service',
            infrastructure: 'Infrastructuur',
            third_party: 'Third party',
        },
        hosting_types: {
            on_premise: 'Op locatie',
            cloud: 'Wolk',
            hybrid: 'Hybride',
            managed: 'Beheerd',
        },
        system_statuses: {
            active: 'Actief',
            decommissioned: 'Buiten gebruik',
            planned: 'Gepland',
            under_review: 'In beoordeling',
        },
        automation_statuses: {
            planning: 'Plannen',
            in_progress: 'Bezig',
            completed: 'Voltooid',
            on_hold: 'In de wacht',
        },
        gap_types: {
            functional: 'Functioneel',
            technical: 'Technisch',
            security: 'Beveiliging',
            compliance: 'Naleving',
            process: 'Proces',
        },
        severity_labels: {
            critical: 'Kritiek',
            high: 'Hoog',
            medium: 'Gemiddeld',
            low: 'Laag',
        },
        gap_statuses: {
            open: 'Openen',
            in_progress: 'Bezig',
            mitigated: 'Gemitigeerd',
            closed: 'Gesloten',
            accepted: 'Geaccepteerd',
        },
        verification_statuses: {
            not_started: 'Niet gestart',
            in_progress: 'Bezig',
            verified: 'Geverifieerd',
            failed: 'Mislukt',
        },
        subscriber_types: {
            webhook: 'Webhook',
            internal: 'Intern',
            email: 'E-mail',
            queue: 'Wachtrij',
        },
        credential_types: {
            password: 'Wachtwoord',
            api_key: 'API-sleutel',
            oauth2_client: 'OAuth2-client',
            certificate: 'Certificaat',
            token: 'Token',
            ssh_key: 'SSH-sleutel',
            imap_credentials: 'IMAP-referenties',
        },
        categories: {
            hr: 'HR',
            crm: 'CRM',
            erp: 'ERP',
            cloud: 'Wolk',
            security: 'Beveiliging',
            itsm: 'ITSM',
            custom: 'Aangepast',
        },
        delivery_statuses: {
            pending: 'In afwachting',
            delivered: 'Afgeleverd',
            partial: 'Gedeeltelijk',
            failed: 'Mislukt',
        },
        automation_planning: {
            title: 'Automatiseringsplanning',
            page_title: 'Automatiseringsplanning',
            program_name: 'Programmanaam',
            program_status: 'Status',
            description: 'Beschrijving',
            linked_artifacts: 'Gekoppelde artefacten',
            create_program: 'Programma aanmaken',
            edit_program: 'Programma bewerken',
            no_programs_found: "Geen programma's gevonden",
            program_created: 'Programma aangemaakt',
            program_updated: 'Programma bijgewerkt',
            target_date: 'Streefdatum',
            owner: 'Eigenaar',
            select_status: 'Status selecteren...',
            aria_programs_table: "Automatiseringsprogramma's",
            aria_edit: '{name} bewerken',
            export_programs: "Programma's exporteren",
            title_label: 'Programmatitel',
            title_help: 'Een korte, voor mensen leesbare naam voor het automatiseringsprogramma, bijvoorbeeld "Uitrol van incidenten naar Jira in Q3", "PDPL automatisering van toegangsverzoeken".',
            title_placeholder: 'bijv. Uitrol van incidenten naar Jira in het derde kwartaal',
            description_help: 'Wat dit programma omvat en de uitkomst die u verwacht als het live is. Optioneel maar aanbevolen.',
            description_placeholder: 'bijv. Stuur monitoringwaarschuwingen door naar Jira, zodat de oproep binnen 60 seconden wordt opgeroepen.',
            owner_label: 'Programma-eigenaar',
            owner_help: 'De persoon die verantwoordelijk is voor dit programma. Ze ontvangen meldingen over de voortgang en poorten.',
            select_owner: 'Selecteer een eigenaar...',
            frameworks_in_scope: 'Kaders in reikwijdte',
            frameworks_in_scope_help: 'Compliance-frameworks die dit programma ondersteunt. Helpt het audittraject te laten zien aan welke regelgeving de automatisering onderworpen is.',
            select_frameworks: 'Kaders selecteren...',
            status_draft: 'Diepgang',
            status_active: 'Actief',
            status_completed: 'Voltooid',
            status_archived: 'Gearchiveerd',
            change_status: 'Wijzig status',
            status_updated: 'Status bijgewerkt',
            aria_change_status: 'Status wijzigen voor {name}',
        },
        system_inventory: {
            title: 'Systeeminventaris',
            page_title: 'Systeeminventaris',
            system_name: 'Systeemnaam',
            system_type: 'Systeemtype',
            hosting: 'Hosten',
            status: 'Status',
            description: 'Beschrijving',
            create_system: 'Systeem aanmaken',
            edit_system: 'Systeem bewerken',
            no_systems_found: 'Geen systemen gevonden',
            system_created: 'Systeem aangemaakt',
            system_updated: 'Systeem bijgewerkt',
            select_type: 'Type selecteren...',
            select_hosting: 'Hosting selecteren...',
            select_status: 'Status selecteren...',
            owner: 'Eigenaar',
            department: 'Afdeling',
            data_categories: 'Gegevenscategorieën',
            data_categories_placeholder: "Door komma's gescheiden, bijv. PII, Financieel, Gezondheid",
            jurisdiction: 'Jurisdictie',
            jurisdiction_placeholder: 'bijv. EU, VS, Wereldwijd',
            interfaces: 'Interfaces',
            interfaces_placeholder: "Door komma's gescheiden, bijv. REST API, SFTP, JDBC",
            owner_placeholder: 'Selecteer een verantwoordelijke eigenaar...',
            department_placeholder: 'Selecteer de verantwoordelijke organisatie-eenheid...',
            system_name_hint: 'Gebruik de bedrijfs-erkende systeem- of applicatienaam.',
            system_type_hint: 'Classificeer het systeem zodat rapportage vergelijkbare activa kan groeperen.',
            hosting_hint: 'Identificeer waar het systeem draait voor verblijfplaats, beveiliging en leveranciersbeoordeling.',
            status_hint: 'Stel de levenscyclusstatus in die wordt gebruikt door beoordelingen, exports en auditbewijsmateriaal.',
            owner_hint: 'Kies de actieve gebruiker die verantwoordelijk is voor systeembeheer en vervolgtaken.',
            department_hint: 'Gebruik de organisatie-eenheid die verantwoordelijk is voor het beheer of de governance van dit systeem.',
            data_categories_hint: "Lijst met gegevenscategorieën die door dit systeem worden verwerkt, gescheiden door komma's.",
            jurisdiction_hint: 'Leg de belangrijkste juridische of hosting-jurisdictie vast voor compliance-mapping.',
            interfaces_hint: 'Lijst met connector- of integratiepaden die worden gebruikt om gegevens uit te wisselen.',
            description_hint: 'Vat samen wat het systeem doet en waarom het belangrijk is voor de datakaart.',
            owner_department_required: 'Eigenaar en Afdeling zijn verplicht.',
            aria_systems_table: 'Systeeminventaris',
            aria_edit: '{name} bewerken',
            export_systems: 'Systemen exporteren',
            aria_filter_systems: 'Filter systems',
            governance_context: 'Governance context',
            governance_context_hint: 'Confirm ownership, jurisdiction, data categories, and interfaces before saving.',
            governance_context_summary: 'This context drives data mapping, residency review, and connector accountability.',
        },
        traceability_matrix: {
            title: 'Traceerbaarheidsmatrix',
            page_title: 'Traceerbaarheidsmatrix',
            brd_selector: 'BRD selecteren',
            requirement: 'Vereiste',
            linked_artifact: 'Gekoppeld artefact',
            verification_status: 'Verificatiestatus',
            verified_by: 'Geverifieerd door',
            create_entry: 'Item aanmaken',
            edit_entry: 'Item bewerken',
            no_entries_found: 'Geen items gevonden',
            entry_created: 'Item aangemaakt',
            entry_updated: 'Item bijgewerkt',
            select_brd: 'Selecteer een BRD...',
            select_status: 'Status selecteren...',
            aria_matrix_table: 'Traceerbaarheidsmatrix',
            aria_edit: 'Item bewerken voor {name}',
            export_matrix: 'Matrix exporteren',
            brd_selector_hint: 'Select the Business Requirement Document (BRD) this entry belongs to.',
            fetch_brds_error: 'Failed to load BRD list',
            requirement_id_hint: 'Unique reference code from the BRD or regulatory source (e.g. REQ-001).',
            requirement_text: 'Requirement Text',
            requirement_text_hint: 'Detailed description of the requirement as defined in the source document.',
            verification_method: 'Verification Method',
            verification_method_hint: 'Process used to confirm the requirement is met (e.g. Unit Test, Audit).',
            verification_method_placeholder: 'e.g. test, inspection, review',
            verification_status_hint: 'Current verification state of the requirement implementation.',
            requirement_id: 'Vereiste-ID',
        },
        gap_register: {
            title: 'Gap-register',
            page_title: 'Gap-register',
            gap_title: 'Gap-titel',
            gap_type: 'Gap-type',
            severity: 'Ernst',
            status: 'Status',
            description: 'Beschrijving',
            remediation_plan: 'Herstelplan',
            assigned_to: 'Toegewezen aan',
            due_date: 'Vervaldatum',
            create_gap: 'Gap aanmaken',
            edit_gap: 'Gap bewerken',
            no_gaps_found: 'Geen gaps gevonden',
            gap_created: 'Gap aangemaakt',
            gap_updated: 'Gap bijgewerkt',
            select_type: 'Type selecteren...',
            select_severity: 'Ernst selecteren...',
            select_status: 'Status selecteren...',
            aria_gaps_table: 'Gap-register',
            aria_edit: '{name} bewerken',
            export_gaps: 'Gaps exporteren',
            gap_title_hint: 'Provide a concise name for the identified gap.',
            gap_type_hint: 'Select the nature of the gap (e.g., missing coverage or integration issue).',
            severity_hint: 'The impact of this gap on compliance and operations.',
            status_hint: 'Current stage of the gap resolution lifecycle.',
            owner_hint: 'The person responsible for tracking and remediating this gap.',
            description_hint: 'Detailed context regarding the gap, its root cause, and impact.',
            mitigation_plan_hint: 'Steps planned or taken to address and close the gap.',
            linked_requirement_ids_hint: 'Comma-separated requirement codes this gap affects.',
            linked_system_ids_hint: 'Comma-separated system IDs associated with this gap.',
            gap_types: {
                coverage: 'Coverage',
                capability: 'Capability',
                integration: 'Integration',
                compliance: 'Compliance',
            },
            linked_ids_placeholder: 'Comma-separated IDs',
            linked_requirement_ids: 'Linked Requirement IDs',
            linked_system_ids: 'Linked System IDs',
            mitigation_plan: 'Mitigation Plan',
            select_owner: 'Select owner',
        },
        regulatory: {
            system_inventory_governance_info: 'System inventory aligns with Data Law enterprise governance. System types map to regulatory categories, hosting classification determines jurisdiction-specific controls. Use framework linkage to connect systems to applicable regulatory frameworks.',
            automation_planning_workflow_info: 'Automation programs align with compliance workflow obligations defined by applicable regulatory frameworks. Framework scope determines which requirements the program addresses.',
            traceability_regulatory_source_info: 'Each traceability entry connects a BRD requirement to its regulatory source. Verification status tracks whether implementation satisfies the regulatory obligation.',
            security_reviews_control_mapping_info: 'Security reviews evaluate framework-specific controls applicable to each connector. Risk level captures assessed exposure, findings document deviations from control requirements.',
            gap_register_framework_info: 'Gap Register captures compliance gaps per regulatory framework. Use framework filter to view gaps for a specific framework. Severity levels align with framework risk classification.',
            poc_scorecards_criteria_info: 'POC scorecards evaluate connectors against regulatory criteria. Criteria structure supports weighted scoring with evidence notes. Vendor linkage connects to vendor record.',
            evidence_trail_info: 'All connector module actions generate audit events for cross-module evidence trail integrity. CSV exports preserve referential integrity.',
        },
        system_inventory_extra: {
            framework_linkage: 'Raamwerkkoppeling',
            framework_linkage_placeholder: 'bijv. GDPR, ISO 27001, NIS2',
        },
        automation_planning_extra: {
            framework_scope: 'Raamwerkbereik',
            framework_scope_placeholder: 'bijv. GDPR, PCI-DSS',
            procedure_linkage: 'Procedurekoppeling',
            procedure_linkage_placeholder: 'bijv. SOP-001, PROC-DATA-003',
        },
        traceability_matrix_extra: {
            regulatory_source: 'Regelgevende bron',
            regulatory_source_placeholder: 'bijv. GDPR Art. 30, ISO 27001 A.8',
        },
        security_reviews_extra: {
            control_mapping_col: 'Controlletoewijzing',
            control_mappings_section: 'Controletoewijzingen',
            no_control_mappings: "Nog geen controletoewijzingen. Klik op 'Controletoewijzing maken' om er een toe te voegen.",
            automation_type_col: 'Automatisering',
        },
        gap_register_extra: {
            framework_filter: 'Raamwerk',
            framework_id: 'Raamwerk-ID',
            framework_id_placeholder: 'bijv. GDPR, ISO 27001',
            all_frameworks: 'Alle raamwerken',
        },
        poc_scorecards_extra: {
            criteria_count: 'Criteria',
        },
        export_labels: {
            export_brds: "BRD's exporteren",
            export_decision_records: 'Besluitregisters exporteren',
            export_security_reviews: 'Beveiligingsbeoordelingen exporteren',
            export_poc_scorecards: 'POC-scorekaarten exporteren',
            export_instances: 'Exemplaren exporteren',
            export_definitions: 'Definities exporteren',
            export_credentials: 'Referenties exporteren',
            export_events: 'Gebeurtenissen exporteren',
            exporting: 'Exporteren...',
            export_success: 'Export voltooid',
            export_error: 'Export mislukt',
        },
    },
    // fix-sprint-20260506-2121-followup F16 — the contracts module's own
    // section (it was filed under vendor_risk).
    contracts: {
        obligation_extraction: {
            extract_label: 'Uittreksel verplichtingen',
            extracting: 'Extraheren…',
            results_heading: 'Geëxtraheerde verplichtingen ({count})',
            tokens_label: '≈ {count} tokens',
            no_obligations: 'Er zijn geen verplichtingspatronen gedetecteerd in deze contracttekst.',
            col_class: 'Klasse',
            col_summary: 'Samenvatting (bewerkbaar)',
            col_excerpt: 'Clausule-uittreksel',
            col_due_date: 'Vervaldatum (bewerkbaar)',
            col_priority: 'Prioriteit',
            due_date_placeholder: 'YYYY-MM-DD',
            class_notice: 'Opzegtermijn',
            class_audit: 'Auditrecht',
            class_insurance: 'Verzekering',
            class_sla: 'Reactie SLA',
            class_breach: 'Melding van inbreuk',
            class_renewal: 'Vernieuwingsvenster',
            citations_label: 'Citaties',
            accept_hint: 'Accepteren creëert één taak per verplichting (cap 8) met de prioriteit + vervaldatum weergegeven.',
            accept_label: 'Accepteer verplichtingen',
            reject_label: 'Rapport afwijzen',
            resolve_summary: 'Oplossing: {status} · {tasks} taak(en) gemaakt',
            error_generic: 'Kon geen verplichtingen afdwingen. Probeer het opnieuw of controleer de contracttekst.',
            summary_input_label: 'Summary for obligation {n}: {classification}',
            due_date_input_label: 'Due date for obligation {n}: {classification}',
            tasks_dropped: '{count} task(s) could not be created. Create them from the obligations above.',
        },
    },
    vendor_risk: {
        risk_acceptance_reasoning: {
            draft_label: 'Ontwerp redenering',
            drafting: 'Redenering opstellen...',
            results_heading: 'AI-opgestelde redenering ({risk_level})',
            tokens_label: '≈ {count} tokens',
            narrative_label: 'Rechtvaardiging verhaal',
            controls_heading: 'Compenserende controles',
            no_controls: 'Er worden geen compenserende controles voorgesteld.',
            citations_label: 'Citaties',
            accept_label: 'Accepteer redenering',
            reject_label: 'Concept afwijzen',
            accept_hint: 'Accept legt de redenering vast voor het aanhoudende ontwerp voor de IAT-afstamming. Het verhaal + de bedieningselementen werken het acceptatierecord niet automatisch bij; plakken door de operator is vereist.',
            resolve_summary: 'Resolutie: {status}',
            error_generic: 'Kan geen redenering opstellen. Probeer het opnieuw of controleer de risicoacceptatiestatus.',
        },
        redline_assistant: {
            run_label: 'Redline met bibliotheek',
            running: 'Rode lijn uitvoeren…',
            results_heading: 'Redline-suggesties ({count})',
            tokens_label: '≈ {count} tokens',
            no_suggestions: 'Er zijn geen hiaten in de standaardclausules gedetecteerd.',
            jurisdiction_label: 'Jurisdictie',
            col_kind: 'Soort',
            col_severity: 'Ernst',
            col_title: 'Suggestie',
            col_excerpt: 'Bijpassend fragment',
            col_apply: 'Toepassen',
            kind_missing: 'Standaard ontbreekt',
            kind_weaker: 'Zwakker dan standaard',
            kind_jurisdiction: 'Jurisdictie-overlay',
            proposed_label: 'Voorgestelde tekst',
            citations_label: 'Citaties',
            accept_label: 'Accepteer geselecteerd',
            reject_label: 'Rapport afwijzen',
            partial_label: 'Selectie toepassen',
            accept_hint: 'Selectie toepassen registreert de gekozen suggesties voor het ontwerp voor de IAT-afstamming. Er wordt geen automatische bewerking toegepast op de contracttekst; de operator bepaalt de rode lijn.',
            resolve_summary: 'Oplossing: {status} · {count} suggestie(s) toegepast',
            error_generic: 'Kon geen rode lijn produceren. Probeer het opnieuw of controleer de contractstatus.',
            clause_library_link: 'Beheer de clausulebibliotheek',
            text_source_extracted: 'the extracted contract text',
            text_source_inline: 'the text supplied for this scan',
            text_source_summary: 'the contract description',
            scanned_text_note: 'Scanned {source} ({count} characters). The uploaded contract file is not read.',
            rescan_button: 'Scan again',
            rescan_label: 'Scan for another jurisdiction (country code)',
            jurisdiction_provided: '{code} (chosen for this scan)',
            jurisdiction_no_overlay: '{code} (no jurisdiction overlay exists for it; only your clause library was checked)',
            jurisdiction_assumed: '{code} (assumed: the contract records no jurisdiction)',
        },
        clause_library: {
            page_title: 'Clausulebibliotheek',
            add_label: 'Clausule toevoegen',
            edit_label: 'Bewerken',
            delete_label: 'Verwijderen',
            slug_label: 'Slak',
            title_label: 'Titel',
            body_label: 'Lichaam',
            jurisdiction_label: 'Jurisdictie',
            tags_label: "Tags (door komma's gescheiden)",
            must_contain_label: "Markeringen die moeten bevatten (door komma's gescheiden)",
            severity_label: 'Ernst bij vermissing',
            no_clauses: 'Nog geen clausules. Voeg er een toe om de Redline-assistent te plaatsen.',
            save_label: 'Opslaan',
            cancel_label: 'Annuleer',
            load_error: 'Kan de clausulebibliotheek niet laden.',
            save_error: 'Kan clausule niet opslaan.',
            delete_error: 'Kan clausule niet verwijderen.',
            delete_confirm: 'Clausule "{slug}" verwijderen? Dit kan niet ongedaan worden gemaakt.',
        },
        subprocessor_impact: {
            assess_label: 'Beoordeel de impact',
            assessing: 'Impact beoordelen…',
            results_heading: 'Impact op subverwerker ({severity})',
            tokens_label: '≈ {count} tokens',
            risk_delta_label: 'Risico-delta',
            notification_heading: 'Klantmelding',
            subject_label: 'Onderwerp',
            deadline_label: 'Deadline voor kennisgeving',
            body_label: 'Kennisgevingsinstantie',
            required_fields_label: 'Velden van de kennisgeving (artikel 28, lid 2)',
            mitigation_heading: 'Mitigatiestappen',
            citations_label: 'Citaties',
            accept_label: 'Accepteer beoordeling',
            reject_label: 'Beoordeling afwijzen',
            accept_hint: 'Met Accepteren wordt een klantmeldingstaak gemaakt met de deadline van SLA als vervaldatum.',
            resolve_summary: 'Resolutie: {status}{task}',
            error_generic: 'Kan deze wijziging in de subverwerker niet beoordelen. Probeer het opnieuw of controleer het wijzigingsrecord.',
        },
        onboarding_evidence: {
            collect_label: 'Voer AI bewijsverzameling uit',
            collecting: 'Bewijs verzamelen…',
            results_heading: 'Bewijsverzameling ({tier})',
            tokens_label: '≈ {count} tokens',
            findings_heading: 'Bestaand bewijs',
            missing_heading: 'Ontbrekende artefacten',
            no_findings: 'Er is geen bestaand bewijs gevonden.',
            no_missing: 'Er ontbreken geen vereiste artefacten voor deze risicolaag.',
            next_step_label: 'Volgende stap',
            class_dpa: 'Verwerkersovereenkomst',
            class_soc2: 'SOC 2-rapport',
            class_iso27001: 'ISO 27001 certificaat',
            class_insurance: 'Cyberverzekering',
            class_incident_history: 'Voorafgaand incident',
            accept_hint: 'Accepting creates a task for each missing artefact (up to 5) and records each document found as draft evidence on the vendor. Draft evidence stays unapproved until a person with evidence approval rights reviews it. Prior incidents are listed for your review only.',
            accept_label: 'Rapport accepteren',
            reject_label: 'Rapport afwijzen',
            resolve_summary: 'Resolution: {status} · {tasks} task(s) created · {evidence} draft evidence record(s) attached',
            error_generic: 'Kan AI bewijsverzameling niet uitvoeren. Probeer het opnieuw of controleer de status van de onboardingaanvraag.',
        },
        no_permission_title: 'Onvoldoende machtigingen',
        no_permission_message: 'U heeft geen toestemming voor toegang tot leveranciersrisicobeheer.',
        unknown_vendor: 'Onbekende leverancier',
        nav: {
            dashboard: 'Risico-overzicht',
            scoring: 'Risicoscoring',
            questionnaires: 'Vragenlijsten',
            contracts: 'Contracten',
            gates: 'Goedkeuringspoorten',
            reviews: 'Leveranciersbeoordelingen',
            signals: 'Risicosignalen',
            reassessments: 'Herbeoordelingen',
            portfolio: 'Risicoportefeuille',
            acceptances: 'Risicoacceptatie',
            review_routing: 'Beoordelingsroutering',
            onboarding_cases: 'Onboarding-dossiers',
            sub_processor_register: 'Register van subverwerkers',
        },
        dashboard: {
            title: 'Leveranciersrisico-overzicht',
            page_title: "Dashboard voor leveranciersrisico's",
            loading: 'Laden van dashboard gegevens...',
            risk_tier_header: 'Risiconiveau',
            count_header: 'Tel',
            percentage_header: 'Percentage',
            sections_nav: 'Risicosecties voor leveranciers',
            urgent_expiry: 'Spoed: vervalt binnen 14 dagen',
            no_previous_tier: 'Geen vorig niveau',
            view_as_chart: 'Bekijk als grafiek',
            view_as_table: 'Bekijk als tafel',
            showing_chart: 'Grafiekweergave',
            showing_table: 'Datatabelweergave',
            load_error: 'Kan niet load leverancier risico dashboard',
            risk_distribution: 'Risico Distribution Overview',
            recent_tier_changes: 'Recent Niveau Wijzigingen',
            no_tier_changes: 'No recent niveau changes.',
            review_kpis: 'Beoordelen KPIs',
            total_vendors_reviewed: 'Totaal Leveranciers Beoordeeld',
            reviews_by_outcome: 'Beoordelingen op Resultaat',
            contract_coverage: 'Contract Dekking',
            total_vendors: 'Totaal Leveranciers',
            with_active_dpa: 'Met Actief DPA',
            without_dpa: 'Zonder DPA',
            dpa_coverage: 'DPA Dekking',
            active_contracts_by_type: 'Actief Contracten op Type',
            expiring_contracts: 'Verlopend Contracten (Next 90 Days)',
            no_expiring_contracts: 'No contracten expiring within de next 90 dagen.',
            vendor: 'Leverancier',
            previous_tier: 'Vorig Niveau',
            current_tier: 'Huidig Niveau',
            changed_at: 'Gewijzigd At',
            expires_at: 'Verloopt op',
            days_remaining: 'Dagen Resterend',
            days: 'dagen',
            vendors_label: 'leveranciers',
            no_data: 'No gegevens',
            critical: 'Kritiek',
            high: 'Hoog',
            medium: 'Gemiddeld',
            low: 'Laag',
            expiring_acceptances_heading: 'Verlopende risicoacceptaties',
            expiring_30d_label: 'Verloopt over 30 dagen',
            expiring_60d_label: 'Verloopt over 60 dagen',
            expiring_90d_label: 'Verloopt over 90 dagen',
        },
        scoring: {
            title: 'Risicoscoring',
            page_title: 'Risicoscore van leveranciers',
            filter_scores: 'Risicoscores filteren',
            filter_by_tier: 'Filter op risicolaag',
            scores_pagination: 'Paginering van risicoscores',
            config_dimensions: 'Configuratie van scoringsafmetingen',
            scores_loaded: '{count} risicoscores geladen',
            weight_must_equal_100: 'Het totaalgewicht moet gelijk zijn aan 100 (huidig: {current})',
            out_of_10: 'van de 10, beoordeeld als {tier}',
            dimension_name: 'Dimensie {index} naam',
            dimension_weight: '{name} gewicht (percentage)',
            must_equal_100: 'moet gelijk zijn aan 100',
            config_title: 'Scoring Configuratie',
            scores_title: 'Leverancier Risico Scores',
            new_score_title: 'Score een Leverancier',
            new: 'Nieuw Score',
            vendor_id: 'Leverancier ID',
            risk_tier: 'Risico Niveau',
            score: 'Scoren',
            scored_at: 'Gescoord op',
            no_scores: 'Geen risico scores gevonden.',
            all_tiers: 'Alle Niveaus',
            load_config_error: 'Kan niet load risico scoring configuratie',
            update_config: 'Scoringsconfiguratie bijwerken',
            update_config_error: 'Kan niet update risico scoring configuratie',
            load_scores_error: 'Kan niet load leverancier risico scores',
            calculate_error: 'Kan niet calculate leverancier risico score',
            weight: 'gewicht',
            score_range: 'Beoordeling (1-5)',
            method: 'Methode',
        },
        questionnaires: {
            title: 'Vragenlijsten',
            page_title: 'Vragenlijsten van leveranciers',
            filter_questionnaires: 'Vragenlijstfilters',
            questionnaire_views: 'Vragenlijstweergaven',
            templates_list: 'Lijst met vragenlijstsjablonen',
            instances_list: 'Lijst met vragenlijstinstanties',
            filter_by_status: 'Filter op vragenlijststatus',
            templates_per_page: 'Sjablonen per pagina',
            instances_per_page: 'Instanties per pagina',
            select_template: 'Selecteer vragenlijstsjabloon',
            template_category: 'Sjablooncategorie',
            templates_tab: 'Sjablonen',
            instances_tab: 'Instanties',
            new: 'Nieuw Questionnaire',
            new_template: 'Nieuw sjabloon',
            new_instance: 'Nieuwe instantie',
            vendor_id: 'Leverancier ID',
            template_id: 'Sjabloon ID',
            assigned_to: 'Toegewezen Naar',
            due_date: 'Due Datum',
            responses: 'Reacties',
            risk_score: 'Voltooiing',
            no_questionnaires: 'Geen vragenlijsten gevonden.',
            load_templates_error: 'Kan niet load vragenlijst sjablonen',
            load_instances_error: 'Kan niet load vragenlijsten',
            create_template_error: 'Kan niet create vragenlijst sjabloon',
            initiate_error: 'Kan niet initiate vragenlijst',
            submit_error: 'Kan niet submit vragenlijst',
            review_error: 'Kan niet beoordeling vragenlijst',
            resubmit: 'Opnieuw indienen',
            approve_error: 'Kon vragenlijst niet goedkeuren',
            reject_error: 'Kon vragenlijst niet afwijzen',
            resubmit_error: 'Kon vragenlijst niet opnieuw indienen',
            templates_loaded: '{count} vragenlijstsjablonen geladen',
            instances_loaded: '{count} vragenlijstinstanties geladen',
            fill_responses_cta: 'Antwoorden invullen',
            fill_responses_title: 'Vragenlijstantwoorden invullen',
            template_missing: 'Sjabloon niet gevonden.',
            responses_saved: 'Antwoorden opgeslagen.',
            load_template_error: 'Sjabloon laden mislukt.',
            version_history: 'Versiegeschiedenis',
            download_previous_version: 'Vorige versie downloaden',
            trigger: 'Trigger',
            template_label: 'Sjabloon',
            reviewer: 'Recensent',
            reviewer_required: 'Voor {trigger} vragenlijsten moet een reviewer worden aangesteld (functiescheiding).',
            select_option: 'Selecteer een optie',
            multi_choice_hint: 'Selecteer alles wat van toepassing is',
            category_initial_onboarding: 'Eerste onboarding',
            category_annual_review: 'Jaarlijks overzicht',
            category_incident_triggered: 'Door incidenten veroorzaakt',
            category_contract_renewal: 'Contractverlenging',
            template_editor: {
                page_title: 'Vragenlijstsjabloon bewerken',
                preview_page_title: 'Voorbeeld van een vragenlijstsjabloon',
                edit_structure: 'Structuur bewerken',
                preview: 'Voorbeeld',
                actions: 'Acties',
                back_to_list: 'Terug naar sjablonen',
                load_error: 'Kan sjabloon niet laden',
                save_error: 'Kan de sjabloonstructuur niet opslaan',
                saved: 'Sjabloonstructuur opgeslagen.',
                sections_heading: 'Secties',
                questions_heading: 'Vragen',
                add_section: 'Sectie toevoegen',
                edit_section: 'Sectie bewerken',
                delete_section: 'Sectie verwijderen',
                move_section_up: 'Verplaats sectie naar boven',
                move_section_down: 'Verplaats sectie naar beneden',
                confirm_delete_section: 'Sectie "{title}" verwijderen? Alle vragen zullen worden verwijderd.',
                section_title: 'Sectietitel',
                section_description: 'Sectiebeschrijving',
                section_id: 'Sectie-ID',
                section_id_hint: 'Automatisch gegenereerd, tenzij u er expliciet een instelt.',
                no_sections: 'Nog geen secties. Voeg een sectie toe om te beginnen.',
                no_questions: 'Er zijn nog geen vragen in deze sectie.',
                add_question: 'Vraag toevoegen',
                edit_question: 'Vraag bewerken',
                delete_question: 'Vraag verwijderen',
                move_question_up: 'Verplaats de vraag naar boven',
                move_question_down: 'Verplaats de vraag naar beneden',
                confirm_delete_question: 'Vraag "{text}" verwijderen?',
                question_text: 'Vraag tekst',
                question_type: 'Vraagtype',
                question_id: 'Vraag-ID',
                evidence_required: 'Bewijs vereist',
                risk_weight: 'Risicogewicht',
                scoring_map: 'Scorekaart',
                scoring_map_json_hint: 'JSON object wijst antwoordwaarden toe aan numerieke scores.',
                scoring_map_invalid_json: 'De scorekaart moet geldig zijn JSON.',
                options_heading: 'Antwoordmogelijkheden',
                add_option: 'Optie toevoegen',
                remove_option: 'Optie verwijderen',
                option_label: 'Etiket',
                option_value: 'Waarde',
                option_risk_weight: 'Risicogewicht',
                min_options_error: 'Keuzevragen moeten minimaal 2 opties hebben.',
                option_label_required: 'Elke optie heeft een label nodig.',
                option_value_required: 'Elke optie heeft een waarde nodig.',
                option_value_unique: 'Optiewaarden moeten uniek zijn.',
                branching_rules_heading: 'Vertakkingsregels',
                branching_rules_hint: 'Toon of verberg deze vraag op basis van het antwoord op een eerdere vraag.',
                add_branching_rule: 'Vertakkingsregel toevoegen',
                remove_branching_rule: 'Vertakkingsregel verwijderen',
                branching_condition_question: 'Indien vraag',
                branching_condition_value: 'heeft waarde',
                branching_action: 'dan',
                branching_action_show: 'laat deze vraag zien',
                branching_action_hide: 'verberg deze vraag',
                branching_select_condition_question: 'Selecteer eerdere vraag',
                branching_select_condition_value: 'Selecteer waarde',
                save: 'Structuur bewaren',
                saving: 'Opslaan...',
                cancel: 'Annuleer',
                close: 'Sluiten',
                question_text_required: 'Vraagtekst is vereist.',
                section_title_required: 'Sectietitel is vereist.',
                type_single_choice: 'Enkele keuze',
                type_multi_choice: 'Meerkeuze',
                type_free_text: 'Vrije tekst',
                type_numeric: 'Numeriek',
                type_date: 'Datum',
                type_file_upload: 'Bestand uploaden',
                type_yes_no: 'Ja / Nee',
                preview_heading: 'Sjabloonvoorbeeld',
                preview_subtitle: 'Alleen-lezen voorbeeld van hoe de vragenlijst er voor een leverancier uitziet.',
                preview_no_content: 'Deze sjabloon bevat nog geen secties of vragen.',
                preview_branching_notice: 'Vertakkingsregels worden geëvalueerd tijdens het invullen; preview toont elke vraag.',
            },
            review_modal: {
                title: 'Bekijk de antwoorden op de vragenlijst',
                open_cta: 'Beoordeling',
                loading: 'Vragenlijst laden...',
                template_missing: 'Sjabloon niet gevonden.',
                load_error: 'Kan de vragenlijst niet ter beoordeling laden.',
                annotation_heading: 'Annotaties',
                annotation_placeholder: 'Voeg een recensentannotatie toe voor deze vraag...',
                annotation_status_label: 'Annotatiestatus',
                annotation_status_acceptable: 'Acceptabel',
                annotation_status_concern: 'Bezorgdheid',
                annotation_status_requires_action: 'Vereist actie',
                review_comments_label: 'Algemene beoordelingsopmerkingen',
                review_comments_placeholder: 'Vat uw reviewbevindingen samen...',
                justification_label: 'Rechtvaardiging van het besluit',
                justification_placeholder: 'Leg de reden voor uw beslissing uit...',
                reject_requires_comments: 'Voor het afwijzen van een vragenlijst zijn beoordelingsopmerkingen vereist.',
                decision_heading: 'Besluit',
                decision: {
                    approved: 'Goedkeuren',
                    rejected: 'Afwijzen',
                    additional_info_requested: 'Vraag aanvullende informatie aan',
                },
                submit: 'Beoordeling indienen',
                submitting: 'Beoordeling indienen...',
                cancel: 'Annuleer',
                submit_success: 'Beoordeling ingediend.',
                submit_error: 'Het is niet gelukt om een beoordeling in te dienen.',
                separation_of_duties_error: 'U kunt een vragenlijst die u moet invullen, niet bekijken. Functiescheiding vraagt ​​om een ​​andere beoordelaar.',
                decision_required: 'Selecteer een beoordelingsbesluit.',
                announce_opened: 'Beoordelingsmodaal geopend.',
                announce_submitted: 'Beoordeling ingediend.',
            },
            instance_detail: {
                submitted_at: 'Ingezonden',
                reviewed_at: 'Beoordeeld',
                reviewer: 'Recensent',
                review_decision: 'Besluit',
                review_comments: 'Opmerkingen beoordelen',
                decision_approved: 'Goedgekeurd',
                decision_rejected: 'Afgewezen',
                decision_additional_info_requested: 'Meer info gevraagd',
                decision_pending: 'In afwachting van beoordeling',
                expand_comments: 'Volledige reacties weergeven',
                collapse_comments: 'Volledige opmerkingen verbergen',
                no_reviewer: 'Geen recensent toegewezen',
                earlier_reviews: 'Earlier reviews',
                earlier_review_entry: '{decision} by {name} on {date}.',
                earlier_review_comments: 'Comments: {comments}',
                earlier_review_question_note: 'Note on {question}: {comments}',
                earlier_review_note: 'Question note: {comments}',
                earlier_review_unknown_person: 'an unrecorded reviewer',
                earlier_review_unknown_date: 'an unrecorded date',
                // Sprint 62.58.0 gate E12.5 pass 1 (P3-4) — the instance export's line
                // for one earlier review: its number, then the page's own sentence
                // (`earlier_review_entry`), so the two never word a review two ways.
                earlier_review_export_line: 'Earlier review {index}: {entry}',
                earlier_review_no_decision: 'No decision recorded',
            },
            instance_statuses: {
                draft: 'Concept',
                in_progress: 'In uitvoering',
                submitted: 'Submitted',
                under_review: 'Under review',
                approved: 'Goedgekeurd',
                rejected: 'Afgewezen',
                expired: 'Verlopen',
            },
            export: {
                portfolio_label: 'Portefeuille exporteren',
                instance_label: 'Exporteren',
                portfolio_success: 'Portefeuille-export gereed.',
                instance_success: 'Vragenlijstexport gereed.',
                portfolio_error: 'Kan vragenlijstportfolio niet exporteren.',
                instance_error: 'Kan de vragenlijst niet exporteren.',
                header_line: '{label}: {value}',
                default_title: 'Vendor questionnaire',
                unassigned: 'Unassigned',
                risk_score_none: 'Not scored',
                column_question: 'Question',
                column_answer: 'Answer',
                column_evidence: 'Evidence',
                column_annotation: 'Reviewer annotation',
                section_no_questions: 'No questions in this section.',
                portfolio_title: 'Vendor questionnaires portfolio',
                portfolio_generated: 'Generated',
                portfolio_rows: 'Rows',
                portfolio_status_filter: 'Status filter',
                portfolio_vendor_filter: 'Vendor filter',
                portfolio_assignee_filter: 'Assignee filter',
            },
            branching: {
                hidden_notice: 'Sommige vragen zijn verborgen door vertakkingsregels.',
                cycle_warning: 'Er is een cyclus gedetecteerd in de vertakkingsregels. Betrokken vragen zijn standaard zichtbaar.',
            },
        },
        contracts: {
            edit_error: 'Failed to update contract',
            edit_success: 'Contract updated',
            edit_contract: 'Edit contract details',
            record_signature: 'Record signature',
            signature_section: 'Signatures on this contract',
            signature_none: 'No signature has been recorded for this contract.',
            signatory_name: 'Signatory name',
            signatory_title: 'Signatory title',
            signatory_party: 'Signing party',
            signatory_party_organization: 'Our organisation',
            signatory_party_counterparty: 'Counterparty',
            signed_on: 'Date signed',
            signature_evidence: 'Evidence reference',
            signature_evidence_none: 'No evidence reference is recorded on this contract yet. Attach the signed document below instead.',
            signature_evidence_attachment: 'Signed document',
            signature_evidence_attachment_hint: 'Upload the signed document when there is no evidence reference to select.',
            signature_evidence_hint: 'A contract enters into force on a recorded signature, so a signature needs evidence: reference evidence already on this contract, or attach the signed document.',
            signature_saved: 'Signature recorded',
            signature_error: 'Failed to record the signature',
            title: 'Contracten',
            page_title: 'Leverancierscontracten',
            new: 'Nieuw Contract',
            vendor_id: 'Leverancier ID',
            filter_contracts: 'Filter contracten',
            filter_by_document_type: 'Filter op documenttype',
            filter_by_status: 'Filter op contractstatus',
            per_page: 'Contracten per pagina',
            expired_notice: 'Dit contract is verlopen',
            transition_reason: 'Reason for this change',
            transition_reason_required: 'A reason is required to take this contract out of force.',
            transition_reason_placeholder: 'Why is this contract changing status?',
            transition_none_available: 'This status is final. A successor contract is a new record.',
            document_type: 'Documenttype',
            expiry_date: 'Vervaldatum Datum',
            no_contracts: 'Geen contracten gevonden.',
            all_document_types: 'Alle Document Types',
            load_error: 'Kan niet load contracten',
            create_error: 'Kan niet create contract',
            delete_error: 'Kan niet delete contract',
            update_error: 'Kan de status niet bijwerken',
            confirm_delete: 'Verwijderen contract "{title}"?',
            renewal_date: 'Verlenging Datum',
            effective_date: 'Effectief Datum',
            parties: 'Partijen',
            summary: 'Samenvatting',
            n_contracts: '{count} contracten',
            n_contract: '{count}-contract',
            contracts_loaded: '{count} contracten geladen',
            doc_type_dpa: 'DPA',
            doc_type_security_addendum: 'Beveiligingsaddendum',
            doc_type_scc_annex: 'SCC-bijlage',
            doc_type_sub_processor_list: 'Sub-verwerkerlijst',
            doc_type_bcr: 'BCR',
            doc_type_nda: 'NDA',
            doc_type_msa: 'MSA',
            doc_type_sla: 'SLA',
            doc_type_other: 'Overig',
            status_draft: 'Concept',
            status_under_negotiation: 'In onderhandeling',
            status_pending_signature: 'Wacht op ondertekening',
            status_active: 'Actief',
            status_expired: 'Verlopen',
            status_terminated: 'Beëindigd',
            status_superseded: 'Vervangen',
            contract_file: 'File',
            upload_contract_file: 'Contract File',
            auto_renew: 'Renews automatically',
            auto_renew_hint: 'On the expiry date the contract renews for one more term of the same length, recorded in its history. It does not renew if its effective date or renewal notice period is missing, or if it is not active.',
            renewal_period_days: 'Renewal notice (days)',
            renewal_period_days_hint: 'A renewal reminder task is raised this many days before the expiry date.',
            clauses_heading: 'Clauses',
            clause_title: 'Clause title',
            clause_title_required: 'A clause with text needs a title.',
            clause_content: 'Clause text',
            clause_mandatory: 'Mandatory clause',
            add_clause: 'Add clause',
            remove_clause: 'Remove clause',
            contract_file_save_first: 'Save the contract first. Its file can be attached once the contract exists.',
            expiry_date_keep_hint: 'An expiry date, once set, can be changed but not removed.',
            renewal_history: 'Renewal history',
            renewal_history_note: 'Each row is an automatic renewal the platform made because the contract renews automatically: the expiry date moved by one term.',
            renewal_col_previous_expiry: 'Previous expiry',
            renewal_col_new_expiry: 'New expiry',
            renewal_col_term: 'Term',
            renewal_col_renewed_on: 'Renewed on',
            renewal_term_months: '{count} month(s)',
            renewal_term_days: '{count} day(s)',
            clause_field_label: 'Clause {n}, {field}',
            field_other: 'Another field',
        },
        gates: {
            title: 'Goedkeuringspoorten',
            page_title: 'Poortevaluaties',
            new: 'Nieuw Gate Evaluation',
            vendor_id: 'Leverancier ID',
            gate_name: 'Gate Naam',
            outcome: 'Resultaat',
            evaluator: 'Beoordelaar',
            no_gates: 'Geen gate evaluations gevonden.',
            load_error: 'Kan niet load gate evaluations',
            evaluate_error: 'Kan niet evaluate leverancier gate',
            detail_error: 'Kan niet fetch evaluatie detail',
            override_error: 'Kan niet override gate evaluatie',
            criteria_results: 'Criteria Resultaten',
            evaluate_form: 'Evalueer de leverancierspoort',
            per_page: 'Evaluaties per pagina',
            passed: 'Geslaagd',
            failed: 'Mislukt',
            override: 'Overschrijven',
            override_justification: 'Overschrijven Rechtvaardiging',
            override_placeholder: 'Invoeren rechtvaardiging voor overriding dit gate evaluatie',
            approval_id: 'Goedkeuring ID',
            overall_passed: 'Geslaagd',
            overall_failed: 'Mislukt',
            overall_overridden: 'Overschreven',
            evaluations_loaded: '{count} poortbeoordelingen geladen',
            create_approval_cta: 'Goedkeuring aanmaken',
        },
        reviews: {
            title: 'Leveranciersbeoordelingen',
            page_title: 'Leveranciersrecensies',
            new: 'Nieuw Beoordelen',
            vendor_id: 'Leverancier ID',
            filter_by_outcome: 'Filter op beoordelingsresultaat',
            per_page: 'Recensies per pagina',
            kpi_summary: 'Bekijk de samenvatting van KPI.',
            review_outcome_label: 'Beoordeel de uitkomst',
            program_unresolved: 'Program {id} (name not available)',
            programs_unavailable: 'Programs could not be listed. Enter the program ID.',
            edit_outcome: 'Beoordelingsresultaat bewerken',
            reviews_loaded: '{count} leveranciersrecensies geladen',
            reviewer: 'Beoordelaar',
            review_type: 'Beoordelen Type',
            outcome: 'Resultaat',
            findings: 'Bevindingen',
            no_reviews: 'Geen leverancier beoordelingen gevonden.',
            load_kpis_error: 'Kan niet fetch leverancier beoordeling KPIs',
            load_error: 'Kan niet fetch leverancier beoordelingen',
            update_error: 'Kan leveranciersrecensie niet bijwerken',
            create_error: 'Kan niet create leverancier beoordeling',
            total_reviewed: 'Totaal Leveranciers Beoordeeld',
            total_reviews: 'Totaal Beoordelingen',
            satisfactory: 'Voldoende',
            concerns_identified: 'Zorgen Identified',
            remediation_required: 'Herstel Vereist',
            escalated: 'Geëscaleerd',
            program_id: 'Programma ID',
            period_id: 'Periode ID',
            task_id: 'Taak ID',
            recommendations: 'Aanbevelingen',
            reviewed_at: 'Reviewed At',
        },
        risk_signals: {
            title: 'Risicosignalen',
            page_title: 'Risicosignalen',
            new_signal: 'Nieuw risicosignaal',
            signal_type: 'Signaaltype',
            severity: 'Ernst',
            description: 'Beschrijving',
            source: 'Bron',
            vendor_id: 'Leverancier-ID',
            certification_expiry: 'Certificaatvervaldatum',
            breach_notification: 'Inbreukmelding',
            financial_instability: 'Financiële instabiliteit',
            compliance_change: 'Nalevingswijziging',
            custom: 'Aangepast',
            active: 'Actief',
            acknowledged: 'Bevestigd',
            resolved: 'Opgelost',
            no_signals: 'Geen risicosignalen gevonden',
            source_external_feed: 'Externe feed',
            source_manual: 'Handmatig',
            source_integration: 'Integratie',
            source_regulatory: 'Regelgevend',
            source_media: 'Media',
            trigger_reassessment_prompt: 'Dit signaal is van hoge ernst. Nu een leveranciersherbeoordeling starten?',
            reassessment_triggered: 'Herbeoordeling gestart.',
            reassessment_trigger_error: 'Kon herbeoordeling niet starten.',
        },
        reassessments: {
            title: 'Herbeoordelingstaken',
            page_title: 'Herbeoordelingstaken',
            trigger: 'Herbeoordeling starten',
            trigger_success: 'Herbeoordeling succesvol gestart',
            reason: 'Reden',
            deadline: 'Deadline',
            assigned_to: 'Toegewezen aan',
            vendor_id: 'Leverancier-ID',
            outcome: 'Resultaat',
            new_risk_level: 'Nieuw risiconiveau',
            deadline_hint: 'The deadline is the end of the chosen day (UTC).',
            reason_required: 'Enter a reason.',
            vendor_required: 'Choose a vendor.',
            already_completed: 'This reassessment has already been completed.',
            complete: 'Herbeoordeling voltooien',
            complete_success: 'Herbeoordeling succesvol voltooid',
            pending: 'In afwachting',
            in_progress: 'Bezig',
            completed: 'Voltooid',
            cancelled: 'Geannuleerd',
            no_tasks: 'Geen herbeoordelingstaken gevonden',
            risk_trend: 'Risicotrend',
            notes: 'Notities',
        },
        portfolio: {
            title: 'Risicoportefeuille',
            page_title: 'Risicoportfolio',
            dashboard: 'Portefeuille-overzicht',
            vendor_id: 'Leverancier-ID',
            total_vendors: 'Totaal aantal leveranciers',
            average_score: 'Gemiddelde risicoscore',
            high_risk_count: 'Hoog-risico leveranciers',
            trend_direction: 'Trendrichting',
            improved: 'Verbeterd',
            stable: 'Stabiel',
            degraded: 'Verslechterd',
            vendor_trend: 'Leverancierstrend',
            outstanding_questionnaires: 'Openstaande vragenlijsten',
            overdue_assessments: 'Achterstallige beoordelingen',
            no_outstanding: 'Geen openstaande vragenlijsten',
            no_overdue: 'Geen achterstallige beoordelingen',
            due_date: 'Vervaldatum',
        },
        risk_acceptance: {
            expiring_within: 'Expiring within {days} days',
            title: 'Risicoacceptatieregister',
            page_title: 'Risicoacceptatieregister',
            new_acceptance: 'Nieuwe risicoacceptatie',
            vendor_id: 'Leverancier-ID',
            risk_level: 'Risiconiveau',
            conditions: 'Voorwaarden',
            justification: 'Rechtvaardiging',
            expiry_date: 'Vervaldatum',
            create_success: 'Risicoacceptatie succesvol aangemaakt',
            review_condition: 'Voorwaarde beoordelen',
            condition_met: 'Voorwaarde voldaan',
            condition_not_met: 'Voorwaarde niet voldaan',
            review_success: 'Voorwaarde succesvol beoordeeld',
            export_register: 'Register exporteren',
            active: 'Actief',
            expired: 'Verlopen',
            revoked: 'Ingetrokken',
            no_acceptances: 'Geen risicoacceptaties gevonden',
            met_count: '{met}/{total} ontmoet',
        },
        review_routing: {
            title: 'Beoordelingsroutering',
            page_title: 'Bekijk de routering',
            route_to_reviewers: 'Naar beoordelaars sturen',
            route_success: 'Succesvol naar beoordelaars gestuurd',
            vendor_id: 'Leverancier-ID',
            reviewer_id: 'Beoordelaar-ID',
            reviewer_role: 'Beoordelaarsrol',
            questionnaire_id: 'Vragenlijst-ID',
            review_status: 'Beoordelingsstatus',
            reviewers_label: 'Reviewers (user_id, rol per regel)',
            pending: 'In afwachting',
            in_review: 'In beoordeling',
            completed: 'Voltooid',
            no_routing: 'Geen beoordelingsroutering gevonden',
            primary_reviewer: 'Primaire beoordelaar',
            secondary_reviewer: 'Secundaire beoordelaar',
            approver: 'Goedkeurder',
        },
        onboarding: {
            title: 'Leverancier-onboarding-dossiers',
            page_title: 'Leverancier-onboarding-dossiers',
            new_case: 'Nieuw onboarding-dossier',
            case_detail: 'Onboarding-dossier',
            open_mps_dossier_filing: 'MPS-indiening openen',
            dossier_filing_description: 'Maakt een nieuwe MPS-indiening vooraf ingevuld vanuit dit onboardinggeval. De indiening wordt teruggekoppeld aan het geval.',
            dossier_filing_dpia_id: 'DPIA-ID',
            dossier_filing_dpia_id_placeholder: 'bv. dpia_01TEST000000000000000001',
            dossier_filing_title: 'Indieningstitel',
            dossier_filing_form_type: 'Formuliertype',
            dossier_filing_submit: 'Indiening maken',
            dossier_filing_validation_error: 'DPIA-ID en titel zijn vereist.',
            dossier_filing_created: 'MPS-indiening aangemaakt',
            dossier_filing_error: 'MPS-indiening kon niet worden aangemaakt.',
            linked_questionnaire_due_date: 'Vervaldatum',
            linked_questionnaire_assignee: 'Toegewezen aan',
            linked_questionnaire_submitted_at: 'Ingediend op',
            score_last_scored: 'Laatst gescoord',
            score_dimensions: 'Dimensies',
            linked_contract_document_type: 'Documenttype',
            linked_contract_effective_date: 'Ingangsdatum',
            linked_contract_expiry_date: 'Verloopdatum',
            linked_sub_processor_countries: 'Verwerkingslanden',
            linked_sub_processor_last_assessment: 'Laatste beoordeling',
            linked_sub_processor_next_assessment: 'Volgende beoordeling',
            case_id: 'Dossier-ID',
            vendor: 'Leverancier',
            case_type: 'Dossiertype',
            service_description: 'Dienstbeschrijving',
            data_categories: 'Gegevenscategorieën',
            systems_accessed: 'Benaderde systemen',
            jurisdiction: 'Jurisdictie',
            hosting_model: 'Hostingmodel',
            controller_role: 'Verwerkingsverantwoordelijke/Verwerker-rol',
            status_pipeline: 'Statuspijplijn',
            tabs_label: 'Onboarding-dossiersecties',
            per_page: 'Dossiers per pagina',
            filter_cases: 'Onboarding-dossiers filteren',
            cases_count: 'dossiers',
            cases_loaded: 'onboarding-dossiers geladen',
            no_cases: 'Geen onboarding-dossiers gevonden.',
            vendor_lookup_placeholder: 'Voer leverancier-ID in',
            data_categories_placeholder: 'bijv., persoonsgegevens, financieel, gezondheid',
            systems_placeholder: 'bijv., CRM, ERP, HR-systeem',
            status_open: 'Openen',
            status_in_progress: 'In uitvoering',
            status_awaiting_approval: 'Wacht op goedkeuring',
            status_approved: 'Goedgekeurd',
            status_remediation_required: 'Herstel vereist',
            status_closed: 'Gesloten',
            type_new_vendor: 'Nieuwe leverancier',
            type_service_change: 'Dienstwijziging',
            type_renewal: 'Verlenging',
            type_reassessment: 'Herbeoordeling',
            tab_overview: 'Overzicht',
            tab_due_diligence: 'Zorgvuldigheidsonderzoek',
            tab_security_assessment: 'Beveiligingsbeoordeling',
            tab_contractual_controls: 'Contractuele controles',
            tab_sub_processors: 'Subverwerkers',
            tab_approval_gates: 'Goedkeuringspoorten',
            tab_evidence: 'Bewijs',
            tab_placeholder: 'De inhoud van deze sectie wordt ingevuld tijdens het onboarding-proces.',
            completion_not_started: 'Niet gestart',
            completion_in_progress: 'In uitvoering',
            completion_completed: 'Voltooid',
            completion_requiring_attention: 'Aandacht vereist',
            gate_name: 'Poortnaam',
            approver: 'Goedkeurder',
            decision: 'Beslissing',
            comments: 'Opmerkingen',
            decide: 'Beslissing nemen',
            decision_approved: 'Goedgekeurd',
            decision_rejected: 'Afgewezen',
            decision_deferred: 'Uitgesteld',
            decision_pending: 'Pending',
            no_approvals: 'Geen goedkeuringspoorten geconfigureerd voor dit dossier.',
            approval_success: 'Goedkeuringsbeslissing succesvol vastgelegd',
            approval_error: 'Kon goedkeuringsbeslissing niet vastleggen',
            transition_status: 'Status wijzigen',
            confirm_transition: 'Dossier van "{from}" naar "{to}" wijzigen?',
            transition_success: 'Dossierstatus succesvol gewijzigd',
            transition_error: 'Kon dossierstatus niet wijzigen',
            create_success: 'Onboarding-dossier succesvol aangemaakt',
            create_error: 'Kon onboarding-dossier niet aanmaken',
            load_error: 'Kon onboarding-dossiers niet laden',
            detail_error: 'Kon dossierdetails niet laden',
            export: 'Exporteren',
            export_error: 'Kon onboarding-dossier niet exporteren',
            evidence_tab_description: 'Upload en exporteer bewijzen voor de onboardingzaak.',
            upload_evidence_label: 'Bewijs uploaden',
            attached_evidence_heading: 'Bijgevoegde bewijzen',
            no_evidence_attached: 'Nog geen bewijzen bijgevoegd.',
            export_bundle_button: 'Bewijsbundel exporteren',
            load_attachments_error: 'Bewijzen laden mislukt.',
            linked_questionnaires_heading: 'Gekoppelde vragenlijsten',
            no_linked_questionnaires: 'Geen gekoppelde vragenlijsten.',
            linked_scores_heading: 'Gekoppelde risicoscores',
            no_linked_scores: 'Geen gekoppelde scores.',
            latest_score_label: 'Laatste score',
            linked_contracts_heading: 'Gekoppelde contracten',
            no_linked_contracts: 'Geen gekoppelde contracten.',
            linked_sub_processors_heading: 'Gekoppelde subverwerkers',
            no_linked_sub_processors: 'Geen gekoppelde subverwerkers.',
            approval_created: 'Approval created',
            create_approval: 'Create Approval',
            export_bundle_success: 'Evidence bundle generated',
            gate_type: 'Gate Type',
            no_recent_bundles: 'No evidence bundles generated yet.',
            recent_bundles: 'Recent Bundles',
            tab_completed: 'Section marked complete',
            tab_gate_not_met: 'This section is already marked complete.',
            tab_linked_records_missing: 'This section cannot be marked complete yet: it needs at least one {kind}.',
            linked_kind_questionnaire: 'due-diligence questionnaire for this vendor that has not expired',
            linked_kind_risk_score: 'risk score for this vendor',
            linked_kind_contract: 'active contract with this vendor that is in force today',
            linked_kind_sub_processor: 'sub-processor entry recorded for this case',
            decide_again: 'Decide again',
            resolve_rejection: 'Resolve rejection',
            resolution_notes: 'Documented resolution',
            resolution_notes_hint: 'Required. Say how the reason for the rejection was addressed. The rejection stays in the gate history and the gate returns to undecided, to be decided again.',
            resolution_notes_required: 'Enter the resolution before reopening the gate.',
            resolve_success: 'Rejection resolved. The gate can be decided again.',
            resolve_error: 'The rejection could not be resolved.',
            prior_gate_blocked: 'An earlier gate is rejected or deferred. Decide it again, or resolve its rejection, first.',
            case_gate_blocked: 'The case cannot be approved while a gate is rejected or deferred.',
            case_gate_undecided: 'The case cannot be approved while a gate is still undecided. Decide every gate first.',
            case_final_gate_missing: 'The case cannot be approved until its final gate has been created and approved.',
            case_status_changed: 'The case or its gates changed while this was being saved. The case has been reloaded; check it and try again.',
            gate_already_decided: 'This gate has already been decided. Reload to see the decision.',
            deferred_gate_hint: 'Deferred: this gate and every gate after it wait until it is decided again.',
            mark_complete: 'Markeer als voltooid',
            earlier_decisions: 'Earlier decisions on this gate',
            history_entry: '{decision} by {name} on {date}.',
            history_notes: 'Notes: {notes}.',
            history_conditions: 'Conditions: {conditions}.',
            history_redecided: 'Decided again on {date}.',
            history_resolved: 'Rejection resolved by {name} on {date}: {notes}',
            history_unknown_person: 'a person not recorded',
            history_unknown_date: 'a date not recorded',
        },
        sub_processors: {
            appointment_decision: 'Decision to appoint anyway',
            appointment_decision_required: 'The DPA chain is not resolved. Record who is accepting that, and why.',
            appointment_decision_placeholder: 'Why is this sub-processor being appointed before the chain is in place?',
            title: 'Register van subverwerkers',
            page_title: 'Register van subverwerkers',
            new_record: 'Nieuwe subverwerker',
            edit_record: 'Subverwerker bewerken',
            onboarding_case: 'Onboarding-dossier',
            services: 'Geleverde diensten',
            data_categories: 'Gegevenscategorieën',
            processing_countries: 'Verwerkingslanden',
            dpa_chain_status: 'DPA-ketenstatus',
            certifications: 'Certificeringen',
            last_assessment: 'Laatste beoordeling',
            next_assessment: 'Volgende beoordeling',
            change_history: 'Wijzigingsgeschiedenis',
            history: 'Geschiedenis',
            changed_by: 'Gewijzigd door',
            comma_separated: 'Kommagescheiden waarden',
            filter_records: 'Registraties filteren',
            filter_by_dpa: 'Filteren op DPA-ketenstatus',
            all_dpa_statuses: 'Alle DPA-statussen',
            per_page: 'Registraties per pagina',
            records_count: 'subverwerkers',
            records_loaded: 'registraties geladen',
            no_records: 'Geen subverwerkers gevonden.',
            no_history: 'Geen wijzigingsgeschiedenis beschikbaar.',
            dpa_active: 'Actief',
            dpa_pending: 'In afwachting',
            dpa_expired: 'Verlopen',
            dpa_not_required: 'Niet vereist',
            create_success: 'Subverwerker succesvol aangemaakt',
            create_error: 'Kon subverwerker niet aanmaken',
            update_success: 'Subverwerker succesvol bijgewerkt',
            update_error: 'Kon subverwerker niet bijwerken',
            load_error: 'Kon register niet laden',
            history_error: 'Kon wijzigingsgeschiedenis niet laden',
            export_csv: 'Exporteer CSV',
            export_json: 'Exporteer JSON',
            export_error: 'Kon gegevens niet exporteren',
        },
    },
    deletion_orchestration: {
        nav: {
            dashboard: 'Orkestratieoverzicht',
            runs: 'Orkestratieruns',
            tasks: 'Uitvoeringstaken',
            templates: 'Checklistsjablonen',
            vendor_requests: 'Leveranciersverzoeken',
            proof_chain: 'Bewijsketen',
            dsr_integration: 'DSR-integratie',
            vendor_verification: 'Leveranciersverificatie',
            completeness: 'Volledigheidsoverzicht',
        },
        dashboard: {
            title: 'Orkestratieoverzicht',
            loading: 'Laden van dashboard gegevens...',
            active_runs: 'Actief Runs',
            completion_rate: 'Voltooiing Tarief',
            avg_completion_time: 'Avg Voltooiing Tijd',
            overdue_tasks: 'Te laat Taken',
            vendor_compliance: 'Leverancier Naleving',
            vendor_compliance_none: 'No vendor request sent in the last 90 days',
            recent_runs: 'Recente uitvoeringen',
            no_recent_runs: 'Geen recente orkestratie-uitvoeringen.',
            run_id: 'Uitvoeren ID',
            ticket_id: 'Ticket-ID',
            progress: 'Voortgang',
            systems: 'Systemen',
            vendors: 'Leveranciers',
            load_error: 'Kan het orkestratiedashboard niet laden. Probeer het opnieuw.',
            aria_table: 'Recente orkestratie-uitvoeringen',
        },
        runs: {
            title: 'Orkestratieruns',
            new: 'Nieuw Uitvoeren',
            run_id: 'Uitvoeren ID',
            ticket_id: 'Ticket-ID',
            progress: 'Voortgang',
            systems: 'Systemen',
            vendors: 'Leveranciers',
            data_subject_ref: 'Gegevens Onderwerp Ref',
            data_categories: 'Gegevens Categorieën',
            enable_proof_chain: 'Inschakelen Bewijs Keten',
            start: 'Starten',
            started: 'Het rennen is begonnen',
            mark_ready: 'Markeer gereed',
            marked_ready: 'Uitvoering gemarkeerd als gereed',
            complete: 'Compleet',
            complete_exceptions: 'Compleet met uitzonderingen',
            pause: 'Pauze',
            resume: 'Hervatten',
            cancel: 'Annuleer',
            no_runs: 'Geen orchestration runs gevonden.',
            load_error: 'Kan orkestratie-uitvoeringen niet laden. Probeer het opnieuw.',
            create_error: 'Kan orkestratierun niet maken',
            aria_filter_status: 'Filter op uitvoeringsstatus',
            aria_table: 'Orkestratie loopt',
            placeholder_ticket_id: 'bijvoorbeeld TKT-000123',
            placeholder_data_subject_ref: 'bijvoorbeeld DS-REF-001',
            placeholder_data_categories: 'bijvoorbeeld personal_data, financieel, gezondheid',
            cancel_confirm_title: 'Cancel this run?',
            cancel_confirm_body: 'Run {run} will be cancelled. A cancelled run cannot be started or resumed again.',
            cancel_reason_label: 'Reason (optional)',
            cancel_reason_hint: 'Recorded with the cancellation.',
            cancel_keep: 'Keep run',
            cancel_confirm: 'Cancel run',
            cancelling: 'Cancelling…',
            cancelled_success: 'Run cancelled',
            cancel_error: 'The run could not be cancelled.',
            title_label: 'Title',
            run_type: 'Run Type',
            run_type_labels: {
                data_subject_request: 'Data Subject Request',
                retention_expiry: 'Retention Expiry',
                manual: 'Manual',
                scheduled: 'Scheduled',
            },
            field_guidelines: {
                title: 'A descriptive name for this orchestration run.',
                run_type: 'The origin or trigger for the deletion process.',
                ticket_id: 'The associated ticket ID from your DSR or support system.',
                data_subject_ref: 'A unique identifier for the data subject (e.g., internal ID, email hash).',
                data_categories: 'Specific categories of personal data to be deleted (comma-separated).',
                proof_enabled: 'Maintains an immutable cryptographic chain of evidence for this run.',
            },
            actions: 'Actions',
            progress_guideline: 'Calculated based on completed system tasks and vendor requests.',
            placeholder_title: 'e.g., Q2 2026 Customer Deletion Batch',
            refusal_run_executing: 'This run is executing and cannot be cancelled. Pause it first, then cancel it.',
            refusal_invalid_status_transition: 'The run is no longer in a status that allows this change. The list has been refreshed.',
            refusal_run_status_changed: 'The run changed status while this was being saved. The list has been refreshed; check the run and try again.',
            refusal_cancel_permission_required: 'Cancelling a deletion run needs the permission to cancel deletion runs, which you do not hold.',
        },
        tasks: {
            title: 'Uitvoeringstaken',
            task_id: 'Taak ID',
            run_id: 'Uitvoeren ID',
            system_name: 'Systeem Naam',
            assigned_to: 'Toegewezen Naar',
            sla_deadline: 'SLA-deadline',
            progress: 'Voortgang',
            enter_run_id: 'Invoeren Uitvoeren ID naar filter...',
            select_run: 'Selecteer een run om taken te bekijken',
            unassigned: 'Niet toegewezen',
            overdue: 'Te laat',
            assign_task: 'Toewijzen Taak',
            assignee_id: 'Toegewezene ID',
            update_status: 'Bijwerken Status',
            new_status: 'Nieuw Status',
            no_transitions: 'Deze taak heeft een eindstatus en kan niet worden gewijzigd.',
            select_status_placeholder: 'Selecteer een nieuwe status',
            skip_justification: 'Reden voor overslaan',
            failure_reason: 'Reden van mislukking',
            no_tasks: 'Geen execution taken gevonden.',
            new_task: 'Nieuwe taak',
            system_id: 'Systeem-ID',
            data_classification: 'Gegevensclassificatie',
            load_error: 'execution tasks laden mislukt. Probeer het opnieuw.',
            assign_error: 'assign task mislukt',
            status_error: 'Bijwerken van taakstatus mislukt',
            no_tasks_for_run: 'Deze verwijderingsrun heeft nog geen uitvoeringstaken.',
            aria_filter_run_id: 'Filter op run-ID',
            aria_filter_status: 'Filter op taakstatus',
            aria_table: 'Uitvoeringstaken',
            view_steps: 'Stappen bekijken',
            steps_modal_title: 'Uitvoeringsstappen',
            step_title: 'Stap',
            step_status: 'Stapstatus',
            step_notes: 'Notities',
            step_evidence: 'Bewijsbestanden',
            step_evidence_required: 'Bewijs is vereist voor deze stap',
            step_evidence_missing: 'Nog geen bewijs geüpload',
            step_save: 'Stap opslaan',
            step_saved: 'Stap bijgewerkt',
            step_save_error: 'Stap bijwerken mislukt',
            create_success: 'Uitvoeringstaak aangemaakt',
            create_error: 'Uitvoeringstaak aanmaken mislukt',
            step_upload_label: 'Bewijs bijvoegen',
            // fix-sprint #431 (gate E12.5 remediation 1) — separation of duties on verification.
            verify_own_task: 'You are assigned to this task, so another person verifies it.',
            verify_separation_of_duties: 'You cannot verify a task you are assigned to: another person must verify it.',
        },
        templates: {
            title: 'Checklistsjablonen',
            new: 'Nieuw Sjabloon',
            template_id: 'Sjabloon ID',
            system_type: 'Systeem Type',
            est_duration: 'Est. Duur',
            steps: 'Stappen',
            action_type: 'Actie Type',
            instructions: 'Instructies',
            mandatory: 'Verplicht',
            add_step: 'Toevoegen Stap',
            step_n: 'Stap',
            no_templates: 'Geen checklist sjablonen gevonden.',
            all_system_types: 'Alle Systeem Types',
            load_error: 'Kan checklistsjablonen niet laden. Probeer het opnieuw.',
            create_error: 'Kan checklistsjabloon niet maken',
            aria_filter_system_type: 'Filter op systeemtype',
            aria_filter_status: 'Filter op sjabloonstatus',
            aria_table: 'Checklist-sjablonen',
            placeholder_name: 'bijvoorbeeld een checklist voor het verwijderen van databases',
            guideline_name: 'Use a clear operating name, for example “Database Deletion Checklist”.',
            guideline_system_type: 'Choose the system category this checklist will usually run against.',
            guideline_status: 'Draft templates can be reviewed before activation; active templates can be reused in runs.',
            guideline_description: 'Summarise scope, retention context, and when teams should select this template.',
            update_success: 'Sjabloon succesvol bijgewerkt',
            create_success_msg: 'Sjabloon succesvol aangemaakt',
            update_error: 'Sjabloon bijwerken mislukt',
            activate_success: 'Sjabloon succesvol geactiveerd',
            activate_error: 'Sjabloon activeren mislukt',
            archive_success: 'Sjabloon succesvol gearchiveerd',
            archive_error: 'Sjabloon archiveren mislukt',
            clone_success: 'Sjabloon succesvol gekloond',
            clone_error: 'Sjabloon klonen mislukt',
        },
        vendor_requests: {
            title: 'Leverancier Deletion Verzoeken',
            new: 'Nieuw Verzoek',
            run: 'Orchestration Run',
            no_run: 'Not part of a run',
            run_hint: 'Optional. A request recorded against a run counts toward that run’s progress.',
            request_id: 'Verzoek ID',
            vendor_name: 'Leverancier Naam',
            vendor_id: 'Leverancier ID',
            data_subject_ref: 'Gegevens Onderwerp Ref',
            data_categories: 'Gegevens Categorieën',
            legal_basis: 'Juridisch Grondslag',
            sla_days: 'SLA Dagen',
            requested_completion: 'Gevraagd Voltooiing',
            communications: 'Communicaties',
            total_requests: 'Totaal Verzoeken',
            no_requests: 'Geen leverancier verwijdering requests gevonden.',
            filter_vendor_id: 'Filteren op Leverancier ID...',
            change_status: 'Status Wijzigen',
            new_status: 'Nieuwe Status',
            actions: 'Acties',
            load_error: 'Kan verwijderingsverzoeken van leverancier niet laden. Probeer het opnieuw.',
            create_error: 'Kan een verwijderingsverzoek van de leverancier niet maken',
            dashboard_error: 'Kan het verwijderingsdashboard van de leverancier niet laden. Probeer het opnieuw.',
            aria_filter_status: 'Filter op aanvraagstatus',
            aria_filter_vendor_id: 'Filter op leveranciers-ID',
            aria_table: 'Verwijderingsverzoeken van leveranciers',
            placeholder_vendor_id: 'bijvoorbeeld VND-001',
            placeholder_vendor_name: 'bijvoorbeeld Acme Analytics',
            placeholder_data_subject_ref: 'bijvoorbeeld DS-REF-001',
            placeholder_data_categories: 'bijvoorbeeld personal_data, analyses',
            placeholder_legal_basis: 'bijv. PDPL Artikel 19',
            // fix-sprint #431 (gate E12.5 remediation 1) — record the vendor’s confirmation; a failed runs read.
            runs_read_error: 'The list of orchestration runs could not be read, so no run can be chosen. Retry, or record the request without a run.',
            confirm_action: 'Record confirmation',
            confirm_title: 'Record the vendor’s confirmation',
            confirm_intro: 'Record that {vendor} confirmed the deletion for request {request}. Recording it completes the request.',
            confirm_date: 'Date confirmed',
            confirm_method: 'How the vendor confirmed',
            confirm_method_choose: 'Choose how…',
            confirm_methods: {
                email: 'Email',
                portal: 'Vendor portal',
                letter: 'Letter',
                phone: 'Phone',
                other: 'Other',
            },
            confirm_contact: 'Vendor contact person',
            confirm_systems: 'Systems affected',
            confirm_systems_hint: 'Optional. Separate systems with commas.',
            confirm_notes: 'Notes',
            confirm_submit: 'Record confirmation',
            confirm_success: 'Vendor confirmation recorded; the request is completed.',
            confirm_refused_status: 'This request cannot receive a confirmation: it is a draft, cancelled or already completed.',
            confirm_missing_fields: 'Enter the date confirmed, how the vendor confirmed and the vendor’s contact person.',
            confirm_not_found: 'This request no longer exists.',
            confirm_error: 'The confirmation could not be recorded.',
            confirm_changed: 'Nothing was recorded: the request changed on the server while this confirmation was being saved. The list behind this dialog is read again; check the request there, and record the confirmation again if it still needs one.',
        },
        proof_chain: {
            title: 'Bewijs Keten Kijker',
            enter_run_id: 'Invoeren Uitvoeren ID...',
            load_chain: 'Laden Keten',
            verify_chain: 'Verifiëren Keten',
            verifying: 'Verifiëren...',
            chain_valid: 'Keten is valid',
            chain_invalid: 'Keten is broken',
            chain_length: 'Keten Length',
            broken_at_index: 'Gebroken bij index',
            no_proof_records: 'Geen bewijs records gevonden voor dit uitvoering..',
            proof_id: 'Bewijs ID',
            entity_type: 'Entiteit Type',
            entity_id: 'Entiteit ID',
            event_hash: 'Gebeurtenis Hash',
            previous_hash: 'Vorig Hash',
            attestation: 'Attestering',
            attestation_actor: 'Uitgevoerd door',
            attestation_signed_at: 'Ondertekend op',
            attestation_signature: 'Handtekening',
            attestation_evidence_refs: 'Bewijsreferenties',
            run_field_label: 'Verwijderingsrun',
            select_run: 'Selecteer een verwijderingsrun…',
            aria_select_run: 'Verwijderingsrun voor het opzoeken van de bewijsketen',
            chain_broken_here: 'Keten broken here',
            load_error: 'Kan de proefketting niet laden. Probeer het opnieuw.',
            verify_error: 'Kan de bewijsketen niet verifiëren. Probeer het opnieuw.',
            aria_run_id: 'Voer ID uit voor het opzoeken van de proefketen',
            // Sprint 62.35.0 — a displayed value the event hash does not cover.
            not_covered_by_hash: 'not covered by the hash',
            // Sprint 62.35.0 (AC8) — English until Phase 25 (R-57).
            destruction: {
                action: 'Record destruction',
                modal_title: 'Record destruction',
                intro: 'Identify the data this run destroyed. The record is added to the proof chain and covered by its hash, so it cannot be changed once saved.',
                section_title: 'Destruction record',
                name: 'Name',
                type: 'Type',
                size: 'Size (bytes)',
                format: 'Format',
                locations: 'Locations',
                locations_hint: 'One location per line. List every copy, including backups, replicas and exports.',
                identifier: 'Identifier',
                hash: 'Hash',
                hash_algorithm: 'Hash algorithm',
                hash_algorithm_hint: 'Required when a hash is given, for example SHA-256.',
                lawful_data_unaffected: 'Lawful data left unaffected',
                lawful_yes: 'Yes, lawful data was left unaffected',
                lawful_no: 'No, lawful data was affected',
                lawful_not_recorded: 'Not recorded',
                lawful_data_note: 'Note on lawful data',
                lawful_data_note_hint: 'Required when lawful data was affected.',
                yes: 'Yes',
                no: 'No',
                not_recorded: 'Not recorded',
                submit: 'Record destruction',
                saving: 'Recording…',
                saved: 'Destruction record added to the proof chain.',
                save_error: 'The destruction record could not be saved.',
                err_required: 'This field is required.',
                err_locations: 'Enter at least one location.',
                err_size: 'Enter a number of bytes, zero or more.',
                err_hash_algorithm: 'Enter the algorithm used to compute this hash.',
                err_algorithm_without_hash: 'Remove the algorithm, or enter the hash it was used for.',
                err_lawful_note: 'Explain how lawful data was affected.',
            },
        },
        dsr_integration: {
            change_status: 'Status wijzigen',
            request_type: 'Verzoektype',
            data_subject_hint: 'Identificatie van de betrokkene wiens gegevens worden gewist (bijv. e-mail of klant-ID). Komt meestal overeen met de aanvrager van de geselecteerde DSR-zaak.',
            title: 'DSR-verwijderingsintegratie',
            create_run: 'DSR-verwijderingsrun aanmaken',
            dsr_case_id: 'DSR-zaak-ID',
            data_subject_id: 'Betrokkene-ID',
            systems: 'Doelsystemen',
            route_to_department: 'Doorsturen naar afdeling',
            confirm_deletion: 'Verwijdering bevestigen',
            no_runs: 'Geen DSR-verwijderingsruns gevonden',
            status: 'Verwijderingsstatus',
            deadline: 'Deadline',
            no_further_status_changes: 'No further status changes',
            refusal_run_executing: 'This deletion run is in progress and cannot be cancelled. Complete it instead, with or without exceptions.',
        },
        vendor_verification: {
            title: 'Leveranciersverwijderingsverificatie',
            create_followup: 'Opvolging aanmaken',
            deadline: 'Bevestigingsdeadline',
            escalation_contact: 'Escalatiecontact',
            escalate: 'Escaleren',
            confirm_escalate_followup: 'Escalate this follow-up? Its status becomes Escalated. Nothing is sent to the vendor or the escalation contact.',
            no_followups: 'Geen leveranciersopvolgingen gevonden',
            vendor_request: 'Leveranciersverzoek',
            vendor: 'Leverancier',
            unknown_vendor: 'Onbekende leverancier',
            overdue_badge: 'Achterstallig',
            mark_confirmed: 'Markeren als bevestigd',
            mark_confirmed_for: 'Verwijdering als bevestigd markeren voor {vendor}',
            confirm_mark_followup_confirmed: 'Mark this follow-up confirmed? The vendor deletion request is not changed; record the vendor’s confirmation on Vendor Requests as well.',
            confirmation_success: 'Leveranciersverwijdering bevestigd',
            confirmation_failed: 'Kon leveranciersverwijdering niet bevestigen',
            select_request: 'Selecteer een leveranciersverzoek…',
            escalation_contact_placeholder: 'bijv. dpo@vendor.example',
            create_success: 'Leveranciersopvolging succesvol aangemaakt',
            create_failed: 'Kon leveranciersopvolging niet aanmaken',
            deadline_help: 'The date by which you expect the vendor to confirm. Every follow-up is listed; a pending one past this date is marked Overdue.',
            escalation_success: 'Leveranciersverwijdering succesvol geëscaleerd',
            escalation_failed: 'Escalatie van leveranciersverwijdering mislukt',
            overdue_filter: 'Overdue (pending, deadline passed)',
            status_labels: {
                pending: 'Pending',
                confirmed: 'Confirmed',
                escalated: 'Escalated',
            },
        },
        completeness: {
            title: 'Verwijderingsvolledigheid',
            check_conflicts: 'Bewaringsconflicten controleren',
            no_conflicts: 'Geen bewaringsconflicten gevonden',
            conflict_resolution: 'Conflictoplossing',
            extend_retention: 'Bewaring verlengen',
            proceed_deletion: 'Doorgaan met verwijdering',
            escalate_conflict: 'Escaleren',
            resolve: 'Conflict oplossen',
            data_subject_id: 'ID van de betrokkene',
            placeholder_data_subject_id: 'Voer de ID van de betrokkene in',
            data_subject_hint: 'Identificatie van de persoon van wie u de verwijdering controleert (bijv. e-mail, klant-ID of DSR-referentie). Kies een bekend subject of typ er een.',
            guidance: 'Voer een betrokkene-ID in of selecteer er een, kies optioneel systemen en voer de controle uit om de volledigheid van de verwijdering en bewaarconflicten te zien.',
            completion: 'Verwijdering voltooid',
            pending_items: 'Openstaande items',
            system: 'Systeem',
            policy: 'Beleid',
            retention_end: 'Retentie einde',
            completed_items: 'Voltooide Items',
            last_review_label: 'Laatste recensie',
            days_ago: 'dagen geleden',
            review_overdue: 'Beoordelingscyclus overschreden. Herbeoordeling vereist.',
            conflicts_not_checked: 'Retention conflicts were not checked because no system is selected. Choose at least one system and run the check again to see them.',
        },
        run_status_labels: {
            pending: 'In behandeling',
            planning: 'Plannen',
            ready: 'Gereed',
            in_progress: 'In Uitvoering',
            paused: 'Gepauzeerd',
            completed: 'Voltooid',
            completed_with_exceptions: 'Voltooid met Uitzonderingen',
            cancelled: 'Geannuleerd',
        },
        task_status_labels: {
            pending: 'In Afwachting',
            assigned: 'Toegewezen',
            in_progress: 'In Uitvoering',
            awaiting_verification: 'Wacht op Verificatie',
            verified: 'Geverifieerd',
            failed: 'Mislukt',
            skipped: 'Overgeslagen',
        },
        system_type_labels: {
            database: 'Database',
            saas_application: 'SaaS-applicatie',
            file_storage: 'Bestandsopslag',
            email_system: 'E-mailsysteem',
            analytics_platform: 'Analyseplatform',
            backup_system: 'Back-upsysteem',
            vendor_managed: 'Leverancierbeheerd',
            custom: 'Aangepast',
        },
        template_status_labels: {
            draft: 'Concept',
            active: 'Actief',
        },
        action_type_labels: {
            manual_check: 'Handmatige Controle',
            api_call: 'API-aanroep',
            sql_query: 'SQL-query',
            script_execution: 'Scriptuitvoering',
            file_deletion: 'Bestandsverwijdering',
            verification: 'Verificatie',
            notification: 'Melding',
            approval: 'Goedkeuring',
        },
        request_status_labels: {
            draft: 'Concept',
            sent: 'Verzonden',
            acknowledged: 'Bevestigd',
            in_progress: 'In Uitvoering',
            completed: 'Voltooid',
            overdue: 'Achterstallig',
            escalated: 'Geëscaleerd',
            disputed: 'Betwist',
            cancelled: 'Cancelled',
        },
        event_type_labels: {
            initiation: 'Initiatie',
            data_deletion: 'Gegevensverwijdering',
            completion: 'Voltooiing',
            verification: 'Verificatie',
            rollback: 'Terugdraaien',
        },
        count_labels: {
            one_template: '1 sjabloon',
            n_templates: '{n} sjablonen',
            one_run: '1 uitvoering',
            n_runs: '{n} uitvoeringen',
            one_task: '1 taak',
            n_tasks: '{n} taken',
            one_request: '1 verzoek',
            n_requests: '{n} verzoeken',
        },
        aria: {
            completion_rate: 'Voltooiingspercentage: {pct}%',
            vendor_compliance: 'Leveranciersconformiteit: {pct}%',
            run_progress: 'Uitvoeringsvoortgang: {pct}%',
            run_progress_systems: 'Voortgang: {completed} van {total} systemen verwerkt',
            chain_broken: 'Keten gebroken bij dit record',
            chain_valid: 'Keten geldig bij dit record',
            chain_unknown: 'Ketenstatus onbekend',
            escalate_vendor: 'Escaleer {id}',
            resolve_conflict: 'Conflict oplossen voor {system}',
        },
        regulatory: {
            annual_review_cycle_info: 'De volledigheid van de verwijdering moet ten minste elke 365 dagen worden beoordeeld. Records die deze drempel overschrijden, worden gemarkeerd voor verplichte herbeoordeling om naleving van de regelgeving te handhaven.',
            runs_data_d1_lifecycle_info: 'De statussen van de orkestratieuitvoering worden toegewezen aan de levenscyclus DATA-D1: planning = catalogus, klaar = classificeren, in_progress = regels toepassen, voltooid = bewijs gegenereerd, completed_with_exceptions = beoordeling vereist. Elke overgang wordt vastgelegd in de proefketen.',
            vendor_record_linkage_info: 'Elk leveranciersverwijderingsverzoek is gekoppeld aan het bijbehorende leveranciersrecord via vendor_id. Voor de voltooiingsbevestiging is cryptografisch bewijs uit de proefketen vereist voordat het verzoek als voltooid kan worden gemarkeerd.',
            templates_internal_classification_info: 'Alle sjablonen voor verwijderingscontrolelijsten zijn geclassificeerd als interne organisatiedocumenten. Ze zijn niet voorzien van officiële identificatieformulieren en mogen niet worden ingediend als registratiedocumenten.',
            proof_chain_evidence_info: 'In elk bewijsrecord worden de betrokkene/categorie, tijdstempel, actor, rechtsgrondslag en een SHA-256-hash vastgelegd. Samen produceren deze velden cryptografisch verwijderingsbewijs dat voldoet aan de wettelijke auditvereisten.',
            retention_data_law_info: "Bewaarschema's op dit dashboard zijn afgeleid van de Data Law-classificatie die aan elke gegevenscategorie is toegewezen. Wanneer een classificatie verandert, worden gekoppelde bewaarregels automatisch bijgewerkt om afstemming van de regelgeving te garanderen.",
            dsr_erasure_route_info: 'DSR-verwijderingsruns zijn gekoppeld aan de oorspronkelijke Rights Request-zaak. De wettelijke deadline (doorgaans 30 dagen volgens GDPR Art. 17) wordt automatisch bijgehouden. Achterstallige zaken worden gemarkeerd voor escalatie.',
        },
    },
    discovery: {
        nav: {
            dashboard: 'Ontdekkingsoverzicht',
            scans: 'Ontdekkingsscans',
            staging_area: 'Tussengebied',
            dedup_rules: 'Deduplicatieregels',
            classification_rules: 'Classificatieregels',
            suggestions: 'Suggestiewachtrij',
            analytics: 'Suggestieanalyse',
            import_templates: 'Importsjablonen',
            connectors: 'Connectorbeheer',
            discovery_cycles: 'Ontdekkingscycli',
            owner_review: 'Eigenaarsbeoordeling',
            classification_lineage: 'Classificatiehistorie',
        },
        dashboard: {
            title: 'Ontdekkingsoverzicht',
            loading: 'Laden van dashboard gegevens...',
            total_scans: 'Totaal Scans',
            active_scans: 'Actief Scans',
            completed_30d: 'Voltooid (30d)',
            failed_30d: 'Mislukt (30d)',
            systems_discovered: 'Systemen Ontdekt',
            pending_staged_records: 'In afwachting Staged Records',
            recent_scans: 'Recente scans',
            no_recent_scans: 'Geen recente scans.',
            scan_id: 'Scannen ID',
            discovered: 'Ontdekt',
            view_all_scans: 'Bekijken Alle Scans',
            staging_area: 'Staginggebied',
            suggestion_queue: 'Suggestie Wachtrij',
            records_reviewed: 'Beoordeelde records',
            records_approved: 'Goedgekeurde records',
            exceptions_pending: 'Openstaande uitzonderingen',
            view_as_chart: 'Als diagram weergeven',
            view_as_table: 'Als tabel weergeven',
        },
        scans: {
            title: 'Ontdekkingsscans',
            new: 'Nieuw Scannen',
            scan_id: 'Scannen ID',
            scan_type: 'Scannen Type',
            progress: 'Voortgang',
            discovered: 'Ontdekt',
            no_scans: 'Geen discovery scans gevonden.',
            all_scan_types: 'Alle Scannen Types',
            source_config: 'Bron Configuratie (JSON)',
            invalid_json: 'Ongeldig JSON in source configuratie',
            load_error: 'Kan niet load discovery scans. Probeer het opnieuw.',
            create_error: 'Kan niet create discovery scan',
            change_status: 'Wijzig status',
            new_status: 'Nieuwe status',
            actions: 'Acties',
            type_endpoint: 'Eindpunt',
            type_spreadsheet_import: 'Spreadsheet-import',
            type_cmdb_sync: 'CMDB-synchronisatie',
            type_cloud_sync: 'Cloud-synchronisatie',
            status_pending: 'In afwachting',
            status_running: 'Wordt uitgevoerd',
            status_completed: 'Voltooid',
            status_failed: 'Mislukt',
            status_cancelled: 'Geannuleerd',
            source_config_placeholder: '{"host": "...", "poort": 443}',
            adapter_not_implemented_toast: 'Dit scantype is nog niet beschikbaar. Alleen endpointscans kunnen worden aangemaakt.',
            adapter_not_implemented_warning: 'Nog niet beschikbaar. Scans van dit type kunnen niet worden aangemaakt totdat ondersteuning ervoor is uitgebracht.',
            source_config_endpoint_help: 'Provide a list of FQDNs in the "domains" array. The worker resolves DNS (CNAME / MX / TXT / A) and probes HTTPS / HTTP for each domain.',
            source_config_example_label: 'Example:',
            source_config_spreadsheet_help: 'Provide the file_id of a previously uploaded CSV / XLSX and the template_id of the import template whose column mappings apply.',
            placeholder_source_config: '{"domains": ["example.com"]}',
        },
        staging_area: {
            title: 'Tussengebied',
            record_id: 'Record-ID',
            source_scan: 'Bron Scannen',
            entity_type: 'Entiteit Type',
            suggested_name: 'Voorgesteld Naam',
            confidence: 'Betrouwbaarheid',
            no_records: 'Geen staged records gevonden.',
            promote: 'Promoveren',
            discard: 'Verwerpen',
            review: 'Beoordelen',
            merge: 'Samenvoegen',
            merge_title: 'Record samenvoegen',
            merge_target_id: 'Samenvoegen Doel ID',
            reject_reason_placeholder: 'Reden voor rejection...',
            promotion_status: 'Promotie Status',
            dedup_status: 'Deduplicatiestatus',
            all_source_types: 'Alle Bron Types',
            all_promotion_statuses: 'Alle Promotie Statuses',
            all_dedup_statuses: 'Alle Dedup Statuses',
            load_error: 'Kan niet load staged records. Probeer het opnieuw.',
            promote_error: 'Kan niet promote record',
            reject_error: 'Kan niet reject record',
            merge_error: 'Kan niet merge record',
            review_error: 'Kan niet mark record voor beoordeling',
            bulk_promote_error: 'Kan niet bulk promote records',
            bulk_reject_error: 'Kan niet bulk reject records',
            service_type_web_server: 'Webserver',
            service_type_database: 'Database',
            service_type_api_gateway: 'API-gateway',
            service_type_storage: 'Opslag',
            service_type_authentication: 'Authenticatie',
            service_type_analytics: 'Analyse',
            service_type_unknown: 'Onbekend',
            source_type_endpoint_scan: 'Eindpuntscan',
            source_type_spreadsheet: 'Spreadsheet',
            source_type_cmdb: 'CMDB',
            source_type_cloud_inventory: 'Cloud-inventaris',
            promotion_status_new: 'Nieuw',
            promotion_status_under_review: 'In beoordeling',
            promotion_status_promoted: 'Gepromoveerd',
            promotion_status_rejected: 'Afgewezen',
            promotion_status_merged: 'Samengevoegd',
            dedup_status_unmatched: 'Geen overeenkomst',
            dedup_status_potential_match: 'Mogelijke overeenkomst',
            dedup_status_exact_match: 'Exacte overeenkomst',
            dedup_status_resolved: 'Opgelost',
            confirm_bulk_reject: 'Weet u zeker dat u de geselecteerde records wilt afwijzen?',
            scan_batch_id: 'Batch-ID',
            promote_to_registry_title: 'Promoten naar gegevensregister',
            promote_modal_instructions: 'Creëert een gegevensregisteritem met collection_method: waargenomen en herkomstkoppeling naar dit ontdekkingsrecord. Het item begint in de conceptstatus ter beoordeling.',
            promote_label_category: 'Categorie',
            promote_label_name_override: 'Naam (optionele overschrijving)',
            placeholder_name_override: 'Standaard ingesteld op de naam/hostnaam van het ontdekkingsrecord',
            promote_label_sensitivity: 'Voorgestelde gevoeligheid',
            inherit_from_category: 'Erven van categorie',
            dedup_status_help: 'Whether this discovered item already exists in inventory. Unmatched = safe to promote; Potential / Exact Match = review or merge first.',
            promote_to_registry: 'Promote to Registry',
            promotion_status_help: 'Where this record is in the review pipeline: New → Under Review → Promoted (or Rejected / Merged).',
            route_to_owner: 'Route to Owner',
            route_to_owner_error: 'Failed to route to owner.',
            route_to_owner_success: 'Owner review task created. Open Owner Review to resolve it.',
            service_type: 'Service Type',
        },
        dedup_rules: {
            title: 'Deduplicatieregels',
            new: 'Nieuw Regel',
            rule_name: 'Regel Naam',
            match_fields: 'Matchen Velden',
            threshold: 'Drempelwaarde',
            no_rules: 'Geen dedup regels gevonden.',
            seed_defaults: 'Initialiseren Standaardwaarden',
            match_type: 'Matchen Type',
            similarity_threshold: 'Gelijkenis Drempelwaarde',
            field_name: 'Veld Naam',
            load_error: 'Kan niet load dedup regels. Probeer het opnieuw.',
            create_error: 'Kan niet create dedup regel',
            toggle_error: 'Kan niet toggle dedup regel',
            seed_error: 'Kan niet seed standaardwaarden',
            priority: 'Prioriteit',
            enabled_status: 'Ingeschakeld',
            disabled_status: 'Uitgeschakeld',
            default_badge: 'Standaard',
            enable: 'Inschakelen',
            disable: 'Uitschakelen',
            match_type_exact: 'Exact',
            match_type_fuzzy: 'Onscherp',
            match_type_regex: 'Regex',
            match_type_ip_range: 'IP-bereik',
            threshold_validation: 'Must be a number between 0 and 1',
        },
        classification_rules: {
            title: 'Classificatieregels',
            new: 'Nieuw Regel',
            rule_name: 'Regel Naam',
            pattern: 'Patroon',
            classification: 'Classificatie',
            no_rules: 'Geen classification regels gevonden.',
            seed_defaults: 'Initialiseren Standaardwaarden',
            run_rules_now: 'Voer nu regels uit',
            run_rules_success: '{count} nieuwe classificatiesuggestie(s) gegenereerd',
            run_rules_no_results: 'Er zijn geen nieuwe suggesties gegenereerd: voor alle overeenkomende regels zijn al suggesties in behandeling',
            run_rules_error: 'Kan classificatieregels niet uitvoeren. Probeer het opnieuw.',
            taxonomy_id: 'Taxonomie ID',
            level_id: 'Niveau ID',
            condition_logic: 'Voorwaarde Logic',
            weight: 'Gewicht',
            conditions: 'Voorwaarden',
            condition_field: 'Veld',
            condition_operator: 'Bewerking',
            condition_value: 'Waarde',
            effectiveness: 'Effectiviteit',
            match_count: 'Overeenkomsten',
            approval_count: 'Goedkeuringen',
            rejection_count: 'Afwijzingen',
            filter_taxonomy: 'Filteren op Taxonomie ID...',
            load_error: 'Kan niet load classification regels. Probeer het opnieuw.',
            create_error: 'Kan niet create classification regel',
            seed_error: 'Kan niet seed standaardwaarden',
            toggle_error: 'Kan niet toggle regel. Probeer het opnieuw.',
            priority: 'Prioriteit',
            enabled_status: 'Ingeschakeld',
            disabled_status: 'Uitgeschakeld',
            enable: 'Inschakelen',
            disable: 'Uitschakelen',
            logic_and: 'EN',
            logic_or: 'OF',
            operator_equals: 'Gelijk aan',
            operator_contains: 'Bevat',
            operator_matches_regex: 'Komt overeen met Regex',
            operator_in_list: 'In lijst',
            operator_not_equals: 'Niet gelijk aan',
            operator_not_contains: 'Bevat niet',
            enabled_label: 'Ingeschakeld',
            disabled_label: 'Uitgeschakeld',
            taxonomy_label: 'Taxonomie',
            operator_label: 'Exploitant',
            value_label: 'Waarde',
            conditions_description: 'Define the signals that must match before this classification is applied.',
        },
        suggestions: {
            title: 'Suggestiewachtrij',
            entity_type: 'Entiteit Type',
            suggested_name: 'Voorgesteld Naam',
            source: 'Bron',
            confidence: 'Betrouwbaarheid',
            accept: 'Accepteren',
            reject: 'Afwijzen',
            no_suggestions: 'No suggesties in queue.',
            modify_approve: 'Wijzigen & Goedkeuren',
            delegate: 'Delegeren',
            delegate_user_id: 'Delegeren Gebruiker ID',
            new_level_id: 'Nieuw Niveau ID',
            reject_reason_placeholder: 'Reden voor rejection...',
            current_level: 'Huidig Niveau',
            suggested_level: 'Voorgesteld Niveau',
            filter_taxonomy: 'Filteren op Taxonomie ID...',
            all_confidence: 'Alle Betrouwbaarheid Bands',
            load_error: 'Kan niet load suggesties. Probeer het opnieuw.',
            approve_error: 'Kan niet approve suggestie',
            reject_error: 'Kan niet reject suggestie',
            modify_error: 'Kan niet modify & approve suggestie',
            delegate_error: 'Kan niet delegate suggestie',
            bulk_approve_error: 'Kan niet bulk approve suggesties',
            bulk_reject_error: 'Kan niet bulk reject suggesties',
            suggestion_type_new_label: 'Nieuw label',
            suggestion_type_validation: 'Validatie',
            suggestion_type_reclassification: 'Herclassificatie',
            status_pending: 'In afwachting',
            status_approved: 'Goedgekeurd',
            status_rejected: 'Afgewezen',
            status_superseded: 'Vervangen',
            confidence_high: 'Hoog',
            confidence_medium: 'Gemiddeld',
            confidence_low: 'Laag',
            confirm_bulk_reject: 'Weet u zeker dat u de geselecteerde suggesties wilt afwijzen?',
            unknown_level_tooltip: 'This rule references a level that is no longer defined in the taxonomy.',
            all_taxonomies: 'Alle Taxonomieën',
        },
        analytics: {
            title: 'Suggestieanalyse',
            total_suggestions: 'Totaal Suggesties',
            accepted: 'Geaccepteerd',
            rejected: 'Afgewezen',
            pending: 'In afwachting',
            acceptance_rate: 'Acceptance Tarief',
            by_entity_type: 'By Entiteit Type',
            by_source: 'By Bron',
            avg_decision_time: 'Avg Beslissing Tijd',
            rule_effectiveness: 'Regel Effectiviteit',
            by_status: 'Per status',
            by_confidence: 'By Betrouwbaarheid Band',
            load_error: 'Kan niet load suggestie analytics. Probeer het opnieuw.',
            confidence_high: 'Hoog',
            confidence_medium: 'Gemiddeld',
            confidence_low: 'Laag',
            by_confidence_band: 'Per betrouwbaarheidsniveau',
            approval_rate: 'Goedkeuringspercentage',
            by_label_state: 'Per labelstatus',
            no_data_hint: 'No suggestions yet. Promote a staged record or click "Run Rules Now" on the Classification Rules page to generate the first batch.',
            rejection_rate: 'Rejection Rate',
        },
        import_templates: {
            title: 'Importsjablonen',
            new: 'Nieuw Sjabloon',
            template_name: 'Sjabloon Naam',
            file_format: 'Bestand Formaat',
            column_mappings: 'Kolom Toewijzingen',
            no_templates: 'Geen import sjablonen gevonden.',
            source_column: 'Bron Kolom',
            target_field: 'Doel Veld',
            transform: 'Transformeren',
            source_column_label: 'Bronkolom',
            target_field_label: 'Doelveld',
            transform_label: 'Transformeren',
            confirm_delete: 'Are you sure you want naar delete dit import sjabloon?',
            load_error: 'Kan niet load import sjablonen. Probeer het opnieuw.',
            save_error: 'Kan niet save import sjabloon',
            delete_error: 'Kan niet delete import sjabloon',
            file_type_csv: 'CSV',
            file_type_xlsx: 'XLSX',
        },
        connectors: {
            title: 'Connectorbeheer',
            new: 'Nieuw Connector',
            connector_name: 'Connector Naam',
            connector_type: 'Soort',
            last_sync: 'Laatste Synchronisatie',
            no_connectors: 'Geen connectors gevonden.',
            cmdb_syncs: 'CMDB Synchronisaties',
            cloud_syncs: 'Cloud Synchronisaties',
            new_sync: 'Nieuw Synchronisatie',
            no_syncs: 'Geen syncs gevonden.',
            cmdb_error: 'Kan niet load CMDB scans. Probeer het opnieuw.',
            cloud_error: 'Kan niet load cloud scans. Probeer het opnieuw.',
            trigger_error: 'Kan niet trigger synchronisatie',
            trigger_sync_error: 'trigger {type} mislukt',
            cmdb_section_title: 'CMDB-synchronisaties',
            cloud_section_title: 'Cloudsynchronisaties',
            column_connector: 'Connector',
            action_start: 'Starten',
            confirm_cancel_sync: 'Deze synchronisatie annuleren?',
            start_error: 'Synchronisatie starten is mislukt',
            cancel_error: 'Synchronisatie annuleren is mislukt',
            type_endpoint: 'Endpoint-scan',
            type_spreadsheet_import: 'Spreadsheet-import',
            type_cmdb_sync: 'CMDB-synchronisatie',
            type_cloud_sync: 'Cloudsynchronisatie',
        },
        discovery_cycles: {
            title: 'Ontdekkingscycli',
            new_cycle: 'Nieuwe ontdekkingscyclus',
            cycle_name: 'Cyclusnaam',
            scope_type: 'Bereiktype',
            scope_ids: "Bereik-ID's",
            cadence_days: 'Frequentie (dagen)',
            status: 'Status',
            no_cycles: 'Geen ontdekkingscycli gevonden',
            compare: 'Cycli vergelijken',
            delta_report: 'Wijzigingsrapport',
            new_records: 'Nieuwe records',
            changed_records: 'Gewijzigde records',
            removed_records: 'Verwijderde records',
            schedule: 'Scan plannen',
            scope_organization: 'Organisatie',
            scope_department: 'Afdeling',
            scope_system: 'Systeem',
            scope_systems: 'Systemen',
            bulk_actions: 'Bulkacties',
            selected: '{count} geselecteerd',
            bulk_create: 'Bulk aanmaken',
            bulk_assign_scope: 'Bulk bereik toewijzen',
            bulk_advance: 'Bulk status bevorderen',
            confirm_bulk_action: 'Bulkactie bevestigen',
            bulk_confirm_message: 'Weet u zeker dat u deze actie wilt uitvoeren?',
            cycles_affected: '{count} cycli worden beïnvloed',
            select_all: 'Alles selecteren',
            select_cycle: 'Cyclus {name} selecteren',
            from_template: 'vanuit sjabloon',
            bulk_results: 'Bulkresultaten',
            failed_label: 'mislukt',
            template_id: 'Sjabloon-ID',
            count: 'Aantal',
            advance_to: 'Doelstatus',
            bulk_success: 'Bulkbewerking voltooid',
        },
        owner_review: {
            title: 'Eigenaarsbeoordelingstaken',
            assign_owner: 'Toewijzen aan eigenaar',
            owner_id: 'Eigenaar',
            decision: 'Beslissing',
            approve: 'Goedkeuren',
            modify: 'Wijzigen',
            reject: 'Afwijzen',
            escalate: 'Escaleren',
            no_tasks: 'Geen beoordelingstaken gevonden',
            bulk_resolve: 'Massaoplossing',
            exceptions: 'Uitzonderingsposten',
            no_exceptions: 'Geen uitzonderingsposten gevonden',
            record_id_label: 'Record-ID',
            selected: 'geselecteerd',
            notes: 'Notities',
            select_all: 'Alles Selecteren',
            select: 'Selecteren',
            confirm_resolve: 'Bevestigen',
        },
        classification_lineage: {
            title: 'Classificatiehistorie',
            link_suggestion: 'Koppelen aan voorgesteld label',
            suggestion_id: 'Suggestie',
            proposed_label_id: 'Voorgesteld label',
            no_lineage: 'Geen historiegegevens gevonden',
            full_lineage: 'Volledige historie',
            record_id_label: 'Record-ID',
            staged_record_label: 'Geënsceneerde opname',
            enter_staged_record_id: 'Voer de geënsceneerde record-ID in',
            scan_id_label: 'Scan-ID',
            select_record: 'Selecteer een record',
            select_suggestion: 'Selecteer een suggestie',
            select_label: 'Selecteer een label',
        },
        common_labels: {
            n_rules: '{n} regels',
            one_rule: '1 regel',
            n_scans: '{n} scant',
            one_scan: '1 scannen',
            n_syncs: '{n} synchroniseert',
            one_sync: '1 synchronisatie',
            n_records: '{n}-records',
            one_record: '1 plaat',
            n_templates: '{n} sjablonen',
            one_template: '1 sjabloon',
            n_suggestions: '{n} suggesties',
            one_suggestion: '1 suggestie',
            n_selected: '{n} geselecteerd',
            dashboard_loaded: 'Discovery-dashboard geladen',
            n_scans_loaded: '{n} scans geladen',
            n_staged_records_loaded: '{n} geënsceneerde records geladen',
            confirm_delete_import_template: 'Weet u zeker dat u dit importsjabloon wilt verwijderen?',
            invalid_json_source_config: 'Ongeldige JSON in source_config',
            scope_organization: 'Organisatie',
            scope_department: 'Afdeling',
            scope_system: 'Systeem',
            aria_filter_scans: 'Filterscans',
            aria_filter_staged_records: 'Filter geënsceneerde records',
            aria_filter_suggestions: 'Filters',
            aria_filter_by_taxonomy_id: 'Filter op taxonomie-ID',
            aria_filter_by_enabled_status: 'Filter op ingeschakelde status',
            aria_filter_by_source_type: 'Filter op brontype',
            aria_filter_by_promotion_status: 'Filter op promotiestatus',
            aria_filter_by_dedup_status: 'Filter op ontdubbelingsstatus',
            aria_filter_by_scan_type: 'Filter op scantype',
            aria_filter_by_status: 'Filter op status',
            aria_filter_by_confidence: 'Filter op vertrouwensband',
            aria_select_all_records: 'Selecteer alle records',
            aria_select_record: 'Selecteer record {id}',
            aria_select_all_suggestions: 'Selecteer alle suggesties',
            aria_select_suggestion: 'Selecteer suggestie {id}',
            aria_promote_record: 'Record promoten {id}',
            aria_discard_record: 'Record verwijderen {id}',
            aria_merge_record: 'Record samenvoegen {id}',
            aria_review_record: 'Reviewrecord {id}',
            aria_accept_suggestion: 'Suggestie accepteren {id}',
            aria_reject_record: 'Opname weigeren',
            aria_merge_record_dialog: 'Record samenvoegen',
            aria_create_new_scan: 'Nieuwe scan maken',
            aria_sync_progress: 'Synchronisatievoortgang: {processed} van {total} items verwerkt',
            aria_scan_progress: 'Scanvoortgang: {processed} van {total} items verwerkt',
            aria_confidence: 'Vertrouwen: {pct}%',
            aria_effectiveness: 'Effectiviteit: {pct}',
            aria_promote_n_records: 'Promoot {n} geselecteerde records',
            aria_discard_n_records: 'Gooi {n} geselecteerde records weg',
            placeholder_pii_detection_rule: 'bijvoorbeeld PII-detectieregel',
            placeholder_tax_id: 'bijvoorbeeld TAX-001',
            placeholder_lvl_id: 'bijvoorbeeld LVL-001',
            placeholder_service_type: 'bijvoorbeeld service_type',
            placeholder_database: 'bijvoorbeeld een database',
            placeholder_hostname_match: 'bijvoorbeeld Exacte match hostnaam',
            placeholder_hostname: 'bijvoorbeeld hostnaam',
            placeholder_hr_systems_csv: 'bijvoorbeeld HR-systemen CSV Importeren',
            placeholder_server_name: 'bijvoorbeeld servernaam',
            placeholder_hostname_field: 'bijvoorbeeld hostnaam',
            placeholder_lowercase: 'bijvoorbeeld kleine letters',
            placeholder_source_config: '{"host": "...", "poort": 443}',
            placeholder_stg_id: 'bijvoorbeeld STG-000123',
            placeholder_lvl_002: 'bijvoorbeeld LVL-002',
            placeholder_usr_id: 'bijvoorbeeld USR-000123',
            decision_for: 'Beslissing voor {id}',
        },
        exports: {
            title: 'Exporteren',
            inventory: 'Data-inventaris',
            classification_register: 'Classificatieregister',
            raci: 'RACI-matrix',
            exceptions: 'Uitzonderingenrapport',
            evidence_pack: 'Bewijspakket',
            delta_report: 'Wijzigingsrapport',
            executive_summary: 'Managementsamenvatting',
            export_button: 'Exporteren',
            downloading: 'Downloaden...',
            download_complete: 'Download voltooid.',
            download_error: 'Export-download mislukt. Probeer het opnieuw.',
        },
        flags: {
            title: 'Downstream-markeringen',
            pending: 'Openstaande markeringen',
            cross_border_review_needed: 'Grensoverschrijdende beoordeling vereist',
            sharing_disclosure_needed: 'Deelingsmelding vereist',
            dpia_review_needed: 'DPIA-beoordeling vereist',
            acknowledge: 'Bevestigen',
            resolve: 'Oplossen',
            no_flags: 'Geen downstream-markeringen.',
        },
        delta: {
            title: 'Wijzigingsoverzicht',
            new_count: '{n} nieuw',
            removed_count: '{n} verwijderd',
            changed_count: '{n} gewijzigd',
            re_review_triggered: 'Herbeoordeling geactiveerd',
            create_re_review: 'Herbeoordeling aanmaken',
            recent_alerts: 'Recente wijzigingswaarschuwingen',
            no_alerts: 'Geen recente wijzigingswaarschuwingen.',
            threshold_exceeded: 'Wijzigingsdrempel overschreden (>10%)',
        },
        regulatory: {
            dashboard_governance_info: "Dit dashboard toont de bestuursstatus op cyclusniveau: gescande systemen, geënsceneerde records, beoordeelde records, goedgekeurde records en in behandeling zijnde uitzonderingen. Dit komt overeen met de succescriteria voor governance-leads: 'live dashboard van wie zijn inventaris, classificatie en deellogboeken heeft bijgewerkt' (P-VN-05).",
            export_artifacts_info: 'Discovery-exports omvatten alle vereiste artefacttypen per UC-VN-10 en DATA-EVD-01: goedgekeurde gegevensinventarisatie, classificatieregister, eigendoms-/RACI-toewijzing, uitzonderingswachtrij, samenvatting, bewijspakket en deltarapport.',
            scan_inventory_pipeline_info: 'Elke voltooide scan produceert records die compatibel zijn met de vn_data_law_data_inventory_register-sjabloon (vereiste 88dd9877). Ontdekte data-assets vullen automatisch het data-inventarisregister in om te voldoen aan de verplichting van de Data Law om een ​​digitale data-inventaris bij te houden.',
            connector_linkage_info: 'Elke connectorconfiguratie moet het volgende bevatten: systeemtype (bijvoorbeeld CMDB, cloudplatform), gegevensopslagreferentie (database, bestandsopslag, SaaS) en koppeling van bedrijfsprocessen (UC-VN-10 vereiste ondersteunende records). Deze velden zorgen ervoor dat ontdekte assets traceerbaar zijn naar hun bronsysteem en bedrijfscontext.',
            owner_routing_info: 'Controletaken worden doorgestuurd naar de juiste afdeling of applicatie-eigenaar op basis van systeem-/gegevenseigendom (UC-VN-10 stap 5). Routing maakt gebruik van het systeemeigendomsregister en niet van ad-hoctoewijzing.',
            owner_actions_audit_info: 'Eigenaren kunnen elke classificatie goedkeuren, wijzigen, afwijzen of escaleren (UC-VN-10 stap 6). Bij elke actie worden de actor, het tijdstempel en de reden vastgelegd, waardoor er gebeurtenissen uit de governance-audit worden geproduceerd per vereiste f12fc090 (jaarlijkse governance-attest). Het audittraject laat zien wie heeft beoordeeld, wat er is gewijzigd en wanneer.',
            escalation_timers_info: 'Escalatietimers: deadline voor beoordeling door eigenaar is 5 werkdagen; escalatie van uitzonderingen is 48 uur (UC-VN-10 timers). Onopgeloste problemen worden automatisch geëscaleerd wanneer de deadline is verstreken. Timers zijn configureerbaar per organisatiebeleid.',
            original_suggestion_preserved_info: 'De oorspronkelijke suggestie voor machine-/systeemclassificatie blijft behouden naast elke wijziging door de eigenaar (UC-VN-10 bewijsvereiste). De oorspronkelijke suggestie wordt nooit overschreven en blijft zichtbaar voor auditdoeleinden.',
            dedup_lineage_info: 'Ontdubbelingsregels voorkomen dubbele inventarisinvoer wanneer hetzelfde gegevensmiddel wordt ontdekt via meerdere scans of connectoren. De meest recente scanmetagegevens worden bewaard als het primaire record, terwijl de afstammingsreferenties naar alle eerdere ontdekkingen behouden blijven voor de traceerbaarheid van de audit.',
            label_states_info: 'Analytics onderscheidt vier labelstatussen voor governancebeoordeling: door de machine voorgesteld (in behandeling), door de eigenaar goedgekeurd, door de eigenaar gewijzigd en afgewezen. Deze toestanden stellen auditors in staat de nauwkeurigheid van de automatisering en de betrokkenheid van de eigenaar te beoordelen.',
            import_pipeline_info: 'Importsjablonen moeten records produceren die compatibel zijn met de detectiepijplijn. Geïmporteerde records worden in de fasering geplaatst met dezelfde metadatavereisten als gescande records: bronsysteemidentificatie, batch-ID en tijdstempel. Kolomtoewijzingen moeten zich richten op velden die zijn gedefinieerd in vn_data_law_data_inventory_register.',
            lineage_full_path_info: 'De classificatielijn volgt het volledige pad: scannen/importeren -> staging -> eigenaarbeoordeling -> goedgekeurd label -> registerinvoer (UC-VN-10 stappen 1-10). Elke fase toont record-ID, acteur, tijdstempel en statusovergang voor volledige audittraceerbaarheid.',
            staging_workflow_info: 'Geënsceneerde records worden visueel onderscheiden van goedgekeurde voorraadinvoeren door hun promotiestatusbadge (UC-VN-10 stap 3). Elk record behoudt metagegevens over de herkomst: bronsysteem, scanbatch-ID en ontdekkingstijdstempel voor volledige audittraceerbaarheid.',
            suggestion_data_d1_info: 'Suggesties komen overeen met de datawetclassificatieprocedure DATA-D1: (1) catalogiseer gegevensassets, (2) classificeer op gevoeligheidsniveau, (3) definieer verwerkingsregels. Elke suggestie bevat gevoeligheids- en belangrijkheidsdimensies per vereiste 18e4bfd1.',
            downstream_flags_info: 'Items die zijn gemarkeerd voor delen, grensoverschrijdende overdracht of behandeling met een hoog risico activeren gekoppelde stroomafwaartse workflows: beoordeling van grensoverschrijdende overdracht, beoordeling van gedeeld risico en DPIA beoordeling (UC-VN-10 stap 8).',
            delta_detection_info: 'Ontdekkingscycli ondersteunen deltadetectie per UC-VN-10 stap 11: elke cyclus laat zien welke records nieuw, gewijzigd of verwijderd zijn sinds de laatste goedgekeurde basislijn. Materiële wijzigingen (>10% delta) activeren automatisch herbeoordelingsworkflows.',
        },
    },
    incident_ops: {
        errors: {
            fetch: 'Kan niet load..',
            save: 'Kan niet save..',
            fetch_escalation_log_failed: 'Kan niet fetch escalatie logboek..',
            fetch_activations_failed: 'Kan niet fetch activations..',
            deactivate_failed: 'Kan niet deactivate..',
            complete_activation_failed: 'Kan niet voltooid activation..',
            fetch_playbook_templates_failed: 'Kan niet fetch draaiboek sjablonen..',
            create_playbook_template_failed: 'Kan niet create draaiboek sjabloon..',
            clone_template_failed: 'Kan niet clone sjabloon..',
            update_status_failed: 'Kan de status niet bijwerken.',
            deprecate_template_failed: 'Kan niet deprecate sjabloon..',
            seed_defaults_failed: 'Kan niet seed standaardwaarden..',
            fetch_regulator_exports_failed: 'Kan niet fetch regulator exports..',
            create_regulator_export_failed: 'Kan niet create regulator export..',
            update_submission_status_failed: 'Kan niet update indiening status..',
            fetch_response_metrics_failed: 'Kan niet fetch response metrieken..',
            fetch_sla_dashboard_failed: 'Kan niet fetch SLA dashboard..',
            fetch_sla_definitions_failed: 'Kan niet fetch SLA definities..',
            create_sla_definition_failed: 'Kan niet create SLA definitie..',
            seed_sla_defaults_failed: 'Kan niet seed SLA standaardwaarden..',
            fetch_vendor_coordination_failed: 'Kan niet fetch leverancier coördinatie..',
            fetch_vendor_detail_failed: 'Kan niet fetch leverancier detail..',
            create_vendor_coordination_failed: 'Kan niet create leverancier coördinatie..',
            add_communication_failed: 'Kan niet add communication..',
            add_info_request_failed: 'Kan niet add info request..',
            add_action_request_failed: 'Kan niet add actie request..',
        },
        nav: {
            playbooks: 'Draaiboeksjablonen',
            sla_definitions: 'SLA-definities',
            sla_dashboard: 'SLA-overzicht',
            escalation_log: 'Escalatielogboek',
            regulator_exports: 'Toezichthouderexports',
            deadlines: 'Melding Deadlines',
            vendor_coordination: 'Leverancierscoördinatie',
            metrics: 'Responsmetingen',
            playbook_links: 'Draaiboek-koppelingen',
            regulatory_sla: 'Regelgevende SLA',
            coordination_dashboard: 'Coördinatie',
            evidence_chain: 'Bewijsketen',
            post_review: 'Post-incident beoordeling',
        },
        playbooks: {
            page_title: 'Speelboek-sjablonen',
            activations_page_title: 'Playbook-activeringen',
            title: 'Draaiboeksjablonen',
            new: 'Nieuw Sjabloon',
            seed_defaults: 'Initialiseren Standaardwaarden',
            scenario_type: 'Scenariotype',
            severity_applicability: 'Ernst Applicability',
            all_scenario_types: 'Alle Scenario Types',
            new_playbook: 'Nieuw Draaiboek Sjabloon',
            clone: 'Klonen',
            clone_template: 'Klonen Sjabloon',
            template_name: 'Sjabloon naam',
            deprecate: 'Afschrijven',
            confirm_deprecate: 'Afschrijven sjabloon "{name}"?',
            create_template: 'Aanmaken Sjabloon',
            update_status: 'Bijwerken Status',
            no_templates: 'Geen draaiboek sjablonen gevonden',
            deactivation_reason_placeholder: 'Reden voor deactivation...',
            description_placeholder: 'Draaiboek beschrijving...',
            name_placeholder: 'Draaiboek naam...',
            procedure_steps: 'Procedure Steps',
        },
        sla_definitions: {
            page_title: 'SLA-definities',
            title: 'SLA-definities',
            new: 'Nieuw SLA Definition',
            sla_name: 'SLA Naam',
            target_hours: 'Doel Hours',
            escalation_after: 'Escalatie After',
            applies_to: 'Applies Naar',
            no_definitions: 'Geen SLA definities gevonden.',
            target_value_placeholder: 'Doel value',
            name_placeholder: 'SLA naam...',
            threshold_hours_label: 'Drempeluren',
            severity_label: 'Ernst',
            all_types: 'Alle typen',
            all_severities: 'Alle ernstniveaus',
            default_label: 'Standaard',
            bound_heading: 'Regulatory clocks behind the default notification target',
            bound_capped: 'Default notification-time targets are capped at {hours} h — the shortest notification period among the installed clocks already in force.',
            bound_not_capped: 'No installed notification clock in force states a period, so default notification-time targets are not capped.',
            bound_bounding_label: 'Bounded by — in force',
            bound_upcoming_label: 'Left out — not yet in force',
            bound_unreadable_label: 'Left out — timing unknown',
            bound_date_not_stated_label: 'Left out — no effective date stated',
            bound_hours: '{hours} h',
            bound_unreadable: 'The regulatory clocks behind the default notification target could not be read.',
            bound_retry_subject: 'the notification clocks',
            load_retry_subject: 'the SLA definitions',
            bound_phase_unresolved_label: 'Left out — phase start not dated',
        },
        sla_dashboard: {
            compliant: 'Conform',
            page_title: 'SLA-overzicht',
            title: 'SLA-overzicht',
            loading: 'Laden van SLA gegevens...',
            compliance_rate: 'Naleving Tarief',
            breached: 'Geschonden',
            at_risk: 'At Risico',
            on_track: 'Op schema',
            by_severity: 'By Ernst',
            total_records: 'Totaal records',
            met: 'Voldaan',
            sla_records: 'SLA-records',
            sla_reference: 'SLA-referentie',
            record_id: 'Record-ID',
            metric_type: 'Metrietype',
            severity: 'Ernst',
            elapsed: 'Verstreken',
            clock_start: 'Klokstart',
            statutory_baseline: 'Statutory baseline: the notification deadline required by the frameworks this organization has installed.',
            statutory_baseline_heading: 'Statutory Baseline',
        },
        escalation_log: {
            page_title: 'Escalatielog',
            title: 'Escalatielogboek',
            incident_id: 'Incident-ID',
            escalated_by: 'Geëscaleerd By',
            escalated_to: 'Geëscaleerd Naar',
            reason: 'Reden',
            escalated_at: 'Geëscaleerd At',
            no_escalations: 'Geen escalations gevonden.',
            filter_incident_id: 'Filteren op Incident ID',
            escalation_id: 'Escalatie-ID',
            level: 'Niveau',
            level_range: 'Niveau (L1–L4)',
            create_escalation: 'Escalatie aanmaken',
            incident_id_placeholder: 'bijv. INC-001',
            target_role_placeholder: 'bijv. dpo, incident_manager',
            select_placeholder: '— selecteren —',
        },
        regulator_exports: {
            page_title: 'Toezichthouderexporten',
            title: 'Toezichthouderexports',
            new: 'Nieuw Exporteren',
            incident_id: 'Incident-ID',
            regulator: 'Toezichthouder',
            export_format: 'Formaat',
            exported_at: 'Geëxporteerd At',
            no_exports: 'Geen regulator exports gevonden.',
            enter_incident_id: 'Invoeren Incident ID',
            export_id: 'Export-ID',
            watermark: 'Watermerk',
            submission_status: 'Indieningsstatus',
            all_jurisdictions: 'Alle jurisdicties',
            no_transitions: 'Geen verdere overgangen beschikbaar vanaf',
            select_new_status: '— nieuwe status selecteren —',
            sections_included: 'Opgenomen secties',
            form_classification: 'Classificatie',
            submission_channel: 'Kanaal',
        },
        deadlines: {
            title: 'Melding Deadlines',
            incident_id: 'Incident-ID',
            deadline: 'Uiterste datum',
            regulator: 'Toezichthouder',
            remaining: 'Resterend',
            no_deadlines: 'Geen melding deadlines gevonden.',
        },
        vendor_coordination: {
            page_title: 'Leverancierscoördinatie',
            title: 'Leverancierscoördinatie',
            new: 'Nieuw Coördinatie',
            incident_id: 'Incident-ID',
            vendor_name: 'Leverancier Naam',
            contact: 'Contactpersoon',
            coordination_status: 'Coördinatie Status',
            status_labels: {
                initiated: 'Initiated',
                in_progress: 'In progress',
                awaiting_vendor: 'Awaiting vendor',
                vendor_responded: 'Vendor responded',
                resolved: 'Resolved',
                closed: 'Closed',
                escalated: 'Escalated',
            },
            no_items: 'Geen leverancier coördinatie items gevonden.',
            assessment_placeholder: 'Initieel beoordeling van leverancier...',
            coordination_id: 'Coördinatie-ID',
            communications: 'Communicatie',
            info_requests: 'Infoverzoeken',
            action_requests: 'Actieverzoeken',
            direction: 'Richting',
            channel_label: 'Kanaal',
            no_transitions: 'Geen verdere overgangen beschikbaar.',
            vendor_ref_number: 'Referentienummer',
            report_date: 'Rapportdatum',
            contact_email: 'E-mail',
            contact_phone: 'Telefoon',
            channel_email: 'E-mail',
            channel_meeting: 'Vergadering',
            channel_phone: 'Telefoon',
            channel_portal: 'Portaal',
            incident_id_placeholder: 'Filteren op Incident ID...',
            priority_critical: 'Kritiek',
            priority_high: 'Hoog',
            priority_low: 'Low',
            priority_medium: 'Gemiddeld',
            vendor_id_placeholder: 'Filteren op Leverancier ID...',
            description_placeholder: 'Beschrijving...',
            filter_vendor_id: 'Filteren op Leverancier ID',
            inbound: 'Inkomend',
            outbound: 'Uitgaand',
            summary_placeholder: 'Samenvatting...',
            priority_label: 'Prioriteit',
            due_date_label: 'Vervaldatum',
            info_request_desc: 'Beschrijving van informatieverzoek',
            action_request_desc: 'Beschrijving actieverzoek',
            content_label: 'Inhoud',
            select_incident: 'Selecteer een incident...',
            select_vendor: 'Leverancier selecteren',
            discovery_date: 'Ontdekking Datum',
            contact_role: 'Contact Role',
            processor_role: 'Processor Role',
            sla_elapsed: 'SLA Elapsed',
            overdue: 'Overdue',
            actor: 'Betrokkene',
        },
        metrics: {
            page_title: 'Responsmetrieken',
            title: 'Responsmetingen',
            loading: 'Laden van metrieken gegevens...',
            avg_response_time: 'Avg Reactie Tijd',
            avg_resolution_time: 'Avg Oplossing Tijd',
            total_incidents: 'Totaal Incidenten',
            by_severity: 'By Ernst',
            by_status: 'Per status',
            trend: 'Ontwikkeling',
            start_sla_clock: 'SLA-klok starten',
            sla_records: 'SLA Records',
            no_sla_records: 'Nog geen SLA-records.',
            record_id: 'Record-ID',
            severity: 'Ernst',
            progress: 'Voortgang',
            incident_id_required: 'Incident-ID is vereist',
            sla_definition_required: 'SLA-definitie is vereist',
            metric_type_required: 'Metrietype is vereist',
            threshold_required: 'Drempeluren zijn vereist',
            sla_clock_started: 'SLA-klok gestart',
            sla_definition: 'SLA-definitie',
            no_definitions: 'Geen SLA-definities gevonden.',
            select_definition: 'SLA-definitie selecteren...',
            select_metric_type: 'Metrietype selecteren...',
            select_severity: 'Ernstniveau selecteren...',
            threshold_hours: 'Drempel (uren)',
            warning_threshold_pct: 'Waarschuwingsdrempel (%)',
            start_clock: 'Klok starten',
            starting: 'Starten...',
            metric_label: 'Metriek',
            value_label: 'Waarde',
            incident_id: 'Incident-ID',
            threshold_placeholder: 'bijv. 24',
            metric_types: {
                triage_time: 'Triagetijd',
                containment_time: 'Inperkingstijd',
                notification_time: 'Meldingstijd',
                resolution_time: 'Oplossingstijd',
            },
            severities: {
                critical: 'Kritiek',
                high: 'Hoog',
                medium: 'Gemiddeld',
                low: 'Low',
            },
            sla_reference: 'SLA-referentie',
        },
        playbook_links: {
            page_title: 'Playbooklinks',
            title: 'Draaiboek-meldingskoppelingen',
            new_link: 'Draaiboek aan melding koppelen',
            activation_id: 'Activerings-ID',
            notification_id: 'Meldings-ID',
            select_activation: 'Selecteer een activering...',
            select_notification: 'Selecteer een melding...',
            no_links: 'Geen draaiboek-koppelingen gevonden',
            seed_pdpl: 'Sjabloonlinks voor draaiboeken toevoegen',
            created: 'Playbook-koppeling aangemaakt',
            seeded: 'Sjabloonlinks voor draaiboeken toegevoegd',
            create_failed: 'Aanmaken van playbook-koppeling mislukt',
            seed_failed: 'Toevoegen van sjabloonlinks voor draaiboeken mislukt',
            seed_already: 'The template playbook links are already present; nothing was added',
            template_activation: 'Template (not an activation of your own)',
            template_notification: 'Template (not a notification of your own)',
        },
        regulatory_sla: {
            // fix-sprint-20260811-2123-followup — keyed on the route's
            // `skipped_reason_code`. The English prose the route also returns is a
            // fallback for an unrecognised code, never the operator's explanation.
            // fix-sprint-20260808-2111 (finding #35) — the declared incident-notification
            // deadlines a seed did not turn into a mapping, and why.
            not_seeded_heading: 'Declared deadlines that were not added',
            not_seeded_intro: 'These incident-notification deadlines are declared by your installed frameworks, but no mapping was added for them:',
            not_seeded_reason: {
                unclassified: 'its deadline type is not one this table records',
                no_numeric_period: 'it states its period in words, not as a number of hours or days, so a mapping cannot hold it',
                not_yet_in_force: 'it is not yet in force: its clock starts to bind on {date}',
                activation_not_determinable: 'neither the framework nor the phase it names states when its clock starts to bind',
            },
            seed_skipped: {
                no_governing_framework: 'Geen geïnstalleerd raamwerk regelt incidentrespons, dus er zijn geen termijnen om toe te voegen.',
                declared_but_none_active: 'Een raamwerk voor incidentrespons is geïnstalleerd maar niet van kracht.',
                no_declared_deadlines: 'De geïnstalleerde raamwerken voor incidentrespons stellen geen meldingstermijnen vast.',
                declared_deadlines_withheld: 'The installed frameworks declare notification deadlines, but none could be added yet. They are listed below under the deadlines that were not added.',
                already_seeded: 'Every notification deadline the installed frameworks declare already has its mapping. Nothing new was added.',
            },
            // Fix sprint F25 — the period is rendered WITH the unit the framework
            // declared. The table hardcoded an `h` suffix, so a deadline the pack
            // states as 15 working days was shown to the operator as "15h": a
            // misstated statutory period on a regulator-facing screen. `deadline_unit`
            // had one writer and zero readers; this is the reader.
            //
            // The pack unit vocabulary is open (26 distinct values in the corpus), so
            // `unit_unknown` carries anything not listed rather than guessing hours.
            deadline_period_label: 'Deadline',
            deadline_units: {
                hours: '{count} uur',
                working_days: '{count} werkdagen',
                calendar_days: '{count} kalenderdagen',
                days: '{count} dagen',
                weeks: '{count} weken',
                months: '{count} maanden',
                years: '{count} jaar',
            },
            deadline_unit_unknown: '{count} {unit}',
            // English until Phase 25 (R-57).
            // fix-sprint #447 W6 (owner ruling OR2) — a reference table: it sets no clock,
            // and a row is refused without the unit its period is stated in.
            reference_notice: 'This is a reference table. A mapping sets no clock and escalates nothing: no incident, breach notification or SLA reads these rows. Every regulatory deadline in the product comes from the deadline records your installed frameworks declare.',
            seed_only_conditional: 'The installed frameworks declare notification deadlines only for specific activities or conditions, so none was added automatically.',
            seed_conditional_heading: 'Deadlines not added automatically',
            seed_conditional_intro: 'These installed deadlines apply only to a stated activity or condition. The platform cannot tell whether your organisation carries it, so a person decides: if it does, add the mapping from here.',
            seed_conditional_scope_activity: 'Applies to the activity: {value}',
            seed_conditional_scope_condition: 'Applies only if: {value}',
            seed_conditional_add: 'Add this mapping',
            deadline_amount: 'Period',
            deadline_unit: 'Unit',
            select_deadline_unit: 'Select a unit...',
            deadline_unit_options: {
                hours: 'Hours',
                working_days: 'Working days',
                calendar_days: 'Calendar days',
                days: 'Days',
                weeks: 'Weeks',
                months: 'Months',
                years: 'Years',
            },
            page_title: 'Regelgevende SLA-toewijzingen',
            title: 'Regelgevende SLA-toewijzingen',
            new_mapping: 'Nieuwe SLA-toewijzing',
            sla_definition: 'SLA-definitie',
            select_sla_definition: 'Selecteer een SLA-definitie...',
            framework_code: 'Raamwerkcode',
            deadline_type: 'Deadlinetype',
            deadline_hours: 'Deadline in uren',
            seed_pdpl: 'Koppelingen uit geïnstalleerde termijnen toevoegen',
            no_mappings: 'Geen regelgevende SLA-toewijzingen gevonden',
            dashboard: 'Regelgevend SLA-overzicht',
            deadline_types: {
                initial_notification: 'Eerste kennisgeving',
                supplement_response: 'Aanvulling reactie',
                authority_response: 'Autoriteit antwoord',
                remediation_report: 'Saneringsrapport',
                subject_notification: 'Data Subject Notification',
            },
            statutory_reference: 'Statutaire referentie',
        },
        coordination_dashboard: {
            page_title: 'Coördinatieoverzicht',
            title: 'Coördinatieoverzicht',
            incident_id: 'Incident-ID',
            select_incident: 'Selecteer een incident...',
            team_role: 'Teamrol',
            members: 'Leden',
            tasks: 'Taken',
            sla_status: 'SLA-status',
            no_teams: 'Geen coördinatieteams gevonden',
            new_team: 'Nieuw coördinatieteam',
            enter_incident_id: 'Voer een incident-ID in om coördinatieteams te bekijken.',
            member_ids_placeholder: 'gebruiker1, gebruiker2',
            edit_team: 'Team bewerken',
            task_count: 'Taaktelling',
            tasks_completed: 'Voltooid',
            team_roles: {
                security: 'Beveiliging',
                legal: 'Juridisch',
                it: 'IT',
                management: 'Beheer',
                department_owner: 'Afdelingseigenaar',
                external: 'Extern',
            },
        },
        evidence_chain: {
            page_title: 'Bewijsketen',
            title: 'Bewijsketen',
            new_entry: 'Nieuwe bewijsinvoer',
            select_incident: 'Selecteer een incident...',
            actor: 'Betrokkene',
            select_actor: 'Selecteer een betrokkene...',
            evidence_id: 'Bewijs-ID',
            action: 'Actie',
            recipient: 'Ontvanger',
            no_entries: 'Geen bewijsketeninvoeren gevonden',
            enter_incident_id: 'Voer een incident-ID in om de bewijsketen te bekijken.',
            evidence_id_placeholder: 'bijv. EVD-2026-0001',
            evidence_id_help: 'Een unieke verwijzing naar het bewijsstuk – een logbestand, een schijfkopie of een schermafbeelding. Er is een suggestie vooraf ingevuld; pas deze aan uw bewijscatalogus aan.',
            select_incident_first: 'Selecteer hierboven een incident voordat u een bewijsvermelding toevoegt.',
            for_incident: 'Voor incident',
            actions: {
                collected: 'Verzameld',
                shared: 'Gedeeld',
                exported: 'Geëxporteerd',
                archived: 'Gearchiveerd',
            },
        },
        post_review: {
            page_title: 'Na-incidentbeoordeling',
            title: 'Post-incident beoordeling',
            new_review: 'Nieuwe beoordeling',
            select_incident: 'Selecteer een incident...',
            lessons_learned: 'Geleerde lessen',
            recommendations: 'Aanbevelingen',
            improvement_actions: 'Verbeteracties',
            submit_for_review: 'Indienen ter beoordeling',
            aria_submit_review: 'Beoordeling {name} indienen ter beoordeling',
            aria_sign_off: 'Beoordeling {name} goedkeuren',
            sign_off: 'Goedkeuring',
            no_reviews: 'Geen post-incident beoordelingen gevonden',
            enter_incident_id: 'Voer een incident-ID in om de na-incidentbeoordeling te bekijken.',
            one_per_line: 'Eén per regel',
            statuses: {
                draft: 'Concept',
                in_review: 'In beoordeling',
                signed_off: 'Goedgekeurd',
                closed: 'Gesloten',
            },
        },
        activations: {
            title: 'Playbook-activeringen',
            activation_id: 'Activerings-ID',
            phases: 'Fasen',
            activated_at: 'Geactiveerd bij',
            activated_by: 'Geactiveerd door',
            deactivate: 'Deactiveren',
            complete: 'Voltooien',
            deactivation_reason: 'Reden van deactivering',
            evidence_button: 'Bewijs',
            evidence_modal_title: 'Activeringsbewijs',
            no_evidence: 'Er is geen bewijs gekoppeld aan deze activering.',
            upload_evidence_label: 'Activeringsbewijs uploaden',
            procedure_followed: 'Procedure Followed',
            phases_completed_sr: '{completed} of {total} phases completed',
        },
    },
    program_governance: {
        // ── Sprint 65.8.0 (AC9) — the audit engagement register ─────
        //
        // The record an audit is planned, scoped, staffed, worked and closed as.
        // Every gate this copy names is the SERVER's — the lifecycle, the two
        // mandatory reasons, the calendar-date period, and the refusal to close an
        // engagement over live findings. Each is stated BEFORE the operator meets
        // it, because a rule an operator first learns as a 409 is a rule the
        // product kept to itself.
        audit_engagements: {
            title: 'Audit Engagements',
            description: 'Plan an audit, record what it covers and who is performing it, raise findings inside it, and close it once every finding has been resolved.',
            module_guide_summary: 'An audit engagement carries its scope, the period audited, who is auditing, and a lifecycle that runs from planning through fieldwork and reporting to closed. Findings are raised inside an engagement and never on their own, and an engagement cannot be closed while any of its findings is still unresolved.',
            load_error: 'The audit engagements could not be loaded. Try again.',
            counts_error: 'The engagement counts could not be read, so no totals are shown.',
            counts_label: 'Engagements by state',
            total: 'All engagements',
            filter_state: 'State',
            filter_all_states: 'All states',
            filter_auditor_kind: 'Audit type',
            filter_all_auditor_kinds: 'All audit types',
            filters_label: 'Filter audit engagements',
            col_title: 'Engagement',
            col_period: 'Period audited',
            col_auditor: 'Auditor',
            col_state: 'State',
            no_engagements: 'No audit engagements have been planned yet.',
            no_engagements_filtered: 'No audit engagement matches these filters.',
            select_hint: 'Select an engagement to see its findings and the actions available on it.',
            open_engagement: 'Open this engagement',
            selected_label: 'Selected engagement',
            new_engagement: 'Plan an engagement',
            create_title: 'Plan an audit engagement',
            section_scope: 'What is being audited',
            section_auditor: 'Who is auditing',
            field_title: 'Title',
            field_scope: 'Scope',
            field_scope_hint: 'What this engagement covers, in the words of whoever scoped it.',
            field_period_start: 'Period start',
            field_period_end: 'Period end',
            period_hint: 'Calendar dates. The period audited is a span of days somebody wrote down, so it carries no time of day.',
            period_invalid: 'The period end must not fall before the period start.',
            period_required: 'Both period dates are required, each as a calendar date.',
            field_auditor_kind: 'Audit type',
            auditor_internal: 'Internal',
            auditor_external: 'External',
            field_auditor_name: 'Auditor',
            auditor_name_hint_internal: 'The person or team performing the audit.',
            auditor_name_hint_external: 'The firm performing the audit. An external auditor has no account on this platform, so no user is named.',
            field_auditor_user: 'Platform user performing the audit',
            auditor_user_placeholder: 'Select a user…',
            auditor_user_required: 'An internal audit must name the platform user performing it.',
            created: 'Audit engagement planned',
            create_error: 'The audit engagement could not be created.',
            lifecycle: 'Lifecycle',
            lifecycle_actions: 'Actions on this engagement',
            advance_to: 'Move to {state}',
            transitioned: 'Engagement moved to {state}',
            transition_error: 'The engagement could not be moved.',
            terminal_notice: 'This engagement is {state}. Its scope, period and auditor can no longer be changed.',
            cancel_engagement: 'Cancel engagement',
            cancel_title: 'Cancel this audit engagement',
            cancel_reason: 'Why is it being cancelled?',
            cancel_reason_required: 'Cancelling an audit engagement requires a reason.',
            cancel_confirm: 'Cancel the engagement',
            cancellation_reason: 'Cancellation reason',
            close_engagement: 'Close engagement',
            close_gate_label: 'Whether this engagement can be closed',
            close_blocked: 'This engagement cannot be closed: {count} finding(s) raised inside it are still unresolved. Close or reject each one first.',
            close_ready: 'Every finding raised inside this engagement is closed or rejected, so the engagement can be closed.',
            close_gate_unknown: 'The unresolved-finding count could not be read, so the platform cannot say whether this engagement may be closed.',
            closed_on: 'Closed on',
            cancelled_on: 'Cancelled on',
            created_on: 'Planned on',
            states: {
                planned: 'Planned',
                in_fieldwork: 'In fieldwork',
                reporting: 'Reporting',
                closed: 'Closed',
                cancelled: 'Cancelled',
            },
            directory_truncated: 'Only the first {count} users are listed here.',
            directory_unavailable: 'The user directory could not be loaded, so no user can be chosen right now.',
            directory_reload: 'Reload',
            directory_reloading: 'Reloading…',
            directory_retry_subject_auditor: 'the user directory for the auditor',
            directory_retry_subject_owner: 'the user directory for the finding owner',
            reload: 'Reload',
            list_retry_subject: 'the audit engagements',
            findings_retry_subject: 'the findings',
            findings: {
                title: 'Findings',
                section_label: 'Findings raised inside this engagement',
                unresolved: 'Unresolved',
                load_error: 'The findings could not be loaded. Try again.',
                no_findings: 'No findings have been raised inside this engagement.',
                new_finding: 'Raise a finding',
                create_title: 'Raise a finding inside this engagement',
                engagement: 'Engagement',
                anchor_hint: 'A finding is always raised inside an engagement. It cannot be recorded without one.',
                field_title: 'Title',
                field_observation: 'What was observed',
                field_severity: 'Severity',
                field_owner: 'Owner',
                owner_placeholder: 'Select a user…',
                col_title: 'Finding',
                col_severity: 'Severity',
                col_owner: 'Owner',
                col_state: 'State',
                created: 'Finding raised',
                create_error: 'The finding could not be raised.',
                transitioned: 'Finding moved to {state}',
                transition_error: 'The finding could not be moved.',
                accept: 'Accept',
                start_remediation: 'Start remediation',
                close: 'Close finding',
                reject: 'Reject',
                reject_title: 'Reject this finding',
                reject_reason: 'Why is this not a finding?',
                reject_reason_required: 'Rejecting an audit finding requires a reason.',
                reject_confirm: 'Reject the finding',
                rejection_reason: 'Rejection reason',
                reject_only_from_open: 'A finding can only be rejected while it is open. Once it has been accepted, the organisation has already said it is a finding.',
                states: {
                    open: 'Open',
                    accepted: 'Accepted',
                    remediating: 'Remediating',
                    closed: 'Closed',
                    rejected: 'Rejected',
                },
            },
        },
        maturity_walkthrough: {
            run_label: 'Leid mij door gaten',
            running: 'Lopende gaten…',
            results_heading: 'Routekaart voor verbetering ({count} kloof{plural})',
            tokens_label: '≈ {count} tokens',
            no_gaps: 'Geen hiaten gedetecteerd: alle domeinen op of boven het doel.',
            col_priority: 'Prioriteit',
            col_domain: 'Domein',
            col_levels: 'Huidig → Doel',
            col_severity: 'Ernst',
            col_actions: 'Saneringsacties',
            citations_label: 'Citaties',
            accept_label: 'Accepteer routekaart',
            reject_label: 'Concept afwijzen',
            export_label: 'Exporteer DOCX',
            exported_label: 'Routekaart geëxporteerd',
            accept_hint: 'Accepting records your decision on the saved draft; it does not change the assessment. Use Export DOCX to download the roadmap, then carry the actions you choose into tasks or your own plan.',
            resolve_summary: 'Resolutie: {status}',
            resolve_status_accepted: 'Geaccepteerd',
            resolve_status_rejected: 'Afgewezen',
            error_generic: 'Kan de gap-walkthrough niet uitvoeren. Probeer het opnieuw of controleer de beoordelingsstatus.',
        },
        policy_gap: {
            run_label: 'Beleidslacunes opsporen',
            running: 'Gaten detecteren…',
            results_heading: 'Beleidslacunes ({count})',
            tokens_label: '≈ {count} tokens',
            no_gaps: 'Er zijn geen dekkingslacunes gedetecteerd.',
            col_severity: 'Ernst',
            col_kind: 'Soort',
            col_clause: 'Clausule / vereiste',
            kind_missing: 'Ontbrekende clausule',
            kind_weak: 'Zwakke dekking',
            kind_stale: 'Verouderd (recensie te laat)',
            remediation_label: 'Voorgestelde sanering',
            citations_label: 'Citaties',
            accept_label: 'Accepteer het hiaatrapport',
            reject_label: 'Rapport afwijzen',
            export_label: 'Exporteer DOCX',
            accept_hint: 'Accepteren registreert het hiaatrapport over het aanhoudende concept. Gebruik Export DOCX om een ​​beleidsauditrapport te genereren; de operator zorgt voor de daadwerkelijke beleidsbewerking.',
            resolve_summary: 'Resolutie: {status}',
            error_generic: 'Kon geen beleidslacunes ontdekken. Probeer het opnieuw of controleer de documentstatus.',
        },
        remediation_plan_generator: {
            run_label: 'Genereer een plan uit hiaten',
            running: 'Plan genereren…',
            results_heading: 'Saneringsplan ({phase_count} fase{plural})',
            tokens_label: '≈ {count} tokens',
            phase_label: 'Fase {index}',
            window_label: 'Venster',
            owner_label: 'Eigenaar rol',
            evidence_label: 'Bewijssoorten vereist',
            tasks_label: 'Taken',
            no_phases: 'Er zijn geen fasen geproduceerd. De input gap-set is mogelijk leeg.',
            citations_label: 'Citaties',
            accept_label: 'Plan accepteren',
            reject_label: 'Plan afwijzen',
            export_label: 'Exporteer DOCX',
            accept_hint: "Accepting creates one task for each task listed (up to 5 per phase and 12 in all). Each task's description names this plan; the tasks are not linked to a record in the Remediation Plans register and have no assignee, so give each one an owner from the task list. Use Export DOCX for the full deliverable.",
            resolve_summary: 'Oplossing: {status} · {tasks} taak(en) gemaakt',
            error_generic: 'Kan herstelplan niet genereren. Probeer het opnieuw of controleer de invoer van de gap-set.',
            source_picker_label: 'Bron',
            source_all: 'Allemaal',
            source_consent: 'Analyse van toestemmingskloof',
            source_policy: 'Detectie van beleidslacunes',
            source_vendor: 'Onboarding van leveranciers',
            load_upstream_label: 'Laden van stroomopwaarts',
            loading_upstream: 'Laden…',
            upstream_count: '{count} opening(en) geladen',
            export_done: 'Saved {filename} to your downloads.',
            no_gaps_in_source: 'The selected source holds no gaps, so nothing was generated and no tasks can be created. Run that analysis first, or choose another source.',
            no_gaps: 'No gaps are loaded, so there is nothing to plan. Choose a source and click Load from upstream.',
        },
        nav: {
            audit_engagements: 'Audit Engagements',
            handbook_assessments: 'Handbook Assessments',
            maturity_models: 'Volwassenheidsmodellen',
            maturity_assessments: 'Volwassenheidsbeoordelingen',
            trend_dashboard: 'Trendoverzicht',
            reporting_periods: 'Rapportageperioden',
            executive_summaries: 'Bestuurlijke samenvattingen',
            audit_readiness: 'Controlebereidheid',
            evidence_gaps: 'Bewijslacunes',
            program_health: 'Programmastatus',
            obligation_mappings: 'Verplichtingskoppelingen',
            governance_documents: 'Bestuursdocumenten',
            remediation_plans: 'Herstelplannen',
            governance_model: 'Governancemodel',
            document_drafting: 'Documentontwerp',
            document_issuance: 'Documentuitgifte',
            acknowledgment_tracking: 'Bevestigingsopvolging',
            change_log: 'Wijzigingslogboek',
            review_cadence: 'Beoordelingscadans',
        },
        maturity_models: {
            title: 'Volwassenheidsmodellen',
            new: 'Nieuw Model',
            seed_defaults: 'Initialiseren Standaardwaarden',
            domains: 'Domeinen',
            activate: 'Activeren',
            no_models: 'Geen volwassenheid models gevonden',
            new_maturity_model: 'Nieuw Volwassenheid Model',
            create_model: 'Aanmaken Model',
            load_error: 'Kan volwassenheidsmodellen niet laden. Probeer opnieuw.',
            create_error: 'Kan volwassenheidsmodel niet aanmaken',
            status_error: 'Kan modelstatus niet bijwerken',
            seed_error: 'Kan standaardwaarden niet genereren',
            aria_seed_defaults: 'Genereer standaard volwassenheidsmodellen',
            aria_create_new: 'Nieuw volwassenheidsmodel aanmaken',
            loading_sr: 'Volwassenheidsmodellen laden',
            model_name_placeholder: 'bijv., Privacy-volwassenheidsmodel',
            status_changed: 'Modelstatus gewijzigd naar {status}',
        },
        maturity_assessments: {
            models_load_error: 'The maturity model list could not be loaded, so model names may show as unknown.',
            no_models_action: 'Open Maturity Models',
            no_models_body: 'An assessment scores your programme against a maturity model, and this organization has none. Create one first.',
            no_models_title: 'No maturity models yet',
            title: 'Volwassenheidsbeoordelingen',
            new: 'Nieuw Beoordeling',
            model: 'Modeltype',
            assessor: 'Beoordelaar',
            overall_score: 'Algeheel Score',
            overall_score_help: 'Een gewogen volwassenheidsbeoordeling van 0–5, die automatisch wordt berekend wanneer een beoordeling wordt afgerond. Verhoog deze door elk domein tijdens de evaluatie hoger te scoren en de beoordeling vervolgens via Indienen ter beoordeling → Afronden te doorlopen.',
            assessed_at: 'Beoordeeld op',
            no_assessments: 'Geen volwassenheid assessments gevonden.',
            load_error: 'Kan volwassenheidsbeoordelingen niet laden. Probeer opnieuw.',
            create_error: 'Kan beoordeling niet aanmaken',
            start_error: 'Kan beoordeling niet starten',
            archive_error: 'Kan beoordeling niet archiveren',
            eval_error: 'Kan beoordeling niet laden voor evaluatie',
            eval_min_score: 'Beoordeel ten minste één domein',
            eval_save_error: 'Kan domeinbeoordelingen niet opslaan',
            review_error: 'Kan niet indienen ter beoordeling',
            finalize_error: 'Kan beoordeling niet afronden',
            start: 'Starten',
            start_confirm_title: 'Beoordeling starten',
            start_confirm_message: 'Weet u zeker dat u deze beoordeling wilt starten?',
            archive_confirm_title: 'Beoordeling archiveren',
            archive_confirm_message: 'Weet u zeker dat u deze beoordeling wilt archiveren?',
            evaluate_domains: 'Evalueren',
            evaluate_domains_title: 'Domeinen evalueren',
            submit_for_review: 'Indienen ter beoordeling',
            finalize: 'Afronden',
            archive: 'Archiveren',
            all_reviewers: 'Alle beoordelaars moeten goedkeuren',
            any_reviewer: 'Elke beoordelaar kan goedkeuren',
            select_level: 'Selecteer volwassenheidsniveau...',
            findings_placeholder: 'Bevindingen, notities, bewijsreferenties...',
            no_domains: 'Geen domeinen gevonden voor dit model.',
        },
        trend_dashboard: {
            title: 'Trendoverzicht',
            loading: 'Laden van trend gegevens...',
            score_trend: 'Scoretrend',
            domain_comparison: 'Domein Vergelijking',
            period: 'Periode',
            load_error: 'Kan trendsamenvatting niet laden. Probeer opnieuw.',
            metrics_load_error: 'Kan trendmetingen niet laden. Probeer opnieuw.',
            module: 'Module',
            metric: 'Metriek',
            value: 'Waarde',
            trend: 'Trend',
            aria_filters: 'Filters voor trendmetingen',
            showing_chart: 'Grafiekweergave',
            showing_table: 'Datatabelweergave',
            create_metric: 'Metriek maken',
            data_points: 'Gegevenspunten',
            trend_direction: 'Trendrichting',
            unit: 'Eenheid',
        },
        reporting_periods: {
            title: 'Rapportageperioden',
            new: 'Nieuw Periode',
            period_name: 'Periode Naam',
            start_date: 'Starten Datum',
            end_date: 'Einde Datum',
            no_periods: 'Geen reporting periodes gevonden.',
            load_error: 'Kan rapportageperiodes niet laden. Probeer opnieuw.',
            create_error: 'Kan rapportageperiode niet aanmaken',
            auto_generate_error: 'Kan rapportageperiodes niet automatisch genereren',
            status_error: 'Kan periodestatus niet bijwerken',
            start_collecting: 'Start verzameling',
            mark_as_draft: 'Markeren als concept',
            submit_for_review: 'Indienen ter beoordeling',
            publish: 'Publiceren',
            auto_generate: 'Rapportageperiodes automatisch genereren',
            period_label_placeholder: 'bijv., K1 2026',
            status_updated: 'Periodestatus bijgewerkt',
        },
        executive_summaries: {
            title: 'Bestuurlijke samenvattingen',
            new: 'Nieuw Samenvatting',
            period: 'Periode',
            generated_at: 'Gegenereerd At',
            generated_by: 'Gegenereerd By',
            no_summaries: 'Geen executive summaries gevonden.',
            generate: 'Genereren',
            load_error: 'Kan directiesamenvattingen niet laden. Probeer opnieuw.',
            generate_error: 'Kan directiesamenvatting niet genereren',
            save_error: 'Kan directiesamenvatting niet opslaan',
        },
        audit_readiness: {
            title: 'Controlebereidheid',
            loading: 'Laden van audit readiness gegevens...',
            overall_readiness: 'Algeheel Readiness',
            by_domain: 'By Domein',
            evidence_coverage: 'Bewijs Dekking',
            gaps: 'Hiaten',
            load_error: 'Kan laatste gereedheid niet laden. Probeer opnieuw.',
            history_error: 'Kan gereedheidshistorie niet laden. Probeer opnieuw.',
            calculate_error: 'Kan auditgereedheid niet berekenen',
            aria_recalculate: 'Herbereken auditgereedheidsscore',
            aria_current_readiness: 'Huidige auditgereedheid',
            aria_history: 'Geschiedenis van gereedheidsscores',
            recalculated: 'Auditgereedheid herberekend',
            loading_sr: 'Auditgereedheidsgegevens laden',
        },
        evidence_gaps: {
            title: 'Bewijslacunes',
            domain: 'Domein',
            control: 'Controle',
            gap_type: 'Hiaat Type',
            severity: 'Ernst',
            no_gaps: 'Geen bewijs hiaten gevonden.',
            load_error: 'Kan bewijslacunes niet laden. Probeer opnieuw.',
            stats_error: 'Kan lacunestatistieken niet laden. Probeer opnieuw.',
            create_error: 'Kan bewijslacune niet aanmaken',
            update_error: 'Kan bewijslacune niet bijwerken',
            status_error: 'Kan status niet bijwerken',
            aria_create_new: 'Nieuwe bewijslacune aanmaken',
            aria_statistics: 'Bewijslacunestatistieken',
            loading_sr: 'Bewijslacunes laden',
            status_updated: 'Lacunestatus bijgewerkt',
            entity_type_placeholder: 'bijv., leverancier, systeem',
            all_modules: 'Alle domeinen',
            all_severities: 'Alle ernstniveaus',
        },
        program_health: {
            title: 'Programmastatus',
            loading: 'Laden van programma gezondheid gegevens...',
            overall_health: 'Algeheel Gezondheid',
            by_module: 'Per module',
            risk_areas: 'Risico Areas',
            recommendations: 'Aanbevelingen',
            load_error: 'Kan programgezondheidsgegevens niet laden. Probeer opnieuw.',
            aria_key_metrics: 'Kerncijfers programgezondheid',
            aria_module_scorecard: 'Moduleresultaatkaart uitsplitsing',
            loading_sr: 'Programgezondheidsgegevens laden',
            trending_up: 'Stijgende trend',
            trending_down: 'Dalende trend',
            trending_stable: 'Stabiel',
            data_loaded: 'Programgezondheidsgegevens geladen',
        },
        obligation_mappings: {
            title: 'Verplichtingskoppelingen',
            create_mapping: 'Verplichtingskoppeling aanmaken',
            framework: 'Raamwerk',
            select_framework: 'Selecteer een kader...',
            obligation: 'Verplichting',
            obligation_ref: 'Verplichtingsreferentie',
            obligation_description: 'Verplichtingsbeschrijving',
            role: 'Rol',
            assigned_role: 'Toegewezen rol',
            select_role: 'Selecteer een rol...',
            assigned_department: 'Toegewezen afdeling',
            select_department: 'Selecteer een afdeling...',
            control_ids: "Controle-ID's",
            control_ids_placeholder: 'bijv. A.5.1, CC6.1',
            control_ids_hint: 'Door komma’s gescheiden controlereferentiecodes.',
            raci_assignments: 'RACI-toewijzingen',
            create_raci: 'RACI-toewijzing aanmaken',
            activity: 'Activiteit',
            activity_name: 'Activiteitsnaam',
            activity_description: 'Activiteitsbeschrijving',
            responsible: 'Verantwoordelijk',
            select_users: 'Selecteer gebruikers...',
            accountable: 'Eindverantwoordelijk',
            select_user: 'Selecteer een gebruiker...',
            consulted: 'Geraadpleegd',
            informed: 'Geïnformeerd',
            review_frequency: 'Beoordelingsfrequentie',
            frequency_monthly: 'Maandelijks',
            frequency_quarterly: 'Elk kwartaal',
            frequency_semi_annual: 'Halfjaarlijks',
            frequency_annual: 'Jaarlijks',
            no_mappings_found: 'Geen verplichtingskoppelingen gevonden.',
            no_raci_found: 'Geen RACI-toewijzingen gevonden.',
            mapping_created: 'Verplichtingstoewijzing aangemaakt',
            mapping_updated: 'Verplichtingskoppeling bijgewerkt',
            mapping_deleted: 'Verplichtingskoppeling verwijderd',
            raci_created: 'RACI-toewijzing aangemaakt',
            raci_updated: 'RACI-toewijzing bijgewerkt',
            raci_deleted: 'RACI-toewijzing verwijderd',
            aria_edit_mapping: 'Koppeling {name} bewerken',
            aria_delete_mapping: 'Koppeling {name} verwijderen',
            aria_edit_raci: 'RACI {name} bewerken',
            aria_delete_raci: 'RACI {name} verwijderen',
            confirm_delete_mapping: 'Deze verplichtingskoppeling verwijderen?',
            confirm_delete_raci: 'Deze RACI-toewijzing verwijderen?',
            table_view: 'Tabelweergave',
            matrix_view: 'Matrixweergave',
            print_export: 'Afdrukken / Exporteren',
            filter_framework: 'Filteren op framework',
            no_matrix_data: 'Geen RACI-toewijzingen om weer te geven in matrixweergave.',
            aria_print_export: 'RACI-matrix afdrukken of exporteren',
        },
        governance_documents: {
            title: 'Bestuursdocumenten',
            create_document: 'Bestuursdocument aanmaken',
            document_title: 'Documenttitel',
            document_type: 'Documenttype',
            content_url: 'Inhouds-URL',
            content_url_placeholder: 'https://...',
            framework_codes: 'Kadercodes',
            select_frameworks: 'Selecteer kaders...',
            effective_date: 'Ingangsdatum',
            review_date: 'Beoordelingsdatum',
            status: 'Status',
            version: 'Versie',
            acknowledgments: 'Bevestigingen',
            acknowledge: 'Bevestigen',
            acknowledged_by: 'Bevestigd door',
            department: 'Afdeling',
            no_documents_found: 'Geen bestuursdocumenten gevonden.',
            draft: 'Concept',
            review: 'Beoordeling',
            approved: 'Goedgekeurd',
            issued: 'Uitgegeven',
            superseded: 'Vervangen',
            document_created: 'Document aangemaakt',
            document_acknowledged: 'Document bevestigd',
            policy: 'Beleid',
            procedure: 'Werkwijze',
            standard: 'Standaard',
            guideline: 'Richtlijn',
        },
        governance_model: {
            title: 'Governancemodel',
            create_model: 'Governancemodel',
            edit_model: 'Governancemodel',
            model_name: 'Model',
            description: 'Beschrijving',
            governance_type: 'Type',
            type_centralized: 'Gecentraliseerd',
            type_federated: 'Federaal',
            type_hybrid: 'Hybride',
            framework_codes: 'Framework',
            select_frameworks: '...',
            review_frequency: 'Frequency',
            freq_monthly: 'Maandelijks',
            freq_quarterly: 'Driemaandelijks',
            freq_semi_annual: 'Halfjaarlijks',
            freq_annual: 'Jaarlijks',
            committees: 'Commissies',
            committee_name: 'Name',
            committee_members: 'Members',
            committee_mandate: 'Mandaat',
            add_committee: '+',
            no_committees: '—',
            delegation_rules: 'Delegation',
            from_role: 'From',
            to_role: 'To',
            scope: 'Reikwijdte',
            add_delegation_rule: '+',
            control_owner_summary: 'RACI',
            no_raci_data: '—',
            activity: 'Activiteit',
            responsible: 'R',
            accountable: 'A',
            consulted: 'C',
            informed: 'I',
            no_models_found: '—',
            model_created: 'OK',
            model_updated: 'OK',
            model_deleted: 'OK',
            confirm_delete: '?',
            expand: '+',
            collapse: '-',
            aria_expand: '{name}',
            aria_edit: '{name}',
            aria_delete: '{name}',
            aria_export_csv: '{name} CSV',
            aria_export_json: '{name} JSON',
            framework_read_permission_warning: 'U heeft de toestemming regulatory_framework:read nodig om frameworks te selecteren. Vraag een beheerder om deze toe te staan.',
            select_members: 'Selecteer commissieleden',
            select_from_role: 'Selecteer delegerende rol',
            select_to_role: 'Selecteer ontvangende rol',
            mandate_markdown_hint: 'Ondersteunde prijsverlagingen: **vet**, lijsten, links.',
            scope_tab_obligation: 'Verplichting',
            scope_tab_framework: 'Kader',
            scope_tab_free_text: 'Vrije tekst',
            select_obligation: 'Selecteer een verplichting',
            select_framework: 'Selecteer een raamwerk',
            aria_remove_committee: 'Commissie {n} verwijderen',
            aria_remove_delegation_rule: 'Delegatieregel {n} verwijderen',
            no_frameworks_available: 'Geen kaders beschikbaar. Voeg eerst regelgevingskaders toe.',
            confirm_discard_changes: 'Discard your unsaved governance model changes?',
        },
        document_drafting: {
            title: 'Documentontwerp',
            document_title: 'Document',
            document_type: 'Type',
            status: 'Status',
            drafter: 'Tekenaar',
            deadline: 'Deadline',
            assign_drafter: 'Assign',
            select_drafter: '...',
            assign: 'Toewijzen',
            submit_for_review: 'Submit',
            legal_review: 'Review',
            reviewer: 'Recensent',
            select_reviewer: '...',
            comments: 'Opmerkingen',
            review_action: 'Action',
            approve: 'Goedkeuren',
            request_changes: 'Wijzigingen aanvragen',
            submit_review: 'Submit',
            executive_approval: 'Approval',
            no_drafts_found: '—',
            drafter_assigned: 'OK',
            status_updated: 'OK',
            review_approved: 'OK',
            changes_requested: 'OK',
            document_approved: 'OK',
            aria_assign: '{name}',
            aria_submit_review: '{name}',
            aria_review: '{name}',
            aria_approve: '{name}',
        },
        document_issuance: {
            title: 'Documentuitgifte',
            document_title: 'Document',
            document_type: 'Type',
            status: 'Status',
            version: 'Versie',
            effective_date: 'Date',
            distribution_scope: 'Distribution',
            distribution_placeholder: '...',
            issuance_number: 'Number',
            issuance_number_placeholder: 'ISS-2026-001',
            supersedes: 'Supersedes',
            none: '—',
            issue_document: 'Issue',
            ready_for_issuance: 'Ready',
            issued_documents: 'Issued',
            no_issued_documents: '—',
            view_lineage: 'Lineage',
            supersession_lineage: 'Supersession',
            document_issued: 'OK',
            document_superseded: 'OK',
            aria_issue: '{name}',
            aria_view_lineage: '{name}',
            no_approved_documents: 'No approved documents available to issue.',
        },
        acknowledgment_tracking: {
            title: 'Bevestigingsopvolging',
            document_title: 'Documenteren',
            acknowledged_count: 'Erkend',
            pending_count: 'In behandeling',
            percentage: '%',
            details: 'Details',
            send_reminder: 'Remind',
            acknowledgment_details: 'Details',
            user: 'Gebruiker',
            department: 'Afdeling',
            acknowledged_at: 'Date',
            status: 'Status',
            acknowledged: 'OK',
            no_issued_documents: '—',
            no_acknowledgments: '—',
            reminder_sent: 'OK',
            aria_expand: '{name}',
            aria_send_reminder: '{name}',
            // Fix sprint #447 W3 — recipients, acknowledging, and the reminder count.
            // English until Phase 25 (R-57).
            recipients: 'Recipients',
            record_recipients: 'Record recipients',
            aria_record_recipients: 'Record recipients for {name}',
            recipients_dialog_title: 'Recipients of {name}',
            recipients_dialog_help: 'Choose the people asked to acknowledge this document. Only people whose role can open Acknowledgment Tracking are listed, because only they can acknowledge.',
            recipients_none_eligible: 'No one in your organization holds a role that can acknowledge documents.',
            recipients_saved: 'Recipients recorded',
            recipients_not_recorded: 'Not recorded',
            acknowledge: 'Acknowledge',
            aria_acknowledge: 'Acknowledge {name}',
            acknowledge_dialog_title: 'Acknowledge {name}',
            acknowledge_dialog_body: 'You confirm that you have read this document. Your name and the time are recorded.',
            acknowledge_department: 'Department (optional)',
            reminder_sent_count: 'Reminder sent to {count} of {pending} people who have not acknowledged',
            export_documents_count: 'Documents',
            export_acknowledgments_count: 'Acknowledgments',
        },
        change_log: {
            title: 'Wijzigingslogboek',
            date: 'Datum',
            document: 'Documenteren',
            change_type: 'Type',
            actor: 'Acteur',
            summary: 'Samenvatting',
            export_csv: 'Exporteer CSV',
            filters: 'Filters',
            filter_document: 'Document',
            all_documents: 'All',
            filter_change_type: 'Type',
            all_types: 'All',
            date_from: 'From',
            date_to: 'To',
            version_history: 'History',
            version_archive: 'Archive',
            no_entries: '—',
            no_history: '—',
            exported: 'OK',
            aria_export: 'CSV',
            aria_view_history: '{name}',
            // English until Phase 25 (R-57).
            // fix-sprint #447 W6 — `DocumentChangeType` labels; the page showed raw codes.
            type_created: 'Created',
            type_status_transition: 'Status changed',
            type_fields_updated: 'Fields updated',
            type_superseded: 'Superseded',
            type_acknowledged: 'Acknowledged',
            // fix-sprint #447 round 2 — metadata line of the server-rendered export.
            export_generated_at: 'Generated at',
        },
        review_cadence: {
            title: 'Beoordelingscadans',
            configure_cadence: 'Beoordelingscadans',
            edit_cadence: 'Beoordelingscadans',
            document: 'Documenteren',
            select_document: '...',
            review_frequency: 'Frequency',
            freq_30: '30',
            freq_60: '60',
            freq_90: '90',
            freq_180: '180',
            freq_365: '365',
            last_reviewed: 'Last',
            next_review: 'Next',
            status: 'Status',
            overdue: 'Te laat',
            on_track: 'OK',
            days: 'days',
            horizon: 'Horizon',
            overdue_warning: 'Overdue',
            overdue_count: '{count}',
            no_cadences: '—',
            cadence_created: 'OK',
            cadence_updated: 'OK',
            aria_edit: '{name}',
        },
        remediation_plans: {
            title: 'Herstelplannen',
            create_plan: 'Herstelplan aanmaken',
            gap_id: 'Lacunereferentie',
            select_gap: 'Selecteer een lacune...',
            plan_title: 'Titel',
            description: 'Beschrijving',
            priority: 'Prioriteit',
            priority_critical: 'Kritiek',
            priority_high: 'Hoog',
            priority_medium: 'Gemiddeld',
            priority_low: 'Laag',
            assigned_to: 'Toegewezen aan',
            select_user: 'Selecteer een gebruiker...',
            target_date: 'Streefdatum',
            framework_code: 'Raamwerk',
            select_framework: 'Selecteer een kader...',
            gap_finding: 'Lacunevaststelling',
            action_items: 'Actiepunten',
            owner: 'Eigenaar',
            deadline: 'Deadline',
            planned: 'Gepland',
            in_progress: 'Bezig',
            completed: 'Voltooid',
            verified: 'Geverifieerd',
            no_plans_found: 'Geen herstelplannen gevonden.',
            evidence: 'Bewijs',
            plan_created: 'Herstelplan aangemaakt',
            action_items_hint: 'Actiepunten (één per regel)',
        },
        statuses: {
            identified: 'Geïdentificeerd',
            assigned: 'Toegewezen',
            in_remediation: 'Bij sanering',
            resolved: 'Opgelost',
            accepted_risk: 'Geaccepteerd risico',
            planned: 'Gepland',
            in_progress: 'In uitvoering',
            under_review: 'Onder beoordeling',
            finalized: 'Afgerond',
            archived: 'Gearchiveerd',
            draft: 'Diepgang',
            active: 'Actief',
            upcoming: 'Aankomend',
            collecting: 'Verzamelen',
            published: 'Gepubliceerd',
            review: 'Beoordeling',
            approved: 'Goedgekeurd',
            issued: 'Uitgegeven',
            superseded: 'Vervangen',
            completed: 'Voltooid',
            verified: 'Geverifieerd',
        },
        severities: {
            critical: 'Kritisch',
            high: 'Hoog',
            medium: 'Middelmatig',
            low: 'Low',
        },
        priorities: {
            critical: 'Kritisch',
            high: 'Hoog',
            medium: 'Middelmatig',
            low: 'Low',
        },
        gap_types: {
            missing: 'Ontbreekt',
            expired: 'Verlopen',
            insufficient: 'Onvoldoende',
        },
        document_types: {
            policy: 'Beleid',
            procedure: 'Werkwijze',
            standard: 'Standaard',
            guideline: 'Richtlijn',
            template: 'Sjabloon (intern)',
        },
        maturity_levels: {
            level_1: 'Niveau 1 — Initieel',
            level_2: 'Niveau 2 — Ontwikkelen',
            level_3: 'Niveau 3 — Gedefinieerd',
            level_4: 'Niveau 4 — Beheerd',
            level_5: 'Niveau 5 — Optimaliseren',
        },
        grades: {
            excellent: 'Uitstekend',
            good: 'Goed',
            fair: 'Eerlijk',
            poor: 'Arm',
            critical: 'Kritisch',
            needs_improvement: 'Verbetering nodig',
            not_assessed: 'Niet beoordeeld',
        },
        modules: {
            data_mapping: 'Gegevens in kaart brengen',
            consent: 'Toestemming',
            governance: 'Bestuur',
            incidents: 'Incidenten',
            monitoring: 'Toezicht',
            classification: 'Classificatie',
            vendor_risk: 'Leveranciersrisico',
            discovery: 'Ontdekking',
            deletion: 'Verwijdering',
        },
        cadences: {
            monthly: 'Maandelijks',
            quarterly: 'Driemaandelijks',
            semi_annual: 'Halfjaarlijks',
            annual: 'Jaarlijks',
        },
        section_labels: {
            opening: 'Openen',
            key_metrics: 'Belangrijke statistieken',
            highlights: 'Hoogtepunten',
            concerns: 'Zorgen',
            recommendations: 'Aanbevelingen',
            outlook: 'Vooruitzichten',
        },
        out_of_100: '/ 100',
        count_gaps: '{count} kloof',
        count_gaps_plural: '{count} hiaten',
        count_summaries: '{count} samenvatting',
        count_summaries_plural: '{count} samenvattingen',
        count_assessments: '{count} beoordeling',
        count_assessments_plural: '{count} beoordelingen',
        count_models: '{count}-model',
        count_models_plural: '{count} modellen',
        count_periods: '{count} periode',
        count_periods_plural: '{count} perioden',
        count_metrics: '{count} statistiek',
        count_metrics_plural: '{count} statistieken',
        unknown_user: 'Onbekende gebruiker',
        unknown_gap: 'Onbekende kloof',
        no_permission_title: 'Onvoldoende machtigingen',
        no_permission_message: 'U heeft geen toestemming om deze pagina te bezoeken.',
        ac_vn_frameworks_title: 'VN-frameworkdekking',
        ac_vn_frameworks_body: 'Maturiteitsbeoordelingen hebben betrekking op alle zes de Vietnamese regelgevingskaders: PDPL, datarecht, telecomrecht, cyberveiligheidsrecht, AI recht en e-commercerecht – elk met raamwerkspecifieke beoordelingscriteria.',
        ac_controller_processor_title: 'Verwerkingsverantwoordelijke versus verwerker',
        ac_controller_processor_body: 'Bij verplichtingstoewijzingen wordt onderscheid gemaakt tussen de verantwoordelijkheden van de gegevensbeheerder en de gegevensverwerker. Wijs rollen toe met behulp van de RACI-matrix om ervoor te zorgen dat elke verplichting een duidelijke eigenaar heeft, ongeacht of de organisatie optreedt als verwerkingsverantwoordelijke of als verwerker.',
        ac_versioned_evidence_title: 'Versieversie van bewijspad',
        ac_versioned_evidence_body: 'Elke statusovergang (concept, beoordeling, goedgekeurd, afgegeven) creëert een record met tijdstempel en versiebeheer dat als controleerbaar bewijsmateriaal dient. Het wijzigingslogboek legt de volledige geschiedenis van de levenscyclus van het document vast.',
        ac_remediation_gap_link_title: 'Gekoppeld aan beoordelingslacunes',
        ac_remediation_gap_link_body: 'Elk saneringsplan is rechtstreeks gekoppeld aan een geïdentificeerde beoordelingskloof. Wijs een eigenaar aan en stel een streefdeadline in om de voortgang van de oplossing van het hiaat tot het sluiten te volgen.',
        ac_annual_attestation_title: 'Jaarlijkse attestatiecyclus',
        ac_annual_attestation_body: "Rapportageperioden komen overeen met de jaarlijkse attestvereiste van de Vietnamese gegevenswet. Gebruik de cadans 'Jaarlijks' (cyclus van 365 dagen) om automatisch op attesten afgestemde perioden te genereren.",
        ac_evidence_per_obligation_title: 'Bewijs per kaderverplichting',
        ac_evidence_per_obligation_body: 'Bewijslacunes identificeren ontbrekend, verlopen of onvoldoende bewijs voor elke kaderverplichting. Filter op domein om te zien welke wettelijke vereisten geen ondersteunend bewijsmateriaal bevatten.',
        ac_cross_framework_title: 'Cross-framework volledigheid',
        ac_cross_framework_body: 'Auditgereedheid biedt een compleetheidsoverzicht dat meerdere kaders omvat. Componentscores weerspiegelen de dekking binnen alle in kaart gebrachte raamwerken, waarbij gebieden worden benadrukt die aandacht behoeven vóór een audit.',
        ac_cadence_default_title: 'Standaard beoordelingscadans',
        ac_cadence_default_body: 'De standaard beoordelingsfrequentie is 180 dagen. Achterstallige beoordelingen worden automatisch gemarkeerd. Pas indien nodig de frequentie per document aan – de opties variëren van 30 tot 365 dagen.',
        ac_templates_internal_title: 'Sjabloonclassificatie',
        ac_templates_internal_body: "Sjablonen worden geclassificeerd als interne documenten. Gebruik het documenttype 'Sjabloon' om herbruikbare governance-artefacten te markeren met een uitsluitend intern distributiebereik.",
        // fix-sprint-20260822-2115 F1/F3 — this panel used to carry a module-local
        // `const COPY = { en: {…}, vi: {…} }` and pick a branch with
        // `locale.startsWith('vi') ? 'vi' : 'en'`. Both shapes are named in
        // I18N.md §2 and §3 as the root cause of 14 locales rendering English, and
        // the strings were registered in no bundle at all, so deepMerge had nothing
        // to fall back to. The four enum label maps below close F3: `status`,
        // `legal_review_status`, `evidence_state` and `answer_state` were rendering
        // raw slugs next to an already-labelled badge on the same row.
        handbook_assessment: {
            ask_forseti: 'Ask Forseti about this checklist',
            candidate_link: 'Link this record',
            candidate_linked: 'Already linked',
            candidate_linking: 'Linking…',
            candidate_no_date: 'No date recorded',
            candidate_open: 'Open record',
            evidence_kind_manual: 'Manual entry',
            evidence_kind_platform: 'Platform record',
            link_manual_fallback: 'If none of the above applies, record the evidence manually.',
            reason_module_not_readable: 'The regulatory pack names a module this platform build cannot read for evidence. Link the record manually instead.',
            reason_module_not_permitted: 'Your role cannot read the module that holds this evidence. Ask an administrator for access, or link the record manually.',
            reason_no_matching_record: 'No record of this kind exists in this organization yet.',
            reason_record_not_accepted: 'A record exists but is not in an accepted state. Complete or approve it, then resolve again.',
            source_form: 'Form',
            source_module: 'Module',
            source_role: 'Evidence role',
            sources_error: 'The platform sources for this question could not be loaded.',
            sources_heading: 'Evidence the platform can supply',
            sources_intro: 'The regulatory pack declares which of your own records can satisfy this question. These are the matching records this organization holds.',
            sources_loading: 'Loading platform sources…',
            unreconciled_question: 'This question is not on this assessment\u2019s own record and cannot be answered here: {question}',
            sources_toggle: 'Show platform sources',
            collapse_all: 'Collapse all questions',
            crosslink_action: 'Open handbook assessments',
            crosslink_body: 'A maturity assessment scores your programme against a maturity model. The official handbook checklist is a separate record: it collects evidence against a regulatory handbook and deliberately produces no score.',
            crosslink_title: 'Official handbook evidence checklist',
            expand_all: 'Expand all questions',
            group_progress: '{answered} of {total} answered',
            module_guide_summary: 'Complete an official regulatory handbook evidence checklist. Each question records an answer, an evidence state and the supporting records held in the platform. The checklist produces no compliance score — a human legal review closes it.',
            page_description: 'Official regulatory handbook evidence checklists. Each question records an answer, an evidence state, and the platform records that support it.',
            page_title: 'Handbook Assessments',
            question_fields: 'Question response',
            loading: 'Loading official handbook assessment',
            load_error: 'The official handbook assessment could not be loaded.',
            start: 'Start official handbook assessment',
            empty: 'No official handbook assessment has been started for this organization.',
            progress: 'questions answered',
            no_score: 'This evidence checklist does not calculate or claim a legal-compliance score. Human legal review is required.',
            save: 'Save answer',
            saving: 'Saving…',
            resolve: 'Resolve existing evidence',
            resolving: 'Resolving…',
            evidence: 'Evidence state',
            answer: 'Answer',
            notes: 'Notes and applicability rationale',
            linked: 'Linked evidence',
            no_evidence: 'No evidence is linked yet.',
            manual: 'Link manual evidence',
            description: 'Evidence description',
            source: 'Evidence source',
            owner: 'Evidence owner',
            date: 'Evidence date',
            url: 'Evidence URL',
            invalid_url: 'Enter a complete http:// or https:// URL.',
            link: 'Link evidence',
            linking: 'Linking…',
            remediate: 'Create remediation task',
            remediating: 'Creating task…',
            review_question: 'Mark question reviewed',
            reviewing: 'Reviewing…',
            submit: 'Submit all 30 questions for review',
            finalize: 'Complete legal review',
            return_revision: 'Return for revision',
            export: 'Export reviewed evidence report',
            exporting: 'Exporting…',
            status: 'Assessment status',
            legal_review: 'Legal review',
            saved: 'Answer saved.',
            resolved: 'Existing evidence resolution completed.',
            linked_done: 'Evidence linked for review.',
            remediation_done: 'Remediation task created.',
            review_done: 'Review state updated.',
            exported: 'Reviewed evidence report exported.',
            export_format: 'Format',
            export_language: 'Language',
            export_format_pdf: 'PDF',
            export_format_docx: 'DOCX',
            export_language_en: 'English',
            export_language_vi: 'Tiếng Việt',
            export_language_bilingual: 'English / Tiếng Việt',
            answer_state: {
                unanswered: 'Unanswered',
                yes: 'Yes',
                no: 'No',
                unknown: 'Unknown',
                not_applicable: 'Not applicable',
            },
            assessment_status: {
                in_progress: 'In progress',
                under_review: 'Under review',
                reviewed: 'Reviewed',
                archived: 'Archived',
            },
            legal_review_status: {
                pending: 'Pending',
                reviewed: 'Reviewed',
                revision_requested: 'Revision requested',
            },
            evidence_state: {
                resolved: 'Resolved',
                missing: 'Missing',
                stale: 'Stale',
                unsupported: 'Unsupported',
                manual_pending_review: 'Manual — pending review',
            },
        },
    },
    mcp_pending: {
        title: 'MCP — Wachtende schrijfopdrachten',
        subtitle: 'Externe Claude-clients stellen hier schrijfopdrachten voor. Keur elke vermelding goed of af — Forseti voert pas uit na goedkeuring door een beheerder.',
        no_pending: 'Geen wachtende schrijfopdrachten.',
        filter_status_label: 'Filter by status',
        filter_all: 'All',
        no_results_for_status: 'No writes with this status.',
        col_tool: 'Gereedschap',
        col_preview: 'Voorbeeld',
        col_key: 'API-toets',
        col_requested: 'Aangevraagd',
        col_expires: 'Verloopt',
        col_status: 'Status',
        status_pending: 'In afwachting',
        status_approved: 'Goedgekeurd',
        status_declined: 'Afgewezen',
        status_expired: 'Verlopen',
        status_executed: 'Uitgevoerd',
        status_failed: 'Failed — nothing written',
        approve_failed: 'Approved, but the write did NOT happen — {tool} was not carried out ({code}). Nothing was created or changed.',
        approve_executed: '{tool} was carried out.',
        approve_label: 'Goedkeuren',
        decline_label: 'Afwijzen',
        refresh_label: 'Vernieuwen',
        error_generic: 'Wachtende schrijfopdrachten konden niet worden geladen.',
        confirm_approve: 'Bij goedkeuring wordt de tool uitgevoerd op uw organisatie met de rechten van de houder van de API key. Doorgaan?',
        confirm_decline: 'Deze wachtende schrijfopdracht afwijzen? Dit kan niet ongedaan worden gemaakt.',
        // Sprint 64.123.0 — approving a queued MCP write now mints the human
        // confirmation the tool manifest requires, so the surface has to say what
        // that approval is worth, and when it is self-attestation.
        confirmation_note: 'Approving records the human confirmation these tools require, and the write is then carried out with the API key holder’s permissions — not yours. Where you proposed the write yourself, your approval is recorded as self-approved and is not an independent review.',
        self_approval_badge: 'You proposed this write — approving it is self-attestation, not a second review.',
        confirm_approve_self: 'You proposed this write. Approving it records YOUR OWN confirmation — no second person will review it. The audit trail will show it as self-approved. Continue?',
        advisory_banner: 'Wachtende schrijfopdrachten verlopen 24 uur na aanmaak. De uurlijkse GC-sweep wijst verlopen vermeldingen automatisch af.',
        consequential_label: 'Regulatorily consequential filing',
        consequential_target: 'Target record',
        consequential_authority: 'Authority',
        consequential_authority_unknown: 'Not declared by the installed template',
        consequential_prior: 'Prior',
        consequential_proposed: 'Proposed',
        consequential_empty: '(none)',
        consequential_unresolved: 'The target record could not be read when this was queued —',
        consequential_captured_at: 'Values read at',
        // fix-sprint-20261003-1200 F5 — the approve refusals, by server code.
        refusal_proposer_lacks_forseti: 'This write was not carried out: the role of the person who proposed it no longer allows Forseti, so it cannot run as them. Nothing was changed. Decline it to remove it from the queue.',
        refusal_proposer_inactive: 'This write was not carried out: the person who proposed it is no longer an active member of your organisation. Nothing was changed. Decline it to remove it from the queue.',
        refusal_key_inactive: 'This write was not carried out: the API key or connected app that proposed it may no longer make it. Nothing was changed. Decline it to remove it from the queue.',
        refusal_already_resolved: 'Someone has already resolved this write. Refresh to see its current status.',
        refusal_expired: 'This write has expired and can no longer be approved.',
        refusal_state_changed: 'This write changed status while you were approving it. Refresh to see its current status.',
        decline_dialog_title: 'Decline pending write',
        decline_reason_label: 'Reason for declining',
        decline_reason_hint: 'Required, up to 500 characters. It is recorded with your decision.',
        decline_submit: 'Decline write',
        decline_cancel: 'Keep pending',
        col_actions: 'Acties',
    },
    audit_walkthrough: {
        title: 'Forseti — Generator voor auditrondgang',
        subtitle: 'Genereer een gestructureerde rondgang voor een auditkickoff: openingsvraag per beheersmaatregel, verwacht bewijs, vervolgvragen, mogelijke hiaten.',
        audit_picker_label: 'Controle',
        audit_picker_hint: 'Kies een geplande audit om een rondgang te genereren.',
        run_label: 'Rondgang genereren',
        running: 'Bezig met genereren…',
        results_heading: 'Rondgang — {count} beheersmaatregel(en)',
        tokens_label: '≈ {count} tokens',
        no_controls: 'Geen beheersmaatregelen binnen scope. Voeg beheersmaatregelen toe aan de auditregistratie.',
        col_control: 'Beheersmaatregel',
        col_question: 'Openingsvraag',
        col_evidence: 'Bewijs',
        col_gaps: 'Mogelijke hiaten',
        expected_evidence_label: 'Verwacht bewijs',
        followup_probes_label: 'Vervolgvragen',
        accept_label: 'Rondgang aanvaarden',
        reject_label: 'Afwijzen',
        export_label: 'Exporteren als DOCX',
        resolve_summary: 'Resolutie: {status}',
        resolve_status_accepted: 'Geaccepteerd',
        resolve_status_rejected: 'Afgewezen',
        controls_range: 'Controls {from}–{to} of {total}',
        next_page_label: 'Generate next controls',
        error_generic: 'De rondgang kon niet worden gegenereerd. Probeer opnieuw of controleer de auditstatus.',
    },
    privilege_creep: {
        title: 'Forseti — Detectie van rechtenophoping',
        subtitle: 'Periodieke scan van rolverdelingen. Bevindingen zijn adviserend — Forseti trekt nooit automatisch rechten in.',
        run_label: 'Nu scannen',
        running: 'Bezig met scannen…',
        findings_heading: '{count} bevinding(en)',
        no_findings: 'Geen rechtenophoping gemarkeerd.',
        col_user: 'Gebruiker',
        col_job_function: 'Functie',
        col_severity: 'Ernst',
        col_unexpected: 'Onverwachte rechten',
        col_remediation: 'Voorgestelde remediatie',
        col_status: 'Status',
        severity_critical: 'Kritiek',
        severity_high: 'Hoog',
        severity_medium: 'Gemiddeld',
        severity_low: 'Laag',
        status_open: 'Openen',
        status_dismissed: 'Genegeerd',
        status_remediated: 'Verholpen',
        dismiss_label: 'Negeren',
        remediate_label: 'Markeren als verholpen',
        note_label: 'Notitie (optioneel)',
        error_generic: 'Kon rechtenophoping niet scannen. Probeer het later opnieuw.',
        advisory_banner: 'Deze bevindingen zijn adviserend. Forseti trekt nooit automatisch rechten in — overleg met de roleigenaar voordat u handelt.',
    },
    compliance_rules: {
        rule_mapping: {
            title: 'Automatische toewijzing van nalevingsregels',
            subtitle: 'Forseti voert een geïnstalleerd regelgevingspakket uit en stelt nieuwe of vernieuwde nalevingsregels voor, gebaseerd op de bronartikelen.',
            pack_picker_label: 'Regelgevend pakket',
            pack_picker_hint: 'Selecteer een geïnstalleerd pakket om in kaart te brengen.',
            run_label: 'Kaart uit verpakking',
            running: 'In kaart brengen…',
            results_heading: '{count} suggestie(s)',
            tokens_label: '≈ {count} tokens uit uw maandelijkse bucket',
            no_suggestions: 'Geen suggesties: pakketinhoud valt mogelijk al volledig onder de bestaande regels.',
            kind_new: 'Nieuwe regel',
            kind_refresh: 'Already covered (review only)',
            kind_no_change: 'Geen verandering vereist',
            col_kind: 'Soort',
            col_title: 'Voorgestelde titel',
            col_existing: 'Bestaande regel',
            col_source: 'Bron',
            proposed_body_label: 'Voorgesteld lichaam',
            source_quote_label: 'Bron citaat',
            accept_selected: 'Accepteer geselecteerd',
            reject_all: 'Alles afwijzen',
            accept_all: 'Accepteer alles',
            resolve_summary: 'Resolution: {status} · {created} draft rule(s) created · {skipped} left unchanged because a rule already covers the requirement',
            error_generic: 'Kan regeltoewijzing niet uitvoeren. Probeer het opnieuw of controleer de pakketcode.',
            write_permission_hint: 'Accepting suggestions needs permission to edit regulatory frameworks. You can still reject the run.',
            examined_label: 'Read passages {from}–{to} of the {total} that this pack search returns, ranked by one fixed search. A new run starts again at passage 1.',
            examined_remaining: '{remaining} passage(s) have not been read yet. Use Read next passages to continue from where this run stopped.',
            read_next_label: 'Read next passages',
            read_next_blocked: 'Accept or reject the suggestions above first; the next passages are read after this draft is resolved.',
            run_blocked: 'Accept or reject the suggestions above first; mapping again from this pack replaces the draft on screen.',
            drafts_hint: 'Accepted suggestions are saved as draft rules in the rule library. A draft rule is not active until someone activates it, and the product has no page for compliance rules yet. A passage that an existing rule already covers is shown for review only and creates nothing.',
        },
    },
    access_accountability: {
        nav: {
            session_dashboard: 'Sessieoverzicht',
            access_log: 'Toegangslogboek',
            justification_review: 'Motivatiebeoordeling',
            investigations: 'Onderzoeken',
            pattern_analysis: 'Patroonanalyse',
            anchoring_status: 'Verankeringsstatus',
            configuration: 'Configuratie',
            access_policies: 'Toegangsbeleid',
            review_campaigns: 'Beoordelingscampagnes',
            evidence_packages: 'Bewijspakketten',
        },
        session_dashboard: {
            title: 'Sessieoverzicht',
            loading: 'Overzichtsgegevens laden...',
            total_sessions: 'Totaal aantal sessies',
            active_sessions: 'Actieve sessies',
            avg_duration: 'Gem. duur',
            peak_day_sessions: 'Drukste dag',
            auth_method_breakdown: 'Verdeling per authenticatiemethode',
            geo_country_breakdown: 'Verdeling per land',
            recent_session_events: 'Recente sessiegebeurtenissen',
            user_id: 'Gebruikers-ID',
            session_id: 'Sessie-ID',
            ip_address: 'IP-adres',
            auth_method: 'Authenticatiemethode',
            started_at: 'Gestart op',
            event_type: 'Gebeurtenistype',
            all_event_types: 'Alle gebeurtenistypen',
            filter_by_user: 'Filteren op gebruiker',
            no_active_sessions: 'Geen actieve sessies',
            no_session_events: 'Geen sessiegebeurtenissen gevonden',
            revoke: 'Intrekken',
            revoke_session: 'Sessie intrekken',
            aria_revoke_session: 'Sessie {user} intrekken',
            revoke_reason: 'Reden',
            revoke_placeholder: 'Geef een reden op voor het intrekken van deze sessie...',
            session_label: 'Sessie:',
            load_failed: 'Kan het sessieoverzicht niet laden. Probeer het opnieuw.',
            events_load_failed: 'Kan sessiegebeurtenissen niet laden. Probeer het opnieuw.',
            revoke_failed: 'Kan sessie niet intrekken',
            session_events_loaded: '{n} sessiegebeurtenissen geladen',
            event_types: {
                session_started: 'Sessie gestart',
                session_refreshed: 'Sessie vernieuwd',
                session_expired: 'Sessie verlopen',
                session_revoked: 'Sessie ingetrokken',
                session_terminated: 'Sessie beëindigd',
            },
        },
        access_log: {
            title: 'Toegangslogboek',
            user_id: 'Gebruiker ID',
            resource: 'Bron',
            action: 'Actie',
            ip_address: 'IP Adres',
            timestamp: 'Tijdstempel',
            no_logs: 'Geen toegang logboek items gevonden.',
            filter_by_user: 'Filteren op Gebruiker ID...',
            load_failed: 'Kan niet load toegang logboek. Probeer het opnieuw.',
            export_success: 'Exporteren initiated successfully',
            export_failed: 'Kan niet export toegang logboek',
            record_count: 'vermelding',
            record_count_plural: 'vermeldingen',
            unclassified: 'Niet geclassificeerd',
            classification_missing: 'Classificatie ontbreekt',
            classification_level: 'Classificatieniveau',
            response_code: 'Responscode',
            access_surface: 'Toegangskanaal',
            api_key: 'API-sleutel',
            classification_levels: {
                public: 'Openbaar',
                internal: 'Intern',
                confidential: 'Vertrouwelijk',
                restricted: 'Beperkt',
                unclassified: 'Niet geclassificeerd',
            },
        },
        justification_review: {
            title: 'Motivatiebeoordeling',
            user_id: 'Gebruiker ID',
            resource: 'Bron',
            justification: 'Rechtvaardiging',
            submitted_at: 'Ingediend At',
            reviewer: 'Beoordelaar',
            no_reviews: 'Geen rechtvaardiging beoordelingen gevonden.',
            load_failed: 'Kan niet load rechtvaardigingen. Probeer het opnieuw.',
            review_failed: 'Kan niet beoordeling rechtvaardiging',
            decline_failed: 'Kan niet decline toegang',
            justification_count: 'rechtvaardiging',
            justification_count_plural: 'rechtvaardigingen',
            reviewed: 'Beoordeeld',
            concerning: 'Zorgwekkend',
            escalated: 'Geëscaleerd',
            link_investigation_placeholder: 'Koppelen naar een onderzoek (optional)',
            review_queue: 'Beoordelen Wachtrij',
            access_id_label: 'Toegangs-ID',
            access_id_placeholder: 'Toegangslogboek-invoer-ID',
            resource_type_label: 'Brontype',
            resource_type_placeholder: 'bijv. document, database',
            resource_id_label: 'Bron-ID',
            resource_id_placeholder: 'Bronidentifier',
            reason_placeholder: 'Reden voor toegang tot deze bron...',
            create_failed: 'create justification mislukt',
            unclassified: 'Niet geclassificeerd',
            review_statuses: {
                pending: 'In behandeling',
                reviewed: 'Beoordeeld',
                concerning: 'Wat betreft',
                escalated: 'Geëscaleerd',
            },
            reason_codes: {
                business_need: 'Zakelijke behoefte',
                legal_obligation: 'Wettelijke verplichting',
                data_subject_request: 'Verzoek van betrokkene',
                audit_requirement: 'Auditvereiste',
                incident_response: 'Reactie op incidenten',
                research: 'Onderzoek',
                administrative: 'Administratief',
                other: 'Anders',
            },
            classification_level: 'Classificatieniveau',
            review_decision: 'Herzie besluit',
        },
        investigations: {
            title: 'Onderzoek Workspace',
            new: 'Nieuw Onderzoek',
            subject: 'Onderwerp',
            investigator: 'Onderzoeker',
            opened_at: 'Geopend At',
            findings: 'Bevindingen',
            no_investigations: 'Geen onderzoeken gevonden.',
            load_failed: 'Kan niet load onderzoeken. Probeer het opnieuw.',
            create_failed: 'Kan niet create onderzoek',
            timeline_add_failed: 'Kan niet add tijdlijn item',
            link_access_failed: "Kan niet link toegang ID's",
            link_justification_failed: "Kan niet link rechtvaardiging ID's",
            close_failed: 'Kan niet close onderzoek',
            export_success: 'Exporteren initiated successfully',
            export_failed: 'Kan niet export onderzoek',
            refresh_failed: 'Kan niet refresh onderzoek detail. Probeer het opnieuw.',
            update_failed: 'Bijwerken van onderzoek mislukt',
            investigation_count: 'onderzoek',
            investigation_count_plural: 'onderzoeken',
            title_placeholder: 'Onderzoek title',
            description_placeholder: 'Detailed beschrijving van de onderzoek...',
            assignee_placeholder: 'Gebruiker ID van assignee (optional)',
            timeline_action_placeholder: 'e.g., Beoordeeld toegang logs',
            timeline_note_placeholder: 'Optioneel note',
            access_ids_placeholder: 'toegang-id-1, toegang-id-2',
            justification_ids_placeholder: 'rechtvaardiging-id-1, rechtvaardiging-id-2',
            findings_placeholder: 'Samenvatten de onderzoek bevindingen...',
            by_actor: 'by',
            close_investigation: 'Sluiten Onderzoek',
            add_timeline_failed: 'Kan niet add tijdlijn item..',
            fetch_failed: 'Kan niet fetch onderzoeken..',
            change_status: 'Onderzoeksstatus wijzigen',
            details: 'Onderzoeksdetails',
            link_evidence: 'Bewijs koppelen',
            link_justifications: 'Rechtvaardigingen koppelen',
            access_ids_label: 'Toegangs-IDs',
            justification_ids_label: 'Rechtvaardigings-IDs',
            access_ids_link_placeholder: 'toegang-id-1, toegang-id-2',
            justification_ids_link_placeholder: 'rechtvaardiging-id-1, rechtvaardiging-id-2',
            investigations_loaded: '{n} investigations geladen',
            status_changed_to: 'Status gewijzigd naar {status}',
            add_timeline_entry: 'Tijdlijn-invoer toevoegen',
            add_access_ids: 'Toegangs-IDs toevoegen',
            add_justification_ids: 'Rechtvaardigings-IDs toevoegen',
            investigation_types: {
                anomaly_review: 'Anomalie beoordeling',
                complaint_driven: 'Klacht gedreven',
                scheduled_review: 'Geplande beoordeling',
                whistleblower_report: 'Klokkenluidersrapport',
            },
            severity_labels: {
                low: 'Laag',
                medium: 'Middelmatig',
                high: 'Hoog',
                critical: 'Kritisch',
            },
            status_labels: {
                open: 'Openen',
                in_progress: 'In uitvoering',
                pending_review: 'In afwachting van beoordeling',
                closed_substantiated: 'Gesloten (onderbouwd)',
                closed_unsubstantiated: 'Gesloten (niet onderbouwd)',
                closed_inconclusive: 'Gesloten (niet doorslaggevend)',
            },
            disposition_labels: {
                substantiated: 'Onderbouwd',
                unsubstantiated: 'Niet onderbouwd',
                inconclusive: 'Niet doorslaggevend',
            },
        },
        pattern_analysis: {
            accesses_count: '{n} accesses',
            title: 'Patroonanalyse',
            resource_type_header: 'Resource type',
            resource_id_header: 'Resource ID',
            loading: 'Laden van patroon gegevens...',
            anomalies_detected: 'Anomalieën Gedetecteerd',
            patterns: 'Patronen',
            by_user: 'By Gebruiker',
            by_resource: 'By Bron',
            time_range: 'Tijd Range',
            load_failed: 'Kan niet analyze patterns. Probeer het opnieuw.',
            top_resources: 'Top-10',
            access_by_hour: 'Toegang op Hour van Dag',
            access_by_day: 'Toegang op Dag van Week',
            fetch_failed: 'Kan niet fetch patroon analysis..',
            analysis_loaded: 'Patroonanalyse geladen voor {user}, {n} sessies gevonden',
            date_format_placeholder: 'DD/MM/JJJJ',
            access_by_hour_heading: 'Toegang per uur',
            access_by_day_heading: 'Toegang per weekdag',
            by_classification: 'Per classificatieniveau',
            top_accessed_resources: 'Meest bezochte bronnen',
            user_access_summary: 'Gebruikerstoegangsoverzicht',
            days: {
                sunday: 'Zondag',
                monday: 'Maandag',
                tuesday: 'Dinsdag',
                wednesday: 'Woensdag',
                thursday: 'Donderdag',
                friday: 'Vrijdag',
                saturday: 'Zaterdag',
            },
            accesses_in_period: 'Accesses in period',
            sessions_label: 'Sessions',
            recent_accesses: 'Recent accesses in the period',
            recent_accesses_desc: "This user's most recent access log entries in the analysed period, newest first (up to {n}).",
        },
        anchoring_status: {
            kpi_summary_label: 'Anchoring status summary',
            title: 'Verankeringsstatus',
            loading: 'Laden van anchoring gegevens...',
            anchored: 'Verankerd',
            pending: 'In afwachting',
            failed: 'Mislukt',
            last_anchor: 'Laatste Anker',
            no_records: 'Geen anchoring records gevonden.',
            status_load_failed: 'Kan niet load anchoring status. Probeer het opnieuw.',
            proofs_load_failed: 'Kan niet load toegang bewijzen. Probeer het opnieuw.',
            verify_failed: 'Kan niet verify bewijs keten. Probeer het opnieuw.',
            anchor_failed: 'Kan niet anker toegang bewijs',
            batch_anchor_failed: 'Kan niet batch anker toegang bewijzen',
            proof_count: 'bewijs',
            proof_count_plural: 'bewijzen',
            anchor_single: 'Anker Single',
            anchor_batch: 'Anker Batch',
            access_id_placeholder: 'Invoeren toegang ID naar anker',
            batch_ids_placeholder: 'toegang-id-1, toegang-id-2, toegang-id-3',
            verify_chain: 'Verifiëren Keten',
            chain_breaks: 'Keten Breaks',
            total_checked: 'Totaal Checked',
            anchor_placeholder: 'Invoeren toegang ID naar anker',
            fetch_failed: 'Kan niet fetch anchoring status..',
            select_to_anchor: 'Select access log entry to anchor',
            select_to_anchor_batch: 'Select entries to anchor',
            no_unanchored_entries: 'No unanchored access-log entries found.',
            loading_unanchored: 'Loading access log entries...',
            fetch_unanchored_failed: 'Failed to load unanchored access log entries.',
            fetch_proofs_failed: 'Kan niet fetch bewijzen..',
            access_id: 'Toegangs-ID',
            chain_sequence: 'Ketenreeks',
            proof_hash_label: 'Bewijs-hash',
            batch_id: 'Batch-ID',
            proofs_loaded: '{n} access proofs geladen',
            proof_version_label: 'Version',
            proof_version_value: 'Version {n}',
            verify_valid_summary: 'The chain is intact. Every link and hash recomputes.',
            verify_invalid_summary: 'The chain does not verify. The proofs listed below no longer match what was anchored.',
            verify_proofs_v1: 'Version 1 proofs',
            verify_proofs_v2: 'Version 2 proofs',
            verify_content_verified: 'Log rows matching their proof',
            verify_content_destroyed: 'Content destroyed after retention',
            verify_content_destroyed_note: 'The chain still verifies for these proofs. The access-log row each one anchors has passed its retention date and been destroyed, so its content can no longer be proven.',
            verify_content_removed_sample: 'Demo log rows removed when sample data was cleared',
            verify_content_removed_sample_note: 'The chain still verifies for these proofs. Each one anchors a demo access-log row that was removed when the sample data was cleared; the removal is recorded by the platform. These are not retention destructions.',
            verify_v1_note: 'Version 1 proofs protect the order of the chain and the fact that an access event existed. They do not cover the content of the log row.',
            verify_v2_note: 'A version 2 proof also covers the content of its log row, for as long as the row exists.',
            verify_breaks_heading: 'Proofs that failed',
            verify_break_types: {
                field_mismatch: 'Log row changed after it was anchored',
                hash_mismatch: 'Proof hash does not recompute',
                chain_link_mismatch: 'Link to the previous proof is broken',
                unsupported_proof_version: 'Proof version is not supported',
                content_missing: 'Log row missing — the platform has no record of removing it',
            },
            anchor_statuses: {
                chained: 'Chained',
                pending: 'In behandeling',
                confirmed: 'Bevestigd',
                failed: 'Mislukt',
            },
            anchor_types: {
                sha256_chain: 'SHA-256 Ketting',
                blockchain_anchor: 'Blockchain-anker',
                zkp_stub: 'ZKP-stomp',
            },
        },
        configuration: {
            title: 'Configuratie',
            session_settings: 'Sessie Instellingen',
            log_retention: 'Logboek Bewaring (days)',
            anomaly_threshold: 'Anomalie Drempelwaarde',
            anchoring_enabled: 'Verankering Ingeschakeld',
            save_config: 'Opslaan Configuratie',
            load_failed: 'Kan niet load configuratie. Probeer het opnieuw.',
            save_success: 'Configuratie saved successfully',
            save_failed: 'Kan niet save configuratie. Probeer het opnieuw.',
            justification_rules: 'Rechtvaardiging Regels',
            reason_codes: 'Reden Codes',
            anchoring_settings: 'Verankering Instellingen',
            anchoring_method: 'Verankering Methode',
            anchoring_frequency: 'Verankering Frequentie',
            batch_interval: 'Batch-interval (minuten)',
            eligibility_criteria: 'Geschiktheid Criteria',
            add_criterion_placeholder: 'Toevoegen eligibility criterion...',
            retention_settings: 'Bewaring Instellingen',
            standard_retention: 'Standaard Bewaring (days)',
            sensitive_retention: 'Gevoelig Bewaring (days)',
            working_hours: 'Werk Hours',
            work_start: 'Starten Tijd',
            work_end: 'Einde Tijd',
            timezone: 'Tijdzone',
            timezone_placeholder: 'bijv. Asia/Ho_Chi_Minh',
            working_days: 'Werk Dagen',
            concurrent_sessions: 'Gelijktijdig Sessie Limits',
            concurrent_threshold: 'Gelijktijdig Sessie Drempelwaarde',
            classification_level: 'Classificatie Niveau',
            prompt_mode: 'Prompt Modus',
            grace_period: 'Respijt Periode (s)',
            code_placeholder: 'codering',
            is_custom: 'Aangepast',
            label_placeholder: 'Kenmerk',
            description_placeholder: 'Beschrijving',
            custom: 'Aangepast',
            fetch_failed: 'Kan niet fetch configuratie..',
            classification_levels: {
                public: 'Openbaar',
                internal: 'Intern',
                confidential: 'Vertrouwelijk',
                restricted: 'Beperkt',
                unclassified: 'Niet geclassificeerd',
            },
            prompt_modes: {
                reason_only: 'Alleen reden',
                reason_text: 'Reden + Tekst',
                reason_text_supervisor: 'Reden + Tekst + Begeleider',
            },
            anchoring_methods: {
                sha256_chain: 'SHA-256 Ketting',
                blockchain_anchor: 'Blockchain-anker',
                zkp_stub: 'ZKP-stomp',
            },
            anchoring_frequencies: {
                realtime: 'Realtime',
                batched: 'In batches',
                on_demand: 'Op aanvraag',
            },
            retention_position: {
                title: 'Log Retention Position',
                description: 'What the retention sweep would destroy, and what it would keep. Reading this changes nothing.',
                as_of: 'As of',
                window_in_force: 'Window in force',
                days: '{n} days',
                swept: 'Past its window',
                retained_held: 'Kept — legal hold or retention rule',
                retained_anchored: 'Kept — anchored by an older proof',
                retained_investigation: 'Kept — linked to an open access investigation',
                unstamped: 'No retention date recorded',
                skipped_swept: 'Already accounted for',
                eligible_sample: 'Oldest entries past their window',
                resource: 'Resource',
                expires_at: 'Window ended',
                run_sweep: 'Run retention sweep',
                running: 'Running...',
                sweep_success: 'Retention sweep complete: {swept} destroyed, {retained} kept.',
                sweep_failed: 'The retention sweep could not be run. Please try again.',
                load_failed: 'The retention position could not be read. Please try again.',
                reload: 'Reload',
                empty: 'No access-log entry is past its retention window.',
                unstamped_note: 'An entry with no retention date is never destroyed. It is counted above so the number is visible rather than assumed.',
                anchored_note: 'An entry anchored by a version 1 proof (made before proofs covered content) is kept past its window, because destroying it would leave that proof pointing at nothing. An entry anchored by a newer proof is destroyed on its date and the chain still verifies.',
            },
        },
        access_policies: {
            title: 'Toegangsbeleid',
            create_policy: 'Toegangsbeleid aanmaken',
            policy_name: 'Beleidsnaam',
            framework_code: 'Raamwerk',
            select_framework: 'Selecteer een raamwerk...',
            review_frequency: 'Beoordelingsfrequentie',
            scope: 'Bereik',
            no_policies_found: 'Geen toegangsbeleid gevonden.',
            policy_created: 'Toegangsbeleid aangemaakt',
            frequency_labels: {
                monthly: 'Maandelijks',
                quarterly: 'Kwartaal',
                semi_annual: 'Halfjaarlijks',
                annual: 'Jaarlijks',
            },
        },
        review_campaigns: {
            title: 'Toegangsbeoordelingscampagnes',
            create_campaign: 'Beoordelingscampagne aanmaken',
            reviewer: 'Beoordelaar',
            due_date: 'Vervaldatum',
            certify: 'Certificeren',
            no_campaigns_found: 'Geen toegangsbeoordelingscampagnes gevonden.',
            status_open: 'Openen',
            status_in_review: 'In beoordeling',
            status_completed: 'Voltooid',
            campaign_created: 'Beoordelingscampagne aangemaakt',
            review_certified: 'Toegangsbeoordeling gecertificeerd',
            certify_campaign: 'Campagne {id} certificeren',
            policy_id: 'Beleids-ID',
            select_policy: 'Selecteer een beleid...',
            reviewer_ids_label: 'Beoordelaars',
            select_reviewers: 'Selecteer beoordelaars...',
            status_labels: {
                open: 'Openen',
                in_review: 'In beoordeling',
                in_progress: 'In uitvoering',
                cancelled: 'Geannuleerd',
                completed: 'Voltooid',
            },
        },
        evidence_packages: {
            title: 'Bewijspakketten',
            generate_package: 'Bewijspakket genereren',
            export_type: 'Exporttype',
            package_type: 'Pakkettype',
            framework_scope: 'Raamwerkbereik',
            period: 'Periode',
            generated_by: 'Gegenereerd door',
            generated_at: 'Gegenereerd op',
            no_packages_found: 'Geen bewijspakketten gevonden.',
            package_generated: 'Bewijspakket gegenereerd',
            export_type_full: 'Volledige export',
            export_type_summary: 'Samenvatting',
            export_type_certification_only: 'Alleen certificering',
            package_type_access_review: 'Toegangsbeoordeling',
            package_type_investigation: 'Onderzoek',
            package_type_certification: 'Certificering',
            package_type_authority_request: 'Autoriteitsverzoek',
            period_start: 'Begin periode',
            period_end: 'Einde periode',
            select_frameworks: 'Selecteer kaders...',
            completeness_col: 'Volledigheid',
            status_col: 'Status',
            generated_by_col: 'Gegenereerd door',
        },
        evidence_package_detail: {
            title: 'Bewijspakketdetails',
            not_found: 'Bewijspakket niet gevonden.',
            package_metadata: 'Pakketmetadata',
            status: 'Status',
            item_count: 'Aantal items',
            disposition: 'Dispositie',
            closed_at: 'Gesloten op',
            completeness_dashboard: 'Volledigheidsoverzicht',
            no_completeness_data: 'Geen volledigheidsgegevens beschikbaar.',
            controls_covered: 'controles gedekt',
            missing: 'Ontbrekend',
            sign_off_tracker: 'Goedkeuringstracker',
            stage_framework_owner: 'Kadereigenaar',
            stage_legal_compliance: 'Juridisch en Compliance',
            stage_internal_audit: 'Interne audit',
            approved: 'Goedgekeurd',
            returned: 'Geretourneerd',
            pending: 'In afwachting',
            approve: 'Goedkeuren',
            return_btn: 'Retourneren',
            sign_off_submitted: 'Goedkeuring ingediend',
            artifacts: 'Artefacten',
            no_artifacts: 'Geen artefacten gevonden.',
            template_id: 'Sjabloon-ID',
            description: 'Beschrijving',
            classification: 'Classificatie',
            evidence_type: 'Bewijstype',
            framework_section: 'Kadersectie',
            history: 'Goedkeuringsgeschiedenis',
            expand: 'Uitvouwen',
            collapse: 'Invouwen',
            action: 'Actie',
            actor: 'Acteur',
            timestamp: 'Tijdstempel',
            comment: 'Opmerking',
            close_case: 'Zaak sluiten',
            confirm_close: 'Sluiting bevestigen',
            closure_notes: 'Sluitingsnotities',
            disposition_compliant: 'Conform',
            disposition_non_compliant: 'Niet-conform',
            disposition_partial: 'Gedeeltelijk',
            case_closed: 'Bewijspakket gesloten',
            export_package: 'Pakket exporteren',
            export_complete: 'Bewijspakket geëxporteerd',
            completeness_explainer: "A control is one of the organisation's active access policies for the framework. It is covered when at least one access-review certification was recorded in the package period under a campaign of that policy.",
            completeness_not_measured: 'No active access policy is declared for this framework, so there is no control to measure coverage against.',
            artifacts_explainer: "Each artifact is an access-review certification recorded in the package period under a campaign of one of the framework's access policies.",
            policy: 'Access policy',
            decision: 'Decision',
            certified_at: 'Certified at',
            certified_by: 'Certified by',
            decision_approved: 'Access approved',
            decision_revoked: 'Access revoked',
            evidence_type_access_certification: 'Access certification',
        },
    },
    consent_properties: {
        nav: {
            dashboard: 'Overzicht',
            properties: 'Eigenschappen',
            groups: 'Groepen',
            bindings: 'Bindingen',
            change_requests: 'Wijzigingsverzoeken',
            comparison: 'Vergelijking',
            inconsistencies: 'Inconsistenties',
            snapshot_schedules: "Momentopnameschema's",
            golden_templates: 'Gouden sjablonen',
            evidence_packs: 'Bewijspakketten',
            audit: 'Controle',
        },
        select_property: 'Eigenschap selecteren…',
        select_policy: 'Beleid selecteren…',
        select_properties: 'Eigenschappen selecteren…',
        select_target_properties: 'Doeleigenschappen selecteren…',
        select_owner: 'Eigenaar selecteren…',
        select_technical_contact: 'Technisch contact selecteren…',
        property_statuses: {
            active: 'Actief',
            inactive: 'Inactief',
            pending: 'In afwachting',
            decommissioned: 'Buiten gebruik',
        },
        dashboard: {
            title: 'Overkoepelend eigendomsoverzicht',
            loading: 'Laden van dashboard gegevens...',
            total_properties: 'Totaal Eigenschappen',
            active_properties: 'Actief Eigenschappen',
            snapshot_coverage: 'Snapshot-dekking',
            properties_covered: 'eigenschappen gedekt',
            total_bindings: 'Totaal bindingen',
            deployed_bindings: 'Geïmplementeerd',
            pending_bindings: 'Wacht op implementatie',
            deployment_progress: 'Implementatievoortgang',
            on_target: 'Op doel',
            below_target: 'Onder doel',
            target_label: 'Doel',
            total_groups: 'Totaal Groepen',
            pending_changes: 'In afwachting Wijzigingen',
            inconsistencies: 'Inconsistenties',
            recent_activity: 'Recent Activiteit',
            no_recent_activity: 'Geen recente activiteit.',
            fetch_error: 'Kan niet load dashboard. Probeer het opnieuw.',
        },
        properties: {
            title: 'Eigendomsregister',
            new: 'Nieuw Eigenschap',
            edit: 'Bewerken Eigenschap',
            property_name: 'Naam',
            property_type: 'Typ',
            identifier: 'Identificator',
            environment: 'Omgeving',
            owner_id: 'Eigenaar ID',
            technical_contact_id: 'Technisch Contact ID',
            decommission: 'Buiten gebruik stellen',
            confirm_decommission: 'Are you sure you want naar decommission dit property?',
            no_properties: 'Geen eigenschappen gevonden.',
            all_environments: 'Alle Environments',
            all_types: 'Alle Types',
            cmp_instance_id: 'CMP Instantie-ID',
            deployment_date: 'Implementatiedatum',
            identifier_placeholder: 'bijvoorbeeld www.example.com of com.example.app',
            owner_name: 'Eigenaar',
            technical_contact_name: 'Technisch contact',
            sensitive_data_categories: 'Gevoelige gegevenscategorieën',
            sensitive_data_categories_placeholder: 'Selecteer gevoelige gegevenscategorieën',
            requires_enhanced_review: 'Vereist uitgebreide beoordeling',
            sensitive_review_status: 'Gevoelige gegevensbeoordeling',
            sensitive_review_pending: 'In afwachting van beoordeling',
            sensitive_review_approved: 'Goedgekeurd',
            sensitive_review_rejected: 'Afgewezen',
            sensitive_review_submit: 'Beoordeling indienen',
            sensitive_review_approve: 'Goedkeuren',
            sensitive_review_reject: 'Afwijzen',
            sensitive_review_notes: 'Beoordelingsnotities',
            sensitive_review_notes_placeholder: 'Beoordelingsopmerkingen invoeren...',
            record_withdrawal: 'Intrekking registreren',
            withdrawal_subject_id: 'Onderwerp-ID',
            withdrawal_consent_id: 'Toestemmings-ID',
            withdrawal_reason: 'Reden',
            withdrawal_downstream_systems: 'Stroomafwaartse systemen',
            withdrawal_downstream_placeholder: 'systeem-1, systeem-2',
            withdrawal_success: 'De intrekking is met succes doorgevoerd.',
            withdrawal_error: 'Het is niet gelukt om de intrekking door te geven.',
            withdrawal_data_subject_id: 'ID van de betrokkene',
            withdrawal_data_subject_id_placeholder: 'bv. usr_abc123',
            withdrawal_consent_record_id: 'Toestemmingsrecord-ID',
            withdrawal_consent_record_id_placeholder: 'bv. consent_xyz789',
            withdrawal_purposes: 'Ingetrokken doeleinden',
            withdrawal_purposes_placeholder: 'Selecteer doeleinden om toestemming in te trekken voor...',
            withdrawal_withdrawn_at: 'Intrekkingsdatum',
            withdrawal_tasks_created: 'Taken gemaakt',
            withdrawal_affected_bindings: 'Betrokken bindingen',
            withdrawal_affected_activities: 'Betrokken verwerkingsactiviteiten',
            withdrawal_history: 'Intrekkingsgeschiedenis',
        },
        groups: {
            title: 'Eigendomsgroepen',
            new: 'Nieuw Groep',
            group_name: 'Groep Naam',
            member_count: 'Leden',
            no_groups: 'Geen property groups gevonden.',
            policies: 'Beleid',
            confirm_delete: 'Weet je zeker dat je deze groep wilt verwijderen?',
        },
        bindings: {
            title: 'Bindingsmatrix',
            property: 'Eigenschap',
            purpose: 'Doel',
            legal_basis: 'Juridisch Grondslag',
            bound: 'Gebonden',
            no_bindings: 'Geen bindings gevonden.',
            bind_policy: 'Bind beleid',
            bulk_bind: 'Bulkbinding',
            bind_policy_to_property: 'Beleid aan eigendom binden',
            bulk_bind_policy: 'Beleid voor bulkbinding',
            unbound: 'Ongebonden',
            policy_id: 'Beleids-ID',
            version_placeholder: 'bijvoorbeeld 1.0.0',
            property_ids_placeholder: 'prop-id-1, prop-id-2, prop-id-3',
            binding_name: 'Bindend',
            property_name: 'Eigenschapsnaam',
            policy_name: 'Beleidsnaam',
            bindings_list: 'Bindinglijst',
            binding_actions: {
                start_deploy: 'Start implementatie',
                mark_deployed: 'Markeer als geïmplementeerd',
                rollback: 'Terugdraaien',
            },
        },
        change_requests: {
            title: 'Wijzigingsverzoekproces',
            new: 'Nieuw Wijzigen Verzoek',
            requester: 'Aanvrager',
            change_type: 'Wijzigen Type',
            property: 'Eigenschap',
            submitted_at: 'Ingediend At',
            no_requests: 'Geen change requests gevonden.',
            fetch_pipeline_error: 'Kan niet fetch pipeline. Probeer het opnieuw.',
            fetch_list_error: 'Kan niet fetch change requests. Probeer het opnieuw.',
            create_error: 'Kan niet create change request. Probeer het opnieuw.',
            action_error: 'Kan niet perform actie. Probeer het opnieuw.',
            approver_name: 'Goedkeurder',
            placeholder_target_props: 'prop-id-1, prop-id-2',
            view_all: 'Alle',
            view_my_properties: 'Mijn Eigenschappen',
            stage_requests: '{stage}-verzoeken',
            policy_required: "Choose the policy this change applies to. A policy update, a new binding or a binding removal changes that policy's bindings.",
        },
        comparison: {
            title: 'Vergelijkingswerkruimte',
            select_properties: 'Selecteren Eigenschappen naar Vergelijken',
            compare: 'Vergelijken',
            no_comparison: 'Selecteren bij least two eigenschappen naar compare.',
            fetch_error: 'Kan niet fetch comparison reports. Probeer het opnieuw.',
            run_error: 'Kan niet uitvoering comparison. Probeer het opnieuw.',
            save_notes_error: 'Kan niet save opmerkingen. Probeer het opnieuw.',
            scan_complete: 'Inconsistentie scan completed successfully.',
            scan_error: 'Kan niet uitvoering inconsistency scan. Probeer het opnieuw.',
            min_two_properties: "Gelieve provide bij least 2 property ID's voor comparison.",
            view_inconsistencies: 'Inconsistenties bekijken',
            click_to_select: 'Klik om twee of meer eigenschappen te selecteren',
            no_properties_available: 'Geen eigenschappen beschikbaar',
            no_matching_properties: 'Geen overeenkomende eigenschappen',
            select_at_least_two_props: 'Selecteer minimaal twee eigenschappen uit het bovenstaande keuzemenu.',
            no_differences_found: 'Geen verschillen gevonden tussen de geselecteerde eigenschappen.',
            found_inconsistencies: '{count} inconsistenties gevonden in {properties} eigenschappen en {bindings} bindingen.',
        },
        inconsistencies: {
            title: 'Inconsistentielijst',
            property: 'Eigenschap',
            field: 'Veld',
            expected: 'Verwacht',
            actual: 'Werkelijk',
            severity: 'Ernst',
            no_inconsistencies: 'Geen inconsistencies gevonden.',
            trend: 'Inconsistentietrend',
            trend_chart_label: 'Inconsistentietrenddiagram met tellingen in de loop van de tijd',
            acknowledge: 'Bevestigen',
            resolve: 'Oplossen',
            suppress: 'Onderdrukken',
            cr_id_placeholder: 'CR-nummer',
            view_all: 'Alle',
            view_my_properties: 'Mijn Eigenschappen',
            view_dpo_review: 'DPO Recensiewachtrij',
            change_request_id_required: 'De wijzigingsaanvraag-ID is vereist om deze inconsistentie op te lossen.',
        },
        snapshot_schedules: {
            title: "Momentopnameschema's",
            new: 'Nieuw Plannen',
            schedule_name: 'Plannen Naam',
            frequency: 'Frequentie',
            next_run: 'Volgende Uitvoeren',
            last_run: 'Laatste Uitvoeren',
            no_schedules: 'Geen snapshot planningen gevonden.',
            target_type: 'Doeltype',
            target_id: 'Doel-ID',
            custom_frequency_days: 'Aangepaste frequentie (dagen)',
            time_of_day: 'Tijd van de dag',
            time: 'Tijd',
            retention_days: 'Retentie (dagen)',
            execute_now: 'Voer nu uit',
            placeholder_days: 'bijvoorbeeld 10',
            target_name: 'Doelnaam',
        },
        golden_templates: {
            title: 'Gouden sjablonen',
            new: 'Nieuw Sjabloon',
            template_name: 'Sjabloon Naam',
            property_type: 'Eigenschap Type',
            no_templates: 'Geen golden sjablonen gevonden.',
            applicable_group_ids: "Toepasselijke groeps-ID's (door komma's gescheiden)",
            applicable_groups: 'Toepasselijke groepen',
            purposes: 'Doeleinden',
            legal_bases: 'Juridische grondslagen',
            banner_config: 'Bannerconfiguratie',
            consent_settings: 'Toestemmingsinstellingen',
            group_ids_placeholder: 'groep-id-1, groep-id-2',
            apply_to_property: 'Toepassen op Eigenschap',
            apply_to_group: 'Toepassen op Groep',
            apply_success: 'Sjabloon succesvol toegepast.',
            applications: 'Toepassingsgeschiedenis',
            no_applications: 'Nog geen sjabloontoepassingen.',
            no_configuration_details: 'Sjabloon heeft geen configuratiedetails. Bewerk het sjabloon om doeleinden, rechtsgrondslagen, bannerinstellingen of toestemmingsinstellingen toe te voegen.',
        },
        rollouts: {
            title: 'Sjabloonuitrol',
            rollout_status: 'Uitrolstatus',
            confirmed: 'Bevestigd',
            pending: 'In afwachting',
            failed: 'Mislukt',
            confirm: 'Eigenschap bevestigen',
            confirm_success: 'Eigenschap bevestigd in uitrol.',
            can_close: 'Alle eigenschappen bevestigd — uitrol voltooid',
            cannot_close: 'Wachten op eigendomsbevestigingen',
            no_rollouts: 'Geen uitrollingen gevonden.',
            in_progress: 'Bezig',
            completed: 'Voltooid',
            cancelled: 'Geannuleerd',
            reject: 'Eigenschap Afwijzen',
            reject_success: 'Eigenschap afgewezen uit uitrol.',
            confirm_all: 'Alles Bevestigen',
            reject_all: 'Alles Afwijzen',
            confirm_label: 'Bevestig uitrol voor',
            reject_label: 'Wijs uitrol af voor',
            confirm_all_label: 'Alle Wachtende Uitrollingen Bevestigen',
            reject_all_label: 'Alle Wachtende Uitrollingen Afwijzen',
            no_properties_in_rollout: 'Deze uitrol heeft geen eigenschappen.',
        },
        analytics: {
            title: 'Eigenschapsanalyse',
            opt_in_rate: 'Opt-in-percentage',
            opt_out_rate: 'Opt-out-percentage',
            withdrawal_rate: 'Intrekkingspercentage',
            total_consents: 'Totaal toestemmingen',
            total_withdrawals: 'Totaal intrekkingen',
            compliance_score: 'Nalevingsscore',
            non_compliant: 'Niet-conforme eigenschappen',
            no_non_compliant: 'Alle eigenschappen zijn conform.',
            threshold: 'Nalevingsdrempel',
            property_id: 'Property-ID',
            compliant: 'Conform',
            non_compliant_status: 'Niet-conform',
            compliance_help_summary: 'Hoe wordt de nalevingsscore berekend en hoe kan ik dit oplossen?',
            compliance_help_intro: 'Een eigenschap wordt als niet-conform gemarkeerd als de score lager is dan 80%. De score wordt als volgt berekend:',
            compliance_help_with_template_label: 'Als een gouden sjabloon wordt toegepast:',
            compliance_help_with_template_text: '% configuratiesleutels (doeleinden, legal_bases, banner_config, consent_settings) die exact overeenkomen met de sjabloon.',
            compliance_help_no_template_label: 'Als er geen Gouden Sjabloon wordt toegepast:',
            compliance_help_no_template_text: '% bindingen (toestemmingstypen toegewezen aan de property) met deployment_status = geïmplementeerd. Een eigenschap zonder bindingen is standaard 100%.',
            compliance_help_fix_intro: 'Om een eigenschap uit deze lijst te verwijderen, voert u een van de volgende handelingen uit:',
            compliance_help_fix_deploy_label: 'Implementeer de bindingen',
            compliance_help_fix_deploy_text: 'open de pagina Bindingsmatrix en stel de implementatiestatus van elke binding in op geïmplementeerd.',
            compliance_help_fix_template_label: 'Pas een gouden sjabloon toe',
            compliance_help_fix_template_text: 'gebruik Toepassen op groep op de sjabloon en bevestig vervolgens de implementatie voor deze property. Zorg ervoor dat de configuratie van de eigenschap overeenkomt met de sjabloon.',
            compliance_help_fix_decommission_label: 'Ontmantel het pand',
            compliance_help_fix_decommission_text: 'gebruik de knop Ontmanteling in het modale objectdetail als deze niet langer in gebruik is.',
            compliance_help_fix_no_bindings_label: 'Verwijder alle bindingen',
            compliance_help_fix_no_bindings_text: 'als een eigenschap geen sjabloon en geen bindingen heeft, scoort deze standaard 100%.',
        },
        evidence_packs: {
            title: 'Bewijspakketten',
            new: 'Nieuw Pakket',
            pack_name: 'Pakket Naam',
            properties: 'Eigenschappen',
            generated_at: 'Gegenereerd At',
            no_packs: 'Geen bewijs packs gevonden.',
            generate: 'Genereren',
            content_type_consent_records: 'Toestemming Records',
            content_type_policy_versions: 'Beleid Versies',
            content_type_binding_history: 'Binding Geschiedenis',
            content_type_change_requests: 'Wijzigen Verzoeken',
            content_type_inconsistency_reports: 'Inconsistentie Rapporten',
            content_type_snapshot_data: 'Snapshot Gegevens',
            content_type_audit_trail: 'Auditspoor',
        },
        audit: {
            title: 'Toestemmingseigendomscontrole',
            event_type: 'Gebeurtenis Type',
            entity_type: 'Entiteit Type',
            entity_id: 'Entiteit ID',
            actor: 'Betrokkene',
            timestamp: 'Tijdstempel',
            no_events: 'Geen audit gebeurtenissen gevonden.',
            entity_type_property: 'Eigenschap',
            entity_type_group: 'Groep',
            entity_type_binding: 'Bindend',
            entity_type_change_request: 'Wijzigingsverzoek',
            entity_type_golden_template: 'Gouden sjabloon',
            entity_type_snapshot_schedule: 'Momentopnameschema',
            entity_type_inconsistency: 'Inconsistentie',
            entity_type_evidence_pack: 'Bewijspakket',
            action_created: 'Aangemaakt',
            action_updated: 'Bijgewerkt',
            action_deleted: 'Verwijderd',
            action_decommissioned: 'Buiten gebruik',
            action_status_changed: 'Status gewijzigd',
            action_submitted: 'Ingediend',
            action_approved: 'Goedgekeurd',
            action_rejected: 'Afgewezen',
            action_deployed: 'Geïmplementeerd',
            action_rolled_back: 'Teruggedraaid',
            action_paused: 'Gepauzeerd',
            action_resumed: 'Hervat',
            action_cancelled: 'Geannuleerd',
            action_acknowledged: 'Bevestigd',
            action_resolved: 'Opgelost',
            action_suppressed: 'Onderdrukt',
            action_executed: 'Uitgevoerd',
        },
        property_types: {
            website: 'Website',
            mobile_app: 'Mobiele app',
            ott: 'OTT',
            iot_device: 'IoT-apparaat',
            kiosk: 'Kiosk',
            api_service: 'API-dienst',
            sdk: 'SDK',
        },
        environments: {
            production: 'Productie',
            staging: 'Staging',
            development: 'Ontwikkeling',
            testing: 'Testen',
        },
        group_types: {
            brand: 'Merk',
            region: 'Regio',
            product_line: 'Productlijn',
            business_unit: 'Bedrijfseenheid',
            custom: 'Aangepast',
        },
        target_types: {
            property: 'Eigenschap',
            group: 'Groep',
            all: 'Alle',
        },
        frequencies: {
            hourly: 'Elk uur',
            daily: 'Dagelijks',
            weekly: 'Wekelijks',
            monthly: 'Maandelijks',
            custom: 'Aangepast',
        },
        binding_statuses: {
            current: 'Huidig',
            outdated: 'Verouderd',
            pending: 'In afwachting',
            deploying: 'Implementeren',
            rolled_back: 'Teruggedraaid',
        },
        sensitive_categories: {
            health: 'Gezondheidsgegevens',
            biometric: 'Biometrische gegevens',
            genetic: 'Genetische gegevens',
            racial_ethnic: 'Raciale/etnische afkomst',
            political_opinions: 'Politieke meningen',
            religious_beliefs: 'Religieuze overtuigingen',
            trade_union: 'Lidmaatschap van een vakbond',
            sexual_orientation: 'Seksuele geaardheid',
            criminal_records: 'Strafregisters',
            financial: 'Financiële gegevens',
            children: 'Gegevens van kinderen',
            geolocation: 'Nauwkeurige geolocatie',
        },
        export_csv: 'CSV exporteren',
        exporting: 'Exporteren...',
        page_x_of_y: 'Pagina {page} van {total}',
        regulatory: {
            capture_method_guidance: 'PDPL vereist een toestemmingsbewijs dat overeenkomt met het verzamelkanaal. Propertytypen worden toegewezen aan methoden voor het vastleggen van toestemming: website/OTT = op banners gebaseerde toestemming, mobiele app/SDK = toestemmingsstroom in de app, kiosk = fysiek/op het scherm vastleggen van toestemming, API service = programmatische toestemming, IoT-apparaat = toestemmingsmechanisme op apparaatniveau.',
            dashboard_metrics_info: 'Dit dashboard biedt de DPO één weergave van de toestemmingsstatus voor alle digitale eigendommen. Belangrijke statistieken zijn onder meer: ​​het dekkingspercentage van de bindingen (geïmplementeerd vs. totale bindingen), naleving van de template via de inconsistentieswidget, openstaande wijzigingsverzoeken die moeten worden beoordeeld, en een uitsplitsing van de eigendomsstatus met eigendommen met actieve, inactieve of buiten gebruik gestelde statussen. Eigendommen met gevoelige gegevenscategorieën worden gevolgd via het eigendomsregister.',
            change_request_impact_info: 'Wijzigingsverzoeken die van invloed zijn op toestemmingseigenschappen moeten de gevolgen van de regelgeving documenteren: welke PDPL vereisten worden beïnvloed, of de wijziging van invloed is op de verwerking van gevoelige gegevens (art. 2.4) en of voor de wijziging hernieuwde toestemming van de betrokkenen vereist is. In het impactoverzicht in elk wijzigingsverzoek worden de PDPL referenties van de regelgeving voor controle-informatie gedetailleerd beschreven.',
            outdated_binding_warning: "Bindingen met de status 'verouderd' geven aan dat een eigenschap een beleidsversie uitvoert die niet actueel is. Deze eigenschappen weerspiegelen mogelijk niet de nieuwste PDPL-vereisten of updates van het organisatiebeleid. Controleer en update verouderde bindingen onmiddellijk om de naleving van PDPL voor alle toestemmingseigenschappen te behouden.",
        },
    },
    localization: {
        nav: {
            dashboard: 'Overzicht',
            market_packs: 'Marktpakketten',
            language_packs: 'Taalpakketten',
            frameworks: 'Regelgevingskaders',
            mappings: 'Moduletoewijzingen',
            submissions: 'Pakketinzendingen',
            sector_overlay_catalog: 'Overlay Catalog',
            translations: 'Vertalingsvoorbeeld',
            audit: 'Controlespoor',
        },
        dashboard: {
            title: 'Lokalisatieoverzicht',
            loading: 'Laden van dashboard gegevens...',
            active_market_packs: 'Actief Markt Pakketten',
            published_packs_available: 'Gepubliceerd market packs available',
            language_packs: 'Taal Pakketten',
            total_language_packs: 'Totaal language packs registered',
            active_regulatory_frameworks: 'Actief Regelgevend Raamwerken',
            frameworks_active: 'Raamwerken met actief status',
            pending_pack_submissions: 'In afwachting Pakket Indieingen',
            submissions_awaiting_review: 'Indieingen awaiting beoordeling',
            recent_audit_events: 'Recent Audit Gebeurtenissen',
            no_recent_audit: 'No recent audit gebeurtenissen.',
            event_type: 'Gebeurtenis Type',
            entity_type: 'Entiteit Type',
            entity_id: 'Entiteit ID',
            actor: 'Betrokkene',
            timestamp: 'Tijdstempel',
        },
        market_packs: {
            title: 'Marktpakketcatalogus',
            new: 'Nieuw Markt Pakket',
            pack_name: 'Pakket Naam',
            market: 'Markt',
            locale: 'Taalinstelling',
            no_packs: 'Geen market packs gevonden.',
        },
        language_packs: {
            title: 'Taalpakketten',
            new: 'Nieuw Taal Pakket',
            language: 'Taal',
            locale_code: 'Taalinstelling Code',
            completeness: 'Volledigheid',
            completeness_detail: 'Volledigheidsdetail',
            overall_completeness: 'Algemene volledigheid',
            total_keys: 'Totaal aantal sleutels',
            translated_keys: 'Vertaalde sleutels',
            missing_keys: 'Ontbrekende sleutels',
            namespace_breakdown: 'Uitsplitsing van de naamruimte',
            namespace: 'Naamruimte',
            translated: 'Vertaald',
            no_packs: 'Geen language packs gevonden.',
            install_language_pack: 'Taalpakket installeren',
            available_packs: 'Beschikbare taalpakketten',
            install: 'Installeren',
            all_installed: 'Alle taalpakketten zijn al geïnstalleerd.',
            install_success: 'Taalpakket succesvol geïnstalleerd.',
            market: 'Markt',
            featured: 'Uitgelicht',
        },
        frameworks: {
            // Sprint 63.32.0 (#543) — every framework_type code, in the reader's language.
            type_national_law: 'National law',
            type_parent_framework: 'Parent framework',
            type_child_decree: 'Child decree',
            type_child_decision: 'Child decision',
            type_child_procedure_decree: 'Procedural decree',
            type_conditional_child_decree: 'Conditional child decree',
            type_implementing_decree: 'Implementing decree',
            type_enforcement_decree: 'Sanctions decree',
            type_sanctions_reference: 'Sanctions reference',
            type_official_guidance: 'Official guidance',
            type_guidance_overlay: 'Guidance overlay',
            type_sector_overlay: 'Sector overlay',
            type_security_overlay: 'Security overlay',
            type_security_control_overlay: 'Security control overlay',
            type_temporary_procedural_overlay: 'Temporary procedural overlay',
            type_cross_sector: 'Cross-sector',
            type_directive: 'Directive',
            type_regulation: 'Regulation',
            type_national_transposition: 'National transposition',
            type_international_standard: 'International standard',
            title: 'Regelgevingskaders',
            new: 'Nieuw Raamwerk',
            framework_name: 'Raamwerk Naam',
            jurisdiction: 'Jurisdictie',
            effective_date: 'Effectief Datum',
            no_frameworks: 'Geen regulatory frameworks gevonden.',
            view_requirements: 'Bekijk vereisten',
            hide_requirements: 'Verberg vereisten',
            show_legacy: 'Toon verouderde kaders',
            effective_until: 'Geldig tot',
            effective_on: 'Geldig vanaf',
            replaced_by: 'Vervangen door',
            replaces_label: 'Vervangt',
            status_active: 'Actief',
            status_draft: 'Concept',
            status_unclassified: 'Unrecognised status',
            status_legacy: 'Verouderd',
            status_upcoming: 'Aanstaand',
            status_active_reference: 'In force, reference only',
            status_temporary_active: 'Temporarily in force',
            watchlist: 'Volglijst',
            watchlist_empty: 'Geen concept- of aanstaande kaders',
            watchlist_empty_desc: 'Frameworks met de status "concept" of "aankomend" zullen hier verschijnen voor monitoring.',
            expected_effective: 'Verwacht effectief',
            effective_date_tbd: 'Ingangsdatum: nader vast te stellen',
            activate: 'Activeer',
            ai_law_title: 'Vietnam AI Wet',
            ai_risk_classification: 'AI Risicoclassificatie',
            ai_system_dossier: 'AI Systeemdossier',
            ai_labeling_checklist: 'AI Etikettering en openbaarmaking',
            ai_incident_report: 'AI Incidentrapport',
            ai_impact_assessment: 'AI Effectbeoordeling',
            ai_sandbox_application: 'Sandbox-applicatie',
            ai_sandbox_monitoring: 'Sandbox-monitoring',
            ai_sandbox_completion: 'Sandbox-voltooiing',
            ai_submission_tracker: 'Inzendingstracker',
            ai_evidence_capture: 'Bewijs vastleggen',
            risk_level_minimal: 'Minimaal risico',
            risk_level_limited: 'Beperkt risico',
            risk_level_high: 'Hoog risico',
            risk_level_unacceptable: 'Onaanvaardbaar risico',
            submission_status_draft: 'Diepgang',
            submission_status_submitted: 'Ingezonden',
            submission_status_returned: 'Teruggestuurd voor wijziging',
            submission_status_accepted: 'Geaccepteerd',
            submission_status_rejected: 'Afgewezen',
            submission_status_closed: 'Gesloten',
            ecommerce_law_title: 'Vietnamees e-commercerecht',
            ecommerce_platform_classification: 'Platformclassificatie',
            ecommerce_seller_register: 'Verkopersregister',
            ecommerce_complaint_register: 'Klachtenregister',
            ecommerce_takedown_register: 'Verwijderingsregister',
            ecommerce_submission_tracking: 'Inzendingen volgen',
            ecommerce_dossier_checklist: 'Dossierchecklist',
            ecommerce_cross_border: 'Grensoverschrijdende naleving',
            ecommerce_trustmark: 'Trustmark-programma',
            ecommerce_notification: 'Kennisgeving',
            ecommerce_registration: 'Registratie',
            ecommerce_termination: 'Beëindiging',
            ecommerce_reporting_readiness: 'Rapportagegereedheid',
            ecommerce_evidence_capture: 'Bewijs vastleggen',
            submission_status_approved: 'Goedgekeurd',
            cybersec_law_2025_title: 'Vietnamese cyberveiligheidswet (2025)',
            cybersec_system_classification: 'Systeemclassificatie',
            cybersec_risk_assessment: 'Risicobeoordeling',
            cybersec_incident_report: 'Incidentrapport',
            cybersec_authority_cooperation: 'Samenwerking tussen autoriteiten',
            cybersec_data_localization: 'Gegevenslokalisatie',
            cybersec_vendor_security: 'Beveiliging van leveranciers',
            cybersec_submission_tracker: 'Inzendingstracker',
            cybersec_evidence_capture: 'Bewijs vastleggen',
            cybersec_dossier_checklist: 'Dossierchecklist',
            cybersec_classification_general: 'Algemeen',
            cybersec_classification_important: 'Belangrijk',
            cybersec_classification_critical: 'Kritisch',
            data_law_title: 'Gegevenswet Vietnam',
            data_inventory: 'Gegevensinventarisatie',
            data_classification: 'Gegevensclassificatie',
            data_sharing: 'Gegevens delen',
            data_governance: 'Gegevensbeheer',
            data_submission_tracker: 'Inzendingstracker',
            data_evidence_capture: 'Bewijs vastleggen',
            data_dossier_checklist: 'Dossierchecklist',
            telecom_law_title: 'Telecomwet van Vietnam',
            telecom_authority_request: 'Autoriteitsverzoek',
            telecom_disclosure: 'Openbaarmaking',
            telecom_confidentiality: 'Vertrouwelijkheid',
            telecom_submission_tracker: 'Inzendingstracker',
            telecom_evidence_capture: 'Bewijs vastleggen',
            telecom_dossier_checklist: 'Dossierchecklist',
            pdpl_law_title: 'Vietnamese wet op de bescherming van persoonsgegevens',
            pdpl_breach_notification: 'Melding van inbreuk',
            pdpl_dpo_notification: 'DPO Afspraakmelding',
            pdpl_data_subject_notification: 'Kennisgeving van de betrokkene',
            pdpl_submission_tracker: 'Inzendingstracker',
            pdpl_evidence_capture: 'Bewijs vastleggen',
            pdpl_dossier_checklist: 'Dossierchecklist',
            pdpl_authority_filing: 'Autoriteit indienen',
            pdpl_mops_filing: 'MoPS-archivering',
        },
        mappings: {
            title: 'Moduletoewijzingen',
            new: 'Nieuw Toewijzing',
            module: 'Onderdeel',
            // fix-sprint #447 W8 — module labels, from the bundle (no `{en, vi}` map on the page).
            module_consent: 'Toestemming',
            module_dpia: 'DPIA',
            module_incident: 'Voorval',
            module_rights_request: 'Rechtenverzoek',
            module_data_mapping: 'Gegevens in kaart brengen',
            module_classification: 'Classificatie',
            module_monitoring: 'Toezicht',
            module_governance: 'Bestuur',
            module_vendor: 'Verkoper',
            module_risk: 'Risico',
            module_sector_licensing: 'Sector Licensing',
            module_investor_verification: 'Investor Verification',
            module_account_data_governance: 'Account Data Governance',
            module_regulatory_disclosure: 'Regulatory Disclosure',
            module_authority_direction: 'Authority Direction',
            module_issued_record: 'Issued Records',
            module_publication_assessment: 'Publication Assessment',
            module_legal_deposit: 'Legal Deposit',
            module_legal_representative: 'Legal Representative',
            market_pack: 'Markt Pakket',
            mapping_status: 'Toewijzing Status',
            // fix-sprint #431 — the list count, chosen by `count === 1` (the bundle's `count_one` convention).
            count: '{count} mappings',
            count_one: '{count} mapping',
            no_mappings: 'Geen module toewijzingen gevonden.',
            incomplete_entries_warning: '{count} toewijzing(en) verborgen vanwege ontbrekend framework-ID, vereiste-ID of module.',
            // fix-sprint #431 — seeding is a person's action, never a side effect of opening the page.
            seed_action: 'Create mappings from installed frameworks',
            seed_hint: 'Creates a direct mapping for each requirement that names a module, in every installed framework that has no mappings yet. Opening this page creates nothing.',
            seed_confirm_title: 'Create mappings from installed frameworks?',
            seed_confirm: 'This creates a mapping for every requirement that names a module, in each installed framework that has no mappings yet. It can add many rows, and each can be edited or deleted afterwards.',
            seed_done: '{count} mapping(s) created.',
            seed_none: 'No mappings were created: every installed framework already has mappings, or none of its requirements names a module.',
            seed_error: 'The mappings could not be created.',
            // fix-sprint #431 (gate E12.5 remediation 1) — a failed list read says so, and nothing else.
            load_error: 'The module mappings could not be read, so none are shown and none can be created from installed frameworks here.',
        },
        submissions: {
            title: 'Pakketinzendingen',
            new: 'Nieuw Indiening',
            submitter: 'Indiener',
            pack: 'Pakket',
            submitted_at: 'Ingediend At',
            no_submissions: 'Geen pakket indieingen gevonden.',
            installed_sector_overlays_title: 'Sector Overlays ({count}) [NL]',
            installed_sector_overlays_aria: 'Installed Sector Overlays [NL]',
            installed_col_name: 'Name [NL]',
            installed_col_code: 'Code [NL]',
            installed_col_sector: 'Sector [NL]',
            installed_col_mode: 'Mode [NL]',
            installed_col_status: 'Status [NL]',
            installed_refresh: 'Refresh [NL]',
            installed_count_one: '1 installed pack',
            installed_count: '{count} installed packs',
            installed_market_packs_title: 'Market Packs ({count})',
            installed_market_packs_aria: 'Installed Market Packs',
            installed_language_packs_title: 'Language Packs ({count})',
            installed_language_packs_aria: 'Installed Language Packs',
            installed_frameworks_title: 'Regulatory Frameworks ({count})',
            installed_frameworks_aria: 'Installed Regulatory Frameworks',
            installed_col_market_code: 'Market Code',
            installed_col_display_name: 'Display Name',
            installed_col_locale: 'Locale',
            installed_col_version: 'Version',
            tab_submissions: 'Submissions',
            tab_install_zip: 'Install from Zip',
            tab_installed: 'Installed Packs',
        },
        translations: {
            title: 'Vertalingsvoorbeeld',
            select_locale: 'Selecteren Taalinstelling',
            select_module: 'Selecteren Module',
            key: 'Key',
            value: 'Waarde',
            no_translations: 'Geen translations gevonden.',
        },
        audit: {
            title: 'Lokalisatie Audit Trail',
            event_type: 'Gebeurtenis Type',
            // Gate E12.5 remediation 1 (fix-sprint #431) — Module Mappings audit events: the key is
            // `event_type_` + the recorded action with `.` read as `_`.
            event_type_regulatory_mapping_created: 'Module mapping created',
            event_type_regulatory_mapping_updated: 'Module mapping updated',
            event_type_regulatory_mapping_deleted: 'Module mapping deleted',
            event_type_regulatory_mapping_seeded_from_frameworks: 'Mappings created from installed frameworks',
            // Gate E12.5 remediation 2 — every other event the localization services record (event_type, else action).
            event_type_market_pack_created: 'Market pack created',
            event_type_market_pack_updated: 'Market pack updated',
            event_type_market_pack_status_changed: 'Market pack status changed',
            event_type_market_pack_installed: 'Market pack installed',
            event_type_market_pack_uninstalled: 'Market pack uninstalled',
            event_type_pack_created: 'Market pack created',
            event_type_pack_updated: 'Market pack updated',
            event_type_pack_status_changed: 'Market pack status changed',
            event_type_pack_installed: 'Market pack installed',
            event_type_pack_uninstalled: 'Market pack uninstalled',
            event_type_cluster_installed: 'Market pack cluster installed',
            event_type_pack_installed_from_zip: 'Pack installed from an uploaded file',
            event_type_pack_zip_install_refused: 'Uploaded pack refused: it carries no signature',
            event_type_pack_submission_created: 'Pack submission created',
            event_type_pack_submission_review_started: 'Pack submission review started',
            event_type_pack_submission_comment_added: 'Comment added to a pack submission',
            event_type_pack_submission_approved: 'Pack submission approved',
            event_type_pack_submission_rejected: 'Pack submission rejected',
            event_type_pack_artifact_created: 'Pack artifact created',
            event_type_pack_artifact_updated: 'Pack artifact updated',
            event_type_pack_artifact_deleted: 'Pack artifact deleted',
            event_type_regulatory_framework_updated: 'Framework updated',
            event_type_regulatory_framework_status_changed: 'Framework status changed',
            event_type_regulatory_framework_requirement_added: 'Requirement added to a framework',
            event_type_regulatory_framework_requirement_removed: 'Requirement removed from a framework',
            event_type_regulatory_framework_stack_layer_status_changed: 'Framework Stack layer status changed',
            event_type_regulatory_framework_transition_review_updated: 'Transition review updated',
            event_type_language_pack_created: 'Language pack created',
            event_type_language_pack_updated: 'Language pack updated',
            event_type_language_pack_activated: 'Language pack activated',
            event_type_language_pack_deactivated: 'Language pack deactivated',
            event_type_language_pack_installed: 'Language pack installed',
            event_type_language_pack_uninstalled: 'Language pack uninstalled',
            event_type_sector_overlay_installed: 'Sector overlay installed',
            event_type_sector_overlay_uninstalled: 'Sector overlay uninstalled',
            event_type_regulatory_overlay_installed: 'Regulatory overlay installed',
            event_type_regulatory_overlay_uninstalled: 'Regulatory overlay uninstalled',
            event_type_installed_framework_mode_changed: 'Installed framework mode changed',
            event_type_phased_obligation_early_implemented: 'Phased obligation marked early-implemented',
            entity_type: 'Entiteit Type',
            entity_id: 'Entiteit ID',
            entity_name: 'Entiteitsnaam',
            actor: 'Betrokkene',
            timestamp: 'Tijdstempel',
            payload: 'Laadvermogen',
            collapse_payload: 'Payloaddetails inklappen',
            payload_none: 'No details recorded',
            payload_one_field_recorded: '1 field recorded',
            payload_fields_recorded: '{count} fields recorded',
            payload_one_field_changed: '1 field changed',
            payload_fields_changed: '{count} fields changed',
            payload_one_field_removed: '1 field removed',
            payload_fields_removed: '{count} fields removed',
            payload_col_field: 'Field',
            payload_col_value: 'Value',
            payload_col_before: 'Before',
            payload_col_after: 'After',
            payload_empty_value: '(empty)',
            payload_not_set: 'Not set',
            proof_not_anchored: 'Not anchored',
            proof_not_anchored_tooltip: 'No proof record exists for this event. Events written before Immutable Audit Trails was enabled are not anchored, and events in batch mode appear here once their batch is sealed.',
            expand_payload: 'Payloaddetails uitklappen',
            collapse: 'Inklappen',
            expand: 'Uitklappen',
            all_entity_types: 'Alle entiteitstypen',
            no_events: 'Geen audit gebeurtenissen gevonden.',
        },
        market_packs_detail: {
            installed_at: 'Geïnstalleerd op',
            pack_details_label: 'Pakketdetails',
            market_code: 'Marktcode',
            supported_locales: 'Ondersteunde talen',
            min_platform_version: 'Minimale platformversie',
            pack_information: 'Pakketinformatie',
            pack_contents: 'Inhoud verpakking',
            included_frameworks: 'Inbegrepen raamwerken',
            pack_artifacts: 'Pakketartefacten',
            no_artifacts: 'Geen artefacten gevonden voor dit pakket',
            pack_not_found: 'Pakket niet gevonden',
        },
        mappings_form: {
            framework_id: 'Raamwerk-ID',
            requirement_id: 'Vereiste-ID',
            module_label: 'Module',
            mapping_type: 'Toewijzingstype',
            configuration_json: 'Configuratie (JSON)',
            conflict_resolution: 'Conflictoplossing',
            is_override: 'Is overschrijving',
            all_modules: 'Alle modules',
            all_mapping_types: 'Alle toewijzingstypen',
        },
        submissions_detail: {
            submission_id: 'Inzendings-ID',
            validation: 'Validatie',
            reviewers: 'Beoordelaars',
            start_review: 'Beoordeling starten',
            hide_comments: 'Opmerkingen verbergen',
            start_review_named: 'Start Review for {name}',
            approve_submission_named: 'Approve submission {name}',
            reject_submission_named: 'Reject submission {name}',
            comments_named: 'Comments for {name}',
            hide_comments_named: 'Hide comments for {name}',
            review_comments: 'Beoordelingscommentaren',
            loading_comments: 'Opmerkingen laden...',
            no_comments: 'Nog geen opmerkingen.',
            add_comment: 'Opmerking toevoegen',
            reject_submission: 'Inzending afwijzen',
            reason_for_rejection: 'Reden voor afwijzing',
            reviewer_ids: "Beoordelaars-IDs (door komma's gescheiden)",
            submit_new_pack: 'Nieuw pakket indienen',
            pack_type: 'Pakkettype',
            market_code: 'Marktcode',
            target_locales: 'Doeltalen',
            pack_file: 'Pakketbestand',
            pack_format_info: 'Pakketindeling: marktpakketten moeten landspecifieke JSON-bestanden met naamruimte/sleutelvertalingen bevatten. Taalpakketten moeten vertalingen bieden voor de opgegeven doellandinstellingen. Zie documentatie voor gedetailleerd schema.',
            accepted_formats: 'Geaccepteerde formaten: .json of .zip. JSON-bestanden moeten een plat object of een object met een naamruimte van vertaalsleutel-waardeparen bevatten. ZIP-bestanden moeten een of meer JSON locale-bestanden bevatten.',
            comma_separated_locales: "Door komma's gescheiden taalcodes voor dit taalpakket",
            confirm_approval: 'Goedkeuring bevestigen',
            approve_message: 'Deze pakketinzending goedkeuren?',
            submission_label: 'Inzending',
            all_pack_types: 'Alle pakkettypen',
            passed: 'geslaagd',
            failed_label: 'mislukt',
            market_code_required: 'Marktcode is vereist',
            version_required: 'Versie is vereist',
            pack_type_regulatory: 'Regelgevend',
            framework_code: 'Raamwerkcode',
            jurisdiction_code: 'Jurisdictiecode',
            pack_source: 'Bron',
            pack_source_manual: 'Handmatig',
            pack_version: 'Pakketversie',
            install_regulatory_pack: 'Regelgevend pakket installeren',
            regulatory_pack_desc: 'Upload een regelgevend raamwerkpakket (.zip)',
            framework_required: 'Kaderregelgevingcode is vereist',
            jurisdiction_required: 'Jurisdictiecode is vereist',
        },
        catalog_detail: {
            packs_installed_of: 'van {total} pakketten geïnstalleerd',
            install_all: 'Alles installeren',
            installing: 'Installeren...',
            removing: 'Verwijderen...',
            install_all_named: 'Install all packs in {name}',
            installing_named: 'Installing... {name}',
            uninstall_named: 'Uninstall {name}',
            removing_named: 'Removing... {name}',
            manage_no_permission: 'Installing or uninstalling a market pack needs permission to manage market packs.',
            available: 'Available',
            cluster_installed_count: '({installed}/{total} installed)',
            frameworks_count_one: '1 framework',
            frameworks_count: '{count} frameworks',
            languages_count_one: '1 language',
            languages_count: '{count} languages',
            forms_count_one: '1 form',
            forms_count: '{count} forms',
            artifacts_count_one: '1 artifact',
            artifacts_count: '{count} artifacts',
            market_packs_count_one: '1 market pack',
            market_packs_count: '{count} market packs',
            install_success: 'Installed {market}: {frameworks}, {items}.',
            cluster_install_success: 'Cluster installed: {frameworks}, {forms}.',
            uninstall_success: 'Uninstalled {market}: removed {frameworks} and {artifacts}.',
            uninstall_confirm: 'Uninstall {market}? This removes all frameworks, artifacts and language packs for this market.',
            dependents_exist: 'Cannot uninstall {market}: other installed packs depend on it.',
            dependencies_required: '{market} needs {dependencies} installed first.',
            dependencies_required_unnamed: '{market} needs another market pack installed first.',
            requires: 'Vereist',
            tab_catalog: 'Catalogus',
            tab_installed: 'Geïnstalleerd',
            details: 'Details',
        },
        // Sprint 63.33.0 (#594) — standards packs installed on their own.
        standalone_packs: {
            title: 'Standards',
            description: 'International standards are installed on their own: no market pack includes them. Installing one adds its frameworks, requirements and forms for your organisation.',
            install: 'Install',
            install_named: 'Install {name}',
            installing: 'Installing…',
            installing_named: 'Installing… {name}',
            installed: 'Installed',
            installed_other_route: 'Installed by another route',
            installed_other_route_hint: 'Its frameworks are present for your organisation, but this standard did not install them: another pack or a record created by hand did. There is nothing for it to remove here.',
            confirm_title: 'Install {name}?',
            confirm_message: 'This installs {name} for your organisation, with the frameworks, requirements and forms it carries.',
            confirm_label: 'Install',
            install_success: '{name} installed: {frameworks}, {forms}.',
            install_failed: 'The standard could not be installed.',
            install_no_permission: 'Installing a standard needs permission to change regulatory frameworks.',
            remove: 'Remove',
            remove_named: 'Remove {name}',
            removing: 'Removing…',
            removing_named: 'Removing… {name}',
            remove_confirm_title: 'Remove {name}?',
            remove_confirm_message: 'This removes {name} from your organisation, with the frameworks, requirements, deadlines and forms it installed, and every Module Mapping recorded against its frameworks, including mappings your organisation created or edited. Installing it again does not restore those mappings. Forms your organisation has filled in are kept. A removal cannot be undone.',
            remove_confirm_label: 'Remove',
            remove_success: '{name} removed: {frameworks}, {forms}.',
            remove_failed: 'The standard could not be removed.',
            remove_no_permission: 'Removing a standard needs permission to change regulatory frameworks.',
            refusal_already_installed: 'This standard is already installed for your organisation.',
            refusal_not_installable_alone: 'This pack is not installed on its own here: it is installed with its market, or from the sector overlay catalogue.',
            refusal_not_found: 'This standard is no longer in the catalogue. Reload the page.',
            refusal_not_installed: 'This standard is not installed for your organisation, so there is nothing to remove.',
            refusal_installed_by_another_route: 'This standard did not install its frameworks for your organisation, so it has nothing to remove. Remove them where they were installed.',
            refusal_forbidden: 'You do not have permission to change regulatory frameworks.',
            refusal_custody: 'This standard installs restricted registers; installing it requires the regulatory-artifact custody permission.',
        },
        translations_detail: {
            source_locale: 'Brontaal',
            target_locale: 'Doeltaal',
            locales_must_differ: 'Bron- en doeltaal moeten verschillend zijn.',
            all_keys_translated: 'Alle sleutels vertaald',
            keys_missing: '{count} sleutel(s) ontbreken',
            show_missing_only: 'Alleen ontbrekende tonen',
            namespace: 'Naamruimte',
            source: 'Bron',
            target: 'Doel',
        },
        frameworks_detail: {
            // fix-sprint-20260923-2136 — the detail page's own states, before the
            // framework record has been read (title, missing code, not found, failed).
            page_title_fallback: 'Framework',
            page_unavailable_heading: 'Framework unavailable',
            page_missing_code: 'No framework code was given in the address.',
            page_not_found: 'Framework {code} not found.',
            page_not_found_generic: 'Framework not found.',
            page_load_failed: 'The framework could not be loaded.',
            tab_frameworks: 'Raamwerken',
            tab_watchlist: 'Volglijst',
            authority: 'Autoriteit',
            requirements_count: 'Vereisten',
            all_types: 'Alle typen',
            collapse_all: 'Alles inklappen',
            expand_all: 'Alles uitklappen',
            all_sectors: 'All sectors',
            filter_by_sector: 'Filter by sector',
            search_frameworks: 'Search by name or code...',
            sef_url_label: 'Framework URL',
            sef_url_copy: 'Copy',
            sef_url_copy_label: 'Copy framework URL',
            sef_url_copied: 'Framework URL copied',
            sef_url_copy_failed: 'Could not copy the framework URL',
            filtered_to: 'Gefilterd op',
            clear_filter: 'Filter wissen',
            no_requirements: 'Geen vereisten gevonden voor dit raamwerk.',
            requirements_label: 'Vereisten',
            status_updated: 'Status succesvol bijgewerkt',
            overview_tab: 'Overzicht',
            forms_tab: 'Formulieren',
            procedures_tab: 'Procedures',
            fines_risks_tab: "Boetes en risico's",
            deadlines_tab: 'Deadlines',
            guidance_tab: 'Begeleiding',
            guidance_empty: 'Er is geen begeleidingsinhoud voor dit raamwerk.',
            guidance_loading: 'Begeleiding laden…',
            guidance_section: 'Begeleidingssectie',
            guidance_view: 'Begeleiding bekijken',
            guidance_map_to_workflow: 'Toewijzen aan workflow',
            guidance_link_evidence: 'Bewijs koppelen',
            guidance_filter_type: 'Typ',
            guidance_filter_category: 'Categorie',
            guidance_filter_module: 'Module',
            guidance_filter_status: 'Status',
            guidance_filter_search: 'Zoekbegeleiding',
            guidance_filter_all: 'Allemaal',
            guidance_table_reference: 'Referentie',
            guidance_table_name: 'Begeleidingsnaam',
            guidance_table_type: 'Typ',
            guidance_table_category: 'Categorie',
            guidance_table_effective_date: 'Ingangsdatum',
            guidance_table_modules: 'Gerelateerde modules',
            guidance_table_actions: 'Acties',
            guidance_reference_only_banner: 'Alleen referentie: geldt voor de sector {{sector}}, maar niet voor uw organisatie.',
            guidance_open_official_source: 'Officiële bron openen',
            guidance_no_detail: 'Geen aanvullende details beschikbaar voor deze richtlijn.',
            enum_guidance_type_cross_framework_note: 'Cross-Framework Note',
            enum_guidance_type_draft_status_note: 'Draft Status Note',
            enum_guidance_type_framework_hierarchy_note: 'Framework Hierarchy Note',
            enum_guidance_type_framework_overlay_note: 'Framework Overlay Note',
            enum_guidance_type_implementing_decree_summary: 'Implementing Decree Summary',
            enum_guidance_type_practical_unofficial_practice: 'Practical (Unofficial) Practice',
            enum_guidance_category_auction: 'Auction',
            enum_guidance_category_complaint_dispute: 'Complaint & Dispute',
            enum_guidance_category_draft_governance: 'Draft Governance',
            enum_guidance_category_email_zip_submission: 'Email/ZIP Submission',
            enum_guidance_category_exchange_classification: 'Exchange Classification',
            enum_guidance_category_framework_hierarchy: 'Framework Hierarchy',
            enum_guidance_category_high_risk_ai: 'High-Risk AI',
            enum_guidance_category_lifecycle_controls: 'Lifecycle Controls',
            enum_guidance_category_listing_review: 'Listing Review',
            enum_guidance_category_national_database_access: 'National Database Access',
            enum_guidance_category_outsourced_processing: 'Outsourced Processing',
            enum_guidance_category_participant_registration: 'Participant Registration',
            enum_guidance_category_personal_data_linkage: 'Personal Data Linkage',
            enum_guidance_category_personnel_conditions: 'Personnel Conditions',
            enum_guidance_category_pricing_and_public_asset: 'Pricing & Public Asset',
            enum_guidance_category_procedural_overlay: 'Procedural Overlay',
            enum_guidance_category_public_sector_ai: 'Public-Sector AI',
            enum_guidance_category_requirement_detail: 'Requirement Detail',
            enum_guidance_category_responsibilities: 'Responsibilities',
            enum_guidance_category_risk_and_remediation: 'Risk & Remediation',
            enum_guidance_category_sandbox: 'Sandbox',
            enum_guidance_category_scope: 'Scope',
            enum_guidance_category_security_drills_incidents: 'Security Drills & Incidents',
            enum_guidance_category_submission_channel: 'Submission Channel',
            enum_guidance_category_transaction_evidence: 'Transaction Evidence',
            enum_guidance_category_transparency_labeling: 'Transparency Labeling',
            enum_module_evidence_management: 'Evidence Management',
            enum_module_security: 'Security',
            form_category_access: 'Access',
            form_category_application: 'Application',
            form_category_authority_response_record: 'Authority Response Record',
            form_category_business_plan: 'Business Plan',
            form_category_capital_adequacy: 'Capital Adequacy',
            form_category_capital_adequacy_report: 'Capital Adequacy Report',
            form_category_certificate: 'Certificate',
            form_category_classification: 'Classification',
            form_category_compliance: 'Compliance',
            form_category_cross_border_transfer: 'Cross-Border Transfer',
            form_category_decision: 'Decision',
            form_category_email_submission_record: 'Email Submission Record',
            form_category_evidence: 'Evidence',
            form_category_evidence_pack: 'Evidence Pack',
            form_category_evidence_pack_manifest: 'Evidence Pack Manifest',
            form_category_forms: 'Forms',
            form_category_information_system: 'Information System',
            form_category_internal_audit: 'Internal Audit',
            form_category_internal_audit_report: 'Internal Audit Report',
            form_category_internal_control: 'Internal Control',
            form_category_internal_control_report: 'Internal Control Report',
            form_category_model_risk: 'Model Risk',
            form_category_notification: 'Notification',
            form_category_official_certificate: 'Official Certificate',
            form_category_official_reference: 'Official Reference',
            form_category_policy: 'Policy',
            form_category_record: 'Record',
            form_category_remediation: 'Remediation',
            form_category_report: 'Report',
            form_category_reporting: 'Reporting',
            form_category_risk: 'Risk',
            form_category_risk_data: 'Risk Data',
            form_category_risk_management_report: 'Risk Management Report',
            form_category_sandbox: 'Sandbox',
            form_category_security_plan: 'Security Plan',
            form_category_technical_plan: 'Technical Plan',
            form_category_training: 'Training',
            unit_absolute_date: 'Absolute date',
            unit_annual: 'Annual',
            unit_before_transfer: 'Before transfer',
            unit_before_use: 'Before use',
            unit_calendar_days_after_event: 'Calendar days after event',
            unit_calendar_days_before_event: 'Calendar days before event',
            unit_date: 'Date',
            unit_days: 'Days',
            unit_event: 'Event',
            unit_hours_from_authentication: 'Hours from authentication',
            unit_month: 'Month',
            unit_months: 'Months',
            unit_months_from_future_effective_date: 'Months from future effective date',
            unit_on_request: 'On request',
            unit_quarterly: 'Quarterly',
            unit_upon_request: 'Upon request',
            unit_weeks: 'Weeks',
            unit_without_undue_delay: 'Without undue delay',
            unit_working_days_before_event: 'Working days before event',
            unit_year: 'Year',
            unit_year_or_material_change: 'Year or material change',
            unit_years: 'Years',
            unit_years_minimum: 'Years (minimum)',
            unit_day: 'Day',
            unit_week: 'Week',
            sla_type_phased_installation_obligation: 'Phased installation obligation',
            sla_type_transitional_window_end: 'Transitional period ends',
            sla_type_data_retention_minimum: 'Data retention, minimum',
            sla_type_authority_appraisal_result_d356_as_issued: 'Authority’s appraisal result (text as issued)',
            sla_type_authority_appraisal_result_amended: 'Authority’s appraisal result (amended text)',
            sla_type_authority_appraisal_result_nq22: 'Authority’s appraisal result (transitional text)',
            sla_type_transitional_end_date: 'Automatic-debit transition ends',
            sla_type_pre_launch_report_submission: 'Pre-launch report to the State Bank',
            sla_type_testing_term_ceiling: 'Maximum testing term',
            sla_type_end_of_purpose_disposition: 'Return, deletion or destruction when the purpose ends',
            sla_type_publicity_on_regulator_requirement: "Notice and publicity on the regulator's requirement",
            sla_type_information_on_regulator_request: "Information provided on the regulator's request",
            sla_type_depositor_mismatch_handling_period: "Handling period for a depositor's mismatch report",
            sla_type_depositor_lookup_start: 'Depositor lookup start',
            sla_type_periodic_security_assessment: 'Annual security assessment',
            sla_type_periodic_software_list_review: 'Approved software list review',
            sla_type_periodic_risk_review: 'Risk situation review',
            sla_type_periodic_continuity_drill: 'Continuity drill',
            sla_type_periodic_staff_training: 'Staff security training',
            sla_type_record_retention_minimum: 'Online log retention, minimum',
            sla_type_record_backup_minimum: 'Log backup retention, minimum',
            sla_type_periodic_vulnerability_scan: 'Internet-facing vulnerability scan',
            sla_type_remediation_patch_window_critical_internet_facing: 'Critical vulnerability patch, component directly connected to the Internet',
            sla_type_remediation_patch_window_critical_other: 'Critical vulnerability patch, other components',
            sla_type_remediation_patch_window_high_internet_facing: 'High-rated vulnerability patch, component directly connected to the Internet',
            sla_type_remediation_patch_window_high_other: 'High-rated vulnerability patch, other components',
            sla_type_periodic_security_review: 'Mobile app security review',
            sla_type_credential_max_validity: 'PIN and password, maximum validity',
            sla_type_credential_default_validity: 'Credential, default validity',
            sla_type_otp_max_validity_sms: 'SMS OTP, maximum validity',
            sla_type_otp_max_validity_voice: 'Voice OTP, maximum validity',
            sla_type_otp_max_validity_email: 'Email OTP, maximum validity',
            sla_type_otp_max_validity_matrix_card: 'OTP from an OTP matrix card, maximum validity',
            sla_type_otp_max_validity_soft: 'Soft OTP code, maximum validity',
            sla_type_otp_max_validity_token: 'Token OTP, maximum validity',
            sla_type_card_max_usable_life: 'OTP matrix card, maximum usable life',
            sla_type_confirmation_request_validity: 'Two-channel confirmation request, validity',
            sla_type_biometric_match_time_limit_device: 'Device biometric match, time limit',
            sla_type_biometric_match_time_limit_stored_by_organisation: 'Biometric match against data held by the organisation, time limit',
            sla_type_ai_risk_classification_before_use: 'AI risk classification (before use)',
            sla_type_annual_risk_assessment_report: 'Annual risk assessment report',
            sla_type_annual_training_readiness: 'Annual training readiness',
            sla_type_art_30_records_availability_to_dpa: 'Art. 30 records availability to DPA',
            sla_type_audit_window_type_i: 'Audit window (Type I)',
            sla_type_audit_window_type_ii_min: 'Audit window (Type II, minimum)',
            sla_type_audit_window_type_ii_typical: 'Audit window (Type II, typical)',
            sla_type_authentication_result_retention_readiness: 'Authentication result retention readiness',
            sla_type_breach_communication_to_data_subjects: 'Breach communication to data subjects',
            sla_type_breach_notification_to_supervisory_authority: 'Breach notification to supervisory authority',
            sla_type_classification_lifecycle_review: 'Classification lifecycle review',
            sla_type_consultation_end: 'Consultation end',
            sla_type_annual_activity_report_proposed: 'Annual activity report (proposed)',
            sla_type_approved_list_publication_proposed: 'Approved list publication (proposed)',
            sla_type_course_result_report_proposed: 'Course result report (proposed)',
            sla_type_foreign_certificate_application_proposed: 'Foreign certificate recognition application (proposed)',
            sla_type_network_registration_review_proposed: 'Training network registration review (proposed)',
            sla_type_recognised_body_change_notice_proposed: 'Recognised body change notice (proposed)',
            sla_type_agency_label_publication_proposed: 'Agency publication of the label and DPID (proposed)',
            sla_type_label_dpid_update_proposed: 'Label update with the DPID (proposed)',
            sla_type_inspection_annual_report_proposed: 'Annual inspection report to the Ministry (proposed)',
            sla_type_content_takedown_response: 'Content takedown response',
            sla_type_cookie_consent_reconfirmation: 'Cookie consent reconfirmation',
            sla_type_corrective_action_default: 'Corrective action (default)',
            sla_type_cross_border_core_authority_review_complex: 'Cross-border core data authority review (complex)',
            sla_type_cross_border_core_authority_review_standard: 'Cross-border core data authority review (standard)',
            sla_type_cross_border_exception_postfiling: 'Cross-border exception (post-filing)',
            sla_type_cross_border_important_prefiling: 'Cross-border important data (pre-filing)',
            sla_type_ctia_dossier_filing: 'CTIA dossier filing',
            sla_type_cross_border_dossier_applicant_completion: 'Applicant completes the cross-border transfer impact dossier',
            sla_type_database_update: 'Database update',
            sla_type_decision_effective_date: 'Decision effective date',
            sla_type_decree_effective_date: 'Decree effective date',
            sla_type_circular_effective_date: 'Circular effective date',
            sla_type_system_upgrade_completion: 'System upgrade completion',
            sla_type_incident_recovery_resumption: 'Resumption after an incident is fixed',
            sla_type_report_correction_resubmission: 'Report correction and resubmission',
            sla_type_regulator_review_period: 'Authority review period',
            sla_type_submission_window_lock: 'Submission window lock',
            sla_type_transitional_reporting_regime: 'Transitional reporting arrangement',
            sla_type_staged_commencement_date: 'Staged commencement date',
            sla_type_decree_issued: 'Decree issued',
            sla_type_direct_marketing_opt_out_honor: 'Direct marketing opt-out honoring',
            sla_type_document_review_annual: 'Document review (annual)',
            sla_type_dpia_prior_consultation_dpa_response: 'DPIA prior consultation — DPA response',
            sla_type_dsar_refusal_notification: 'DSAR refusal notification',
            sla_type_dsar_response: 'DSAR response',
            sla_type_dsar_response_extension: 'DSAR response extension',
            sla_type_emergency_drill_cadence: 'Emergency drill cadence',
            sla_type_entrusted_processing_record_retention: 'Entrusted processing record retention',
            sla_type_event_review: 'Event review',
            // Sprint 62.49.1 (DB-10) — the consumer-rights pack's acknowledgement-of-receipt row.
            sla_type_consumer_complaint_receipt_acknowledgement: 'Notice to the consumer that a report, request or complaint was received',
            sla_type_food_traceability_records_request: 'Food traceability records request (24 hours)',
            sla_type_food_traceability_system_update: 'Food traceability system update',
            sla_type_evidence_currency_limit: 'Personal record statement currency (no more than 6 months before filing)',
            sla_type_framework_effective_date: 'Framework effective date',
            sla_type_high_risk_conformity_assessment_before_use: 'High-risk conformity assessment (before use)',
            sla_type_ict_systems_yearly_testing: 'ICT systems yearly testing',
            sla_type_important_core_transfer_approval_readiness: 'Important/core data transfer approval readiness',
            sla_type_incident_early_warning: 'Incident early warning',
            sla_type_incident_final_report: 'Incident final report',
            sla_type_incident_initial_notification: 'Incident initial notification',
            sla_type_cybersecurity_law_art45_1_assigned_level_protection_transition: 'Information systems with an assigned security level — protection conditions transition',
            sla_type_incident_initial_notification_outer_limit: 'Incident initial notification — outer limit from awareness',
            sla_type_incident_intermediate_report: 'Incident intermediate report',
            sla_type_incident_notification: 'Incident notification',
            sla_type_information_sharing_arrangement_notification: 'Information-sharing arrangement notification',
            sla_type_internal_audit_annual: 'Internal audit (annual)',
            sla_type_internal_post_consultation_review: 'Internal post-consultation review',
            sla_type_internal_post_session_review: 'Internal post-session review',
            sla_type_internal_review: 'Internal review',
            sla_type_law_effective_date: 'Law effective date',
            sla_type_lawful_deletion_request: 'Lawful deletion request',
            sla_type_management_body_training_cycle: 'Management body training cycle',
            sla_type_management_review_annual: 'Management review (annual)',
            sla_type_national_assembly_session: 'National Assembly session',
            sla_type_national_assembly_plenary_submission: 'Government submission heard by the National Assembly',
            sla_type_national_synthetic_database_response: 'National synthetic database response',
            sla_type_personnel_reassessment_readiness: 'Personnel reassessment readiness',
            sla_type_platform_registration_amendment: 'Platform registration amendment',
            sla_type_processing_log_retention: 'Processing log retention',
            sla_type_recertification_annual: 'Recertification (annual)',
            sla_type_recertification_cycle: 'Recertification cycle',
            sla_type_reclassification_authority_notification: 'Reclassification authority notification',
            sla_type_recurring_review: 'Recurring review',
            sla_type_reference_issued: 'Reference issued',
            sla_type_remediation_default: 'Remediation (default)',
            sla_type_risk_management_review_cycle: 'Risk management review cycle',
            sla_type_risk_management_self_assessment_cycle: 'Risk management self-assessment cycle',
            sla_type_roadmap_issued: 'Roadmap issued',
            sla_type_ropa_review_cycle: 'RoPA review cycle',
            sla_type_sandbox_certificate_review_ministry: 'Sandbox certificate review (ministry)',
            sla_type_sandbox_certificate_review_multi_authority: 'Sandbox certificate review (multi-authority)',
            sla_type_sandbox_certificate_review_provincial: 'Sandbox certificate review (provincial)',
            sla_type_sandbox_completion_report: 'Sandbox completion report',
            sla_type_sandbox_completion_certificate_review: 'Sandbox completion certificate review',
            sla_type_sandbox_dossier_validity_check: 'Sandbox dossier validity check',
            sla_type_sandbox_extension_application: 'Sandbox extension application',
            sla_type_sandbox_extension_decision: 'Sandbox extension decision',
            sla_type_sandbox_transitional_extension_request: 'Transitional operation extension request',
            sla_type_sandbox_transitional_extension_decision: 'Transitional operation extension decision',
            sla_type_sandbox_periodic_report_level_1_2: 'Sandbox periodic report (level 1–2)',
            sla_type_sandbox_periodic_report_level_3: 'Sandbox periodic report (level 3)',
            sla_type_sandbox_serious_incident_report: 'Sandbox serious incident report',
            sla_type_self_sovereign_log_sync_readiness: 'Self-sovereign log sync readiness',
            sla_type_serious_incident_final_report: 'Serious incident final report',
            sla_type_serious_incident_preliminary_other: 'Serious incident preliminary report (other)',
            sla_type_serious_incident_preliminary_severe: 'Serious incident preliminary report (severe)',
            sla_type_state_agency_ai_impact_assessment_before_use: 'State-agency AI impact assessment (before use)',
            sla_type_supply_chain_reassessment_cycle: 'Supply-chain reassessment cycle',
            sla_type_surveillance_audit_annual: 'Surveillance audit (annual)',
            sla_type_telecom_breach_authority_notification: 'Telecom breach — authority notification',
            sla_type_telecom_breach_authority_supplement: 'Telecom breach — authority supplement',
            sla_type_telecom_breach_subject_notification: 'Telecom breach — subject notification',
            sla_type_third_party_register_annual_reporting: 'Third-party register annual reporting',
            sla_type_tlpt_cycle: 'TLPT cycle',
            sla_type_traceability_system_connection_start: 'Traceability system connection start',
            sla_type_traffic_data_erasure_review: 'Traffic data erasure review',
            sla_type_transition_existing_systems: 'Transition — existing systems',
            sla_type_transition_new_systems: 'Transition — new systems',
            sla_type_transition_readiness: 'Transition readiness',
            guidance_related_link: 'Gerelateerde richtlijnen',
            deadlines_empty: 'Geen regelgevende deadlines gevonden voor dit raamwerk.',
            deadlines_sla_type: 'SLA-type',
            deadlines_value: 'Deadline',
            deadlines_unit: 'Eenheid',
            deadlines_extension: 'Verlenging',
            deadlines_last_day: 'Last day: {date}',
            deadlines_date_basis_computed: 'computed from the {period} period under the counting rule the legal reference names; not printed in the text',
            deadlines_date_basis_printed: 'printed in the signed text',
            deadlines_date_basis_next_working_day: 'an end on a non-working day moves to the next working day',
            deadlines_reference: 'Juridische referentie',
            framework_stack: 'Raamwerkstructuur',
            layer_parent_law: 'Hoofdwet',
            layer_implementing_decree: 'Uitvoeringsbesluit',
            layer_enforcement_decree: 'Handhavingsbesluit',
            layer_admin_procedures: 'Administratieve procedures',
            // fix-sprint-20260912-1003 — Framework Stack layer groups and row states.
            layer_prime_minister_decision: 'Prime Minister Decision',
            layer_official_roadmap: 'Reference',
            layer_child_instrument: 'Onderliggende instrumenten',
            stack_status_in_force: 'In force',
            stack_status_in_force_reference: 'In force (reference)',
            stack_status_in_force_until: 'In force to {date}',
            stack_status_from_date: 'From {date}',
            stack_status_draft: 'Draft',
            stack_status_promulgated: 'Promulgated {date}',
            stack_status_superseded: 'Superseded {date}',
            stack_status_reference: 'Reference',
            stack_status_unknown: 'Status not recognised',
            stack_show_lineage: 'Show lineage',
            stack_became: 'enacted as this instrument on {date}',
            stack_replaced_by: 'replaced by this instrument on {date}',
            stack_became_law: 'enacted as this law on {date}',
            stack_replaced_by_law: 'replaced by this law on {date}',
            stack_has_lineage_promulgated: 'enacted from a draft',
            stack_has_lineage_superseded: 'replaced an earlier instrument',
            stack_no_framework_page: 'no framework page',
            status_active: 'Actief',
            status_draft: 'Concept',
            draft_enforcement_badge: 'Concept — nog niet uitgevaardigd',
            effective_date_draft: 'Concept — nog niet van kracht',
            effective_date: 'Inwerkingtreding',
            forms_table_id: 'Formulier-ID',
            forms_table_name: 'Formuliernaam',
            forms_table_source: 'Bron',
            forms_table_category: 'Categorie',
            forms_table_type: 'Typ',
            fill_form: 'Formulier invullen',
            view_reference: 'Bekijk referentie',
            forms_empty: 'Geen formulieren gevonden voor dit raamwerk.',
            forms_loading: 'Formulieren laden...',
            filter_by_source_layer: 'Filter op bronlaag',
            all_layers: 'Alle lagen',
            procedures_id: 'Procedure-ID',
            procedures_name: 'Werkwijze',
            procedures_trigger: 'Trigger',
            procedures_steps: 'Stappen',
            procedures_outputs: 'Resultaten',
            procedures_related_forms: 'Gerelateerde formulieren',
            procedures_submission_channels: 'Inzendingskanalen',
            procedures_empty: 'Geen procedures gevonden voor dit raamwerk.',
            fines_area: 'Overtredingsgebied',
            fines_articles: 'Artikelen',
            fines_individual: 'Boete (individu)',
            fines_organization: 'Boete (organisatie)',
            fines_revenue: 'Omzetescalatie',
            fines_deadlines: 'Deadlines',
            fines_remediation: 'Herstelmaatregelen',
            fines_related: 'Gerelateerde procedures',
            fines_empty: 'Geen handhavingsgegevens beschikbaar voor dit raamwerk.',
            // fix-sprint D1 F22 — `fetchWatchlistFrameworks`'s catch set an empty
            // watchlist with no flag, so a failed read printed "No draft or upcoming
            // frameworks" — a POSITIVE finding about the regulatory horizon, and the
            // most consequential thing that tab can say wrongly.
            watchlist_load_failed: 'The draft and upcoming frameworks could not be read. Reload to try again.',
            watchlist_retry_subject: 'the regulatory watchlist',
            /*
              fix-sprint D1 F9 — the six regulatory-content tabs on the framework
              detail view swallowed every read failure into an empty array and then
              STATED absence: "No requirements found for this framework.", "No forms
              found…", "No procedures found…", "No guidance content…". On a
              regulatory surface "this framework declares no requirements" is a claim
              an operator acts on, and a failed read is not that claim. Each tab now
              states the failure instead, beside the reload that clears it.
            */
            content_reload: 'Reload',
            content_reloading: 'Reloading…',
            requirements_load_failed: 'The requirements for this framework could not be read. Reload to try again.',
            forms_load_failed: 'The forms for this framework could not be read. Reload to try again.',
            procedures_load_failed: 'The procedures for this framework could not be read. Reload to try again.',
            deadlines_load_failed: 'The deadlines for this framework could not be read. Reload to try again.',
            fines_load_failed: 'The enforcement data for this framework could not be read. Reload to try again.',
            guidance_load_failed: 'The guidance for this framework could not be read. Reload to try again.',
            content_partial_failed: 'Part of this content could not be read, so what is shown may be incomplete. Reload to try again.',
            requirements_retry_subject: 'the requirements for this framework',
            forms_retry_subject: 'the forms for this framework',
            procedures_retry_subject: 'the procedures for this framework',
            deadlines_retry_subject: 'the deadlines for this framework',
            fines_retry_subject: 'the enforcement data for this framework',
            guidance_retry_subject: 'the guidance for this framework',
            fines_risk_type: 'Risicotype',
            fines_risk_triggers: 'Wat het activeert',
            fines_risk_only_note: 'Deze vermeldingen beschrijven nalevings- en operationeel risico, geen geldboetes. Waar boetes gelden, zijn ze vastgelegd in het bovenliggende kader.',
            fines_enforcement_pending: 'Handhavingskader nog niet gepubliceerd. Sanctiegegevens worden toegevoegd wanneer het uitvoeringsinstrument is afgekondigd.',
            view_details: 'Details bekijken',
            back_to_list: 'Terug naar de lijst',
            description: 'Beschrijving',
            jurisdiction: 'Jurisdictie',
            lineage: 'Afstamming',
            replaces: 'Vervangt',
            replaced_by: 'Vervangen door',
            requirements: 'Vereisten',
            tab_navigation: 'Raamwerkdetail-tabbladen',
            forms_table_actions: 'Acties',
            create_form: 'Formulier aanmaken',
            mark_promulgated: 'Markeren als afgekondigd',
            status_promulgated: 'Afgekondigd',
            promulgated_success: 'Laagstatus bijgewerkt naar afgekondigd',
            transition_review: 'Overgangsbeoordeling',
            transition_review_desc: 'Dit kader heeft een openstaande overgangsbeoordeling voor de invoering van de opvolgende wet.',
            transition_review_by: 'Beoordelingsdatum',
            transition_review_status: 'Beoordelingsstatus',
            transition_review_notes: 'Opmerkingen',
            transition_review_pending: 'In afwachting',
            transition_review_in_progress: 'In uitvoering',
            transition_review_completed: 'Afgerond',
            transition_review_successor: 'Opvolgend kader',
            transition_review_lead_time: 'Voorbereidingstijd',
            transition_review_lead_time_days: '{days} dagen voor inwerkingtreding',
            transition_review_overdue: 'overdue',
            transition_review_forms_heading: 'Formulierovergangsbeoordeling',
            transition_review_forms_desc: 'Beoordeel elk operationeel sjabloon op relevantie onder het opvolgende kader.',
            transition_review_outcome: 'Resultaat',
            transition_review_outcome_carry_forward: 'Behouden',
            transition_review_outcome_carry_forward_revised: 'Behouden (herzien)',
            transition_review_outcome_retire: 'Intrekken',
            transition_review_outcome_split: 'Splitsen',
            transition_review_outcome_awaiting_decree: 'Wachtend op besluit',
            transition_review_no_forms: 'Geen formulierovergangsbeoordeling geconfigureerd.',
            transition_review_update_success: 'Overgangsbeoordeling succesvol bijgewerkt',
            transition_review_operational_note: 'Dit zijn procesondersteunende operationele sjablonen, geen wettelijke formuliertellingen. De juridische toewijzing blijft bij het oude kader totdat de opvolgende wet in werking treedt.',
            rights_tab: 'Rechten',
            rights_empty: 'Geen rechten van betrokkenen gedefinieerd voor dit kader.',
            rights_code: 'Codeer',
            rights_name: 'Recht',
            rights_legal_basis: 'Rechtsgrondslag',
            rights_elaborating_articles: 'Uitwerkingsartikelen',
            rights_description: 'Beschrijving',
            requirements_reference: 'Referentie',
            requirements_mandatory: 'Verplicht',
            // Sprint 62.58.0 (AC-B1, AC-B3) — whom each requirement addresses, and the filter over the declared classes.
            requirements_addressed_to: 'Addressed to',
            requirements_addressee_filter_hint: 'Shows the requirements addressed to any class you choose. Nothing is chosen for you.',
            requirements_addressee_count: 'Showing {shown} of {total} requirements',
            requirements_addressee_clear: 'Show all requirements',
            requirements_basis_note_show: 'Read the whole note',
            requirements_basis_note_hide: 'Show less',
            requirements_yes: 'Yes',
            requirements_no: 'No',
            requirements_source: 'Bron',
            category_legal_basis: 'Wettelijke basis',
            category_data_subject_rights: 'Rechten van betrokkenen',
            category_notification: 'Kennisgeving',
            category_assessment: 'Beoordeling',
            category_governance: 'Bestuur',
            category_record_keeping: 'Registratie bijhouden',
            category_cross_border: 'Grensoverschrijdend',
            category_technical_measures: 'Technische maatregelen',
            category_incident_reporting: 'Incidentrapportage',
            category_risk_management: 'Risicobeheer',
            category_supply_chain: 'Toeleveringsketen',
            category_certification: 'Certificering',
            child_frameworks: '{count} onderliggend raamwerk',
            child_frameworks_plural: '{count} onderliggende raamwerken',
            replaces_prefix: 'Vervangt:',
            replaced_by_prefix: 'Vervangen door:',
            responsible_prefix: 'Verantwoordelijk:',
            watchlist_empty_hint: 'Frameworks met de status "concept" of "aankomend" zullen hier verschijnen voor monitoring.',
            framework_count: '{count}-framework',
            framework_count_plural: '{count} raamwerken',
            hiding_legacy: '({count} verouderd verbergen)',
            label_VN_PDPD_DECREE_13: 'Wbp-decreet 13/2023',
            label_VN_PDPL_LAW_2025: 'PDPL Wet 91/2025',
            label_VN_PDPL_DECREE_356_2025: 'Decreet 356/2025/ND-CP',
            label_VN_PDPL_ADMIN_PROCEDURES_778_2026: 'QĐ 778/QĐ-BCA-A05',
            label_VN_PDPL_ENFORCEMENT_DRAFT_2026: 'PDPL Handhavingsontwerp 2026',
            label_VN_MOPS_ADMIN_SIMPLIFICATION_NQ22_2026: 'NQ22 Temporary Overlay 2026',
            label_VN_DATA_LAW: 'Gegevenswet',
            label_VN_DATA_LAW_DECREE_165_2025: 'Besluit gegevenswet 165/2025',
            label_VN_DATA_EXCHANGES_DRAFT_2026: 'Data-uitwisselingen concept 2026',
            label_VN_TELECOM_LAW_2023: 'Telecommunicatiewet 2023',
            label_VN_TELECOM_DECREE_163_2024: 'Telecomdecreet 163/2024',
            label_VN_CYBERSEC_LAW: 'Cyberveiligheidswet (24/2018)',
            label_VN_CYBERSEC_DECREE_53: 'Cybersec-decreet 53/2022',
            label_VN_CYBERSEC_LAW_2025: 'Cyberveiligheidswet 116/2025',
            label_VN_ECOMMERCE_LAW_2025: 'Wet op de elektronische handel 122/2025',
            label_VN_AI_LAW_2026: 'AI Wet 2026',
            label_VN_AI_DECREE_142_2026: 'AI Decree 142/2026/NĐ-CP',
            issuer_company: 'Bedrijf',
            issuer_authority_a05_mps: 'MPS/A05',
            issuer_authority_mic: 'MIC',
            issuer_authority_mps_ndc: 'MPS/NDC',
            issuer_other_authority: 'Autoriteit',
            sla_type_rights_request_ack: 'Rechten vragen om erkenning',
            'sla_type_Access control review': 'Toegangscontrole beoordeling',
            'sla_type_Annual governance attestation': 'Jaarlijks bestuursattest',
            'sla_type_Audit review cycle': 'Auditbeoordelingscyclus',
            'sla_type_Authority request response': 'Antwoord van autoriteitsverzoek',
            'sla_type_Change notification processing': 'Meldingsverwerking wijzigen',
            'sla_type_Complaint acknowledgment': 'Klachtbevestiging',
            'sla_type_Complaint resolution': 'Klachtenafhandeling',
            'sla_type_Confidentiality policy review': 'Herziening van het vertrouwelijkheidsbeleid',
            'sla_type_Cybersecurity incident full report': 'Volledig rapport over cyberbeveiligingsincidenten',
            'sla_type_Cybersecurity incident initial report': 'Eerste rapport cyberveiligheidsincidenten',
            'sla_type_Data inventory review': 'Beoordeling van gegevensinventarisatie',
            'sla_type_Disclosure log review cycle': 'Beoordelingscyclus van openbaarmakingslogboek',
            'sla_type_Incident classification decision': 'Beslissing over classificatie van incidenten',
            'sla_type_Information system classification review': 'Beoordeling van de classificatie van informatiesystemen',
            'sla_type_Notification processing (sales website/app)': 'Meldingsverwerking (verkoopwebsite/app)',
            'sla_type_Pen test cycle': 'Pentestcyclus',
            'sla_type_Registration processing (service platform)': 'Registratieverwerking (serviceplatform)',
            'sla_type_Risk assessment cycle': 'Risicobeoordelingscyclus',
            'sla_type_Takedown decision (after review)': 'Takedownbesluit (na beoordeling)',
            'sla_type_Termination notice (public)': 'Beëindigingsmededeling (openbaar)',
            sla_type_ai_impact_assessment_completion: 'AI effectbeoordeling voltooid',
            sla_type_ai_sandbox_application_review: 'AI beoordeling van sandbox-applicaties',
            sla_type_ai_serious_incident_report: 'AI rapport over ernstige incidenten',
            sla_type_ai_system_registration: 'AI systeemregistratie',
            sla_type_authority_request_response: 'Antwoord van autoriteitsverzoek',
            sla_type_certificate_issuance_review: 'Beoordeling van certificaatuitgifte',
            sla_type_complaint_handling_approach_notice: 'Kennisgeving klachtafhandeling',
            sla_type_complaint_incomplete_info_request: 'Klacht onvolledig infoverzoek',
            sla_type_data_governance_attestation: 'Attest voor gegevensbeheer',
            sla_type_dsr_acknowledgment: 'DSR-erkenning',
            sla_type_dsr_cl2_withdraw_restrict_object: 'DSR (Kl.2) — Intrekken / beperken / bezwaar maken',
            sla_type_dsr_cl2_withdraw_restrict_object_third_party: 'DSR (Kl.2) — Derde partij',
            sla_type_dsr_cl3_access_correct_provide: 'DSR (Kl.3) — Toegang / corrigeren / verstrekken',
            sla_type_dsr_cl3_access_correct_provide_third_party: 'DSR (Kl.3) — Derde partij',
            sla_type_dsr_cl4_deletion: 'DSR (Kl.4) — Schrapping',
            sla_type_dsr_cl4_deletion_third_party: 'DSR (Kl.4) — Schrapping, derde partij',
            sla_type_dsr_cl3_access_third_party: 'DSR (Cl.3) — Access, third party',
            sla_type_chemical_incident_system_declaration: 'Chemical incident — declaration to the System',
            sla_type_code_termination_explanation_window: 'Code termination — window to explain',
            sla_type_code_restoration_by_managing_body: 'Code restoration by the managing body',
            sla_type_traceability_data_update_frequency: 'Traceability data — update frequency',
            sla_type_authority_decision_deadline: 'Authority decision — due date',
            sla_type_authority_list_update_cycle: 'Authority list — update cycle',
            sla_type_dsr_cl4_provide_restrict_third_party: 'DSR (Cl.4) — Restriction, third party',
            sla_type_dsr_cl5_protective_measures: 'DSR (Kl.5) — Beschermende maatregelen',
            sla_type_telecom_authority_request_response: 'Telecomautoriteit verzoek om reactie',
            sla_type_rights_request_completion: 'Voltooiing van het rechtenverzoek',
            sla_type_incident_authority_notification: 'Melding incidentautoriteit',
            sla_type_incident_subject_notification: 'Melding van incidentonderwerp',
            sla_type_dpia_completion: 'DPIA voltooiing',
            sla_type_data_retention_review: 'Beoordeling van gegevensbewaring',
            sla_type_authority_filing_review: 'Beoordeling van de indiening door de autoriteit',
            sla_type_authority_supplement_response: 'Reactie van autoriteitssupplement',
            sla_type_authority_resubmission: 'Herindiening van de autoriteit',
            sla_type_authority_filing_supplement_response: 'Antwoord van de autoriteit die een supplement indient',
            sla_type_authority_filing_resubmission: 'Herindiening van de autoriteit',
            sla_type_authority_delivery_window: 'Authority delivery window',
            sla_type_authority_examination_window: 'Authority examination window',
            sla_type_authority_notice_trigger: 'Authority notice trigger',
            sla_type_authority_processing_window: 'Authority processing window',
            sla_type_before_event: 'Before the event',
            sla_type_instalment_maximum_period: 'Instalment maximum period',
            sla_type_lead_time_before_event: 'Lead time before the event',
            sla_type_reporting_cycle: 'Reporting cycle',
            sla_type_reporting_period: 'Reporting period',
            sla_type_statutory_filing_window: 'Statutory filing window',
            sla_type_statutory_payment_deadline: 'Statutory payment deadline',
            sla_type_taxpayer_filing_window: 'Taxpayer filing window',
            sla_type_taxpayer_notification_window: 'Taxpayer notification window',
            sla_type_taxpayer_response_window: 'Taxpayer response window',
            sla_type_taxpayer_supplementation_window: 'Taxpayer supplementation window',
            sla_type_transition_deadline: 'Transition deadline',
            sla_type_provision_end_date: 'Provision ceases to apply',
            sla_type_authority_notification_window: 'Authority notification window',
            sla_type_enforcement_trigger: 'Enforcement applies from',
            sla_type_authority_advance_notice: 'Authority advance notice',
            sla_type_refund_limitation_period: 'Refund limitation period',
            sla_type_reinspection_limitation_period: 'Re-inspection limitation period',
            sla_type_violation_classification_threshold: 'Violation classification threshold',
            sla_type_write_off_eligibility_threshold: 'Write-off eligibility threshold',
            unit_working_days: 'werkdagen',
            unit_calendar_days: 'kalenderdagen',
            unit_hours: 'uur',
            form_category_dpa: 'DPA',
            form_category_dsr: 'DSR',
            form_category_consent: 'Toestemming',
            form_category_privacy: 'Privacy',
            form_category_breach: 'Schending',
            form_category_dpia: 'DPIA',
            form_category_register: 'Registreren',
            form_category_monitoring: 'Toezicht',
            form_category_vendor: 'Verkoper',
            form_category_org: 'Organisatorisch',
            form_category_sector: 'Sector',
            form_category_data_mapping: 'Gegevens in kaart brengen',
            form_category_governance: 'Bestuur',
            form_category_incident: 'Voorval',
            form_category_authority_submission: 'Inzending van autoriteit',
            form_category_notice: 'Let op',
            form_category_assessment_report: 'Beoordelingsrapport',
            form_category_request: 'Verzoek',
            form_category_dossier: 'Dossier',
            form_category_filing: 'Indienen',
            module_consent: 'Toestemming',
            module_rights_request: 'Rechtenverzoek',
            module_classification: 'Classificatie',
            module_dpia: 'DPIA',
            module_governance: 'Bestuur',
            module_monitoring: 'Toezicht',
            module_register: 'Registreren',
            module_risk: 'Risico',
            module_data_mapping: 'Gegevens in kaart brengen',
            module_incident: 'Voorval',
            module_assessment: 'Beoordeling',
            module_vendor: 'Verkoper',
            module_policy: 'Beleid',
            module_audit: 'Controle',
            module_training: 'Opleiding',
            module_discovery: 'Ontdekking',
            // Sprint 62.58.0 (walk O-2) — requirement applicable_module values with no label
            module_sector_licensing: 'Sector Licensing',
            module_electronic_transactions: 'Electronic Transactions',
            module_consumer_protection: 'Consumer Protection',
            module_ecommerce_governance: 'E-Commerce Governance',
            module_identity_verification: 'Identity Verification',
            module_program_governance: 'Program Governance',
            module_compliance_operations: 'Compliance Operations',
            module_vendor_governance: 'Vendor Governance',
            module_cybersecurity: 'Cybersecurity',
            module_authority_direction: 'Authority Direction',
            module_incident_response: 'Incident Response',
            module_data_governance: 'Data Governance',
            module_incident_operations: 'Incident Operations',
            module_regulatory_disclosure: 'Regulatory Disclosure',
            module_customer_protection: 'Customer Protection',
            module_regulatory_change: 'Regulatory Change',
            module_third_party_risk: 'Third-Party Risk',
            module_legal_register: 'Legal Register',
            module_change_control: 'Change Control',
            module_records: 'Records',
            enum_guidance_type_calculation_guidance: 'Berekeningsbegeleiding',
            enum_guidance_type_implementation_guidance: 'Implementatiebegeleiding',
            enum_guidance_type_interpretive_note: 'Interpretatieve opmerking',
            enum_guidance_type_legal_guidance: 'Juridische begeleiding',
            enum_guidance_type_procedural_guidance: 'Procedurele begeleiding',
            enum_guidance_type_sector_guidance: 'Sectorbegeleiding',
            enum_guidance_type_technical_methodology: 'Technische Methodologie',
            enum_guidance_category_annual_reporting: 'Jaarlijkse rapportage',
            enum_guidance_category_authority_cooperation: 'Samenwerking tussen autoriteiten',
            enum_guidance_category_authority_coordination: 'Coördinatie van autoriteiten',
            enum_guidance_category_business_continuity: 'Bedrijfscontinuïteit',
            enum_guidance_category_capital_adequacy: 'Kapitaaltoereikendheid',
            enum_guidance_category_complaint_handling: 'Klachtenafhandeling',
            enum_guidance_category_consent: 'Toestemming',
            enum_guidance_category_consent_management: 'Toestemmingsbeheer',
            enum_guidance_category_consumer_protection: 'Consumentenbescherming',
            enum_guidance_category_control_environment: 'Controleomgeving',
            enum_guidance_category_cross_border: 'Grensoverschrijdend',
            enum_guidance_category_cross_border_transfers: 'Grensoverschrijdende overdrachten',
            enum_guidance_category_data_classification: 'Gegevensclassificatie',
            enum_guidance_category_data_localization: 'Gegevenslokalisatie',
            enum_guidance_category_data_protection_impact_assessment: 'Gegevensbeschermingseffectbeoordeling',
            enum_guidance_category_direct_marketing: 'Directe marketing',
            enum_guidance_category_dpia: 'DPIA',
            enum_guidance_category_governance: 'Bestuur',
            enum_guidance_category_ict_risk_management: 'ICT-risicobeheer',
            enum_guidance_category_incident_management: 'Incidentbeheer',
            enum_guidance_category_internal_audit: 'Interne audit',
            enum_guidance_category_isms_documentation: 'ISMS-documentatie',
            enum_guidance_category_lawful_basis: 'Wettelijke basis',
            enum_guidance_category_licensing: 'Licentie',
            enum_guidance_category_local_presence: 'Lokale aanwezigheid',
            enum_guidance_category_logging_and_retention: 'Registratie en retentie',
            enum_guidance_category_logical_and_physical_access: 'Logische en fysieke toegang',
            enum_guidance_category_operational_resilience_testing: 'Operationele veerkracht testen',
            enum_guidance_category_outsourcing: 'Uitbesteding',
            enum_guidance_category_platform_registration: 'Platformregistratie',
            enum_guidance_category_quality_of_service: 'Kwaliteit van de dienstverlening',
            enum_guidance_category_risk_classification: 'Risicoclassificatie',
            enum_guidance_category_risk_management: 'Risicobeheer',
            enum_guidance_category_scope_definition: 'Definitie van reikwijdte',
            enum_guidance_category_sectoral_localisation: 'Sectorale lokalisatie',
            enum_guidance_category_subscriber_protection: 'Abonneebescherming',
            enum_guidance_category_supply_chain: 'Toeleveringsketen',
            enum_guidance_category_system_classification: 'Systeemclassificatie',
            enum_guidance_category_system_operations: 'Systeembewerkingen',
            enum_guidance_category_telecommunications_data: 'Telecommunicatiegegevens',
            enum_guidance_category_third_party_risk: 'Risico van derden',
            enum_guidance_category_transitional_provisions: 'Overgangsbepalingen',
            enum_guidance_category_vulnerability_management: 'Kwetsbaarheidsbeheer',
            enum_guidance_status_active: 'Actief',
            enum_guidance_status_draft: 'Diepgang',
            // Sprint 62.58.0 (walk D-1) — every guidance status a pack declares
            enum_guidance_status_deprecated: 'Deprecated',
            enum_guidance_status_superseded: 'Superseded',
            enum_guidance_status_upcoming: 'Upcoming',
            enum_guidance_status_legacy: 'Legacy',
            enum_guidance_status_temporary_active: 'Temporarily in effect',
            enum_guidance_status_active_reference: 'In force, reference only',
            enum_module_access_management: 'Toegangsbeheer',
            enum_module_ai_governance: 'AI Bestuur',
            enum_module_asset_management: 'Vermogensbeheer',
            enum_module_audit_trail: 'Audittraject',
            enum_module_breach_notification: 'Melding van inbreuk',
            enum_module_capital_adequacy: 'Kapitaaltoereikendheid',
            enum_module_classification: 'Classificatie',
            enum_module_consent: 'Toestemming',
            enum_module_consent_management: 'Toestemmingsbeheer',
            enum_module_cross_border_transfers: 'Grensoverschrijdende overdrachten',
            enum_module_data_localization: 'Gegevenslokalisatie',
            enum_module_data_mapping: 'Gegevens in kaart brengen',
            enum_module_data_protection: 'Gegevensbescherming',
            enum_module_data_subject_rights: 'Rechten van betrokkenen',
            enum_module_dpia: 'DPIA',
            enum_module_encryption: 'Encryptie',
            enum_module_governance: 'Bestuur',
            enum_module_incident: 'Voorval',
            enum_module_incident_management: 'Incidentbeheer',
            enum_module_internal_audit: 'Interne audit',
            enum_module_isms_documentation: 'ISMS-documentatie',
            enum_module_isms_governance: 'ISMS-bestuur',
            enum_module_logging_and_monitoring: 'Loggen en monitoren',
            enum_module_monitoring: 'Toezicht',
            enum_module_policy_management: 'Beleidsbeheer',
            enum_module_risk: 'Risico',
            enum_module_risk_management: 'Risicobeheer',
            enum_module_training: 'Opleiding',
            enum_module_vendor: 'Verkoper',
            enum_module_vendor_management: 'Leveranciersbeheer',
            enum_module_vendor_risk: 'Leveranciersrisico',
            enum_module_vulnerability_management: 'Kwetsbaarheidsbeheer',
            // Sprint 62.58.0 (walk O-6) — English copy; translated in Phase 25 (R-57).
            enum_guidance_type_annex_reference: 'Annex Reference',
            enum_guidance_type_boundary_guidance: 'Boundary Guidance',
            enum_guidance_type_calculation_methodology: 'Calculation Methodology',
            enum_guidance_type_classification_guidance: 'Classification Guidance',
            enum_guidance_type_cross_framework_mapping: 'Cross-Framework Mapping',
            enum_guidance_type_enforcement_mechanics: 'Enforcement Mechanics',
            enum_guidance_type_follow_on_instrument_watchlist: 'Follow-On Instrument Watchlist',
            enum_guidance_type_framework_overview: 'Framework Overview',
            enum_guidance_type_framework_relationship: 'Framework Relationship',
            enum_guidance_type_framework_relationship_note: 'Framework Relationship Note',
            enum_guidance_type_handling_guidance: 'Handling Guidance',
            enum_guidance_type_implementation_note: 'Implementation Note',
            enum_guidance_type_implementation_timeline: 'Implementation Timeline',
            enum_guidance_type_obligation_map: 'Obligation Map',
            enum_guidance_type_official_reference_artifact: 'Official Reference Artifact',
            enum_guidance_type_operational_boundary: 'Operational Boundary',
            enum_guidance_type_operational_guidance: 'Operational Guidance',
            enum_guidance_type_platform_boundary: 'Platform Boundary',
            enum_guidance_type_readiness_mapping: 'Readiness Mapping',
            enum_guidance_type_reference_note: 'Reference Note',
            enum_guidance_type_regulatory_overview: 'Regulatory Overview',
            enum_guidance_type_requirement_detail: 'Requirement Detail',
            enum_guidance_type_scope_and_applicability: 'Scope and Applicability',
            enum_guidance_type_scope_guidance: 'Scope Guidance',
            enum_module_compliance: 'Compliance',
            enum_guidance_type_source_limitation: 'Source Limitation',
            enum_guidance_type_source_limits: 'Source Limits',
            enum_guidance_type_source_note: 'Source Note',
            enum_guidance_type_technical_reference: 'Technical Reference',
            enum_guidance_type_timeline: 'Timeline',
            enum_guidance_type_transition_guidance: 'Transition Guidance',
            enum_guidance_type_transition_note: 'Transition Note',
            enum_guidance_category_access: 'Access',
            enum_guidance_category_account_eligibility: 'Account Eligibility',
            enum_guidance_category_account_linkage: 'Account Linkage',
            enum_guidance_category_ai_functions: 'AI Functions',
            enum_guidance_category_amendments: 'Amendments',
            enum_guidance_category_annex: 'Annex',
            enum_guidance_category_assessment: 'Assessment',
            enum_guidance_category_audit: 'Audit',
            enum_guidance_category_authority: 'Authority',
            enum_guidance_category_authority_submission: 'Authority Submission',
            enum_guidance_category_boundary: 'Boundary',
            enum_guidance_category_certification: 'Certification',
            enum_guidance_category_claims: 'Platform Claims',
            enum_guidance_category_classification: 'Classification',
            enum_guidance_category_confidentiality: 'Confidentiality',
            enum_guidance_category_connection_procedure: 'Connection Procedure',
            enum_guidance_category_coordination: 'Coordination',
            enum_guidance_category_data_disclosure: 'Data Disclosure',
            enum_guidance_category_data_lifecycle: 'Data Lifecycle',
            enum_guidance_category_data_protection_and_security: 'Data Protection and Security',
            enum_guidance_category_database_regime: 'Database Regime',
            enum_guidance_category_deadlines: 'Deadlines',
            enum_guidance_category_dispute_support: 'Dispute Support',
            enum_guidance_category_document_reuse: 'Document Reuse',
            enum_guidance_category_duty_allocation: 'Duty Allocation',
            enum_guidance_category_effective_date: 'Effective Date',
            enum_guidance_category_electronic_enforcement: 'Electronic Enforcement',
            enum_guidance_category_electronic_submission: 'Electronic Submission',
            enum_guidance_category_enforcement: 'Enforcement',
            enum_guidance_category_enforcement_authority: 'Enforcement Authority',
            enum_guidance_category_enforcement_measures: 'Enforcement Measures',
            enum_guidance_category_evidence: 'Evidence',
            enum_guidance_category_exploitation_duties: 'Exploitation Duties',
            enum_guidance_category_forms_and_transition: 'Forms and Transition',
            enum_guidance_category_framework_map: 'Framework Map',
            enum_guidance_category_framework_mapping: 'Framework Mapping',
            enum_guidance_category_framework_relationships: 'Framework Relationships',
            enum_guidance_category_handling_measures: 'Handling Measures',
            enum_guidance_category_identification: 'Identification',
            enum_guidance_category_identification_code: 'Identification Code',
            enum_guidance_category_implementation: 'Implementation',
            enum_guidance_category_incident: 'Incident',
            enum_guidance_category_legal_basis: 'Legal Basis',
            enum_guidance_category_lifecycle: 'Lifecycle',
            enum_guidance_category_listing: 'Listing',
            enum_guidance_category_mapping: 'Mapping',
            enum_guidance_category_marketing_communications_boundary: 'Marketing Communications Boundary',
            enum_guidance_category_methodology: 'Methodology',
            enum_guidance_category_monitoring: 'Monitoring',
            enum_guidance_category_obligations: 'Obligations',
            enum_guidance_category_penalties: 'Penalties',
            enum_guidance_category_personal_data: 'Personal Data',
            enum_guidance_category_personal_data_protection: 'Personal Data Protection',
            enum_guidance_category_phase_in_timeline: 'Phase-In Timeline',
            enum_guidance_category_platform_boundary: 'Platform Boundary',
            enum_guidance_category_platform_duties: 'Digital Platform Duties',
            enum_guidance_category_platform_operation: 'Digital Platform Operation',
            enum_guidance_category_pricing: 'Pricing',
            enum_guidance_category_privacy: 'Privacy',
            enum_guidance_category_procedures: 'Procedures',
            enum_guidance_category_product_scope: 'Product Scope',
            enum_guidance_category_provenance: 'Provenance',
            enum_guidance_category_public_sector: 'Public Sector',
            enum_guidance_category_readiness: 'Readiness',
            enum_guidance_category_record_keeping: 'Record Keeping',
            enum_guidance_category_reference_data: 'Reference Data',
            enum_guidance_category_regulatory_watch: 'Regulatory Watch',
            enum_guidance_category_related_instruments: 'Related Instruments',
            enum_guidance_category_remediation: 'Remediation',
            enum_guidance_category_reporting: 'Reporting',
            enum_guidance_category_sanctions: 'Sanctions',
            enum_guidance_category_source: 'Source',
            enum_guidance_category_status: 'Status',
            enum_guidance_category_system_connection: 'System Connection',
            enum_guidance_category_technical_measures: 'Technical Measures',
            enum_guidance_category_testing: 'Testing',
            enum_guidance_category_timeline: 'Timeline',
            enum_guidance_category_transition: 'Transition',
            enum_guidance_category_transitional: 'Transitional Rules',
            enum_module_access: 'Access',
            enum_module_access_review: 'Access Reviews',
            enum_module_ai_advisor: 'AI Advisor',
            enum_module_assessment: 'Assessment',
            enum_module_audit: 'Audit',
            enum_module_authority_operations: 'Authority Operations',
            enum_module_authority_direction: 'Authority Direction',
            enum_module_continuity: 'Business Continuity',
            enum_module_deidentification: 'De-identification',
            enum_module_deletion_orchestration: 'Deletion Orchestration',
            enum_module_identity_verification: 'Identity Verification',
            enum_module_legal_matters: 'Legal Matters',
            enum_module_program_governance: 'Program Governance',
            enum_module_rights_request: 'Rights Request',
            enum_module_sector_licensing: 'Sector Licensing',
            enum_module_technical_measures: 'Technical Measures',
            category_access: 'Toegang',
            category_availability: 'Beschikbaarheid',
            category_change_management: 'Verandermanagement',
            category_classification: 'Classificatie',
            category_communication_and_information: 'Communicatie & Informatie',
            category_compliance: 'Naleving',
            category_confidentiality: 'Vertrouwelijkheid',
            category_control_activities: 'Controleactiviteiten',
            category_control_environment: 'Controleomgeving',
            category_incident: 'Voorval',
            category_internal_audit: 'Interne audit',
            category_internal_control: 'Interne controle',
            category_logical_and_physical_access: 'Logische en fysieke toegang',
            category_mis_data: 'Managementinformatiesysteemgegevens',
            category_monitoring: 'Toezicht',
            category_monitoring_activities: 'Controleactiviteiten',
            category_outsourcing: 'Uitbesteding',
            category_privacy: 'Privacy',
            category_processing_integrity: 'Verwerkingsintegriteit',
            category_reporting: 'Rapportage',
            category_risk_assessment: 'Risicobeoordeling',
            category_risk_mitigation: 'Risicobeperking',
            category_sector_applicability: 'Sectorale toepasbaarheid',
            category_system_operations: 'Systeembewerkingen',
            category_training: 'Opleiding',
            category_vendor: 'Verkoper',
            guidance_related_forms_advisory: 'Sommige formulieren hebben bijbehorende richtlijnen — bekijk in de',
            guidance_related_procedures_advisory: 'Sommige procedures hebben bijbehorende richtlijnen — bekijk in de',
            guidance_related_requirements_advisory: 'Voor sommige vereisten gelden bijbehorende richtlijnen — bekijk in de',
        },
        channels: {
            title: 'Inzendingskanalen en deadlines',
            channels: 'Inzendingskanalen',
            add_channel: 'Kanaal toevoegen',
            channel_type: 'Typ',
            name: 'Naam',
            instructions: 'Instructies',
            active: 'Actief',
            no_channels: 'Geen inzendingskanalen geconfigureerd',
            portal: 'Portaal',
            in_person: 'Persoonlijk',
            postal: 'Post',
            email: 'E-mail',
            add: 'Toevoegen',
            deadlines: 'Regelgevende deadlines',
            add_deadline: 'Deadline toevoegen',
            label: 'Etiket',
            days: 'Dagen',
            day_type: 'Dagtype',
            calendar_days: 'Kalenderdagen',
            working_days: 'Werkdagen',
            no_deadlines: 'Geen deadlines geconfigureerd',
            description: 'Beschrijving',
            failed_load: 'load channel data mislukt',
            channel_added: 'Kanaal toegevoegd',
            channel_added_announce: 'Inzendingskanaal toegevoegd',
            failed_add_channel: 'add channel mislukt',
            deadline_added: 'Deadline toegevoegd',
            deadline_added_announce: 'Regelgevende deadline toegevoegd',
            failed_add_deadline: 'add deadline mislukt',
            channel_code: 'Declared channel',
            channel_code_none: 'Not bound to a declared channel',
            channel_code_hint: 'Which channel the instrument provides. Only a channel bound here can be used on a filing.',
            framework_declares_no_channels: 'This framework declares no submission channels',
            framework_not_installed: 'This framework is not installed for your organisation',
        },
        module_links: {
            title: 'Raamwerkkoppeling',
            modules: 'Gekoppelde modules',
            add_module: 'Module toevoegen',
            module_code: 'Modulecode',
            required: 'Vereist',
            link: 'Koppelen',
            no_modules: 'Geen modules gekoppeld',
            notes: 'Notities',
            personas: "Gekoppelde persona's",
            add_persona: 'Persona toevoegen',
            persona_id: 'Persona-ID',
            role_description: 'Rolbeschrijving',
            no_personas: "Geen persona's gekoppeld",
            failed_load: 'load framework links mislukt',
            module_linked: 'Module gekoppeld',
            module_linked_announce: 'Module succesvol gekoppeld',
            failed_link_module: 'link module mislukt',
            persona_linked: 'Persona gekoppeld',
            persona_linked_announce: 'Persona succesvol gekoppeld',
            failed_link_persona: 'link persona mislukt',
        },
        regulatory_updates: {
            statuses: {
                pending: 'In behandeling',
                assessed: 'Beoordeeld',
                applied: 'Toegepast',
                dismissed: 'Afgewezen',
            },
            title: 'Regelgevende updates en raamwerk-dashboard',
            page_description: 'Coverage at a glance for every installed regulatory framework, plus the in-product change log of regulatory updates: amendments, effective dates, and impact assessments. [NL]',
            coverage_dashboard: 'Dashboard raamwerkdekking',
            no_frameworks: 'Geen raamwerken gevonden',
            framework: 'Raamwerk',
            modules: 'Modules',
            templates: 'Sjablonen',
            forms: 'Formulieren',
            channels: 'Kanalen',
            deadlines: 'Deadlines',
            pending: 'Wachtende updates',
            updates: 'Regelgevende updates',
            create_update: 'Update aanmaken',
            framework_id: 'Raamwerk-ID',
            update_title: 'Titel',
            description: 'Beschrijving',
            effective_date: 'Ingangsdatum',
            amendment_ref: 'Amendementreferentie',
            create: 'Aanmaken',
            no_updates: 'Geen regelgevende updates',
            status: 'Status',
            impact: 'Impact',
            change_summary: 'Wijzigingsoverzicht',
            assess: 'Beoordelen',
            failed_load: 'load data mislukt',
            update_created: 'Update aangemaakt',
            update_created_announce: 'Regelgevende update aangemaakt',
            failed_create: 'create update mislukt',
            update_assessed: 'Update beoordeeld',
            failed_assess: 'assess update mislukt',
            col_pack_version: 'Pack Version',
            amendment_ref_inline: 'Amendment reference: {reference}',
            default_assess_note: 'Impact assessed — review complete',
        },
        framework_relationships: {
            panel_title: 'Gerelateerde kaders',
            panel_intro: 'Hoe dit kader zich verhoudt tot andere. Een overlay kan tegelijk onder meerdere bovenliggende kaders vallen; elk verschijnt hier precies één keer.',
            outgoing_heading: 'Dit kader is van toepassing op',
            permalink_aria: 'Link to {name} in this list',
            incoming_heading: 'Valt onder dit kader',
            outgoing_caption: 'Kaders waarnaar dit kader verwijst',
            incoming_caption: 'Kaders die onder dit kader vallen',
            col_framework: 'Kader',
            col_relationship: 'Relatie',
            col_scope: 'Bron',
            scope_global: 'Uit pakketinhoud',
            scope_org: 'Organisatiespecifiek',
            load_failed: 'Gerelateerde kaders konden niet worden geladen',
            relationship_kinds: {
                parent: 'Bovenliggend kader',
                applies_to: 'Van toepassing op',
                cross_reference: 'Kruisverwijzing',
                supersedes: 'Vervangt',
            },
        },
        sector_overlay: {
            sector_labels: {
                banking: 'Banking [NL]',
                insurance: 'Insurance [NL]',
                healthcare: 'Healthcare [NL]',
                education: 'Education',
                ict_critical: 'Critical ICT [NL]',
                telecom: 'Telecommunications [NL]',
                energy: 'Energy [NL]',
                transport: 'Transport [NL]',
                digital_services: 'Digital services [NL]',
                digital_technology_industry: 'Digitale technologie-industrie',
                manufacturing: 'Manufacturing',
                public_administration: 'Public administration [NL]',
                payments: 'Payments [NL]',
                publishing: 'Uitgeverij',
                crypto_assets: 'Cryptoactiva',
                other: 'Other [NL]',
            },
            scope_labels: {
                cross_sector: 'Cross-sector',
                security_overlay: 'Security overlay',
                security_control_overlay: 'Security control overlay',
                activity_scope: 'Activity scope',
                process_scope: 'Process scope',
                consumer_facing_process_scope: 'Consumer-facing processes',
                platform_and_einvoice_process_scope: 'Platform and e-invoice processes',
                regulatory_overlay: 'Regulatory overlay',
            },
            sector_label: 'Sector [NL]',
            direct_scope_label: 'Direct legal scope [NL]',
            chip_direct: 'Directly applicable [NL]',
            chip_reference: 'Reference only [NL]',
            banner_direct_copy: 'Directly applicable to your organization. Sector reports and deadlines are active. [NL]',
            banner_reference_copy: 'Reference only — your organization is not classified in this sector. [NL]',
            edit_mode_button: 'Edit mode [NL]',
            edit_form_title: 'Change install mode [NL]',
            mode_direct_label: 'Direct — legally applicable [NL]',
            mode_direct_help: 'Schedules sector deadlines and dashboard widgets. [NL]',
            mode_reference_label: 'Reference only [NL]',
            mode_reference_help: 'Browseable but no deadlines or widgets. [NL]',
            reason_note_label: 'Reason (optional) [NL]',
            reason_note_placeholder: 'e.g. Bank subsidiary acquired [NL]',
            cancel: 'Cancel [NL]',
            confirm: 'Confirm [NL]',
            confirming: 'Saving… [NL]',
            save: 'Save [NL]',
            saving: 'Saving… [NL]',
            toast_mode_changed: 'Sector overlay mode updated. [NL]',
            toast_installed: 'Sector overlay installed. [NL]',
            toast_sectors_saved: 'Sector self-identification saved. [NL]',
            install_modal_title: 'Install sector overlay [NL]',
            install_modal_intro: 'Sector overlays are opt-in regulatory packs scoped to a single industry. [NL]',
            install_modal_footer: 'You can change install mode later from the framework detail page. [NL]',
            install_button: 'Install [NL]',
            installed_label: 'Installed [NL]',
            uninstall_button: 'Verwijderen',
            uninstall_confirm: 'Bevestig het verwijderen',
            uninstall_cancel: 'Annuleer',
            suggested_label: 'Suggested [NL]',
            catalog_page_title: 'Sector overlays [NL]',
            catalog_page_intro: 'Optional regulatory packs scoped to a single industry sector. [NL]',
            catalog_empty_title: 'No sector overlays available [NL]',
            catalog_empty_description: 'Sector overlay packs published in your platform will appear here. [NL]',
            authority_label: 'Authority [NL]',
            org_sectors_title: 'Industry sectors [NL]',
            org_sectors_intro: 'Select the industry sectors your organization operates in. [NL]',
            widget_title: 'Sector overlay reports due [NL]',
            widget_aria_label: 'Sector overlay reports due [NL]',
            widget_empty: 'No upcoming sector overlay deadlines. [NL]',
            audit_event_mode_changed: 'Sector overlay install mode changed [NL]',
            audit_event_installed: 'Sector overlay installed [NL]',
            view_details_button: 'View details → [NL]',
            banner_aria_label: 'Sector overlay status [NL]',
            toast_load_failed: 'Failed to load sector overlays [NL]',
            toast_install_failed: 'Failed to install sector overlay [NL]',
            toast_uninstall_confirm_required: 'Type "{packCode}" to confirm uninstall. [NL]',
            toast_uninstalled: 'Uninstalled {packCode}. [NL]',
            toast_uninstall_failed: 'Failed to uninstall sector overlay [NL]',
            uninstall_warning_destructive_label: 'Destructive action. [NL]',
            uninstall_warning_body: 'Uninstalling will delete the framework record and any persisted regulatory deadlines tied to {frameworkCode} from this organisation. [NL]',
            uninstall_warning_type_to_confirm: 'Type {packCode} to confirm. [NL]',
            uninstall_in_progress: 'Uninstalling… [NL]',
            widget_view_all: 'View all sector overlays → [NL]',
            widget_due_in_days: 'Due in {days}d [NL]',
            widget_overdue_days: 'Overdue by {days}d [NL]',
            widget_due_today: 'Due today [NL]',
            widget_due_unknown: '— [NL]',
            // Catalog overview, filters and feature-views modal
            catalog_summary_total: 'Overlays',
            catalog_summary_installed: 'Installed',
            catalog_summary_direct: 'Direct',
            catalog_summary_reference: 'Reference only',
            catalog_summary_available: 'Not installed',
            filter_toolbar_label: 'Filter the overlay catalog',
            filter_search_label: 'Search overlays',
            filter_search_placeholder: 'Search by name, code or authority',
            filter_sector_all: 'All sectors and scopes',
            filter_status_all: 'All statuses',
            filter_status_installed: 'Installed',
            filter_status_not_installed: 'Not installed',
            filter_mode_label: 'Install mode',
            filter_mode_all: 'All modes',
            filter_clear: 'Clear filters',
            filter_results: 'Showing {shown} of {total} overlays',
            filter_no_match_title: 'No overlay matches these filters',
            filter_no_match_description: 'Change or clear the filters to see more of the catalog.',
            col_overlay: 'Overlay',
            col_scope: 'Sector or scope',
            col_mode: 'Mode',
            col_authority: 'Authority',
            col_dates: 'Dates',
            col_version: 'Version',
            col_status: 'Status',
            col_views: 'Feature views',
            col_actions: 'Actions',
            date_effective: 'Effective',
            date_issued: 'Issued',
            date_released: 'Released',
            desc_expand: 'Show description',
            desc_collapse: 'Hide description',
            status_not_installed: 'Not installed',
            views_none: 'None',
            views_none_title: 'This overlay declares nothing that a dedicated screen reads.',
            views_button: '{enabled} of {total} enabled',
            views_button_aria: 'Show the feature views that {name} brings into use ({enabled} of {total} enabled)',
            views_modal_title: 'Feature views of {name}',
            views_modal_intro: 'The registers and pages that read what the pack of this overlay declares. A view is enabled once the overlay is installed as Direct and the module it belongs to is switched on.',
            views_col_view: 'View',
            views_col_menu: 'Where in the menu',
            views_col_state: 'State',
            views_open: 'Open',
            views_state_enabled: 'Enabled',
            views_state_reference: 'Reference only — shows nothing until installed as Direct',
            views_state_not_installed: 'Not enabled — overlay not installed',
            views_state_module_off: 'Not enabled — module switched off',
            views_no_access: 'Your role cannot open this view',
            views_module_state_unconfirmed: 'Module settings could not be read, so a switched-off module is not shown here.',
            views_unclaimed_heading: 'Declared without a dedicated screen',
            views_unclaimed_description: 'The pack also declares these record kinds, but no dedicated screen reads them for this overlay: {kinds}.',
            declared_kind_labels: {
                biometric_technical_regulation_commencement: 'Start of the voice and iris biometric technical regulations',
                chemical_traceability_code_termination: 'Chemical traceability code termination',
                chemical_traceability_commencement: 'Start of chemical traceability',
                chemical_traceability_incident_declaration: 'Chemical traceability incident declaration',
                consumer_complaint_receipt_acknowledgement: 'Notice to the consumer that a report, request or complaint was received',
                cybersecurity_fee_schedule_commencement: 'Start of the cybersecurity fee schedule',
                food_database_traceability_commencement: 'Start of food database traceability',
                food_traceability_connection_start: 'Start of the food traceability connection',
                food_traceability_records_request: 'Food traceability records request',
                food_traceability_system_update: 'Food traceability system update',
            },
            views_declared_only: 'No dedicated screen',
            views_declared_only_aria: 'Show what {name} declares without a dedicated screen',
            views_basis_records: 'Reads records the pack declares',
            views_basis_regime: 'Reads a regime the pack declares',
            views_basis_fine_areas: 'Uses fine areas the pack declares',
            views_open_framework: 'Open the framework page',
            views_independent_note: 'Screens that work the same with or without an overlay, such as Legal Holds, are not listed.',
            menu_payments_evidence: 'Payments & eKYC Evidence',
            menu_trust_evidence: 'Trust & Identity Evidence',
            menu_platform_evidence: 'Platform Evidence',
            menu_public_data: 'Public Data Exchange',
            menu_product_compliance: 'Product Compliance & Traceability',
            menu_producer_substance: 'Producer & Substance Obligations',
            menu_trade_customs: 'Trade & Customs Evidence',
            menu_parcel_delivery: 'Parcel & Delivery Evidence',
            view_labels: {
                controlled_testing_cases: 'Controlled-Testing Cases',
                credit_information_supply: 'Credit-Information Supply Register',
                authority_directions: 'Authority Directions',
                regulated_accounts: 'Regulated Accounts',
                sector_licences: 'Sector Licences',
                licence_applications: 'Licence Applications and Fees',
                fee_lines_by_period: 'Fee Lines by Period',
                data_service_providers: 'Data-Service Provider Records',
                pcf_shared_information: 'Shared Supervisory Information',
                pcf_branch_publicity: 'Branch Publicity Evidence',
                pcf_branch_information_requests: 'Branch Information Requests',
                pcf_depositor_enquiries: 'Depositor Enquiries',
                pcf_supervision_system_records: 'Supervision System Records',
                data_exploitation_routes: 'Exploitation Routes',
                data_exploitation_requests: 'Requests and Grants',
                data_exploitation_testing: 'Controlled Testing Records',
                data_exploitation_notices: 'Incident Notices',
                data_exploitation_dispositions: 'End-of-Purpose Disposition',
                data_exploitation_incubator: 'Incubator Records',
                data_exploitation_foreign_cooperation: 'Foreign Cooperation Assessments',
                cybersecurity_assurance_index_periods: 'Assessment Periods',
                cybersecurity_assurance_index_statistics: 'Direction and Management Statistics',
                cybersecurity_assurance_index_supplier_requests: 'Supplier Evidence Requests',
                food_traceability_establishment: 'Food Establishment',
                food_traceability_lot_results: 'Lot Test Results',
                food_traceability_inspections: 'Food Inspections',
                food_traceability_warnings: 'Warnings',
                food_traceability_notices: 'Notices and Quantities',
                food_traceability_decisions: 'Violation Decisions',
                food_traceability_corrections: 'Corrections and Supplier Corrections',
                food_traceability_complaints: 'Complaints',
                food_traceability_routes: 'Connection Routes',
                biometric_capture_components: 'Biometric Capture Components',
                biometric_chain_of_custody: 'Biometric Chain-of-Custody Records',
                biometric_readiness: 'Biometric Readiness Reviews',
                biometric_supervision_records: 'Biometric Supervision and Inspection Records',
                product_classifications: 'Product Classifications',
                conformity_certificates: 'Conformity Certificates',
                student_records: 'Student Records',
                publications: 'Publications',
                publication_assessments: 'Publication Assessments',
                regulatory_placeholders: 'Regulatory Placeholders',
                supervisory_reporting: 'Supervisory Reporting',
                aml_programme: 'AML Programme',
                aml_cases: 'Suspicious-Activity Cases',
                identity_exchanges: 'Cross-Border Identity Exchanges',
                marketing_approvals: 'Marketing Approvals',
                investor_verifications: 'Investor Verifications',
                account_data_disclosures: 'Account Data Disclosures',
                regulatory_disclosures: 'Regulatory Disclosures',
                legal_representatives: 'Legal Representatives',
                penalty_readiness: 'Penalty Readiness',
                exposure_inputs: 'Exposure Inputs',
                enforcement_register: 'Enforcement Register',
                enforcement_cases: 'Enforcement Cases',
                identity_register: 'Identity Register',
                trust_providers: 'Trust Providers',
                electronic_submissions: 'Electronic Submissions',
                electronic_agreements: 'Electronic Agreements',
                payment_services: 'Payment Services',
                ekyc_evidence: 'eKYC Evidence',
                exception_review: 'Exception Review',
                retention_reconciliation: 'Retention Reconciliation',
                seller_register: 'Seller Register',
                transaction_reconciliation: 'Transaction Reconciliation',
                authority_correspondence: 'Authority Correspondence',
                dataset_catalog: 'Dataset Catalog',
                sharing_register: 'Sharing Register',
                open_data_releases: 'Open Data Releases',
                product_register: 'Product Register',
                batch_register: 'Batch & Serial Register',
                trace_timeline: 'Trace Timeline',
                conformity_evidence: 'Conformity Evidence',
                epr_reconciliation: 'EPR Reconciliation',
                chemical_register: 'Chemical Register',
                chemical_traceability_declarations: 'Chemical Traceability Declarations',
                customs_evidence: 'Customs Evidence',
                customs_tax_dossiers: 'Tax Dossiers and Received Documents',
                customs_tax_payments: 'Tax Payments and Due Dates',
                customs_tax_duty_free_lists: 'Duty-Free Lists and Usage Notifications',
                customs_tax_examinations: 'Tax Examinations',
                customs_tax_transition: 'Transition and Applicability',
                parcel_claims: 'Claims',
                postal_authorisations: 'Postal Authorisations',
            },
        },
        phased_obligations: {
            panel_title: 'Phased obligations [NL]',
            panel_aria_label: 'Phased obligations [NL]',
            panel_intro: 'Some clauses in this framework activate later than the framework itself. [NL]',
            phased_effective_date_label: 'Phased effective: [NL]',
            status_pending: 'Pending [NL]',
            status_active: 'Active [NL]',
            status_early: 'Active (early-implemented) [NL]',
            notice_window_label: 'Authority notice window: [NL]',
            days_label: 'days [NL]',
            recipient_label: 'Recipient: [NL]',
            early_implement_button: 'Mark early-implemented [NL]',
            early_implement_closed: 'Early implementation can be recorded only before the phased effective date, {date}.',
            effective_at_before_hint: 'Choose a date before the phased effective date, {date}.',
            effective_at_too_late: 'The date must be before the phased effective date, {date}.',
            early_implement_modal_title: 'Mark phased obligation as early-implemented [NL]',
            early_implement_modal_intro: 'Recording an early implementation stores the date your organisation began applying this obligation. This panel then shows the obligation as active from that date, and shows the date the notice is due, counted from the recorded date. The platform does not send the notice and keeps no record of it being sent. [NL]',
            effective_at_label: 'Effective from [NL]',
            reason_label: 'Reason (optional) [NL]',
            reason_placeholder: 'e.g. Board approved early adoption 2026-09-01 [NL]',
            confirm: 'Confirm [NL]',
            toast_early_implemented: 'Early implementation recorded. [NL]',
            early_read_failed: 'The early implementations recorded for this framework could not be read. Until they are, the status of each obligation is not shown and none can be marked early-implemented.',
            early_read_subject: 'the early implementations',
            audit_event_early_implemented: 'Phased obligation marked early-implemented [NL]',
            widget_rolling_window_label: 'Phased activations within 90 days [NL]',
            notice_due_label: 'Notice due by the end of [NL]',
            notice_due_time_suffix: '(organisation time) [NL]',
            notice_rolled_note: 'The last day fell on a weekend or holiday, so it moved to the next working day. [NL]',
            notice_platform_sends_nothing: 'The platform does not send the notice and keeps no record of it being sent. [NL]',
            notice_basis: {
                day_after_is_day_one_last_day_rolls_past_non_working_day: 'Computed: the day after implementation is day 1, the last day moves past a weekend or holiday. [NL]',
                day_after_is_day_one: 'Computed: the day after implementation is day 1. [NL]',
            },
            notice_gap: {
                no_notice_clock_declared: 'No notice due date: no installed record states the notice period. [NL]',
                notice_clock_ambiguous: 'No notice due date: more than one installed record states a notice period, and none says which one applies. [NL]',
                notice_clock_not_computable: 'No notice due date: the platform does not compute the period the installed record states. [NL]',
                implemented_on_unreadable: 'No notice due date: the recorded date could not be read. [NL]',
                notice_clock_not_in_force: 'No notice due date: the framework is not yet in force on the day the notice would fall due. [NL]',
                framework_installed_for_reference: 'No notice due date: this framework is installed for reference, so its clocks are not run. [NL]',
                notice_clock_activation_unknown: 'No notice due date: whether the clock binds is not known, because the framework record states no date or could not be read. [NL]',
                implemented_on_not_before_phased_date: 'No notice due date: the recorded date is on or after the phased effective date, so the obligation applies from then anyway and was not implemented early.',
            },
        },
        deadline_activation: {
            token_live: 'In force',
            token_upcoming: 'Not yet in force',
            in_force_from: 'In force from {date}',
            start_date_not_stated: 'Start date not stated',
            token_unknown: 'Timing unknown',
            unknown_sentence: 'The framework record this deadline belongs to could not be read, so whether it applies yet is not known.',
            retry: 'Retry',
            retry_busy: 'Retrying…',
            // Gate E12.5 pass 1 (P2-3) — the framework record was read and states no date.
            token_phase_unresolved: 'Phase start not dated',
            phase_unresolved_sentence: 'This deadline applies from a phase of its framework, and the framework does not date that phase, so it is not treated as in force.',
            token_not_stated: 'No effective date stated',
            not_stated_sentence: 'The framework states no date from which this deadline applies, so it is not treated as in force.',
            retry_subject: 'the regulatory deadlines',
            column_heading: 'When it applies',
            widget_overlays_failed: 'The installed sector overlays could not be read.',
            widget_reads_failed: 'Deadlines for {count} sector overlay(s) could not be read.',
            widget_retry_subject: 'the sector overlay deadlines',
            settings_sources_label: 'Deadlines behind this suggestion',
            settings_upcoming_not_counted: 'Not used for the suggestion until it is in force.',
            settings_unknown_not_counted: 'Not used for the suggestion.',
            settings_not_calendar_days_not_counted: 'Not used for the suggestion: its period is not counted in calendar days.',
            settings_retry_subject: 'the framework deadlines',
            channels_retry_subject: 'the submission channels and deadlines',
            token_not_owed: 'Not owed',
            not_owed_framework_status: 'The framework is not in force.',
            not_owed_row_suspended: 'This duty is suspended.',
            not_owed_superseded_by: 'Superseded by {framework}.',
            not_owed_superseded_unnamed: 'Superseded by another framework.',
            not_owed_row_legacy: 'Legacy — no longer in force.',
            not_owed_unrecognised: 'The record states that this duty is not owed.',
            settings_not_owed_not_counted: 'Not used for the suggestion: this duty is not owed.',
        },
        // Sprint 62.58.0 (Slice C) — English for key parity until Phase 25 (R-57).
        framework_briefings: {
            summary_export: {
                title: 'Framework summary: {name}',
                section_status: 'Status and dates',
                col_field: 'Field',
                col_value: 'Value',
                field_code: 'Framework code',
                field_status: 'Status',
                field_issued: 'Issued',
                field_effective: 'In force from',
                draft_not_in_force: 'Draft — not yet in force',
                not_stated: 'Not stated',
                section_parent: 'Parent framework',
                parent_line: "This framework is read with its parent framework, {name} ({code}), status: {status}. The dated rows below are the parent framework's, not this framework's.",
                parent_not_installed: 'The parent framework {code} is not installed in this organization, so its dated rows are not shown.',
                no_parent: 'This framework names no parent framework.',
                parent_no_rows: 'No dated rows of the parent framework are in use in this organization.',
                col_belongs_to: 'Belongs to',
                section_guidance: 'Guidance',
                no_guidance: 'No guidance notes are installed for this framework.',
                col_title: 'Title',
                col_summary: 'Summary',
                section_requirements: 'Requirements',
                no_requirements: 'No requirements are installed for this framework.',
                col_requirement_id: 'ID',
                col_reference: 'Reference',
                col_addressed_to: 'Addressed to',
                col_mandatory: 'Mandatory',
                mandatory_yes: 'Yes',
                mandatory_no: 'No',
                mandatory_not_stated: 'Not stated',
                mandatory_no_draft: 'No — proposed in a draft',
                mandatory_no_legacy: 'No — the framework is legacy',
                mandatory_no_suspended: "No — the framework's application is suspended",
                section_deadlines: 'Deadlines',
                no_deadlines: 'No deadline rows are installed for this framework.',
                col_deadline: 'Deadline',
                col_date_or_period: 'Date or period',
                col_note: 'Note',
                stated_date: '{date} (as stated)',
                authority_clock: "The authority's period, not a duty of the organization.",
                section_pack: 'Pack',
                pack_line: 'Installed from pack {code}, version {version}.',
                pack_not_recorded: 'The pack this framework was installed from is not recorded.',
                generated_line: "Generated on {date} from this organization's installed framework record. This summary is for reading only; ComplianceOne submits nothing to any authority.",
            },
            record: {
                tab: 'Briefings',
                heading: 'Briefings and consultation comments',
                intro: 'Record the briefings your organization held about this framework and the consultation comments it sent. An entry is kept as recorded; a correction is a new entry that names the one it corrects.',
                boundary: 'A consultation comment records what your organization sent, to whom and when. ComplianceOne submits nothing to any authority.',
                kind_briefing: 'Briefing',
                kind_consultation_comment: 'Consultation comment',
                channel_meeting: 'Meeting',
                channel_email: 'Email',
                channel_document: 'Document',
                channel_webinar: 'Webinar',
                channel_other: 'Other',
                col_kind: 'Kind',
                col_audience: 'Audience',
                col_held_on: 'Date',
                col_channel: 'Channel',
                col_approver: 'Approver',
                col_approval: 'Approval',
                col_pack_version: 'Pack version',
                approval_none: 'No approver named',
                approval_pending: 'Awaiting approval',
                approval_approved: 'Approved on {date}',
                record: 'Record an entry',
                recording: 'Recording…',
                approve: 'Approve',
                approving: 'Approving…',
                approve_aria: 'Approve the entry of {date} for {audience}',
                field_kind: 'Kind',
                field_audience: 'Audience',
                field_audience_hint_comment: 'For a consultation comment: to whom your organization sent it.',
                field_held_on: 'Date held or sent',
                field_held_on_hint: 'A calendar date, not after today.',
                field_channel: 'Channel',
                field_channel_note: 'Channel details',
                field_channel_note_required: 'Describe the channel when it is Other.',
                field_reference: 'Reference',
                field_reference_hint: 'The reference of what was sent, such as a letter number or a portal receipt.',
                field_summary: 'Summary',
                field_approver: 'Approver',
                field_correction_of: 'Corrects entry',
                field_correction_note: 'Correction note',
                empty: 'No briefings or consultation comments are recorded for this framework.',
                load_failed: 'The briefings and consultation comments could not be loaded.',
                record_failed: 'The entry could not be recorded.',
                approve_failed: 'The entry could not be approved.',
                recorded_toast: 'Entry recorded.',
                approved_toast: 'Entry approved.',
                pack_changed: 'Recorded against pack version {version}; this framework has changed since — see Regulatory Updates',
                corrected_by: 'Corrected by a later entry',
                correction_label: 'Correction',
                evidence_count: 'Documents: {count}',
                attach_summary_offer: 'Attach this summary to an entry',
                kind_placeholder: 'Choose a kind',
                channel_placeholder: 'Choose a channel',
                approver_placeholder: 'No approver',
                correction_none: 'Not a correction',
                correction_option: '{kind} of {date} for {audience}',
                correction_picker_failed: 'The earlier entries could not be read, so a correction cannot be chosen now.',
                error_required: 'This field is required.',
                error_held_on_future: 'The date cannot be after today.',
                error_correction_note_required: 'Explain what this entry corrects.',
                error_approver_not_found: 'The approver is not a user of this organization.',
                error_not_approver: 'Only the named approver can approve this entry.',
                error_already_approved: 'This entry is already approved.',
                error_self_approval: 'The approver must be someone other than the person recording the entry.',
                form_intro: 'Record what was held or sent. Documents are added after the entry is recorded, from Documents on its row.',
                summary_count: '{count} of {max} characters',
                loading: 'Loading the entries…',
                retry_subject: 'the entries',
                reload: 'Reload',
                reloading: 'Reloading…',
                pagination: 'Pages of entries',
                col_summary: 'Summary',
                col_documents: 'Documents',
                documents_open: 'Documents',
                documents_open_aria: 'Documents of the entry of {date} for {audience}',
                reference_line: 'Reference: {reference}',
                channel_note_line: 'Channel details: {note}',
                correction_note_line: 'Correction note: {note}',
                corrects_line: 'Corrects the entry of {date}',
                corrects_unknown: 'Corrects an earlier entry',
                pack_changed_open_comparison: 'See what changed',
                pack_changed_open_updates: 'Open Regulatory Updates',
                replaced_since: 'Recorded against this text; it has since been replaced by another framework —',
                pack_version_not_recorded: 'Not recorded',
                recorded_while: 'Framework status when recorded: {status}',
                framework_status_suspended: 'Suspended',
                recorded_line: 'Recorded by {person} on {date}',
                files_heading: 'Documents',
                files_intro: 'The documents behind this entry, such as the slides, the letter sent or the downloaded framework summary.',
                files_slot: 'Documents of this entry',
                files_none: 'No document is attached to this entry.',
                file_hash: 'SHA-256: {hash}',
                file_not_held: 'Document no longer held',
                file_download: 'Download',
                file_remove: 'Remove',
                file_upload: 'Add a document',
                file_bound: 'Document added.',
                file_removed: 'Document removed.',
                file_bind_failed: 'The document could not be added.',
                file_remove_failed: 'The document could not be removed.',
                file_not_owned: 'The entry this document belongs to was not found.',
                file_remove_confirm: 'Remove this document from the entry and delete it?',
                files_approved_kept: 'This entry has been approved, so its documents are kept as approved and cannot be removed. To correct it, record a correction entry.',
                documents_load_failed: 'The documents could not be loaded.',
                export_label: 'Export summary',
                export_done: 'Framework summary exported.',
                export_failed: 'The framework summary could not be exported.',
                attach_summary_hint: 'To keep this summary with an entry, open the Briefings tab, choose Documents on the entry and add the downloaded file.',
                directory_unavailable: 'The list of people could not be read, so an approver cannot be chosen now.',
                approver_retry_subject: 'the list of people',
            },
        },
        // Sprint 62.58.0 (Slice F) — the regulatory-update comparison and its review.
        update_comparison: {
            page: {
                module_guide: "What changed between the text installed before and the text installed now, requirement by requirement, with your organization's reading of it and the records filled under the earlier text.",
                title: 'What changed: {name}',
                title_fallback: 'What changed',
                description: 'The requirements of the text installed before, beside the text installed now. This comparison is information: it does not change what your organization owes.',
                back: 'Back to Regulatory Updates',
                loading: 'Loading the comparison…',
                load_failed: 'The comparison could not be loaded.',
                not_found: 'This regulatory update was not found.',
                retry_subject: 'the comparison',
                reload: 'Reload',
                reloading: 'Reloading…',
                no_comparison: 'This regulatory update holds no requirement comparison. An update recorded by hand, or one installed before comparisons were kept, has none.',
                status_heading: 'Status change',
                status_change: '{from} → {to}',
                replaced_by: 'Replaced by {name}',
                status_not_recorded: 'Not recorded',
                basis_computed: 'Compared requirement by requirement between the two installed versions of this framework.',
                basis_declared: 'Compared using the correspondence the replacing framework declares.',
                derived_on_read: 'These rows were worked out when this page was opened, from the frameworks installed now.',
                from_heading: 'Earlier text',
                to_heading: 'Text installed now',
                side_status: 'Status: {status}',
                side_pack_version: 'Pack version: {version}',
                counts_heading: 'Summary of changes',
                count_line: '{label}: {count}',
                change_changed: 'Changed',
                change_removed: 'Removed',
                change_new: 'New',
                change_unchanged: 'Unchanged',
                rows_heading: 'Requirements',
                filter_label: 'Show',
                filter_all: 'All requirements',
                showing: 'Showing {shown} of {total}',
                col_requirement: 'Requirement',
                col_change: 'Change',
                col_from_title: 'Title in the earlier text',
                col_to_title: 'Title in the text installed now',
                col_mandatory: 'Mandatory',
                col_note: 'Note',
                mandatory_yes: 'Yes',
                mandatory_no: 'No',
                mandatory_not_stated: 'Not stated',
                mandatory_change: '{from} → {to}',
                not_in_text: 'Not in this text',
                also_corresponds: 'Also corresponds to: {ids}',
                rows_empty: 'No requirement matches this filter.',
                rows_pagination: 'Pages of requirements',
                superseded_texts_not_installed: 'No comparison: one of the two texts is no longer installed in your organization, or the replacing framework no longer declares that it replaces the earlier text. This regulatory update is kept as it was recorded.',
                withheld_other_pack: 'No comparison: the previous record was installed from another pack ({code}).',
                withheld_other_pack_unknown: 'No comparison: the previous record was not installed from this pack.',
            },
            review: {
                heading: "Your organization's reading",
                intro: "Record how each requirement that changed, was removed or is new affects your organization, and what you concluded. A reading is your organization's own record: it does not change what the framework requires, its status or any deadline.",
                record: 'Record comparison review',
                recording: 'Recording…',
                none: 'No comparison review is recorded yet.',
                current_heading: 'Current review',
                recorded_line: 'Recorded by {person} on {date}',
                conclusion: 'Conclusion',
                next_steps: 'Next steps',
                no_next_steps: 'No next steps recorded.',
                readings_heading: 'Readings',
                col_reading: 'Reading',
                reading_affects_us: 'Affects us',
                reading_does_not_affect_us: 'Does not affect us',
                reading_to_assess: 'To assess',
                no_rows_read: 'This review gives no reading for a single requirement.',
                history_heading: 'Earlier reviews',
                history_pagination: 'Pages of earlier reviews',
                col_recorded: 'Recorded',
                col_recorded_by: 'Recorded by',
                col_conclusion: 'Conclusion',
                col_readings: 'Readings',
                readings_summary: '{affects} affect us, {not_affect} do not, {to_assess} to assess',
                modal_intro: 'Give a reading for every requirement that changed, was removed or is new, then state your conclusion.',
                row_legend: '{id}: {title}',
                field_reading: 'Reading',
                field_reading_placeholder: 'Choose a reading',
                field_note: 'Note',
                field_conclusion: 'Conclusion',
                field_next_steps: 'Next steps',
                text_count: '{count} of {max} characters',
                error_reading_required: 'Choose a reading for this requirement.',
                error_conclusion_required: 'State your conclusion.',
                record_failed: 'The comparison review could not be recorded.',
                recorded_toast: 'Comparison review recorded.',
                no_comparison_error: 'This update holds no comparison to review.',
                rows_loading: 'Loading the requirements to read…',
                rows_load_failed: 'The requirements to read could not be loaded.',
                no_rows_to_read: 'Every requirement is unchanged, so only a conclusion is needed.',
            },
            reread: {
                heading: 'Records filled under the replaced text',
                intro: 'These records were filled while the earlier text applied. They are kept as they are: nothing in them is changed, scored or reopened. Re-read each one against the final text.',
                forms_heading: 'Form records',
                col_record: 'Record',
                col_form: 'Form',
                col_status: 'Status',
                col_updated: 'Last updated',
                forms_none: 'No form record was filled under the replaced text.',
                forms_withheld: 'Form records are listed only to people who may read form records.',
                untitled: 'Untitled record',
                forms_pagination: 'Pages of form records',
                questionnaires_heading: 'Vendor questionnaires',
                notice: 'Filled under {from}, which has been replaced by {to} — re-read it against the final text',
                notice_link: 'See what changed',
                notice_link_aria: 'See what changed between {from} and {to}',
                notice_same_text: 'Filled under pack version {version} of {name}; this text has changed since — re-read it against the current text',
                notice_same_text_unversioned: 'Filled under an earlier pack version of {name}; this text has changed since — re-read it against the current text',
                notice_same_text_link_aria: 'See what changed in {name}',
            },
            recheck: {
                will_draft: 'The pack publisher will recheck this draft on {date}.',
                will_legacy: 'The pack publisher will recheck this legacy framework on {date}.',
                will_suspended: 'The pack publisher will recheck this framework, whose application is suspended, on {date}.',
                was_due_draft: 'The pack publisher was due to recheck this draft on {date}; this record has not yet been updated.',
                was_due_legacy: 'The pack publisher was due to recheck this legacy framework on {date}; this record has not yet been updated.',
                was_due_suspended: 'The pack publisher was due to recheck this framework, whose application is suspended, on {date}; this record has not yet been updated.',
                records_line: "This framework's record changes only when a new pack version is installed; your own records are never changed.",
            },
            list: {
                col_changed: 'Changed requirements',
                no_comparison_short: 'No comparison',
                pagination: 'Pages of regulatory updates',
                view_comparison: 'View comparison',
                view_comparison_aria: 'View comparison for {title}',
                reviews_count: 'Reviews: {count}',
            },
        },
        // Sprint 62.49.1 (DB-2) — Framework Detail's Suspension section.
        framework_suspension: {
            heading: 'Suspension',
            intro: 'As the record states it: when the suspension of this framework’s application began, each instrument that suspended it, and what ends it.',
            start_label: 'Began',
            start_value: 'Suspended from {date}',
            basis_label: 'Basis for that day',
            steps_caption: 'Instruments that suspended this framework, in the order they took effect',
            col_instrument: 'Instrument',
            col_issued: 'Issued',
            col_effective: 'Took effect',
            col_until: 'Suspends until',
            col_replaces: 'Replaces',
            replaces_none: 'None',
            until_date: 'Until {date}',
            source_line: 'Read from: {source}',
            end_label: 'Ends',
            end_date: 'On {date}',
            end_event: 'When this happens: {event}',
            event_not_occurred: 'Not yet occurred',
            event_occurred_on: 'Occurred on {date}',
        },
        market_pack_installed: 'Market pack installed',
        missing_market_code: 'Cannot uninstall: missing market code',
        pack_installed_announce: 'Pack installed successfully',
        install_zip_refused_title: 'Uploaded pack files are not installed',
        install_zip_refused_body: 'An uploaded pack .zip must be signed, and no pack signing key is configured for this installation, so every uploaded zip is refused. Packs that ship with the release are installed from Localization (Market Packs, Language Packs and the Overlay Catalog), and each one is checked against the release before it installs.',
        pack_uninstalled: 'Language pack uninstalled',
    },
    partner_marketplace: {
        /**
         * fix-sprint-20260828-2118 F20 — promoted out of
         * `pages/admin/partner-marketplace/strings.ts`.
         *
         * That file is an inline `{ en, vi }` pair resolved by `extractLocale`, so
         * the 14 non-EN/VI locales received the English sentence at all 25 of this
         * module's call sites — on pages whose every other string comes from `t.*`.
         * The wording is 62.5.0's, unchanged.
         */
        selector_truncated: 'Showing {shown} of {total}. Narrow the search to reach the rest.',
        nav: {
            dashboard: 'Overzicht',
            partner_orgs: 'Partnerorganisaties',
            service_tiers: 'Serviceniveaus',
            deliverable_templates: 'Opleverpuntsjablonen',
            deliverables: 'Opleverpunten',
            brand_profiles: 'Merkprofielen',
            support_tickets: 'Ondersteuningstickets',
            workflow_templates: 'Werkstroomsjablonen',
            workflows: 'Werkstromen',
            escalation: 'Escalatie',
            reports: 'Rapporten',
            audit: 'Controlespoor',
        },
        dashboard: {
            title: 'Partneroverzicht',
            loading: 'Laden van dashboard gegevens...',
            total_managed_tenants: 'Totaal Beheerd Tenants',
            avg_health_score: 'Avg Gezondheid Score',
            programme_health_score: 'Partner Programme Health',
            health_band_green: 'Healthy',
            health_band_amber: 'Needs attention',
            health_band_red: 'Critical',
            health_coverage_note: 'Scored from your own partner operations: open tickets, resolution times against target, deliverable timeliness and escalations. It does not cover the compliance posture or data freshness of the tenants you manage.',
            open_tickets: 'Open tickets',
            upcoming_deliverables: 'Aanstaand Deliverables',
            escalated_tickets: 'Geëscaleerd Tickets',
            managed_tenants: 'Beheerd Tenants',
            partner_tier: 'Partner Niveau',
            managed_tenants_count: 'Beheerd Tenants Aantal',
            onboarding_date: 'Onboarding Datum',
            view_details: 'Bekijken Details',
            no_partner_orgs: 'Geen partner organisaties gevonden.',
        },
        partner_orgs: {
            title: 'Partnerorganisaties',
            new: 'Nieuw Partner Org',
            contact_name: 'Contact Naam',
            contact_email: 'Contact E-mail',
            partner_tier: 'Partner Niveau',
            managed_orgs: 'Beheerd Orgs',
            onboarding_date: 'Onboarding Datum',
            no_orgs: 'Geen partner organisaties gevonden.',
            partner_type: 'Partner Type',
            type_consulting: 'Consulting Firm',
            type_technology: 'Technology Provider',
            type_legal: 'Legal Firm',
            type_audit: 'Audit & Assurance',
            type_managed_services: 'Managed Services Provider',
            type_reseller: 'Reseller',
            contact_phone: 'Contact Telefoon',
            metadata_json: 'Metadata (JSON-indeling)',
            change_status: 'Wijzigen Status',
            new_status: 'Nieuw Status',
            invalid_json_metadata: 'Ongeldig JSON in metadata field',
            suspended: 'Opgeschort',
            offboarded: 'Uitgeschreven',
            all_tiers: 'Alle Niveaus',
            silver: 'Zilver',
            gold: 'Goud',
            platinum: 'Platina',
            status_offboarded: 'Uitgeschreven',
            status_suspended: 'Opgeschort',
            tier_gold: 'Goud',
            tier_platinum: 'Platina',
            tier_silver: 'Zilver',
            org_status_updated: 'Organization status updated',
        },
        service_tiers: {
            title: 'Serviceniveaus',
            new: 'Nieuw Niveau',
            tier_name: 'Niveau Naam',
            monthly_fee: 'Maandelijks Fee',
            features: 'Functies',
            no_tiers: 'Geen service niveaus gevonden.',
            tier_code: 'Niveau Code',
            response_time_hrs: 'Reactie Tijd (hrs)',
            resolution_time_hrs: 'Oplossing Tijd (hrs)',
            review_frequency: 'Beoordelen Frequentie',
            sla_response_time: 'SLA Reactie Tijd (hours)',
            sla_resolution_time: 'SLA Oplossing Tijd (hours)',
            module_profile: 'Included Modules',
            module_profile_hint: 'The modules this tier covers. Descriptive only — it records what the tier is sold as, and does not grant or restrict access.',
            module_profile_selected: '{count} of {total} selected',
            module_profile_select_all: 'Select all',
            module_profile_clear_all: 'Clear all',
            module_profile_unknown: 'Unrecognised — kept from an earlier version of this tier',
            comparing: 'Vergelijken...',
            compare: 'Vergelijken',
            comparison: 'Service Niveau Vergelijking',
            attribute: 'Attribuut',
            deprecate: 'Afschrijven',
            deprecated: 'Afgeschreven',
            select_compare: 'Selecteren bij least 2 niveaus naar compare',
            comparison_title: 'Service Niveau Vergelijking',
            status_deprecated: 'Afgeschreven',
            activate: 'Activeren',
            resolution_time: 'Oplossing Tijd',
            response_time: 'Reactie Tijd',
            select_at_least_two: 'Selecteren bij least 2 niveaus',
        },
        deliverable_templates: {
            title: 'Opleverpuntsjablonen',
            new: 'Nieuw Sjabloon',
            template_name: 'Sjabloon Naam',
            deliverable_type: 'Type deliverable',
            no_templates: 'Geen deliverable sjablonen gevonden.',
            frequency: 'Frequentie',
            est_effort_hrs: 'Est. Inspanning (hrs)',
            estimated_effort: 'Geschat Inspanning (hours)',
            associated_module: 'Gekoppelde module',
            linked_tiers_count: 'Gekoppeld Niveaus Aantal',
            evidence_requirements: 'Bewijs Vereisten (comma-separated)',
            tier_ids: "Niveau ID's (comma-separated)",
            module_none: 'No module',
            module_unrecognised: '{value} — not a platform module; kept as stored',
            frequency_one_time: 'Eén Tijd',
            frequency_monthly: 'Maandelijks',
            frequency_quarterly: 'Kwartaal',
            frequency_annually: 'Jaarlijks',
            type_report: 'Rapport',
            type_review: 'Beoordelen',
            type_assessment: 'Beoordeling',
            type_meeting: 'Vergadering',
            type_configuration: 'Configuratie',
            freq_one_time: 'Eén Tijd',
            freq_monthly: 'Maandelijks',
            freq_quarterly: 'Kwartaal',
            freq_annually: 'Jaarlijks',
            status_deprecated: 'Afgeschreven',
        },
        deliverables: {
            title: 'Opleverpunten',
            new: 'Nieuw Deliverable',
            partner: 'Zakenpartner',
            deliverable_type: 'Type deliverable',
            due_date: 'Due Datum',
            no_deliverables: 'Geen deliverables gevonden.',
            assigned_to: 'Toegewezen Naar',
            completion_date: 'Voltooiing Datum',
            update_status: 'Bijwerken Status',
            hide_details: 'Verbergen Details',
            view_details: 'Bekijken Details',
            evidence_ids: "Bewijs ID's",
            evidence_expected: 'Evidence expected',
            evidence_expected_none: 'No evidence requirements are recorded on this deliverable.',
            client_approval: 'Client Goedkeuring',
            bulk_update: 'Bulk Bijwerken',
            bulk_update_status: 'Bulk Bijwerken Status',
            generate_schedule: 'Genereren Plannen',
            generate_deliverable_schedule: 'Genereren Deliverable Plannen',
            unassigned: 'Unassigned',
            assigned_to_user: 'Assigned to {name}.',
            assignee_cleared: 'Deliverable is now unassigned.',
            edit_notes: 'Edit notes',
            edit_evidence: 'Edit evidence',
            evidence_select_label: 'Select evidence items',
            evidence_none_available: 'No evidence items are available. Add them in the evidence repository first.',
            approval_note_label: 'Note (optional)',
            approval_status_approved: 'Approved',
            approval_status_rejected: 'Rejected',
            approval_by_at: 'by {name} on {date}',
            approval_recorded: 'Client approval recorded.',
            approval_rejection_recorded: 'Client rejection recorded.',
            notes_saved: 'Notes saved.',
            evidence_saved: 'Evidence updated.',
            generated_count: 'Generated {count} deliverables.',
            generated_none: 'No deliverables were generated. No active deliverable template is linked to this tier — create one under Deliverable Templates and link it to this tier code first.',
            organization_id: 'Organisatie ID',
            service_tier_id: 'Service Niveau ID',
            filter_assigned_to: 'Filteren op Toegewezen Naar',
            select_status: 'Selecteren status...',
            new_status: 'Nieuw Status',
            upcoming: 'Aanstaand',
            overdue: 'Te laat',
            status_deferred: 'Uitgesteld',
            status_overdue: 'Te laat',
            status_upcoming: 'Aanstaand',
        },
        brand_profiles: {
            title: 'Merkprofielen',
            new: 'Nieuw Merk Profiel',
            partner: 'Zakenpartner',
            brand_name: 'Merk Naam',
            primary_color: 'Primair Kleur',
            logo_url: 'Logo-adres',
            no_profiles: 'Geen brand profiles gevonden.',
            secondary_color: 'Secundair Kleur',
            header_text: 'Koptekst Text',
            footer_text: 'Voettekst Text',
            contact_email: 'Contact E-mail',
            contact_phone: 'Contact Telefoon',
            contact_website: 'Contactwebsite',
            contact_address: 'Contact Adres',
            is_default: 'Is Standaard',
            set_default: 'Set Standaard',
            preview: 'Voorbeeld',
            report_header_preview: 'Rapport Koptekst Voorbeeld',
            report_footer_preview: 'Rapport Voettekst Voorbeeld',
            preview_description: 'This is een preview van de report header met de geselecteerd brand colors.',
            contact_website_short: 'Webpagina',
            logo_preview: 'Logo-voorbeeld',
        },
        support_tickets: {
            title: 'Ondersteuningstickets',
            new: 'Nieuw Ticket',
            partner: 'Zakenpartner',
            subject: 'Onderwerp',
            assigned_to: 'Toegewezen Naar',
            no_tickets: 'Geen support tickets gevonden.',
            category: 'Categorie',
            priority: 'Prioriteit',
            escalation_tier: 'Escalatie Niveau',
            sla_status: 'SLA-status',
            update_status: 'Bijwerken Status',
            escalate: 'Escaleren',
            add_note: 'Toevoegen Opmerking',
            escalate_ticket: 'Escaleren Ticket',
            escalate_to_tier: 'Escaleren Naar Niveau',
            notes_timeline: 'Opmerkingen Tijdlijn',
            no_notes: 'No opmerkingen yet.',
            attachments: 'Bijlagen',
            no_attachments: 'Geen bijlagen.',
            client_org_id: 'Klantorganisatie-ID',
            content: 'Inhoud',
            note_type: 'Soort',
            view_detail: 'Bekijken Detail',
            hide: 'Verbergen',
            new_status: 'Nieuw Status',
            all_categories: 'Alle Categorieën',
            all_priorities: 'Alle Priorities',
            all_tiers: 'Alle Niveaus',
            ticket_not_found: 'Ticket not gevonden',
            ticket_id: 'Ticket-ID',
            client_org: 'Klantorganisatie',
            deescalate: 'De-escaleren',
            deescalate_ticket: 'Ticket de-escaleren',
            deescalate_to_tier: 'De-escalate Naar Niveau',
            add_attachment: 'Toevoegen Bijlage',
            file: 'Bestand',
            linked_entity: 'Gekoppeld Entiteit',
            sla_target: 'Doel',
            breached: 'Geschonden',
            remaining_hours: 'resterend',
            na: 'N/A',
            tickets_found: 'tickets gevonden',
            reason: 'Reden',
            target: 'Doel',
            upload: 'Uploaden',
        },
        workflow_templates: {
            title: 'Werkstroomsjablonen',
            new: 'Nieuw Sjabloon',
            template_name: 'Sjabloon Naam',
            steps: 'Stappen',
            no_templates: 'Geen workflow sjablonen gevonden.',
            category: 'Categorie',
            view_steps: 'Bekijken Stappen',
            hide_steps: 'Verbergen Stappen',
            step: 'Stap',
            step_title: 'Titel',
            step_description: 'Beschrijving',
            assignee_role: 'Toegewezene Rol',
            sla_hours: 'SLA-uren',
            evidence_required: 'Bewijs Vereist',
            guidance_notes: 'Richtlijn Opmerkingen',
            add_step: 'Toevoegen Stap',
            no_steps: 'Geen stappen gedefinieerd.',
            no_steps_defined: 'Geen stappen gedefinieerd.',
            templates_found: 'sjablonen gevonden',
            all_categories: 'Alle Categorieën',
        },
        workflows: {
            client_org: 'Klantorganisatie',
            title: 'Workflow Instanties',
            new: 'Nieuw Workflow',
            template: 'Sjabloon',
            partner: 'Zakenpartner',
            current_step: 'Huidig Stap',
            progress: 'Voortgang',
            no_workflows: 'Geen workflow instanties gevonden.',
            instances_found: 'instanties gevonden',
        },
        escalation: {
            title: 'Escalatie Dashboard',
            ticket: 'Melding',
            partner: 'Zakenpartner',
            escalated_at: 'Geëscaleerd At',
            reason: 'Reden',
            no_escalations: 'Geen escalations gevonden.',
            time_in_tier: 'Tijd in Niveau',
            auto_evaluate: 'Auto Evalueren',
            priority_critical: 'Kritiek',
            priority_high: 'Hoog',
            priority_medium: 'Gemiddeld',
            priority_low: 'Low',
            deescalated_from_dashboard: 'De-escalated van dashboard',
            sla_breached: 'SLA Geschonden',
            sla_remaining: 'SLA Resterend: {hours}h',
            sr_total_escalated: 'Total escalated',
            sr_by_tier: 'By tier',
            sr_by_priority: 'By priority',
        },
        reports: {
            client_org: 'Klantorganisatie',
            title: 'Rapporten',
            partner: 'Zakenpartner',
            report_type: 'Rapport Type',
            generated_at: 'Gegenereerd At',
            no_reports: 'Geen reports gevonden.',
            generate: 'Genereren Rapport',
        },
        audit: {
            title: 'Partner-auditspoor',
            event_type: 'Gebeurtenis Type',
            entity_type: 'Entiteit Type',
            entity: 'Entiteit',
            entity_id: 'Entiteit ID',
            actor: 'Betrokkene',
            timestamp: 'Tijdstempel',
            no_events: 'Geen audit gebeurtenissen gevonden.',
            events_found: 'gebeurtenissen gevonden',
            client_org: 'Klantorganisatie',
            all_actors: 'Alle actoren',
            event_type_search: 'Zoek gebeurtenistype',
            event_type_created: 'Gemaakt',
            event_type_updated: 'Bijgewerkt',
            event_type_deleted: 'Verwijderd',
            event_type_set_default: 'Set as Default',
            event_type_status_changed: 'Status Changed',
            event_type_status_updated: 'Status Updated',
            event_type_generated: 'Generated',
            event_type_export_generated: 'Export Generated',
            event_type_escalated: 'Escalated',
            event_type_auto_escalated: 'Auto-Escalated',
            event_type_deescalated: 'De-escalated',
            event_type_managed_org_added: 'Managed Client Added',
            event_type_managed_org_removed: 'Managed Client Removed',
            event_type_versioned: 'New Version',
            event_type_attachment_added: 'Attachment Added',
            event_type_note_added: 'Note Added',
            event_type_step_advanced: 'Step Advanced',
            event_type_cancelled: 'Cancelled',
            entity_type_partner_org: 'Partnerorg',
            entity_type_service_tier: 'Servicelaag',
            entity_type_deliverable_template: 'Leverbaar sjabloon',
            entity_type_deliverable_instance: 'Leverbaar exemplaar',
            entity_type_brand_profile: 'Merkprofiel',
            entity_type_support_ticket: 'Ondersteuningsticket',
            entity_type_workflow_template: 'Workflow-sjabloon',
            entity_type_workflow_instance: 'Werkstroominstantie',
            entity_type_generated_report: 'Gegenereerd rapport',
            entity_type_audit_export: 'Auditexport',
        },
        no_permission_title: 'Onvoldoende machtigingen',
        no_permission_message: 'U heeft geen toestemming voor toegang tot de partnerwerkruimte.',
        status_active: 'Actief',
        status_suspended: 'Opgeschort',
        status_offboarded: 'Buiten boord',
        status_completed: 'Voltooid',
        status_cancelled: 'Geannuleerd',
        status_deprecated: 'Afgekeurd',
        status_new: 'Nieuw',
        status_triaged: 'Triaged',
        status_in_progress: 'In uitvoering',
        status_waiting_on_client: 'Wachten op cliënt',
        status_waiting_on_vendor: 'Wachten op verkoper',
        status_escalated: 'Geëscaleerd',
        status_resolved: 'Opgelost',
        status_closed: 'Gesloten',
        status_pending: 'In behandeling',
        status_skipped: 'Overgeslagen',
        client_org_column: 'Client org',
        client_org_unresolved: 'Client org not recorded',
        category_onboarding: 'Aan boord',
        category_incident_escalation: 'Incidentescalatie',
        category_config_change: 'Configuratiewijziging',
        category_periodic_review: 'Periodieke beoordeling',
        category_offboarding: 'Offboarden',
        category_custom: 'Aangepast',
        category_configuration: 'Configuratie',
        category_incident: 'Voorval',
        category_compliance_question: 'Nalevingsvraag',
        category_feature_request: 'Functieverzoek',
        category_bug_report: 'Bugrapport',
        priority_critical: 'Kritisch',
        priority_high: 'Hoog',
        priority_medium: 'Middelmatig',
        priority_low: 'Laag',
        tier_silver: 'Zilver',
        tier_gold: 'Goud',
        tier_platinum: 'Platina',
        report_type_compliance_summary: 'Nalevingsoverzicht',
        report_type_risk_overview: 'Risicooverzicht',
        report_type_incident_report: 'Incidentrapport',
        report_type_consent_status: 'Toestemmingsstatus',
        report_type_program_maturity: 'Programma volwassenheid',
        report_type_executive_pack: 'Executive-pakket',
        note_type_internal: 'Intern',
        note_type_client_visible: 'Klant zichtbaar',
        status_updated: 'Status succesvol bijgewerkt',
        deliverable_status_updated: 'De status van het leverbare product is succesvol bijgewerkt',
        ticket_status_updated: 'Ticketstatus is succesvol bijgewerkt',
        ticket_escalated: 'Ticket is succesvol geëscaleerd',
        note_added: 'Opmerking succesvol toegevoegd',
        attachment_uploaded: 'Bijlage succesvol geüpload',
        ticket_deescalated: 'Ticket is gede-escaleerd',
        auto_evaluation_completed: 'Automatische evaluatie voltooid',
        template_created: 'Workflowsjabloon is succesvol aangemaakt',
        template_updated: 'Workflowsjabloon succesvol bijgewerkt',
        brand_set_default: 'Merkprofiel is succesvol als standaard ingesteld',
        brand_created: 'Merkprofiel is succesvol aangemaakt',
        brand_updated: 'Merkprofiel succesvol bijgewerkt',
        tier_created: 'Servicelaag is succesvol gemaakt',
        tier_updated: 'Servicelaag is succesvol bijgewerkt',
        tier_deprecated: 'Servicelaag is beëindigd',
        tier_activated: 'Servicelaag is succesvol geactiveerd',
        period: 'Periode',
        format: 'Formaat',
        period_start: 'Periode begin',
        period_end: 'Einde periode',
        reports_count: '{count} rapporten',
        reports_count_one: '{count} rapport',
        metric: 'Metrisch',
        value: 'Waarde',
        showing_chart_view: 'Grafiekweergave weergeven',
        showing_table_view: 'Gegevenstabelweergave weergeven',
        evidence_ids_placeholder: 'bijv. ev-001, ev-002',
        confirm_cancel_workflow: 'Weet u zeker dat u deze workflow wilt annuleren? Deze actie kan niet ongedaan worden gemaakt.',
        export_csv: 'Exporteer CSV',
        compliance_qualifications_title: 'Kwalificaties van complianceservices',
        compliance_qualifications_desc: "The partner tier (Silver, Gold or Platinum) is a label on the partner record. The platform reads it in one place: a support ticket opened for an organisation the partner manages takes its resolution-time target from the partner's active service tier with the same code, or 24 hours when there is none. The tier grants no access and does not qualify a partner for any kind of engagement.",
        regulatory_output_title: 'Regelgevende outputafstemming',
        regulatory_output_desc: 'Elk sjabloontype is toegewezen aan een regelgevende outputcategorie: rapporten produceren nalevingsdocumentatie, beoordelingen valideren de effectiviteit van controles, beoordelingen omvatten risico-evaluaties (DPIA, kloofanalyse), vergaderingen documenteren governancesessies. Bewijsvereisten definiëren de artefacten waarnaar elk product moet verwijzen om gereed te zijn voor een audit.',
        sla_compliance_title: 'SLA Bijhouden voor nalevingslevering',
        sla_compliance_desc: "SLA's voor respons- en oplossingstijd zijn contractueel bindend voor de levering van compliancediensten. Beoordelingsfrequentie bepaalt hoe vaak de nalevingsresultaten van partners worden geëvalueerd. SLA-inbreuken op compliance-kritieke tickets leiden tot automatische escalatie en worden geregistreerd in de Partner Audit Trail voor regelgevend bewijs.",
        regulatory_engagement_title: 'Regelgevende betrokkenheidspatronen',
        regulatory_engagement_desc: 'Workflowcategorieën worden toegewezen aan typen toezichtsbetrokkenheid: Onboarding omvat de initiële compliance-instellingen en analyse van hiaten, Periodieke beoordeling behandelt terugkerende compliance-beoordelingen, Incidentescalatie beheert de reactie op inbreuken op de regelgeving, Offboarding zorgt ervoor dat aan de verplichtingen voor het bewaren van gegevens wordt voldaan. Voor elke stap kan het verzamelen van bewijsmateriaal nodig zijn om de audittrail volledig te maken.',
        compliance_escalation_title: 'Compliance-kritische escalatieprocedures',
        compliance_escalation_desc: "L1 behandelt standaard operationele problemen. T2 heeft betrekking op tekortkomingen die gevolgen hebben voor de naleving (gemiste deadlines voor regelgeving, lacunes in de controle). L3 is gereserveerd voor scenario's voor inbreuken op de regelgeving die onmiddellijke aandacht van het management vereisen. SLA inbreuk op L2/L3-tickets genereert automatisch audit trail-invoer. Alle escalatie- en de-escalatieacties worden permanent geregistreerd.",
        audit_regulatory_title: 'Regelgevingsbewijstraject',
        audit_regulatory_desc: 'Dit audittraject biedt fraudebestendige gegevens voor naleving van de regelgeving. Elke partneractie (maken, bijwerken, verwijderen, escalatie, statuswijziging) wordt geregistreerd met de identiteit van de acteur, tijdstempel en volledige payload-verschillen. Gebruik Entiteitstype-filters om bewijsmateriaal te isoleren op nalevingsdomein. Exporteer gefilterde resultaten om auditklare documentatie te produceren voor wettelijke inspecties.',
        evidence_trail_title: 'Moduleoverschrijdend bewijspad',
        evidence_trail_desc: "Elke workflowstap kan bewijs-ID's verzamelen die zijn gekoppeld aan deliverables, auditgebeurtenissen en compliance-artefacten in verschillende modules. Door een stap verder te gaan met bewijs-ID's ontstaat er een verifieerbare keten die de workflows van partners verbindt met wettelijke documentatie. Voltooide workflows met volledige bewijsketens dienen als bewijs van nalevingsbetrokkenheid.",
        evidence_ids_label: "Bewijs-ID's",
    },
    org_groups: {
        title: 'Organisatiegroepen',
        new: 'Aanmaken Groep',
        group_name: 'Groep Naam',
        member_count: 'Lid Aantal',
        root_org: 'Hoofd Org',
        no_groups: 'Geen org groups gevonden.',
        add_organization: 'Organisatie Toevoegen',
        select_organization: 'Selecteer een organisatie...',
        no_available_orgs: 'Geen beschikbare organisaties om toe te voegen.',
        no_members: 'Geen lidorganisaties in deze groep.',
        member_added: 'Organisatie toegevoegd aan groep.',
        member_removed: 'Organisatie verwijderd uit groep.',
        already_in_group: 'Deze organisatie behoort al tot een groep.',
        org_not_found: 'Organisatie niet gevonden.',
        add_member_error: 'Kan organisatie niet toevoegen aan groep.',
        remove_member_error: 'Kan organisatie niet verwijderen uit groep.',
        confirm_remove_member: 'Weet u zeker dat u "{name}" uit deze groep wilt verwijderen?',
        load_error: 'Kan org. groepsdetails niet laden.',
        page_title_list: 'Organisatiegroepen',
        page_title_detail: 'Details van organisatiegroep',
        page_title_create: 'Organisatiegroep maken',
        page_title_settings: 'Organisatiegroepinstellingen',
        page_title_users: 'Groepsgebruikers',
        page_title_reports: 'Groepsrapporten',
        page_title_dashboard: 'Groepsdashboard',
        page_title_audit: 'Groepsaudit',
        page_title_org_audit: 'Organisatiegroepaudit',
        page_title_shared_vendors: 'Gedeelde leveranciers',
        page_title_shared_frameworks: 'Gedeelde raamwerken',
        page_title_shared_forms: 'Gedeelde formuliersjablonen',
        page_title_merged_vendors: 'Samengevoegde leveranciers',
        page_title_settings_inheritance: 'Instellingen Overerving',
        announce_loaded_groups: '{count} organisatiegroepen geladen',
        announce_loaded_group: 'Geladen groep {name}',
        announce_loaded_users: 'Groepsgebruikers van {count} geladen',
        announce_loaded_reports: '{count} rapporten geladen',
        announce_loaded_vendors: '{count} gedeelde leveranciers geladen',
        announce_loaded_frameworks: '{count} gedeelde raamwerken geladen',
        announce_loaded_templates: '{count} gedeelde formuliersjablonen geladen',
        announce_loaded_audit: '{count} auditgebeurtenissen geladen',
        announce_loaded_merged: '{shared} gedeelde en {local} lokale leveranciers geladen',
        announce_dashboard_loaded: 'Dashboard geladen.',
        announce_dashboard_refreshed: 'Dashboard vernieuwd.',
        announce_dashboard_refresh_failed: 'Kan het dashboard niet vernieuwen.',
        announce_refreshing: 'Dashboard vernieuwen...',
        announce_settings_loaded: 'Overname van instellingen geladen.',
        announce_settings_saved: 'Instellingen succesvol opgeslagen.',
        announce_saving_settings: 'Instellingen opslaan...',
        announce_error_saving: 'Fout bij opslaan van instellingen.',
        announce_group_dissolved: 'Groep opgelost.',
        announce_creating_group: 'Organisatiegroep maken...',
        announce_group_created: 'Organisatiegroep is succesvol aangemaakt.',
        announce_error_creating: 'Fout bij maken van organisatiegroep.',
        announce_vendor_published: 'Leverancier succesvol gepubliceerd.',
        announce_vendor_retired: 'Verkoper is met pensioen.',
        announce_framework_published: 'Framework met succes gepubliceerd.',
        announce_framework_retired: 'Kader met pensioen.',
        announce_template_published: 'Formuliersjabloon succesvol gepubliceerd.',
        announce_template_retired: 'Formuliersjabloon buiten gebruik gesteld.',
        announce_role_assigned: 'Rol toegewezen.',
        announce_error_assign_role: 'Fout bij het toewijzen van rol.',
        announce_role_revoked: 'Rol is succesvol ingetrokken.',
        announce_report_generating: 'Rapport genereren...',
        announce_report_requested: 'Rapportgeneratie gevraagd.',
        announce_error_report: 'Fout bij het opvragen van rapport.',
        announce_report_deleted: 'Rapport verwijderd.',
        announce_search_applied: 'Zoek toegepast.',
        announce_search_cleared: 'Zoeken gewist.',
        announce_tab_selected: 'Tabblad {tab} geselecteerd',
        announce_error_add_member: 'Fout bij toevoegen van lid aan groep.',
        announce_category_reverted: '{category} is teruggekeerd naar overgenomen van ouder.',
        announce_category_overridden: '{category} ingesteld op lokale overschrijving.',
        announce_override_saved: '{category} overschrijven opgeslagen.',
        announce_export_started: 'Controle exporteren als {format}...',
        announce_export_completed: 'Exporteren als {format} voltooid.',
        announce_export_failed: 'Exporteren is mislukt.',
        announce_filter_org: 'Gefilterd op organisatie {name}',
        announce_filter_org_cleared: 'Organisatiefilter gewist',
        announce_filter_event: 'Gefilterd op gebeurtenistype {type}',
        announce_filter_cleared: 'Filter gewist',
        announce_download: '{name} downloaden als {format}',
        error_load_groups: 'Kan organisatiegroepen niet laden. Probeer het opnieuw.',
        error_load_users: 'Kan groepsgebruikers niet laden.',
        error_load_reports: 'Kan groepsrapporten niet laden.',
        error_load_dashboard: 'Kan groepsdashboard niet laden.',
        error_load_settings: 'Kan groepsinstellingen niet laden.',
        error_load_vendors: 'Kan samengevoegde leveranciers niet laden.',
        error_save_settings: 'Kan instellingen niet opslaan.',
        error_dissolve_group: 'Kan groep niet ontbinden.',
        error_assign_role: 'Kan rol niet toewijzen.',
        error_create_group: 'Kan organisatiegroep niet maken. Probeer het opnieuw.',
        error_request_report: 'Kan rapport niet opvragen.',
        error_group_name_required: 'Groepsnaam is vereist.',
        success_settings_saved: 'Instellingen succesvol opgeslagen.',
        confirm_retire_vendor: 'Weet u zeker dat u leverancier "{name}" wilt beëindigen?',
        confirm_retire_framework: 'Weet u zeker dat u raamwerk "{name}" wilt beëindigen?',
        confirm_retire_template: "Weet u zeker dat u formuliersjabloon '{name}' wilt intrekken?",
        confirm_dissolve_group: "Weet u zeker dat u '{name}' wilt ontbinden? Deze actie kan niet ongedaan worden gemaakt.",
        confirm_revoke_role: "Weet u zeker dat u de rol '{role}' wilt intrekken van '{name}'?",
        confirm_delete_report: "Weet u zeker dat u het rapport '{name}' wilt verwijderen?",
        dissolve_group: 'Groep oplossen',
        dissolve_warning: 'Hierdoor wordt de organisatiegroep definitief ontbonden. Alle aangesloten organisaties worden verwijderd.',
        count_groups: '{count} groep',
        count_groups_plural: '{count} groepen',
        count_users: '{count} gebruiker',
        count_users_plural: '{count} gebruikers met groepsrollen',
        count_events: '{count} evenement',
        count_events_plural: '{count} evenementen',
        count_reports: '{count} rapport',
        count_reports_plural: '{count} rapporten',
        count_vendors: '{count} leverancier gedeeld door de groep',
        count_vendors_plural: '{count} leveranciers gedeeld door de groep',
        count_frameworks: '{count} raamwerk gedeeld door de hele groep',
        count_frameworks_plural: '{count} raamwerken gedeeld door de groep',
        count_templates: '{count} sjabloon gedeeld door de groep',
        count_templates_plural: '{count} sjablonen gedeeld door de groep',
        count_shared_vendors: '{count} gedeelde leveranciers',
        count_local_vendors: '{count} lokale leveranciers',
        merged_combined_view: 'Gecombineerde weergave van {shared} gedeelde en {local} lokale leveranciers',
        dashboard: {
            title: 'Groepsoverzicht',
            loading: 'Laden van dashboard gegevens...',
            total_groups: 'Totaal Groepen',
            total_members: 'Totaal Leden',
            active_groups: 'Actief Groepen',
            overall_compliance_score: 'Algemene nalevingsscore',
            group_risk_level: 'Groepsrisiconiveau',
            sla_compliance: 'SLA Naleving',
            compliance_posture: 'Nalevingshouding',
            risk_heatmap: 'Risico-hittekaart',
            sla_performance: 'SLA Prestaties',
            recent_incidents: 'Recente incidenten',
            recent_activity: 'Recente activiteit',
            refresh_label: 'Vernieuw dashboardgegevens',
            score_good: 'Goed',
            score_needs_attention: 'Heeft aandacht nodig',
            score_critical: 'Kritisch',
            trend_up: 'Trend omhoog',
            trend_down: 'Trend naar beneden',
            trend_stable: 'Stabiel',
            risk_low: 'Low',
            risk_medium: 'Middelmatig',
            risk_high: 'Hoog',
            risk_critical: 'Kritisch',
            risk_unknown: 'Onbekend',
            col_organization: 'Organisatie',
            col_score: 'Scoren',
            col_trend: 'Trend',
            col_open_issues: 'Openstaande problemen',
            col_critical_issues: 'Kritieke problemen',
            col_health: 'Gezondheid',
            col_vendor_risk: 'Leveranciersrisico',
            col_data_risk: 'Gegevensrisico',
            col_compliance_risk: 'Nalevingsrisico',
            col_total_cases: 'Totaal aantal gevallen',
            col_on_time: 'Op tijd',
            col_breached: 'geschonden',
            col_sla_percent: 'SLA %',
            col_avg_days: 'Gem. dagen',
            sr_vendor_risk: 'Leveranciersrisicoscore: {score}',
            sr_data_risk: 'Gegevensrisicoscore: {score}',
            sr_compliance_risk: 'Compliancerisicoscore: {score}',
            sr_sla_breaches: 'Waarschuwing: {count} SLA inbreuken',
            sr_severity: 'Ernst: {severity}',
        },
        detail: {
            title: 'Groepsdetails',
            members: 'Leden',
            settings: 'Instellingen',
            audit: 'Controle',
            sr_loading: 'Organisatiegroepdetails laden',
            sr_loading_hierarchy: 'Hiërarchie laden',
            tab_tasks: 'Shared tasks',
            tasks_intro: "The tasks of every organization in this group, read across the group because task sharing is switched on in this group's settings.",
            tasks_total: '{count} tasks across the group',
            tasks_overdue: '{count} overdue',
            tasks_by_status: 'By status',
            tasks_by_org: 'By organization',
            tasks_latest: 'Latest tasks',
            tasks_showing: 'Showing the {shown} most recent of {total}.',
            tasks_empty: 'No organization in this group has any tasks.',
            col_title: 'Title',
            col_org: 'Organization',
            col_status: 'Status',
            col_priority: 'Priority',
            col_due: 'Due',
            tasks_load_failed: "The group's shared tasks could not be read, so nothing is shown for them.",
            tasks_subject: 'shared tasks',
            sr_loading_tasks: 'Loading shared tasks',
            member_org: 'leden organisatie',
            root_org_label: 'wortelorganisatie',
            hierarchy: 'Hierarchy',
        },
        users: {
            title: 'Groepsgebruikers',
            add_user: 'Toevoegen Gebruiker',
            no_users: 'No users in dit groep.',
            role_group_admin: 'Groepsbeheerder',
            role_group_reader: 'Groepslezer',
            sr_loading: 'Groepsgebruikers laden',
        },
        reports: {
            title: 'Groepsrapporten',
            no_reports: 'Geen reports gevonden.',
            generate_report: 'Nieuw rapport genereren',
            report_type_compliance: 'Nalevingsoverzicht',
            report_type_risk: 'Risicooverzicht',
            report_type_sla: 'SLA Prestaties',
            report_type_incident: 'Incidentrapport',
            report_type_executive: 'Uitvoerende briefing',
            report_type_board: 'Bestuursrapport',
            status_queued: 'In de wachtrij',
            status_generating: 'Genereren',
            status_completed: 'Voltooid',
            status_failed: 'Mislukt',
            period_start: 'Periode begin',
            period_end: 'Einde periode',
            sr_loading: 'Groepsrapporten laden',
            /*
             * The two row actions' accessible names, interpolated rather than built
             * as English template literals.
             *
             * They shipped as `aria-label={`Delete report ${…}`}` and
             * `aria-label={`Download ${…} as ${…}`}` — an `aria-label` is
             * user-visible text, so 15 non-English locales heard these in English.
             * The same defect was fixed by hand one page over in the same commit
             * that touched these two lines and left them.
             */
            download_report_aria: 'Download {title} as {format}',
            delete_report_aria: 'Delete report {title}',
        },
        shared_vendors: {
            title: 'Gedeelde leveranciers',
            no_vendors: 'Geen shared leveranciers gevonden.',
            merged_vendors: 'Samengevoegd Leveranciers',
            publish_form_label: 'Publiceer gedeelde leverancier',
            sr_loading: 'Gedeelde leveranciers laden',
            sr_loading_merged: 'Samengevoegde leveranciers laden',
            local_vendors: 'Lokale verkopers',
            source_shared: 'Gedeeld vanuit groep',
            source_local: 'Lokale verkoper',
            category_technology: 'Technologie',
            category_consulting: 'Advies',
            category_outsourcing: 'Uitbesteding',
            category_cloud: 'Wolk',
            category_data_processing: 'Gegevensverwerking',
            category_other: 'Anders',
            risk_tier_low: 'Low',
            risk_tier_medium: 'Middelmatig',
            risk_tier_high: 'Hoog',
            risk_tier_critical: 'Kritisch',
            compliance_compliant: 'Conform',
            compliance_non_compliant: 'Niet-conform',
            compliance_pending: 'In behandeling',
            compliance_under_review: 'Onder beoordeling',
            status_active: 'Actief',
            status_retired: 'Gepensioneerd',
        },
        shared_frameworks: {
            // Fix sprint F28 (WCAG 3.3.2) — the framework-code field was labelled
            // `common.type` ("Type"), so its accessible name said nothing about what
            // it takes. With no installed framework the placeholder resolves to '',
            // leaving a required field with neither a meaningful name nor a hint.
            framework_code_label: 'Raamwerkcode',
            title: 'Gedeelde kaders',
            no_frameworks: 'Geen shared frameworks gevonden.',
            publish_form_label: 'Publiceer een gedeeld raamwerk',
            sr_loading: 'Gedeelde raamwerken laden',
            status_active: 'Actief',
            status_retired: 'Gepensioneerd',
        },
        shared_forms: {
            title: 'Gedeelde formuliersjablonen',
            no_forms: 'Geen shared formulier sjablonen gevonden.',
            publish_form_label: 'Gedeelde formuliersjabloon publiceren',
            sr_loading: 'Gedeelde formuliersjablonen laden',
            status_active: 'Actief',
            status_retired: 'Gepensioneerd',
        },
        audit: {
            title: 'Groepscontrole',
            consolidated_title: 'Geconsolideerd Audit Trail',
            consolidated_subtitle: 'Cross-organization audit gebeurtenissen voor dit groep',
            audit_log_title: 'Groep Audit Logboek',
            event_type: 'Gebeurtenis Type',
            actor: 'Betrokkene',
            timestamp: 'Tijdstempel',
            source_org: 'Bron Org',
            org: 'Org',
            summary: 'Samenvatting',
            details: 'Bijzonderheden',
            no_events: 'Geen audit gebeurtenissen gevonden.',
            all_events: 'Alle Gebeurtenissen',
            all_orgs: 'Alle Orgs',
            organization: 'Organisatie',
            from: 'Van',
            to: 'To',
            search_events: 'Zoeken gebeurtenissen...',
            clear: 'Wissen',
            events_24h: 'Gebeurtenissen (24h)',
            events_7d: 'Gebeurtenissen (7d)',
            active_orgs: 'Actief Orgs',
            exporting: 'Exporteren...',
            export_csv: 'Exporteren CSV',
            export_json: 'Exporteren JSON',
            back_to_group: 'Back naar Groep',
            back_to_detail: 'Back naar Detail',
            sr_loading: 'Auditgebeurtenissen laden',
            sr_loading_summary: 'Auditsamenvatting laden',
            disabled_by_settings: 'The consolidated audit is switched off in the settings of this group. The root organization of the group can switch it back on under Settings.',
        },
        settings: {
            title: 'Groep Instellingen',
            shared_tasks: 'Share task lists, statistics and calendars across the member organizations of this group',
            inheritance: 'Instellingen Inheritance',
            inheritance_subtitle: 'Configureer welke instellingencategorieën van de groep overnemen en welke lokale overschrijvingen gebruiken',
            sla_settings: 'SLA Instellingen',
            calendar_settings: 'Kalender Instellingen',
            notification_settings: 'Melding Instellingen',
            intake_settings: 'Intake Instellingen',
            cmp_integration: 'CMP Integratie',
            overridden: 'Overschreven',
            inheriting: 'Overerving',
            hide_defaults: 'Verbergen Standaardwaarden',
            show_group_defaults: 'Tonen Groep Standaardwaarden',
            group_defaults: 'Groep Standaardwaarden',
            inherit_from_group: 'Overnemen van Groep',
            use_local_override: 'Use Local Overschrijven',
            local_override_json: 'Local Overschrijven (JSON)',
            save_override: 'Opslaan Overschrijven',
            saving: 'Opslaan...',
            sr_loading: 'Overname van instellingen laden',
            sr_loading_settings: 'Groepsinstellingen laden',
            sr_enabled: 'Ingeschakeld',
            sr_disabled: 'Uitgeschakeld',
            sr_override_active: 'Lokale overschrijving actief',
            sr_inherited: 'Geërfd van ouder',
            danger_zone: 'Gevarenzone',
            sharing_options: 'Opties voor delen',
            advanced_options: 'Geavanceerde opties',
            max_member_orgs: 'Maximum member organizations',
        },
        status_active: 'Actief',
        status_dissolved: 'Opgelost',
        status_pending: 'In behandeling',
        status_suspended: 'Opgeschort',
    },
    integrations: {
        nav: {
            webhooks: 'Webhook-koppelingen',
            domains: 'Domeinen',
            export_schedules: "Exportschema's",
            email_log: 'E-maillogboek',
        },
    },
    platform: {
        abandoned_uploads: {
            title: 'Abandoned uploads',
            description: 'Files uploaded on a form nobody saved, past the grace window. Reading this changes nothing.',
            grace_window: 'Grace window',
            hours: '{n} hours',
            as_of: 'As of',
            candidates: 'Uploads no record claimed',
            retained_held: 'Kept — legal hold',
            file: 'File',
            uploaded_for: 'Uploaded for',
            uploaded_by: 'Uploaded by',
            uploaded_at: 'Uploaded',
            past_window_by: 'Past the window by',
            hours_short: '{n} h',
            run_sweep: 'Delete abandoned uploads',
            running: 'Deleting...',
            sweep_success: '{swept} upload(s) deleted, {held} kept under a legal hold.',
            sweep_failed: 'The sweep could not be run. Please try again.',
            load_failed: 'The abandoned-upload register could not be read. Please try again.',
            reload: 'Reload',
            empty: 'No upload is past the grace window unclaimed.',
            restore_note: 'A deleted upload is recoverable: it is soft-deleted and can be restored from this page.',
        },
        nav: {
            users: 'Gebruikers',
            roles: 'Rollen',
            profile: 'Mijn profiel',
        },
        security: {
            mfa: 'MFA-instellingen',
            sso: 'SSO-configuratie',
            api_keys: 'API-sleutels',
            service_accounts: 'Serviceaccounts',
            ip_allowlist: 'IP-acceptatielijst',
            delegations: 'Delegaties',
            break_glass: 'Noodtoegang',
            session_settings: 'Sessie-instellingen',
            active_sessions: 'Actieve sessies',
            access_reviews: 'Toegangsbeoordelingen',
            my_reviews: 'Mijn beoordelingen',
            role_analytics: 'Rolanalyses',
            attestation_schedules: "Attestatieschema's",
            sod_rules: 'SoD-regels',
            sod_violations: 'SoD-schendingen',
        },
        module_config: {
            title: 'Moduleconfiguratie',
            subtitle: 'Schakel functiemodules in of uit voor uw organisatie. Uitgeschakelde modules worden verborgen in de navigatie.',
            enabled: 'Ingeschakeld',
            disabled: 'Uitgeschakeld',
            save: 'Configuratie opslaan',
            saving: 'Opslaan...',
            saved: 'Configuratie succesvol opgeslagen',
            nav_label: 'Moduleconfiguratie',
            settings: 'Instellingen',
            webhooks: 'Webhook-koppelingen',
            domains: 'Domeinen',
            export_schedules: "Exportschema's",
            email_log: 'E-maillogboek',
            announce_loaded: 'Module configuration loaded',
        },
        files_nav: {
            section: 'Bestanden',
            configuration: 'Configuratie',
            browser: 'Bestandsbrowser',
            dashboard: 'Bestandendashboard',
        },
        immutable_audit_trails: {
            nav_group_label: 'Controle en bewijs',
            nav_settings: 'Instellingen',
            nav_status: 'Bewijsstatus',
            nav_integrity_center: 'Integriteit Centrum',
            nav_usage: 'Gebruik en tegoeden',
            settings_title: 'Onveranderlijke audittrails: instellingen',
            settings_subtitle: 'Instellingen per organisatie voor de add-on Onveranderlijke audittrails.',
            settings_banner: 'De invoegtoepassing Onveranderlijke audittrails is niet ingeschakeld voor uw organisatie. Schakel dit hier in om te beginnen met het verankeren van bewijs voor Tier-A- en Tier-B-auditgebeurtenissen.',
            status_title: 'Onveranderlijke audittrails — Bewijsstatus',
            status_subtitle: 'Proefrelaties per gebeurtenis tussen modules.',
            status_banner: 'De invoegtoepassing Onveranderlijke audittrails is niet ingeschakeld voor uw organisatie. Ga naar Instellingen om dit in te schakelen.',
            integrity_center_title: 'Onveranderlijke audittrails — Integrity Center',
            integrity_center_subtitle: 'Verifieer bewijzen en exporteer integriteitspakketten.',
            integrity_center_banner: 'De invoegtoepassing Onveranderlijke audittrails is niet ingeschakeld voor uw organisatie. Ga naar Instellingen om dit in te schakelen voordat u de verificatie uitvoert of Integriteitspakketten exporteert.',
            usage_title: 'Onveranderlijke audittrails: gebruik en credits',
            usage_subtitle: 'Kredietsaldo, pakketaankopen en gebruiksgeschiedenis.',
            usage_banner: 'Schakel onveranderlijke audittrails in Instellingen in om proefcredits te gaan gebruiken.',
            module_tips_placeholder: 'Tips en richtlijnen voor dit oppervlak zijn beschikbaar in het Helpcentrum.',
            column_label: 'Bewijs',
            no_proof_aria: 'Geen bewijs geregistreerd',
            proof_status_receipt_label: 'bewijsstuk {id}',
            in_progress_hint: 'In uitvoering: wordt automatisch bijgewerkt',
            status_pills: {
                pending: 'In behandeling',
                submitted: 'Ingezonden',
                anchored: 'Verankerd',
                confirmed: 'Bevestigd',
                retrying: 'Opnieuw proberen',
                paused_cap: 'Gepauzeerd (limiet)',
                failed: 'Mislukt',
                failed_auth: 'Verificatie mislukt',
            },
            status_tooltips: {
                pending: 'In behandeling — in de wachtrij voor de proefservice. Gaat binnenkort naar Ingediend; geen actie nodig.',
                submitted: 'Ingediend: uw evenement is naar de proefservice gestuurd. Voor batchmodus wacht het om te worden verzegeld en aan de ketting te worden verankerd. Wordt automatisch bijgewerkt; geen actie nodig.',
                anchored: 'Verankerd – de batch is on-chain geschreven en wacht op definitieve bevestiging. Wordt automatisch bijgewerkt; geen actie nodig.',
                confirmed: 'Bevestigd – het bewijs is verankerd in de keten en verifieerbaar.',
                retrying: 'Opnieuw proberen: er is een tijdelijke fout opgetreden. De afstemmingsfunctie probeert het automatisch opnieuw; geen actie nodig.',
                paused_cap: 'Onderbroken: uw maandelijkse proefkredietlimiet is bereikt. Wissel meer tegoeden in of wacht tot de volgende factureringsmaand.',
                failed: 'Mislukt: het verankeren is niet gelukt. De reconciler zal het automatisch opnieuw proberen; neem contact op met de ondersteuning als het probleem zich blijft voordoen.',
                failed_auth: 'Verificatie mislukt: de proefservice heeft onze inloggegevens afgewezen. Controleer de pagina Onveranderlijke audittrails-instellingen.',
            },
            mode_pills: {
                direct: 'Direct',
                batch: 'Partij',
            },
            drawer: {
                title: 'Bewijsdetails',
                close: 'Sluiten',
                section_proof: 'Bewijs',
                section_batch: 'Batch-lidmaatschap',
                status: 'Status',
                proof_reference: 'Bewijsreferentie',
                receipt_id: 'Ontvangst-ID',
                taxonomy_class: 'Taxonomie klasse',
                units_consumed: 'Eenheden verbruikt',
                created_at: 'Gemaakt',
                verification_ref: 'Ketenverificatiereferentie',
                chain: 'Keten',
                tx_hash: 'Tx-hash',
                block_ref: 'Blokref',
                batch_reference: 'Batchreferentie',
                leaf_index: 'Bladindex',
                local_event_hash: 'Hash van lokale evenementen',
                show_inclusion: 'Toon opnamepad',
                hide_inclusion: 'Insluitingspad verbergen',
                privacy_note: 'Payload is gehasht; Er zijn geen persoonlijke gegevens achtergelaten bij ComplianceOne voor dit evenement.',
                verify_now: 'Verifieer nu',
                verify_pending: 'Verificatie wordt uitgevoerd...',
                verify_verified: 'Geverifieerd – lokale + ketencontroles geslaagd.',
                verify_issue_prefix: 'Verificatieprobleem: ',
                verify_failed: 'Verificatiegesprek mislukt.',
                verify_result_prefix: 'Verificatie oordeel: ',
                verify_pending_note: 'Verificatie in behandeling: dit bewijs wacht nog op verankering aan de ketting. Probeer het opnieuw nadat de batch is verzegeld.',
                awaiting_anchor_note: 'Dit bewijs maakt deel uit van een open batch. Verificatie wordt beschikbaar zodra de batch is verzegeld en in de keten is verankerd, doorgaans binnen het geconfigureerde batchvenster van de organisatie. De statusbadge wordt automatisch bijgewerkt.',
                terminal_error_note: 'Dit bewijs slaagde er niet in om te verankeren. Het wordt niet automatisch opnieuw geprobeerd; handmatige verificatie hoeft nog niets te controleren.',
            },
            integrity_center: {
                section_run: 'Voer verificatie uit',
                section_progress: 'Voer de voortgang uit',
                section_issues: 'Problemen',
                period_start: 'Begin van de periode',
                period_end: 'Einde periode',
                module_filter: "Modules (door komma's gescheiden, optioneel)",
                modules_placeholder: 'rights-requests, incidents',
                run_button: 'Voer verificatie uit',
                export_button: 'Integriteitspakket exporteren',
                export_success: 'Integriteitspakket geëxporteerd.',
                export_failed: 'Exporteren is mislukt.',
                empty_disabled: 'Schakel onveranderlijke audittrails in Instellingen in om verificatiepassen uit te voeren.',
                error_period_required: 'Kies een begin- en einddatum.',
                error_start_run: 'Kan het rennen niet starten.',
                processed: 'Verwerkt: {processed} van {total}',
                col_verdict: 'Vonnis',
                col_count: 'Tel',
                col_proof_link: 'Bewijslink',
                col_module: 'Module',
                col_event_type: 'Type evenement',
                col_expected: 'Verwachte hasj',
                col_recomputed: 'Opnieuw berekende hasj',
                col_raised_at: 'Opgegroeid bij',
                any_verdict: 'Welk oordeel dan ook',
                no_issues: 'Geen problemen gevonden.',
            },
            settings_page: {
                load_error: 'Kan instellingen niet laden.',
                save: 'Opslaan',
                save_success: 'Instellingen opgeslagen.',
                save_error: 'Opslaan mislukt.',
                discard: 'Wijzigingen negeren',
                read_only_banner: 'U heeft alleen-lezen toegang. Neem contact op met een platformbeheerder om wijzigingen aan te brengen.',
                section_status: 'Status en activering',
                enabled_label: 'Add-on ingeschakeld',
                enabled_help: 'Indien ingeschakeld, wordt elke Tier-A- en Tier-B-auditgebeurtenis gehasht en verankerd.',
                section_mode: 'Standaard proefmodus',
                mode_direct: 'Direct (één anker per evenement)',
                mode_batch: 'Batch (Merkle-rootanker per groep)',
                mode_hybrid: 'Hybride (registerstandaard per klasse)',
                batch_window_label: 'Batchvenster (minuten, 1–60)',
                section_taxonomy: 'Taxonomie van beschermde gebeurtenissen',
                taxonomy_help: 'Overschrijvingen per gebeurtenisklasse. Niveau A → directe modus, Niveau B → batchmodus, Uit → niet gecontroleerd.',
                taxonomy_search: 'Zoekoverschrijvingen',
                taxonomy_reset: 'Resetten naar standaardwaarden',
                taxonomy_event_class: 'Evenement klasse',
                taxonomy_override: 'Overschrijven',
                taxonomy_actions: 'Acties',
                taxonomy_clear: 'Duidelijk',
                taxonomy_no_overrides: 'Geen overschrijvingen — met behulp van registerstandaardwaarden.',
                taxonomy_add_class: 'event_class.code',
                taxonomy_add: 'Overschrijving toevoegen',
                section_caps: 'Caps en limieten',
                cap_label: 'Maandlimiet (eenheden; blanco = onbeperkt)',
                warn_label: 'Waarschuwdrempel (eenheden; moet kleiner zijn dan de limiet)',
                auto_pause_label: 'Pauzeer nieuwe proefdrukken automatisch zodra de limiet is bereikt',
                section_credentials: 'Referenties',
                credential_id: 'Referentie-ID',
                credential_rotated_at: 'Laatst gedraaid',
                rotate_button: 'Referentie roteren',
                rotate_success: 'Referentie geroteerd. Oude inloggegevens geldig voor nog {n} minuten.',
                rotate_failed: 'Rotatie van inloggegevens is mislukt.',
                rotate_modal_title: 'AesirX-referentie roteren?',
                rotate_modal_body: 'Rotatie geeft een nieuwe identificatie af. De oude inloggegevens blijven geaccepteerd door AesirX voor een uitstelperiode van 60 minuten, zodat werknemers aan boord hun huidige taken kunnen afmaken. Na 60 minuten wordt de oude identificatie ingetrokken.',
                rotate_modal_confirm: 'Draai nu',
                tier_a_off_modal_title: 'Controle uitschakelen voor een gebeurtenisklasse?',
                tier_a_off_modal_body: "Door een gebeurtenisklasse op 'Uit' in te stellen, kan de proeftap deze gebeurtenissen niet meer verankeren. Architectuur §6 legt de implicaties van de regelgeving uit: Tier-A-gebeurtenissen zijn relevant voor de toezichthouder; Als u ze uitschakelt, kan dit de verdedigbaarheid van uw audittraject aantasten.",
                tier_a_off_modal_confirm: 'Ik begrijp het, pas overschrijving toe',
                error_warn_lt_cap: 'De waarschuwingsdrempel moet lager zijn dan de maandelijkse limiet.',
                error_window_range: 'Batchvenster moet tussen 1 en 60 minuten liggen.',
                error_run_in_progress: 'Er wordt een verificatierun uitgevoerd; kan de add-on momenteel niet uitschakelen.',
            },
            status_page: {
                filter_status: 'Status',
                filter_mode: 'Bewijsmodus',
                filter_module: 'Module',
                filter_source_collection: 'Bron collectie',
                filter_from_date: 'Van',
                filter_to_date: 'Aan',
                filter_search: 'Zoeken',
                any_status: 'Elke status',
                any_mode: 'Elke modus',
                col_module: 'Module',
                col_event_type: 'Type evenement',
                col_proof_status: 'Status',
                col_proof_mode: 'Modus',
                col_created_at: 'Gemaakt',
                col_units: 'Eenheden',
                empty_disabled: 'De invoegtoepassing Onveranderlijke audittrails is niet ingeschakeld voor uw organisatie. Ga naar Instellingen om dit in te schakelen.',
                empty_enabled_no_rows: 'Er zijn geen bewijslinks die overeenkomen met de huidige filters.',
                export_csv: 'Exporteer CSV',
                export_xlsx: 'Exporteer XLSX',
                export_success: 'Exporteren voltooid',
                export_failed: 'Exporteren is mislukt',
            },
            usage_page: {
                section_alerts: 'Meldingen',
                alert_dismiss: 'Negeren',
                alert_dismiss_aria: 'Waarschuwing sluiten {id}',
                alert_dismissed: 'Waarschuwing afgewezen',
                alert_dismiss_failed: 'Het negeren van de waarschuwing is mislukt',
                section_balance: 'Creditsaldo',
                balance_label: 'Beschikbare kredieten',
                balance_hint: 'Resterende eenheden (1 eenheid = 1 beschermde gebeurtenisbestendig).',
                section_current_month: 'Huidige maand',
                consumed_label: 'Verbruikt',
                granted_label: 'Toegegeven',
                refunded_label: 'Terugbetaald',
                net_label: 'Netto',
                section_history: 'Gebruiksgeschiedenis',
                history_kind: 'Soort',
                history_units: 'Eenheden',
                history_balance_after: 'Saldo achteraf',
                history_notes: 'Opmerkingen',
                history_at: 'Wanneer',
                cap_warning_banner: 'U heeft uw waarschuwingsdrempel voor deze factureringsmaand overschreden. Overweeg om extra credits in te wisselen.',
                cap_paused_banner: 'Maandlimiet bereikt. Het indienen van proefdrukken wordt opgeschort tot volgende maand of totdat de limiet wordt verhoogd.',
                section_redeem: 'Kredietpakket inwisselen',
                redeem_purchase_id: 'Factuurnummer',
                redeem_package: 'Pakket',
                redeem_button: 'Inwisselen',
                redeem_pending: 'Inwisselen…',
                redeem_success: 'Tegoeden ingewisseld',
                redeem_failed: 'Inwisselen mislukt',
                redeem_option_template: '{name} — {units} {units_label} — {price}',
                redeem_option_units: 'eenheden',
                alert_dismissing: 'Afwijzen…',
                alert_kind: {
                    cap_reached: 'Maandlimiet bereikt',
                    low_balance: 'Laag saldo',
                    auth_failure: 'Authenticatie mislukt',
                    merkle_mismatch: 'Merkle-mismatch',
                    batch_terminal_failure: 'Batchterminalfout',
                    direct_terminal_failure: 'Terminalfout voor directe indiening',
                },
                usage_kind: {
                    consume: 'Consumeer',
                    refund: 'Terugbetaling',
                    grant: 'Verleen',
                    adjustment: 'Aanpassing',
                    reservation: 'Reservering',
                    credit_grant: 'Credit granted',
                    consumption: 'Proof consumed',
                    refund_failed_proof: 'Refund (failed proof)',
                },
                package_starter: 'Voorgerecht',
                package_growth: 'Groei',
                package_enterprise: 'Onderneming',
                // Sprint 48.5.x — self-service "Buy More" billing (Flow A + B).
                buy_more: {
                    buy_button: 'Meer tegoeden kopen',
                    purchase_title: 'Meer tegoeden kopen',
                    purchase_subtitle: 'Kies een pakket. U wordt naar de beveiligde Stripe-afrekenpagina geleid en uw factuur komt per e-mail.',
                    checkout_button: 'Doorgaan naar afrekenen',
                    checkout_pending: 'Afrekenen openen…',
                    cancel_button: 'Annuleren',
                    purchase_error: 'Kan het afrekenen niet starten. Probeer het straks opnieuw.',
                    redeem_subtitle: 'Al betaald? Selecteer het pakket dat u hebt gekocht en voer de factuur-ID uit uw e-mail in om de tegoeden toe te voegen.',
                    error_not_found: 'Er is geen factuur gevonden met dat nummer. Controleer het factuurnummer en probeer het opnieuw.',
                    error_not_paid: 'Deze factuur is nog niet als betaald gemarkeerd.',
                    error_org_mismatch: 'Deze factuur is niet aan uw organisatie uitgereikt.',
                    error_package_mismatch: 'Deze factuur komt niet overeen met het geselecteerde pakket.',
                    error_billing_unavailable: 'Kan de factureringsservice niet bereiken. Probeer het straks opnieuw.',
                },
                cap_over_banner: 'The monthly cap has been reached. Proofs keep being submitted because pausing at the cap is switched off.',
                resume_paused_button: 'Resume paused proofs',
                resume_paused_hint: 'Proofs held back when the monthly cap was reached are released by themselves when the cap is raised or removed, or when pausing at the cap is switched off. Use this once a new month has begun.',
                resume_paused_success: '{count} paused proofs released.',
                resume_paused_none: 'No paused proofs to release.',
                resume_paused_failed: 'The paused proofs could not be released.',
            },
        },
        files_config: {
            title: 'Bestandenconfiguratie',
            subtitle: 'Configureer bestandsuploadlimieten, bewaarbeleid en overschrijvingen per eigenaartype voor het platformkoppelingsoppervlak.',
            section_global: 'Globale standaardinstellingen',
            section_global_help: 'Deze limieten zijn van toepassing op elk bestand dat wordt geüpload door gebruikers in uw organisatie, tenzij een overschrijving per eigenaartype hieronder deze limieten wijzigt.',
            section_overrides: 'Overschrijvingen per eigenaartype',
            section_overrides_help: 'Overschrijf de algemene limieten voor een specifiek eigenaarstype. Waarden per eigenaartype winnen altijd de globale instelling.',
            max_file_size_label: 'Maximale bestandsgrootte',
            max_file_size_hint: 'Per individuele upload (MiB)',
            max_total_attachments_label: 'Maximaal totaal aantal bijlagen',
            max_total_attachments_hint: 'Door de hele organisatie heen',
            max_total_size_label: 'Maximale totale opslag',
            max_total_size_hint: 'Over de gehele organisatie (MiB)',
            allowed_mime_types_label: 'Toegestane bestandstypen',
            allowed_mime_types_placeholder: 'Selecteer MIME-typen',
            retention_days_label: 'Bewaartermijn (dagen)',
            retention_days_hint: 'Hoe lang bestanden moeten worden bewaard voordat ze in aanmerking komen voor verwijdering. Laat dit veld leeg als er geen bewaarbeleid is.',
            legal_hold_label: 'Juridische wacht',
            legal_hold_hint: 'Indien ingeschakeld, vereist het definitief verwijderen van een bestand van dit eigenaarstype de toestemming om de wettelijke bewaarplicht te overschrijven en de expliciete aanmelding van de operator.',
            save: 'Wijzigingen opslaan',
            saving: 'Opslaan…',
            reset: 'Opnieuw instellen',
            save_success: 'Instellingen opgeslagen',
            empty_state_title: 'Standaardplatforminstellingen gebruiken',
            empty_state_body: 'Uw organisatie gebruikt momenteel het standaard bestandsbeleid van het platform. Bewerk een willekeurige waarde hieronder om een ​​overschrijving op organisatieniveau te maken.',
            read_only_banner: 'U heeft alleen-lezen toegang tot de bestandsconfiguratie. Neem contact op met een platformbeheerder om wijzigingen aan te brengen.',
            owner_type_default_badge: 'Standaard',
            owner_type_overridden_badge: 'Overschreven',
            owner_type_label: 'Type eigenaar',
            owner_type_collapse: 'Samenvouwen',
            owner_type_expand: 'Uitbreiden',
            override_save: 'Overschrijven opslaan',
            override_remove: 'Overschrijving verwijderen',
            mime_labels: {
                'application/pdf': 'PDF-document',
                'image/png': 'PNG-afbeelding',
                'image/jpeg': 'JPEG-afbeelding',
                'image/jpg': 'JPG-afbeelding',
                'application/msword': 'Word-document (verouderd)',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word-document',
                'application/vnd.ms-excel': 'Excel-spreadsheet (verouderd)',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel-spreadsheet',
                'text/csv': 'CSV bestand',
                'text/plain': 'Platte tekst',
                'message/rfc822': 'E-mailbericht',
            },
            global_legal_hold_label: 'Organization-wide legal hold',
            global_legal_hold_hint: 'When enabled, NO file across any owner type can be hard-deleted unless the operator has the legal_hold_override permission and explicitly opts in.',
            global_retention_days_label: 'Default retention period (days)',
            global_retention_days_hint: 'How long files must be kept before they become eligible for deletion. Leave blank for no retention policy. Per-owner-type overrides below can tighten.',
            global_abandoned_grace_label: 'Abandoned upload grace window (hours)',
            global_abandoned_grace_hint: 'How long a file that no saved record has claimed is left alone before the abandoned-upload sweep soft-deletes it. At least 24 hours, so a form that is still open is never affected.',
            global_abandoned_grace_invalid: 'Enter a whole number of hours from {min} to {max}.',
        },
        files_browser: {
            title: 'Bestandsbrowser',
            subtitle: 'Zoek, filter en beheer elke bijlage die binnen de organisatie wordt geüpload.',
            filter_owner_type: 'Type eigenaar',
            filter_uploader: 'Geüpload door',
            filter_from: 'Daarna geüpload',
            filter_to: 'Eerder geüpload',
            filter_size_min: 'Minimale grootte (MiB)',
            filter_size_max: 'Maximale grootte (MiB)',
            filter_mime: 'Bestandstype',
            filter_deleted_state: 'Verwijderingsstatus',
            filter_deleted_any: 'Alle bestanden',
            filter_deleted_only: 'Alleen verwijderd',
            filter_deleted_exclude: 'Uitsluiten verwijderd',
            filter_orphaned: 'Alleen wees',
            filter_search_placeholder: 'Bestandsnaam zoeken…',
            filter_apply: 'Pas filters toe',
            filter_clear: 'Filters wissen',
            col_filename: 'Bestandsnaam',
            col_owner_type: 'Type eigenaar',
            col_owner_entity: 'Eigenaar',
            col_uploader: 'Geüpload door',
            col_uploaded_at: 'Geüpload',
            col_size: 'Grootte',
            col_mime: 'Typ',
            col_state: 'Staat',
            col_actions: 'Acties',
            state_active: 'Actief',
            state_deleted: 'Verwijderd',
            state_orphaned: 'Wees',
            action_view_detail: 'Bekijk details',
            action_download: 'Downloaden',
            action_ai_edits: 'AI Bewerkingen',
            action_soft_delete: 'Zacht verwijderen',
            action_restore: 'Herstellen',
            action_hard_delete: 'Moeilijk verwijderen',
            bulk_soft_delete: 'Zacht verwijderen geselecteerd',
            bulk_export_csv: 'Exporteer CSV',
            bulk_select_all: 'Selecteer alles',
            reason_label: 'Reden',
            reason_min_10_chars: 'Reden is vereist (minstens 10 tekens)',
            reason_min_20_chars: 'Reden is vereist (minimaal 20 tekens)',
            legal_hold_override_label: 'Wettelijke bewaarplicht negeren',
            legal_hold_override_warning: 'Dit bestand valt onder wettelijke bewaring. Als u de melding definitief verwijdert, wordt deze met uw reden en overschrijvingsmarkering in de audittrail opgenomen.',
            empty_state_title: 'Er zijn geen bestanden die overeenkomen met uw filters',
            empty_state_body: 'Pas uw filters aan om resultaten te zien, of wis ze om alle bestanden te bekijken.',
            pagination_prev: 'Vorige',
            pagination_next: 'Volgende',
            pagination_page: 'Pagina {page} van {total}',
            pagination_size_label: 'Per pagina',
            pagination_total: 'Totaal: {count}',
            toast_soft_deleted: 'Bestand zacht verwijderd',
            toast_restored: 'Bestand hersteld',
            toast_hard_deleted: 'Bestand permanent verwijderd',
            toast_bulk_deleted: '{count} bestand(en) verwijderd',
            toast_error: 'Operatie mislukt',
            confirm_soft_delete_title: 'Zacht verwijderbestand',
            confirm_restore_title: 'Bestand herstellen',
            confirm_hard_delete_title: 'Bestand definitief verwijderen',
            confirm_bulk_soft_delete_title: 'Zachte verwijdering van geselecteerde bestanden',
            confirm_submit: 'Bevestig',
            confirm_cancel: 'Annuleer',
            col_view_entity: 'View',
        },
        files_detail: {
            modal_title: 'Bestandsdetails',
            close: 'Sluiten',
            meta_filename: 'Bestandsnaam',
            meta_size: 'Grootte',
            meta_mime: 'Typ',
            meta_owner_type: 'Type eigenaar',
            meta_owner_entity: 'Eigenaar entiteit',
            meta_uploader: 'Geüpload door',
            meta_uploaded_at: 'Geüpload',
            meta_storage_path: 'Opslagpad',
            meta_deleted_state: 'Verwijderingsstatus',
            audit_section_title: 'Audittrail',
            audit_load_more: 'Laad meer evenementen',
            audit_empty: 'Er zijn geen auditgebeurtenissen geregistreerd voor dit bestand.',
            audit_config_changed: 'Configuratie gewijzigd',
            audit_soft_deleted: 'Zacht verwijderd',
            audit_restored: 'Hersteld',
            audit_hard_deleted: 'Permanent verwijderd',
            audit_downloaded: 'Gedownload',
            audit_quota_breached: 'Poging tot quotaschending',
            audit_owner_type_download_denied: 'Ophalen geweigerd',
            /*
             * The audit row as ONE composed string, not three JSX fragments glued
             * with English.
             *
             * The row used to render `{' by '}` and `{' on '}` as literals, so every
             * locale read "Endgültig gelöscht by Anna Schmidt on 11. Apr 2026" — the
             * event label and the actor came from `t` and the user map, the
             * connective words did not. A locale that orders these differently
             * cannot be rendered correctly by concatenation at all, which is why
             * this is a template rather than two `by`/`on` keys.
             *
             * `{actor}` is a link, so the page splits this on the placeholders and
             * substitutes nodes rather than calling `.replace`.
             */
            audit_row: '{event} door {actor} op {at}',
            action_download: 'Downloaden',
            action_soft_delete: 'Zacht verwijderen',
            action_restore: 'Herstellen',
            action_hard_delete: 'Moeilijk verwijderen',
            effective_settings_title: 'Effectief beleid',
            effective_max_file_size: 'Maximale bestandsgrootte',
            effective_retention_days: 'Retentie',
            effective_legal_hold: 'Juridische wacht',
            effective_legal_hold_yes: 'Actief',
            effective_legal_hold_no: 'Geen',
        },
        files_dashboard: {
            title: 'Bestandendashboard',
            subtitle: 'Opslaggebruik, groei en zwevende bestanden in de hele organisatie.',
            kpi_total_files: 'Totaal bestanden',
            kpi_total_size: 'Totale opslag',
            kpi_near_quota: 'Quotumgebruik',
            kpi_orphaned: 'Verweesde bestanden',
            chart_by_owner_type: 'Bestanden op type eigenaar',
            chart_growth_30d: '30 dagen groei',
            toggle_count: 'Op telling',
            toggle_size: 'Op maat',
            orphaned_panel_title: 'Weesbestanden (top 20)',
            orphaned_view_all: 'Bekijk alle weesbestanden',
            orphaned_empty: 'Geen weesbestanden gevonden.',
            growth_empty: 'Geen uploadactiviteit in de afgelopen 30 dagen.',
            retention_n_days: '{days} dag(en)',
            retention_none: 'Geen beleid',
        },
    },
    category_labels: {
        'user-guide': 'Gebruikershandleiding',
        'admin-guide': 'Beheerdersgids',
        'getting-started': 'Aan de slag',
        faq: 'Veelgestelde vragen',
    },
    tag_labels: {
        faq: 'FAQ',
        dsar: 'DSAR',
        dsr: 'DSR',
        cmp: 'CMP',
        hmac: 'HMAC',
        sso: 'SSO',
        mfa: 'MFA',
        rbac: 'RBAC',
        api: 'API',
        'api-keys': 'API Toetsen',
        webhook: 'Webhook',
        webhooks: 'Webhaken',
        admin: 'Beheerder',
        aesirx: 'AesirX',
        purposes: 'Doeleinden',
        preferences: 'Voorkeuren',
        onboarding: 'Aan boord',
        'first-steps': 'Eerste stappen',
        login: 'Inloggen',
        navigation: 'Navigatie',
        users: 'Gebruikers',
        user: 'Gebruiker',
        roles: 'Rollen',
        role: 'Rol',
        'access-control': 'Toegangscontrole',
        'access-management': 'Toegangsbeheer',
        permissions: 'Machtigingen',
        permission: 'Toestemming',
        consent: 'Toestemming',
        'consent-management': 'Toestemmingsbeheer',
        privacy: 'Privacy',
        pdpl: 'PDPL',
        gdpr: 'GDPR',
        iso27001: 'ISO 27001',
        audit: 'Controle',
        auditing: 'Controle',
        breach: 'Schending',
        incident: 'Voorval',
        incidents: 'Incidenten',
        notification: 'Kennisgeving',
        notifications: 'Meldingen',
        reporting: 'Rapportage',
        risk: 'Risico',
        vendor: 'Verkoper',
        vendors: 'Verkopers',
        'vendor-risk': 'Leveranciersrisico',
        assessment: 'Beoordeling',
        assessments: 'Beoordelingen',
        dpia: 'DPIA',
        'cross-border': 'Grensoverschrijdend',
        transfer: 'Overdracht',
        transfers: 'Overdrachten',
        deletion: 'Verwijdering',
        retention: 'Retentie',
        'data-mapping': 'Gegevens in kaart brengen',
        discovery: 'Ontdekking',
        classification: 'Classificatie',
        workflow: 'Werkstroom',
        workflows: 'Werkstromen',
        template: 'Sjabloon',
        templates: 'Sjablonen',
        catalog: 'Catalogus',
        monitoring: 'Toezicht',
        tasks: 'Taken',
        task: 'Taak',
        'task-management': 'Taakbeheer',
        organizations: 'Organisaties',
        org: 'Organisatie',
        governance: 'Bestuur',
        compliance: 'Naleving',
        forms: 'Formulieren',
        form: 'Vorm',
        connectors: 'Connectoren',
        connector: 'Connector',
        integration: 'Integratie',
        integrations: 'Integraties',
        security: 'Beveiliging',
        encryption: 'Encryptie',
        backup: 'Back-up',
        recovery: 'Herstel',
        maintenance: 'Onderhoud',
        settings: 'Instellingen',
        configuration: 'Configuratie',
        sla: 'SLA',
        escalation: 'Escalatie',
        remediation: 'Sanering',
        evidence: 'Bewijs',
        'case-management': 'Casebeheer',
        profile: 'Profiel',
        delegation: 'delegatie',
        'service-accounts': 'Serviceaccounts',
        license: 'Licentie',
        'license-management': 'Licentiebeheer',
        plan: 'Plannen',
        plans: 'Plannen',
        dashboard: 'Dashboard',
        'web-consent': 'Webtoestemming',
        'partner-marketplace': 'Partnermarktplaats',
        partner: 'Partner',
        partners: 'Partners',
        deliverables: 'Leveringen',
        'service-tiers': 'Serviceniveaus',
        'support-tickets': 'Ondersteuningstickets',
        'audit-trail': 'Audittraject',
        'first-time-setup': 'Eerste installatie',
        setup: 'Installatie',
        quickstart: 'Snel beginnen',
        tour: 'Rondleiding',
        tours: 'Rondleidingen',
        'sample-data': 'Voorbeeldgegevens',
        'data-subject': 'Betrokkene',
        'data-subjects': 'Betrokkenen',
        pseudonymous: 'Pseudoniem',
        identified: 'Geïdentificeerd',
        'help-center': 'Helpcentrum',
        article: 'Artikel',
        articles: 'Artikelen',
        search: 'Zoeken',
        'platform-settings': 'Platforminstellingen',
        'platform-config': 'Platformconfiguratie',
        'module-config': 'Moduleconfiguratie',
        'module-configuration': 'Moduleconfiguratie',
        'rights-requests': 'Rechtenverzoeken',
        'access-accountability': 'Toegang tot verantwoording',
        'data-discovery': 'Gegevens ontdekken',
        'incident-ops': 'Incidentoperaties',
        'deletion-orchestration': 'Verwijderingsorkestratie',
        'compliance-forms': 'Nalevingsformulieren',
        'program-governance': 'Programmabeheer',
        localization: 'Lokalisatie',
        'privacy-scanner': 'Privacyscanner',
        'consent-properties': 'Toestemmingseigenschappen',
        acceptances: 'Acceptaties',
        access: 'Toegang',
        'access-review': 'Toegang tot beoordeling',
        'access-reviews': 'Toegang tot beoordelingen',
        accountability: 'Verantwoording',
        acknowledgment: 'Erkenning',
        'action-plans': 'Actieplannen',
        activation: 'Activering',
        activations: 'Activeringen',
        'active-response': 'Actieve reactie',
        activity: 'Activiteit',
        adequacy: 'Geschiktheid',
        'admin-guide': 'Beheerdersgids',
        aggregation: 'Aggregatie',
        agreements: 'Overeenkomsten',
        ai: 'AI',
        alerts: 'Waarschuwingen',
        analytics: 'Analyse',
        anchoring: 'Verankering',
        approval: 'Goedkeuring',
        approvals: 'Goedkeuringen',
        apps: 'Apps',
        assets: 'Activa',
        assignments: 'Opdrachten',
        attestation: 'Attest',
        'audit-log': 'Auditlogboek',
        'audit-pack': 'Auditpakket',
        authentication: 'Authenticatie',
        authority: 'Autoriteit',
        'auto-play': 'Automatisch afspelen',
        automated: 'Geautomatiseerd',
        automation: 'Automatisering',
        baseline: 'Basislijn',
        batches: 'Batches',
        bidirectional: 'Bidirectioneel',
        bilingual: 'Tweetalig',
        bindings: 'Bindingen',
        blockchain: 'Blockchain',
        board: 'Bord',
        brand: 'Merk',
        brds: "BRD's",
        'break-glass': 'Breekglas',
        builder: 'Bouwer',
        bulk: 'Bulk',
        'bulk-sync': 'Bulksynchronisatie',
        bundles: 'Bundels',
        calendar: 'Kalender',
        campaigns: 'Campagnes',
        cases: 'Gevallen',
        categories: 'Categorieën',
        category: 'Categorie',
        certification: 'Certificering',
        chain: 'Keten',
        change: 'Verandering',
        'change-awareness': 'Verander bewustzijn',
        'change-log': 'Wijzigingslogboek',
        'change-management': 'Verandermanagement',
        'change-requests': 'Wijzigingsverzoeken',
        'cmp-validation': 'CMP Validatie',
        collection: 'Collectie',
        comparison: 'Vergelijking',
        completeness: 'Volledigheid',
        completion: 'Voltooiing',
        'compliance-posture': 'Nalevingshouding',
        'compliance-proof': 'Nalevingsbewijs',
        'compliance-reviews': 'Nalevingsbeoordelingen',
        config: 'Configuratie',
        confirmation: 'Bevestiging',
        conflicts: 'Conflicten',
        consolidation: 'Consolidatie',
        continuous: 'Continu',
        contracts: 'Contracten',
        controls: 'Controles',
        cookies: 'Koekjes',
        cooperation: 'Samenwerking',
        coordination: 'Coördinatie',
        'corporate-governance': 'Corporate governance',
        'corrective-actions': 'Corrigerende acties',
        coverage: 'Dekking',
        'credential-vault': 'Referentiekluis',
        credentials: 'Referenties',
        cycles: 'Cycli',
        dashboards: 'Dashboards',
        'data-assets': 'Gegevensactiva',
        'data-flows': 'Gegevensstromen',
        'data-sensitivity': 'Gegevensgevoeligheid',
        'data-steward': 'Gegevensbeheerder',
        deadlines: 'Deadlines',
        'decree-356': 'Decreet 356',
        dedup: 'Ontdubbelen',
        definitions: 'Definities',
        deliverable: 'Leverbaar',
        departments: 'Afdelingen',
        'dependency-order': 'Afhankelijkheidsorde',
        deployment: 'Implementatie',
        detection: 'Detectie',
        diff: 'Verschil',
        'digital-properties': 'Digitale eigendommen',
        dimensions: 'Afmetingen',
        disable: 'Uitschakelen',
        'disaster-recovery': 'Herstel na een ramp',
        disclosure: 'Openbaarmaking',
        documentation: 'Documentatie',
        documents: 'Documenten',
        domains: 'Domeinen',
        dossier: 'Dossier',
        dpa: 'DPA',
        drift: 'Afdrijven',
        'drift-detection': 'Driftdetectie',
        'due-diligence': 'Due diligence',
        email: 'Email',
        emergency: 'Noodsituatie',
        enable: 'Inschakelen',
        'enterprise-map': 'Enterprise-kaart',
        erasure: 'Wissen',
        evaluation: 'Evaluatie',
        'event-bus': 'Evenementenbus',
        exceptions: 'Uitzonderingen',
        execution: 'Uitvoering',
        executive: 'Uitvoerend',
        exercises: 'Oefeningen',
        export: 'Exporteren',
        exports: 'Exporteert',
        filing: 'Indienen',
        filter: 'Filteren',
        filtering: 'Filteren',
        findings: 'Bevindingen',
        flows: 'Stromen',
        'follow-up': 'Vervolg',
        forensic: 'Forensisch',
        forensics: 'Forensisch onderzoek',
        'form-filler': 'Formuliervuller',
        framework: 'Kader',
        frameworks: 'Kaders',
        'gap-analysis': 'Gap-analyse',
        'gap-report': 'Gap-rapport',
        gaps: 'Hiaten',
        gates: 'Poorten',
        'getting-started': 'Aan de slag',
        'golden-templates': 'Gouden sjablonen',
        grc: 'GRC',
        'group-management': 'Groepsbeheer',
        groups: 'Groepen',
        'guided-tours': 'Rondleidingen',
        guides: 'Gidsen',
        handling: 'Behandeling',
        'hands-on': 'Hands-on',
        health: 'Gezondheid',
        help: 'Hulp',
        hierarchy: 'Hiërarchie',
        history: 'Geschiedenis',
        i18n: 'i18n',
        iam: 'IAM',
        'identity-provider': 'Identiteitsprovider',
        implementation: 'Implementatie',
        import: 'Importeren',
        improvement: 'Verbetering',
        'incident-response': 'Reactie op incidenten',
        inconsistencies: 'Inconsistenties',
        instances: 'Instanties',
        intake: 'Inname',
        'intake-key': 'Innamesleutel',
        interactive: 'Interactief',
        international: 'Internationaal',
        inventory: 'Inventaris',
        investigation: 'Onderzoek',
        investigations: 'Onderzoeken',
        'ip-allowlist': 'Toegestane IP-lijst',
        issues: 'Problemen',
        justification: 'Rechtvaardiging',
        justifications: 'Rechtvaardigingen',
        kanban: 'Kanban',
        keyboard: 'Toetsenbord',
        keys: 'Sleutels',
        'knowledge-base': 'Kennisbank',
        koncernstyring: 'Corporate governance',
        kpi: 'KPI',
        labeling: 'Etikettering',
        language: 'Taal',
        'language-packs': 'Taalpakketten',
        languages: 'Talen',
        lawfulness: 'Rechtmatigheid',
        layout: 'Indeling',
        'legal-basis': 'Wettelijke basis',
        'lessons-learned': 'Geleerde lessen',
        levels: 'Niveaus',
        library: 'Bibliotheek',
        lifecycle: 'Levenscyclus',
        lineage: 'Afstamming',
        links: 'Koppelingen',
        listings: 'Advertenties',
        live: 'Leef',
        locale: 'Lokaal',
        log: 'Log',
        logging: 'Loggen',
        'managed-service': 'Beheerde dienst',
        mapping: 'In kaart brengen',
        mappings: 'Toewijzingen',
        'market-packs': 'Marktpakketten',
        marketplace: 'Marktplaats',
        maturity: 'Volwassenheid',
        metrics: 'Statistieken',
        models: 'Modellen',
        module: 'Module',
        modules: 'Modules',
        mps: 'MPS',
        'multi-factor': 'Multi-factor',
        'multi-market': 'Multi-markt',
        'multi-org': 'Multi-Org',
        'multi-property': 'Meerdere eigendommen',
        'my-tasks': 'Mijn taken',
        obligations: 'Verplichtingen',
        offboarding: 'Offboarden',
        oidc: 'OIDC',
        orchestration: 'Orkestratie',
        'org-groups': 'Organisatiegroepen',
        organization: 'Organisatie',
        overdue: 'Te laat',
        overview: 'Overzicht',
        'owner-review': 'Eigenaar beoordeling',
        package: 'Pakket',
        packages: 'Pakketten',
        packs: 'Pakken',
        pagination: 'Paginering',
        'parent-child': 'Ouder-kind',
        patterns: 'Patronen',
        performance: 'Prestaties',
        periodic: 'Periodiek',
        periods: 'Perioden',
        personal: 'Persoonlijk',
        'personal-data': 'Persoonlijke gegevens',
        personas: "Persona's",
        planning: 'Plannen',
        platform: 'Platform',
        playbooks: 'Speelboeken',
        poc: 'PoC',
        'point-in-time': 'Punt-in-tijd',
        policies: 'Beleid',
        portfolio: 'Portefeuille',
        'post-incident': 'Post-incident',
        'power-user': 'Hoofdgebruiker',
        practice: 'Oefen',
        pricing: 'Prijzen',
        'privacy-impact': 'Privacy-impact',
        procedures: 'Procedures',
        processes: 'Processen',
        processing: 'Verwerking',
        'processing-activities': 'Verwerkingsactiviteiten',
        procurement: 'Inkoop',
        productivity: 'Productiviteit',
        profiles: 'Profielen',
        programs: "Programma's",
        progress: 'Vooruitgang',
        proof: 'Bewijs',
        propagation: 'Voortplanting',
        properties: 'Eigenschappen',
        'pub-sub': 'Pub-Sub',
        'public-form': 'Publieke vorm',
        quality: 'Kwaliteit',
        questionnaires: 'Vragenlijsten',
        'quick-links': 'Snelle koppelingen',
        'quick-start': 'Snel beginnen',
        'quick-starts': 'Snelle start',
        raci: 'RACI',
        readiness: 'Bereidheid',
        'real-time': 'Realtime',
        reassessments: 'Herbeoordelingen',
        reconciliation: 'Verzoening',
        record: 'Opnemen',
        records: 'Opnames',
        recurring: 'Terugkerend',
        register: 'Registreren',
        registry: 'Register',
        regulations: 'Regelgeving',
        regulator: 'Regelaar',
        regulatory: 'Regelgevend',
        'regulatory-frameworks': 'Regelgevende kaders',
        remap: 'Opnieuw toewijzen',
        reminders: 'Herinneringen',
        reports: 'Rapporten',
        requests: 'Verzoeken',
        requirements: 'Vereisten',
        response: 'Reactie',
        'response-plans': 'Reactieplannen',
        'response-team': 'Reactieteam',
        'response-templates': 'Antwoordsjablonen',
        'response-time': 'Reactietijd',
        responses: 'Reacties',
        responsibility: 'Verantwoordelijkheid',
        results: 'Resultaten',
        review: 'Beoordeling',
        reviews: 'Recensies',
        'right-to-delete': 'Recht om te verwijderen',
        rights: 'Rechten',
        'risk-tiers': 'Risiconiveaus',
        rollouts: 'Uitrol',
        rollup: 'Samenvouwen',
        ropa: 'ROPA',
        routing: 'Routering',
        rpo: 'RPO',
        rto: 'RTO',
        rules: 'Regels',
        runs: 'Loopt',
        saml: 'SAML',
        scan: 'Scannen',
        scanning: 'Scannen',
        scc: 'SCC',
        schedule: 'Schema',
        scheduled: 'Gepland',
        schedules: "Schema's",
        scheduling: 'Planning',
        scores: 'Scores',
        scoring: 'Scoren',
        scripts: 'Scripts',
        'security-reviews': 'Beveiligingsrecensies',
        'sensitive-data': 'Gevoelige gegevens',
        sensitivity: 'Gevoeligheid',
        'separation-of-duties': 'Scheiding van taken',
        service: 'Dienst',
        sessions: 'Sessies',
        'setup-wizard': 'Installatiewizard',
        severity: 'Ernst',
        'shared-governance': 'Gedeeld bestuur',
        'shared-settings': 'Gedeelde instellingen',
        shortcuts: 'Snelkoppelingen',
        sidebar: 'Zijbalk',
        signals: 'Signalen',
        'single-sign-on': 'Eenmalige aanmelding',
        snapshots: 'Momentopnamen',
        sod: 'SoD',
        sorting: 'Sorteren',
        sources: 'Bronnen',
        staging: 'Staging',
        standards: 'Normen',
        status: 'Status',
        structure: 'Structuur',
        'sub-processor': 'Subverwerker',
        'sub-processors': 'Subverwerkers',
        submissions: 'Inzendingen',
        subprocessors: 'Subverwerkers',
        subscription: 'Abonnement',
        suggestions: 'Suggesties',
        summaries: 'Samenvattingen',
        supplements: 'Supplementen',
        support: 'Ondersteuning',
        sync: 'Synchroniseren',
        system: 'Systeem',
        systems: 'Systemen',
        tables: 'Tafels',
        tagging: 'Labelen',
        'tamper-proof': 'Fraudebestendig',
        taxonomies: 'Taxonomieën',
        taxonomy: 'Taxonomie',
        tenants: 'Huurders',
        termination: 'Beëindiging',
        'third-party': 'Derde partij',
        'ticket-sync': 'Ticketsynchronisatie',
        tickets: 'Kaartjes',
        tiers: 'Niveaus',
        tokens: 'Tokens',
        totp: 'TOTP',
        traceability: 'Traceerbaarheid',
        tracking: 'Volgen',
        trail: 'Parcours',
        training: 'Opleiding',
        translation: 'Vertaling',
        translations: 'Vertalingen',
        transparency: 'Transparantie',
        trends: 'Trends',
        unified: 'Verenigd',
        updates: 'Updates',
        validation: 'Validatie',
        vault: 'Kluis',
        'vendor-requests': 'Leveranciersverzoeken',
        verification: 'Verificatie',
        versioning: 'Versiebeheer',
        versions: 'Versies',
        vietnam: 'Vietnam',
        violations: 'Overtredingen',
        visualization: 'Visualisatie',
        waivers: 'Ontheffingen',
        'web-records': 'Webrecords',
        website: 'Website',
        websites: 'Websites',
        weights: 'Gewichten',
        'white-label': 'White-label',
        withdrawal: 'Intrekking',
    },
    module_labels: {
        // Sprint 63.25.0 (spec G2) — the source modules that had no label.
        compliance_rules: 'Compliance Rules',
        access_review: 'Access Reviews',
        consolidation: 'Consolidation',
        case_audit: 'Module Audit Trail',
        rights_requests: 'Verzoeken om rechten',
        data_mapping: 'Gegevensinventarisatie',
        governance: 'Bestuur',
        consent: 'Toestemmingsgovernance',
        consent_properties: 'Toestemmingseigenschappen',
        assessments: 'GEB & beoordelingen',
        classification: 'Gegevensclassificatie',
        monitoring: "Monitoringprogramma's",
        program: 'Monitoringprogramma',
        incidents: 'Incidentrespons',
        connectors: 'Connectoren',
        vendor_risk: 'Leveranciersrisico',
        deletion_orchestration: 'Verwijderingsorkestratie',
        discovery: 'Gegevensontdekking',
        incident_ops: 'Incidentoperaties',
        program_governance: 'Programmabestuur',
        access_accountability: 'Toegangsverantwoording',
        localization: 'Lokalisatie',
        sector_overlays: 'Sector Overlays',
        partner_workspace: 'Partnerwerkruimte',
        privacy_scanner: 'Privacyscanner',
        onboarding: 'Aan boord',
        compliance_forms: 'Nalevingsformulieren',
        audit_trail: 'Audittrail',
        data_subjects: 'Betrokkenen',
        organizations: 'Organisaties',
        task_management: 'Taakbeheer',
        deletion_orch: 'Verwijderingsorkestratie',
        forms: 'Nalevingsformulieren',
        partner_marketplace: 'Partnermarktplaats',
        dashboard: 'Dashboard',
        platform_settings: 'Platforminstellingen',
        help_center: 'Helpcentrum',
    },
    module_descriptions: {
        rights_requests: 'Beheer verzoeken van betrokkenen om hun rechten uit te oefenen, waaronder inzage, verwijdering en overdraagbaarheid. Volg de levenscyclus van verzoeken, wijs behandelaars toe en haal wettelijke termijnen.',
        data_mapping: 'Breng gegevensstromen in kaart binnen uw organisatie. Volg verwerkingsactiviteiten, gegevenscategorieën, doeleinden, bewaartermijnen en systeemintegraties.',
        governance: 'Toezicht en het bewijs daarachter. Beheer het leveranciersregister en de levenscyclus ervan, keur de beslissingen goed die deze module afschermt, let op compliancedrift en houd de registers bij voor vertrouwen en identiteit, regelgevingsartefacten, betalingen en eKYC, en platformbewijs.',
        consent: 'Beheer het verzamelen, opslaan en intrekken van toestemming. Volg toestemmingsregistraties, doeleinden en integratie met toestemmingsbeheerplatformen.',
        consent_properties: 'Configureer toestemmingseigenschappen, groepen en gouden sjablonen. Definieer herbruikbare toestemmingsconfiguraties voor consistente uitrol.',
        assessments: "Voer gegevensbeschermingseffectbeoordelingen (GEB) en privacyrisico-beoordelingen uit. Evalueer verwerkingsactiviteiten op nalevingsrisico's.",
        classification: 'Classificeer gegevensactiva op gevoeligheidsniveau. Pas taxonomielabels toe, volg levenscyclusrecords en identificeer classificatielacunes.',
        monitoring: "Stel monitoringprogramma's in om nalevingsmetrieken, belangrijke risico-indicatoren en de effectiviteit van controles in de loop der tijd te volgen.",
        incidents: 'Meld en beheer privacyincidenten en datalekken. Volg onderzoeks-, meldings- en herstelworkflows.',
        connectors: 'Configureer gegevensbronconnectoren en integraties. Verbind externe systemen voor geautomatiseerde gegevensontdekking en monitoring.',
        vendor_risk: "Beoordeel en bewaak risico's van externe leveranciers. Scoor leveranciers, volg risicobeperking en beheer configuraties voor leveranciersrisico.",
        deletion_orchestration: 'Orkestreer gegevensverwijdering over systemen heen. Volg verwijderingsverzoeken, verifieer bewijsketens en waarborg volledige gegevensverwijdering.',
        discovery: 'Ontdek persoonsgegevens in verbonden systemen. Scan gegevensbronnen om een uitgebreide gegevensinventaris op te bouwen.',
        incident_ops: 'Operationeel incidentbeheer met draaiboeken, escalatieworkflows en SLA-tracking voor incidentoplossing.',
        program_governance: 'Volg de algehele volwassenheid van het privacyprogramma, nalevingsscores, bewijslacunes en directierapportage binnen uw organisatie.',
        access_accountability: 'Onderzoek en controleer gebruikerstoegangspatronen. Volg toegangsconfiguraties, monitor verdachte activiteit en handhaaf verantwoordingsplicht.',
        localization: 'Installeer en beheer marktlokalisatiepakketten. Voeg regelgevende kaders, taalpakketten en regiospecifieke nalevingsinhoud toe.',
        sector_overlays: 'Install and manage optional sector overlays, and open the sector registers an activated overlay brings into use, organised by sector. Available in the Enterprise package.',
        partner_workspace: 'Werk samen met externe partners. Deel nalevingsgegevens, volg partnerverplichtingen en beheer partnerrelaties.',
        privacy_scanner: 'Scan websites op privacynalevingsproblemen. Monitor cookiegebruik, trackingscripts en de aanwezigheid van privacybeleid in realtime.',
        onboarding: 'Begeleid nieuwe organisaties bij het instellen van het platform. Stapsgewijze onboarding-wizard voor initiële configuratie en gegevensimport.',
        compliance_forms: 'Maak en beheer nalevingsformulieren vanuit een sjablonencatalogus. Vul regelgevende formulieren in, volg inzendingen en genereer rapporten.',
        audit_trail: 'Bekijk een volledig auditspoor van alle systeemacties. Volg wie wat deed, wanneer en waar in alle modules.',
        organizations: 'Beheer organisaties en multi-tenantinstellingen. Configureer organisatieprofielen, hiërarchieën en relaties tussen organisaties.',
    },
    privacy_scanner: {
        anomaly_explain: {
            explain_label: 'Anomalieën uitleggen',
            explaining: 'Uitleggen…',
            results_heading: 'Verklaringen voor afwijkingen ({severity})',
            tokens_label: '≈ {count} tokens',
            items_label: '{count} artikelen',
            remediation_label: 'Saneringsstappen',
            items_detail_label: 'Betrokken artikelen',
            accept_label: 'Accepteer uitleg',
            reject_label: 'Verklaring afwijzen',
            error_generic: 'Kon de afwijkingen niet verklaren. Probeer het opnieuw of controleer of de scan succesvol is voltooid.',
        },
        cookie_insight: {
            insight_label: 'Cookie & tracker AI inzicht',
            producing: 'Inzicht genereren…',
            results_heading: 'Cookie- en trackerinzicht ({severity})',
            tokens_label: '≈ {count} tokens',
            items_label: '{count} artikelen',
            classification_label: 'Classificatie',
            severity_label: 'Ernst',
            banner_copy_label: 'Voorgestelde bannerkopie',
            retention_label: 'Bewaaradvies',
            citations_label: 'Citaties',
            kind_cookie: 'Koekje',
            kind_tracker: 'Volger',
            class_necessary: 'Noodzakelijk',
            class_analytics: 'Analyse',
            class_marketing: 'Marketing',
            class_social: 'Sociaal',
            accept_label: 'Accepteer inzicht',
            reject_label: 'Inzicht afwijzen',
            no_items: 'Er zijn bij deze scan geen cookies of trackers gedetecteerd.',
            error_generic: 'Kan het cookie-inzicht niet produceren. Probeer het opnieuw of controleer of de scan succesvol is voltooid.',
        },
        monitoring: {
            title: 'Privacymonitoring',
            new_scan: 'Nieuw Scannen',
            scan_url: 'URL naar Scannen',
            scan_frequency: 'Scannen Frequentie',
            last_scan: 'Laatste Scannen',
            score: 'Privacyscore',
            issues: 'Gevonden problemen',
            history: 'Scannen Geschiedenis',
            settings: 'Monitoring Instellingen',
        },
        real_time: {
            title: 'Realtimescanner',
            scan_now: 'Scannen Now',
            scanning: 'Scannen...',
            results: 'Scannen Resultaten',
            no_results: 'Nog geen scanresultaten.',
            cookies: 'Browsercookies',
            trackers: 'Volgsystemen',
            consent_banners: 'Toestemming Banners',
            third_party: 'Scripts van derden',
            archive: 'Resultaten Archiveren',
            settings: 'Scanner Instellingen',
        },
        dashboard: {
            page_title: 'Dashboard voor privacybewaking',
            privacy_monitoring: 'Privacybewaking',
            plan_label: '{package}-abonnement',
            expires_label: 'verloopt op {date}',
            settings: 'Instellingen',
            refresh: 'Vernieuwen',
            refreshing_dashboard: 'Vernieuwend monitoringdashboard',
            refresh_aria: 'Vernieuw het monitoringdashboard',
            not_configured_title: 'Privacybewaking niet geconfigureerd',
            not_configured_desc: 'Sluit uw AesirX Privacy Monitoring-service aan om de naleving van de website te volgen.',
            configure_monitoring: 'Configureer monitoring',
            license_expired_title: 'Licentie verlopen',
            license_expired_desc: 'Uw AesirX Privacy Monitoring-licentie is verlopen. Verleng deze om serviceonderbreking te voorkomen.',
            compliance_score: 'Nalevingsscore',
            cookies_detected: 'Cookies gedetecteerd',
            trackers_label: 'Volgers',
            open_issues: 'Openstaande problemen',
            score_good: 'Goed',
            score_needs_improvement: 'Verbetering nodig',
            score_critical: 'Kritisch',
            action_required: 'Actie vereist',
            cookies_sr: 'cookies gedetecteerd',
            trackers_sr: 'spoorzoekers gevonden',
            open_issues_sr: 'openstaande kwesties',
            action_required_sr: 'actie vereist',
            metric_label: 'Metrisch',
            value_label: 'Waarde',
            status_label: 'Status',
            aria_metrics: 'Statistieken voor privacymonitoring',
            plan_limit_warning: 'Scannen URL: {current}/{max} — je hebt je abonnementslimiet bereikt.',
            manage_domains: 'Beheer domeinen of upgrade uw abonnement.',
            monitored_domains: 'Bewaakte domeinen',
            domain_added: 'Domein {domain} toegevoegd',
            domain_removed: 'Domein {domain} verwijderd',
            failed_add_domain: 'Kan domein niet toevoegen',
            failed_remove_domain: 'Kan domein niet verwijderen',
            failed_load: 'Kan het monitoringdashboard niet laden',
            new_domain_aria: 'Nieuw domein URL',
            add_domain: 'Domein toevoegen',
            adding_domain: 'Toevoegen…',
            no_domains: 'Er zijn nog geen bewaakte domeinen geconfigureerd.',
            aria_domains_table: 'Gecontroleerde domeinen',
            col_domain: 'Domein',
            col_compliance_score: 'Nalevingsscore',
            col_status: 'Status',
            col_cookies: 'Koekjes',
            col_trackers: 'Volgers',
            col_issues: 'Problemen',
            col_last_scan: 'Laatste scan',
            col_action: 'Actie',
            compliance_status_sr: 'nalevingsstatus',
            showing_chart: 'Grafiekweergave weergeven',
            showing_table: 'Gegevenstabelweergave weergeven',
            loading_sr: 'Monitoringdashboard laden',
            col_framework_tags: 'Kadertags',
            col_last_monitored: 'Laatst gecontroleerd',
            framework_tags_sr: 'toepasselijke regelgevingskaders',
            last_monitored_sr: 'tijdstempel voor continue monitoring',
            audit_trail_heading: 'Audittraject',
            audit_trail_info: 'Elke monitoringscan wordt voorzien van een tijdstempel en geregistreerd. Wijzigingen in de compliance op domeinniveau zorgen voor een onveranderlijk auditrecord dat kan worden geëxporteerd voor controle door de toezichthouder.',
            cybersecurity_law_info_heading: 'Detectie van cyberbeveiligingswetten',
            cybersecurity_law_info: 'Gecontroleerde domeinen worden automatisch gecontroleerd op de vereisten van de cyberbeveiligingswetgeving (bijvoorbeeld de Vietnamese cyberbeveiligingswet, NIS2 richtlijn). Kadertags geven aan welke regelgeving van toepassing is. Kwesties worden gecategoriseerd per regelgevingskader voor gericht herstel.',
        },
        scan: {
            page_title: 'Realtime scannen',
            title: 'Realtime privacyscanner',
            settings: 'Instellingen',
            results_archive: 'Resultatenarchief',
            launch_scan: 'Start Scannen',
            target_url: 'Doel URL',
            protocol_aria: 'Protocol',
            url_required: 'URL is vereist',
            url_whitespace: 'Verwijder de witruimte uit de URL',
            url_invalid: 'Voer een geldig domein in (bijvoorbeeld voorbeeld.com)',
            add_subpages: "Subpagina's toevoegen",
            add_subpages_aria: "Subpagina's toevoegen, {count} toegevoegd",
            start_scan: 'Start scannen',
            launching: 'Lancering…',
            scan_launched: 'Scannen is succesvol gestart',
            scan_failed: 'Kan de scan niet starten',
            scan_progress: 'Scanvoortgang',
            scanning_in_progress: 'Bezig met scannen…',
            scan_id: 'Scan-ID',
            status_label: 'Status',
            status_unknown: 'unknown',
            progress_label: 'Vooruitgang',
            pages_label: "Pagina's",
            refresh_status: 'Vernieuwingsstatus',
            new_scan: 'Nieuwe scan',
            scan_completed: 'Scan voltooid, resultaten beschikbaar',
            failed_check_status: 'Kan de scanstatus niet controleren',
            scan_results: 'Scanresultaten',
            main_page: 'Hoofdpagina',
            page_n: 'Pagina {n}',
            result_pages_nav: "Resultatenpagina's",
            page_url: 'Pagina URL',
            risk_score: 'Risicoscore',
            risk_score_sr: 'risicoscore',
            cookies_label: 'Koekjes',
            cookies_sr: 'cookies gedetecteerd',
            scripts_label: 'Scripts',
            scripts_sr: 'scripts gedetecteerd',
            issues_label: 'Problemen',
            issues_sr: 'problemen gevonden',
            issues_found: 'Problemen gevonden',
            aria_issues_table: 'Problemen gevonden tijdens de scan',
            col_severity: 'Ernst',
            col_category: 'Categorie',
            col_title: 'Titel',
            col_remediation: 'Sanering',
            severity_sr: 'severity',
            scan_result_metrics: 'Resultaatstatistieken scannen',
            view_full_detail: 'Bekijk alle details',
            col_consent_category: 'Toestemmingscategorie',
            consent_category_sr: 'categorisering van toestemming',
            compliance_evidence_heading: 'Bewijs van naleving',
            compliance_evidence_info: 'Scanbevindingen worden automatisch gekoppeld aan compliancegevallen. Elk probleem kan worden herleid tot een toestemmingscategorie (essentieel, functioneel, analytisch, marketing) en levert auditklaar bewijsmateriaal op voor controle door de toezichthouder.',
            rule_config_heading: 'Regelconfiguratie',
            rule_config_info: 'Scanregels zijn configureerbaar per regelgevingskader. Cookiecontroles, scriptdetectie en leveranciersanalyse kunnen via Instellingen worden in- of uitgeschakeld. Aangepaste regels kunnen worden gedefinieerd om te voldoen aan de nalevingsvereisten van uw organisatie.',
            case_linkage_heading: 'Koppeling van compliancezaken',
            case_linkage_info: 'Problemen die tijdens realtime scans worden aangetroffen, kunnen worden gekoppeld aan nalevingsgevallen voor tracking en herstel. Navigeer naar de pagina Scanresultatendetail om case-associaties te bekijken.',
            subpages_modal_title: "Subpagina's toevoegen",
            subpages_modal_desc: "Voeg maximaal vijf subpagina-URL's toe om naast het hoofddomein te scannen.",
            subpage_n: 'Subpagina {n}',
        },
        result_detail: {
            page_title: 'Details van scanresultaten',
            title: 'Scanresultaat',
            back_to_archive: 'Terug naar archief',
            failed_load: 'Kan het scanresultaat niet laden',
            loading_sr: 'Scanresultaat laden…',
            scan_id: 'Scan-ID',
            target_url: 'Doel URL',
            status_label: 'Status',
            status_sr: 'status',
            started: 'Gestart',
            completed: 'Voltooid',
            pages_scanned: "Pagina's gescand",
            https_label: 'HTTPS',
            highest_risk_score: 'Hoogste risicoscore',
            most_cookies: 'De meeste koekjes',
            most_vendors: 'De meeste externe leveranciers',
            most_issues: 'De meeste problemen',
            across_pages: "over pagina's heen",
            highest_values_aria: "Hoogste waarden op alle pagina's",
            scanned_pages_nav: "Gescande pagina's",
            main_page: 'Hoofdpagina',
            page_n: 'Pagina {n}',
            page_url: 'Pagina URL',
            risk_score: 'Risicoscore',
            overall_risk_sr: 'algemene risicoscore',
            cookies_label: 'Koekjes',
            cookies_sr: 'cookies gedetecteerd',
            vendors_label: 'Externe leveranciers',
            vendors_sr: 'externe leveranciers gedetecteerd',
            issues_label: 'Problemen',
            issues_sr: 'problemen gevonden',
            risk_breakdown_aria: 'Uitsplitsing van de risicoscore',
            issues_found: 'Problemen gevonden',
            aria_issues_table: 'Problemen gevonden tijdens de scan',
            col_severity: 'Ernst',
            col_category: 'Categorie',
            col_title: 'Titel',
            col_remediation: 'Sanering',
            severity_sr: 'severity',
            cookies_heading: 'Cookies ({count})',
            aria_cookies_table: 'Cookies gedetecteerd',
            col_name: 'Naam',
            col_domain: 'Domein',
            col_path: 'Pad',
            col_secure: 'Veilig',
            col_httponly: 'Alleen HTTPP',
            col_samesite: 'Dezelfde site',
            vendors_heading: 'Externe leveranciers ({count})',
            aria_vendors_table: 'Externe leveranciers gedetecteerd',
            col_hostname: 'Hostnaam',
            scripts_heading: 'Scripts/bakens ({count})',
            aria_scripts_table: 'Scripts en bakens gedetecteerd',
            col_url: 'URL',
            col_type: 'Typ',
            links_heading: 'Koppelingen',
            first_party: 'Eerste partij',
            third_party: 'Derden',
            col_risk_category: 'Risicocategorie',
            col_regulatory_framework: 'Regelgevingskader',
            col_actionable_detail: 'Bruikbare details',
            risk_category_sr: 'risicocategorie op het gebied van de regelgeving',
            risk_category_mapping_heading: 'Risicocategorie in kaart brengen',
            risk_category_mapping_info: 'Elke scanbevinding wordt toegewezen aan een risicocategorie van de regelgeving (gegevensverzameling, grensoverschrijdende overdracht, toestemmingsbeheer, gegevensbewaring, beveiligingscontroles). Deze mapping maakt gericht herstel mogelijk, geprioriteerd op basis van de impact van de regelgeving.',
            compliance_case_heading: 'Vereniging voor nalevingszaken',
            compliance_case_info: 'Scanresultaten worden gekoppeld aan compliance-cases voor end-to-end tracking. Problemen kunnen rechtstreeks vanuit de scanresultaten worden geëscaleerd naar de workflow voor nalevingszaken.',
        },
        results_archive: {
            page_title: 'Archief scanresultaten',
            title: 'Archief scanresultaten',
            back_to_scanner: 'Terug naar Scanner',
            failed_load: 'Kan scanresultaten niet laden',
            results_loaded: 'Scanresultaten geladen, {count} records',
            total_label: 'Totaal: {count}',
            aria_table: 'Archief met scanresultaten',
            col_scan_date: 'Scandatum',
            col_target_url: 'Doel URL',
            col_duration: 'Duur',
            col_issues_found: 'Problemen gevonden',
            col_risk_score: 'Risicoscore',
            col_status: 'Status',
            col_actions: 'Acties',
            scan_status_sr: 'scanstatus',
            view_result: 'Bekijk',
            no_results: 'Geen scanresultaten gevonden.',
            loading_sr: 'Scanresultaten laden',
            pagination_aria: 'Paginering van scanresultaten',
            col_risk_category: 'Risicocategorie',
            risk_category_sr: 'risicocategorie op het gebied van de regelgeving',
            archive_retention_heading: 'Archiefbehoud',
            archive_retention_info: 'Scanresultaten worden bewaard gedurende de periode die is gespecificeerd in het gegevensbewaarbeleid van uw organisatie. Gearchiveerde resultaten omvatten de volledige scancontext (framework-tags, risicocategorieën, herstelbegeleiding) en kunnen worden geëxporteerd als CSV voor regelgevend bewijs.',
            export_context_info: 'Geëxporteerde CSV-bestanden bevatten de regelgevingscontext: risicocategorie, toepasselijke raamwerken en bruikbare hersteldetails voor elke bevinding.',
        },
        scanner_settings: {
            page_title: 'Scannerinstellingen',
            title: 'Scannerinstellingen',
            back_to_scanner: 'Terug naar Scanner',
            failed_load: 'Kan de scannerconfiguratie niet laden',
            endpoint_url: 'Eindpunt URL',
            save_success: 'Scannerconfiguratie succesvol opgeslagen.',
            save_failed: 'Kan de scannerconfiguratie niet opslaan',
            test_connection: 'Verbinding testen',
            testing: 'Testen...',
            saving: 'Opslaan...',
            loading_sr: 'Scannerinstellingen laden',
            endpoint_fixed_hint: 'The scanner address is fixed by the platform and cannot be changed here. Test Connection checks the AesirX credentials saved under Monitoring Settings.',
        },
        monitoring_settings: {
            page_title: 'Instellingen voor privacybewaking',
            title: 'Controle-instellingen',
            back_to_dashboard: 'Terug naar Dashboard',
            failed_load: 'Kan de monitoringconfiguratie niet laden',
            credentials_heading: 'AesirX Inloggegevens',
            connected_as: 'Verbonden als {username}',
            credentials_desc: 'Voer uw AesirX-accountgegevens in om verbinding te maken met Privacy Monitoring. Zoek uw lidmaatschaps-ID op de profielpagina van uw AesirX-account.',
            client_id: 'Klant-ID',
            client_secret: 'Klantgeheim',
            username: 'AesirX Gebruikersnaam (e-mail)',
            password: 'AesirX Wachtwoord',
            placeholder_keep: '(laat leeg om bestaand te blijven)',
            credential_form_aria: 'AesirX inlogformulier',
            test_connection: 'Verbinding testen',
            testing: 'Testen…',
            test_aria: 'Test AesirX-verbinding',
            testing_aria: 'Verbinding testen',
            save_configuration: 'Configuratie opslaan',
            saving: 'Opslaan…',
            save_aria: 'Bewaar inloggegevens',
            saving_aria: 'Inloggegevens opslaan',
            credentials_saved: 'AesirX inloggegevens zijn succesvol opgeslagen.',
            credentials_saving: 'AesirX inloggegevens opgeslagen. Configuratie opnieuw laden…',
            credentials_save_failed: 'Kan de inloggegevens niet opslaan',
            client_id_required: 'Klant-ID en gebruikersnaam zijn vereist.',
            all_fields_required: 'Alle inloggegevensvelden zijn verplicht.',
            email_heading: 'E-mailmeldingen',
            receive_emails: 'Ontvang e-mails met scanrapporten',
            email_toggle_aria: 'Schakel e-mails met scanrapporten in of uit',
            only_on_change: 'Alleen e-mailen als de risicostatus verandert',
            only_on_change_aria: 'Schakel alleen e-mail in bij wijziging van de risicostatus',
            emails_enabled: 'E-mails met scanrapporten ingeschakeld',
            emails_disabled: 'E-mails met scanrapporten uitgeschakeld',
            only_change_on: 'Alleen e-mailen over risicowijzigingen',
            only_change_off: 'E-mailen bij elke scan',
            email_failed: 'Kan de e-mailinstelling niet updaten',
            email_settings_aria: 'Instellingen voor e-mailmeldingen',
            loading_sr: 'Bewakingsinstellingen laden',
        },
        entity_types: {
            user: 'Gebruiker',
            case: 'Geval',
            incident: 'Voorval',
            assessment: 'Beoordeling',
            vendor: 'Verkoper',
            consent: 'Toestemming',
            policy: 'Beleid',
            data_map: 'Gegevenskaart',
        },
        actions: {
            create: 'Creëer',
            update: 'Bijwerken',
            delete: 'Verwijderen',
            view: 'Bekijk',
            export: 'Exporteren',
            status_change: 'Statuswijziging',
        },
    },
    forms: {
        // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
        // no locale declared, so each fell through to its hardcoded English third
        // argument and rendered English in all 16 locales. The two DPIA row-action
        // tooltips were worse: their fallbacks are themselves translated keys, so the
        // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
        // action read 'Approve/Reject' where it intends 'Review'.
        title: 'Form',
        dashboard: {
            title: 'Formulieroverzicht',
            total_templates: 'Totaal Sjablonen',
            installed: 'Geïnstalleerd',
            available: 'Beschikbaar',
            by_market: 'By Markt',
            back_dashboard: 'Back naar Dashboard',
            browse_templates: 'Bladeren Sjablonen',
            catalog_coverage: 'Catalogus Dekking',
            quick_start: 'Snel Starten',
            recent_instances: 'Recent Instanties',
            total_instances: 'Totaal Instanties',
            fetch_failed: 'Kan niet fetch dashboard gegevens..',
            no_instances: 'Geen instanties gevonden.',
            form_metrics: 'Overzicht formuliermetrieken',
            templates_total: 'sjablonen totaal',
            instances_total: 'instanties totaal',
            installed_of_available: '{installed} van {available} geïnstalleerd',
            templates_in_category: 'sjablonen in {category}',
            uncategorized: 'Uncategorized',
            other_framework: 'Other',
            category_templates_aria: '{category} templates',
            no_templates: 'No form templates available.',
        },
        templates: {
            title: 'Formuliersjablonen',
            new: 'Nieuw Sjabloon',
            edit: 'Bewerken Sjabloon',
            name: 'Sjabloon Naam',
            form_type: 'Formulier Type',
            market: 'Markt',
            language: 'Taal',
            schema: 'Formulier Schema',
            preview: 'Voorbeeld',
            duplicate: 'Duplicate Sjabloon',
            grid_view: 'Raster Bekijken',
            list_view: 'Lijst Bekijken',
            fetch_failed: 'Kan niet fetch sjablonen..',
            filter_templates: 'Sjablonen filteren',
            version_history: 'Versiegeschiedenis',
            no_version_history: 'Geen versiegeschiedenis — dit is de initiële versie.',
            form_id: 'Formulier-ID',
            source_instrument: 'Broninstrument',
            issuer: 'Uitgever',
            all_frameworks: 'Alle raamwerken',
            view_mode: 'Weergavemodus',
            classification: 'Classificatie',
        },
        catalog: {
            title: 'Formuliercatalogus',
            install: 'Installeren',
            uninstall: 'Verwijderen',
            open_form: 'Open form',
            install_all: 'Installeren Alle',
            installed_tab: 'Geïnstalleerd',
            catalog_tab: 'Catalogus',
            search_placeholder: 'Zoeken sjablonen...',
            no_templates: 'Geen sjablonen gevonden.',
            template_count: '{count} sjablonen',
            all_markets: 'Alle Markets',
            sections: 'Secties',
            all_categories: 'Alle Categorieën',
            category: 'Categorie',
            category_install_failed: 'Kan niet install category sjablonen..',
            category_install_success: 'Categorie sjablonen installed.',
            fetch_failed: 'Kan niet fetch sjablonen..',
            fetch_installed_failed: 'Kan niet fetch installed sjablonen..',
            fields: 'Velden',
            install_failed: 'Kan niet install sjabloon..',
            install_success: 'Sjabloon installed successfully.',
            installing: 'Installeren...',
            removing: 'Verwijderen...',
            uninstall_confirm: 'Are you sure you want naar uninstall dit sjabloon?',
            uninstall_failed: 'Kan niet uninstall sjabloon..',
            uninstall_success: 'Sjabloon uninstalled successfully.',
            installation_summary: 'Installatieoverzicht',
            filter_catalog: 'Catalogus filteren',
            all_frameworks: 'Alle raamwerken',
            templates_installed_count: '{count} sjabloon(s) succesvol geïnstalleerd',
            all_already_installed: 'Alle sjablonen in deze categorie zijn al geïnstalleerd',
            category_install_partial: '{installed} of {requested} template(s) installed. {refused} restricted register(s) were not installed — adding those requires the regulatory-artifact custody permission.',
            category_install_none: 'No templates were installed. {refused} restricted register(s) in this category require the regulatory-artifact custody permission.',
            form_id: 'Formulier-ID',
            issuer: 'Uitgever',
            catalog_tabs: 'Formuliercatalogus-tabs',
        },
        filler: {
            back_templates: 'Back naar Sjablonen',
            no_sections: 'Geen secties gevonden.',
            draft_created: 'Concept created.',
            draft_saved: 'Concept saved.',
            fetch_template_failed: 'Kan niet fetch sjabloon..',
            save_draft: 'Opslaan Concept',
            save_draft_failed: 'Kan niet save concept..',
            saving: 'Opslaan...',
            select_option: 'Selecteren een optie',
            submit: 'Indienen',
            submit_failed: 'Kan niet submit..',
            submitting: 'Indienen...',
            title: 'Formulier Wizard',
            form_id_label: 'Formulier-ID',
            schema_label: 'Schema',
            form_number_label: 'Form',
            framework_label: 'Raamwerk',
            issuer_label: 'Uitgever',
            source_label: 'Bron',
            market_label: 'Markt',
            section_of: 'Sectie {current} van {total}',
            form_navigation: 'Formuliernavigatie',
            form_progress: 'Formuliervoortgang: Sectie {current} van {total}',
            removed_item: 'Item {index} verwijderd uit {field}',
            added_item: 'Nieuw item toegevoegd aan {field}',
            go_to_previous: 'Ga naar vorige sectie, sectie {current} van {total}',
            go_to_next: 'Ga naar volgende sectie, sectie {current} van {total}',
            risk_matrix_placeholder: '1–5',
        },
        filing: {
            title: 'Indieningsstatus',
            advance: 'Indieningsstatus bijwerken...',
            not_started: 'Niet gestart',
            drafting: 'Opstellen',
            internal_review: 'Interne beoordeling',
            approved_for_submission: 'Goedgekeurd voor indiening',
            submitted_to_authority: 'Ingediend bij autoriteit',
            supplement_requested: 'Aanvulling gevraagd',
            supplement_in_progress: 'Aanvulling in behandeling',
            resubmitted: 'Opnieuw ingediend',
            accepted: 'Geaccepteerd',
            rejected: 'Afgewezen',
            closed: 'Gesloten',
            // Sprint 63.37.0 (Stream J) — the filing milestones the instance page lists.
            milestone_submitted_at: 'Submitted',
            milestone_supplement_requested_at: 'Supplement requested',
            milestone_supplement_submitted_at: 'Supplement submitted',
            milestone_accepted_at: 'Accepted',
            milestone_rejected_at: 'Rejected',
            milestone_closed_at: 'Closed',
        },
        instances: {
            approved_by: 'Goedgekeurd By',
            form_answers: 'Formulier Antwoorden',
            column_header: 'Form',
            no_answers: 'Geen answers gevonden.',
            view_outputs: 'Bekijken Uitvoeren',
            approve: 'Goedkeuren',
            approve_failed: 'Kan niet approve..',
            approving: 'Goedkeuren...',
            detail_title: 'Instantie Detail',
            fetch_failed: 'Kan niet fetch instanties..',
            generate_failed: 'Kan niet generate uitvoer..',
            generate_output: 'Genereren Uitvoer',
            generating: 'Genereren...',
            submitted_at: 'Ingediend At',
            instance_metadata: 'Formulierinstantie-metadata',
            filing_status_steps: 'Archiveringsstatus-stappen',
            current_status: 'huidige status',
            filing_status_update_failed: 'Bijwerken van indieningstatus mislukt',
            // Sprint 63.37.0 (I1) — the export buttons and the sample notice of an instance.
            form_number: 'Form number',
            export_pdf: 'Export PDF',
            export_docx: 'Export Word (DOCX)',
            exporting: 'Exporting {format}…',
            export_done: '{format} exported. The download has started.',
            export_failed: 'The {format} could not be exported.',
            export_not_approved: 'This form cannot be exported yet. Approve it first; a form inside a record is exported once it is completed or finalized.',
            sample_notice: 'This form is sample data. Every page of its exports carries the stamp "{stamp}".',
            // Sprint 63.37.0 (Stream J) — the finalize action and the form actions a parent record's row offers.
            finalize: 'Finalize',
            finalized: 'Form finalized.',
            finalize_failed: 'The form could not be finalized.',
            edit_form: 'Edit',
            view_form: 'View',
            open_instance: 'Open {form}',
            // Sprint 63.40.0 (decision 8) — the page description and the notification dossier.
            detail_description: 'The answers of one form, its exports and, where the form opens one, the notification dossier prepared from it.',
            notice_dossier: {
                title: 'Notification dossier',
                description: 'The platform prepares this dossier from the completed form. It does not file it: your organisation files it with the receiving authority.',
                prepare: 'Prepare the dossier for {form}',
                preparing: 'Preparing the dossier for {form}…',
                prepared: 'The dossier for {form} is prepared.',
                prepare_failed: 'The dossier could not be prepared.',
                prepare_after_final: 'Complete and approve the form to prepare its dossier.',
                load_failed: 'The dossier could not be loaded.',
                reload: 'Load the dossier again',
                loading: 'Loading the dossier…',
                reference: 'Dossier reference',
                receiving_authority: 'Receiving authority',
                receiving_authority_hint: 'As the installed form states it.',
                addressee_label: 'Name of the receiving body',
                addressee_hint: 'The installed form names a class of body, not one body. Enter the body you address this dossier to. Where the completed form already names it on its own addressee line, the dossier starts with that name. A name you enter or clear here is kept and does not change the form. You can leave it empty until it is decided.',
                addressee_recorded: 'Recorded name',
                not_recorded: 'Not recorded',
                statements_title: 'Your statements',
                statements_description: 'Only you, as the applicant, can make these statements. The platform records your answer and does not decide it.',
                statement_basis: 'Basis: {basis}',
                stated_yes: 'This applies',
                stated_no: 'This does not apply',
                stated_unanswered: 'Not answered',
                authority_step_title: "The receiving body's step",
                authority_step_note: "This step is the receiving body's. No deadline is set against your organisation.",
                save: 'Save the dossier',
                saving: 'Saving the dossier…',
                saved: 'The dossier is saved.',
                save_failed: 'The dossier could not be saved.',
                export_title: 'Export the dossier',
                export_hint: "The submission slip prints the receiving body's name as saved on the dossier. Until a name is saved, its addressee line is left blank.",
                export_open: 'Preview the submission slip',
            },
        },
        outputs: {
            back_instance: 'Back naar Instantie',
            no_outputs_msg: 'No uitvoeren have been generated yet.',
            no_outputs_title: 'No Uitvoeren',
            output_name: 'Uitvoer Naam',
            download: 'Downloaden',
            download_failed: 'Kan niet download..',
            fetch_failed: 'Kan niet fetch uitvoeren..',
            format: 'Formaat',
            generated_at: 'Gegenereerd At',
            generated_by: 'Gegenereerd By',
            size: 'Grootte',
            title: 'Formulier Uitvoeren',
            print: 'Afdrukken',
            print_outputs: 'Formulieruitvoer afdrukken',
            printing: 'Formulieruitvoer afdrukken',
            pdf_preview: 'PDF-voorbeeld',
            downloading: '{name} downloaden',
            preview_in_new_window: 'Voorbeeld {name} ({format}) in nieuw venster',
            // Sprint 62.45.0 AC9 — column headers of the form print export (PDF table).
            print_col_field: 'Field',
            print_col_value: 'Value',
            // Sprint 62.45.0 AC9 — metadata lines of the form print export.
            print_meta_instance: 'Instance ID: {id}',
            print_meta_status: 'Status: {status}',
            print_meta_generated: 'Generated: {timestamp}',
            // Sprint 63.37.0 (H2) — the header lines of a printed official form.
            official_header_form_number: 'Form number',
            official_header_instrument: 'Source instrument',
            official_header_issuer: 'Issued by',
            official_header_authority: 'Authority',
            // Sprint 62.58.0 (AC-A4) — the draft-instrument notice an output prints before its title.
            draft_notice_frame: 'Prepared under a draft instrument that is not in force. Nothing in this document is an obligation.',
            draft_notice_framework: '{name} — pack version {version}',
            draft_notice_regenerate: 'Generated before the draft notice was added — generate it again.',
            // Sprint 62.58.0 gate E12.5 pass 2 (P3-5) — an output whose printed notices no longer match the frameworks' state.
            draft_notice_status_changed: "The framework's status has changed since this was generated — generate it again.",
        },
        // Sprint 62.58.0 (AC-A3) — what an instance surface of a draft instrument's template adds.
        draft_notice: {
            instance_note: 'Filling in this form does not make anything in it an obligation.',
        },
        cybersec_domains: {
            scope: 'Reikwijdte en toepasselijkheid',
            risk: 'Risicobeoordeling en controlekartografie',
            policy: 'Organisatorisch beveiligingsbeleid',
            localization: 'Datalokalisatie en lokale aanwezigheid',
            vendor: 'Derden en toeleveringsketen',
            authority: 'Afhandeling van verzoeken van autoriteiten',
            incident: 'Incidentrespons en bewijsvoering',
            training: 'Opleiding en gereedheid',
        },
        market_finalization_domains: {
            data_law: 'Datawet — Governance en Operationele Controles',
            pdpl_2025: 'PDPL 2025 — Privacy-governance en Rechten van Betrokkenen',
            decree_356: 'Besluit 356/2025 — Leveranciers- en Grensoverschrijdende Controles',
            telecom: 'Telecommunicatiewet — Vertrouwelijkheid en Autoriteitsverzoeken',
        },
        nav: {
            filing_case_links: 'Indieningszaakkoppelingen',
            section_routing: 'Sectieroutering',
            submission_packages: 'Inzendingspakketten',
        },
        filing_case_links: {
            title: 'Indieningszaakkoppelingen',
            link_form: 'Formulier koppelen aan indieningszaak',
            filing_case_type: 'Indieningszaaktype',
            form_instance: 'Formulierinstantie',
            select_form_instance: 'Selecteer een formulierinstantie...',
            select_filing_case: 'Selecteer een indieningszaak...',
            no_links_found: 'Geen indieningszaakkoppelingen gevonden.',
            linked: 'Gekoppeld',
            filing_case_id: 'Archiveringscase-ID',
            link_created: 'Archiveringscase-koppeling aangemaakt',
            link_deleted: 'Indieningszaakkoppeling verwijderd',
            aria_delete_link: 'Koppeling {name} verwijderen',
            case_type_dpia_dossier: 'DPIA-dossier',
            case_type_cross_border_filing: 'Grensoverschrijdende indiening',
            case_type_breach_notification: 'Melding van inbreuk',
            no_instances_guidance: 'Nog geen formulierinstanties. Maak er een aan vanuit Formuliersjablonen.',
            no_filings_guidance: 'Nog geen indieningszaken. Maak er een aan vanuit Beoordelingen.',
        },
        section_routing: {
            title: 'Sectieroutering',
            assign_section: 'Sectie toewijzen',
            department: 'Afdeling',
            select_department: 'Selecteer een afdeling...',
            assignee: 'Toegewezen aan',
            select_assignee: 'Selecteer een toegewezene...',
            section: 'Sectie',
            select_section: 'Selecteer een sectie...',
            select_form_instance: 'Selecteer een formulierinstantie...',
            no_assignments_found: 'Geen sectietoewijzingen gevonden.',
            select_instance_prompt: 'Selecteer een formulierinstantie om sectietoewijzingen te bekijken.',
            complete_section: 'Sectie voltooien',
            status_pending: 'In afwachting',
            status_in_progress: 'Bezig',
            status_completed: 'Voltooid',
            status_reviewed: 'Beoordeeld',
            form_instance_id: 'Formulierinstantie-ID',
            assignment_created: 'Sectietoewijzing aangemaakt',
            assignment_deleted: 'Sectietoewijzing verwijderd',
            status_updated: 'Sectiestatus bijgewerkt',
            aria_delete_assignment: 'Toewijzing {name} verwijderen',
            deadline: 'Deadline',
            deadline_help: 'Optioneel. Toewijzingen na deze datum worden als achterstallig weergegeven.',
            overdue: 'Achterstallig',
            days_remaining: 'dagen resterend',
            approval_requested: 'Ingediend ter beoordeling',
            form_approved: 'Formulier goedgekeurd',
            form_rejected: 'Formulier afgewezen',
            no_departments_guidance: 'Geen afdelingen geconfigureerd. Stel in via Organisatie-instellingen.',
            no_sections_guidance: 'Geen secties gedefinieerd in het sjabloon. Zorg ervoor dat het formuliersjabloon secties bevat.',
            select_instance_first: 'Selecteer eerst een formulierinstantie om beschikbare secties te laden.',
        },
        submission_packages: {
            supporting_documents: 'Supporting Documents',
            supporting_documents_help: 'Files filed with this package. Each is stored against the package and listed on its record.',
            no_supporting_documents: 'No supporting documents attached yet.',
            attach_supporting_document: 'Attach a supporting document',
            supporting_document_attached: 'Supporting document attached',
            title: 'Inzendingspakketten',
            assemble_package: 'Inzendingspakket samenstellen',
            dossier: 'Dossier',
            select_dossier: 'Selecteer een dossier...',
            package_label: 'Pakketlabel',
            cover_letter: 'Begeleidende brief',
            checklist: 'Controlelijst',
            evidence_index: 'Bewijsindex',
            completeness: 'Volledigheid',
            incomplete_warning: 'Kan niet samenstellen: formulier heeft onvolledige secties.',
            no_packages_found: 'Geen inzendingspakketten gevonden.',
            version: 'Versie',
            package_assembled: 'Inzendingspakket samengesteld',
            package_deleted: 'Inzendingspakket verwijderd',
            aria_delete_package: 'Pakket {name} verwijderen',
            status_draft: 'Concept',
            status_complete: 'Voltooid',
            status_submitted: 'Ingediend',
            status_accepted: 'Geaccepteerd',
            status_rejected: 'Afgewezen',
            package_updated: 'Inzendingspakket bijgewerkt',
            document_generated: 'Document gegenereerd',
            documents: 'Documenten',
            generate: 'Genereren',
            preview: 'Voorbeeld',
            download: 'Downloaden',
            download_all: 'Alles downloaden',
            no_dossiers_guidance: 'Nog geen dossiers. Maak er een aan vanuit Beoordelingen.',
            package_plan_help: 'Opnemen in pakketplan (automatische documentgeneratie beschikbaar):',
            cover_letter_help: 'Officiële begeleidende brief gericht aan de ontvangende autoriteit',
            checklist_help: 'Nalevingschecklist die verifieert dat aan alle indieningsvereisten is voldaan',
            evidence_index_help: 'Index van ondersteunende bewijsdocumenten waarnaar in de indiening wordt verwezen',
            completeness_skipped: 'Volledigheidscontrole overgeslagen — geen formulierinstantie gekoppeld aan dit dossier.',
            completeness_unavailable: 'Volledigheidscontrole niet beschikbaar — doorgaan met pakketsamenstelling.',
            duplicate_package_error: 'Er bestaat al een indieningspakket voor dit dossier.',
            doc_cover_letter: 'Begeleidende brief',
            doc_checklist: 'Controlelijst',
            doc_evidence_index: 'Bewijsstukkenindex',
            export_csv: 'CSV exporteren',
            export_success: 'Koppelingen naar indieningszaken succesvol geëxporteerd',
            no_assignments_guidance: 'Nog geen sectietoewijzingen. Wijs secties toe aan afdelingen en teamleden om de routering te starten.',
            completeness_hint: 'Completeness is the share of the six dossier fields a package cannot be filed without that the dossier holds: dossier type, framework, authority, submission method, applicant organisation and applicant contact.',
            completeness_not_measured: 'Not measured: assembled before completeness was computed',
            missing_fields_label: 'Missing from the dossier',
            dossier_field_dossier_type: 'Dossier type',
            dossier_field_regulatory_framework_code: 'Framework',
            dossier_field_authority: 'Authority',
            dossier_field_submission_method: 'Submission method',
            dossier_field_applicant_org_name: 'Applicant organisation',
            dossier_field_applicant_contact: 'Applicant contact',
        },
        // Sprint 63.37.0 (D4) — the one official-standing badge (`OfficialFormBadge`).
        official_standing: {
            official: 'Official form',
            internal: 'Internal form',
        },
        template_picker: {
            title: 'Een sjabloon selecteren',
            hint: 'Kies een regelgevend formuliersjabloon of begin met een leeg formulier.',
            market_filter: 'Filteren op markt',
            framework_filter: 'Filteren op kader',
            all_markets: 'Alle markten',
            all_frameworks: 'Alle kaders',
            search_placeholder: 'Sjablonen zoeken...',
            blank_form: 'Leeg / generiek formulier',
            blank_form_desc: 'Aanmaken zonder sjabloon — velden handmatig invullen.',
            no_templates: 'Geen sjablonen gevonden voor dit filter.',
            templates_unreadable: 'Not every template for this form could be loaded, so the list below may be empty or incomplete. That does not mean there are none.',
            templates_read_subject: 'the form templates',
            filters_unreadable: 'The market and framework filters are not shown: their choices could not be loaded.',
            select: 'Selecteren',
            link_error: 'Sjabloonkoppeling mislukt — u kunt een sjabloon later koppelen vanuit de Bewerkenweergave.',
            internal_badge: 'Intern sjabloon',
            legacy_badge: 'Verouderd',
            draft_badge: 'Concept',
            upcoming_badge: 'Aankomend',
            hidden_badge: 'Verborgen',
            show_hidden_toggle: 'Verborgen / verouderde sjablonen tonen',
            show_hidden_toggle_hint: 'Beheerdersweergave: bevat sjablonen met de markering internal_only, hidden, legacy of draft.',
        },
        cover_letter_modal: {
            title: 'Begeleidende brief: voorbeeld en bewerken',
            iframe_title: 'Voorbeeld van begeleidende brief',
            stage_label: 'Fase',
            stage_initial: 'Eerste indiening',
            stage_supplement: 'Aanvullend antwoord',
            stage_resubmission: 'Herindiening',
            subject_label: 'Onderwerp',
            body_label: 'Hoofdalinea',
            notes_label: 'Aanvullende opmerkingen',
            refresh_preview: 'Voorbeeld vernieuwen',
            confirm: 'Bevestigen en opslaan',
            regenerate: 'Opnieuw genereren',
            snapshot_saved: 'Begeleidende brief opgeslagen',
        },
        related_panel: {
            title: 'Gerelateerde regelgevingsformulieren',
            open_form: 'Formulier invullen',
            no_forms: 'Geen gerelateerde formulieren gevonden.',
            hint_vn: 'VN-specifieke regelgevingsformulieren beschikbaar — selecteer een formulier om de nalevingsassistent te openen.',
            internal_badge: 'Intern',
            official_badge: 'Officieel',
            loading: 'Gerelateerde formulieren laden...',
            error: 'Kan gerelateerde formulieren niet laden.',
            form_id: 'Formulier-ID',
            form_name: 'Formuliernaam',
            framework: 'Regelgevend kader',
        },
        view_form: 'Formulier bekijken',
        edit_form: 'Formulier bewerken',
        save_draft: 'Concept opslaan',
        module_settings: 'Module-instellingen',
        validation_errors: 'Corrigeer de validatiefouten',
        custom_name_help: 'Optioneel — standaard de sjabloonnaam als dit leeg is',
        select_placeholder: 'Selecteer...',
        // Gate E12.5 pass 3 (QA nit) — the stored-text fallback of a date-and-time field.
        datetime_stored_text_hint: 'Recorded before this field took a date and time; kept as written.',
        previous_section: 'Vorige',
        next_section: 'Volgende',
        loading: 'Bezig met laden...',
        array_add: 'Toevoegen',
        array_remove: 'Verwijderen',
        array_item_label: '{label} item {index}',
        array_add_item: 'Item toevoegen aan {label}',
        array_remove_item: 'Item {index} verwijderen uit {label}',
        legacy_banner: 'Deze record is gemaakt vóór de geünificeerde formulieren. Sommige gegevens kunnen verschillen tussen de entiteit en de formulierinstantie.',
        field_required: '{label} is verplicht',
        section_announcement: 'Sectie {current} van {total}: {title}',
        sections_nav_label: 'Formulier secties',
        no_sections: 'Er zijn geen sjabloonsecties beschikbaar.',
        section_label_tpl: 'Sectie {index}: {title}',
        field_shown_announcement: '{field} wordt nu weergegeven.',
        field_required_announcement: '{field} is nu verplicht.',
        submit_blocked_hint_one: '1 verplicht veld heeft nog een antwoord nodig.',
        submit_blocked_hint_many: '{count} verplichte velden hebben nog een antwoord nodig.',
        module_settings_auto_applied: 'Modulespecifieke instellingen worden automatisch toegepast.',
        operation_failed: 'Operatie mislukt',
        failed_to_load_form: 'Formulier kon niet worden geladen',
        failed_to_save_form: 'Formulier kon niet worden opgeslagen',
        failed_to_submit_form: 'Formulier kon niet worden verzonden',
        failed_to_load_template: 'Sjabloon kon niet worden geladen',
        failed_to_save_draft: 'Concept kon niet worden opgeslagen',
        failed_to_create: 'Aanmaken is mislukt',
        no_content_generated: 'No content generated',
    },
    pack_catalog: {
        title: 'Pakket Catalogus',
        install: 'Installeren Pakket',
        uninstall: 'Verwijderen Pakket',
        install_batch: 'Installeren Cluster',
        installed_tab: 'Geïnstalleerd',
        catalog_tab: 'Catalogus',
        pack_details: 'Pakket Details',
        frameworks: 'Raamwerken',
        requirements: 'Vereisten',
        artifacts: 'Artefacten',
        language_pack: 'Taal Pakket',
        dependencies: 'Afhankelijkheden',
        no_packs: 'Geen packs gevonden.',
    },
    platform_pages: {
        users: {
            title: 'Gebruikers',
            new: 'Nieuw Gebruiker',
            invite: 'Invite Gebruiker',
            edit: 'Bewerken Gebruiker',
            name: 'Naam',
            email: 'E-mail',
            role: 'Rol',
            status: 'Toestand',
            identity_type: 'Identiteitstype',
            initial_password: 'Initial password',
            require_password_change_action: 'Require password change',
            require_password_change_confirm: '{name} will reach only the change-password step, from their next request, until they choose a new password. Continue?',
            require_password_change_done: 'The user must now change their password.',
            require_password_change_failed: 'The password change could not be required.',
            password_change_pending: 'Password change required',
            initial_password_note: 'Share it through a secure channel. While a password change is required, the user can do nothing but choose a new password.',
            last_login: 'Laatste Login',
            failed_login_attempts: 'Failed Login Attempts',
            never_signed_in: 'Never',
            deactivate: 'Deactiveren',
            reactivate: 'Heractiveren',
            unlock: 'Ontgrendelen',
            reset_password: 'Wachtwoord resetten',
            remove: 'Verwijderen',
            remove_confirm: '"{name}" verwijderen? Deze actie is permanent en de gebruiker verliest alle toegang. Het record wordt bewaard voor auditdoeleinden.',
            user_details_loaded: 'User details loaded',
            password_required: 'Password is required',
            password_min_length: 'Password must be at least 12 characters',
            password_uppercase: 'Password must contain at least one uppercase letter',
            password_lowercase: 'Password must contain at least one lowercase letter',
            password_digit: 'Password must contain at least one digit',
            password_special: 'Password must contain at least one special character',
            deactivate_failed: 'Failed to deactivate user',
            reactivate_failed: 'Failed to reactivate user',
            remove_failed: 'Failed to remove user',
            // fix-sprint-20261017-1500 F4 — UserDetailPage's error toasts read these, not English literals.
            update_failed: 'Failed to update user',
            unlock_failed: 'Failed to unlock user',
            assign_role_failed: 'Failed to assign role',
            revoke_role_failed: 'Failed to revoke role',
            create_failed: 'Failed to create user',
            count_one: '{total} user',
            count_other: '{total} users',
            announce_loaded: 'Users loaded',
        },
        roles: {
            title: 'Rollen',
            new: 'Nieuw Rol',
            edit: 'Bewerken Rol',
            name: 'Rol Naam',
            description: 'Beschrijving',
            permissions: 'Rechten',
            users_count: 'Gebruikers',
            detail: 'Rol Detail',
            all_tiers: 'Alle niveaus',
            tier: 'Niveau',
            type_system: 'Systeem',
            type_custom: 'Aangepast',
            role_name_placeholder: 'bv. regionaal compliance-manager',
            permissions_heading: 'Permissions',
            permissions_selected: '({count} selected)',
            select_all_offered: 'Select all',
            clear_all: 'Clear all',
            grant_bounds_hint: 'You can grant only permissions you hold yourself. Permissions that no system role holds by design, such as AML case access, can be granted only by someone who holds the role-assignment permission in their own right.',
            permission_not_held_summary: 'You do not hold these permissions, so you cannot grant them:',
            permission_not_held_item: 'You do not hold this permission',
            system_role_fixed: 'A system role’s permissions are fixed. Create a custom role to grant other permissions.',
            permission_required: 'At least one permission is required.',
            system_role_no_delete: 'System roles cannot be deleted.',
            create_failed: 'Failed to create role',
            delete_failed: 'Failed to delete role',
            update_failed: 'Failed to update role',
            delete_blocked_active_users: 'This role is still assigned to {count} user(s). Move them to another role, then delete this one.',
            delete_blocked_sso_config: 'Single sign-on still uses this role ({names}), as the default role for new users or in its group mapping. Change that setting first, then delete this role.',
            announce_loaded: 'Roles loaded',
            announce_detail_loaded: 'Role details loaded',
        },
        profile: {
            title: 'My Profiel',
            name: 'Naam',
            display_name: 'Weergavenaam',
            email: 'E-mail',
            phone: 'Telefoon',
            department: 'Afdeling',
            job_title: 'Functietitel',
            language: 'Taal',
            role: 'Rol',
            persona: 'Gedetecteerde persona',
            last_login: 'Laatste Login',
            save: 'Wijzigingen opslaan',
            save_success: 'Profiel succesvol bijgewerkt',
            module_tips_section: 'Moduletips',
            tips_toggle: 'Toon regelgevingstips in modules',
            dismissed_tips: 'Genegeerde tips',
            reset_dismissed: 'Alle genegeerde tips resetten',
            notification_prefs: 'Meldingsvoorkeuren',
            notification_prefs_link: 'Meldingsvoorkeuren beheren →',
            change_password: 'Wijzigen Password',
            current_password: 'Huidig Password',
            new_password: 'Nieuw Password',
            confirm_password: 'Bevestigen Password',
            accessibility: 'Toegankelijkheid',
            high_contrast: 'Hoog contrast modus',
            high_contrast_desc: 'Verhoog het contrast voor betere leesbaarheid. Voldoet aan WCAG 2.1 AAA.',
            passwords_no_match: 'Wachtwoorden komen niet overeen',
            password_changed_logout: 'Wachtwoord gewijzigd. U wordt uitgelogd.',
            failed_update: 'Profiel bijwerken mislukt',
            failed_change_password: 'Wachtwoord wijzigen mislukt',
            none: 'Geen',
            never: 'Nooit',
            password_requirements: 'Min. 12 tekens, hoofdletter, kleine letter, cijfer, speciaal teken',
            announce_updated: 'Profile updated',
            announce_loaded: 'Profile loaded',
        },
        security: {
            mfa_title: 'MFA Instellingen',
            mfa_enable: 'Inschakelen MFA',
            mfa_disable: 'MFA uitschakelen',
            mfa_setup: 'Scannen dit QR code met your authenticator app',
            sso_title: 'SSO Configuratie',
            sso_provider: 'Aanbieder',
            sso_client_id: 'Client-ID',
            sso_redirect: 'Omleidings-URI',
            sso_idp_section: 'Identity Provider',
            sso_idp_entity_id: 'Identity provider entity ID',
            sso_idp_sso_url: 'Identity provider sign-in URL',
            sso_idp_binding: 'Request binding',
            sso_binding_redirect: 'HTTP Redirect',
            sso_binding_post: 'HTTP POST',
            sso_allow_idp_initiated: 'Accept sign-ins started at the identity provider',
            sso_jit_provisioning: 'Create a user on first sign-in',
            sso_default_role: 'Role for users created on first sign-in',
            sso_default_role_none: 'No default — refuse the sign-in',
            sso_assertions_signed: 'Signed assertions are always required.',
            sso_acs_hint: 'Give your identity provider this assertion consumer URL:',
            // ── Sprint 63.6.1 — OpenID Connect ────────────────────────────
            sso_oidc_issuer: 'Issuer',
            sso_oidc_issuer_hint: 'The issuer identifier your provider publishes, exactly as it appears in its discovery document. The platform reads that document from this address and refuses a configuration whose issuer does not match.',
            sso_oidc_redirect_uri: 'Redirect URI to register',
            sso_oidc_redirect_uri_hint: 'Register this exact address with your provider. The platform decides it from the configuration, so it cannot be edited here.',
            sso_oidc_redirect_uri_pending: 'Available once the configuration is saved',
            sso_oidc_client_auth: 'Client authentication',
            sso_oidc_client_auth_basic: 'Client secret, in the Authorization header',
            sso_oidc_client_auth_post: 'Client secret, in the request body',
            sso_oidc_scopes: 'Scopes',
            sso_oidc_scopes_hint: 'Separate with spaces. Leave empty for openid, profile and email. The openid scope is always requested: without it the provider returns no identity token.',
            sso_oidc_scopes_placeholder: 'openid profile email',
            sso_oidc_identity_claim_note: 'Identity comes from the email claim, and the address must be one your provider reports as verified. No other claim is read, and no claim grants a permission.',
            sso_certificates: 'Signing certificates',
            sso_certificates_empty: 'No signing certificate. A sign-in cannot be verified until one is added.',
            sso_certificate_pem: 'Certificate (PEM)',
            sso_certificate_add: 'Add certificate',
            sso_certificate_expires: 'Expires',
            sso_certificate_remove: 'Remove certificate',
            sso_certificate_limit: 'Two certificates are held at most, so a rotation needs no downtime.',
            sso_verify: 'Verify configuration',
            sso_verify_hint: 'Runs a full sign-in against this configuration and reports every check. It creates no session and no user, so you stay signed in.',
            sso_verify_report: 'Verification report',
            sso_verify_passed: 'Every check passed.',
            sso_verify_failed: 'Some checks failed.',
            sso_check_passed: 'Passed',
            sso_check_failed: 'Failed',
            sso_enforcement: 'SSO-only sign-in',
            sso_enforcement_off: 'Off — everyone can sign in with a password.',
            sso_enforcement_on: 'On — only exempt administrators can sign in with a password.',
            sso_enforcement_activate: 'Turn on SSO-only sign-in',
            sso_enforcement_deactivate: 'Turn off SSO-only sign-in',
            sso_enforcement_exempt: 'Administrators who keep password sign-in',
            sso_enforcement_before: 'Before you can turn this on:',
            sso_enforcement_need_verify: 'Verify this configuration and complete a sign-in through it, within the last 15 minutes.',
            sso_enforcement_need_exempt: 'Name at least one active administrator who has a password.',
            sso_enforcement_recovery: 'If the identity provider stops working, an exempt administrator can sign in with a password and turn this off; deactivating the configuration also restores password sign-in for everyone.',
            sso_mfa_heading: "Accept this provider's MFA",
            sso_mfa_intro: "By default the platform asks for its own MFA after every sign-in through this provider. Turn this on only if you trust this provider's MFA to stand in for the platform's.",
            sso_mfa_accept: "Accept this provider's MFA instead of the platform's",
            sso_mfa_state_off: "Off. The platform's own MFA applies to every sign-in through this provider.",
            sso_mfa_state_on: "On. A sign-in counts as MFA-verified only when the values the provider reports match the ones you list below. Every other sign-in is held at the platform's own MFA.",
            sso_mfa_no_meaning: "The platform attaches no meaning to any value. Only values you enter here, matched exactly and with the same capitalisation, count. A value you have not listed, a sign-in older than the maximum age and a missing value all fall back to the platform's own MFA.",
            sso_mfa_amr: 'Accepted authentication method values (amr)',
            sso_mfa_amr_hint: 'The exact amr values your provider reports for an MFA sign-in, separated by spaces or commas. OpenID Connect only.',
            sso_mfa_acr_oidc: 'Accepted authentication context values (acr)',
            sso_mfa_acr_saml: 'Accepted authentication context values (AuthnContextClassRef)',
            sso_mfa_acr_hint: 'The exact values your provider reports for an MFA sign-in, separated by spaces or commas.',
            sso_mfa_lists_rule: 'List at least one value, or nothing will count. When you list more than one kind of value, a sign-in must match every kind.',
            sso_mfa_empty_warning: 'Nothing is listed, so no sign-in through this provider will count as MFA-verified.',
            sso_mfa_max_age: 'Maximum authentication age (seconds)',
            sso_mfa_max_age_hint: 'A sign-in at the provider older than this does not count. Leave empty for no age check.',
            sso_mfa_max_age_invalid: 'Enter a whole number of seconds greater than zero, or leave the field empty.',
            sso_revoke_sessions: "End this provider's sessions",
            sso_revoke_confirm_title: "End this provider's sessions?",
            sso_revoke_confirm_message: 'Everyone currently signed in through {name} is signed out and must sign in again. Sessions that arrived by password or through another provider are not ended. Federated sessions created before sessions recorded which configuration they came from cannot be attributed to one provider, so they are ended as well.',
            sso_revoke_confirm_button: 'End sessions',
            sso_revoke_working: 'Ending sessions…',
            sso_revoke_result_heading: 'Sessions ended',
            sso_revoke_result_for: 'Provider: {name}',
            sso_revoke_count_total: 'Sessions ended',
            sso_revoke_count_legacy: 'Of these, sessions that could not be attributed to a configuration',
            sso_revoke_legacy_note: "These sessions pre-date the field that records which configuration a session came from, so they are ended whichever provider's sessions you end.",
            sso_revoke_announce: 'Sessions ended: {count}. Of these, {legacy} could not be attributed to a configuration because they pre-date the field that records it.',
            sso_revoke_failed: "Failed to end this provider's sessions.",
            sso_mfa_accepted_badge: 'Provider MFA accepted',
            api_keys_title: 'API-sleutels',
            api_keys_new: 'Nieuw API Sleutel',
            api_keys_name: 'Sleutel Naam',
            api_keys_last_used: 'Laatste Used',
            api_keys_expires: 'Verloopt',
            api_keys_revoke: 'Intrekken',
            service_accounts_title: 'Serviceaccounts',
            ip_allowlist_title: 'IP-toelatingslijst',
            ip_add: 'Toevoegen IP Adres',
            ip_address: 'IP Adres',
            ip_description: 'Beschrijving',
            delegations_title: 'Delegaties',
            delegations_new: 'Nieuw Delegation',
            delegations_from: 'Van Gebruiker',
            delegations_to: 'Naar Gebruiker',
            delegations_expires: 'Verloopt',
            break_glass_title: 'Break-Glass Toegang',
            break_glass_requester_only: 'You can request break-glass access here and follow your own requests below. Other people\u2019s requests, active sessions and history are shown to approvers.',
            break_glass_my_requests: 'Your requests',
            break_glass_request: 'Verzoek Toegang',
            break_glass_reason: 'Reden voor Toegang',
            break_glass_duration: 'Duur',
            session_settings_title: 'Sessie Instellingen',
            eviction_policy_section: 'Uitzettingsbeleid',
            session_timeout: 'Sessie Time-out (minutes)',
            session_max_concurrent: 'Max Gelijktijdig Sessies',
            session_inactivity_warning: 'Inactivity Warning (minutes)',
            session_timeout_hint: 'Sessions will expire after this duration of total time. Valid range: 5-480 minutes.',
            session_max_concurrent_hint: 'Maximum number of active sessions per user. Valid range: 1-20.',
            session_inactivity_warning_hint: 'Show a warning after this many minutes of inactivity. Must be less than the session timeout.',
            session_timeout_range_error: 'Session timeout must be between 5 and 480 minutes.',
            session_max_concurrent_range_error: 'Max concurrent sessions must be between 1 and 20.',
            session_inactivity_warning_range_error: 'Inactivity warning must be between 1 minute and less than the session timeout.',
            session_settings_load_error: 'Failed to load session settings.',
            session_settings_save_error: 'Failed to save session settings.',
            eviction_policy_hint: 'What happens when a user exceeds the max concurrent sessions limit.',
            eviction_policy_reject: 'Reject New Login - Prevent new login until an existing session is terminated',
            eviction_policy_evict_oldest: 'Evict Oldest Session - Automatically terminate the oldest session to allow new login',
            active_sessions_title: 'Actief Sessies',
            active_sessions_device: 'Apparaat',
            active_sessions_ip: 'IP Adres',
            active_sessions_started: 'Gestart',
            active_sessions_revoke: 'Intrekken Sessie',
            active_sessions_confirm_terminate: 'Weet u zeker dat u deze sessie wilt beëindigen?',
            active_sessions_confirm_terminate_current: 'Dit is uw huidige sessie. Als u de overeenkomst beëindigt, wordt u onmiddellijk afgemeld. Doorgaan?',
            active_sessions_confirm_terminate_all: 'Weet u zeker dat u alle andere sessies wilt beëindigen? Alleen uw huidige sessie blijft actief.',
            active_sessions_terminated: 'Sessie succesvol beëindigd.',
            active_sessions_all_terminated: 'Alle andere sessies zijn beëindigd.',
            active_sessions_terminate_all: 'Beëindig alle andere sessies',
            active_sessions_last_activity: 'Laatste activiteit',
            active_sessions_current: 'Huidige sessie',
            active_sessions_revoke_selected: 'Geselecteerde intrekken',
            active_sessions_confirm_terminate_selected: 'Weet u zeker dat u de geselecteerde sessies wilt beëindigen?',
            active_sessions_selected_terminated: 'Geselecteerde sessies succesvol beëindigd.',
            ip_allowlist_desc: 'Beheer toegestane IP-adressen voor toegang tot API.',
            ip_no_entries: 'Geen IP-adressen geconfigureerd.',
            session_settings_desc: 'Configureer sessietime-out en gelijktijdige sessielimieten.',
            session_settings_save_policy: 'Sessiebeleid opslaan',
            session_settings_saved: 'Sessie-instellingen zijn succesvol bijgewerkt.',
            session_settings_idle_timeout: 'Time-out bij inactiviteit (minuten)',
            session_settings_absolute_timeout: 'Absolute time-out (minuten)',
            session_settings_help_idle: 'De sessie vervalt na deze periode van inactiviteit.',
            session_settings_help_absolute: 'De sessie vervalt na deze totale duur, ongeacht de activiteit.',
            session_settings_help_max: 'Maximaal aantal gelijktijdige sessies per gebruiker.',
            sso_note: 'Wijzigingen in de SSO-configuratie vereisen mogelijk dat gebruikers zich opnieuw moeten verifiëren.',
            service_accounts_warning: 'Deze API-sleutel wordt slechts één keer weergegeven. Kopieer het nu en bewaar het veilig.',
            service_accounts_new: 'Nieuw serviceaccount',
            service_accounts_name: 'Accountnaam',
            service_accounts_role: 'Rol',
            service_accounts_created: 'Gemaakt',
            service_accounts_desc: 'Beheer machine-to-machine serviceaccounts voor API-integraties.',
            break_glass_warning: 'De toegang tot breekglas wordt gecontroleerd en geregistreerd. Alleen gebruiken voor echte noodgevallen.',
            break_glass_min_chars: '{count} / {min} minimum aantal tekens',
            break_glass_permission_count: '{count} toestemming(en) geselecteerd',
            break_glass_submitted: 'Breekglasaanvraag ingediend. In afwachting van goedkeuring.',
            break_glass_approved: 'Aanvraag breekglas goedgekeurd.',
            break_glass_denied: 'Verzoek om glasbreuk afgewezen.',
            break_glass_confirm_activate: 'Approve this break-glass request? The approval is recorded with the permissions requested and the time it expires. It does not itself change what the requester can do.',
            break_glass_confirm_title: 'Bevestig activering',
            break_glass_pending: 'In behandeling zijnde verzoeken',
            break_glass_active_sessions: 'Approved Break-Glass Requests',
            break_glass_time_remaining: 'Resterende tijd',
            break_glass_expired: 'Verlopen',
            break_glass_approved_denied_by: 'Goedgekeurd/geweigerd door',
            delegations_desc: 'Beheer tijdelijke machtigingsdelegaties tussen gebruikers.',
            delegations_my: 'Mijn delegaties',
            delegations_to_me: 'Aan mij gedelegeerd',
            delegations_scope_hint: 'An “all my permissions” delegation carries what you hold today. It does not change if your role changes later.',
            delegations_window_hint: 'The delegate holds these permissions from 00:00 on the start day until 23:59 on the end day, in your time zone, or until you revoke the delegation, checked on every request. Each use is recorded in the security audit trail.',
            delegations_start_in_past: 'The start cannot be before today.',
            delegations_end_before_start: 'The end must be after the start.',
            delegations_legacy_all: 'Grants nothing: made before delegations recorded what they carry. Revoke it and create a new one.',
            delegations_not_delegable_hint: 'Permissions held only through a named custom role or by no organisation, and single sign-on and group-user administration, cannot be delegated and are not listed.',
            delegations_reason: 'Reason',
            delegations_start: 'Start',
            delegations_end: 'End',
            delegations_scope_all: 'Alle machtigingen',
            delegations_scope_specific: 'Specifieke machtigingen',
            delegations_created: 'Delegatie is succesvol aangemaakt.',
            delegations_revoked: 'Delegatie ingetrokken.',
            delegations_confirm_revoke: 'Weet u zeker dat u deze delegatie wilt intrekken?',
            delegations_confirm_revoke_title: 'Bevestig intrekken',
            delegations_permission_count: '{count} toestemming(en)',
            delegations_no_longer_delegable: 'No longer delegable, so not granted: {permissions}',
            api_keys_status: 'Status',
            api_keys_prefix: 'Sleutelvoorvoegsel',
            api_keys_permissions_count: 'Machtigingen',
            api_keys_ip_count: 'IP-beperkingen',
            api_keys_created: 'Gemaakt',
            api_keys_confirm_revoke_title: 'Bevestig intrekken',
            api_keys_confirm_revoke_message: 'Weet u zeker dat u de API-sleutel "{name}" wilt intrekken? Deze actie kan niet ongedaan worden gemaakt.',
            api_keys_confirm_rotate_title: 'Bevestig rotatie',
            api_keys_confirm_rotate_message: 'Rotate the API key "{name}"? A new key is issued now; the current key keeps working for 24 hours so you can replace it, then stops. If the key may be compromised, revoke it instead.',
            api_keys_created_success: 'API sleutel is succesvol aangemaakt.',
            api_keys_revoked_success: 'API sleutel "{name}" is ingetrokken.',
            api_keys_rotated_success: 'API sleutel "{name}" is gedraaid. Kopieer de nieuwe sleutel hieronder.',
            api_keys_load_error: 'Kan de sleutels van API niet laden.',
            api_keys_create_error: 'Kan de sleutel API niet maken.',
            api_keys_revoke_error: 'Kan de sleutel API niet intrekken.',
            api_keys_rotate_error: 'Kan de API-sleutel niet roteren.',
            api_keys_allowed_origins: 'Allowed origins',
            api_keys_allowed_origins_hint: 'One origin per line: scheme, host and optional port, e.g. https://app.example.com. When set, a request must carry an Origin header that matches one entry exactly, or it is refused. Leave empty for a server-to-server key.',
            api_keys_rate_limit: 'Requests per minute',
            api_keys_rate_limit_hint: 'Leave empty for no limit. Over the limit, a request is refused with 429 and a Retry-After header until the next minute starts.',
            api_keys_rate_limit_invalid: 'Requests per minute must be a whole number from 1 to 10,000.',
            api_keys_status_at_creation: 'Status at creation',
            api_keys_status_option_active: 'Active: the key works as soon as it is created',
            api_keys_status_option_inactive: 'Inactive: the key authenticates nothing until you activate it',
            api_keys_col_origins: 'Origins',
            api_keys_col_rate_limit: 'Limit per minute',
            api_keys_no_limit: 'No limit',
            api_keys_edit_title: 'Edit API key',
            api_keys_save: 'Save changes',
            api_keys_updated_success: 'API key "{name}" has been updated.',
            api_keys_update_error: 'Failed to update API key.',
            api_keys_edit_action: 'Edit key {name}',
            api_keys_activate_action: 'Activate key {name}',
            api_keys_pause_action: 'Pause key {name}',
            api_keys_rotate_action: 'Rotate key {name}',
            api_keys_revoke_action: 'Revoke key {name}',
            api_keys_copy_raw_key: 'Copy API key to clipboard',
            api_keys_activated_success: 'API key "{name}" is active.',
            api_keys_paused_success: 'API key "{name}" is paused. It authenticates nothing until it is activated.',
            api_keys_status_error: 'Failed to change the API key status.',
            api_keys_status_rotated: 'Rotated',
            api_keys_rotated_grace_until: 'Still works until {time}. Revoke it to stop it now.',
            service_accounts_load_error: 'Failed to load service accounts.',
            service_accounts_created_success: 'Service account created successfully.',
            service_accounts_create_error: 'Failed to create service account.',
            service_accounts_confirm_deactivate_title: 'Confirm Deactivation',
            service_accounts_confirm_deactivate_message: 'Are you sure you want to deactivate the service account "{name}"? All associated API keys will stop working.',
            service_accounts_deactivated_success: 'Service account "{name}" has been deactivated.',
            service_accounts_deactivate_error: 'Failed to deactivate service account.',
            service_accounts_deactivate_action: 'Deactivate service account {name}',
            service_accounts_table_label: 'Service accounts',
            service_accounts_name_placeholder: 'e.g., Data Sync Bot',
            service_accounts_purpose_placeholder: 'e.g., Automated data synchronization',
            permission_picker_selected_count: '{count} selected',
            permission_picker_result_count: '{count} of {total} permissions shown',
            permission_picker_selected_heading: 'Selected permissions',
            permission_picker_remove: 'Remove {permission}',
            break_glass_duration_15: '15 minuten',
            break_glass_duration_30: '30 minuten',
            break_glass_duration_60: '60 minuten (1u)',
            break_glass_duration_120: '120 minuten (2u)',
            break_glass_duration_240: '240 minuten (4 uur)',
            sod_rules_title: 'Regels voor functiescheiding',
            sod_violations_report: 'Overtredingenrapport',
            sod_create_rule: 'Regel maken',
            sod_col_name: 'Naam',
            sod_col_description: 'Beschrijving',
            sod_col_conflict_count: 'Conflicttelling',
            sod_col_severity: 'Ernst',
            sod_col_status: 'Status',
            sod_col_actions: 'Acties',
            sod_no_rules: 'Geen SoD-regels gevonden',
            sod_severity_warning: 'Waarschuwing',
            sod_severity_blocking: 'Blokkeren',
            sod_active: 'Actief',
            sod_inactive: 'Inactief',
            sod_edit: 'Bewerken',
            sod_exceptions: 'Uitzonderingen',
            sod_deactivate: 'Deactiveren',
            sod_confirm_deactivate_title: 'Bevestig de deactivering',
            sod_confirm_deactivate_message: 'Regel "{name}" deactiveren? Het zal niet langer overtredingen signaleren.',
            sod_deactivate_error: 'Kan de regel niet deactiveren',
            sod_rule_count: '{count} rules',
            active_sessions_loaded: 'Active sessions loaded',
            api_keys_loaded: 'API keys loaded',
            backup_codes_regenerated: 'Backup codes regenerated successfully.',
            break_glass_loaded: 'Break glass data loaded',
            delegations_loaded: 'Delegations loaded',
            ip_added: 'IP address added.',
            ip_allowlist_loaded: 'IP allowlist loaded',
            ip_removed: 'IP address removed.',
            mfa_disabled: 'MFA has been disabled.',
            mfa_status_loaded: 'MFA status loaded',
            service_accounts_loaded: 'Service accounts loaded',
            session_settings_loaded: 'Session settings loaded',
            // ── Sprint 63.7.0: organisation-level MFA enforcement + IP allowlist enforcement ──
            mfa_org_enforcement_heading: 'Organisation enforcement',
            // fix-sprint-20261017-1500 F6 — a failed policy read is stated, with a retry.
            mfa_org_policy_read_failed: "The organisation's MFA policy could not be read, so its current level is not shown.",
            mfa_org_policy_retry_subject: "the organisation's MFA policy",
            mfa_org_enforcement_intro: 'Choose whether this organisation requires a second factor. The default is Optional, which is what the platform has always done: people who set up an authenticator are asked for a code, and people who do not are not. Raising the level never locks anyone out — a person who is required to have a second factor and does not yet have one is asked to set one up at their next sign-in, and can complete that themselves.',
            mfa_level_optional: 'Optional',
            mfa_level_optional_desc: 'Each person decides. Anyone who has set up an authenticator is asked for a code; anyone who has not signs in with their password alone.',
            mfa_level_admin_required: 'Required for administrators',
            mfa_level_admin_required_desc: "Anyone whose role can change this organisation's security settings must have a second factor. This is decided by what the role can do, not by what the role is called, so your own custom roles are covered too.",
            mfa_level_all_required: 'Required for everyone',
            mfa_level_all_required_desc: 'Everyone in this organisation must have a second factor.',
            mfa_enforcement_current: 'Current',
            mfa_enforcement_apply: 'Use this level',
            mfa_unenrolled_count: '{count} people would be asked to set up an authenticator at their next sign-in.',
            mfa_enforcement_confirm_title: 'Change the enforcement level?',
            mfa_enforcement_confirm_message: 'This sets the level to {level}. {count} people will be asked to set up an authenticator the next time they sign in. They can complete it themselves and nobody is locked out.',
            mfa_enforcement_saved: 'Enforcement level updated.',
            mfa_enforcement_save_failed: 'The enforcement level could not be changed.',
            mfa_enforcement_no_lockout: 'Raising the level cannot lock this organisation out. Someone who is required to have a second factor and does not yet have one still signs in; they are taken to a setup screen and can finish it without help.',
            mfa_admin_heading: 'Two-factor authentication',
            mfa_admin_enrolled: 'Set up',
            mfa_admin_not_enrolled: 'Not set up',
            mfa_admin_backup_codes_remaining: '{count} unused backup codes',
            mfa_admin_reason_label: 'Reason',
            mfa_admin_reason_hint: 'At least 20 characters. This is recorded in the audit trail and is the only lasting record of why.',
            mfa_admin_reset: 'Reset',
            mfa_admin_disable: 'Turn off',
            mfa_admin_revoke_codes: 'Revoke backup codes',
            mfa_admin_action_done: 'Done. This person has been notified.',
            mfa_admin_action_failed: 'That could not be completed.',
            // ── fix-sprint-20260921-2110: EN placeholders pending Phase 25 (R-57) ──
            mfa_page_intro: 'Set up and manage the second verification step for your own account. If your organisation requires one, the level it has chosen is shown below.',
            mfa_not_enrolled_intro: 'Protect your account with a second verification step.',
            mfa_scan_instruction: 'Scan the QR code with your authenticator app, then enter the 6-digit code it shows to confirm.',
            mfa_qr_alt: 'QR code holding the setup key for your authenticator app. If you cannot scan it, the same key is written out in the field below.',
            mfa_secret_manual_label: 'Or enter this setup key manually',
            mfa_confirm_code_label: 'Enter the 6-digit code from your authenticator app',
            mfa_enrolled_description: 'This account is protected by a second verification step.',
            mfa_enabled_since: 'In use since {date}.',
            mfa_status_load_failed: 'Your two-factor status could not be loaded.',
            mfa_enroll_failed: 'Setting up a second factor could not be started.',
            mfa_invalid_code: 'That code was not accepted. Check your authenticator app and enter the code it shows now.',
            mfa_regenerate_failed: 'New backup codes could not be generated.',
            mfa_disable_confirm_title: 'Turn off two-factor authentication?',
            mfa_disable_confirm_message: 'Your account will be protected by its password alone, which makes it easier for someone else to sign in as you. You can set up a second factor again at any time.',
            mfa_disable_failed: 'Two-factor authentication could not be turned off.',
            mfa_backup_codes_heading: 'Your backup codes',
            mfa_backup_codes_save_now: 'These codes are shown once and cannot be shown again. Save them somewhere safe now — each one signs you in if you lose your authenticator, and each works only once.',
            mfa_backup_codes_file_heading: 'AesirX ComplianceOne — MFA backup codes',
            mfa_backup_codes_file_notice: 'Store these codes in a safe place. Each code can only be used once.',
            mfa_backup_codes_file_generated: 'Generated: {date}',
            mfa_enforcement_apply_named: 'Use this level: {level}',
            mfa_admin_no_read_notice: 'You can see whether this person has a second factor and you can take it away. You cannot see what it is, and you cannot set one up on their behalf.',
            ip_enforcement_heading: 'Enforcement',
            ip_enforcement_dry_run: 'This list is not refusing anything. It is being checked against every signed-in request and the results are recorded, so you can see who would be refused before anyone is.',
            ip_enforcement_active: 'Enforcing since {date}.',
            ip_enforcement_would_block: '{count} recorded occasions in the last 30 days where a signed-in request came from an address this list does not cover.',
            ip_enforcement_would_block_zero: 'No signed-in request from an address outside this list has been recorded in the last 30 days. That can also mean nobody has signed in, so it is a reason to look rather than an all-clear.',
            ip_enforcement_confirm_button: 'Start enforcing',
            ip_enforcement_withdraw_button: 'Stop enforcing',
            ip_enforcement_confirm_title: 'Start refusing requests from outside this list?',
            ip_enforcement_confirm_message: 'From now on, a signed-in request from an address this list does not cover is refused.',
            ip_enforcement_confirmed: 'This list is now being enforced.',
            ip_enforcement_withdrawn: 'This list is no longer being enforced.',
            ip_enforcement_sessions_note: 'Turning this on does not sign anybody out. Each request is judged as it arrives, so a session from an address that is no longer covered simply stops working. To end sessions now, use Active Sessions.',
            ip_enforcement_edit_withdraws: 'Editing the ranges stops enforcement until you confirm again, because the recorded figures you confirmed against were about the old list.',
            ip_configure: 'Configure',
            ip_configure_title: 'Configure IP Allowlist',
            ip_configure_enabled_hint: 'When enabled, only requests from listed IP addresses will be allowed.',
            ip_enforcement_mode_label: 'Enforcement Mode',
            ip_enforcement_mode_all_access: 'All Access — restrict both UI and API',
            ip_enforcement_mode_api_keys_only: 'API Keys Only — only restrict API key access',
            ip_address_placeholder: '203.0.113.1 or 192.168.1.0/24',
            ip_description_placeholder: 'e.g., Office network',
            ip_entries_table_label: 'IP allowlist entries',
            ip_remove_action: 'Remove {cidr} from allowlist',
            ip_remove_confirm_title: 'Confirm Remove',
            ip_remove_confirm_message: 'Are you sure you want to remove {cidr} from the allowlist?',
            ip_load_failed: 'Failed to load IP allowlist settings.',
            ip_cidr_invalid: 'Invalid format. Enter a single IP (203.0.113.1) or CIDR range (192.168.1.0/24)',
            ip_cidr_duplicate: 'This CIDR is already in the allowlist.',
            ip_add_failed: 'Failed to add IP address.',
            ip_remove_failed: 'Failed to remove IP address.',
            ip_save_failed: 'Failed to save settings.',
            ip_enforcement_confirm_caller_covered: 'You are connecting from {ip}, and this list covers it, so you will not lose access to this screen.',
            ip_enforcement_confirm_caller_not_covered: 'You are connecting from {ip}, which this list does not cover. Confirming is refused rather than locking you out, so add this address to the list first.',
            ip_enforcement_confirm_caller_unknown: 'Whether this list covers the address you are connecting from could not be established on this screen. It is checked again when you confirm, and a confirmation that would lock you out is refused.',
            ip_enforcement_readiness_unreadable: 'The 30-day record of what this list would have refused could not be read, so this screen cannot show you the figure.',
            sso_metadata_url: 'Metadata / discovery URL',
            sso_metadata_url_hint: 'The OpenID Connect discovery or metadata document published by the identity provider. This is not the redirect URI: the redirect URI to register with the provider is shown above, is derived by the platform, and cannot be typed here.',
            sso_client_secret: 'Client Secret',
            sso_issuer_placeholder: 'https://login.example.com',
            sso_sp_entity_id_placeholder: 'https://grc.example.com/saml/metadata',
            sso_configs_table_label: 'SSO configurations',
            sso_configs_unreadable: 'The SSO configurations could not be read, so this screen cannot say whether this organisation has any.',
            sso_configs_retry: 'Reload',
            sso_configs_retry_busy: 'Reloading…',
            sso_configs_retry_subject: 'the SSO configurations',
            sso_load_failed: 'Failed to load SSO configurations.',
            sso_report_read_failed: 'Failed to read the verification report.',
            sso_save_failed: 'Failed to save SSO configuration.',
            sso_delete_failed: 'Failed to delete SSO configuration.',
            sso_certificate_add_failed: 'Failed to add the certificate.',
            sso_verify_start_failed: 'Failed to start the verification run.',
            sso_enforcement_on_failed: 'Failed to turn on SSO-only sign-in.',
            sso_enforcement_off_failed: 'Failed to turn off SSO-only sign-in.',
            sso_updated: 'SSO configuration updated successfully.',
            sso_created: 'SSO configuration created successfully.',
            sso_delete_confirm_title: 'Confirm Delete',
            sso_delete_confirm_message: 'Are you sure you want to delete the SSO configuration "{name}"?',
            sso_deleted: 'SSO configuration "{name}" deleted.',
            sso_delete_end_sessions: "Also end this provider's sessions",
            sso_delete_end_sessions_hint: 'Deleting a configuration stops new sign-ins through it but does not end the sessions it has already started. Leave this ticked to end them now. If you untick it, you can end them later under "Deleted configurations".',
            sso_delete_sessions_not_ended: 'The configuration was deleted, but its sessions could not be ended. End them under "Deleted configurations".',
            sso_deleted_heading: 'Deleted configurations',
            sso_deleted_intro: 'These configurations no longer accept sign-ins. Deleting a configuration does not end the sessions it started; end them here with "End this provider\'s sessions".',
            sso_deleted_table_label: 'Deleted SSO configurations',
            sso_deleted_unreadable: 'The deleted SSO configurations could not be read.',
            sso_deleted_retry_subject: 'the deleted SSO configurations',
            sso_configs_loaded: 'SSO configurations loaded',
            active_sessions_select_aria: 'Select session from {ip}',
            active_sessions_terminate_aria: 'Terminate session from {ip}',
            active_sessions_terminate_current_aria: 'Terminate session from {ip} (current session)',
            active_sessions_unknown_ip: 'unknown IP',
            active_sessions_select_all_aria: 'Select all sessions',
        },
        access_reviews: {
            title: 'Toegang Beoordelingen',
            campaigns: 'Beoordelen Campagnes',
            new_campaign: 'Nieuw Campagne',
            campaign_name: 'Campagne Naam',
            scope: 'Bereik',
            reviewers: 'Beoordelaars',
            due_date: 'Due Datum',
            progress: 'Voortgang',
            my_reviews: 'My Beoordelingen',
            approve: 'Goedkeuren',
            revoke: 'Intrekken',
            analytics: 'Rol Analytics',
            analytics_title: 'Rol Analytics',
            permission_usage: 'Toestemming Usage',
            role_coverage: 'Rol Dekking',
            campaign_detail: 'Campagnedetails',
            campaign_description: 'Beschrijving',
            campaign_status: 'Status',
            campaign_deadline: 'Deadline',
            campaign_scope_type: 'Bereiktype',
            campaign_completion: 'Voltooiing',
            campaign_total_items: 'Totaal aantal artikelen',
            campaign_pending_items: 'In behandeling',
            campaign_confirmed_items: 'Bevestigd',
            campaign_revoked_items: 'Ingetrokken',
            campaign_escalated_items: 'Geëscaleerd',
            campaign_launch: 'Campagne lanceren',
            campaign_cancel: 'Campagne annuleren',
            campaign_confirm_launch: 'Weet u zeker dat u deze campagne wilt lanceren?',
            campaign_confirm_cancel: 'Weet u zeker dat u deze campagne wilt annuleren?',
            campaign_launched: 'Campagne succesvol gelanceerd.',
            campaign_cancelled: 'Campagne geannuleerd.',
            review_items: 'Artikelen beoordelen',
            review_item_user: 'Gebruiker',
            review_item_role: 'Rol',
            review_item_reviewer: 'Recensent',
            review_item_decision: 'Besluit',
            review_item_justify: 'Rechtvaardiging',
            review_item_confirm: 'Bevestig',
            review_item_revoke: 'Intrekken',
            review_item_escalate: 'Escaleren',
            all_decisions: 'Alle beslissingen',
            bulk_action: 'Bulkactie',
            bulk_confirm: 'Bulkbevestiging',
            bulk_revoke: 'Bulkintrekking',
            selected_count: '{count} geselecteerd',
            justify_required: 'Er is een rechtvaardiging vereist',
            scope_all_users: 'Alle gebruikers',
            scope_role_owner: 'Rol Eigenaar',
            scope_department: 'Afdeling',
            back_to_campaigns: 'Terug naar Campagnes',
            recommendations: 'Aanbevelingen',
            refreshing: 'Verfrissend...',
            decide: 'Beslis',
            decision_by: 'Beslissing door',
            loading_items: 'Artikelen laden...',
            loading_review_items: 'Review-items laden...',
            campaign_not_found: 'Campagne niet gevonden',
            loading_campaign: 'Campagnedetails laden...',
            campaign_completed: 'Campagne succesvol afgerond.',
            confirm_cancel_title: 'Bevestig Annuleren',
            justify_bulk_confirm: 'Rechtvaardiging voor bulkbevestiging:',
            justify_bulk_revoke: 'Reden voor bulkintrekking:',
            items_selected: '{count} artikel(en) geselecteerd',
            justify_bulk_required: 'Voor bulkacties is een rechtvaardiging vereist',
            create_campaign_title: 'Maak een toegangsbeoordelingscampagne',
            name_required: 'Naam is vereist',
            scope_type_required: 'Scopetype is vereist',
            reviewer_strategy_required: 'Reviewerstrategie is vereist',
            deadline_required: 'Deadline is vereist',
            reviewer_id_required: 'Reviewer-ID is vereist voor een specifieke gebruikersstrategie',
            invalid_json: 'Ongeldige JSON',
            scope_filter_label: 'Bereikfilter (JSON)',
            reviewer_strategy_label: 'Recensent Strategie',
            reviewer_id_label: 'Recensent-ID',
            deadline_label: 'Deadline',
            create_campaign_btn: 'Campagne maken',
            scope_type_label: 'Bereiktype',
            scope_by_role: 'Per rol',
            scope_by_department: 'Per afdeling',
            scope_custom: 'Aangepast',
            reviewer_manager: 'Beheerder',
            reviewer_specific_user: 'Specifieke gebruiker',
            enter_json_filter: 'Voer het JSON-filter in',
            hint_label: 'Tip:',
            role_usage: 'Rolgebruik',
            over_privileged_users: 'Overbevoorrechte gebruikers',
            role_name_col: 'Rolnaam',
            assigned_users: 'Toegewezen gebruikers',
            active_users_30d: 'Actieve gebruikers (30d)',
            total_perms: 'Totaal permanenten',
            used_perms: 'Gebruikte permanenten',
            unused_perms: 'Ongebruikte permanenten',
            no_data_available: 'Geen gegevens beschikbaar',
            roles_col: 'Rollen',
            total_col: 'Totaal',
            used_col: 'Gebruikt',
            unused_pct: 'Ongebruikt %',
            current_count: 'Huidige telling',
            recommended_count: 'Aanbevolen aantal',
            rationale: 'Reden',
            loading_analytics: 'Analyses laden...',
            loading_role_analytics: 'Rolanalysegegevens laden...',
            computed_at: 'Berekend op:',
            form_errors: 'Formulier bevat {count} fout(en). Corrigeer ze alstublieft.',
            scope_filter_invalid_json: 'Bereikfilter bevat ongeldige JSON',
            scope_hint_all_users: 'Geen filter nodig: alle gebruikers worden opgenomen.',
            review_type_label: 'Beoordelingstype',
            review_type_required: 'Beoordelingstype is verplicht',
            reviewer_ids_label: "Beoordelaar-ID's",
            reviewer_ids_hint: "Voer gebruikers-ID's in, gescheiden door komma's",
            reviewer_ids_required: 'Minimaal één beoordelaar-ID is vereist',
            start_date_label: 'Startdatum',
            start_date_required: 'Startdatum is verplicht',
            end_date_label: 'Einddatum',
            end_date_required: 'Einddatum is verplicht',
            review_type_user_access: 'Gebruikerstoegang',
            review_type_role_permissions: 'Rolmachtigingen',
            review_type_service_accounts: 'Serviceaccounts',
            review_type_privileged_access: 'Bevoorrechte toegang',
            review_type_application_access: 'Applicatietoegang',
            auto_revoke_label: 'Automatisch intrekken bij weigering',
            campaigns_title: 'Toegang tot beoordelingscampagnes',
            create_campaign_label: '+ Campagne maken',
            no_campaigns_found: 'Geen campagnes gevonden',
            loading_campaigns: 'Campagnes laden...',
            no_reviews_assigned: 'Er zijn geen beoordelingen aan u toegewezen',
            loading_reviews: 'Beoordelingen laden...',
            review_decision_title: 'Herzie besluit',
            justification_label: 'Rechtvaardiging',
            announce_loaded_review_items: '{count} recensie-items geladen',
            announce_loaded_campaigns: '{count} campagnes geladen',
            announce_decision_submitted: "Beslissing '{decision}' ingediend voor beoordelingsitem",
            announce_decision_submitted_for: 'Beslissing "{decision}" ingediend voor {name}',
            announce_bulk_decision: 'Bulk {decision} toegepast op {count} artikelen',
        },
        attestation: {
            training_ids_label: 'Training Covered',
            training_ids_help: 'The training courses this attestation covers. The link is carried into each campaign the schedule generates.',
            training_ids_none: 'No training linked',
            training_ids_count: '{count} course(s)',
            training_ids_load_error: 'Training courses could not be read. The list may be incomplete.',
            title: 'Attestering Schedules',
            new: 'Nieuw Plannen',
            edit: 'Bewerken Plannen',
            create_title: 'Attesteringsschema aanmaken',
            edit_title: 'Attesteringsschema bewerken',
            schedule_history: 'Schemageschiedenis',
            next_run_date: 'Volgende uitvoeringsdatum',
            schedule_name: 'Plannen Naam',
            frequency: 'Frequentie',
            next_run: 'Volgende Uitvoeren',
            last_run: 'Laatste Uitvoeren',
            history: 'Attestering Geschiedenis',
            description: 'Beschrijving',
            scope: 'Reikwijdte',
            status: 'Status',
            created_at: 'Gemaakt',
            updated_at: 'Bijgewerkt',
            trigger_now: 'Activeer nu',
            back_to_schedules: "Terug naar Schema's",
            confirm_trigger: 'Trigger this schedule now?',
            confirm_delete: 'Delete this schedule?',
            triggered: 'Schema is succesvol geactiveerd.',
            deleted: 'Schema verwijderd.',
            saved: 'Schema is succesvol opgeslagen.',
            campaigns_generated: 'Campagnes gegenereerd',
            run_date: 'Uitvoeringsdatum',
            run_status: 'Status uitvoeren',
            create_schedule: 'Schema maken',
            view_history: 'Geschiedenis bekijken',
            deactivate: 'Deactiveren',
            overdue: 'OVERDUE',
            overdue_sr: 'Het schema is te laat',
            confirm_trigger_title: 'Bevestig actie',
            confirm_trigger_message: 'Are you sure you want to trigger this schedule now?',
            confirm_deactivate_title: 'Bevestig de deactivering',
            confirm_deactivate_message: 'Weet u zeker dat u dit schema wilt deactiveren?',
            triggered_success: 'Schema is succesvol geactiveerd.',
            triggered_unmapped_reviewer: 'The cycle was started, but the reviewer rule "{strategy}" has no source in the platform, so the campaign names no reviewer: whoever launches it reviews its items.',
            trigger_scope_unresolvable: 'This schedule’s scope cannot be turned into a review campaign. Edit the schedule so its scope filter names at least one role, department or user, or choose a supported scope.',
            trigger_reviewer_not_found: 'The reviewer named on this schedule is no longer an active user of this organisation. Edit the schedule and choose another reviewer.',
            reviewer_not_found: 'The reviewer ID does not name an active user of this organisation',
            scope_unsupported: 'This scope type is not supported: a review campaign scopes by user, role and department only',
            deactivated_success: 'Schema gedeactiveerd.',
            name_label: 'Naam *',
            frequency_label: 'Frequentie *',
            scope_type_label: 'Scopetype *',
            scope_filter_label: 'Bereikfilter (JSON)',
            reviewer_strategy_label: 'Reviewer Strategie *',
            reviewer_id_label: 'Recensent-ID *',
            frequency_quarterly: 'Driemaandelijks',
            frequency_semi_annual: 'Halfjaarlijks',
            frequency_annual: 'Jaarlijks',
            scope_all_users: 'Alle gebruikers',
            scope_by_role: 'Per rol',
            scope_by_department: 'Per afdeling',
            scope_custom: 'Aangepast',
            reviewer_manager: 'Beheerder',
            reviewer_role_owner: 'Rol Eigenaar',
            reviewer_specific_user: 'Specifieke gebruiker',
            computed_next_run_date: 'Berekende volgende rundatum',
            current_scheduled_date: 'Huidige geplande datum',
            recomputed_frequency_changed: '(opnieuw berekend - frequentie gewijzigd)',
            estimated_days_hint: 'Geschat op basis van {days} dagen vanaf vandaag',
            validation_name_required: 'Naam is vereist',
            validation_frequency_required: 'Frequentie is vereist',
            validation_scope_type_required: 'Scopetype is vereist',
            validation_reviewer_strategy_required: 'Reviewerstrategie is vereist',
            validation_reviewer_id_required: 'Reviewer-ID is vereist voor een specifieke gebruikersstrategie',
            validation_invalid_json: 'Ongeldige JSON',
            validation_scope_filter_invalid_json: 'Bereikfilter bevat ongeldige JSON',
            form_errors: 'Formulier bevat {count} fout(en). Corrigeer ze alstublieft.',
            create_schedule_btn: 'Schema maken',
            schedule_history_title: 'Schemageschiedenis',
            campaign_name_col: 'Campagnenaam',
            items_col: 'Artikelen',
            no_campaigns_yet: 'Er zijn nog geen campagnes gegenereerd',
            loading_history: 'Geschiedenis laden...',
            loading_schedule: 'Schema laden...',
            total_campaigns: 'Totaal aantal campagnes: {count}',
            results_per_page: 'Resultaten per pagina',
            page_size_option: '{size} / pagina',
        },
        sod: {
            title: 'Scheiding van Duties Regels',
            new: 'Nieuw Regel',
            edit: 'Bewerken Regel',
            create_title: 'SoD-regel aanmaken',
            edit_title: 'SoD-regel bewerken',
            rule_name: 'Regel Naam',
            conflicting_permissions: 'Conflicterende rechten',
            permission_a: 'Toestemming A',
            permission_b: 'Toestemming B',
            violations: 'SoD-schendingen',
            violation_user: 'Gebruiker',
            violation_rule: 'Regel Violated',
            violation_date: 'Gedetecteerd At',
            exceptions: 'SoD Uitzonderingen',
            exception_reason: 'Uitzondering Reden',
            exception_approver: 'Goedgekeurd By',
            exception_status: 'Uitzonderingsstatus',
            has_exception: 'Heeft uitzondering',
            no_exception: 'Geen uitzondering',
            grant_exception: 'Uitzondering verlenen',
            granting: 'Verlenen...',
            grant_new_exception: 'Nieuwe uitzondering toestaan',
            exceptions_for: 'Uitzonderingen voor: {name}',
            back_to_rules: 'Terug naar Regels',
            no_violations: 'Geen overtredingen gevonden',
            no_exceptions_found: 'Er zijn geen uitzonderingen gevonden op deze regel',
            all_exception_statuses: 'Alle uitzonderingsstatussen',
            user_id_label: 'Gebruikers-ID',
            justification_label: 'Rechtvaardiging',
            compensating_controls_label: 'Compenserende controles',
            expires_at_label: 'Verloopt op',
            granting_exception_for: 'Een uitzondering toestaan voor regel: {name}',
            severity: 'Ernst',
            severity_warning: 'Waarschuwing',
            severity_blocking: 'Blokkeren',
            violation_count: '{count} overtreding(en)',
            exception_count: '{count} uitzondering(en)',
            rule_description: 'Beschrijving',
            confirm_delete: 'Weet u zeker dat u deze regel wilt verwijderen?',
            deleted: 'Regel verwijderd.',
            framework_code: 'Regelgevend kader',
            name_label: 'Naam *',
            severity_label: 'Ernst *',
            conflict_pairs_legend: 'Conflicterende toestemmingsparen',
            conflict_pairs_hint: 'Elk paar definieert twee machtigingen die niet door dezelfde gebruiker mogen worden beheerd.',
            placeholder_permission_a: '-- Toestemming A --',
            placeholder_permission_b: '-- Toestemming B --',
            permission_a_for_pair: 'Toestemming A voor paar {index}',
            permission_b_for_pair: 'Toestemming B voor paar {index}',
            conflict_pair_label: 'Conflictpaar {index}',
            remove_conflict_pair: 'Conflictpaar verwijderen {index}',
            add_pair: '+ Voeg paar toe',
            placeholder_name: 'bijv. Betalingsgoedkeuring gescheiden',
            create_rule: 'Regel maken',
            loading_rule: 'SoD-regel laden...',
            failed_load_rule: 'Kan regel niet laden.',
            failed_create_rule: 'Kan SoD-regel niet maken.',
            failed_update_rule: 'Kan de SoD-regel niet updaten.',
            validation_name_required: 'Naam is vereist.',
            validation_pair_required: 'Er is ten minste één conflictmachtigingspaar vereist.',
            validation_pair_both_required: 'Koppel {index}: beide machtigingen moeten worden geselecteerd.',
            validation_pair_must_differ: 'Paar {index}: Toestemming A en Toestemming B moeten verschillend zijn.',
            added_conflict_pair: 'Conflictpaar toegevoegd {index}',
            removed_conflict_pair: 'Conflictpaar {index} verwijderd',
            announce_loaded_violations: 'Er zijn {count} overtredingen geladen',
            announce_loaded_exceptions: '{count} uitzonderingen geladen',
            announce_loaded_rules: '{count} SoD-regels geladen',
            announce_loading_exceptions: 'Uitzonderingen laden…',
            regulatory_info: {
                sod_context: 'SoD-regels handhaven de regelgevende rolscheiding die door de PDPL wordt vereist. Overtredingen gekoppeld aan een framework_code kunnen worden opgenomen in kaderspecifieke bewijspakketten (UC-VN-09).',
                violation_export_context: 'SoD-overtredingsexporten bevatten framework_code, conflicterende rollen, ernst, maker en detectiedatum voor bewijspakketten (P-VN-10).',
                exception_context: 'SoD-uitzonderingen produceren audit-bewijs: goedkeurder, rechtvaardiging, compenserende controles en vervaldatum. Verlopen uitzonderingen blijven zichtbaar (UC-VN-09 Stap 5).',
            },
        },
        regulatory_info: {
            attestation_context: 'Attestation schedules repeat an access review on a cycle (quarterly, semi-annual or annual). Each cycle creates a review campaign for all users, for named roles or departments, or for named users, and a named reviewer answers it. A reviewer rule the campaign model cannot express (manager or role owner) is shown when the cycle is started, and whoever launches the campaign then reviews its items. Schedule history lists the campaigns each schedule has generated.',
            api_keys_context: 'API key telemetry constitutes account evidence per VN_CYBERSEC_LAW_2025 Section B.5. Each key record includes last_used_at, ip_allowlist, permissions, status, and expires_at.',
            session_context: 'Session management enforces access controls per Telecom Law confidentiality requirements (Section B.4). Session events produce audit trail entries.',
            break_glass_context: 'Break-glass procedures produce regulatory justification evidence: requestor identity, justification, permissions, duration, approver identity, and approval/denial timestamps.',
            license_context: 'License settings gate modules by tier using PACKAGE_DEFINITIONS. Framework-specific modules require appropriate license tiers. Tier changes produce audit trail entries.',
            module_config_context: 'Module configuration supports framework-driven enablement. Disabling a module required by an active regulatory framework may break evidence completeness.',
            notification_context: 'Compliance-critical notifications (breach deadlines, attestation due dates) are marked with a compliance_critical flag and cannot be fully suppressed.',
            delegation_context: 'Delegation governance produces audit-grade evidence: delegator_id, delegate_id, permission scope, start/end dates, and status. Expired delegations remain visible.',
            access_control_context: 'Access control configuration meets Cybersecurity Law requirements (VN_CYBERSEC_LAW_2025): IP allowlist, MFA, service accounts, and SSO changes are audit-trailed.',
        },
    },
    cmp: {
        integration: {
            title: 'CMP Integratie',
            status: 'Verbindingsstatus',
            endpoint: 'CMP Eindpunt',
            last_sync: 'Laatste Synchronisatie',
            configure: 'Configureren',
        },
        purpose_mapping: {
            title: 'Doel Toewijzing',
            cmp_purpose: 'CMP Doel',
            grc_purpose: 'GRC Doel',
            mapping_status: 'Toewijzing Status',
            auto_map: 'Auto Toewijzen',
            new_mapping: 'Nieuw Toewijzing',
        },
        webhook: {
            title: 'Webhook Configuratie',
            webhook_url: 'Webhook-URL',
            secret: 'Webhook Geheim',
            events: 'Gebeurtenis Types',
            test_webhook: 'Webhook testen',
        },
        bulk_sync: {
            title: 'Bulk Synchronisatie',
            sync_now: 'Synchronisatie Now',
            last_run: 'Laatste Uitvoeren',
            records_synced: 'Gesynchroniseerde records',
            sync_history: 'Synchronisatie Geschiedenis',
            syncing: 'Synchroniseren...',
        },
        sync_settings: {
            sync_failures: 'Synchronisatie Failures',
            sync_secret_warning: 'Synchronisatie Geheim (save dit now — it will not be shown again):',
            push_description: 'Configureren de GRC Suite naar push case status updates naar AesirX CMP.',
            technical_description: 'Deze configuratie bepaalt hoe de GRC Suite updates van de casestatus pusht naar AesirX CMP.',
            direction_label: 'Richting',
            direction_value: 'GRC Suite → AesirX CMP (uitgaand)',
            auth_label: 'Authenticatie',
            auth_value: 'HMAC-SHA256-handtekening in X-GRC-Signature-header',
            retry_label: 'Opnieuw proberen Beleid',
            retry_value: '3 retries met exponential backoff (30s, 2min, 10min)',
            inbound_label: 'Inkomend Configuratie',
            inbound_value: 'Inkomend webhook (CMP → GRC) is configured in',
            cmp_webhook_settings: 'CMP Webhook Instellingen',
            page_title: 'CMP Synchronisatie-instellingen',
            outbound_sync_configured: 'Uitgaande synchronisatie geconfigureerd.',
            configure_sync_failed: 'Synchronisatie configureren mislukt',
            configuration_updated: 'Configuratie bijgewerkt.',
            update_configuration_failed: 'Configuratie bijwerken mislukt',
            connection_test_failed: 'Verbindingstest mislukt',
            test_result_message: 'Test verzonden naar {endpoint}. Status: {status}',
            endpoint_placeholder: 'https://your-cmp-domain/api/grc-sync',
            events: {
                new_to_under_review: 'Nieuw → Onder Beoordelen (assigned)',
                under_review_to_identity_verification: 'Onder Beoordelen → Identity Verificatie',
                under_review_to_in_progress: 'Onder Beoordelen → In Voortgang',
                in_progress_to_completed: 'In Voortgang → Voltooid',
                in_progress_to_denied: 'In Voortgang → Denied',
                any_to_closed: 'Any → Gesloten',
            },
        },
    },
    settings_pages: {
        webhooks: {
            title: 'Webhaken',
            new: 'Nieuw Webhook',
            url: 'URL',
            url_placeholder: 'https://example.com/webhook',
            events: 'Gebeurtenissen',
            secret: 'Geheim',
            active: 'Actief',
            test: 'Proef',
            create_failed: 'Kan niet create webhook..',
            create_success: 'Webhook succesvol aangemaakt.',
            deactivate_failed: 'Kan niet deactivate webhook..',
            deactivate_success: 'Webhook gedeactiveerd.',
            fetch_failed: 'Kan niet fetch webhooks..',
            send_test_failed: 'Kan niet send test..',
            test_failed: 'Webhook test mislukt.',
            test_success: 'Webhooktest geslaagd.',
            edit_title: 'Webhook bewerken',
            announce_loaded: 'Webhooks geladen',
            update_success: 'Webhook succesvol bijgewerkt.',
            update_failed: 'Kan webhook niet bijwerken.',
            activate_success: 'Webhook geactiveerd.',
            activate_failed: 'Kan webhook niet activeren.',
        },
        domains: {
            title: 'Domeinen',
            new: 'Nieuw Domein',
            domain_name: 'Domein Naam',
            verified: 'Geverifieerd',
            verify: 'Verifiëren',
            add_failed: 'Kan niet add domein..',
            add_success: 'Domein added successfully.',
            domain_placeholder: 'bijv. grc.example.com',
            fetch_failed: 'Kan niet fetch domeinen..',
            remove_failed: 'Kan niet remove domein..',
            remove_success: 'Domein removed successfully.',
            txt_record: 'TXT-record',
            verify_dns_failed: 'DNS verification mislukt.',
            verify_failed: 'Kan niet verify domein..',
            verify_success: 'Domein verified successfully.',
            announce_loaded: 'Domains loaded',
        },
        export_schedules: {
            title: "Exportschema's",
            new: 'Nieuw Plannen',
            schedule_name: 'Naam',
            frequency: 'Frequentie',
            format: 'Formaat',
            last_run: 'Laatste Uitvoeren',
            next_run: 'Volgende Uitvoeren',
            create_failed: 'Kan niet create planning..',
            create_success: 'Plannen created successfully.',
            day_of_month: 'Dag van Maand',
            day_of_week: 'Dag van Week',
            deactivate_failed: 'Kan niet deactivate planning..',
            deactivate_success: 'Plannen deactivated.',
            fetch_failed: 'Kan niet fetch planningen..',
            freq_daily: 'Dagelijks',
            freq_monthly: 'Maandelijks',
            freq_weekly: 'Wekelijks',
            recipients: 'Ontvangers',
            time_utc: 'Tijd (UTC)',
            trigger_failed: 'Kan niet trigger export..',
            trigger_success: 'Exporteren triggered successfully.',
            type_audit_trail: 'Auditspoor',
            type_compliance_report: 'Naleving Rapport',
            type_custom: 'Aangepast',
            type_incident_report: 'Incident Rapport',
            type_ropa: 'ROPA',
            announce_loaded: 'Export schedules loaded',
            execution_note: "Each run produces the file as the schedule's run actor, shown under Runs as: the person who created the schedule, or who last changed what it produces, with that person's current permissions. Each run is recorded below. Recipients receive an e-mail with a link to this page; the file downloads under each reader's own permissions.",
            paused_reason_label: 'Paused by the platform',
            type_withdrawn: 'Withdrawn type',
            runs: 'Runs',
            runs_for: 'Runs for {name}',
            runs_empty: 'This schedule has not run yet.',
            runs_fetch_failed: 'Failed to load the runs.',
            close_runs: 'Close runs',
            run_status: 'Status',
            run_scheduled_for: 'Run for',
            run_trigger: 'Started by',
            run_attempts: 'Attempts',
            run_reason: 'Reason',
            run_file: 'File',
            run_status_running: 'Running',
            run_status_completed: 'Completed',
            run_status_failed: 'Failed',
            run_trigger_scheduled: 'Schedule',
            run_trigger_manual: 'Manual trigger',
            run_will_retry: 'It will be tried again.',
            download: 'Download',
            triggering: 'Exporting…',
            disabling: 'Disabling…',
            trigger_completed: 'Export completed. The file is in the run list.',
            trigger_run_failed: 'The export ran and failed: {reason}',
            failure_actor_inactive: 'The person this schedule runs as is no longer an active member, so the export cannot run as them.',
            failure_actor_lacks_permission: 'The person this schedule runs as no longer holds the permission this export needs.',
            failure_export_type_withdrawn: 'This export type was withdrawn, so the schedule cannot produce it. To export again, a new schedule with another export type is needed.',
            failure_export_too_large: 'The export selects more rows than one file can hold.',
            failure_generation_failed: 'The file could not be generated.',
            failure_interrupted: 'The run was interrupted before it finished.',
            failure_never_executed: 'Requested before scheduled exports were executed, so this run never ran and produced no file.',
            report: {
                incident_title: 'Incident report',
                compliance_title: 'Compliance report — framework coverage',
                window: 'Period: {start} to {end}',
                count: 'Rows: {count}',
                overall: 'Overall mapped coverage: {pct}',
                overall_none: 'Overall mapped coverage: not measurable (no installed framework declares requirements)',
                col_reference: 'Reference',
                col_title: 'Title',
                col_status: 'Status',
                col_severity: 'Severity',
                col_created: 'Created',
                col_closed: 'Closed',
                col_framework: 'Framework',
                col_requirements: 'Requirements',
                col_mapped: 'Mapped',
                col_coverage: 'Coverage',
                col_open_gaps: 'Open gaps',
            },
            email: {
                subject: 'Scheduled export ready: {name}',
                body: 'The scheduled export "{name}" ran for {date}. Open the run list to download the file: {link}',
                body_html: 'The scheduled export "{name}" ran for {date}. Open the run list to download the file:',
            },
            type_none_held: 'You hold none of the permissions an export type needs, so you cannot create a schedule: a schedule runs as the person who creates it.',
            type_offered_hint: 'Only the export types you may export yourself are offered: the schedule runs as you.',
            grant_ropa: 'the permission to export the record of processing activities as PDF',
            grant_audit_trail: 'the permission to export audit events',
            grant_compliance_report: 'the permission to read regulatory frameworks',
            grant_incident_report: 'the permission to run incident exports',
            refusal_permission_not_held: 'You do not hold {permission}, which this export needs, so you cannot make this schedule produce it.',
            refusal_permission_not_held_generic: 'You do not hold the permission this export needs.',
            refusal_schedule_inactive: 'This schedule is switched off, so it cannot be run. Enable it first.',
            refusal_export_type_withdrawn: "This schedule's export type was withdrawn, so it cannot be run or switched on. To keep exporting, create a schedule with another export type using New Schedule.",
            col_runs_as: 'Runs as',
            run_executed_as: 'Ran as',
            run_delivered_to: 'E-mail queued for',
            run_delivered_none: 'No e-mail queued',
            run_delivered_hint: 'An address under E-mail queued for means the e-mail was placed in the sending queue, not that it arrived. The Notification Admin Queue, open to administrators of notifications, shows whether each e-mail was sent.',
            enable: 'Enable',
            enabling: 'Enabling…',
            enable_success: 'Schedule enabled. Its next run is counted from now.',
            enable_failed: 'The schedule could not be enabled.',
            refusal_run_actor_cannot_run: 'This schedule runs as {name}, who cannot run it now: they are no longer an active member, or no longer hold the permission its export needs. It can be switched on once {name} can run it again: ask an administrator to restore their membership or permission. To export now, create a schedule with New Schedule: it runs as you.',
            refusal_run_actor_inactive: 'This schedule runs as {name}, who is no longer an active member of the organisation, so its runs would be refused and it cannot be switched on. It can be switched on once {name} is an active member again: ask an administrator to restore their membership. To export now, create a schedule with New Schedule: it runs as you.',
            refusal_run_actor_lacks_permission: 'This schedule runs as {name}, who no longer holds {permission}, which its export needs, so it cannot be switched on. It can be switched on once {name} holds it again: ask an administrator to give {name} that permission. To export now, create a schedule with New Schedule: it runs as you.',
            run_actor_unnamed: 'the person shown as Runs as',
            people_names_failed: 'The user directory could not be loaded, so a person may be shown by their account identifier instead of their name.',
        },
        email_log: {
            title: 'E-maillogboek',
            recipient: 'Ontvanger',
            subject: 'Onderwerp',
            sent_at: 'Verzonden op',
            status: 'Toestand',
            resend: 'Opnieuw verzenden',
            fetch_failed: 'Kan niet fetch e-mail logs..',
            announce_loaded: 'Email log loaded',
        },
    },
    organizations: {
        title: 'Organisaties',
        create: 'Aanmaken Organisatie',
        edit: 'Bewerken Organisatie',
        name: 'Naam',
        slug: 'URL-naam',
        timezone: 'Tijdzone',
        locale: 'Standaard Taalinstelling',
        parent: 'Bovenliggend Organisatie',
        group: 'Groep',
        org_group: 'Organisatiegroep',
        no_group: '(Geen – niet in een groep)',
        created: 'Aangemaakt',
        no_parent: '(Geen – zelfstandig)',
        no_orgs: 'Geen organisaties gevonden',
        load_error: 'Kan niet load organisaties',
        save_error: 'Kan niet save organisatie',
        created_msg: 'Organisatie created',
        updated: 'Organisatie updated',
        deactivate: 'Deactiveren',
        deactivated: 'Organisatie deactivated',
        confirm_deactivate: 'Are you sure you want naar deactivate dit organisatie?',
        loaded_results: 'Organizations geladen, {count} resultaten',
        form_loaded: 'Organization-formulier geladen',
        locale_vi_vn: 'Tiếng Việt (vi-VN)',
        locale_en_us: 'Engels (en-VS)',
        // fix-sprint #431 — the organisation's country (ISO 3166-1 alpha-2).
        country: 'Country',
        country_not_recorded: 'Not recorded',
        country_hint: 'The country this organisation is established in. The cross-border transfer check on rights requests compares a data subject’s country with it; while it is not recorded, that check is not evaluated.',
        // Gate E12.5 remediation 2 (fix-sprint #431) — the row Edit reads the record first.
        edit_reading: 'Reading the organisation’s record…',
        edit_read_error: 'The organisation’s record could not be read, so its current values are not shown and it cannot be saved here.',
        no_head: 'No Head',
        all_frameworks: 'Alle kaders',
        assigned_at: 'Toegewezen op',
    },
    intake_configs: {
        title: 'Openbaar Intake Configs',
        create: 'Aanmaken Configuratie',
        edit: 'Bewerken Intake Configuratie',
        label_col: 'Kenmerk',
        key: 'Intake Sleutel',
        key_hint: 'Openbaar URL will be: /rights-request?key=',
        mapped_org: 'Doel Organisatie',
        status: 'Toestand',
        enabled: 'Ingeschakeld',
        disabled: 'Uitgeschakeld',
        request_types: 'Ingeschakeld Verzoek Types',
        language: 'Standaard Taal',
        public_url: 'Openbaar URL',
        no_configs: 'Geen intake configs gevonden Aanmaken one naar enable public rights request forms..',
        load_error: 'Kan niet load intake configs',
        save_error: 'Kan niet save',
        delete_error: 'Kan niet delete',
        created_msg: 'Configuratie created',
        updated: 'Configuratie updated',
        deleted: 'Intake configuratie deleted',
        confirm_delete: 'Verwijderen dit intake configuratie?',
        select_types: 'Selecteren bij least one request type',
        select_org: 'Selecteren een target organisatie',
        routing_mode: 'Routeringsmodus',
        single_org: 'Enkele Organisatie',
        org_group_mode: 'Organisatiegroep',
        org_group: 'Organisatiegroep',
        select_group: 'Selecteer een organisatiegroep...',
        group_root_hint: 'Wordt automatisch ingesteld op de hoofdorganisatie van de groep wanneer de groep wordt geselecteerd.',
        loaded_results: 'Intake configurations geladen, {count} resultaten',
        form_loaded: 'Intake configuration-formulier geladen',
    },
    frequency: {
        weekly: 'Wekelijks',
        monthly: 'Maandelijks',
        quarterly: 'Kwartaal',
        semi_annually: 'Halfjaarlijks',
        annually: 'Jaarlijks',
        custom: 'Aangepast',
    },
    /**
     * Sprint 65.6.0 (AC8) — DPO-01 and PUB-01, the privacy office's two registers.
     *
     * One block with two children rather than two top-level blocks: the
     * publication vocabulary (`channels`, `publication_states`, the target
     * editor's labels) is shared by both registers, and a second spelling of it
     * would be free to drift from the first — the defect `PUBLICATION_CHANNELS`
     * is declared once in shared to avoid.
     *
     * R-57: EN and VI are authored. The other fourteen locales carry the EN
     * string for structural parity and are translated by the Phase 25 batch —
     * stated here rather than left to be inferred from the text.
     */
    privacy_office: {
        publication_states: {
            not_published: 'Not published',
            published: 'Published',
            withdrawn: 'Withdrawn',
        },
        channels: {
            public_website: 'Public website',
            mobile_app: 'Mobile app',
            intranet: 'Intranet',
            physical_notice: 'Physical notice',
            direct_notice: 'Direct notice to people',
            other: 'Other',
        },
        filter_publication_state: 'Publication',
        targets_label: 'Where it was published',
        targets_hint: 'At least one place a person can go and read it. A publication with no location is a claim nobody can check.',
        target_channel: 'Channel',
        target_locator: 'Address or location',
        target_locator_hint: 'A web address for a website, a screen name for an app, or a described location for a notice board.',
        target_locale: 'Language (optional)',
        target_add: 'Add another location',
        target_remove: 'Remove this location',
        field_published_on: 'Published on',
        field_published_on_hint: 'The date it actually became public, not the date you are entering it here.',
        appointments: {
            title: 'Data Protection Officer appointments',
            subtitle: 'Record who is appointed, the decision that appointed them, and where the appointment was made public.',
            module_guide: 'Record the appointment of your data protection officer as a register entry rather than a filled-in form: who is appointed, who decided it and when, when the appointment takes effect, and where you said so publicly. An appointment is saved as a draft and raises no duty until you bring it into effect — that transition is the one that says your organisation has a named officer in post. Only an appointment that is in effect can be published, and a publication must say where a person can go and read it.',
            nav: 'DPO appointments',
            record: 'Record appointment',
            table_label: 'Data protection officer appointments',
            sr_loading: 'Loading appointments',
            announce_loaded: '{count} appointments loaded',
            none: 'No appointments match these filters.',
            filters_label: 'Filter appointments',
            filter_status: 'Status',
            col_appointee: 'Appointed person',
            col_position: 'Position',
            col_decision: 'Appointing decision',
            col_takes_effect: 'Takes effect',
            col_status: 'Status',
            col_publication: 'Publication',
            takes_effect_unknown: 'Not stated',
            statuses: {
                draft: 'Draft',
                in_effect: 'In effect',
                ended: 'Ended',
                superseded: 'Superseded',
            },
            action_take_effect: 'Bring into effect',
            action_publish: 'Record publication',
            action_end: 'End appointment',
            modal_record_title: 'Record a DPO appointment',
            modal_record_hint: 'Record a decision your organisation has already taken. The appointment is saved as a draft and raises no duty until you bring it into effect.',
            field_full_name: 'Full name',
            field_position_title: 'Position title',
            field_contact_email: 'Contact email',
            field_contact_email_hint: 'Written as name@example.com. This is the address the public sees.',
            field_contact_phone: 'Contact phone (optional)',
            field_decided_by: 'Decided by',
            field_decided_by_hint: 'The body or person with the authority to appoint.',
            field_decided_on: 'Decision date',
            field_decision_reference: 'Your reference for the decision (optional)',
            field_takes_effect_on: 'Takes effect on (optional)',
            field_takes_effect_hint: 'Leave this empty if the start date is not settled. The duty is then raised undated with that reason, rather than dated from the day you typed it in.',
            field_ends_on: 'Ends on (optional)',
            modal_take_effect_title: 'Bring the appointment into effect',
            modal_take_effect_hint: 'From this point your organisation has a named officer in post. State the date it started if it is not already recorded.',
            field_correction_note: 'Note about the correction (optional)',
            modal_publish_title: 'Record where the appointment was published',
            modal_publish_hint: 'Only an appointment that is in effect can be published — a person is not named publicly as your officer before your organisation says they are one.',
            modal_end_title: 'End the appointment',
            modal_end_hint: 'An appointment that ran its course and one replaced by a newer appointment are different answers to "why is this person no longer the officer". Both are kept.',
            field_end_outcome: 'Outcome',
            field_end_reason: 'Reason',
            field_end_date: 'End date (optional)',
            toast_recorded: 'Appointment recorded.',
            toast_in_effect: 'Appointment is now in effect.',
            toast_published: 'Publication recorded.',
            toast_ended: 'Appointment ended.',
        },
        notices: {
            title: 'Privacy notices',
            subtitle: 'Draft a revision of a privacy notice, for the public or for named data subjects, have it approved, and record where it was published.',
            module_guide: 'Keep a register of the privacy notices your organisation gives, to the public or to named data subjects of its processing activities: which revision is live, who approved the text, and where a person can read it. Approval and publication are two separate acts, usually by two different people, so the register can show a notice that was approved and never actually published — the failure this record exists to make visible. A notice cannot go straight from draft to published, and a published notice that is taken down keeps its publication history with the reason it was withdrawn.',
            nav: 'Privacy notices',
            draft: 'New revision',
            table_label: 'Privacy notices',
            sr_loading: 'Loading privacy notices',
            announce_loaded: '{count} privacy notices loaded',
            none: 'No privacy notices match these filters.',
            filters_label: 'Filter privacy notices',
            filter_status: 'Status',
            col_title: 'Title',
            col_revision: 'Revision',
            col_locales: 'Languages',
            col_status: 'Status',
            col_approval: 'Approved by',
            col_publication: 'Publication',
            approval_none: 'Not approved',
            statuses: {
                draft: 'Draft',
                approved: 'Approved',
                published: 'Published',
                withdrawn: 'Withdrawn',
                superseded: 'Superseded',
            },
            action_approve: 'Approve',
            action_publish: 'Publish',
            action_withdraw: 'Withdraw',
            action_supersede: 'Supersede',
            modal_draft_title: 'New privacy notice revision',
            modal_draft_hint: 'The revision is the version number your published text carries. It is not a version this platform counts for you.',
            field_notice_title: 'Title',
            field_revision: 'Revision',
            field_content_url: 'Where the text lives',
            field_content_url_hint: 'The address of the notice text itself — the page, document or file a reader would open.',
            field_locales: 'Languages',
            field_locales_hint: 'One language code per line, or separated by commas.',
            field_supersedes: 'Replaces which notice (optional)',
            modal_approve_title: 'Approve the notice',
            modal_approve_hint: 'Approving records who signed the text off. It does not give the notice to its audience — publishing is a separate act.',
            field_approved_by: 'Approved by (optional)',
            field_approved_by_hint: 'Leave this empty to record yourself as the approver.',
            field_approval_reference: 'Your reference for the approval (optional)',
            modal_publish_title: 'Publish the notice',
            modal_publish_hint: 'Record that the approved text is now given to its audience, and where a person can read it.',
            publish_needs_approval: 'This notice carries no recorded approval, so it cannot be published. Record who approved the text first.',
            modal_withdraw_title: 'Withdraw the notice',
            modal_withdraw_hint: 'Withdrawing keeps the publication history: "we published this and took it down" is a different answer from "we never published this".',
            field_withdraw_reason: 'Reason for withdrawing',
            modal_supersede_title: 'Supersede the notice',
            modal_supersede_hint: 'Superseding closes this revision because a newer one has replaced it, and it is the last thing that can happen to it in this register.',
            field_supersede_reason: 'Reason for superseding',
            toast_drafted: 'Privacy notice revision created.',
            toast_approved: 'Privacy notice approved.',
            toast_published: 'Privacy notice published.',
            toast_withdrawn: 'Privacy notice withdrawn.',
            toast_superseded: 'Privacy notice superseded.',
        },
    },
    /* Sprint 65.18.0 — continuity (IRP-01). English text held for structural
       parity; real translation deferred to Phase 25 per R-57. */
    /* Sprint 65.19.0 -- legal matters (toolkit row LEG-01). */
    /* 2026-09-24 owner ruling — legacy instrument notice. English text held for
       structural parity; real translation deferred to Phase 25 per R-57. */
    framework_lifecycle_notice: {
        legacy_chip: 'Legacy — no longer in force',
        legacy_notice_label: 'Legacy instrument notice',
        legacy_body: 'This instrument has lapsed or been replaced. Results determined under it may still apply under transition rules. Check the current instrument before relying on it.',
        suspended_chip: 'Suspended — not an obligation',
        suspended_notice_label: 'Suspended instrument notice',
        suspended_body: 'The application of this instrument is suspended, so its duties are not owed while the suspension lasts. Check what ends the suspension before relying on anything recorded under it.',
    },
    framework_articles: {
        title: 'Articles',
        article: 'Article',
        heading: 'Heading',
        state: 'Platform coverage',
        totals_summary: '{total} articles in this instrument — {mapped} implemented by a requirement, {declared} recorded as imposing no duty, {unmapped} not yet mapped.',
        not_article_structured: 'This instrument is not divided into articles — it uses control identifiers — so there is no article map to show. That is correct for this framework and not a gap.',
        not_mapped_yet: 'This instrument is divided into articles and the installed pack does not yet carry them. Until it does, the platform cannot say which of its provisions are implemented.',
        structure_unverified: 'Nobody has read this instrument\u2019s official text yet, so the platform does not know how it divides its provisions and cannot say which of them are implemented. This is a statement about the platform, not about the instrument.',
        structure_not_declared: 'This record does not say how its instrument divides its provisions, so the platform does not know whether it has an article map. That is a gap in the installed pack, not a statement about the instrument.',
        structure_not_required: 'This record comes from a market pack, and a market pack is not required to declare how its instrument divides its provisions, so the platform has no article map to show for it. That is not a gap in the installed pack.',
        structure_origin_unknown: 'This record does not say how its instrument divides its provisions, and the platform cannot tell which kind of pack it was installed from, so it does not know whether an article map should exist.',
        coverage_line: 'Article coverage: {coverage}',
        coverage_levels: {
            mapped: 'Every article is accounted for',
            partial: 'Some articles are not yet mapped',
            not_assessed: 'Not assessed',
        },
        load_failed: 'The articles for this framework could not be read.',
        recorded_text: {
            column: 'Recorded text',
            read: 'Read',
            read_aria: 'Read the recorded text of Article {article}',
            dialog_title: 'Article {article} — recorded text',
            load_failed: 'The recorded text of this article could not be read.',
            load_more: 'Show more',
            source_line: 'Source: {title}',
            page_single: 'page {page} of the source',
            pages_range: 'pages {start}–{end} of the source',
            extraction: {
                ocr: 'read by OCR from the signed scan',
                pdf_text_layer: 'extracted from the PDF text layer',
                docx_text: 'extracted from the DOCX original',
                html_text: 'extracted from the official web page',
                unrecorded: 'extraction method not recorded',
            },
            missing: 'The recorded source does not carry this article\u2019s text, so none is shown. Only its heading is known.',
            partial: 'The source ends without a closing mark after this article, so the text may run on into material that follows it.',
            notice: 'This is the instrument\u2019s original Vietnamese text as recorded from its signed source. It may contain recognition errors, it does not certify current legal effect, and no translation is provided.',
        },
        source_tab: {
            title: 'Source',
            intro: 'The instrument\u2019s recorded text, read in full: chapters, articles, annexes and page marks as the signed source carries them.',
            source_select: 'Recorded source',
            page_mark: 'Page {page}',
            progress: 'Showing {shown} of {total} characters',
            load_more: 'Show more',
            load_all: 'Show the rest',
            load_failed: 'The recorded source text could not be read.',
            no_recorded_source: 'No recorded source text is held for this framework. Its requirements and articles are on the other tabs.',
            composite: 'This framework groups other instruments and has no text of its own. Read the source of each of them:',
            superseded_draft: 'This consultation draft was later promulgated, and its own text is not held. Read the instrument that replaced it:',
            open_in_source: 'In source',
            open_in_source_aria: 'Show Article {article} in the full source',
            article_not_in_source: 'The recorded source does not carry Article {article}\u2019s heading, so it cannot be shown in place.',
        },
        states: {
            mapped: 'Implemented',
            no_duty_declared: 'No duty on the controller',
            not_yet_mapped: 'Not yet mapped',
        },
    },
    credit_information: {
        title: 'Credit-Information Uses',
        scope_note: 'Law 91/2025/QH15 Điều 27(1)(b) prohibits using a data subject’s credit information for credit scoring, credit rating, credit-information assessment or creditworthiness assessment unless that data subject has consented. Decree 356/2025/NĐ-CP Điều 8 details the Article: consent must state the purposes — including those four where they are carried out — the collection sources and sharing parties, the retention period and how consent may be withdrawn; compliance is assessed once every 01 year; and disclosure or loss of sensitive data in this sector is notified to the authority and the data subject within 72 hours.',
        boundary_heading: 'Scope and boundary — from your installed frameworks',
        boundary_frame: 'These statements come from the frameworks installed for your organisation, shown as each framework states them. This register records that a use took place, for which purpose, and the consent it rested on. The platform computes no credit score, produces no rating, assesses no creditworthiness and makes no lending decision. A use no consent covers is refused here rather than recorded with a warning beside it.',
        boundary_none: 'No installed framework declares a boundary statement for this register. This describes the content installed for your organisation, not your organisation’s scope.',
        boundary_load_error: 'The installed frameworks’ boundary statements could not be read, so none is shown. This does not mean that no framework declares one.',
        boundary_retry_subject: 'the boundary statements',
        purposes_load_failed: 'The purposes your installed frameworks declare could not be read, so a use cannot be recorded yet. This does not mean that none is declared.',
        purposes_retry_subject: 'the declared purposes',
        consent_check_timing: 'The consent is checked as it stands when you record the use, not as it stood at the time of use. A consent withdrawn, or given, between those two moments is judged as it is now.',
        kpi_label: 'Register summary',
        kpi_total: 'Total',
        kpi_total_hint: 'all uses recorded in this register',
        kpi_on_this_page: 'on this page',
        kpi_not_loaded: 'not loaded',
        uses: 'Recorded uses',
        record: 'Record a use',
        data_subject_ref: 'Data subject reference',
        information_description: 'What credit information was used',
        purpose: 'Purpose',
        used_at: 'Used at',
        consent_evidence_id: 'Consent evidence',
        consent_evidence_hint: 'The consent-evidence entry this use rests on. It is resolved and checked on the server — naming one that does not cover this purpose is refused.',
        consent_purpose_id: 'Which purpose on that consent',
        consent_purpose_hint: 'Which purpose named on that consent covers this use. Your organisation decides which of its own purposes corresponds; the platform checks that the one you name is genuinely on that consent.',
        consent_state: 'Consent',
        record_withdrawal: 'Record a withdrawal',
        withdrawn_at: 'Withdrawn on',
        no_purposes_installed: 'No framework installed for your organisation declares a credit-information purpose, so a use cannot be recorded against one here.',
        empty_uses: 'No credit-information use has been recorded yet.',
        load_failed: 'The credit-information register could not be read.',
        record_failed: 'The use could not be recorded.',
        recorded: 'Use recorded.',
        withdrawal_recorded: 'Withdrawal recorded.',
        withdrawal_failed: 'The withdrawal could not be recorded.',
        states: {
            consent_in_force: 'In force',
            consent_withdrawn: 'Withdrawn',
        },
    },
    legal_matters: {
        title: 'Legal Matters',
        scope_note: 'No installed instrument requires the controller to operate this register. Law 91/2025/QH15 Điều 4(1)(đ) declares that a data subject may complain and may denounce, and Decree 356/2025/NĐ-CP Điều 36(6) assigns the resolution of complaints and denunciations to the state authority — so a right is declared, and no duty to receive it falls on you. Running this register is a capability your organisation chooses, and any rhythm shown here is your own rather than a statutory deadline.',
        matters: 'Matters',
        receive: 'Record a matter',
        kind: 'Kind',
        origin: 'Rests on',
        right_code: 'Which right',
        raised_by: 'Raised by',
        subject: 'What it concerns',
        state_label: 'State',
        received_at: 'Received',
        acknowledged_at: 'Acknowledged',
        acknowledge: 'Record the acknowledgement',
        resolve: 'Record the conclusion',
        resolution_summary: 'What was concluded',
        concluded_at: 'Concluded on',
        not_acknowledged: 'Not yet acknowledged',
        // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
        intake_from: 'From rights request {reference}',
        right_not_recorded: 'The intake did not say which right it raises; a person records it.',
        record_right: 'Record the right',
        record_right_hint: 'Choose the right this matter rests on, from those the installed frameworks declare at the provision the intake was made under.',
        right_recorded: 'Right recorded',
        // fix-sprint-20261026-0900 Finding 6 — what a matter awaiting its right rests on,
        // the related-matters column, and the refusals of Record the right.
        rests_on_provision: 'At {basis} of {framework}',
        rests_on_provision_no_basis: 'Under {framework}; no provision is stated',
        related_matters: 'Related matters',
        no_related_matters: 'None',
        record_right_closed: 'This matter is closed, so the right it rests on can no longer be recorded.',
        record_right_not_at_provision: 'That right does not rest on the provision the intake was made under. Choose one of the rights offered.',
        record_right_not_declared: 'No installed framework declares that right. Choose another.',
        record_right_already_recorded: 'The right this matter rests on has already been recorded.',
        suggested_link: 'Possibly the same matter as “{subject}”: an earlier request gave the same email address, which is not verified.',
        suggested_link_open: 'Open that matter',
        suggested_link_accept: 'Same matter',
        suggested_link_dismiss: 'Not the same matter',
        suggested_link_accepted: 'A person recorded this as the same matter as “{subject}”.',
        suggested_link_dismissed: 'A person recorded this as not the same matter as “{subject}”.',
        suggested_link_decided: 'Decision recorded',
        linked_matter_not_found: 'The legal matter this link points to could not be read.',
        linked_matter_shown: 'The legal matter the link points to is shown first.',
        no_rights_installed: 'No framework installed for your organisation declares a right, so a matter cannot rest on one here.',
        rights_loading: 'Reading the rights your installed frameworks declare…',
        rights_load_failed: 'The rights your installed frameworks declare could not be read, so a matter cannot rest on one until they are.',
        rights_retry_subject: 'the declared rights',
        matters_retry_subject: 'the legal-matters register',
        loading: 'Loading the legal-matters register',
        right_placeholder: 'Choose the right',
        withdrawn_on_by: 'Withdrawn at {at}, recorded by {by}.',
        withdrawal_reason_not_recorded: 'No reason was recorded for this withdrawal.',
        timeline_acknowledged_before_received: 'The acknowledgement cannot be earlier than when the matter was received.',
        timeline_concluded_before_received: 'The conclusion cannot be earlier than when the matter was received.',
        timeline_concluded_before_acknowledged: 'The conclusion cannot be earlier than the acknowledgement.',
        kpi_row_label: 'Legal-matters summary',
        kpi_not_loaded: 'Not loaded',
        kpi_total: 'Matters',
        kpi_total_hint: 'in the register',
        kpi_on_this_page: 'on this page',
        withdraw: 'Record a withdrawal',
        withdrawal_reason: 'Why it was withdrawn',
        withdrawn: 'Withdrawal recorded.',
        empty_matters: 'No matter has been recorded yet.',
        load_failed: 'The legal-matters register could not be read.',
        create_failed: 'The record could not be saved.',
        created: 'Recorded.',
        acknowledged: 'Acknowledgement recorded.',
        resolved: 'Conclusion recorded.',
        kinds: {
            complaint: 'Complaint',
            denunciation: 'Denunciation',
            claim: 'Claim',
            litigation: 'Litigation',
            regulatory_penalty: 'Regulatory penalty',
            other: 'Other',
        },
        origins: {
            statutory_right: 'A right an installed framework declares',
            elective: 'A matter your organisation chose to track',
        },
        states: {
            received: 'Received',
            acknowledged: 'Acknowledged',
            resolved: 'Resolved',
            withdrawn: 'Withdrawn',
        },
        raised_by_options: {
            data_subject: 'Data subject',
            employee: 'Employee',
            regulator: 'Regulator',
            counterparty: 'Counterparty',
            other: 'Other',
        },
    },
    continuity: {
        title: 'Continuity Exercises',
        scope_note: 'No installed instrument requires continuity exercises. Law 91/2025/QH15 and Decree 356/2025/NĐ-CP were both read in full and neither imposes this control, so any rhythm shown here is your organisation’s own and never a statutory deadline. An exercise that raised improvement actions is not finished until each has been retested.',
        plans: 'Continuity plans',
        exercises: 'Exercises',
        plan_name: 'Plan name',
        plan_scope: 'Scope',
        plan_owner: 'Owner',
        scope_note_label: 'Say what it covers',
        create_plan: 'Add a plan',
        schedule: 'Schedule an exercise',
        kind: 'Kind',
        status_label: 'Status',
        outcome_label: 'Outcome',
        held_at: 'Held on',
        participants: 'Participants',
        open_loops: 'Improvement actions awaiting retest',
        no_open_loops: 'All retested',
        last_reviewed: 'Last reviewed',
        never_reviewed: 'Never reviewed',
        empty_plans: 'No continuity plan has been recorded yet.',
        empty_exercises: 'No exercise has been recorded yet.',
        complete: 'Complete the exercise',
        cancel: 'Cancel the exercise',
        cancel_reason: 'Why it was cancelled',
        completed: 'Exercise completed.',
        cancelled: 'Exercise cancelled.',
        retested: 'Retest recorded.',
        record_retest: 'Record a retest',
        retested_at: 'Retested on',
        retest_note: 'What the retest showed',
        participant_name: 'Who led it',
        participant_role: 'Their role',
        summary: 'What happened',
        improvement_action: 'Improvement action (optional)',
        action_owner: 'Action owner',
        load_failed: 'The continuity register could not be read.',
        create_failed: 'The record could not be saved.',
        created: 'Recorded.',
        retry_plans_subject: 'the continuity plans',
        retry_exercises_subject: 'the exercises',
        complete_for: 'Complete the exercise: {exercise}',
        cancel_for: 'Cancel the exercise: {exercise}',
        record_retest_for: 'Record a retest for {exercise}: {action}',
        completing_heading: 'Completing the exercise: {exercise}',
        cancelling_heading: 'Cancelling the exercise: {exercise}',
        retest_heading: 'Recording a retest for {exercise}: {action}',
        scopes: {
            systems: 'Systems',
            premises: 'Premises',
            people: 'People',
            suppliers: 'Suppliers',
            data_recovery: 'Data recovery',
            other: 'Other',
        },
        kinds: {
            tabletop: 'Tabletop',
            simulation: 'Simulation',
            live: 'Live',
            other: 'Other',
        },
        outcomes: {
            objectives_met: 'Objectives met',
            objectives_partially_met: 'Objectives partially met',
            objectives_not_met: 'Objectives not met',
            not_completed: 'Not completed',
        },
        statuses: {
            scheduled: 'Scheduled',
            completed: 'Completed',
            cancelled: 'Cancelled',
        },
    },
    /* Sprint 65.17.0 — de-identification runs (ANON-01). English text held for
     structural parity; real translation deferred to Phase 25 per R-57. */
    deidentification: {
        title: 'De-identification Runs',
        scope_note: 'Decree 356/2025/NĐ-CP Điều 10(6) requires that a data subject be able to rectify, anonymise and delete identification records in artificial-intelligence and metaverse systems. Outside that scope this is a control your organisation chooses to run. A run is not complete until somebody has judged what remains re-identifiable.',
        population: 'Population transformed',
        method: 'Method',
        method_note: 'Name the method',
        create: 'Record a run',
        created: 'De-identification run recorded.',
        empty: 'No de-identification run has been recorded yet.',
        status_label: 'Status',
        sample_label: 'Sample',
        residual_risk_label: 'Residual re-identification risk',
        load_failed: 'The de-identification runs could not be read.',
        create_failed: 'The run could not be recorded.',
        residual_risk_absent: 'Not yet judged',
        record_judgement: 'Record the sample and judgement',
        record_judgement_for: 'Record the sample and judgement for {population}',
        sample_outcome: 'Sample outcome',
        records_examined: 'Records examined',
        sample_finding: 'What the sample found',
        residual_risk_reasoning: 'Residual re-identification risk — your reasoning',
        residual_risk_hint: 'The platform derives no figure. What remains re-identifiable depends on data your organisation does not hold, so this is recorded as your judgement, against your account and the moment you save it.',
        updated: 'De-identification run updated.',
        update_failed: 'The run could not be updated.',
        approve_for: 'Approve the run for {population}',
        reject: 'Reject the run',
        reject_for: 'Reject the run for {population}',
        reject_heading_for: 'Rejecting the run for {population}',
        decision_note: 'Why the run was rejected',
        approved: 'De-identification run approved.',
        rejected: 'De-identification run rejected.',
        decide_failed: 'The decision could not be recorded.',
        total_runs: '{count} run(s) recorded',
        methods: {
            suppression: 'Suppression',
            generalisation: 'Generalisation',
            pseudonymisation: 'Pseudonymisation',
            aggregation: 'Aggregation',
            noise_addition: 'Noise addition',
            hashing_with_deleted_key: 'Hashing with the key deleted',
            other: 'Other',
        },
        sample_outcomes: {
            not_sampled: 'Not sampled',
            sample_clean: 'Sample found no identifiers',
            sample_found_identifiers: 'Sample found identifiers',
        },
        statuses: {
            draft: 'Draft',
            awaiting_approval: 'Awaiting approval',
            approved: 'Approved',
            rejected: 'Rejected',
        },
    },
    training: {
        title: 'Training & Awareness',
        subtitle: 'Assign training to named people and record each completion against the evidence that shows it happened.',
        module_guide: 'Define the training your organisation requires, assign it to named people with a deadline, and close each assignment against evidence — a completed form, an uploaded certificate or a recorded attestation. An assignment cannot be marked complete on its own; exempting someone is a decision recorded against the person who took it, with a reason. Completion rates here feed the training component of your audit-readiness score.',
        nav_assignments: 'Training & Awareness',
        new_course: 'New course',
        assign: 'Assign training',
        summary_open: 'Open',
        summary_completed: 'Completed',
        summary_overdue: 'Overdue',
        summary_exempted: 'Exempted',
        filters_label: 'Filter training assignments',
        filter_status: 'Status',
        filter_overdue_only: 'Overdue only',
        focused_heading: 'Assignment:',
        sr_loading: 'Loading training assignments',
        no_assignments: 'No training assignments match these filters.',
        table_label: 'Training assignments',
        col_course: 'Course',
        col_assignee: 'Assignee',
        col_due: 'Due',
        col_status: 'Status',
        col_evidence: 'Evidence',
        overdue_badge: 'Overdue',
        evidence_none: 'None recorded',
        exempted_by_label: 'Exempted by',
        cancelled_by_label: 'Withdrawn by',
        cancellation_reason_not_recorded: 'Withdrawn — no reason was stored',
        action_start: 'Start',
        action_complete: 'Record completion',
        action_exempt: 'Exempt',
        action_cancel: 'Withdraw',
        modal_course_title: 'New training course',
        field_course_title: 'Course title',
        field_course_description: 'Description',
        field_recurrence_months: 'Repeat every (months)',
        field_recurrence_hint: 'Leave empty for one-off training. This is an internal cadence, not a statutory deadline.',
        modal_assign_title: 'Assign training',
        field_course: 'Course',
        no_courses_hint: 'No active courses yet. Define a course before assigning training.',
        courses_heading: 'Courses',
        courses_table_label: 'Active training courses',
        no_courses: 'No active courses yet.',
        col_description: 'Description',
        col_recurrence: 'Repeats',
        recurrence_one_off: 'One-off',
        recurrence_every: 'Every {count} months',
        action_assign_course: 'Assign',
        field_assignees: 'People',
        field_assignees_hint: 'One user id per line, or separated by commas. Each person gets their own assignment and their own task.',
        field_due_at: 'Due date',
        modal_complete_title: 'Record completion',
        modal_complete_hint: 'A completion must cite the evidence that shows the training was done — a submitted form, an uploaded file or a recorded attestation. The assignment cannot be closed without it.',
        field_evidence_kind: 'Evidence type',
        field_evidence_reference: 'Evidence reference',
        modal_exempt_title: 'Exempt from training',
        modal_exempt_hint: 'An exemption is recorded against you, with the reason you give. It is never counted as a completion.',
        field_exemption_reason: 'Reason for the exemption',
        modal_cancel_title: 'Withdraw assignment',
        field_cancel_reason: 'Reason for withdrawing',
        toast_started: 'Training assignment started.',
        toast_course_created: 'Training course created.',
        toast_assigned: 'Training assigned.',
        toast_completed: 'Completion recorded.',
        toast_exempted: 'Exemption recorded.',
        toast_cancelled: 'Assignment withdrawn.',
        announce_loaded: '{count} training assignments loaded',
        statuses: {
            assigned: 'Assigned',
            in_progress: 'In progress',
            completed: 'Completed',
            exempted: 'Exempted',
            cancelled: 'Withdrawn',
        },
        evidence_kinds: {
            form_instance: 'Completed form',
            attachment: 'Uploaded file',
            attestation: 'Recorded attestation',
        },
    },
    task_management: {
        // Sprint 63.32.0 (#543) — the module a task came from, in the reader's language.
        source_modules: {
            rights_requests: 'Rights Requests',
            assessments: 'DPIA & Assessments',
            incidents: 'Incident Response',
            incident_ops: 'Incident Operations',
            monitoring: 'Monitoring Programs',
            vendor_risk: 'Vendor Risk',
            governance: 'Governance',
            deletion_orchestration: 'Deletion Orchestration',
            access_review: 'Access Reviews',
            consent: 'Consent Governance',
            consent_properties: 'Consent Properties',
            forms: 'Compliance Forms',
            localization: 'Localization',
            program_governance: 'Program Governance',
            privacy_scanner: 'Privacy Scanner',
            classification: 'Data Classification',
            discovery: 'Data Discovery',
            data_mapping: 'Data Mapping',
            compliance_operations: 'Compliance Work Programme',
            training: 'Training',
            privacy_office: 'Privacy Office',
            authority_operations: 'Authority Operations',
            ai_governance: 'AI Governance',
            regulatory_classification: 'Regulatory Classification',
            org_settings: 'Organization Profile',
            customs_tax_administration: 'Customs Tax Administration',
            manual: 'Manual',
        },
        nav: {
            calendar: 'Kalender',
            all_tasks: 'Alle taken',
            my_tasks: 'Mijn taken',
            overdue: 'Te laat',
            board: 'Bord',
            workflows: 'Werkstromen',
        },
        list: {
            title: 'Alle taken',
            my_title: 'Mijn taken',
            overdue_title: 'Te late taken',
            new_task: 'Taak aanmaken',
            no_tasks: 'Geen taken gevonden.',
            empty_state: 'Geen taken gevonden. Taken worden gegenereerd vanuit moduleworkflows zoals beoordelingen, incidenten en beoordelingscampagnes.',
            total: 'Totaal taken',
            search_placeholder: 'Taken zoeken...',
            bulk_actions: 'Bulkacties',
            change_status: 'Status wijzigen',
            change_priority: 'Prioriteit wijzigen',
            assign: 'Toewijzen',
            sr_my_tasks_count: '{count} taken aan u toegewezen',
            sr_days_overdue: '{count} dagen te laat',
            sla_label: 'SLA',
        },
        detail: {
            workflow_step: 'Workflow step',
            title: 'Taakdetail',
            description: 'Beschrijving',
            workflow_progress: 'Workflowvoortgang',
            checklist: 'Controlelijst',
            dependencies: 'Afhankelijkheden',
            comments: 'Opmerkingen',
            activity_timeline: 'Activiteitstijdlijn',
            assignees: 'Toegewezenen',
            add_assignee: 'Toegewezene toevoegen',
            remove_assignee: 'Toegewezene verwijderen',
            due_date: 'Vervaldatum',
            start_date: 'Startdatum',
            sla_status: 'SLA-status',
            sla_on_track: 'Op schema',
            sla_at_risk: 'In gevaar',
            sla_breached: 'Geschonden',
            tags: 'Labels',
            source_entity: 'Bronentiteit',
            view_source: 'Bron weergeven',
            add_dependency: 'Afhankelijkheid toevoegen',
            remove_dependency: 'Afhankelijkheid verwijderen',
            write_comment: 'Schrijf een opmerking...',
            post_comment: 'Plaatsen',
            edit_comment: 'Bewerken',
            delete_comment: 'Verwijderen',
            add_checklist_item: 'Item toevoegen',
            no_dependencies: 'Geen afhankelijkheden',
            no_comments: 'Nog geen opmerkingen',
            blocks: 'Blokkeert',
            blocked_by: 'Geblokkeerd door',
            related: 'Gerelateerd',
            working_days_remain: 'werkdagen resterend',
            created_by: 'Aangemaakt door',
            completed_at: 'Voltooid op',
            remove_assignee_aria: 'Remove assignee: {name}',
            // Sprint 63.32.0 (#543) — the seeded role names an assignment can carry.
            assignee_role_labels: {
                platform_admin: 'Platform Admin',
                compliance_owner: 'Compliance Owner',
                case_manager: 'Case Manager',
                auditor: 'Auditor',
                approver: 'Approver',
                partner_external_counsel: 'External Counsel (Partner)',
                processor_liaison: 'Processor Liaison',
            },
            remove_dependency_aria: 'Remove dependency: {name}',
            no_assignees: 'Geen toegewezenen',
            add_assignee_placeholder: 'Voer gebruikers-ID in...',
            edited: 'bewerkt',
            timeline: 'Activiteitstijdlijn',
            no_timeline: 'Nog geen activiteit',
            timeline_reason: 'Reason',
            no_workflow: 'Geen workflow toegewezen',
            add_dependency_placeholder: 'Voer taak-ID in...',
            sr_status_updated: 'Status bijgewerkt naar {status}',
            sr_priority_updated: 'Prioriteit bijgewerkt naar {priority}',
            // Sprint 63.30.0 (#590, #574) — due date edit and Escalate.
            save_due_date: 'Save due date',
            due_date_saved: 'Due date set to {date}',
            due_date_save_failed: 'The due date could not be saved.',
            escalate: 'Escalate',
            escalate_title: 'Escalate this task',
            escalate_description: 'Escalating moves the task to Escalated. Your reason is recorded with the status change and shown beside it in the Activity Timeline.',
            escalate_reason: 'Reason for escalating',
            escalate_reason_required: 'Enter a reason for escalating.',
            escalating: 'Escalating...',
            sr_escalated: 'Task escalated',
            escalate_failed: 'The task could not be escalated.',
            convert_to_workflow: 'Uitvoeren als workflow',
            convert_modal_title: 'Converteer taak naar werkstroomuitvoering',
            convert_workflow_label: 'Werkstroom',
            convert_workflow_placeholder: 'Kies een werkstroom...',
            convert_attachments_label: 'Bijlagen',
            convert_attachments_empty: 'Geen bijlagen bij deze taak.',
            convert_persona_label: 'Persona-overschrijving (optioneel)',
            convert_submit: 'Start de werkstroomuitvoering',
            convert_starting: 'Werkstroomuitvoering starten...',
            convert_started: 'Workflowrun gestart - openingsrun...',
            convert_disabled_in_flight: 'Er wordt al een werkstroom uitgevoerd voor deze taak.',
            linked_workflow_chip: 'Van {workflow_title}',
            linked_chat_chip: 'Van AI Chat',
            linked_more: '+{count} meer',
        },
        calendar: {
            title: 'Taakkalender',
            page_title: 'Kalender',
            create_task: 'Taak aanmaken',
            month: 'Maand',
            week: 'Week',
            day: 'Dag',
            today: 'Vandaag',
            more_tasks: '+{count} meer',
            no_tasks_day: 'Geen taken deze dag',
            all_day: 'Hele dag',
            quick_create_tooltip: 'Taak aanmaken voor deze dag',
            view_changed: 'Kalenderweergave gewijzigd naar {view}',
            tasks_in_view: '{count} taken in weergave',
            task_in_view: '{count} taak in beeld',
            filter_my_tasks: 'Mijn taken',
            filter_overdue: 'Achterstallig',
            filter_high_priority: 'Hoge prioriteit',
            filter_all: 'Alle',
        },
        filters: {
            assignee: 'Toegewezene',
            me: 'Mij',
            role: 'Rol',
            status: 'Status',
            priority: 'Prioriteit',
            task_type: 'Taaktype',
            source_module: 'Bronmodule',
            organization: 'Organisatie',
            sla_breached_only: 'Alleen SLA-geschonden tonen',
            clear_filters: 'Filters wissen',
            apply_filters: 'Toepassen',
            source_label: 'Bron',
            source_manual: 'Handmatig',
            source_module_kind: 'Module-gegenereerd',
            source_chat: 'Van AI Chat',
            source_workflow_run: 'Van AI Workflowuitvoering',
        },
        status: {
            pending: 'In afwachting',
            in_progress: 'In behandeling',
            blocked: 'Geblokkeerd',
            completed: 'Voltooid',
            overdue: 'Te laat',
            escalated: 'Geëscaleerd',
            cancelled: 'Geannuleerd',
        },
        priority: {
            critical: 'Kritiek',
            high: 'Hoog',
            medium: 'Gemiddeld',
            low: 'Laag',
        },
        task_types: {
            manual_task: 'Handmatige taak',
            dsr_case: 'DSR-zaak',
            dsr_review: 'DSR-beoordeling',
            dsr_escalation: 'DSR-escalatie',
            dpia_review: 'DPIA-beoordeling',
            dpia_approval: 'DPIA-goedkeuring',
            transfer_review: 'Overdrachtsbeoordeling',
            incident_task: 'Incidenttaak',
            incident_notification: 'Incidentmelding',
            playbook_step: 'Playbook-stap',
            regulator_deadline: 'Regelgevingsdeadline',
            monitoring_task: 'Monitoringtaak',
            kpi_alert: 'KPI-waarschuwing',
            vendor_review: 'Leveranciersbeoordeling',
            vendor_questionnaire: 'Leveranciersvragenlijst',
            governance_review: 'Bestuursbeoordeling',
            drift_alert: 'Afwijkingswaarschuwing',
            governance_approval: 'Bestuursgoedkeuring',
            deletion_task: 'Verwijderingstaak',
            deletion_verification: 'Verwijderingsverificatie',
            access_review: 'Toegangsbeoordeling',
            sod_alert: 'SoD-waarschuwing',
            consent_workflow: 'Toestemmingsworkflow',
            consent_alert: 'Toestemmingswaarschuwing',
            property_review: 'Eigenschapsbeoordeling',
            inconsistency_alert: 'Inconsistentiewaarschuwing',
            form_review: 'Formulierbeoordeling',
            dossier_deadline: 'Dossierdeadline',
            submission_task: 'Indieningstaak',
            translation_review: 'Vertalingsbeoordeling',
            governance_task: 'Bestuurstaak',
            reporting_deadline: 'Rapportagedeadline',
            scanner_alert: 'Scannerwaarschuwing',
            classification_review: 'Classificatiebeoordeling',
            lifecycle_task: 'Levenscyclustaak',
            discovery_review: 'Ontdekkingsbeoordeling',
            forseti_task: 'Forseti Task',
        },
        training_assignment: 'Trainingstoewijzing',
        task_type_groups: {
            training: 'Training',
            manual: 'Handmatig',
            dsr: 'DSR / Rechtenverzoeken',
            assessments: 'Beoordelingen',
            incidents: 'Incidenten',
            incident_ops: 'Incidentoperaties',
            monitoring: 'Toezicht',
            vendor_risk: 'Leveranciersrisico',
            governance: 'Bestuur',
            deletion: 'Verwijdering',
            access_review: 'Toegangsbeoordeling',
            consent: 'Toestemming',
            consent_properties: 'Toestemmingseigenschappen',
            forms: 'Formulieren',
            localization: 'Lokalisatie',
            program_governance: 'Programmabestuur',
            privacy_scanner: 'Privacyscanner',
            classification: 'Classificatie',
            discovery: 'Ontdekking',
            forseti: 'Forseti',
        },
        create: {
            title: 'Taak aanmaken',
            task_title: 'Titel',
            task_description: 'Beschrijving',
            task_type: 'Taaktype',
            task_type_label: 'Taaktype',
            task_type_placeholder: 'Selecteer een taaktype',
            priority: 'Prioriteit',
            assignees: 'Toegewezenen',
            assignee_search_placeholder: 'Gebruikers zoeken...',
            assignee_search_no_results: 'No users found.',
            assignee_search_loading: 'Loading…',
            tags_placeholder: 'tag1, tag2',
            remove_assignee: '{name} verwijderen',
            due_date: 'Vervaldatum',
            start_date: 'Startdatum',
            checklist: 'Controlelijst',
            tags: 'Labels',
            add_checklist_item: 'Checklistitem toevoegen',
            validation: {
                title_required: 'Titel is verplicht',
                title_max_length: 'Titel mag maximaal 500 tekens bevatten',
                description_max_length: 'Beschrijving mag maximaal 5000 tekens bevatten',
                due_date_before_start: 'Vervaldatum moet na startdatum liggen',
                max_reminders: 'Maximaal 5 herinneringen per taak',
            },
            reminders: {
                title: 'Herinneringen',
                add: 'Herinnering toevoegen',
                at_due_date: 'Op vervaldatum',
                minutes_before: '{count} minuten eerder',
                hours_before: '{count} uur eerder',
                days_before: '{count} dagen ervoor',
                weeks_before: '{count} weken eerder',
                custom: 'Aangepast',
                channel_platform: 'Platform',
                channel_email: 'E-mail',
                channel_both: 'Beide',
                offset_label: 'Herinnering {n}: tijdstip',
                channel_label: 'Herinnering {n}: kanaal',
                remove_label: 'Herinnering {n} verwijderen',
            },
            workflow: {
                label: 'Werkstroom',
                none: 'Geen (handmatig statusbeheer)',
                steps_count: '{count} stappen',
                preview_label: 'Workflowstappen',
            },
        },
        errors: {
            fetch_failed: 'Taken konden niet worden geladen.',
            save_failed: 'Taak kon niet worden opgeslagen.',
            delete_failed: 'Taak kon niet worden verwijderd.',
            status_change_failed: 'Taakstatus kon niet worden gewijzigd.',
            assign_failed: 'Taak kon niet worden toegewezen.',
            comment_failed: 'Opmerking kon niet worden opgeslagen.',
            checklist_failed: 'Bijwerken van checklist mislukt.',
            dependency_failed: 'Bijwerken van afhankelijkheid mislukt.',
            workflow_failed: 'Workflow kon niet worden opgeslagen.',
            workflow_activate_failed: 'Workflow kon niet worden geactiveerd.',
            workflow_delete_failed: 'Workflow kon niet worden verwijderd.',
            template_install_failed: 'Sjabloon kon niet worden geïnstalleerd.',
            search_failed: 'Zoeken mislukt.',
            convert_failed: 'Kan de werkstroomuitvoering niet starten.',
            convert_quota_exhausted: 'Tokenemmer laag. Vul bij voordat u opnieuw begint.',
        },
        dependency_types: {
            blocks: 'Blokkeert',
            blocked_by: 'Geblokkeerd door',
            related: 'Gerelateerd',
        },
        workflow: {
            current_step: 'Huidige stap',
            transition: 'Overgang',
            history: 'Stapgeschiedenis',
            no_workflow: 'Geen workflow toegewezen',
            detail_title: 'Workflowdetails',
            list_title: 'Workflowdefinities',
            create_workflow: 'Workflow aanmaken',
            browse_templates: 'Sjablonen bekijken',
            template_catalog_title: 'Workflowsjablooncatalogus',
            name: 'Naam',
            description: 'Beschrijving',
            task_types: 'Taaktypen',
            steps: 'Stappen',
            version: 'Versie',
            created: 'Aangemaakt',
            actions: 'Acties',
            status_draft: 'Concept',
            status_active: 'Actief',
            status_archived: 'Gearchiveerd',
            activate: 'Activeren',
            archive: 'Archiveren',
            save: 'Opslaan',
            edit: 'Bewerken',
            delete: 'Verwijderen',
            clone: 'Klonen',
            install: 'Installeren',
            installed: 'Geïnstalleerd',
            system: 'Systeem',
            general_settings: 'Algemene instellingen',
            default_assignment: 'Standaardtoewijzing',
            default_sla: 'Standaard SLA',
            workflow_steps: 'Workflowstappen',
            add_step: 'Stap toevoegen',
            step_name: 'Stapnaam',
            step_status: 'Statuswaarde',
            assignment_strategy: 'Toewijzingsstrategie',
            assignment_role: 'Toewijzingsrol',
            sla_hours: 'SLA-uren',
            sla_calendar_type: 'Kalendertype',
            working_days: 'Werkdagen',
            calendar_days: 'Kalenderdagen',
            is_terminal: 'Eindstap',
            required_fields: 'Verplichte velden',
            checklist_template: 'Checklistsjabloon',
            transitions: 'Overgangen',
            add_transition: 'Overgang toevoegen',
            from_step: 'Van',
            to_step: 'Naar',
            permission: 'Toestemming',
            sla_action: 'SLA-actie',
            notify: 'Melden',
            escalation_rules: 'Escalatieregels',
            add_rule: 'Regel toevoegen',
            trigger: 'Trigger',
            threshold: 'Drempelwaarde',
            action: 'Actie',
            target_role: 'Doelrol',
            trigger_sla_threshold: 'SLA-drempel',
            trigger_sla_breach: 'SLA-schending',
            trigger_blocked_duration: 'Geblokkeerde duur',
            trigger_manual: 'Handmatig',
            action_notify: 'Melden',
            action_escalate: 'Escalatieniveau',
            action_reassign: 'Opnieuw toewijzen',
            sla_start: 'Begin',
            sla_pause: 'Pauzeren',
            sla_resume: 'Hervatten',
            sla_stop: 'Stoppen',
            strategy_manual: 'Handmatig',
            strategy_role_based: 'Rolgebaseerd',
            strategy_round_robin: 'Ronde Robin',
            strategy_least_loaded: 'Minst belast',
            strategy_creator: 'Aanmaker',
            confirm_delete: 'Weet u zeker dat u deze workflow wilt verwijderen?',
            confirm_archive: 'Weet u zeker dat u deze workflow wilt archiveren?',
            cannot_delete_active: 'Actieve workflows kunnen niet worden verwijderd. Archiveer eerst.',
            validation_errors: 'Workflowvalidatiefouten',
            step_count: '{count} stappen',
            no_workflows: 'Geen workflows gevonden.',
            no_templates: 'Geen sjablonen beschikbaar.',
            sr_templates_count: '{count} sjablonen beschikbaar',
            template_installed: 'Sjabloon succesvol geïnstalleerd.',
            activate_success: 'Workflow succesvol geactiveerd.',
            archive_success: 'Workflow succesvol gearchiveerd.',
            save_success: 'Workflow succesvol opgeslagen.',
            delete_success: 'Workflow succesvol verwijderd.',
            applicable_task_types: 'Toepasselijke taaktypen',
            steps_column: 'Stappen',
            status_value_hint: 'Workflow-statussleutel die door overgangen wordt gebruikt. Kleine letters, cijfers en underscores.',
            from_status: 'Van-status',
            to_status: 'Naar-status',
            required_permissions: 'Vereiste rechten',
            transition_required_fields: 'Verplichte velden (overgang)',
            auto_assign: 'Automatisch toewijzen',
            auto_assign_role: 'Rol voor automatisch toewijzen',
            threshold_percent: 'Drempel (%)',
            blocked_hours: 'Geblokkeerde uren',
            notify_roles: 'Rollen op de hoogte stellen',
            escalate_to_role: 'Escaleren naar rol',
            notification_template: 'Meldingssjabloon',
            action_escalate_tier: 'Niveau escaleren',
            create_modal_title: 'Workflow maken',
            create_modal_description: 'Definieer stappen, overgangen en escalatieregels. De workflow wordt als concept gemaakt.',
            create_modal_footer_note: 'Gemaakt als concept. Activeer deze zodra de grafiek geldig is.',
            validation_name_required: 'Workflownaam is verplicht.',
            validation_task_types_required: 'Selecteer minstens één toepasselijk taaktype.',
            validation_min_one_step: 'Voeg minstens één stap toe.',
            validation_min_one_transition: 'Voeg minstens één overgang toe.',
            validation_step_name_required: 'Stapnaam is verplicht.',
            validation_status_value_required: 'Statuswaarde is verplicht.',
            validation_status_value_duplicate: 'Statuswaarde moet uniek zijn over stappen.',
            validation_transition_endpoints_required: 'Selecteer zowel een van-status als een naar-status.',
            validation_transition_unknown_status: 'Deze overgang verwijst naar een status die geen enkele stap definieert.',
            validation_sla_positive: 'SLA-uren moeten groter zijn dan nul. Laat leeg voor geen SLA.',
            validation_threshold_percent_range: 'De drempel moet tussen 0 en 100 liggen.',
            validation_blocked_hours_positive: 'Geblokkeerde uren moeten groter zijn dan nul.',
            warning_no_terminal_step: 'Geen eindstap. Activering mislukt totdat één stap als eind is gemarkeerd.',
            warning_step_no_outgoing: 'Niet-eindstappen zonder uitgaande overgangen: {statuses}',
            warning_unreachable_steps: 'Stappen die niet bereikbaar zijn vanaf de eerste stap: {statuses}',
            orphaned_transitions_warning: '{count} overgangen verwijzen naar een verwijderde stap.',
            remove_orphaned_transitions: 'Verweesde overgangen verwijderen',
            transitions_renamed_announcement: '{count} overgangen bijgewerkt naar de nieuwe statuswaarde.',
            add_checklist_item: 'Checklist-item toevoegen',
            checklist_item_label: 'Checklist-item',
        },
        columns: {
            priority: 'Prioriteit',
            title: 'Titel',
            status: 'Status',
            type: 'Typ',
            assignees: 'Toegewezene(n)',
            due_date: 'Vervaldatum',
            source: 'Bron',
            actions: 'Acties',
        },
        groups: {
            overdue: 'Te laat',
            due_today: 'Vandaag verschuldigd',
            due_this_week: 'Deze week verschuldigd',
            due_later: 'Later verschuldigd',
            no_due_date: 'Geen vervaldatum',
            closed: 'Voltooid of geannuleerd',
        },
        board: {
            title: 'Taakbord',
            drag_hint: 'Sleep taken tussen kolommen om de status te wijzigen',
            empty_column: 'Geen taken',
            keyboard_hint: 'Toetsenbord: pijltoetsen om te navigeren, spatie om een taak op te pakken, links/rechts om kolom te selecteren, Enter om neer te zetten, Escape om te annuleren.',
            sr_moving: '{title} verplaatsen. Boven {column}. Druk Enter om neer te zetten, Escape om te annuleren.',
            sr_tasks_shown: '{count} taken weergegeven',
            sr_picked_up: '{title} opgepakt',
            sr_returned: '{title} is teruggekeerd naar {column}',
            sr_dropped: '{title} neergezet in {column}, positie {position} van {total}',
            sr_move_cancelled: 'Verplaatsing geannuleerd',
            sr_over_column: 'Boven {column}',
            sr_moved_to: '{title} verplaatst naar {column}',
            invalid_transition: 'Kan taak niet verplaatsen van {from} naar {to}.',
            terminal_status: 'Deze taak heeft een eindstatus en kan niet worden verplaatst.',
            invalid_drop_target: 'Ongeldig doel',
        },
        team: {
            title: 'Teamwerkbelasting',
            assignee: 'Toegewezene',
            active: 'Actief',
            overdue: 'Te laat',
            blocked: 'Geblokkeerd',
            completed_7d: 'Voltooid (7d)',
            load: 'Belasting',
            load_high: 'Hoog',
            load_medium: 'Gemiddeld',
            load_normal: 'Normaal',
        },
        graph: {
            title: 'Afhankelijkheidsgrafiek',
            list_view: 'Lijstweergave',
            graph_view: 'Grafiekweergave',
            zoom_in: 'Inzoomen',
            zoom_out: 'Uitzoomen',
            reset_zoom: 'Opnieuw instellen',
            blocks_label: 'blocks',
            related_label: 'related',
            no_dependencies: 'Geen afhankelijkheden om te visualiseren.',
        },
        add_dependency_modal: {
            title: 'Afhankelijkheid toevoegen',
            search_placeholder: 'Zoek taken op titel of ID...',
            no_results: 'Geen taken gevonden.',
            this_blocks: 'Deze taakblokken zijn geselecteerd',
            this_blocked_by: 'Deze taak is geblokkeerd door geselecteerd',
            related: 'Gerelateerd',
            max_dependencies: 'Maximaal 10 afhankelijkheden per taak.',
            circular_error: 'Kan niet toevoegen: zou een circulaire afhankelijkheid creëren.',
        },
        conflict_detection: {
            title: 'Conflictdetectie',
            unresolved: 'onopgelost',
            no_conflicts: 'Geen conflicten gedetecteerd',
            section: 'Sectie',
            field: 'Veld',
            dept_a: 'Afdeling A',
            value_a: 'Waarde A',
            dept_b: 'Afdeling B',
            value_b: 'Waarde B',
            severity: 'Ernst',
            status: 'Status',
            resolved: 'Opgelost',
            open: 'Openen',
            resolve: 'Oplossen',
            load_failed: 'load conflicts mislukt',
            resolve_success: 'Conflict succesvol opgelost',
            resolve_failed: 'resolve conflict mislukt',
            severity_critical: 'Kritisch',
            severity_warning: 'Waarschuwing',
            severity_info: 'Info',
        },
        consolidation: {
            title: 'Consolidatievoortgang',
            add_child: 'Subtaak toevoegen',
            child_task_id: 'Subtaak-ID',
            department_id: 'Afdelings-ID',
            link: 'Koppelen',
            overall_completion: 'Algehele voltooiing',
            departments_complete: 'Afdelingen voltooid',
            overdue: 'Te laat',
            ready: 'Gereed',
            not_ready: 'Niet gereed',
            master_status: 'Hoofdstatus',
            department_breakdown: 'Afdelingsoverzicht',
            department: 'Afdeling',
            status: 'Status',
            completion: 'Voltooiing',
            due_date: 'Vervaldatum',
            overdue_flag: 'Te laat',
            child_tasks: 'Subtaken',
            task: 'Taak',
            load_failed: 'load consolidation data mislukt',
            child_linked: 'Subtaak succesvol gekoppeld',
            link_failed: 'link child task mislukt',
            confirm_unlink: 'Weet u zeker dat u deze subtaak wilt ontkoppelen?',
            child_unlinked: 'Subtaak ontkoppeld',
            unlink_failed: 'Ontkoppelen van subtaak mislukt',
            unlink: 'Ontkoppelen',
            status_completed: 'Voltooid',
            status_in_progress: 'In uitvoering',
            status_pending: 'In afwachting',
            select_child_task: 'Selecteer subtaak',
            select_department: 'Selecteer afdeling',
        },
        section_assignments: {
            title: 'Sectietoewijzingen',
            assign_section: 'Sectie toewijzen',
            section_ref: 'Sectiereferentie',
            section_label: 'Sectielabel',
            department_id: 'Afdelings-ID',
            department: 'Afdeling',
            assignee_id: 'Toegewezen-ID',
            assignee: 'Toegewezen persoon',
            mandatory: 'Verplicht',
            assign: 'Toewijzen',
            no_assignments: 'Geen sectietoewijzingen',
            status: 'Status',
            completion: 'Voltooiing',
            mark_complete: 'Voltooien',
            load_failed: 'load section assignments mislukt',
            assign_success: 'Sectie succesvol toegewezen',
            assign_failed: 'assign section mislukt',
            status_updated: 'Status bijgewerkt',
            status_update_failed: 'Bijwerken van status mislukt',
            section_ref_placeholder: 'bijv. 3.2',
            status_completed: 'Voltooid',
            status_in_progress: 'In uitvoering',
            status_pending: 'In afwachting',
            select_department: 'Selecteer afdeling',
            select_assignee: 'Selecteer persoon',
        },
        overdue_columns: {
            days_overdue: 'Dagen te laat',
        },
        regulatory_info: {
            deadline_context: 'Bij het aanmaken van DPIA-taken moeten vervaldatums overeenkomen met PDPL-indieningsdeadlines: afdelingsinvoer = 10 werkdagen. Cyberbeveiligings- en telecomdeadlines zijn ook van toepassing.',
            overdue_deadline_context: 'Regelgevende deadlines uit pakketgegevens: PDPL afdelingsinvoer = 10 werkdagen, aanvullend antwoord = 15 werkdagen, herindiening = 10 werkdagen. SLA-geschonden taken worden visueel onderscheiden.',
            section_assignment_context: 'DPIA multi-afdelingsworkflow (PDPL-P1): DPO wijst dossiersecties toe aan afdelingen. Afdelingsinvoerdeadline: 10 werkdagen.',
            template_catalog_context: 'Systeemsjablonen dekken kaderspecifieke processen: PDPL DPIA, Datawet-attestering, cyberbeveiligingsincidentrespons, telecom-autoriteitsverzoeken en AI-registratie. Systeemsjablonen zijn niet bewerkbaar.',
            workflow_instantiation_context: 'Geinstalleerde sjablonen kunnen worden geinstantieerd naar live workflows. Taken worden automatisch aangemaakt met berekende vervaldatums.',
        },
        overdue: {
            days_overdue: 'dagen te laat',
        },
    },
    notifications: {
        bell: {
            title: 'Meldingen',
            unread_count: 'unread',
        },
        drawer: {
            title: 'Meldingen',
            mark_all_read: 'Markeer alles als gelezen',
            no_notifications: 'Geen meldingen',
            filter: {
                all: 'All',
                unread: 'Ongelezen',
                tasks: 'Taken',
                approvals: 'Goedkeuringen',
                alerts: 'Waarschuwingen',
            },
        },
        preferences: {
            title: 'Meldingsvoorkeuren',
            description: 'Configureer hoe u meldingen ontvangt',
            category: {
                label: 'Categorie',
                task_assigned: 'Taak toegewezen',
                task_sla_approaching: 'SLA Ik nader',
                task_overdue: 'Taak achterstallig',
                task_completed: 'Taak voltooid',
                task_escalated: 'Taak geëscaleerd',
                workflow_transition: 'Workflow-transitie',
                approval_requested: 'Goedkeuring aangevraagd',
                comment_added: 'Commentaar toegevoegd',
                weekly_digest: 'Wekelijkse samenvatting',
                sla_breach_report: 'SLA Schendingsrapport',
                system_alert: 'Systeemwaarschuwing',
                // English until Phase 25 (R-57).
                rights_request_received: 'New Rights Request',
                // English until Phase 25 (R-57).
                acknowledgment_reminder: 'Acknowledgment Reminder',
                approval_expired: 'Approval Request Expired',
                scheduled_export_delivered: 'Scheduled Export Delivered',
                cross_border_transfer_added: 'Cross-Border Transfer Added',
            },
            channel: {
                platform: 'Platform',
                email: 'Email',
            },
            quiet_hours: {
                title: 'Rustige uren',
                start: 'Starten',
                end: 'End',
                timezone: 'Tijdzone',
            },
            digest: {
                title: 'Digest-frequentie',
                daily: 'Dagelijks',
                weekly: 'Wekelijks',
                none: 'Geen',
            },
            watched: {
                title: 'Gekeken entiteiten',
                empty: 'Geen bekeken entiteiten',
            },
            save: 'Bewaar voorkeuren',
            saving: 'Opslaan...',
            saved_success: 'Voorkeuren zijn succesvol opgeslagen',
            load_error: 'Kan opgeslagen voorkeuren niet laden. Standaardwaarden weergeven: wijzigingen worden opgeslagen wanneer u op Opslaan klikt.',
            enabled: 'Ingeschakeld',
            disabled: 'Uitgeschakeld',
            unwatch: 'Uitkijken',
            announce_loaded: 'Notification preferences loaded',
            save_error: 'Could not save your notification preferences. Your changes are still on the page; try saving again.',
        },
        admin_queue: {
            title: 'Beheerderswachtrij voor meldingen',
            description: 'E-mailbezorgingsstatus bewaken en mislukte berichten opnieuw proberen',
            load_error: 'Kan e-mailwachtrijgegevens niet laden',
            retry_error: 'Kan e-mail niet opnieuw proberen',
            filter_status: 'Filteren op status',
            all_statuses: 'Alle',
            refresh: 'Vernieuwen',
            status_queued: 'In wachtrij',
            status_sent: 'Verzonden',
            status_failed: 'Mislukt',
            status_bounced: 'Geweigerd',
            status_sending: 'Sending',
            col_recipient: 'Ontvanger',
            col_category: 'Categorie',
            col_template: 'Sjabloon',
            col_status: 'Status',
            col_retries: 'Pogingen',
            col_created: 'Aangemaakt',
            col_sent: 'Verzonden op',
            col_actions: 'Acties',
            no_items: 'Geen items in de e-mailwachtrij gevonden',
            retry: 'Opnieuw proberen',
            retrying: 'Opnieuw aan het proberen...',
            prev: 'Vorige',
            next: 'Volgende',
            access_denied_title: 'Beheerderstoegang vereist',
            access_denied: 'Deze pagina is alleen beschikbaar voor beheerders. Neem contact op met uw beheerder als u toegang nodig heeft.',
            announce_queue_loaded: 'E-mailwachtrij geladen, {count} items',
            announce_processed: 'Verwerkte {count} e-mail(s)',
            process_now: 'Verwerk nu',
            processing_ellipsis: 'Verwerken…',
        },
    },
    ui_assist: {
        toolbar: {
            title: 'UI-assistentie',
            activate: 'Activeer',
            deactivate: 'Deactiveren',
            connected: 'Verbonden',
            disconnected: 'Verbinding verbroken',
            disconnected_hint: "Verbinding verbroken: voer 'npm run relay' uit om verbinding te maken met Claude CLI",
            open_panel: 'Open het UI Assist-paneel',
            history: 'Geschiedenis',
            settings: 'Instellingen',
        },
        prompt: {
            placeholder: 'Beschrijf de verandering die u wilt doorvoeren...',
            send: 'Verzenden',
            cancel: 'Annuleer',
            char_count: '{count} / {max}',
        },
        session: {
            streaming: 'Verwerken…',
            complete: 'Compleet',
            error: 'Fout',
            abort: 'Afbreken',
            abort_confirm: 'Weet u zeker dat u wilt afbreken?',
            aborted_hint: 'Sessie werd onderbroken — u kunt vanaf hier verdergaan.',
            undo: 'Ongedaan maken',
            redo: 'Opnieuw uitvoeren',
            retry: 'Opnieuw proberen',
            dismiss: 'Negeren',
        },
        label: {
            component: 'Onderdeel',
            element: 'Element',
            file: 'Bestand',
        },
        response: {
            you: 'You',
            claude: 'Claude',
            empty: 'Nog geen berichten. Selecteer elementen en stuur een prompt om aan de slag te gaan.',
        },
        panel: {
            tab_conversation: 'Chatten',
            tab_tasks: 'Taken',
            tab_sprint: 'Sprinten',
            tab_sessions: 'Sessies',
            tab_lobby: 'Lobby',
            follow_up: 'Vervolg',
            followup_placeholder: 'Stel een vervolgvraag…',
            interrupt_placeholder: 'Typ om te onderbreken en om te leiden…',
            send_interrupt: 'Verzenden & Onderbreken',
            will_interrupt: 'Breekt het huidige antwoord af en verzendt dit in plaats daarvan',
            expand: 'Uitvouwen naar volledig scherm',
            collapse: 'Samenvouwen naar zijpaneel',
            no_sessions: 'Geen eerdere sessies. Selecteer elementen en stuur een bericht om te beginnen.',
            session_active: 'Actief',
            session_ended: 'Beëindigd',
            session_messages: '{count} berichten',
            switch_session: 'Overschakelen naar deze sessie',
            load_session: 'Sessiegeschiedenis laden',
            session_restored: 'Sessie hersteld uit de geschiedenis',
            archive_session: 'Archiefsessie',
            archive_confirm: 'Deze sessie archiveren? Het sessiebestand wordt verwijderd.',
            new_session: 'Nieuwe sessie',
            new_session_desc: 'Gebruik Activeren om een UI-element te selecteren en een nieuwe sessie te starten',
            start_new_session: 'Nieuwe sessie starten',
            no_active_session_chat: 'Start een nieuwe sessie of gebruik Activeren om een UI-element op de pagina te selecteren om te beginnen.',
            no_active_session_tasks: 'Start een nieuwe sessie of selecteer een bestaande sessie om taken aan te maken.',
            no_active_session_sprint: 'Start een nieuwe sessie of selecteer een bestaande sessie om een sprint te plannen.',
            previous_sessions: 'Vorige sessies',
        },
        tasks: {
            none_found: 'Geen taken gevonden in het antwoord. Vraag Claude om taken voor te stellen.',
            selected: 'selected',
            create_selected: 'Taken maken',
            execute_tasks: 'Taken uitvoeren',
            creating: 'Creëren…',
            created: 'taken gecreëerd',
            create_error: 'Kan taak niet maken',
            plan_sprint: 'Sprint plannen',
            priority_critical_desc: 'Blokkerend — direct oplossen',
            priority_high_desc: 'Belangrijk — deze sprint oplossen',
            priority_medium_desc: 'Normaal — plannen voor komende sprint',
            priority_low_desc: 'Nice to have — backlog-kandidaat',
            add_custom: 'Taak toevoegen',
            add_title_placeholder: 'Taaktitel…',
            add_desc_placeholder: 'Beschrijving (optioneel)',
            add_save: 'Toevoegen',
            add_cancel: 'Annuleren',
        },
        sprint: {
            title: 'Sprintplanner',
            assigned: 'assigned',
            new_placeholder: 'Sprintnaam (bijv. Sprint 18.1.0)',
            add: 'Add',
            backlog: 'Achterstand',
            move_to: 'Ga naar…',
            empty: 'Maak een sprintgroep en wijs er taken aan toe.',
            create_all: 'Maak alle taken',
            remove: 'Verwijderen',
            drop_here: 'Wijs taken toe vanuit de onderstaande backlog',
            add_task: 'Taak toevoegen vanuit achterstand…',
        },
        feature_disabled: 'UI Assist is niet ingeschakeld',
        permission_denied: 'U heeft geen toestemming om UI Assist te gebruiken',
    },
    license: {
        title: 'Licentie & pakket',
        current_license: 'Huidige licentie',
        license_key: 'Licentiesleutel',
        activate: 'Activeer licentie',
        deactivate: 'Licentie deactiveren',
        validate_now: 'Valideer nu',
        status: 'Licentiestatus',
        active: 'Actief',
        expired: 'Verlopen',
        suspended: 'Opgeschort',
        invalid: 'Ongeldig',
        pending: 'In behandeling',
        no_license: 'Geen licentie',
        expires_at: 'Verloopt',
        last_validated: 'Laatst gevalideerd',
        domain: 'Gelicentieerd domein',
        activation_success: 'Licentie succesvol geactiveerd.',
        activation_failed: 'Licentieactivering mislukt.',
        domain_mismatch: 'Licentiedomein komt niet overeen met het huidige domein.',
        deactivation_success: 'Licentie gedeactiveerd.',
        validation_success: 'Licentie succesvol gevalideerd.',
        validation_failed: 'Licentievalidatie mislukt.',
        enter_key: 'Voer uw licentiesleutel in',
        confirm_deactivate: 'Weet u zeker dat u deze licentie wilt deactiveren? Modulebeperkingen zijn van toepassing op basis van het standaardniveau.',
        announce_loaded: 'License settings loaded',
        verification_stale: 'This licence has not been verified with the licence service for more than {days} days, so it reads as unverified. It reads as valid again once a revalidation succeeds.',
        scope_installation: 'The licence and its package apply to the whole installation: every organisation on it. Only a platform administrator can activate, deactivate or change them.',
    },
    package_mgmt: {
        title: 'Pakket',
        module_column: 'Module',
        current_tier: 'Huidig pakket',
        light: 'Licht',
        standard: 'Standaard',
        enterprise_unlimited: 'Onderneming onbeperkt',
        change_package: 'Pakket wijzigen',
        upgrade: 'Upgraden',
        downgrade: 'Downgraden',
        target_tier: 'Doelpakket',
        new_license_key: 'Licentiesleutel voor doelpakket',
        confirm_change: 'Bevestig pakketwijziging',
        change_success: 'Pakket succesvol gewijzigd.',
        change_failed: 'Pakketwijziging mislukt.',
        modules_included: 'Modules inbegrepen',
        modules_restricted: 'Niet beschikbaar in huidig pakket',
        upgrade_to_enable: 'Upgrade om deze module in te schakelen.',
        module_availability: 'Beschikbaarheid van modules',
        comparison: 'Pakketvergelijking',
        power_users: 'Hoofdgebruikers',
        unlimited: 'Onbeperkt',
        impact_warning: 'Als u downgradet, worden de volgende modules uitgeschakeld:',
        invalid_license: 'De verstrekte licentiesleutel is niet geldig voor dit pakket.',
        tier_mismatch: 'De licentiesleutel is voor een andere pakketlaag.',
    },
    board_report: {
        title: 'Bestuurlijk Compliancerapport',
        subtitle: 'Samenvatting van compliance over alle modules',
        date_from: 'Datum vanaf',
        date_to: 'Datum tot',
        language: 'Rapporttaal',
        generate: 'Rapport genereren',
        generating: 'Rapport wordt gegenereerd...',
        generate_requires_export: 'Generating a report needs the permission to export governance reports. Ask an administrator to grant it.',
        download_pdf: 'PDF downloaden',
        close: 'Rapport sluiten',
        open_board_report: 'Bestuursrapport genereren',
        close_board_report: 'Bestuursrapport sluiten',
        report_period: 'Rapportperiode',
        generated_at: 'Gegenereerd op',
        generated_by: 'Gegenereerd door',
        organization: 'Organisatie',
        section_rights_requests: 'Rechtenverzoeken',
        section_vendors: 'Leveranciers',
        section_dpias: "DPIA's",
        section_consent: 'Toestemmingsbeleid',
        section_data_mapping: 'Datakoppeling',
        section_incidents: 'Incidenten',
        section_classification: 'Dataclassificatie',
        section_governance: 'Bestuur',
        open: 'Openen',
        closed: 'Gesloten',
        sla_compliance: 'SLA-naleving',
        total: 'Totaal',
        expired_dpa: 'Verlopen DPA',
        high_risk: 'Hoog risico',
        completed: 'Voltooid',
        overdue: 'Achterstallig',
        policies_active: 'Actief beleid',
        coverage: 'Dekking',
        systems: 'Systemen',
        completeness: 'Volledigheid',
        avg_resolution_days: 'Gem. oplostijd (dagen)',
        labeled: 'Gelabeld',
        unlabeled_systems: 'Ongelabelde systemen',
        compliance_score: 'Compliancescore',
        drift_events_open: 'Open afwijkingen',
        no_data: 'Geen gegevens beschikbaar voor de geselecteerde periode.',
        error_date_range: 'Selecteer zowel een begin- als einddatum.',
        table_category: 'Categorie',
        table_metric: 'Metriek',
        table_value: 'Waarde',
        showing_chart_view: 'Grafiekweergave wordt getoond',
        showing_table_view: 'Datatabelweergave wordt getoond',
        error_generate: 'Bestuursrapport kon niet worden gegenereerd.',
        playbook_evidence_title: 'Bewijs van playbook-uitvoering',
        playbook_evidence_description: 'Uitvoeringsactiviteit per playbook en bijgevoegde bewijsbestanden voor de rapportperiode.',
        playbook_name: 'Speelboek',
        playbook_execution_count: 'Uitvoeringen',
        playbook_steps_total: 'Stappen',
        playbook_steps_required: 'Bewijs vereist',
        playbook_steps_attached: 'Bewijs bijgevoegd',
        playbook_coverage_pct: 'Dekking',
        playbook_evidence_files: 'Bewijsbestanden',
        playbook_no_evidence: 'Geen playbook-uitvoeringen in deze periode.',
        playbook_download_file: 'Bestand downloaden',
    },
    framework_module_links: {
        title: 'Raamwerkkoppeling',
        modules: 'Gekoppelde modules',
        add_module: 'Module toevoegen',
        module_code: 'Modulecode',
        required: 'Verplicht',
        notes: 'Notities',
        link: 'Koppelen',
        no_modules: 'Geen modules gekoppeld',
        personas: "Gekoppelde persona's",
        add_persona: 'Persona toevoegen',
        persona_id: 'Persona-id',
        role_description: 'Rolbeschrijving',
        no_personas: "Geen persona's gekoppeld",
    },
    framework_channels: {
        title: 'Indieningskanalen & Deadlines',
        channels: 'Indieningskanalen',
        add_channel: 'Kanaal toevoegen',
        channel_type: 'Typ',
        name: 'Naam',
        instructions: 'Instructies',
        active: 'Actief',
        no_channels: 'Geen indieningskanalen geconfigureerd',
        portal: 'Portaal',
        in_person: 'Persoonlijk',
        postal: 'Post',
        email: 'E-mail',
        add: 'Toevoegen',
        deadlines: 'Regelgevingsdeadlines',
        add_deadline: 'Deadline toevoegen',
        label: 'Etiket',
        days: 'Dagen',
        day_type: 'Dagtype',
        calendar_days: 'Kalenderdagen',
        working_days: 'Werkdagen',
        description: 'Beschrijving',
        no_deadlines: 'Geen deadlines geconfigureerd',
    },
    regulatory_updates: {
        title: 'Regelgevingsupdates & Raamwerk-dashboard',
        coverage_dashboard: 'Raamwerkdekking-dashboard',
        no_frameworks: 'Geen raamwerken gevonden',
        framework: 'Raamwerk',
        status: 'Status',
        modules: 'Modules',
        templates: 'Sjablonen',
        forms: 'Formulieren',
        channels: 'Kanalen',
        deadlines: 'Deadlines',
        pending: 'Lopende updates',
        updates: 'Regelgevingsupdates',
        create_update: 'Update aanmaken',
        framework_id: 'Raamwerk-id',
        update_title: 'Titel',
        description: 'Beschrijving',
        effective_date: 'Inwerkingtreedingsdatum',
        create: 'Aanmaken',
        no_updates: 'Geen regelgevingsupdates',
        impact: 'Impact',
        assess: 'Beoordelen',
    },
    module_tips: nlModuleTips,
    framework_rights: {
        be_informed: 'Recht op informatie',
        consent: 'Recht op toestemming',
        refuse_consent: 'Recht om toestemming te weigeren',
        withdraw_consent: 'Recht om toestemming in te trekken',
        access: 'Recht van inzage',
        rectification: 'Recht op rectificatie',
        data_provision: 'Recht op gegevensverstrekking',
        deletion: 'Recht op verwijdering',
        restriction_of_processing: 'Recht op beperking van verwerking',
        object_to_processing: 'Recht van bezwaar tegen verwerking',
        complaint: 'Recht om een klacht in te dienen',
        denunciation: 'Recht op aangifte',
        lawsuit: 'Recht om een rechtszaak aan te spannen',
        claim_damages: 'Recht op schadevergoeding',
        request_protection_measures: 'Recht om beschermingsmaatregelen te verzoeken',
    },
    framework_content: {
        EU_EPRIVACY_DIRECTIVE: {
            display_name: 'Richtlijn 2002/58/EG (ePrivacy-richtlijn)',
            description: 'Moederrichtlijn voor nationale ePrivacy-omzettingen. Omvat cookies, privacy van elektronische communicatie, direct marketing. Elke lidstaat moet deze omzetten in nationaal recht.',
        },
        EU_GDPR: {
            display_name: 'Algemene Verordening Gegevensbescherming (EU) 2016/679',
            description: 'De Algemene Verordening Gegevensbescherming (GDPR) is een EU-verordening inzake gegevensbescherming en privacy. Het is rechtstreeks van toepassing in alle EU/EER-lidstaten.',
        },
        EU_NIS2_DIRECTIVE: {
            display_name: 'Richtlijn (EU) 2022/2555 (NIS2)',
            description: 'Moederrichtlijn voor nationale NIS2-omzettingen. Omvat cyberbeveiligingsrisicobeheer, incidentrapportage en beveiliging van de toeleveringsketen voor essentiële en belangrijke entiteiten.',
        },
        VN_AI_LAW_2026: {
            display_name: 'Vietnam AI wet (134/2025/QH15) — van kracht vanaf 1 maart 2026',
            description: 'Vietnam AI Wet (Luật Trí tuệ nhân tạo 134/2025/QH15) die de ontwikkeling, inzet en gebruik van kunstmatige-intelligentiesystemen regelt. Stelt verplichtingen op het gebied van risicoclassificatie, transparantie en etikettering, vereisten voor het melden van incidenten, verplichtingen voor effectbeoordeling en een gecontroleerd testmechanisme (sandbox) vast voor AI-activiteiten in Vietnam.',
        },
        VN_AI_DECREE_142_2026: {
            display_name: 'Decree 142/2026/NĐ-CP — AI Law Implementation',
            description: 'Decree 142/2026/NĐ-CP (Nghị định số 142/2026/NĐ-CP) detailing certain articles and implementing measures of the Vietnam AI Law (Law 134/2025/QH15). Issued 2026-04-30, effective 2026-05-01. Lead authority: Ministry of Science and Technology. Operationalises AI risk classification, classification dossier, reclassification review, high-risk conformity assessment, high-risk risk-management system, transparency / explanation / technical marking / visible labeling, serious AI incident reporting (72h / 5wd / 15d), state-agency AI impact assessment (Mẫu AI02), and the controlled testing / AI sandbox lifecycle (Mẫu AI03a-AI09b). Submission via the AI one-stop e-portal or MoST transitional channel. No statutory fines.',
        },
        VN_CYBERSEC_LAW: {
            display_name: 'Vietnam Cybersecurity-wet (tijdperk 24/2018)',
            description: 'De Vietnamese wet inzake cyberbeveiliging (wet nr. 24/2018/QH14) waarin eisen worden vastgelegd voor cyberbeveiliging, gegevenslokalisatie en incidentrapportage. Actief totdat deze wordt vervangen door wet 116/2025/QH15, van kracht op 01-07-2026.',
        },
        VN_CYBERSEC_DECREE_53: {
            display_name: 'Decreet 53 — Implementatie van de cyberbeveiligingswet',
            description: 'Decreet 53/2022/ND-CP ter implementatie van de cyberbeveiligingswet, met betrekking tot bepaling binnen de reikwijdte, triggers voor gegevenslokalisatie, vereisten voor lokale aanwezigheid en workflows voor samenwerking tussen autoriteiten.',
        },
        VN_CYBERSEC_LAW_2025: {
            display_name: 'Wet op cyberbeveiliging (116/2025/QH15)',
            description: 'De geconsolideerde wet van Vietnam inzake cyberbeveiliging (Wet 116/2025/QH15), ter vervanging van Wet 24/2018/QH14, van kracht per 1 juli 2026. Consolideert de reikwijdte van Decreet 53/2022/NĐ-CP (zie VN_CYBERSEC_DECREE_53).',
        },
        VN_DATA_LAW: {
            display_name: 'Gegevenswet Vietnam',
            description: 'De Vietnamese datawet (wet 60/2024/QH15) die uitgebreide vereisten voor databeheer vastlegt, waaronder data-inventarisatie, classificatie, gedeeld beheer en grensoverschrijdend beheer van gegevensoverdracht. Overlapt met VN_PDPL_LAW_2025 (persoonlijke gegevensaspecten) en VN_CYBERSEC_LAW_2025 (gegevenslokalisatie/classificatie).',
        },
        VN_DATA_LAW_DECREE_165_2025: {
            display_name: 'Decreet 165/2025/NĐ-CPĐ-CP (implementatie gegevenswet)',
            description: 'Decreet 165/2025/NĐ-CP ter implementatie van de Vietnamese gegevenswet (wet 60/2024/QH15). Biedt officiële formulieren voor de effectbeoordeling van grensoverschrijdende gegevensoverdracht, jaarlijkse risicobeoordelingsrapportage en toegang tot nationale databases. Definieert 11 officiële Mẫu số-vormen.',
        },
        VN_DATA_EXCHANGES_DRAFT_2026: {
            display_name: 'Ontwerpbesluit data-uitwisselingen 2026',
            description: 'Ontwerpdecreet betreffende de werking van platforms voor gegevensuitwisseling (Dự thảo Nghị định quy định về hoạt động của sàn dữ liệu). Regelt platformclassificatie, geschiktheid van deelnemers, controles op dataproducten en -services, sandbox-testen, transactieworkflow, elektronische contracten, certificaten van eigendomsrechten op gegevens en afhandeling van klachten/geschillen. Momenteel in openbare consultatie; nog niet afgekondigd. Citeert de wet inzake gegevens 60/2024/QH15, de wet inzake de bescherming van persoonsgegevens 91/2025/QH15, de wet inzake cyberbeveiliging 116/2025/QH15 en de wet inzake elektronische transacties 20/2023/QH15.',
        },
        VN_ECOMMERCE_LAW_2025: {
            display_name: 'Wet op de elektronische handel (122/2025/QH15)',
            description: 'De Vietnamese wet inzake elektronische handel (Luật Thương mại điện tử 122/2025/QH15) tot vaststelling van een uniform nationaal regime voor elektronische handel, dat betrekking heeft op binnenlandse en grensoverschrijdende platformoperaties, platformbeheer en -verantwoording, consumentenbescherming, controles op verkopers en partners, klachtenafhandeling, inhoudsmoderatie en verwijderingshandhaving, en gereedheid voor administratieve procedures voor het staatsbeheersysteem voor e-commerce.',
        },
        VN_PDPD_DECREE_13: {
            display_name: 'Decreet 13/2023/NĐ-CP',
            description: 'Decreet 13/2023/ND-CP, het operationele instrument voor PDPL taken, waaronder het indienen van dossiers, bestuursartefacten, grensoverschrijdende mechanismen en administratieve MoPS-procedures.',
        },
        VN_PDPL_LAW_2025: {
            display_name: 'Wet bescherming persoonsgegevens (91/2025/QH15)',
            description: "De Vietnamese wet op de bescherming van persoonsgegevens (wet 91/2025/QH15) die een alomvattend raamwerk voor de bescherming van persoonsgegevens tot stand brengt, van kracht vanaf 1 januari 2026. Heeft betrekking op nalevingsthema's A tot en met J: grondslagen voor rechtmatige verwerking, toestemming voor gevoelige gegevens, rechten van betrokkenen (toegang/rectificatie/verwijdering/beperking/bezwaar/portabiliteit), melding van inbreuken aan autoriteiten en betrokkenen, DPIA voor verwerking met een hoog risico, beheer van grensoverschrijdende overdrachten, DPO aanduiding, gegevens van verwerkingsactiviteiten, contractuele verplichtingen van de verwerker. Verplichte indiening door de autoriteit via MoPS (VN_PDPL_ADMIN_PROCEDURES_778_2026). Kruisverwijzingen: VN_PDPL_DECREE_356_2025 (uitvoeringsbesluit), VN_CYBERSEC_LAW_2025 (overlappingen op het gebied van cyberbeveiliging), VN_DATA_LAW (data governance).",
        },
        VN_PDPL_DECREE_356_2025: {
            display_name: 'Decreet 356/2025/ND-CP (PDPL Uitvoering)',
            description: 'Decreet 356/2025/ND-CP ter uitvoering van de Wet bescherming persoonsgegevens (Wet 91/2025/QH15). Omvat dossierarchivering, governance-artefacten, grensoverschrijdende mechanismen en certificering van dienstverleners. Bijlageformulier inventaris: Phụ lục I–VIII (effectbeoordeling, grensoverschrijdende overdracht, certificering). Administratieve MoPS-procedures via VN_PDPL_ADMIN_PROCEDURES_778_2026 (Besluit 778/QĐ-BCA-A05). Certificeringsworkflow: aanvraag → uitgifte → wijzigingsverzoek → heruitgave → intrekking. Kruisverwijzingen: VN_PDPL_LAW_2025 (moederwet), VN_CYBERSEC_LAW_2025 (gegevenslokalisatie), VN_DATA_LAW (overlappingen op het gebied van gegevensbeheer).',
        },
        VN_PDPL_ADMIN_PROCEDURES_778_2026: {
            display_name: 'MPS A05 Administratieve procedures (Besluit 778/QĐ-BCA-A05)',
            description: 'Besluit 778/QĐ-BCA-A05 (februari 2026) — Administratieve procedures voor de bescherming van persoonsgegevens onder A05 (MPS). Definieert de procedures voor het indienen, verwerken en uitgeven van resultaten, inclusief DLCN-HC-formulieren en Mẫu số wettelijke formulieren.',
        },
        VN_PDPL_ENFORCEMENT_DRAFT_2026: {
            display_name: 'Ontwerp Handhavingsbesluit 2026',
            description: 'Ontwerphandhavings- en sanctiebesluit voor overtredingen van de Wet bescherming persoonsgegevens (Wet 91/2025/QH15). Definieert de strafmaat, verzwarende/verzachtende factoren en handhavingsprocedures. Momenteel in conceptstatus in afwachting van afkondiging.',
        },
        VN_MOPS_ADMIN_SIMPLIFICATION_NQ22_2026: {
            display_name: 'Government Resolution NQ22/2026 — MoPS Administrative Simplification (Temporary Overlay)',
            description: 'Temporary procedural overlay (effective 2026-04-29, expiring 2027-03-01) introducing three official channels (National Public Service Portal, direct submission to MoPS, postal submission to MoPS) for PDPL Decree 356/2025 and Decision 778/2026 dossier submissions. Email is NOT a NQ22 official channel — a separate unofficial email-ZIP submission guidance (VN_PDPL_GUIDANCE_EMAIL_ZIP_DOSSIER_SUBMISSION_2026) is shipped with requires_legal_review: true. Procedural overlay only — does NOT alter substantive obligations, fine ranges, or the binding force of underlying frameworks. Review task scheduled 2027-02-01.',
        },
        VN_TELECOM_LAW_2023: {
            display_name: 'Vietnam Telecommunicatiewet 2023',
            description: 'Vietnam Telecommunicatiewet 2023 (Luật Viễn tông), een sectoroverlay voor telco-/ISP-/platformbedrijven met verplichtingen op het gebied van vertrouwelijkheid, openbaarmaking en afhandeling van autoriteitsverzoeken. Kruisverwijzingen: VN_CYBERSEC_LAW_2025 (beveiliging van informatiesystemen), VN_PDPL_LAW_2025 (verwerking van gebruikersgegevens), VN_DATA_LAW (gegevensbeheer).',
        },
        VN_TELECOM_DECREE_163_2024: {
            display_name: 'Decreet 163/2024/NĐ-CPĐ-CP (implementatie telecomwet)',
            description: 'Decreet 163/2024/NĐ-CP ter uitvoering van de telecommunicatiewet (wet 24/2023/QH15). Biedt officiële formulieren voor kennisgevingen aan telecomdiensten, licenties, registratie en ontwikkelingsplannen voor datacenters. Definieert 7 officiële Mẫu số-vormen.',
        },
    },
    framework_requirements: {
        vn_dp_draft_f: {
            title: 'Klachten, geschillen en risicobeheer',
            description: "Platformexploitanten moeten zorgen voor klachteninname, een workflow voor geschillenbehandeling met behoud van bewijsmateriaal, controles op risico-identificatie en vroegtijdige waarschuwing, en periodieke evaluatie van de risico's op het gebied van privacy, cyberbeveiliging en toegangsbeheer.",
        },
        vn_dp_draft_e: {
            title: 'Transactieworkflow en certificaat van eigendomsrechten op gegevens',
            description: 'Transacties vereisen accountregistratie, goedkeuring van de aanbieding, elektronische contractering, betalingsbevestiging, leveringsbewijs en uitgifte van een certificaat van eigendomsrechten op gegevens, indien van toepassing. Transactiegeschiedenis en traceerbaarheid moeten worden bewaard in een transactieregister.',
        },
        vn_dp_draft_d: {
            title: 'Sandbox- en testomgevingscontroles',
            description: 'Testomgevingen moeten worden geïsoleerd, met beperkte toegang, met beveiligingsmonitoring en worden beoordeeld voordat er enige output uit wordt gehaald. Er mogen geen brongegevens uit de testomgeving worden gehaald en het afsluiten/opschonen van testsessies moet worden vastgelegd.',
        },
        vn_dp_draft_c: {
            title: 'Bedieningselementen voor gegevensproducten en -services',
            description: 'Advertenties moeten worden ondersteund door bewijs van wettige bron en eigendom/gebruiksrecht, technische validatiegegevens, openbaarmakingen over de kwaliteit en gegevens over de beoordeling en goedkeuring van vermeldingen op het platform. Verboden gegevens moeten worden gefilterd voordat ze worden vermeld.',
        },
        vn_dp_draft_b: {
            title: 'Geschiktheid van deelnemers en onboarding',
            description: 'Platformexploitanten moeten de juridische status, juridische capaciteit en voorwaardelijke zakelijke vereisten voor verkopers en kopers verifiëren, transactie- en betaalrekeningen registreren en een deelnemersregister bijhouden.',
        },
        vn_dp_draft_a: {
            title: 'Classificatie en bedrijfsmodel van dataplatforms',
            description: 'Platformexploitanten moeten een classificatie van platformtype vaststellen (nationaal dataplatform versus andere dataplatforms), de geschiktheid en verantwoordelijkheden van operators definiëren, en prijzen, vergoedingen, werkingsregels en technisch bestuur documenteren.',
        },
        d165_r5: {
            title: 'Toegang tot nationale database-informatie',
            description: 'Voor verzoeken om informatie uit de nationale digitale databank te halen/gebruiken worden voorgeschreven formulieren gebruikt (Mẫu số 07a/07b).',
        },
        d165_r4: {
            title: 'Jaarlijkse risicobeoordelingsrapportage',
            description: 'Jaarlijkse risicobeoordelingsrapporten moeten worden ingediend met behulp van voorgeschreven formulieren (Mẫu số 05a/05b, 06).',
        },
        d165_r3: {
            title: 'Kennisgeving van beëindiging van de grensoverschrijdende overdracht',
            description: 'Beëindiging van grensoverschrijdende overdracht moet worden gemeld met behulp van voorgeschreven formulieren (Mẫu số 04a/04b).',
        },
        d165_r2: {
            title: 'Kennisgeving van beoordelingsresultaten',
            description: 'De autoriteit geeft mededelingen over beoordelingsresultaten uit volgens het voorgeschreven formulier (Mẫu số 03).',
        },
        d165_r1: {
            title: 'Indiening van effectbeoordeling van grensoverschrijdende gegevensoverdracht',
            description: 'Organisaties moeten grensoverschrijdende gegevensoverdracht/effectbeoordelingsdossiers indienen met behulp van voorgeschreven formulieren (Mẫu số 01a/01b, 02).',
        },
        a964190d_8d1d_4284_bc7d_4023e2866751: {
            title: 'Toestemming voor cookies en soortgelijke technologieën',
            description: 'Het opslaan van of toegang krijgen tot informatie op eindapparatuur is alleen toegestaan met voorafgaande geïnformeerde toestemming, behalve voor technisch noodzakelijke opslag.',
        },
        '8437d96d_cf01_4f87_89f6_4faaf8eefdf6': {
            title: 'Vertrouwelijkheid van communicatie',
            description: 'De lidstaten waarborgen de vertrouwelijkheid van communicatie en daarmee samenhangende verkeersgegevens door middel van een openbaar communicatienetwerk en openbaar beschikbare elektronische-communicatiediensten.',
        },
        c5420073_50c6_4981_816a_398460e5f546: {
            title: 'Toestemming voor direct marketing (opt-in)',
            description: 'Voor het gebruik van elektronische communicatiesystemen voor direct marketing is voorafgaande toestemming van de abonnee vereist.',
        },
        c8bf67d2_874b_4647_92d7_39aaa3230819: {
            title: 'Zachte opt-in voor bestaande klanten',
            description: 'Directe marketing naar bestaande klanten voor vergelijkbare producten/diensten is zonder toestemming toegestaan als er een opt-outmogelijkheid bestaat.',
        },
        b546df40_9217_4204_b71c_5af5022a0ff9: {
            title: 'Inbreukmelding voor telecomaanbieders',
            description: 'Aanbieders van openbare elektronische-communicatiediensten stellen de bevoegde nationale autoriteit in kennis van inbreuken op persoonsgegevens.',
        },
        '5bd66ae4_dee3_4611_9104_71742df55484': {
            title: 'Recht om bezwaar te maken tegen direct marketing',
            description: 'Abonnees krijgen de mogelijkheid om op elk moment en kosteloos bezwaar te maken tegen het ontvangen van directmarketingberichten.',
        },
        a10af31f_ba28_494e_ab50_f8679e86a0b0: {
            title: 'Bewaren en wissen van verkeersgegevens',
            description: 'Verkeersgegevens met betrekking tot abonnees en gebruikers moeten worden gewist of geanonimiseerd wanneer ze niet langer nodig zijn voor verzending, met uitzondering van facturering of interconnectiebetalingen.',
        },
        '8577e60f_66a8_4651_8336_d561db9929ad': {
            title: 'Voorwaarden voor verwerking van locatiegegevens',
            description: 'Andere locatiegegevens dan verkeersgegevens mogen alleen worden verwerkt als ze geanonimiseerd zijn of met toestemming van de gebruiker, voor zover en voor de duur die nodig is.',
        },
        '7f3abb41_8713_4276_93b5_6902f419a016': {
            title: 'Rechtmatigheid van de verwerking – toestemming',
            description: 'De verwerking is alleen rechtmatig als de betrokkene toestemming heeft gegeven voor de verwerking van zijn persoonsgegevens voor een of meer specifieke doeleinden.',
        },
        '1c2a945a_8eac_428d_8105_e80e95288ea4': {
            title: 'Rechtmatigheid van de verwerking – contract',
            description: 'De verwerking is noodzakelijk voor de uitvoering van een overeenkomst waarbij de betrokkene partij is.',
        },
        '2b50c0fb_ee96_412e_a092_c3c1f3f817e8': {
            title: 'Rechtmatigheid van de verwerking – wettelijke verplichting',
            description: 'De verwerking is noodzakelijk om te voldoen aan een wettelijke verplichting die op de verwerkingsverantwoordelijke rust.',
        },
        '702ea760_9e5c_4c32_babc_0b5094ca9735': {
            title: 'Rechtmatigheid van de verwerking — vitale belangen',
            description: 'De verwerking is noodzakelijk om de vitale belangen van de betrokkene of een andere natuurlijke persoon te beschermen.',
        },
        '8b55ce88_2d5e_467b_a55c_8c5b0efab67e': {
            title: 'Rechtmatigheid van de verwerking – algemeen belang',
            description: 'De verwerking is noodzakelijk voor de uitvoering van een taak die wordt uitgevoerd in het algemeen belang of in de uitoefening van openbaar gezag.',
        },
        '8a72dd16_ee3c_4947_b5cd_9484719adad8': {
            title: 'Rechtmatigheid van de verwerking – legitieme belangen',
            description: 'De verwerking is noodzakelijk voor de legitieme belangen van de verwerkingsverantwoordelijke of een derde partij.',
        },
        afefc965_8f83_4d75_a9fc_92d4d4f549d6: {
            title: 'Verwerking van bijzondere categorieën (gevoelige gegevens)',
            description: 'De verwerking van bijzondere categorieën van persoonsgegevens (raciale afkomst, politieke opvattingen, gezondheid, enz.) is verboden, tenzij specifieke voorwaarden in Art. 9(2) is voldaan.',
        },
        '10a2d63f_744d_4222_91f6_86a5a4cdaefd': {
            title: 'Voorwaarden voor toestemming',
            description: 'Verwerkingsverantwoordelijke moet aantonen dat er toestemming is gegeven. Toestemming moet vrijelijk, specifiek, geïnformeerd en ondubbelzinnig worden gegeven. Het intrekken moet net zo eenvoudig zijn als het geven van toestemming.',
        },
        '27697f99_efef_49e6_b8e7_c2dc550d1791': {
            title: 'Toestemming van het kind — digitale diensten',
            description: 'Voor de verwerking van persoonsgegevens van een kind jonger dan 16 jaar (of een door de lidstaat vastgestelde lagere leeftijd, niet jonger dan 13 jaar) is toestemming van de ouders vereist voor diensten van de informatiemaatschappij.',
        },
        '53883480_508e_4f8e_984f_9354c18de285': {
            title: 'Transparante informatie en communicatie',
            description: 'De verwerkingsverantwoordelijke verstrekt informatie met betrekking tot de verwerking in een beknopte, transparante, begrijpelijke en gemakkelijk toegankelijke vorm.',
        },
        b5eb22b7_78f2_4c55_b342_827d5f8ac90c: {
            title: 'Informatie voor directe verzameling',
            description: 'Wanneer persoonsgegevens van de betrokkene worden verzameld, verstrekt de verwerkingsverantwoordelijke specifieke informatie op het moment van verzameling.',
        },
        e55f7dbf_cd2a_4713_887e_8c64744e2d95: {
            title: 'Informatie voor indirecte verzameling',
            description: 'Wanneer er geen persoonsgegevens van de betrokkene zijn verkregen, verstrekt de verwerkingsverantwoordelijke binnen een redelijke termijn gespecificeerde informatie.',
        },
        '6bc5ec07_0589_4593_882a_13f41c31e1f6': {
            title: 'Recht op toegang',
            description: 'De betrokkene heeft het recht om bevestiging van de verwerking van en toegang tot de persoonsgegevens en aanvullende informatie te verkrijgen.',
        },
        '0e011e25_dcea_4a1e_ae55_583e683cea44': {
            title: 'Recht op rectificatie',
            description: 'De betrokkene heeft het recht om zonder onnodige vertraging rectificatie van onjuiste persoonsgegevens te verkrijgen.',
        },
        e8ec5a55_f657_4a99_9b92_3e9861067d28: {
            title: 'Recht op verwijdering (recht om vergeten te worden)',
            description: 'De betrokkene heeft het recht om zonder onnodige vertraging de verwijdering van persoonsgegevens te verkrijgen indien specifieke redenen van toepassing zijn.',
        },
        '5b0c92fd_cd08_47be_bfd2_0540ad73f84f': {
            title: 'Recht op beperking van de verwerking',
            description: 'De betrokkene heeft het recht om beperking van de verwerking te verkrijgen wanneer specifieke voorwaarden van toepassing zijn.',
        },
        '3b829862_39f4_41be_8f2b_d469469a801b': {
            title: 'Meldingsplicht — rectificatie/verwijdering/beperking',
            description: 'De verwerkingsverantwoordelijke zal elke rectificatie, verwijdering of beperking meedelen aan elke ontvanger aan wie gegevens zijn bekendgemaakt.',
        },
        '9cf34be6_121b_4fdf_ad7c_c4773696522e': {
            title: 'Recht op gegevensportabiliteit',
            description: 'De betrokkene heeft het recht om zijn persoonsgegevens te ontvangen in een gestructureerd, algemeen gebruikt, machinaal leesbaar formaat.',
        },
        becae433_90b9_4cc0_bd33_df50976c1561: {
            title: 'Recht om bezwaar te maken',
            description: 'De betrokkene heeft het recht om bezwaar te maken tegen verwerking op basis van openbaar belang of legitieme belangen, inclusief profilering.',
        },
        '4ef387f1_439c_4579_82a3_01565e6dc05d': {
            title: 'Geautomatiseerde individuele besluitvorming, inclusief profilering',
            description: 'De betrokkene heeft het recht om niet te worden onderworpen aan een uitsluitend op geautomatiseerde verwerking gebaseerde beslissing waaraan juridische of soortgelijke significante gevolgen zijn verbonden.',
        },
        '667ae5eb_a39a_4809_a032_923cc804eaed': {
            title: 'Melding van inbreuk aan toezichthoudende autoriteit (72 uur)',
            description: 'In geval van een inbreuk in verband met persoonsgegevens stelt de verwerkingsverantwoordelijke de toezichthoudende autoriteit binnen 72 uur nadat hij hiervan op de hoogte is gesteld, op de hoogte, tenzij het onwaarschijnlijk is dat dit tot een risico zal leiden.',
        },
        '40e4c2ad_bc85_436c_814f_265f02c8c3b1': {
            title: 'Melding van inbreuk aan betrokkenen (hoog risico)',
            description: 'Wanneer een inbreuk waarschijnlijk een hoog risico voor de rechten en vrijheden met zich meebrengt, deelt de verwerkingsverantwoordelijke de inbreuk zonder onnodige vertraging mee aan de betrokkene.',
        },
        '9dbb47bd_172f_4335_b7f3_4558476c4192': {
            title: 'Reactie op verzoeken van betrokkenen (30 dagen)',
            description: 'De verwerkingsverantwoordelijke reageert zonder onnodige vertraging op verzoeken van betrokkenen en binnen één maand na ontvangst, verlengbaar met nog eens twee maanden voor complexe verzoeken.',
        },
        '175d16b7_a0fc_44ff_bb9e_5ccb5d3de5a9': {
            title: 'Impactbeoordeling van gegevensbescherming',
            description: 'Wanneer de verwerking waarschijnlijk een hoog risico voor de rechten en vrijheden met zich meebrengt, voert de verwerkingsverantwoordelijke voorafgaand aan de verwerking een DPIA uit.',
        },
        e54a7b09_33dd_4cda_8e21_d10a51e5e925: {
            title: 'Voorafgaand overleg met toezichthoudende autoriteit',
            description: 'De verwerkingsverantwoordelijke moet de toezichthoudende autoriteit raadplegen wanneer een DPIA aangeeft dat de verwerking zonder risicobeperkende maatregelen tot een hoog risico zou leiden.',
        },
        '047b7838_bae5_4861_9c38_c99fedec9c96': {
            title: 'DPIA verplichte verwerkingslijst',
            description: 'DPIA is vereist voor systematische evaluatie/profilering, grootschalige verwerking van speciale categorieën en systematische monitoring van openbare ruimtes.',
        },
        aa08dc83_7c1a_441d_a8e7_960f65c8de87: {
            title: 'Registratie van verwerkingsactiviteiten (verwerkingsverantwoordelijke)',
            description: 'Elke verwerkingsverantwoordelijke houdt een register bij van de verwerkingsactiviteiten die onder zijn verantwoordelijkheid vallen, inclusief doeleinden, categorieën, ontvangers, overdrachten en bewaring.',
        },
        b7eb6296_5962_449c_b267_d322d7919122: {
            title: 'Registratie van verwerkingsactiviteiten (verwerker)',
            description: 'Elke verwerker houdt een register bij van alle categorieën verwerkingsactiviteiten die namens een verwerkingsverantwoordelijke worden uitgevoerd.',
        },
        '0d57e6d5_bbf5_4ea6_9a89_0f57bed61b5b': {
            title: 'Beveiliging van de verwerking',
            description: 'Verwerkingsverantwoordelijke en verwerker implementeren passende technische en organisatorische maatregelen om de veiligheid te waarborgen die is afgestemd op het risico.',
        },
        ad85edfc_3c92_4ce4_a414_3ba6186cae98: {
            title: 'Gegevensbescherming door ontwerp en standaard',
            description: 'Verwerkingsverantwoordelijke treft passende maatregelen om ervoor te zorgen dat alleen persoonsgegevens worden verwerkt die noodzakelijk zijn voor elk specifiek doel (gegevensminimalisatie).',
        },
        '933b438d_53b8_4b6a_b1db_8e7f1b429ede': {
            title: 'Aanwijzing functionaris voor gegevensbescherming',
            description: 'De verwerkingsverantwoordelijke en de verwerker wijzen een DPO aan wanneer de verwerking wordt uitgevoerd door een overheidsinstantie, regelmatige en systematische monitoring inhoudt, of grootschalige verwerking van bijzondere categorieën.',
        },
        '28810a5d_d818_411a_9e8c_792d1b2c9b2f': {
            title: 'Algemeen principe voor overdrachten',
            description: 'Elke overdracht van persoonsgegevens naar een derde land vindt alleen plaats als aan de voorwaarden van Hoofdstuk V is voldaan.',
        },
        '60add64b_bb0c_44e7_baaf_666af65b2d0f': {
            title: 'Overdrachten op basis van een adequaatheidsbesluit',
            description: 'Er kan sprake zijn van overdracht wanneer de Commissie heeft besloten dat het derde land een passend beschermingsniveau waarborgt.',
        },
        cde8c7cd_3727_40ce_a321_e6b72b335179: {
            title: 'Overdrachten onderworpen aan passende waarborgen',
            description: "Bij ontstentenis van een adequaatheidsbesluit kunnen overdrachten plaatsvinden indien passende waarborgen worden geboden, waaronder SCC's of BCR's.",
        },
        '0e8edaa1_2156_44d9_95f2_1b284f581658': {
            title: 'Standaardcontractbepalingen',
            description: 'Modelcontractbepalingen die door de Commissie of door een toezichthoudende autoriteit zijn aangenomen, bieden passende waarborgen voor internationale overdrachten.',
        },
        '608a8de7_6c75_4f88_bd4c_1cc308780ab9': {
            title: 'Bindende bedrijfsregels',
            description: 'Bindende bedrijfsregels die zijn goedgekeurd door een bevoegde toezichthoudende autoriteit bieden passende waarborgen voor internationale overdrachten binnen de groep.',
        },
        '420ced80_0d3b_4a28_adad_14ba54df3e40': {
            title: 'Afwijkingen voor specifieke situaties',
            description: 'Bij gebrek aan adequaatheid of waarborgen kunnen overdrachten plaatsvinden op basis van expliciete toestemming, contractnoodzaak, openbaar belang, juridische claims, vitale belangen of openbaar register.',
        },
        c86fbc0d_7188_486c_8ea5_96b3fe957c6f: {
            title: 'Verantwoordelijkheid en opleiding van het bestuursorgaan',
            description: 'Beheersorganen van essentiële en belangrijke entiteiten moeten maatregelen op het gebied van cyberbeveiligingsrisicobeheer goedkeuren en regelmatig training volgen.',
        },
        ffb95a09_993c_4db0_a4bb_c48000e43b33: {
            title: 'Maatregelen voor risicobeheer op het gebied van cyberveiligheid',
            description: 'Essentiële en belangrijke entiteiten nemen passende en evenredige technische, operationele en organisatorische maatregelen om de cyberveiligheidsrisico’s te beheersen.',
        },
        '6a7c8c8f_4b81_4b47_adb7_c6abd12acd4b': {
            title: 'Beleid en procedures voor incidentafhandeling',
            description: 'Entiteiten moeten beleid ten aanzien van de afhandeling van incidenten implementeren, inclusief detectie-, analyse-, inperkings- en responsprocedures.',
        },
        de019eab_9992_4361_be7c_3e592f209d76: {
            title: 'Bedrijfscontinuïteit en crisismanagement',
            description: 'Entiteiten implementeren maatregelen voor bedrijfscontinuïteit, waaronder back-upbeheer, noodherstel en crisisbeheer.',
        },
        '6b5f482f_301c_4f69_bd91_f9ae1b21de6c': {
            title: 'Beveiliging van de toeleveringsketen',
            description: 'Entiteiten moeten veiligheidsgerelateerde aspecten van relaties met directe leveranciers en dienstverleners aanpakken, inclusief risicobeoordelingen van de toeleveringsketen.',
        },
        c8e7738b_358c_4c79_9fc2_a564f15a6e37: {
            title: 'Beveiliging in netwerk- en informatiesystemen',
            description: 'Entiteiten implementeren beveiliging bij de aanschaf, ontwikkeling en het onderhoud van netwerk- en informatiesystemen, inclusief het omgaan met kwetsbaarheden.',
        },
        '72520ed1_51ff_4983_a785_9be1f72cf7dd': {
            title: 'Behandeling en openbaarmaking van kwetsbaarheden',
            description: 'Entiteiten implementeren beleid en procedures voor de gecoördineerde openbaarmaking en afhandeling van kwetsbaarheden.',
        },
        f009e65d_d55e_4c98_9532_63c150694698: {
            title: 'Effectiviteit van cyberbeveiligingsrisicobeoordeling',
            description: 'Entiteiten implementeren beleid en procedures om de effectiviteit van maatregelen voor cyberbeveiligingsrisicobeheer te beoordelen.',
        },
        '00e88824_675d_4b37_960c_757b3176c079': {
            title: 'Cryptografie- en encryptiebeleid',
            description: 'Entiteiten implementeren beleid en procedures met betrekking tot het gebruik van cryptografie en, indien van toepassing, encryptie.',
        },
        de29d287_81f1_46b1_b327_9cbf06ddad3d: {
            title: 'HR-beveiliging, toegangscontrole, asset management',
            description: 'Entiteiten moeten personeelsbeveiliging, toegangscontrolebeleid en activabeheer implementeren.',
        },
        '750f4459_9ba4_415e_bc47_5b0ec27d35a2': {
            title: 'Authenticatie met meerdere factoren',
            description: 'Entiteiten maken gebruik van oplossingen voor meervoudige authenticatie of continue authenticatie, beveiligde spraak-/video-/tekstcommunicatie en beveiligde noodcommunicatiesystemen.',
        },
        '359474f9_1820_4e3e_9031_a593b66ccde4': {
            title: 'Vroegtijdige waarschuwing voor CSIRT (24 uur)',
            description: 'Entiteiten moeten binnen 24 uur nadat zij zich bewust zijn geworden van een significant incident een vroegtijdige waarschuwing indienen bij het bevoegde CSIRT.',
        },
        '66acd92c_3091_4148_b935_68b0d24308e7': {
            title: 'Melding van incidenten aan CSIRT (72 uur)',
            description: 'Entiteiten dienen binnen 72 uur nadat zij kennis hebben gekregen van een significant incident een incidentmelding in bij het bevoegde CSIRT.',
        },
        '68269688_06b2_43ea_8e3e_a84532086557': {
            title: 'Eindrapport aan het CSIRT (1 maand)',
            description: 'Entiteiten dienen uiterlijk één maand na de melding van het incident een eindrapport in bij het bevoegde CSIRT.',
        },
        c8512bde_b064_4926_b333_06b9c95cecd7: {
            title: 'Toepasselijkheidsbepaling voor AI activiteiten',
            description: 'Bepaal of de organisatie AI activiteiten uitvoert die vallen onder de Vietnamese AI wet en documenteer de toepasselijkheidsbeslissing.',
        },
        '3a87ad63_179a_45ba_8743_98d865df80ab': {
            title: "AI classificatie van systeemrisico's op risiconiveau",
            description: 'Classificeer elk AI-systeem op risiconiveau (minimaal, beperkt, hoog, onaanvaardbaar) met behulp van de begeleide classificatiemethodologie.',
        },
        '0f4475d3_77a6_4b41_bfe2_d4b299b1bd45': {
            title: 'AI Onderhoud van systeemregisterinvoer',
            description: 'Houd een register bij van AI systemen met systeemnaam, eigenaar, doel, implementatiecontext, risiconiveau en betrokkenheid van de leverancier.',
        },
        '2a73ce94_906a_4c95_8a45_a5bdf2a8118c': {
            title: 'AI Systeemdossier met artefactchecklist en bewijsmateriaal',
            description: 'Bouw en onderhoud een systeemdossier met de vereiste artefactchecklist, bewijsbibliotheek en versiegeschiedenis.',
        },
        e92cfb42_d91b_444f_adbc_c03903844eac: {
            title: 'Transparantieverplichtingen voor AI systeembeheerders',
            description: 'Implementeer transparantiemaatregelen voor AI-systemen, waaronder gebruikersmeldingen, openbaarmaking van de betrokkenheid van AI en systeemdocumentatie.',
        },
        '45eb43d7_9b63_4166_aa0d_fa6aeeaa70ba': {
            title: 'AI uitvoerlabels, watermerken en ondertekening van metagegevens',
            description: 'Implementeer labeling en watermerken voor door AI gegenereerde inhoud, inclusief ondertekening van metagegevens, indien van toepassing.',
        },
        '9566c94d_0dd3_419c_869f_6707d2a972f9': {
            title: 'Openbaarmakingsvereisten voor door AI gegenereerde inhoud',
            description: 'Laat gebruikers weten wanneer inhoud AI is gegenereerd en zorg voor mechanismen waarmee gebruikers de betrokkenheid van AI kunnen identificeren.',
        },
        '21d39ea5_df78_45c6_8710_b203db229e25': {
            title: 'Uitlegbaarheid en gebruikersgerichte transparantieverplichtingen',
            description: 'Zorg voor verklaarbare resultaten voor AI-besluiten die gevolgen hebben voor individuen en onderhoud gebruikersgerichte transparantiedocumentatie.',
        },
        f523fbc2_156d_466d_ad50_6a0c51b3ac9d: {
            title: 'Verplichtingen voor menselijk toezicht op risicovolle AI-systemen',
            description: 'Zorg ervoor dat er mechanismen voor menselijk toezicht aanwezig zijn voor risicovolle AI-systemen, inclusief escalatie- en override-mogelijkheden.',
        },
        bd180a89_4976_4790_9e50_f1c5c97dc576: {
            title: 'AI monitoring en detectie van incidenten',
            description: 'Implementeer monitoringmogelijkheden om AI systeemincidenten, storingen en nadelige gevolgen te detecteren.',
        },
        e7c1c60b_856c_4d3d_b6c9_092cde9b56bd: {
            title: 'Ernstig AI incident gemeld aan de autoriteit',
            description: 'Meld ernstige AI incidenten binnen de voorgeschreven termijn (72 uur) aan de bevoegde autoriteit.',
        },
        '97f15adc_558a_49f2_bb06_86597cb9579b': {
            title: 'AI naleving van de tijdlijn voor incidentmeldingen',
            description: 'Houd u aan de meldingstijdlijnen voor AI-incidenten, inclusief initiële meldingen, updates en afsluitingsrapporten.',
        },
        '96c33d7e_fe52_4730_adac_70c8c477c88e': {
            title: 'AI effectbeoordeling voor systemen met een hoog risico',
            description: "Voer effectbeoordelingen uit voor risicovolle AI-systemen met betrekking tot risico's, mitigaties, menselijk toezicht en monitoringplannen.",
        },
        '0f3b72c0_1030_44ff_ba53_596f45601cd7': {
            title: 'Gecontroleerd testen / bepaling van geschiktheid voor de sandbox',
            description: 'Bepaal of u in aanmerking komt voor deelname aan gecontroleerde tests (sandbox) en documenteer de geschiktheidsbeoordeling.',
        },
        '4a6aed20_f655_4182_a760_aef1f329d5df': {
            title: 'Indiening van sandbox-applicaties en volgen van goedkeuring',
            description: 'Dien sandbox-aanvragen in bij de bevoegde autoriteit en volg de goedkeuringsstatus gedurende de gehele indieningscyclus.',
        },
        '8376988d_cd32_491e_a5b8_3340d3973358': {
            title: 'Controlepunten voor sandboxbewaking en voltooiing/afsluiting',
            description: 'Voer periodieke monitoringcontrolepunten uit tijdens sandbox-deelname en bereid voltooiings-/afsluitrapporten voor.',
        },
        '7cb2b544_0512_4702_a092_9f1c10872376': {
            title: 'Etikettering van vrijstelling/uitzonderingsdocumentatie met onderbouwing',
            description: 'Documenteer eventuele vrijstellingen of uitzonderingen op het gebied van etikettering met onderbouwing en ondersteunend bewijsmateriaal.',
        },
        dccd7fbf_872a_4716_87a4_b9c5ce8e2387: {
            title: 'Gegevenslokalisatie voor Vietnamese gebruikers',
            description: 'Gegevens van Vietnamese gebruikers over diensten die in Vietnam worden geleverd, moeten op het grondgebied van Vietnam worden opgeslagen.',
        },
        '9eb86a03_e165_4963_8813_2272b638d529': {
            title: 'Rapportage van cyberveiligheidsincidenten',
            description: 'Cybersecurity-incidenten moeten tijdig aan de bevoegde autoriteiten worden gemeld.',
        },
        '736ac7de_7a6a_4bfd_b2be_c594e290038a': {
            title: 'Classificatie van informatiesystemen',
            description: 'Informatiesystemen moeten worden geclassificeerd naar belangrijkheidsniveau en er moeten passende beveiligingsmaatregelen worden toegepast.',
        },
        ffb807cf_f121_4b52_8e50_e091c33180c9: {
            title: 'Risicobeoordeling op het gebied van cyberbeveiliging',
            description: 'Er moeten regelmatig cyberveiligheidsrisicobeoordelingen worden uitgevoerd voor informatiesystemen.',
        },
        a4de518b_a5f1_481f_9b08_f8d2bc9db7e5: {
            title: 'In-scope bepaling (beslisboom)',
            description: 'Organisaties moeten aan de hand van de voorgeschreven beslisboom bepalen of zij onder de reikwijdte van Besluit 53 vallen.',
        },
        '01f99e9a_5e30_4d14_aae5_a2ce322a700b': {
            title: 'Beoordeling en gereedheid van gegevenslokalisatie',
            description: 'Organisaties moeten de vereisten voor datalokalisatie beoordelen en gereedheid tonen.',
        },
        cf24f011_dc23_4d84_aef5_994dde440571: {
            title: 'Controlelijst voor gereedheid voor opslag en bewaring',
            description: 'Er moet een checklist voor de gereedheid voor opslag en retentie worden ingevuld om te voldoen aan de eisen voor gegevenslokalisatie.',
        },
        b3884cb8_c785_4258_9517_9e393be4f4e1: {
            title: 'Beoordeling van lokale aanwezigheidsvereisten',
            description: 'Organisaties moeten beoordelen of lokale aanwezigheidsvereisten van toepassing zijn en dienovereenkomstig plannen maken.',
        },
        c1dc645b_cb1a_42a6_8ca5_9b15cfa84156: {
            title: 'Autoriteit verzoek om ontvangst en verificatie',
            description: 'Autoriteitsverzoeken moeten worden ontvangen, geverifieerd en verwerkt volgens voorgeschreven procedures.',
        },
        d4571651_f39d_4c51_8f2d_7248c37f7b14: {
            title: 'Autoriteit vraagt om reactie binnen de deadline',
            description: 'Antwoorden op verzoeken van autoriteiten moeten binnen de voorgeschreven termijn worden verstrekt.',
        },
        '66c1df70_3456_40e9_b645_6c6bd3be702e': {
            title: 'Logboek voor afhandeling van autoriteitsverzoeken',
            description: 'Er moet een logboek worden bijgehouden van alle activiteiten voor het afhandelen van autoriteitsverzoeken.',
        },
        '04bbba2a_9804_4199_a517_054b6f4689fb': {
            title: 'Toepasbaarheid en bepaling binnen de reikwijdte (geconsolideerd)',
            description: 'Organisaties moeten bepalen of ze binnen het toepassingsgebied vallen onder de geconsolideerde cyberbeveiligingswet, die zowel het oude toepassingsgebied van Wet 24/2018 als Decreet 53/2022 bestrijkt.',
        },
        '89a21eaf_cf09_4fec_bbb4_a1dadd652f6e': {
            title: 'Classificatie van informatiesystemen op belangrijkheidsniveau',
            description: 'Informatiesystemen moeten worden geclassificeerd op belangrijkheidsniveau (algemeen, belangrijk, kritisch) met bijbehorende beveiligingsverplichtingen.',
        },
        '83ad981e_23ff_4a92_bebd_cd24487e912f': {
            title: 'Bestuursstructuur voor cyberbeveiliging en verantwoordelijk personeel',
            description: 'Organisaties moeten bestuursstructuren voor cyberbeveiliging opzetten en verantwoordelijk personeel aanwijzen.',
        },
        '98d9a523_fefb_400b_bb7f_ebaf609a6ed4': {
            title: 'Periodieke cyberveiligheidsrisicobeoordeling',
            description: 'Voor alle geheime informatiesystemen moeten regelmatig cyberveiligheidsrisicobeoordelingen worden uitgevoerd.',
        },
        '5236539f_4c1a_4eff_b2eb_fc11eb4eab23': {
            title: 'Beveiligingsmaatregelen per informatiesysteemclassificatie',
            description: 'Er moeten technische beveiligingsmaatregelen worden toegepast die geschikt zijn voor elk classificatieniveau van een informatiesysteem.',
        },
        b69a8be2_ae0c_408c_b8f1_73b573eca876: {
            title: 'Toegangscontrole en identiteitsbeheer',
            description: 'Voor informatiesystemen moeten maatregelen voor toegangscontrole en identiteitsbeheer worden geïmplementeerd.',
        },
        ac774c26_feb7_4123_b799_c54f77bd87b5: {
            title: 'Registratie, monitoring en audittrail',
            description: 'Informatiesystemen moeten de mogelijkheden voor logboekregistratie, monitoring en audittrails behouden.',
        },
        a86cb01d_239b_44e2_9e09_e6973bcf9658: {
            title: 'Kwetsbaarheidsbeheer en patching',
            description: 'Kwetsbaarheidsbeheer en patchingprocessen moeten worden opgezet en onderhouden.',
        },
        '826a789f_0c03_4918_81a1_6aa1cc2c205c': {
            title: 'Gegevenslokalisatie voor Vietnamese gebruikersgegevens',
            description: 'Gegevens van Vietnamese gebruikers over diensten die in Vietnam worden geleverd, moeten op het grondgebied van Vietnam worden opgeslagen.',
        },
        '8bee2b51_ace8_4118_89c8_010b96640016': {
            title: 'Lokale aanwezigheidsvereisten voor buitenlandse dienstverleners',
            description: 'Buitenlandse dienstverleners die in Vietnam actief zijn, moeten zoals voorgeschreven lokaal aanwezig zijn.',
        },
        '74d89250_b45f_41b6_bfe3_f915071580de': {
            title: 'Lokale opslagarchitectuur en grensoverschrijdende stroomdocumentatie',
            description: 'De lokale opslagarchitectuur moet worden gedocumenteerd en grensoverschrijdende datastromen moeten in kaart worden gebracht en gecontroleerd.',
        },
        '04dec6f8_3280_494c_8506_292804b16d4d': {
            title: 'Detectie en respons op cyberbeveiligingsincidenten',
            description: 'Organisaties moeten over de mogelijkheden beschikken om cyberveiligheidsincidenten te detecteren en erop te reageren.',
        },
        faeda71b_5050_497d_b08b_e3f3f5313ad4: {
            title: 'Verplichte melding van incidenten aan de bevoegde autoriteit',
            description: 'In aanmerking komende cyberbeveiligingsincidenten moeten binnen de voorgeschreven termijnen worden gerapporteerd aan MPS/MIC.',
        },
        d732b5a2_2b2c_48a0_955d_97a39daf132d: {
            title: 'Naleving van de tijdlijn voor incidentmeldingen',
            description: 'Incidentmeldingen moeten voldoen aan de voorgeschreven tijdlijnvereisten (eerste melding binnen 24 uur, volledige melding binnen 72 uur).',
        },
        '8e2dffb0_3481_44f0_baf2_130ddd8cfe12': {
            title: 'Behandeling en samenwerking van autoriteitsverzoeken',
            description: 'Autoriteitsverzoeken moeten snel worden afgehandeld en er moet medewerking worden verleend volgens voorgeschreven procedures.',
        },
        e4c5b870_1d61_48fe_a6c2_1b9f5a458ea3: {
            title: 'Autoriteitsverzoekzaakregister en openbaarmakingslogboek',
            description: 'Alle autoriteitsverzoeken en openbaarmakingen moeten worden vastgelegd in een casusregister met volledig audittraject.',
        },
        '8eff8b17_65bc_4f94_8ece_c509a9fd9abb': {
            title: 'Beveiligingsbeoordeling van leveranciers en derden',
            description: 'Externe leveranciers met toegang tot informatiesystemen moeten een beveiligingsbeoordeling ondergaan.',
        },
        '69891e1b_97f6_464c_9cce_b1276794fe4a': {
            title: 'Controles op toegang tot leveranciersgegevens en melding van incidenten',
            description: 'De toegang tot leveranciersgegevens moet worden gedocumenteerd en contractuele verplichtingen voor het melden van incidenten moeten worden vastgelegd.',
        },
        d89b6bc0_7a9e_4816_b31e_7c0abc02e90c: {
            title: 'Beveiligingsbewustzijnstrainingen en -oefeningen',
            description: 'Er moeten regelmatig cybersecurity-bewustzijnstrainingen en tabletop-oefeningen worden gehouden.',
        },
        f9c5d3cc_5bb2_4b30_a5fb_ada34e407981: {
            title: 'Periodieke pentest en beveiligingsaudit',
            description: 'Er moeten periodieke penetratietests en beveiligingsaudits worden uitgevoerd op geheime informatiesystemen.',
        },
        '88dd9877_d2e2_4e41_a248_ae39b1ff478f': {
            title: 'Onderhouden van digitale data-inventaris',
            description: 'Organisaties moeten een uitgebreide digitale data-inventaris bijhouden waarin alle data-assets worden gecatalogiseerd.',
        },
        '18e4bfd1_42cf_4d87_870f_5a34242962ef': {
            title: 'Gegevensclassificatie op basis van gevoeligheid en belang',
            description: 'Gegevens moeten worden geclassificeerd op basis van gevoeligheids- en belangrijkheidsniveaus zoals gedefinieerd in de Gegevenswet.',
        },
        b89981ac_7559_46f9_a74c_a143976dde4e: {
            title: 'Definieer verwerkingsregels per classificatieniveau',
            description: 'Voor elk gegevensclassificatieniveau moeten verwerkingsregels worden vastgesteld.',
        },
        '3bf8019d_b72c_41d7_82cf_fc367efd9526': {
            title: 'Volg het delen en openbaar maken van gegevens met goedkeuringen',
            description: 'Alle activiteiten voor het delen en openbaar maken van gegevens moeten worden gevolgd met de juiste goedkeuringen.',
        },
        '8623eca2_4cf9_41bd_bfbd_2300c06ca1f3': {
            title: 'Beheer van grensoverschrijdende gegevensoverdracht',
            description: 'Grensoverschrijdende gegevensoverdrachten moeten voldoen aan de governancevereisten op grond van de Gegevenswet.',
        },
        '006b8aa6_9379_4eff_b8ab_935f9a087601': {
            title: 'Handhaaf het governancebeleid en de verantwoordelijkheden',
            description: 'Organisaties moeten een data governance-beleid handhaven en governanceverantwoordelijkheden toewijzen.',
        },
        f12fc090_5c3e_4fa8_9813_74bea0359c5c: {
            title: 'Jaarlijks bestuursattest',
            description: 'Er moet jaarlijks een bestuursattest worden ingevuld om de voortdurende naleving te bevestigen.',
        },
        f6c3060a_5e52_4f97_b1af_c6a5f00bfaee: {
            title: 'Gegevensrisicobeoordeling',
            description: "Er moet een datarisicobeoordeling worden uitgevoerd om de risico's op het gebied van databeheer te identificeren en te beperken.",
        },
        '6f228998_1c61_49cf_a585_3d3e9baff55a': {
            title: 'Verantwoordelijkheid van platformexploitanten en governanceverplichtingen',
            description: 'Zet verantwoordingsstructuren voor platformexploitanten op, inclusief governancebeleid, monitoringverantwoordelijkheden en handhavingsverplichtingen.',
        },
        eee1d423_3783_4530_bc64_eef6fd9d02dc: {
            title: 'Platformregels publicatie en transparantie van informatie',
            description: 'Publiceer platformregels, handelsvoorwaarden en operationele openbaarmakingen op een transparante en toegankelijke manier.',
        },
        '50b57433_7a51_4e92_9c84_c748d6865b70': {
            title: 'Melding van website/app voor e-commerceverkoop aan MOIT',
            description: 'Melding indienen bij MOIT voor e-commerce verkoopwebsites en -applicaties via Online.gov.vn of aangewezen kanalen.',
        },
        '92854abe_6654_4155_8495_f3419a8a8da0': {
            title: 'E-commerce serviceplatform/marktplaatsregistratie bij MOIT',
            description: 'Registreer e-commerce serviceplatforms en marktplaatsen bij MOIT, inclusief het indienen van dossiers en het volgen van goedkeuringen.',
        },
        b8bff437_805e_4a4a_a62e_89cb90b89d3f: {
            title: 'Onderhoud verkopersregister (binnen- en buitenland)',
            description: 'Houd een register bij van alle verkopers op het platform, inclusief binnenlandse en buitenlandse verkopers, met onboarding, verificatie en tracking van de levenscyclus.',
        },
        '75778639_5235_4d13_a728_fa772c27fc0c': {
            title: 'Livestream-verkopersregister en nalevingscontroles',
            description: 'Houd een register bij van livestreamverkopers met identiteitsverificatie, het volgen van activiteiten en nalevingscontroles.',
        },
        '405f2932_742a_4bcb_9784_1a3d81f2e714': {
            title: 'Affiliate- en marketingpartnerregister',
            description: 'Houd een register bij van aangesloten marketeers en verwijzingspartners met de contractstatus, inhoudsrichtlijnen en nalevingsbevestigingen.',
        },
        cce5a4eb_a576_459c_95b5_bdd0e8ed30a8: {
            title: 'Productvermelding en handhaving van verboden/voorwaardelijke goederen',
            description: 'Implementeer controles op productvermeldingen, inclusief screening, monitoring en handhavingsacties van verboden en voorwaardelijke goederen.',
        },
        '9fc01e19_18dd_4d60_b95a_c1f4bcdbb6ae': {
            title: 'Consumentenklachten registreren met intake, acties en audittrail',
            description: 'Houd een klachtenregister bij met intakegegevens, onderzoeksacties, oplossingsresultaten en een volledig audittraject.',
        },
        '8a21764e_e5f4_4934_9913_c235f4c462ca': {
            title: 'Klachtenbehandelingsprocedure en oplossingstermijnen',
            description: 'Stel klachtenbehandelingsprocedures op met gedefinieerde tijdlijnen voor erkenning, onderzoek en oplossing.',
        },
        '8406373c_ec31_4bf8_810f_72d9d73b3de8': {
            title: 'Contentmoderatie, monitoring en verwijderingshandhaving',
            description: 'Implementeer procedures voor inhoudsmoderatie, monitoring en verwijdering van illegale, verboden of in strijd zijnde inhoud.',
        },
        '6f32c315_323b_4ce6_a4bb_3262a5b56afe': {
            title: 'Verwijderings- en handhavingsacties worden geregistreerd met behoud van bewijsmateriaal',
            description: 'Houd een register bij van verwijderings- en handhavingsacties met beslissingsregistraties, bewaring van bewijsmateriaal en verkopersmeldingen.',
        },
        c0bbbfbf_29bd_4813_89f1_956eab9f1a23: {
            title: 'Platformbeleid en openbaarmakingsregister (versies, publicatiedata, acceptatie)',
            description: 'Houd een register bij van platformbeleid en openbaarmakingen met versiegeschiedenis, publicatiedata en acceptatiebewijs.',
        },
        c9fcbe90_622d_43ca_9ec6_db26c0eda490: {
            title: 'Consumenteninformatieplichten - nauwkeurige productinformatie en anti-misleiding',
            description: 'Zorg voor nauwkeurige, duidelijke product- en service-informatie en implementeer maatregelen om misleidende praktijken op het platform te voorkomen.',
        },
        '8f49603b_454c_4470_ab07_2f845a447595': {
            title: 'Bedrijfsvoorwaarden en registratie/melding van buitenlandse platformen',
            description: 'Voldoe aan de bedrijfsvoorwaarden voor buitenlandse platforms die Vietnamese gebruikers bedienen, inclusief registratie- of meldingsvereisten.',
        },
        e0dc85cc_5d2a_4142_b1a6_cc2fde891f7b: {
            title: 'Lokale vertegenwoordiging of autorisatie voor buitenlandse platforms',
            description: 'Wijs een lokale vertegenwoordiger of geautoriseerde entiteit in Vietnam aan voor buitenlandse platforms, zoals vereist door de wet.',
        },
        '5719f47c_f298_429e_90c3_df6ceed856e1': {
            title: 'Grensoverschrijdende coördinatie van verkoper- en klachtenafhandeling',
            description: 'Coördineren van de grensoverschrijdende afhandeling van verkopers en het oplossen van klachten, inclusief escalatie en bewijsbeheer.',
        },
        c0b3db29_9161_4cef_a889_94e3b85d78fe: {
            title: 'Staat voor interactie met e-commercebeheersystemen en rapportagegereedheid',
            description: 'Bereid je voor op interactie met het e-commercebeheersysteem van de staat, inclusief het in kaart brengen van gegevensvelden en rapportagemogelijkheden.',
        },
        '145a7016_720a_4a06_badd_00145842821e': {
            title: 'Kennisgeving van materiële wijzigingen na goedkeuring',
            description: 'Meldingen indienen voor materiële wijzigingen in platformdetails, beleid of activiteiten na initiële goedkeuring.',
        },
        '4bc0ee2a_610d_490d_8122_d6d1afadaa22': {
            title: 'Beëindigings- en deactiveringsmelding',
            description: 'Meldingen van beëindiging of deactivering indienen bij het stopzetten van e-commerceactiviteiten, inclusief openbare kennisgeving en afhandeling van consumentenbescherming.',
        },
        be95bc85_2b25_4de7_a3e7_154b780289e4: {
            title: 'Geschiktheid en onderhoud van het Trustmark- en geloofwaardigheidsbeoordelingsprogramma',
            description: "Beoordeel of u in aanmerking komt voor trustmark- en geloofwaardigheidsbeoordelingsprogramma's en bewaar nalevingsbewijs voor voortdurende deelname.",
        },
        f639afe2_18f2_4c0a_82d9_626308994267: {
            title: 'Toestemming voor verwerking van persoonsgegevens',
            description: 'Voor de verwerking van persoonsgegevens is toestemming van de betrokkene vereist, tenzij er een uitzondering van toepassing is.',
        },
        '5dd8e1b9_ac2d_48af_8c62_538937f0a4f7': {
            title: 'Sensitive personal data: tell the data subject the data is sensitive',
            description: 'For processing sensitive personal data, the data subject must be told that the data to be processed is sensitive personal data (Art. 11(8)). Art. 28 adds the measures for protecting it: applying the measures of Art. 26(2) and Art. 27; designating a unit with the function of protecting personal data and staff in charge of it, and exchanging their details with the specialised personal data protection agency; and notifying the data subject that their sensitive personal data is being processed, except in the cases of Art. 13(4), Art. 17 and Art. 18.',
        },
        '5a958c76_6943_4449_a15c_ddb503ce526f': {
            title: 'Recht op toegang',
            description: 'Betrokkenen hebben recht op toegang tot hun persoonsgegevens die in het bezit zijn van de verantwoordelijke voor de verwerking.',
        },
        '1ae0a58b_65a5_4a32_9b2c_0baed7799a32': {
            title: 'Recht op rectificatie',
            description: 'Betrokkenen hebben het recht om rectificatie van onjuiste persoonsgegevens te verzoeken.',
        },
        '25c61e57_273f_48cb_a318_d6315ad744f1': {
            title: 'Recht op verwijdering',
            description: 'Betrokkenen hebben het recht om verwijdering van hun persoonsgegevens te verzoeken.',
        },
        '0eafe047_ed8b_4a9a_bda9_b4ef2ae353c2': {
            title: 'Recht om de verwerking te beperken',
            description: 'Betrokkenen hebben het recht om beperking van de verwerking van hun persoonsgegevens te verzoeken.',
        },
        '77f9677a_f436_43ec_a947_3bdc44fc192a': {
            title: 'Recht om bezwaar te maken',
            description: 'Betrokkenen hebben het recht om bezwaar te maken tegen de verwerking van hun persoonsgegevens.',
        },
        c8b19e70_782c_49c9_8a71_14bef4147d6b: {
            title: 'Recht om toestemming in te trekken',
            description: 'Betrokkenen hebben het recht om eerder gegeven toestemming op elk moment in te trekken.',
        },
        '7f472a1c_b37f_498d_adc7_df8d8eafbae9': {
            title: 'Melding van inbreuk aan autoriteit (72 uur)',
            description: 'De verwerkingsverantwoordelijke moet het ministerie van Openbare Veiligheid binnen 72 uur na ontdekking van een inbreuk in verband met persoonsgegevens op de hoogte stellen.',
        },
        '98bed7ab_e814_43f5_8e70_e95c413266a1': {
            title: 'Melding van inbreuk aan betrokkenen',
            description: 'De verwerkingsverantwoordelijke moet de getroffen betrokkenen zonder onnodige vertraging op de hoogte stellen van een inbreuk in verband met persoonsgegevens.',
        },
        '404a3576_dd84_4bac_acf4_29719615c45e': {
            title: 'DPIA voor verwerking met een hoog risico',
            description: 'Er moet een gegevensbeschermingseffectbeoordeling worden uitgevoerd voor verwerkingsactiviteiten die waarschijnlijk een hoog risico met zich meebrengen.',
        },
        '29d9ed94_9360_457c_9aca_37aefac66969': {
            title: 'Beoordeling grensoverschrijdende overdracht',
            description: 'Er moet een effectbeoordeling worden uitgevoerd voordat persoonlijke gegevens buiten Vietnam worden overgedragen.',
        },
        '9620ff08_b83c_4e1f_9350_eba835b145fe': {
            title: 'Registratie van verwerkingsactiviteiten',
            description: 'Gegevensbeheerders moeten een Register van Verwerkingsactiviteiten (RoPA) bijhouden waarin alle verwerkingsactiviteiten worden gedocumenteerd.',
        },
        '96ee0ff3_3ead_431e_aa57_08ccff4821fc': {
            title: 'Registratie van gegevensverwerkingsovereenkomsten',
            description: 'Er moeten registers worden bijgehouden van gegevensverwerkingsovereenkomsten met verwerkers en subverwerkers.',
        },
        '434dc319_1922_49e4_b757_37622135c4b2': {
            title: 'Waarborgen voor grensoverschrijdende overdrachten',
            description: 'Voor de overdracht van persoonsgegevens buiten Vietnam zijn passende waarborgen en gedocumenteerde beoordelingen vereist.',
        },
        '3372c651_d5a8_4cae_ae57_2fc684ad0768': {
            title: 'Bevestiging binnen 2 werkdagen',
            description: 'Verzoeken van betrokkenen moeten binnen 2 werkdagen na ontvangst worden bevestigd.',
        },
        b15fa62d_3f52_4e42_8442_f4969e08883a: {
            title: 'Oplevering binnen 15 werkdagen',
            description: 'Verzoeken van betrokkenen moeten binnen 15 werkdagen worden afgehandeld, met een mogelijke verlenging van 5 werkdagen.',
        },
        '5d81233c_f671_4827_b874_827762edfc20': {
            title: 'MoPS-impactbeoordelingsdossier indienen',
            description: 'Organisaties die risicovolle verwerkingen uitvoeren, moeten een effectbeoordelingsdossier indienen bij het Ministerie van Openbare Veiligheid volgens de voorgeschreven administratieve procedure.',
        },
        '83bf5bf8_edbd_4f0a_9f8e_5f47db01324d': {
            title: 'Indiening van MoPS-dossiers voor grensoverschrijdende overdracht',
            description: 'Organisaties die persoonsgegevens buiten Vietnam overdragen, moeten een dossier voor de effectbeoordeling van grensoverschrijdende overdrachten indienen bij het ministerie van Openbare Veiligheid.',
        },
        '9454e465_e618_4b6f_80b3_d79fdd9cc5cb': {
            title: 'Certificering van verwerkingsdienstverlener',
            description: 'Organisaties die de verwerking van persoonsgegevens als dienst aanbieden, moeten een certificaat voor de verwerking van diensten verkrijgen van het Ministerie van Openbare Veiligheid.',
        },
        b88aec34_046a_4e0a_9c77_ddad27ea4bc4: {
            title: 'Verwerking van het impactbeoordelingsdossier wanneer de triggers zijn bereikt',
            description: 'Er moet een verwerkingseffectbeoordelingsdossier worden ingediend wanneer aan de voorgeschreven triggers wordt voldaan.',
        },
        '9d34a206_b85e_4649_a23b_e2c6f1398402': {
            title: 'Impactdossier grensoverschrijdende overdracht',
            description: 'Voor internationale gegevensoverdrachten moet een impactdossier voor grensoverschrijdende overdrachten worden opgesteld en ingediend.',
        },
        '210d3ec3_e6f2_424b_ae25_cad76bc13320': {
            title: 'Aanwijzing functionaris voor gegevensbescherming',
            description: 'Er moet een functionaris voor gegevensbescherming worden aangewezen zoals vereist door Decreet 13.',
        },
        '776183b2_2797_40e8_8d81_c4f512c566d2': {
            title: 'Governance-artefacten (beleid, rollen, procedures)',
            description: 'Governance-artefacten, waaronder beleid, rollen en procedures, moeten worden gehandhaafd.',
        },
        '4f1385f1_6dfc_4368_b4af_1040cd3f77b7': {
            title: 'Volledigheid van het bewijspakket en een onveranderlijk audittraject',
            description: 'Bewijspakketten moeten compleet zijn en er moet een onveranderlijk audittraject worden bijgehouden.',
        },
        f6826e26_8991_4a0f_9117_481490230bdd: {
            title: 'Ondersteuning van de workflow voor het indienen van dossiers',
            description: 'Workflows voor het indienen van zaken moeten worden ondersteund voor effectbeoordelingen en grensoverschrijdende dossiers.',
        },
        '8a28ee33_305a_491f_a93f_90e59f8da91b': {
            title: 'Contractuele vereisten van verwerker',
            description: 'Er moeten contractuele vereisten voor gegevensverwerkers worden vastgesteld en gecontroleerd.',
        },
        '4fd36e83_8286_40e0_b9b2_a4bc5e8377b3': {
            title: 'Jaarlijkse nalevingsbeoordeling',
            description: 'Er moet jaarlijks een nalevingsbeoordeling worden uitgevoerd om de voortdurende naleving te verifiëren.',
        },
        '8b6b6c13_0d32_4439_819f_4bd8cff02217': {
            title: 'Autoriteit die indieningsgegevens indient',
            description: 'Er moeten gegevens worden bijgehouden van alle indieningen bij de autoriteit.',
        },
        e7ce6c23_03dc_42dd_bccd_fa571c3afee3: {
            title: 'Wettelijke grondslagen voor de verwerking van persoonsgegevens',
            description: 'De verwerking van persoonsgegevens moet gebaseerd zijn op wettelijke grondslagen zoals gedefinieerd door de PDPL.',
        },
        c919c66c_6a74_4403_bb97_04b773d270bf: {
            title: 'Sensitive personal data: identify it, protect it, and say so when seeking consent',
            description: "Law 91/2025/QH15 defines sensitive personal data in Article 2(3) as personal data tied to an individual's privacy which, when infringed, directly affects the lawful rights and interests of agencies, organisations and individuals, on a list the Government issues; Decree 356/2025/NĐ-CP Article 4(1) is that list. The Law attaches no consent rule to sensitive personal data as a class; sector articles carry their own. Article 26(1)(a) requires the data subject's consent in collecting and processing personal data for health information and in insurance business, except in the Article 19(1) cases. Article 27(1)(b) forbids using a data subject's credit information for credit scoring, credit rating, credit information assessment or creditworthiness assessment without their consent. Article 31(3)(a) forbids location tracking by radio-frequency identification and other technologies, except with the data subject's consent, at the request of a competent authority as provided by law, or where the law provides otherwise. Among what the instruments attach to sensitive data: in processing it, an agency or organisation must set rules limiting access rights, a processing procedure and security measures (Decree 356 Article 4(2)); when consent to process it is sought, the data subject must be told that the data to be processed is sensitive personal data (Decree 356 Article 6(4)); a transfer of it must carry physical security for storage and transmission devices, encryption, anonymisation and other security measures (Decree 356 Article 7(2)); an organisation or individual in finance, banking or credit information must fully comply with the rules on protecting sensitive personal data (Law 91 Article 27(1)(a)), and where a data subject's sensitive data in that sector is disclosed or lost, whoever directly collected it must notify the specialised personal data protection agency and the data subject within 72 hours of discovering it (Decree 356 Article 8(3)); and a small business, start-up, household business or micro-enterprise that directly processes sensitive personal data does not have the option or exemption Law 91 Article 38(2)–(3) and Decree 356 Article 41 give from Articles 21 and 22 and Article 33(2) of the Law. Record which of the organisation's personal data falls on that list.",
        },
        '419cc7c3_a257_4293_a054_304dc2fcb8df': {
            title: 'Rechten van betrokkenen (toegang, rectificatie, verwijdering, beperking, bezwaar, portabiliteit)',
            description: 'Betrokkenen hebben uitgebreide rechten, waaronder toegang, rectificatie, verwijdering, beperking, bezwaar en portabiliteit.',
        },
        '638e6e25_3a74_4864_8bff_8e21ab82860b': {
            title: 'Melding van inbreuk aan de autoriteit',
            description: 'Gegevensbeheerders moeten de bevoegde autoriteit binnen de voorgeschreven termijn op de hoogte stellen van inbreuken op persoonsgegevens.',
        },
        '6e75c111_66b1_4726_8c27_6537846edb30': {
            title: 'Melding van inbreuk aan betrokkenen',
            description: 'Gegevensbeheerders moeten de getroffen betrokkenen op de hoogte stellen van inbreuken op persoonsgegevens.',
        },
        f686123d_3bdd_4a61_a53c_efe5b52d9058: {
            title: 'Impactbeoordeling van gegevensbescherming',
            description: 'Voor verwerkingsactiviteiten met een hoog risico moet een gegevensbeschermingseffectbeoordeling worden uitgevoerd.',
        },
        '554ea5b6_092d_4295_a57a_1bd9b2eef291': {
            title: 'Grensoverschrijdende overdrachtsgovernance',
            description: 'Grensoverschrijdende overdrachten van persoonsgegevens moeten voldoen aan de governancevereisten.',
        },
        cf9a5b29_46fd_4072_b877_13b8a69df82c: {
            title: 'Aanwijzing functionaris voor gegevensbescherming',
            description: 'Organisaties moeten indien nodig een functionaris voor gegevensbescherming aanwijzen.',
        },
        f5a4965d_eb0c_47df_9933_f185ae46463b: {
            title: 'Registratie van verwerkingsactiviteiten',
            description: 'Organisaties moeten een register bijhouden van alle verwerkingsactiviteiten.',
        },
        ce8986ff_684d_41ec_8147_b3f2a1c5206c: {
            title: 'Contractuele verplichtingen van verwerker',
            description: 'Er moeten contractuele verplichtingen worden vastgelegd met gegevensverwerkers.',
        },
        '7a3d7554_7b6f_49d7_8864_4a5cd71fd6bd': {
            title: 'Indiening van effectbeoordelingsdossiers',
            description: 'Effectbeoordelingsdossiers moeten zoals voorgeschreven bij de bevoegde autoriteit worden ingediend.',
        },
        '65dba62f_0788_44c1_89e3_09d802e28eb9': {
            title: 'Impactdossier grensoverschrijdende overdracht',
            description: 'Voor internationale gegevensoverdrachten moet een impactdossier voor grensoverschrijdende overdrachten worden opgesteld en ingediend.',
        },
        '0eccb520_b1fa_4fa3_a3ab_2d30823dd00e': {
            title: 'DPO afspraak en melding',
            description: 'Er moet een functionaris voor gegevensbescherming worden aangesteld en de autoriteit moet op de hoogte worden gesteld.',
        },
        '43ead19e_982a_4615_bafd_e6a3a4e80b3a': {
            title: 'Bestuursbeleid en -procedures',
            description: 'Het beleid en de procedures op het gebied van governance moeten worden vastgesteld en onderhouden.',
        },
        '9ebc7a42_e1f1_49c4_ac90_22a691fd2219': {
            title: 'Bewijspakket en onveranderlijk audittraject',
            description: 'Bewijspakketten moeten compleet zijn en er moet een onveranderlijk audittraject worden bijgehouden.',
        },
        '5b0ba1c0_7b86_4c80_b9f9_26e4ff9652c2': {
            title: 'Levenscyclus van dossierdossiers',
            description: 'De levenscycli van dossiers moeten worden beheerd via voorgeschreven fasen.',
        },
        '83f2f346_47bb_4377_a711_3cef26742f39': {
            title: 'Contractuele vereisten van verwerker',
            description: 'Er moeten contractuele vereisten voor gegevensverwerkers worden vastgesteld en gecontroleerd.',
        },
        cb972e06_f773_492e_8ad5_f65d1e999b79: {
            title: 'Certificering van dienstverlener voor verwerking',
            description: 'Organisaties die gegevensverwerkingsdiensten leveren, moeten een certificering verkrijgen.',
        },
        '4ba36ab5_39f0_4d79_927e_c39587bed226': {
            title: 'Autoriteit die indieningsgegevens indient',
            description: 'Er moeten gegevens worden bijgehouden van alle indieningen bij de autoriteit.',
        },
        f7a62c88_3cda_4031_bc52_1598661a5128: {
            title: 'Jaarlijkse nalevingsbeoordeling',
            description: 'Er moet jaarlijks een nalevingsbeoordeling worden uitgevoerd om de voortdurende naleving te verifiëren.',
        },
        '3e97f4d4_60ad_46a8_ad2b_615d4ca04aee': {
            title: 'Technische maatregelen voor gegevensbeveiliging',
            description: 'Technische maatregelen voor gegevensbeveiliging moeten worden geïmplementeerd en onderhouden.',
        },
        '45f4e745_a923_4879_94ce_5a2b41b7e886': {
            title: 'Decreet 356 officiële Mẫu số-formuliervereisten',
            description: 'De 13 officiële Mẫu số administratieve procedureformulieren (Mẫu số 01a–10), voorgeschreven door Decreet 356/2025/NĐ-CPĐ-CP, moeten worden gebruikt voor de overeenkomstige aangiften bij het Ministerie van Openbare Veiligheid. De Phụ lục I-VIII-items zijn interne gegevensstructuurtoewijzingen, geen officiële regelgevingsformulieren.',
        },
        ff452604_7461_4c12_aeab_cb7dd1fb5ba3: {
            title: 'DPIA dossierindiening bij A05',
            description: 'Organisaties moeten DPIA-dossiers indienen bij A05/MPS voor verwerkingsactiviteiten waarbij gevoelige persoonsgegevens betrokken zijn of grootschalige verwerking.',
        },
        '003600ae_7a0f_4034_b206_72f59409b8f3': {
            title: 'Indiening van dossiers voor grensoverschrijdende overdracht',
            description: 'Organisaties moeten dossiers voor grensoverschrijdende gegevensoverdracht indienen ter beoordeling en goedkeuring volgens A05/MPS.',
        },
        '4632b20c_293a_4c7e_968e_4ce178c3b7d8': {
            title: 'Verwerken servicecertificaataanvraag',
            description: 'Aanbieders van gegevensverwerkingsdiensten moeten certificaten aanvragen bij A05/MPS.',
        },
        be96add4_ebdb_4b6c_9426_4dc87b7cd2ce: {
            title: 'Melding verwerking persoonsgegevens',
            description: 'Verwerkingsverantwoordelijken moeten A05/MPS op de hoogte stellen van de verwerkingsactiviteiten van persoonsgegevens door middel van voorgeschreven formulieren (Mẫu số 01a/01b).',
        },
        '39f50471_27cc_46bd_acff_9aa2211d43ab': {
            title: 'Jaarlijkse situatierapportage',
            description: 'Verwerkingsverantwoordelijken moeten jaarlijkse situatierapporten over de verwerking van persoonsgegevens indienen (Mẫu số 09).',
        },
        '3b45c6fa_d66a_448a_9ff1_597caa055e09': {
            title: 'Standaard vertrouwelijkheid van gebruikersinformatie',
            description: 'Gebruikersinformatie moet standaard als vertrouwelijk worden behandeld.',
        },
        '2994510e_b1f8_4cdd_947c_d9542746433b': {
            title: 'Minimale organisatorische en technische beveiligingsmaatregelen',
            description: 'Er moeten minimale organisatorische en technische beveiligingsmaatregelen worden geïmplementeerd.',
        },
        '8643160f_9a10_4d93_baf5_082c6a7c768a': {
            title: 'Alleen bekendmaking op juiste basis',
            description: 'Openbaarmaking van gebruikersinformatie is alleen toegestaan als er een goede wettelijke basis is.',
        },
        c1fb7d85_fdc0_402e_ab0c_ff134bd72dfa: {
            title: 'Verplichte openbaarmaking en controleerbaarheid',
            description: 'Alle openbaarmakingen moeten worden geregistreerd en controleerbaar zijn.',
        },
        efa36bc0_abae_417a_b676_9509af37f89d: {
            title: 'Interne goedkeuringsworkflow voor openbaarmakingen',
            description: 'Voor alle openbaarmakingen moet een interne goedkeuringsworkflow worden opgezet.',
        },
        d086fdcd_9a23_4500_893c_db8ac62bd787: {
            title: 'Afhandeling van respons op autoriteitsverzoeken',
            description: 'Autoriteitsverzoeken moeten worden behandeld en beantwoord volgens voorgeschreven procedures.',
        },
    },
    framework_rights_descriptions: {
        be_informed: 'De betrokkene heeft het recht om geïnformeerd te worden over de verwerking van persoonsgegevens.',
        consent: 'De betrokkene heeft het recht om toestemming te geven voor de verwerking van zijn persoonsgegevens.',
        refuse_consent: 'De betrokkene heeft het recht om geen toestemming te geven voor de verwerking van zijn persoonsgegevens.',
        withdraw_consent: 'De betrokkene heeft het recht om de intrekking van eerder gegeven toestemming te verzoeken.',
        access: 'De betrokkene heeft recht op inzage en toegang tot de persoonsgegevens die de verwerkingsverantwoordelijke bewaart.',
        rectification: 'De betrokkene heeft het recht om onjuiste persoonsgegevens te corrigeren of te vragen.',
        data_provision: 'De betrokkene heeft het recht om te verzoeken dat zijn persoonsgegevens worden verstrekt (yêu cầu cung cấp dữ liệu cá nhân).',
        deletion: 'De betrokkene heeft het recht om verwijdering of vernietiging van persoonlijke gegevens te verzoeken, behoudens uitzonderingen in de wet.',
        restriction_of_processing: 'De betrokkene heeft het recht om beperking van de verwerking van zijn persoonsgegevens te verzoeken.',
        object_to_processing: 'De betrokkene heeft het recht om een verzoek te sturen waarin hij bezwaar maakt tegen de verwerking van zijn persoonsgegevens.',
        complaint: 'De betrokkene heeft het recht om een klacht (khiếu nại) in te dienen over de verwerking van persoonsgegevens.',
        denunciation: 'De betrokkene heeft het recht om aangifte te doen (tố cáo) met betrekking tot schendingen van de bescherming van persoonsgegevens.',
        lawsuit: 'De betrokkene heeft het recht om juridische stappen (khởi kiện) te ondernemen wegens schending van zijn gegevensbeschermingsrechten.',
        claim_damages: 'De betrokkene heeft het recht om schadevergoeding te eisen voor schade veroorzaakt door schendingen van de bescherming van persoonsgegevens.',
        request_protection_measures: 'De betrokkene heeft het recht om bevoegde autoriteiten of relevante organisaties/individuen te verzoeken maatregelen te nemen om hun persoonsgegevens te beschermen.',
    },
    framework_procedures: {
        VN_DP_DRAFT_P7: {
            name: 'Risicobeheer en -monitoring (ontwerp)',
            steps: {
                '1': 'Identificeer risicocategorieën',
                '2': 'Zorg voor controles op het gebied van vroegtijdige waarschuwing',
                '3': "Beoordeel de risico's voor verzamelen, opslaan, verwerken, delen en transacties",
                '4': "Leg de risico's op het gebied van privacy, cyberbeveiliging en toegangsbeheer vast",
                '5': 'Activeer herstelacties',
                '6': 'Behoud de recensiegeschiedenis',
            },
        },
        VN_DP_DRAFT_P6: {
            name: 'Klachten en geschillen (concept)',
            steps: {
                '1': 'Ontvang klachten-/geschilintake',
                '2': 'Controleer de volledigheid van de vereiste informatie; bij onvolledigheid binnen 24 uur een aanvulling aanvragen',
                '3': 'Breng de partijen binnen drie werkdagen na ontvangst van een volledig verzoek op de hoogte van de aanpak en de verwachte tijdlijn',
                '4': 'Vraag om antwoorden en ondersteunende documenten',
                '5': 'Beoordeel betwistingsfeiten en platformverantwoordelijkheid',
                '6': 'Resultaat van de recordresolutie',
                '7': 'Bewaar bewijsmateriaal en sluit de zaak',
            },
        },
        VN_DP_DRAFT_P5: {
            name: 'Levering en certificaat van eigendomsrechten op gegevens (concept)',
            steps: {
                '1': 'Product/dienst leveren volgens contractmethode',
                '2': 'Registreer de bezorggebeurtenis en -tijd',
                '3': 'Genereer een certificaat van eigendomsrechten op gegevens',
                '4': 'Registreer de voltooiing van de transactie',
                '5': 'Schakel later opzoeken/verificatie in waar nodig',
            },
        },
        VN_DP_DRAFT_P4: {
            name: 'Transactie-initiatie en contractering (concept)',
            steps: {
                '1': 'Bekijk de vermelding en het evaluatieresultaat',
                '2': 'Start een workflow voor aankoop, onderhandeling of veiling',
                '3': 'Genereer elektronisch contract',
                '4': 'Verzeker de acceptatie van beide partijen',
                '5': 'Registreer de voltooiing van de betaling',
                '6': 'Levering autoriseren',
            },
        },
        VN_DP_DRAFT_P3: {
            name: 'Toegang tot sandbox/testomgeving (concept)',
            steps: {
                '1': 'Sandbox-verzoek indienen',
                '2': 'Definieer het bereik, de toegestane gegevens, de tijdsperiode en de verwerkingsrechten',
                '3': 'Voorzien van geïsoleerde technische omgeving',
                '4': 'Bewaak de beveiliging en toegang tijdens het testen',
                '5': 'Controleer de uitvoer vóór extractie',
                '6': 'Laat extractie alleen goedkeuren als dit is toegestaan',
                '7': 'Sluit de testsessie af en ruim de omgeving op',
            },
        },
        VN_DP_DRAFT_P2: {
            name: 'Gegevensproduct-/dienstlijst (concept)',
            steps: {
                '1': 'Maak een vermeldingstoepassing',
                '2': 'Leg bron- en eigendoms-/gebruiksrechtbewijs vast',
                '3': 'Leg technische kenmerken, formaat, API details en beperkingen vast',
                '4': 'Documenteer kwaliteitsinformatie',
                '5': 'Dien het vermeldingsdossier in voor platformbeoordeling',
                '6': 'Platform voert volledigheids-, wettigheids- en technische controles uit',
                '7': 'Lijst goedkeuren of afwijzen',
                '8': 'Indien goedgekeurd, publiceert u de vermelding met het bijgevoegde evaluatieresultaat',
            },
        },
        VN_DP_DRAFT_P1: {
            name: 'Onboarding van deelnemers (concept)',
            steps: {
                '1': 'Bepaal het type deelnemer: verkoper of koper',
                '2': 'Controleer de juridische status en handelingsbekwaamheid',
                '3': 'Controleer aanvullende voorwaarden als er voorwaardelijke bedrijfslijnen van toepassing zijn',
                '4': 'Transactieaccount registreren',
                '5': 'Betaalrekening koppelen',
                '6': 'Platformdeelname goedkeuren',
                '7': 'Bewaar deelnemersrecord en audittrail',
            },
        },
        AI_A1: {
            name: 'AI Systeemregistratie/melding',
            steps: {
                '1': 'Toepasbaarheidscontrole en AI systeemclassificatie',
                '2': 'Registratie-/meldingsdossier opbouwen',
                '3': 'Interne goedkeuring',
                '4': 'Inzenden via kanaal',
                '5': 'Amendementlus — indien geretourneerd, bijwerken en opnieuw indienen',
                '6': 'Bevestiging sluiten en opslaan',
            },
        },
        AI_A2: {
            name: 'AI Workflow voor risicoclassificatie',
            steps: {
                '1': 'Maak een AI Systeemregisterinvoer',
                '2': 'Voer de classificatiewizard uit',
                '3': 'Bewijsmateriaal bijvoegen',
                '4': 'Classificatie goedkeuren',
                '5': 'Koppel uitgangen aan systeemdossier',
            },
        },
        AI_A3: {
            name: 'Naleving van etikettering en openbaarmaking',
            steps: {
                '1': 'Identificeer etiketteringsverplichtingen per systeem en gebruiksscenario',
                '2': 'Implementeer etiketteringsaankondigingen',
                '3': 'Leg bewijs vast: screenshots, UI-kopie, technische metadata',
                '4': 'Behandel vrijstellingen en leg de rechtvaardiging vast',
                '5': 'Goedkeuren en versie',
            },
        },
        AI_A4: {
            name: 'Rapportage van ernstige incidenten',
            steps: {
                '1': 'Maak een AI incidentrecord',
                '2': 'Classificeer de ernst',
                '3': 'Voer insluitingsacties uit en verzamel bewijsmateriaal',
                '4': 'Genereer een sjabloonexport voor een rapport over ernstige incidenten',
                '5': 'Via kanaal indienen bij de autoriteit',
                '6': 'Volg de reacties en afsluitingen van autoriteiten',
            },
        },
        AI_A5: {
            name: 'AI Effectbeoordeling',
            steps: {
                '1': 'Beoordelingsrecord maken',
                '2': 'Vul de vereiste secties in',
                '3': 'Voeg bewijsmateriaal toe: gegevensstromen, modeldocumentatie, controles',
                '4': 'Interne goedkeuring',
                '5': 'Rapport exporteren en opslaan in dossier',
            },
        },
        AI_A6: {
            name: 'Gecontroleerd testen / Sandbox-deelname',
            steps: {
                '1': 'Volledige geschiktheids- en gereedheidsrecord',
                '2': 'Aanvraag voorbereiden en exporteren',
                '3': 'Inzenden via kanaal',
                '4': 'Goedkeuring bijhouden',
                '5': 'Voer monitoringcontrolepunten en logboeken uit',
                '6': 'Compleet opleveringsrapport en afsluitingspakket',
            },
        },
        CYBERSEC_C1: {
            name: 'Kritieke veiligheidsbeoordeling van informatiesystemen',
            steps: {
                '1': 'Classificeer informatiesystemen volgens de nationale cyberbeveiligingsvereisten',
                '2': 'Voer beveiligingsbeoordelingen en risico-evaluatie uit voor geclassificeerde systemen',
                '3': 'Implementeer de vereiste beveiligingsmaatregelen en basiscontroles',
                '4': 'Documenteer het bewijs van naleving en plan periodieke beoordelingen',
            },
        },
        CYBERSEC_C2: {
            name: 'Melding van cyberbeveiligingsincidenten',
            steps: {
                '1': 'Detecteer, classificeer en beoordeel het cyberbeveiligingsincident',
                '2': 'Voer onmiddellijke responsacties uit en bewaar bewijsmateriaal',
                '3': 'Rapporteer aan de autoriteit en neem deel aan de nationale coördinatie van incidentenrespons',
                '4': 'Voer een beoordeling na het incident uit en documenteer de geleerde lessen',
            },
        },
        CYBERSEC_C3: {
            name: 'Verificatie van naleving van gegevenslokalisatie',
            steps: {
                '1': 'Beoordeel of triggers voor gegevenslokalisatie van toepassing zijn op basis van het servicetype en de gegevenscategorieën',
                '2': 'Controleer of de lokale opslagarchitectuur en grensoverschrijdende stroomcontroles aanwezig zijn',
                '3': 'Beoordeel lokale aanwezigheidsvereisten (filiaal/vertegenwoordigingskantoor) indien van toepassing',
                '4': 'Documenteer nalevingsbewijs en bewaar dit voor beoordeling door de autoriteit',
            },
        },
        CYBERSEC_C4: {
            name: 'Antwoord van autoriteitssamenwerkingsverzoek',
            steps: {
                '1': 'Ontvang en verifieer de legitimiteit en reikwijdte van het autorisatieverzoek',
                '2': 'Verzamel de gevraagde informatie en bereid deze voor of voer de vereiste acties uit',
                '3': 'Dien uw reactie binnen de vereiste tijdlijn in bij de autoriteit',
                '4': 'Registreer de openbaarmaking en sluit de zaak',
            },
        },
        CYBERSEC_C5: {
            name: 'Kwalificatieverificatie van cyberbeveiligingspersoneel',
            steps: {
                '1': 'Beoordeel de beveiligingssituatie van leveranciers met behulp van het beoordelingskader voor cyberbeveiliging',
                '2': 'Controleer het bereik van de toegang tot de leveranciersgegevens en registreer u in het toegangslogboek van de leverancier',
                '3': 'Bekijk de clausule over de melding van leveranciersincidenten en de contractuele cyberbeveiligingsvereisten',
                '4': 'Plan periodieke beveiligingsbeoordelingen van leveranciers en controleer de naleving',
            },
        },
        DATA_D1: {
            name: 'Gegevensinventarisatie en classificatie instellen',
            steps: {
                '1': 'Catalogiseer alle gegevensassets met typen en bronnen in het gegevensinventarisregister',
                '2': 'Classificeer elk activum op gevoeligheidsniveau',
                '3': 'Definieer afhandelingsregels per classificatieniveau',
                '4': 'Leg bewijs vast: screenshots, architectuurdiagrammen, toegangscontroles',
                '5': 'Plan periodieke beoordelingen',
            },
        },
        DATA_D2: {
            name: 'Beheer van het delen van gegevens',
            steps: {
                '1': 'Evalueer nieuwe regelingen voor delen via risicobeoordeling voor het delen van gegevens',
                '2': 'Voltooi de interne goedkeuringsworkflow',
                '3': 'Registreer de deelgebeurtenis in het logboek voor het delen/openbaar maken van gegevens',
                '4': 'Verzamel bewijsmateriaal: goedkeuringsgegevens, contractuele documenten',
                '5': 'Periodieke evaluatie van de deelregelingen',
            },
        },
        DATA_D3: {
            name: 'Beoordeling van grensoverschrijdende gegevensoverdracht',
            steps: {
                '1': 'Identificeer grensoverschrijdende overdrachten in de gegevensinventaris',
                '2': 'Voer een overdrachtsrisicobeoordeling uit',
                '3': 'Pas waarborgen en beheersmaatregelen toe',
                '4': 'Document in het grensoverschrijdende deel van de bestuursattest',
                '5': 'Exporteer beoordelingspakket voor audit',
            },
        },
        DATA_D4: {
            name: 'Jaarlijkse bestuursattest',
            steps: {
                '1': 'Beoordeel de governance RACI-matrix',
                '2': 'Beleidsstatus verifiëren (beheer, classificatie, delen, bewaren)',
                '3': 'Compleet attestpakket',
                '4': 'Zorg voor interne goedkeuring',
                '5': 'Attestatie exporteren en archiveren',
            },
        },
        DATA_D5: {
            name: 'Afhandeling van gegevensincidenten',
            steps: {
                '1': 'Classificeer het incident met behulp van het beslissingsrecord',
                '2': 'Voer incidentrespons uit per draaiboek',
                '3': 'Bewijsmateriaal vastleggen: logboeken, schermafbeeldingen, communicatie',
                '4': 'Voer een beoordeling na een incident uit',
                '5': 'Als kennisgeving aan de autoriteit vereist is: maak een indieningsaanvraag aan en dien deze in bij MIC',
                '6': 'Sluiten en archiveren',
            },
        },
        ECOM_P1: {
            name: 'Melding (verkoopwebsite/app)',
            steps: {
                '1': 'Classificatie bepalen: verkoopsite/app versus marktplaats/serviceplatform',
                '2': 'Creëer een intern dossierrecord met bedrijfsidentiteit, website-/app-informatie en vereiste openbaarmakingen',
                '3': "Genereer een bewijspakket: screenshots van openbaarmakingen, beleids-URL's, versiegeschiedenis",
                '4': 'Indienen via Online.gov.vn – upload documenten, noteer tijdstempel en bevestiging',
                '5': 'Volg de verwerking: leg de redenen vast als u het terugstuurt voor wijziging en verzend het opnieuw',
                '6': 'Sluiten: bevestiging opslaan, bewijsmateriaal koppelen en audittraject',
            },
        },
        ECOM_P2: {
            name: 'Registratie (serviceplatforms / marktplaatsen)',
            steps: {
                '1': 'Bepaal het platformmodel en de verplichtingen – marktplaats versus gemengde versus sociale handel',
                '2': 'Registratiedossier opbouwen – aanvraagformulier, dienstverleningsschema, algemene voorwaarden, platformregels',
                '3': 'Interne goedkeuringen — wijs de juridische/compliance-eigenaar toe, keur deze goed voor indiening',
                '4': 'Indienen via Online.gov.vn – dossier uploaden, tijdstempel vastleggen',
                '5': 'Verwerkingslus — indien geretourneerd voor wijziging, bijwerken en opnieuw indienen',
                '6': 'Goedkeuring en operationalisering – opslagbevestiging, bijhouden van doorlopende nalevingsregisters',
            },
        },
        ECOM_P3: {
            name: 'Update/wijzigingsmelding',
            steps: {
                '1': 'Identificeer de veranderingstrigger: materiële verandering in dossieritems',
                '2': 'Genereer een wijzigingspakket: wijzigingssamenvatting, bijgewerkte documenten, bewijsupdates',
                '3': 'Verzend de update en registreer de indieningsstatus',
                '4': 'Volg de verwerking en sluit af',
            },
        },
        ECOM_P4: {
            name: 'Beëindiging / deactivering',
            steps: {
                '1': 'Bereid het beëindigingspakket voor - stopdatum, reden, afhandelingsplan voor consumentenbescherming, bewijs van openbare kennisgeving',
                '2': 'Beëindiging indienen – noteer tijdstempel en bevestiging',
                '3': 'Sluiten: bewaar de definitieve status en bewijsmateriaal',
            },
        },
        ECOM_P5: {
            name: 'Trustmark / Geloofwaardigheidsbeoordelingsprogramma',
            steps: {
                '1': 'Beoordeling van geschiktheid en gereedheid',
                '2': 'Bereid het dossier voor met behulp van het officiële formulier',
                '3': 'Verzenden en volgen',
                '4': 'Zorg voor doorlopend nalevingsbewijs',
            },
        },
        PDPL_P1: {
            name: 'DPIA Indiening indienen bij MPS',
            steps: {
                '1': 'Voltooi DPIA met behulp van het formuliertraject van Decreet 356 (Mẫu số 02a/02b)',
                '2': 'Verzamel omslagdocumenten en ondersteunend bewijsmateriaal',
                '5': 'Bewaar het dossier en het bewijs van indiening voor het audittraject',
            },
        },
        PDPL_P2: {
            name: 'Aanvraag grensoverschrijdende overdracht',
            steps: {
                '1': 'Vul het dossier voor grensoverschrijdende overdracht in met behulp van Decreet 356-formulieren (Mẫu số 01a/01b, Mẫu số 09)',
            },
        },
        PDPL_P3: {
            name: 'Certificaataanvraag voor gegevensverwerkingsservice',
            steps: {
                '1': 'Bereid een certificeringsaanvraagdossier voor volgens de vereisten van Decreet 356',
                '2': 'Dien uw aanvraag in bij MPS met de vereiste ondersteunende documenten',
                '3': 'Reageer op MPS beoordelingsreacties en aanvullingsverzoeken',
            },
        },
        PDPL_P4: {
            name: 'Verwerking van rechten van betrokkenen',
            steps: {
                '1': 'Aanvraag ontvangen en loggen; verifieer de identiteit van de betrokkene binnen 2 werkdagen',
                '2': 'Beoordeel het type verzoek en bepaal de toepasselijke deadline (10/15/20 dagen per type)',
                '3': 'Voer fulfilmentacties uit in relevante systemen en afdelingen',
                '4': 'Bereid een antwoord voor en stuur het naar de betrokkene, met resultaat en onderbouwing',
                '5': 'Sluit de zaak en bewaar bewijsmateriaal voor audit; documenteer eventuele weigeringsredenen',
            },
        },
        PDPL_P5: {
            name: 'Kennisgeving van inbreuk aan MPS',
            steps: {
                '1': 'De inbreuk detecteren en beoordelen; vaststellen of de meldingsplicht geldt en de toepasselijke meldingstermijn afleiden uit de geïnstalleerde termijnrecords',
                '2': 'Bereid een meldingspakket voor volgens de PDPL vereisten',
                '3': 'De melding bij MPS indienen binnen de meldingstermijn die volgt uit de geïnstalleerde termijnrecords, gerekend vanaf de ontdekking',
                '4': 'Reageren op MPS vervolgverzoeken; supplementen en coördinatiebewijs indienen',
            },
        },
        TELECOM_T1: {
            name: 'Afhandeling van autoriteitsverzoeken',
            steps: {
                '1': 'Ontvang en registreer het machtigingsverzoek via het intakerecord',
                '2': 'Controleer de identiteit van de autoriteit, de rechtsgrondslag en de reikwijdte van het verzoek',
                '3': 'Voer een interne juridische beoordeling uit en verkrijg goedkeuring van het management',
                '4': 'Bereid een responspakket voor met verzameld bewijsmateriaal',
                '5': 'Antwoordbrief opstellen en goedkeuren',
                '6': 'Creëer een indieningsaanvraag en dien uw reactie in via het kanaal',
                '7': 'Openbaarmaking van logboeken en erkenning van de autoriteit',
                '8': 'Sluit de zaak en bewaar bewijsmateriaal voor het audittraject',
            },
        },
        TELECOM_T2: {
            name: 'Beheer van openbaarmaking',
            steps: {
                '1': 'Ontvang een openbaarmakingsverzoek (toestemming van de gebruiker, wettelijke verplichting of autoriteitsverzoek)',
                '2': 'Voltooi de goedkeuringsworkflow voor openbaarmaking',
                '3': 'Voer de openbaarmaking uit en noteer het in het logregister',
                '4': 'Verzamel bewijsmateriaal: goedkeuringsgegevens, communicatiegegevens',
                '5': 'Periodieke beoordeling van het openbaarmakingslogboek',
            },
        },
        TELECOM_T3: {
            name: 'Vertrouwelijkheidsbeleid en toegangscontrole instellen',
            steps: {
                '1': 'Opstellen en goedkeuren van het vertrouwelijkheidsbeleid',
                '2': 'Opstellen en goedkeuren van toegangscontrole- en logbeleid',
                '3': 'Documentverwerkingsactiviteiten in het gebruikersinformatieverwerkingsregister',
                '4': 'Verzamel bewijsmateriaal: beleidsdocumenten, implementatiebewijs',
                '5': 'Plan periodieke beoordelingen',
            },
        },
        TELECOM_T4: {
            name: 'Beoordeling van telecomincidenten',
            steps: {
                '1': 'Classificeer en beoordeel het incident',
                '2': 'Bepaal de meldingsvereisten',
                '3': 'Als kennisgeving aan de autoriteit vereist is: maak een indieningsdossier aan',
                '4': 'Bewijsmateriaal vastleggen en bewaren',
                '5': 'Resolutie en afsluiting',
            },
        },
        TELECOM_T5: {
            name: 'Beheer van bewaren en verwijderen',
            steps: {
                '1': 'Definieer het bewaar- en verwijderingsschema per gegevenscategorie',
                '2': 'Implementeer verwijderingsprocedures',
                '3': 'Leg bewijsmateriaal vast – verwijder records',
                '4': 'Periodieke beoordeling van planningen',
            },
        },
    },
    framework_fines: {
        VN_CYBERSEC_LAW: {},
        VN_ECOMMERCE_LAW_2025: {
            'ECOM-FINE-A': 'Registratie / Melding / Administratieve gereedheid',
            'ECOM-FINE-B': 'Klachten en consumentenbescherming',
            'ECOM-FINE-C': 'Toezicht en verwijderingshandhaving',
            'ECOM-FINE-D': 'Grensoverschrijdende operaties',
            'ECOM-FINE-E': 'Platformregels en transparantie',
        },
        VN_TELECOM_LAW_2023: {
            'TELECOM-FINE-A': 'Mislukkingen bij het afhandelen van autoriteitsverzoeken',
            'TELECOM-FINE-B': 'Vertrouwelijkheid en ongeoorloofde openbaarmaking',
            'TELECOM-FINE-C': 'Toegangscontrole en logboekfouten',
            'TELECOM-FINE-D': 'Overtredingen bij het bewaren en verwijderen',
            'TELECOM-FINE-E': 'Mislukte beoordeling van telecomincidenten',
        },
    },
    framework_fines_detail: {
        VN_TELECOM_LAW_2023: {
            'TELECOM-FINE-E': {
                fine_individual: 'Tot 100 miljoen VND',
                fine_org: 'Tot 200 miljoen VND',
                deadlines: 'Onmiddellijk bij detectie',
                remediation: [
                    'Mandatory incident classification',
                    'Mandatory severity and decision records',
                    'Mandatory evidence preservation for confidentiality incidents',
                    'Mandatory post-incident closure',
                ],
            },
            'TELECOM-FINE-D': {
                fine_individual: 'Tot 100 miljoen VND',
                fine_org: 'Tot 200 miljoen VND',
                deadlines: 'Doorlopende verplichting',
                remediation: [
                    'Mandatory retention schedule implementation',
                    'Mandatory deletion evidence preservation',
                    'Mandatory retention exception decision records',
                ],
            },
            'TELECOM-FINE-C': {
                fine_individual: 'Tot 100 miljoen VND',
                fine_org: 'Tot 200 miljoen VND',
                deadlines: 'Doorlopende verplichting',
                remediation: [
                    'Mandatory access-control policy implementation',
                    'Mandatory logging of access events',
                    'Mandatory review of access rights',
                ],
            },
            'TELECOM-FINE-B': {
                fine_individual: 'Tot 100 miljoen VND',
                fine_org: 'Tot 200 miljoen VND',
                deadlines: 'Doorlopende verplichting',
                remediation: [
                    'Mandatory confidentiality policy implementation',
                    'Mandatory disclosure approval workflow',
                    'Mandatory disclosure trail maintenance',
                    'Mandatory user information access controls',
                ],
            },
            'TELECOM-FINE-A': {
                fine_individual: 'Tot 100 miljoen VND',
                fine_org: 'Tot 200 miljoen VND',
                deadlines: 'Zoals gespecificeerd in het machtigingsverzoek',
                remediation: [
                    'Mandatory intake and verification of authority request',
                    'Mandatory response within prescribed timeline',
                    'Mandatory disclosure log entry',
                    'Mandatory evidence preservation',
                ],
            },
        },
        VN_ECOMMERCE_LAW_2025: {
            'ECOM-FINE-E': {
                fine_individual: 'Tot 80 miljoen VND',
                fine_org: 'Tot 160 miljoen VND',
                deadlines: 'Doorlopende verplichting',
                remediation: [
                    'Mandatory platform rules publication and versioning',
                    'Mandatory user-facing disclosures',
                    'Mandatory seller/affiliate/livestream obligations evidenced',
                    'Mandatory control history and approval chain',
                ],
            },
            'ECOM-FINE-D': {
                fine_individual: 'Tot 80 miljoen VND',
                fine_org: 'Tot 160 miljoen VND',
                deadlines: 'Doorlopende verplichting',
                remediation: [
                    'Mandatory cross-border obligations assessment',
                    'Mandatory authorized representative readiness',
                    'Mandatory cross-border operations register',
                    'Mandatory evidence of compliance action',
                ],
            },
            'ECOM-FINE-C': {
                fine_individual: 'Tot 80 miljoen VND',
                fine_org: 'Tot 160 miljoen VND',
                deadlines: 'Doorlopende verplichting',
                remediation: [
                    'Mandatory review of prohibited or unlawful listings',
                    'Mandatory takedown decision records',
                    'Mandatory evidence preservation',
                    'Mandatory seller notification and closure trail',
                ],
            },
            'ECOM-FINE-B': {
                fine_individual: 'Tot 80 miljoen VND',
                fine_org: 'Tot 160 miljoen VND',
                deadlines: 'Zoals gespecificeerd per klachtafhandelingsverplichtingen',
                remediation: [
                    'Mandatory complaint intake procedure',
                    'Mandatory resolution record',
                    'Mandatory complaint audit trail',
                    'Mandatory escalation for cross-border seller issues',
                ],
            },
            'ECOM-FINE-A': {
                fine_individual: 'Tot 80 miljoen VND',
                fine_org: 'Tot 160 miljoen VND',
                deadlines: 'Voordat u met e-commerceactiviteiten begint',
                remediation: [
                    'Mandatory platform classification',
                    'Mandatory notification or registration completion',
                    'Mandatory evidence of portal submission',
                    'Mandatory update/termination tracking',
                ],
            },
        },
        VN_CYBERSEC_LAW: {},
    },
    framework_procedures_detail: {
        VN_DP_DRAFT_P7: {
            trigger: 'Voortdurende exploitatie van het platform',
            outputs: [
                'Risk assessment record',
                'Early warning log',
                'Remediation record',
                'Review trail',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Platformexploitant / Risico',
                '2': 'Platformoperator / Beveiliging',
                '3': 'Platformexploitant / Risico',
                '4': 'Platformoperator / Beveiliging',
                '5': 'Platformoperator',
                '6': 'Naleving',
            },
            step_outputs: {
                '1': ['Risk assessment record'],
                '2': ['Early warning log'],
                '3': ['Risk review findings'],
                '4': ['Risk register entries'],
                '5': ['Remediation record'],
                '6': ['Review trail'],
            },
        },
        VN_DP_DRAFT_P6: {
            trigger: 'Een deelnemer dient een klacht of geschilverzoek in',
            outputs: [
                'Complaint intake record',
                'Dispute resolution record',
                'Evidence pack',
                'Case closure record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Platformoperator',
                '2': 'Platformoperator',
                '3': 'Platformoperator',
                '4': 'Platformoperator',
                '5': 'Platformexploitant / Juridisch',
                '6': 'Platformoperator',
                '7': 'Naleving',
            },
            step_outputs: {
                '1': ['Complaint intake record'],
                '2': ['Completeness check', 'Supplement request (if any)'],
                '3': ['Handling approach notice'],
                '4': ['Evidence pack'],
                '5': ['Assessment findings'],
                '6': ['Dispute resolution record'],
                '7': ['Case closure record', 'Archived evidence pack'],
            },
        },
        VN_DP_DRAFT_P5: {
            trigger: 'De levering vindt plaats nadat aan de contract- en betalingsvoorwaarden is voldaan',
            outputs: [
                'Delivery record',
                'Certificate of property rights over data',
                'Transaction register entry',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Verkoper / Platformexploitant',
                '2': 'Platformoperator',
                '3': 'Platformoperator',
                '4': 'Platformoperator',
                '5': 'Platformoperator',
            },
            step_outputs: {
                '1': ['Delivery record'],
                '2': ['Delivery event log entry'],
                '3': ['Property rights certificate'],
                '4': ['Transaction register entry'],
                '5': ['Verification access record'],
            },
        },
        VN_DP_DRAFT_P4: {
            trigger: 'Een koper initieert een transactie voor gegevens, producten of diensten',
            outputs: [
                'Transaction initiation record',
                'Electronic contract',
                'Payment confirmation',
                'Delivery authorization record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Koper',
                '2': 'Koper / Platformexploitant',
                '3': 'Platformoperator',
                '4': 'Koper / Verkoper',
                '5': 'Platformexploitant / Betaling',
                '6': 'Platformoperator',
            },
            step_outputs: {
                '1': ['Listing review decision'],
                '2': ['Transaction initiation record'],
                '3': ['Electronic contract record'],
                '4': ['Contract acceptance record'],
                '5': ['Payment confirmation'],
                '6': ['Delivery authorization record'],
            },
        },
        VN_DP_DRAFT_P3: {
            trigger: 'Een deelnemer wil dataproducten/diensten evalueren in de geïsoleerde omgeving',
            outputs: [
                'Sandbox request',
                'Sandbox scope record',
                'Output review record',
                'Sandbox termination record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Deelnemer',
                '2': 'Platformexploitant / Deelnemer',
                '3': 'Platformoperator / IT',
                '4': 'Platformoperator / Beveiliging',
                '5': 'Platformoperator',
                '6': 'Platformoperator',
                '7': 'Platformoperator / IT',
            },
            step_outputs: {
                '1': ['Sandbox request'],
                '2': ['Sandbox scope record'],
                '3': ['Provisioned environment'],
                '4': ['Security monitoring log'],
                '5': ['Output review record'],
                '6': ['Extraction approval decision'],
                '7': ['Sandbox termination record'],
            },
        },
        VN_DP_DRAFT_P2: {
            trigger: 'Een verkoper wil gegevens, producten of gegevensdiensten aanbieden om te verhandelen',
            outputs: [
                'Listing application',
                'Lawful-source verification record',
                'Technical validation record',
                'Quality disclosure record',
                'Listing approval record',
                'Listing register entry',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Verkoper',
                '2': 'Verkoper / Juridisch',
                '3': 'Verkoper / IT',
                '4': 'Verkoper',
                '5': 'Verkoper',
                '6': 'Platformoperator',
                '7': 'Platformoperator',
                '8': 'Platformoperator',
            },
            step_outputs: {
                '1': ['Listing application'],
                '2': ['Lawful-source verification record'],
                '3': ['Technical validation record'],
                '4': ['Quality disclosure record'],
                '5': ['Submitted listing dossier'],
                '6': ['Review findings'],
                '7': ['Listing approval record'],
                '8': ['Published listing', 'Listing register entry'],
            },
        },
        VN_DP_DRAFT_P1: {
            trigger: 'Een verkoper of koper sluit zich aan bij het dataplatform',
            outputs: [
                'Seller / buyer onboarding record',
                'Participant register entry',
                'Account setup evidence',
                'Approval record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Platformoperator',
                '2': 'Platformexploitant / Juridisch',
                '3': 'Platformexploitant / Juridisch',
                '4': 'Platformoperator',
                '5': 'Platformoperator',
                '6': 'Platformoperator',
                '7': 'Naleving',
            },
            step_outputs: {
                '1': ['Participant type decision'],
                '2': ['Legal capacity verification record'],
                '3': ['Conditional-business check record'],
                '4': ['Transaction account registration record'],
                '5': ['Payment account link record'],
                '6': ['Participation approval record'],
                '7': ['Participant register entry', 'Audit trail'],
            },
        },
        PDPL_P1: {
            trigger: 'Aanvang van de verwerking van persoonsgegevens waarvoor een effectbeoordeling vereist is',
            outputs: ['DPIA dossier', 'Submission confirmation', 'Supplement package'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'DPO / Privacyteam',
                '2': 'Privacy-team',
                '3': 'Juridisch / Naleving',
                '4': 'DPO / Privacyteam',
                '5': 'Nalevingsteam',
            },
            step_outputs: {
                '1': ['Completed DPIA dossier'],
                '2': ['Dossier package', 'Cover letter'],
                '3': ['Submission confirmation', 'Tracking reference'],
                '4': ['Supplement package', 'Updated dossier'],
                '5': ['Archived dossier', 'Audit evidence pack'],
            },
        },
        PDPL_P2: {
            trigger: 'Intentie om persoonlijke gegevens buiten Vietnam over te dragen',
            outputs: ['Transfer dossier', 'Submission confirmation', 'Supplement response'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'DPO / Privacyteam',
                '2': 'Juridisch / Naleving',
                '3': 'DPO / Privacyteam',
            },
            step_outputs: {
                '1': ['Transfer dossier package'],
                '2': ['Submission confirmation', 'Tracking reference'],
                '3': ['Supplement response', 'Updated dossier'],
            },
        },
        PDPL_P3: {
            trigger: 'Organisatie die gegevensverwerkingsdiensten levert waarvoor certificering vereist is',
            outputs: ['Certification dossier', 'Certificate', 'Re-issue and exchange record'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'Juridisch / Naleving',
                '2': 'Juridisch / Naleving',
                '3': 'DPO / Privacyteam',
                '4': 'Nalevingsteam',
            },
            step_outputs: {
                '1': ['Certification application dossier'],
                '2': ['Submission confirmation'],
                '3': ['Supplement response'],
                '4': ['Certificate record', 'Re-issue and exchange record'],
            },
        },
        PDPL_P4: {
            trigger: 'Ontvangst van een verzoek om rechten van een betrokkene',
            outputs: [
                'DSR intake record',
                'Identity verification',
                'Response letter',
                'Case closure report',
            ],
            channels: ['Online portal', 'Email', 'In-person'],
            responsible: {
                '1': 'Privacyteam / Klantenservice',
                '2': 'DPO / Privacyteam',
                '3': 'Afdelingsleiders/IT',
                '4': 'DPO / Privacyteam',
                '5': 'Nalevingsteam',
            },
            step_outputs: {
                '1': ['DSR intake record', 'Identity verification record'],
                '2': ['Request assessment', 'Deadline assignment'],
                '3': ['Fulfillment evidence', 'Department confirmations'],
                '4': ['Response letter'],
                '5': ['Case closure report', 'Audit evidence pack'],
            },
        },
        PDPL_P5: {
            trigger: 'Ontdekking van een schending of inbreuk op de bescherming van persoonsgegevens',
            outputs: [
                'Breach assessment record',
                'Notification package',
                'Submission confirmation',
                'Supplement package',
            ],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'Incidentresponsteam / DPO',
                '2': 'DPO / Privacyteam',
                '3': 'Juridisch / Naleving',
                '4': 'DPO / Incidentresponsteam',
            },
            step_outputs: {
                '1': ['Breach assessment record', 'Notification decision record'],
                '2': ['Breach notification package'],
                '3': ['Submission confirmation', 'Tracking reference'],
                '4': ['Supplement package', 'Coordination evidence'],
            },
        },
    },
    framework_guidance_references: {
        'Authority practice (unofficial) — email-ZIP submission of PDPL evidence packs during NQ22 era': 'Authority practice (unofficial) — email-ZIP submission of PDPL evidence packs during NQ22 era',
        'Cross-reference frameworks listed on VN_ECOMMERCE_DECREE_248_2026.': 'Cross-reference frameworks listed on VN_ECOMMERCE_DECREE_248_2026.',
        'Cross-reference frameworks on VN_DIGITAL_TECH_INDUSTRY_LAW_2025.': 'Cross-reference frameworks on VN_DIGITAL_TECH_INDUSTRY_LAW_2025.',
        'Cross-reference frameworks on VN_DIGITAL_TRANSFORMATION_LAW_2025.': 'Cross-reference frameworks on VN_DIGITAL_TRANSFORMATION_LAW_2025.',
        'Government/MOIT public reporting.': 'Government/MOIT public reporting.',
        'Government/MOIT public reporting; deadline VN_ECOM248_DEADLINE_TAKEDOWN_RESPONSE.': 'Government/MOIT public reporting; deadline VN_ECOM248_DEADLINE_TAKEDOWN_RESPONSE.',
        'MPS consultation dossier — draft information-systems protection decree.': 'MPS consultation dossier — draft information-systems protection decree.',
        'MPS consultation — draft general implementing decree.': 'MPS consultation — draft general implementing decree.',
        'Sector overlay install model (mirrors VN_BANKING_CIRCULAR_83_2025).': 'Sector overlay install model (mirrors VN_BANKING_CIRCULAR_83_2025).',
        'Shared draft enforcement overlay VN_CYBER_PDPL_ENFORCEMENT_DRAFT_2026 (home pack: vn-pdpl).': 'Shared draft enforcement overlay VN_CYBER_PDPL_ENFORCEMENT_DRAFT_2026 (home pack: vn-pdpl).',
    },
    framework_deadline_references: {
        'ComplianceOne internal review': 'ComplianceOne internal review',
        'ComplianceOne internal review date': 'ComplianceOne internal review date',
        'Draft personnel provisions': 'Draft personnel provisions',
        'Draft specialist provisions': 'Draft specialist provisions',
        'Internal child-instrument review.': 'Internal child-instrument review.',
        'Internal platform review.': 'Internal platform review.',
        'Internal recommendation based on draft lifecycle governance': 'Internal recommendation based on draft lifecycle governance',
        'Internal recommended digital-service procurement/leasing vendor review (before procurement/leasing and on material change).': 'Internal recommended digital-service procurement/leasing vendor review (before procurement/leasing and on material change).',
        'Internal recommended digital-transformation control review.': 'Internal recommended digital-transformation control review.',
        'Internal recommended review of digital-system minimum requirements (annual or on material change).': 'Internal recommended review of digital-system minimum requirements (annual or on material change).',
        'National digital technology industry database — ad-hoc update at authority request (where direct scope is confirmed).': 'National digital technology industry database — ad-hoc update at authority request (where direct scope is confirmed).',
        'National digital technology industry database — quarterly update (where direct scope is confirmed).': 'National digital technology industry database — quarterly update (where direct scope is confirmed).',
        'Official MPS consultation page': 'Official MPS consultation page',
        'Platform operational control — not a statutory deadline': 'Platform operational control — not a statutory deadline',
        'PDPL Art. 9': 'PDPL Art. 9',
        'PDPL Art. 17': 'PDPL Art. 17',
        'PDPL Art. 23': 'PDPL Art. 23',
        'Decree 13 Art. 24': 'Decreet 13 Art. 24',
        'MoPS Admin Procedures': 'MoPS-beheerprocedures',
        'Annual cycle': 'Jaarlijkse cyclus',
        'Decree 13/2023/NĐ-CP Art. 24': 'Decreet 13/2023/NĐ-CPĐ-CP Art. 24',
        'Decree 356/2025/NĐ-CP Art. 25(4)': 'Decreet 356/2025/NĐ-CPĐ-CP Art. 25(4)',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 2': 'Decreet 356/2025/NĐ-CPĐ-CP Art. 5 Cl. 2',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 2–5': 'Decreet 356/2025/NĐ-CPĐ-CP Art. 5 Cl. 2–5',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 3': 'Decreet 356/2025/NĐ-CPĐ-CP Art. 5 Cl. 3',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 4': 'Decreet 356/2025/NĐ-CPĐ-CP Art. 5 Cl. 4',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 5': 'Decreet 356/2025/NĐ-CPĐ-CP Art. 5 Cl. 5',
        'Decree 356/2025/NĐ-CP Art. 8(3)': 'Decreet 356/2025/NĐ-CPĐ-CP Art. 8(3)',
        'Draft Decree on Data Platforms 2026': 'Ontwerpbesluit Dataplatforms 2026',
        'Law 122/2025/QH15 Ch. III; NĐ 98/2020; NĐ 24/2025': 'Wet 122/2025/QH15 Hfdst. III; NĐ 98/2020; NĐ 24/2025',
        'Law 122/2025/QH15 Ch. IV (Consumer Protection)': 'Wet 122/2025/QH15 Hfdst. IV (Consumentenbescherming)',
        'Law 122/2025/QH15 — Online.gov.vn operational procedure': 'Wet 122/2025/QH15 — Operationele procedure Online.gov.vn',
        'Law 134/2025/QH15 Article 12': 'Wet 134/2025/QH15 Artikel 12',
        'Law 134/2025/QH15 Article 21': 'Wet 134/2025/QH15 Artikel 21',
        'Law 134/2025/QH15 Article 27': 'Wet 134/2025/QH15 Artikel 27',
        'Law 134/2025/QH15 Articles 9–10': 'Wet 134/2025/QH15 Artikelen 9–10',
        'Law 24/2023/QH15 — article mapping pending': 'Wet 24/2023/QH15 — artikeltoewijzing in behandeling',
        'Law 60/2024/QH15 (internal control target)': 'Wet 60/2024/QH15 (doel voor interne controle)',
        'Law 91/2025/QH15 Art. 21': 'Wet 91/2025/QH15 Art. 21',
        'Law 91/2025/QH15 Art. 23': 'Wet 91/2025/QH15 Art. 23',
        'Policy-defined': 'Beleid gedefinieerd',
        Quarterly: 'Driemaandelijks',
        'QĐ 778/QĐ-BCA-A05 administrative procedure': 'QĐ 778/QĐ-BCA-A05 administratieve procedure',
        'internal control target (Law 116/2025/QH15 Art. 24(2)(b), 41(5) — exact timeline pending implementing instrument)': 'doelstelling voor interne controle (Wet 116/2025/QH15 Art. 24, lid 2, onder b, 41, lid 5 — exact tijdschema in afwachting van uitvoeringsinstrument)',
        'internal control target (Law 116/2025/QH15 Art. 41(3) — exact timeline pending implementing instrument)': 'internecontroledoelstelling (Wet 116/2025/QH15 Artikel 41, lid 3 — exact tijdschema in afwachting van uitvoeringsinstrument)',
        'source verification pending': 'bronverificatie in behandeling',
        'Circular 83/2025/TT-NHNN — Ad-hoc Internal Audit Reporting to SBV (Layer 5)': 'Circular 83/2025/TT-NHNN — Ad-hoc Internal Audit Reporting to SBV (Layer 5) [NL]',
        'Circular 83/2025/TT-NHNN — Annual ICAAP Report (Phụ lục III) (Layer 5)': 'Circular 83/2025/TT-NHNN — Annual ICAAP Report (Phụ lục III) (Layer 5) [NL]',
        'Circular 83/2025/TT-NHNN — Annual Phụ lục I (Layer 5)': 'Circular 83/2025/TT-NHNN — Annual Phụ lục I (Layer 5) [NL]',
        'Circular 83/2025/TT-NHNN — Annual Phụ lục IV (Layer 5)': 'Circular 83/2025/TT-NHNN — Annual Phụ lục IV (Layer 5) [NL]',
        'Circular 83/2025/TT-NHNN — Annual Risk Management Report (Phụ lục II) (Layer 5)': 'Circular 83/2025/TT-NHNN — Annual Risk Management Report (Phụ lục II) (Layer 5) [NL]',
        'Circular 83/2025/TT-NHNN — Asset Management Responsibility (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Asset Management Responsibility (Layer 2, theme C) [NL]',
        "Circular 83/2025/TT-NHNN — Board / Members' Council Oversight (Layer 2, theme A)": "Circular 83/2025/TT-NHNN — Board / Members' Council Oversight (Layer 2, theme A) [NL]",
        'Circular 83/2025/TT-NHNN — Board Committees (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Board Committees (Layer 2, theme A) [NL]',
        'Circular 83/2025/TT-NHNN — Board Decisions and Minutes (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Board Decisions and Minutes (Layer 2, theme A) [NL]',
        'Circular 83/2025/TT-NHNN — Branch Control Procedures (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Branch Control Procedures (Layer 2, theme C) [NL]',
        'Circular 83/2025/TT-NHNN — Compliance Assessment of Internal Rules (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Compliance Assessment of Internal Rules (Layer 2, theme D) [NL]',
        'Circular 83/2025/TT-NHNN — Compliance Function (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Compliance Function (Layer 2, theme D) [NL]',
        'Circular 83/2025/TT-NHNN — Compliance Remediation Monitoring (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Compliance Remediation Monitoring (Layer 2, theme D) [NL]',
        'Circular 83/2025/TT-NHNN — Concentration Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Concentration Risk (Layer 3, theme E) [NL]',
        'Circular 83/2025/TT-NHNN — Conflict of Interest (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Conflict of Interest (Layer 2, theme C) [NL]',
        'Circular 83/2025/TT-NHNN — Control Culture (Layer 2, theme A/B)': 'Circular 83/2025/TT-NHNN — Control Culture (Layer 2, theme A/B) [NL]',
        'Circular 83/2025/TT-NHNN — Credit Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Credit Risk (Layer 3, theme E) [NL]',
        'Circular 83/2025/TT-NHNN — Delegated Authority (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Delegated Authority (Layer 2, theme C) [NL]',
        'Circular 83/2025/TT-NHNN — Early Implementation Notification (Layer 5)': 'Circular 83/2025/TT-NHNN — Early Implementation Notification (Layer 5) [NL]',
        'Circular 83/2025/TT-NHNN — General Director Responsibility (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — General Director Responsibility (Layer 2, theme A) [NL]',
        'Circular 83/2025/TT-NHNN — ICAAP (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — ICAAP (Layer 3, theme E) [NL]',
        'Circular 83/2025/TT-NHNN — Interest-Rate Risk in the Banking Book (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Interest-Rate Risk in the Banking Book (Layer 3, theme E) [NL]',
        'Circular 83/2025/TT-NHNN — Internal Audit Charter (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Charter (Layer 2, theme H) [NL]',
        'Circular 83/2025/TT-NHNN — Internal Audit Engagements (Theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Engagements (Theme H) [NL]',
        'Circular 83/2025/TT-NHNN — Internal Audit Findings Communication (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Findings Communication (Layer 2, theme H) [NL]',
        'Circular 83/2025/TT-NHNN — Internal Audit Findings and Corrective Actions (Theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Findings and Corrective Actions (Theme H) [NL]',
        'Circular 83/2025/TT-NHNN — Internal Audit Follow-Up (Theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Follow-Up (Theme H) [NL]',
        'Circular 83/2025/TT-NHNN — Internal Audit Governance': 'Circular 83/2025/TT-NHNN — Internal Audit Governance [NL]',
        'Circular 83/2025/TT-NHNN — Internal Audit Independence (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Independence (Layer 2, theme H) [NL]',
        'Circular 83/2025/TT-NHNN — Internal Audit Plan (Layer 2/Theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Plan (Layer 2/Theme H) [NL]',
        'Circular 83/2025/TT-NHNN — Internal Audit Reporting to SBV (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Reporting to SBV (Layer 2, theme H) [NL]',
        'Circular 83/2025/TT-NHNN — Internal Control System Governance (Layer 2)': 'Circular 83/2025/TT-NHNN — Internal Control System Governance (Layer 2) [NL]',
        'Circular 83/2025/TT-NHNN — Internal Reporting Procedure (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Internal Reporting Procedure (Layer 2, theme C) [NL]',
        'Circular 83/2025/TT-NHNN — Internal Reports Register (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Internal Reports Register (Layer 4, theme F) [NL]',
        'Circular 83/2025/TT-NHNN — Law-Change Monitoring (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Law-Change Monitoring (Layer 2, theme D) [NL]',
        'Circular 83/2025/TT-NHNN — Legal and Reputational Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Legal and Reputational Risk (Layer 3, theme E) [NL]',
        'Circular 83/2025/TT-NHNN — Limit Breach Escalation (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Limit Breach Escalation (Layer 3, theme E) [NL]',
        'Circular 83/2025/TT-NHNN — Liquidity Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Liquidity Risk (Layer 3, theme E) [NL]',
        'Circular 83/2025/TT-NHNN — MIS Annual Review (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS Annual Review (Layer 4, theme F) [NL]',
        'Circular 83/2025/TT-NHNN — MIS Backup and Continuity (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS Backup and Continuity (Layer 4, theme F) [NL]',
        'Circular 83/2025/TT-NHNN — MIS Escalation Mechanisms (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS Escalation Mechanisms (Layer 4, theme F) [NL]',
        'Circular 83/2025/TT-NHNN — MIS Reliability and Confidentiality (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS Reliability and Confidentiality (Layer 4, theme F) [NL]',
        'Circular 83/2025/TT-NHNN — MIS Risk Data Sources (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS Risk Data Sources (Layer 4, theme F) [NL]',
        'Circular 83/2025/TT-NHNN — Management Information System (Layer 4)': 'Circular 83/2025/TT-NHNN — Management Information System (Layer 4) [NL]',
        'Circular 83/2025/TT-NHNN — Market Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Market Risk (Layer 3, theme E) [NL]',
        'Circular 83/2025/TT-NHNN — Material Risk Register (Layer 3)': 'Circular 83/2025/TT-NHNN — Material Risk Register (Layer 3) [NL]',
        'Circular 83/2025/TT-NHNN — Model Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Model Risk (Layer 3, theme E) [NL]',
        'Circular 83/2025/TT-NHNN — New Product Review (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — New Product Review (Layer 3, theme E) [NL]',
        'Circular 83/2025/TT-NHNN — Operational Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Operational Risk (Layer 3, theme E) [NL]',
        'Circular 83/2025/TT-NHNN — Outsourcing BCP (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing BCP (Layer 2, theme G) [NL]',
        'Circular 83/2025/TT-NHNN — Outsourcing Contracts (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing Contracts (Layer 2, theme G) [NL]',
        'Circular 83/2025/TT-NHNN — Outsourcing Controls': 'Circular 83/2025/TT-NHNN — Outsourcing Controls [NL]',
        'Circular 83/2025/TT-NHNN — Outsourcing Due Diligence (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing Due Diligence (Layer 2, theme G) [NL]',
        'Circular 83/2025/TT-NHNN — Outsourcing Fallback Provider (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing Fallback Provider (Layer 2, theme G) [NL]',
        'Circular 83/2025/TT-NHNN — Outsourcing Materiality (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing Materiality (Layer 2, theme G) [NL]',
        'Circular 83/2025/TT-NHNN — Outsourcing Monitoring (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing Monitoring (Layer 2, theme G) [NL]',
        'Circular 83/2025/TT-NHNN — Phụ lục V/VI Technical References (Layer 5)': 'Circular 83/2025/TT-NHNN — Phụ lục V/VI Technical References (Layer 5) [NL]',
        'Circular 83/2025/TT-NHNN — Policy Register (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Policy Register (Layer 2, theme C) [NL]',
        'Circular 83/2025/TT-NHNN — Professional Ethics (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Professional Ethics (Layer 2, theme C) [NL]',
        'Circular 83/2025/TT-NHNN — Risk Appetite (Layer 3)': 'Circular 83/2025/TT-NHNN — Risk Appetite (Layer 3) [NL]',
        'Circular 83/2025/TT-NHNN — Risk Committee Coordination (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risk Committee Coordination (Layer 2, theme E) [NL]',
        'Circular 83/2025/TT-NHNN — Risk Data Governance (Layer 3, theme E/F) — phased to 2028-01-01': 'Circular 83/2025/TT-NHNN — Risk Data Governance (Layer 3, theme E/F) — phased to 2028-01-01 [NL]',
        'Circular 83/2025/TT-NHNN — Risk Limit Review (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Risk Limit Review (Layer 3, theme E) [NL]',
        'Circular 83/2025/TT-NHNN — Risk Limits Framework (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Risk Limits Framework (Layer 3, theme E) [NL]',
        'Circular 83/2025/TT-NHNN — Risk Management Function (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risk Management Function (Layer 2, theme E) [NL]',
        'Circular 83/2025/TT-NHNN — Risk Methodology and Models (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risk Methodology and Models (Layer 2, theme E) [NL]',
        'Circular 83/2025/TT-NHNN — Risk Reporting (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risk Reporting (Layer 2, theme E) [NL]',
        'Circular 83/2025/TT-NHNN — Risk Reporting Cadence (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Risk Reporting Cadence (Layer 4, theme F) [NL]',
        'Circular 83/2025/TT-NHNN — SBV Remediation Submission (Layer 5)': 'Circular 83/2025/TT-NHNN — SBV Remediation Submission (Layer 5) [NL]',
        'Circular 83/2025/TT-NHNN — SBV Submission Channels (Layer 5)': 'Circular 83/2025/TT-NHNN — SBV Submission Channels (Layer 5) [NL]',
        'Circular 83/2025/TT-NHNN — Sector Applicability (Layer 1)': 'Circular 83/2025/TT-NHNN — Sector Applicability (Layer 1) [NL]',
        'Circular 83/2025/TT-NHNN — Segregation of Duties (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Segregation of Duties (Layer 2, theme C) [NL]',
        'Circular 83/2025/TT-NHNN — Senior Management Supervision (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Senior Management Supervision (Layer 2, theme A) [NL]',
        'Circular 83/2025/TT-NHNN — Strategic Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Strategic Risk (Layer 3, theme E) [NL]',
        'Circular 83/2025/TT-NHNN — Stress Testing (Layer 3, theme E) — phased to 2028-01-01': 'Circular 83/2025/TT-NHNN — Stress Testing (Layer 3, theme E) — phased to 2028-01-01 [NL]',
        'Circular 83/2025/TT-NHNN — Supervisory Board (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Supervisory Board (Layer 2, theme A) [NL]',
        'Circular 83/2025/TT-NHNN — Supervisory Findings Register (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Supervisory Findings Register (Layer 4, theme F) [NL]',
        'Circular 83/2025/TT-NHNN — Three Lines Roles and Escalation (Layer 2, theme B)': 'Circular 83/2025/TT-NHNN — Three Lines Roles and Escalation (Layer 2, theme B) [NL]',
        'Circular 83/2025/TT-NHNN — Three Lines of Defence (Layer 2)': 'Circular 83/2025/TT-NHNN — Three Lines of Defence (Layer 2) [NL]',
        'Circular 83/2025/TT-NHNN — Violation Escalation (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Violation Escalation (Layer 2, theme D) [NL]',
        // Sprint 62.49.1 (E12.5 pass 1, P3) — consumer-rights and food-traceability references.
        'Law 19/2023/QH15 — Điều 31 khoản 2': 'Law 19/2023/QH15 — Điều 31 khoản 2',
        'Law 19/2023/QH15 and Decree 55/2024/ND-CP official Government portal metadata.': 'Law 19/2023/QH15 and Decree 55/2024/ND-CP official Government portal metadata.',
        'Circular 11/2026/TT-BCT — Điều 6 khoản 2': 'Circular 11/2026/TT-BCT — Điều 6 khoản 2',
        'Circular 11/2026/TT-BCT — Điều 12 khoản 2': 'Circular 11/2026/TT-BCT — Điều 12 khoản 2',
        'Circular 11/2026/TT-BCT — Điều 15 khoản 1': 'Circular 11/2026/TT-BCT — Điều 15 khoản 1',
        'Circular 11/2026/TT-BCT — Điều 15 khoản 2': 'Circular 11/2026/TT-BCT — Điều 15 khoản 2',
        'Circular 63/2026/TT-BCT — Điều 10 khoản 2': 'Circular 63/2026/TT-BCT — Điều 10 khoản 2',
        'Circular 63/2026/TT-BCT — Điều 10 khoản 3': 'Circular 63/2026/TT-BCT — Điều 10 khoản 3',
        'Circular 63/2026/TT-BCT — Điều 19 khoản 1': 'Circular 63/2026/TT-BCT — Điều 19 khoản 1',
    },
    framework_authorities: {
        'Quốc hội (National Assembly)': 'Quốc hội (Nationale Vergadering)',
        'Ministry of Public Security / A05 Department': 'Ministerie van Openbare Veiligheid / Afdeling A05',
        'Ministry of Public Security': 'Ministerie van Openbare Veiligheid',
        'Ministry of Information and Communications': 'Ministerie van Informatie en Communicatie',
        'Government of Vietnam': 'Regering van Vietnam',
        'European Data Protection Board (EDPB)': 'Europees Comité voor gegevensbescherming (EDPB)',
        'ENISA / National CSIRTs': "ENISA / Nationale CSIRT's",
        'State Bank of Vietnam': 'Staatsbank van Vietnam',
    },
    tour_titles: {
        access_accountability: 'Toegang tot verantwoordingsrondleiding',
        assessments: 'Beoordelingsrondleiding',
        audit_trail: 'Audit Trail-tour',
        classification: 'Classificatie Ronde',
        compliance_forms: 'Rondleiding door nalevingsformulieren',
        connectors: 'Connectoren-rondleiding',
        consent_properties: 'Rondleiding door toestemmingseigenschappen',
        consent: 'Toestemmingsrondleiding',
        dashboard: 'Dashboardrondleiding',
        data_mapping: 'Data Mapping-rondleiding',
        data_subjects: 'Rondleiding door betrokkenen',
        deletion_orchestration: 'Schrapping Orchestration Tour',
        discovery: 'Ontdekkingstocht',
        governance: 'Bestuursrondleiding',
        help_center: 'Rondleiding door het Helpcentrum',
        incident_ops: 'Rondleiding incidentoperaties',
        incidents: 'Incidentenrondleiding',
        localization: 'Lokalisatierondleiding',
        monitoring: 'Bewakingsrondleiding',
        onboarding: 'Onboarding-rondleiding',
        organizations: 'Organisaties Rondleiding',
        partner_workspace: 'Rondleiding door de partnerwerkruimte',
        platform_settings: 'Rondleiding platforminstellingen',
        privacy_scanner: 'Rondleiding door de privacyscanner',
        program_governance: 'Programmabeheerrondleiding',
        rights_requests: 'Rondleiding over rechtenverzoeken',
        task_management: 'Rondleiding voor taakbeheer',
        vendor_risk: 'Leveranciersrisicotour',
    },
    tour_descriptions: {
        access_accountability: 'Als hoofd van de interne audit (P-VN-10) beoordeel je het toegangsbewijs, controleer je het gebruik van toestemmingen en onderzoek je afwijkende patronen om de integriteit en vertrouwelijkheid van artikel 5, lid 1, onder f aan te tonen.',
        assessments: "Voer als onderneming DPO (P-VN-01) DPIA's uit volgens Decreet 356 Mau so 02a, identificeer lacunes in de naleving en beoordeel de risico's van grensoverschrijdende overdrachten onder PDPL.",
        audit_trail: 'Als hoofd van de interne audit (P-VN-10) bevraagt u moduleoverschrijdend bewijsmateriaal, stelt u auditpakketten samen en exporteert u fraudebestendige logboeken voor indieningen bij de toezichthouders.',
        classification: 'Als Data Steward (P-VN-05) definieert u taxonomieën voor gegevensclassificatie, wijst u gevoeligheidslabels toe, dwingt u bewaarbeleid af en lost u de dekkingslacunes op die nodig zijn voor PDPL-compliance.',
        compliance_forms: 'Blader als de Enterprise DPO (P-VN-01) door officiële Vietnamese regelgevingsformulieren, waaronder Phu luc I, Phu luc II en Mau so-sjablonen voor deponeringen bij de PDP-commissie, en dien deze in.',
        connectors: 'Als IT Systems Lead (P-VN-05) leg je de bedrijfsvereisten voor integraties vast, breng je connectoren in kaart voor compliance-gegevensstromen en verifieer je de POC-gereedheid voordat je live gaat.',
        consent_properties: 'Configureer als Enterprise DPO (P-VN-01) de implementatie van CMP: digitale eigendomsregistratie, gouden sjablonen voor consistente PDPL toestemmingsbanners en bindende matrices die eigenschappen koppelen aan verwerkingsactiviteiten.',
        consent: 'Beheer als onderneming DPO (P-VN-01) de levenscyclus van het toestemmingsbeleid: definieer verzameldoeleinden, dwing PDPL intrekkingsrechten af en integreer met uw toestemmingsbeheerplatform.',
        dashboard: "Oriënteer u als Enterprise DPO (P-VN-01) rond het compliance-houdingscommandocentrum dat PDPL-deadlines naar voren brengt, SLA's overtreedt en KPI's voor auditgereedheid biedt.",
        data_mapping: 'Als Data Steward (P-VN-05) bouwt u uw ROPA-inventaris op, brengt u grensoverschrijdende gegevensstromen in kaart en houdt u het verwerkingsactiviteitenregister bij dat vereist is door PDPL.',
        data_subjects: 'Beheer als Enterprise DPO of Education Compliance Lead uniforme profielen van betrokkenen die toestemmingsrecords, rechtenverzoeken en tijdlijnactiviteiten op het hele platform koppelen.',
        deletion_orchestration: 'Als DPO (P-VN-01) verifieer je de end-to-end gegevensverwijdering in interne systemen en externe leveranciers, met cryptografisch bewijs voor naleving van het recht op verwijdering van GDPR Artikel 17.',
        discovery: 'Voer als Data Steward (P-VN-05) geautomatiseerde scans uit om persoonlijke gegevens op verbonden systemen te ontdekken, gefaseerde bevindingen te bekijken en resultaten in de classificatiemodule in te voeren voor PDPL-labeling.',
        governance: "Als Vendor Risk Manager (P-VN-08) houd je het leveranciersregister bij, volg je subprocessorketens, beoordeel je de risico's van grensoverschrijdende overdrachten en handhaaf je governance-playbooks onder PDPL.",
        help_center: "Voor alle persona's (DPO's, auditors, IT-leads en compliance-managers) vindt u antwoorden, handleidingen en modulespecifieke documentatie ter ondersteuning van uw dagelijkse compliance-workflows.",
        incident_ops: 'Activeer als incidentcommandant (P-VN-09) de reactieplaybooks, houd de naleving van SLA bij aan de hand van het 72-uur durende PDPL notificatievenster, beheer escalaties en coördineer de reactie van de leverancier.',
        incidents: 'Als incidentcommandant (P-VN-09) detecteer en rapporteer je datalekken met een meldingsplicht van 72 uur PDPL aan het PDP-comité, sorteer je op ernst en stel je incidentdossiers op auditniveau op.',
        localization: 'Beheer als Enterprise DPO (P-VN-01) de levenscyclus van het regelgevingskader: installeer Vietnamese marktpakketten, activeer PDPL en Decreet 13 kaders, wijs vereisten toe aan platformmodules en beheer vertalingen.',
        monitoring: "Stel als onderneming DPO (P-VN-01) doorlopende programma's voor nalevingsmonitoring op, volg wijzigingen in de regelgeving, meet KPI's en beheer gevallen van interactie tussen regelgeving onder PDPL.",
        onboarding: "Geef als Compliance Trainer (P-VN-02) training via rondleidingen, laad realistische voorbeeldgegevens voor praktische oefeningen en voer de installatiewizard uit voor snelstartscenario's.",
        organizations: 'Configureer als DPO (P-VN-01) compliance-structuren voor meerdere organisaties, stel afdelingshiërarchieën in en stuur verzoeken van betrokkenen door uw bedrijfsgroep.',
        partner_workspace: 'Als Partner Account Manager onboard je partnerorganisaties, configureer je serviceniveaus, volg je compliance-resultaten en beheer je de partnermarktplaats.',
        platform_settings: 'Beheer als platformbeheerder alle operationele configuraties (identiteit, beveiliging, integraties en moduletoegang) die ten grondslag liggen aan uw complianceprogramma.',
        privacy_scanner: 'Voer als privacyanalist (P-VN-06) voortdurend monitoringscans uit om schendingen van de toestemming voor cookies, ongeautoriseerde trackers en lacunes in het beleid op te sporen, waardoor bewijs wordt gegenereerd voor ePrivacy en naleving van GDPR.',
        program_governance: 'Navigeer tussen volwassenheidsmodellen, beoordelingen, hiaten in bewijsmateriaal, samenvattingen en meer. Elke submodule draagt ​​bij aan een holistische kijk op de gezondheid van uw interne programma.',
        rights_requests: 'Beheer als onderneming DPO (P-VN-01) de rechtenverzoeken van betrokkenen onder PDPL met wettelijke deadlines van 15 dagen, identiteitsverificatie en oplossingen op auditniveau.',
        task_management: "Stuur compliancetaken door verschillende persona's, houd SLA deadlines bij en automatiseer workflows zodat uw team verantwoordelijk blijft.",
        vendor_risk: "Als Vendor Risk Manager (P-VN-08) scoor je risico's van derden, dwing je poortbeoordelingen af en activeer je herbeoordelingen - in tegenstelling tot Program Governance, dat je interne volwassenheid bijhoudt.",
    },
    consolidation: {
        title: 'Consolidatiezaken',
        loading_cases: 'Loading consolidation cases',
        loading_case_detail: 'Loading case detail',
        new_case: 'Consolidatiecase maken',
        case_count: 'case',
        created: 'Consolidatiecase gemaakt',
        create: 'Casus maken',
        creating: 'Creëren...',
        cancel: 'Annuleer',
        no_cases: 'Geen consolidatiegevallen gevonden.',
        all_statuses: 'Alle statussen',
        all_purposes: 'Alle doeleinden',
        filter_status: 'Filter op status',
        filter_purpose: 'Filter op doel',
        col_title: 'Titel',
        col_purpose: 'Doel',
        col_status: 'Status',
        col_progress: 'Vooruitgang',
        col_deadline: 'Deadline',
        col_department: 'Afdeling',
        col_version: 'Versie',
        col_evidence: 'Bewijs',
        sections_accepted: 'accepted',
        prev: 'Vorige',
        next: 'Volgende',
        page: 'Pagina',
        prev_page: 'Vorige pagina',
        next_page: 'Volgende pagina',
        field_title: 'Titel',
        field_purpose: 'Doel',
        field_framework: 'Kadercode',
        field_deadline: 'Deadline',
        field_reviewer: 'Gebruikers-ID van recensent',
        title_placeholder: 'Voer de casetitel in',
        framework_placeholder: 'bijvoorbeeld PDPD-2023',
        reviewer_placeholder: 'Voer de gebruikers-ID van de reviewer in',
        // fix-sprint-20260821-2140 F6 — structural parity only. R-57 defers real
        // translation of the 14 non-EN/VI locales to Phase 25 (sprint-57.7.0); these
        // land EN-identical and are counted there rather than repaired here.
        field_summary: 'Summary',
        field_details: 'Details',
        field_risk_assessment: 'Risk Assessment',
        field_mitigation_measures: 'Mitigation Measures',
        purposes: {
            dpia_filing: 'DPIA Archivering',
            cross_border_filing: 'Grensoverschrijdende indiening',
            annual_governance_attestation: 'Jaarlijkse bestuursattest',
            breach_package: 'Inbreukpakket',
            audit_inspection_pack: 'Auditinspectiepakket',
        },
        statuses: {
            draft: 'Diepgang',
            collecting: 'Verzamelen',
            consolidating: 'Consolideren',
            review: 'Beoordeling',
            corrections: 'Correcties',
            pending_approval: 'In afwachting van goedkeuring',
            approved: 'Goedgekeurd',
            completed: 'Voltooid',
            closed: 'Gesloten',
            rejected: 'Afgewezen',
        },
        section_statuses: {
            pending: 'In behandeling',
            in_progress: 'In uitvoering',
            submitted: 'Ingezonden',
            accepted: 'Geaccepteerd',
            needs_correction: 'Heeft correctie nodig',
            corrected: 'Gecorrigeerd',
        },
        flag_types: {
            missing_section: 'Ontbrekende sectie',
            conflicting_entry: 'Conflicterende invoer',
            stale_entry: 'Verouderde inzending',
            missing_evidence: 'Ontbrekend bewijs',
            incomplete_data: 'Onvolledige gegevens',
            duplicate_entry: 'Dubbele invoer',
        },
        flag_severities: {
            critical: 'Kritisch',
            warning: 'Waarschuwing',
            info: 'Info',
        },
        sections_title: 'Secties',
        no_sections: 'Nog geen secties toegewezen.',
        flags_title: 'Vlaggen',
        no_flags: 'Geen vlaggen gedetecteerd.',
        open: 'open',
        approval_chain: 'Goedkeuringsketen',
        pending: 'In behandeling',
        audit_trail: 'Audittraject',
        no_audit: 'Geen auditgebeurtenissen.',
        auto_assign: 'Secties automatisch toewijzen',
        start_collection: 'Verzameling starten',
        review_case: 'Beoordeling zaak',
        generate_export: 'Exporteren genereren',
        review_title: 'Consolidatieoverzicht',
        advance_approval: 'Ga door naar goedkeuring',
        back_to_case: 'Terug naar Casus',
        accept: 'Accepteer',
        return_section: 'Keer terug',
        return_comment: 'Reactie retourneren...',
        resolve: 'Oplossen',
        dismiss: 'Negeren',
        resolution_notes: 'Resolutie-opmerkingen...',
        notes: 'Opmerkingen',
        section_accepted: 'Sectie geaccepteerd',
        section_returned: 'Sectie teruggestuurd voor correctie',
        advanced_to_approval: 'De zaak is in behandeling genomen in afwachting van goedkeuring',
        section_data: 'Sectiegegevens',
        save_progress: 'Bewaar vooruitgang',
        submit_section: 'Sectie indienen',
        submitting: 'Verzenden...',
        resubmit: 'Opnieuw indienen',
        progress_saved: 'Vooruitgang opgeslagen',
        section_submitted: 'Sectie ingediend',
        correction_required: 'Correctie vereist',
        evidence_title: 'Bewijs',
        no_evidence: 'Geen bewijsmateriaal bijgevoegd.',
        add_evidence: 'Bewijs toevoegen',
        evidence_added: 'Bewijs toegevoegd',
        evidence_files: 'bewijsbestanden',
        uploaded_by: 'geüpload door',
        comments_title: 'Opmerkingen',
        no_comments: 'Nog geen opmerkingen.',
        comment_placeholder: 'Voeg een reactie toe...',
        add_comment: 'Commentaar toevoegen',
        comment_added: 'Commentaar toegevoegd',
        view_data: 'Bekijk ingediende gegevens',
        export_title: 'Dossier exporteren',
        export_generated: 'Exportpakket gegenereerd',
        regenerate: 'Regenereer',
        regenerating: 'Regenereren...',
        generated_at: 'Gegenereerd op',
        artifacts_title: 'Artefacten exporteren',
        artifact_type: 'Artefact',
        file_name: 'Bestandsnaam',
        file_size: 'Grootte',
        generated: 'Gegenereerd',
        download: 'Downloaden',
        artifacts: {
            master_report: 'Hoofdrapport',
            evidence_bundle: 'Bewijsbundel (ZIP)',
            contributor_lineage: 'Afkomst van de bijdrager',
            conflict_resolution: 'Conflictoplossing',
            approval_history: 'Goedkeuringsgeschiedenis',
        },
        official_forms: 'Officiële formulieren',
        forms_description: 'Uit de geconsolideerde gegevens kunnen de volgende officiële formulieren worden gegenereerd. Officiële formulieren staan ​​los van de interne werkdocumenten.',
        announce_loaded_cases: '{count} consolidatiecases geladen',
        announce_case_detail_loaded: 'Zaakdetail geladen',
        announce_case_transitioned: 'Case overgezet naar {status}',
        announce_flag_resolved: 'Vlag opgelost',
        announce_flag_dismissed: 'Vlag afgewezen',
        case_detail_loaded: 'Zaakdetail geladen',
        sections_auto_assigned: 'Secties worden automatisch toegewezen vanuit de afdelingshiërarchie',
        errors: {
            load_failed: 'Kan gegevens niet laden. Probeer het opnieuw.',
            create_failed: 'Kan casus niet maken. Probeer het opnieuw.',
            required_fields: 'Vul alle verplichte velden in.',
            not_found: 'Niet gevonden.',
            comment_required: 'Bij het retourneren van een onderdeel is commentaar vereist.',
            notes_required: 'Er zijn opmerkingen vereist bij het oplossen of negeren van een vlag.',
        },
    },
    rights_requests: {
        import: {
            title: 'Zaken importeren',
            nav_title: 'Bulkimport',
            description: 'Upload een CSV-bestand om rechtenverzoekcasussen in bulk te importeren.',
            select_file: 'CSV-bestand selecteren',
            file_help: 'CSV met kopteksten die overeenkomen met zaakvelden.',
            column_count_help: 'A row whose number of values differs from the number of columns in the header is not imported; it is listed under Errors with its row number.',
            // fix-sprint #431 — was hard-coded English on the Import page.
            required_columns: 'Required columns:',
            file_too_large: 'Bestand te groot. Maximale grootte is 2MB.',
            preview: 'Voorbeeld',
            columns_detected: 'Kolommen gedetecteerd: {count}',
            column_mapping: 'Kolomtoewijzing',
            import_button: 'Importeren',
            import_complete: 'Import voltooid',
            import_error: 'Import mislukt',
            result: 'Importresultaat',
            total: 'Totaal',
            imported: 'Geïmporteerd',
            skipped: 'Overgeslagen',
            failed: 'Mislukt',
            errors: 'Fouten',
            error_row: 'Rij',
            error_field: 'Veld',
            error_message: 'Bericht',
            history: 'Importgeschiedenis',
            no_history: 'Geen importgeschiedenis.',
            history_file: 'Bestandsnaam',
            history_date: 'Datum',
        },
        batch: {
            nav_title: 'Batchproces',
            page_title: 'DSR-batchproces',
            page_subtitle: 'Upload een CSV aan openstaande vraagresponsen. Forseti stelt een bevestigingsantwoord per verzoek op en zet een verificatietaak per rij in de wachtrij. De DPO beoordelingen + accepteren ze allemaal voordat ze worden verzonden.',
            upload_heading: 'Uploaden',
            csv_hint: 'Vereiste kolommen: request_id, requester_email, request_type. Optioneel: deadline (ISO-8601), notities.',
            rows_heading: '{count} rijen geparseerd',
            submit_label: 'Batch uitvoeren',
            submitting: 'Opstellen…',
            col_request_id: 'Aanvraag id',
            col_email: 'E-mailadres van de aanvrager',
            col_type: 'Typ',
            col_status: 'Status',
            col_actions: 'Acties',
            status_queued: 'In de wachtrij',
            status_drafted: 'Opgesteld',
            status_failed: 'Mislukt',
            status_accepted: 'Geaccepteerd',
            status_rejected: 'Afgewezen',
            status_parsing_error: 'Parseerfout',
            templates: {
                access: 'Acknowledging your data subject access request. We will respond by {deadline} with a copy of the personal data we process about you, the purposes and the recipients, per [{frameworks}]. Please reply with any clarifying detail; otherwise we will proceed with full disclosure.',
                deletion: 'Acknowledging your erasure request under [{frameworks}]. We will identify the records in scope, apply any retention that still applies (a legal hold, a contractual obligation) and confirm completion by {deadline}.',
                correction: 'Acknowledging your correction request under [{frameworks}]. Please reply with the field or fields you want corrected and the new values; we will route the request to the relevant business unit and confirm by {deadline}.',
                objection: 'Acknowledging your objection to processing under [{frameworks}]. We will pause the relevant processing while we review your reasons and respond by {deadline}.',
                portability: 'Acknowledging your data portability request under [{frameworks}]. We will export your data in scope in a structured, commonly used, machine-readable format and confirm by {deadline}.',
                restriction: 'Acknowledging your restriction request under [{frameworks}]. We will mark the relevant records as restricted and respond by {deadline}.',
                consent_withdrawal: 'Acknowledging your withdrawal of consent under [{frameworks}]. Future processing that relies solely on the withdrawn consent will stop; processing on another basis (a legal obligation, a contract) continues unless you object to it separately. We will confirm by {deadline}.',
                protective_measures: 'Acknowledging your request for personal data protection measures and solutions under [{frameworks}]. We will assess and implement the applicable safeguards and confirm by {deadline}.',
                processing_information: 'Acknowledging your request to be informed about the processing of your personal data under [{frameworks}]. We will tell you what personal data we process about you, why, and who receives it, and respond by {deadline}.',
                consent_decision: 'Acknowledging your decision on consent to the processing of your personal data under [{frameworks}]. We will record it and apply it to the processing that relies on your consent, and confirm by {deadline}.',
                deadline_fallback: 'the deadline set by the law that applies to your request',
            },
            errors: {
                csv_empty: 'The CSV file is empty.',
                csv_missing_header: 'The CSV file is missing the required column: {header}.',
                row_missing_field: 'A required field is missing (request_id, requester_email or request_type).',
                row_unknown_type: 'Unknown request type: {type}.',
                read_failed: 'The CSV file could not be read.',
                run_failed: 'The batch could not be run.',
                decision_failed: 'The decision could not be saved.',
            },
            synthesis_statuses: {
                model: 'Drafted by the AI model',
                template_parse_failed: 'Standard template: the AI model returned no usable text',
                template_provider_failed: 'Standard template: the AI model call failed',
                template_not_configured: 'Standard template: no AI model is configured',
                template_by_design: 'Standard template: this workflow does not use the AI model for this field',
            },
            csv_picker_label: 'CSV file',
            draft_source: 'Draft source',
            draft_language: 'Draft language: {locale}',
            accept: 'Accepteer',
            reject: 'Afwijzen',
            permission_denied: 'You need both the case import and Forseti chat permissions to run a batch.',
            col_draft: 'Drafted response',
            view_draft: 'View drafted response',
            frameworks_cited: 'Frameworks cited',
        },
    },
    assessment_triggers: {
        title: 'Beoordelingstriggers',
        subtitle: 'Configureer het automatisch maken van beoordelingen en DPO-meldingen voor privacyworkflows.',
        auto_create_dpia_label: 'Automatisch DPIA maken bij het verwerken van activiteit gemarkeerd als DPIA vereist',
        auto_notify_new_purpose_label: 'Stel DPO automatisch op de hoogte wanneer er een nieuw doel wordt aangemaakt met gevoelige gegevenscategorieën',
        auto_notify_cross_border_label: 'Stel DPO automatisch op de hoogte wanneer grensoverschrijdende overdracht wordt toegevoegd aan de verwerkingsactiviteit',
        default_dpo_label: 'Standaard DPO',
        default_dpo_placeholder: 'Selecteer standaard DPO...',
        notification_channels_label: 'Meldingskanalen',
        channel_in_app: 'In-app',
        channel_email: 'Email',
        save_success: 'Beoordelingstriggerinstellingen opgeslagen.',
        save_error: 'Kan de triggerinstellingen voor de beoordeling niet opslaan.',
        fetch_error: 'Kan de triggerinstellingen voor de beoordeling niet laden.',
        fetch_users_error: 'Kan gebruikers niet laden.',
        announce_loaded: 'Instellingen voor beoordelingstrigger geladen',
        dpia_status: 'DPIA Status',
        dpia_linked: 'DPIA Gekoppeld',
        dpia_required_no_dpia: 'DPIA Vereist — niet gemaakt',
        create_dpia: 'Maak DPIA',
        create_dpia_success: 'DPIA gemaakt en gekoppeld aan activiteit.',
        create_dpia_error: 'Kan DPIA niet maken voor activiteit.',
        view_dpia: 'Bekijk DPIA',
        dpo_warning_title: 'The default DPO is not an active user',
        dpo_warning_body: 'The default DPO is no longer an active user of this organisation. New DPIAs are created with no reviewer named, so any user who can approve DPIAs may decide them. Choose an active user as the default DPO.',
        auto_notify_cross_border_help: 'When a processing activity is saved with a cross-border destination it did not list before, the default DPO is notified on the channels below. With no default DPO, the Compliance Owners are notified.',
        auto_notify_processor_change_label: 'Raise a DPIA reassessment when a processor changes',
        auto_notify_processor_change_help: "A change to a vendor, a sub-processor or a processing activity's processor details raises one reassessment task, with a checklist of the evidence to re-check, for each DPIA that names that vendor or activity. A DPIA whose previous reassessment task is still open does not get a second one.",
        notification_channels_help: "Where the cross-border notification is sent. Each recipient's own notification preferences still apply.",
    },
    data_subjects: {
        title: 'Profielen van betrokkenen',
        nav_title: 'Betrokkenen',
        nav: {
            profiles: 'Profielen',
        },
        create: 'Profiel aanmaken',
        edit: 'Profiel bewerken',
        delete: 'Profiel verwijderen',
        delete_confirm: 'Weet u zeker dat u dit betrokkenenprofiel wilt verwijderen?',
        display_name: 'Weergavenaam',
        email: 'Email',
        phone: 'Telefoon',
        employee_id: 'Medewerker-ID',
        student_id: 'Student-ID',
        notes: 'Opmerkingen',
        external_refs: 'Externe referenties',
        cases_count: 'Gevallen',
        workflows_count: 'Toestemmingsregistraties',
        last_activity: 'Laatste activiteit',
        timeline: 'Tijdlijn',
        link_case: 'Link Case',
        link_workflow: 'Link Workflow',
        no_profiles: 'Er zijn geen profielen van betrokkenen gevonden.',
        search_placeholder: 'Zoek op naam, e-mailadres, telefoonnummer of ID...',
        create_success: 'Profiel van betrokkene aangemaakt.',
        create_error: 'Kan het profiel van de betrokkene niet maken.',
        update_success: 'Profiel van betrokkene bijgewerkt.',
        update_error: 'Kan het profiel van de betrokkene niet bijwerken.',
        delete_success: 'Profiel van betrokkene verwijderd.',
        delete_error: 'Kan het profiel van de betrokkene niet verwijderen.',
        link_case_success: 'Casus gekoppeld aan profiel.',
        link_case_error: 'Kan case niet aan profiel koppelen.',
        link_workflow_success: 'Workflow gekoppeld aan profiel.',
        link_workflow_error: 'Kan workflow niet aan profiel koppelen.',
        load_error: 'Kan profielen van betrokkenen niet laden.',
        detail_load_error: 'Kan het profiel van de betrokkene niet laden.',
        timeline_load_error: 'Kan de tijdlijn niet laden.',
        tab_cases: 'DSR-gevallen',
        tab_consent: 'Toestemmingsregistraties',
        tab_timeline: 'Tijdlijn',
        tab_consent_workflows: 'Toestemmingsworkflows',
        tab_web_consent: 'CMP Toestemming',
        web_consent_empty: 'Er zijn geen CMP toestemmingsrecords gevonden voor dit onderwerp.',
        web_consent_pagination: 'CMP consent record pages',
        web_consent_actions: {
            grant: 'Granted',
            revoke: 'Revoked',
            update: 'Updated',
            reject_all: 'Rejected all',
            accept_all: 'Accepted all',
        },
        web_consent_load_error: 'Kan CMP toestemmingsrecords niet laden.',
        col_policy_version: 'Beleidsversie',
        col_versions: 'Versies',
        col_consent_version: 'Toestemming',
        col_cookie_declaration_version: 'Cookieverklaring',
        col_purposes: 'Doeleinden',
        purpose_granted: 'Toegegeven',
        purpose_denied: 'Geweigerd',
        granted_domains: 'Toegegeven',
        denied_domains: 'Geweigerd',
        col_action: 'Actie',
        col_timestamp: 'Tijdstempel',
        col_domain: 'Domein',
        no_cases: 'Geen gekoppelde cases.',
        no_workflows: 'Geen gekoppelde toestemmingsworkflows.',
        no_timeline: 'Geen tijdlijngebeurtenissen.',
        case_id: 'Case-ID',
        case_status: 'Status',
        case_type: 'Verzoektype',
        case_created: 'Gemaakt',
        workflow_id: 'Werkstroom-ID',
        workflow_type: 'Typ',
        workflow_status: 'Status',
        workflow_created: 'Gemaakt',
        timeline_date: 'Datum',
        timeline_event: 'Evenement',
        timeline_details: 'Details',
        timeline_time: 'Tijd',
        timeline_type: 'Typ',
        timeline_summary: 'Samenvatting',
        type_case: 'DSR-zaak',
        type_consent_workflow: 'Toestemmingsworkflow',
        profile_info: 'Profielinformatie',
        announce_loaded: 'Profielen van betrokkenen geladen',
        announce_detail_loaded: 'Profiel van betrokkene geladen',
        select_case: '— Selecteer een zaak —',
        select_workflow: '— Selecteer een werkstroom —',
        no_cases_hint: 'Koppel zaken om ze hier te zien.',
        no_workflows_hint: 'Koppel toestemmingsworkflows om ze hier te zien.',
        case_options_read_subject: 'the cases offered for linking',
        workflow_options_read_subject: 'the consent workflows offered for linking',
        external_identifiers: 'External identifiers',
        external_identifiers_desc: 'The identifiers the installed record kinds declare for a data subject. An erasure request is checked against the records issued under them.',
        external_identifiers_none_declared: 'No installed record kind declares an external identifier for a data subject.',
        external_identifiers_load_error: 'The external identifier fields could not be read.',
        external_identifiers_edit_unavailable: 'The external identifier fields could not be read, so they are not changed by this save.',
        external_identifier_hint: 'Leave blank to remove it.',
        external_ref_conflicts_heading: 'Identifier conflicts',
        external_ref_conflict: 'On {date}, a {kind} record was created for this profile with a different {label} from the one recorded here. The recorded value was kept; check which is correct.',
        external_ref_conflict_unlabelled: 'On {date}, an issued record was created for this profile with a different {label} from the one recorded here. The recorded value was kept; check which is correct.',
        external_ref_conflict_open_record: 'Open the issued record',
        profile_stale: 'This profile was changed after you opened it, so your change was not saved. The profile has been reloaded; make your change again.',
        identifier_produced_by_record: 'The {label} was recorded by an issued record, so it cannot be removed or changed here.',
    },
    flows: {
        map: {
            description_prefix: '{systems} systemen · {flows} stromen · {crossBorder} grensoverschrijdend · {highRisk} hoog/kritiek risico',
            switch_to_list: 'Schakel naar lijstweergave',
            switch_to_list_aria: 'Schakel naar tabelweergave',
            svg_aria: 'Gegevensstroomkaart: {systems} systemen, {flows} stromen, {crossBorder} grensoverschrijdend',
            selected_system: 'Geselecteerd systeem',
            drill_hint: 'Open het tabblad „Lijst" om de gekoppelde stromen, DPIA’s en leverancierstoewijzingen voor dit systeem te bewerken.',
        },
    },
    packages: {
        light: {
            name: 'Licht',
            description: 'Essentiële privacy-compliance-tools voor kleine teams',
        },
        standard: {
            name: 'Standaard',
            description: 'Volledig uitgerust compliance-platform voor groeiende organisaties',
        },
        enterprise_unlimited: {
            name: 'Onderneming onbeperkt',
            description: 'Compliance op enterprise-niveau met multi-org-ondersteuning en toegewijde diensten',
        },
    },
    forseti: {
        // Sprint 63.32.0 (Q1, #543) — the record summary's facts, rendered on screen;
        // the model prompt keeps the English summary.
        summary_fields: {
            accepted_until: 'Accepted until',
            active_timers: 'Active timers',
            actual_release: 'Actual release',
            ad_hoc_answered: 'Ad-hoc reports answered',
            ad_hoc_report_entries: 'Ad-hoc report entries',
            affected_systems: 'Affected systems',
            amended_on: 'Last amended',
            amendments: 'Amendments',
            answered_on: 'Answered',
            approval_mode: 'Approval mode',
            approved: 'Approved',
            approved_on: 'Approved',
            approved_risk_tier: 'Approved risk tier',
            artifact_kind: 'Artefact kind',
            authorisation_kind: 'Authorisation kind',
            authority: 'Authority',
            authority_reference_recorded: 'Authority reference recorded',
            authority_request: 'Authority request',
            authority_requests: 'Authority requests',
            batches: 'Batches',
            breach_notification: 'Breach notification',
            categories: 'Category tags',
            channel: 'Channel',
            claimant: 'Claimant',
            classification: 'Classification',
            classifications: 'Classifications',
            closed_on: 'Closed',
            closure_approved_on: 'Closure approved',
            completed_on: 'Completed',
            conformity_records: 'Conformity records',
            conformity_route: 'Conformity route',
            consent_cover_state: 'Consent cover',
            consent_evidence_referenced: 'Consent evidence referenced',
            corrective_measures: 'Corrective measures',
            country: 'Country',
            covered_services: 'Covered services',
            credentials: 'Credentials',
            cross_border_transfers: 'Cross-border transfers',
            cross_mapping_recorded_on: 'Cross-mapping recorded',
            cryptographic_assets: 'Cryptographic assets',
            current_version: 'Current version',
            data_categories: 'Data categories',
            data_element: 'Data element',
            data_tier: 'Data tier',
            dataset: 'Dataset',
            deadline: 'Deadline',
            decided_on: 'Decided',
            declared_on: 'Declared',
            declared_quantity: 'Declared quantity',
            deployment_context: 'Deployment',
            deposit_submission_recorded: 'Deposit submission recorded',
            description_recorded: 'Description recorded',
            destination_country: 'Destination country',
            details_recorded: 'Details recorded',
            dictionary_version: 'Dictionary version',
            document_type: 'Document type',
            documents: 'Documents',
            domains_evaluated: 'Domains evaluated',
            dpa_status: 'DPA status',
            dpo_assigned: 'DPO assigned',
            due: 'Due',
            effective_on: 'Effective',
            evidence: 'Evidence references',
            evidence_attachments: 'Evidence attachments',
            evidence_pack_linked: 'Evidence pack linked',
            expires_on: 'Expires',
            filing_linked: 'Filing record linked',
            filing_type: 'Filing type',
            financial_crime_signed: 'Financial-crime function signed',
            fine_areas: 'Fine areas cited',
            form_type: 'Form',
            format: 'Format',
            framework: 'Framework',
            gate_kind: 'Deposit gate kind',
            generated_tasks: 'Generated tasks',
            identity_linked: 'Linked to an identity record',
            identity_verification: 'Identity verification',
            incident: 'Incident',
            incidents: 'Incidents',
            intake_evidence: 'Intake evidence',
            intake_kind: 'Intake kind',
            issued_on: 'Issued',
            jurisdiction: 'Jurisdiction',
            kind: 'Kind',
            kind_declared_by_pack: 'Kind declared by an installed pack',
            legal_basis: 'Legal basis',
            legal_review_status: 'Legal review',
            legal_reviews: 'Legal reviews',
            licence_kind: 'Licence kind',
            lifecycle_events: 'Lifecycle events',
            lifecycle_stage: 'Lifecycle stage',
            linked_activities: 'Linked activities',
            linked_authority_requests: 'Linked authority requests',
            linked_dpia: 'Linked DPIA',
            linked_frameworks: 'Linked frameworks',
            location: 'Location',
            lodged_by_role: 'Lodged by',
            lodged_on: 'Lodged',
            model_version: 'Model version',
            name: 'Name',
            notification_entries: 'Notification entries',
            notify_decision: 'Notification decision',
            offerings: 'Named offerings',
            onboarding_status: 'Onboarding',
            open_findings: 'Open findings',
            ot_asset: 'OT asset',
            outcome_reports: 'Outcome reports',
            overall_score: 'Overall score',
            parcel: 'Parcel',
            period_end: 'Period end',
            period_start: 'Period start',
            periodic_report_entries: 'Periodic report entries',
            personal_data: 'Personal data',
            phases_completed: 'Phases completed',
            phases_total: 'Phases',
            planned_release: 'Planned release',
            playbook_version: 'Playbook version',
            pre_existing_on_commencement: 'Pre-existing on commencement',
            priority: 'Priority',
            privacy_signed: 'Privacy function signed',
            procedure_reference: 'Procedure reference',
            product: 'Product',
            products: 'Products',
            proof_attachments: 'Proof attachments',
            provider: 'Provider',
            provider_type: 'Provider type',
            publication_evidence: 'Publication evidence',
            published_on: 'Published',
            purpose_recorded: 'Purpose recorded',
            purposes: 'Purposes recorded',
            questions_answered: 'Questions answered',
            questions_total: 'Questions',
            raised_on: 'Raised',
            rationale_recorded: 'Rationale recorded',
            readiness_record: 'Readiness record',
            recall_scope: 'Recall scope',
            receipt_recorded: 'Receipt recorded',
            received_on: 'Received',
            recipient: 'Recipient',
            recipient_class: 'Recipient class',
            reclassification_changed_fields: 'Inputs changed since the approved run',
            reclassification_review: 'Reclassification prompt review',
            reclassification_signal_since: 'Reclassification signal since',
            reconciliation_exceptions: 'Reconciliation exceptions',
            record_kind: 'Record kind',
            recorded_on: 'Recorded',
            reference: 'Reference',
            releasable_marked_on: 'Marked releasable',
            relied_on_events: 'Parcel events relied on',
            remediation_plans: 'Remediation plans',
            remediation_tasks: 'Remediation tasks raised',
            renewal_of_earlier: 'Renewal of an earlier credential',
            replaces: 'Replaces',
            reporting_years: 'Reporting years',
            representative_consents: 'Representative consent records',
            request_type: 'Request type',
            requester_recorded: 'Requester on record',
            responses: 'Responses',
            responsible_person_recorded: 'Responsible person recorded',
            retention_duty: 'Retention duty',
            review_due: 'Review due',
            revoked_on: 'Revoked',
            rights_evidence: 'Rights evidence entries',
            risk_level: 'Risk level',
            root_cause_recorded: 'Root cause recorded',
            rule_type: 'Rule type',
            sellers: 'Sellers',
            serial_reference: 'Serial reference',
            service_type: 'Service type',
            sharing_events: 'Sharing events',
            sharing_refusals: 'Refused sharing attempts',
            shipment: 'Shipment',
            sla_state: 'Deadline clock',
            source_country: 'Source country',
            stage: 'Stage',
            state: 'State',
            status: 'Status',
            sub_status: 'Sub-status',
            subject: 'Subject',
            subject_kind: 'Subject kind',
            submission_kind: 'Submission kind',
            submitted_on: 'Submitted',
            successor_recorded: 'Successor credential recorded',
            supplied_on: 'Supplied',
            takedown_reason_recorded: 'Takedown reason recorded',
            taken_down_on: 'Taken down',
            target_date: 'Target date',
            technical_actions: 'Technical actions',
            technical_regulation: 'Technical regulation',
            title: 'Title',
            transfer_assessment_referenced: 'Transfer assessment referenced',
            transfer_mechanism: 'Transfer mechanism',
            transfer_review_state: 'Transfer review',
            transfer_source_recorded: 'Transfer source institution recorded',
            valid_from: 'Valid from',
            valid_until: 'Valid until',
            vendor: 'Vendor',
            vendor_type: 'Vendor type',
            version: 'Version',
            versions: 'Versions recorded',
            withdrawal_determination_recorded: 'Withdrawal determination recorded',
        },
        summary_codes: {
            acknowledged: 'Acknowledged',
            active: 'Active',
            all: 'All approvers',
            any: 'Any approver',
            approved: 'Approved',
            archived: 'Archived',
            blocked: 'Blocked',
            breached: 'Breached',
            cancelled: 'Cancelled',
            closed: 'Closed',
            completed: 'Completed',
            critical: 'Critical',
            declared: 'Declared',
            do_not_notify: 'Do not notify',
            draft: 'Draft',
            escalated: 'Escalated',
            expired: 'Expired',
            filed: 'Filed',
            high: 'High',
            in_progress: 'In progress',
            in_review: 'In review',
            inactive: 'Inactive',
            known_data_subject: 'A known data subject',
            labelled_claimant: 'A labelled claimant',
            lodged: 'Lodged',
            low: 'Low',
            medium: 'Medium',
            new: 'New',
            not_started: 'Not started',
            notify: 'Notify',
            open: 'Open',
            overdue: 'Overdue',
            paused: 'Paused',
            pending: 'Pending',
            personal_data: 'Personal data',
            published: 'Published',
            received: 'Received',
            rejected: 'Rejected',
            resolved: 'Resolved',
            revoked: 'Revoked',
            signed: 'Signed',
            special_category: 'Special-category personal data',
            submitted: 'Submitted',
            suspended: 'Suspended',
            taken_down: 'Taken down',
            unclassified: 'Unclassified',
            undecided: 'Undecided',
            under_review: 'Under review',
            unverified: 'Unverified',
            verified: 'Verified',
            withdrawn: 'Withdrawn',
        },
        summary_flag_yes: 'Yes',
        summary_flag_no: 'No',
        summary_not_recorded: 'Not recorded',
        summary_fields_label: 'Record facts',
        providerSettings: {
            title: 'Forseti AI Provider-instellingen',
            subtitle: 'Configureer de Claude-provider voor de Forseti AI juridisch adviseur.',
            healthHeading: 'Gezondheid van de aanbieder',
            statusLabel: 'Status',
            statusOk: 'Oké',
            statusFailed: 'Niet beschikbaar',
            providerLabel: 'Aanbieder',
            providerClaudeApi: 'Claude API',
            providerCliRelay: 'Claude CLI-relais',
            providerAesirManaged: 'Door Aesir beheerde tokens',
            lastCheckedLabel: 'Laatst gecontroleerd',
            reasonLabel: 'Reden',
            refreshButton: 'Vernieuwen',
            apiKeyHeading: 'Claude API-toets',
            apiKeyHint: 'Als je een API-toets instelt, wordt de Claude-modus API geactiveerd. Laat deze instelling uitgeschakeld om de lokale CLI-relay te gebruiken (ontwikkelaar/zelf-hostende modus).',
            apiKeyLabel: 'API-toets',
            apiKeyMinLength: 'Minimaal 32 tekens. Versleuteld in rust; nooit meer getoond.',
            apiKeyTooShort: 'De sleutel API moet minimaal 32 tekens lang zijn.',
            apiKeySaved: 'API sleutel opgeslagen. Verfrissende gezondheid…',
            apiKeyCleared: 'API-toets gewist. Forseti maakt nu gebruik van het lokale CLI-relais.',
            setKeyButton: 'Sla de API-sleutel op',
            clearKeyButton: 'Wis de API-toets (gebruik CLI-relais)',
        },
        // Owner ruling 2026-10-10 — Forseti → AI Model (provider, model, effort).
        aiModel: {
            title: 'AI Model',
            sidebarLabel: 'AI Model',
            subtitle: 'Choose the AI provider, model and effort behind every Forseti request. Your organisation connects its own provider account with its own API key, and that provider bills you directly for what Forseti uses.',
            loadError: 'The AI model settings could not be read.',
            retrySubject: 'the AI model settings',
            generalHeading: 'General',
            generalDescription: 'The active provider answers every Forseti chat, workflow, tabular review and redline. Only a provider with a saved API key can be made active.',
            activeProviderLabel: 'Active provider',
            activeProviderHint: 'A provider without a saved API key cannot be selected. Save its key in its card below first.',
            providerNoKey: '{provider} (no API key saved)',
            thinkingLabel: 'Show the reasoning summary in chat',
            thinkingHint: 'When the model returns a summary of its reasoning, Forseti shows it above the answer in interactive chat. Workflows and reviews are not affected.',
            relayProviderLabel: 'Local CLI relay (development)',
            relayUrlLabel: 'Relay address',
            relayUrlHint: 'The address of the local CLI relay. Used only in development and self-hosted installations.',
            saveGeneral: 'Save general settings',
            generalSaved: 'General settings saved.',
            providersHeading: 'Providers',
            providersDescription: 'Each provider keeps its own API key, model and effort. Saving a provider does not make it active unless you tick Make this the active provider.',
            cardDescription: "Models from {provider}, called with your organisation's own API key.",
            cardDescriptionCompatible: "Any endpoint that implements the OpenAI API, such as a model gateway or a self-hosted model server, called with that endpoint's API key.",
            activeBadge: 'Active',
            keySetBadge: 'API key saved',
            keyMissingBadge: 'No API key',
            apiKeyLabel: 'API key',
            apiKeySavedHint: 'A key ending {hint} is saved. Leave this blank to keep the saved key.',
            apiKeyNoneHint: 'No key is saved. The key is stored encrypted and is never shown again after you save it.',
            removeKey: 'Remove key',
            removeKeyTitle: 'Remove the saved API key?',
            removeKeyMessage: 'Forseti cannot call {provider} again until a new key is saved. The model and effort settings stay as they are.',
            removeKeyConfirm: 'Remove key',
            removing: 'Removing the key…',
            keyRemoved: 'The {provider} API key was removed.',
            modelLabel: 'Model',
            modelHint: 'Choose a suggested model or type any model id {provider} offers.',
            modelHintCompatible: 'Type the model id exactly as the endpoint expects it.',
            modelRequired: 'Enter a model.',
            fastModelLabel: 'Fast model (optional)',
            fastModelHint: 'Used for conversation titles and tabular extraction. Leave blank to use the main model.',
            effortLabel: 'Effort',
            effortHint: 'How much reasoning the model spends on each answer. Levels the selected model does not accept are unavailable.',
            effortUnsupported: '{effort} is not available for this model.',
            efforts: {
                default: 'Model default',
                low: 'Low',
                medium: 'Medium',
                high: 'High',
                xhigh: 'Extra high',
                max: 'Maximum',
            },
            apiModeLabel: 'API mode',
            apiModeHint: 'Automatic uses the Responses API for models that require it and Chat Completions for the rest.',
            apiModes: {
                auto: 'Automatic',
                responses: 'Responses API',
                chat_completions: 'Chat Completions API',
            },
            reasoningModeLabel: 'Reasoning mode',
            reasoningModeHint: 'Pro spends more reasoning on each answer and costs more. It is offered only for models that support it.',
            reasoningModeUnavailable: 'Pro is not available for the selected model.',
            reasoningModes: {
                standard: 'Standard',
                pro: 'Pro',
            },
            serviceTierLabel: 'Service tier',
            serviceTierHint: 'Project default uses the tier set on your provider project.',
            serviceTiers: {
                auto: 'Project default',
                default: 'Standard',
            },
            baseUrlLabel: 'Base URL',
            baseUrlHint: "The endpoint's API root over HTTPS, for example https://openrouter.ai/api/v1.",
            baseUrlRequired: "Enter the endpoint's base URL.",
            makeActiveLabel: 'Make this the active provider',
            saveButton: 'Save',
            saved: '{provider} settings saved.',
            testButton: 'Test connection',
            testing: 'Testing…',
            testNote: 'Tests the saved settings, so save your changes first. The test sends one short request containing no customer data, which your provider may charge for.',
            unsavedNote: 'This card has unsaved changes. The test uses the saved settings.',
            lastTestHeading: 'Last test',
            testNever: 'Not tested yet.',
            testPassed: 'Passed',
            testFailed: 'Failed',
            testCheckedAt: 'Checked {time}',
            testError: 'The connection test could not be run.',
            saveError: 'The settings could not be saved.',
            removeError: 'The API key could not be removed.',
            errors: {
                api_key_required: 'Save an API key for this provider before making it active.',
                api_key_required_for_new_host: 'The base URL now names another host. Enter the API key for that host before saving; the saved key is not sent to a new host.',
                invalid_api_key: 'The provider rejected this API key.',
                invalid_api_key_format: 'This does not look like a key for this provider. Check that you copied the whole key.',
                unsupported_model_effort: 'The selected model does not accept this effort level.',
                model_requires_responses: 'This model needs the Responses API. Choose Automatic or Responses API.',
                base_url_required: "Enter the endpoint's base URL.",
                invalid_base_url: 'Enter a valid HTTPS URL.',
                unsupported_provider_options: 'These options are not available for this provider.',
            },
        },
        usage: {
            title: 'Gebruik',
            recentHeading: 'Recente activiteit',
            historyHeading: 'Laatste 12 periodes',
            noActivity: 'Nog geen activiteit deze periode.',
            featureLabel: 'Functie',
            whenLabel: 'Wanneer',
            periodLabel: 'Periode',
            byFeatureHeading: 'Deze maand per functie',
            byFeatureEmpty: 'Nog geen feature-activiteit deze periode.',
            featureColumn: 'Functie',
            featureUnitsColumn: 'Eenheden',
            featureRunsColumn: 'Loopt',
            byUserHeading: 'Deze maand per gebruiker (beheerdersweergave)',
            byUserEmpty: 'Er is nog geen activiteit per gebruiker deze periode.',
            userColumn: 'Gebruiker',
            reconciliationLinkLabel: 'Bekijk het afstemmingsafwijkingsrapport',
            reconciliationLinkHint: 'Alleen Aesir-interne beheerder.',
            // Owner ruling 2026-10-10 — consumption by provider, model and effort.
            subtitleProvider: "What Forseti has used, and the provider, model and effort each request ran on. Your organisation's AI provider bills you directly for this consumption.",
            routingHeading: 'Current routing',
            routingDescription: 'The provider, model and effort each Forseti feature uses now.',
            routingProviderLabel: 'Active provider',
            routingFeatureColumn: 'Feature',
            routingModelColumn: 'Model',
            routingEffortColumn: 'Effort',
            routingChangeLink: 'Change the AI model settings',
            routingLoadError: 'The current routing could not be read.',
            routingRetrySubject: 'the current routing',
            periodHeading: 'Consumption',
            periodDescription: 'Requests and tokens for the period you choose. The current month is shown first.',
            fromLabel: 'From',
            toLabel: 'To',
            applyPeriod: 'Show period',
            periodInvalid: 'The start date must be on or before the end date.',
            summaryLoadError: 'Consumption for this period could not be read.',
            summaryRetrySubject: 'consumption for this period',
            totalsCalls: 'Requests',
            totalsInput: 'Input tokens',
            totalsOutput: 'Output tokens',
            totalsCached: 'Cached input tokens',
            byModelHeading: 'By provider, model and effort',
            byFeaturePeriodHeading: 'By feature',
            providerColumn: 'Provider',
            modelColumn: 'Model',
            effortColumn: 'Effort',
            callsColumn: 'Requests',
            inputColumn: 'Input tokens',
            outputColumn: 'Output tokens',
            cachedColumn: 'Cached input tokens',
            tokensColumn: 'Tokens (in / out)',
            timeColumn: 'Time',
            noConsumption: 'Forseti made no requests in this period.',
            notRecorded: 'Not recorded',
            recentDescription: 'The most recent requests in the selected period.',
            recentLoadError: 'Recent activity could not be read.',
            recentRetrySubject: 'recent activity',
            byUserPeriodHeading: 'By user',
            byUserBasis: 'Counted over the {count} most recent requests in this period.',
            historyMonthlyHeading: 'Monthly consumption',
            historyConsumedColumn: 'Consumed units',
            historyLoadError: 'Monthly consumption could not be read.',
            historyRetrySubject: 'monthly consumption',
            historyEmpty: 'No earlier months recorded yet.',
            featureLabels: {
                chat: 'Chatten',
                workflow: 'Workflow uitgevoerd',
                tabular: 'Tabellarische extractie',
                redline: 'Redline-recensie',
                title: 'Titel van het gesprek',
                unknown: 'Anders',
            },
        },
        reconciliation: {
            title: 'Verzoening van Forseti-quota',
            subtitle: 'Drift between the fast quota counter cache and the authoritative billing ledger. The reconciler runs every 5 minutes and records any drift to the structured log channel. Aesir-internal admin only.',
            backLink: 'Terug naar Forseti-gebruik',
            stubHeading: 'Where reconciliation results are recorded',
            stubBody: 'This page does not show run history. Each reconciliation run writes its drift figures to the structured log channel; read them there.',
            permissionDenied: 'Deze pagina is beperkt tot Aesir-interne beheerders. Als u denkt dat u toegang zou moeten hebben, neem dan contact op met uw AesirX-vertegenwoordiger.',
        },
        projects: {
            title: 'Projecten',
            subtitle: 'Werkruimten voor documenten, gesprekken, workflows en beoordelingen.',
            modeLabel: 'Bekijk',
            tabMine: 'Mijn projecten',
            tabShared: 'Gedeeld met mij',
            searchLabel: 'Zoek projecten',
            searchPlaceholder: 'Zoek…',
            newButton: 'Nieuw project',
            colName: 'Naam',
            colVisibility: 'Zichtbaarheid',
            colDocs: 'Documenten',
            colMembers: 'Leden',
            colUpdated: 'Bijgewerkt',
            visibilityOwn: 'Alleen eigen',
            visibilityShared: 'Gedeeld',
            emptyMine: 'Nog geen projecten. Maak er een om aan de slag te gaan.',
            emptyShared: 'Er zijn geen projecten met u gedeeld.',
            pageHint: 'Toont {shown} van {total}.',
            createTitle: 'Nieuw project',
            createSubmit: 'Project aanmaken',
            fieldName: 'Naam',
            fieldDescription: 'Beschrijving (optioneel)',
            fieldCmNumber: 'Zaak-/zaaknummer (optioneel)',
            fieldVisibility: 'Zichtbaarheid',
            errorNameRequired: 'Naam is vereist.',
            errorSelectorRequired: 'Selecteer een gebruiker of organisatiegroep die u wilt toevoegen.',
            renameButton: 'Hernoemen',
            renamePrompt: 'Projectnaam wijzigen',
            deleteButton: 'Verwijderen',
            deleteConfirm: 'Delete this project? It will disappear from your projects and cannot be restored from the product.',
            deleteFolderConfirm: 'Deze map verwijderen? Documenten daarin blijven in het project, maar worden niet opgeslagen.',
            removeMemberConfirm: 'Dit lid verwijderen?',
            foldersHeading: 'Mappen',
            foldersEmpty: 'Nog geen mappen.',
            addFolderButton: 'Nieuwe map',
            addFolderTitle: 'Nieuwe map',
            documentsHeading: 'Documenten',
            documentsEmpty: 'Er zijn nog geen documenten bijgevoegd.',
            colFilename: 'Bestandsnaam',
            colAdded: 'Toegevoegd',
            unattachButton: 'Maak los',
            membersHeading: 'Leden',
            membersEmpty: 'Alleen eigenaar.',
            addMemberButton: 'Lid toevoegen',
            addMemberTitle: 'Lid toevoegen',
            addMemberSubmit: 'Lid toevoegen',
            colMember: 'Lid',
            colRole: 'Rol',
            selectorTypeLabel: 'Voeg toe door',
            selectorUser: 'Gebruikers-ID',
            selectorGroup: 'Organisatiegroep-ID',
            fieldUserId: 'Gebruiker',
            fieldGroupId: 'Org groep',
            fieldRole: 'Rol',
            roleAdmin: 'Beheerder',
            roleEditor: 'Redacteur',
            roleViewer: 'Kijker',
            notFound: 'Project niet gevonden.',
            selectUserPlaceholder: 'Selecteer een gebruiker…',
            selectGroupPlaceholder: 'Selecteer een organisatiegroep…',
            noUsersAvailable: 'Geen gebruikers beschikbaar.',
            noGroupsAvailable: 'Geen organisatiegroepen beschikbaar.',
            loadFailed: 'Kan opties niet laden.',
            deleteProjectTitle: 'Project verwijderen?',
            deleteFolderTitle: 'Map verwijderen?',
            removeMemberTitle: 'Lid verwijderen?',
            removeMemberConfirmGeneric: 'Dit lid uit het project verwijderen?',
            addDocumentButton: 'Document toevoegen',
            addDocumentTitle: 'Document toevoegen aan project',
            fieldDocument: 'Selecteer document',
            selectDocPlaceholder: 'Zoek een document...',
            ownerLabel: 'Systeemeigenaar',
        },
        chat: {
            sidebarLabel: 'Chats',
            overviewTitle: 'Forseti',
            overviewSubtitle: 'Uw AI Juridisch en compliance-adviseur — gebaseerd op uw huurdergegevens en uw geïnstalleerde regelgevingspakketten.',
            personaSelectorLabel: 'Persoon',
            personaHeading: 'Kies een persoonlijkheid',
            startChatButton: 'Start een nieuw chatgesprek',
            startingChat: 'Chat starten…',
            recentHeading: 'Recente chats',
            recentEmpty: 'Nog geen chats: begin er hierboven één.',
            untitledChat: 'Nieuwe chat',
            chatLoadFailed: 'Forseti could not load this chat.',
            streamFailed: 'Forseti could not finish this reply. Your message is still in the box.',
            personaSwitchFailed: 'The persona could not be changed.',
            renameFailed: 'The chat could not be renamed.',
            deleteFailed: 'The chat could not be deleted.',
            messageListLabel: 'Gesprek',
            detailSubtitle: 'Persoon: {persona}',
            composeHeading: 'Stuur een bericht',
            composeLabel: 'Bericht Forseti',
            composeDisabledPlaceholder: 'This chat is read-only.',
            composeDisabledHint: 'You can read this conversation but not add to it.',
            switchPersonaHeading: 'Persoon',
            presetPersona: 'Vooraf ingestelde persona',
            changePersona: 'Persona wijzigen',
            hidePersonaOptions: 'Personaopties verbergen',
            starterHeading: 'Begin met een van deze acties',
            renameButton: 'Hernoemen',
            renamePrompt: 'Chat hernoemen',
            deleteButton: 'Verwijderen',
            deleteConfirm: 'Delete this chat? It will disappear from your chats and cannot be restored from the product.',
            notFound: 'Chat niet gevonden.',
            emptyChat: 'Nog geen berichten. Zeg hallo om een gesprek te beginnen.',
            roleUser: 'Jij',
            roleAssistant: 'Forseti',
            roleSystem: 'Systeem',
            thinkingLabel: 'Denken',
            toolCallPlaceholder: 'Gereedschapsoproep: ',
            toolResultPlaceholder: 'Tool result hidden.',
            docEditPlaceholder: 'Documentbewerkingen voorgesteld (V{version}).',
            disclaimer: 'Forseti ondersteunt uw oordeel; het vervangt geen bevoegde raadsman.',
            sendButton: 'Verzenden',
            sending: 'Verzenden…',
            composePlaceholder: 'Vraag Forseti…',
            composingPlaceholder: 'Forseti reageert…',
            streamingPlaceholder: 'Forseti is aan het typen…',
            promptInjectionTitle: 'Forseti liet enkele controlefiches vallen.',
            promptInjectionBody: 'Gestripte patronen: {patterns}. Forseti ziet alleen uw berichttekst.',
            rateLimitTitle: 'Je verzendt berichten te snel.',
            rateLimitRetryAfter: 'Probeer het over {seconds}s opnieuw.',
            messageLength: '{count} / {max} characters',
            messageTooLong: 'Messages to Forseti are limited to {max} characters. Shorten the message or split it in two.',
            counselReviewTitle: 'Forseti is hier onzeker.',
            counselReviewBody: 'Neem contact op met een bevoegde raadsman voordat u op basis van dit antwoord handelt.',
            legalConclusionRefused: 'Forseti weigerde een juridische conclusie te trekken zonder grond. Probeer eerst search_regulatory of escaleer naar een raadsman.',
            toolCallDone: 'Klaar.',
            toolCallArgsLabel: 'Argumenten',
            toolCallResultLabel: 'Resultaat',
            toolResultLogged: 'Toolresultaat vastgelegd in auditlogboek.',
            toolResultAria: 'gereedschap resultaat',
            documentEditAria: 'document bewerken',
            createTaskFromMessage: 'Maak hier een taak van',
            createTaskHeading: 'Maak een taak op basis van dit bericht',
            createTaskTitleLabel: 'Titel',
            createTaskTitleRequired: 'Titel is vereist.',
            createTaskDescriptionLabel: 'Beschrijving',
            createTaskAssigneeLabel: 'Ontvanger',
            createTaskAssigneePlaceholder: 'Selecteer toegewezen…',
            createTaskAssigneeNone: '— niet toegewezen —',
            createTaskDueDateLabel: 'Vervaldatum',
            createTaskWorkflowLabel: 'Gekoppelde workflow (optioneel)',
            createTaskWorkflowPlaceholder: 'Selecteer een werkstroom...',
            createTaskWorkflowNone: '— geen —',
            renameModalSubmit: 'Hernoemen',
            renameModalSaving: 'Opslaan…',
            deleteConfirmTitle: 'Chat verwijderen?',
            createTaskSubmit: 'Taak maken',
            createTaskSubmitting: 'Creëren…',
            cancelButton: 'Annuleer',
            autoRouteHintMessage: 'Deze vraag ligt meer in de stuurhut van {persona}.',
            autoRouteSwitchButton: 'Schakel over naar {persona}',
            autoRouteSwitching: 'Overstappen…',
            handoffSuggestionMessage: 'Of open een nieuwe chat met {persona} en stel deze vraag.',
            handoffSuggestionButton: 'Start een nieuwe chat met {persona}',
            handoffSuggestionWorking: 'Openen…',
            handoffSuggestionDismiss: 'Niet nu',
            handoffSuggestionFailed: 'The new chat could not be opened.',
            persona_display: {
                'forseti-generalist': 'Forseti',
                'forseti-compliance': 'Compliance-specialist',
                'forseti-legal': 'Juridisch adviseur',
                'forseti-dpo': 'FG-adviseur',
                'forseti-risk': 'Risicoanalist',
                'forseti-auditor': 'Auditorblik',
            },
            exportButton: 'Exporteren als PDF',
            exportRunning: 'Exporteren…',
            exportSuccess: 'Geëxporteerd: {filename} (open de bestandsbrowser).',
            exportOpenFile: 'Openen in de bestandsbrowser',
            exportDownload: 'PDF downloaden',
            citation: {
                verifyBadge: '[VERIFIEER]',
                verifyAria: 'citaat kon niet worden geverifieerd - controleer het voordat u erop vertrouwt',
                popoverAria: 'Bron voor {label}',
                openSource: 'Open source-pagina',
            },
        },
        auditTrail: {
            forsetiBadge: 'Forseti',
            forsetiBadgeTooltip: 'AI-gegenereerde gebeurtenis uit het Forseti-audittraject.',
        },
        rowAction: {
            ask: 'Vraag Forseti',
            tooltipOpen: 'Vraag Forseti hierover {recordType}',
            tooltipLocked: 'Deze {recordType} is vergrendeld — Forseti kan vragen beantwoorden, maar kan geen wijzigingen aanbrengen.',
        },
        // ── Sprint 63.22.0 — the global Forseti launcher and its launchpad ──
        launcher: {
            openLabel: 'Open Forseti',
            tooltip: 'Ask Forseti (Ctrl+Shift+F)',
            modalTitle: 'Forseti',
            personaHeading: 'Choose who to ask',
            scopeHeading: 'What the chat is about',
            askAboutRecord: 'Ask about this {record}',
            generalChat: 'General chat',
            attachRecord: 'Attach a record',
            attachRecordRequired: 'Choose a record to attach, or choose General chat.',
            recordScopeLost: 'This page no longer shows a record, so this will be a general chat.',
            recentChats: 'Recent chats',
            recentEmpty: 'You have no chats yet.',
            resumeChat: 'Resume {title} — {persona}, {date}',
            startChat: 'Start chat',
            starting: 'Starting chat…',
            openFullView: 'Open in full view',
            loadFailed: 'Forseti could not load your personas and recent chats.',
            noPersonas: 'No Forseti persona is enabled for your organisation.',
        },
        advisorRunPanel: {
            // fix-sprint-20260627-2217-followup F11 — the chat's back control names
            // where it goes (WCAG 2.4.6); it returns to the launchpad.
            backToOptions: 'Back to Forseti options',
            title: 'Forseti — over deze plaat',
            heading: 'Forseti — {recordType}',
            summaryLabel: 'Samenvatting',
            suggestedPersona: 'Voorgestelde persona',
            askForseti: 'Vraag Forseti',
            runWorkflow: 'Voer een workflow uit',
            viewLineage: 'Bekijk de auditlijn',
            createPdplEvidencePack: 'PDPL-bewijspakket maken',
            prepareBreachDraft: 'Kennisgevingsconcept voorbereiden',
            primaryActions: {
                projectSummary: 'Projectdocument samenvatten',
                supplementSummary: 'Aanvullend verzoek samenvatten',
                authoritySummary: 'Autoriteitsverzoek samenvatten',
                rightsRequestResponse: 'DSR-antwoord opstellen',
                dpiaQuickReview: 'Snelle DPIA-review uitvoeren',
                taskControlWalkthrough: 'Controlebewijs doorlopen',
                vendorRiskNarrative: 'Leveranciersrisico-narratief genereren',
                incidentLetter: 'PDPL-inbreukbrief opstellen',
                ropaSufficiency: 'RoPA-toereikendheidscontrole uitvoeren',
                vendorDpaReview: 'Leveranciers-DPA-review uitvoeren',
            },
            builtinWorkflowViews: {
                ropaSufficiency: {
                    title: 'RoPA Sufficiency Check',
                    notice: 'Runs the existing RoPA sufficiency workflow for this processing activity. The run may create draft follow-up tasks; operators still review and execute them.',
                },
                vendorDpaReview: {
                    title: 'Vendor DPA Review',
                    notice: 'Runs the existing Vendor DPA Review workflow for this vendor contract. The run may create draft follow-up tasks; operators still review every finding and task.',
                },
                vendorRiskNarrative: {
                    title: 'Vendor Risk Narrative',
                    notice: 'Runs the existing vendor risk narrative workflow for this vendor. Output remains draft evidence; the human risk owner reviews and approves it before use.',
                },
                incidentLetter: {
                    title: 'PDPL Breach Notification Letter',
                    notice: 'Runs the existing VN PDPL breach-notification letter workflow for this incident or playbook activation. Output remains draft only; formal regulator filing still requires human sign-off.',
                },
                projectSummary: {
                    title: 'Project Document Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this Forseti project. The workflow is read-only and does not create tasks, evidence, or record updates.',
                },
                supplementSummary: {
                    title: 'Supplement Request Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this supplement request. The workflow is read-only and does not create tasks, evidence, record updates, or filing decisions.',
                },
                authoritySummary: {
                    title: 'Authority Request Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this authority request. The workflow is read-only and does not create tasks, evidence, record updates, filing decisions, or authority-response drafts.',
                },
                rightsRequestResponse: {
                    title: 'DSR Response Draft',
                    notice: 'Runs the existing DSR response draft workflow for this rights request. Output remains draft only; sending and case updates still require the existing human-reviewed rights-request flow.',
                },
                dpiaQuickReview: {
                    title: 'DPIA Quick Review',
                    notice: 'Runs the existing DPIA Quick Review workflow for this DPIA. The run may create draft follow-up tasks; operators still review every finding and task.',
                },
                taskControlWalkthrough: {
                    title: 'Control Evidence Walkthrough',
                    notice: 'Runs the existing compliance-control walkthrough for this task. The workflow must retrieve regulatory anchors before citing them, does not issue legal conclusions or mark controls/evidence approved, and only creates draft evidence placeholders when the operator explicitly requests them.',
                },
            },
            breachDraftNotice: 'Forseti bereidt alleen conceptinhoud voor het gekoppelde Mẫu số 08 voor. Het beslist niet of melding vereist is en dient of verzendt het formulier niet.',
            breachDraftReady: 'Concept Mẫu số 08 klaar voor beoordeling',
            breachDraftReviewBeforeSelecting: 'Beoordelen vóór selectie',
            workflowHeading: 'Relevante workflows',
            workflowLoading: 'Relevante workflows laden…',
            workflowEmpty: 'Er zijn geen relevante workflows beschikbaar voor dit record.',
            workflowError: 'Kan relevante workflows niet laden.',
            selectWorkflow: '{title} selecteren',
            workflowSelected: 'Workflow geselecteerd. De inline-uitvoerder opent in de volgende stap.',
            workflowRunSelected: 'Geselecteerde workflow uitvoeren',
            workflowStarting: 'Workflow wordt gestart…',
            workflowRunError: 'Deze workflow kon niet worden gestart.',
            workflowCreateGuided: 'Een begeleide workflow voor dit record maken',
            lineageHeading: 'Auditherkomst voor dit record',
            lineageLoading: 'Auditherkomst laden…',
            lineageEmpty: 'Er zijn geen Forseti-herkomstgebeurtenissen vastgelegd voor dit record.',
            lineageError: 'Kan auditherkomst niet laden.',
            retry: 'Opnieuw proberen',
            chatContextLoading: 'Recordcontext laden…',
            chatGuidance: {
                general: 'Forseti kan dit record samenvatten, ontbrekende informatie vaststellen en beoordelingsstappen voorstellen.',
                privacy: 'Forseti kan privacyfeiten beoordelen, verplichtingen uit geïnstalleerde kaders controleren en helpen een concept voor menselijke beoordeling op te stellen.',
                compliance: 'Forseti kan nalevingshiaten vaststellen, vereisten onderbouwen met geïnstalleerde bronnen en verantwoordelijke vervolgstappen voorstellen.',
                legal: 'Forseti kan het record samenvatten, onderbouwd bronmateriaal vinden en helpen een concept voor juridische beoordeling op te stellen.',
                risk: 'Forseti kan vastgelegde risico’s beoordelen, ontbrekende feiten markeren en evenredige beoordelingsstappen voorstellen.',
                audit: 'Forseti kan bewijshiaten onderzoeken, het record aan geïnstalleerde beheersmaatregelen koppelen en auditklare opvolging voorstellen.',
            },
            starterPrompts: {
                identifyGaps: 'Welke informatie of welk bewijs ontbreekt in dit record?',
                reviewFrameworks: 'Welke verplichtingen uit onze geïnstalleerde kaders moet ik voor dit record beoordelen?',
                prepareDraft: 'Help mij een concept op te stellen dat uitsluitend op dit record en onderbouwde geïnstalleerde bronnen is gebaseerd.',
                nextSteps: 'Welke beoordelingsstappen moet ik hierna nemen?',
                summarizeRecord: 'Vat dit record samen en markeer punten die door een mens moeten worden beoordeeld.',
                assessRisk: 'Beoordeel de vastgelegde risico’s, stel ontbrekende feiten vast en stel beoordelingsstappen voor.',
            },
            disclaimer: 'Forseti ondersteunt uw oordeel; het vervangt geen bevoegde raadsman.',
            recordType: {
                project: 'Project',
                dpia: 'DPIA',
                dossier_filing: 'Dossierarchivering',
                transfer_assessment: 'Grensoverschrijdende overdracht',
                vendor: 'Verkoper',
                vendor_contract: 'Leverancierscontract',
                vendor_acceptance: "Acceptatie van leveranciersrisico's",
                incident: 'Voorval',
                incident_op: 'Incidentoperatie',
                breach_notification: 'Melding van inbreuk',
                authority_request: 'Autoriteitsverzoek',
                supplement_request: 'Aanvraag voor aanvulling',
                rights_request: 'Rechtenverzoek',
                contract: 'Contract',
                task: 'Taak',
                data_mapping_activity: 'RoPA-activiteit',
                compliance_rule: 'Nalevingsregel',
                program: 'Programma',
                governance_document: 'Bestuursdocument',
                remediation_plan: 'Saneringsplan',
                cross_border_filing: 'Grensoverschrijdende indiening',
                enforcement_case: 'Enforcement Case',
                authority_direction: 'Authority Direction',
                regulated_account: 'Regulated Account',
                regulatory_placeholder: 'Regulatory Placeholder',
                sector_licence: 'Sector Licence',
                credit_information_supply: 'Credit-Information Supply',
                data_service_provider: 'Data-Service Provider Record',
                regulatory_disclosure: 'Regulatory Disclosure',
                issued_record: 'Student Record',
                publication: 'Publication',
            },
        },
        autocomplete: {
            // fix-sprint-20260627-2217-followup F9, verifier round 1 — the 409 refusal
            // when no installed framework grounds the draft (raw English before).
            error_no_grounding_scope: 'Forseti cannot draft this DPIA: no framework for this frame is installed with a usable status, so there is no regulatory text to ground the draft on. Nothing was drafted.',
            button_label: 'Vraag Forseti om dit op te stellen',
            drafting: 'Opstellen…',
            hint_pick_activity: 'Kies een gekoppelde verwerkingsactiviteit om het opstellen van Forseti mogelijk te maken.',
            draft_origin_label: 'Forseti-concept — beoordeling per veld',
            tokens_label: '≈ {count} tokens uit uw maandelijkse bucket',
            accept_selected: 'Accepteer geselecteerd',
            reject_all: 'Concept afwijzen',
            field_checkbox_label: 'Forseti-concept toepassen voor {field}',
            field_labels: {
                description: 'Beschrijving',
                purpose: 'Doel',
                data_subjects: 'Betrokkenen',
                data_types: 'Gegevenstypen',
                necessity_justification: 'Noodzaak & proportionaliteit',
                consultation_notes: 'Aantekeningen bij overleg',
                dpo_opinion: 'DPO mening',
                system_ids: 'Gekoppelde systemen',
                vendor_ids: 'Gekoppelde leveranciers',
            },
            error_generic: 'Forseti kon dit DPIA niet opstellen. Probeer het opnieuw of verfijn het activiteitenrecord.',
            variant_legend: 'Kaderframe',
            variant_label_gdpr: 'GDPR',
            variant_none_available: 'Geen geïnstalleerd framework beschikbaar',
            variant_label_pdpl: 'PDPL',
            variant_label_nis2: 'NIS2',
            variant_label_iso27001: 'ISO27001',
            variant_label_soc2: 'SOC 2',
            conflict_banner: 'Let op: {count} raamwerkoverschrijdende conflicten zijn van toepassing op deze variant. Controleer voordat u deze accepteert.',
        },
        formDraft: {
            button_label: 'Forseti vragen dit formulier op te stellen',
            drafting: 'Bezig met opstellen…',
            draft_origin_label: 'Forseti-concept — controleer elk veld',
            tokens_label: '≈ {count} tokens uit uw maandbundel',
            accept_selected: 'Selectie toepassen',
            reject_all: 'Concept afwijzen',
            field_checkbox_label: 'Forseti-concept toepassen voor {field}',
            excluded_note: '{count} veld(en) kunnen niet worden opgesteld (verwijzingen, bijlagen of alleen-lezen).',
            error_generic: 'Forseti kon dit formulier niet opstellen. Probeer het opnieuw.',
            empty_draft: 'Forseti heeft geen veldconcepten voor dit formulier gemaakt.',
        },
        tia: {
            // fix-sprint-20260627-2217-followup F9, verifier round 1 — the 409 refusal
            // when no installed framework grounds the draft (raw English before).
            error_no_grounding_scope: 'Forseti cannot draft this TIA: no framework that governs this transfer is installed with a usable status, so there is no regulatory text to ground the draft on. Nothing was drafted.',
            button_label: 'Vraag Forseti om deze TIA op te stellen',
            drafting: 'TIA opstellen…',
            hint_pick_destination: 'Stel het land van bestemming in het overdrachtsrecord in voordat Forseti kan opstellen.',
            draft_origin_label: 'Forseti TIA-concept — beoordeling per veld',
            tokens_label: '≈ {count} tokens uit uw maandelijkse bucket',
            accept_selected: 'Accepteer geselecteerd',
            reject_all: 'Concept afwijzen',
            field_checkbox_label: 'Forseti-concept toepassen voor {field}',
            error_generic: 'Forseti kon deze TIA niet opstellen. Probeer het opnieuw of verfijn het overdrachtsrecord.',
            usage_details: 'AI-gebruiksdetails',
            applied_status: 'Draft accepted. The selected fields are saved to the transfer record.',
            recommended_updates_title: 'Aanbevolen recordupdates',
            mechanism_recommendation: 'Overdrachtsmechanisme: {value}',
            dpo_review_recommendation: 'Ask your DPO to review this sensitive transfer.',
            review_date_warning: 'Stel een beoordelingstermijn in voordat u deze gevoelige overdracht bijwerkt.',
            processing_activity_warning: 'Koppel ten minste één verwerkingsactiviteit / ROPA-record voordat u de TIA voltooit.',
            go_to_field: 'Ga naar veld',
            adequacy_label: 'Bestemmingsregime',
            adequacy_label_source_route: 'Transfer route',
            adequacy_status_adequacy_decision: 'EU adequacy decision (exports from the EU/EEA)',
            adequacy_status_non_adequacy_with_sccs: 'No EU adequacy decision — GDPR Art. 46 safeguard for exports from the EU/EEA',
            adequacy_status_derogation: 'GDPR Art. 49 derogation (exports from the EU/EEA)',
            adequacy_needs_action: 'Action needed',
            adequacy_for_information: 'For information',
            adequacy_status_transitional: 'Overgangs-/EER-snelkoppeling',
            adequacy_status_unknown: 'Onbekend: stel de bestemming in',
            adequacy_status_source_jurisdiction_route: 'Transfer out of Vietnam — impact-assessment dossier route',
            adequacy_summary_no_destination: 'No destination country is set. Select the destination before Forseti drafts a TIA.',
            adequacy_summary_intra_eea: 'Transfer within the EEA ({code}) — for an export from the EU/EEA, no third-country TIA is required under GDPR Arts. 44–50. A transfer out of Vietnam is assessed under Law 91/2025/QH15 Art. 20 and Decree 356/2025/NĐ-CP Arts. 17–18 unless an evidenced exception applies.',
            adequacy_summary_inbound: 'Vietnam destination — an exporter in the EU/UK establishes its own transfer mechanism; the Vietnam-origin transfer impact-assessment rules do not apply to an inbound route. GDPR applies only where the record establishes an EU/EEA jurisdiction trigger.',
            adequacy_summary_us_dpf: 'US recipient certified under the Data Privacy Framework — for an export from the EU/EEA, covered by the European Commission adequacy decision of 10 July 2023 while the recipient stays on the active DPF list. Confirm the certification at https://www.dataprivacyframework.gov before relying on it.',
            adequacy_summary_us_no_dpf: 'US destination not under the DPF — for an export from the EU/EEA, no EU adequacy decision covers a non-certified recipient, and the Schrems II surveillance-law concerns (FISA §702, EO 12333) call for supplementary measures.',
            adequacy_summary_eu_adequacy: '{code} has an active European Commission adequacy decision — an export from the EU/EEA may proceed without SCCs or supplementary measures, subject to the Commission’s ongoing monitoring.',
            adequacy_summary_eu_no_adequacy: '{code} has no active EU adequacy decision. An export from the EU/EEA may rely on GDPR Art. 46 safeguards (standard contractual clauses, BCRs, certification or a code of conduct) with a Schrems II assessment of the destination, or on a GDPR Art. 49 derogation.',
            adequacy_summary_source_jurisdiction_route: 'Transfer out of Vietnam to {code} — assessed on the route Law 91/2025/QH15 Art. 20 states: the transfer impact-assessment dossier (Decree 356/2025/NĐ-CP Arts. 17–18), unless an exception those instruments state is evidenced. The EU adequacy classification is not used to frame this route; where the record establishes that the GDPR reaches the exporter for this processing (Art. 3), its Chapter V applies in addition.',
            adequacy_summary_source_jurisdiction_route_exemption: 'Transfer out of Vietnam to {code} — assessed on the route Law 91/2025/QH15 Art. 20 states: the transfer impact-assessment dossier (Decree 356/2025/NĐ-CP Arts. 17–18), unless an exception those instruments state is evidenced. A possible exception is flagged from the record; it is not a confirmed exception and needs affirmative evidence and Compliance/Legal review. The EU adequacy classification is not used to frame this route; where the record establishes that the GDPR reaches the exporter for this processing (Art. 3), its Chapter V applies in addition.',
            conflict_banner: 'Let op: {count} Kaderoverschrijdende conflicten zijn van toepassing op dit overdrachtsregime. Controleer voordat u deze accepteert.',
            field_label_description: 'Description',
            field_label_purpose: 'Purpose',
            field_label_transfer_mechanism_detail: 'Transfer mechanism detail',
            field_label_recipient_name: 'Recipient name',
            field_label_recipient_details: 'Recipient details',
            field_label_data_subjects: 'Data subjects',
            field_label_data_types: 'Data types',
        },
        // Sprint 48.3.6 — shared draft output-guardrail labels (TIA + DPIA + all draft workflows).
        guardrails: {
            missing_facts_title: 'Ontbrekende vereiste gegevens',
            missing_facts_hint: 'Forseti markeerde gegevens die niet in het record staan. Velden die ervan afhangen, zijn niet vooraf geselecteerd — vul de gegevens aan of accepteer het veld bewust.',
            risk_label: 'Gekalibreerd risico',
            risk_low: 'Laag',
            risk_medium: 'Gemiddeld',
            risk_high: 'Hoog',
            jurisdiction_label: 'Jurisdictietriggers',
            jurisdiction_none_installed: 'Geen enkel geïnstalleerd kader dekt deze beoordeling — er is geen rechtsstelsel beoordeeld. Installeer een passend regelgevingspakket voordat u vertrouwt op de juridische conclusies in dit concept.',
            jurisdiction_none_assessed: 'Forseti heeft geen jurisdictiebeoordeling teruggegeven. Er is niets beoordeeld — dit is geen bevinding dat geen enkel stelsel van toepassing is.',
            jurisdiction_inactive: 'The framework covering this assessment is installed but no longer in force — so no legal regime was assessed. There is nothing to install: activate the current instrument, or the one that replaces it, before relying on the legal conclusions here.',
            jurisdiction_resolving: 'Namen van de beoordeelde stelsels worden geladen…',
            trigger_applicable: 'Van toepassing',
            trigger_not_applicable: 'Niet getriggerd',
            trigger_unknown: 'Onbekend — gegeven ontbreekt',
            recipient_role_label: 'Rol van de ontvanger',
            recipient_role_controller: 'Verwerkingsverantwoordelijke',
            recipient_role_processor: 'Verwerker',
            recipient_role_joint_controller: 'Gezamenlijke verwerkingsverantwoordelijke',
            recipient_role_unknown: 'Onbekend — niet vermeld',
            opt_in_hint: 'Juridische conclusie — selecteer alleen als u deze accepteert met de beschikbare feiten.',
        },
        mcp: {
            pageTitle: 'MCP-connectorsleutels',
            pageSubtitle: 'Connect Claude, Slack agents and other MCP clients to Forseti. A connected client reads your organisation’s records within the access you grant, and anything it proposes to change waits in the approval queue until an administrator approves it.',
            issueButton: 'Nieuwe sleutel uitgeven',
            issueModalTitle: 'MCP-sleutel API uitgeven',
            labelField: 'Etiket',
            labelHint: 'Een korte beschrijving (bijvoorbeeld "AI capaciteitslaboratorium — regelgevingsonderzoek").',
            permittedToolsField: 'Toegestane gereedschappen',
            expiresAtField: 'Verloopt (optioneel)',
            expiresAtHint: "Laat dit leeg voor 'verloopt nooit'.",
            submit: 'Sleutel uitgeven',
            cancel: 'Annuleer',
            keyDisplayedOnce: 'Kopieer deze sleutel nu. U kunt deze NIET meer zien. Bewaar het in uw geheime manager (1Password, AWS Secrets Manager, Vault, etc.).',
            keyDisplayedOnceClose: 'Ik heb het gekopieerd - sluit',
            colLabel: 'Etiket',
            colPrefix: 'Voorvoegsel',
            colStatus: 'Status',
            colTools: 'Gereedschap',
            colCreatedAt: 'Gemaakt',
            colLastUsed: 'Laatst gebruikt',
            colCalls: 'Oproepen',
            colActions: 'Acties',
            statusActive: 'Actief',
            statusRevoked: 'Ingetrokken',
            statusExpired: 'Verlopen',
            revokeAction: 'Intrekken',
            rotateAction: 'Draaien',
            confirmRevokeTitle: 'Deze MCP-sleutel intrekken?',
            confirmRevokeBody: 'Externe Claude-klanten die deze sleutel gebruiken, verliezen onmiddellijk de toegang. Dit kan niet ongedaan worden gemaakt.',
            confirmRevoke: 'Sleutel intrekken',
            rotatedTitle: 'Sleutel gedraaid',
            rotatedBody: 'De nieuwe leesbare tekstsleutel staat hieronder. De oude sleutel blijft nog 5 minuten werken, zodat eventuele gesprekken tijdens de vlucht worden beëindigd.',
            emptyState: 'Er zijn nog geen MCP-sleutels uitgegeven.',
            errorIssue: 'Kan de MCP-sleutel niet uitgeven.',
            errorList: 'Kan MCP-sleutels niet laden.',
            errorRevoke: 'Kan de sleutel niet intrekken.',
            errorRotate: 'Kan de sleutel niet draaien.',
            neverUsed: '—',
            neverExpires: 'Nooit',
            // Sprint 63.23.0 — the connector page leads with how to connect.
            reloadKeys: 'Reload API keys',
            issuing: 'Issuing…',
            labelRequired: 'Enter a label for the key.',
            toolsRequired: 'Choose at least one tool.',
            webhookField: 'Status callback URL (optional)',
            webhookHint: 'ComplianceOne posts the outcome of each queued write to this address, signed with a secret that is shown once.',
            webhookInvalid: 'Enter a full address, for example https://example.com/hooks/mcp.',
            webhookSecretLabel: 'Webhook signing secret',
            webhookSecretOnce: 'Copy the signing secret now. It is shown once.',
            toolGroupRead: 'Read tools',
            toolGroupWrite: 'Write tools',
            toolGroupWriteHint: 'A write tool never changes a record directly. Each call goes to the approval queue and runs only after an administrator approves it.',
            toolGroupMemory: 'Organisation memory',
            toolGroupSubagent: 'Persona handoff',
            toolQueued: 'goes to the approval queue',
            allToolsInScope: 'Every tool its scopes allow',
            copy: 'Copy',
            copyValue: 'Copy {label}',
            copiedValue: '{label} copied',
            copyFailed: 'Could not copy {label}. Select the value and copy it yourself.',
            apiKeyLabel: 'API key',
            registers: {
                clients: 'OAuth clients',
                grants: 'Connected apps',
                keys: 'API keys',
            },
            connection: {
                title: 'Connection details',
                intro: 'Every client needs the server URL. OAuth clients also use the addresses below; clients that use an API key send it as a bearer token.',
                serverUrl: 'Server URL',
                transport: 'Transport',
                transportStreamableHttp: 'Streamable HTTP',
                protectedResourceMetadataUrl: 'Protected resource metadata URL',
                asMetadataUrl: 'Authorization server metadata URL',
                authorizeUrl: 'Authorize URL',
                tokenUrl: 'Token URL',
                registrationUrl: 'Client registration URL',
                revokeUrl: 'Revocation URL',
                scopes: 'Scopes',
                loading: 'Loading connection details…',
                error: 'The connection details could not be read, so no server URL is shown.',
                reload: 'Reload connection details',
            },
            dcr: {
                label: 'Let clients register themselves',
                hint: 'When this is on, a client such as Claude can register itself and ask a signed-in user to approve it. It has no access until a user of this organisation approves it on the consent screen. Switching it off disconnects apps that registered themselves at their next call; clients registered below and API keys keep working. Switching it back on lets those apps work again.',
                stateOn: 'On — clients can register themselves.',
                stateOff: 'Off — only clients registered below can connect.',
                saving: 'Saving…',
                error: 'Could not change whether clients can register themselves.',
                confirmOffTitle: 'Switch off self-registration?',
                confirmOffBody: 'Apps that registered themselves, such as Claude, are disconnected at their next call for everyone in this organisation, and nobody can approve a new one. Clients registered below and API keys keep working. The connections are suspended, not revoked: switching self-registration back on lets them work again.',
                confirmOff: 'Switch off',
            },
            tabs: {
                label: 'How to connect',
                claude: 'Claude',
                slack: 'Slack and other agent builders',
                apiKey: 'API key',
            },
            claudeTab: {
                step1: 'In Claude, add a custom connector.',
                step2: 'Paste the server URL. Claude finds out how to sign in by itself.',
                step3: 'Sign in to ComplianceOne when asked, then read the consent screen and approve it.',
                consentHeading: 'What the consent screen shows',
                consentItemClient: 'the app’s name and the address it will send you back to;',
                consentItemOrg: 'your organisation;',
                consentItemScopes: 'what it may do — read your organisation’s records, and propose changes for approval if that is requested — and the tools that reach;',
                consentItemOrigin: 'whether the app registered itself or an administrator registered it.',
                actsAsUser: 'Claude then acts as the person who approved it, with no more access than that person’s own role gives them.',
                registrationOff: 'Clients cannot register themselves in this organisation, so Claude cannot connect with the server URL alone. Switch self-registration on, or register a client for Claude under OAuth clients.',
            },
            slackTab: {
                intro: 'Agent builders such as Slack ask for OAuth fields. Register a client under OAuth clients, then copy each value into the builder’s form.',
                colField: 'Field in the agent builder',
                colValue: 'Value',
                fieldServerUrl: 'MCP server URL',
                fieldClientId: 'Client ID',
                fieldClientSecret: 'Client secret',
                fieldAuthorizeUrl: 'Authorize URL',
                fieldTokenUrl: 'Token URL',
                fieldScopes: 'Scopes',
                valueClientId: 'Shown on the client’s row under OAuth clients.',
                valueScopes: 'Enter only the scopes shown on your client’s row under OAuth clients. A scope the client was not registered with is refused.',
                valueClientSecret: 'Shown once, when you register the client or rotate its secret. Store it in the agent builder straight away.',
                redirectNote: 'The agent builder shows the redirect URI it uses. Add it to the client exactly as shown: ComplianceOne sends people back only to a redirect URI registered on the client.',
                registerCta: 'Register a client',
            },
            apiKeyTab: {
                intro: 'Scripts and custom clients can use an API key instead of OAuth. Issue a key under API keys and send it on every request.',
                headerLabel: 'Header',
                configLabel: 'Client configuration (JSON)',
                curlLabel: 'Check the connection with curl',
                actsAsIssuer: 'A call made with a key acts as the administrator who issued it, limited to the tools chosen on the key.',
            },
            clients: {
                errorRevokedClient: 'This client has been revoked, so it has no secret to rotate.',
                paginationLabel: 'OAuth clients pages',
                intro: 'Clients that agent builders use with a client ID and secret. Clients that registered themselves are listed here too.',
                registerButton: 'Register client',
                empty: 'No OAuth clients yet.',
                error: 'The OAuth clients could not be read.',
                reload: 'Reload OAuth clients',
                colName: 'Name',
                colClientId: 'Client ID',
                colType: 'Type',
                colOrigin: 'Registered by',
                colRedirects: 'Redirect URIs',
                colScopes: 'Scopes',
                colTools: 'Tools',
                colLastUsed: 'Last used',
                colStatus: 'Status',
                colActions: 'Actions',
                typeConfidential: 'Confidential',
                typePublic: 'Public',
                originAdmin: 'An administrator',
                originDynamic: 'The client itself',
                statusActive: 'Active',
                statusRevoked: 'Revoked',
                rotateSecret: 'Rotate secret',
                revoke: 'Revoke',
                modalTitle: 'Register OAuth client',
                nameField: 'Name',
                nameHint: 'People see this name on the consent screen.',
                nameRequired: 'Enter a name.',
                typeField: 'Client type',
                typeConfidentialHint: 'Confidential — the client keeps a secret on a server. Use this for Slack and other agent builders.',
                typePublicHint: 'Public — the client cannot keep a secret (a desktop or browser app) and proves itself with PKCE instead.',
                redirectField: 'Redirect URIs',
                redirectHint: 'The exact addresses the client sends people back to. A loopback address such as localhost accepts any port.',
                redirectUriLabel: 'Redirect URI {n}',
                addRedirect: 'Add another redirect URI',
                removeRedirect: 'Remove redirect URI {n}',
                redirectInvalid: 'Enter a full address, for example https://example.com/callback.',
                redirectRequired: 'Add at least one redirect URI.',
                scopesField: 'Scopes',
                scopesRequired: 'Choose at least one scope.',
                toolsField: 'Limit to these tools (optional)',
                toolsHint: 'Leave every box clear to allow each tool the chosen scopes reach.',
                submit: 'Register client',
                saving: 'Registering…',
                errorCreate: 'Could not register the client.',
                createdTitle: 'Client registered',
                secretTitle: 'New client secret',
                secretOnce: 'Copy the client secret now. It is shown once and cannot be shown again; rotate the secret to get a new one.',
                publicNoSecret: 'This is a public client, so it has no secret. It signs in with PKCE.',
                clientIdLabel: 'Client ID',
                clientSecretLabel: 'Client secret',
                confirmRotateTitle: 'Rotate the client secret?',
                confirmRotateBody: 'The current secret stops working now. Put the new secret into the agent builder straight away.',
                errorRotate: 'Could not rotate the client secret.',
                confirmRevokeTitle: 'Revoke this OAuth client?',
                confirmRevokeBody: 'Every app connected through this client loses access now, and its connections are revoked. This cannot be undone.',
                confirmRevoke: 'Revoke client',
                errorRevoke: 'Could not revoke the client.',
            },
            grants: {
                errorNotFound: 'This connection no longer exists; it may already have been revoked.',
                paginationLabel: 'Connected apps pages',
                intro: 'Apps that people in this organisation approved on the consent screen. Revoking one stops its next call.',
                empty: 'No connected apps.',
                error: 'The connected apps could not be read.',
                reload: 'Reload connected apps',
                colApp: 'App',
                colUser: 'Approved by',
                colScopes: 'Scopes',
                colCreated: 'Connected',
                colLastUsed: 'Last used',
                colActions: 'Actions',
                revoke: 'Revoke',
                confirmRevokeTitle: 'Revoke this connection?',
                confirmRevokeBody: 'The app loses access now. The person can connect it again by approving it on the consent screen.',
                confirmRevoke: 'Revoke connection',
                errorRevoke: 'Could not revoke the connection.',
            },
            myApps: {
                errorNotFound: 'This app is no longer connected; it may already have been revoked.',
                title: 'Connected apps',
                intro: 'Apps you have allowed to use ComplianceOne on your behalf. Each acts as you, with no more access than your role gives you.',
                empty: 'You have not connected any apps.',
                error: 'Your connected apps could not be read.',
                reload: 'Reload connected apps',
                colApp: 'App',
                colScopes: 'Scopes',
                colCreated: 'Connected',
                colLastUsed: 'Last used',
                colActions: 'Actions',
                revoke: 'Revoke',
                confirmRevokeTitle: 'Revoke this app?',
                confirmRevokeBody: 'The app loses access now. You can connect it again by approving it on the consent screen.',
                confirmRevoke: 'Revoke app',
                errorRevoke: 'Could not revoke the app.',
            },
        },
        memory: {
            pageTitle: 'Forseti-geheugen',
            pageSubtitle: 'Blader door en beheer de organisatiespecifieke context die Forseti heeft verzameld in gesprekken en beslissingen. Gecureerde inzendingen zijn beveiligd tegen nachtelijke retentie en blijven beschikbaar voor elke persona.',
            filterCategory: 'Categorie',
            filterAllCategories: 'Alle categorieën',
            totalCount: '{count} vermeldingen',
            colAnchor: 'Anker',
            colCategory: 'Categorie',
            colSource: 'Bron',
            colContent: 'Inhoud',
            colCreatedAt: 'Gemaakt',
            colActions: 'Acties',
            editButton: 'Bewerken',
            deleteButton: 'Verwijderen',
            promoteButton: 'Promoot tot samengesteld',
            emptyState: 'Nog geen herinneringen: Forseti verzamelt context terwijl je chat.',
            categories: {
                framework_interpretation: 'Kaderinterpretatie',
                process_preference: 'Procesvoorkeur',
                escalation_threshold: 'Escalatiedrempel',
                decision_context: 'Beslissingscontext',
                glossary_term: 'Verklarende term',
                auto_inferred: 'Automatisch afgeleid',
            },
            sources: {
                admin_curated: 'Samengesteld',
                user_explicit: 'Expliciet',
                auto: 'Afgeleid',
            },
            deleteConfirmTitle: 'Deze herinnering verwijderen?',
            deleteConfirmBody: 'Als u verwijdert, wordt dit anker verwijderd uit elke toekomstige Forseti-chatboot. Anker: {anchor}',
            deleteConfirmLabel: 'Verwijderen',
            deleteCancelLabel: 'Annuleer',
            editModalTitle: 'Geheugen bewerken',
            editAnchorLabel: 'Anker',
            editCategoryLabel: 'Categorie',
            editContentLabel: 'Inhoud',
            editContentCount: '{n}/{max} tekens',
            editEmptyError: 'Inhoud mag niet leeg zijn.',
            editTooLongError: 'Inhoud moet ≤1000 tekens bevatten.',
            editAutoPromoteHint: 'Als u dit automatisch afgeleide geheugen opslaat, wordt het gemarkeerd als door de gebruiker bevestigd en verwijderd uit de retentiecontrole.',
            editCancelButton: 'Annuleer',
            editSaveButton: 'Opslaan',
            editSaving: 'Opslaan…',
            loadFailed: 'The memories could not be loaded.',
            deleteFailed: 'The memory could not be deleted.',
            promoteFailed: 'The memory could not be promoted to curated.',
            saveFailed: 'The memory could not be saved.',
        },
        auditLineage: {
            title: 'Audit-afstamming',
            subtitle: 'Traceer elk AI-gegenereerd artefact dat aan een record is gekoppeld. Chats, tooloproepen, taken, bewijsconcepten en documentversies.',
            filterForm: 'Filters',
            recordType: 'Recordtype',
            recordId: 'Record-ID',
            dateStart: 'Van',
            dateEnd: 'Aan',
            search: 'Zoeken',
            loading: 'Laden…',
            exportPdf: 'Exporteer PDF',
            exportCsv: 'Exporteer CSV',
            nodesHeading: 'Afstammingsknooppunten',
            colType: 'Typ',
            colLabel: 'Etiket',
            colOccurredAt: 'Voorgekomen bij',
            colActor: 'Acteur',
            colId: 'Identiteitskaart',
            noResults: 'Geen adviseuractiviteit voor deze record.',
            draftBadge: 'AI gegenereerd concept',
            prev: 'Vorige',
            next: 'Volgende',
            viewToggleTable: 'Bekijk als tafel',
            viewToggleGraph: 'Bekijk als grafiek',
            graphAriaLabel: 'Afstammingsgrafiek (visueel). Gebruik de tabelweergave voor ondersteunende technologie.',
            graphLegendDraft: 'Concept van AI bewijsmateriaal',
            graphEmpty: 'Geen knooppunten om te tekenen.',
            paginationAriaLabel: 'Paginering',
            exportSuccessTemplate: '{format}: {filename}',
            exportErrorTemplate: 'Exporteren mislukt: {message}',
            truncatedNotice: 'Deze herkomstketen is niet volledig. Het platform leest per bron hooguit een vast aantal rijen en de limiet is bereikt voor: {sources}. Rijen voorbij een limiet, als die er zijn, worden niet getoond. Beperk het datumbereik om ze mee te nemen.',
            truncatedChatLinksNotice: 'This lineage is not complete: only the {limit} newest chats of this record are used to find the tool calls, workflow runs, tasks, evidence and document versions that were started from a chat. Anything reached only through an older chat is not shown, and narrowing the date range does not bring it in.',
            recordTypes: {
                project: 'Project',
                dpia: 'DPIA',
                transfer_assessment: 'Cross-border transfer assessment',
                dossier_filing: 'Dossier filing',
                cross_border_filing: 'Cross-border filing',
                vendor: 'Verkoper',
                incident: 'Voorval',
                incident_op: 'Incidentoperatie',
                rights_request: 'Rechten verzoek',
                contract: 'Contract',
                task: 'Taak',
                data_mapping_activity: 'Activiteit voor het in kaart brengen van gegevens',
                compliance_rule: 'Nalevingsregel',
                program: 'Programma',
            },
            nodeTypes: {
                chat: 'Chatten',
                tool_call: 'Gereedschapsoproep',
                workflow_run: 'Workflow uitgevoerd',
                task: 'Taak',
                evidence: 'Bewijs',
                document_version: 'Documentversie',
            },
            recordIdManualHint: "This record type has no list here. Enter the record's own id, as it appears in the address of its page.",
            recordIdManualLabel: 'Record ID',
            colDetails: 'Details',
        },
        integrityCenter: {
            verdicts: {
                ok: 'Oké',
                drifted: 'Afgedreven',
                hash_mismatch: 'Hasj-mismatch',
                merkle_mismatch: 'Merkle-mismatch',
                source_missing: 'Bron ontbreekt',
                not_found: 'Niet gevonden',
                chain_unreachable: 'Keten onbereikbaar',
                service_unreachable: 'Dienst onbereikbaar',
                pending: 'In afwachting van verankering',
            },
        },
        evidenceDraft: {
            draftBadge: 'AI-gegenereerd DRAFT',
            pendingReviewBadge: 'In afwachting van beoordeling',
            approvedBadge: 'Goedgekeurd',
            rejectedBadge: 'Afgewezen',
            moveToReviewButton: 'Markeer klaar voor beoordeling',
            approveButton: 'Goedkeuren',
            rejectButton: 'Afwijzen',
            rejectReasonLabel: 'Reden van afwijzing',
            humanSignoffBanner: 'Forseti ondersteunt uw oordeel; het vervangt geen bevoegde raadsman. AI-gegenereerd bewijs vereist menselijke goedkeuring voordat het als formeel bewijs geldt.',
            pageTitle: 'AI bewijsconcepten',
            pageSubtitle: 'Beoordeel, keur goed of wijs AI-gegenereerd bewijsmateriaal af. Concepten moeten door een mens worden goedgekeurd voordat ze als formeel bewijs gelden.',
            filterAll: 'Allemaal uitvoerbaar',
            filterDrafts: 'Alleen concepten',
            filterPending: 'In afwachting van beoordeling',
            filterApproved: 'Recent goedgekeurd',
            filterRejected: 'Onlangs afgewezen',
            colId: 'Bewijs-ID',
            colStatus: 'Status',
            colSource: 'Bron',
            colCreatedAt: 'Gemaakt',
            colActions: 'Acties',
            empty: 'Geen AI bewijsconcepten in deze status.',
            confirmApprove: 'Dit door AI gegenereerde bewijsmateriaal als formeel goedkeuren?',
            confirmReject: 'Wijs dit bewijsconcept af. Dit kan niet ongedaan worden gemaakt.',
            reasonRequired: 'Er is een afwijzingsreden vereist.',
            rejectModalTitle: 'Ontwerpbewijsmateriaal afwijzen',
            approveModalTitle: 'Conceptbewijs goedkeuren',
            operationFailed: 'Operatie mislukt. Probeer het opnieuw.',
            moveToReviewSuccess: 'Gemarkeerd als gereed voor beoordeling.',
            approveSuccess: 'Goedgekeurd.',
            rejectSuccess: 'Afgewezen.',
            sourceLabelWorkflowRunTitle: 'Werkstroom: {title}',
            sourceLabelWorkflowRunId: 'Workflowuitvoering {id}',
            sourceLabelChatTitle: 'Chatten: {title}',
            sourceLabelChatId: 'Forseti-chat {id}',
            sourceLabelEmpty: '—',
            filtersAriaLabel: 'Filterstatus',
        },
        workflows: {
            pageTitle: 'AI-workflows',
            pageSubtitle: 'Opgeslagen AI-promptsjablonen die taken en conceptbewijs produceren.',
            newWorkflow: 'Nieuwe workflow',
            newWorkflowHeading: 'Nieuwe AI-workflow',
            tabAll: 'Alle',
            tabBuiltin: 'Ingebouwd pakket',
            tabMine: 'Mijn workflows',
            tabDetail: 'Details',
            tabLinkedTasks: 'Gekoppelde taken',
            builtinBadge: 'Ingebouwd',
            builtinSubtitle: 'Ingebouwde workflow.',
            userSubtitle: 'Aangepaste workflow.',
            emptyHint: 'Nog geen workflows.',
            loadTimeoutError: 'Kon workflows na 10 seconden niet laden. Probeer opnieuw, of controleer het Netwerktabblad van de browser op een vastlopend verzoek.',
            loadError: 'Kon workflows niet laden.',
            hideError: 'Verbergen mislukt.',
            unhideError: 'Tonen mislukt.',
            retry: 'Opnieuw proberen',
            producesTasks: 'Produceert taken',
            producesEvidence: 'Produceert conceptbewijs',
            hideButton: 'Verbergen uit mijn lijst',
            unhideButton: 'Tonen',
            runButton: 'Uitvoeren',
            editButton: 'Bewerken',
            shareButton: 'Delen',
            backToList: 'Terug naar lijst',
            requireTitleAndPrompt: 'Titel en prompt zijn vereist.',
            fieldTitle: 'Titel',
            fieldDescription: 'Beschrijving',
            fieldType: 'Typ',
            fieldPersona: 'Persona-affiniteit',
            fieldPractice: 'Praktijk',
            fieldPrompt: 'Prompt (Markdown)',
            fieldProducesTasks: 'Produceert taken',
            fieldProducesEvidence: 'Produceert conceptbewijs',
            fieldProjectId: 'Project (optioneel)',
            fieldAttachmentIds: 'Bijlagen',
            fieldParentRecord: 'Bovenliggend record (optioneel)',
            fieldPersonaOverride: 'Persona-overschrijving (optioneel)',
            typeAssistant: 'Assistent',
            typeTabular: 'Tabelvormig',
            personaNone: '— geen —',
            parentNone: '— geen —',
            parentIdPlaceholder: 'ID van bovenliggend record',
            selectProjectPlaceholder: 'Selecteer een project…',
            selectProjectFirst: 'Selecteer een project om bijlagen te kiezen.',
            selectAttachmentsPlaceholder: 'Selecteer documenten…',
            noAttachmentsInProject: 'Dit project heeft geen bijgevoegde documenten.',
            selectParentRecordPlaceholder: 'Selecteer een record…',
            selectParentRecordFirst: 'Selecteer eerst een recordtype.',
            noRecordsForType: 'Geen records van dit type beschikbaar.',
            shareUserSelectorLabel: 'Ontvangers (gebruikers)',
            shareGroupSelectorLabel: 'Ontvangers (organisatiegroepen)',
            selectUsersPlaceholder: 'Selecteer gebruikers…',
            selectOrgGroupsPlaceholder: 'Selecteer organisatiegroepen…',
            loadingOptions: 'Laden…',
            loadFailed: 'Kon opties niet laden.',
            parentRecordTypes: {
                dpia: 'DPIA',
                vendor: 'Leverancier',
                incident: 'Voorval',
                rights_request: 'Rechtenverzoek',
                contract: 'Contract',
                data_mapping_activity: 'RoPA-activiteit',
                compliance_rule: 'Compliance-regel',
                program: 'Programma',
            },
            cancelButton: 'Annuleren',
            closeButton: 'Sluiten',
            createButton: 'Aanmaken',
            creating: 'Aanmaken…',
            saveButton: 'Opslaan',
            saving: 'Opslaan…',
            shareWorkflowHeading: 'Workflow delen',
            shareIntro: 'Delen: {title}',
            shareUserIds: "Gebruikers-ID's (gescheiden door spatie of komma)",
            shareGroupIds: "Organisatiegroep-ID's (gescheiden door spatie of komma)",
            shareAllowEdit: 'Ontvangers toestaan te bewerken',
            shareRequireRecipient: 'Geef ten minste één gebruikers-ID of groeps-ID op.',
            shareSuccess: 'Gedeeld.',
            sharing: 'Delen…',
            runWorkflowHeading: 'Uitvoeren: {title}',
            runStarting: 'Starten…',
            runStatusLabel: 'Status: {status}',
            runProducedCounts: 'Taken: {tasks} · Conceptbewijs: {evidence}',
            runPanelHeading: 'Workflowuitvoering',
            runPanelHeadingWithTitle: 'Workflowuitvoering: {title}',
            runViewTasks: 'Gegenereerde taken bekijken',
            runViewEvidence: 'Conceptbewijs bekijken',
            runResultHeading: 'Resultaat van de adviseur',
            runNoResult: 'De uitvoering is afgerond, maar heeft geen te beoordelen resultaat opgeleverd. Er zijn geen taken, bewijzen of bijlagen aangemaakt. Probeer het opnieuw met het vereiste record of de vereiste documenten geselecteerd.',
            runCitationWarning: 'Sommige citaten konden niet automatisch worden geverifieerd. Controleer ze voordat u op dit resultaat vertrouwt.',
            runToolCallWarning: 'Forseti attempted a tool this workflow could not run, and returned the attempt as text. Nothing was produced from it.',
            runNoEnvelopeWarning: 'Forseti returned a result this workflow could not read, so no tasks or evidence were created from it.',
            runNoArtefactsWarning: 'This run completed without creating any tasks or draft evidence. Review the result before treating the work as done.',
            runLoadFailed: 'De workflowuitvoering kon niet worden geladen.',
            runStreamFailed: 'De verbinding met de workflowvoortgang is mislukt.',
            runStatusValues: {
                pending: 'In behandeling',
                running: 'Wordt uitgevoerd',
                completed: 'Voltooid',
                succeeded: 'Geslaagd',
                failed: 'Mislukt',
                cancelled: 'Geannuleerd',
            },
            runProgressStatus: 'Status: {status}',
            runProgressTaskCreated: 'Taak aangemaakt.',
            runProgressEvidenceDrafted: 'Conceptbewijs aangemaakt.',
            runProgressAttachmentGenerated: 'Bijlage gegenereerd.',
            guidedWorkflowTitle: 'Begeleide workflow voor {recordType}',
            guidedWorkflowDescription: 'Gemaakt voor records van het type {recordType}. Beoordeel en pas deze workflow aan voordat u hem opslaat.',
            guidedWorkflowPrompt: 'Beoordeel het aangeleverde record van het type {recordType}, stel ontbrekende feiten vast en stel onderbouwde vervolgstappen voor menselijke beoordeling voor.',
            promptHeading: 'Prompt',
            howItWorksHeading: 'Wat er gebeurt wanneer u deze workflow uitvoert',
            builtinGuidance: 'Forseti beoordeelt de records en documenten die u selecteert, retourneert een resultaat dat de operator kan beoordelen en maakt alleen conceptaken of -bewijzen aan wanneer het resultaat dat ondersteunt. Er wordt niets automatisch goedgekeurd of ingediend.',
            editWorkflowHeading: 'Workflow bewerken',
            linkedTasksHint: 'Tasks created by this workflow show up in the Tasks list, filtered by this workflow’s ID.',
            notFound: 'Workflow niet gevonden.',
            schedules: {
                tabTitle: "Schema's",
                sectionHeading: 'Geplande uitvoeringen',
                empty: "Nog geen schema's. Maak er een aan om deze workflow automatisch uit te voeren.",
                addButton: 'Schema toevoegen',
                addHeading: 'Workflow plannen',
                editHeading: 'Schema bewerken',
                fieldFrequency: 'Frequentie',
                fieldDayOfWeek: 'Dag van de week',
                fieldDayOfMonth: 'Dag van de maand',
                fieldTime: 'Tijd (UTC)',
                fieldTimezone: 'Tijdzone',
                fieldProject: 'Project (optioneel)',
                fieldAttachments: 'Standaardbijlagen',
                fieldPersonaOverride: 'Persona-overschrijving (optioneel)',
                frequencyDaily: 'Dagelijks',
                frequencyWeekly: 'Wekelijks',
                frequencyMonthly: 'Maandelijks',
                weekdaySun: 'Zondag',
                weekdayMon: 'Maandag',
                weekdayTue: 'Dinsdag',
                weekdayWed: 'Woensdag',
                weekdayThu: 'Donderdag',
                weekdayFri: 'Vrijdag',
                weekdaySat: 'Zaterdag',
                statusActive: 'Actief',
                statusPaused: 'Gepauzeerd',
                nextRunLabel: 'Volgende uitvoering',
                lastRunLabel: 'Laatste uitvoering',
                pauseButton: 'Pauzeren',
                resumeButton: 'Hervatten',
                deleteButton: 'Verwijderen',
                triggerButton: 'Nu uitvoeren',
                savingButton: 'Opslaan…',
                saveButton: 'Opslaan',
                triggered: 'Uitvoering gestart — controleer de uitvoeringsgeschiedenis.',
            },
            branching: {
                helpHeading: 'Voorwaardelijke prompt-syntaxis',
                helpBody: 'Use {% if persona.slug == "forseti-legal" %}…{% endif %} to vary the prompt by context. Variables with a value: persona.slug (the persona chosen when the run starts, otherwise the workflow’s persona), org.country and org.locale (your organisation’s country and default language), and project.name (the project the run belongs to). attachment.framework_code, attachment.locale and project.tier are accepted but currently have no source, so {{ }} on them renders empty and a condition on them is always false. Nested {% if %} blocks are not supported.',
            },
            runCancelButton: 'Cancel run',
            runCancelling: 'Cancelling…',
            runCancelRequested: 'Cancellation requested. The model request already under way cannot be recalled, but when it returns its answer is discarded and no tasks, draft evidence or files are created.',
            runCancelledNote: 'This run was cancelled. No tasks, draft evidence or files were created from it.',
            runCancelFailed: 'Could not cancel the run.',
        },
        crossFrameworkConflicts: {
            pageTitle: 'Conflicten tussen kaders',
            pageSubtitle: 'Samengestelde catalogus van waar uw geïnstalleerde regelgevingspakketten op conflicterende manieren met elkaar omgaan. Forseti-oppervlakken; mensen beslissen.',
            noInstalledPacks: 'Er zijn geen regelgevingspakketten geïnstalleerd voor deze organisatie. Installeer ten minste twee pakketten om cross-framework-conflicten aan het licht te brengen.',
            noConflicts: 'Er zijn geen conflicten die overeenkomen met de huidige filters.',
            allAcknowledged: 'Elk zichtbaar conflict is erkend. Leuk werk.',
            columnSeverity: 'Ernst',
            columnTitle: 'Titel',
            columnFrameworks: 'Kaders',
            columnTopic: 'Onderwerp',
            columnStatus: 'Status',
            filterFrameworkPlaceholder: 'Alle kaders',
            filterTopicPlaceholder: 'Alle onderwerpen',
            filterSeverityPlaceholder: 'Alle ernst',
            filterStatusPlaceholder: 'Alle statussen',
            filterStatusAcknowledged: 'Erkend',
            filterStatusUnacknowledged: 'Niet erkend',
            severityCritical: 'Kritisch',
            severityHigh: 'Hoog',
            severityMedium: 'Middelmatig',
            severityLow: 'Laag',
            statusAcknowledged: 'Erkend',
            statusInReview: 'Wordt beoordeeld',
            statusResolved: 'Opgelost',
            statusWontFix: 'Zal niet oplossen',
            statusUnacknowledged: 'Niet erkend',
            drawerSummaryHeading: 'Samenvatting',
            drawerSideAHeading: 'Kant A',
            drawerSideBHeading: 'Kant B',
            drawerRequirementsLabel: 'Vereisten genoemd',
            drawerProvisionsLabel: 'Genoemde bepalingen',
            drawerSuggestedResolutionHeading: 'Voorgestelde resolutie',
            drawerLegalCounselBadge: 'Beoordeling door een juridisch adviseur vereist',
            drawerAckHeading: 'Erkenning',
            drawerAckStatusLabel: 'Status',
            drawerAckAssigneeLabel: 'Toegewezen aan',
            drawerAckDueDateLabel: 'Vervaldatum',
            drawerAckNotesLabel: 'Opmerkingen',
            drawerAckButtonAcknowledge: 'Erken',
            drawerAckButtonUpdate: 'Bijwerken',
            drawerAckButtonUnacknowledge: 'Bevestiging ongedaan maken',
            // fix-sprint-20260912-2123 F23 — R-57: structural parity, EN value carried.
            drawerCloseLabel: 'Close',
            drawerAckAssigneePlaceholder: 'User ID',
            drawerAckSaveFailed: 'Could not save the acknowledgement.',
            drawerAckUnacknowledgeFailed: 'Could not remove the acknowledgement.',
            loadFailed: 'Could not load conflicts.',
            searchPlaceholder: 'Titel of samenvatting zoeken…',
            searchButton: 'Zoeken',
            searchButtonAriaLabel: 'Voer een conflictzoekopdracht uit',
            exportSuccessTemplate: 'CSV: {filename}',
            exportSuccessTemplateXlsx: 'XLSX: {filename}',
            exportErrorTemplate: 'Exporteren mislukt: {message}',
            exportAriaLabel: 'Conflicten exporteren',
            // fix-sprint D1 F23 — the page-level load failure now renders through
            // `withRetry`, which needs the control's label, its busy label and the
            // subject a screen reader hears after it.
            reload: 'Reload',
            reloading: 'Reloading…',
            retrySubject: 'the conflict list',
        },
        tabularReviews: {
            pageTitle: 'Tabellarische beoordelingen',
            pageSubtitle: 'Vergelijk N documenten over M attributen met onderbouwde citaten.',
            newReview: 'Nieuwe beoordeling',
            newReviewHeading: 'Nieuwe tabellarische beoordeling',
            emptyHint: 'Nog geen tabellarische beoordelingen.',
            docsLabel: '{n} documenten',
            colsLabel: '{n} kolommen',
            detailSubtitle: '{docs} documenten · {cols} kolommen',
            addColumn: 'Kolom toevoegen',
            addColumnHeading: 'Kolom toevoegen',
            columnInfo: 'Prompt en formaat tonen',
            columnInfoAria: 'Prompt en formaat voor kolom {name} tonen',
            columnInfoPrompt: 'Prompt',
            columnInfoFormat: 'Retourformaat',
            columnInfoPresets: 'Toegestane waarden',
            columnInfoCitations: 'Citaten',
            columnInfoCitationsRequired: 'Vereist',
            removeColumn: 'Kolom verwijderen',
            removeColumnAria: 'Kolom {name} verwijderen',
            removeColumnConfirm: 'Kolom "{name}" verwijderen? De geëxtraheerde cellen worden verwijderd.',
            deleteReview: 'Verwijderen',
            deleteReviewAria: 'Beoordeling {title} verwijderen',
            deleteReviewConfirm: 'Beoordeling "{title}" verwijderen? Hiermee worden de kolommen, cellen en discussies permanent verwijderd.',
            runAll: 'Alles uitvoeren',
            running: 'Wordt uitgevoerd…',
            cancel: 'Annuleren',
            exportXlsx: 'Excel exporteren',
            exportCsv: 'CSV exporteren',
            exporting: 'Exporteren…',
            backToList: 'Terug naar lijst',
            notFound: 'Tabellarische beoordeling niet gevonden.',
            loadFailed: 'The tabular review could not be read. Reload to try again.',
            emptyPayload: 'The server answered without the tabular review. Reload to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retrySubject: 'this tabular review',
            cancelFailed: 'The run could not be cancelled.',
            removeColumnFailed: 'The column could not be removed.',
            exportFailed: 'The export could not be produced.',
            noDocuments: 'Voeg documenten toe om de beoordeling te starten.',
            noColumns: 'Voeg kolommen toe om te definiëren wat moet worden geëxtraheerd.',
            tableAriaLabel: 'Extractieraster',
            threadsCountAria: '{n} discussiebericht(en)',
            startDiscussionAria: 'Discussie starten',
            docColumn: 'Documenteren',
            statusPending: 'In behandeling',
            statusRunning: 'Wordt uitgevoerd…',
            statusFailed: 'Mislukt: {msg}',
            statusCancelled: 'Geannuleerd',
            citationsCount: '{n} citaat/citaten',
            citationsView: 'Bronpassages bekijken',
            citationPage: 'Pagina {n}',
            titleRequired: 'Titel is vereist.',
            fieldTitle: 'Titel',
            fieldProjectId: 'Project (optioneel)',
            fieldAttachmentIds: 'Bijlagen',
            fieldColumnName: 'Kolomnaam',
            fieldColumnNameHelp: 'Een kort, beschrijvend label voor deze kolom in het extractierooster.',
            columnNamePlaceholder: 'bijv. Startdatum contract',
            fieldExtractionPrompt: 'Extractieprompt',
            fieldExtractionPromptHelp: 'Beschrijf de informatie die u wilt extraheren. Wees specifiek voor betere resultaten.',
            columnPromptPlaceholder: 'Waar moet Forseti in de documenten naar zoeken?',
            fieldReturnFormat: 'Retourformaat',
            fieldReturnFormatHelp: 'Bepaalt hoe de AI het antwoord opmaakt (bijv. als datum of getal).',
            fieldPresets: 'Toegestane waarden (één per regel)',
            fieldPresetsPlaceholder: 'ja\nnee\ngedeeltelijk',
            fieldCitationsRequired: 'Citaat vereist',
            formatShortText: 'Korte tekst',
            formatLongText: 'Lange tekst',
            formatEnum: 'Enum',
            formatNumber: 'Getal',
            formatDate: 'Datum',
            formatList: 'Lijst',
            columnRequireFields: 'Naam en prompt zijn vereist.',
            addColumnError: 'Kolom toevoegen mislukt.',
            cancelButton: 'Annuleren',
            createButton: 'Aanmaken',
            creating: 'Aanmaken…',
            addButton: 'Toevoegen',
            adding: 'Toevoegen…',
            runConfirmHeading: 'Extractie uitvoeren',
            estimateBody: 'Geschat ~{units} factureerbare eenheden ({confidence} vertrouwen).',
            starting: 'Starten…',
            confirmRun: 'Uitvoering bevestigen',
            charts: {
                title: 'Kolomgrafieken',
                tabsAria: 'Grafiekkolommen',
                enumAria: 'Verdeling van enum-waarden',
                numberAria: 'Histogram van getalverdeling',
                dateAria: 'Datumtijdlijn',
                enumEmpty: 'Geen voltooide cellen.',
                numberEmpty: 'Geen numerieke waarden.',
                numberExcluded: '{n} cel(len) uitgesloten (leeg / niet-numeriek / niet voltooid).',
                dateEmpty: 'Geen datumwaarden.',
                dateExcluded: '{n} cel(len) uitgesloten.',
                unsupportedFormat: 'Niet-ondersteund kolomformaat.',
            },
            threads: {
                drawerTitle: 'Discussie',
                loading: 'Laden…',
                empty: 'Nog geen berichten. Start het gesprek.',
                composerLabel: 'Nieuwe opmerking',
                composerPlaceholder: 'Een opmerking toevoegen…',
                post: 'Plaatsen',
                posting: 'Plaatsen…',
                edit: 'Bewerken',
                editLabel: 'Bericht bewerken',
                delete: 'Verwijderen',
                save: 'Opslaan',
                cancel: 'Annuleren',
                close: 'Sluiten',
                editedBadge: 'bewerkt',
                loadError: 'Kon thread niet laden',
                postError: 'Kon niet plaatsen',
                editError: 'Kon niet bewerken',
                deleteError: 'Kon niet verwijderen',
            },
            compare: {
                pageTitle: 'Beoordelingen vergelijken',
                leftReviewLabel: 'Linker beoordeling',
                rightReviewLabel: 'Rechter beoordeling',
                pickReview: 'Kies een beoordeling om te vergelijken',
                noSharedDocuments: 'Deze beoordelingen delen geen documenten — vergelijking vereist ten minste één gemeenschappelijke bijlage.',
                sharedDocumentsHeading: 'Gedeelde documenten',
                leftOnlyHeading: 'Kolommen alleen in de linker beoordeling',
                rightOnlyHeading: 'Kolommen alleen in de rechter beoordeling',
                differs: 'Verschilt',
                identical: 'Identiek',
                emptyValue: '—',
                compareButton: 'Vergelijken met…',
                documentHeading: 'Documenteren',
                errorMissingIds: "Zowel linker als rechter beoordelings-ID's zijn vereist.",
                errorComparisonFailed: 'Vergelijking mislukt.',
                errorLoadFailed: 'Kon beoordelingen niet laden.',
                pickerLoading: 'Laden…',
                pickerNoOtherReviews: 'Geen andere beoordelingen beschikbaar om mee te vergelijken.',
                pickerCancel: 'Annuleren',
            },
        },
        documentReview: {
            pageTitle: 'Documentbeoordeling',
            pageSubtitle: 'Accepteer of wijs AI voorgestelde bewerkingen van dit document af.',
            sidePanelLabel: 'AI rode lijnen',
            sidePanelHeading: 'AI rode lijnen',
            noPendingEdits: 'Geen bewerkingen in behandeling.',
            pendingCount: '{count} in behandeling',
            acceptAllButton: 'Accepteer alles',
            rejectAllButton: 'Alles afwijzen',
            acceptAllTitle: 'Alle openstaande bewerkingen accepteren?',
            acceptAllConfirm: 'Hiermee worden {count} openstaande bewerkingen geaccepteerd en een of meer nieuwe versies gemaakt. Doorgaan?',
            editsHeading: 'Bewerkingen',
            editsEmpty: 'Geen bewerkingen om te beoordelen.',
            versionsHeading: 'Versiegeschiedenis',
            versionsEmpty: 'Nog geen versies.',
            editLabel: 'Bewerken',
            editLabelWithId: 'Bewerken #{id}',
            acceptButton: 'Accepteer',
            rejectButton: 'Afwijzen',
            accepting: 'Accepteren…',
            rejecting: 'Afwijzen…',
            diffDeletedAria: 'Verwijderd',
            diffInsertedAria: 'Ingevoegd',
            statusPending: 'In behandeling',
            statusAccepted: 'Geaccepteerd',
            statusRejected: 'Afgewezen',
            versionLabel: 'Versie',
            versionAriaLabel: 'Versie {n}',
            versionAriaLabelCurrent: 'Versie {n} ({current})',
            versionCurrentTag: 'actueel',
            versionSourceLabel: 'Bron',
            versionCreatedLabel: 'Gemaakt',
            versionModelLabel: 'Model',
            sourceUpload: 'Uploaden',
            sourceUserUpload: 'Gebruikersupload',
            sourceAssistantEdit: 'Assistent bewerken',
            sourceUserAccept: 'Gebruiker heeft bewerking geaccepteerd',
            sourceUserReject: 'Gebruiker heeft bewerking afgewezen',
            sourceGenerated: 'Gegenereerd',
            extractionExtracted: 'Tekst geëxtraheerd',
            extractionFailed: 'Tekstextractie mislukt',
            extractionUnsupported: 'Tekst kon niet worden geëxtraheerd',
            extractionPages: '{n} pagina’s',
            extractionWords: '{n} woorden',
            extractionReasonScanned: 'Dit lijkt een gescand document of een document met alleen afbeeldingen. Forseti kan het nog niet lezen — upload een tekstbestand of plak de tekst rechtstreeks.',
            extractionReasonEncrypted: 'Het bestand is met een wachtwoord beveiligd. Verwijder de beveiliging en upload het opnieuw.',
            extractionReasonMalformed: 'Het bestand kon niet worden geparseerd en is mogelijk beschadigd.',
            extractionReasonParserUnavailable: 'The server’s PDF reader is not installed correctly, so the text could not be read. This says nothing about your file. Ask an administrator to check the installation.',
            extractionReasonTooLarge: 'Het bestand is te groot om te verwerken.',
            extractionReasonTimeout: 'De verwerking duurde te lang. Probeer een kleiner of eenvoudiger bestand.',
            extractionReasonUnknown: 'Er zijn geen verdere details beschikbaar.',
            // Sprint B - Forseti actions on the document review detail page.
            // EN values pending the next 56.x locale fan-out sprint.
            askForsetiButton: 'Ask Forseti',
            runRedlineButton: 'Run redline',
            forsetiModalTitleAsk: 'Ask Forseti — {name}',
            forsetiModalTitleRedline: 'Run redline — {name}',
            forsetiGuidanceAsk: 'Ask Forseti about this document. Pick a starter below or type your own question.',
            forsetiGuidanceRedline: 'Forseti will read this document and propose redline edits. Pick the redline starter below to begin — edits appear in the panel for you to accept or reject.',
            forsetiStarterRedline: 'Read attachment {id} and propose redline edits for legal and compliance issues',
            forsetiStarterSummarise: 'Summarise the key obligations in attachment {id}',
            forsetiStarterRisks: 'Review attachment {id} for legal risks and missing clauses',
            forsetiStarterFrameworks: 'Which compliance frameworks apply to attachment {id}?',
            textPaneLabel: 'Documenttekst',
            textPaneTitle: 'Documenttekst',
            textPaneVersion: 'Versie {n}',
            textPaneEmpty: 'Er is geen tekst beschikbaar voor deze versie. Gescande PDF’s en sommige documenttypen bevatten geen extraheerbare tekst.',
            downloadVersionAria: 'Download version {n}',
            downloadVersionButton: 'Download',
            rejectAllConfirm: 'This rejects {count} pending edits. The reason you give is recorded on each one.',
            rejectAllTitle: 'Reject all pending edits?',
            rejectReasonShown: 'Reason',
            rejectConfirmButton: 'Confirm rejection',
            loadRedlinesFailed: 'Failed to load redlines',
            acceptEditsFailed: 'Failed to accept edits',
            rejectEditsFailed: 'Failed to reject edits',
            batchPartialFailure: '{failed} of {total} failed: {reason}',
            actionFailed: 'Action failed',
            rejectReasonLabel: 'Reason for rejecting (optional)',
        },
        documentReviewIndex: {
            sidebarLabel: 'Documentbeoordeling',
            pageTitle: 'Documentbeoordeling',
            pageSubtitle: 'Documenten in uw organisatie met door AI voorgestelde bewerkingen die wachten op beoordeling, of versies afkomstig van Forseti.',
            colDocument: 'Documenteren',
            colLinkedRecord: 'Gekoppeld record',
            colPendingEdits: 'Openstaande bewerkingen',
            colLastActivity: 'Laatste activiteit',
            colOpen: 'Openen',
            open: 'Openen',
            empty: 'Nog geen documenten onder Forseti-beoordeling. Documenten komen in deze wachtrij wanneer Forseti een bewerking voorstelt tijdens een chat, wanneer een AI-workflow een redline produceert, of wanneer u een document via de bovenstaande knop ter beoordeling stuurt.',
            loading: 'Laden…',
            loadError: 'Kon documenten in beoordeling niet laden.',
            retryButton: 'Opnieuw proberen',
            noLinkedRecord: '—',
            pendingZero: 'Geen',
            sendForReviewButton: 'Een document ter beoordeling sturen',
            sendForReviewTitle: 'Een document ter beoordeling sturen',
            sendForReviewSubtitle: 'Upload een .txt-, .md-, .pdf- of .docx-document (tot 50 MB). Het wordt aan de beoordelingsrij toegevoegd en Forseti voert er een redline-doorloop op uit.',
            sendForReviewTabUpload: 'Nieuw uploaden',
            sendForReviewPickerLabel: 'Bijlagen zoeken',
            sendForReviewPickerPlaceholder: 'Typ om te zoeken…',
            sendForReviewPickerEmpty: 'Geen overeenkomende bijlagen. Probeer een andere zoekopdracht.',
            sendForReviewSubmit: 'Ter beoordeling sturen',
            sendForReviewCancel: 'Annuleren',
            sendForReviewSubmitting: 'Versturen…',
            sendForReviewError: 'Kon het document niet ter beoordeling sturen. Probeer het opnieuw.',
            sendForReviewSuccess: 'Document ter beoordeling verzonden.',
            sendForReviewAcceptedExt: 'Geaccepteerd: {ext}.',
            sendForReviewMaxSize: 'Max. grootte {mb} MB.',
        },
        chats: {
            exportError: 'Export mislukt.',
        },
    },
    pdplEvidencePacks: {
        title: 'PDPL-bewijspakketten',
        subtitle: 'Klantgestuurde indiening bij MoPS A05',
        nav: { emailProviders: 'E-mailproviders' },
        providers: {
            pageTitle: 'E-mailproviders',
            pageSubtitle: 'Configureer de uitgaande mailprovider van uw organisatie voor het indienen van PDPL-bewijspakketten bij MoPS A05.',
            architectureRuleHeading: 'Architectuurregel (vergrendeld)',
            architectureRuleBody: 'AesirX is geen afzender van bewijspakket-e-mails. Elke verzending gebruikt een provider die u zelf bezit en beheert. AesirX verzendt nooit namens u.',
            empty: 'Nog geen providers geconfigureerd.',
            createCta: 'Provider aanmaken',
            statusPendingConsent: 'Wacht op toestemming',
            statusPendingConnectionCheck: 'Wacht op verbindingstest',
            statusActive: 'Actief',
            statusConnectionCheckFailed: 'Verbindingstest mislukt',
            statusDisabled: 'Uitgeschakeld',
            acceptConsentCta: 'Toestemming aanvaarden',
            runConnectionCheckCta: 'Verbindingstest uitvoeren',
            disableCta: 'Uitschakelen',
            disableConfirm: 'Door deze provider uit te schakelen stoppen directe verzendingen totdat een nieuwe provider is geconfigureerd en de toestemming opnieuw is bevestigd. Doorgaan?',
            columnDisplayName: 'Naam',
            columnFromAddress: 'Afzenderadres',
            columnStatus: 'Status',
            columnLastCheck: 'Laatste test',
            columnActions: 'Acties',
            createModalTitle: 'Nieuwe SMTP-provider',
            createDisplayNameLabel: 'Weergavenaam',
            createFromAddressLabel: 'Afzenderadres',
            createReplyToLabel: 'Antwoordadres (optioneel)',
            createSmtpHostLabel: 'SMTP-server',
            createSmtpPortLabel: 'SMTP-poort',
            createSmtpUsernameLabel: 'SMTP-gebruikersnaam',
            createSmtpPasswordLabel: 'SMTP-wachtwoord',
            createSmtpEncryptionLabel: 'Versleuteling',
            createSmtpEncryptionStarttls: 'STARTTLS',
            createSmtpEncryptionTls: 'TLS (impliciet)',
            createSmtpEncryptionNone: 'Geen',
            createOauthM365Tab: 'Microsoft 365',
            createOauthGoogleTab: 'Google Workspace',
            createOauthDisabledBadge: 'Binnenkort beschikbaar',
            createOauthDisabledHint: 'OAuth-providers zijn nog niet ingeschakeld. Gebruik voorlopig SMTP.',
            createSubmitCta: 'Provider aanmaken',
            createCancelCta: 'Annuleren',
            createErrorInvalidFromAddress: 'Het afzenderadres moet een geldige mailbox zijn.',
            createErrorDuplicateFromAddress: 'Er is al een andere provider geconfigureerd voor dit afzenderadres.',
            createErrorGeneric: 'Provider kon niet worden aangemaakt. Controleer het formulier en probeer opnieuw.',
            consentModalTitle: 'Architectuurregel bevestigen',
            consentLocaleToggleEn: 'Engels',
            consentLocaleToggleVi: 'Vietnamees',
            consentAcknowledgeCta: 'Ik bevestig — toestemming aanvaarden',
            consentCancelCta: 'Annuleren',
            consentErrorMismatch: 'De toestemmingstekst is gewijzigd en komt niet meer overeen met de canonieke disclaimer. Open het venster opnieuw en verzend opnieuw.',
            testModalTitle: 'Verbindingstest',
            testRunningCta: 'Verbinding testen…',
            testRunCta: 'Verbindingstest uitvoeren',
            testRetryCta: 'Verbindingstest opnieuw proberen',
            testSuccessHeading: 'Verbinding gelukt',
            testSuccessBody: 'Geverifieerd tegen {host}:{port} in {latencyMs} ms. De provider is klaar voor directe verzendingen.',
            testFailureHeading: 'Verbinding mislukt',
            testFailureBody: 'De test kon niet worden voltooid. Bekijk de reden hieronder en probeer het opnieuw nadat dit is verholpen.',
            testFailureReasonLabel: 'Foutreden',
            testCloseCta: 'Sluiten',
            detailDrawerTitle: 'Providerdetails',
            detailDrawerHistoryHeading: 'Geschiedenis van verbindingstests',
            detailDrawerConsentHeading: 'Toestemmingsbevestiging',
            detailDrawerNoHistory: 'Nog geen verbindingstests vastgelegd.',
            detailDrawerNoConsent: 'Toestemming is nog niet bevestigd.',
            outcomeSuccess: 'Succesvol',
            outcomeFailure: 'Mislukt',
        },
        builder: {
            pageTitle: 'Bewijspakket samenstellen',
            pageSubtitle: 'Upload de 6 deelbestanden, kies het pakkettype en stel de deterministische 8-delige ZIP samen voor indiening.',
            packTypeLabel: 'Pakkettype',
            packTypeDpia: 'Processing impact-assessment pack (notice 02a/02b; report 10)',
            packTypeOtia: 'Cross-border impact-assessment pack (notice 01a/01b; report 09)',
            part01CoverLetter: 'Deel 01 — Begeleidingsbrief (PDF)',
            part02AuthorityForm: 'Deel 02 — Autoriteitsformulier (PDF)',
            part03Supporting: 'Deel 03 — Onderbouwend bewijs (≥1)',
            part04Safeguards: 'Deel 04 — Waarborgen',
            part04SafeguardsRequiredOtia: '(verplicht voor OTIA)',
            part05PriorCorrespondence: 'Deel 05 — Eerdere correspondentie (optioneel)',
            part06Signature: 'Deel 06 — Handtekening- en stempelbewijs (≥1)',
            uploadPickFile: 'Bestand kiezen',
            uploadPickFiles: 'Bestanden kiezen',
            uploadRemoveFile: 'Verwijderen',
            requiredMissing: 'Verplicht onderdeel ontbreekt',
            assembleCta: 'Pakket samenstellen',
            assembling: 'Samenstellen…',
            assembleSuccessHeading: 'Pakket samengesteld',
            assembleSuccessSummary: '{fileCount} bestanden · {bytes} bytes · SHA-256 {shaPrefix}…',
            composeNextCta: 'E-mailconcept opstellen',
            warningsHeading: 'Validatiewaarschuwingen',
            assembleError: 'Pakket kon niet worden samengesteld. Controleer invoer en probeer opnieuw.',
            forsetiAssistHeading: 'Forseti AI-ondersteuning (optioneel)',
            forsetiAssistHint: 'Voer de PDPL Evidence Pack Assembly Advisor uit om ontbrekende onderdelen te belichten, begeleidende brieven op te stellen en beveiligingshiaten te markeren. AesirX is niet de afzender — de AI adviseert alleen; u keurt elk artefact goed.',
            forsetiAssistCta: 'Forseti workflow-bibliotheek openen',
            forsetiAiRunIdLabel: 'Gekoppelde Forseti AI-uitvoering-id (plakken om AI-ondersteuning vast te leggen)',
        },
        draft: {
            pageTitle: 'E-mailconcept',
            pageSubtitle: 'Stel het e-mailconcept op en bewerk het. Direct verzenden komt in een latere release.',
            composeCta: 'Concept opstellen',
            composing: 'Opstellen…',
            noDraftYet: 'Nog geen concept. Stel er een op om te beginnen.',
            localeToggleLabel: 'Taal',
            localeEn: 'Engels',
            localeVi: 'Vietnamees',
            subjectLabel: 'Onderwerp',
            bodyLabel: 'Bericht',
            saveCta: 'Wijzigingen opslaan',
            saving: 'Opslaan…',
            saveError: 'Het concept kon niet worden opgeslagen. Probeer opnieuw.',
            manifestSummaryLabel: 'Manifest-overzicht',
            coverLetterLabel: 'Begeleidingsbrief (PDF)',
            coverLetterDownloadCta: 'PDF downloaden',
            editHistoryHeading: 'Bewerkingsgeschiedenis',
            editHistoryEmpty: 'Nog geen bewerkingen.',
            editHistoryEntry: 'v{version} · {fields} · {when} · {who}',
            unofficialChannelHeading: 'NQ22 onofficieel-kanaal melding',
            uploadSentProofCta: 'Handmatig verzendbewijs uploaden',
            versionLabel: 'Versie',
            statusDraft: 'Concept',
            statusEdited: 'Bewerkt',
            statusFinalized: 'Afgerond',
        },
        sentProof: {
            modalTitle: 'Handmatig verzendbewijs uploaden',
            architectureLockNote: 'AesirX is niet de afzender. Dit bewijs legt de handmatige verzending vast die u via uw eigen mailprogramma hebt uitgevoerd.',
            fileLabel: 'Bewijsbestand (.eml, .png, .jpg)',
            envelopeFromLabel: 'Van',
            envelopeToLabel: 'Aan',
            envelopeSubjectLabel: 'Onderwerp (bij verzending)',
            envelopeSentAtLabel: 'Verzonden op',
            envelopeAttachmentShaLabel: 'Bijlage SHA-256 (bij verzending)',
            uploadCta: 'Bewijs uploaden',
            uploading: 'Uploaden…',
            uploadSuccess: 'Verzendbewijs geüpload.',
            uploadErrorInvalidMime: 'Bestandstype niet toegestaan. Gebruik .eml, .png, .jpg of .jpeg.',
            uploadErrorGeneric: 'Het bewijs kon niet worden geüpload. Probeer opnieuw.',
            existingProofsHeading: 'Geregistreerde verzendbewijzen',
            existingProofsEmpty: 'Nog geen verzendbewijzen geregistreerd.',
            proofEntry: '{when} · {filename}',
        },
        packDetail: {
            pageTitle: 'Bewijspakket',
            notFoundMessage: 'Pakket niet gevonden — het is mogelijk verwijderd of de link is onjuist.',
            manifestHeading: 'Manifest',
            dossierLabel: 'Dossier',
            packShaLabel: 'Pakket-SHA-256',
            fileCountLabel: 'Bestanden',
            bytesLabel: 'Grootte',
            composeDraftCta: 'Concept opstellen',
            sendComingSoonCta: 'Direct verzenden: binnenkort beschikbaar',
            sendComingSoonHint: 'De direct-verzendfunctie is in actieve ontwikkeling. Gebruik tot die tijd "Handmatig verzendbewijs uploaden" op de conceptpagina.',
            sendCta: 'Verzenden via geconfigureerde provider',
            sendDisabledNoProviderHint: 'Configureer eerst een door de klant beheerde e-mailprovider. AesirX verzendt nooit namens u.',
            timelineHeading: 'Verzendgeschiedenis',
        },
        // Sprint 58.4.2 (Phase 25 / Sprint E2) — direct-send + timeline + authority response + hard-copy follow-up.
        send: {
            // Send dialog
            dialogTitle: 'Bewijspakket verzenden',
            providerPickerLabel: 'Geconfigureerde e-mailprovider',
            providerPickerHint: 'AesirX verzendt nooit namens u. Kies hieronder een van uw geconfigureerde providers.',
            providerPickerEmpty: 'Er zijn geen actieve providers geconfigureerd. Configureer er een op de pagina E-mailproviders voordat u verzendt.',
            providerPickerEmptyCta: 'E-mailproviders openen',
            draftPickerLabel: 'E-mailconcept',
            draftPickerOption: '{locale} · v{version}',
            draftPickerEmpty: 'Er zijn nog geen concepten opgesteld voor dit pakket. Stel er eerst een op op de conceptpagina.',
            recipientToLabel: 'Aan',
            recipientToHint: "Een of meer e-mailadressen, gescheiden door komma's.",
            recipientCcLabel: "Cc (gescheiden door komma's)",
            recipientCcHint: "Optioneel. Door komma's gescheiden lijst met e-mailadressen.",
            recipientBccLabel: "Bcc (gescheiden door komma's)",
            recipientBccHint: "Optioneel. Door komma's gescheiden lijst met e-mailadressen.",
            consentReaffirmHeading: 'Toestemming van provider opnieuw bevestigen',
            submitCta: 'Nu verzenden',
            sending: 'Bezig met verzenden…',
            cancelCta: 'Annuleren',
            sendSuccess: 'Verzending geregistreerd. Het pakket is verzonden via uw geconfigureerde provider.',
            sendErrorGeneric: 'Het pakket kon niet worden verzonden. Bekijk de fout hieronder en probeer het opnieuw.',
            sendErrorAlreadyFinalized: 'Dit concept is al gebruikt voor een geslaagde verzending. Stel een nieuw concept op voordat u opnieuw verzendt.',
            sendErrorInvalidRecipients: 'Een of meer ontvangeradressen zijn geen geldige e-mailpostbussen.',
            // Timeline
            timelineEmpty: 'Nog geen verzendingen geregistreerd. Gebruik hierboven "Verzenden via geconfigureerde provider" om te versturen via uw door de klant beheerde SMTP-, Microsoft 365- of Google Workspace-provider.',
            timelineProviderLabel: 'Verzonden via uw geconfigureerde provider',
            timelineSentAtLabel: 'Verzonden',
            timelineRecipientLabel: 'Ontvanger',
            timelineOutcomeSent: 'Verzonden',
            timelineOutcomeFailed: 'Mislukt',
            timelineViewDetailsCta: 'Details bekijken',
            // Authority-response chips
            authorityChipNone: 'In afwachting van antwoord',
            authorityChipAcknowledgement: 'Ontvangstbevestiging',
            authorityChipAcceptance: 'Aanvaarding',
            authorityChipRejection: 'Afwijzing',
            authorityChipSupplementRequest: 'Verzoek om aanvulling',
            authorityChipNoResponse: 'Geen antwoord',
            // Hard-copy chips
            hardCopyChipNone: 'Geen papieren opvolging',
            hardCopyChipPending: 'Verstuurd — bezorging in behandeling',
            hardCopyChipDelivered: 'Bezorgd',
            // Drawer
            drawerTitle: 'Verzendrecord',
            drawerEnvelopeHeading: 'Envelop',
            drawerEnvelopeFromLabel: 'Van',
            drawerEnvelopeToLabel: 'Aan',
            drawerEnvelopeCcLabel: 'Cc',
            drawerEnvelopeBccLabel: 'Bcc',
            drawerEnvelopeSubjectLabel: 'Onderwerp',
            drawerEnvelopeMessageIdLabel: 'Bericht-ID',
            drawerOutcomeHeading: 'Resultaat',
            drawerSnapshotHeading: 'Momentopname van bewaringsketen',
            drawerSnapshotPackShaLabel: 'Pakket-SHA-256 bij verzending',
            drawerSnapshotDraftVersionLabel: 'Conceptversie bij verzending',
            drawerSnapshotAttachmentLabel: 'Verzonden ZIP',
            drawerSnapshotAttachmentDownloadCta: 'ZIP downloaden',
            drawerAuthorityResponseHeading: 'Antwoord van autoriteit',
            drawerHardCopyHeading: 'Papieren opvolging',
            drawerSentByLabel: 'Verzonden door',
            drawerSendIdLabel: 'Verzendrecord-ID',
            drawerSmtpResponseLabel: 'SMTP-antwoord',
            drawerFailureReasonLabel: 'Reden van mislukking',
            drawerLatencyLabel: 'Vertraging',
            drawerLatencyValue: '{ms} ms',
            drawerAcceptedLabel: 'Geaccepteerd',
            drawerRejectedLabel: 'Geweigerd',
            drawerCloseCta: 'Sluiten',
            // Authority-response panel
            authorityRecordCta: 'Antwoord registreren',
            authorityReclassifyCta: 'Herclassificeren',
            authorityClassificationLabel: 'Classificatie',
            authorityReceivedAtLabel: 'Ontvangen op',
            authorityFreeTextLabel: 'Notities (optioneel)',
            authorityEvidenceLabel: 'Bewijsbestand (.eml / .pdf / .png / .jpg — optioneel)',
            authorityReasonLabel: 'Reden voor herclassificatie',
            authorityHistoryHeading: 'Geschiedenis van herclassificaties',
            authorityHistoryEmpty: 'Er zijn geen herclassificaties geregistreerd.',
            authorityHistoryEntry: '{when} · {previous} → {next} · {who}{reason}',
            authoritySubmitCta: 'Antwoord opslaan',
            authoritySubmitting: 'Bezig met opslaan…',
            authorityErrorInvalidMime: 'Bestandstype van bewijs niet toegestaan. Gebruik .eml, .pdf, .png of .jpg.',
            authorityErrorGeneric: 'Het antwoord kon niet worden opgeslagen. Probeer het opnieuw.',
            authorityRecordedAtLabel: 'Geregistreerd op',
            authorityRecordedByLabel: 'Geregistreerd door',
            authorityEvidenceFilenameLabel: 'Bewijs',
            authorityEvidenceDownloadCta: 'Bewijs downloaden',
            // Hard-copy panel
            hardCopyRecordCta: 'Papieren opvolging registreren',
            hardCopyConfirmCta: 'Bezorging bevestigen',
            hardCopyEditNotesCta: 'Bezorgingsnotities bewerken',
            hardCopyPostedAtLabel: 'Verstuurd op',
            hardCopyCourierLabel: 'Koerier',
            hardCopyTrackingLabel: 'Trackingnummer',
            hardCopyRecipientLabel: 'Ontvanger op het etiket',
            hardCopyReceiptLabel: 'Ontvangstbewijs (.pdf / .png / .jpg — optioneel)',
            hardCopyReceiptDownloadCta: 'Ontvangstbewijs downloaden',
            hardCopyDeliveredAtLabel: 'Bezorgd op',
            hardCopyDeliveryNotesLabel: 'Bezorgingsnotities (optioneel)',
            hardCopySubmitCta: 'Opvolging opslaan',
            hardCopySubmitting: 'Bezig met opslaan…',
            hardCopyErrorInvalidMime: 'Bestandstype van ontvangstbewijs niet toegestaan. Gebruik .pdf, .png of .jpg.',
            hardCopyErrorGeneric: 'De opvolging kon niet worden opgeslagen. Probeer het opnieuw.',
        },
    },
    // Sprint 62.53.0 — organisation-profile statements: the recorded attributes'
    // names and the applicability review a statement of "unknown" raises.
    org_profile_statements: {
        attribute_names: {
            data_exploitation_routes: 'Data exploitation routes',
            regulated_entity_types: 'Regulated entity types',
        },
        review_task: {
            title: 'Applicability review: {attribute}',
            description: 'Your organization stated that it does not know its answer for “{attribute}”. The requirements that depend on it await an applicability review and are not treated as out of scope. Record the answer on the Organization profile page.',
        },
        nav_label: 'Organization profile',
        page: {
            title: 'Organization profile',
            description: 'What your organization states about itself: the industry sectors it operates in, the data exploitation routes it takes part in and the regulated-entity role it holds. Your installed frameworks read these statements to decide which of their requirements apply to you. A requirement that depends on an answer you have not given, or have stated as unknown, awaits an applicability review and is never treated as out of scope.',
            sectors_heading: 'Industry sectors',
        },
        section: {
            state_legend: 'Your answer',
            state_selected: 'Selected',
            state_selected_help: 'One or more of the options below apply to your organization. Tick each one that does.',
            state_none: 'None of these',
            state_none_help: 'None of the options apply. Requirements that depend only on them read as out of scope.',
            state_unknown: 'Unknown — review needed',
            state_unknown_help: 'You do not know yet. An applicability review is added to your task list, and the requirements that depend on this answer are never treated as out of scope.',
            values_legend: 'Options that apply',
            values_disabled_hint: 'Choose “Selected” to pick the options that apply.',
            values_required: 'Choose at least one option, or choose another answer.',
            used_by_label: 'Used by',
            used_by_none: 'No installed framework names this option.',
            used_by_item: '{framework} ({count} requirements)',
            used_by_item_one: '{framework} (1 requirement)',
            used_by_item_reference: '{framework} ({count} requirements, installed for reference only)',
            used_by_item_reference_one: '{framework} (1 requirement, installed for reference only)',
            note_label: 'Note',
            note_hint: 'Optional. The basis for this answer, in your own words (up to {max} characters).',
            evidence_heading: 'Evidence',
            evidence_hint: 'Files that support this answer. A file named by a saved statement is kept for as long as the statement history exists.',
            evidence_none: 'No evidence attached.',
            evidence_not_held: 'File no longer held',
            evidence_upload_label: 'Attach evidence',
            evidence_unbind: 'Remove from statement',
            evidence_unbind_label: 'Remove {filename} from this statement',
            evidence_unbind_not_held_label: 'Remove the file that is no longer held from this statement',
            evidence_limit: 'A statement can name at most {max} files.',
            current_none: 'No statement recorded yet. Requirements that depend on this answer await an applicability review.',
            current_revision: 'Current statement: revision {revision}, recorded {date}.',
            save: 'Save statement',
            saving: 'Saving…',
            reset: 'Reset',
            saved: 'Statement saved.',
            save_failed: 'The statement could not be saved.',
            conflict: 'Someone else changed this statement while you were editing it (you read revision {expected}; it is now revision {current}). Nothing was saved, and your changes are still on screen.',
            reload_current: 'Reload the current statement',
            evidence_not_held_error: 'Some evidence files named in this statement are no longer held ({count}). Remove them from the statement and save again.',
            load_failed: 'This statement could not be read.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the statement for {attribute}',
            read_only: 'You can view this statement but not change it.',
            history_show: 'Show statement history ({count})',
            history_hide: 'Hide statement history',
            history_caption: 'Statement history, newest first',
            history_revision: 'Revision',
            history_date: 'Recorded',
            history_answer: 'Answer',
            history_values: 'Options',
            history_note: 'Note',
            history_evidence: 'Evidence files',
            history_none: '—',
        },
        attribute_descriptions: {
            data_exploitation_routes: 'The routes by which your organization takes part in the exploitation of data held or offered by the State. Requirements that depend on a route apply only when you select it.',
            regulated_entity_types: 'The regulated-entity role your organization holds in the credit-cooperative and deposit-protection system, if any. Requirements addressed to a role apply only when you select it.',
        },
        value_labels: {
            data_exploitation_routes: {
                public_data_access_request: 'Requesting access to State-held data',
                state_data_centre_exploitation: 'Exploiting data at a State data centre',
                state_data_incubator: 'Taking part in a State data incubator',
                data_controlled_testing: 'Controlled testing of a data solution',
                state_data_centre_data_sharing: 'Sharing data with a State data centre',
                state_data_centre_investment: 'Investing in or sponsoring a State data centre',
                foreign_party_data_cooperation: 'Data cooperation with a foreign party',
                state_data_incentive_application: 'Applying for a State data incentive',
            },
            regulated_entity_types: {
                credit_cooperative: 'People’s credit fund',
                cooperative_central_bank: 'Cooperative bank',
                deposit_insurer: 'Deposit insurer',
                microfinance_institution: 'Microfinance institution',
            },
        },
        value_help: {
            data_exploitation_routes: {
                public_data_access_request: 'Your organization requests access to, or the exploitation of, data held by State bodies through a formal request.',
                state_data_centre_exploitation: 'Your organization exploits data that a State data creation and exploitation centre makes available.',
                state_data_incubator: 'Your organization takes part in a data incubation programme the State runs.',
                data_controlled_testing: 'Your organization tests a data product, service or solution under a controlled-testing mechanism.',
                state_data_centre_data_sharing: 'Your organization supplies or shares data with a State data centre, without exploiting the centre’s data.',
                state_data_centre_investment: 'Your organization invests in, contributes assets to or sponsors a State data centre, without exploiting the centre’s data.',
                foreign_party_data_cooperation: 'Your organization cooperates with a foreign organization or individual in these data activities.',
                state_data_incentive_application: 'Your organization applies for, or receives, a State incentive or support for data activities. Applying is not exploiting data.',
            },
            regulated_entity_types: {
                credit_cooperative: 'Your organization is a credit institution organised as a cooperative that takes deposits from and lends to its members.',
                cooperative_central_bank: 'Your organization is the cooperative bank of the credit-cooperative system.',
                deposit_insurer: 'Your organization insures the deposits held at credit institutions.',
                microfinance_institution: 'Your organization is a microfinance institution.',
            },
        },
        review: {
            title: 'Applicability review',
            description: 'Requirements of your installed frameworks that wait on an answer on this page. Each one applies, or is out of scope, once the answer is recorded; until then it is not treated as out of scope.',
            state_not_stated: 'No statement recorded',
            state_selected: 'Selected',
            state_none: 'None of these',
            state_unknown: 'Unknown — review needed',
            group_state: 'Current answer: {state}',
            group_count: '{count} requirements await this answer.',
            group_count_one: '1 requirement awaits this answer.',
            group_empty: 'No requirement of an installed framework awaits this answer.',
            group_empty_reference: 'No requirement of a directly applicable framework awaits this answer.',
            reference_intro: 'These frameworks are installed for reference only, so their requirements are not applied to your organization. If they were installed as directly applicable, these requirements would await this answer:',
            reference_item: '{framework} ({count} requirements)',
            reference_item_one: '{framework} (1 requirement)',
            column_framework: 'Framework',
            column_requirement: 'Requirement',
            load_failed: 'The applicability review could not be read.',
            retry_subject: 'the applicability review',
        },
        outcome: {
            column: 'Applies to your organization',
            applicable: 'Applies',
            out_of_scope: 'Out of scope',
            not_applicable: 'Reference only — not assigned to any organization',
            installed_for_reference: 'Installed for reference only — not applied to your organization',
            not_entitled: 'Not in your package — not applied to your organization',
            not_declared: 'Not declared',
            profile_incomplete: 'Awaiting your organization profile',
            profile_incomplete_named: 'Awaiting your organization profile: {attributes}',
            attributes: {
                sector: 'industry sectors',
                data_exploitation_routes: 'data exploitation routes',
                regulated_entity_types: 'regulated entity types',
            },
        },
    },
    // Sprint 63.37.0 (E0, E5) — English until the Phase 25 locale pass (R-57).
    org_identity: {
        nav_label: 'Organisation identity',
        module_guide: 'Organisation identity: the legal name, offices, registration, tax code, contacts and legal representative that official forms and cover letters are filled from.',
        title: 'Organisation identity',
        description: "Record your organisation's legal name, offices, registration, tax code, contacts and legal representative once. Official forms are filled from this record when you choose Fill from record, and cover letters read it too.",
        section_names: 'Names and offices',
        section_registration: 'Registration',
        section_contact: 'Contact details',
        section_representative: 'Legal representative',
        field_legal_name: 'Legal name',
        field_legal_name_help: 'The name exactly as it appears on the registration certificate.',
        field_trading_name: 'Trading name',
        field_trading_name_help: 'A short or trading name, if the organisation uses one.',
        field_head_office_address: 'Head office address',
        field_transaction_office_address: 'Transaction office address',
        field_transaction_office_address_help: 'Leave blank if it is the same as the head office.',
        field_registration_number: 'Registration Number',
        field_registration_instrument: 'Registration document',
        field_registration_instrument_help: 'The document that records the registration, for example a business registration certificate or an establishment decision.',
        field_registration_issuer: 'Issued by',
        field_registration_issue_date: 'Date of issue',
        field_registration_issue_place: 'Place of issue',
        field_tax_code: 'Tax Code',
        field_tax_code_help: 'The tax code is recorded separately from the registration number, even where the two are the same.',
        field_phone: 'Phone',
        field_email: 'Email',
        field_website: 'Website',
        field_website_help: 'Starting with https:// or http://.',
        field_legal_representative_name: 'Full name',
        field_legal_representative_title: 'Position',
        save: 'Save identity',
        saving: 'Saving…',
        saved: 'Organisation identity saved.',
        save_failed: 'The organisation identity could not be saved.',
        fix_fields: 'Some fields need correcting before the identity can be saved.',
        load_failed: 'The organisation identity could not be read.',
        retry: 'Retry',
        conflict_message: 'Someone else saved the organisation identity after you opened this page. Your changes were not saved. Reload the saved identity, then make your changes again.',
        conflict_reload: 'Reload the saved identity',
        conflict_reloaded: 'The saved identity is shown. Make your changes again, then save.',
        loading: 'Loading the organisation identity',
        read_only: 'You can view the organisation identity. Saving it needs the settings write permission.',
        restricted_title: 'You do not have access to the organisation identity',
        restricted_message: 'Ask an administrator for the settings read permission.',
        last_saved: 'Last saved {date}',
        never_saved: 'Nothing has been recorded yet. Fields left blank are left blank on forms too.',
        error_too_long: 'This value is too long.',
        error_invalid_date: 'Enter a real date.',
        error_invalid_email: 'Enter an email address, for example name@example.com.',
        error_invalid_url: 'Enter a web address starting with https:// or http://.',
        error_invalid_phone: 'Use digits, spaces and + ( ) . - only.',
        error_invalid: 'This value is not accepted.',
    },
    form_prefill: {
        draft_undeclared: "The form does not say where {count} of the draft's fields go, so they were not drafted.",
        fill_from_record: 'Fill from record',
        fill_from_named_record: 'Fill from {record}',
        filling: 'Filling…',
        result: 'Filled {filled} fields; {differs} differ from the record.',
        failed: 'The form could not be filled from the record.',
        from_source: 'From {source}',
        not_recorded_in_source: 'Not recorded in {source}',
        family_organisation: 'the organisation identity',
        family_dpo: 'the DPO appointment',
        family_dpia: 'the DPIA',
        family_transfer: 'the transfer assessment',
        family_incident: 'the incident',
        family_dossier: 'the dossier filing',
        record_generic: 'the record',
        hint: 'Fills empty fields only. A value you entered is never replaced.',
    },
    // Owner rulings 2026-10-26 — idea #717 and OQ-86-6. English until the
    // Phase 25 locale work (R-57); structural parity with en.ts.
    customs_tax_exports: {
        titles: {
            dossiers: 'Customs tax dossiers and documents received',
            payments: 'Customs tax payments',
            duty_free_lists: 'Duty-free lists',
            examinations: 'Customs tax examinations',
            debt_instruments: 'Customs tax debt instruments',
            transition_statement: 'Transition statement',
        },
        sections: {
            dossiers: 'Dossiers',
            documents_received: 'Documents received',
            figures_side_by_side: 'Stated figures side by side',
            deductions: 'Deductions as customs recorded them',
            usage_notifications: 'Usage notifications recorded',
            steps: 'Steps',
            office_examinations: 'At the customs office',
            determinations: 'Determinations recorded',
            refund_records: 'Refund records',
            enforcement_notices: 'Enforcement notices received',
            instalment_payments: 'Payments recorded against instalment notices (each as its document states it; no total)',
            noted_alternatives: 'Noted alternative readings of the usage schedule',
        },
        columns: {
            paper_form_number: 'Paper form number',
            electronic_indicator_set: 'Electronic indicator set',
            matched_by_title: 'Matched by title',
            issued_by_authority: 'Issued by the authority',
            dossier: 'Dossier',
            procedure: 'Procedure',
            register_role: 'Register role',
            customs_records: 'Customs records',
            channel: 'Channel',
            signed_off_on: 'Signed off on',
            lodged_on: 'Lodged on',
            confirmation_answer: 'Confirmation answer',
            install_mode_when_made: 'Install mode when made',
            review_approval: 'Review approval',
            document: 'Document',
            kind: 'Kind',
            number_as_printed: 'Number as printed',
            received_on: 'Received on',
            stated_figures: 'Stated figures',
            stated_deadlines: 'Stated deadlines',
            reply: 'Reply',
            payment: 'Payment',
            paid_on: 'Paid on',
            stated_amount: 'Stated amount',
            payer_class: 'Payer class',
            payment_date_basis: 'Payment-date basis',
            document_number: 'Document number',
            pays: 'Pays',
            receipt: 'Receipt',
            received_document: 'Received document',
            figure: 'Figure',
            payment_figure_as_stated: 'Payment figure (as stated)',
            document_figure_as_stated: 'Document figure (as stated)',
            comparison: 'Comparison',
            closed: 'Closed',
            list: 'List',
            reference: 'Reference',
            acknowledged_on: 'Acknowledged on',
            imports_ended_on: 'Imports ended on',
            expires_on: 'Expires on',
            deduction: 'Deduction',
            customs_record: 'Customs record',
            goods_as_stated: 'Goods as stated',
            unit_as_stated: 'Unit as stated',
            cleared_as_stated: 'Cleared (as stated)',
            remaining_as_stated: 'Remaining (as stated)',
            notification: 'Notification',
            notice_kind: 'Notice kind',
            financial_year_ended_on: 'Financial year ended on',
            notified_on: 'Notified on',
            examination: 'Examination',
            purpose: 'Purpose',
            decision_signed_on: 'Decision signed on',
            decision_sent_on: 'Decision sent on',
            announced_on: 'Announced on',
            re_examination_of: 'Re-examination of',
            step: 'Step',
            occurred_on: 'Occurred on',
            refused_to_sign: 'Refused to sign',
            record: 'Record',
            authority_request: 'Authority request',
            explanation_sent_on: 'Explanation sent on',
            outcome_on: 'Outcome on',
            instrument: 'Instrument',
            role: 'Role',
            effective_on: 'Effective on',
            instalment_band: 'Instalment band',
            approved_on: 'Approved on',
            stated_debt_figure: 'Stated debt figure',
            guarantee_valid_until: 'Guarantee valid until',
            record_type: 'Record type',
            governing_date: 'Governing date',
            outcome: 'Outcome',
            reading: 'Reading',
            open: 'Open',
            determination: 'Determination',
            determined_on: 'Determined on',
            refund_record: 'Refund record',
            dossier_register_role: 'Dossier register role',
            classification: 'Classification',
            decision_document: 'Decision document',
            offset_notice_document: 'Offset notice received',
            post_refund_examination: 'Post-refund examination',
            stated_case: 'Case as the decision states it',
            stated_case_source: 'Case stated in',
            stated_case_citation: 'Provision cited for the case',
            received_as: 'Received as',
            document_date: 'Document date',
            amount_stated_for_notice: 'Amount stated for this notice',
            financial_year_reported: 'Financial year reported (ending)',
            noted_alternative_year: 'Noted alternative financial year (ending)',
            note: 'Note',
            reading_kind: 'Kind of reading',
            classification_case: 'Case the classification notice states',
            acceptance_notice: "Acceptance notice (the authority's clock)",
            handling_time: "Handling time (the authority's clock)",
            stated_case_choices: 'Installed cases matched (as stated)',
            stated_due_on: 'Due date as stated',
            period_ceiling: 'Longest instalment period',
            stated_plan_end: 'Latest end the Law allows',
            enforcement_from: "Enforcement threshold (the authority's clock)",
        },
        cells: {
            review_approved: 'Approved by {name} on {date}',
            review_approved_earlier_version: 'Approved by {name} on {date}, against an earlier version of the record',
            register_role_refund: 'Refund',
            register_role_overpaid_refund: 'Refund of an overpaid amount',
            noted_not_due: 'Noted reading, not a due date',
            received_as_third_party: 'Received as a third party',
            received_as_enforced_taxpayer: 'The taxpayer under enforcement',
            clock_authority_ends: "The authority's clock ends {date} ({clock})",
            clock_authority_ends_before_excluded: "The authority's clock ends {date} before the time not counted is added ({clock})",
            clock_authority_no_date: "The authority's clock; no date, because the event it runs from is not recorded ({clock})",
            clock_period_ends: 'The period ends {date} ({clock})',
            clock_closed: 'Closed on {date}',
            clock_not_applicable: 'Not applicable to this record ({clock})',
            clock_not_live: 'Not in force yet ({clock})',
            clock_classification_not_recorded: 'No date: the classification the authority notified is not recorded',
            clock_not_declared: 'No installed clock declares it',
            clock_instrument_not_installed: 'Stated in an instrument that is not installed ({instruments}); no date',
            clock_no_date: 'No date ({clock})',
            plan_end_by_ceiling: "{date}, the end of the longest period; a shorter schedule in the written acceptance is the plan's own",
            plan_end_by_guarantee: "{date}, the end of the guarantee's validity, which comes first; a shorter schedule in the written acceptance is the plan's own",
            clock_not_counted_before_commencement: 'Not counted by the platform: debts outstanding at the end of {outstanding} follow the Law ({reference}), but no point is shown for a due date before {activation}',
            clock_not_counted_before_commencement_unnamed: 'Not counted by the platform: no point is shown for a due date before {activation}',
            meta_boundary: 'Boundary ({framework}): {summary}',
            meta_boundary_none: 'Boundary: no installed framework declares a boundary statement for these registers.',
            meta_amounts_as_stated: 'Every amount is a figure a person entered from a document; nothing here is computed.',
            meta_statement_for: 'Statement for: {date}',
            meta_nothing_remains: 'Nothing remains open under the earlier rules.',
            meta_records_remain: 'Records remain open under the earlier rules.',
            meta_transition_unavailable: 'The transition reading is not available on this installation.',
            role_notified_debt: 'Tax debt as notified',
            role_freeze: 'Freeze',
            role_freeze_end: 'End of a freeze',
            role_write_off: 'Write-off',
            role_restoration: 'Restoration',
            role_instalment_request: 'Instalment request',
            role_instalment_notice: 'Instalment notice',
            received_document_label: '{kind} {number}, dated {date}',
            received_document_label_no_number: '{kind}, received {date}',
            case_no_longer_installed: 'A case from an instrument that is no longer installed',
        },
    },
    cross_border_filing_fence: {
        register_customs_tax_dossiers: 'Tax Dossiers and Received Documents',
        refused: 'This form is recorded in {register}, not as a cross-border filing. Record the dossier there.',
        open_register: 'Open {register}',
        option_owned_elsewhere: '{form} — recorded in {register}',
        picker_hint: 'Some installed forms are recorded in {register} and cannot be chosen here.',
        advisory_title: 'This form is now recorded in {register}',
        advisory_body: 'This filing was recorded here before forms like it moved to {register}. It stays readable here and has not been changed. Record new dossiers on this form in {register}.',
        list_marker: 'Now recorded in {register}',
    },
    // Sprint 63.43.0 — English for key parity until Phase 25 (R-57; sprint 57.7.0).
    sector_overlays_module: {
        nav: {
            overview: 'Overview',
        },
        overview: {
            title: 'Sector Overlays',
            subtitle: 'What your active sector overlays bring into use, grouped by sector.',
            loading: 'Loading sector overlays…',
            load_failed: 'Sector overlays could not be loaded.',
            not_entitled_title: 'Sector Overlays is not in your package',
            not_entitled_body: "Sector Overlays is an Enterprise module. Your organization's package does not include it, so no sector overlay is shown here.",
            empty_title: 'No sector overlay is active',
            empty_body: 'Install an overlay in the Overlay Catalog and choose Direct to bring its views into use here. An overlay installed for reference only adds no views.',
            open_catalog: 'Open the Overlay Catalog',
            organization_profile: 'Organization profile',
            organization_profile_hint: "Your organization's sectors decide which overlays apply to you directly.",
            provided_by: 'Provided by',
            views_heading: 'Views in this sector',
            shared_views_heading: 'Shared views this sector uses',
            shared_view_location: 'In {location}',
            open_view_aria: '{view} — {location}',
            no_visible_views: "None of this sector's views is open to your role.",
            sector_count_one: '1 overlay active',
            sector_count: '{count} overlays active',
        },
    },
    evidence_reports: {
        intendedFilingReadinessHint: 'A prospective filing date is required for statutory readiness for a new dossier. You may leave it blank to save a draft or gap report. An existing dossier filing can supply the basis.',
        captureUnconfirmed: 'Capture may still be running. Retry this same capture to check or recover it; a completed version is reused.',
        retryCapture: 'Check or retry this capture',
        sampleEvidenceLimitation: 'Required content may be complete, but admitted evidence is synthetic sample data and cannot establish readiness for statutory filing. Internal or training exports remain available.',
        referenceRendition: 'Reference rendition',
        languagePreservationHint: 'Language selection changes headings and labels. Saved answers and evidence retain their authored language. This output does not translate or certify the supplied text.',
        title: 'Prepare an evidence report',
        intro: 'Define the question and scope, review actual records, and freeze an attributable report. Blank templates do not demonstrate completed work.',
        legacy: 'Legacy catalog export',
        legacyHint: 'Catalog exports contain framework references. They are not reviewed evidence reports.',
        name: 'Report title',
        framework: 'Installed framework',
        purpose: 'Purpose',
        internal_readiness: 'Internal readiness review',
        authority_request: 'Response to an authority request',
        processing_assessment: 'Processing assessment dossier',
        transfer_assessment: 'Transfer assessment dossier',
        dossier_update: 'Dossier update',
        assessmentKind: 'Assessment type',
        dpia: 'Processing assessment',
        otia: 'Transfer assessment',
        applicant: 'Applicant',
        organization: 'Organization',
        individual: 'Individual',
        role: 'Organization role',
        controller: 'Controller',
        controller_processor: 'Controller and processor',
        processor: 'Processor',
        other: 'Other',
        coverage: 'Coverage',
        bounded: 'Selected activity / bounded scope',
        organization_complete: 'Complete organization and processing lifetime',
        coverageStatement: 'Scope and coverage statement',
        coverageHint: 'State what this report covers and excludes. A selected activity does not establish organization-wide statutory coverage.',
        recipient: 'Intended recipient',
        questions: 'Request questions (one per line)',
        language: 'Report language',
        en: 'English',
        vi: 'Vietnamese',
        asOf: 'Evidence as of',
        timezone: 'IANA timezone',
        periodStart: 'Period start (optional)',
        periodEnd: 'Period end (optional)',
        intendedFiling: 'Intended filing date',
        filing: 'Existing dossier filing',
        sources: 'Select actual source records',
        sourceKind: 'Source type',
        sourceSearch: 'Search authorized source records',
        sourceHint: 'Only existing records you may read are offered. Select none for an honest gap report. Search or change page to reach further records.',
        search: 'Search',
        next: 'Next page',
        previous: 'Previous page',
        remove: 'Remove',
        review: 'Review evidence and requirements',
        preview: 'Refresh review and preview',
        freeze: 'Freeze report and generate files',
        busy: 'Working…',
        failed: 'The request failed. Your entries have been retained.',
        required: 'Complete the required fields before continuing.',
        emptySources: 'No source records selected. Missing content remains explicit.',
        candidate: 'Source evidence',
        noCandidates: 'No accessible evidence supplied. This report contains gaps, not proof of completed work.',
        openSource: 'Open source record',
        inspect: 'Inspect captured content',
        sourceRevision: 'Source revision',
        state: 'Source state',
        include: 'Include',
        exclude: 'Exclude',
        decision: 'Evidence decision',
        reason: 'Reason for decision',
        requirementLinks: 'Requirements supported',
        requirements: 'Requirement assessment',
        applicability: 'Applicability',
        applicable: 'Applicable',
        not_applicable: 'Not applicable',
        undetermined: 'Undetermined',
        adequacy: 'Content adequacy',
        unreviewed: 'Unreviewed',
        adequate: 'Adequate',
        partial: 'Partial',
        unsupported: 'Unsupported',
        missing: 'Missing content',
        present: 'Content present',
        supported: 'Supported requirements',
        blockers: 'Mandatory blockers',
        ready: 'Ready for selected purpose',
        notReady: 'Not ready for selected purpose',
        statutoryReady: 'Statutory content readiness',
        readinessHint: 'Content readiness, internal approval, dispatch, official receipt and authority outcome are separate decisions. An adverse finding can be part of a complete assessment.',
        diagnostics: 'Profile and source limitations',
        supplement: 'Supplemental assessment content',
        conclusions: 'Assessment conclusions and residual risks',
        limitations: 'Limitations and unresolved questions',
        disclosure: 'Release decision',
        full: 'Release full reviewed content',
        redacted_release: 'Release selected evidence only',
        releaseReason: 'Reason and recipient disclosure review',
        releaseEvidence: 'Evidence permitted in the released copy',
        gapTask: 'Assign gap action',
        taskTitle: 'Action title',
        assignee: 'Responsible person',
        dueDate: 'Due date',
        createTask: 'Create task',
        taskCreated: 'Gap action created',
        openTask: 'Open task',
        taskHint: 'Completing a task does not establish adequate evidence. Return and refresh after the source record has been corrected.',
        previewTitle: 'Readable report preview',
        version: 'Version',
        snapshot: 'Frozen report',
        capturedAt: 'Captured at',
        capturedBy: 'Captured by',
        reviewedBy: 'Reviewed by',
        manifestHash: 'Snapshot manifest hash',
        files: 'Report and evidence files',
        download: 'Download',
        noFiles: 'Files are not available for this version.',
        newVersion: 'Revise as a new version',
        frozenHint: 'This is the frozen version. Changes require a new preview and version; existing approval applies only to its exact version.',
        pending: 'Files pending',
        finalized: 'Files finalized',
        failedState: 'File generation failed',
        sourceContent: 'Captured source content',
        attachments: 'Source attachments',
        close: 'Close',
        saveReview: 'Apply review to preview',
        profile: 'Assessment profile',
        noProfile: 'No supported assessment profile is installed for this selection.',
        returnReview: 'Return to review',
        create: 'Create report',
        cancel: 'Cancel',
        dpiaSource: 'Processing assessments',
        transfer_assessmentSource: 'Transfer assessments',
        processing_activitySource: 'Processing activities',
        systemSource: 'Systems',
        vendorSource: 'Vendors',
        consentSource: 'Consent records',
        rights_requestSource: 'Rights requests',
        incidentSource: 'Incidents',
        form_instanceSource: 'Completed / draft form instances',
        attachmentSource: 'Attachments',
        evidenceSource: 'Evidence records',
        selected: 'Selected source records',
        sourceUnavailable: 'Source unavailable or permission restricted',
        supplementHint: 'Provide the missing substantive assessment content. Empty content remains a gap.',
        choose: 'Choose…',
        page: 'Page',
        exemption: 'Assert an exemption',
        exemptionReason: 'Exemption grounds and supporting evidence',
        approvalStatus: 'Internal approval / dispatch status',
        scope: 'Report scope',
        sourceOutcomes: 'Source retrieval outcomes',
        openReportForm: 'Complete the assessment report form',
        openNoticeForm: 'Complete the applicant notice',
        formHint: 'Complete and finalize these installed forms, then select their saved instances as source records. Opening a blank form does not supply evidence.',
        reportTitle: 'Evidence report',
        final: 'Final saved content',
        draft: 'Draft content',
        historical: 'Historical content',
        unverified: 'Unverified source',
        sourceStatusOther: 'Other status',
        captureCurrent: 'Capture current saved content',
        captureHint: 'New or corrected records saved after this instant are outside this report. Keep this time for a historical report, or update it deliberately to include current saved content.',
        sourceFacts: 'Recorded facts',
        provenance: 'Source provenance',
        approveReportConfirm: 'Approve version {version} for release to the report’s intended recipient? This records your approval; it does not record statutory filing, official receipt or an authority decision.',
        consent_evidenceSource: 'Consent receipts and proofs',
        documentRoles: 'Supporting document components',
        historicalUse: 'Use historical evidence for this reporting period',
        historicalHint: 'Historical evidence can support a bounded internal review or authority response only when dated admitted supporting files establish validity throughout the selected period. It does not establish current statutory readiness.',
        historicalReason: 'Reason for using historical evidence',
        validFrom: 'Valid from (ISO date and time with offset)',
        validUntil: 'Valid until (ISO date and time with offset)',
        historicalProof: 'Dated supporting files admitted to this report',
        savedContentErrors: 'Saved form content requires correction',
        historicalCoverageError: 'The stated validity range must cover the entire reporting period.',
        linkTask: 'Link an existing task',
    },
};
//# sourceMappingURL=nl.js.map